// Performance: Track when app.js starts parsing
const APP_JS_PARSE_START = typeof performance !== 'undefined' ? performance.now() : Date.now();
const PERF_MARKS_SEEN = new Set();
if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
    performance.mark('app_start');
    PERF_MARKS_SEEN.add('app_start');
}

let isMobileCache = null;
let isMobileCacheRaf = 0;
const USER_MENU_RAIL_CSS_VAR = '--user-menu-rail-width';
let userMenuRailMeasureRaf = 0;
let userMenuRailResizeObserver = null;
function computeIsMobile() {
    if (typeof window.matchMedia === 'function') {
        return window.matchMedia('(max-width: 768px)').matches || window.matchMedia('(pointer: coarse)').matches;
    }
    return window.innerWidth <= 768;
}

function getIsMobileCached() {
    if (isMobileCache === null) {
        isMobileCache = computeIsMobile();
        if (!isMobileCacheRaf && typeof requestAnimationFrame === 'function') {
            isMobileCacheRaf = requestAnimationFrame(() => {
                isMobileCache = null;
                isMobileCacheRaf = 0;
            });
        }
    }
    return isMobileCache;
}

// Crash-proof wrapper for history.replaceState. Some iOS browser extensions /
// userscripts patch history.replaceState so it throws ("Can only call
// History.replaceState on instances of History"), which previously aborted task
// modal open/close (the modal would not close). Try the normal call; if it
// throws, call the native prototype directly to bypass a broken override; if
// that also fails, give up quietly — a missing URL update must never break the UI.
function safeReplaceState(state, title, url) {
    try {
        window.history.replaceState(state, title, url);
        return;
    } catch (e1) {
        try {
            History.prototype.replaceState.call(window.history, state, title, url);
            return;
        } catch (e2) {
            if (typeof window !== 'undefined' && typeof window.__diag === 'function') {
                window.__diag('replaceState skipped: ' + (e2 && e2.message));
            }
        }
    }
}

/* Breadcrumb into the on-device diagnostic overlay (index.html). No-op unless
   the user has opted in via ?diag=1, so it costs nothing in normal use. */
function diagLog(message) {
    try {
        if (typeof window !== 'undefined' && typeof window.__diag === 'function') {
            window.__diag(message);
        }
    } catch (e) {}
}

const APP_VERSION = '2.7.1';
const APP_VERSION_LABEL = `v${APP_VERSION}`;

// Bridge appState getters/setters to current module-scope state.
function bindAppState() {
    const bindings = {
        projects: () => projects,
        tasks: () => tasks,
        feedbackItems: () => feedbackItems,
        projectCounter: () => projectCounter,
        taskCounter: () => taskCounter,
        feedbackCounter: () => feedbackCounter,
        dependencies: () => dependencies,
        projectsSortedView: () => projectsSortedView,
        selectedCards: () => selectedCards,
        lastSelectedCardId: () => lastSelectedCardId,
        projectToDelete: () => projectToDelete,
        tempAttachments: () => tempAttachments,
        massEditState: () => massEditState,
        projectNavigationReferrer: () => projectNavigationReferrer,
        calendarNavigationState: () => calendarNavigationState,
        previousPage: () => previousPage,
        currentFeedbackScreenshotData: () => currentFeedbackScreenshotData,
        feedbackPendingPage: () => feedbackPendingPage,
        feedbackDonePage: () => feedbackDonePage,
        settings: () => settings
    };

    Object.entries(bindings).forEach(([key, getter]) => {
        Object.defineProperty(appState, key, {
            get: getter,
            set: (val) => {
                switch (key) {
                    case 'projects': projects = val; break;
                    case 'tasks': tasks = val; break;
                    case 'feedbackItems': feedbackItems = val; break;
                    case 'feedbackIndex': feedbackIndex = val; break;
                    case 'projectCounter': projectCounter = val; break;
                    case 'taskCounter': taskCounter = val; break;
                    case 'feedbackCounter': feedbackCounter = val; break;
                    case 'dependencies': dependencies = val; break;
                    case 'projectsSortedView': projectsSortedView = val; break;
                    case 'selectedCards': selectedCards = val; break;
                    case 'lastSelectedCardId': lastSelectedCardId = val; break;
                    case 'projectToDelete': projectToDelete = val; break;
                    case 'tempAttachments': tempAttachments = val; break;
                    case 'massEditState': massEditState = val; break;
                    case 'projectNavigationReferrer': projectNavigationReferrer = val; break;
                    case 'calendarNavigationState': calendarNavigationState = val; break;
                    case 'previousPage': previousPage = val; break;
                    case 'currentFeedbackScreenshotData': currentFeedbackScreenshotData = val; break;
                    case 'feedbackPendingPage': feedbackPendingPage = val; break;
                    case 'feedbackDonePage': feedbackDonePage = val; break;
                    case 'settings': settings = val; break;
                }
            },
            configurable: true,
            enumerable: true
        });
    });
}
bindAppState();

function clearSelectedCards() {
    appState.selectedCards.clear();
    appState.lastSelectedCardId = null;
    document.querySelectorAll(".task-card.selected").forEach((card) => {
        card.classList.remove("selected");
    });
}

// settings now bound via appState.settings

// Debug utilities imported from src/utils/debug.js

const SUPPORTED_LANGUAGES = ['en', 'es'];

// I18N translations loaded from separate module for better code splitting
import { I18N_LOCALES, I18N } from './src/config/i18n.js';
import flatpickr from 'flatpickr';
import { Spanish as flatpickrSpanish } from 'flatpickr/dist/esm/l10n/es.js';

// Note: I18N block moved to src/config/i18n.js (1314 lines extracted)
// This reduces app.js parsing time significantly

function normalizeLanguage(value) {
    return SUPPORTED_LANGUAGES.includes(value) ? value : 'es';
}

function getCurrentLanguage() {
    return normalizeLanguage(settings.language || 'es');
}

function getLocale() {
    const lang = getCurrentLanguage();
    return I18N_LOCALES[lang] || I18N_LOCALES.en;
}

function t(key, vars) {
    const lang = getCurrentLanguage();
    const dict = I18N[lang] || I18N.en;
    let text = dict[key] || I18N.en[key] || key;
    if (!vars) return text;
    return text.replace(/\{\{(\w+)\}\}|\{(\w+)\}/g, (match, doubleName, singleName) => {
        const name = doubleName || singleName;
        if (Object.prototype.hasOwnProperty.call(vars, name)) {
            return String(vars[name]);
        }
        return match;
    });
}

function getProjectArchiveToastKey(count, action) {
    const prefix = action === 'restore' ? 'toast.project.restored' : 'toast.project.archived';
    if (count === 0) return `${prefix}.none`;
    if (count === 1) return `${prefix}.one`;
    return `${prefix}.many`;
}

function applyTranslations(root = document) {
    const elements = root.querySelectorAll('[data-i18n]');
    elements.forEach((el) => {
        const key = el.getAttribute('data-i18n');
        if (!key) return;
        const attr = el.getAttribute('data-i18n-attr');
        if (attr) {
            attr.split(',').map((name) => name.trim()).filter(Boolean).forEach((name) => {
                el.setAttribute(name, t(key));
            });
        } else {
            el.textContent = t(key);
        }
    });
}

// "Click outside" for overlays/backdrops. Selecting text inside a dialog and releasing
// the mouse over its backdrop fires `click` ON the backdrop (the common ancestor of the
// press and the release), so a backdrop click only counts when the press STARTED on
// that same backdrop.
let lastPointerDownTarget = null;
document.addEventListener('pointerdown', (event) => { lastPointerDownTarget = event.target; }, true);

function isBackdropClick(event, backdrop) {
    return event.target === backdrop && lastPointerDownTarget === backdrop;
}

window.i18n = {
    t,
    applyTranslations,
    getCurrentLanguage
};

function getStatusLabel(status) {
    const map = {
        backlog: t('tasks.status.backlog'),
        todo: t('tasks.status.todo'),
        progress: t('tasks.status.progress'),
        review: t('tasks.status.review'),
        done: t('tasks.status.done')
    };
    return map[status] || STATUS_LABELS[status] || status || '';
}

function getProjectStatusLabel(status) {
    const map = {
        backlog: t('projects.status.backlog'),
        planning: t('projects.status.planning'),
        active: t('projects.status.active'),
        completed: t('projects.status.completed'),
        archived: t('projects.status.archived')
    };
    return map[status] || status || '';
}

function getPriorityLabel(priority) {
    const map = {
        high: t('tasks.priority.high'),
        medium: t('tasks.priority.medium'),
        low: t('tasks.priority.low')
    };
    return map[priority] || PRIORITY_LABELS[priority] || priority || '';
}

function applyLanguage() {
    settings.language = getCurrentLanguage();
    document.documentElement.lang = settings.language;
    applyTranslations();
    syncAllSelectTriggers();
    applyDocsNavVisibility();
    sortAskKaiModeChips();
    updateFeedbackPlaceholderForViewport();
    const timeZoneSelect = document.getElementById('email-notification-timezone');
    const timeZoneValue = document.getElementById('email-notification-timezone-value');
    if (timeZoneSelect && timeZoneValue) {
        timeZoneValue.textContent =
            timeZoneSelect.options?.[timeZoneSelect.selectedIndex]?.textContent ||
            timeZoneSelect.value ||
            '';
    }
    const avatarDropzone = document.getElementById('user-avatar-dropzone');
    if (avatarDropzone) {
        avatarDropzone.dataset.defaultText = t('settings.avatarUploadDefault');
        avatarDropzone.dataset.changeText = t('settings.avatarUploadChange');
    }
    const logoDropzone = document.getElementById('workspace-logo-dropzone');
    if (logoDropzone) {
        logoDropzone.dataset.defaultText = t('settings.logoUploadDefault');
        logoDropzone.dataset.changeText = t('settings.logoUploadChange');
    }
    const taskAttachmentDropzone = document.getElementById('attachment-file-dropzone');
    if (taskAttachmentDropzone) {
        const isMobileScreen = getIsMobileCached();
        const attachmentText = isMobileScreen
            ? t('tasks.modal.attachmentsDropzoneTap')
            : t('tasks.modal.attachmentsDropzoneDefault');
        taskAttachmentDropzone.dataset.defaultText = attachmentText;
        const textEl = taskAttachmentDropzone.querySelector('.task-attachment-dropzone-text');
        if (textEl) textEl.textContent = attachmentText;
    }
    const screenshotInput = document.getElementById('feedback-screenshot-url');
    if (screenshotInput) {
        const isMobileScreen = (typeof window.matchMedia === 'function')
            ? window.matchMedia('(max-width: 768px)').matches
            : getIsMobileCached();
        const screenshotDefaultText = isMobileScreen
        ? t('feedback.screenshotDropzoneTap')
        : t('feedback.screenshotDropzoneDefault');
        screenshotInput.dataset.defaultText = screenshotDefaultText;
        if (!screenshotInput.dataset.hasInlineImage) {
            screenshotInput.innerHTML = renderFeedbackDropzoneLabel(screenshotDefaultText);
        }
    }
    updateThemeMenuText();
    refreshUserAvatarSettingsUI();
    document.dispatchEvent(new CustomEvent('refresh-workspace-logo-ui'));
    updateTrendIndicators();
    try { updateSortUI(); } catch (e) {}
    try { updateProjectsUpdatedFilterUI(); } catch (e) {}
    try { updateKanbanUpdatedFilterUI(); } catch (e) {}
    try { refreshProjectsSortLabel(); } catch (e) {}
    renderProjectProgressBars();
    renderActivityFeed();
    renderUpdatesPage();
    updateNotificationState();
    renderInsights();
    try { refreshFlatpickrLocale(); } catch (e) {}
    const activePeriod = document.querySelector('.filter-chip.active')?.dataset?.period || 'week';
    updateDashboardForPeriod(activePeriod);
    const activityPage = document.getElementById('activity-page');
    if (activityPage && activityPage.classList.contains('active')) {
        renderAllActivity();
    }
    // Refresh active pages to ensure all text updates immediately after language change.
    try {
        if (document.getElementById('projects')?.classList.contains('active')) {
            renderProjects();
        }
        if (document.getElementById('tasks')?.classList.contains('active')) {
            renderTasks();
            if (document.getElementById('list-view')?.classList.contains('active')) {
                renderListView();
            }
        }
        if (document.getElementById('calendar')?.classList.contains('active')) {
            renderCalendar();
        }
        if (document.getElementById('feedback')?.classList.contains('active')) {
            renderFeedback();
            updateFeedbackSaveStatus();
        }
        if (document.getElementById('docs')?.classList.contains('active')) {
            renderDocsPage();
        } else {
            updateDocsNavTree(null);
        }
    } catch (e) {}
    document.dispatchEvent(new CustomEvent('nautilus:languagechange', {
        detail: {
            language: settings.language,
            locale: getLocale()
        }
    }));
}

let workspaceLogoDraft = {
    hasPendingChange: false,
    dataUrl: null
};

let avatarDraft = {
    hasPendingChange: false,
    dataUrl: null
};

// Workspace logo crop state
let cropState = {
    originalImage: null,        // Image object
    originalDataUrl: null,      // Original data URL
    canvas: null,               // Canvas element
    ctx: null,                  // Canvas context
    selection: {
        x: 0,
        y: 0,
        size: 0                 // Square size
    },
    isDragging: false,
    isResizing: false,
    dragStartX: 0,
    dragStartY: 0,
    activeHandle: null,
    shape: 'square',            // 'square' | 'circle' (UI + output mask)
    outputMimeType: 'image/jpeg',
    outputMaxSize: null,        // number (px) or null
    onApply: null,
    successMessage: null
};

let defaultWorkspaceIconText = null;

import {
    loadData,
    saveData,
    saveFeedbackItem,
    saveFeedbackIndex,
    deleteFeedbackItem as deleteFeedbackItemStorage,
    batchFeedbackOperations,
    listProjectDocs,
    loadProjectDoc,
    createProjectDoc,
    updateProjectDoc,
    deleteProjectDoc,
    listTrashedProjectDocs,
    restoreTrashedProjectDoc,
    permanentlyDeleteTrashedProjectDoc,
    emptyTrashedProjectDocs,
    updateProjectDocTree,
    publishProjectDoc,
    repairDocPageFiles,
    loadPublishedProjectDoc,
    savePublicDocCheckbox,
    recordDocSaveEvent,
    listProjectDocHistory,
    loadProjectDocRevision,
    restoreProjectDocRevision,
    askKaiGenerate,
    reportSaveSuccess,
    reportSaveFailure,
    isStorageHealthy,
    saveDependencies as saveDependenciesD1,
    saveDocTemplate,
    deleteDocTemplate,
    getDocTemplates,
} from "./storage-client.js?v=20261005-id-reservation";
import {
    saveAll as saveAllData,
    saveTasks as saveTasksData,
    saveProjects as saveProjectsData,
    saveFeedbackItems as saveFeedbackItemsData,
    saveSingleFeedbackItem,
    updateSingleFeedbackItem,
    deleteSingleFeedbackItem,
    saveProjectColors as saveProjectColorsData,
    saveTagColors as saveTagColorsData,
    saveTagColorsVersion as saveTagColorsVersionData,
    saveProjectColorOverrides as saveProjectColorOverridesData,
    saveTagColorOverrides as saveTagColorOverridesData,
    saveSortState as saveSortStateData,
    loadAll as loadAllData,
    loadSortState as loadSortStateData,
    loadProjectColors as loadProjectColorsData,
    loadTagColors as loadTagColorsData,
    loadTagColorsVersion as loadTagColorsVersionData,
    loadProjectColorOverrides as loadProjectColorOverridesData,
    loadTagColorOverrides as loadTagColorOverridesData,
    saveSettings as saveSettingsData,
    loadSettings as loadSettingsData,
    saveTemplates as saveTemplatesData,
    loadTemplates as loadTemplatesData,
    loadAutomations as loadAutomationsData,
    saveAutomations as saveAutomationsData,
    loadSavedPrompts as loadSavedPromptsData,
    saveSavedPrompts as saveSavedPromptsData,
    // Row-level D1 helpers (Phase 2)
    saveTaskCreate as saveTaskCreateData,
    saveTaskUpdate as saveTaskUpdateData,
    saveTaskDelete as saveTaskDeleteData,
    saveTasksReorder as saveTasksReorderData,
    saveProjectCreate as saveProjectCreateData,
    saveProjectUpdate as saveProjectUpdateData,
    saveProjectDelete as saveProjectDeleteData,
    reserveIds as reserveIdsData,
} from "./src/services/storage.js?v=20260317-row-ops";
import {
    createTask as createTaskService,
    updateTask as updateTaskService,
    updateTaskField as updateTaskFieldService,
    deleteTask as deleteTaskService,
    duplicateTask as duplicateTaskService,
    validateTask,
    applyCompletedDateTransition,
    backfillLegacyCompletedDates
} from "./src/services/taskService.js";
import {
    createProject as createProjectService,
    updateProject as updateProjectService,
    updateProjectField as updateProjectFieldService,
    deleteProject as deleteProjectService,
    getProjectTasks,
    validateProject,
    archiveProject as archiveProjectService,
    restoreProject as restoreProjectService,
    summarizeProjectTasks
} from "./src/services/projectService.js";
import {
    buildRuleFromTask,
    toISODate,
    validateRule,
    validateArchiveRule,
    buildArchiveRule,
    selectTasksForAutoArchive,
    archiveRuleShouldRun,
    advanceArchiveRule
} from "./src/services/automationService.js";
import {
    AUTOMATION_TYPES,
    getAutomationType,
    getAvailableAutomationTypes
} from "./src/services/automationTypes.js";
import {
    addDependency,
    removeDependency,
    removeDependenciesForTask,
    getPrerequisites,
    getDependents,
    isTaskBlocked,
    serializeDependencies,
    deserializeDependencies
} from "./src/services/dependencyService.js";
import {
    escapeHtml,
    sanitizeInput,
    resolveLinkPresentation,
    renderPlainTextWithBeautifiedLinks,
    renderSingleLinePreviewWithBeautifiedLinks
} from "./src/utils/html.js";
import {
    looksLikeDMY,
    looksLikeISO,
    toISOFromDMY,
    toDMYFromISO,
    formatDate,
    formatDatePretty,
    formatActivityDate,
    formatDocTimestamp,
    normalizeISODate,
    formatDisplayDate,
    formatDisplayDateTime,
    isoFromDate
} from "./src/utils/date.js";
import { datePickerOptions, attachDateMask } from "./src/utils/datePicker.js";
import { PROJECT_COLORS, hexToRGBA } from "./src/utils/colors.js";
import { buildCompleteBackupPayload } from "./src/utils/backup.js";
import {
    ensureTagColor,
    buildTagColorMapFromTasks,
    TAG_COLOR_ASSIGNMENT_VERSION,
    getTagTextColorForBackground,
    normalizeTagColorKey,
    getTintedTagStyle,
    applyTintedTagStyle
} from "./src/utils/tagColors.js";
import {
    VALID_STATUSES,
    VALID_PRIORITIES,
    STATUS_LABELS,
    PRIORITY_LABELS,
    PRIORITY_ORDER,
    STATUS_ORDER,
    PRIORITY_OPTIONS,
    PRIORITY_COLORS
} from "./src/config/constants.js";
import {
    RELEASE_NOTES,
    getSortedReleaseNotes,
    getLatestReleaseNote,
    getLastSeenReleaseId,
    setLastSeenReleaseId
} from "./src/config/release-notes.js";
import { generateDateChipHTML, generateKanbanDueChipHTML, calculateDateUrgency } from "./src/components/taskCard.js";
import { openContextMenu } from "./src/components/contextMenu.js";
// User profile is now managed by auth.js via window.authSystem
// NOTE: generateWordReport is dynamically imported in generateReport() to avoid blocking load
import {
    applyDebugLogSetting,
    isDebugLogsEnabled,
    logDebug,
    logPerformanceMilestone,
    debugTimeStart,
    debugTimeEnd
} from "./src/utils/debug.js";
import {
    showNotification,
    showErrorNotification,
    showSuccessNotification,
    showActionNotification
} from "./src/ui/notification.js";
import {
    convertFileToBase64,
    uploadFile,
    deleteFile,
    buildFileReadUrl
} from "./src/utils/file.js";
// Note: convertFileToBase64 is kept for avatar/logo canvas crops only.
import { isValidEmailAddress, isDeleteConfirmation } from "./src/utils/validation.js";
import { capitalizeFirst } from "./src/utils/string.js";
import {
    getCalendarDayNames,
    formatCalendarMonthYear,
    formatCalendarMonthYearShort,
    stripTime
} from "./src/utils/date.js";
import {
    DEFAULT_USER_TIMEZONE,
    SUPPORTED_USER_TIMEZONES,
    getDetectedUserTimeZone,
    coerceSupportedUserTimeZone,
    normalizeHHMM,
    snapHHMMToStep,
    hhmmToMinutes,
    minutesToHHMM,
    clampHHMMToRange,
    getKanbanUpdatedCutoffTime,
    getTaskUpdatedTime,
    formatTaskUpdatedDateTime
} from "./src/utils/time.js";
import {
    debounce,
    toggleSet
} from "./src/utils/functional.js";
import {
    filterTasks,
    getTodayISO,
    getDateOffsetISO,
    matchesSearch,
    matchesStatus,
    matchesPriority,
    matchesProject,
    matchesTags,
    matchesAnyDatePreset,
    matchesDateRange
} from "./src/utils/filterPredicates.js";
import { buildKaiVocabulary, serializeKaiVocabularyForLLM } from "./src/utils/kaiVocabulary.js";
import { parseKaiQuery, isKaiQueryLikeInstruction } from "./src/utils/kaiQueryParser.js";
import {
    appendAskKaiUnresolvedProjectNote,
    resolveAskKaiProjectReference
} from "./utils/ask-kai-semantics.js";
import {
    kaiQueryToFilterState,
    kaiQueryToProjectFilterState,
    filterStateToKaiQuery,
    describeKaiQuery,
    explainEmptyKaiResult,
    validateKaiQueryAgainstVocabulary,
    buildKaiQueryReview,
    stripUnaskedNoneSentinels,
    stripUnaskedArchivedScope,
    excludeDoneForDueQueries,
    mergeKaiQuery,
    normalizeKaiQuery,
    countKaiQueryFilters
} from "./src/utils/kaiQuery.js";
import {
    calculateDashboardStats,
    calculateProjectProgress,
    calculateTrendIndicators,
    currentMonthStartIso,
    buildAttentionQueue,
    buildDueOverdueAgenda,
    buildUpcomingQueue,
    generateInsightsData,
    getRelativeTimeInfo,
    generateProgressBarsHTML,
    generateActivityFeedHTML
} from "./src/views/dashboard.js";
import { computeSummaryReport, PACE_TOLERANCE } from "./src/services/reportMetrics.js";
import { renderSummaryReportHtml } from "./src/services/reportView.js";
import { buildSummaryReportDoc } from "./src/services/reportDoc.js";
import { buildTasksXlsx } from "./src/services/xlsxExport.js";
import {
    groupTasksByStatus,
    sortGroupedTasks,
    getStatusCounts,
    prepareKanbanData,
    generateKanbanColumnHTML,
    computeManualInsertIndex
} from "./src/views/kanban.js";
import {
    sortTasksByPriorityAndDate,
    sortTasksByColumn,
    calculateSmartDateInfo,
    prepareListViewData,
    generateListViewHTML
} from "./src/views/listView.js";
import {
    calculateCalendarGrid,
    generateCalendarDays,
    scopeTasksToProjects,
    countTasksInMonth,
    formatDateISO,
    countOverlappingProjects,
    getTasksForDate,
    calculateMonthNavigation,
    isCurrentMonth as isCurrentMonthFn,
    prepareCalendarData,
    generateCalendarGridHTML,
    computeBarLayout
} from "./src/views/calendar.js";
import {
    calculateProjectTaskStats,
    calculateCompletionPercentage,
    determineProjectStatus,
    sortProjects as sortProjectsFn,
    filterProjectsBySearch,
    filterProjectsByStatus,
    prepareProjectsViewData,
    generateProjectItemHTML as generateProjectItemHTMLModule,
    generateProjectsListHTML
} from "./src/views/projectsView.js";
import {
    calculateMobileFieldPlacement,
    shouldHideDetailsTab,
    generateTagsDisplayHTML,
    getInitialDateState,
    calculateTaskNavigation
} from "./src/components/taskDetails.js";
import { setupEventDelegation } from "./src/core/events.js";
import { appState } from "./src/core/state.js";
import {
    initializeTaskDescriptionEditor,
    focusTaskDescriptionEditor,
    getTaskDescriptionDateChipState,
    getTaskDescriptionHTML,
    setTaskDescriptionHTML,
    clearTaskDescriptionEditor,
    initializeProjectDescriptionEditor,
    setProjectDescriptionText,
    setProjectDescriptionHTML,
    getProjectDescriptionHTML,
    clearProjectDescriptionEditor,
    destroyProjectDescriptionEditor,
    runProjectDescriptionCommand,
    insertProjectDescriptionHeading,
    insertProjectDescriptionDivider,
    insertProjectDescriptionChecklist,
    insertProjectDescriptionDateChip,
    updateProjectDescriptionDateChip,
    getProjectDescriptionDateChipState,
    focusProjectDescriptionEditor,
    setDescriptionLinkPresentationContextResolver,
    runTaskDescriptionCommand,
    insertTaskDescriptionHeading,
    insertTaskDescriptionDivider,
    insertTaskDescriptionChecklist,
    insertTaskDescriptionDateChip,
    updateTaskDescriptionDateChip
} from "./src/editor/descriptionEditor.js";
import {
    initializeDocsEditor,
    destroyDocsEditor,
    setDocsEditorContent,
    getDocsEditorJSON,
    getDocsEditorHistoryAvailability,
    getDocsEditorSelectionInfo,
    replaceDocsEditorSelection,
    replaceDocsEditorContentLive,
    applyDocsStatusChips,
    runDocsEditorCommand,
    getDocsSlashContext,
    getDocsCaretRect,
    deleteDocsRange,
    insertDocsImage,
    insertDocsFileAttachment,
    insertDocsYoutubeVideo,
    extractDocsToc,
    focusDocsEditor,
    setDocsEditorEditable,
    toggleDocsEmojiPicker,
    getDocsEditorTableState,
    getDocsEditorTableWrapper,
    copyDocsTableAsText,
    refreshDocsEditorLayout,
    getDocsEditorStatusChipState,
    getDocsEditorDateChipState,
    getDocsCalloutState,
    getDocsEditorLinkState,
    refreshDocsChildListBlocks,
    findDocsTextMatches,
    selectDocsMatch,
    replaceDocsMatch,
    replaceAllDocsText,
    normalizeReadonlyDocsTables,
    setDocsLinkPresentationContextResolver,
    setDocsFileAttachmentResolver,
    setDocsFileUploadHandler
} from "./src/editor/docsEditor.js";

const APP_JS_IMPORTS_READY = typeof performance !== 'undefined' ? performance.now() : Date.now();
const APP_JS_NAV_START = (typeof window !== 'undefined' && typeof window.__pageLoadStart === 'number')
    ? window.__pageLoadStart
    : APP_JS_PARSE_START;
logPerformanceMilestone('app-js-executed', {
    sinceNavStartMs: Math.round(APP_JS_IMPORTS_READY - APP_JS_NAV_START)
});

try {
    const handleViewportMetricsChange = debounce(() => {
        isMobileCache = null;
        scheduleUserMenuRailWidthUpdate();
    }, 150);
    window.addEventListener('resize', handleViewportMetricsChange);
    window.addEventListener('orientationchange', () => {
        isMobileCache = null;
        scheduleUserMenuRailWidthUpdate();
    });
} catch (e) {}

const PERF_DEBUG_QUERY_KEY = 'debugPerf';
let perfDebugForced = false;

function isPerfDebugQueryEnabled() {
    try {
        const url = new URL(window.location.href);
        const value = url.searchParams.get(PERF_DEBUG_QUERY_KEY);
        return value === '1' || value === 'true';
    } catch (e) {
        return false;
    }
}

function markPerfOnce(label, meta) {
    if (PERF_MARKS_SEEN.has(label)) return;
    PERF_MARKS_SEEN.add(label);
    if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
        performance.mark(label);
    }
    logPerformanceMilestone(label, meta);
}

// ==== CORE STATE BINDINGS ====
let projects = [];
let tasks = [];
let automations = [];
let savedPrompts = [];
let feedbackItems = [];
let projectCounter = 1;
let taskCounter = 1;
let feedbackCounter = 1;

// ── Server-reserved id blocks ─────────────────────────────────────────────────
// New task/project ids come from a block the server reserved for THIS tab, so two
// devices (or a stale tab and the recurring job) never hand out the same id.
// taskCounter/projectCounter stay "next id to use"; these track where the block
// ends. 0 = no block yet → falls back to max(id)+1 until one arrives.
const ID_BLOCK_SIZE = { task: 200, project: 50 };
const ID_BLOCK_LOW_WATER = { task: 50, project: 10 };
let taskIdBlockEnd = 0;
let projectIdBlockEnd = 0;
let idBlockGeneration = 0;
const idBlockRequests = { task: null, project: null };

function remainingReservedIds(kind) {
    const end = kind === 'project' ? projectIdBlockEnd : taskIdBlockEnd;
    const next = kind === 'project' ? projectCounter : taskCounter;
    return end > 0 ? end - next + 1 : 0;
}

function applyIdBlock(kind, block) {
    // Never move the counter backwards: ids below it are already in use on this tab.
    if (kind === 'project') {
        if (projectCounter > block.end) return;
        projectCounter = Math.max(projectCounter, block.start);
        projectIdBlockEnd = block.end;
    } else {
        if (taskCounter > block.end) return;
        taskCounter = Math.max(taskCounter, block.start);
        taskIdBlockEnd = block.end;
    }
}

function requestIdBlock(kind, count) {
    if (!idBlockRequests[kind]) {
        const generation = idBlockGeneration;
        idBlockRequests[kind] = reserveIdsData(kind, count)
            .then((block) => {
                if (generation !== idBlockGeneration) return false; // user switched meanwhile
                applyIdBlock(kind, block);
                logDebug('storage', `reserved ${kind} ids`, block);
                return true;
            })
            .catch((error) => {
                logDebug('storage', `${kind} id reservation failed; using max(id)+1`, { error: error?.message || String(error) });
                return false;
            })
            .finally(() => { idBlockRequests[kind] = null; });
    }
    return idBlockRequests[kind];
}

// Background top-up so creating stays synchronous (called after every create).
function topUpIdBlocks() {
    ['task', 'project'].forEach((kind) => {
        if (remainingReservedIds(kind) < ID_BLOCK_LOW_WATER[kind]) {
            void requestIdBlock(kind, ID_BLOCK_SIZE[kind]);
        }
    });
}

// Before bulk creates: make sure `needed` ids fit in the current block.
async function ensureReservedIds(kind, needed) {
    if (remainingReservedIds(kind) >= needed) return;
    if (idBlockRequests[kind]) await idBlockRequests[kind];
    if (remainingReservedIds(kind) >= needed) return;
    await requestIdBlock(kind, Math.max(ID_BLOCK_SIZE[kind], needed));
}

function resetIdBlocks() {
    idBlockGeneration++;
    taskIdBlockEnd = 0;
    projectIdBlockEnd = 0;
}

let dependencies = {}; // Task dependency graph: { taskId: [prerequisiteTaskIds] }
let projectsSortedView = null;
let selectedCards = new Set();
let lastSelectedCardId = null;
let projectToDelete = null;
let tempAttachments = [];
let currentImageGallery = []; // image attachments for current task, used by gallery viewer
// Mass Edit state
let massEditState = {
    selectedTaskIds: new Set(),  // Set of selected task IDs
    lastSelectedId: null,         // For shift-click range selection
    isToolbarVisible: false,      // Toolbar visibility
    pendingChanges: []            // Array of queued changes (multiple fields)
};
let projectNavigationReferrer = 'projects';
let calendarNavigationState = null;
let previousPage = '';
let currentFeedbackScreenshotData = "";
let currentFeedbackAttachmentFile = null; // raw File object for non-image attachments
let feedbackPendingPage = 1;
let feedbackDonePage = 1;
let docsState = null;
let docsWorkspaceActive = false;
let _storageHealthy = true;
let recurrenceDraft = null;
let pendingRecurrenceAfterTaskCreate = null;

function createDefaultRecurrenceDraft() {
    const today = new Date();
    return {
        automationId: null,
        name: '',
        sourceTaskId: null,
        sourceMode: 'search',        // 'search' | 'new' — redesign sliding pill
        pattern: 'daily',
        daysOfWeek: [today.getDay()],
        intervalDays: 1,
        intervalUnit: 'days',
        endDate: '',
        endMode: 'never',            // 'never' | 'date' | 'count' — redesign
        maxOccurrences: null,        // used when endMode === 'count'
        monthlyMode: 'date',         // 'date' | 'weekday' — redesign radio
        firstOccurrenceDate: '',
        taskStartDateMode: '',
        taskEndDateMode: '',
        taskEndDateOffset: 1
    };
}

function createDocsState() {
    return {
        loaded: false,
        loading: false,
        loadingDocId: null,
        items: [],
        currentDocId: null,
        editorBoundDocId: null,
        editorGeneration: 0,
        baselineDocId: null,
        baselineGeneration: 0,
        baselineSnapshot: '',
        dirty: false,
        saving: false,
        hydrating: false,
        deferredSilentSaveContext: null,
        pendingCheckboxSaveContext: null,
        checkboxAutoSave: null,
        contentAutoSave: null,
        lastAutoSavedAt: 0,
        sessionAutoSaved: false,
        lastBaselineMismatchEventKey: '',
        migrationDone: false,
        status: 'idle',
        flashMessage: '',
        flashState: '',
        flashTimer: null,
        error: '',
        conflict: null,
        bulkMode: false,
        selectedDocIds: new Set(),
        trashLoading: false,
        trashItems: []
    };
}

docsState = createDocsState();

function showCfOutageBanner() {
    if (document.getElementById('cf-outage-banner')) return;
    const el = document.createElement('div');
    el.id = 'cf-outage-banner';
    el.className = 'cf-outage-banner';
    el.innerHTML = `
        <span class="cf-outage-msg" data-i18n="error.cfOutageBanner">${t('error.cfOutageBanner')}</span>
        <a class="cf-outage-link" data-i18n="error.cfOutageContact" href="mailto:franciscovillanu@gmail.com">
            ${t('error.cfOutageContact')}
        </a>
        <button type="button" class="cf-outage-close" aria-label="Dismiss" title="Dismiss">×</button>
    `;
    el.querySelector('.cf-outage-close')?.addEventListener('click', () => hideCfOutageBanner());
    document.body.prepend(el);
    document.body.classList.add('has-cf-outage');
}

function hideCfOutageBanner() {
    document.getElementById('cf-outage-banner')?.remove();
    document.body.classList.remove('has-cf-outage');
}

const FEEDBACK_ITEMS_PER_PAGE = 10;
const FEEDBACK_OPEN_ITEMS_PER_PAGE = 20;
const FEEDBACK_CACHE_KEY = "feedbackItemsCache:v1";
// The user's effective time zone (configured setting, else detected/default).
function getUserTimeZone() {
    return coerceSupportedUserTimeZone(settings?.emailNotificationTimeZone);
}

// "Now" decomposed in the user's configured time zone — used for greeting,
// today's date, and any "is it today" comparison so the browser clock and a
// different configured zone never disagree (e.g. just past midnight in Canary).
function getNowPartsInUserTimeZone() {
    const tz = getUserTimeZone();
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
        }).formatToParts(new Date());
        const get = (type) => parts.find(p => p.type === type)?.value || '';
        let hour = parseInt(get('hour'), 10);
        if (hour === 24 || Number.isNaN(hour)) hour = 0; // some engines emit 24 at midnight
        return {
            isoDate: `${get('year')}-${get('month')}-${get('day')}`,
            hour
        };
    } catch (e) {
        const now = new Date();
        return {
            isoDate: now.toISOString().split('T')[0],
            hour: now.getHours()
        };
    }
}
// settings defaults
function getDefaultSettings() {
    return {
        autoSetStartDateOnStatusChange: false,
        autoSetEndDateOnStatusChange: false,
        enableReviewStatus: true,
        calendarIncludeBacklog: false,
        calendarShowProjects: true,
        calendarShowTasks: true,
        debugLogsEnabled: false,
        diagnosticsOverlayEnabled: false,
        historySortOrder: 'newest',
        docsNavHidden: false,
        language: 'es',
        customWorkspaceLogo: null,
        notificationEmail: "",
        emailNotificationsEnabled: true,
        emailNotificationsWeekdaysOnly: false,
        emailNotificationsIncludeStartDates: false,
        emailNotificationsIncludeBacklog: false,
        emailNotificationsIncludeWeekAhead: true,
        emailNotificationTime: "09:00",
        emailNotificationTimeZone: getDetectedUserTimeZone()
    };
}
let settings = getDefaultSettings();

if (isPerfDebugQueryEnabled()) {
    perfDebugForced = true;
    settings.debugLogsEnabled = true;
    applyDebugLogSetting(true);
}

// keep appState in sync via accessors (live bindings)
[
    'projects',
    'tasks',
    'feedbackItems',
    'projectCounter',
    'taskCounter',
    'feedbackCounter',
    'dependencies',
    'projectsSortedView',
    'selectedCards',
    'lastSelectedCardId',
    'projectToDelete',
    'tempAttachments',
    'projectNavigationReferrer',
    'calendarNavigationState',
    'previousPage',
    'currentFeedbackScreenshotData',
    'feedbackPendingPage',
    'feedbackDonePage',
    'settings'
].forEach((key) => {
    Object.defineProperty(appState, key, {
        get: () => {
            // Explicit getters (eval doesn't work with bundling)
            switch (key) {
                case 'projects': return projects;
                case 'tasks': return tasks;
                case 'feedbackItems': return feedbackItems;
                case 'projectCounter': return projectCounter;
                case 'taskCounter': return taskCounter;
                case 'feedbackCounter': return feedbackCounter;
                case 'dependencies': return dependencies;
                case 'projectsSortedView': return projectsSortedView;
                case 'selectedCards': return selectedCards;
                case 'lastSelectedCardId': return lastSelectedCardId;
                case 'projectToDelete': return projectToDelete;
                case 'tempAttachments': return tempAttachments;
                case 'projectNavigationReferrer': return projectNavigationReferrer;
                case 'calendarNavigationState': return calendarNavigationState;
                case 'previousPage': return previousPage;
                case 'currentFeedbackScreenshotData': return currentFeedbackScreenshotData;
                case 'feedbackPendingPage': return feedbackPendingPage;
                case 'feedbackDonePage': return feedbackDonePage;
                case 'settings': return settings;
            }
        },
        set: (val) => {
            switch (key) {
                case 'projects': projects = val; break;
                case 'tasks': tasks = val; break;
                case 'feedbackItems': feedbackItems = val; break;
                case 'projectCounter': projectCounter = val; break;
                case 'taskCounter': taskCounter = val; break;
                case 'feedbackCounter': feedbackCounter = val; break;
                case 'dependencies': dependencies = val; break;
                case 'projectsSortedView': projectsSortedView = val; break;
                case 'selectedCards': selectedCards = val; break;
                case 'lastSelectedCardId': lastSelectedCardId = val; break;
                case 'projectToDelete': projectToDelete = val; break;
                case 'tempAttachments': tempAttachments = val; break;
                case 'projectNavigationReferrer': projectNavigationReferrer = val; break;
                case 'calendarNavigationState': calendarNavigationState = val; break;
                case 'previousPage': previousPage = val; break;
                case 'currentFeedbackScreenshotData': currentFeedbackScreenshotData = val; break;
                case 'feedbackPendingPage': feedbackPendingPage = val; break;
                case 'feedbackDonePage': feedbackDonePage = val; break;
                case 'settings': settings = val; break;
            }
        },
        configurable: true,
        enumerable: true
    });
});

// Expose storage functions for historyService
window.saveData = saveData;
window.loadData = loadData;

// Guard to avoid persisting to storage while the app is initializing/loading
let isInitializing = false;

// Track pending save operations to prevent data loss on page unload
// This counter tracks all async save operations in flight. The beforeunload
// listener warns users if they try to close the tab/browser with pending saves.
// Combined with await on all save calls, this provides two layers of protection:
// 1. await prevents in-app navigation before saves complete
// 2. beforeunload warns user before browser/tab close with pending saves
let pendingSaves = 0;

// Notification functions imported from src/ui/notification.js

// Debounced project field updates (used to ensure fields like description save even if the DOM is removed before blur/change fires)
const __projectFieldDebounceTimers = new Map();
function debouncedUpdateProjectField(projectId, field, value, options) {
    const opts = options || {};
    const delayMs = Number.isFinite(opts.delayMs) ? opts.delayMs : 500;
    const key = `${projectId}:${field}`;
    const existing = __projectFieldDebounceTimers.get(key);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
        __projectFieldDebounceTimers.delete(key);
        updateProjectField(projectId, field, value, opts);
    }, delayMs);
    __projectFieldDebounceTimers.set(key, timer);
}

function flushDebouncedProjectField(projectId, field, value, options) {
    const key = `${projectId}:${field}`;
    const existing = __projectFieldDebounceTimers.get(key);
    if (existing) {
        clearTimeout(existing);
        __projectFieldDebounceTimers.delete(key);
    }
    updateProjectField(projectId, field, value, options || {});
}

function validateDateRange() {
    // Validation removed - flatpickr constraints prevent invalid date selection
    // No need to validate or disable buttons since users can't pick invalid dates
    return true;
}

const DUE_TODAY_SEEN_STORAGE_KEY = 'nautilusDueTodaySeen';
const NOTIFICATION_HISTORY_KEY = 'nautilusNotificationHistory';
const NOTIFICATION_HISTORY_MAX_DAYS = 30;
const NOTIFICATION_STATE_CACHE_TTL = 30000;
const NOTIFICATION_PENDING_SEEN_KEY = 'nautilusNotificationPendingSeen';
const USE_SERVER_NOTIFICATIONS = true;
let notificationStateCache = { timestamp: 0, data: null };
let notificationPendingFlushInFlight = false;

function resolveReleaseText(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        const locale = getLocale();
        const lang = locale && locale.startsWith('es') ? 'es' : 'en';
        return value[lang] || value.en || value.es || '';
    }
    return String(value);
}

function formatReleaseDate(dateStr) {
    if (!dateStr) return '';
    return formatDatePretty(dateStr, getLocale());
}

function getSeenDueToday() {
    try {
        const raw = localStorage.getItem(DUE_TODAY_SEEN_STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        const today = new Date().toISOString().split('T')[0];
        if (!data || data.date !== today || !Array.isArray(data.ids)) return null;
        return data;
    } catch (e) {
        return null;
    }
}

function setSeenDueToday(taskIds) {
    try {
        const today = new Date().toISOString().split('T')[0];
        localStorage.setItem(
            DUE_TODAY_SEEN_STORAGE_KEY,
            JSON.stringify({ date: today, ids: taskIds || [] })
        );
    } catch (e) {}
}

function getUnseenDueTodayCount(dueToday) {
    if (!dueToday || dueToday.length === 0) return 0;
    const seen = getSeenDueToday();
    if (!seen) return dueToday.length;
    const seenSet = new Set(seen.ids || []);
    return dueToday.filter((task) => !task || !task.id || !seenSet.has(task.id)).length;
}

function getDueTodayTasks() {
    const today = new Date().toISOString().split('T')[0];
    return tasks.filter((task) => {
        if (!task || task.status === 'done') return false;
        const due = normalizeISODate(task.endDate || '');
        const start = normalizeISODate(task.startDate || '');
        return (due !== '' && due === today) || (start !== '' && start === today);
    });
}

// ============================================================================
// Notification History System
// ============================================================================

function getNotificationHistory() {
    try {
        const raw = localStorage.getItem(NOTIFICATION_HISTORY_KEY);
        if (!raw) return { notifications: [], lastChecked: null };
        const data = JSON.parse(raw);
        return {
            notifications: Array.isArray(data.notifications) ? data.notifications : [],
            lastChecked: data.lastChecked || null
        };
    } catch (e) {
        return { notifications: [], lastChecked: null };
    }
}

function saveNotificationHistory(history) {
    try {
        localStorage.setItem(NOTIFICATION_HISTORY_KEY, JSON.stringify(history));
    } catch (e) {}
}

function createNotificationId(type, taskId, date) {
    return `notif_${date}_${type}_${taskId}`;
}

function addTaskNotification(taskId, dueDate) {
    const history = getNotificationHistory();
    const notifId = createNotificationId('task_due', taskId, dueDate);

    // Check if notification already exists
    const exists = history.notifications.some(n => n.id === notifId);
    if (exists) return;

    const notification = {
        id: notifId,
        type: 'task_due',
        taskId: taskId,
        date: dueDate,
        read: false,
        dismissed: false,
        createdAt: new Date().toISOString()
    };

    history.notifications.push(notification);
    saveNotificationHistory(history);
}

// OPTIMIZED: Batch all notification creation to reduce localStorage operations
function checkAndCreateDueTodayNotifications() {
    // Safety check: don't run if tasks array is not initialized
    if (!Array.isArray(tasks) || tasks.length === 0) return;

    const notifyTimer = debugTimeStart("notifications", "create-due-today", {
        taskCount: tasks.length
    });
    const today = new Date().toISOString().split('T')[0];
    const history = getNotificationHistory();

    // Build a Set of existing notification IDs for fast lookup
    const existingIds = new Set(history.notifications.map(n => n.id));
    const newNotifications = [];

    // Collect tasks starting and due today
    // Check if start date notifications are enabled
    const includeStartDates = settings.emailNotificationsIncludeStartDates !== false;
    const includeBacklog = !!settings.emailNotificationsIncludeBacklog; // Default to false (exclude backlog)

    tasks.forEach(task => {
        if (!task || task.status === 'done' || !task.id) return;

        // Skip backlog tasks unless user has enabled them in settings
        if (task.status === 'backlog' && !includeBacklog) return;

        const start = normalizeISODate(task.startDate || '');
        const due = normalizeISODate(task.endDate || '');

        // Create start notification if starting today (only if setting is enabled)
        if (includeStartDates && start === today) {
            const notifId = createNotificationId('task_starting', task.id, today);
            if (!existingIds.has(notifId)) {
                newNotifications.push({
                    id: notifId,
                    type: 'task_starting',
                    taskId: task.id,
                    date: today,
                    read: false,
                    dismissed: false,
                    createdAt: new Date().toISOString()
                });
            }
        }

        // Create due notification if due today
        if (due === today) {
            const notifId = createNotificationId('task_due', task.id, today);
            if (!existingIds.has(notifId)) {
                newNotifications.push({
                    id: notifId,
                    type: 'task_due',
                    taskId: task.id,
                    date: today,
                    read: false,
                    dismissed: false,
                    createdAt: new Date().toISOString()
                });
            }
        }
    });

    // CATCH-UP: Collect notifications for tasks in past 7 days
    const pastDays = 7;
    for (let i = 1; i <= pastDays; i++) {
        const pastDate = new Date();
        pastDate.setDate(pastDate.getDate() - i);
        const pastDateStr = pastDate.toISOString().split('T')[0];

        tasks.forEach(task => {
            if (!task || task.status === 'done' || !task.id) return;

            const start = normalizeISODate(task.startDate || '');
            const due = normalizeISODate(task.endDate || '');

            // Create past start notification (only if setting is enabled)
            if (includeStartDates && start === pastDateStr) {
                const notifId = createNotificationId('task_starting', task.id, pastDateStr);
                if (!existingIds.has(notifId)) {
                    newNotifications.push({
                        id: notifId,
                        type: 'task_starting',
                        taskId: task.id,
                        date: pastDateStr,
                        read: false,
                        dismissed: false,
                        createdAt: new Date().toISOString()
                    });
                }
            }

            // Create past due notification
            if (due === pastDateStr) {
                const notifId = createNotificationId('task_due', task.id, pastDateStr);
                if (!existingIds.has(notifId)) {
                    newNotifications.push({
                        id: notifId,
                        type: 'task_due',
                        taskId: task.id,
                        date: pastDateStr,
                        read: false,
                        dismissed: false,
                        createdAt: new Date().toISOString()
                    });
                }
            }
        });
    }

    // Only write to localStorage if there are new notifications
    if (newNotifications.length > 0) {
        history.notifications.push(...newNotifications);
    }

    // Update last checked
    history.lastChecked = new Date().toISOString();

    // Single localStorage write instead of multiple
    saveNotificationHistory(history);
    debugTimeEnd("notifications", notifyTimer, {
        newCount: newNotifications.length,
        totalCount: history.notifications.length
    });
}

function markNotificationRead(notificationId) {
    const history = getNotificationHistory();
    const notif = history.notifications.find(n => n.id === notificationId);
    if (notif) {
        notif.read = true;
        saveNotificationHistory(history);
    }
}

function markAllNotificationsRead() {
    const history = getNotificationHistory();
    history.notifications.forEach(n => {
        if (!n.dismissed) {
            n.read = true;
        }
    });
    saveNotificationHistory(history);
}

function dismissNotification(notificationId) {
    const history = getNotificationHistory();
    const notif = history.notifications.find(n => n.id === notificationId);
    if (notif) {
        notif.dismissed = true;
        saveNotificationHistory(history);
    }
}

function dismissNotificationsByDate(date) {
    const history = getNotificationHistory();
    history.notifications.forEach(n => {
        if (n.date === date) {
            n.dismissed = true;
        }
    });
    saveNotificationHistory(history);
}

function dismissAllNotifications() {
    const history = getNotificationHistory();
    history.notifications.forEach(n => {
        n.dismissed = true;
    });
    saveNotificationHistory(history);
}

function cleanupOldNotifications() {
    const cleanupTimer = debugTimeStart("notifications", "cleanup");
    const history = getNotificationHistory();
    const beforeCount = history.notifications.length;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - NOTIFICATION_HISTORY_MAX_DAYS);
    const cutoffISO = cutoffDate.toISOString().split('T')[0];

    history.notifications = history.notifications.filter(n => {
        return n.date >= cutoffISO;
    });

    saveNotificationHistory(history);
    debugTimeEnd("notifications", cleanupTimer, {
        beforeCount,
        afterCount: history.notifications.length
    });
}

function getActiveNotifications() {
    const history = getNotificationHistory();
    return history.notifications.filter(n => !n.dismissed);
}

function getUnreadNotificationCount() {
    const active = getActiveNotifications();
    return active.filter(n => !n.read).length;
}

function getTaskNotificationsByDate() {
    const active = getActiveNotifications();
    const taskNotifs = active.filter(n => n.type === 'task_due' || n.type === 'task_starting');

    // Group by date
    const grouped = new Map();
    taskNotifs.forEach(notif => {
        if (!grouped.has(notif.date)) {
            grouped.set(notif.date, []);
        }
        grouped.get(notif.date).push(notif);
    });

    // Sort dates descending (most recent first)
    const sorted = Array.from(grouped.entries()).sort((a, b) => {
        return b[0].localeCompare(a[0]);
    });

    return sorted;
}

// ============================================================================

function buildNotificationState() {
    const stateTimer = debugTimeStart("notifications", "build-state");
    // Notification creation happens once at app init, not on every state build
    const latest = getLatestReleaseNote();
    const lastSeen = getLastSeenReleaseId();
    // Release notifications are currently hidden from the dropdown, so suppress their badge.
    const releaseUnseen = false;

    const taskNotificationsByDate = getTaskNotificationsByDate();
    const unreadCount = getUnreadNotificationCount();

    const state = {
        latest,
        releaseUnseen,
        taskNotificationsByDate,
        unreadCount
    };
    debugTimeEnd("notifications", stateTimer, {
        unreadCount,
        taskGroups: taskNotificationsByDate.length
    });
    return state;
}

function getNotificationAuthToken() {
    return window.authSystem?.getAuthToken?.() || localStorage.getItem('authToken');
}

function loadPendingNotificationSeen() {
    const raw = localStorage.getItem(NOTIFICATION_PENDING_SEEN_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.seenAt) return null;
        return parsed;
    } catch (e) {
        return null;
    }
}

function savePendingNotificationSeen(payload) {
    if (!payload || !payload.seenAt) return;
    localStorage.setItem(NOTIFICATION_PENDING_SEEN_KEY, JSON.stringify(payload));
}

function queueNotificationSeen(seenAt, clearAll = false) {
    const existing = loadPendingNotificationSeen();
    if (!existing) {
        savePendingNotificationSeen({ seenAt, clearAll: !!clearAll });
        return;
    }
    const existingTime = new Date(existing.seenAt).getTime();
    const nextTime = new Date(seenAt).getTime();
    const merged = {
        seenAt: Number.isFinite(existingTime) && Number.isFinite(nextTime) && existingTime > nextTime
            ? existing.seenAt
            : seenAt,
        clearAll: !!(existing.clearAll || clearAll)
    };
    savePendingNotificationSeen(merged);
}

async function flushPendingNotificationSeen() {
    if (!USE_SERVER_NOTIFICATIONS || notificationPendingFlushInFlight) return;
    const pending = loadPendingNotificationSeen();
    if (!pending) return;
    const token = getNotificationAuthToken();
    if (!token) return;

    notificationPendingFlushInFlight = true;
    try {
        const payload = {
            seenAt: pending.seenAt,
            clearAll: !!pending.clearAll
        };
        // Include dismissDate if present
        if (pending.dismissDate) {
            payload.dismissDate = pending.dismissDate;
        }

        const response = await fetch('/api/notifications/state', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });
        if (response.ok) {
            localStorage.removeItem(NOTIFICATION_PENDING_SEEN_KEY);
        }
    } catch (error) {
        console.warn('[notifications] failed to flush pending seen', error);
    } finally {
        notificationPendingFlushInFlight = false;
    }
}

async function fetchNotificationState({ force = false } = {}) {
    if (USE_SERVER_NOTIFICATIONS) {
        await flushPendingNotificationSeen();
        const now = Date.now();
        if (!force && notificationStateCache.data && (now - notificationStateCache.timestamp) < NOTIFICATION_STATE_CACHE_TTL) {
            return notificationStateCache.data;
        }

        const token = getNotificationAuthToken();
        if (token) {
            try {
                const response = await fetch('/api/notifications/state', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    const payload = await response.json();
                    const latest = getLatestReleaseNote();
                    const releaseUnseen = false;
                    const state = {
                        latest,
                        releaseUnseen,
                        taskNotificationsByDate: Array.isArray(payload.taskNotificationsByDate) ? payload.taskNotificationsByDate : [],
                        unreadCount: Number(payload.unreadCount) || 0
                    };
                    notificationStateCache = { timestamp: now, data: state };
                    return state;
                }
            } catch (error) {
                console.warn('[notifications] failed to fetch server state', error);
            }
        }
    }

    return buildNotificationState();
}

function updateNotificationBadge(state = buildNotificationState()) {
    const badge = document.getElementById('notification-count');
    if (!badge) return;
    const releaseCount = state.releaseUnseen && state.latest ? 1 : 0;
    const total = releaseCount + state.unreadCount;
    if (total > 0) {
        badge.textContent = total > 99 ? '99+' : String(total);
        badge.classList.add('is-visible');
    } else {
        badge.textContent = '';
        badge.classList.remove('is-visible');
    }
}

function getRelativeDateLabel(dateStr) {
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (dateStr === today) return t('notifications.today') || 'Today';
    if (dateStr === yesterdayStr) return t('notifications.yesterday') || 'Yesterday';
    return formatDatePretty(dateStr, getLocale());
}

function renderNotificationDropdown(state = buildNotificationState()) {
    const list = document.getElementById('notification-list');
    if (!list) return;
    const renderTimer = debugTimeStart("notifications", "render-dropdown", {
        taskGroups: state.taskNotificationsByDate.length,
        unreadCount: state.unreadCount
    });
    const sections = [];

    // Release notification - HIDDEN FOR NOW, will add in future
    /*
    if (state.releaseUnseen && state.latest) {
        const release = state.latest;
        const titleParts = [];
        if (release.version) titleParts.push(escapeHtml(release.version));
        const releaseTitle = resolveReleaseText(release.title);
        if (releaseTitle) titleParts.push(escapeHtml(releaseTitle));
        const headline = titleParts.join(' - ');
        const dateLabel = t('notifications.releaseMeta', { date: formatReleaseDate(release.date) });
        const summary = release.summary ? escapeHtml(resolveReleaseText(release.summary)) : '';
        sections.push(`
            <div class="notify-section notify-section--release">
                <div class="notify-section-heading">
                    <div class="notify-section-title">${t('notifications.releaseTitle')}</div>
                    <div class="notify-section-meta">${dateLabel}</div>
                </div>
                <div class="notify-section-body">
                    <div class="notify-section-release-title">${headline}</div>
                    ${summary ? `<div class="notify-section-release-summary">${summary}</div>` : ''}
                </div>
                <div class="notify-section-actions">
                    <button type="button" class="notify-link" data-action="openUpdatesFromNotification">${t('notifications.releaseCta')}</button>
                </div>
            </div>
        `);
    }
    */

    // Task notifications grouped by date
    const projectMap = new Map(projects.map((project) => [project.id, project]));
    const taskMap = new Map(tasks.map((task) => [task.id, task]));
    const includeBacklog = !!settings.emailNotificationsIncludeBacklog;

    state.taskNotificationsByDate.forEach(([date, notifications]) => {
        const dateLabel = getRelativeDateLabel(date);

        const startingTasks = notifications
            .filter(notif => notif.type === 'task_starting')
            .map(notif => taskMap.get(notif.taskId))
            .filter(task => task && task.status !== 'done' && (includeBacklog || task.status !== 'backlog'))
            .filter(task => normalizeISODate(task.startDate || '') === date);

        const dueTasks = notifications
            .filter(notif => notif.type === 'task_due')
            .map(notif => taskMap.get(notif.taskId))
            .filter(task => task && task.status !== 'done' && (includeBacklog || task.status !== 'backlog'))
            .filter(task => normalizeISODate(task.endDate || '') === date);

        if (startingTasks.length === 0 && dueTasks.length === 0) return; // Skip if all tasks are completed or filtered out

        // Sort by priority
        const sortTasks = (tasksArr) => [...tasksArr].sort((a, b) => {
            const pa = PRIORITY_ORDER[a.priority || 'low'] || 0;
            const pb = PRIORITY_ORDER[b.priority || 'low'] || 0;
            if (pa !== pb) return pb - pa;
            return String(a.title || '').localeCompare(String(b.title || ''));
        });

        const sortedStartingTasks = sortTasks(startingTasks);
        const sortedDueTasks = sortTasks(dueTasks);

        const renderTaskList = (tasksArr) => {
            return tasksArr.slice(0, 3).map((task) => {
                const project = task.projectId ? projectMap.get(task.projectId) : null;
                const projectName = project ? project.name : '';
                const projectColor = project ? getProjectColor(project.id) : '';
                const priorityKey = (task.priority || 'low').toLowerCase();
                const priorityLabel = ['high', 'medium', 'low'].includes(priorityKey)
                    ? getPriorityLabel(priorityKey)
                    : (priorityKey || '');

                return `
                    <div class="notify-task" data-task-id="${task.id}">
                        <div class="notify-task-header">
                            <div class="notify-task-title">${escapeHtml(task.title || t('tasks.untitled'))}</div>
                            <span class="notify-priority notify-priority--${priorityKey}">${escapeHtml(priorityLabel)}</span>
                        </div>
                    </div>
                `;
            }).join('');
        };

        let taskListHTML = '';
        let totalCount = 0;
        let totalOverflow = 0;

        if (sortedStartingTasks.length > 0) {
            const preview = sortedStartingTasks.slice(0, 3);
            const overflow = Math.max(sortedStartingTasks.length - preview.length, 0);
            totalCount += sortedStartingTasks.length;
            totalOverflow += overflow;
            taskListHTML += `<div class="notify-section-subheader notify-section-subheader--starting">🚀 ${t('notifications.sectionStarting')} <span class="notify-section-count notify-section-count--starting" aria-label="${sortedStartingTasks.length} starting tasks">${sortedStartingTasks.length}</span></div>`;
            taskListHTML += renderTaskList(preview);
            if (overflow > 0) {
                taskListHTML += `<div class="notify-task-overflow">${t('notifications.moreStarting', { count: overflow })}</div>`;
                taskListHTML += `<button type="button" class="notify-link notify-link--starting notify-link--full" data-action="openDueTodayFromNotification" data-date="${date}" data-date-field="startDate">${t('notifications.viewStarting', { count: sortedStartingTasks.length })}</button>`;
            }
        }

        if (sortedDueTasks.length > 0) {
            const preview = sortedDueTasks.slice(0, 3);
            const overflow = Math.max(sortedDueTasks.length - preview.length, 0);
            totalCount += sortedDueTasks.length;
            totalOverflow += overflow;
            if (sortedStartingTasks.length > 0) {
                taskListHTML += `<div style="height: 1px; background: var(--border); margin: 10px 0;"></div>`;
            }
            taskListHTML += `<div class="notify-section-subheader notify-section-subheader--due">🎯 ${t('notifications.sectionDue')} <span class="notify-section-count notify-section-count--due" aria-label="${sortedDueTasks.length} due tasks">${sortedDueTasks.length}</span></div>`;
            taskListHTML += renderTaskList(preview);
            if (overflow > 0) {
                taskListHTML += `<div class="notify-task-overflow">${t('notifications.moreDue', { count: overflow })}</div>`;
                taskListHTML += `<button type="button" class="notify-link notify-link--due notify-link--full" data-action="openDueTodayFromNotification" data-date="${date}" data-date-field="endDate">${t('notifications.viewDue', { count: sortedDueTasks.length })}</button>`;
            }
        }

        const today = new Date().toISOString().split('T')[0];
        const isToday = date === today;

        sections.push(`
            <div class="notify-section notify-section--due" data-date="${date}">
                <div class="notify-section-heading">
                    <div class="notify-section-title">
                        <span class="notify-section-title-text">${dateLabel}</span>
                        ${!isToday ? `<div class="notify-section-title-actions"><button type="button" class="notify-dismiss-btn" data-action="dismissDate" data-date="${date}" aria-label="${t('notifications.dismiss')}" title="${t('notifications.dismiss')}">x</button></div>` : ''}
                    </div>
                </div>
                <div class="notify-task-list">
                    ${taskListHTML}
                </div>
            </div>
        `);
    });

    if (sections.length === 0) {
        sections.push(`<div class="notify-empty">${t('notifications.empty')}</div>`);
    }

    list.innerHTML = sections.join('');
    debugTimeEnd("notifications", renderTimer, {
        sections: sections.length,
        taskGroups: state.taskNotificationsByDate.length
    });
}

// Debounced version to prevent excessive updates
let updateNotificationStateTimer = null;
function updateNotificationState({ force = false } = {}) {
    // Clear any pending update
    if (updateNotificationStateTimer) {
        clearTimeout(updateNotificationStateTimer);
    }

    // Debounce by 100ms to batch rapid updates
    updateNotificationStateTimer = setTimeout(async () => {
        const state = await fetchNotificationState({ force });
        updateNotificationBadge(state);
        const dropdown = document.getElementById('notification-dropdown');
        if (dropdown && dropdown.classList.contains('active')) {
            renderNotificationDropdown(state);
        }
        updateNotificationStateTimer = null;
    }, 100);
}

// Immediate version for when you need instant update (e.g., opening dropdown)
async function updateNotificationStateImmediate({ force = false } = {}) {
    if (updateNotificationStateTimer) {
        clearTimeout(updateNotificationStateTimer);
        updateNotificationStateTimer = null;
    }
    const state = await fetchNotificationState({ force });
    updateNotificationBadge(state);
    const dropdown = document.getElementById('notification-dropdown');
    if (dropdown && dropdown.classList.contains('active')) {
        renderNotificationDropdown(state);
    }
}

async function markNotificationsSeen(state = null, { clearAll = false } = {}) {
    if (state && state.latest && state.latest.id) {
        setLastSeenReleaseId(state.latest.id);
    }
    if (USE_SERVER_NOTIFICATIONS) {
        const seenAt = new Date().toISOString();
        const token = getNotificationAuthToken();
        if (!token) {
            queueNotificationSeen(seenAt, clearAll);
            const cached = state || notificationStateCache.data || buildNotificationState();
            const updatedState = {
                ...cached,
                unreadCount: 0
            };
            notificationStateCache = { timestamp: Date.now(), data: updatedState };
            updateNotificationBadge(updatedState);
            return;
        }
        if (token) {
            try {
                const response = await fetch('/api/notifications/state', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        seenAt,
                        clearAll: !!clearAll
                    })
                });
                if (response.ok) {
                    const cached = state || notificationStateCache.data || buildNotificationState();
                    const updatedState = {
                        ...cached,
                        unreadCount: 0
                    };
                    notificationStateCache = { timestamp: Date.now(), data: updatedState };
                    updateNotificationBadge(updatedState);
                    return;
                }
                queueNotificationSeen(seenAt, clearAll);
            } catch (error) {
                console.warn('[notifications] failed to mark seen', error);
                queueNotificationSeen(new Date().toISOString(), clearAll);
            }
        }
    }

    markAllNotificationsRead();
    const updatedState = buildNotificationState();
    updateNotificationBadge(updatedState);
}

async function dismissNotificationByDate(date) {
    if (USE_SERVER_NOTIFICATIONS) {
        const seenAt = new Date().toISOString();
        const token = getNotificationAuthToken();
        if (!token) {
            // Offline mode - store in pending queue
            savePendingNotificationSeen({ seenAt, dismissDate: date });
            // Immediately update UI to hide the dismissed date
            if (notificationStateCache.data && notificationStateCache.data.taskNotificationsByDate) {
                const updatedState = {
                    ...notificationStateCache.data,
                    taskNotificationsByDate: notificationStateCache.data.taskNotificationsByDate.filter(([d]) => d !== date)
                };
                notificationStateCache = { timestamp: Date.now(), data: updatedState };
                updateNotificationBadge(updatedState);
                renderNotificationDropdown(updatedState);
            }
            return;
        }
        try {
            const response = await fetch('/api/notifications/state', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    seenAt,
                    dismissDate: date
                })
            });
            if (response.ok) {
                // Immediately update UI to hide the dismissed date
                if (notificationStateCache.data && notificationStateCache.data.taskNotificationsByDate) {
                    const updatedState = {
                        ...notificationStateCache.data,
                        taskNotificationsByDate: notificationStateCache.data.taskNotificationsByDate.filter(([d]) => d !== date)
                    };
                    notificationStateCache = { timestamp: Date.now(), data: updatedState };
                    updateNotificationBadge(updatedState);
                    renderNotificationDropdown(updatedState);
                }
                return;
            }
            // If failed, queue for later
            savePendingNotificationSeen({ seenAt, dismissDate: date });
        } catch (error) {
            console.warn('[notifications] failed to dismiss date', error);
            savePendingNotificationSeen({ seenAt, dismissDate: date });
        }
    } else {
        // Local storage mode
        dismissNotificationsByDate(date);
        updateNotificationState();
    }
}

function markLatestReleaseSeen() {
    const latest = getLatestReleaseNote();
    if (!latest || !latest.id) return;
    setLastSeenReleaseId(latest.id);
    updateNotificationState();
}

function closeNotificationDropdown() {
    const dropdown = document.getElementById('notification-dropdown');
    const toggle = document.getElementById('notification-toggle');
    if (dropdown) {
        dropdown.classList.remove('active');
    }
    if (toggle) toggle.classList.remove('active');
    setNotificationScrollLock(false);
}

function setNotificationScrollLock(isLocked) {
    const isMobile = (typeof window.matchMedia === 'function')
        ? window.matchMedia('(max-width: 768px)').matches
        : false;
    if (!isMobile) return;
    document.body.classList.toggle('notify-scroll-lock', !!isLocked);
}

function setupNotificationMenu() {
    const toggle = document.getElementById('notification-toggle');
    const dropdown = document.getElementById('notification-dropdown');
    if (!toggle || !dropdown || toggle.dataset.bound) return;
    toggle.dataset.bound = 'true';

    toggle.addEventListener('click', async (event) => {
        event.stopPropagation();
        const isOpen = dropdown.classList.contains('active');
        if (isOpen) {
            closeNotificationDropdown();
            return;
        }
        closeUserDropdown();
        const state = await fetchNotificationState({ force: true });
        renderNotificationDropdown(state);
        dropdown.classList.add('active');
        toggle.classList.add('active');
        setNotificationScrollLock(true);
        await markNotificationsSeen(state);
    });

    if (!dropdown.dataset.outsideListener) {
        dropdown.dataset.outsideListener = 'true';
        document.addEventListener('click', (event) => {
            if (!dropdown.classList.contains('active')) return;
            const target = event.target;
            if (!target) return;
            if (dropdown.contains(target) || toggle.contains(target)) return;
            closeNotificationDropdown();
        });
    }

    // Event delegation for notification actions
    if (!dropdown.dataset.actionListener) {
        dropdown.dataset.actionListener = 'true';
        dropdown.addEventListener('click', async (event) => {
            const target = event.target;
            if (!target) return;

            // Check if clicking on a task card
            const taskCard = target.closest('.notify-task');
            if (taskCard && taskCard.dataset.taskId) {
                event.preventDefault();
                event.stopPropagation();
                const taskId = parseInt(taskCard.dataset.taskId, 10);
                if (!isNaN(taskId)) {
                    closeNotificationDropdown();
                    openTaskDetails(taskId);
                }
                return;
            }

            const actionBtn = target.closest('[data-action]');
            if (!actionBtn) return;

            event.preventDefault();
            event.stopPropagation();

            const action = actionBtn.dataset.action;
            const date = actionBtn.dataset.date;

            if (action === 'dismissDate' && date) {
                await dismissNotificationByDate(date);
            } else if (action === 'dismissAll') {
                if (USE_SERVER_NOTIFICATIONS) {
                    const currentState = notificationStateCache.data || await fetchNotificationState({ force: true });
                    await markNotificationsSeen(currentState, { clearAll: true });
                    updateNotificationState({ force: true });
                } else {
                    dismissAllNotifications();
                    updateNotificationState();
                }
            } else if (action === 'openUpdatesFromNotification') {
                openUpdatesFromNotification();
            } else if (action === 'openDueTodayFromNotification') {
                if (date) {
                    const dateField = actionBtn.getAttribute('data-date-field') || 'endDate';
                    openDueTodayFromNotification(date, dateField);
                }
            }
        });
    }

    updateNotificationState();
}

function openUpdatesFromNotification() {
    closeNotificationDropdown();
    if (window.location.hash.replace('#', '') === 'updates') {
        showPage('updates');
        return;
    }
    window.location.hash = 'updates';
}

function openDueTodayFromNotification(dateStr, dateField = 'endDate') {
    closeNotificationDropdown();

    // Check if date is today
    const today = new Date().toISOString().split('T')[0];
    const isToday = dateStr === today;
    const includeBacklog = !!settings.emailNotificationsIncludeBacklog;
    const statusList = includeBacklog
        ? 'backlog,todo,progress,review'
        : 'todo,progress,review';

    // Use preset filters for today, date range filters for other dates
    let targetHash;
    if (isToday) {
        const preset = dateField === 'startDate' ? 'start-today' : 'end-today';
        targetHash = `#tasks?view=list&datePreset=${preset}&status=${statusList}`;
    } else {
        // For past/future dates, use date range filter
        targetHash = `#tasks?view=list&dateFrom=${dateStr}&dateTo=${dateStr}&dateField=${dateField}&status=${statusList}`;
    }

    if (window.location.hash === targetHash) {
        showPage('tasks');
        return;
    }
    window.location.hash = targetHash.replace('#', '');
}

// Kanban sort state: 'priority' (default) or 'manual'
let sortMode = 'priority'; // 'priority' or 'manual'
let manualTaskOrder = {}; // { columnName: [taskId, ...] }

// Toggle between priority/manual sorting
function toggleSortMode() {
    // One-way action: enforce priority ordering
    sortMode = 'priority';
    // Clear any manual order so we show true priority ordering
    manualTaskOrder = {};
    const sortBtn = document.getElementById('sort-btn');
    const sortLabel = document.getElementById('sort-label');
    const sortIcon = document.getElementById('sort-icon');
    if (sortBtn) sortBtn.classList.remove('manual');
    if (sortLabel) sortLabel.textContent = t('tasks.sort.orderByPriority');
    if (sortIcon) sortIcon.textContent = '⬆️';
    renderTasks();
    saveSortPreferences();
}

async function saveSortPreferences() {
    try {
        await saveSortStateData(sortMode, manualTaskOrder);
    } catch (e) {
        // Storage client may be unavailable in some environments; fallback to localStorage
        try {
            localStorage.setItem('sortMode', sortMode);
            localStorage.setItem('manualTaskOrder', JSON.stringify(manualTaskOrder));
        } catch (err) {}
    }
}

async function loadSortPreferences() {
    try {
        const { sortMode: savedMode, manualTaskOrder: savedOrder } = await loadSortStateData();
        if (savedMode) sortMode = savedMode;
        if (savedOrder) manualTaskOrder = savedOrder;
    } catch (e) {
        try {
            const lm = localStorage.getItem('sortMode');
            const lo = localStorage.getItem('manualTaskOrder');
            if (lm) sortMode = lm;
            if (lo) manualTaskOrder = JSON.parse(lo);
        } catch (err) {}
    }
    // Normalize/guard persisted values (older versions used 'auto' to mean priority ordering)
    if (sortMode === 'auto' || (sortMode !== 'priority' && sortMode !== 'manual')) {
        sortMode = 'priority';
    }
    updateSortUI();
}

function updateSortUI() {
    const sortToggle = document.getElementById('sort-toggle');
    const sortBtn = document.getElementById('sort-btn');
    const sortLabel = document.getElementById('sort-label');
    const sortIcon = document.getElementById('sort-icon');
    const kanbanUpdatedGroup = document.getElementById('group-kanban-updated');
    if (!sortToggle || !sortBtn) return;
    // Show only when Kanban board is visible
    const kanban = document.querySelector('.kanban-board');
    const isKanban = kanban && !kanban.classList.contains('hidden');
    sortToggle.style.display = isKanban ? 'flex' : 'none';
    const isList = document.getElementById('list-view')?.classList.contains('active');
    const isCalendar = document.getElementById('calendar-view')?.classList.contains('active');
    const showUpdated = isKanban || isList || isCalendar;
    if (kanbanUpdatedGroup) kanbanUpdatedGroup.style.display = showUpdated ? '' : 'none';
    try { updateKanbanUpdatedFilterUI(); } catch (e) {}
    // Keep the button as a static one-time action label (Order by Priority).
    // Manual mode may exist internally (via dragging), but the button UI should not change to "Manual".
    try { sortBtn.classList.remove('manual'); } catch (e) {}
    if (sortLabel) sortLabel.textContent = t('tasks.sort.orderByPriority');
    if (sortIcon) sortIcon.textContent = '⇅';

    // If the currently visible ordering already matches priority ordering, disable the button
    try {
        // Using imported PRIORITY_ORDER
        const statuses = window.kanbanShowBacklog === true
            ? ['backlog', 'todo', 'progress', 'review', 'done']
            : ['todo', 'progress', 'review', 'done'];
        let filtered = typeof getFilteredTasks === 'function' ? getFilteredTasks() : tasks.slice();
        const cutoff = getKanbanUpdatedCutoffTime(window.kanbanUpdatedFilter);
        if (cutoff !== null) {
            filtered = filtered.filter((t) => getTaskUpdatedTime(t) >= cutoff);
        }

        const arraysEqual = (a, b) => {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
            return true;
        };

        let allMatch = true;
        for (const status of statuses) {
            const visibleNodes = Array.from(document.querySelectorAll(`#${status}-tasks .task-card`));
            const visibleIds = visibleNodes.map(n => parseInt(n.dataset.taskId, 10)).filter(n => !Number.isNaN(n));

            const expected = filtered
                .filter(t => t.status === status)
                .slice()
                .sort((a, b) => (PRIORITY_ORDER[b.priority] || 0) - (PRIORITY_ORDER[a.priority] || 0))
                .map(t => t.id);

            if (!arraysEqual(visibleIds, expected)) {
                allMatch = false;
                break;
            }
        }

        if (allMatch) {
            sortBtn.disabled = true;
            sortBtn.classList.add('disabled');
            sortBtn.setAttribute('aria-disabled', 'true');
        } else {
            sortBtn.disabled = false;
            sortBtn.classList.remove('disabled');
            sortBtn.removeAttribute('aria-disabled');
        }
    } catch (e) {
        // If anything goes wrong, keep the button enabled
        sortBtn.disabled = false;
        sortBtn.classList.remove('disabled');
    }
}

// Add this near the top of app.js after imports

async function persistAll() {
    if (isInitializing) return;
    pendingSaves++;
    try {
        await saveAllData(tasks, projects, feedbackItems);
    } catch (error) {
        console.error("Error persisting data:", error);
        showErrorNotification(t('error.saveDataFailed'));
        throw error;
    } finally {
        pendingSaves--;
    }
}

async function saveProjects() {
    if (isInitializing) return;
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-projects", { projectCount: projects.length });
    let success = false;
    try {
        await saveProjectsData(projects);
        success = true;
    } catch (error) {
        console.error("Error saving projects:", error);
        showErrorNotification(t('error.saveProjectsFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, projectCount: projects.length });
        pendingSaves--;
    }
}

async function saveTasks() {
    if (isInitializing) return;
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-tasks", { taskCount: tasks.length });
    let success = false;
    try {
        await saveTasksData(tasks);
        success = true;
    } catch (error) {
        const msg = error?.message ?? String(error);
        const stack = error?.stack;
        console.error("[Storage] Save tasks failed:", msg);
        if (stack) console.error("[Storage] Stack:", stack);
        showErrorNotification(t('error.saveTasksFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, taskCount: tasks.length });
        pendingSaves--;
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Row-level D1 wrappers (Phase 2) - Individual task/project operations
// ═══════════════════════════════════════════════════════════════════════════════

async function saveTaskCreate(task) {
    if (isInitializing) return;
    topUpIdBlocks();
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-task-create", { taskId: task.id });
    let success = false;
    try {
        await saveTaskCreateData(task);
        success = true;
    } catch (error) {
        const msg = error?.message ?? String(error);
        console.error("[Storage] Save task create failed:", msg);
        showErrorNotification(t('error.saveTasksFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, taskId: task.id });
        pendingSaves--;
    }
}

async function saveTaskUpdate(taskId, changedFields) {
    if (isInitializing) return;
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-task-update", { taskId, fields: Object.keys(changedFields) });
    let success = false;
    try {
        await saveTaskUpdateData(taskId, changedFields);
        success = true;
    } catch (error) {
        const msg = error?.message ?? String(error);
        console.error("[Storage] Save task update failed:", msg);
        showErrorNotification(t('error.saveTasksFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, taskId });
        pendingSaves--;
    }
}

async function saveTaskDelete(taskId) {
    if (isInitializing) return;
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-task-delete", { taskId });
    let success = false;
    try {
        await saveTaskDeleteData(taskId);
        success = true;
    } catch (error) {
        const msg = error?.message ?? String(error);
        console.error("[Storage] Save task delete failed:", msg);
        showErrorNotification(t('error.saveTasksFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, taskId });
        pendingSaves--;
    }
}

async function saveTasksReorder(updates) {
    if (isInitializing) return;
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-tasks-reorder", { count: updates.length });
    let success = false;
    try {
        await saveTasksReorderData(updates);
        success = true;
    } catch (error) {
        const msg = error?.message ?? String(error);
        console.error("[Storage] Save tasks reorder failed:", msg);
        showErrorNotification(t('error.saveTasksFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, count: updates.length });
        pendingSaves--;
    }
}

async function saveProjectCreate(project) {
    if (isInitializing) return;
    topUpIdBlocks();
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-project-create", { projectId: project.id });
    let success = false;
    try {
        await saveProjectCreateData(project);
        success = true;
    } catch (error) {
        const msg = error?.message ?? String(error);
        console.error("[Storage] Save project create failed:", msg);
        showErrorNotification(t('error.saveProjectsFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, projectId: project.id });
        pendingSaves--;
    }
}

async function saveProjectUpdate(projectId, changedFields) {
    if (isInitializing) return;
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-project-update", { projectId, fields: Object.keys(changedFields) });
    let success = false;
    try {
        await saveProjectUpdateData(projectId, changedFields);
        success = true;
    } catch (error) {
        const msg = error?.message ?? String(error);
        console.error("[Storage] Save project update failed:", msg);
        showErrorNotification(t('error.saveProjectsFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, projectId });
        pendingSaves--;
    }
}

async function saveProjectDelete(projectId) {
    if (isInitializing) return;
    pendingSaves++;
    const timer = debugTimeStart("storage", "save-project-delete", { projectId });
    let success = false;
    try {
        await saveProjectDeleteData(projectId);
        success = true;
    } catch (error) {
        const msg = error?.message ?? String(error);
        console.error("[Storage] Save project delete failed:", msg);
        showErrorNotification(t('error.saveProjectsFailed'));
        throw error;
    } finally {
        debugTimeEnd("storage", timer, { success, projectId });
        pendingSaves--;
    }
}

// Feedback cache helper (used by D1 individual operations for instant persistence)
function persistFeedbackCache() {
    try {
        localStorage.setItem(FEEDBACK_CACHE_KEY, JSON.stringify(feedbackItems));
    } catch (e) {}
}

function updateFeedbackPlaceholderForViewport() {
    const input = document.getElementById('feedback-description');
    if (!input) return;
    const isCompact = window.matchMedia('(max-width: 768px)').matches;
    const key = isCompact ? 'feedback.descriptionPlaceholderShort' : 'feedback.descriptionPlaceholder';
    input.setAttribute('placeholder', t(key));
}

async function saveProjectColors(options = {}) {
    const { showError = true } = options;
    if (isInitializing) {
        _pendingProjectColorsSave = true; // Queue — don't silently drop
        return;
    }
    try {
        await saveProjectColorsData(projectColorMap);
    } catch (error) {
        console.error("Error saving project colors:", error);
        if (showError) {
            showErrorNotification(t('error.saveProjectColorsFailed'));
        }
        throw error;
    }
}

async function loadProjectColors() {
    try {
        projectColorMap = await loadProjectColorsData();
    } catch (error) {
        console.error("Error loading project colors:", error);
        projectColorMap = {};
    }
}

async function saveTagColors(options = {}) {
    const force = options.force === true;
    if (isInitializing && !force) return;

    try {
        // Validate all colors before saving
        const validatedMap = {};
        for (const [key, color] of Object.entries(tagColorMap)) {
            if (validateTagColor(color)) {
                validatedMap[key] = color;
            } else {
                console.warn(`Invalid tag color format for ${key}: ${color}, skipping save`);
            }
        }
        
        await saveTagColorsData(validatedMap);
        
        // Verify save was successful by reading back
        try {
            const verify = await loadTagColorsData();
            const savedCount = Object.keys(verify).length;
            const expectedCount = Object.keys(validatedMap).length;
            
            if (savedCount !== expectedCount) {
                console.error(`Tag colors save verification failed: expected ${expectedCount}, got ${savedCount}`);
            }
        } catch (verifyError) {
            console.warn("Could not verify tag colors save:", verifyError);
        }
    } catch (error) {
        console.error("Error saving tag colors:", error);
        throw error;
    }
}

async function saveTagColorsVersion(options = {}) {
    const force = options.force === true;
    if (isInitializing && !force) return;

    try {
        await saveTagColorsVersionData(tagColorsVersion);
    } catch (error) {
        console.error("Error saving tag colors version:", error);
    }
}

async function saveSettings() {
    const timer = debugTimeStart("settings", "save");
    let success = false;
    try {
        await saveSettingsData(settings);
        success = true;
        // Only clear dirty state after confirmed persistence — never on failure
        window.initialSettingsFormState = null;
        window.settingsFormIsDirty = false;
    } catch (e) {
        console.error("Error saving settings:", e);
        showErrorNotification(t('error.saveChangesFailed'));
    } finally {
        debugTimeEnd("settings", timer, { success });
    }
}

async function loadSettings() {
    let loadedSettings = null;
    try {
        loadedSettings = await loadSettingsData();
        if (loadedSettings && typeof loadedSettings === "object") {
            settings = { ...settings, ...loadedSettings };
            settings.language = normalizeLanguage(settings.language);
        }
    } catch (e) {
        console.error("Error loading settings:", e);
    }
    settings.emailNotificationTimeZone = coerceSupportedUserTimeZone(
        settings.emailNotificationTimeZone
    );
    applyDebugLogSetting(settings.debugLogsEnabled);
    applyDocsNavVisibility();

    const missingPersistedTimeZone =
        !loadedSettings ||
        typeof loadedSettings !== "object" ||
        !String(loadedSettings.emailNotificationTimeZone || "").trim();
    if (missingPersistedTimeZone && settings.emailNotificationTimeZone) {
        saveSettingsData(settings).catch((error) => {
            console.warn("Unable to persist default timezone setting:", error);
        });
    }
}

// Time utilities imported from src/utils/time.js
// isValidEmailAddress imported from src/utils/validation.js

// ===== Portal-based floating dropdown for notification time =====
let notificationTimePortalEl = null;
let notificationTimePortalAnchor = null;
let notificationTimeZonePortalEl = null;
let notificationTimeZonePortalAnchor = null;

function buildNotificationTimeOptionsHTML(selectedValue) {
    const start = 8 * 60;
    const end = 18 * 60;
    const step = 30;
    const selected = String(selectedValue || "");
    const bits = [];
    for (let minutes = start; minutes <= end; minutes += step) {
        const hhmm = minutesToHHMM(minutes);
        const selectedClass = hhmm === selected ? " selected" : "";
        bits.push(`<div class="time-option${selectedClass}" role="option" data-value="${hhmm}">${hhmm}</div>`);
    }
    return bits.join("");
}

function buildNotificationTimeZoneOptionsHTML(selectEl) {
    if (!selectEl || !selectEl.options) return "";
    const selected = String(selectEl.value || "");
    const bits = [];
    Array.from(selectEl.options).forEach((opt) => {
        // "Select option..." placeholders are hidden+disabled in the native list too
        if (opt.hidden || opt.disabled) return;
        const value = String(opt.value || "");
        const label = String(opt.textContent || value);
        const selectedClass = value === selected ? " selected" : "";
        bits.push(`<div class="tz-option${selectedClass}" role="option" data-value="${escapeHtml(value)}">${escapeHtml(label)}</div>`);
    });
    return bits.join("");
}

// Case- and accent-insensitive match ("canarias" finds "Islas Canarias", "mexico" finds "México")
function normalizeTimeZoneSearchText(value) {
    return String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

function showNotificationTimeZonePortal(triggerBtn, selectEl, valueEl, options = {}) {
    if (!triggerBtn || !selectEl) return;

    if (!notificationTimeZonePortalEl) {
        notificationTimeZonePortalEl = document.createElement("div");
        notificationTimeZonePortalEl.className = "tz-options-portal";
        document.body.appendChild(notificationTimeZonePortalEl);
    }

    const searchable = options.searchable === true;
    notificationTimeZonePortalEl.classList.toggle("tz-options-portal--search", searchable);
    notificationTimeZonePortalEl.innerHTML = searchable
        ? `<div class="tz-search-wrap">
               <input type="text" class="tz-search" autocomplete="off" spellcheck="false"
                      placeholder="${escapeHtml(t('settings.timeZoneSearch'))}"
                      aria-label="${escapeHtml(t('settings.timeZoneSearch'))}">
           </div>
           ${buildNotificationTimeZoneOptionsHTML(selectEl)}
           <div class="tz-no-results" hidden>${escapeHtml(t('settings.timeZoneNoResults'))}</div>`
        : buildNotificationTimeZoneOptionsHTML(selectEl);
    notificationTimeZonePortalEl.style.display = "block";

    notificationTimeZonePortalAnchor = triggerBtn;
    positionNotificationTimeZonePortal(triggerBtn, notificationTimeZonePortalEl);
    requestAnimationFrame(() => positionNotificationTimeZonePortal(triggerBtn, notificationTimeZonePortalEl));

    triggerBtn.setAttribute("aria-expanded", "true");

    const pick = (value) => {
        if (!value) return;
        selectEl.value = value;
        if (valueEl) {
            const label = selectEl.options?.[selectEl.selectedIndex]?.textContent || value;
            valueEl.textContent = label;
        }
        selectEl.dispatchEvent(new Event("change", { bubbles: true }));
        hideNotificationTimeZonePortal();
    };

    const onClick = (evt) => {
        evt.stopPropagation();
        const opt = evt.target.closest(".tz-option");
        if (!opt) return;
        pick(opt.dataset.value);
    };

    notificationTimeZonePortalEl.onclick = onClick;

    if (searchable) {
        const searchInput = notificationTimeZonePortalEl.querySelector(".tz-search");
        const noResults = notificationTimeZonePortalEl.querySelector(".tz-no-results");
        const optionEls = Array.from(notificationTimeZonePortalEl.querySelectorAll(".tz-option"));
        searchInput?.addEventListener("input", () => {
            const query = normalizeTimeZoneSearchText(searchInput.value);
            let visible = 0;
            optionEls.forEach((opt) => {
                const match = !query || normalizeTimeZoneSearchText(opt.textContent).includes(query);
                opt.hidden = !match;
                if (match) visible += 1;
            });
            if (noResults) noResults.hidden = visible > 0;
        });
        searchInput?.addEventListener("keydown", (evt) => {
            if (evt.key !== "Enter") return;
            // Inside the settings form: Enter must not submit it
            evt.preventDefault();
            const first = optionEls.find((opt) => !opt.hidden);
            if (first) pick(first.dataset.value);
        });
        // Focus only with a mouse/trackpad: on touch it would pop the keyboard over the list
        if (searchInput && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
            searchInput.focus({ preventScroll: true });
        }
    }
    setTimeout(() => document.addEventListener("click", handleNotificationTimeZoneOutsideClick, true), 0);
    window.addEventListener("scroll", handleNotificationTimeZoneReposition, true);
    window.addEventListener("resize", handleNotificationTimeZoneReposition, true);
    document.addEventListener("keydown", handleNotificationTimeZoneEsc, true);
}

function hideNotificationTimeZonePortal() {
    if (!notificationTimeZonePortalEl) return;
    notificationTimeZonePortalEl.style.display = "none";
    notificationTimeZonePortalEl.innerHTML = "";
    notificationTimeZonePortalEl.onclick = null;
    if (notificationTimeZonePortalAnchor) {
        notificationTimeZonePortalAnchor.setAttribute("aria-expanded", "false");
    }
    notificationTimeZonePortalAnchor = null;
    document.removeEventListener("click", handleNotificationTimeZoneOutsideClick, true);
    window.removeEventListener("scroll", handleNotificationTimeZoneReposition, true);
    window.removeEventListener("resize", handleNotificationTimeZoneReposition, true);
    document.removeEventListener("keydown", handleNotificationTimeZoneEsc, true);
}

function handleNotificationTimeZoneOutsideClick(evt) {
    const target = evt.target;
    if (!notificationTimeZonePortalEl || notificationTimeZonePortalEl.style.display === "none") return;
    if (notificationTimeZonePortalEl.contains(target)) return;
    if (notificationTimeZonePortalAnchor && notificationTimeZonePortalAnchor.contains(target)) return;
    hideNotificationTimeZonePortal();
}

function handleNotificationTimeZoneReposition() {
    if (!notificationTimeZonePortalAnchor || !notificationTimeZonePortalEl || notificationTimeZonePortalEl.style.display === "none") return;
    positionNotificationTimeZonePortal(notificationTimeZonePortalAnchor, notificationTimeZonePortalEl);
}

function handleNotificationTimeZoneEsc(evt) {
    if (evt.key !== "Escape") return;
    if (!notificationTimeZonePortalEl || notificationTimeZonePortalEl.style.display === "none") return;
    evt.preventDefault();
    hideNotificationTimeZonePortal();
}

function positionNotificationTimeZonePortal(button, portal) {
    const rect = button.getBoundingClientRect();
    portal.style.width = `${rect.width}px`;
    const viewportH = window.innerHeight;
    const portalHeight = Math.min(portal.scrollHeight, 240);
    const spaceBelow = viewportH - rect.bottom;
    const showAbove = spaceBelow < portalHeight + 12;
    const top = showAbove ? Math.max(8, rect.top - portalHeight - 4) : rect.bottom + 4;
    const viewportW = window.innerWidth;
    const portalWidth = portal.getBoundingClientRect().width || rect.width;
    const desiredLeft = rect.left;
    const clampedLeft = Math.min(
        Math.max(8, desiredLeft),
        Math.max(8, viewportW - portalWidth - 8)
    );
    portal.style.left = `${clampedLeft}px`;
    portal.style.top = `${top}px`;
}

function showNotificationTimePortal(triggerBtn, hiddenInput, valueEl) {
    if (!triggerBtn || !hiddenInput) return;

    if (!notificationTimePortalEl) {
        notificationTimePortalEl = document.createElement("div");
        notificationTimePortalEl.className = "time-options-portal";
        document.body.appendChild(notificationTimePortalEl);
    }

    const currentValue = String(hiddenInput.value || "").trim();
    notificationTimePortalEl.innerHTML = buildNotificationTimeOptionsHTML(currentValue);
    notificationTimePortalEl.style.display = "block";

    notificationTimePortalAnchor = triggerBtn;
    positionNotificationTimePortal(triggerBtn, notificationTimePortalEl);
    requestAnimationFrame(() => positionNotificationTimePortal(triggerBtn, notificationTimePortalEl));

    triggerBtn.setAttribute("aria-expanded", "true");

    const onClick = (evt) => {
        evt.stopPropagation();
        const opt = evt.target.closest(".time-option");
        if (!opt) return;
        const value = opt.dataset.value;
        if (!value) return;
        hiddenInput.value = value;
        if (valueEl) valueEl.textContent = value;
        if (window.markSettingsDirtyIfNeeded) window.markSettingsDirtyIfNeeded();
        hideNotificationTimePortal();
    };

    notificationTimePortalEl.addEventListener("click", onClick);
    setTimeout(() => document.addEventListener("click", handleNotificationTimeOutsideClick, true), 0);
    window.addEventListener("scroll", handleNotificationTimeReposition, true);
    window.addEventListener("resize", handleNotificationTimeReposition, true);
    document.addEventListener("keydown", handleNotificationTimeEsc, true);
}

function hideNotificationTimePortal() {
    if (!notificationTimePortalEl) return;
    notificationTimePortalEl.style.display = "none";
    notificationTimePortalEl.innerHTML = "";
    if (notificationTimePortalAnchor) {
        notificationTimePortalAnchor.setAttribute("aria-expanded", "false");
    }
    notificationTimePortalAnchor = null;
    document.removeEventListener("click", handleNotificationTimeOutsideClick, true);
    window.removeEventListener("scroll", handleNotificationTimeReposition, true);
    window.removeEventListener("resize", handleNotificationTimeReposition, true);
    document.removeEventListener("keydown", handleNotificationTimeEsc, true);
}

function handleNotificationTimeOutsideClick(evt) {
    const target = evt.target;
    if (!notificationTimePortalEl || notificationTimePortalEl.style.display === "none") return;
    if (notificationTimePortalEl.contains(target)) return;
    if (notificationTimePortalAnchor && notificationTimePortalAnchor.contains(target)) return;
    hideNotificationTimePortal();
}

function handleNotificationTimeReposition() {
    if (!notificationTimePortalAnchor || !notificationTimePortalEl || notificationTimePortalEl.style.display === "none") return;
    positionNotificationTimePortal(notificationTimePortalAnchor, notificationTimePortalEl);
}

function handleNotificationTimeEsc(evt) {
    if (evt.key !== "Escape") return;
    if (!notificationTimePortalEl || notificationTimePortalEl.style.display === "none") return;
    evt.preventDefault();
    hideNotificationTimePortal();
}

function positionNotificationTimePortal(button, portal) {
    const rect = button.getBoundingClientRect();
    portal.style.width = `${rect.width}px`;
    const viewportH = window.innerHeight;
    const portalHeight = Math.min(portal.scrollHeight, 260);
    const spaceBelow = viewportH - rect.bottom;
    const showAbove = spaceBelow < portalHeight + 12;
    const top = showAbove ? Math.max(8, rect.top - portalHeight - 4) : rect.bottom + 4;
    const viewportW = window.innerWidth;
    const portalWidth = portal.getBoundingClientRect().width || rect.width;
    const desiredLeft = rect.left;
    const clampedLeft = Math.min(
        Math.max(8, desiredLeft),
        Math.max(8, viewportW - portalWidth - 8)
    );
    portal.style.left = `${clampedLeft}px`;
    portal.style.top = `${top}px`;
}

function applyWorkspaceLogo() {
    const iconEl = document.querySelector('.workspace-icon');
    if (!iconEl) return;

    if (defaultWorkspaceIconText === null) {
        defaultWorkspaceIconText = iconEl.textContent || '';
    }

    if (settings.customWorkspaceLogo) {
        iconEl.style.backgroundImage = `url(${settings.customWorkspaceLogo})`;
        iconEl.textContent = '';
    } else {
        iconEl.style.backgroundImage = '';
        iconEl.textContent = defaultWorkspaceIconText;
    }
}

function applyLoadedAllData({ tasks: loadedTasks, projects: loadedProjects, feedbackItems: loadedFeedback } = {}) {
    projects = loadedProjects || [];
    tasks = loadedTasks || [];
    feedbackItems = loadedFeedback || [];

    // Normalize IDs to numbers
    projects.forEach(p => {
        if (p && p.id != null) p.id = parseInt(p.id, 10);
        // Migration: Ensure all projects have tags array
        if (!Array.isArray(p.tags)) {
            p.tags = [];
        }
    });
    tasks.forEach(t => {
        if (t) {
            if (t.id != null) t.id = parseInt(t.id, 10);
            if (t.projectId != null && t.projectId !== "null") {
                t.projectId = parseInt(t.projectId, 10);
            } else {
                t.projectId = null;
            }
            if (t.automationId != null && t.automationId !== "null" && t.automationId !== "") {
                t.automationId = parseInt(t.automationId, 10);
            } else {
                t.automationId = null;
            }
            // Normalize date fields to strings (older versions may have missing fields)
            if (t.startDate === undefined || t.startDate === null) t.startDate = "";
            if (t.endDate === undefined || t.endDate === null) t.endDate = "";
            if (typeof t.startDate !== "string") t.startDate = String(t.startDate || "");
            if (typeof t.endDate !== "string") t.endDate = String(t.endDate || "");

            // Normalize tags/attachments to arrays
            if (!Array.isArray(t.tags)) {
                if (typeof t.tags === 'string' && t.tags.trim() !== '') {
                    t.tags = t.tags.split(',').map(s => s.trim()).filter(Boolean);
                } else {
                    t.tags = [];
                }
            }
            if (!Array.isArray(t.attachments)) t.attachments = [];

            // Migration: Track whether dates were ever set (used for mobile modal organization).
            // IMPORTANT: We can't use property-existence because we add missing fields during migration.
            if (t.startDateWasEverSet === undefined) t.startDateWasEverSet = t.startDate.trim() !== "";
            if (t.endDateWasEverSet === undefined) t.endDateWasEverSet = t.endDate.trim() !== "";

            // Migration: Convert dueDate to endDate
            if (t.dueDate && !t.endDate) {
                t.endDate = t.dueDate;
                t.endDateWasEverSet = true;
            }
            // Remove dueDate field (no longer used)
            delete t.dueDate;
        }
    });
    feedbackItems.forEach(f => {
        if (f && f.id != null) f.id = String(f.id); // D1 stores IDs as TEXT; keep as string throughout
    });

    // Delta queue removed - using simple direct save

    persistFeedbackCache();

    // Calculate counters from existing IDs (fallback until a server id block arrives).
    // While this tab holds a reserved block, keep using it: max(id)+1 of freshly
    // loaded data may point into a block another device reserved.
    const reservedProjectNext = projectIdBlockEnd > 0 && projectCounter <= projectIdBlockEnd ? projectCounter : 0;
    const reservedTaskNext = taskIdBlockEnd > 0 && taskCounter <= taskIdBlockEnd ? taskCounter : 0;

    if (reservedProjectNext) {
        projectCounter = reservedProjectNext;
    } else if (projects.length > 0) {
        projectCounter = Math.max(...projects.map(p => p.id || 0)) + 1;
    } else {
        projectCounter = 1;
    }

    if (reservedTaskNext) {
        taskCounter = reservedTaskNext;
    } else if (tasks.length > 0) {
        taskCounter = Math.max(...tasks.map(t => t.id || 0)) + 1;
    } else {
        taskCounter = 1;
    }

    if (feedbackItems.length > 0) {
        // Parse IDs as integers (handle both string and number IDs, and strip .0 from floats)
        feedbackCounter = Math.max(...feedbackItems.map(f => parseInt(f.id) || 0)) + 1;
    } else {
        feedbackCounter = 1;
    }
}

async function loadDataFromKV() {
    // Load fresh data directly (no SWR caching)
    const all = await loadAllData({
        feedback: {
            limitPending: FEEDBACK_OPEN_ITEMS_PER_PAGE,
            limitDone: FEEDBACK_ITEMS_PER_PAGE,
            cacheKey: FEEDBACK_CACHE_KEY
        }
    });
    applyLoadedAllData(all);
}


// Project templates state
let templates = [];

// Color state management
let tagColorMap = {}; // Maps tag names to auto-generated colors
let projectColorMap = {}; // Maps project IDs to auto-assigned colors
let tagColorOverrides = {}; // User-explicitly-chosen tag colors — NEVER wiped by migrations
let projectColorOverrides = {}; // User-explicitly-chosen project colors — NEVER wiped by migrations
let tagColorsVersion = 0;
let pendingTagColorsSaveTimer = null;
let tagColorsReady = false;
let _pendingProjectColorsSave = false; // Queue save if called during init

// Validate hex color format to prevent corrupted data
function validateTagColor(color) {
    return /^#[0-9a-fA-F]{6}$/.test(String(color || '').trim());
}

function scheduleTagColorsSave() {
    if (!tagColorsReady) return;

    if (pendingTagColorsSaveTimer) {
        clearTimeout(pendingTagColorsSaveTimer);
    }

    pendingTagColorsSaveTimer = setTimeout(() => {
        pendingTagColorsSaveTimer = null;
        saveTagColors().catch((error) => {
            console.error("Error saving tag colors:", error);
        });
    }, 300);
}

function getTagColor(tagName) {
    // User-chosen override always wins over auto-generated color
    const key = normalizeTagColorKey(tagName);
    if (key && tagColorOverrides[key] && validateTagColor(tagColorOverrides[key])) {
        return tagColorOverrides[key];
    }
    
    const { color, changed } = ensureTagColor(tagName, tagColorMap);
    if (changed) {
        scheduleTagColorsSave();
    }
    return color;
}

function getTagTextColor(tagName) {
    const color = getTagColor(tagName);
    return getTagTextColorForBackground(color);
}

function getProjectColor(projectId) {
    // User-chosen override always wins over auto-assigned color
    if (projectColorOverrides[projectId]) return projectColorOverrides[projectId];
    if (!projectColorMap[projectId]) {
        const usedColors = new Set([
            ...Object.values(projectColorMap),
            ...Object.values(projectColorOverrides)
        ]);
        const availableColors = PROJECT_COLORS.filter(color => !usedColors.has(color));
        projectColorMap[projectId] = availableColors.length > 0
            ? availableColors[0]
            : PROJECT_COLORS[Object.keys(projectColorMap).length % PROJECT_COLORS.length];
        // Auto-save the newly assigned color
        saveProjectColors().catch((error) => {
            console.error("Error auto-saving project colors:", error);
        });
    }
    return projectColorMap[projectId];
}

async function saveProjectColorOverrides() {
    try {
        await saveProjectColorOverridesData(projectColorOverrides);
    } catch (error) {
        console.error("Error saving project color overrides:", error);
        throw error;
    }
}

async function saveTagColorOverrides() {
    try {
        // Validate all override colors before saving
        const validatedOverrides = {};
        for (const [key, color] of Object.entries(tagColorOverrides)) {
            if (validateTagColor(color)) {
                validatedOverrides[key] = color;
            } else {
                console.warn(`Invalid tag color override format for ${key}: ${color}, skipping save`);
            }
        }
        
        await saveTagColorOverridesData(validatedOverrides);
    } catch (error) {
        console.error("Error saving tag color overrides:", error);
        throw error;
    }
}

async function setProjectColor(projectId, color) {
    const hadOverride = Object.prototype.hasOwnProperty.call(projectColorOverrides, projectId);
    const previousOverride = projectColorOverrides[projectId];
    const hadProjectColor = Object.prototype.hasOwnProperty.call(projectColorMap, projectId);
    const previousProjectColor = projectColorMap[projectId];

    // Store as an explicit user override — permanently protected from migrations
    projectColorOverrides[projectId] = color;
    projectColorMap[projectId] = color; // Keep in sync for display consistency

    try {
        await saveProjectColorOverrides();
    } catch (error) {
        if (hadOverride) {
            projectColorOverrides[projectId] = previousOverride;
        } else {
            delete projectColorOverrides[projectId];
        }

        if (hadProjectColor) {
            projectColorMap[projectId] = previousProjectColor;
        } else {
            delete projectColorMap[projectId];
        }

        showErrorNotification(t('error.saveProjectColorsFailed'));
        return false;
    }

    try {
        await saveProjectColors({ showError: false });
    } catch (error) {
        console.warn("Project color override saved, but legacy project color map failed to persist:", error);
    }

    // Refresh calendar if it's active
    if (document.getElementById("calendar-view")?.classList.contains("active")) {
        renderCalendar();
    }

    return true;
}

function toggleProjectColorPicker(projectId) {
    const picker = document.getElementById(`color-picker-${projectId}`);
    if (picker) {
        const isVisible = picker.style.display !== 'none';
        // Close all other color pickers
        document.querySelectorAll('.color-picker-dropdown').forEach(p => p.style.display = 'none');
        // Toggle this one
        picker.style.display = isVisible ? 'none' : 'block';
    }
}

async function updateProjectColor(projectId, color) {
    const saved = await setProjectColor(projectId, color);
    if (!saved) return;

    // Update the current color display
    const currentColorDiv = document.querySelector(`#color-picker-${projectId}`).previousElementSibling;
    if (currentColorDiv) {
        currentColorDiv.style.backgroundColor = color;
    }
    // Update color picker borders
    const picker = document.getElementById(`color-picker-${projectId}`);
    if (picker) {
        picker.querySelectorAll('.color-option').forEach(option => {
            const optionColor = option.style.backgroundColor;
            const rgbColor = optionColor.replace(/rgb\(|\)|\s/g, '').split(',');
            const hexColor = '#' + rgbColor.map(x => parseInt(x, 10).toString(16).padStart(2, '0')).join('');
            option.style.border = hexColor.toUpperCase() === color.toUpperCase() ? '2px solid white' : '2px solid transparent';
        });

        const customSwatch = picker.querySelector('.custom-color-swatch');
        if (customSwatch) {
            customSwatch.style.border = '2px solid transparent';
        }
    }

    // Refresh project tags to show new color
    const project = projects.find(p => p.id === projectId);
    if (project && project.tags) {
        renderProjectDetailsTags(project.tags, projectId);
    }

    // Refresh projects list to update tag colors in cards
    if (document.getElementById('projects')?.classList.contains('active')) {
        appState.projectsSortedView = null;
        renderProjects();
    }
}

async function handleProjectCustomColorChange(projectId, color) {
    if (!color) return;

    const saved = await setProjectColor(projectId, color);
    if (!saved) return;

    const pickerRoot = document.getElementById(`color-picker-${projectId}`);
    if (pickerRoot) {
        const currentColorDiv = pickerRoot.previousElementSibling;
        if (currentColorDiv) {
            currentColorDiv.style.backgroundColor = color;
        }

        const customSwatch = pickerRoot.querySelector('.custom-color-swatch');
        if (customSwatch) {
            customSwatch.style.backgroundColor = color;
        }
    }

    const picker = document.getElementById(`color-picker-${projectId}`);
    if (picker) {
        picker.querySelectorAll('.color-option').forEach(option => {
            option.style.border = '2px solid transparent';
        });
        const customSwatch = picker.querySelector('.custom-color-swatch');
        if (customSwatch) {
            customSwatch.style.border = '2px solid white';
        }
    }

    // Refresh project tags to show new color
    const project = projects.find(p => p.id === projectId);
    if (project && project.tags) {
        renderProjectDetailsTags(project.tags, projectId);
    }

    // Refresh projects list to update tag colors in cards
    if (document.getElementById('projects')?.classList.contains('active')) {
        appState.projectsSortedView = null;
        renderProjects();
    }
}

function openCustomProjectColorPicker(projectId) {
    const input = document.getElementById(`project-color-input-${projectId}`);
    if (!input) return;
    ignoreNextOutsideColorClick = true;
    input.click();
}


// === Global filter state ===
let filterState = {
    search: "",
    statuses: new Set(),
    statusExcludeMode: false, // true = exclude selected statuses; false = include only selected
    priorities: new Set(),
    priorityExcludeMode: false,
    projects: new Set(),
    projectExcludeMode: false,
    tags: new Set(),
    tagExcludeMode: false,
    tagAndMode: false, // true = AND (task must have ALL selected tags); false = OR (task has ANY)
    datePresets: new Set(), // Quick date filters: overdue, today, tomorrow, week, month (multi-select)
    dateFrom: "",
    dateTo: "",
    // Which date the range filter applies to. Was previously only ever set by
    // handleRouting(), so a range set any other way inherited whatever the last
    // URL happened to leave behind.
    dateField: "endDate", // 'startDate' | 'endDate'
    // Activity windows (inclusive ISO bounds) — created/updated/completed. Set only
    // by Kai today (no toolbar control); surfaced as removable chips.
    createdFrom: "", createdTo: "",
    updatedFrom: "", updatedTo: "",
    completedFrom: "", completedTo: "",
    taskIds: new Set(), // Focused task-ID filter - only set via URL, not UI (email notifications, Ask Kai "Ver")
    taskIdsSource: '', // where the taskIds filter came from: '' (email default) | 'generated' (Ask Kai) | 'report' (status report) | 'overdue' (project overdue chip)
    linkTypes: new Set(), // Link type filters: blocks, is_blocked_by, relates_to, has_web_links, no_links
    duplicateTitles: false, // true = only tasks whose title is shared by another task (set only by Kai)
    // Kai "top N most recently modified/created" — recency ranking + result cap.
    // Set only by a Kai query; no toolbar control. Applied at the end of getFilteredTasks.
    kaiSort: '',    // '' | 'recent-updated' | 'recent-created'
    kaiLimit: null, // positive integer, or null for no cap
};

// === Project filter state ===
let projectFilterState = {
    search: "",
    statuses: new Set(), // planning, active, completed
    statusExcludeMode: false, // true = exclude selected statuses; false = include only selected
    taskFilter: "", // 'has-tasks', 'no-tasks', or empty
    updatedFilter: "all", // all | 5m | 30m | 24h | week | month
    tags: new Set(), // project tags filter
    tagExcludeMode: false, // true = exclude selected tags; false = include only selected
    projectIds: new Set(), // focused set of project IDs (e.g. Ask Kai "view generated"); URL-only, like tasks taskIds
    projectIdsSource: '', // '' (Ask Kai, default) | 'report' (status report links)
    // Completion percentage (done tasks / total tasks). The value was already
    // computed for the "% Completed" sort and shown on every project card; these
    // make it filterable. null = unbounded. Both are inclusive.
    completionMin: null, // 0-100
    completionMax: null, // 0-100
};

// === Calendar entity mode + project filter state ===
let calendarShowProjects = true;
let calendarShowTasks = true;

let calendarProjectFilterState = {
    search: '',
    statuses: new Set(),
    statusExcludeMode: false,
    tags: new Set(),
    tagExcludeMode: false,
    updatedFilter: 'all',
};

function applyCalendarEntityUI() {
    const gf = document.getElementById('global-filters');
    if (!gf) return;
    if (calendarShowProjects) gf.removeAttribute('data-cal-hide-projects');
    else gf.setAttribute('data-cal-hide-projects', '');
    if (calendarShowTasks) gf.removeAttribute('data-cal-hide-tasks');
    else gf.setAttribute('data-cal-hide-tasks', '');

    // Sync checkbox checked state
    document.querySelectorAll('.cal-check[data-cal-toggle="projects"]').forEach(cb => {
        cb.setAttribute('aria-checked', calendarShowProjects ? 'true' : 'false');
    });
    document.querySelectorAll('.cal-check[data-cal-toggle="tasks"]').forEach(cb => {
        cb.setAttribute('aria-checked', calendarShowTasks ? 'true' : 'false');
    });
}

function resetCalendarFilters() {
    calendarShowProjects = settings.calendarShowProjects !== false;
    calendarShowTasks = settings.calendarShowTasks !== false;
    calendarProjectFilterState.search = '';
    calendarProjectFilterState.statuses.clear();
    calendarProjectFilterState.statusExcludeMode = false;
    calendarProjectFilterState.tags.clear();
    calendarProjectFilterState.tagExcludeMode = false;
    calendarProjectFilterState.updatedFilter = 'all';

    applyCalendarEntityUI();

    const search = document.getElementById('cal-project-search');
    if (search) search.value = '';

    document.querySelectorAll('#calendar-project-filters .filter-group:not(#cal-group-project-top) input[type="checkbox"]').forEach(cb => { cb.checked = false; });
    document.querySelectorAll('#calendar-project-filters input[type="radio"][value="all"]').forEach(rb => { rb.checked = true; });

    updateCalendarProjectFilterBadges();
    updateCalClearBtn();
    setCalendarFilterPanel(false);
}

function clearCalendarAllFilters() {
    filterState.statuses.clear();
    filterState.statusExcludeMode = false;
    filterState.priorities.clear();
    filterState.priorityExcludeMode = false;
    filterState.projects.clear();
    filterState.projectExcludeMode = false;
    filterState.tags.clear();
    filterState.tagExcludeMode = false;
    filterState.tagAndMode = false;
    filterState.linkTypes.clear();
    filterState.search = '';
    filterState.datePresets.clear();
    filterState.dateFrom = '';
    filterState.dateTo = '';

    calendarProjectFilterState.search = '';
    calendarProjectFilterState.statuses.clear();
    calendarProjectFilterState.statusExcludeMode = false;
    calendarProjectFilterState.tags.clear();
    calendarProjectFilterState.tagExcludeMode = false;
    calendarProjectFilterState.updatedFilter = 'all';

    document.querySelectorAll('#filter-search, #cal-filter-search').forEach((taskSearch) => {
        taskSearch.value = '';
    });

    const projectSearch = document.getElementById('cal-project-search');
    if (projectSearch) projectSearch.value = '';

    const dateFromEl = document.getElementById('filter-date-from');
    if (dateFromEl) {
        dateFromEl.value = '';
        const dateFromWrapper = dateFromEl.closest('.date-input-wrapper');
        const displayInput = dateFromWrapper?.querySelector('.date-display');
        if (displayInput && displayInput._flatpickr) {
            displayInput._flatpickr.clear();
        }
    }

    const dateToEl = document.getElementById('filter-date-to');
    if (dateToEl) {
        dateToEl.value = '';
        const dateToWrapper = dateToEl.closest('.date-input-wrapper');
        const displayInput = dateToWrapper?.querySelector('.date-display');
        if (displayInput && displayInput._flatpickr) {
            displayInput._flatpickr.clear();
        }
    }

    document.querySelectorAll('#global-filters > .filters-toolbar .dropdown-panel input[type="checkbox"], #cal-filter-drawer .cal-task-filters .dropdown-panel input[type="checkbox"], #calendar-project-filters input[type="checkbox"]').forEach((cb) => {
        cb.checked = false;
    });
    document.querySelectorAll('#calendar-project-filters input[type="radio"][value="all"]').forEach((rb) => {
        rb.checked = true;
    });

    updateAllFilterModeUI();
    document.querySelectorAll('#cal-group-project-status .filter-mode-btn, #cal-group-project-tags .filter-mode-btn').forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.mode === 'include');
    });

    updateFilterBadges();
    updateCalendarProjectFilterBadges();
    renderAfterFilterChange();
    updateClearButtonVisibility();
    updateCalClearBtn();
    renderCalendar();
}

function updateCalendarProjectFilterBadges() {
    const statusBadge = document.getElementById('cal-badge-project-status');
    if (statusBadge) statusBadge.textContent = calendarProjectFilterState.statuses.size || '';

    const tagBadge = document.getElementById('cal-badge-project-tags');
    if (tagBadge) tagBadge.textContent = calendarProjectFilterState.tags.size || '';

    const updatedBadge = document.getElementById('cal-badge-project-updated');
    if (updatedBadge) updatedBadge.textContent = calendarProjectFilterState.updatedFilter !== 'all' ? '1' : '';
}

function updateCalClearBtn() {
    const hasFilters = calendarProjectFilterState.search ||
        calendarProjectFilterState.statuses.size > 0 ||
        calendarProjectFilterState.tags.size > 0 ||
        calendarProjectFilterState.updatedFilter !== 'all';

    const btn = document.getElementById('cal-btn-clear-project-filters');
    if (btn) btn.style.display = hasFilters ? '' : 'none';

    renderCalProjectFilterChips();
    updateCalControlBar();
}

function renderCalProjectFilterChips() {
    const wrap = document.getElementById('cal-project-active-filters');
    if (!wrap) return;
    wrap.innerHTML = '';

    const addChip = (label, value, onRemove) => {
        const chip = document.createElement('span');
        chip.className = 'filter-chip';
        const text = document.createElement('span');
        text.className = 'chip-text';
        text.textContent = `${label}: ${value}`;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'chip-remove';
        btn.setAttribute('aria-label', t('filters.chip.removeAria', { label }));
        btn.textContent = '×';
        btn.addEventListener('click', onRemove);
        chip.appendChild(text);
        chip.appendChild(btn);
        wrap.appendChild(chip);
    };

    if (calendarProjectFilterState.search)
        addChip(t('filters.chip.search'), calendarProjectFilterState.search, () => {
            calendarProjectFilterState.search = '';
            const el = document.getElementById('cal-project-search');
            if (el) el.value = '';
            updateCalClearBtn();
            renderCalendar();
        });

    const statusChipLabel = calendarProjectFilterState.statusExcludeMode
        ? t('tasks.filters.excluding')
        : t('projects.filters.status');
    calendarProjectFilterState.statuses.forEach(s =>
        addChip(statusChipLabel, getProjectStatusLabel(s), () => {
            calendarProjectFilterState.statuses.delete(s);
            const cb = document.querySelector(`#cal-group-project-status input[value="${s}"]`);
            if (cb) cb.checked = false;
            updateCalendarProjectFilterBadges();
            updateCalClearBtn();
            renderCalendar();
        })
    );

    calendarProjectFilterState.tags.forEach(tag =>
        addChip(t('filters.chip.tag'), tag.toUpperCase(), () => {
            calendarProjectFilterState.tags.delete(tag);
            const cb = document.querySelector(`#cal-project-tags-options input[value="${tag}"]`);
            if (cb) cb.checked = false;
            updateCalendarProjectFilterBadges();
            updateCalClearBtn();
            renderCalendar();
        })
    );

    if (calendarProjectFilterState.updatedFilter !== 'all')
        addChip(t('filters.chip.updated'), getProjectUpdatedFilterLabel(calendarProjectFilterState.updatedFilter), () => {
            calendarProjectFilterState.updatedFilter = 'all';
            const radio = document.querySelector('#cal-group-project-updated input[value="all"]');
            if (radio) radio.checked = true;
            updateCalendarProjectFilterBadges();
            updateCalClearBtn();
            renderCalendar();
        });
}


// === Compact Calendar Control Bar ===

let calendarFilterPanelOpen = false;

function getCalendarActiveFilterCount() {
    let count = 0;
    // Task filters
    count += filterState.statuses.size;
    count += filterState.priorities.size;
    count += filterState.projects.size;
    count += filterState.tags.size;
    count += filterState.linkTypes.size;
    count += filterState.datePresets.size;
    if (filterState.search) count += 1;
    if (filterState.dateFrom || filterState.dateTo) count += 1;
    // Project filters
    count += calendarProjectFilterState.statuses.size;
    count += calendarProjectFilterState.tags.size;
    if (calendarProjectFilterState.search) count += 1;
    if (calendarProjectFilterState.updatedFilter !== 'all') count += 1;
    return count;
}

function updateCalControlBar() {
    const totalCount = getCalendarActiveFilterCount();

    // Badge
    const badge = document.getElementById('cal-ctrl-badge');
    if (badge) badge.textContent = totalCount > 0 ? totalCount : '';

    const clearBtn = document.getElementById('cal-ctrl-clear-all');
    if (clearBtn) clearBtn.style.display = totalCount > 0 ? '' : 'none';

    // Filters button aria-expanded
    const filtersBtn = document.getElementById('cal-ctrl-filters-btn');
    if (filtersBtn) filtersBtn.setAttribute('aria-expanded', calendarFilterPanelOpen ? 'true' : 'false');

    // Toggle pill active state — Projects
    const projBtn = document.getElementById('cal-ctrl-toggle-projects');
    if (projBtn) projBtn.classList.toggle('active', calendarShowProjects);

    // Toggle pill active state — Tasks
    const tasksBtn = document.getElementById('cal-ctrl-toggle-tasks');
    if (tasksBtn) tasksBtn.classList.toggle('active', calendarShowTasks);

}

function setCalendarFilterPanel(open) {
    calendarFilterPanelOpen = open;
    const drawer = document.getElementById('cal-filter-drawer');
    const backdrop = document.getElementById('cal-drawer-backdrop');
    if (drawer) drawer.classList.toggle('cal-drawer-open', open);
    if (backdrop) backdrop.classList.toggle('cal-drawer-open', open);
    // Close any open filter dropdown panels when drawer opens
    if (open) document.querySelectorAll('.filter-group.open').forEach(g => g.classList.remove('open'));
    // Prevent body scroll when drawer is open
    document.body.classList.toggle('cal-drawer-active', open);
    updateCalControlBar();
}

function initCalControlBarListeners() {
    // Filters toggle button
    const filtersBtn = document.getElementById('cal-ctrl-filters-btn');
    if (filtersBtn) {
        filtersBtn.addEventListener('click', () => {
            setCalendarFilterPanel(!calendarFilterPanelOpen);
        });
    }

    ['cal-ctrl-clear-all', 'cal-drawer-clear-all'].forEach((id) => {
        document.getElementById(id)?.addEventListener('click', () => {
            clearCalendarAllFilters();
        });
    });

    document.querySelectorAll('.cal-step-quick-btn').forEach((btn) => {
        btn.addEventListener('click', () => applyCalendarProjectScopePreset(btn.dataset.calScope));
    });
    document.getElementById('cal-step-projects-clear')?.addEventListener('click', () => applyCalendarProjectScopePreset('all'));
    document.getElementById('cal-step-tasks-clear')?.addEventListener('click', clearCalendarTaskStepFilters);

    // Close button inside drawer
    const closeBtn = document.getElementById('cal-drawer-close');
    if (closeBtn) {
        closeBtn.addEventListener('click', () => setCalendarFilterPanel(false));
    }

    // Backdrop click closes drawer
    const backdrop = document.getElementById('cal-drawer-backdrop');
    if (backdrop) {
        backdrop.addEventListener('click', (e) => {
            // A text selection dragged out of the drawer must not close it.
            if (isBackdropClick(e, backdrop)) setCalendarFilterPanel(false);
        });
    }

    // Escape key closes drawer
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && calendarFilterPanelOpen) setCalendarFilterPanel(false);
    });

    // Projects toggle pill
    const projBtn = document.getElementById('cal-ctrl-toggle-projects');
    if (projBtn) {
        projBtn.addEventListener('click', () => {
            calendarShowProjects = !calendarShowProjects;
            settings.calendarShowProjects = calendarShowProjects;
            saveSettings();
            applyCalendarEntityUI();
            updateCalControlBar();
            renderCalendar();
        });
    }

    // Tasks toggle pill
    const tasksBtn = document.getElementById('cal-ctrl-toggle-tasks');
    if (tasksBtn) {
        tasksBtn.addEventListener('click', () => {
            calendarShowTasks = !calendarShowTasks;
            settings.calendarShowTasks = calendarShowTasks;
            saveSettings();
            applyCalendarEntityUI();
            updateCalControlBar();
            renderCalendar();
        });
    }
}


function initCalendarFilterEventListeners() {
    // Row pills — toggle entity on/off
    document.querySelectorAll('.cal-check[data-cal-toggle]').forEach(cb => {
        const toggle = () => {
            const which = cb.dataset.calToggle;
            if (which === 'projects') {
                calendarShowProjects = !calendarShowProjects;
                settings.calendarShowProjects = calendarShowProjects;
            } else if (which === 'tasks') {
                calendarShowTasks = !calendarShowTasks;
                settings.calendarShowTasks = calendarShowTasks;
            }
            saveSettings();
            applyCalendarEntityUI();
            updateCalControlBar();
            renderCalendar();
        };
        cb.addEventListener('click', toggle);
        cb.addEventListener('keydown', e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); } });
    });

    // Project search — Enter/blur only (no per-keystroke renders)
    const calSearch = document.getElementById('cal-project-search');
    if (calSearch) {
        const applyCalSearch = () => {
            calendarProjectFilterState.search = calSearch.value;
            updateCalClearBtn();
            renderCalendar();
        };
        calSearch.addEventListener('blur', applyCalSearch);
        calSearch.addEventListener('keydown', e => {
            if (e.key === 'Enter') { e.preventDefault(); calSearch.blur(); }
        });
    }

    // Project status checkboxes
    document.querySelectorAll('#cal-group-project-status input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
            if (cb.checked) calendarProjectFilterState.statuses.add(cb.value);
            else calendarProjectFilterState.statuses.delete(cb.value);
            updateCalendarProjectFilterBadges();
            updateCalClearBtn();
            renderCalendar();
        });
    });

    // Project status include/exclude mode
    document.querySelectorAll('#cal-group-project-status .filter-mode-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('#cal-group-project-status .filter-mode-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            calendarProjectFilterState.statusExcludeMode = btn.dataset.mode === 'exclude';
            renderCalendar();
        });
    });

    // Project tags checkboxes (populated dynamically)
    document.getElementById('cal-project-tags-options')?.addEventListener('change', (e) => {
        const cb = e.target;
        if (cb.type !== 'checkbox') return;
        if (cb.checked) calendarProjectFilterState.tags.add(cb.value);
        else calendarProjectFilterState.tags.delete(cb.value);
        updateCalendarProjectFilterBadges();
        updateCalClearBtn();
        renderCalendar();
    });

    // Project tags include/exclude mode
    document.querySelectorAll('#cal-group-project-tags .filter-mode-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            document.querySelectorAll('#cal-group-project-tags .filter-mode-btn').forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            calendarProjectFilterState.tagExcludeMode = btn.dataset.mode === 'exclude';
            renderCalendar();
        });
    });

    // Project updated radio
    document.querySelectorAll('input[name="cal-project-updated-filter"]').forEach(rb => {
        rb.addEventListener('change', () => {
            calendarProjectFilterState.updatedFilter = rb.value;
            updateCalendarProjectFilterBadges();
            updateCalClearBtn();
            renderCalendar();
        });
    });

    // Clear project filters
    document.getElementById('cal-btn-clear-project-filters')?.addEventListener('click', () => {
        calendarProjectFilterState.search = '';
        calendarProjectFilterState.statuses.clear();
        calendarProjectFilterState.statusExcludeMode = false;
        calendarProjectFilterState.tags.clear();
        calendarProjectFilterState.tagExcludeMode = false;
        calendarProjectFilterState.updatedFilter = 'all';

        const search = document.getElementById('cal-project-search');
        if (search) search.value = '';
        document.querySelectorAll('#calendar-project-filters .filter-group:not(#cal-group-project-top) input[type="checkbox"]').forEach(cb => { cb.checked = false; });
        document.querySelectorAll('#calendar-project-filters input[type="radio"][value="all"]').forEach(rb => { rb.checked = true; });
        document.querySelectorAll('#cal-group-project-status .filter-mode-btn, #cal-group-project-tags .filter-mode-btn').forEach(b => {
            b.classList.toggle('active', b.dataset.mode === 'include');
        });

        updateCalendarProjectFilterBadges();
        updateCalClearBtn();
        renderCalendar();
    });
}

function populateCalendarProjectTagsDropdown() {
    const list = document.getElementById('cal-project-tags-options');
    if (!list) return;
    const allTags = [...new Set(projects.flatMap(p => p.tags || []))].sort();
    list.innerHTML = allTags.map(tag =>
        `<li><label><input type="checkbox" value="${tag}" ${calendarProjectFilterState.tags.has(tag) ? 'checked' : ''}> ${tag.toUpperCase()}</label></li>`
    ).join('');
}

// === Project sort state for ASC/DESC toggle ===
const DEFAULT_PROJECT_SORT = 'updated-desc';
let projectSortState = {
    lastSort: '',
    direction: 'asc' // 'asc' or 'desc'
};

// Initialize filters UI - only call once on page load
let filtersUIInitialized = false;

function syncExactDateFilterInputs() {
    const isExactDate = !!filterState.dateFrom
        && filterState.dateFrom === filterState.dateTo;

    document.querySelectorAll('input[data-date-filter-exact]:not(.date-display)').forEach((input) => {
        const value = isExactDate && input.dataset.dateFilterExact === filterState.dateField
            ? filterState.dateFrom : '';
        if (input.value === value) return;
        input.value = value;
        input._flatpickrInstance?.setDate(value || null, false);
    });
}

function applyExactDateFilter(iso, dateField) {
    filterState.dateField = dateField;
    filterState.dateFrom = iso;
    filterState.dateTo = iso;
    filterState.datePresets.clear();
    ['filter-date-from', 'filter-date-to'].forEach((id) => {
        const rangeInput = document.getElementById(id);
        if (!rangeInput) return;
        rangeInput.value = iso;
        rangeInput._flatpickrInstance?.setDate(iso || null, false);
    });
    document.querySelectorAll('input[data-filter="date-preset"]').forEach((preset) => {
        preset.checked = false;
    });
    updateFilterBadges();
    renderAfterFilterChange();
    if (document.getElementById('calendar-view')) renderCalendar();
    updateClearButtonVisibility();
}

function initFiltersUI() {
    if (filtersUIInitialized) return; // Prevent duplicate initialization
    filtersUIInitialized = true;

    populateProjectOptions();
    populateTagOptions();
    populateCalendarTaskProjectOptions();
    populateCalendarTaskTagOptions();
    updateNoDateOptionVisibility();
    setupFilterEventListeners();
    initCalendarFilterEventListeners();
    initCalControlBarListeners();
}

// Separate function to only update project options
function populateProjectOptions() {
    const ul = document.getElementById("project-options");
    if (ul) {
        // Preserve current selection
        const selected = new Set(filterState.projects);

        ul.innerHTML = "";

        // Add "No Project" option only if there are tasks without assigned projects
        const hasNoProjectTasks = tasks.some(t => !t.projectId);
        if (hasNoProjectTasks) {
            const noProjectLi = document.createElement("li");
            const checked = selected.has('none') ? 'checked' : '';
            noProjectLi.innerHTML = `<label><input type="checkbox" id="proj-none" value="none" data-filter="project" ${checked}><span class="filter-option-text">${t('tasks.noProject')}</span></label>`;
            ul.appendChild(noProjectLi);
        } else {
            // Ensure state is consistent if 'none' was previously selected
            if (selected.has('none')) {
                filterState.projects.delete('none');
                updateFilterBadges();
            }
        }

        if (projects.length === 0) {
            const li = document.createElement("li");
            li.textContent = t('filters.noOtherProjects');
            li.style.color = "var(--text-muted)";
            li.style.padding = "8px 12px";
            ul.appendChild(li);
        } else {
            // Sort alphabetically by name (case-insensitive)
            projects
              .slice()
              .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
              .forEach((p) => {
                const li = document.createElement("li");
                const id = `proj-${p.id}`;
                const checked = selected.has(String(p.id)) ? 'checked' : '';
                li.innerHTML = `<label><input type="checkbox" id="${id}" value="${p.id}" data-filter="project" ${checked}><span class="filter-option-text">${p.name}</span></label>`;
                ul.appendChild(li);
            });
        }

        // Re-attach change listeners for project checkboxes
        ul.querySelectorAll('input[type="checkbox"][data-filter="project"]').forEach((cb) => {
            cb.addEventListener("change", () => {
                toggleSet(filterState.projects, cb.value, cb.checked);
                updateFilterBadges();
                renderAfterFilterChange();
                const calendarView = document.getElementById("calendar-view");
                if (calendarView) {
                    renderCalendar();
                }
                updateClearButtonVisibility();
            });
        });
    }
}

// Separate function to only update tag options (can be called independently)
function populateTagOptions() {
    const tagUl = document.getElementById("tag-options");
    if (tagUl) {
        // Preserve previously selected tags from state instead of reading DOM
        const currentlySelected = new Set(filterState.tags);

        // Collect all unique tags from all tasks
        const allTags = new Set();
        tasks.forEach(t => {
            if (t.tags && t.tags.length > 0) {
                t.tags.forEach(tag => allTags.add(tag));
            }
        });
        
        tagUl.innerHTML = "";
        
        // Add "No Tags" only if there are tasks without tags
        const hasNoTagTasks = tasks.some(t => !t.tags || t.tags.length === 0);
        if (hasNoTagTasks) {
            const noTagsLi = document.createElement("li");
            const noTagsChecked = currentlySelected.has('none') ? 'checked' : '';
            noTagsLi.innerHTML = `<label><input type="checkbox" id="tag-none" value="none" data-filter="tag" ${noTagsChecked}> ${t('filters.noTags')}</label>`;
            tagUl.appendChild(noTagsLi);
        } else {
            if (currentlySelected.has('none')) {
                filterState.tags.delete('none');
                updateFilterBadges();
            }
        }
        
        if (allTags.size === 0) {
            const li = document.createElement("li");
            li.textContent = t('filters.noOtherTags');
            li.style.color = "var(--text-muted)";
            li.style.padding = "8px 12px";
            tagUl.appendChild(li);
        } else {
            Array.from(allTags).sort().forEach((tag) => {
                const li = document.createElement("li");
                const id = `tag-${tag}`;
                const checked = currentlySelected.has(tag) ? 'checked' : '';
                li.innerHTML = `<label><input type="checkbox" id="${id}" value="${tag}" data-filter="tag" ${checked}> ${tag.toUpperCase()}</label>`;
                tagUl.appendChild(li);
            });
        }
        
        // Re-attach event listeners for new checkboxes
        tagUl.querySelectorAll('input[type="checkbox"]').forEach(cb => {
            cb.addEventListener("change", () => {
                const type = cb.dataset.filter;
                if (type === "tag") toggleSet(filterState.tags, cb.value, cb.checked);
                updateFilterBadges();
                renderAfterFilterChange();
                
                // Same calendar fix as task deletion/creation
                const calendarView = document.getElementById("calendar-view");
                if (calendarView) {
                    renderCalendar();
                }
                
                updateClearButtonVisibility();
            });
        });

        // Clear tag filter search when repopulating so list shows all
        const tagSearchInput = document.getElementById("tag-filter-search-input");
        if (tagSearchInput) {
            tagSearchInput.value = "";
            filterTagOptions("");
        }
    }
}

function filterTagOptions(query) {
    const tagUl = document.getElementById("tag-options");
    if (!tagUl) return;
    const q = (query || "").toLowerCase().trim();
    tagUl.querySelectorAll("li").forEach((li) => {
        const text = (li.textContent || "").toLowerCase();
        const match = !q || text.includes(q);
        li.style.display = match ? "" : "none";
    });
}

// Keyboard arrow navigation for filter search inputs + their list panels
function attachFilterListKeyNav(searchInput, listEl) {
    if (!searchInput || !listEl) return;

    function visibleItems() {
        return Array.from(listEl.querySelectorAll("li")).filter(li => li.style.display !== "none");
    }

    searchInput.addEventListener("keydown", (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            const items = visibleItems();
            if (items.length) items[0].focus();
        } else if (e.key === "Enter") {
            // Enter in the search field toggles the top matching option and keeps the
            // panel open, so you can type -> Enter -> type -> Enter to tick off matches.
            e.preventDefault();
            const items = visibleItems();
            const cb = items.length ? items[0].querySelector('input[type="checkbox"]') : null;
            if (cb) { cb.checked = !cb.checked; cb.dispatchEvent(new Event("change", { bubbles: true })); }
        }
    });

    listEl.addEventListener("keydown", (e) => {
        const items = visibleItems();
        const idx = items.indexOf(document.activeElement);
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (idx < items.length - 1) items[idx + 1].focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (idx > 0) items[idx - 1].focus();
            else searchInput.focus();
        } else if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            const cb = document.activeElement.querySelector('input[type="checkbox"]');
            if (cb) { cb.checked = !cb.checked; cb.dispatchEvent(new Event("change", { bubbles: true })); }
        } else if (e.key === "Escape") {
            searchInput.focus();
        }
    });

    // Make list items focusable
    const observer = new MutationObserver(() => {
        listEl.querySelectorAll("li").forEach(li => {
            if (!li.getAttribute("tabindex")) li.setAttribute("tabindex", "-1");
        });
    });
    observer.observe(listEl, { childList: true });
    // Apply to any already-present items
    listEl.querySelectorAll("li").forEach(li => {
        if (!li.getAttribute("tabindex")) li.setAttribute("tabindex", "-1");
    });
}

function filterProjectOptions(query) {
    const projectUl = document.getElementById("project-options");
    if (!projectUl) return;
    const q = (query || "").toLowerCase().trim();
    projectUl.querySelectorAll("li").forEach((li) => {
        const text = (li.textContent || "").toLowerCase();
        const match = !q || text.includes(q);
        li.style.display = match ? "" : "none";
    });
}

function populateCalendarTaskProjectOptions() {
    // Task-section and Projects-section dropdowns share filterState.projects.
    ["cal-project-options", "cal-project-top-options"].forEach(populateCalendarProjectOptionsList);
}

function populateCalendarProjectOptionsList(listId) {
    const ul = document.getElementById(listId);
    if (!ul) return;

    const selected = new Set(filterState.projects);
    ul.innerHTML = "";

    const hasNoProjectTasks = tasks.some(t => !t.projectId);
    if (hasNoProjectTasks) {
        const noProjectLi = document.createElement("li");
        const checked = selected.has('none') ? 'checked' : '';
        noProjectLi.innerHTML = `<label><input type="checkbox" value="none" data-filter="project" ${checked}><span class="filter-option-text">${t('tasks.noProject')}</span></label>`;
        ul.appendChild(noProjectLi);
    }

    if (projects.length === 0) {
        const li = document.createElement("li");
        li.textContent = t('filters.noOtherProjects');
        li.style.color = "var(--text-muted)";
        li.style.padding = "8px 12px";
        ul.appendChild(li);
    } else {
        projects
            .slice()
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
            .forEach((p) => {
                const li = document.createElement("li");
                const checked = selected.has(String(p.id)) ? 'checked' : '';
                li.innerHTML = `<label><input type="checkbox" value="${p.id}" data-filter="project" ${checked}><span class="filter-option-text">${p.name}</span></label>`;
                ul.appendChild(li);
            });
    }

    ul.querySelectorAll('input[type="checkbox"][data-filter="project"]').forEach((cb) => {
        cb.addEventListener("change", () => {
            toggleSet(filterState.projects, cb.value, cb.checked);
            syncFilterCheckboxesFromState("project");
            updateFilterBadges();
            renderAfterFilterChange();
            const calendarView = document.getElementById("calendar-view");
            if (calendarView) renderCalendar();
            updateClearButtonVisibility();
        });
    });
}

function populateCalendarTaskTagOptions() {
    const tagUl = document.getElementById("cal-tag-options");
    if (!tagUl) return;

    const currentlySelected = new Set(filterState.tags);
    const allTags = new Set();
    tasks.forEach(t => {
        if (t.tags && t.tags.length > 0) {
            t.tags.forEach(tag => allTags.add(tag));
        }
    });

    tagUl.innerHTML = "";

    const hasNoTagTasks = tasks.some(t => !t.tags || t.tags.length === 0);
    if (hasNoTagTasks) {
        const noTagsLi = document.createElement("li");
        const checked = currentlySelected.has('none') ? 'checked' : '';
        noTagsLi.innerHTML = `<label><input type="checkbox" value="none" data-filter="tag" ${checked}> ${t('filters.noTags')}</label>`;
        tagUl.appendChild(noTagsLi);
    }

    if (allTags.size === 0) {
        const li = document.createElement("li");
        li.textContent = t('filters.noOtherTags');
        li.style.color = "var(--text-muted)";
        li.style.padding = "8px 12px";
        tagUl.appendChild(li);
    } else {
        Array.from(allTags).sort().forEach((tag) => {
            const li = document.createElement("li");
            const checked = currentlySelected.has(tag) ? 'checked' : '';
            li.innerHTML = `<label><input type="checkbox" value="${tag}" data-filter="tag" ${checked}> ${tag.toUpperCase()}</label>`;
            tagUl.appendChild(li);
        });
    }

    tagUl.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.addEventListener("change", () => {
            if (cb.dataset.filter === "tag") toggleSet(filterState.tags, cb.value, cb.checked);
            updateFilterBadges();
            renderAfterFilterChange();
            const calendarView = document.getElementById("calendar-view");
            if (calendarView) renderCalendar();
            updateClearButtonVisibility();
        });
    });
}

function filterCalendarTaskTagOptions(query) {
    const tagUl = document.getElementById("cal-tag-options");
    if (!tagUl) return;
    const q = (query || "").toLowerCase().trim();
    tagUl.querySelectorAll("li").forEach((li) => {
        const text = (li.textContent || "").toLowerCase();
        li.style.display = !q || text.includes(q) ? "" : "none";
    });
}

function filterCalendarTaskProjectOptions(query) {
    ["cal-project-options", "cal-project-top-options"].forEach((id) => {
        const projectUl = document.getElementById(id);
        if (!projectUl) return;
        const q = (query || "").toLowerCase().trim();
        projectUl.querySelectorAll("li").forEach((li) => {
            const text = (li.textContent || "").toLowerCase();
            li.style.display = !q || text.includes(q) ? "" : "none";
        });
    });
}

// Populate and show custom tag autocomplete dropdown
function showTagAutocomplete(query) {
    const dropdown = document.getElementById("tag-autocomplete-dropdown");
    if (!dropdown) return;

    const taskForm = document.getElementById("task-form");
    const editingTaskId = taskForm?.dataset.editingTaskId;
    const currentTaskTags = new Set(
        editingTaskId
            ? (tasks.find((t) => t.id === parseInt(editingTaskId, 10))?.tags || [])
            : (window.tempTags || [])
    );
    
    // Collect all unique tags from all tasks
    const allTags = new Set();
    tasks.forEach(t => {
        if (t.tags && t.tags.length > 0) {
            t.tags.forEach(tag => allTags.add(tag));
        }
    });
    
    // Filter tags based on query
    const q = query.toLowerCase().trim();
    const matchingTags = Array.from(allTags)
        .filter(tag => !currentTaskTags.has(tag))
        .filter(tag => tag.toLowerCase().includes(q))
        .sort();
    
    // Clear dropdown
    dropdown.innerHTML = "";
    
    if (matchingTags.length === 0 || !q) {
        dropdown.style.display = "none";
        return;
    }
    
    // Populate dropdown with matching tags as colored badges
    matchingTags.forEach(tag => {
        const item = document.createElement("div");
        item.className = "tag-autocomplete-item";
        
        // Create colored badge
        const badge = document.createElement("span");
        const tagColor = getTagColor(tag);
        applyTintedTagStyle(badge, tagColor);
        badge.style.padding = "4px 10px";
        badge.style.borderRadius = "4px";
        badge.style.fontSize = "12px";
        badge.style.fontWeight = "500";
        badge.textContent = tag.toUpperCase();
        
        item.appendChild(badge);
        
        item.addEventListener("click", async () => {
            // Add tag directly without needing to type and press +
            const taskId = document.getElementById('task-form').dataset.editingTaskId;
            
            if (taskId) {
                const task = tasks.find(t => t.id === parseInt(taskId));
                if (!task) return;
                if (!task.tags) task.tags = [];
                if (task.tags.includes(tag)) {
                    dropdown.style.display = "none";
                    return; // Already exists
                }

                // Store old state for history
                const oldTaskCopy = JSON.parse(JSON.stringify(task));

                task.tags = [...task.tags, tag];
                renderTags(task.tags);

                // Record history
                if (window.historyService) {
                    window.historyService.recordTaskUpdated(oldTaskCopy, task);
                }

                // Check and record project status change
                if (task.projectId) {
                    checkAndRecordProjectStatusChange(task.projectId, task);
                    checkProjectCompletionDateWarning(task.projectId, task);
                }

                const refreshedProjectViews = refreshProjectViews(task.projectId);
                if (!refreshedProjectViews) {
                    renderTasks();
                    const isMobile = getIsMobileCached();
                    if (isMobile || document.getElementById('list-view').classList.contains('active')) {
                        renderListView();
                    }
                }

                // Save using row-level operation
                populateTagOptions();
                populateCalendarTaskTagOptions();
                const now = new Date().toISOString();
                await saveTaskUpdate(parseInt(taskId), { tags: task.tags, updatedAt: now });
            } else {
                // Creating new task — use temp array (same as addTag())
                if (!window.tempTags) window.tempTags = [];
                if (!window.tempTags.includes(tag)) {
                    window.tempTags = [...window.tempTags, tag];
                    renderTags(window.tempTags);
                }
            }

            // Clear input and hide dropdown
            const tagInput = document.getElementById("tag-input");
            if (tagInput) tagInput.value = "";
            dropdown.style.display = "none";
        });
        
        dropdown.appendChild(item);
    });
    
    dropdown.style.display = "block";
}

function hideTagAutocomplete() {
    const dropdown = document.getElementById("tag-autocomplete-dropdown");
    if (dropdown) {
        // Small delay to allow click events to fire
        setTimeout(() => {
            dropdown.style.display = "none";
        }, 150);
    }
}

// Setup tag autocomplete event listeners
let tagAutocompleteListenersSetup = false;
function setupTagAutocompleteListeners() {
    if (tagAutocompleteListenersSetup) return;
    const tagInput = document.getElementById("tag-input");
    if (!tagInput) return;
    tagAutocompleteListenersSetup = true;
    
    // Show autocomplete on input
    tagInput.addEventListener("input", (e) => {
        showTagAutocomplete(e.target.value);
    });

    // Keyboard navigation for autocomplete
    tagInput.addEventListener("keydown", (e) => {
        const dropdown = document.getElementById("tag-autocomplete-dropdown");
        if (!dropdown || dropdown.style.display === "none") {
            // Still allow Escape to close if open state is out of sync
            if (e.key === "Escape") {
                hideTagAutocomplete();
            }
            return;
        }

        const items = Array.from(dropdown.querySelectorAll(".tag-autocomplete-item"));
        if (items.length === 0) {
            if (e.key === "Escape") {
                hideTagAutocomplete();
            }
            return;
        }

        const currentIndex = items.findIndex((el) => el.classList.contains("is-active"));

        if (e.key === "ArrowDown") {
            e.preventDefault();
            const nextIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, items.length - 1);
            items.forEach((el, idx) => {
                el.classList.toggle("is-active", idx === nextIndex);
            });
            items[nextIndex].scrollIntoView({ block: "nearest" });
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const prevIndex = currentIndex === -1 ? items.length - 1 : Math.max(currentIndex - 1, 0);
            items.forEach((el, idx) => {
                el.classList.toggle("is-active", idx === prevIndex);
            });
            items[prevIndex].scrollIntoView({ block: "nearest" });
        } else if (e.key === "Enter" || e.key === " ") {
            if (currentIndex !== -1) {
                e.preventDefault();
                const active = items[currentIndex];
                active.click();
            }
        } else if (e.key === "Escape") {
            hideTagAutocomplete();
        }
    });

    // Hide autocomplete on blur
    tagInput.addEventListener("blur", () => {
        hideTagAutocomplete();
    });
}

// Setup event listeners (only call once)
function setupFilterEventListeners() {

    const isMobile =
        !!(window.matchMedia && (
            window.matchMedia("(max-width: 768px)").matches ||
            window.matchMedia("(pointer: coarse)").matches
        ));

    // Show/hide project select-all based on number of projects
    ["project-select-all", "cal-project-select-all", "cal-project-top-select-all"].forEach((id) => {
        const selectAllProjectRow = document.getElementById(id);
        if (selectAllProjectRow && selectAllProjectRow.parentElement.parentElement) {
            selectAllProjectRow.parentElement.parentElement.style.display = projects.length > 1 ? 'block' : 'none';
        }
    });

    // Open/close dropdown panels for Status, Priority, Project, Tags, End Date, Start Date
    const groups = [
        document.getElementById("group-status"),
        document.getElementById("group-priority"),
        document.getElementById("group-project"),
        document.getElementById("group-tags"),
        document.getElementById("group-end-date"),
        document.getElementById("group-start-date"),
        document.getElementById("group-links"),
        document.getElementById("group-kanban-updated"),
        document.getElementById("cal-group-status"),
        document.getElementById("cal-group-priority"),
        document.getElementById("cal-group-project"),
        document.getElementById("cal-group-project-top"),
        document.getElementById("cal-group-tags"),
        document.getElementById("cal-group-end-date"),
        document.getElementById("cal-group-start-date"),
        document.getElementById("cal-group-links"),
        document.getElementById("cal-group-kanban-updated"),
        document.getElementById("group-project-status"),
        document.getElementById("group-project-updated"),
        document.getElementById("group-project-completion"),
        document.getElementById("group-project-tags"),
        // Calendar project filter dropdowns
        document.getElementById("cal-group-project-status"),
        document.getElementById("cal-group-project-updated"),
        document.getElementById("cal-group-project-tags"),
    ].filter(Boolean);

    const ensureBackdrop = () => {
        let backdrop = document.getElementById("dropdown-backdrop");
        if (!backdrop) {
            backdrop = document.createElement("div");
            backdrop.id = "dropdown-backdrop";
            backdrop.setAttribute("aria-hidden", "true");
            backdrop.addEventListener("click", () => closeAllPanels());
            document.body.appendChild(backdrop);
        }
        return backdrop;
    };

    // Restore any panel we portaled to <body> back into its original filter group.
    const restorePortaledPanels = () => {
        document.querySelectorAll(".dropdown-panel.sheet-portal").forEach((panel) => {
            panel.classList.remove("sheet-portal");
            const home = panel._sheetHome;
            if (home && home.parent) {
                home.parent.insertBefore(panel, home.next || null);
            }
            panel._sheetHome = null;
        });
    };

    // Clear the in-panel search boxes (Tags/Project, Tasks + Calendar) and reset
    // their filtered option lists, so a menu never reopens "stuck" on the last query.
    const resetFilterSearchInputs = () => {
        [
            ["tag-filter-search-input", filterTagOptions],
            ["project-filter-search-input", filterProjectOptions],
            ["cal-tag-filter-search-input", filterCalendarTaskTagOptions],
            ["cal-project-filter-search-input", filterCalendarTaskProjectOptions],
            ["cal-project-top-filter-search-input", filterCalendarTaskProjectOptions],
        ].forEach(([id, resetFn]) => {
            const input = document.getElementById(id);
            if (input && input.value) {
                input.value = "";
                if (typeof resetFn === "function") {
                    try { resetFn(""); } catch (e) {}
                }
            }
        });
    };

    const closeAllPanels = () => {
        groups.forEach((g) => g.classList.remove("open"));
        restorePortaledPanels();
        resetFilterSearchInputs();
        document.body.classList.remove("dropdown-sheet-open");
        const backdrop = document.getElementById("dropdown-backdrop");
        if (backdrop) backdrop.classList.remove("open");
    };

    const enhanceMobilePanel = (g) => {
        const panel = g.querySelector(".dropdown-panel");
        if (!panel) return;

        panel.classList.add("has-sheet-header");

        // Mobile-specific positioning and backdrop
        if (isMobile) {
        // Only the Tasks filters (inside #global-filters) get portaled to <body>.
        // Projects (.projects-filters-toolbar) and Calendar (#cal-filter-drawer)
        // keep their existing in-place sheet styling — out of scope for this fix.
        const isTaskFilter = !!g.closest("#global-filters");
        // Default to anchored under the trigger; fall back to bottom sheet when space is tight.
        try {
            const rect = g.getBoundingClientRect();
            const margin = 12;
            const gap = 8;
            const vh = window.innerHeight || document.documentElement.clientHeight || 0;
            const availableBelow = Math.max(0, vh - rect.bottom - margin);
            const availableAbove = Math.max(0, rect.top - margin);
            const preferredMax = Math.min(460, Math.floor(vh * 0.6));

            let placeBelow = availableBelow >= 260 || availableBelow >= availableAbove;
            let maxH = Math.min(preferredMax, placeBelow ? availableBelow : availableAbove);
            const useBottomSheet = maxH < 220; // too cramped to be useful when anchored

            panel.classList.toggle("sheet-bottom", useBottomSheet);
            panel.classList.toggle("sheet-anchored", !useBottomSheet);

            if (!useBottomSheet) {
                if (maxH < 240) maxH = Math.min(240, Math.max(0, placeBelow ? availableBelow : availableAbove));
                let top = placeBelow ? rect.bottom + gap : rect.top - gap - maxH;
                top = Math.max(margin, Math.min(top, vh - margin - 140));
                panel.style.setProperty("--sheet-top", `${Math.round(top)}px`);
                panel.style.setProperty("--sheet-maxh", `${Math.round(maxH)}px`);
            } else {
                panel.style.removeProperty("--sheet-top");
                panel.style.removeProperty("--sheet-maxh");
            }
        } catch (e) {
            panel.classList.add("sheet-bottom");
            panel.classList.remove("sheet-anchored");
        }

        // Portal the panel to <body> so it shares the ROOT stacking context with
        // #dropdown-backdrop. Nested inside .filters-toolbar, an iOS WebKit ancestor
        // stacking context trapped the panel's z-index BELOW the root backdrop
        // (the "menu behind the black layer" bug), and the toolbar's overflow scroll
        // container swallowed its touch events (the "can't scroll/tap options" bug).
        // As a root sibling of the backdrop it paints above it (1200 > 1199) and no
        // scroll container can intercept taps. Restored to its group on close.
        if (isTaskFilter && panel.parentNode !== document.body) {
            panel._sheetHome = { parent: panel.parentNode, next: panel.nextSibling };
            document.body.appendChild(panel);
            panel.classList.add("sheet-portal");
        }

        ensureBackdrop().classList.add("open");
        document.body.classList.add("dropdown-sheet-open");
        } // End mobile-specific

        if (!panel.querySelector(".dropdown-sheet-header")) {
            const titleText =
                panel.querySelector(".dropdown-section-title")?.textContent?.trim() ||
                g.querySelector(".filter-button")?.textContent?.trim() ||
                t('filters.sheet.title');

            const header = document.createElement("div");
            header.className = "dropdown-sheet-header";

            const handle = document.createElement("div");
            handle.className = "dropdown-sheet-handle";

            const title = document.createElement("div");
            title.className = "dropdown-sheet-title";
            title.textContent = titleText.toUpperCase();

            const done = document.createElement("button");
            done.type = "button";
            done.className = "dropdown-sheet-done";
            done.textContent = t('common.done');
            done.addEventListener("click", () => closeAllPanels());

            header.appendChild(handle);
            header.appendChild(title);
            header.appendChild(done);
            panel.insertBefore(header, panel.firstChild);
        }

        // Focus the in-panel search box on open (desktop only — on mobile this would
        // pop the on-screen keyboard over the options list). Deferred so layout settles.
        if (!isMobile) {
            const searchInput = panel.querySelector('.tag-filter-search input[type="text"]');
            if (searchInput) {
                requestAnimationFrame(() => { try { searchInput.focus(); } catch (e) {} });
            }
        }
    };

    groups.forEach((g) => {
        const btn = g.querySelector(".filter-button");
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const isOpen = g.classList.contains("open");
            closeAllPanels();
            if (!isOpen) {
                if ((g.id === "group-status" || g.id === "cal-group-status") && typeof applyBacklogFilterVisibility === "function") {
                    try { applyBacklogFilterVisibility(); } catch (err) {}
                }
                if (g.id === "cal-group-project-tags") {
                    populateCalendarProjectTagsDropdown();
                }
                g.classList.add("open");
                enhanceMobilePanel(g);
            }
        });

        // keep panel open when clicking inside (so multiple checks don't close it)
        const panel = g.querySelector(".dropdown-panel");
        if (panel) {
            panel.addEventListener("click", (e) => e.stopPropagation());
        }
    });

    // Clicking anywhere else closes panels
    document.addEventListener("click", () => {
        // Selecting text inside an open dropdown and releasing outside it is not a click outside.
        if (lastPointerDownTarget?.closest?.(".dropdown-panel")) return;
        closeAllPanels();
    });

    // Escape closes panels
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") closeAllPanels();
    });

    // Checkboxes for status, priority, project, and link-type (task filters only)
    document.querySelectorAll(
        '.dropdown-panel input[type="checkbox"][data-filter="status"],' +
        '.dropdown-panel input[type="checkbox"][data-filter="priority"],' +
        '.dropdown-panel input[type="checkbox"][data-filter="project"],' +
        '.dropdown-panel input[type="checkbox"][data-filter="link-type"]'
    ).forEach((cb) => {
        cb.addEventListener("change", () => {
            const type = cb.dataset.filter;
            if (type === "status") toggleSet(filterState.statuses, cb.value, cb.checked);
            if (type === "priority") toggleSet(filterState.priorities, cb.value, cb.checked);
            if (type === "project") toggleSet(filterState.projects, cb.value, cb.checked);
            if (type === "link-type") toggleSet(filterState.linkTypes, cb.value, cb.checked);
            // Tags are handled separately in populateTagOptions()
            updateFilterBadges();
            renderAfterFilterChange();

            // Same calendar fix as task deletion/creation
            const calendarView = document.getElementById("calendar-view");
            if (calendarView) {
                renderCalendar();
            }

            updateClearButtonVisibility();
        });
    });

    // Tag filter search (same pattern as project portal search)
    const tagFilterSearchInput = document.getElementById("tag-filter-search-input");
    if (tagFilterSearchInput) {
        tagFilterSearchInput.addEventListener("input", () => filterTagOptions(tagFilterSearchInput.value));
        attachFilterListKeyNav(tagFilterSearchInput, document.getElementById("tag-options"));
    }
    const calTagFilterSearchInput = document.getElementById("cal-tag-filter-search-input");
    if (calTagFilterSearchInput) {
        calTagFilterSearchInput.addEventListener("input", () => filterCalendarTaskTagOptions(calTagFilterSearchInput.value));
        attachFilterListKeyNav(calTagFilterSearchInput, document.getElementById("cal-tag-options"));
    }

    // Project filter search
    const projectFilterSearchInput = document.getElementById("project-filter-search-input");
    if (projectFilterSearchInput) {
        projectFilterSearchInput.addEventListener("input", () => filterProjectOptions(projectFilterSearchInput.value));
        attachFilterListKeyNav(projectFilterSearchInput, document.getElementById("project-options"));
    }
    const calProjectFilterSearchInput = document.getElementById("cal-project-filter-search-input");
    if (calProjectFilterSearchInput) {
        calProjectFilterSearchInput.addEventListener("input", () => filterCalendarTaskProjectOptions(calProjectFilterSearchInput.value));
        attachFilterListKeyNav(calProjectFilterSearchInput, document.getElementById("cal-project-options"));
    }

    const calProjectTopSearchInput = document.getElementById("cal-project-top-filter-search-input");
    if (calProjectTopSearchInput) {
        calProjectTopSearchInput.addEventListener("input", () => filterCalendarTaskProjectOptions(calProjectTopSearchInput.value));
        attachFilterListKeyNav(calProjectTopSearchInput, document.getElementById("cal-project-top-options"));
    }

    // Filter Include / Exclude toggles (Status, Priority, Tags, Project)
    // Only handle task-filter toggles here. Project-page and calendar toggles
    // are wired up in setupProjectsControls / setupCalendarControls respectively.
    const taskFilterTypes = new Set(['status', 'tags', 'priority', 'project']);
    document.querySelectorAll(".filter-mode-toggle").forEach((toggle) => {
        const filterType = toggle.dataset.filterType;
        if (!taskFilterTypes.has(filterType)) return; // skip non-task toggles
        const includeBtn = toggle.querySelector('.filter-mode-btn[data-mode="include"]');
        const excludeBtn = toggle.querySelector('.filter-mode-btn[data-mode="exclude"]');

        if (includeBtn) {
            includeBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                // Map filterType to filterState key (tags -> tag)
                const stateKey = filterType === "tags" ? "tag" : filterType;
                const excludeModeKey = `${stateKey}ExcludeMode`;
                if (filterState[excludeModeKey]) {
                    filterState[excludeModeKey] = false;
                    updateFilterModeUI(filterType);
                    updateFilterBadges();
                    renderAfterFilterChange();
                    const cal = document.getElementById("calendar-view");
                    if (cal) renderCalendar();
                    updateClearButtonVisibility();
                }
            });
        }
        if (excludeBtn) {
            excludeBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                // Map filterType to filterState key (tags -> tag)
                const stateKey = filterType === "tags" ? "tag" : filterType;
                const excludeModeKey = `${stateKey}ExcludeMode`;
                if (!filterState[excludeModeKey]) {
                    filterState[excludeModeKey] = true;
                    updateFilterModeUI(filterType);
                    updateFilterBadges();
                    renderAfterFilterChange();
                    const cal = document.getElementById("calendar-view");
                    if (cal) renderCalendar();
                    updateClearButtonVisibility();
                }
            });
        }
    });

    // Tag AND/OR match toggle (Any vs All)
    document.querySelectorAll(".tag-match-toggle").forEach((toggle) => {
        const orBtn = toggle.querySelector('.filter-mode-btn[data-match="or"]');
        const andBtn = toggle.querySelector('.filter-mode-btn[data-match="and"]');
        if (orBtn) {
            orBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                if (filterState.tagAndMode) {
                    filterState.tagAndMode = false;
                    updateTagMatchToggleUI();
                    updateFilterBadges();
                    renderAfterFilterChange();
                    const cal = document.getElementById("calendar-view");
                    if (cal) renderCalendar();
                    updateClearButtonVisibility();
                }
            });
        }
        if (andBtn) {
            andBtn.addEventListener("click", (e) => {
                e.stopPropagation();
                if (!filterState.tagAndMode) {
                    filterState.tagAndMode = true;
                    updateTagMatchToggleUI();
                    updateFilterBadges();
                    renderAfterFilterChange();
                    const cal = document.getElementById("calendar-view");
                    if (cal) renderCalendar();
                    updateClearButtonVisibility();
                }
            });
        }
    });

    // Checkboxes for date preset filter (multi-select)
    document.querySelectorAll('input[type="checkbox"][data-filter="date-preset"]').forEach((checkbox) => {
        checkbox.addEventListener("change", () => {
            const val = checkbox.value;

            if (checkbox.checked) {
                filterState.datePresets.add(val);

                // Clear manual date inputs when any preset is selected
                if (filterState.datePresets.size > 0) {
                    filterState.dateFrom = "";
                    filterState.dateTo = "";
                    const dateFromEl = document.getElementById("filter-date-from");
                    const dateToEl = document.getElementById("filter-date-to");
                    if (dateFromEl) dateFromEl.value = "";
                    if (dateToEl) dateToEl.value = "";
                }
            } else {
                filterState.datePresets.delete(val);
            }

            updateFilterBadges();
            renderAfterFilterChange();

            const calendarView = document.getElementById("calendar-view");
            if (calendarView) {
                renderCalendar();
            }

            updateClearButtonVisibility();
        });
    });

    // "Select / Unselect All" for Projects
    const bindProjectSelectAll = (id) => {
        const checkbox = document.getElementById(id);
        if (!checkbox) return;
        checkbox.addEventListener("change", () => {
            const projectCheckboxes = document.querySelectorAll('.dropdown-panel input[type="checkbox"][data-filter="project"]');
            const allChecked = checkbox.checked;
            projectCheckboxes.forEach((cb) => {
                cb.checked = allChecked;
                if (allChecked) filterState.projects.add(cb.value);
                else filterState.projects.delete(cb.value);
            });
            updateFilterBadges();
            renderAfterFilterChange();
            const calendarView = document.getElementById("calendar-view");
            if (calendarView) renderCalendar();
            updateClearButtonVisibility();
        });
    };
    bindProjectSelectAll("project-select-all");
    bindProjectSelectAll("cal-project-select-all");
    bindProjectSelectAll("cal-project-top-select-all");

    // Search field — live in kanban/list, Enter/blur only in calendar
    document.querySelectorAll("#filter-search, #cal-filter-search").forEach((searchEl) => {
        const applyTaskSearch = () => {
            filterState.search = (searchEl.value || "").trim().toLowerCase();
            document.querySelectorAll("#filter-search, #cal-filter-search").forEach((el) => {
                if (el !== searchEl) el.value = searchEl.value;
            });
            updateFilterBadges();
            renderAfterFilterChange();
            updateClearButtonVisibility();
        };
        searchEl.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                searchEl.blur();
            }
        });
        searchEl.addEventListener("blur", applyTaskSearch);
        searchEl.addEventListener("input", () => {
            if (searchEl.id === "cal-filter-search") {
                clearTimeout(searchEl._calSearchTimer);
                searchEl._calSearchTimer = setTimeout(applyTaskSearch, 250);
                return;
            }
            if (document.getElementById("calendar-view")?.classList.contains("active")) return;
            applyTaskSearch();
        });
    });

    // Radio for Kanban-only updated-recency filter
    document.querySelectorAll('input[type="radio"][data-filter="kanban-updated"]').forEach((rb) => {
        rb.addEventListener("change", () => {
            if (!rb.checked) return;
            setKanbanUpdatedFilter(rb.value);
            updateClearButtonVisibility();
        });
    });

    // Date range inputs
    const dateFromEl = document.getElementById("filter-date-from");
    const dateToEl = document.getElementById("filter-date-to");

if (dateFromEl) {
        dateFromEl.addEventListener("change", () => {
            filterState.dateFrom = dateFromEl.value;
updateFilterBadges();
            renderAfterFilterChange();

            const calendarView = document.getElementById("calendar-view");
            if (calendarView) {
                renderCalendar();
            }

            updateClearButtonVisibility();
        });
    } else {
        console.error("[Filter] Date From element not found!");
    }

    if (dateToEl) {
        dateToEl.addEventListener("change", () => {
            filterState.dateTo = dateToEl.value;
updateFilterBadges();
            renderAfterFilterChange();

            const calendarView = document.getElementById("calendar-view");
            if (calendarView) {
                renderCalendar();
            }

            updateClearButtonVisibility();
        });
    } else {
        console.error("[Filter] Date To element not found!");
    }

    // Clear all filters
    const clearTaskFilters = () => {
            filterState.search = "";
            filterState.statuses.clear();
            filterState.statusExcludeMode = false;
            filterState.priorities.clear();
            filterState.priorityExcludeMode = false;
            filterState.projects.clear();
            filterState.projectExcludeMode = false;
            filterState.tags.clear();
            filterState.tagExcludeMode = false;
            filterState.tagAndMode = false;
            filterState.linkTypes.clear();
            filterState.datePresets.clear();
            filterState.dateFrom = "";
            filterState.dateTo = "";
            try { setKanbanUpdatedFilter('all', { render: false }); } catch (e) {}

            // Reset UI elements
            document
                .querySelectorAll('#global-filters > .filters-toolbar .dropdown-panel input[type="checkbox"], #cal-filter-drawer .cal-task-filters .dropdown-panel input[type="checkbox"], #cal-group-project-top input[type="checkbox"]')
                .forEach((cb) => (cb.checked = false));

            document.querySelectorAll("#filter-search, #cal-filter-search").forEach((el) => {
                el.value = "";
            });
            document.querySelectorAll('#global-filters .filter-mode-toggle[data-filter-type="status"], #global-filters .filter-mode-toggle[data-filter-type="priority"], #global-filters .filter-mode-toggle[data-filter-type="tags"], #global-filters .filter-mode-toggle[data-filter-type="project"], #cal-filter-drawer .filter-mode-toggle[data-filter-type="status"], #cal-filter-drawer .filter-mode-toggle[data-filter-type="priority"], #cal-filter-drawer .filter-mode-toggle[data-filter-type="tags"], #cal-filter-drawer .filter-mode-toggle[data-filter-type="project"]').forEach((toggle) => {
                toggle.querySelectorAll('.filter-mode-btn').forEach((btn) => {
                    btn.classList.toggle('active', btn.dataset.mode === 'include');
                });
            });
            updateTagMatchToggleUI();
            document.querySelectorAll('input[type="radio"][data-filter="kanban-updated"]').forEach((rb) => {
                rb.checked = rb.value === 'all';
            });

            // Clear date filter inputs and their Flatpickr instances
            if (dateFromEl) {
                dateFromEl.value = "";
                // Clear the Flatpickr display input
                const dateFromWrapper = dateFromEl.closest('.date-input-wrapper');
                if (dateFromWrapper) {
                    const displayInput = dateFromWrapper.querySelector('.date-display');
                    if (displayInput && displayInput._flatpickr) {
                        displayInput._flatpickr.clear();
                    }
                }
            }
            if (dateToEl) {
                dateToEl.value = "";
                // Clear the Flatpickr display input
                const dateToWrapper = dateToEl.closest('.date-input-wrapper');
                if (dateToWrapper) {
                    const displayInput = dateToWrapper.querySelector('.date-display');
                    if (displayInput && displayInput._flatpickr) {
                        displayInput._flatpickr.clear();
                    }
                }
            }

            updateFilterBadges();
            renderAfterFilterChange();
            
            // Same calendar fix as task deletion/creation
            const calendarView = document.getElementById("calendar-view");
            if (calendarView) {
                renderCalendar();
            }
            
            updateClearButtonVisibility();
    };
    document.querySelectorAll("#btn-clear-filters, #cal-btn-clear-filters").forEach((clearBtn) => {
        clearBtn.addEventListener("click", clearTaskFilters);
    });

    // First run
    updateFilterBadges();
    renderActiveFilterChips();
    updateKanbanUpdatedFilterUI();
    updateClearButtonVisibility();
    }

function syncFilterCheckboxesFromState(filterType) {
    // Map filterType to data-filter attribute (tags -> tag)
    const dataFilterAttr = filterType === "tags" ? "tag" : filterType;
    document.querySelectorAll(`input[type="checkbox"][data-filter="${dataFilterAttr}"]`).forEach((cb) => {
        const filterSet = filterState[filterType === "status" ? "statuses" : 
                          filterType === "priority" ? "priorities" :
                          filterType === "project" ? "projects" : "tags"];
        cb.checked = filterSet.has(cb.value);
    });
}

function updateFilterModeUI(filterType) {
    const toggles = document.querySelectorAll(`.filter-mode-toggle[data-filter-type="${filterType}"]`);
    if (!toggles.length) return;
    const stateKey = filterType === "tags" ? "tag" : filterType;
    const excludeModeKey = `${stateKey}ExcludeMode`;
    const excludeMode = filterState[excludeModeKey] || false;
    toggles.forEach((toggle) => {
        const includeBtn = toggle.querySelector('.filter-mode-btn[data-mode="include"]');
        const excludeBtn = toggle.querySelector('.filter-mode-btn[data-mode="exclude"]');
        if (includeBtn) includeBtn.classList.toggle("active", !excludeMode);
        if (excludeBtn) excludeBtn.classList.toggle("active", !!excludeMode);
    });
    syncFilterCheckboxesFromState(filterType);
}

function updateAllFilterModeUI() {
    ["status", "priority", "tags", "project"].forEach(type => updateFilterModeUI(type));
}

function updateTagMatchToggleUI() {
    document.querySelectorAll(".tag-match-toggle").forEach((toggle) => {
        const orBtn = toggle.querySelector('.filter-mode-btn[data-match="or"]');
        const andBtn = toggle.querySelector('.filter-mode-btn[data-match="and"]');
        if (orBtn) orBtn.classList.toggle("active", !filterState.tagAndMode);
        if (andBtn) andBtn.classList.toggle("active", !!filterState.tagAndMode);
    });
}

// Update project filter mode UI (include/exclude toggle)
function updateProjectFilterModeUI(filterType) {
    const toggle = document.querySelector(`.filter-mode-toggle[data-filter-type="${filterType}"]`);
    if (!toggle) return;
    
    let excludeMode = false;
    if (filterType === 'project-status') {
        excludeMode = projectFilterState.statusExcludeMode || false;
    } else if (filterType === 'project-tags') {
        excludeMode = projectFilterState.tagExcludeMode || false;
    }
    
    const includeBtn = toggle.querySelector('.filter-mode-btn[data-mode="include"]');
    const excludeBtn = toggle.querySelector('.filter-mode-btn[data-mode="exclude"]');
    if (includeBtn) includeBtn.classList.toggle("active", !excludeMode);
    if (excludeBtn) excludeBtn.classList.toggle("active", !!excludeMode);
}

function updateClearButtonVisibility() {
    const kanban = document.querySelector('.kanban-board');
    const isKanban = kanban && !kanban.classList.contains('hidden');
    const isList = document.getElementById('list-view')?.classList.contains('active');
    const isCalendar = document.getElementById('calendar-view')?.classList.contains('active');
    const showUpdated = isKanban || isList || isCalendar;
    const hasKanbanUpdated =
        showUpdated &&
        window.kanbanUpdatedFilter &&
        window.kanbanUpdatedFilter !== 'all';

    const hasFilters =
        (filterState.search && filterState.search.trim() !== "") ||
        filterState.statuses.size > 0 ||
        filterState.priorities.size > 0 ||
        filterState.projects.size > 0 ||
        filterState.tags.size > 0 ||
        filterState.linkTypes.size > 0 ||
        filterState.datePresets.size > 0 ||
        (filterState.dateFrom && filterState.dateFrom !== "") ||
        (filterState.dateTo && filterState.dateTo !== "") ||
        filterState.duplicateTitles ||
        hasKanbanUpdated;

    document.querySelectorAll("#btn-clear-filters, #cal-btn-clear-filters").forEach((btn) => {
        btn.style.display = hasFilters ? "inline-flex" : "none";
    });
}

// Update numeric badges for each dropdown
function updateFilterBadges() {
    syncExactDateFilterInputs();
    const b1 = document.getElementById("badge-status");
    const b1Cal = document.getElementById("cal-badge-status");
    const b2 = document.getElementById("badge-priority");
    const b2Cal = document.getElementById("cal-badge-priority");
    const b3 = document.getElementById("badge-project");
    const b3Cal = document.getElementById("cal-badge-project");
    const b3CalTop = document.getElementById("cal-badge-project-top");
    const b4 = document.getElementById("badge-tags");
    const b4Cal = document.getElementById("cal-badge-tags");
    const bEndDate = document.getElementById("badge-end-date");
    const bEndDateCal = document.getElementById("cal-badge-end-date");
    const bStartDate = document.getElementById("badge-start-date");
    const bStartDateCal = document.getElementById("cal-badge-start-date");
    const bLinks = document.getElementById("badge-links");
    const bLinksCal = document.getElementById("cal-badge-links");

    // Show count when active, empty when inactive (no more "All")
    if (b1) b1.textContent = filterState.statuses.size === 0 ? "" : filterState.statuses.size;
    if (b1Cal) b1Cal.textContent = filterState.statuses.size === 0 ? "" : filterState.statuses.size;
    if (b2) b2.textContent = filterState.priorities.size === 0 ? "" : filterState.priorities.size;
    if (b2Cal) b2Cal.textContent = filterState.priorities.size === 0 ? "" : filterState.priorities.size;
    if (b3) b3.textContent = filterState.projects.size === 0 ? "" : filterState.projects.size;
    if (b3Cal) b3Cal.textContent = filterState.projects.size === 0 ? "" : filterState.projects.size;
    if (b3CalTop) b3CalTop.textContent = filterState.projects.size === 0 ? "" : filterState.projects.size;
    if (b4) b4.textContent = filterState.tags.size === 0 ? "" : filterState.tags.size;
    if (b4Cal) b4Cal.textContent = filterState.tags.size === 0 ? "" : filterState.tags.size;
    if (bLinks) bLinks.textContent = filterState.linkTypes.size === 0 ? "" : filterState.linkTypes.size;
    if (bLinksCal) bLinksCal.textContent = filterState.linkTypes.size === 0 ? "" : filterState.linkTypes.size;

    // Count end date and start date filters separately
    const endDatePresets = ["no-date", "overdue", "end-today", "end-tomorrow", "end-7days", "end-week", "end-month"];
    const startDatePresets = ["no-start-date", "already-started", "start-today", "start-tomorrow", "start-7days", "start-week", "start-month"];

    let endDateCount = 0;
    let startDateCount = 0;

    filterState.datePresets.forEach(preset => {
        if (endDatePresets.includes(preset)) {
            endDateCount++;
        } else if (startDatePresets.includes(preset)) {
            startDateCount++;
        }
    });

    if (bEndDate) bEndDate.textContent = endDateCount === 0 ? "" : endDateCount;
    if (bStartDate) bStartDate.textContent = startDateCount === 0 ? "" : startDateCount;
    if (bEndDateCal) bEndDateCal.textContent = endDateCount === 0 ? "" : endDateCount;
    if (bStartDateCal) bStartDateCal.textContent = startDateCount === 0 ? "" : startDateCount;

    // Update active state on filter buttons
    const updateButtonState = (badgeIds, isActive) => {
        badgeIds.forEach((badgeId) => {
            const badge = document.getElementById(badgeId);
            if (!badge) return;
            const button = badge.closest('.filter-button');
            if (button) {
                button.classList.toggle('active', isActive);
            }
        });
    };

    updateButtonState(["badge-status", "cal-badge-status"], filterState.statuses.size > 0);
    updateButtonState(["badge-priority", "cal-badge-priority"], filterState.priorities.size > 0);
    updateButtonState(["badge-project", "cal-badge-project", "cal-badge-project-top"], filterState.projects.size > 0);
    updateButtonState(["badge-tags", "cal-badge-tags"], filterState.tags.size > 0);
    updateButtonState(["badge-end-date", "cal-badge-end-date"], endDateCount > 0);
    updateButtonState(["badge-start-date", "cal-badge-start-date"], startDateCount > 0);
    updateButtonState(["badge-links", "cal-badge-links"], filterState.linkTypes.size > 0);

    updateAllFilterModeUI();
    renderActiveFilterChips();
    updateClearButtonVisibility();
    updateCalControlBar();
    logDebug("filters", "badges", {
        statusCount: filterState.statuses.size,
        priorityCount: filterState.priorities.size,
        projectCount: filterState.projects.size,
        tagCount: filterState.tags.size,
        endDateCount,
        startDateCount,
        linkTypesCount: filterState.linkTypes.size
    });
}

// Show active filter "chips" under the toolbar
function renderActiveFilterChips() {
    const chipsTimer = debugTimeStart("filters", "chips");
    const wraps = [
        document.getElementById("active-filters"),
        document.getElementById("cal-task-active-filters"),
    ].filter(Boolean);
    if (!wraps.length) return;
    wraps.forEach((wrap) => { wrap.innerHTML = ""; });

    const addChip = (label, value, onRemove, type, rawValue) => {
        wraps.forEach((wrap) => {
        const chip = document.createElement("span");
        chip.className = "filter-chip";

        // Add type-specific class for styling
        if (type === "status") {
            chip.classList.add("chip-status");
        } else if (type === "priority") {
            chip.classList.add("chip-priority");
        }

        const text = document.createElement("span");
        text.className = "chip-text";

        // Add colored dot for status chips
        if (type === "status" && rawValue) {
            const dot = document.createElement("span");
            dot.className = `dot ${rawValue}`;
            text.appendChild(dot);
            text.appendChild(document.createTextNode(` ${label}: ${value}`));
        } else {
            text.textContent = `${label}: ${value}`;
        }

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "chip-remove";
        btn.setAttribute("aria-label", t('filters.chip.removeAria', { label }));
        btn.textContent = "×";
        btn.addEventListener("click", onRemove);

        chip.appendChild(text);
        chip.appendChild(btn);
        wrap.appendChild(chip);
        });
    };

    // Search chip
    if (filterState.search)
        addChip(t('filters.chip.search'), filterState.search, () => {
            filterState.search = "";
            document.querySelectorAll("#filter-search, #cal-filter-search").forEach((el) => {
                el.value = "";
            });
            updateFilterBadges(); // Ensure badges are updated
            updateClearButtonVisibility(); // Update clear button state
            renderAfterFilterChange();
        });

    // Status chips (label = "Status" or "Excluding" when exclude mode)
    const statusChipLabel = filterState.statusExcludeMode ? t('tasks.filters.excluding') : t('tasks.filters.status');
    filterState.statuses.forEach((v) =>
        addChip(statusChipLabel, getStatusLabel(v), () => {
            filterState.statuses.delete(v);
            const cb = document.querySelector(
                `input[type="checkbox"][data-filter="status"][value="${v}"]`
            );
            if (cb) cb.checked = false;
            updateFilterBadges();
            renderAfterFilterChange();
        }, "status", v)
    );

    // Priority chips (label = "Priority" or "Excluding" when exclude mode)
    const priorityChipLabel = filterState.priorityExcludeMode ? t('tasks.filters.excluding') : t('tasks.filters.priority');
    filterState.priorities.forEach((v) =>
        addChip(priorityChipLabel, getPriorityLabel(v), () => {
            filterState.priorities.delete(v);
            const cb = document.querySelector(
                `input[type="checkbox"][data-filter="priority"][value="${v}"]`
            );
            if (cb) cb.checked = false;
            updateFilterBadges();
            renderAfterFilterChange();
        }, "priority", v)
    );

    // Project chips (label = "Project" or "Excluding" when exclude mode)
    const projectChipLabel = filterState.projectExcludeMode ? t('tasks.filters.excluding') : t('filters.chip.project');
    filterState.projects.forEach((pid) => {
        const proj = projects.find((p) => p.id.toString() === pid.toString());
        addChip(projectChipLabel, proj ? proj.name : pid, () => {
            filterState.projects.delete(pid);
            const cb = document.querySelector(
                `input[type="checkbox"][data-filter="project"][value="${pid}"]`
            );
            if (cb) cb.checked = false;
            updateFilterBadges();
            renderAfterFilterChange();
        });
    });

    // Tags chips (label = "Tag" or "Excluding" when exclude mode)
    const tagChipLabel = filterState.tagExcludeMode ? t('tasks.filters.excluding') : t('filters.chip.tag');
    filterState.tags.forEach((tag) =>
        addChip(tagChipLabel, tag.toUpperCase(), () => {
            filterState.tags.delete(tag);
            const cb = document.querySelector(
                `input[type="checkbox"][data-filter="tag"][value="${tag}"]`
            );
            if (cb) cb.checked = false;
            updateFilterBadges();
            renderAfterFilterChange();
        })
    );

    // Date preset chips (one chip per selected preset)
    filterState.datePresets.forEach((preset) => {
        const datePresetLabels = {
            "no-date": t('tasks.filters.noDate'),
            "overdue": t('tasks.filters.overdue'),
            "end-today": t('tasks.filters.endToday'),
            "end-tomorrow": t('tasks.filters.endTomorrow'),
            "end-7days": t('tasks.filters.end7Days'),
            "end-week": t('tasks.filters.endThisWeek'),
            "end-month": t('tasks.filters.endThisMonth'),
            "no-start-date": t('tasks.filters.noStartDate'),
            "already-started": t('tasks.filters.alreadyStarted'),
            "start-today": t('tasks.filters.startToday'),
            "start-tomorrow": t('tasks.filters.startTomorrow'),
            "start-7days": t('tasks.filters.start7Days'),
            "start-week": t('tasks.filters.startThisWeek'),
            "start-month": t('tasks.filters.startThisMonth')
        };
        const label = withResolvedPresetDate(datePresetLabels[preset] || preset, preset);
        addChip(t('filters.chip.date'), label, () => {
            filterState.datePresets.delete(preset);
            // Uncheck the corresponding checkbox
            const checkbox = document.querySelector(`input[type="checkbox"][data-filter="date-preset"][value="${preset}"]`);
            if (checkbox) checkbox.checked = false;
            updateFilterBadges();
            renderAfterFilterChange();
        });
    });

    // Kanban/List Updated filter chip (not shown on Calendar)
    try {
        const kanban = document.querySelector('.kanban-board');
        const isKanban = kanban && !kanban.classList.contains('hidden');
        const isList = document.getElementById('list-view')?.classList.contains('active');
        const isCalendar = document.getElementById('calendar-view')?.classList.contains('active');
        const showUpdated = isKanban || isList || isCalendar;
        if (showUpdated && window.kanbanUpdatedFilter && window.kanbanUpdatedFilter !== 'all') {
            addChip(t('filters.chip.updated'), getKanbanUpdatedFilterLabel(window.kanbanUpdatedFilter), () => {
                setKanbanUpdatedFilter('all');
                updateClearButtonVisibility();
            });
        }
    } catch (e) {}

    // Date range chips
    if (filterState.dateFrom || filterState.dateTo) {
        let dateLabel = "";
        if (filterState.dateFrom && filterState.dateFrom === filterState.dateTo) {
            dateLabel = formatDate(filterState.dateFrom);
        } else if (filterState.dateFrom && filterState.dateTo) {
            dateLabel = `${formatDate(filterState.dateFrom)} - ${formatDate(filterState.dateTo)}`;
        } else if (filterState.dateFrom) {
            dateLabel = `${t('filters.dateRange.from')} ${formatDate(filterState.dateFrom)}`;
        } else if (filterState.dateTo) {
            dateLabel = `${t('filters.dateRange.until')} ${formatDate(filterState.dateTo)}`;
        }
        const dateChipLabel = t(filterState.dateField === 'startDate'
            ? 'filters.chip.startDate' : 'filters.chip.endDate');
        addChip(dateChipLabel, dateLabel, () => {
            filterState.dateFrom = "";
            filterState.dateTo = "";
            const fromEl = document.getElementById("filter-date-from");
            const toEl = document.getElementById("filter-date-to");

            // Clear date filter inputs and their Flatpickr instances
            if (fromEl) {
                fromEl.value = "";
                const dateFromWrapper = fromEl.closest('.date-input-wrapper');
                if (dateFromWrapper) {
                    const displayInput = dateFromWrapper.querySelector('.date-display');
                    if (displayInput && displayInput._flatpickr) {
                        displayInput._flatpickr.clear();
                    }
                }
            }
            if (toEl) {
                toEl.value = "";
                const dateToWrapper = toEl.closest('.date-input-wrapper');
                if (dateToWrapper) {
                    const displayInput = dateToWrapper.querySelector('.date-display');
                    if (displayInput && displayInput._flatpickr) {
                        displayInput._flatpickr.clear();
                    }
                }
            }

            renderAfterFilterChange();
        });
    }

    // Activity-window chips (created / updated / completed). Set only by Kai; there
    // is no toolbar control, so the chip is the only way to see and remove them.
    const activityChips = [
        ['createdFrom', 'createdTo', 'filters.chip.created'],
        ['updatedFrom', 'updatedTo', 'filters.chip.updated'],
        ['completedFrom', 'completedTo', 'filters.chip.completed']
    ];
    for (const [fromKey, toKey, labelKey] of activityChips) {
        if (!filterState[fromKey] && !filterState[toKey]) continue;
        let label = '';
        if (filterState[fromKey] && filterState[toKey]) {
            label = `${formatDate(filterState[fromKey])} - ${formatDate(filterState[toKey])}`;
        } else if (filterState[fromKey]) {
            label = `${t('filters.dateRange.from')} ${formatDate(filterState[fromKey])}`;
        } else {
            label = `${t('filters.dateRange.until')} ${formatDate(filterState[toKey])}`;
        }
        addChip(t(labelKey), label, () => {
            filterState[fromKey] = '';
            filterState[toKey] = '';
            renderAfterFilterChange();
        });
    }

    // Task IDs chip (from email notifications or Ask Kai "Ver") - special read-only filter
    if (filterState.taskIds && filterState.taskIds.size > 0) {
        const taskCount = filterState.taskIds.size;
        const taskChipLabel = filterState.taskIdsSource === 'generated'
            ? t('filters.chip.generatedTasks')
            : filterState.taskIdsSource === 'report'
                ? t('filters.chip.reportTasks')
                : filterState.taskIdsSource === 'overdue'
                    ? t('filters.chip.overdueTasks')
                    : t('filters.chip.emailTasks');
        addChip(taskChipLabel, `${taskCount} ${taskCount === 1 ? 'task' : 'tasks'}`, () => {
            filterState.taskIds = new Set();
            filterState.taskIdsSource = '';
            // Navigate to tasks without taskIds to clear the filter
            safeReplaceState(null, '', '#tasks');
            renderAfterFilterChange();
        });
    }

    // Link types chips
    const linkTypeLabels = {
        'blocks': t('tasks.filters.blocksOthers'),
        'is_blocked_by': t('tasks.filters.isBlockedByOthers'),
        'relates_to': t('tasks.filters.relatesTo'),
        'has_web_links': t('tasks.filters.hasWebLinks'),
        'no_links': t('tasks.filters.noLinks')
    };
    filterState.linkTypes.forEach((linkType) => {
        addChip(t('tasks.filters.links'), linkTypeLabels[linkType] || linkType, () => {
            filterState.linkTypes.delete(linkType);
            const cb = document.querySelector(
                `input[type="checkbox"][data-filter="link-type"][value="${linkType}"]`
            );
            if (cb) cb.checked = false;
            updateFilterBadges();
            renderAfterFilterChange();
        });
    });

    const chipCount = wraps.reduce((sum, wrap) => sum + wrap.children.length, 0);
    debugTimeEnd("filters", chipsTimer, { chipCount });
}

// Sync current filter state to URL for shareable links and browser history
function syncURLWithFilters() {
    const params = new URLSearchParams();
    const isArchivedRoute = window.location.hash.slice(1).split('?')[0] === 'tasks/archive';

    // Add view parameter (kanban, list, or calendar)
    const isListView = document.getElementById("list-view")?.classList.contains("active");
    const isCalendarView = document.getElementById("calendar-view")?.classList.contains("active");
    if (isListView) {
        params.set("view", "list");
    } else if (isCalendarView) {
        params.set("view", "calendar");
        // Persist the viewed month so a refresh restores it.
        params.set("month", getCalendarMonthParam());
    }
    // Don't add 'kanban' as it's the default

    // Add search query
    if (filterState.search && filterState.search.trim() !== "") {
        params.set("search", filterState.search.trim());
    }

    // Add status filters (comma-separated)
    if (filterState.statuses.size > 0) {
        params.set("status", Array.from(filterState.statuses).join(","));
        if (filterState.statusExcludeMode) params.set("statusExclude", "1");
    }

    // Add priority filters (comma-separated)
    if (filterState.priorities.size > 0) {
        params.set("priority", Array.from(filterState.priorities).join(","));
        if (filterState.priorityExcludeMode) params.set("priorityExclude", "1");
    }

    // Add project filters (comma-separated)
    if (filterState.projects.size > 0) {
        params.set("project", Array.from(filterState.projects).join(","));
        if (filterState.projectExcludeMode) params.set("projectExclude", "1");
    }

    // Add tag filters (comma-separated)
    if (filterState.tags.size > 0) {
        params.set("tags", Array.from(filterState.tags).join(","));
        if (filterState.tagExcludeMode) params.set("tagExclude", "1");
        if (filterState.tagAndMode) params.set("tagAnd", "1");
    }

    // Add date preset filters (comma-separated)
    if (filterState.datePresets.size > 0) {
        params.set("datePreset", Array.from(filterState.datePresets).join(","));
    }

    // Add date range filters
    if (filterState.dateFrom && filterState.dateFrom !== "") {
        params.set("dateFrom", filterState.dateFrom);
        if (filterState.dateField && filterState.dateField !== 'endDate') {
            params.set("dateField", filterState.dateField);
        }
    }
    if (filterState.dateTo && filterState.dateTo !== "") {
        params.set("dateTo", filterState.dateTo);
    }

    // Add updated filter (kanban/list/calendar)
    if (window.kanbanUpdatedFilter && window.kanbanUpdatedFilter !== 'all') {
        params.set("updated", window.kanbanUpdatedFilter);
    }

    // Add link type filters (comma-separated)
    if (filterState.linkTypes.size > 0) {
        params.set("links", Array.from(filterState.linkTypes).join(","));
    }

    // Activity windows (created/updated/completed) so a Kai result is shareable and
    // survives a refresh.
    for (const key of ['createdFrom', 'createdTo', 'updatedFrom', 'updatedTo', 'completedFrom', 'completedTo']) {
        if (filterState[key]) params.set(key, filterState[key]);
    }

    // Build new URL
    const queryString = params.toString();
    const route = isArchivedRoute ? '#tasks/archive' : '#tasks';
    const newHash = queryString ? `${route}?${queryString}` : route;

    // Update URL without triggering hashchange event (prevents infinite loop)
    if (window.location.hash !== newHash) {
        safeReplaceState(null, "", newHash);
    }
}

// Called whenever filters change
function renderAfterFilterChange() {
    syncURLWithFilters(); // Keep URL in sync with filters
    renderActiveFilterChips(); // Update filter chips display
    renderKaiFilterPill(); // Kai's interpretation, alongside (never inside) the chips
    if (document.getElementById("archived-view")?.classList.contains("active")) {
        renderArchivedView();
        return;
    }
    renderTasks(); // Kanban

    // Always render list view on mobile (for mobile cards) or when active on desktop
    const isMobile = getIsMobileCached();
    if (isMobile || document.getElementById("list-view").classList.contains("active")) {
        renderListView(); // List (includes mobile cards)
    }

    const calendarView = document.getElementById("calendar-view");
    if (calendarView && calendarView.classList.contains("active")) {
        renderCalendar(); // Calendar - first render
        
        // Double-render when on calendar (layout settle - same as changeMonth/goToToday)
        // This is CRITICAL - it allows layout to settle between renders for proper bar positioning
        renderCalendar(); // Calendar - second render for proper bar positioning
    }
}

// Return filtered tasks array
// Uses filterTasks from src/utils/filterPredicates.js for pure filtering logic
function getFilteredTasks() {
    const filterTimer = debugTimeStart("filters", "tasks", {
        totalTasks: tasks.length,
        hasSearch: !!filterState.search,
        statusCount: filterState.statuses.size,
        priorityCount: filterState.priorities.size,
        projectCount: filterState.projects.size,
        tagCount: filterState.tags.size,
        datePresetCount: filterState.datePresets.size,
        hasDateRange: !!(filterState.dateFrom || filterState.dateTo)
    });

    // Use the pure filterTasks function from filterPredicates module
    const filtered = filterTasks(tasks, {
        search: filterState.search,
        statuses: filterState.statuses,
        statusExcludeMode: filterState.statusExcludeMode,
        priorities: filterState.priorities,
        priorityExcludeMode: filterState.priorityExcludeMode,
        projects: filterState.projects,
        projectExcludeMode: filterState.projectExcludeMode,
        tags: filterState.tags,
        tagExcludeMode: filterState.tagExcludeMode,
        tagAndMode: filterState.tagAndMode,
        datePresets: filterState.datePresets,
        dateFrom: filterState.dateFrom,
        dateTo: filterState.dateTo,
        dateField: filterState.dateField || 'endDate',
        createdFrom: filterState.createdFrom,
        createdTo: filterState.createdTo,
        updatedFrom: filterState.updatedFrom,
        updatedTo: filterState.updatedTo,
        completedFrom: filterState.completedFrom,
        completedTo: filterState.completedTo,
        taskIds: filterState.taskIds, // Email notification filter
        linkTypes: filterState.linkTypes, // Link type filter
        duplicateTitles: filterState.duplicateTitles, // "same title" filter (Kai)
        dependencies: dependencies, // For link type filtering
    }, { timeZone: getUserTimeZone() });

    // Kai "top N most recently modified/created": rank the matches by recency and
    // cap them, so the live list is the same set the review previewed. Only active
    // while a Kai limit/sort query is applied; cleared by clearAllFilters().
    let result = filtered;
    if (filterState.kaiSort === 'recent-created') {
        const createdTime = (t) => Date.parse(t?.createdAt || t?.createdDate || '') || 0;
        result = [...result].sort((a, b) => createdTime(b) - createdTime(a));
    } else if (filterState.kaiSort === 'recent-updated') {
        result = [...result].sort((a, b) => getTaskUpdatedTime(b) - getTaskUpdatedTime(a));
    }
    if (filterState.kaiLimit > 0) {
        result = result.slice(0, filterState.kaiLimit);
    }

    debugTimeEnd("filters", filterTimer, {
        totalTasks: tasks.length,
        filteredCount: result.length
    });
    return result;
}

function initializeDatePickers() {
  const flatpickrFn = flatpickr;
  if (typeof flatpickrFn !== "function") {
    const retry = (initializeDatePickers.__retryCount || 0) + 1;
    initializeDatePickers.__retryCount = retry;
    if (retry <= 50) {
      setTimeout(initializeDatePickers, 100);
    } else {
      console.warn("flatpickr is not available; date pickers disabled");
    }
    return;
  }

  const flatpickrLocale = getFlatpickrLocale();
  // Format, typing and calendar options come from src/utils/datePicker.js (app-wide).
  const dateConfig = datePickerOptions(flatpickrLocale, { altInput: false });

  // Shared typing mask; a complete date syncs into flatpickr without triggering persistence.
  function addDateMask(input, flatpickrInstance) {
    const mask = attachDateMask(input, {
      onComplete(iso) {
        if (!flatpickrInstance || !flatpickrInstance.config) return;
        try {
          flatpickrInstance.__suppressChange = true;
          flatpickrInstance.setDate(new Date(`${iso}T00:00:00`), false);
          setTimeout(() => (flatpickrInstance.__suppressChange = false), 0);
        } catch (e) {}
      }
    });

    if (flatpickrInstance) {
      flatpickrInstance.config.onChange.push(() => mask.reset());
    }
  }

  // Patch flatpickr instance to mark programmatic changes
  function patchProgrammaticGuards(fp) {
    if (fp.__patchedProgrammaticGuards) return;
    fp.__patchedProgrammaticGuards = true;

    const origClear = fp.clear.bind(fp);
    fp.clear = function (...args) {
      fp.__suppressChange = true;
      const res = origClear(...args);
      setTimeout(() => (fp.__suppressChange = false), 0);
      return res;
    };

    const origSetDate = fp.setDate.bind(fp);
    fp.__origSetDate = origSetDate;
    fp.setDate = function (date, triggerChange, ...rest) {
      // Any programmatic set should not persist. Even if triggerChange is true, we guard it.
      fp.__suppressChange = true;
      const res = origSetDate(date, triggerChange, ...rest);
      setTimeout(() => (fp.__suppressChange = false), 0);
      return res;
    };
  }

  function addTodayButton(fp) {
    if (!fp || !fp.calendarContainer) return;

    if (fp.calendarContainer.querySelector('.flatpickr-today-btn')) return;

    const footer = document.createElement('div');
    footer.className = 'flatpickr-today-footer';

    const todayBtn = document.createElement('button');
    todayBtn.type = 'button';
    todayBtn.className = 'flatpickr-today-btn';
    todayBtn.textContent = t('calendar.today') || 'Today';

    todayBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const today = new Date();
      fp.jumpToDate(today);

      // Use the original setDate so onChange behaves like a user selection.
      fp.__suppressChange = false;
      if (typeof fp.__origSetDate === 'function') {
        fp.__origSetDate(today, true);
      } else {
        fp.setDate(today, true);
      }
    });

    footer.appendChild(todayBtn);
    fp.calendarContainer.appendChild(footer);
  }

  // Attach pickers
  document.querySelectorAll('input[type="date"], input.datepicker').forEach((input) => {
    if (input._flatpickrInstance) return;

    if (input.type === "date") {
      // One-time wrap into display input + hidden ISO input
      if (input._wrapped) return;
      input._wrapped = true;

      const wrapper = document.createElement("div");
      wrapper.className = "date-input-wrapper";

      const displayInput = document.createElement("input");
      displayInput.type = "text";
      displayInput.className = "form-input date-display";
      if (input.dataset.dateFilterExact) {
        const labelKey = input.dataset.dateFilterExact === 'startDate'
          ? 'tasks.filters.specificStartDate' : 'tasks.filters.specificEndDate';
        displayInput.setAttribute('aria-label', t(labelKey));
        displayInput.dataset.i18n = labelKey;
        displayInput.dataset.i18nAttr = 'aria-label';
      }

      // Set placeholder - all date inputs use the same format
      displayInput.placeholder = t('common.datePlaceholder');
      displayInput.maxLength = "10";

      // Hide original date input and keep ISO value there
      input.style.display = "none";
      input.type = "hidden";

      input.parentNode.insertBefore(wrapper, input);
      wrapper.appendChild(input);
      wrapper.appendChild(displayInput);

      const initialISO = input.value && looksLikeISO(input.value) ? input.value : "";
      if (initialISO) {
        displayInput.value = toDMYFromISO(initialISO);
      } else {
        displayInput.value = "";
      }

      const fp = flatpickrFn(displayInput, {
        ...dateConfig,
        defaultDate: initialISO ? new Date(initialISO) : null,
        onReady(_, __, inst) {
          addTodayButton(inst);
        },
        onOpen(_, __, inst) {
          if (!input.value && !inst.selectedDates.length) {
            inst.jumpToDate(new Date());
          }
        },
        onChange: function (selectedDates) {
          const previousValue = input.value || '';
          let iso = "";
          if (selectedDates.length > 0) {
            const d = selectedDates[0];
            iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
              d.getDate()
            ).padStart(2, "0")}`;
          }
          input.value = iso;

          const form = document.getElementById("task-form");
          if (form && (input.name === "startDate" || input.name === "endDate")) {
            const modal = document.querySelector('.modal.active');
            if (modal) {
              const startInput = modal.querySelector('input[name="startDate"]');
              const endInput = modal.querySelector('input[name="endDate"]');

              if (startInput && endInput) {
                const startValue = (startInput.value || '').trim();
                const endValue = (endInput.value || '').trim();

                if (startValue && endValue && endValue < startValue) {
                  input.value = previousValue;
                  if (previousValue) {
                    displayInput._flatpickr.setDate(new Date(previousValue), false);
                  } else {
                    displayInput._flatpickr.clear();
                  }

                  const wrapper = displayInput.closest('.date-input-wrapper');
                  const formGroup = wrapper ? wrapper.closest('.form-group') : null;

                  if (formGroup) {
                    const existingError = formGroup.querySelector('.date-validation-error');
                    if (existingError) existingError.remove();

                    const errorMsg = document.createElement('div');
                    errorMsg.className = 'date-validation-error';
                    errorMsg.style.cssText = 'color: var(--error); font-size: 12px; margin-top: 6px; padding: 8px 12px; background: var(--bg-error, rgba(239, 68, 68, 0.08)); border-left: 3px solid var(--error); border-radius: 4px;';
                    errorMsg.innerHTML = '⚠️ ' + (input.name === "endDate" ? 'End Date cannot be before Start Date' : 'Start Date cannot be after End Date');

                    wrapper.parentNode.insertBefore(errorMsg, wrapper.nextSibling);
                    setTimeout(() => { if (errorMsg.parentElement) errorMsg.remove(); }, 5000);
                  }
                  return;
                } else {
                  const wrapper = displayInput.closest('.date-input-wrapper');
                  const formGroup = wrapper ? wrapper.closest('.form-group') : null;
                  if (formGroup) {
                    const existingError = formGroup.querySelector('.date-validation-error');
                    if (existingError) existingError.remove();
                  }
                }
              }
            }
          }

          // Handle filter date inputs
          if (input.dataset.dateFilterExact) {
            applyExactDateFilter(iso, input.dataset.dateFilterExact);
            return;
          }
          if (input.id === "filter-date-from") {
            filterState.dateFrom = iso;
            updateFilterBadges();
            renderAfterFilterChange();
            const calendarView = document.getElementById("calendar-view");
            if (calendarView) {
              renderCalendar();
            }
            updateClearButtonVisibility();
            return;
          } else if (input.id === "filter-date-to") {
            filterState.dateTo = iso;
            updateFilterBadges();
            renderAfterFilterChange();
            const calendarView = document.getElementById("calendar-view");
            if (calendarView) {
              renderCalendar();
            }
            updateClearButtonVisibility();
            return;
          }

          // Persist only when:
          // - this field is a task date field (startDate or endDate)
          // - a task is being edited
          // - the change was user-initiated (not programmatic)
          const isEditing = !!(form && form.dataset.editingTaskId);
          const fieldName = input.name;
          const isDateField = fieldName === "startDate" || fieldName === "endDate";
          if (isEditing && isDateField && !fp.__suppressChange) {
            updateTaskField(fieldName, iso);
            return;
          }

          // Handle project date changes (si usan type="date" envueltos)
          if (displayInput.classList.contains('editable-date') && !fp.__suppressChange) {
            // Extraer info del atributo onchange original (método modular)
            const onchangeAttr = displayInput.getAttribute('onchange');
            if (onchangeAttr && onchangeAttr.includes('updateProjectField')) {
              const match = onchangeAttr.match(/updateProjectField\((\d+),\s*['"](\w+)['"]/);
              if (match) {
                const projectId = parseInt(match[1], 10);
                const field = match[2];
                // Llamada directa a la función modular (sin window)
                updateProjectField(projectId, field, displayInput.value);
              }
            }
          }
        },
      });

      patchProgrammaticGuards(fp);
      addDateMask(displayInput, fp);
      input._flatpickrInstance = fp;

      // Add an explicit Clear button for task date fields
      const inTaskForm = !!displayInput.closest('#task-form');
      const isTaskDateField = input.name === 'startDate' || input.name === 'endDate';
      if (inTaskForm && isTaskDateField) {
        const clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.dataset.i18n = 'common.clear';
        clearBtn.textContent = t('common.clear');
        clearBtn.style.padding = '6px 10px';
        clearBtn.style.border = '1px solid var(--border)';
        clearBtn.style.background = 'var(--bg-tertiary)';
        clearBtn.style.color = 'var(--text-secondary)';
        clearBtn.style.borderRadius = '6px';
        clearBtn.style.cursor = 'pointer';
        clearBtn.style.flex = '0 0 auto';

        const wrapperNode = displayInput.parentElement;
        if (wrapperNode) {
          wrapperNode.style.display = 'flex';
          wrapperNode.style.gap = '8px';
        }

        wrapperNode.appendChild(clearBtn);
        clearBtn.addEventListener('click', () => {
          displayInput.value = '';
          input.value = '';
          if (fp) {
            fp.__suppressChange = true;
            fp.clear();
            setTimeout(() => (fp.__suppressChange = false), 0);
          }
          const form = document.getElementById('task-form');
          const isEditing = !!(form && form.dataset.editingTaskId);
          if (isEditing) {
            updateTaskField(input.name, '');
          }
        });
      }

      // Add clear button for project creation modal date fields
      const inProjectModal = !!displayInput.closest('#project-modal');
      const isProjectModalDateField = input.name === 'startDate' || input.name === 'endDate';
      if (inProjectModal && isProjectModalDateField) {
        const clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.textContent = t('common.clear');
        clearBtn.className = 'btn-secondary';
        clearBtn.style.cssText = 'padding: 0 10px; align-self: stretch; flex-shrink: 0; font-size: 13px;';

        const wrapperNode = displayInput.parentElement;
        if (wrapperNode) {
          wrapperNode.style.display = 'flex';
          wrapperNode.style.gap = '8px';
          wrapperNode.style.alignItems = 'stretch';
        }
        wrapperNode.appendChild(clearBtn);
        clearBtn.addEventListener('click', () => {
          displayInput.value = '';
          input.value = '';
          if (fp) {
            fp.__suppressChange = true;
            fp.clear();
            setTimeout(() => (fp.__suppressChange = false), 0);
          }
        });
      }
    } else {
      // 🌟 CORRECCIÓN AQUI: Plain text inputs with .datepicker (Project Fields)
      input.maxLength = "10";
      const fp = flatpickrFn(input, {
        ...dateConfig,
        defaultDate: null,
        onReady(_, __, inst) {
          addTodayButton(inst);
        },
        onChange: function (selectedDates, dateStr) {
          if (fp.__suppressChange) return;

          // Check if this is a project date field using data attributes
          const projectId = input.dataset.projectId;
          const fieldName = input.dataset.field;

          if (projectId && fieldName) {
            // Save without re-rendering the panel (avoids flicker); update duration in-place
            updateProjectField(parseInt(projectId, 10), fieldName, dateStr, { render: false });
            refreshProjectDurationUI(parseInt(projectId, 10));
          }
        },
      });

      patchProgrammaticGuards(fp);
      addDateMask(input, fp);
      input._flatpickrInstance = fp;

      // Add clear button for project details panel date fields (only once — guard against re-init)
      const detailProjectId = input.dataset.projectId;
      const detailFieldName = input.dataset.field;
      if (detailProjectId && detailFieldName && !input.closest('.project-date-wrapper')) {
        const clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.textContent = t('common.clear');
        clearBtn.className = 'btn-secondary';
        clearBtn.style.cssText = 'padding: 0 10px; align-self: stretch; flex-shrink: 0; font-size: 13px;';

        const dateWrapper = document.createElement('div');
        dateWrapper.className = 'project-date-wrapper';
        dateWrapper.style.cssText = 'display: flex; gap: 6px; align-items: stretch; flex: 1;';
        input.parentNode.insertBefore(dateWrapper, input);
        dateWrapper.appendChild(input);
        dateWrapper.appendChild(clearBtn);

        // Use input._flatpickrInstance dynamically so the handler works after re-init
        clearBtn.addEventListener('click', () => {
          input.value = '';
          const fpInst = input._flatpickrInstance;
          if (fpInst) {
            fpInst.__suppressChange = true;
            fpInst.clear();
            setTimeout(() => { if (input._flatpickrInstance) input._flatpickrInstance.__suppressChange = false; }, 0);
          }
          // Save without re-rendering (avoids flicker); update duration in-place
          updateProjectField(parseInt(input.dataset.projectId, 10), input.dataset.field, '', { render: false });
          refreshProjectDurationUI(parseInt(input.dataset.projectId, 10));
        });
      }
    }
  });
}

function getFlatpickrLocale() {
  const lang = getCurrentLanguage();
  const locale = lang === 'es' ? flatpickrSpanish : flatpickr.l10ns.default;
  return locale ? { ...locale, firstDayOfWeek: 1 } : { firstDayOfWeek: 1 };
}

// Refresh the header meta chips (dates range, delay, overdue) after a date edit (no full re-render)
function refreshProjectDurationUI(projectId) {
    const metaEl = document.getElementById('project-details-meta');
    if (!metaEl) return;
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    metaEl.innerHTML = buildProjectDetailsMetaHtml(project, getProjectStatus(projectId));
}

function parseProjectDateValue(value) {
    if (!value || typeof value !== 'string') return null;
    
    let isoValue = value;
    
    // Convert DD/MM/YYYY to ISO format if needed
    if (looksLikeDMY(value)) {
        isoValue = toISOFromDMY(value);
    } else if (!looksLikeISO(value)) {
        return null;
    }
    
    // Parse the ISO format
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoValue.trim());
    if (!match) return null;
    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isFinite(date.getTime()) ? date : null;
}

function startOfLocalDay(date) {
    if (!(date instanceof Date) || !Number.isFinite(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}


function checkAndRecordProjectStatusChange(projectId, triggeringTask = null) {
    if (!window.historyService) return;
    
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    const currentStatus = getProjectStatus(projectId);
    
    // Get the most recent status change history for this project
    const projectHistory = window.historyService.getEntityHistory('project', projectId);
    let lastKnownStatus = null;
    
    if (projectHistory && projectHistory.length > 0) {
        // Look for the most recent status change
        // History is newest-first (unshift), so index 0 is the latest entry
        for (let i = 0; i < projectHistory.length; i++) {
            const entry = projectHistory[i];
            if (entry.changes && entry.changes.status) {
                lastKnownStatus = entry.changes.status.after;
                break;
            }
        }
    }
    
    // If status changed, record it with task trigger information
    if (lastKnownStatus !== currentStatus) {
        const meta = {};
        if (triggeringTask) {
            meta.origin = `Task "${triggeringTask.title}" marked as ${triggeringTask.status}`;
            meta.triggeringTaskId = triggeringTask.id;
            meta.triggeringTaskTitle = triggeringTask.title;
            meta.triggeringTaskStatus = triggeringTask.status;
        }
        
        window.historyService.recordProjectStatusChanged(project, lastKnownStatus, currentStatus, meta);
    }
}

function checkProjectCompletionDateWarning(projectId, task) {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    const projectTasks = tasks.filter(t => t.projectId === projectId);
    const allTasksDone = projectTasks.every(t => t.status === 'done');
    
    if (allTasksDone && task.status === 'done') {
        const projectEndDate = parseProjectDateValue(project.endDate);
        const today = new Date();
        
        if (projectEndDate && projectEndDate < today) {
            const confirmUpdate = confirm(
                `Project "${project.name || 'Untitled'}" was planned to finish on ${formatDate(projectEndDate)} but is completing today (${formatDate(today)}).\n\nWould you like to update the project end date to today?`
            );
            
            if (confirmUpdate) {
                const oldEndDate = project.endDate;
                project.endDate = today.toISOString().split('T')[0];
                
                // Record project update
                if (window.historyService) {
                    const oldProjectCopy = JSON.parse(JSON.stringify(project));
                    oldProjectCopy.endDate = oldEndDate;
                    window.historyService.recordProjectUpdated(oldProjectCopy, project);
                }
            }
        }
    }
}

function getProjectActualCompletionDate(projectId) {
    const project = projects.find(p => p.id === projectId);
    if (!project) return null;
    
    const currentStatus = getProjectStatus(projectId);
    
    // If project is not completed, no actual completion date
    if (currentStatus !== 'completed') {
        return null;
    }
    
    // If project is completed, check if we have a recorded status change date
    if (window.historyService) {
        const projectHistory = window.historyService.getEntityHistory('project', projectId);
        if (projectHistory && projectHistory.length > 0) {
            // Look for the most recent status change to 'completed'
            // History is newest-first (unshift), so index 0 is the latest entry
            for (let i = 0; i < projectHistory.length; i++) {
                const entry = projectHistory[i];
                if (entry.changes && entry.changes.status && entry.changes.status.after === 'completed') {
                    return new Date(entry.timestamp);
                }
            }
        }
    }
    
    // Fallback: if project is completed but no recorded status change, 
    // assume it completed within its planned date range
    const endDate = parseProjectDateValue(project.endDate);
    const startDate = parseProjectDateValue(project.startDate);
    
    if (endDate) {
        return endDate; // Assume it completed on or before the planned end date
    }
    
    return null;
}

function getProjectTimelineDetails(project, projectStatus = null) {
    const startDate = parseProjectDateValue(project?.startDate);
    const endDate = parseProjectDateValue(project?.endDate);
    const durationDays =
        startDate && endDate
            ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
            : null;
    const durationText = Number.isFinite(durationDays)
        ? (durationDays === 1 ? '1 day' : t('projects.details.durationDays', { count: durationDays }))
        : '-';

    if (!endDate) {
        return { durationText, remainingText: '', remainingState: 'hidden' };
    }

    if ((projectStatus || getProjectStatus(project?.id)) === 'completed') {
        // Hide timeline for completed projects
        return { durationText, remainingText: '', remainingState: 'hidden' };
    }

    const today = startOfLocalDay(new Date());
    const endDay = startOfLocalDay(endDate);
    const remainingDays = Math.ceil((endDay - today) / (1000 * 60 * 60 * 24));

    if (remainingDays < 0) {
        const daysCount = Math.abs(remainingDays);
        let remainingText;
        if (daysCount === 1) {
            remainingText = '1 day overdue';
        } else {
            remainingText = t('projects.details.remainingOverdueDays', { count: daysCount });
        }
        return {
            durationText,
            remainingText,
            remainingState: 'overdue'
        };
    }

    if (remainingDays === 0) {
        return {
            durationText,
            remainingText: t('projects.details.remainingDueToday'),
            remainingState: 'due-today'
        };
    }

    let remainingText;
    if (remainingDays === 1) {
        remainingText = '1 day left';
    } else {
        remainingText = t('projects.details.remainingDaysLeft', { count: remainingDays });
    }
    
    return {
        durationText,
        remainingText,
        remainingState: 'active'
    };
}

function refreshFlatpickrLocale() {
  const locale = getFlatpickrLocale();
  document.querySelectorAll('input').forEach((input) => {
    const fp = input._flatpickrInstance;
    if (fp && typeof fp.set === 'function') {
      fp.set('locale', locale);
      if (typeof fp.redraw === 'function') {
        fp.redraw();
      }
    }
  });
}

let lastDataFingerprint = null;
let lastCalendarFingerprint = null;

// Apply a fresh server snapshot only when it differs from what is loaded.
// Shared by the startup background refresh and the report view's Refresh.
// Returns true when the data changed.
function applyFreshAllData(fresh) {
    if (!fresh) return false;
    const nextFingerprint = buildDataFingerprint(fresh);
    const currentFingerprint = buildDataFingerprint({ tasks, projects, feedbackItems });
    if (nextFingerprint === currentFingerprint) return false;

    applyLoadedAllData(fresh);
    // Delta queue removed - saves happen on change
    backfillMissingCompletedDates();

    const nextCalendarFingerprint = buildCalendarFingerprint(fresh.tasks || tasks, fresh.projects || projects);
    const calendarChanged = lastCalendarFingerprint && nextCalendarFingerprint !== lastCalendarFingerprint;
    lastDataFingerprint = nextFingerprint;
    lastCalendarFingerprint = nextCalendarFingerprint;
    renderActivePageOnly({ calendarChanged });
    return true;
}
let calendarRenderDebounceId = null;

function getMaxUpdatedTime(items, getTimeFn) {
    let maxTime = 0;
    const list = Array.isArray(items) ? items : [];
    for (const item of list) {
        const time = getTimeFn ? getTimeFn(item) : Date.parse(item?.updatedAt || item?.createdAt || item?.createdDate || "") || 0;
        if (Number.isFinite(time) && time > maxTime) maxTime = time;
    }
    return maxTime;
}

function getIdSum(items) {
    let sum = 0;
    const list = Array.isArray(items) ? items : [];
    for (const item of list) {
        const id = Number(item?.id);
        if (!Number.isNaN(id)) sum += id;
    }
    return sum;
}

function buildDataFingerprint(data) {
    const t = Array.isArray(data?.tasks) ? data.tasks : [];
    const p = Array.isArray(data?.projects) ? data.projects : [];
    const f = Array.isArray(data?.feedbackItems) ? data.feedbackItems : [];
    const tMax = getMaxUpdatedTime(t, typeof getTaskUpdatedTime === "function" ? getTaskUpdatedTime : null);
    const pMax = getMaxUpdatedTime(p, (proj) => Date.parse(proj?.updatedAt || proj?.createdAt || "") || 0);
    const fMax = getMaxUpdatedTime(f, (fb) => Date.parse(fb?.updatedAt || fb?.createdAt || fb?.createdDate || "") || 0);
    return `t:${t.length}:${tMax}:${getIdSum(t)}|p:${p.length}:${pMax}:${getIdSum(p)}|f:${f.length}:${fMax}:${getIdSum(f)}`;
}

function buildCalendarFingerprint(sourceTasks = tasks, sourceProjects = projects) {
    const includeBacklog = !!settings.calendarIncludeBacklog;
    const projectFilter = filterState?.projects;
    const filteredProjectIds = projectFilter && projectFilter.size > 0
        ? new Set(Array.from(projectFilter).map((id) => parseInt(id, 10)))
        : null;

    let hash = 0;
    let count = 0;
    const list = Array.isArray(sourceTasks) ? sourceTasks : [];
    for (const task of list) {
        if (!task) continue;
        if (!includeBacklog && task.status === 'backlog') continue;
        if (filteredProjectIds && !filteredProjectIds.has(Number(task.projectId))) continue;
        const start = task.startDate || "";
        const end = task.endDate || "";
        const hasStart = typeof start === 'string' && start.length === 10 && start.includes('-');
        const hasEnd = typeof end === 'string' && end.length === 10 && end.includes('-');
        if (!hasStart && !hasEnd) continue;

        count++;
        const id = Number(task.id) || 0;
        const status = task.status ? String(task.status) : "";
        const proj = Number(task.projectId) || 0;
        const title = task.title ? String(task.title) : "";
        const updated = Date.parse(task.updatedAt || task.createdAt || task.createdDate || "") || 0;
        hash = ((hash * 31) + id + proj + updated) | 0;
        for (let i = 0; i < start.length; i++) hash = ((hash * 31) + start.charCodeAt(i)) | 0;
        for (let i = 0; i < end.length; i++) hash = ((hash * 31) + end.charCodeAt(i)) | 0;
        for (let i = 0; i < status.length; i++) hash = ((hash * 31) + status.charCodeAt(i)) | 0;
        for (let i = 0; i < title.length; i++) hash = ((hash * 31) + title.charCodeAt(i)) | 0;
    }

    const projectList = Array.isArray(sourceProjects) ? sourceProjects : [];
    let projectHash = 0;
    let projectCount = 0;
    for (const project of projectList) {
        if (!project) continue;
        if (filteredProjectIds && !filteredProjectIds.has(Number(project.id))) continue;
        projectCount++;
        const pid = Number(project.id) || 0;
        const name = project.name ? String(project.name) : "";
        const startDate = project.startDate ? String(project.startDate) : "";
        const endDate = project.endDate ? String(project.endDate) : "";
        const updatedAt = Date.parse(project.updatedAt || project.createdAt || "") || 0;
        projectHash = ((projectHash * 31) + pid + updatedAt) | 0;
        for (let i = 0; i < name.length; i++) projectHash = ((projectHash * 31) + name.charCodeAt(i)) | 0;
        for (let i = 0; i < startDate.length; i++) projectHash = ((projectHash * 31) + startDate.charCodeAt(i)) | 0;
        for (let i = 0; i < endDate.length; i++) projectHash = ((projectHash * 31) + endDate.charCodeAt(i)) | 0;
    }

    return `m:${currentYear}-${currentMonth + 1}|b:${includeBacklog ? 1 : 0}|pf:${filteredProjectIds ? filteredProjectIds.size : 0}|c:${count}|h:${hash}|pc:${projectCount}|ph:${projectHash}`;
}

function scheduleCalendarRenderDebounced(delayMs = 500) {
    if (calendarRenderDebounceId !== null) {
        clearTimeout(calendarRenderDebounceId);
    }
    calendarRenderDebounceId = setTimeout(() => {
        calendarRenderDebounceId = null;
        renderCalendar();
        lastCalendarFingerprint = buildCalendarFingerprint();
    }, delayMs);
}

function renderWithoutCalendar() {
    updateCounts();
    renderDashboard();
    renderProjects();
    renderAutomations();
    updateDocsNavTree(null);
    renderTasks();
    renderFeedback();
    renderUpdatesPage();
    renderListView();
    renderAppVersionLabel();
}

function getActivePageId() {
    const active = document.querySelector('.page.active');
    return active ? active.id : null;
}

function applyInitialRouteShell() {
    const hash = window.location.hash.slice(1).split('?')[0];
    if (!hash) return;

    const validPages = ['dashboard', 'projects', 'tasks', 'automations', 'updates', 'feedback', 'calendar'];
    let pageToShow = 'dashboard';

    if (hash === 'dashboard/recent_activity') {
        pageToShow = 'dashboard';
    } else if (hash.startsWith('project-')) {
        pageToShow = 'projects';
    } else if (hash === 'tasks/archive') {
        pageToShow = 'tasks';
    } else if (hash === 'projects/archive') {
        pageToShow = 'projects';
    } else if (validPages.includes(hash)) {
        pageToShow = hash === 'calendar' ? 'tasks' : hash;
    } else {
        return;
    }

    document.querySelectorAll(".page").forEach((page) => page.classList.remove("active"));
    document.getElementById(pageToShow)?.classList.add("active");
    document.getElementById("project-details")?.classList.remove("active");

    document.querySelectorAll(".nav-item").forEach((nav) => nav.classList.remove("active"));
    if (hash === 'calendar') {
        document.querySelector('.nav-item[data-page="calendar"]')?.classList.add('active');
    } else {
        document.querySelector(`.nav-item[data-page="${pageToShow}"]`)?.classList.add("active");
    }
}

function renderActivePageOnly(options = {}) {
    const calendarChanged = !!options.calendarChanged;
    const calendarImmediate = !!options.calendarImmediate;
    const activeId = getActivePageId();
    console.log('🕒 TIMING: renderActivePageOnly() called, activeId =', activeId);
    updateCounts();
    renderAppVersionLabel();
    updateDocsNavTree(null);

    if (activeId === "dashboard") {
        renderDashboard();
        return;
    }
    if (activeId === "projects") {
        renderProjects();
        return;
    }
    if (activeId === "automations") {
        renderAutomations();
        return;
    }
    if (activeId === "docs") {
        renderDocsPage();
        return;
    }
    if (activeId === "tasks") {
        try { applyBacklogFilterVisibility(); } catch (e) {}
        const kanbanBoard = document.querySelector(".kanban-board");
        if (kanbanBoard && !kanbanBoard.classList.contains("hidden")) {
            renderTasks();
        }
        if (getIsMobileCached() || document.getElementById("list-view")?.classList.contains("active")) {
            renderListView();
        }
        if (document.getElementById("calendar-view")?.classList.contains("active")) {
            if (calendarChanged || calendarImmediate) {
                if (calendarImmediate) {
                    if (calendarRenderDebounceId !== null) {
                        clearTimeout(calendarRenderDebounceId);
                        calendarRenderDebounceId = null;
                    }
                    renderCalendar();
                    lastCalendarFingerprint = buildCalendarFingerprint();
                } else {
                    logPerformanceMilestone('calendar-refresh-debounced', { reason: 'data-changed' });
                    scheduleCalendarRenderDebounced(500);
                }
            } else {
                logPerformanceMilestone('calendar-refresh-skipped', { reason: 'fingerprint-match' });
            }
        }
        const archivedView = document.getElementById("archived-view");
        if (archivedView && archivedView.style.display !== 'none') {
            renderArchivedView();
        }
        return;
    }
    if (activeId === "updates") {
        renderUpdatesPage();
        return;
    }
    if (activeId === "feedback") {
        renderFeedback();
    }

    const projectDetailsView = document.getElementById("project-details");
    if (projectDetailsView && projectDetailsView.classList.contains("active")) {
        const hash = window.location.hash.slice(1);
        if (hash.startsWith('project-')) {
            const projectId = parseInt(hash.replace('project-', ''), 10);
            if (!Number.isNaN(projectId)) {
                showProjectDetails(projectId);
            }
        }
    }
}

// Prevent double initialization
let isInitialized = false;
// Track the active hashchange routing listener so we can remove it before adding a new one
// on user switch. Without this, every re-init adds a second listener causing double renders.
let activeHandleRouting = null;

export async function init(options = {}) {
    const skipCache = !!options.skipCache;
    // Prevent multiple simultaneous initializations.
    // Allow re-initialization when switching users (skipCache: true is only
    // passed from completeLogin() during login/user-switch, never on first load).
    if (isInitialized && !skipCache) {
        // console.log('[PERF] Init already completed, skipping duplicate call');
        return;
    }
    isInitialized = true;
    logPerformanceMilestone('init-start', {
        hasAuth: !!localStorage.getItem('authToken'),
        hasAdmin: !!localStorage.getItem('adminToken')
    });
    const initTimer = debugTimeStart("init", "init", {
        hasAuth: !!localStorage.getItem('authToken'),
        hasAdmin: !!localStorage.getItem('adminToken')
    });

    // console.time('[PERF] Total Init Time');

    // Public docs links are accessible regardless of login state — check before auth.
    const _publicRoute = getDocsRouteState();
    if (_publicRoute.isDocsRoute && _publicRoute.publicToken) {
        await renderPublicDocsStandalone(_publicRoute.publicToken, _publicRoute.width);
        isInitialized = false;
        return;
    }

    // No public token — require authentication before booting the app shell.
    if (!localStorage.getItem('authToken') && !localStorage.getItem('adminToken')) {
        console.log('Waiting for authentication before initializing app...');
        isInitialized = false;
        return;
    }

    // console.time('[PERF] Authentication Check');
    // Progress tracking
    if (typeof updateBootSplashProgress === 'function') {
        updateBootSplashProgress(10); // Starting initialization
    }
    // console.timeEnd('[PERF] Authentication Check');

    // Clear old data before loading new user's data
    // This ensures clean state when switching users
    projects = [];
    tasks = [];
    automations = [];
    savedPrompts = [];
    feedbackItems = [];
    tagColorMap = {};
    tagColorOverrides = {};
    projectColorOverrides = {};
    tagColorsVersion = 0;
    tagColorsReady = false;
    _pendingProjectColorsSave = false;
    if (pendingTagColorsSaveTimer) {
        clearTimeout(pendingTagColorsSaveTimer);
        pendingTagColorsSaveTimer = null;
    }
    projectCounter = 1;
    taskCounter = 1;
    // Drop the previous user's id blocks and reserve fresh ones for this user right
    // away, so they are usually in place before the first create.
    resetIdBlocks();
    topUpIdBlocks();
    // Reset settings to defaults so a new user doesn't inherit the previous
    // user's preferences (language, email prefs, etc.) when no custom settings
    // are saved for them yet.
    settings = getDefaultSettings();
    // Reset filter state so a new user doesn't see the previous user's active filters.
    filterState = {
        search: "", statuses: new Set(), statusExcludeMode: false,
        priorities: new Set(), priorityExcludeMode: false,
        projects: new Set(), projectExcludeMode: false,
        tags: new Set(), tagExcludeMode: false,
        datePresets: new Set(), dateFrom: "", dateTo: "",
        taskIds: new Set(), linkTypes: new Set(),
    };

    isInitializing = true;

    if (typeof updateBootSplashProgress === 'function') {
        updateBootSplashProgress(20); // Loading data...
    }

    // Pre-apply the route shell to avoid dashboard flash on deep links (e.g. /tasks).
    // Rendering still waits for data load, but the correct page is visible immediately.
    applyInitialRouteShell();
    markPerfOnce('first_skeleton_paint', { reason: 'route-shell' });

    // console.time('[PERF] Load All Data');
    // Load fresh data directly (no SWR caching - simpler and avoids stale data bugs)
    const handleAllDataRefresh = (fresh) => { applyFreshAllData(fresh); };

    const allDataPromise = loadAllData({
        preferCache: !skipCache,
        onRefresh: skipCache ? null : handleAllDataRefresh,
        feedback: {
            limitPending: FEEDBACK_OPEN_ITEMS_PER_PAGE,
            limitDone: FEEDBACK_ITEMS_PER_PAGE,
            cacheKey: FEEDBACK_CACHE_KEY,
            preferCache: !skipCache
        }
    });
    const sortStatePromise = loadSortStateData().catch(() => null);
    const projectColorsPromise = loadProjectColorsData().catch(() => ({}));
    const tagColorsPromise = loadTagColorsData().catch(() => ({}));
    const tagColorsVersionPromise = loadTagColorsVersionData().catch(() => 0);
    const projectColorOverridesPromise = loadProjectColorOverridesData().catch(() => ({}));
    const tagColorOverridesPromise = loadTagColorOverridesData().catch(() => ({}));
    const settingsPromise = loadSettingsData().catch(() => ({}));
    const automationsPromise = loadAutomationsData().catch(() => []);
    const savedPromptsPromise = loadSavedPromptsData().catch(() => []);
    const dependenciesPromise = loadData("dependencies").then(data => deserializeDependencies(data)).catch(() => ({}));
    const historyPromise = window.historyService?.loadHistory
        ? window.historyService.loadHistory().catch(() => null)
        : Promise.resolve(null);

    const loadTimer = debugTimeStart("init", "load-data");

    // Apply cached data ASAP so the UI isn't blank while other async loads finish.
    const allData = await allDataPromise;
    if (typeof updateBootSplashProgress === 'function') {
        updateBootSplashProgress(50); // Cached data ready (if available)
    }
    applyLoadedAllData(allData);
    // Delta queue removed - saves happen on change
    // Fast render from cached data so the UI isn't empty while other async loads finish.
    renderActivePageOnly();

    const [sortState, loadedProjectColors, loadedTagColors, loadedTagColorsVersion, loadedSettings, loadedAutomations, loadedSavedPrompts, loadedDependencies, _loadedHistory, loadedProjectColorOverrides, loadedTagColorOverrides] = await Promise.all([
        sortStatePromise,
        projectColorsPromise,
        tagColorsPromise,
        tagColorsVersionPromise,
        settingsPromise,
        automationsPromise,
        savedPromptsPromise,
        dependenciesPromise,
        historyPromise,
        projectColorOverridesPromise,
        tagColorOverridesPromise,
    ]);
    debugTimeEnd("init", loadTimer, {
        taskCount: allData?.tasks?.length || 0,
        projectCount: allData?.projects?.length || 0,
        feedbackCount: allData?.feedbackItems?.length || 0
    });
    logPerformanceMilestone('init-data-loaded', {
        taskCount: allData?.tasks?.length || 0,
        projectCount: allData?.projects?.length || 0,
        feedbackCount: allData?.feedbackItems?.length || 0
    });
    // console.timeEnd('[PERF] Load All Data');

    if (typeof updateBootSplashProgress === 'function') {
        updateBootSplashProgress(60); // Data loaded, applying...
    }

    // Apply sort prefs (keep the same normalization behavior as loadSortPreferences)
    if (sortState && typeof sortState === "object") {
        const savedMode = sortState.sortMode;
        const savedOrder = sortState.manualTaskOrder;
        if (savedMode) sortMode = savedMode;
        if (savedOrder) manualTaskOrder = savedOrder;
    } else {
        try {
            const lm = localStorage.getItem('sortMode');
            const lo = localStorage.getItem('manualTaskOrder');
            if (lm) sortMode = lm;
            if (lo) manualTaskOrder = JSON.parse(lo);
        } catch (err) {}
    }
    // Normalize/guard persisted values (older versions used 'auto' to mean priority ordering)
    if (sortMode === 'auto' || (sortMode !== 'priority' && sortMode !== 'manual')) {
        sortMode = 'priority';
    }
    updateSortUI();

    // Load user-chosen overrides FIRST — these are never wiped by any migration
    projectColorOverrides = loadedProjectColorOverrides && typeof loadedProjectColorOverrides === "object"
        ? loadedProjectColorOverrides : {};
    tagColorOverrides = loadedTagColorOverrides && typeof loadedTagColorOverrides === "object"
        ? Object.fromEntries(
            Object.entries(loadedTagColorOverrides).filter(([key, color]) => validateTagColor(color))
          )
        : {};

    projectColorMap = loadedProjectColors && typeof loadedProjectColors === "object" ? loadedProjectColors : {};
    const persistedTagColors = loadedTagColors && typeof loadedTagColors === "object" 
        ? Object.fromEntries(
            Object.entries(loadedTagColors).filter(([key, color]) => validateTagColor(color))
          )
        : {};
    const persistedTagColorsVersion = Number.isFinite(Number(loadedTagColorsVersion))
        ? Number(loadedTagColorsVersion)
        : 0;
    const hasPersistedTagColors = Object.keys(persistedTagColors).length > 0;
    
    // Only rebootstrap for breaking changes - use a threshold version instead of current version
    const REBOOTSTRAP_THRESHOLD_VERSION = 12; // Only force rebootstrap below this version
    const requiresTagColorRebootstrap =
        persistedTagColorsVersion < REBOOTSTRAP_THRESHOLD_VERSION || !hasPersistedTagColors;

    if (requiresTagColorRebootstrap) {
        console.log(`Tag color rebootstrap required: version ${persistedTagColorsVersion} < ${REBOOTSTRAP_THRESHOLD_VERSION} or no colors`);
        // Wipe only the auto-generated map — tagColorOverrides (user choices) are NEVER touched
        tagColorMap = {};
        buildTagColorMapFromTasks(tasks, tagColorMap);
        tagColorsVersion = TAG_COLOR_ASSIGNMENT_VERSION;
        await Promise.all([
            saveTagColors({ force: true }),
            saveTagColorsVersion({ force: true })
        ]);
    } else {
        tagColorMap = { ...persistedTagColors };
        console.log(`Using existing tag colors: ${Object.keys(tagColorMap).length} tags`);
    }
    tagColorsVersion = persistedTagColorsVersion;

    tagColorsReady = true;

    // Flush any project color save that was queued during initialization
    if (_pendingProjectColorsSave) {
        _pendingProjectColorsSave = false;
        saveProjectColors().catch(err => console.error("Error flushing pending project colors save:", err));
    }

    if (loadedSettings && typeof loadedSettings === "object") {
        settings = { ...settings, ...loadedSettings };
    }
    automations = Array.isArray(loadedAutomations) ? loadedAutomations : [];
    savedPrompts = Array.isArray(loadedSavedPrompts) ? loadedSavedPrompts : [];

    // Load dependencies
    if (loadedDependencies && typeof loadedDependencies === "object") {
        dependencies = loadedDependencies;
    }
    
    settings.language = normalizeLanguage(settings.language);
    applyDebugLogSetting(settings.debugLogsEnabled);
    // Drive the on-screen diagnostics overlay from the synced setting, unless a
    // ?diag= URL flag explicitly overrode it for this session.
    if (typeof window.__diagSetEnabled === 'function' && !window.__diagUrlOverride) {
        window.__diagSetEnabled(!!settings.diagnosticsOverlayEnabled);
    }
    // Called by the overlay's own "off" button so the disable is persisted to
    // KV settings and survives a refresh (not just the localStorage fast-path).
    window.__diagSaveDisabled = function () {
        settings.diagnosticsOverlayEnabled = false;
        saveSettingsData(settings).catch(function () {});
        const tog = document.getElementById('diag-overlay-toggle');
        if (tog) tog.checked = false;
    };
    if (perfDebugForced) {
        settings.debugLogsEnabled = true;
        applyDebugLogSetting(true);
    }
    applyWorkspaceLogo(); // Apply any custom workspace logo

    // Sync window.enableReviewStatus with settings
    if (typeof settings.enableReviewStatus !== 'undefined') {
        window.enableReviewStatus = settings.enableReviewStatus;
        localStorage.setItem('enableReviewStatus', String(settings.enableReviewStatus));
    }
    applyReviewStatusVisibility(); // Apply review status visibility
    applyBacklogColumnVisibility(); // Apply backlog column visibility

    // Sync calendar entity visibility from persisted settings
    calendarShowProjects = settings.calendarShowProjects !== false;
    calendarShowTasks = settings.calendarShowTasks !== false;

    // Docs sidebar hide/show (eye) — persisted in settings.docsNavHidden
    bindDocsNavVisibilityToggle();
    applyDocsNavVisibility();

    if (typeof updateBootSplashProgress === 'function') {
        updateBootSplashProgress(75); // Setting up UI...
    }

    // console.time('[PERF] Setup UI');
    // Basic app setup
    setupNavigation();
    setupStatusDropdown();
    setupPriorityDropdown();
    setupProjectDropdown();
    setupUserMenus();
    setupNotificationMenu();
    hydrateUserProfile();
    initializeDatePickers();
    initFiltersUI();
    setupModalTabs();
    applyLanguage();
    logPerformanceMilestone('init-ui-ready');
    // console.timeEnd('[PERF] Setup UI');

    // Load templates in background (non-blocking)
    loadTemplatesData().then(loaded => {
        templates = loaded;
        renderTemplateDropdown();
    }).catch(err => console.error("Error loading templates:", err));

    setupTemplateDropdown();

    if (typeof updateBootSplashProgress === 'function') {
        updateBootSplashProgress(90); // Rendering...
    }

    // Finished initializing - allow saves again
    isInitializing = false;

    backfillMissingCompletedDates();

    // Run auto-archive automations now that tasks + rules are loaded. Mutates
    // archived tasks synchronously so the renders below exclude them; the
    // persistence runs in the background.
    try { processAutoArchiveRules(); } catch (err) { console.error('[auto-archive] init run failed', err); }

    // console.time('[PERF] Initial Rendering');
    // On a full refresh, always start with a clean slate (no persisted filters).
    // This intentionally ignores any hash query params and clears any saved view state.
    filterState.search = "";
    filterState.statuses.clear();
    filterState.priorities.clear();
    filterState.projects.clear();
    filterState.tags.clear();
    filterState.datePresets.clear();
    filterState.dateFrom = "";
    filterState.dateTo = "";
    filterState.createdFrom = ""; filterState.createdTo = "";
    filterState.updatedFrom = ""; filterState.updatedTo = "";
    filterState.completedFrom = ""; filterState.completedTo = "";

    projectFilterState.search = "";
    projectFilterState.statuses.clear();
    projectFilterState.taskFilter = "";
    projectFilterState.updatedFilter = "all";

    try { localStorage.removeItem('projectsViewState'); } catch (e) {}
    try { localStorage.removeItem('kanbanUpdatedFilter'); } catch (e) {}
    window.kanbanUpdatedFilter = 'all';

    // Allow URL parameters to persist for deep linking and sharing filtered views

    // Check for URL hash
    const hash = window.location.hash.slice(1).split('?')[0];
    const validPages = ['dashboard', 'projects', 'docs', 'tasks', 'updates', 'feedback', 'calendar'];

    // Clear all nav highlights first
    document.querySelectorAll(".nav-item").forEach((nav) => nav.classList.remove("active"));

    if (hash === 'calendar') {
        // Show calendar view directly on initial load
        showCalendarView();
    } else if (hash === 'dashboard/recent_activity') {
        // Handle recent activity subpage
        document.querySelector('.nav-item[data-page="dashboard"]')?.classList.add("active");
        showPage('dashboard/recent_activity');
    } else if (hash === 'projects/archive') {
        document.querySelector('.nav-item[data-page="projects"]')?.classList.add("active");
        showPage('projects');
    } else if (hash.startsWith('project-')) {
        // Handle project detail URLs
        const projectId = parseInt(hash.replace('project-', ''));
        document.querySelector('.nav-item[data-page="projects"]')?.classList.add("active");
        showProjectDetails(projectId);
    } else {
        // Normal page navigation
        const pageToShow = validPages.includes(hash) ? (hash === 'calendar' ? 'tasks' : hash) : 'dashboard';
        const navItem = document.querySelector(`.nav-item[data-page="${pageToShow}"]`);
        if (hash === 'calendar') {
            // Ensure calendar nav is highlighted
            document.querySelector('.nav-item[data-page="calendar"]')?.classList.add('active');
        } else {
            if (navItem) navItem.classList.add("active");
        }
        // Just show the page without rendering - routing below handles the first render
        document.querySelectorAll(".page").forEach((page) => page.classList.remove("active"));
        document.getElementById(pageToShow).classList.add("active");
        if (hash === 'calendar') {
            // Switch to calendar sub-view after tasks page mounts
            document.querySelector('.kanban-board')?.classList.add('hidden');
            document.getElementById('list-view')?.classList.remove('active');
            document.getElementById('calendar-view')?.classList.add('active');
            const viewToggle = document.querySelector('.view-toggle');
            if (viewToggle) viewToggle.classList.add('hidden');
            renderCalendar();
        }
    }

    // Initial rendering is handled by routing below.
    // console.timeEnd('[PERF] Initial Rendering');

    // Initialization complete
    if (typeof updateBootSplashProgress === 'function') {
        updateBootSplashProgress(100); // Complete!
    }

    // console.time('[PERF] Initialize Notifications');
    // Initialize notifications ONCE after app is fully loaded
    // This is much faster than running on every notification state build
    if (!USE_SERVER_NOTIFICATIONS) {
        checkAndCreateDueTodayNotifications();
        cleanupOldNotifications();
    }
    logPerformanceMilestone('init-notifications-ready');
    // console.timeEnd('[PERF] Initialize Notifications');
    // console.timeEnd('[PERF] Paint & Finalize');

    // console.timeEnd('[PERF] Total Init Time');
    debugTimeEnd("init", initTimer, {
        taskCount: tasks.length,
        projectCount: projects.length,
        feedbackCount: feedbackItems.length,
        pendingSaves
    });
    logPerformanceMilestone('init-complete', {
        taskCount: tasks.length,
        projectCount: projects.length,
        feedbackCount: feedbackItems.length
    });
    markPerfOnce('interactive_ready', {
        taskCount: tasks.length,
        projectCount: projects.length
    });

    // Route handler function (used for both initial load and hashchange)
    function handleRouting() {
        const hash = window.location.hash.slice(1); // Remove #

        // Parse hash and query parameters
        const [page, queryString] = hash.split('?');
        const params = new URLSearchParams(queryString || '');

        // Skip auth-related hashes (let auth.js handle these)
        if (page === 'login' || page === 'admin-login' || page === 'setup') {
            return;
        }

        // Clear all nav highlights first
        document.querySelectorAll(".nav-item").forEach((nav) => nav.classList.remove("active"));
        document.querySelectorAll(".nav-section-docs").forEach((section) => section.classList.remove("active"));

        if (page === 'dashboard/recent_activity') {
            projectNavigationReferrer = 'projects'; // Reset referrer when leaving project details
            document.querySelector('.nav-item[data-page="dashboard"]')?.classList.add("active");
            showPage('dashboard/recent_activity');
            previousPage = page;
        } else if (page.startsWith('project-')) {
            const projectId = parseInt(page.replace('project-', ''));
            document.querySelector('.nav-item[data-page="projects"]')?.classList.add("active");
            showProjectDetails(projectId);
            previousPage = page;
        } else if (page.startsWith('task-')) {
            const taskId = parseInt(page.replace('task-', ''));
            document.querySelector('.nav-item[data-page="tasks"]')?.classList.add("active");
            showPage('tasks');
            const task = tasks.find(t => t.id === taskId);
            if (task) openTaskDetails(taskId);
            previousPage = page;
        } else if (page === 'calendar') {
            projectNavigationReferrer = 'projects'; // Reset referrer when leaving project details
            // Avoid thrashing: highlight and ensure calendar is visible
            document.querySelector('.nav-item[data-page="calendar"]')?.classList.add('active');
            showCalendarView();
            previousPage = page;
        } else if (page === 'docs') {
            projectNavigationReferrer = 'projects';
            document.querySelector('.nav-section-docs')?.classList.add("active");
            showPage('docs');
            previousPage = page;
        } else if (page === 'tasks' || page === 'tasks/archive') {
            projectNavigationReferrer = 'projects'; // Reset referrer when leaving project details
            document.querySelector('.nav-item[data-page="tasks"]')?.classList.add("active");

            // Apply ALL filters from URL parameters BEFORE showing page.
            // Only do this when a query string is present — if there are no params
            // (e.g. user clicked the Tasks nav to return), preserve existing filterState
            // so active filters are not silently cleared on navigation.
            if (queryString) {

            // Search filter
            if (params.has('search')) {
                filterState.search = (params.get('search') || '').trim().toLowerCase();
            } else {
                filterState.search = '';
            }

            // Status filters
            if (params.has('status')) {
                const statuses = params.get('status').split(',').filter(Boolean);
                filterState.statuses.clear();
                statuses.forEach(s => filterState.statuses.add(s.trim()));
                filterState.statusExcludeMode = params.get('statusExclude') === '1';
            } else {
                filterState.statuses.clear();
                filterState.statusExcludeMode = false;
            }

            // Priority filters
            if (params.has('priority')) {
                const priorities = params.get('priority').split(',').filter(Boolean);
                filterState.priorities.clear();
                priorities.forEach(p => filterState.priorities.add(p.trim()));
                filterState.priorityExcludeMode = params.get('priorityExclude') === '1';
            } else {
                filterState.priorities.clear();
                filterState.priorityExcludeMode = false;
            }

            // Project filters
            if (params.has('project')) {
                const projectIds = params.get('project').split(',').filter(Boolean);
                filterState.projects.clear();
                projectIds.forEach(id => filterState.projects.add(id.trim()));
                filterState.projectExcludeMode = params.get('projectExclude') === '1';
            } else {
                filterState.projects.clear();
                filterState.projectExcludeMode = false;
            }

            // Tag filters
            if (params.has('tags')) {
                const tags = params.get('tags').split(',').filter(Boolean);
                filterState.tags.clear();
                tags.forEach(t => filterState.tags.add(t.trim()));
                filterState.tagExcludeMode = params.get('tagExclude') === '1';
                filterState.tagAndMode = params.get('tagAnd') === '1';
            } else {
                filterState.tags.clear();
                filterState.tagExcludeMode = false;
                filterState.tagAndMode = false;
            }

            // Date preset filters
            if (params.has('datePreset')) {
                const datePresetParam = params.get('datePreset') || '';
                const presets = datePresetParam.split(',').filter(Boolean);
                filterState.datePresets.clear();
                presets.forEach(p => filterState.datePresets.add(p.trim()));
                // Clear manual date inputs when preset is set
                filterState.dateFrom = '';
                filterState.dateTo = '';
                filterState.dateField = 'endDate'; // Default to end date
            } else if (params.has('dateFrom') || params.has('dateTo')) {
                const dateFrom = params.get('dateFrom') || '';
                const dateTo = params.get('dateTo') || '';
                filterState.dateFrom = dateFrom;
                filterState.dateTo = dateTo;
                filterState.dateField = params.get('dateField') || 'endDate'; // Use startDate or endDate
                // Clear preset when manual dates are set
                filterState.datePresets.clear();
            } else {
                filterState.datePresets.clear();
                filterState.dateFrom = '';
                filterState.dateTo = '';
                filterState.dateField = 'endDate';
            }

            // Task IDs filter (from email notifications) - only via URL, not UI
            // This filter shows only the specified task IDs, bypassing other filters
            if (params.has('taskIds')) {
                const taskIdsParam = params.get('taskIds') || '';
                const ids = taskIdsParam.split(',').filter(Boolean).map(id => id.trim());
                filterState.taskIds = new Set(ids);
                const taskSrc = params.get('taskSrc');
                filterState.taskIdsSource = ['generated', 'report', 'overdue'].includes(taskSrc) ? taskSrc : '';
            } else {
                filterState.taskIds = new Set();
                filterState.taskIdsSource = '';
            }

            // Handle updated filter (last 5 minutes, etc.)
            if (params.has('updated')) {
                const updatedValue = params.get('updated');
                const allowed = new Set(['all', '5m', '30m', '24h', 'week', 'month']);
                if (allowed.has(updatedValue)) {
                    try {
                        setKanbanUpdatedFilter(updatedValue, { render: false });
                    } catch (e) {
                        console.error('Failed to set kanbanUpdatedFilter from URL:', e);
                    }
                }
            }

            // Handle links filter (blocks, is_blocked_by, relates_to, has_web_links, no_links)
            if (params.has('links')) {
                const linksParam = params.get('links') || '';
                const allowedLinks = new Set(['blocks', 'is_blocked_by', 'relates_to', 'has_web_links', 'no_links']);
                const links = linksParam.split(',').filter(Boolean).map(l => l.trim()).filter(l => allowedLinks.has(l));
                filterState.linkTypes = new Set(links);
                // Sync checkboxes
                document.querySelectorAll('[data-filter="link-type"]').forEach(cb => {
                    cb.checked = filterState.linkTypes.has(cb.value);
                });
            } else {
                filterState.linkTypes = new Set();
            }

            // Activity windows (created/updated/completed) — restored from a shared or
            // refreshed Kai result. Anything that isn't a valid ISO date is ignored.
            for (const key of ['createdFrom', 'createdTo', 'updatedFrom', 'updatedTo', 'completedFrom', 'completedTo']) {
                const value = params.get(key) || '';
                filterState[key] = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
            }

            } // end if (queryString)

            // Resolve requested tasks subview from URL.
            // Use deterministic class switching instead of synthetic button clicks,
            // which can race when returning from calendar/project details flows.
            const requestedTasksView = (params.get('view') || '').toLowerCase();

            // Now show the page (which will render with updated filters and handle view logic)
            showPage('tasks');

            // Enforce subview immediately from URL to avoid stale calendar state.
            if (requestedTasksView === 'list') {
                document.querySelectorAll('.view-btn').forEach((b) => b.classList.remove('active'));
                const listBtn = document.querySelector('.view-btn[data-view="list"]');
                if (listBtn) listBtn.classList.add('active');

                document.querySelector('.kanban-board')?.classList.add('hidden');
                document.getElementById('calendar-view')?.classList.remove('active');
                document.getElementById('list-view')?.classList.add('active');
                const archivedViewEl = document.getElementById('archived-view');
                if (archivedViewEl) {
                    archivedViewEl.style.display = 'none';
                    archivedViewEl.classList.remove('active');
                }
                document.getElementById('archived-view-btn')?.classList.remove('active');
                document.querySelector('.kanban-header')?.classList.remove('calendar-mode');

                const globalFilters = document.getElementById('global-filters');
                if (globalFilters) globalFilters.style.display = '';

                const kanbanSettingsContainer = document.getElementById('kanban-settings-btn')?.parentElement;
                if (kanbanSettingsContainer) kanbanSettingsContainer.style.display = 'none';

                const backlogBtn = document.getElementById('backlog-quick-btn');
                if (backlogBtn) backlogBtn.style.display = 'none';

                const archivedViewBtnEl = document.getElementById('archived-view-btn');
                if (archivedViewBtnEl) archivedViewBtnEl.style.display = '';

                renderListView();
            } else if (requestedTasksView === 'kanban') {
                document.querySelectorAll('.view-btn').forEach((b) => b.classList.remove('active'));
                const kanbanBtn = document.querySelector('.view-btn[data-view="kanban"]');
                if (kanbanBtn) kanbanBtn.classList.add('active');

                document.querySelector('.kanban-board')?.classList.remove('hidden');
                document.getElementById('calendar-view')?.classList.remove('active');
                document.getElementById('list-view')?.classList.remove('active');
                const archivedViewEl = document.getElementById('archived-view');
                if (archivedViewEl) {
                    archivedViewEl.style.display = 'none';
                    archivedViewEl.classList.remove('active');
                }
                document.getElementById('archived-view-btn')?.classList.remove('active');
                document.querySelector('.kanban-header')?.classList.remove('calendar-mode');

                const kanbanSettingsContainer = document.getElementById('kanban-settings-btn')?.parentElement;
                if (kanbanSettingsContainer) kanbanSettingsContainer.style.display = '';

                const backlogBtn = document.getElementById('backlog-quick-btn');
                if (backlogBtn) backlogBtn.style.display = 'inline-flex';

                const archivedViewBtnEl = document.getElementById('archived-view-btn');
                if (archivedViewBtnEl) archivedViewBtnEl.style.display = '';

                renderTasks();
            } else if (requestedTasksView === 'calendar') {
                showCalendarView();
            }
            if (page === 'tasks/archive') {
                showArchivedTasksView('kanban');
            }

            // Update ALL filter UI inputs after page is shown (use setTimeout to ensure DOM is ready)
            setTimeout(() => {
                // Search input - always update to match filterState
                const searchEl = document.getElementById('filter-search');
                if (searchEl) searchEl.value = filterState.search;

                // Status checkboxes
                document.querySelectorAll('input[data-filter="status"]').forEach(cb => {
                    cb.checked = filterState.statuses.has(cb.value);
                });

                // Priority checkboxes
                document.querySelectorAll('input[data-filter="priority"]').forEach(cb => {
                    cb.checked = filterState.priorities.has(cb.value);
                });

                // Project checkboxes
                document.querySelectorAll('input[data-filter="project"]').forEach(cb => {
                    cb.checked = filterState.projects.has(cb.value);
                });

                // Tag checkboxes
                document.querySelectorAll('input[data-filter="tag"]').forEach(cb => {
                    cb.checked = filterState.tags.has(cb.value);
                });

                // Date preset checkboxes
                document.querySelectorAll('input[data-filter="date-preset"]').forEach(cb => {
                    cb.checked = filterState.datePresets.has(cb.value);
                });

                // Link type checkboxes
                document.querySelectorAll('input[data-filter="link-type"]').forEach(cb => {
                    cb.checked = filterState.linkTypes.has(cb.value);
                });

                // Date range inputs
                const dateFromEl = document.getElementById('filter-date-from');
                const dateToEl = document.getElementById('filter-date-to');

                if (dateFromEl && filterState.dateFrom) {
                    dateFromEl.value = filterState.dateFrom;
                    const displayInput = dateFromEl.closest('.date-input-wrapper')?.querySelector('.date-display');
                    if (displayInput) displayInput.value = formatDate(filterState.dateFrom);
                }

                if (dateToEl && filterState.dateTo) {
                    dateToEl.value = filterState.dateTo;
                    const displayInput = dateToEl.closest('.date-input-wrapper')?.querySelector('.date-display');
                    if (displayInput) displayInput.value = formatDate(filterState.dateTo);
                }

                // Update filter badges and active chips to reflect URL filters
                updateAllFilterModeUI();
                updateTagMatchToggleUI();
                updateFilterBadges();
                renderActiveFilterChips();
                updateClearButtonVisibility();

                // Auto-open task modal if task= param is present (e.g. after refresh)
                if (params.has('task')) {
                    const taskIdToOpen = parseInt(params.get('task'));
                    const taskToOpen = tasks.find(t => t.id === taskIdToOpen);
                    if (taskToOpen) openTaskDetails(taskIdToOpen);
                }
            }, 100);

            previousPage = page;
        } else if (page === 'projects' || page === 'projects/archive') {
            projectNavigationReferrer = 'projects'; // Reset referrer when leaving project details
            document.querySelector('.nav-item[data-page="projects"]')?.classList.add("active");
            const isProjectArchiveRoute = page === 'projects/archive';

            // Parse URL parameters for deep linking (similar to Tasks page)
            const urlProjectFilters = {};

            if (params.has('search')) {
                urlProjectFilters.search = params.get('search');
            }

            // Status filter (comma-separated: backlog, planning, active, completed).
            // 'archived' is intentionally NOT a valid status — archived projects
            // are reached only via the #projects/archive route.
            if (params.has('status')) {
                const statuses = params.get('status').split(',').filter(Boolean);
                const validStatuses = ['backlog', 'planning', 'active', 'completed'];
                urlProjectFilters.statuses = statuses.filter(s => validStatuses.includes(s.trim()));
            } else {
                urlProjectFilters.statuses = [];
            }

            if (isProjectArchiveRoute) {
                urlProjectFilters.isArchivedRoute = true;
            }

            // Status exclude mode
            if (params.has('statusExclude')) {
                urlProjectFilters.statusExcludeMode = params.get('statusExclude') === '1';
            }

            // Tags filter (comma-separated)
            if (params.has('tags')) {
                const tags = params.get('tags').split(',').filter(Boolean);
                urlProjectFilters.tags = tags;
            }

            // Tag exclude mode
            if (params.has('tagExclude')) {
                urlProjectFilters.tagExcludeMode = params.get('tagExclude') === '1';
            }

            // Chip filter (has-tasks or no-tasks)
            if (params.has('filter')) {
                const filter = params.get('filter');
                const validFilters = ['has-tasks', 'no-tasks'];
                if (validFilters.includes(filter)) {
                    urlProjectFilters.filter = filter;
                }
            }

            if (params.has('sort')) {
                const sort = params.get('sort');
                const validSorts = ['default', 'status', 'name', 'created-desc', 'updated-desc', 'tasks-desc', 'completion-desc'];
                if (validSorts.includes(sort)) {
                    urlProjectFilters.sort = sort === 'default' ? 'status' : sort;
                }
            }

            if (params.has('sortDirection')) {
                const sortDirection = params.get('sortDirection');
                if (sortDirection === 'asc' || sortDirection === 'desc') {
                    urlProjectFilters.sortDirection = sortDirection;
                }
            }

            if (params.has('updatedFilter')) {
                const updatedFilter = params.get('updatedFilter');
                const validUpdatedFilters = ['all', '5m', '30m', '24h', 'week', 'month'];
                if (validUpdatedFilters.includes(updatedFilter)) {
                    urlProjectFilters.updatedFilter = updatedFilter;
                }
            }

            // Completion bounds (0-100). Anything out of range is ignored rather than clamped.
            ['completionMin', 'completionMax'].forEach((key) => {
                if (!params.has(key)) return;
                const value = Number.parseInt(params.get(key), 10);
                if (Number.isFinite(value) && value >= 0 && value <= 100) {
                    urlProjectFilters[key] = value;
                }
            });

            // Focused project-ID filter (from Ask Kai "Ver" navigation) - URL only, like tasks taskIds
            if (params.has('projectIds')) {
                const projectIdsParam = params.get('projectIds') || '';
                urlProjectFilters.projectIds = projectIdsParam.split(',').map(id => id.trim()).filter(Boolean);
                urlProjectFilters.projectIdsSource = params.get('projectSrc') === 'report' ? 'report' : '';
            }

            // Read view preference from URL and persist it
            if (params.has('view')) {
                const view = params.get('view');
                if (view === 'rows' || view === 'cards') {
                    localStorage.setItem('projectsViewMode', view);
                }
            }

            // Store URL filters temporarily for setupProjectsControls to use
            window.urlProjectFilters = urlProjectFilters;

            showPage('projects');
            previousPage = page;
        } else if (page === 'automations') {
            projectNavigationReferrer = 'projects';
            document.querySelector('.nav-item[data-page="automations"]')?.classList.add("active");
            showPage('automations');
            previousPage = page;
        } else if (page === 'updates') {
            projectNavigationReferrer = 'projects'; // Reset referrer when leaving project details
            document.querySelector('.nav-item[data-page="updates"]')?.classList.add("active");
            showPage('updates');
            previousPage = page;
        } else if (page === 'feedback') {
            projectNavigationReferrer = 'projects'; // Reset referrer when leaving project details
            document.querySelector('.nav-item[data-page="feedback"]')?.classList.add("active");
            showPage('feedback');
            previousPage = page;
        } else if (page === '' || page === 'dashboard') {
            projectNavigationReferrer = 'projects'; // Reset referrer when leaving project details
            document.querySelector('.nav-item[data-page="dashboard"]')?.classList.add("active");
            showPage('dashboard');
            previousPage = page || 'dashboard';
        }
    }

    // Handle initial URL on page load
    handleRouting();

    markPerfOnce('first_contentful_page_ready', {
        page: document.querySelector('.page.active')?.id || 'unknown'
    });
    lastDataFingerprint = buildDataFingerprint({ tasks, projects, feedbackItems });
    lastCalendarFingerprint = buildCalendarFingerprint(tasks, projects);
    markPerfOnce('init-render-complete');

    // Ensure the first render is actually painted before we claim "ready"
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    markPerfOnce('init-first-paint');

    // Add hashchange event listener for URL routing.
    // Remove any previous listener first (guards against re-init on user switch
    // adding a second stale listener that would cause double renders).
    if (activeHandleRouting) {
        window.removeEventListener('hashchange', activeHandleRouting);
    }
    activeHandleRouting = handleRouting;
    window.addEventListener('hashchange', handleRouting);

    window.addEventListener('storage:unhealthy', () => {
        _storageHealthy = false;
        showCfOutageBanner();
    });
    window.addEventListener('storage:healthy', () => {
        _storageHealthy = true;
        hideCfOutageBanner();
    });

    // View switching between Kanban and List
    document.querySelectorAll(".view-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            document.querySelectorAll(".view-btn").forEach((b) => b.classList.remove("active"));
            const button = e.currentTarget;
            button.classList.add("active");

            const view = (button.dataset.view || '').toLowerCase();
            try {
                const backlogBtn = document.getElementById('backlog-quick-btn');
                if (backlogBtn) backlogBtn.style.display = (view === 'kanban') ? 'inline-flex' : 'none';
            } catch (e) {}

            document.querySelector(".kanban-board").classList.add("hidden");
            document.getElementById("list-view").classList.remove("active");
            document.getElementById("calendar-view").classList.remove("active");
            const archivedViewEl = document.getElementById("archived-view");
            if (archivedViewEl) {
                archivedViewEl.style.display = 'none';
                archivedViewEl.classList.remove('active');
            }
            const archivedViewBtnEl = document.getElementById("archived-view-btn");
            if (archivedViewBtnEl) archivedViewBtnEl.classList.remove("active");

            // Restore "All Tasks" title when leaving calendar
            const pageTitle = document.querySelector('#tasks .page-title');
            if (pageTitle) pageTitle.textContent = t('tasks.title');
            const pageSubtitle = document.querySelector('#tasks .page-subtitle');
            if (pageSubtitle) pageSubtitle.textContent = t('tasks.subtitle');

            if (view === "list") {
                // Show filters in list view
                const globalFilters = document.getElementById('global-filters');
                if (globalFilters) globalFilters.style.display = '';
                document.getElementById("list-view").classList.add("active");
                renderListView();
                updateSortUI();
                // ensure kanban header is in default state
                try{ document.querySelector('.kanban-header')?.classList.remove('calendar-mode'); }catch(e){}
                // Hide kanban settings in list view
                const kanbanSettingsContainer = document.getElementById('kanban-settings-btn')?.parentElement;
                if (kanbanSettingsContainer) kanbanSettingsContainer.style.display = 'none';
                // Restore Archived button (hidden in calendar view)
                const archivedViewBtnEl = document.getElementById('archived-view-btn');
                if (archivedViewBtnEl) archivedViewBtnEl.style.display = '';
                // Update URL to include view parameter
                syncURLWithFilters();
                try { applyBacklogFilterVisibility(); } catch (e) {}
            } else if (view === "kanban") {
                // Show backlog button in kanban view
                const backlogBtn = document.getElementById('backlog-quick-btn');
                if (backlogBtn) backlogBtn.style.display = 'inline-flex';
                // Show filters in kanban view
                const globalFilters = document.getElementById('global-filters');
                if (globalFilters) globalFilters.style.display = '';
                document.querySelector(".kanban-board").classList.remove("hidden");
                renderTasks();
                updateSortUI();
                // ensure kanban header is in default state
                try{ document.querySelector('.kanban-header')?.classList.remove('calendar-mode'); }catch(e){}
                // Show kanban settings in kanban view
                const kanbanSettingsContainer = document.getElementById('kanban-settings-btn')?.parentElement;
                if (kanbanSettingsContainer) kanbanSettingsContainer.style.display = '';
                // Restore Archived button (hidden in calendar view)
                const archivedViewBtnEl = document.getElementById('archived-view-btn');
                if (archivedViewBtnEl) archivedViewBtnEl.style.display = '';
                // Update URL to remove view parameter (kanban is default)
                syncURLWithFilters();
                try { applyBacklogFilterVisibility(); } catch (e) {}
            }
        });
    });

    // Archived: standalone button — switches to the dedicated archived list view
    const archivedViewBtn = document.getElementById('archived-view-btn');
    if (archivedViewBtn && !archivedViewBtn.dataset.bound) {
        archivedViewBtn.dataset.bound = '1';
        archivedViewBtn.addEventListener('click', () => {
            // Remember which view to return to via the Back button
            const activeViewBtn = document.querySelector('.view-btn.active');
            _viewBeforeArchived = activeViewBtn ? (activeViewBtn.dataset.view || 'kanban') : 'kanban';
            if (window.location.hash !== '#tasks/archive') {
                window.location.hash = 'tasks/archive';
            } else {
                showArchivedTasksView(_viewBeforeArchived);
            }
        });
    }

    // Archived view: Back button — restore chrome and return to the previous view
    const archivedBackBtn = document.getElementById('archived-back-btn');
    if (archivedBackBtn && !archivedBackBtn.dataset.bound) {
        archivedBackBtn.dataset.bound = '1';
        archivedBackBtn.addEventListener('click', () => {
            const kanbanHeader = document.querySelector('#tasks .kanban-header');
            if (kanbanHeader) kanbanHeader.style.display = '';
            const target = document.querySelector(`.view-btn[data-view="${_viewBeforeArchived || 'kanban'}"]`)
                || document.querySelector('.view-btn[data-view="kanban"]');
            if (target) target.click();
        });
    }

    // Kanban quick access: open Backlog in List view
    try {
        const existing = document.getElementById('backlog-quick-btn');
        const viewToggle = document.querySelector('.kanban-header .view-toggle');
        if (!existing && viewToggle && viewToggle.parentElement) {
            const backlogBtn = document.createElement('button');
            backlogBtn.type = 'button';
            backlogBtn.id = 'backlog-quick-btn';
            backlogBtn.className = 'backlog-quick-btn';
            backlogBtn.title = t('tasks.backlogQuickTitle');
            backlogBtn.textContent = t('tasks.backlogQuickLabel');
            backlogBtn.addEventListener('click', (evt) => {
                evt.preventDefault();
                window.location.hash = '#tasks?view=list&status=backlog';
            });
            viewToggle.insertAdjacentElement('afterend', backlogBtn);

            // Only show in Kanban view — also guard against calendar context (hash may load before button exists)
            const activeView = (document.querySelector('.view-btn.active')?.dataset.view || '').trim().toLowerCase();
            const currentHash = window.location.hash.slice(1);
            const isCalendarContext = currentHash === 'calendar' || currentHash.includes('view=calendar');
            backlogBtn.style.display = (!isCalendarContext && activeView === 'kanban') ? 'inline-flex' : 'none';
        }

    } catch (e) {}
    
    // Setup dashboard interactions
    setupDashboardInteractions();
}

function setupDashboardInteractions() {
    const kaiInput = document.getElementById('dash-kai-input');
    if (kaiInput && !kaiInput.dataset.enterBound) {
        kaiInput.dataset.enterBound = '1';
        kaiInput.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' || event.isComposing) return;
            event.preventDefault();
            dashKaiSubmit();
        });
    }

    // Time filter chips
    const filterChips = document.querySelectorAll('.filter-chip');
    filterChips.forEach(chip => {
        chip.addEventListener('click', function() {
            filterChips.forEach(c => c.classList.remove('active'));
            this.classList.add('active');
            const period = this.dataset.period;
            updateDashboardForPeriod(period);
        });
    });
    
    // Enhanced stat card interactions
    const statCards = document.querySelectorAll('.enhanced-stat-card');
    statCards.forEach(card => {
        // Hover effects
        card.addEventListener('mouseenter', function() {
            this.style.transform = 'translateY(-4px) scale(1.02)';
        });
        
        card.addEventListener('mouseleave', function() {
            this.style.transform = 'translateY(0) scale(1)';
        });
        
        // Click to navigate to filtered tasks
        card.addEventListener('click', function() {
            const filterType = this.dataset.filter;
            const filterValue = this.dataset.value;
            
            if (filterType && filterValue) {
                navigateToFilteredTasks(filterType, filterValue);
            }
        });
    });
    
    // Quick action interactions
    const quickActions = document.querySelectorAll('.quick-action');
    quickActions.forEach(action => {
        action.addEventListener('click', function() {
            this.style.transform = 'scale(0.95)';
            setTimeout(() => {
                this.style.transform = 'scale(1)';
            }, 150);
        });
    });

    // Completion ring click handlers - navigate to tasks/projects pages
    const tasksCompletionRing = document.getElementById('tasks-completion-ring');
    if (tasksCompletionRing) {
        tasksCompletionRing.addEventListener('click', function() {
            window.location.hash = 'tasks';
            document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
            const tasksNavItem = document.querySelector('.nav-item[data-page="tasks"]');
            if (tasksNavItem) tasksNavItem.classList.add('active');
            showPage('tasks');
        });
    }

    const projectsCompletionRing = document.getElementById('projects-completion-ring');
    if (projectsCompletionRing) {
        projectsCompletionRing.addEventListener('click', function() {
            window.location.hash = 'projects';
            document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
            const projectsNavItem = document.querySelector('.nav-item[data-page="projects"]');
            if (projectsNavItem) projectsNavItem.classList.add('active');
            showPage('projects');
        });
    }
}

function navigateToFilteredTasks(filterType, filterValue) {
    // Navigate to tasks page
    window.location.hash = 'tasks';
    
    // Clear all nav highlights and set tasks as active
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    const tasksNavItem = document.querySelector('.nav-item[data-page="tasks"]');
    if (tasksNavItem) tasksNavItem.classList.add('active');
    
    // Show tasks page
    showPage('tasks');
    
    // Ensure view toggle is visible
    const viewToggle = document.querySelector('.view-toggle');
    if (viewToggle) viewToggle.classList.remove('hidden');
    
    // Switch to Kanban view
    document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
    const kanbanBtn = document.querySelector('.view-btn:first-child');
    if (kanbanBtn) kanbanBtn.classList.add('active');
    
    // Show kanban, hide list and calendar
    const kanbanBoard = document.querySelector('.kanban-board');
    const listView = document.getElementById('list-view');
    const calendarView = document.getElementById('calendar-view');
    
    if (kanbanBoard) kanbanBoard.classList.remove('hidden');
    if (calendarView) calendarView.classList.remove('active');
    if (listView) listView.classList.remove('active');

    // ensure header returns to default when navigating to filtered Kanban
    try{ document.querySelector('.kanban-header')?.classList.remove('calendar-mode'); }catch(e){}
    
    // Clear existing filters first
    clearAllFilters();
    
    // Apply the specific filter
    setTimeout(() => {
        applyDashboardFilter(filterType, filterValue);
        renderTasks(); // Use renderTasks for Kanban view
    }, 100);
}

function applyDashboardFilter(filterType, filterValue) {
    if (filterType === 'status') {
        // Apply status filter
        filterState.statuses.clear();
        filterState.statuses.add(filterValue);
        filterState.statusExcludeMode = false;
        filterState.priorityExcludeMode = false;
        filterState.projectExcludeMode = false;
        filterState.tagExcludeMode = false;
        
        // Update status filter UI
        const statusCheckboxes = document.querySelectorAll('input[data-filter="status"]');
        statusCheckboxes.forEach(checkbox => {
            checkbox.checked = checkbox.value === filterValue;
        });
    } else if (filterType === 'priority') {
        // Apply priority filter
        filterState.priorities.clear();
        filterState.priorities.add(filterValue);
        filterState.priorityExcludeMode = false;
        filterState.projectExcludeMode = false;
        filterState.tagExcludeMode = false;
        
        // Also exclude completed tasks for priority filters
        filterState.statuses.clear();
        filterState.statuses.add('todo');
        filterState.statuses.add('progress');
        filterState.statuses.add('review');
        filterState.statusExcludeMode = false;
        
        // Update priority filter UI
        const priorityCheckboxes = document.querySelectorAll('input[data-filter="priority"]');
        priorityCheckboxes.forEach(checkbox => {
            checkbox.checked = checkbox.value === filterValue;
        });
        
        // Update status checkboxes
        const statusCheckboxes = document.querySelectorAll('input[data-filter="status"]');
        statusCheckboxes.forEach(checkbox => {
            checkbox.checked = ['todo', 'progress', 'review'].includes(checkbox.value);
        });
    } else if (filterType === 'overdue') {
        // Strictly overdue means due before today, not due today.
        const { isoDate } = getNowPartsInUserTimeZone();
        const yesterday = new Date(`${isoDate}T00:00:00Z`);
        yesterday.setUTCDate(yesterday.getUTCDate() - 1);
        const overdueCutoff = yesterday.toISOString().slice(0, 10);
        filterState.dateTo = overdueCutoff;

        // Update the date filter inputs
        const dateToEl = document.getElementById('filter-date-to');
        if (dateToEl) {
            dateToEl.value = overdueCutoff;
        }
        
        // Exclude completed tasks
        filterState.statuses.clear();
        filterState.statuses.add('todo');
        filterState.statuses.add('progress');
        filterState.statuses.add('review');
        filterState.statusExcludeMode = false;
        filterState.priorityExcludeMode = false;
        filterState.projectExcludeMode = false;
        filterState.tagExcludeMode = false;
        
        // Update status checkboxes
        const statusCheckboxes = document.querySelectorAll('input[data-filter="status"]');
        statusCheckboxes.forEach(checkbox => {
            checkbox.checked = ['todo', 'progress', 'review'].includes(checkbox.value);
        });
    } else if (filterType === 'dueToday') {
        const today = new Date().toISOString().split('T')[0];
        filterState.dateFrom = today;
        filterState.dateTo = today;

        const dateFromEl = document.getElementById('filter-date-from');
        const dateToEl = document.getElementById('filter-date-to');
        if (dateFromEl) dateFromEl.value = today;
        if (dateToEl) dateToEl.value = today;

        filterState.statuses.clear();
        filterState.statuses.add('todo');
        filterState.statuses.add('progress');
        filterState.statuses.add('review');
        filterState.statusExcludeMode = false;
        filterState.priorityExcludeMode = false;
        filterState.projectExcludeMode = false;
        filterState.tagExcludeMode = false;

        const statusCheckboxes = document.querySelectorAll('input[data-filter="status"]');
        statusCheckboxes.forEach(checkbox => {
            checkbox.checked = ['todo', 'progress', 'review'].includes(checkbox.value);
        });
    } else if (filterType === 'completedRecently') {
        // Done tasks whose completedDate falls in the current calendar month — the same
        // window the dashboard "Completed" tile counts.
        const { isoDate } = getNowPartsInUserTimeZone();
        filterState.completedFrom = currentMonthStartIso(isoDate);
        filterState.completedTo = isoDate;

        filterState.statuses.clear();
        filterState.statuses.add('done');
        filterState.statusExcludeMode = false;
        filterState.priorityExcludeMode = false;
        filterState.projectExcludeMode = false;
        filterState.tagExcludeMode = false;

        const statusCheckboxes = document.querySelectorAll('input[data-filter="status"]');
        statusCheckboxes.forEach(checkbox => {
            checkbox.checked = checkbox.value === 'done';
        });
    }

    // Update filter badges and render
    updateFilterBadges();
    renderAfterFilterChange();
}

function updateDashboardForPeriod(period) {
    // This function would filter data based on the selected period
    // For now, we'll just update the stats labels to show the period
    const periodLabels = {
        week: t('dashboard.trend.thisWeek'),
        month: t('dashboard.trend.thisMonth'),
        quarter: t('dashboard.trend.thisQuarter')
    };
    
    const label = periodLabels[period] || t('dashboard.trend.thisWeek');
    
    // Update trend text with the selected period
    const progressChange = document.getElementById('progress-change');
    const completedChange = document.getElementById('completed-change');
    
    if (progressChange) {
        progressChange.textContent = `+${Math.max(1, Math.floor(tasks.length * 0.1))} ${label}`;
    }
    if (completedChange) {
        completedChange.textContent = `+${tasks.filter(t => t.status === 'done').length} ${label}`;
    }
    
    // Could add more sophisticated filtering here based on actual dates
}

function setupNavigation() {
    document.querySelectorAll(".nav-item").forEach((item) => {
        item.addEventListener("click", (event) => {
            const page = item.dataset.page;
            if (page) {
                if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                }
                event.preventDefault();
                if (page === 'calendar') {
                    // Calendar nav: do not hard switch to tasks every time; call dedicated handler
                    showCalendarView();
                    // Highlight calendar nav explicitly
                    document.querySelectorAll('.nav-item').forEach((nav) => nav.classList.remove('active'));
                    item.classList.add('active');
                    return;
                }

                // CRITICAL: If navigating to Tasks, remove calendar/list active classes FIRST
                if (page === "tasks") {
                    document.getElementById("calendar-view")?.classList.remove("active");
                    document.getElementById("list-view")?.classList.remove("active");
                }

                // Update URL hash for bookmarking
                window.location.hash = page;

                // Filters are preserved when navigating back to Tasks so the user
                // doesn't lose their active filter state on every page switch.

                // Clear all nav highlights and set the clicked one
                document.querySelectorAll(".nav-item").forEach((nav) => nav.classList.remove("active"));
                item.classList.add("active");

                showPage(page);

                // Show view toggle only for tasks page
                const viewToggle = document.querySelector(".view-toggle");
                if (viewToggle) {
                    if (page === "tasks") {
                        viewToggle.classList.remove("hidden");
                    } else {
                        viewToggle.classList.add("hidden");
                    }
                }
            }
        });
    });
}

async function showPage(pageId) {
    // Handle subpages (like dashboard/recent_activity)
    if (pageId.includes('/')) {
        const [mainPage, subPage] = pageId.split('/');
        if (mainPage === 'dashboard' && subPage === 'recent_activity') {
            showRecentActivityPage();
            return;
        }
    }

    // Scroll to top on mobile when switching pages (ensures mobile header is visible)
    if (getIsMobileCached()) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Hide ALL pages including project-details
    if (docsWorkspaceActive && pageId !== "docs") {
        cancelPendingDocsCheckboxAutoSave();
        cancelPendingDocsContentAutoSave();
        const canContinue = await saveActiveDocIfDirty({ silent: true });
        if (!canContinue) {
            restoreCurrentDocsRoute();
            document.querySelectorAll(".nav-item").forEach((nav) => nav.classList.remove("active"));
            document.querySelectorAll(".nav-section-docs").forEach((section) => section.classList.remove("active"));
            document.querySelector('.nav-section-docs')?.classList.add("active");
            return false;
        }
        teardownDocsWorkspace();
        resetDocsBulkSelection();
    }
    document.querySelectorAll(".page").forEach((page) => page.classList.remove("active"));
    document.getElementById("project-details").classList.remove("active");

    // Hide activity page when returning to main dashboard
    const activityPage = document.getElementById('activity-page');
    if (activityPage) activityPage.style.display = 'none';
    const dashboardGrid = document.querySelector('.dashboard-grid');
    if (dashboardGrid) dashboardGrid.style.display = 'grid';
    const insightsCard = document.querySelector('.insights-card');
    if (insightsCard) insightsCard.style.display = '';

    // Restore user menu when exiting project details
    const userMenu = document.querySelector(".user-menu");
    if (userMenu) userMenu.style.display = "";

    // Show the requested page
    document.getElementById(pageId).classList.add("active");

    // Clear docs nav active state when leaving docs
    if (pageId !== "docs") {
        updateDocsNavTree(null);
    }

    if (pageId === "dashboard") {
        updateCounts();
        // Only render if not already rendered during initialization
        // This prevents duplicate renders when handleRouting() is called right after renderWithoutCalendar()
        // dashboardRendered is set to true after the first render completes
        if (!dashboardRendered) {
            renderDashboard();
        } else {
            // Reset flag so future navigations can render (e.g., coming back from another page)
            dashboardRendered = false;
        }
    } else if (pageId === "projects") {
        updateCounts();
        // Don't call renderProjects() here - setupProjectsControls() handles initial render with filters applied
        // Initialize projects header controls fresh whenever Projects page is shown
        try {
            populateProjectTagOptions();
            setupProjectsControls();
        } catch (e) { /* ignore */ }
    } else if (pageId === "automations") {
        updateCounts();
        renderAutomations();
    } else if (pageId === "docs") {
        updateCounts();
        // Reset .app scroll offset so the sidebar resizer is always accessible.
        // #docs.page.active is height:100vh, which clamps .app content to 100vh.
        // If .app had a positive scrollTop from another page, the layout would be
        // shifted up, displacing the resizer outside the reachable pointer area.
        const appEl = document.querySelector('.app');
        if (appEl) appEl.scrollTop = 0;
        renderDocsPage();
    } else if (pageId === "tasks") {
        updateCounts();
        const globalFilters = document.getElementById('global-filters');
        if (globalFilters) globalFilters.style.display = '';

        // Calendar and non-task pages hide this control. Restore it for every
        // Tasks entry point, including Ask Kai navigation which calls showPage()
        // directly instead of going through the sidebar click handler.
        document.querySelector('.view-toggle')?.classList.remove('hidden');

        // Check if URL has a view parameter - if not, default to Kanban
        // This ensures "All Tasks" nav always defaults to Kanban view regardless of previous page
        const hash = window.location.hash.slice(1);
        const [hashPage, queryString] = hash.split('?');
        const params = new URLSearchParams(queryString || '');
        if (hashPage === 'calendar' || (params.get('view') || '').toLowerCase() === 'calendar') {
            showCalendarView();
            return;
        }
        const hasViewParam = params.has('view');
        const requestedView = (params.get('view') || '').toLowerCase();

        // Always reset to Kanban when no view parameter (e.g., clicking "All Tasks" from nav)
        if (!hasViewParam) {
            document.querySelectorAll(".view-btn").forEach((b) => b.classList.remove("active"));
            document.querySelector(".view-btn:nth-child(1)").classList.add("active");
            document.querySelector(".kanban-board").classList.remove("hidden");
            document.getElementById("list-view").classList.remove("active");
            document.getElementById("calendar-view").classList.remove("active");
            // Leave the archived view if it was open and restore the toolbar
            const archivedViewEl = document.getElementById("archived-view");
            if (archivedViewEl) {
                archivedViewEl.style.display = 'none';
                archivedViewEl.classList.remove('active');
            }
            const archivedViewBtnEl = document.getElementById("archived-view-btn");
            if (archivedViewBtnEl) {
                archivedViewBtnEl.classList.remove("active");
                archivedViewBtnEl.style.display = '';
            }
            const kanbanHeaderEl = document.querySelector('#tasks .kanban-header');
            if (kanbanHeaderEl) kanbanHeaderEl.style.display = '';
            // Restore "All Tasks" title
            const pageTitle = document.querySelector('#tasks .page-title');
            if (pageTitle) pageTitle.textContent = t('tasks.title');
            const pageSubtitle = document.querySelector('#tasks .page-subtitle');
            if (pageSubtitle) pageSubtitle.textContent = t('tasks.subtitle');
            // ensure header is in default (kanban) layout so Add Task stays right-aligned
            try{ document.querySelector('.kanban-header')?.classList.remove('calendar-mode'); }catch(e){}
            // Show kanban settings in tasks kanban view
            const kanbanSettingsContainer = document.getElementById('kanban-settings-btn')?.parentElement;
            if (kanbanSettingsContainer) kanbanSettingsContainer.style.display = '';
            // Show backlog button in kanban view
            const backlogBtn = document.getElementById('backlog-quick-btn');
            if (backlogBtn) backlogBtn.style.display = 'inline-flex';
        } else if (requestedView !== 'calendar') {
            // Defensive reset: when URL asks for list/kanban, never keep stale calendar active.
            document.getElementById("calendar-view").classList.remove("active");
            const archivedViewEl = document.getElementById("archived-view");
            if (archivedViewEl) {
                archivedViewEl.style.display = 'none';
                archivedViewEl.classList.remove('active');
            }
            const archivedViewBtnEl = document.getElementById("archived-view-btn");
            if (archivedViewBtnEl) {
                archivedViewBtnEl.classList.remove("active");
                archivedViewBtnEl.style.display = '';
            }
            try { document.querySelector('.kanban-header')?.classList.remove('calendar-mode'); } catch (e) {}
        }

        renderTasks();
        renderListView();
    } else if (pageId === "updates") {
        updateCounts();
        renderUpdatesPage();
        markLatestReleaseSeen();
    } else if (pageId === "feedback") {
        renderFeedback();
    }
    try { updateAskKaiActionUI(); } catch (e) { /* ignore */ }
    return true;
}

let renderCallsThisTick = 0;
let renderResetScheduled = false;
let renderExtraCallLogged = false;

function trackRenderCall() {
    renderCallsThisTick += 1;
    if (renderCallsThisTick > 1 && !renderExtraCallLogged && isDebugLogsEnabled()) {
        renderExtraCallLogged = true;
        console.warn('[perf] render called multiple times in the same tick', new Error().stack);
    }
    if (!renderResetScheduled) {
        renderResetScheduled = true;
        queueMicrotask(() => {
            renderCallsThisTick = 0;
            renderResetScheduled = false;
        });
    }
}

function render() {
    console.log('🕒 TIMING: render() called');
    trackRenderCall();
    renderActivePageOnly();
}

function renderAppVersionLabel() {
    const label = APP_VERSION_LABEL;
    document.querySelectorAll('.app-version').forEach((el) => (el.textContent = label));
    document.querySelectorAll('.mobile-version').forEach((el) => (el.textContent = label));
}

function renderReleaseSectionList(items) {
    const normalizedItems = (items || []).map((item) => resolveReleaseText(item)).filter(Boolean);
    if (!normalizedItems.length) {
        return `<div class="release-section-empty">${t('updates.sectionEmpty')}</div>`;
    }
    return `
        <ul class="release-list">
            ${normalizedItems.map((item) => `<li><span class="release-point-icon" aria-hidden="true"></span><span>${escapeHtml(item)}</span></li>`).join('')}
        </ul>
    `;
}

function renderUpdatesPage() {
    const container = document.getElementById('updates-content');
    if (!container) return;
    const releases = getSortedReleaseNotes();
    if (releases.length === 0) {
        container.innerHTML = `<div class="updates-empty">${t('updates.empty')}</div>`;
        return;
    }

    const latest = releases[0];
    const history = releases.slice(1);
    const latestTitle = [latest.version, resolveReleaseText(latest.title)].filter(Boolean).join(' - ');
    const latestMeta = t('notifications.releaseMeta', { date: formatReleaseDate(latest.date) });
    const latestSummary = latest.summary ? escapeHtml(resolveReleaseText(latest.summary)) : '';
    const sections = latest.sections || {};
    const sectionDefs = [
        { key: 'new', label: t('updates.sections.new') },
        { key: 'improvements', label: t('updates.sections.improvements') },
        { key: 'fixes', label: t('updates.sections.fixes') }
    ];
    const sectionHtml = sectionDefs.map((def) => {
        const items = sections[def.key] || [];
        return `
            <div class="release-section-card release-section-card--${def.key}">
                <div class="release-section-card-header">
                    <span class="release-section-icon" data-kind="${def.key}" aria-hidden="true"></span>
                    <div>
                        <div class="release-section-title">${def.label}</div>
                    </div>
                </div>
                ${renderReleaseSectionList(items)}
            </div>
        `;
    }).join('');

    const historyHtml = history.length
        ? history.map((release) => {
            const title = [release.version, resolveReleaseText(release.title)].filter(Boolean).join(' - ');
            const meta = formatReleaseDate(release.date);
            const summary = release.summary ? escapeHtml(resolveReleaseText(release.summary)) : '';
            return `
                <div class="release-history-item">
                    <div class="release-history-title">${escapeHtml(title)}</div>
                    <div class="release-history-meta">${meta}</div>
                    ${summary ? `<div class="release-history-summary">${summary}</div>` : ''}
                </div>
            `;
        }).join('')
        : `<div class="release-history-empty">${t('updates.historyEmpty')}</div>`;

    container.innerHTML = `
        <div class="release-notes-stack">
            <article class="release-hero" data-release-id="${latest.id}" aria-label="${t('updates.latestLabel')}">
                <div class="release-hero-top">
                    <span class="release-badge">${t('updates.latestLabel')}</span>
                    <span class="release-hero-meta">${latestMeta}</span>
                </div>
                <div class="release-hero-title">${escapeHtml(latestTitle)}</div>
                ${latestSummary ? `<p class="release-hero-summary">${latestSummary}</p>` : ''}
            </article>
            <div class="release-section-stack">
                ${sectionHtml}
            </div>
            <section class="release-history-block" aria-label="${t('updates.historyLabel')}">
                <div class="release-history-header">
                    <div>
                        <div class="release-history-label">${t('updates.historyLabel')}</div>
                        <div class="release-history-subtitle">${t('updates.subtitle')}</div>
                    </div>
                </div>
                <div class="release-history-list">
                    ${historyHtml}
                </div>
            </section>
        </div>
    `;
}






// Track if dashboard has been rendered to prevent duplicate renders
let dashboardRendered = false;
let dashboardRenderInProgress = false;

function renderDashboard() {
    // Prevent duplicate renders during initialization
    if (dashboardRenderInProgress) {
        return;
    }
    
    dashboardRenderInProgress = true;
    const renderTimer = debugTimeStart("render", "dashboard", {
        taskCount: tasks.length,
        projectCount: projects.length
    });
    renderDashboardGreeting();
    updateDashboardStats();
    renderDashboardUpcoming();
    renderDashboardTodayAgenda();
    renderDashboardTodayPanel();
    renderActivityFeed();
    animateDashboardElements();

    debugTimeEnd("render", renderTimer, {
        taskCount: tasks.length,
        projectCount: projects.length
    });
    dashboardRenderInProgress = false;
}

// Animate a number element from its current value up to `target` (count-up).
// Respects prefers-reduced-motion and caps duration for large jumps.
function animateDashStat(el, target) {
    if (!el) return;
    target = Number(target) || 0;
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const from = Number(el.dataset.count || 0) || 0;
    el.dataset.count = String(target);
    if (prefersReduced || from === target) {
        el.textContent = String(target);
        return;
    }
    const duration = Math.min(700, 200 + Math.abs(target - from) * 25);
    const start = performance.now();
    const tick = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        el.textContent = String(Math.round(from + (target - from) * eased));
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = String(target);
    };
    requestAnimationFrame(tick);
}

function updateDashboardStats() {
    const statsTimer = debugTimeStart("render", "dashboard-stats", {
        taskCount: tasks.length,
        projectCount: projects.length
    });

    // Use pure functions from dashboard module for calculations
    const stats = calculateDashboardStats(tasks, projects);
    const { isoDate } = getNowPartsInUserTimeZone();
    const { counts } = buildAttentionQueue(tasks, projects, { limit: 0, today: isoDate });

    // Fill the priority stat tiles with count-up animation.
    const setTile = (id, value) => animateDashStat(document.getElementById(id), value);
    setTile('dash-stat-progress', stats.inProgressTasks);
    // Completed = tasks completed this calendar month (by completedDate), not the
    // all-time done count; the tile opens exactly this set.
    const trends = calculateTrendIndicators(tasks, projects, { today: isoDate });
    setTile('dash-stat-completed', trends.monthCompleted);
    setTile('dash-stat-high', counts.high);
    setTile('dash-stat-overdue', counts.overdue);

    // Context sub-lines turn the raw counts into control metrics (trend / aging).
    const openCount = stats.pendingTasks + stats.inProgressTasks + stats.reviewTasks;
    const setSub = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text || ''; };
    setSub('dash-sub-progress', t('dashboard.summary.sub.open', { count: openCount }));
    setSub('dash-sub-completed', t('dashboard.summary.sub.thisMonth'));
    setSub('dash-sub-high', counts.highOverdue > 0 ? t('dashboard.summary.sub.highOverdue', { count: counts.highOverdue }) : '');
    setSub('dash-sub-overdue', '');

    // Dim tiles that are at zero so attention stays on the live ones.
    [
        ['dash-stat-progress', stats.inProgressTasks],
        ['dash-stat-completed', trends.monthCompleted],
        ['dash-stat-high', counts.high],
        ['dash-stat-overdue', counts.overdue]
    ]
        .forEach(([id, count]) => {
            document.getElementById(id)?.closest('.dash-stat-tile')?.classList.toggle('is-zero', count === 0);
        });

    debugTimeEnd("render", statsTimer, {
        activeProjects: stats.activeProjects,
        totalTasks: stats.totalTasks,
        tasksCompletionRate: stats.tasksCompletionRate,
        projectsCompletionRate: stats.projectsCompletionRate
    });
}

function updateTrendIndicators() {
    // The standalone trend sub-labels were removed in the dashboard redesign.
    // The language-refresh path still calls this, so keep it re-rendering the
    // greeting (which carries the live overdue/today/high summary line).
    renderDashboardGreeting();
}

// Greeting heading + date + one-line "what needs you" summary sentence
function renderDashboardGreeting() {
    const greetingEl = document.getElementById('dash-greeting-text');
    const dateEl = document.getElementById('dash-greeting-date');
    const summaryEl = document.getElementById('dash-greeting-summary');
    const { isoDate, hour } = getNowPartsInUserTimeZone();
    if (greetingEl) {
        // morning 5–11, afternoon 12–17, evening/night otherwise
        const slot = (hour >= 5 && hour < 12) ? 'morning'
            : (hour >= 12 && hour < 18) ? 'afternoon'
            : 'evening';
        const name = (window.authSystem?.getCurrentUser?.()?.name || '').trim();
        greetingEl.textContent = name
            ? t(`dashboard.greeting.${slot}Name`, { name })
            : t(`dashboard.greeting.${slot}`);
    }
    if (dateEl) {
        try {
            dateEl.textContent = formatDatePretty(isoDate, getLocale());
        } catch (e) {
            dateEl.textContent = '';
        }
    }

    if (summaryEl) {
        const { counts } = buildAttentionQueue(tasks, projects, { limit: 0, today: isoDate });
        if (counts.total === 0) {
            summaryEl.textContent = t('dashboard.greeting.allClear');
            summaryEl.classList.add('is-clear');
        } else {
            summaryEl.classList.remove('is-clear');
            summaryEl.textContent = t(
                counts.total === 1 ? 'dashboard.greeting.summaryOne' : 'dashboard.greeting.summaryMany',
                { count: counts.total }
            );
        }
    }
}

function getDashboardPriorityClass(priority) {
    const value = String(priority || 'medium').toLowerCase();
    return ['high', 'medium', 'low'].includes(value) ? value : 'medium';
}

function getDashboardDayDiff(dateIso, todayIso) {
    const dateUtc = Date.parse(`${String(dateIso || '').slice(0, 10)}T00:00:00Z`);
    const todayUtc = Date.parse(`${String(todayIso || '').slice(0, 10)}T00:00:00Z`);
    if (!Number.isFinite(dateUtc) || !Number.isFinite(todayUtc)) return null;
    return Math.round((dateUtc - todayUtc) / 86400000);
}

function formatDashRelativeDate(dateIso, todayIso) {
    const diff = getDashboardDayDiff(dateIso, todayIso);
    if (diff === null) return dateIso || '';
    if (diff === 1) return t('dashboard.upcoming.tomorrow');
    if (diff >= 2 && diff <= 6) {
        try {
            return new Intl.DateTimeFormat(getLocale(), { weekday: 'short' }).format(new Date(`${dateIso}T00:00:00`));
        } catch (e) { return dateIso; }
    }
    try {
        return new Intl.DateTimeFormat(getLocale(), { month: 'short', day: 'numeric' }).format(new Date(`${dateIso}T00:00:00`));
    } catch (e) { return dateIso; }
}

function renderDashboardListEmpty(container, titleKey, subtitleKey) {
    container.innerHTML = `
        <div class="dash-list-empty">
            <div class="dash-list-empty-title">${escapeHtml(t(titleKey))}</div>
            <div class="dash-list-empty-sub">${escapeHtml(t(subtitleKey))}</div>
        </div>`;
}

// Needs attention: OVERDUE tasks only. Circle checkbox = mark done.
function renderDashboardTodayAgenda() {
    const container = document.getElementById('dash-today-list');
    if (!container) return;

    const { isoDate } = getNowPartsInUserTimeZone();
    const allRows = buildDueOverdueAgenda(tasks, projects, { limit: 20, today: isoDate });
    const rows = allRows.filter(r => r.dateKind === 'overdue').slice(0, 5);

    if (rows.length === 0) {
        renderDashboardListEmpty(container, 'dashboard.dueOverdue.emptyTitle', 'dashboard.dueOverdue.emptySubtitle');
        return;
    }

    container.innerHTML = rows.map(row => {
        const priority = getDashboardPriorityClass(row.priority);
        const taskTitle = row.title || t('tasks.untitled');
        const openLabel = t('dashboard.attention.openTask');
        const completeAria = t('dashboard.attention.completeAria');
        const status = row.status || 'todo';
        const days = Math.abs(getDashboardDayDiff(row.endDate, isoDate) || 1);
        const contextText = t('dashboard.attention.reason.overdueDays', { count: days });

        return `
        <div class="dash-attention-row reason-overdue">
            <button type="button" class="dash-circle-check"
                data-action="dashCompleteTask" data-param="${row.id}"
                data-stop-propagation="1"
                aria-label="${escapeHtml(completeAria)}: ${escapeHtml(taskTitle)}">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                    <path d="M2 5.5l2 2L8 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
            <button type="button" class="dash-attention-main" data-action="openTaskDetails" data-param="${row.id}"
                aria-label="${escapeHtml(openLabel)}: ${escapeHtml(taskTitle)}">
                <span class="dash-attention-title">${escapeHtml(taskTitle)}</span>
                ${row.projectName ? `<span class="dash-attention-sub">${escapeHtml(row.projectName)}</span>` : ''}
            </button>
            <div class="dash-row-right">
                <span class="priority-pill priority-${priority}">${escapeHtml(getPriorityLabel(priority))}</span>
                <span class="status-badge ${status}">${escapeHtml(getStatusLabel(status))}</span>
                <span class="dash-context-badge dash-context-badge--overdue">${escapeHtml(contextText)}</span>
            </div>
        </div>`;
    }).join('');
}

// Coming up: future tasks, same three-column right rail (Priority | Status | Date).
function renderDashboardUpcoming() {
    const container = document.getElementById('dash-upcoming-list');
    if (!container) return;

    const { isoDate } = getNowPartsInUserTimeZone();
    const currentDateEl = document.getElementById('dash-upcoming-current-date');
    if (currentDateEl) {
        currentDateEl.dateTime = isoDate;
        currentDateEl.innerHTML = `<span class="dash-card-current-date-label">${escapeHtml(t('calendar.today'))}:</span> <span class="dash-card-current-date-value">${escapeHtml(toDMYFromISO(isoDate))}</span>`;
    }
    const rows = buildUpcomingQueue(tasks, projects, { limit: 4, today: isoDate });

    if (rows.length === 0) {
        renderDashboardListEmpty(container, 'dashboard.upcoming.emptyTitle', 'dashboard.upcoming.emptySubtitle');
        return;
    }

    container.innerHTML = rows.map(row => {
        const priority = getDashboardPriorityClass(row.priority);
        const status = row.status || 'todo';
        const projColor = row.projectId != null ? getProjectColor(row.projectId) : '';

        return `
        <div class="dash-list-row" data-action="openTaskDetails" data-param="${row.id}" role="button" tabindex="0">
            <span class="dash-list-dot"${projColor ? ` style="background:${projColor}"` : ''} aria-hidden="true"></span>
            <div class="dash-list-main">
                <div class="dash-list-title">${escapeHtml(row.title || '')}</div>
                ${row.projectName ? `<div class="dash-list-sub">${escapeHtml(row.projectName)}</div>` : ''}
            </div>
            <div class="dash-row-right">
                <span class="priority-pill priority-${priority}">${escapeHtml(getPriorityLabel(priority))}</span>
                <span class="status-badge ${status}">${escapeHtml(getStatusLabel(status))}</span>
                ${generateDateChipHTML(row.endDate, row.status, { escapeHtml, formatDate })}
            </div>
        </div>`;
    }).join('');
}

// Today panel (right column): due today + starts today, same three-column layout.
function renderDashboardTodayPanel() {
    const container = document.getElementById('dash-today-panel-list');
    if (!container) return;

    const { isoDate } = getNowPartsInUserTimeZone();
    const allRows = buildDueOverdueAgenda(tasks, projects, { limit: 20, today: isoDate });
    const todayRows = allRows.filter(r => r.dateKind === 'due' || r.dateKind === 'start').slice(0, 5);
    const upcomingCount = buildUpcomingQueue(tasks, projects, { limit: 100, today: isoDate }).length;

    if (todayRows.length === 0) {
        container.innerHTML = `
            <div class="dash-list-empty">
                <div class="dash-list-empty-title">${escapeHtml(t('dashboard.today.emptyTitle'))}</div>
                <div class="dash-list-empty-sub">${escapeHtml(t('dashboard.today.emptySubtitle'))}</div>
            </div>`;
        return;
    }

    const rows = todayRows.map(row => {
        const priority = getDashboardPriorityClass(row.priority);
        const status = row.status || 'todo';
        const projColor = row.projectId != null ? getProjectColor(row.projectId) : '';
        const timingKey = row.startsToday && row.dueToday
            ? 'dashboard.today.startsEndsToday'
            : row.startsToday
                ? 'dashboard.today.startsToday'
                : 'dashboard.today.endsToday';
        const timingClass = row.startsToday && !row.dueToday ? 'start' : 'due';
        return `
        <div class="dash-today-item" data-action="openTaskDetails" data-param="${row.id}" role="button" tabindex="0">
            <span class="dash-today-dot dash-today-dot--${status}"${projColor ? ` style="background:${projColor}"` : ''} aria-hidden="true"></span>
            <div class="dash-today-item-main">
                <span class="dash-today-item-title">${escapeHtml(row.title || t('tasks.untitled'))}</span>
                ${row.projectName ? `<span class="dash-today-item-sub">${escapeHtml(row.projectName)}</span>` : ''}
            </div>
            <div class="dash-row-right dash-row-right--today">
                <span class="priority-pill priority-${priority}">${escapeHtml(getPriorityLabel(priority))}</span>
                <span class="status-badge ${status}">${escapeHtml(getStatusLabel(status))}</span>
                <span class="dash-context-badge dash-context-badge--${timingClass}">${escapeHtml(t(timingKey))}</span>
            </div>
        </div>`;
    }).join('');

    container.innerHTML = rows;
}

function renderProjectProgressBars() {
    const container = document.getElementById('project-progress-bars');
    if (!container) return;

    const activeProjects = projects.filter((project) => getProjectStatus(project.id) === 'active');

    if (activeProjects.length === 0) {
        container.innerHTML = `
            <div style="text-align: center; padding: 40px; color: var(--text-muted);">
                <div style="font-size: 48px; margin-bottom: 16px;">🌊</div>
                <div style="font-size: 16px; margin-bottom: 8px;">${t('dashboard.emptyProjects.title')}</div>
                <div style="font-size: 14px;">${t('dashboard.emptyProjects.subtitle')}</div>
            </div>
        `;
        return;
    }
    
    // Use pure function from dashboard module for calculations
    const projectProgressData = calculateProjectProgress(activeProjects, tasks, 5);

    // Use module function for HTML generation
    container.innerHTML = generateProgressBarsHTML(projectProgressData, {
        tasksLabel: t('dashboard.tasks')
    });
}

function getDashboardActivityDate(item, preferredField = 'created') {
    if (!item) return '';
    if (preferredField === 'completed') {
        return item.completedDate || item.completedAt || item.updatedAt || item.createdAt || item.createdDate || '';
    }
    return item.createdAt || item.createdDate || '';
}

function sortDashboardActivityItems(items, preferredField = 'created') {
    return [...items].sort((a, b) => {
        const bTime = Date.parse(getDashboardActivityDate(b, preferredField)) || 0;
        const aTime = Date.parse(getDashboardActivityDate(a, preferredField)) || 0;
        return bTime - aTime;
    });
}

function buildDashboardActivities({ all = false } = {}) {
    const activities = [];
    const findProject = (projectId) => projects.find(p => Number(p.id) === Number(projectId)) || null;

    const completedTasks = sortDashboardActivityItems(
        tasks.filter(task => task.status === 'done'),
        'completed'
    );
    (all ? completedTasks : completedTasks.slice(0, 3)).forEach(task => {
        const project = findProject(task.projectId);
        activities.push({
            type: 'completed',
            kind: 'task_completed',
            taskId: task.id,
            taskTitle: task.title || t('tasks.untitled'),
            projectId: project?.id || null,
            projectName: project?.name || '',
            date: getDashboardActivityDate(task, 'completed'),
            icon: '\u2713'
        });
    });

    const recentProjects = sortDashboardActivityItems(projects);
    (all ? recentProjects : recentProjects.slice(0, 2)).forEach(project => {
        activities.push({
            type: 'created',
            kind: 'project_created',
            projectId: project.id,
            projectName: project.name,
            date: getDashboardActivityDate(project),
            icon: '+'
        });
    });

    const recentTasks = sortDashboardActivityItems(tasks.filter(task => task.status !== 'done'));
    (all ? recentTasks : recentTasks.slice(0, 2)).forEach(task => {
        const project = findProject(task.projectId);
        activities.push({
            type: 'created',
            kind: 'task_created',
            taskId: task.id,
            taskTitle: task.title || t('tasks.untitled'),
            projectId: project?.id || null,
            projectName: project?.name || '',
            date: getDashboardActivityDate(task),
            icon: '+'
        });
    });

    return activities
        .sort((a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0))
        .map(activity => ({
            ...activity,
            html: buildDashboardActivityText(activity)
        }));
}

function buildDashboardActivityEntityLink(kind, id, label) {
    const safeLabel = escapeHtml(label || '');
    if (!safeLabel || id === null || id === undefined || id === '') return safeLabel;

    if (kind === 'task') {
        return `<button type="button" class="activity-link" data-action="openTaskDetails" data-param="${Number(id)}" data-stop-propagation="1">${safeLabel}</button>`;
    }
    if (kind === 'project') {
        return `<button type="button" class="activity-link" data-action="showProjectDetails" data-param="${Number(id)}" data-stop-propagation="1">${safeLabel}</button>`;
    }
    if (kind === 'doc') {
        return `<a class="activity-link" href="#docs?doc=${encodeURIComponent(String(id))}">${safeLabel}</a>`;
    }
    return safeLabel;
}

function buildDashboardActivityText(activity) {
    const taskLink = buildDashboardActivityEntityLink('task', activity.taskId, activity.taskTitle);
    const projectLink = buildDashboardActivityEntityLink('project', activity.projectId, activity.projectName);
    const docLink = buildDashboardActivityEntityLink('doc', activity.docId, activity.docTitle);

    if (activity.docId && docLink) {
        const action = activity.actionText || t('dashboard.activity.updatedDocAction');
        return `${escapeHtml(action)} ${docLink}`;
    }
    if (activity.kind === 'task_completed') {
        const projectPart = projectLink
            ? ` <span class="activity-preposition">${escapeHtml(t('dashboard.activity.inLabel'))}</span> ${projectLink}`
            : '';
        return `${escapeHtml(t('dashboard.activity.completedAction'))} ${taskLink}${projectPart}`;
    }
    if (activity.kind === 'project_created') {
        return `${escapeHtml(t('dashboard.activity.createdProjectAction'))} ${projectLink}`;
    }
    if (activity.kind === 'task_created') {
        const projectPart = projectLink
            ? ` <span class="activity-preposition">${escapeHtml(t('dashboard.activity.toLabel'))}</span> ${projectLink}`
            : '';
        return `${escapeHtml(t('dashboard.activity.addedTaskAction'))} ${taskLink}${projectPart}`;
    }
    return escapeHtml(activity.text || '');
}

function renderActivityFeed() {
    const container = document.getElementById('activity-feed');
    if (!container) return;

    const activities = buildDashboardActivities({ all: false });

    if (activities.length === 0) {
        container.innerHTML = `
            <div class="activity-item">
                <div class="activity-icon completed">~</div>
                <div class="activity-content">
                    <div class="activity-text">${escapeHtml(t('dashboard.activity.emptyTitle'))}</div>
                    <div class="activity-time">${escapeHtml(t('dashboard.activity.emptySubtitle'))}</div>
                </div>
            </div>
        `;
        return;
    }

    container.innerHTML = generateActivityFeedHTML(activities.slice(0, 4), formatDashboardActivityDate);
}


function showAllActivity() {
    // Update URL hash to create proper page routing
    window.location.hash = 'dashboard/recent_activity';
}

async function backToDashboard() {
    if (docsWorkspaceActive) {
        cancelPendingDocsCheckboxAutoSave();
        cancelPendingDocsContentAutoSave();
        const canContinue = await saveActiveDocIfDirty({ silent: true });
        if (!canContinue) {
            restoreCurrentDocsRoute();
            return;
        }
        teardownDocsWorkspace();
    }
    // Navigate back to main dashboard
    window.location.hash = 'dashboard';
}

function showRecentActivityPageLegacy() {
    // Hide all main pages
    document.querySelectorAll('.page').forEach(page => {
        page.classList.remove('active');
    });
    
    // Show dashboard page but hide its main content
    document.getElementById('dashboard').classList.add('active');
    document.querySelector('.dashboard-grid').style.display = 'none';
    const insightsCard = document.querySelector('.insights-card');
    if (insightsCard) insightsCard.style.display = 'none';
    
    // Create or show activity page
    let activityPage = document.getElementById('activity-page');
    if (!activityPage) {
        activityPage = document.createElement('div');
        activityPage.id = 'activity-page';
        activityPage.className = 'activity-page';
        activityPage.innerHTML = `
            <div class="activity-page-header">
                <button class="back-btn" data-action="backToDashboard" data-i18n="dashboard.activity.backToDashboard">${t('dashboard.activity.backToDashboard')}</button>
                <h2 data-i18n="dashboard.activity.allTitle">${t('dashboard.activity.allTitle')}</h2>
            </div>
            <div id="all-activity-list" class="all-activity-list"></div>
        `;
        document.querySelector('.dashboard-content').appendChild(activityPage);
    }
    
    activityPage.style.display = 'block';
    renderAllActivity();

    // Ensure the activity view is scrolled into view (important on mobile)
    try {
        activityPage.scrollIntoView({ behavior: 'auto', block: 'start' });
    } catch (e) {
        window.scrollTo(0, 0);
    }

    // Update active nav item
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    document.querySelector('.nav-item[data-page="dashboard"]').classList.add('active');
}

function showRecentActivityPage() {
    // Hide all main pages
    document.querySelectorAll('.page').forEach(page => {
        page.classList.remove('active');
    });

    // Create or show dedicated activity page as a full page (sibling of dashboard/projects/tasks)
    let activityPage = document.getElementById('activity-page');
    if (!activityPage) {
        activityPage = document.createElement('div');
        activityPage.id = 'activity-page';
        activityPage.className = 'page';
        activityPage.innerHTML = `
            <div class="page-header">
                <div>
                    <h1 class="page-title" data-i18n="dashboard.activity.allTitle">${t('dashboard.activity.allTitle')}</h1>
                    <p class="page-subtitle" data-i18n="dashboard.activity.allSubtitle">${t('dashboard.activity.allSubtitle')}</p>
                </div>
                <button class="view-all-btn back-btn" data-action="backToDashboard" data-i18n="dashboard.activity.backToDashboard">${t('dashboard.activity.backToDashboard')}</button>
            </div>
            <div class="page-content">
                <div id="all-activity-list" class="all-activity-list"></div>
            </div>
        `;
        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
            mainContent.appendChild(activityPage);
        }
    }

    activityPage.classList.add('active');
    activityPage.style.display = 'flex';
    renderAllActivity();

    // Ensure the activity view is scrolled into view (important on mobile)
    try {
        activityPage.scrollIntoView({ behavior: 'auto', block: 'start' });
    } catch (e) {
        window.scrollTo(0, 0);
    }

    // Update active nav item
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    document.querySelector('.nav-item[data-page="dashboard"]').classList.add('active');
}

function formatDashboardActivityFullDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(getLocale(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function renderAllActivity() {
    const container = document.getElementById('all-activity-list');
    if (!container) return;

    const activities = buildDashboardActivities({ all: true });
    
    const activityHTML = activities.map(activity => `
        <div class="activity-item-full">
            <div class="activity-icon ${escapeHtml(activity.type)}">${escapeHtml(activity.icon)}</div>
            <div class="activity-content">
                <div class="activity-text">${activity.html}</div>
            </div>
            <div class="activity-full-date">${escapeHtml(formatDashboardActivityFullDate(activity.date))}</div>
        </div>
    `).join('');
    
    container.innerHTML = activityHTML;
}

function renderInsights() {
    const container = document.getElementById('insights-list');
    if (!container) return;

    const insights = generateInsights();    const insightsHTML = insights.map(insight => `
        <div class="insight-item ${insight.type}">
            <div class="insight-icon">${insight.icon}</div>
            <div class="insight-content">
                <div class="insight-title">${insight.title}</div>
                <div class="insight-desc">${insight.description}</div>
            </div>
        </div>
    `).join('');
    
    container.innerHTML = insightsHTML;
    // Insights count badge removed
}

function generateInsights() {
    const insights = [];
    
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'done').length;
    const today = new Date().toISOString().split('T')[0];
    const overdueTasks = tasks.filter(t => t.endDate && t.endDate < today && t.status !== 'done').length;
    const highPriorityTasks = tasks.filter(t => t.priority === 'high').length;
    const todayTasks = tasks.filter(t => {
        if (!t.endDate) return false;
        const today = new Date().toDateString();
        return new Date(t.endDate).toDateString() === today && t.status !== 'done';
    }).length;
    
    // Real task completion insights
    if (totalTasks > 0) {
        const completionRate = (completedTasks / totalTasks) * 100;
        if (completionRate >= 80) {
            insights.push({
                type: 'success',
                icon: '🎯',
                title: t('dashboard.insights.excellentTitle'),
                description: t('dashboard.insights.excellentDesc', { percent: completionRate.toFixed(0) })
            });
        } else if (completionRate >= 60) {
            insights.push({
                type: 'success',
                icon: '📈',
                title: t('dashboard.insights.goodTitle'),
                description: t('dashboard.insights.goodDesc', { percent: completionRate.toFixed(0) })
            });
        } else if (completionRate >= 30) {
            insights.push({
                type: 'warning',
                icon: '⚡',
                title: t('dashboard.insights.opportunityTitle'),
                description: t('dashboard.insights.opportunityDesc', { percent: completionRate.toFixed(0) })
            });
        } else {
            insights.push({
                type: 'priority',
                icon: '🚨',
                title: t('dashboard.insights.actionTitle'),
                description: t('dashboard.insights.actionDesc', { percent: completionRate.toFixed(0) })
            });
        }
    }
    
    // Due today tasks
    if (todayTasks > 0) {
        insights.push({
            type: 'priority',
            icon: '📅',
            title: t('dashboard.insights.todayTitle'),
            description: t(todayTasks === 1 ? 'dashboard.insights.todayDescOne' : 'dashboard.insights.todayDescMany', { count: todayTasks })
        });
    }
    
    // Overdue tasks (only if not already covered by today's tasks)
    if (overdueTasks > 0 && todayTasks === 0) {
        insights.push({
            type: 'warning',
            icon: '⏰',
            title: t('dashboard.insights.overdueTitle'),
            description: t(overdueTasks === 1 ? 'dashboard.insights.overdueDescOne' : 'dashboard.insights.overdueDescMany', { count: overdueTasks })
        });
    }
    
    // High priority tasks
    if (highPriorityTasks > 0) {
        const urgentCount = tasks.filter(t => t.priority === 'high' && t.status !== 'done').length;
        if (urgentCount > 0) {
            insights.push({
                type: 'priority',
                icon: '🔥',
                title: t('dashboard.insights.highPriorityTitle'),
                description: t(urgentCount === 1 ? 'dashboard.insights.highPriorityDescOne' : 'dashboard.insights.highPriorityDescMany', { count: urgentCount })
            });
        }
    }
    
    // Project insights
    if (projects.length > 1) {
        const projectsWithTasks = projects.filter(p => tasks.some(t => t.projectId === p.id)).length;
        const projectsWithoutTasks = projects.length - projectsWithTasks;
        
        if (projectsWithoutTasks > 0) {
            insights.push({
                type: 'warning',
                icon: '�',
                title: t('dashboard.insights.emptyProjectsTitle'),
                description: t(projectsWithoutTasks === 1 ? 'dashboard.insights.emptyProjectsDescOne' : 'dashboard.insights.emptyProjectsDescMany', { count: projectsWithoutTasks })
            });
        }
    }
    
    // Productivity pattern insights
    if (totalTasks >= 5) {
        const recentlyCompleted = tasks.filter(t => {
            if (t.status !== 'done' || !t.completedDate) return false;
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return new Date(t.completedDate) > weekAgo;
        }).length;
        
        if (recentlyCompleted >= 3) {
            insights.push({
                type: 'success',
                icon: '🚀',
                title: t('dashboard.insights.momentumTitle'),
                description: t(recentlyCompleted === 1 ? 'dashboard.insights.momentumDescOne' : 'dashboard.insights.momentumDescMany', { count: recentlyCompleted })
            });
        }
    }
    
    // Default insights for empty state
    if (insights.length === 0) {
        if (totalTasks === 0) {
            insights.push({
                type: 'priority',
                icon: '🌊',
                title: t('dashboard.insights.readyTitle'),
                description: t('dashboard.insights.readyDesc')
            });
        } else {
            insights.push({
                type: 'success',
                icon: '\u{2705}',
                title: t('dashboard.insights.caughtUpTitle'),
                description: t('dashboard.insights.caughtUpDesc')
            });
        }
    }
    
    return insights.slice(0, 3); // Limit to 3 most relevant insights
}

function animateDashboardElements() {
    // Animate stat cards with staggered entrance
    const statCards = document.querySelectorAll('.enhanced-stat-card');
    statCards.forEach((card, index) => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
        setTimeout(() => {
            card.style.transition = 'all 0.4s ease';
            card.style.opacity = '1';
            card.style.transform = 'translateY(0)';
        }, index * 100);
    });
    
    // Animate hero stats
    const heroCards = document.querySelectorAll('.hero-stat-card');
    heroCards.forEach((card, index) => {
        card.style.opacity = '0';
        card.style.transform = 'translateX(-30px)';
        setTimeout(() => {
            card.style.transition = 'all 0.6s ease';
            card.style.opacity = '1';
            card.style.transform = 'translateX(0)';
        }, index * 200);
    });
}

function formatRelativeTime(dateString) {
    if (!dateString) return t('dashboard.activity.recently');
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return t('dashboard.activity.justNow');
    if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return t(minutes === 1 ? 'dashboard.activity.minuteAgo' : 'dashboard.activity.minutesAgo', { count: minutes });
    }
    if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return t(hours === 1 ? 'dashboard.activity.hourAgo' : 'dashboard.activity.hoursAgo', { count: hours });
    }
    if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return t(days === 1 ? 'dashboard.activity.dayAgo' : 'dashboard.activity.daysAgo', { count: days });
    }
    return formatDisplayDate(date);
}

function formatDashboardActivityDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('dashboard.activity.today');
    if (diffDays === 1) return t('dashboard.activity.yesterday');
    if (diffDays < 7) return t('dashboard.activity.daysAgoShort', { count: diffDays });

    return date.toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' });
}

// Quick action functions
let isSigningOut = false; // Prevents beforeunload dialog from blocking sign-out reloads
function signOut() {
    if (docsWorkspaceActive) {
        flushPendingDocsCheckboxAutoSave();
    }
    if (window.authSystem && window.authSystem.logout) {
        isSigningOut = true;
        window.authSystem.logout();
    }
}

// Show export confirmation modal
function exportDashboardData() {
    document.getElementById('export-data-modal').classList.add('active');
}

// Close export confirmation modal
function closeExportDataModal() {
    document.getElementById('export-data-modal').classList.remove('active');
}

// Perform the actual export after confirmation
function confirmExportData() {
    // Close the modal
    closeExportDataModal();

    // Get current user info
    const currentUser = window.authSystem ? window.authSystem.getCurrentUser() : null;

    // Export EVERYTHING - complete backup of all user data
    const completeBackup = buildCompleteBackupPayload({
        currentUser,
        projects,
        tasks,
        dependencies: serializeDependencies(dependencies),
        projectColors: projectColorMap,
        projectColorOverrides,
        tagColors: tagColorMap,
        tagColorOverrides,
        tagColorsVersion,
        sortMode,
        manualTaskOrder,
        settings,
        history: window.historyService ? window.historyService.getHistory() : [],
        projectCounter,
        taskCounter,
    });

    const dataStr = JSON.stringify(completeBackup, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);

    // Include username in filename if available
    const username = currentUser?.username || 'user';
    const exportFileDefaultName = `nautilus-complete-backup-${username}-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    // Show success notification
    showNotification('Complete backup exported successfully! All data, settings, and history included.', 'success');
}

// Import Data Functions
let importFileData = null;

function openImportDataModal() {
    const modal = document.getElementById('import-data-modal');
    modal.classList.add('active');
    
    // Reset state
    importFileData = null;
    document.getElementById('import-file-input').value = '';
    document.getElementById('import-confirm-input').value = '';
    document.getElementById('import-confirm-error').classList.remove('show');
    document.getElementById('import-preview').style.display = 'none';
    
    // Find elements that need HTML rendering BEFORE applyTranslations runs
    const warningBodyEl = modal.querySelector('[data-i18n="import.warningBody"]');
    const confirmTextEl = modal.querySelector('[data-i18n="import.confirmText"]');
    
    // Temporarily remove data-i18n so applyTranslations skips them
    if (warningBodyEl) warningBodyEl.removeAttribute('data-i18n');
    if (confirmTextEl) confirmTextEl.removeAttribute('data-i18n');
    
    // Apply translations for elements that don't need HTML rendering
    applyTranslations(modal);
    
    // Now set HTML content for elements that need HTML rendering
    if (warningBodyEl) {
        warningBodyEl.innerHTML = t('import.warningBody');
    }
    
    if (confirmTextEl) {
        confirmTextEl.innerHTML = t('import.confirmText');
    }
    
    // Setup drag-and-drop dropzone
    const dropzone = document.getElementById('import-file-dropzone');
    const fileInput = document.getElementById('import-file-input');
    const dropzoneText = dropzone.querySelector('.import-dropzone-text');
    
    // Update dropzone text
    if (dropzoneText) {
        dropzoneText.textContent = t('import.dropzoneDefault');
    }
    
    // Click to open file picker
    dropzone.addEventListener('click', () => fileInput.click());
    dropzone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInput.click();
        }
    });
    
    // File input change handler
    fileInput.addEventListener('change', handleImportFileSelect, { once: true });
    
    // Drag and drop handlers
    let dragDepth = 0;
    
    dropzone.addEventListener('dragenter', (e) => {
        e.preventDefault();
        dragDepth++;
        dropzone.classList.add('import-dropzone-dragover');
        dropzone.style.borderColor = 'var(--accent-blue)';
        dropzone.style.background = 'var(--hover-bg)';
    });
    
    dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('import-dropzone-dragover');
        dropzone.style.borderColor = 'var(--accent-blue)';
        dropzone.style.background = 'var(--hover-bg)';
    });
    
    dropzone.addEventListener('dragleave', (e) => {
        e.preventDefault();
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) {
            dropzone.classList.remove('import-dropzone-dragover');
            dropzone.style.borderColor = 'var(--border-primary)';
            dropzone.style.background = 'var(--bg-secondary)';
        }
    });
    
    dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dragDepth = 0;
        dropzone.classList.remove('import-dropzone-dragover');
        dropzone.style.borderColor = 'var(--border-primary)';
        dropzone.style.background = 'var(--bg-secondary)';
        
        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.name.endsWith('.json') || file.type === 'application/json') {
                handleImportFileFromDrop(file, dropzoneText);
            } else {
                showErrorNotification(t('import.errorInvalidFile'));
            }
        }
    });
    
    dropzone.addEventListener('dragend', () => {
        dragDepth = 0;
        dropzone.classList.remove('import-dropzone-dragover');
        dropzone.style.borderColor = 'var(--border-primary)';
        dropzone.style.background = 'var(--bg-secondary)';
    });
    
    // Setup lowercase handler for confirm input
    const confirmInput = document.getElementById('import-confirm-input');
    const lowercaseHandler = function(e) {
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        e.target.value = e.target.value.toLowerCase();
        e.target.setSelectionRange(start, end);
    };
    confirmInput.addEventListener('input', lowercaseHandler);
    
    // Handle Enter key
    confirmInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            confirmImportData();
        }
    }, { once: true });
    
    confirmInput.focus();
}

function closeImportDataModal() {
    const modal = document.getElementById('import-data-modal');
    modal.classList.remove('active');
    
    // Reset state
    importFileData = null;
    const fileInput = document.getElementById('import-file-input');
    fileInput.value = '';
    
    // Reset dropzone text
    updateImportDropzoneText(null);
    const dropzone = document.getElementById('import-file-dropzone');
    if (dropzone) {
        dropzone.classList.remove('import-dropzone-dragover');
        dropzone.style.borderColor = 'var(--border-primary)';
        dropzone.style.background = 'var(--bg-secondary)';
    }
    
    // Clone input to remove event listeners
    const confirmInput = document.getElementById('import-confirm-input');
    const newInput = confirmInput.cloneNode(true);
    confirmInput.parentNode.replaceChild(newInput, confirmInput);
    
    document.getElementById('import-confirm-error').classList.remove('show');
    document.getElementById('import-preview').style.display = 'none';
}

function handleImportFileSelect(event) {
    const file = event.target.files[0];
    if (!file) {
        importFileData = null;
        document.getElementById('import-preview').style.display = 'none';
        updateImportDropzoneText(null);
        return;
    }
    
    handleImportFileFromDrop(file);
}

function handleImportFileFromDrop(file, dropzoneTextEl = null) {
    if (!file) {
        importFileData = null;
        document.getElementById('import-preview').style.display = 'none';
        updateImportDropzoneText(null);
        return;
    }
    
    if (!file.name.endsWith('.json') && file.type !== 'application/json') {
        showErrorNotification(t('import.errorInvalidFile'));
        const fileInput = document.getElementById('import-file-input');
        if (fileInput) fileInput.value = '';
        importFileData = null;
        document.getElementById('import-preview').style.display = 'none';
        updateImportDropzoneText(null);
        return;
    }
    
    // Update dropzone text to show filename
    updateImportDropzoneText(file.name, dropzoneTextEl);
    
    const reader = new FileReader();
    reader.onload = function(e) {
        try {
            const jsonData = JSON.parse(e.target.result);
            
            // Validate JSON structure
            if (!validateImportData(jsonData)) {
                showErrorNotification(t('import.errorInvalidFormat'));
                const fileInput = document.getElementById('import-file-input');
                if (fileInput) fileInput.value = '';
                importFileData = null;
                document.getElementById('import-preview').style.display = 'none';
                updateImportDropzoneText(null);
                return;
            }
            
            // Store valid data
            importFileData = jsonData;
            
            // Show preview
            showImportPreview(jsonData);
        } catch (error) {
            console.error('Import file parse error:', error);
            showErrorNotification(t('import.errorParseFailed'));
            const fileInput = document.getElementById('import-file-input');
            if (fileInput) fileInput.value = '';
            importFileData = null;
            document.getElementById('import-preview').style.display = 'none';
            updateImportDropzoneText(null);
        }
    };
    
    reader.onerror = function() {
        showErrorNotification(t('import.errorReadFailed'));
        const fileInput = document.getElementById('import-file-input');
        if (fileInput) fileInput.value = '';
        importFileData = null;
        document.getElementById('import-preview').style.display = 'none';
        updateImportDropzoneText(null);
    };
    
    reader.readAsText(file);
}

function updateImportDropzoneText(filename, dropzoneTextEl = null) {
    const dropzone = document.getElementById('import-file-dropzone');
    if (!dropzone) return;
    
    const textEl = dropzoneTextEl || dropzone.querySelector('.import-dropzone-text');
    if (!textEl) return;
    
    if (filename) {
        textEl.textContent = filename;
        textEl.style.fontWeight = '500';
        textEl.style.color = 'var(--text-primary)';
    } else {
        textEl.textContent = t('import.dropzoneDefault');
        textEl.style.fontWeight = 'normal';
        textEl.style.color = 'var(--text-secondary)';
    }
}

function validateImportData(data) {
    // Must be an object
    if (!data || typeof data !== 'object') {
        return false;
    }
    
    // Must have tasks and projects arrays (at minimum)
    if (!Array.isArray(data.tasks) || !Array.isArray(data.projects)) {
        return false;
    }
    
    // Validate tasks structure (basic check)
    for (const task of data.tasks) {
        if (!task || typeof task !== 'object' || !task.id || !task.title) {
            return false;
        }
    }
    
    // Validate projects structure (basic check)
    for (const project of data.projects) {
        if (!project || typeof project !== 'object' || !project.id || !project.name) {
            return false;
        }
    }
    
    return true;
}

function showImportPreview(data) {
    const previewEl = document.getElementById('import-preview');
    const contentEl = document.getElementById('import-preview-content');
    
    const stats = {
        projects: data.projects?.length || 0,
        tasks: data.tasks?.length || 0,
        exportDate: data.exportDate || 'Unknown',
        exportVersion: data.exportVersion || 'Unknown'
    };
    
    contentEl.innerHTML = `
        <div style="margin-bottom: 8px;">
            <strong>${stats.projects}</strong> ${stats.projects === 1 ? 'project' : 'projects'}
        </div>
        <div style="margin-bottom: 8px;">
            <strong>${stats.tasks}</strong> ${stats.tasks === 1 ? 'task' : 'tasks'}
        </div>
        <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border-primary); color: var(--text-muted); font-size: 12px;">
            Exported: ${formatDisplayDate(new Date(stats.exportDate))} (v${stats.exportVersion})
        </div>
    `;
    
    previewEl.style.display = 'block';
}

async function confirmImportData() {
    const input = document.getElementById('import-confirm-input');
    const errorMsg = document.getElementById('import-confirm-error');
    const confirmText = input.value.trim().toLowerCase();
    
    if (confirmText !== 'import') {
        errorMsg.classList.add('show');
        input.focus();
        return;
    }
    
    if (!importFileData) {
        showErrorNotification(t('import.errorNoFile'));
        return;
    }
    
    // Validate one more time
    if (!validateImportData(importFileData)) {
        showErrorNotification(t('import.errorInvalidFormat'));
        return;
    }
    
    try {
        // Show loading notification
        showNotification(t('import.processing'), 'info');
        
        // REPLACE all data (not merge!)
        const importedData = importFileData;
        
        // Replace tasks
        if (Array.isArray(importedData.tasks)) {
            tasks = importedData.tasks;
            // Update task counter if provided
            if (importedData.taskCounter && typeof importedData.taskCounter === 'number') {
                taskCounter = importedData.taskCounter;
            } else {
                // Calculate max ID if counter not provided
                const maxTaskId = tasks.length > 0 ? Math.max(...tasks.map(t => t.id || 0)) : 0;
                taskCounter = maxTaskId + 1;
            }
        }
        
        // Replace projects
        if (Array.isArray(importedData.projects)) {
            projects = importedData.projects;
            // Update project counter if provided
            if (importedData.projectCounter && typeof importedData.projectCounter === 'number') {
                projectCounter = importedData.projectCounter;
            } else {
                // Calculate max ID if counter not provided
                const maxProjectId = projects.length > 0 ? Math.max(...projects.map(p => p.id || 0)) : 0;
                projectCounter = maxProjectId + 1;
            }
        }
        
        projectColorMap = importedData.projectColors && typeof importedData.projectColors === 'object'
            ? importedData.projectColors
            : {};

        const importedTagColorsVersion = Number.isFinite(Number(importedData.tagColorsVersion))
            ? Number(importedData.tagColorsVersion)
            : 0;

        // Old backups may not contain override keys; preserve current state in that case.
        if (importedData.tagColorOverrides && typeof importedData.tagColorOverrides === 'object') {
            tagColorOverrides = importedData.tagColorOverrides;
        }
        if (importedData.projectColorOverrides && typeof importedData.projectColorOverrides === 'object') {
            projectColorOverrides = importedData.projectColorOverrides;
        }

        // Use imported auto-color map only when it matches current assignment version.
        if (
            importedData.tagColors &&
            typeof importedData.tagColors === 'object' &&
            importedTagColorsVersion >= TAG_COLOR_ASSIGNMENT_VERSION
        ) {
            tagColorMap = importedData.tagColors;
            tagColorsVersion = importedTagColorsVersion;
        } else {
            // Rebuild auto-colors — tagColorOverrides (user choices) are unaffected.
            tagColorMap = {};
            buildTagColorMapFromTasks(tasks, tagColorMap);
            tagColorsVersion = TAG_COLOR_ASSIGNMENT_VERSION;
        }
        tagColorsReady = true;
        
        // Replace sort mode if provided
        if (importedData.sortMode) {
            sortMode = importedData.sortMode;
        }
        
        // Replace manual task order if provided
        if (importedData.manualTaskOrder && typeof importedData.manualTaskOrder === 'object') {
            manualTaskOrder = importedData.manualTaskOrder;
        }
        
        // Replace settings if provided (but be careful - don't overwrite critical settings)
        if (importedData.settings && typeof importedData.settings === 'object') {
            // Merge settings carefully - keep some current settings like language, theme
            const currentLanguage = settings.language;
            const currentTheme = document.documentElement.getAttribute('data-theme');
            
            settings = {
                ...settings,
                ...importedData.settings
            };
            
            // Restore critical settings
            settings.language = currentLanguage;
            applyDocsNavVisibility();
            if (currentTheme) {
                document.documentElement.setAttribute('data-theme', currentTheme);
            }
        }
        
        // Import history if available (optional - historyService may not have importHistory method)
        if (importedData.history && window.historyService && Array.isArray(importedData.history)) {
            try {
                if (typeof window.historyService.importHistory === 'function') {
                    window.historyService.importHistory(importedData.history);
                } else {
                    console.warn('History import not available - historyService.importHistory not found');
                }
            } catch (err) {
                console.warn('Failed to import history:', err);
            }
        }
        
        // Restore dependencies if present in backup (v2.1+)
        if (importedData.dependencies) {
            try {
                dependencies = deserializeDependencies(importedData.dependencies);
            } catch (err) {
                console.warn('Failed to parse imported dependencies:', err);
                dependencies = {};
            }
        }

        // Save all imported data
        await Promise.all([
            saveTasks().catch(err => {
                console.error('Failed to save imported tasks:', err);
                throw new Error('Failed to save tasks');
            }),
            saveProjects().catch(err => {
                console.error('Failed to save imported projects:', err);
                throw new Error('Failed to save projects');
            }),
            saveSettings().catch(err => {
                console.error('Failed to save imported settings:', err);
                // Don't throw - settings save failure is less critical
            }),
            importedData.dependencies
                ? saveDependenciesD1(serializeDependencies(dependencies)).catch(err => {
                    console.warn('Failed to save imported dependencies:', err);
                  })
                : Promise.resolve()
        ]);
        
        const colorPersistencePromises = [
            saveProjectColorOverrides().catch(err => {
                console.error('Failed to save imported project color overrides:', err);
                throw new Error('Failed to save project color overrides');
            }),
            saveTagColorOverrides().catch(err => {
                console.error('Failed to save imported tag color overrides:', err);
                throw new Error('Failed to save tag color overrides');
            }),
            saveTagColors({ force: true }).catch(err => {
                console.error('Failed to save imported tag colors:', err);
                throw new Error('Failed to save tag colors');
            }),
            saveTagColorsVersion({ force: true }).catch(err => {
                console.error('Failed to save imported tag color version:', err);
                throw new Error('Failed to save tag color version');
            })
        ];

        colorPersistencePromises.push(saveProjectColors({ showError: false }).catch(err => {
            console.error('Failed to save imported project colors:', err);
            throw new Error('Failed to save project colors');
        }));

        await Promise.all(colorPersistencePromises);
        
        // Close modal
        closeImportDataModal();
        
        // Show success notification
        showSuccessNotification(t('import.success'));
        
        // Refresh UI
        render();
        
        // Navigate to dashboard to show imported data
        window.location.hash = '#dashboard';
        showPage('dashboard');
        
    } catch (error) {
        console.error('Import error:', error);
        showErrorNotification(t('import.errorFailed') + ': ' + (error.message || 'Unknown error'));
    }
}

// Standard summary report (docs/plans/2026-10-03-summary-reports.md).
// Every figure is computed once (reportMetrics.js). "Generate report" opens the
// presentation view (reportView.js, "Download PDF" prints it); "Save to Docs"
// turns the SAME facts into an editable Docs page (reportDoc.js), so both
// outputs always show the same numbers.
const summaryReportState = {
    el: null,
    report: null,
    generatedAt: null,
    title: '',
    saving: false,
    refreshing: false,
    printTheme: null,
    onKeydown: null,
    onBeforePrint: null,
    onAfterPrint: null
};

// Absolute app URL (no hash) for the report's "Open in Tasks/Projects" links.
function getSummaryReportBaseUrl() {
    return `${window.location.origin}${window.location.pathname}`;
}

function computeCurrentSummaryReport() {
    const { isoDate } = getNowPartsInUserTimeZone();
    return computeSummaryReport(tasks, projects, { today: isoDate });
}

function summaryReportIcon(name) {
    const paths = {
        back: '<polyline points="15 18 9 12 15 6"/>',
        refresh: '<path d="M21 12a9 9 0 1 1-3-6.7"/><polyline points="21 4 21 10 15 10"/>',
        docs: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/>',
        pdf: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>'
    };
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[name]}</svg>`;
}

function renderSummaryReportView() {
    const el = summaryReportState.el;
    if (!el || !summaryReportState.report) return;
    el.querySelector('.rv-shell').innerHTML = renderSummaryReportHtml(summaryReportState.report, {
        t,
        locale: getLocale(),
        title: summaryReportState.title || t('report.view.defaultTitle'),
        generatedAt: summaryReportState.generatedAt,
        baseUrl: getSummaryReportBaseUrl(),
        tolerance: PACE_TOLERANCE
    });
}

function bindSummaryReportViewEvents(el) {
    const tip = el.querySelector('.rv-tip');
    const showTip = (target, ev) => {
        tip.innerHTML = target.getAttribute('data-tip');
        tip.hidden = false;
        const r = tip.getBoundingClientRect();
        let left = ev.clientX + 14;
        let top = ev.clientY + 14;
        if (left + r.width > window.innerWidth - 8) left = ev.clientX - r.width - 14;
        if (top + r.height > window.innerHeight - 8) top = ev.clientY - r.height - 14;
        tip.style.left = `${Math.max(8, left)}px`;
        tip.style.top = `${Math.max(8, top)}px`;
    };
    const onPointer = (ev) => {
        const target = ev.target.closest?.('[data-tip]');
        if (target && el.contains(target)) showTip(target, ev);
        else tip.hidden = true;
    };
    el.addEventListener('pointermove', onPointer);
    el.addEventListener('pointerdown', onPointer);
    el.addEventListener('pointerleave', () => { tip.hidden = true; });
    el.querySelector('.rv-scroller').addEventListener('scroll', () => { tip.hidden = true; }, { passive: true });

    // Editable report title: plain text only, Enter commits.
    el.addEventListener('input', (ev) => {
        if (!ev.target.matches?.('[data-rv-title]')) return;
        summaryReportState.title = ev.target.textContent.replace(/\s+/g, ' ').trim().slice(0, 140);
    });
    el.addEventListener('keydown', (ev) => {
        if (ev.target.matches?.('[data-rv-title]') && ev.key === 'Enter') {
            ev.preventDefault();
            ev.target.blur();
        }
    });
    el.addEventListener('paste', (ev) => {
        if (!ev.target.matches?.('[data-rv-title]')) return;
        ev.preventDefault();
        const text = (ev.clipboardData?.getData('text/plain') || '').replace(/\s+/g, ' ');
        document.execCommand('insertText', false, text);
    });
    el.addEventListener('focusout', (ev) => {
        if (!ev.target.matches?.('[data-rv-title]')) return;
        if (!summaryReportState.title) ev.target.textContent = t('report.view.defaultTitle');
    });
}

function generateSummaryReport() {
    openSummaryReportView();
}

function openSummaryReportView() {
    try {
        summaryReportState.report = computeCurrentSummaryReport();
        summaryReportState.generatedAt = new Date();
    } catch (error) {
        console.error('[SummaryReport] Failed to compute report', error);
        showErrorNotification(t('report.summary.toast.failed', { error: error?.message || 'Unknown error' }));
        return;
    }

    if (!summaryReportState.el) {
        const el = document.createElement('div');
        el.id = 'report-view';
        el.className = 'report-view';
        el.setAttribute('role', 'dialog');
        el.setAttribute('aria-modal', 'true');
        el.setAttribute('aria-label', t('report.view.toolbarTitle'));
        el.innerHTML = `
            <div class="rv-toolbar">
                <button type="button" class="rv-btn" data-action="closeSummaryReport">${summaryReportIcon('back')}<span>${escapeHtml(t('report.view.back'))}</span></button>
                <span class="rv-toolbar-title">${escapeHtml(t('report.view.toolbarTitle'))}</span>
                <span class="rv-toolbar-spacer"></span>
                <div class="rv-toolbar-actions">
                    <button type="button" class="rv-btn" data-action="refreshSummaryReport" title="${escapeHtml(t('report.view.refreshHint'))}">${summaryReportIcon('refresh')}<span>${escapeHtml(t('report.view.refresh'))}</span></button>
                    <button type="button" class="rv-btn" data-action="saveSummaryReportToDocs">${summaryReportIcon('docs')}<span>${escapeHtml(t('report.view.saveDocs'))}</span></button>
                    <button type="button" class="rv-btn primary" data-action="printSummaryReport" title="${escapeHtml(t('report.view.pdfHint'))}">${summaryReportIcon('pdf')}<span>${escapeHtml(t('report.view.downloadPdf'))}</span></button>
                </div>
            </div>
            <div class="rv-scroller"><div class="rv-shell"></div></div>
            <div class="rv-tip" hidden></div>`;
        document.body.appendChild(el);
        bindSummaryReportViewEvents(el);
        summaryReportState.el = el;

        summaryReportState.onKeydown = (ev) => {
            if (ev.key !== 'Escape' || !summaryReportState.el) return;
            if (ev.target?.matches?.('[data-rv-title]')) { ev.target.blur(); return; }
            ev.stopPropagation();
            closeSummaryReport();
        };
        // Ctrl/Cmd+P while the view is open prints the report in the light theme too.
        summaryReportState.onBeforePrint = () => forceSummaryReportLightTheme();
        summaryReportState.onAfterPrint = () => restoreSummaryReportTheme();
        document.addEventListener('keydown', summaryReportState.onKeydown, true);
        window.addEventListener('beforeprint', summaryReportState.onBeforePrint);
        window.addEventListener('afterprint', summaryReportState.onAfterPrint);
    }

    document.body.classList.add('report-view-open');
    renderSummaryReportView();
    summaryReportState.el.querySelector('.rv-scroller').scrollTop = 0;
    summaryReportState.el.querySelector('[data-action="closeSummaryReport"]')?.focus();
}

function closeSummaryReport() {
    const el = summaryReportState.el;
    if (!el) return;
    document.removeEventListener('keydown', summaryReportState.onKeydown, true);
    window.removeEventListener('beforeprint', summaryReportState.onBeforePrint);
    window.removeEventListener('afterprint', summaryReportState.onAfterPrint);
    restoreSummaryReportTheme();
    el.remove();
    document.body.classList.remove('report-view-open');
    summaryReportState.el = null;
    summaryReportState.report = null;
    summaryReportState.generatedAt = null;
    summaryReportState.title = '';
}

// Refresh = pull the latest data from the server (changes made on another
// device or by someone else), then recompute. Skips the fetch while local saves
// are in flight so it never overwrites them; offline falls back to loaded data.
async function refreshSummaryReport() {
    const el = summaryReportState.el;
    if (!el || summaryReportState.refreshing) return;
    summaryReportState.refreshing = true;
    const button = el.querySelector('[data-action="refreshSummaryReport"]');
    const label = button?.querySelector('span');
    if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        if (label) label.textContent = t('report.view.refreshing');
    }
    const startedAt = Date.now();
    let changed = false;
    let offline = false;
    try {
        if (pendingSaves === 0) {
            try {
                const fresh = await loadAllData({
                    feedback: {
                        limitPending: FEEDBACK_OPEN_ITEMS_PER_PAGE,
                        limitDone: FEEDBACK_ITEMS_PER_PAGE,
                        cacheKey: FEEDBACK_CACHE_KEY,
                        // Feedback is irrelevant to the report and can be slow cold; read it from cache.
                        preferCache: true
                    }
                });
                // loadAll swallows network errors and returns empty arrays: never let that
                // wipe the loaded workspace. Empty-while-we-have-data = treat as offline.
                const looksEmpty = !Array.isArray(fresh?.tasks) || !Array.isArray(fresh?.projects)
                    || (fresh.tasks.length === 0 && fresh.projects.length === 0 && (tasks.length > 0 || projects.length > 0));
                if (looksEmpty) offline = true;
                else changed = applyFreshAllData(fresh);
            } catch (error) {
                console.error('[SummaryReport] Could not fetch fresh data', error);
                offline = true;
            }
        }
        summaryReportState.report = computeCurrentSummaryReport();
        summaryReportState.generatedAt = new Date();
        // Keep the spinner visible long enough to read as feedback.
        const elapsed = Date.now() - startedAt;
        if (elapsed < 450) await new Promise(resolve => setTimeout(resolve, 450 - elapsed));
        if (!summaryReportState.el) return;
        const scroller = summaryReportState.el.querySelector('.rv-scroller');
        const scrollTop = scroller.scrollTop;
        renderSummaryReportView();
        scroller.scrollTop = scrollTop;
        if (offline) showErrorNotification(t('report.view.toast.offline'));
        else showSuccessNotification(t(changed ? 'report.view.toast.updated' : 'report.view.toast.upToDate'));
    } catch (error) {
        console.error('[SummaryReport] Failed to refresh report', error);
        showErrorNotification(t('report.summary.toast.failed', { error: error?.message || 'Unknown error' }));
    } finally {
        summaryReportState.refreshing = false;
        if (button?.isConnected) {
            button.disabled = false;
            button.classList.remove('is-loading');
            button.removeAttribute('aria-busy');
            if (label) label.textContent = t('report.view.refresh');
        }
    }
}

function forceSummaryReportLightTheme() {
    const root = document.documentElement;
    if (summaryReportState.printTheme) return;
    const current = root.getAttribute('data-theme');
    if (current === 'dark') {
        summaryReportState.printTheme = current;
        root.setAttribute('data-theme', 'light');
    }
}

function restoreSummaryReportTheme() {
    if (!summaryReportState.printTheme) return;
    document.documentElement.setAttribute('data-theme', summaryReportState.printTheme);
    summaryReportState.printTheme = null;
}

function printSummaryReport() {
    if (!summaryReportState.el) return;
    forceSummaryReportLightTheme();
    // Let the light tokens apply before the print snapshot is taken.
    requestAnimationFrame(() => {
        window.print();
    });
}

async function saveSummaryReportToDocs() {
    if (summaryReportState.saving || !summaryReportState.report) return;
    summaryReportState.saving = true;
    const button = summaryReportState.el?.querySelector('[data-action="saveSummaryReportToDocs"]');
    if (button) button.disabled = true;
    try {
        const customTitle = summaryReportState.title && summaryReportState.title !== t('report.view.defaultTitle')
            ? summaryReportState.title
            : '';
        const { title, contentJson } = buildSummaryReportDoc(summaryReportState.report, { t, locale: getLocale(), title: customTitle, baseUrl: getSummaryReportBaseUrl() });

        await ensureDocsListLoaded();
        const state = getDocsState();
        const response = await createProjectDoc({
            title,
            contentJson,
            sortOrder: state.items.reduce((max, item) => Math.max(max, Number(item.sortOrder || 0)), 0) + 1,
            treeOrder: state.items.reduce((max, item) => Math.max(max, Number(item.treeOrder || 0)), 0) + 1,
            parentId: '',
            icon: '📊',
            widthMode: 'wide',
            historyMeta: { origin: 'summary_report' }
        });
        if (!response?.item?.id) throw new Error(t('report.summary.toast.noPage'));

        const created = mergeDocsRecord(null, response.item);
        state.currentDocId = created.id;
        setDocsBaseline(null, created);
        syncDocsRouteState({
            docId: created.id,
            slug: created.slug || slugifyProjectDocTitle(created.title),
            mode: 'view'
        });
        await ensureDocLoaded(null, created.id);
        closeSummaryReport();
        refreshDocsSurfaces();
        if (!isDocsPageActive()) showPage('docs');
        renderDocsPage();
        showSuccessNotification(t('report.summary.toast.created'));
    } catch (error) {
        console.error('[SummaryReport] Failed to save report to Docs', error);
        showErrorNotification(t('report.summary.toast.failed', { error: error?.message || 'Unknown error' }));
    } finally {
        summaryReportState.saving = false;
        if (button && button.isConnected) button.disabled = false;
    }
}

async function generateReport() {
    // Show loading notification
    showNotification('Generando reporte...', 'info');

    try {
        // Dynamic import to avoid blocking app load (docx library is ~500KB from CDN)
        const { generateWordReport } = await import('./src/services/reportGenerator.js');
        const result = await generateWordReport(projects, tasks);

        if (result.success) {
            showNotification(`\u{2705} Reporte generado: ${result.filename}`, 'success');

            // Show summary in console for debugging
            console.log('Report Summary:', {
                activeProjects: result.insights.activeProjectsCount,
                completedTasks: `${result.insights.completedTasks}/${result.insights.totalTasks}`,
                progress: `${result.insights.completionPercent}%`
            });
        } else {
            throw new Error(result.error);
        }
    } catch (error) {
        console.error('Error generating report:', error);
        showNotification('❌ Error al generar el reporte: ' + error.message, 'error');
    }
}

function updateCounts() {
    const activeTasks = tasks.filter((task) => !task.archivedAt);
    const activeProjects = projects.filter((project) => !project.archivedAt);
    document.getElementById("projects-count").textContent = activeProjects.length;
    document.getElementById("tasks-count").textContent = activeTasks.length;
    updateAutomationsCount();
    
    // Legacy dashboard stats (keep for backward compatibility)
    const activeProjectsEl = document.getElementById("active-projects");
    const pendingTasksEl = document.getElementById("pending-tasks");
    const completedTasksEl = document.getElementById("completed-tasks");
    
    if (activeProjectsEl) activeProjectsEl.textContent = projects.length;
    if (pendingTasksEl) pendingTasksEl.textContent = tasks.filter((t) => t.status !== "done").length;
    if (completedTasksEl) completedTasksEl.textContent = tasks.filter((t) => t.status === "done").length;

    // Kanban counts
    const todoCountEl = document.getElementById("todo-count");
    const progressCountEl = document.getElementById("progress-count");
    const reviewCountEl = document.getElementById("review-count");
    const doneCountEl = document.getElementById("done-count");
    
    if (todoCountEl) todoCountEl.textContent = activeTasks.filter((t) => t.status === "todo").length;
    if (progressCountEl) progressCountEl.textContent = activeTasks.filter((t) => t.status === "progress").length;
    if (reviewCountEl) reviewCountEl.textContent = activeTasks.filter((t) => t.status === "review").length;
    if (doneCountEl) doneCountEl.textContent = activeTasks.filter((t) => t.status === "done").length;
    
    // Feedback count
    const feedbackCount = feedbackItems.filter(f => f.status === 'open').length;
    const feedbackCountEl = document.getElementById("feedback-count");
    if (feedbackCountEl) feedbackCountEl.textContent = feedbackCount;

    updateNotificationState();
}

function updateAutomationsCount() {
    const automationsCountEl = document.getElementById("automations-count");
    if (automationsCountEl) automationsCountEl.textContent = Array.isArray(automations) ? automations.length : 0;
}

let currentSort = { column: null, direction: "asc" };

// Mobile card sort state (independent from desktop list sort)
let mobileSortState = { field: 'endDate', dir: 'asc' };

function sortTable(column) {
    if (currentSort.column === column) {
        currentSort.direction =
            currentSort.direction === "asc" ? "desc" : "asc";
    } else {
        currentSort.column = column;
        currentSort.direction = "asc";
    }

    document.querySelectorAll(".tasks-table th .sort-indicator").forEach((span) => {
        span.textContent = "↕";
        span.style.opacity = "0.5";
    });

    const indicator = document.getElementById(`sort-${column}`);
    if (indicator) {
        indicator.textContent = currentSort.direction === "asc" ? "↑" : "↓";
        indicator.style.opacity = "1";
    }

    renderListView();
}

// Rows last rendered in the list view, in on-screen order (filters + sort applied).
// The Excel export uses them so the file matches what the user is looking at.
let lastListViewRows = [];

function renderListView() {
    const tbody = document.getElementById("tasks-table-body");

    const source = typeof getFilteredTasks === "function" ? getFilteredTasks() : tasks.slice();
    const cutoff = getKanbanUpdatedCutoffTime(window.kanbanUpdatedFilter);

    // Detect single-project filter for drag-and-drop ordering
    const isSingleProject = filterState.projects && filterState.projects.size === 1;
    const singleProjectId = isSingleProject ? [...filterState.projects][0] : null;

    // Default the sort for the active Kai query, unless the user has explicitly
    // picked a column. Duplicate-title groups by name; a recency query ("the 5
    // most recently modified") lands newest-first on Updated/Created so the top
    // row is the most recent — otherwise a stale column sort makes it look random.
    const kaiRecencyColumn = filterState.kaiSort === 'recent-created' ? 'createdAt'
        : filterState.kaiSort === 'recent-updated' ? 'updatedAt'
        : null;
    const effectiveSort = currentSort.column
        ? currentSort
        : filterState.duplicateTitles
        ? { column: "title", direction: "asc" }
        : kaiRecencyColumn
        ? { column: kaiRecencyColumn, direction: "desc" }
        : currentSort;

    // Reflect the effective sort in the column-header arrows (covers the default
    // recency sort, not just user clicks).
    document.querySelectorAll('.tasks-table th .sort-indicator').forEach((span) => {
        span.textContent = '↕';
        span.style.opacity = '0.5';
    });
    if (effectiveSort.column) {
        const ind = document.getElementById(`sort-${effectiveSort.column}`);
        if (ind) {
            ind.textContent = effectiveSort.direction === 'asc' ? '↑' : '↓';
            ind.style.opacity = '1';
        }
    }

    // Use list view module for filtering and sorting
    const listData = prepareListViewData(source, {
        currentSort: isSingleProject ? null : effectiveSort, // Disable column sort when manual ordering
        projects: projects,
        updatedCutoff: cutoff,
        getTaskUpdatedTime: getTaskUpdatedTime
    });

    let rows = listData.tasks;

    // In single-project mode, sort by projectOrder if any task has it
    const hasManualOrder = isSingleProject && rows.some(t => typeof t.projectOrder === 'number');
    if (hasManualOrder) {
        rows = [...rows].sort((a, b) => {
            const ao = typeof a.projectOrder === 'number' ? a.projectOrder : 9999;
            const bo = typeof b.projectOrder === 'number' ? b.projectOrder : 9999;
            return ao - bo;
        });
    }

    lastListViewRows = rows;

    // Show/hide drag handle column header
    const dragTh = document.getElementById('list-drag-th');
    if (dragTh) dragTh.style.display = isSingleProject ? '' : 'none';

    // Show/hide Auto-sort button
    syncListViewAutoSortBtn(singleProjectId, isSingleProject, hasManualOrder);

    const listCountText = t('tasks.list.count', { count: rows.length }) || `${rows.length} results`;
    const listCountEl = document.getElementById('tasks-list-count');
    if (listCountEl) {
        listCountEl.textContent = listCountText;
    }
    const listCountBottomEl = document.getElementById('tasks-list-count-bottom');
    if (listCountBottomEl) {
        listCountBottomEl.textContent = listCountText;
    }

    if (tbody) {
        // Use module function for HTML generation
        const helpers = {
            escapeHtml,
            formatDate,
            getTagColor,
            getTagTextColor,
            getProjectColor,
            getPriorityLabel,
            getStatusLabel,
            formatTaskUpdatedDateTime,
            projects,
            automationLabel: t('automations.recurrence.generatedBy'),
            noProjectText: t('tasks.noProject'),
            noDateText: t('tasks.noDate'),
            archivedLabel: t('projects.status.archived'),
            showDragHandle: isSingleProject
        };
        tbody.innerHTML = generateListViewHTML(rows, helpers);
    }

    // Set up drag-and-drop when single project is filtered
    if (isSingleProject && singleProjectId !== null) {
        setupListViewDragDrop(singleProjectId);
    }

    // Also render mobile cards (shown on mobile, hidden on desktop)
    // Apply mobile-specific sort (independent from desktop table sort)
    renderMobileCardsPremium(sortRowsForMobile(rows));

    // Update mass edit UI to reflect current selection state
    updateMassEditUI();

    // Initialize field button click listeners (once)
    initMassEditFieldButtons();
}

async function exportTasksToExcel() {
    const rows = lastListViewRows;
    if (!rows.length) {
        showNotification(t('tasks.export.empty'), 'info');
        return;
    }
    const buttons = document.querySelectorAll('[data-action="exportTasksToExcel"]');
    buttons.forEach((btn) => { btn.disabled = true; });
    try {
        const blob = await buildTasksXlsx(rows, { t, projects, getStatusLabel, getPriorityLabel });
        const rawUser = window.authSystem?.getCurrentUser?.()?.username || '';
        const userPart = String(rawUser).replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '');
        const filename = `nautilus-tasks-${userPart ? `${userPart}-` : ''}${getTodayISO(new Date(), getUserTimeZone())}.xlsx`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        showSuccessNotification(t('tasks.export.success', { count: rows.length }));
    } catch (error) {
        console.error('[ExcelExport] Failed to export tasks', error);
        showErrorNotification(t('tasks.export.failed'));
    } finally {
        buttons.forEach((btn) => { btn.disabled = false; });
    }
}

function setupListViewDragDrop(projectId) {
    const tbody = document.getElementById('tasks-table-body');
    if (!tbody) return;

    // On touch devices the `draggable` attribute suppresses the subsequent click
    // event in iOS Chrome, preventing the task modal from opening. iOS Chrome does
    // not reliably report `(pointer: coarse)`, so use getIsMobileCached() which also
    // matches `(max-width: 768px)`. Drag-to-reorder requires a mouse anyway.
    if (getIsMobileCached()) {
        tbody.querySelectorAll('[draggable]').forEach(el => el.removeAttribute('draggable'));
        return;
    }

    let draggingViaHandle = false;
    let dragSrc = null;

    const getRows = () => [...tbody.querySelectorAll('tr.task-row[data-task-id]')];

    // Keep dropping reliable even when pointer is between rows.
    if (tbody.dataset.listDragFallback !== '1') {
        tbody.addEventListener('dragover', (e) => { e.preventDefault(); });
        tbody.dataset.listDragFallback = '1';
    }

    getRows().forEach(row => {
        const handle = row.querySelector('.list-drag-handle');
        if (handle) {
            handle.addEventListener('mousedown', () => { draggingViaHandle = true; });
            handle.addEventListener('touchstart', () => { draggingViaHandle = true; }, { passive: true });
            handle.addEventListener('click', (e) => e.stopPropagation());
        }

        row.addEventListener('dragstart', (e) => {
            if (!draggingViaHandle) { e.preventDefault(); return; }
            dragSrc = row;
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', row.dataset.taskId);
            setTimeout(() => row.classList.add('dragging'), 0);
        });

        row.addEventListener('dragend', () => {
            draggingViaHandle = false;
            dragSrc?.classList.remove('dragging');
            getRows().forEach(r => r.classList.remove('drag-over-top', 'drag-over-bottom'));
            dragSrc = null;
        });

        row.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (!dragSrc || row === dragSrc) return;
            const mid = row.getBoundingClientRect().top + row.getBoundingClientRect().height / 2;
            getRows().forEach(r => r.classList.remove('drag-over-top', 'drag-over-bottom'));
            row.classList.add(e.clientY < mid ? 'drag-over-top' : 'drag-over-bottom');
        });

        row.addEventListener('dragleave', (e) => {
            if (!row.contains(e.relatedTarget)) {
                row.classList.remove('drag-over-top', 'drag-over-bottom');
            }
        });

        row.addEventListener('drop', (e) => {
            e.preventDefault();
            if (!dragSrc || row === dragSrc) return;
            const before = row.classList.contains('drag-over-top');
            row.classList.remove('drag-over-top', 'drag-over-bottom');
            if (before) {
                tbody.insertBefore(dragSrc, row);
            } else {
                tbody.insertBefore(dragSrc, row.nextSibling);
            }
            // Persist new order and re-render so Auto-sort button appears
            const reorderUpdates = getRows().map((el, idx) => ({
                id: parseInt(el.dataset.taskId, 10),
                projectOrder: idx
            }));
            saveTasksReorder(reorderUpdates).catch(err => {
                console.error('Failed to save task reorder:', err);
                showErrorNotification(t('error.saveChangesFailed'));
            });
            syncListViewAutoSortBtn(projectId, true, true);
        });
    });
}

function resetListViewTaskOrder(projectId) {
    tasks.forEach(task => {
        if (task.projectId === projectId) {
            delete task.projectOrder;
        }
    });
    const reorderUpdates = tasks.filter(t => t.projectId === projectId).map(t => ({ id: t.id, projectOrder: null }));
    saveTasksReorder(reorderUpdates).catch(err => {
        console.error('Failed to save task order reset:', err);
        showErrorNotification(t('error.saveChangesFailed'));
    });
    renderListView();
}

function syncListViewAutoSortBtn(projectId, isSingleProject = false, hasManualOrder = null) {
    const resetBtn = document.getElementById('list-view-reset-sort-btn');
    if (!resetBtn) return;

    const projectIdNum = Number(projectId);
    const validProject = Number.isInteger(projectIdNum);
    const manualOrderActive = typeof hasManualOrder === 'boolean'
        ? hasManualOrder
        : (validProject && tasks.some(task => task.projectId === projectIdNum && typeof task.projectOrder === 'number'));

    if (isSingleProject && validProject && manualOrderActive) {
        resetBtn.style.display = '';
        resetBtn.dataset.param = String(projectIdNum);
        resetBtn.title = t('projects.details.resetSortTitle') || 'Reset to automatic sort by priority';
        resetBtn.textContent = `↕ ${t('projects.details.resetSort') || 'Auto-sort'}`;
        return;
    }

    resetBtn.style.display = 'none';
}

/**
 * Initializes click listeners for mass edit field buttons (status, priority, etc.)
 * Only adds listeners once to avoid duplicates
 */
let massEditFieldButtonsInitialized = false;
function initMassEditFieldButtons() {
    if (massEditFieldButtonsInitialized) return;
    massEditFieldButtonsInitialized = true;
    
    const fieldButtons = [
        { id: 'mass-edit-status-btn', field: 'status' },
        { id: 'mass-edit-priority-btn', field: 'priority' },
        { id: 'mass-edit-dates-btn', field: 'dates' },
        { id: 'mass-edit-project-btn', field: 'project' },
        { id: 'mass-edit-tags-btn', field: 'tags' }
    ];
    
    fieldButtons.forEach(({ id, field }) => {
        const btn = document.getElementById(id);
        if (btn) {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                openMassEditPopover(field, btn);
            });
        }
    });

    // Add delete button handler
    const deleteBtn = document.getElementById('mass-edit-delete-btn');
    if (deleteBtn) {
        deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            massDelete();
        });
    }
}

// ================================
// MASS EDIT FUNCTIONALITY
// ================================

/**
 * Toggles selection of a task (handles Ctrl/Cmd and Shift modifiers)
 */
function toggleTaskSelection(taskId, event) {
    const isShiftHeld = event?.shiftKey;
    const isCtrlOrCmdHeld = event?.ctrlKey || event?.metaKey;

    if (isShiftHeld && massEditState.lastSelectedId !== null) {
        // Shift-click: Select range
        selectTaskRange(massEditState.lastSelectedId, taskId);
    } else if (isCtrlOrCmdHeld) {
        // Ctrl/Cmd-click: Toggle individual
        if (massEditState.selectedTaskIds.has(taskId)) {
            massEditState.selectedTaskIds.delete(taskId);
        } else {
            massEditState.selectedTaskIds.add(taskId);
            massEditState.lastSelectedId = taskId;
        }
    } else {
        // Regular click: Toggle individual
        if (massEditState.selectedTaskIds.has(taskId)) {
            massEditState.selectedTaskIds.delete(taskId);
            if (massEditState.lastSelectedId === taskId) {
                massEditState.lastSelectedId = null;
            }
        } else {
            massEditState.selectedTaskIds.add(taskId);
            massEditState.lastSelectedId = taskId;
        }
    }

    updateMassEditUI();
}

/**
 * Selects range of tasks between two IDs (for Shift-click)
 * Uses getVisibleTasks() so range only includes tasks actually shown (incl. Updated filter)
 */
function selectTaskRange(startId, endId) {
    const visibleTasks = getVisibleTasks();
    const startIndex = visibleTasks.findIndex(t => t.id === startId);
    const endIndex = visibleTasks.findIndex(t => t.id === endId);

    if (startIndex === -1 || endIndex === -1) return;

    const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex];

    for (let i = from; i <= to; i++) {
        massEditState.selectedTaskIds.add(visibleTasks[i].id);
    }

    massEditState.lastSelectedId = endId;
}

/**
 * Gets truly visible tasks (applies ALL filters including Updated filter)
 * This is the authoritative source for what tasks are actually visible in list view
 */
function getVisibleTasks() {
    let filtered = getFilteredTasks();
    
    // CRITICAL: Also apply the "Updated" filter (kanbanUpdatedFilter)
    // This filter is NOT in filterState, it's a separate global
    const cutoff = getKanbanUpdatedCutoffTime(window.kanbanUpdatedFilter);
    if (cutoff !== null) {
        filtered = filtered.filter(t => getTaskUpdatedTime(t) >= cutoff);
    }
    
    return filtered;
}

/**
 * Selects all filtered tasks
 */
function selectAllFilteredTasks() {
    // Use getVisibleTasks() to include ALL filters (including Updated filter)
    const visibleTasks = getVisibleTasks();
    
    // CRITICAL: Clear any selections that are no longer visible
    // Then select only the currently visible tasks
    massEditState.selectedTaskIds.clear();
    visibleTasks.forEach(task => massEditState.selectedTaskIds.add(task.id));
    
    if (visibleTasks.length > 0) {
        massEditState.lastSelectedId = visibleTasks[0].id;
    }
    updateMassEditUI();
}

/**
 * Clears all selected tasks
 */
function clearMassEditSelection() {
    massEditState.selectedTaskIds.clear();
    massEditState.lastSelectedId = null;
    massEditState.pendingChanges = [];
    updateMassEditUI();
    updatePendingChangesUI();
}

/**
 * Updates the mass edit UI (toolbar, checkboxes, row highlights)
 */
function updateMassEditUI() {
    // Use getVisibleTasks() to include ALL filters (including Updated filter)
    const visibleTasks = getVisibleTasks();
    const totalVisible = visibleTasks.length;
    
    // CRITICAL FIX: Only count selected tasks that are CURRENTLY VISIBLE
    // This prevents showing "13 selected" when only 3 tasks are visible after filtering
    const visibleSelectedIds = new Set();
    visibleTasks.forEach(task => {
        if (massEditState.selectedTaskIds.has(task.id)) {
            visibleSelectedIds.add(task.id);
        }
    });
    const selectedCount = visibleSelectedIds.size;

    // Update toolbar visibility - only show if there are VISIBLE selected tasks
    const toolbar = document.getElementById('mass-edit-toolbar');
    if (toolbar) {
        toolbar.style.display = selectedCount > 0 ? 'flex' : 'none';
    }

    // Update count text - show count of VISIBLE selected tasks
    const countText = document.getElementById('mass-edit-count');
    if (countText) {
        countText.textContent = t('tasks.massEdit.selected', {
            count: selectedCount,
            total: totalVisible
        });
    }

    // Update "select all" checkbox state
    const selectAllCheckbox = document.getElementById('mass-edit-select-all');
    if (selectAllCheckbox) {
        if (selectedCount === 0) {
            selectAllCheckbox.checked = false;
            selectAllCheckbox.indeterminate = false;
        } else if (selectedCount === totalVisible) {
            selectAllCheckbox.checked = true;
            selectAllCheckbox.indeterminate = false;
        } else {
            selectAllCheckbox.checked = false;
            selectAllCheckbox.indeterminate = true;
        }
    }

    // Update table header checkbox
    const tableSelectAll = document.getElementById('table-select-all');
    if (tableSelectAll) {
        const visibleSelectedCount = visibleTasks.filter(t => 
            massEditState.selectedTaskIds.has(t.id)
        ).length;

        if (visibleSelectedCount === 0) {
            tableSelectAll.checked = false;
            tableSelectAll.indeterminate = false;
        } else if (visibleSelectedCount === totalVisible) {
            tableSelectAll.checked = true;
            tableSelectAll.indeterminate = false;
        } else {
            tableSelectAll.checked = false;
            tableSelectAll.indeterminate = true;
        }
    }

    // Update individual row checkboxes and highlighting
    const tbody = document.getElementById('tasks-table-body');
    if (tbody) {
        const rows = tbody.querySelectorAll('tr[data-task-id]');
        rows.forEach(row => {
            const taskId = parseInt(row.dataset.taskId, 10);
            const checkbox = row.querySelector('.task-select-checkbox');
            const isSelected = massEditState.selectedTaskIds.has(taskId);

            if (checkbox) {
                checkbox.checked = isSelected;
            }

            if (isSelected) {
                row.classList.add('mass-edit-selected');
            } else {
                row.classList.remove('mass-edit-selected');
            }
        });
    }

    // Also update pending changes UI
    updatePendingChangesUI();
}

/**
 * Opens mass edit popover for a specific field
 */
function openMassEditPopover(field, buttonElement) {
    // Close any existing popover
    closeMassEditPopover();

    const popover = createMassEditPopover(field);
    document.body.appendChild(popover);

    // Position popover below button
    const buttonRect = buttonElement.getBoundingClientRect();
    popover.style.left = `${buttonRect.left}px`;
    popover.style.top = `${buttonRect.bottom + 8}px`;

    // Adjust if off-screen
    const popoverRect = popover.getBoundingClientRect();
    if (popoverRect.right > window.innerWidth) {
        popover.style.left = `${window.innerWidth - popoverRect.width - 20}px`;
    }
    if (popoverRect.bottom > window.innerHeight) {
        popover.style.top = `${buttonRect.top - popoverRect.height - 8}px`;
    }

    // Store current field
    popover.dataset.field = field;

    // RESTORE PREVIOUS SELECTION: Check if there's a pending change for this field
    const pendingChange = massEditState.pendingChanges.find(c => c.field === field);
    if (pendingChange) {
        if (field === 'status' && pendingChange.value) {
            const radio = popover.querySelector(`input[name="mass-edit-status"][value="${pendingChange.value}"]`);
            if (radio) radio.checked = true;
        } else if (field === 'priority' && pendingChange.value) {
            const radio = popover.querySelector(`input[name="mass-edit-priority"][value="${pendingChange.value}"]`);
            if (radio) radio.checked = true;
        } else if (field === 'project' && pendingChange.value !== undefined) {
            const val = pendingChange.value === null ? 'none' : pendingChange.value;
            const radio = popover.querySelector(`input[name="mass-edit-project"][value="${val}"]`);
            if (radio) radio.checked = true;
        } else if (field === 'dates') {
            // Values will be set after Flatpickr initialization below
        } else if (field === 'tags' && pendingChange.tags && pendingChange.tags.length > 0) {
            const modeRadio = popover.querySelector(`input[name="mass-edit-tags-mode"][value="${pendingChange.mode}"]`);
            if (modeRadio) {
                modeRadio.checked = true;
                const modeOptions = popover.querySelectorAll('.mass-edit-tags-mode-option');
                modeOptions.forEach(opt => opt.classList.remove('active'));
                modeRadio.closest('.mass-edit-tags-mode-option')?.classList.add('active');
            }
            const tagsList = popover.querySelector('#mass-edit-tags-list');
            if (tagsList) {
                tagsList.innerHTML = '';
                pendingChange.tags.forEach(tagName => {
                    const tagColor = getTagColor(tagName);
                    const tagItem = document.createElement('div');
                    tagItem.className = 'mass-edit-tag-item';
                    tagItem.dataset.tagName = tagName;
                    applyTintedTagStyle(tagItem, tagColor);
                    tagItem.innerHTML = `<span>${escapeHtml(tagName.toUpperCase())}</span><button class="mass-edit-tag-remove">×</button>`;
                    const removeBtn = tagItem.querySelector('.mass-edit-tag-remove');
                    if (removeBtn) removeBtn.style.color = 'inherit';
                    removeBtn?.addEventListener('click', () => {
                        tagItem.remove();
                        const existingTag = Array.from(popover.querySelectorAll('.mass-edit-existing-tag')).find(el => el.dataset.tag === tagName);
                        if (existingTag) existingTag.classList.remove('selected');
                        updateMassEditPopoverButtonStates(popover);
                    });
                    tagsList.appendChild(tagItem);
                });
                pendingChange.tags.forEach(tagName => {
                    const existingTag = Array.from(popover.querySelectorAll('.mass-edit-existing-tag')).find(el => el.dataset.tag === tagName);
                    if (existingTag) existingTag.classList.add('selected');
                });
            }
        }
    }

    // Initialize Flatpickr for date fields
    if (field === 'dates') {
        const flatpickrFn = window.flatpickr;
        if (typeof flatpickrFn === 'function') {
            const flatpickrLocale = getFlatpickrLocale();
            const dateConfig = datePickerOptions(flatpickrLocale);

            const startInput = popover.querySelector('#mass-edit-start-date');
            const endInput = popover.querySelector('#mass-edit-end-date');

            // Shared typing mask; a complete date syncs into flatpickr and the ISO value.
            const addMassEditDateMask = (input, fp) => {
                attachDateMask(input, {
                    onComplete(iso) {
                        if (fp) fp.setDate(new Date(`${iso}T00:00:00`), false);
                        input.dataset.isoValue = iso;
                    }
                });
            };

            if (startInput) {
                const fpStart = flatpickrFn(startInput, {
                    ...dateConfig,
                    defaultDate: pendingChange?.startDate ? new Date(pendingChange.startDate) : null,
                    onChange: (selectedDates) => {
                        if (selectedDates.length > 0) {
                            const d = selectedDates[0];
                            startInput.dataset.isoValue = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        } else {
                            startInput.dataset.isoValue = '';
                        }
                    }
                });
                addMassEditDateMask(startInput, fpStart);
                if (pendingChange?.startDate) {
                    startInput.dataset.isoValue = pendingChange.startDate;
                }
            }

            if (endInput) {
                const fpEnd = flatpickrFn(endInput, {
                    ...dateConfig,
                    defaultDate: pendingChange?.endDate ? new Date(pendingChange.endDate) : null,
                    onChange: (selectedDates) => {
                        if (selectedDates.length > 0) {
                            const d = selectedDates[0];
                            endInput.dataset.isoValue = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                        } else {
                            endInput.dataset.isoValue = '';
                        }
                    }
                });
                addMassEditDateMask(endInput, fpEnd);
                if (pendingChange?.endDate) {
                    endInput.dataset.isoValue = pendingChange.endDate;
                }
            }
        }
    }

    // Store initial form state (Apply disabled until user changes selection)
    popover.dataset.initialStateJson = getPopoverFormState(popover);
    updateMassEditPopoverButtonStates(popover);
    // Re-run after paint to ensure disabled state sticks (handles any DOM timing edge cases)
    requestAnimationFrame(() => updateMassEditPopoverButtonStates(popover));
}

/**
 * Creates the HTML for a mass edit popover based on field type
 */
function createMassEditPopover(field) {
    const popover = document.createElement('div');
    popover.id = 'mass-edit-popover';
    popover.className = 'mass-edit-popover';
    popover.dataset.field = field;  // set immediately so checkPopoverHasSelection works

    let bodyHTML = '';

    if (field === 'status') {
        bodyHTML = `
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-status" value="backlog" id="mass-status-backlog">
                <label for="mass-status-backlog" class="mass-edit-option-label">
                    <span class="status-badge backlog">${t('tasks.status.backlog')}</span>
                </label>
            </div>
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-status" value="todo" id="mass-status-todo">
                <label for="mass-status-todo" class="mass-edit-option-label">
                    <span class="status-badge todo">${t('tasks.status.todo')}</span>
                </label>
            </div>
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-status" value="progress" id="mass-status-progress">
                <label for="mass-status-progress" class="mass-edit-option-label">
                    <span class="status-badge progress">${t('tasks.status.progress')}</span>
                </label>
            </div>
            ${settings.enableReviewStatus ? `
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-status" value="review" id="mass-status-review">
                <label for="mass-status-review" class="mass-edit-option-label">
                    <span class="status-badge review">${t('tasks.status.review')}</span>
                </label>
            </div>
            ` : ''}
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-status" value="done" id="mass-status-done">
                <label for="mass-status-done" class="mass-edit-option-label">
                    <span class="status-badge done">${t('tasks.status.done')}</span>
                </label>
            </div>
        `;
    } else if (field === 'priority') {
        bodyHTML = `
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-priority" value="high" id="mass-priority-high">
                <label for="mass-priority-high" class="mass-edit-option-label">
                    <span class="priority-pill priority-high">HIGH</span>
                </label>
            </div>
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-priority" value="medium" id="mass-priority-medium">
                <label for="mass-priority-medium" class="mass-edit-option-label">
                    <span class="priority-pill priority-medium">MEDIUM</span>
                </label>
            </div>
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-priority" value="low" id="mass-priority-low">
                <label for="mass-priority-low" class="mass-edit-option-label">
                    <span class="priority-pill priority-low">LOW</span>
                </label>
            </div>
        `;
    } else if (field === 'dates') {
        bodyHTML = `
            <div class="mass-edit-date-group">
                <label class="mass-edit-date-label" for="mass-edit-start-date">
                    ${t('tasks.massEdit.startDate')}
                </label>
                                <input type="text" id="mass-edit-start-date" class="mass-edit-date-input" placeholder="${escapeHtml(t('common.datePlaceholder'))}" autocomplete="off">
            </div>
            <div class="mass-edit-date-group">
                <label class="mass-edit-date-label" for="mass-edit-end-date">
                    ${t('tasks.massEdit.endDate')}
                </label>
                                <input type="text" id="mass-edit-end-date" class="mass-edit-date-input" placeholder="${escapeHtml(t('common.datePlaceholder'))}" autocomplete="off">
            </div>
        `;
    } else if (field === 'project') {
        const projectOptions = projects.map(proj => `
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-project" value="${proj.id}" id="mass-project-${proj.id}">
                <label for="mass-project-${proj.id}" class="mass-edit-option-label">
                    <span class="project-dot" style="background: ${getProjectColor(proj.id)};"></span>
                    ${escapeHtml(proj.name)}
                </label>
            </div>
        `).join('');

        bodyHTML = `
            <div class="mass-edit-option">
                <input type="radio" name="mass-edit-project" value="none" id="mass-project-none">
                <label for="mass-project-none" class="mass-edit-option-label">
                    ${t('tasks.noProject')}
                </label>
            </div>
            ${projectOptions}
        `;
    } else if (field === 'tags') {
        // Get all unique tags from selected tasks
        const visibleTasks = getVisibleTasks();
        const selectedTasks = visibleTasks.filter(task => massEditState.selectedTaskIds.has(task.id));
        const existingTagsSet = new Set();
        selectedTasks.forEach(task => {
            if (task.tags && Array.isArray(task.tags)) {
                task.tags.forEach(tag => existingTagsSet.add(tag));
            }
        });
        const existingTags = [...existingTagsSet].sort();

        const existingTagsHTML = existingTags.length > 0
            ? existingTags.map(tag => {
                const tagColor = getTagColor(tag);
                return `
                <div class="mass-edit-existing-tag" data-tag="${escapeHtml(tag)}">
                    <span class="tag-badge" style="${getTintedTagStyle(tagColor)}">${escapeHtml(tag.toUpperCase())}</span>
                </div>
            `;
            }).join('')
            : `<div class="mass-edit-no-tags">${t('tasks.massEdit.tags.noExisting') || 'No existing tags'}</div>`;

        bodyHTML = `
            <div class="mass-edit-tags-mode">
                <div class="mass-edit-tags-mode-option active" data-mode="add">
                    <input type="radio" name="mass-edit-tags-mode" value="add" id="mass-tags-mode-add" checked>
                    <label for="mass-tags-mode-add" class="mass-edit-option-label">
                        ${t('tasks.massEdit.tags.add')}
                    </label>
                </div>
                <div class="mass-edit-tags-mode-option" data-mode="replace">
                    <input type="radio" name="mass-edit-tags-mode" value="replace" id="mass-tags-mode-replace">
                    <label for="mass-tags-mode-replace" class="mass-edit-option-label">
                        ${t('tasks.massEdit.tags.replace')}
                    </label>
                </div>
                <div class="mass-edit-tags-mode-option" data-mode="remove">
                    <input type="radio" name="mass-edit-tags-mode" value="remove" id="mass-tags-mode-remove">
                    <label for="mass-tags-mode-remove" class="mass-edit-option-label">
                        ${t('tasks.massEdit.tags.remove')}
                    </label>
                </div>
            </div>
            <div class="mass-edit-existing-tags-section">
                <div class="mass-edit-existing-tags-label">Click to select:</div>
                <div class="mass-edit-existing-tags" id="mass-edit-existing-tags">
                    ${existingTagsHTML}
                </div>
            </div>
            <div class="mass-edit-tags-input">
                <input type="text" id="mass-edit-tag-input" placeholder="Add tag">
                <button class="btn btn-secondary" id="mass-edit-add-tag-btn">Add</button>
            </div>
            <div class="mass-edit-selected-label">Selected:</div>
            <div class="mass-edit-tags-list" id="mass-edit-tags-list"></div>
        `;
    }

    popover.innerHTML = `
        <div class="mass-edit-popover-header">
            <h3 class="mass-edit-popover-title">${getMassEditPopoverTitle(field)}</h3>
            <button type="button" class="mass-edit-popover-done">${t('common.done')}</button>
        </div>
        <div class="mass-edit-popover-body">
            ${bodyHTML}
        </div>
        <div class="mass-edit-popover-footer">
            <button class="btn btn-outline" id="mass-edit-popover-clear-btn" disabled>Clear</button>
            <button class="btn btn-primary" id="mass-edit-apply-btn" data-action="applyMassEdit" disabled>${t('tasks.massEdit.apply')}</button>
        </div>
    `;

    // Setup Clear and Apply buttons
    const clearBtn = popover.querySelector('#mass-edit-popover-clear-btn');
    const applyBtn = popover.querySelector('#mass-edit-apply-btn');

    if (clearBtn) {
        clearBtn.addEventListener('click', () => {
            clearMassEditPopover();
            updateMassEditPopoverButtonStates(popover);
        });
    }

    // Done button closes popover
    const doneBtn = popover.querySelector('.mass-edit-popover-done');
    if (doneBtn) {
        doneBtn.addEventListener('click', () => closeMassEditPopover());
    }

    // Listen for changes in popover to update button states (no timeouts)
    popover.addEventListener('change', () => updateMassEditPopoverButtonStates(popover));
    popover.addEventListener('click', () => updateMassEditPopoverButtonStates(popover));

    // Make entire row clickable for all radio options (status, priority, project)
    const massEditOptions = popover.querySelectorAll('.mass-edit-option');
    massEditOptions.forEach(option => {
        option.addEventListener('click', (e) => {
            // Avoid double-triggering if radio was clicked directly
            if (e.target.tagName === 'INPUT') return;
            const radio = option.querySelector('input[type="radio"]');
            if (radio) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    });

    // Setup tag mode switching
    if (field === 'tags') {
        const modeOptions = popover.querySelectorAll('.mass-edit-tags-mode-option');
        modeOptions.forEach(option => {
            option.addEventListener('click', () => {
                modeOptions.forEach(opt => opt.classList.remove('active'));
                option.classList.add('active');
                option.querySelector('input').checked = true;
            });
        });

        // Setup add tag button
        const addTagBtn = popover.querySelector('#mass-edit-add-tag-btn');
        const tagInput = popover.querySelector('#mass-edit-tag-input');
        addTagBtn.addEventListener('click', () => {
            const tagName = tagInput.value.trim().toLowerCase();
            if (!tagName) return;

            const tagsList = popover.querySelector('#mass-edit-tags-list');
            const existingTags = Array.from(tagsList.querySelectorAll('.mass-edit-tag-item'))
                .map(el => el.dataset.tagName);

            if (!existingTags.includes(tagName)) {
                const tagColor = getTagColor(tagName);
                const tagItem = document.createElement('div');
                tagItem.className = 'mass-edit-tag-item';
                tagItem.dataset.tagName = tagName;
                applyTintedTagStyle(tagItem, tagColor);
                tagItem.innerHTML = `
                    <span>${escapeHtml(tagName.toUpperCase())}</span>
                    <button type="button" class="mass-edit-tag-remove" data-action="removeParentElement">×</button>
                `;
                const removeBtn = tagItem.querySelector('.mass-edit-tag-remove');
                if (removeBtn) removeBtn.style.color = 'inherit';
                tagsList.appendChild(tagItem);
                tagInput.value = '';
            }
        });

        // Enter key to add tag
        tagInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                addTagBtn.click();
            }
        });

        // Click-to-select existing tags
        const existingTagsContainer = popover.querySelector('#mass-edit-existing-tags');
        if (existingTagsContainer) {
            existingTagsContainer.addEventListener('click', (e) => {
                const tagEl = e.target.closest('.mass-edit-existing-tag');
                if (!tagEl) return;

                const tagName = tagEl.dataset.tag;
                if (!tagName) return;

                // Toggle selection state
                tagEl.classList.toggle('selected');

                const tagsList = popover.querySelector('#mass-edit-tags-list');
                const existingSelected = Array.from(tagsList.querySelectorAll('.mass-edit-tag-item'))
                    .map(el => el.dataset.tagName);

                if (tagEl.classList.contains('selected')) {
                    // Add to selected list if not already there
                    if (!existingSelected.includes(tagName)) {
                        const tagColor = getTagColor(tagName);
                        const tagItem = document.createElement('div');
                        tagItem.className = 'mass-edit-tag-item';
                        tagItem.dataset.tagName = tagName;
                        applyTintedTagStyle(tagItem, tagColor);
                        tagItem.innerHTML = `
                            <span>${escapeHtml(tagName.toUpperCase())}</span>
                            <button class="mass-edit-tag-remove">×</button>
                        `;
                        const removeBtn = tagItem.querySelector('.mass-edit-tag-remove');
                        if (removeBtn) removeBtn.style.color = 'inherit';
                        removeBtn?.addEventListener('click', () => {
                            tagItem.remove();
                            tagEl.classList.remove('selected');
                        });
                        tagsList.appendChild(tagItem);
                    }
                } else {
                    // Remove from selected list (iterate to avoid CSS selector injection from tagName)
                    const toRemove = Array.from(tagsList.querySelectorAll('.mass-edit-tag-item'))
                        .find(el => el.dataset.tagName === tagName);
                    if (toRemove) toRemove.remove();
                }
            });
        }
    }

    return popover;
}

/**
 * Gets the popover title for each field
 */
function getMassEditPopoverTitle(field) {
    const count = massEditState.selectedTaskIds.size;
    const fieldTitles = {
        'status': `${t('tasks.massEdit.status')} (${count} tasks)`,
        'priority': `${t('tasks.massEdit.priority')} (${count} tasks)`,
        'dates': `${t('tasks.massEdit.dates')} (${count} tasks)`,
        'project': `${t('tasks.massEdit.project')} (${count} tasks)`,
        'tags': `${t('tasks.massEdit.tags')} (${count} tasks)`
    };
    return fieldTitles[field] || '';
}

/**
 * Closes the mass edit popover
 */
function closeMassEditPopover() {
    const popover = document.getElementById('mass-edit-popover');
    if (popover) {
        popover.remove();
    }
}

function checkPopoverHasSelection(popover) {
    if (!popover) return false;

    const field = popover.dataset.field;
    if (!field) return false;

    // Field-specific: only count the actual value inputs, never mode/structure radios
    if (field === 'status') {
        return !!popover.querySelector('input[name="mass-edit-status"]:checked');
    }
    if (field === 'priority') {
        return !!popover.querySelector('input[name="mass-edit-priority"]:checked');
    }
    if (field === 'project') {
        return popover.querySelector('input[name="mass-edit-project"]:checked') != null;
    }
    if (field === 'dates') {
        const start = document.getElementById('mass-edit-start-date')?.dataset?.isoValue ?? '';
        const end = document.getElementById('mass-edit-end-date')?.dataset?.isoValue ?? '';
        return !!(start || end);
    }
    if (field === 'tags') {
        const selectedTags = popover.querySelectorAll('.mass-edit-tag-item');
        if (selectedTags.length > 0) return true;
        const selectedExisting = popover.querySelectorAll('.mass-edit-existing-tag.selected');
        return selectedExisting.length > 0;
    }

    return false;
}

/**
 * Updates Clear and Apply button states in the mass edit popover.
 * Clear: disabled when no selection; enabled when there is a selection.
 * Apply: disabled by default. Enabled ONLY when (1) a selection exists AND (2) it differs from baseline.
 *        Baseline = what was already queued when we opened, or empty if fresh.
 */
function updateMassEditPopoverButtonStates(popover) {
    if (!popover) return;
    const clearBtn = popover.querySelector('#mass-edit-popover-clear-btn');
    const applyBtn = popover.querySelector('#mass-edit-apply-btn');
    const hasSelection = checkPopoverHasSelection(popover);
    if (clearBtn) clearBtn.disabled = !hasSelection;
    const baselineJson = popover.dataset.initialStateJson ?? '';
    const currentJson = getPopoverFormState(popover);
    const selectionDiffersFromBaseline = (currentJson !== baselineJson);
    const applyEnabled = hasSelection && selectionDiffersFromBaseline;
    if (applyBtn) {
        if (applyEnabled) {
            applyBtn.removeAttribute('disabled');
            applyBtn.disabled = false;
            applyBtn.setAttribute('aria-disabled', 'false');
        } else {
            applyBtn.setAttribute('disabled', '');
            applyBtn.disabled = true;
            applyBtn.setAttribute('aria-disabled', 'true');
        }
    }
}

/** Serializes popover form state for comparison (Apply enabled only when user changes selection) */
function getPopoverFormState(popover) {
    if (!popover) return '';
    const field = popover.dataset.field;
    if (field === 'status') {
        return popover.querySelector('input[name="mass-edit-status"]:checked')?.value ?? '';
    }
    if (field === 'priority') {
        return popover.querySelector('input[name="mass-edit-priority"]:checked')?.value ?? '';
    }
    if (field === 'project') {
        const r = popover.querySelector('input[name="mass-edit-project"]:checked')?.value;
        return r === undefined ? '' : String(r);
    }
    if (field === 'dates') {
        const s = document.getElementById('mass-edit-start-date')?.dataset?.isoValue ?? '';
        const e = document.getElementById('mass-edit-end-date')?.dataset?.isoValue ?? '';
        return `${s}|${e}`;
    }
    if (field === 'tags') {
        const mode = popover.querySelector('input[name="mass-edit-tags-mode"]:checked')?.value ?? '';
        const tags = Array.from(popover.querySelectorAll('#mass-edit-tags-list .mass-edit-tag-item'))
            .map(el => el.dataset.tagName).sort().join(',');
        const existing = Array.from(popover.querySelectorAll('.mass-edit-existing-tag.selected'))
            .map(el => el.dataset.tag).sort().join(',');
        return `${mode}|${tags}|${existing}`;
    }
    return '';
}

function clearMassEditPopover() {
    const popover = document.getElementById('mass-edit-popover');
    if (!popover) return;

    const field = popover.dataset.field;

    // Unqueue this field from pending changes and refresh toolbar UI
    massEditState.pendingChanges = massEditState.pendingChanges.filter(c => c.field !== field);
    updatePendingChangesUI();

    // Clear radio selections
    const radios = popover.querySelectorAll('input[type="radio"]');
    radios.forEach(radio => radio.checked = false);

    // Clear date inputs
    const dateInputs = popover.querySelectorAll('.mass-edit-date-input');
    dateInputs.forEach(input => {
        input.value = '';
        input.dataset.isoValue = '';
        if (input._flatpickr) input._flatpickr.clear();
    });

    // Clear tags selection
    const tagsList = popover.querySelector('#mass-edit-tags-list');
    if (tagsList) tagsList.innerHTML = '';

    const existingTags = popover.querySelectorAll('.mass-edit-existing-tag');
    existingTags.forEach(tag => tag.classList.remove('selected'));

    const tagInput = popover.querySelector('#mass-edit-tag-input');
    if (tagInput) tagInput.value = '';

    // Reset mode options visual state
    const modeOptions = popover.querySelectorAll('.mass-edit-tags-mode-option');
    modeOptions.forEach((opt, i) => {
        opt.classList.toggle('active', i === 0);
        const radio = opt.querySelector('input[type="radio"]');
        if (radio) radio.checked = (i === 0);
    });

    // Reset initial state so Apply stays disabled until user selects again
    popover.dataset.initialStateJson = getPopoverFormState(popover);
}

/**
 * Prepares mass edit changes and opens confirmation modal
 */
/**
 * Queues a mass edit change (called when user clicks Apply in popover)
 * This doesn't save immediately - changes are applied when "Apply Changes" is clicked
 */
function queueMassEditChange() {
    const popover = document.getElementById('mass-edit-popover');
    if (!popover) return;

    // Guard: Apply only valid when (1) selection exists and (2) differs from baseline (already queued)
    const hasSelection = checkPopoverHasSelection(popover);
    const baselineJson = popover.dataset.initialStateJson ?? '';
    const currentJson = getPopoverFormState(popover);
    const selectionDiffersFromBaseline = (currentJson !== baselineJson);
    if (!hasSelection || !selectionDiffersFromBaseline) {
        showNotification(t('tasks.massEdit.selectFirst'), 'info');
        return;
    }

    const field = popover.dataset.field;
    let change = null;

    if (field === 'status') {
        const selectedValue = popover.querySelector('input[name="mass-edit-status"]:checked')?.value;
        if (!selectedValue) {
            showNotification(t('tasks.massEdit.selectFirst'), 'info');
            return;
        }
        change = { field: 'status', value: selectedValue };
    } else if (field === 'priority') {
        const selectedValue = popover.querySelector('input[name="mass-edit-priority"]:checked')?.value;
        if (!selectedValue) {
            showNotification(t('tasks.massEdit.selectFirst'), 'info');
            return;
        }
        change = { field: 'priority', value: selectedValue };
    } else if (field === 'dates') {
        const startInput = document.getElementById('mass-edit-start-date');
        const endInput = document.getElementById('mass-edit-end-date');
        // Read ISO value from Flatpickr's onChange handler (stored in dataset)
        const startDate = startInput?.dataset.isoValue || null;
        const endDate = endInput?.dataset.isoValue || null;

        if (!startDate && !endDate) {
            showNotification(t('tasks.massEdit.selectFirst'), 'info');
            return;
        }

        change = { field: 'dates', startDate, endDate };
    } else if (field === 'project') {
        const selectedValue = popover.querySelector('input[name="mass-edit-project"]:checked')?.value;
        if (selectedValue === undefined) {
            showNotification(t('tasks.massEdit.selectFirst'), 'info');
            return;
        }
        const projectId = selectedValue === 'none' ? null : parseInt(selectedValue, 10);
        change = { field: 'project', value: projectId };
    } else if (field === 'tags') {
        const mode = popover.querySelector('input[name="mass-edit-tags-mode"]:checked')?.value;
        const tagElements = popover.querySelectorAll('#mass-edit-tags-list .mass-edit-tag-item');
        const tags = Array.from(tagElements).map(el => el.dataset.tagName);

        if (tags.length === 0) {
            showNotification(t('tasks.massEdit.selectFirst'), 'info');
            return;
        }

        change = { field: 'tags', mode, tags };
    }

    if (!change) return;

    // Remove any existing change for the same field (replace with new one)
    massEditState.pendingChanges = massEditState.pendingChanges.filter(c => c.field !== field);
    
    // Add the new change
    massEditState.pendingChanges.push(change);

    // Close popover
    closeMassEditPopover();

    // Update UI to show pending state
    updatePendingChangesUI();

    // Show feedback
    showNotification(t('tasks.massEdit.changeQueued'), 'info');
}

/**
 * Updates the pending changes UI (indicator and button state)
 */
function updatePendingChangesUI() {
    const pendingEl = document.getElementById('mass-edit-pending');
    const pendingCountEl = document.getElementById('mass-edit-pending-count');
    const applyAllBtn = document.getElementById('mass-edit-apply-all-btn');
    
    const count = massEditState.pendingChanges.length;
    const hasSelection = massEditState.selectedTaskIds.size > 0;
    
    // Update pending indicator
    if (pendingEl && pendingCountEl) {
        if (count > 0) {
            pendingEl.style.display = 'flex';
            pendingCountEl.textContent = count;
        } else {
            pendingEl.style.display = 'none';
        }
    }
    
    // Update Apply Changes button
    if (applyAllBtn) {
        applyAllBtn.disabled = !(count > 0 && hasSelection);
    }
    
    // Update field buttons to show which have pending changes
    const fieldButtons = {
        'status': document.getElementById('mass-edit-status-btn'),
        'priority': document.getElementById('mass-edit-priority-btn'),
        'dates': document.getElementById('mass-edit-dates-btn'),
        'project': document.getElementById('mass-edit-project-btn'),
        'tags': document.getElementById('mass-edit-tags-btn')
    };
    
    const pendingFields = new Set(massEditState.pendingChanges.map(c => c.field));
    
    Object.entries(fieldButtons).forEach(([field, btn]) => {
        if (btn) {
            if (pendingFields.has(field)) {
                btn.classList.add('has-pending');
            } else {
                btn.classList.remove('has-pending');
            }
        }
    });
}

/**
 * Shows confirmation modal with summary of all queued changes
 */
function applyAllMassEditChanges() {
    const changes = massEditState.pendingChanges;
    if (changes.length === 0) {
        return;
    }

    // CRITICAL: Only work with VISIBLE selected tasks (includes Updated filter)
    const visibleTasks = getVisibleTasks();
    const visibleSelectedIds = visibleTasks
        .filter(task => massEditState.selectedTaskIds.has(task.id))
        .map(task => task.id);
    
    if (visibleSelectedIds.length === 0) {
        return;
    }

    // Show confirmation modal with all changes
    showMassEditConfirmation(changes);
}

// Keep the old function for backwards compatibility (confirmation modal flow)
function prepareMassEditConfirmation() {
    // This function is now replaced by queueMassEditChange
    // but kept for any legacy references
    queueMassEditChange();
}

/**
 * Shows confirmation modal with summary of ALL queued changes
 */
function showMassEditConfirmation(changesArray) {
    const modal = document.getElementById('mass-edit-confirm-modal');
    const summaryEl = document.getElementById('mass-edit-confirm-summary');
    const changesEl = document.getElementById('mass-edit-confirm-changes');
    const applyBtn = document.getElementById('mass-edit-confirm-apply-btn');

    // Get selected tasks
    const visibleTasks = getVisibleTasks();
    const selectedTasks = visibleTasks.filter(task => massEditState.selectedTaskIds.has(task.id));
    const count = selectedTasks.length;

    // Summary
    summaryEl.textContent = t('tasks.massEdit.confirm.summary', { count }).replace(/:\s*$/, '.');

    // Helper: get original value or "Various"
    const getOriginal = (field, getVal, getLabel) => {
        const vals = selectedTasks.map(t => getVal(t));
        const uniq = [...new Set(vals)];
        if (uniq.length === 1) {
            return getLabel(uniq[0]);
        }
        return '<span class="mass-edit-various">Various</span>';
    };

    // Build changes as simple visual rows
    let changesHTML = '';

    changesArray.forEach(change => {
        if (change.field === 'status') {
            const toLabel = getStatusLabel(change.value);
            // Get unique original statuses
            const originalStatuses = [...new Set(selectedTasks.map(t => t.status))];
            const fromHTML = originalStatuses.length === 1
                ? `<span class="status-badge ${originalStatuses[0]}">${getStatusLabel(originalStatuses[0])}</span>`
                : `<span class="mass-edit-multiple">${t('tasks.massEdit.multiple')}</span>`;
            changesHTML += `
                <div class="mass-edit-visual-row">
                    <span class="mass-edit-row-label">${t('tasks.massEdit.status')}</span>
                    <span class="mass-edit-row-from">${fromHTML}</span>
                    <span class="mass-edit-row-arrow">→</span>
                    <span class="mass-edit-row-to"><span class="status-badge ${change.value}">${toLabel}</span></span>
                </div>
            `;
        } else if (change.field === 'priority') {
            const toLabel = getPriorityLabel(change.value).toUpperCase();
            // Get unique original priorities
            const originalPriorities = [...new Set(selectedTasks.map(t => t.priority))];
            const fromHTML = originalPriorities.length === 1
                ? `<span class="priority-pill priority-${originalPriorities[0]}">${getPriorityLabel(originalPriorities[0]).toUpperCase()}</span>`
                : `<span class="mass-edit-multiple">${t('tasks.massEdit.multiple')}</span>`;
            changesHTML += `
                <div class="mass-edit-visual-row">
                    <span class="mass-edit-row-label">${t('tasks.massEdit.priority')}</span>
                    <span class="mass-edit-row-from">${fromHTML}</span>
                    <span class="mass-edit-row-arrow">→</span>
                    <span class="mass-edit-row-to"><span class="priority-pill priority-${change.value}">${toLabel}</span></span>
                </div>
            `;
        } else if (change.field === 'dates') {
            const noDateLabel = t('tasks.noDate') || 'No Date';
            if (change.startDate) {
                // Get unique start dates from selected tasks
                const originalStartDates = [...new Set(selectedTasks.map(task => task.startDate || ''))];
                let fromHTML;
                if (originalStartDates.length === 1) {
                    fromHTML = originalStartDates[0] ? formatDate(originalStartDates[0]) : `<span class="mass-edit-multiple">${noDateLabel}</span>`;
                } else {
                    fromHTML = `<span class="mass-edit-multiple">${t('tasks.massEdit.multiple')}</span>`;
                }
                changesHTML += `
                    <div class="mass-edit-visual-row">
                        <span class="mass-edit-row-label">${t('tasks.massEdit.startDate')}</span>
                        <span class="mass-edit-row-from">${fromHTML}</span>
                        <span class="mass-edit-row-arrow">→</span>
                        <span class="mass-edit-row-to">${formatDate(change.startDate)}</span>
                    </div>
                `;
            }
            if (change.endDate) {
                // Get unique end dates from selected tasks
                const originalEndDates = [...new Set(selectedTasks.map(task => task.endDate || ''))];
                let fromHTML;
                if (originalEndDates.length === 1) {
                    fromHTML = originalEndDates[0] ? formatDate(originalEndDates[0]) : `<span class="mass-edit-multiple">${noDateLabel}</span>`;
                } else {
                    fromHTML = `<span class="mass-edit-multiple">${t('tasks.massEdit.multiple')}</span>`;
                }
                changesHTML += `
                    <div class="mass-edit-visual-row">
                        <span class="mass-edit-row-label">${t('tasks.massEdit.endDate')}</span>
                        <span class="mass-edit-row-from">${fromHTML}</span>
                        <span class="mass-edit-row-arrow">→</span>
                        <span class="mass-edit-row-to">${formatDate(change.endDate)}</span>
                    </div>
                `;
            }
        } else if (change.field === 'project') {
            const toName = change.value === null ? t('tasks.noProject') : (projects.find(p => p.id === change.value)?.name || '');
            const noProjectLabel = t('tasks.noProject');
            const fromNames = selectedTasks.map(task => {
                if (!task.projectId) return noProjectLabel;
                const p = projects.find(pr => pr.id === task.projectId);
                return p ? p.name : '';
            });
            const uniqFrom = [...new Set(fromNames)];
            const fromHTML = uniqFrom.length === 1 && uniqFrom[0] ? escapeHtml(uniqFrom[0]) : '<span class="mass-edit-various">Various</span>';
            changesHTML += `
                <div class="mass-edit-visual-row">
                    <span class="mass-edit-row-label">${t('tasks.massEdit.project')}</span>
                    <span class="mass-edit-row-from">${fromHTML}</span>
                    <span class="mass-edit-row-arrow">→</span>
                    <span class="mass-edit-row-to">${escapeHtml(toName)}</span>
                </div>
            `;
        } else if (change.field === 'tags') {
            const modeLabel = {
                'add': t('tasks.massEdit.tags.add'),
                'replace': t('tasks.massEdit.tags.replace'),
                'remove': t('tasks.massEdit.tags.remove')
            }[change.mode];
            const tagsHTML = change.tags.map(tag => {
                const tagColor = getTagColor(tag);
                return `<span class="tag-badge" style="${getTintedTagStyle(tagColor)} font-size: 10px; padding: 2px 6px;">${escapeHtml(tag.toUpperCase())}</span>`;
            }).join(' ');
            changesHTML += `
                <div class="mass-edit-visual-row">
                    <span class="mass-edit-row-label">${modeLabel}</span>
                    <span class="mass-edit-row-from"></span>
                    <span class="mass-edit-row-arrow"></span>
                    <span class="mass-edit-row-to">${tagsHTML}</span>
                </div>
            `;
        }
    });

    changesEl.innerHTML = changesHTML;

    // Button text
    const applyBtnText = applyBtn.querySelector('[data-i18n]');
    if (applyBtnText) {
        applyBtnText.textContent = t('tasks.massEdit.confirm.confirm');
    }

    modal.style.display = '';
    modal.classList.add('active');
}

/**
 * Closes mass edit confirmation modal
 */
function closeMassEditConfirm() {
    const modal = document.getElementById('mass-edit-confirm-modal');
    modal.classList.remove('active');
    setTimeout(() => {
        modal.style.display = 'none';
    }, 300);
}

function closeMassEditConfirmOnBackdrop(e) {
    // Only close if clicking directly on the backdrop (not on modal content)
    if (e.target.id === 'mass-edit-confirm-modal') {
        closeMassEditConfirm();
    }
}

/**
 * Applies confirmed mass edit changes (handles array of changes)
 */
async function applyMassEditConfirmed() {
    console.log('[Mass Edit] applyMassEditConfirmed() called');
    const changes = massEditState.pendingChanges;
    if (!changes || changes.length === 0) {
        console.warn('[Mass Edit] No pending changes, exiting');
        return;
    }

    // CRITICAL: Only apply to VISIBLE selected tasks (includes Updated filter)
    const visibleTasks = getVisibleTasks();
    const visibleSelectedIds = visibleTasks
        .filter(task => massEditState.selectedTaskIds.has(task.id))
        .map(task => task.id);
    
    const count = visibleSelectedIds.length;
    console.log('[Mass Edit] Starting mass edit:', { count, changes });

    // Add loading state
    const applyBtn = document.getElementById('mass-edit-confirm-apply-btn');
    const originalBtnText = applyBtn ? applyBtn.innerHTML : '';
    if (applyBtn) {
        applyBtn.disabled = true;
        applyBtn.innerHTML = '<span class="spinner"></span> ' + (t('common.updating') || 'Updating...');
    }

    try {
        // Only use visible selected IDs (already filtered above)
        const validIds = visibleSelectedIds;

        // Apply ALL changes to each task
        validIds.forEach(taskId => {
            const task = tasks.find(t => t.id === taskId);
            if (!task) return;

            const oldTask = { ...task, tags: [...(task.tags || [])] };

            // Apply each queued change
            changes.forEach(change => {
                if (change.field === 'status') {
                    task.status = change.value;
                    applyCompletedDateTransition(task, oldTask.status);
                } else if (change.field === 'priority') {
                    task.priority = change.value;
                } else if (change.field === 'dates') {
                    if (change.startDate) {
                        task.startDate = change.startDate;
                    }
                    if (change.endDate) {
                        task.endDate = change.endDate;
                    }
                } else if (change.field === 'project') {
                    task.projectId = change.value;
                } else if (change.field === 'tags') {
                    const oldTags = [...(task.tags || [])];
                    
                    if (change.mode === 'add') {
                        const newTags = new Set([...oldTags, ...change.tags]);
                        task.tags = Array.from(newTags);
                    } else if (change.mode === 'replace') {
                        task.tags = [...change.tags];
                    } else if (change.mode === 'remove') {
                        task.tags = oldTags.filter(tag => !change.tags.includes(tag));
                    }
                }
            });

            // Update timestamp
            task.updatedAt = new Date().toISOString();

            // Record history
            if (window.historyService) {
                window.historyService.recordTaskUpdated(oldTask, task);
            }

            // Check and record project status change
            if (task.projectId) {
                checkAndRecordProjectStatusChange(task.projectId, task);
                checkProjectCompletionDateWarning(task.projectId, task);
            }
        });

        // Save to storage using row-level operations
        console.log('[Mass Edit] Calling saveTaskUpdate for each task...');
        await Promise.all(validIds.map(taskId => {
            const task = tasks.find(t => t.id === taskId);
            if (!task) return Promise.resolve();
            
            // Collect only the changed fields
            const changes = {
                status: task.status,
                completedDate: task.completedDate || null,
                priority: task.priority,
                projectId: task.projectId,
                startDate: task.startDate || '',
                endDate: task.endDate || '',
                tags: task.tags || [],
                updatedAt: task.updatedAt
            };
            return saveTaskUpdate(taskId, changes);
        }));
        console.log('[Mass Edit] saveTaskUpdate completed successfully');

        // Show success notification
        showNotification(t('tasks.massEdit.success', { count: validIds.length }), 'success');

        // Close modal
        closeMassEditConfirm();

        // Clear pending changes and selection
        massEditState.pendingChanges = [];
        clearMassEditSelection();

        // Re-render views
        renderTasks();
        renderListView();

        // Update projects view if active
        if (document.getElementById('projects')?.classList.contains('active')) {
            renderProjects();
        }

    } catch (error) {
        const msg = error?.message ?? String(error);
        const stack = error?.stack;
        const cause = error?.cause;
        console.error('[Mass Edit] Save failed:', msg);
        if (stack) console.error('[Mass Edit] Stack:', stack);
        if (cause) console.error('[Mass Edit] Cause:', cause?.message ?? cause);
        showNotification(t('error.saveChangesFailed'), 'error');
    } finally {
        // Restore button state
        if (applyBtn) {
            applyBtn.disabled = false;
            applyBtn.innerHTML = originalBtnText;
        }
    }
}

// ================================
// END MASS EDIT FUNCTIONALITY
// ================================

/**
 * Show mass delete confirmation modal
 */
function massDelete() {
    const visibleTasks = getVisibleTasks();
    const visibleSelectedTasks = visibleTasks
        .filter(task => massEditState.selectedTaskIds.has(task.id));
    
    const count = visibleSelectedTasks.length;
    
    if (count === 0) {
        showNotification('No tasks selected', 'warning');
        return;
    }

    // Show confirmation modal
    const modal = document.getElementById('mass-delete-confirm-modal');
    const message = document.getElementById('mass-delete-message');
    const taskList = document.getElementById('mass-delete-task-list');
    const input = document.getElementById('mass-delete-confirm-input');
    const errorMsg = document.getElementById('mass-delete-confirm-error');
    
    if (message) {
        message.innerHTML = `You are about to delete <strong>${count} task(s)</strong>:`;
    }
    
    // Build task list summary
    if (taskList) {
        const taskItems = visibleSelectedTasks.map(task => {
            const project = projects.find(p => p.id === task.projectId);
            const projectName = project ? project.name : 'No Project';
            const statusLabel = getStatusLabel(task.status);
            const priorityLabel = getPriorityLabel(task.priority);
            
            return `
                <div style="padding: 8px; margin-bottom: 6px; background: var(--bg-card); border-radius: 4px; border-left: 3px solid ${project ? getProjectColor(project.id) : 'var(--border)'};">
                    <div style="font-weight: 600; margin-bottom: 4px;">${escapeHtml(task.title || 'Untitled')}</div>
                    <div style="display: flex; gap: 8px; font-size: 11px; color: var(--text-secondary);">
                        <span>${escapeHtml(projectName)}</span>
                        <span>•</span>
                        <span>${escapeHtml(statusLabel)}</span>
                        <span>•</span>
                        <span>${escapeHtml(priorityLabel)}</span>
                    </div>
                </div>
            `;
        }).join('');
        
        taskList.innerHTML = taskItems;
    }
    
    if (input) {
        input.value = '';
    }
    
    if (errorMsg) {
        errorMsg.classList.remove('show');
    }
    
    if (modal) {
        modal.classList.add('active');
        // Focus input after modal is visible
        setTimeout(() => input && input.focus(), 100);
    }

    // Auto-convert to lowercase as user types
    const lowercaseHandler = function(e) {
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        e.target.value = e.target.value.toLowerCase();
        e.target.setSelectionRange(start, end);
    };
    input.addEventListener('input', lowercaseHandler);

    // Keyboard support
    const keyHandler = function(e) {
        const modal = document.getElementById('mass-delete-confirm-modal');
        if (!modal || !modal.classList.contains('active')) return;
        
        if (e.key === 'Escape') {
            e.preventDefault();
            closeMassDeleteConfirmModal();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            confirmMassDelete();
        }
    };
    document.addEventListener('keydown', keyHandler);
}

/**
 * Close mass delete confirmation modal
 */
function closeMassDeleteConfirmModal() {
    const modal = document.getElementById('mass-delete-confirm-modal');
    if (modal) {
        modal.classList.remove('active');
    }
}

/**
 * Confirm and execute mass delete
 */
async function confirmMassDelete() {
    const input = document.getElementById('mass-delete-confirm-input');
    const errorMsg = document.getElementById('mass-delete-confirm-error');
    if (!isDeleteConfirmation(input.value)) {
        errorMsg.classList.add('show');
        input.focus();
        return;
    }

    const visibleTasks = getVisibleTasks();
    const visibleSelectedIds = visibleTasks
        .filter(task => massEditState.selectedTaskIds.has(task.id))
        .map(task => task.id);
    
    const count = visibleSelectedIds.length;
    const deletedTaskIds = [...visibleSelectedIds];  // Capture for row-level deletes

    // Delete each selected task
    visibleSelectedIds.forEach(taskId => {
        const taskIndex = tasks.findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
            const task = tasks[taskIndex];
            
            // Record history before deletion
            if (window.historyService) {
                window.historyService.recordTaskDeleted(task);
            }
            
            // Remove from tasks array
            tasks.splice(taskIndex, 1);
            
            // Remove all dependencies involving this task
            const depResult = removeDependenciesForTask(taskId, dependencies);
            dependencies = depResult.dependencies;
        }
    });

    // Close modal
    closeMassDeleteConfirmModal();

    // Clear selection
    clearMassEditSelection();

    // Update UI immediately
    render();
    updateCounts();

    // Refresh calendar if exists
    const calendarView = document.getElementById('calendar-view');
    if (calendarView) {
        renderCalendar();
        if (calendarView.classList.contains('active')) {
            renderCalendar();
        }
    }

    // Save in background - row-level deletes
    Promise.all(deletedTaskIds.map(id => saveTaskDeleteData(id))).catch(err => {
        console.error('Failed to save task deletion:', err);
        showErrorNotification('Failed to save changes');
    });
    
    // Save dependencies in background
    saveDependenciesD1(serializeDependencies(dependencies)).catch(err => {
        console.error('Failed to save dependency cleanup:', err);
        showErrorNotification(t('error.saveChangesFailed'));
    });

    showNotification(`${count} task(s) deleted successfully`, 'success');
}


// ================================
// MOBILE SORT
// ================================

function sortRowsForMobile(rows) {
    const { field, dir } = mobileSortState;
    const mult = dir === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
        let va, vb;
        if (field === 'endDate' || field === 'startDate') {
            // Null/empty dates sort to the end regardless of direction
            va = a[field] || null;
            vb = b[field] || null;
            if (!va && !vb) return 0;
            if (!va) return 1;
            if (!vb) return -1;
            return mult * va.localeCompare(vb);
        } else if (field === 'priority') {
            va = PRIORITY_ORDER[a.priority] ?? 99;
            vb = PRIORITY_ORDER[b.priority] ?? 99;
            return mult * (va - vb);
        } else if (field === 'status') {
            va = STATUS_ORDER[a.status] ?? 99;
            vb = STATUS_ORDER[b.status] ?? 99;
            return mult * (va - vb);
        } else if (field === 'title') {
            va = (a.title || '').toLowerCase();
            vb = (b.title || '').toLowerCase();
            return mult * va.localeCompare(vb);
        }
        return 0;
    });
}

// Pending sort selection in the sheet (committed on Apply)
let pendingMobileSort = null;

function openMobileSortSheet() {
    const sheet = document.getElementById('mobile-sort-sheet');
    if (!sheet) return;
    // Initialise pending to current state
    pendingMobileSort = { ...mobileSortState };
    updateMobileSortSheetUI();
    sheet.classList.add('active');
    document.body.style.overflow = 'hidden';
}

function closeMobileSortSheet() {
    const sheet = document.getElementById('mobile-sort-sheet');
    if (!sheet) return;
    sheet.classList.remove('active');
    document.body.style.overflow = '';
    pendingMobileSort = null;
}

function updateMobileSortSheetUI() {
    const { field, dir } = pendingMobileSort || mobileSortState;
    document.querySelectorAll('.mobile-sort-option').forEach(opt => {
        const f = opt.dataset.sortField;
        const isActive = f === field;
        opt.classList.toggle('active', isActive);
    });
    document.querySelectorAll('.mobile-sort-dir-btn').forEach(btn => {
        const isActive = btn.dataset.sortField === field && btn.dataset.sortDir === dir;
        btn.classList.toggle('active', isActive);
    });
}

function updateMobileSortBtnLabel() {
    const labelEl = document.getElementById('mobile-sort-btn-label');
    const arrowEl = document.getElementById('mobile-sort-btn-arrow');
    if (!labelEl) return;

    const fieldLabels = {
        endDate:   t('tasks.table.endDate'),
        startDate: t('tasks.table.startDate'),
        priority:  t('tasks.table.priority'),
        status:    t('tasks.table.status'),
        title:     t('tasks.mobile.sortName'),
    };
    labelEl.textContent = fieldLabels[mobileSortState.field] || mobileSortState.field;
    if (arrowEl) {
        arrowEl.classList.toggle('is-up', mobileSortState.dir === 'asc');
        arrowEl.classList.toggle('is-down', mobileSortState.dir === 'desc');
    }
}

function applyMobileSort() {
    if (pendingMobileSort) {
        mobileSortState = { ...pendingMobileSort };
    }
    closeMobileSortSheet();
    updateMobileSortBtnLabel();
    renderListView();
}

// Handle clicks inside the bottom sheet (row select + dir button)
function handleMobileSortSheetClick(e) {
    const dirBtn = e.target.closest('.mobile-sort-dir-btn');
    const optRow = e.target.closest('.mobile-sort-option');
    if (!pendingMobileSort) return;

    if (dirBtn) {
        pendingMobileSort.field = dirBtn.dataset.sortField;
        pendingMobileSort.dir = dirBtn.dataset.sortDir;
        updateMobileSortSheetUI();
        return;
    }
    if (optRow && optRow.dataset.sortField) {
        pendingMobileSort.field = optRow.dataset.sortField;
        updateMobileSortSheetUI();
    }
}

// Attach sheet-internal click listener once
(function initMobileSortSheetListener() {
    document.addEventListener('click', (e) => {
        const sheet = document.getElementById('mobile-sort-sheet');
        if (!sheet || !sheet.classList.contains('active')) return;
        const content = sheet.querySelector('.mobile-sort-sheet-content');
        if (content && content.contains(e.target)) {
            handleMobileSortSheetClick(e);
        }
    });
})();

// ================================
// PREMIUM MOBILE CARDS
// ================================

// Smart date formatter with urgency indication
// Uses calculateSmartDateInfo from listView module for pure calculation
function getSmartDateInfo(endDate, status = null) {
    const info = calculateSmartDateInfo(endDate, status, getTodayISO(new Date(), getUserTimeZone()));
    
    if (!info.hasDate) {
        return { text: t('tasks.noEndDate'), class: "", showPrefix: false };
    }
    
    // Apply translations based on urgency
    let text;
    switch (info.urgency) {
        case 'overdue':
            text = info.daysOverdue === 1
                ? t('tasks.due.yesterday')
                : t('tasks.due.daysOverdue', { count: info.daysOverdue });
            break;
        case 'tomorrow':
            text = t('tasks.due.tomorrow');
            break;
        default:
            text = formatDate(endDate);
    }
    
    return { text, class: info.class, showPrefix: info.showPrefix };
}

// Render premium mobile cards
function renderMobileCardsPremium(tasks) {
    const container = document.getElementById("tasks-list-mobile");
    if (!container) return;

    // Preserve expanded card state across re-renders.
    const expandedIds = new Set(
        Array.from(container.querySelectorAll('.task-card-mobile.expanded'))
            .map(card => parseInt(card.dataset.taskId, 10))
            .filter(Number.isFinite)
    );

    const getDescriptionForMobileCard = (html) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html || "";

        // Parse TipTap task lists and legacy checklist formats
        const taskListItems = Array.from(
            tempDiv.querySelectorAll('ul[data-type="taskList"] > li[data-checked]')
        );
        const cbItems = Array.from(tempDiv.querySelectorAll(".cb-item"));
        const legacyRows = Array.from(tempDiv.querySelectorAll(".checkbox-row"));
        const checklistLines = [];
        taskListItems.forEach((item) => {
            const isChecked = item.getAttribute("data-checked") === "true";
            const text = (item.textContent || "").replace(/\s+/g, " ").trim();
            if (text) checklistLines.push(`${isChecked ? "✓" : "☐"} ${text}`);
        });
        cbItems.forEach((item) => {
            const isChecked = item.getAttribute("data-checked") === "true";
            const text = (item.textContent || "").trim();
            if (text) checklistLines.push(`${isChecked ? "✓" : "☐"} ${text}`);
        });
        legacyRows.forEach((row) => {
            const toggle = row.querySelector(".checkbox-toggle");
            const isChecked =
                toggle?.getAttribute("aria-pressed") === "true" ||
                toggle?.classList?.contains("checked");
            const text = (row.querySelector(".check-text")?.textContent || "").trim();
            if (text) checklistLines.push(`${isChecked ? "✓" : "☐"} ${text}`);
        });

        Array.from(tempDiv.querySelectorAll('ul[data-type="taskList"]')).forEach((list) => list.remove());
        cbItems.forEach((item) => item.remove());
        legacyRows.forEach((row) => row.remove());

        const baseText = (tempDiv.textContent || tempDiv.innerText || "").replace(/\s+/g, " ").trim();
        const checklistText = checklistLines.join("\n").trim();
        if (baseText && checklistText) return `${baseText}\n${checklistText}`;
        return baseText || checklistText;
    };

    // Empty state
    if (tasks.length === 0) {
        container.innerHTML = `
            <div class="tasks-list-mobile-empty">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3>${t('projects.details.noTasksFound')}</h3>
                <p>Try adjusting your filters or create a new task</p>
            </div>
        `;
        return;
    }

    // Render cards
    container.innerHTML = tasks.map((task) => {
        const proj = projects.find((p) => p.id === task.projectId);
        const projColor = proj ? getProjectColor(proj.id) : "#999";
        const dateInfo = getSmartDateInfo(task.endDate, task.status);

        const descText = getDescriptionForMobileCard(task.description);

        // Tags
        const tagsHTML = task.tags && task.tags.length > 0
            ? task.tags.map(tag => {
                const tagColor = getTagColor(tag);
                return `<span class="card-tag-premium" style="${getTintedTagStyle(tagColor)} border-color: transparent;">${escapeHtml(tag.toUpperCase())}</span>`;
            }).join('')
            : '';

        // Attachments count
        const attachmentCount = task.attachments && task.attachments.length > 0 ? task.attachments.length : 0;

        const doneClass = task.status === 'done' ? ' is-done' : '';

        return `
            <div class="task-card-mobile${doneClass}" data-priority="${task.priority}" data-task-id="${task.id}">
                <!-- Header (always visible) -->
                <div class="card-header-premium">
                    <div class="card-header-content" data-card-action="toggle">
                        <div class="card-title-premium-row">
                            <h3 class="card-title-premium">${escapeHtml(task.title || t('tasks.untitled'))}</h3>
                        </div>
                        <div class="card-meta-premium">
                            <span class="status-badge-mobile ${task.status}">${getStatusLabel(task.status)}</span>
                            ${dateInfo.text ? `<span class="card-date-smart ${dateInfo.class}">${dateInfo.showPrefix ? t('tasks.endDatePrefix') : ''}${dateInfo.text}</span>` : ''}
                        </div>
                    </div>
                    <div class="card-actions-premium">
                        <button class="card-open-btn-premium" data-card-action="open" title="${t('tasks.openTaskDetails')}">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </button>
                        <svg class="card-chevron-premium" data-card-action="toggle" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>

                <!-- Expandable body -->
                <div class="card-body-premium">
                    ${descText ? `
                    <div class="card-description-premium">
                        <div class="card-description-premium-label">${t('tasks.card.description')}</div>
                        <div class="card-description-premium-text">${escapeHtml(descText)}</div>
                    </div>
                    ` : ''}

                    ${tagsHTML ? `
                    <div class="card-tags-premium">${tagsHTML}</div>
                    ` : ''}

                    ${proj ? `
                    <div class="card-project-premium">
                        <span class="card-project-dot-premium" style="background-color: ${projColor};"></span>
                        <span class="card-project-name-premium">${escapeHtml(proj.name)}</span>
                        ${proj.archivedAt ? `<span class="project-archived-tag">${escapeHtml(t('projects.status.archived'))}</span>` : ''}
                    </div>
                    ` : ''}

                    <div class="card-footer-premium">
                        ${task.startDate ? `
                        <div class="card-meta-item-premium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>${t('tasks.startDatePrefix')}${formatDate(task.startDate)}</span>
                        </div>
                        ` : ''}
                        ${task.endDate ? `
                        <div class="card-meta-item-premium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>${t('tasks.endDatePrefix')}${formatDate(task.endDate)}</span>
                        </div>
                        ` : ''}
                        ${attachmentCount > 0 ? `
                        <div class="card-meta-item-premium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <span>${attachmentCount}</span>
                        </div>
                        ` : ''}
                    </div>
                </div>
            </div>
        `;
    }).join("");

    // Attach event listeners
    attachMobileCardListeners();

    // Restore expanded state after re-render.
    if (expandedIds.size > 0) {
        expandedIds.forEach((id) => {
            const card = container.querySelector(`.task-card-mobile[data-task-id="${id}"]`);
            if (card) card.classList.add('expanded');
        });
    }
}

// Attach event listeners to cards
function attachMobileCardListeners() {
    const cards = document.querySelectorAll('.task-card-mobile');

    cards.forEach(card => {
        const taskId = parseInt(card.dataset.taskId);
        const toggleElements = card.querySelectorAll('[data-card-action="toggle"]');
        const openButton = card.querySelector('[data-card-action="open"]');

        // Toggle expand/collapse on header content or chevron click
        toggleElements.forEach(element => {
            element.addEventListener('click', (e) => {
                e.stopPropagation();

                // Close other expanded cards for cleaner UX
                document.querySelectorAll('.task-card-mobile.expanded').forEach(otherCard => {
                    if (otherCard !== card) {
                        otherCard.classList.remove('expanded');
                    }
                });

                // Toggle this card
                card.classList.toggle('expanded');
            });
        });

        // Open full modal on button click
        if (openButton) {
            openButton.addEventListener('click', (e) => {
                e.stopPropagation();
                openTaskDetails(taskId);
            });
        }
    });
}

function updateSelectDisplay(selectId) {
    const select = document.getElementById(selectId);
    const display = select.parentElement.querySelector(".select-display");
    const selected = Array.from(select.selectedOptions);
    const hasAll = selected.some((o) => o.hasAttribute("data-all"));

    // Remove/add selection styling
    if (selected.length === 0 || hasAll) {
        display.classList.remove("has-selection");
    } else {
        display.classList.add("has-selection");
    }

    if (selected.length === 0 || hasAll) {
        if (selectId === "filter-status")
            display.textContent = "All Statuses ▼";
        else if (selectId === "filter-priority")
            display.textContent = "All Priorities ▼";
        else if (selectId === "filter-project")
            display.textContent = "All Projects ▼";
    } else if (selected.length === 1) {
        display.textContent = selected[0].textContent + " ▼";
    } else {
        display.textContent = `${selected.length} selected ▼`;
    }
}

function toggleMultiSelect(selectId) {
    const select = document.getElementById(selectId);
    const isActive = select.classList.contains("active");

    // Close all other multi-selects
    document
        .querySelectorAll(".multi-select.active")
        .forEach((s) => s.classList.remove("active"));

    if (!isActive) {
        select.classList.add("active");
        select.focus();
    }
}

// Close multi-selects when clicking outside
document.addEventListener("click", function (e) {
    if (!e.target.closest(".multi-select-wrapper")) {
        document
            .querySelectorAll(".multi-select.active")
            .forEach((s) => s.classList.remove("active"));
    }
});

// Helper function to generate HTML for a single project list item
// Uses module function with helper dependencies
function generateProjectItemHTML(project) {
    const helpers = {
        escapeHtml,
        renderPlainTextWithBeautifiedLinks,
        renderSingleLinePreviewWithBeautifiedLinks,
        linkPresentationContext: buildLinkPresentationContext(),
        formatDatePretty,
        getProjectColor,
        getProjectStatus,
        getProjectStatusLabel,
        getTagColor,
        getTagTextColor,
        getPriorityLabel,
        getStatusLabel,
        getLocale,
        formatDate,
        stripDescriptionHtml,
        t
    };
    return generateProjectItemHTMLModule(project, tasks, helpers);
}

let expandedTaskLayoutRafId = null;
function updateExpandedTaskRowLayouts(root = document) {
    // Set max-height on each card's task container to exactly 10 visible rows
    const containers = root.querySelectorAll?.('.pc-item .expanded-tasks-container') || [];
    containers.forEach((container) => {
        if (container.offsetParent === null) return;
        const header = container.querySelector('.expanded-tasks-header');
        const rows = container.querySelectorAll('.expanded-task-item');
        if (!rows.length) return;
        const headerH = header ? header.getBoundingClientRect().height + 12 /* margin-bottom */ : 0;
        // Measure the first row (representative height including margin)
        const firstRow = rows[0];
        const rowH = firstRow.getBoundingClientRect().height + 8; /* margin-bottom */
        const style = getComputedStyle(container);
        const paddingV = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        container.style.maxHeight = (paddingV + headerH + rowH * 10) + 'px';
    });
}

function scheduleExpandedTaskRowLayoutUpdate(root = document) {
    if (expandedTaskLayoutRafId !== null) cancelAnimationFrame(expandedTaskLayoutRafId);
    expandedTaskLayoutRafId = requestAnimationFrame(() => {
        expandedTaskLayoutRafId = null;
        updateExpandedTaskRowLayouts(root);
    });
}

const PROJECTS_EXPANDED_STORAGE_KEY = 'projectsExpandedProjectIds';

function loadProjectsExpandedIds() {
    try {
        const raw = localStorage.getItem(PROJECTS_EXPANDED_STORAGE_KEY);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return new Set();
        return new Set(parsed.map(id => String(id)));
    } catch (e) {
        return new Set();
    }
}

function saveProjectsExpandedIds(expandedIds) {
    try {
        localStorage.setItem(PROJECTS_EXPANDED_STORAGE_KEY, JSON.stringify(Array.from(expandedIds)));
    } catch (e) {}
}

function persistExpandedProjectIdsFromDOM(root = document) {
    const expandedIds = new Set();
    root.querySelectorAll('[id^="project-item-"].expanded').forEach(item => {
        const projectId = item.id.replace('project-item-', '');
        expandedIds.add(String(projectId));
    });
    saveProjectsExpandedIds(expandedIds);
}

function renderProjects() {
    // In archive mode the projects page shows a dedicated table instead of cards.
    // Route through the filter pipeline so renderView() renders the archive table.
    if (isProjectsArchiveRoute()) {
        applyProjectFilters();
        return;
    }
    console.log('🕒 TIMING: renderProjects() called, projects.length =', projects.length);
    if (projects.length > 0) {
        console.log('🕒 TIMING: Last project in array =', projects[projects.length - 1]?.name);
    }
    const renderTimer = debugTimeStart("render", "projects", { projectCount: projects.length });
    const container = document.getElementById("projects-list");
    if (projects.length === 0) {
        container.innerHTML =
            `<div class="empty-state"><h3>${t('projects.empty.title')}</h3><p>${t('projects.empty.subtitle')}</p></div>`;
        // Also clear mobile container if it exists
        const mobileContainer = document.getElementById("projects-list-mobile");
        if (mobileContainer) mobileContainer.innerHTML = '';
        updateProjectsListCount(0);
        debugTimeEnd("render", renderTimer, { projectCount: projects.length, rendered: 0 });
        return;
    }

    // Save expanded state and current DOM order before re-rendering
    const expandedProjects = loadProjectsExpandedIds();
    const currentDOMOrder = [];
    container.querySelectorAll('.pc-item').forEach(item => {
        const projectId = item.id.replace('project-item-', '');
        currentDOMOrder.push(projectId);
        if (item.classList.contains('expanded')) expandedProjects.add(projectId);
    });

    // If any project is expanded, preserve current DOM order to avoid displacing the expanded card
    let projectsToRender;
    if (expandedProjects.size > 0 && currentDOMOrder.length > 0) {
        const base = appState.projectsSortedView || projects;
        const projectMap = new Map(base.map(p => [String(p.id), p]));
        const orderedFromDOM = currentDOMOrder.map(id => projectMap.get(id)).filter(Boolean);
        const inDOM = new Set(currentDOMOrder);
        const newOnes = base.filter(p => !inDOM.has(String(p.id)));
        projectsToRender = [...orderedFromDOM, ...newOnes];
    } else {
        projectsToRender = appState.projectsSortedView || projects;
    }
// Re-render
    container.innerHTML = projectsToRender.map(generateProjectItemHTML).join("");
    const renderedProjectIds = new Set(projectsToRender.map(p => String(p.id)));
    const expandedToRestore = new Set(Array.from(expandedProjects).filter(id => renderedProjectIds.has(String(id))));

// Restore expanded state
    expandedToRestore.forEach(projectId => {
        const item = document.getElementById(`project-item-${projectId}`);
        if (item) {
            item.classList.add('expanded');
} else {
}
    });
    saveProjectsExpandedIds(expandedToRestore);

    // Render mobile cards
    renderMobileProjects(projectsToRender);

    scheduleExpandedTaskRowLayoutUpdate(container);
    setupExpandedProjectsDragDrop();
    // Restore the active view mode after re-render
    const savedMode = localStorage.getItem('projectsViewMode') || 'cards';
    const projectList = document.getElementById('projects-list');
    if (projectList) projectList.classList.toggle('view-rows', savedMode === 'rows');
    syncProjectListRowsWidthToControls();
    updateProjectsListCount(projectsToRender.length);
    updateProjectsExpandAllButton();

    debugTimeEnd("render", renderTimer, { projectCount: projects.length, rendered: projectsToRender.length });
}

function setProjectsView(mode) {
    const projectList = document.getElementById('projects-list');
    if (!projectList) return;
    projectList.classList.toggle('view-rows', mode === 'rows');
    syncProjectListRowsWidthToControls();
    localStorage.setItem('projectsViewMode', mode);
    document.getElementById('btn-view-cards')?.classList.toggle('active', mode === 'cards');
    document.getElementById('btn-view-rows')?.classList.toggle('active', mode === 'rows');
    syncURLWithProjectFilters();
    updateProjectsExpandAllButton();
}
window.setProjectsView = setProjectsView;

let projectsRowsWidthResizeObserver = null;

function setupProjectsRowsWidthObserver() {
    if (projectsRowsWidthResizeObserver) {
        projectsRowsWidthResizeObserver.disconnect();
        projectsRowsWidthResizeObserver = null;
    }

    if (typeof ResizeObserver === 'undefined') return;

    const projectsPage = document.getElementById('projects');
    const header = projectsPage?.querySelector('.projects-header');
    const controls = [
        projectsPage?.querySelector('.projects-filters-toolbar'),
        projectsPage?.querySelector('.projects-sort-view')
    ].filter(Boolean);

    if (!header || controls.length === 0) return;

    projectsRowsWidthResizeObserver = new ResizeObserver(() => {
        syncProjectListRowsWidthToControls();
    });

    projectsRowsWidthResizeObserver.observe(header);
    controls.forEach((control) => projectsRowsWidthResizeObserver.observe(control));
}

function syncProjectListRowsWidthToControls(retries = 3) {
    requestAnimationFrame(() => {
        const projectList = document.getElementById('projects-list');
        const projectsPage = document.getElementById('projects');
        const header = projectsPage?.querySelector('.projects-header');
        const controls = [
            projectsPage?.querySelector('.projects-filters-toolbar'),
            projectsPage?.querySelector('.projects-sort-view')
        ].filter(Boolean);
        if (!projectList || !header || controls.length === 0) return;

        if (!projectList.classList.contains('view-rows')) {
            projectsPage.style.removeProperty('--projects-rows-target-width');
            return;
        }

        const left = header.getBoundingClientRect().left;
        const right = controls.reduce((furthestRight, control) => {
            if (control.offsetParent === null) return furthestRight;
            return Math.max(furthestRight, control.getBoundingClientRect().right);
        }, left);
        const width = Math.max(0, Math.round(right - left));
        // Guard against early layout reads (0/very small widths) during page/view init.
        if (width < 360) {
            projectsPage.style.removeProperty('--projects-rows-target-width');
            if (retries > 0) {
                setTimeout(() => syncProjectListRowsWidthToControls(retries - 1), 80);
            }
            return;
        }

        projectsPage.style.setProperty('--projects-rows-target-width', `${width}px`);
    });
}

window.addEventListener('resize', syncProjectListRowsWidthToControls);

function updateProjectsListCount(count) {
    const countEl = document.getElementById('projects-list-count');
    if (!countEl) return;

    const lang = (document.documentElement.lang || 'en').toLowerCase();
    if (lang.startsWith('es')) {
        countEl.textContent = `${count} ${count === 1 ? 'proyecto' : 'proyectos'}`;
        return;
    }

    countEl.textContent = `${count} ${count === 1 ? 'project' : 'projects'}`;
}

function updateProjectsExpandAllButton() {
    const expandButton = document.getElementById('btn-projects-expand-all');
    const collapseButton = document.getElementById('btn-projects-collapse-all');
    const projectList = document.getElementById('projects-list');
    if ((!expandButton && !collapseButton) || !projectList) return;

    const items = Array.from(projectList.querySelectorAll('[id^="project-item-"]'));
    const hasExpanded = items.some(item => item.classList.contains('expanded'));
    const hasCollapsed = items.some(item => !item.classList.contains('expanded'));

    if (expandButton) {
        expandButton.disabled = items.length === 0 || !hasCollapsed;
        expandButton.title = t('projects.expandAllTitle');
    }
    if (collapseButton) {
        collapseButton.disabled = items.length === 0 || !hasExpanded;
        collapseButton.title = t('projects.collapseAllTitle');
    }
}

function expandAllProjects() {
    const projectList = document.getElementById('projects-list');
    if (!projectList) return;

    const items = Array.from(projectList.querySelectorAll('[id^="project-item-"]'));
    if (items.length === 0) return;

    items.forEach(item => item.classList.add('expanded'));
    persistExpandedProjectIdsFromDOM(projectList);
    scheduleExpandedTaskRowLayoutUpdate(projectList);
    updateProjectsExpandAllButton();
}
window.expandAllProjects = expandAllProjects;

function collapseAllProjects() {
    const projectList = document.getElementById('projects-list');
    if (!projectList) return;

    const items = Array.from(projectList.querySelectorAll('[id^="project-item-"]'));
    if (items.length === 0) return;

    items.forEach(item => item.classList.remove('expanded'));
    persistExpandedProjectIdsFromDOM(projectList);
    scheduleExpandedTaskRowLayoutUpdate(projectList);
    updateProjectsExpandAllButton();
}
window.collapseAllProjects = collapseAllProjects;

function toggleProjectExpand(projectId) {
    const item = document.getElementById(`project-item-${projectId}`);
    if (item) {
        item.classList.toggle('expanded');
        persistExpandedProjectIdsFromDOM(document.getElementById('projects-list') || document);
        scheduleExpandedTaskRowLayoutUpdate(item);
        updateProjectsExpandAllButton();

        // Smooth scroll card into view when expanding, but only if it overflows the visible area
        if (item.classList.contains('expanded') && item.classList.contains('pc-item')) {
            const doScrollIfNeeded = () => {
                const rect = item.getBoundingClientRect();
                if (rect.bottom > window.innerHeight) {
                    item.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            };
            const onTransitionEnd = (e) => {
                if (e.target !== item) return;
                item.removeEventListener('transitionend', onTransitionEnd);
                doScrollIfNeeded();
            };
            item.addEventListener('transitionend', onTransitionEnd);
            // Fallback if no transition fires
            setTimeout(() => {
                item.removeEventListener('transitionend', onTransitionEnd);
                doScrollIfNeeded();
            }, 400);
        }
    }
}
// Make function globally accessible for inline onclick handlers
window.toggleProjectExpand = toggleProjectExpand;

// ================================
// MOBILE PROJECT CARDS
// ================================

function renderMobileProjects(projects, containerId = "projects-list-mobile", emptyState = null) {
    const container = document.getElementById(containerId);
    if (!container) return;

    if (projects.length === 0) {
        const emptyTitle = emptyState?.title || t('projects.empty.searchTitle');
        const emptySubtitle = emptyState?.subtitle || t('projects.empty.searchSubtitle');
        container.innerHTML = `
            <div class="projects-list-mobile-empty">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <h3>${escapeHtml(emptyTitle)}</h3>
                <p>${escapeHtml(emptySubtitle)}</p>
            </div>
        `;
        return;
    }

    container.innerHTML = projects.map((project) => {
        const projectTasks = getLiveProjectTasks(project.id);
        const completed = projectTasks.filter((t) => t.status === 'done').length;
        const inProgress = projectTasks.filter((t) => t.status === 'progress').length;
        const review = projectTasks.filter((t) => t.status === 'review').length;
        const todo = projectTasks.filter((t) => t.status === 'todo').length;
        const backlog = projectTasks.filter((t) => t.status === 'backlog').length;
        const total = projectTasks.length;
        const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;

        const swatchColor = getProjectColor(project.id);
        const projectStatus = getProjectStatus(project.id);
        const isArchived = Boolean(project.archivedAt);

        return `
            <div class="project-card-mobile" data-project-id="${project.id}">
                <!-- Header (always visible) -->
                <div class="project-card-header-premium">
                    <div class="project-card-header-content" data-card-action="toggle">
                        <div class="project-card-title-row">
                            <span class="project-swatch-mobile" style="background: ${swatchColor};"></span>
                            <h3 class="project-card-title-premium">${escapeHtml(project.name || "Untitled Project")}</h3>
                        </div>
                        <div class="project-card-meta-premium">
                            <span class="project-status-badge-mobile ${projectStatus}">${getProjectStatusLabel(projectStatus)}</span>
                            <span class="project-card-tasks-count">${t('projects.card.tasksCount', { count: total })}</span>
                            ${completionPct > 0 ? `<span class="project-card-completion">${t('projects.card.percentDone', { count: completionPct })}</span>` : ''}
                        </div>
                    </div>
                    <div class="project-card-actions-premium">
                        ${isArchived ? `<button class="project-card-restore-btn-premium" data-action="openProjectRestoreModal" data-param="${project.id}" title="${t('projects.restore.confirm')}" data-i18n="projects.restore.confirm">${t('projects.restore.confirm')}</button>` : ''}
                        <button class="project-card-open-btn-premium" data-action="showProjectDetails" data-param="${project.id}" title="${t('projects.openDetailsTitle')}">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </button>
                        <svg class="project-card-chevron-premium" data-card-action="toggle" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>

                <!-- Expandable body -->
                <div class="project-card-body-premium">
                    ${project.tags && project.tags.length > 0 ? `
                    <div class="project-card-tags-premium" style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px;">
                        ${project.tags.map(tag => `<span style="${getTintedTagStyle(getProjectColor(project.id))} padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">${escapeHtml(tag.toUpperCase())}</span>`).join('')}
                    </div>
                    ` : ''}

                    ${project.description ? `
                    <div class="project-card-description-premium">
                        <div class="project-card-description-label">${t('tasks.card.description')}</div>
                        <div class="project-card-description-text">${renderSingleLinePreviewWithBeautifiedLinks(stripDescriptionHtml(project.description), buildLinkPresentationContext())}</div>
                    </div>
                    ` : ''}

                    <div class="project-card-footer-premium">
                        ${project.startDate ? `
                        <div class="project-card-meta-item-premium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>${t('projects.details.startDate')}: ${formatDate(project.startDate)}</span>
                        </div>
                        ` : ''}
                        ${project.endDate ? `
                        <div class="project-card-meta-item-premium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>${t('tasks.endDatePrefix')}${formatDate(project.endDate)}</span>
                        </div>
                        ` : ''}
                    </div>

                    ${total > 0 ? `
                    <div class="project-card-progress-premium">
                        <div class="project-card-progress-label">${t('projects.details.taskProgress')}</div>
                        <div class="project-card-progress-bar">
                            <div class="progress-segment done" style="width: ${(completed/total)*100}%;"></div>
                            <div class="progress-segment review" style="width: ${(review/total)*100}%;"></div>
                            <div class="progress-segment progress" style="width: ${(inProgress/total)*100}%;"></div>
                            <div class="progress-segment todo" style="width: ${(todo/total)*100}%;"></div>
                            <div class="progress-segment backlog" style="width: ${(backlog/total)*100}%;"></div>
                        </div>
                        <div class="project-card-breakdown">
                            ${completed > 0 ? `<span class="breakdown-item done">${completed} ${t('tasks.status.done')}</span>` : ''}
                            ${review > 0 ? `<span class="breakdown-item review">${review} ${t('tasks.status.review')}</span>` : ''}
                            ${inProgress > 0 ? `<span class="breakdown-item progress">${inProgress} ${t('tasks.status.progress')}</span>` : ''}
                            ${todo > 0 ? `<span class="breakdown-item todo">${todo} ${t('tasks.status.todo')}</span>` : ''}
                            ${backlog > 0 ? `<span class="breakdown-item backlog">${backlog} ${t('tasks.status.backlog')}</span>` : ''}
                        </div>
                    </div>
                    ` : ''}
                </div>
            </div>
        `;
    }).join("");

    // Attach event listeners (scoped to this container so re-rendering one list
    // never double-binds cards living in the other project mobile list).
    attachMobileProjectCardListeners(container);
}

function attachMobileProjectCardListeners(root = document) {
    const cards = root.querySelectorAll('.project-card-mobile');

    cards.forEach(card => {
        const projectId = parseInt(card.dataset.projectId);
        const toggleElements = card.querySelectorAll('[data-card-action="toggle"]');
        const openButton = card.querySelector('[data-action="showProjectDetails"]');
        const restoreButton = card.querySelector('[data-action="openProjectRestoreModal"]');

        // Toggle expand/collapse on header content or chevron click
        toggleElements.forEach(element => {
            element.addEventListener('click', (e) => {
                e.stopPropagation();

                // Close other expanded cards for cleaner UX
                document.querySelectorAll('.project-card-mobile.expanded').forEach(otherCard => {
                    if (otherCard !== card) {
                        otherCard.classList.remove('expanded');
                    }
                });

                // Toggle this card
                card.classList.toggle('expanded');
            });
        });

        // Open project details on button click
        if (openButton) {
            openButton.addEventListener('click', (e) => {
                e.stopPropagation();
                showProjectDetails(projectId);
            });
        }
        if (restoreButton) {
            restoreButton.addEventListener('click', (e) => {
                e.stopPropagation();
                openProjectRestoreModal(projectId);
            });
        }
    });
}

function renderTasks() {
    const renderTimer = debugTimeStart("render", "tasks", { taskCount: tasks.length });
    const source =
        typeof getFilteredTasks === "function"
            ? getFilteredTasks()
            : tasks.slice();

    const cutoff = getKanbanUpdatedCutoffTime(window.kanbanUpdatedFilter);
    
    // Use kanban module for grouping and sorting
    const kanbanData = prepareKanbanData(source, {
        showBacklog: window.kanbanShowBacklog === true,
        sortMode: sortMode,
        manualTaskOrder: manualTaskOrder,
        updatedCutoff: cutoff,
        getTaskUpdatedTime: getTaskUpdatedTime
    });
    
    const byStatus = kanbanData.byStatus;
    const sourceForKanban = kanbanData.totalFiltered;

    const cols = {
        backlog: document.getElementById("backlog-tasks"),
        todo: document.getElementById("todo-tasks"),
        progress: document.getElementById("progress-tasks"),
        review: document.getElementById("review-tasks"),
        done: document.getElementById("done-tasks"),
    };

    // Update counts using kanban module data
    const counts = kanbanData.counts;
    const cBacklog = document.getElementById("backlog-count");
    const cTodo = document.getElementById("todo-count");
    const cProg = document.getElementById("progress-count");
    const cRev = document.getElementById("review-count");
    const cDone = document.getElementById("done-count");
    if (cBacklog) cBacklog.textContent = counts.backlog;
    if (cTodo) cTodo.textContent = counts.todo;
    if (cProg) cProg.textContent = counts.progress;
    if (cRev) cRev.textContent = counts.review;
    if (cDone) cDone.textContent = counts.done;

    // Render cards using module function
    const cardHelpers = {
        escapeHtml,
        formatDate,
        getProjectColor,
        getTagColor,
        getTagTextColor,
        getPriorityLabel,
        projects,
        selectedCards: appState.selectedCards,
        showProjects: window.kanbanShowProjects !== false,
        showTags: window.kanbanShowTags !== false,
        showNoDate: window.kanbanShowNoDate === true,
        automationLabel: t('automations.recurrence.generatedBy'),
        noProjectText: t('tasks.noProject'),
        noDateText: t('tasks.noDate'),
        overdueText: t('tasks.kanban.overdue')
    };

    ["backlog", "todo", "progress", "review", "done"].forEach((status) => {
        const wrap = cols[status];
        if (!wrap) return;
        wrap.innerHTML = generateKanbanColumnHTML(byStatus[status], cardHelpers);
    });
        
    setupDragAndDrop();
    updateSortUI();
    
    // Initialize mass edit listeners after render (list view specific)
    initializeMassEditListeners();
    
    debugTimeEnd("render", renderTimer, {
        taskCount: tasks.length,
        filteredCount: source.length,
        kanbanCount: sourceForKanban
    });
}


// Dynamically reorganize Details fields based on which are filled (mobile only)
// SIMPLIFIED LOGIC:
// - Start/End Date: If task was EVER created with dates, they ALWAYS stay in General (even if cleared)
// - Tags/Links: Move between General (filled) and Details (empty) dynamically
function reorganizeMobileTaskFields() {
    if (window.innerWidth > 768) return; // Desktop only

    const modal = document.getElementById("task-modal");
    if (!modal) return;

    const form = modal.querySelector("#task-form");
    const editingTaskId = form?.dataset.editingTaskId;

    // Only reorganize when editing existing task
    if (!editingTaskId) return;

    // Check if dates were INITIALLY set when modal opened (stored as data attributes)
    const startDateWasEverSet = modal.dataset.initialStartDate === 'true';
    const endDateWasEverSet = modal.dataset.initialEndDate === 'true';

    // Get task data for calculation
    let taskData = null;
    if (editingTaskId) {
        taskData = tasks.find(t => t.id === parseInt(editingTaskId, 10));
    }
    if (!taskData) {
        // Fallback to temp data when creating
        taskData = {
            tags: window.tempTags || [],
            attachments: tempAttachments || []
        };
    }

    // Calculate field placement using pure function
    const fieldState = calculateMobileFieldPlacement(taskData, {
        startDateWasEverSet,
        endDateWasEverSet
    });

    // Get form groups for DOM manipulation
    const tagInput = modal.querySelector('#tag-input');
    const tagsGroup = tagInput ? tagInput.closest('.form-group') : null;

    const startDateInputs = modal.querySelectorAll('input[name="startDate"]');
    let startDateGroup = null;
    for (const input of startDateInputs) {
        const group = input.closest('.form-group');
        if (group) {
            startDateGroup = group;
            break;
        }
    }

    const endDateInputs = modal.querySelectorAll('input[name="endDate"]');
    let endDateGroup = null;
    for (const input of endDateInputs) {
        const group = input.closest('.form-group');
        if (group) {
            endDateGroup = group;
            break;
        }
    }

    const linksList = modal.querySelector('#attachments-links-list');
    const linksGroup = linksList ? linksList.closest('.form-group') : null;

    // Apply field placement from calculation
    if (tagsGroup) {
        tagsGroup.classList.toggle('mobile-general-field', fieldState.tagsInGeneral);
        tagsGroup.classList.toggle('mobile-details-field', !fieldState.tagsInGeneral);
    }

    if (startDateGroup) {
        startDateGroup.classList.toggle('mobile-general-field', fieldState.startDateInGeneral);
        startDateGroup.classList.toggle('mobile-details-field', !fieldState.startDateInGeneral);
    }

    if (endDateGroup) {
        endDateGroup.classList.toggle('mobile-general-field', fieldState.endDateInGeneral);
        endDateGroup.classList.toggle('mobile-details-field', !fieldState.endDateInGeneral);
    }

    if (linksGroup) {
        linksGroup.classList.toggle('mobile-general-field', fieldState.linksInGeneral);
        linksGroup.classList.toggle('mobile-details-field', !fieldState.linksInGeneral);
    }

    // Update Details tab visibility using pure function
    const detailsTab = modal.querySelector('.modal-tab[data-tab="details"]');
    const hideDetailsTab = shouldHideDetailsTab(fieldState);

    console.log('\u{1F504} Reorganizing fields:', {
        ...fieldState,
        hideDetailsTab
    });

    if (detailsTab) {
        if (hideDetailsTab) {
            console.log('\u{2705} Hiding Details tab - Tags and Links both filled');
            detailsTab.classList.add('hide-details-tab');
        } else {
            console.log('👁️ Showing Details tab - some dynamic fields empty');
            detailsTab.classList.remove('hide-details-tab');
        }
    }
}

// Task navigation context for navigating between tasks in a project
let currentTaskNavigationContext = null;

// URL to restore when the task modal is closed (preserves filter state)
let taskModalReturnHash = null;

function buildProjectTaskMetaHtml(task) {
    const startDate = task.startDate ? formatDate(task.startDate) : '';
    const endDate = task.endDate ? formatDate(task.endDate) : '';

    if (startDate && endDate) {
        return `
            <span class="project-task-meta-row project-task-date-range" title="${t('tasks.startDatePrefix')} ${startDate} · ${t('tasks.endDatePrefix')} ${endDate}">
                <span class="project-task-meta-value">${startDate}</span>
                <span class="project-task-date-separator" aria-hidden="true">→</span>
                <span class="project-task-meta-value">${endDate}</span>
            </span>
        `;
    }

    if (startDate) {
        return `<span class="project-task-meta-row"><span class="project-task-meta-label">${t('tasks.startDatePrefix')}</span><span class="project-task-meta-value">${startDate}</span></span>`;
    }

    if (endDate) {
        return `<span class="project-task-meta-row"><span class="project-task-meta-label">${t('tasks.endDatePrefix')}</span><span class="project-task-meta-value">${endDate}</span></span>`;
    }

    return '';
}

// Daylight project details: one task row with kanban priority dot, due chip and tinted status chip
function buildProjectDetailsTaskRowHtml(task, { reorderable = true } = {}) {
    const priority = task.priority || 'low';
    const canDrag = reorderable && !getIsMobileCached();
    const dueChipHtml = generateKanbanDueChipHTML(task.endDate, task.status, {
        escapeHtml,
        formatDate,
        overdueText: t('tasks.kanban.overdue'),
        startDate: task.startDate
    });
    const tagsHtml = task.tags && task.tags.length > 0
        ? `<div class="task-tags">${task.tags.map(tag => {
            const tagColor = getTagColor(tag);
            return `<span class="pd-task-tag" style="${getTintedTagStyle(tagColor)}">${escapeHtml(tag.toUpperCase())}</span>`;
        }).join('')}</div>`
        : '';
    return `
        <div class="project-task-item pd-task-row${task.status === 'done' ? ' is-done' : ''}" ${canDrag ? 'draggable="true"' : ''} data-task-id="${task.id}" data-action="openTaskDetails" data-param="${task.id}">
            ${reorderable ? `<div class="drag-handle" title="${t('projects.details.dragToReorder')}">⠿</div>` : ''}
            <div class="project-task-info">
                <div class="project-task-title">${escapeHtml(task.title || '')}</div>
                ${tagsHtml}
            </div>
            <div class="pd-task-due">${dueChipHtml}</div>
            <div class="project-task-priority"><span class="kanban-priority priority-${priority}"><span class="kanban-priority-dot" aria-hidden="true"></span>${getPriorityLabel(priority)}</span></div>
            <div class="project-task-status-col"><span class="pd-status-chip" data-status="${task.status}">${getStatusLabel(task.status)}</span></div>
        </div>`;
}

// Project details task list grouping (per-viewer preference, so localStorage)
const PROJECT_TASK_GROUP_BY_KEY = 'nautilus:projectTaskGroupBy';
const PROJECT_TASK_GROUP_BY_MODES = ['none', 'status', 'priority'];
const PROJECT_TASK_STATUS_GROUP_ORDER = ['progress', 'review', 'todo', 'backlog', 'done'];
const PROJECT_TASK_PRIORITY_GROUP_ORDER = ['high', 'medium', 'low'];
const PROJECT_TASK_COLLAPSED_GROUPS_KEY = 'nautilus:projectTaskGroupsCollapsed';

function getCollapsedProjectTaskGroups() {
    try {
        const saved = JSON.parse(localStorage.getItem(PROJECT_TASK_COLLAPSED_GROUPS_KEY) || '[]');
        return new Set(Array.isArray(saved) ? saved : []);
    } catch (e) {
        return new Set();
    }
}

// Groups that start collapsed; opening one is remembered in a separate "expanded" set
const PROJECT_TASK_DEFAULT_COLLAPSED_GROUPS = new Set(['status-done']);
const PROJECT_TASK_EXPANDED_GROUPS_KEY = 'nautilus:projectTaskGroupsExpanded';

function getExpandedProjectTaskGroups() {
    try {
        const saved = JSON.parse(localStorage.getItem(PROJECT_TASK_EXPANDED_GROUPS_KEY) || '[]');
        return new Set(Array.isArray(saved) ? saved : []);
    } catch (e) {
        return new Set();
    }
}

function isProjectTaskGroupCollapsed(groupId, collapsedSet, expandedSet) {
    return PROJECT_TASK_DEFAULT_COLLAPSED_GROUPS.has(groupId)
        ? !expandedSet.has(groupId)
        : collapsedSet.has(groupId);
}

function toggleProjectTaskGroup(groupId) {
    const group = document.querySelector(`#project-tasks-list .pd-task-group[data-group-id="${groupId}"]`);
    if (!group) return;
    const collapsed = group.classList.toggle('is-collapsed');
    group.querySelector('.pd-task-group-header')?.setAttribute('aria-expanded', String(!collapsed));
    const defaultCollapsed = PROJECT_TASK_DEFAULT_COLLAPSED_GROUPS.has(groupId);
    const key = defaultCollapsed ? PROJECT_TASK_EXPANDED_GROUPS_KEY : PROJECT_TASK_COLLAPSED_GROUPS_KEY;
    const set = defaultCollapsed ? getExpandedProjectTaskGroups() : getCollapsedProjectTaskGroups();
    if (collapsed !== defaultCollapsed) set.add(groupId); else set.delete(groupId);
    try { localStorage.setItem(key, JSON.stringify([...set])); } catch (e) { /* preference only */ }
}

function getProjectTaskGroupBy() {
    try {
        const saved = localStorage.getItem(PROJECT_TASK_GROUP_BY_KEY);
        return PROJECT_TASK_GROUP_BY_MODES.includes(saved) ? saved : 'status';
    } catch (e) {
        return 'status';
    }
}

function buildProjectTaskGroupByControlHtml(projectId, groupBy) {
    const options = PROJECT_TASK_GROUP_BY_MODES.map((mode) => `
        <button type="button" class="pd-groupby-opt${mode === groupBy ? ' is-active' : ''}" aria-pressed="${mode === groupBy}"
                data-action="setProjectTaskGroupBy" data-param="${projectId}" data-param2="${mode}"
                data-i18n="projects.details.groupBy.${mode}">${t(`projects.details.groupBy.${mode}`)}</button>`).join('');
    return `
        <div class="pd-groupby" role="group" aria-label="${t('projects.details.groupBy.label')}">
            <span class="pd-groupby-label" data-i18n="projects.details.groupBy.label">${t('projects.details.groupBy.label')}</span>
            <div class="pd-groupby-seg">${options}</div>
        </div>`;
}

function buildProjectTasksListHtml(projectTasks, groupBy) {
    if (projectTasks.length === 0) {
        return `<div class="empty-state">${t('tasks.empty.epic')}</div>`;
    }
    // Manual drag order only applies to the flat list; groups are always chronological
    const useManualOrder = groupBy !== 'status' && groupBy !== 'priority'
        && projectTasks.some(task => typeof task.projectOrder === 'number');
    const isoOf = (value) => !value ? '' : (looksLikeDMY(value) ? toISOFromDMY(value) : value);
    const compareIso = (a, b) => (a === b ? 0 : !a ? 1 : !b ? -1 : (a < b ? -1 : 1));
    const sorted = [...projectTasks].sort((a, b) => {
        if (useManualOrder) {
            const ao = typeof a.projectOrder === 'number' ? a.projectOrder : 9999;
            const bo = typeof b.projectOrder === 'number' ? b.projectOrder : 9999;
            if (ao !== bo) return ao - bo;
        }
        // Start date (end date when missing), then end date; undated tasks last
        const aEnd = isoOf(a.endDate);
        const bEnd = isoOf(b.endDate);
        const byStart = compareIso(isoOf(a.startDate) || aEnd, isoOf(b.startDate) || bEnd);
        if (byStart !== 0) return byStart;
        const byEnd = compareIso(aEnd, bEnd);
        if (byEnd !== 0) return byEnd;
        return (PRIORITY_ORDER[b.priority] || 1) - (PRIORITY_ORDER[a.priority] || 1);
    });

    if (groupBy !== 'status' && groupBy !== 'priority') {
        return sorted.map((task) => buildProjectDetailsTaskRowHtml(task)).join('');
    }

    // Drag-to-reorder only makes sense in the flat list; grouped rows are read-only order
    const isStatus = groupBy === 'status';
    const order = isStatus ? PROJECT_TASK_STATUS_GROUP_ORDER : PROJECT_TASK_PRIORITY_GROUP_ORDER;
    const keyOf = (task) => isStatus ? (task.status || 'todo') : (task.priority || 'low');
    const collapsedGroups = getCollapsedProjectTaskGroups();
    const expandedGroups = getExpandedProjectTaskGroups();
    return order.map((key) => {
        const groupTasks = sorted.filter((task) => keyOf(task) === key);
        if (groupTasks.length === 0) return '';
        const label = isStatus ? getStatusLabel(key) : getPriorityLabel(key);
        const attr = isStatus ? `data-status="${key}"` : `data-priority="${key}"`;
        const groupId = `${groupBy}-${key}`;
        const collapsed = isProjectTaskGroupCollapsed(groupId, collapsedGroups, expandedGroups);
        return `
            <div class="pd-task-group${collapsed ? ' is-collapsed' : ''}" ${attr} data-group-id="${groupId}">
                <button type="button" class="pd-task-group-header" aria-expanded="${!collapsed}"
                        data-action="toggleProjectTaskGroup" data-param="${groupId}">
                    <span class="pd-task-group-dot" aria-hidden="true"></span>
                    <span class="pd-task-group-label">${label}</span>
                    <span class="pd-task-group-count">${groupTasks.length}</span>
                    <svg class="pd-task-group-chevron" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6l4 4 4-4"/></svg>
                </button>
                <div class="pd-task-group-rows">
                    ${groupTasks.map((task) => buildProjectDetailsTaskRowHtml(task, { reorderable: false })).join('')}
                </div>
            </div>`;
    }).join('');
}

function setProjectTaskGroupBy(projectId, mode) {
    if (!PROJECT_TASK_GROUP_BY_MODES.includes(mode)) return;
    try { localStorage.setItem(PROJECT_TASK_GROUP_BY_KEY, mode); } catch (e) { /* preference only */ }

    const section = document.querySelector('#project-details-content .project-tasks-section');
    const list = document.getElementById('project-tasks-list');
    if (!section || !list) return;

    section.dataset.groupBy = mode;
    section.querySelectorAll('.pd-groupby-opt').forEach((btn) => {
        const active = btn.dataset.param2 === mode;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
    });

    // Fresh node drops the old drag listeners before re-wiring them
    const nextList = list.cloneNode(false);
    nextList.classList.add('pd-list-enter');
    nextList.addEventListener('animationend', () => nextList.classList.remove('pd-list-enter'), { once: true });
    nextList.innerHTML = buildProjectTasksListHtml(tasks.filter(task => task.projectId === projectId), mode);
    list.replaceWith(nextList);
    if (mode === 'none') setupProjectTasksDragDrop(projectId);
}

// Daylight project details: date range, duration, time left and overdue-task chips under the title
function buildProjectDetailsMetaHtml(project, projectStatus = null) {
    if (!project) return '';
    const status = projectStatus || getProjectStatus(project.id);
    const timeline = getProjectTimelineDetails(project, status);
    const chips = [];
    if (project.startDate || project.endDate) {
        const start = project.startDate ? escapeHtml(formatDatePretty(project.startDate, getLocale())) : '—';
        const end = project.endDate ? escapeHtml(formatDatePretty(project.endDate, getLocale())) : '—';
        chips.push(`<span class="pd-chip"><svg class="pd-chip-icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><rect x="2.5" y="3.5" width="11" height="10" rx="2"/><path d="M2.5 6.5h11M5.5 2v3M10.5 2v3"/></svg>${start} → ${end}</span>`);
    }
    if (timeline.durationText && timeline.durationText !== '-') {
        chips.push(`<span class="pd-chip">${escapeHtml(timeline.durationText)}</span>`);
    }
    if (timeline.remainingText) {
        chips.push(`<span class="pd-chip pd-chip-state" data-state="${timeline.remainingState}">${escapeHtml(timeline.remainingText)}</span>`);
    }
    const overdueIds = tasks.filter(task =>
        task.projectId === project.id && calculateDateUrgency(task.endDate, task.status).urgency === 'overdue'
    ).map(task => task.id);
    if (overdueIds.length > 0) {
        // Opens exactly these tasks in the list view (same taskIds filter the report links use)
        chips.push(`<a class="pd-chip pd-chip-state pd-chip-link" data-state="overdue" href="#tasks?view=list&taskIds=${overdueIds.join(',')}&taskSrc=overdue" title="${escapeHtml(t('projects.details.viewOverdueTasks'))}">${t('projects.details.overdueTasks', { count: overdueIds.length })}</a>`);
    }
    return chips.join('');
}

// Strip task= param from a hash string
function hashWithoutTaskParam(hash) {
    const bare = hash.startsWith('#') ? hash.slice(1) : hash;
    const [page, qs] = bare.split('?');
    const params = new URLSearchParams(qs || '');
    params.delete('task');
    const newQs = params.toString();
    return '#' + page + (newQs ? '?' + newQs : '');
}

function getRecurrenceModalElements() {
    const modal = document.getElementById('recurrence-modal');
    return {
        modal,
        title: document.getElementById('recurrence-modal-title'),
        ruleName: document.getElementById('rec-rule-name'),
        endDate: document.getElementById('recurrence-end-date'),
        endDateError: document.getElementById('recurrence-end-date-error'),
        interval: document.getElementById('recurrence-interval-days'),
        intervalError: document.getElementById('recurrence-interval-error'),
        intervalUnit: document.getElementById('recurrence-interval-unit'),
        weeklyGroup: document.getElementById('recurrence-weekly-group'),
        intervalGroup: document.getElementById('rec-interval-row'),
        intervalUnitLabel: document.getElementById('rec-interval-unit-label'),
        monthlyOpts: document.getElementById('rec-monthly-opts'),
        occurrencesRow: document.getElementById('rec-occurrences-row'),
        occurrencesInput: document.getElementById('rec-occurrences'),
        firstOccurrence: document.getElementById('rec-first-occurrence'),
        firstOccurrenceError: document.getElementById('recurrence-first-occurrence-error'),
        sourceToggle: document.getElementById('rec-source-toggle'),
        sourcePill: document.getElementById('rec-source-pill'),
        summary: document.getElementById('recurrence-source-summary'),
        sourceTaskSelect: document.getElementById('recurrence-source-task'),
        sourceError: document.getElementById('recurrence-source-error'),
        weeklyError: document.getElementById('recurrence-weekly-error'),
        taskStartModeError: document.getElementById('recurrence-task-start-mode-error'),
        taskEndModeError: document.getElementById('recurrence-task-end-mode-error'),
        formError: document.getElementById('recurrence-form-error'),
        submit: document.getElementById('recurrence-submit-btn'),
        search: document.getElementById('recurrence-task-search'),
        comboboxWrap: document.getElementById('recurrence-combobox'),
        comboboxList: document.getElementById('recurrence-combobox-list'),
        comboboxClear: document.getElementById('recurrence-combobox-clear'),
        selectedChip: document.getElementById('recurrence-selected-chip'),
        chipTitle: document.getElementById('recurrence-chip-title'),
        chipPriority: document.getElementById('recurrence-chip-priority'),
        summaryCard: document.getElementById('recurrence-summary-card'),
        summaryText: document.getElementById('recurrence-summary-text'),
        typePicker: document.getElementById('automation-type-picker'),
        typePickerGrid: document.getElementById('automation-type-picker-grid'),
        typePickerBtn: document.getElementById('automation-new-rule-btn'),
        typePickerBackdrop: document.getElementById('automation-type-picker-backdrop'),
        taskStartMode: document.getElementById('recurrence-task-start-mode'),
        taskEndMode: document.getElementById('recurrence-task-end-mode'),
        taskEndOffset: document.getElementById('recurrence-task-end-offset'),
        taskEndOffsetError: document.getElementById('recurrence-task-end-offset-error'),
        taskEndOffsetWrap: document.getElementById('recurrence-task-end-offset-wrap'),
        automationEndMode: document.getElementById('recurrence-automation-end-mode'),
        automationEndDateWrap: document.getElementById('recurrence-automation-end-date-wrap')
    };
}

function getAutomationBadgeHTML(extraClass = '') {
    return `<span class="task-automation-badge${extraClass}" title="${escapeHtml(t('automations.recurrence.generatedBy'))}">🔃</span>`;
}

function getLocalizedWeekdayShort(dayIndex) {
    const keys = [
        'recurrence.weekday.sun',
        'recurrence.weekday.mon',
        'recurrence.weekday.tue',
        'recurrence.weekday.wed',
        'recurrence.weekday.thu',
        'recurrence.weekday.fri',
        'recurrence.weekday.sat'
    ];
    return t(keys[dayIndex] || keys[0]);
}

function getLocalizedAutomationPattern(rule) {
    if (!rule) return '';
    if (rule.pattern === 'daily') return t('recurrence.pattern.daily');
    if (rule.pattern === 'monthly') return t('recurrence.pattern.monthly');
    if (rule.pattern === 'every-x-days') {
        const unitKey = `recurrence.intervalUnit.${rule.intervalUnit || 'days'}`;
        return t('recurrence.pattern.everyXDaysLabel', {
            count: String(rule.intervalDays || 1),
            unit: t(unitKey).toLowerCase()
        });
    }
    const days = Array.isArray(rule.daysOfWeek) ? [...rule.daysOfWeek].sort((a, b) => a - b) : [];
    if (days.length === 0) return t('recurrence.pattern.weekly');
    return `${t('recurrence.pattern.weekly')} · ${days.map(getLocalizedWeekdayShort).join(', ')}`;
}

function formatAutomationDateLabel(isoDate) {
    if (!isoDate) return '—';
    try {
        return formatDate(isoDate);
    } catch (error) {
        return isoDate;
    }
}

function computeInitialAutomationRunDate(task, draft) {
    const todayISO = getTodayISO();
    let initialISO = task?.startDate || todayISO;
    if (!initialISO || initialISO < todayISO) initialISO = todayISO;

    if (draft.pattern === 'weekly') {
        const selectedDays = Array.isArray(draft.daysOfWeek) ? draft.daysOfWeek : [];
        if (selectedDays.length > 0) {
            const probe = new Date(`${initialISO}T00:00:00`);
            let guard = 0;
            while (!selectedDays.includes(probe.getDay()) && guard < 14) {
                probe.setDate(probe.getDate() + 1);
                guard++;
            }
            initialISO = toISODate(probe);
        }
    }

    return initialISO;
}

function syncRecurrenceSubmitState() {
    const { submit } = getRecurrenceModalElements();
    if (!submit || !recurrenceDraft) return;
    const task = tasks.find((item) => item.id === recurrenceDraft.sourceTaskId);
    submit.disabled = !task;
}

function clearRecurrenceValidationErrors() {
    const {
        sourceError,
        weeklyError,
        intervalError,
        endDateError,
        taskEndOffsetError,
        firstOccurrenceError,
        taskStartModeError,
        taskEndModeError,
        formError,
        search,
        endDate,
        interval,
        firstOccurrence,
        taskStartMode,
        taskEndMode,
        taskEndOffset,
        weeklyGroup
    } = getRecurrenceModalElements();
    [sourceError, weeklyError, intervalError, endDateError, taskEndOffsetError, firstOccurrenceError, taskStartModeError, taskEndModeError, formError].forEach((el) => {
        if (!el) return;
        el.hidden = true;
        el.textContent = '';
    });
    [search, endDate, interval, firstOccurrence, getSelectTrigger(taskStartMode), getSelectTrigger(taskEndMode), taskEndOffset, weeklyGroup].forEach((el) => {
        if (!el) return;
        el.classList.remove('recurrence-invalid');
        el.removeAttribute('aria-invalid');
    });
}

function resetRecurrenceModalFields() {
    const {
        ruleName,
        sourceTaskSelect,
        search,
        comboboxWrap,
        comboboxList,
        comboboxClear,
        selectedChip,
        chipTitle,
        chipPriority,
        interval,
        intervalUnit,
        firstOccurrence,
        endDate,
        occurrencesInput,
        taskStartMode,
        taskEndMode,
        taskEndOffset,
        taskEndOffsetWrap,
        automationEndMode,
        automationEndDateWrap
    } = getRecurrenceModalElements();

    if (ruleName) ruleName.value = '';
    if (sourceTaskSelect) sourceTaskSelect.value = '';
    if (search) {
        search.value = '';
        search.dataset.selected = '';
    }
    if (comboboxWrap) comboboxWrap.hidden = false;
    if (comboboxList) {
        comboboxList.hidden = true;
        comboboxList.innerHTML = '';
    }
    if (comboboxClear) comboboxClear.hidden = true;
    if (selectedChip) selectedChip.hidden = true;
    if (chipTitle) chipTitle.textContent = '';
    if (chipPriority) chipPriority.className = 'recurrence-chip-priority';

    if (interval) interval.value = '1';
    if (intervalUnit) intervalUnit.value = 'days';
    writeRecurrenceFirstOccurrenceValue('');
    if (endDate) endDate.value = '';
    if (occurrencesInput) occurrencesInput.value = '10';
    if (taskStartMode) taskStartMode.value = '';
    if (taskEndMode) taskEndMode.value = '';
    if (taskEndOffset) taskEndOffset.value = '1';
    if (taskEndOffsetWrap) taskEndOffsetWrap.hidden = true;
    if (automationEndMode) automationEndMode.value = 'never';
    if (automationEndDateWrap) automationEndDateWrap.hidden = true;

    clearRecurrenceValidationErrors();
}

function setRecurrenceFieldError(target, message) {
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
}

function getRecurrenceTaskSummaryText(task) {
    if (!task) return t('recurrence.sourceTaskHint');
    const project = projects.find((p) => p.id === task.projectId);
    const projectLabel = project ? project.name : '';
    if (projectLabel) return projectLabel;
    if (task.description) {
        return task.description.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 120);
    }
    return t('recurrence.sourceTaskHint');
}

function syncRecurrenceSourceSummary() {
    const { summary } = getRecurrenceModalElements();
    if (!summary || !recurrenceDraft) return;

    const sourceTask = tasks.find((item) => item.id === recurrenceDraft.sourceTaskId);
    if (!sourceTask && recurrenceDraft.sourceTaskId != null) {
        summary.textContent = t('recurrence.sourceSummary');
        summary.hidden = false;
        return;
    }

    summary.textContent = '';
    summary.hidden = true;
}

function syncRecurrenceSourceSelect(selectedTaskId = null) {
    const { sourceTaskSelect } = getRecurrenceModalElements();
    if (!sourceTaskSelect || !recurrenceDraft) return;

    const selectedValue = selectedTaskId != null
        ? String(selectedTaskId)
        : (recurrenceDraft.sourceTaskId != null ? String(recurrenceDraft.sourceTaskId) : '');
    const missingTaskId = recurrenceDraft.sourceTaskId != null
        && !tasks.some((item) => String(item.id) === String(recurrenceDraft.sourceTaskId));

    const taskOptions = [...tasks]
        .sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')))
        .map((task) => `<option value="${escapeHtml(String(task.id))}">${escapeHtml(task.title || t('tasks.untitled'))}</option>`)
        .join('');

    const missingOption = missingTaskId
        ? `<option value="${escapeHtml(String(recurrenceDraft.sourceTaskId))}">${escapeHtml(t('recurrence.sourceTaskMissingOption'))}</option>`
        : '';

    sourceTaskSelect.innerHTML = `<option value="">${escapeHtml(t('recurrence.sourceTaskHint'))}</option>${taskOptions}${missingOption}`;
    sourceTaskSelect.value = selectedValue;
    sourceTaskSelect.onchange = () => setRecurrenceSelectedTask(sourceTaskSelect.value);
}

function setRecurrenceSelectedTask(taskId) {
    if (!recurrenceDraft) recurrenceDraft = createDefaultRecurrenceDraft();
    clearRecurrenceValidationErrors();
    const parsed = taskId != null ? parseInt(taskId, 10) : null;
    const task = Number.isFinite(parsed) ? tasks.find((item) => item.id === parsed) : null;
    recurrenceDraft.sourceTaskId = task ? task.id : null;
    const { search, comboboxClear, comboboxList, comboboxWrap, selectedChip, chipTitle, chipPriority } = getRecurrenceModalElements();

    if (task) {
        // Show chip, hide combobox
        if (comboboxWrap) comboboxWrap.hidden = true;
        if (selectedChip) selectedChip.hidden = false;
        if (chipTitle) chipTitle.textContent = task.title || t('tasks.untitled');
        if (chipPriority) {
            chipPriority.className = `recurrence-chip-priority priority-${task.priority || 'medium'}`;
        }
    } else {
        // Show combobox, hide chip
        if (comboboxWrap) comboboxWrap.hidden = false;
        if (selectedChip) selectedChip.hidden = true;
        if (search) { search.value = ''; search.dataset.selected = ''; }
    }

    syncRecurrenceTaskEndOffset(task);
    syncRecurrenceSourceSummary();
    syncRecurrenceSourceSelect(task ? task.id : null);
    if (comboboxClear) comboboxClear.hidden = !task;
    if (comboboxList) {
        comboboxList.hidden = true;
        comboboxList.innerHTML = '';
    }
    syncRecurrenceSubmitState();
    syncRecurrenceSummary();
}

function renderRecurrenceComboboxResults(query) {
    const { comboboxList } = getRecurrenceModalElements();
    if (!comboboxList) return;
    const q = String(query || '').trim().toLowerCase();
    const sorted = [...tasks].sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    const filtered = q
        ? sorted.filter((task) => {
            const title = String(task.title || '').toLowerCase();
            const project = projects.find((p) => p.id === task.projectId);
            const projectName = String(project?.name || '').toLowerCase();
            return title.includes(q) || projectName.includes(q);
        })
        : sorted;
    const limited = filtered.slice(0, 50);
    if (limited.length === 0) {
        comboboxList.innerHTML = `<li class="recurrence-combobox-empty">${escapeHtml(t('recurrence.source.noMatches'))}</li>`;
    } else {
        comboboxList.innerHTML = limited.map((task) => {
            const project = projects.find((p) => p.id === task.projectId);
            const title = escapeHtml(task.title || t('tasks.untitled'));
            const meta = project ? escapeHtml(project.name) : '';
            const priorityClass = `recurrence-combobox-pri recurrence-combobox-pri-${escapeHtml(task.priority || 'medium')}`;
            return `<li class="recurrence-combobox-item" role="option" data-action="selectRecurrenceComboboxTask" data-param="${task.id}">
                <span class="${priorityClass}" aria-hidden="true"></span>
                <span class="recurrence-combobox-item-main">
                    <span class="recurrence-combobox-item-title">${title}</span>
                    ${meta ? `<span class="recurrence-combobox-item-meta">${meta}</span>` : ''}
                </span>
            </li>`;
        }).join('');
    }
    comboboxList.hidden = false;
}

function handleRecurrenceSearchInput(event) {
    const value = event?.target?.value ?? '';
    const { comboboxClear, search } = getRecurrenceModalElements();
    clearRecurrenceValidationErrors();
    if (comboboxClear) comboboxClear.hidden = !value;
    if (search) search.dataset.selected = '';
    if (recurrenceDraft) {
        recurrenceDraft.sourceTaskId = null;
    }
    renderRecurrenceComboboxResults(value);
    syncRecurrenceSubmitState();
    syncRecurrenceSummary();
}

function selectRecurrenceComboboxTask(taskId) {
    setRecurrenceSelectedTask(taskId);
}

function clearRecurrenceCombobox() {
    setRecurrenceSelectedTask(null);
    const { search } = getRecurrenceModalElements();
    if (search) search.focus();
}

function getRecurrenceDraftTitle() {
    const task = tasks.find((item) => item.id === recurrenceDraft?.sourceTaskId);
    return task ? (task.title || t('tasks.untitled')) : '';
}

function formatRecurrenceSummaryDate(iso) {
    if (!iso) return '';
    try { return formatDatePretty(iso, getLocale()); } catch (e) { return iso; }
}

function getRecurrenceFirstOccurrenceElements() {
    const { firstOccurrence } = getRecurrenceModalElements();
    const wrapper = firstOccurrence?.closest?.('.date-input-wrapper') || null;
    const displayInput = wrapper?.querySelector?.('.date-display') || null;
    const flatpickrInstance = displayInput?._flatpickr || null;
    return { firstOccurrence, wrapper, displayInput, flatpickrInstance };
}

function readRecurrenceFirstOccurrenceValue() {
    const { firstOccurrence, displayInput } = getRecurrenceFirstOccurrenceElements();
    const hiddenValue = (firstOccurrence?.value || '').trim();
    if (hiddenValue) return hiddenValue;

    const displayValue = (displayInput?.value || '').trim();
    if (!displayValue) return recurrenceDraft?.firstOccurrenceDate || '';
    return looksLikeDMY(displayValue) ? toISOFromDMY(displayValue) : displayValue;
}

function writeRecurrenceFirstOccurrenceValue(value) {
    const normalized = (value || '').trim();
    const { firstOccurrence, displayInput, flatpickrInstance } = getRecurrenceFirstOccurrenceElements();

    if (firstOccurrence) firstOccurrence.value = normalized;

    if (displayInput) {
        if (flatpickrInstance) {
            if (normalized) {
                flatpickrInstance.setDate(new Date(`${normalized}T00:00:00`), false);
            } else {
                flatpickrInstance.clear();
            }
        } else {
            displayInput.value = normalized ? toDMYFromISO(normalized) : '';
        }
    }
}

function syncRecurrenceTaskDateDependencies() {
    const { taskStartMode, taskEndMode, taskEndOffsetWrap } = getRecurrenceModalElements();
    const offsetOption = taskEndMode?.querySelector('option[value="offset-days"]') || null;
    const usesCreationBase = taskStartMode?.value !== 'creation-day';
    if (offsetOption) {
        offsetOption.textContent = usesCreationBase
            ? t('recurrence.taskDates.endOffsetCreation')
            : t('recurrence.taskDates.endOffset');
    }

    if (taskEndOffsetWrap) {
        taskEndOffsetWrap.hidden = taskEndMode?.value !== 'offset-days';
    }
}

function isRecurrenceTaskEndOffsetMode(mode) {
    return mode === 'offset-days' || mode === 'offset-creation-days';
}

function normalizeRecurrenceTaskEndModeForUI(mode) {
    return mode === 'offset-creation-days' ? 'offset-days' : (mode || '');
}

function getRecurrenceSourceTaskDurationDays(task) {
    if (!task?.startDate || !task?.endDate) return 1;
    const start = new Date(`${task.startDate}T00:00:00`);
    const end = new Date(`${task.endDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) || 1);
}

function syncRecurrenceTaskEndOffset(task = null) {
    if (!recurrenceDraft) return;
    const { taskEndOffset } = getRecurrenceModalElements();
    const resolved = task
        ? getRecurrenceSourceTaskDurationDays(task)
        : Math.max(1, parseInt(recurrenceDraft.taskEndDateOffset, 10) || 1);
    recurrenceDraft.taskEndDateOffset = resolved;
    if (taskEndOffset) taskEndOffset.value = String(resolved);
}

function syncRecurrenceSummary() {
    const { summaryText, summaryCard, interval: intervalEl, intervalUnit: intervalUnitEl, occurrencesInput, endDate: endDateEl, firstOccurrence } = getRecurrenceModalElements();
    if (!summaryText || !recurrenceDraft) return;

    const title = getRecurrenceDraftTitle();
    if (!title) {
        summaryText.innerHTML = escapeHtml(t('recurrence.summary.empty'));
        if (summaryCard) summaryCard.classList.remove('has-content');
        return;
    }

    const interval = Math.max(1, parseInt(intervalEl?.value, 10) || recurrenceDraft.intervalDays || 1);
    let repeatStr = '';

    if (recurrenceDraft.pattern === 'daily') {
        repeatStr = interval > 1
            ? t('recurrence.preview.everyNDays', { count: String(interval) })
            : t('recurrence.preview.everyDay');
    } else if (recurrenceDraft.pattern === 'weekly') {
        const days = Array.isArray(recurrenceDraft.daysOfWeek) ? [...recurrenceDraft.daysOfWeek].sort((a, b) => a - b) : [];
        if (!days.length) {
            summaryText.innerHTML = escapeHtml(t('recurrence.summary.pickDay'));
            if (summaryCard) summaryCard.classList.remove('has-content');
            return;
        }
        const dayNames = days.map(getLocalizedWeekdayShort).join(', ');
        repeatStr = interval > 1
            ? t('recurrence.preview.everyNWeeksOn', { count: String(interval), days: dayNames })
            : t('recurrence.preview.everyWeekOn', { days: dayNames });
    } else if (recurrenceDraft.pattern === 'monthly') {
        repeatStr = recurrenceDraft.monthlyMode === 'weekday'
            ? t('recurrence.preview.monthlyWeekday')
            : t('recurrence.preview.monthlyDate');
    } else {
        const unit = intervalUnitEl?.value || recurrenceDraft.intervalUnit || 'days';
        const unitLabel = t(`recurrence.intervalUnit.${unit}`).toLowerCase();
        repeatStr = t('recurrence.preview.everyNUnit', { count: String(interval), unit: unitLabel });
    }

    const resolvedEndDate = (endDateEl?.value || recurrenceDraft.endDate || '').trim();
    const resolvedEndMode = resolvedEndDate ? 'date' : (recurrenceDraft.endMode || 'never');
    let endStr = '';
    if (resolvedEndMode === 'date') {
        endStr = t('recurrence.preview.until', { date: formatRecurrenceSummaryDate(resolvedEndDate) });
    } else if (resolvedEndMode === 'count') {
        const count = Math.max(1, parseInt(occurrencesInput?.value, 10) || recurrenceDraft.maxOccurrences || 1);
        endStr = t('recurrence.preview.forNOccurrences', { count: String(count) });
    } else {
        endStr = t('recurrence.preview.forever');
    }

    let startStr = '';
    const firstISO = readRecurrenceFirstOccurrenceValue();
    if (firstISO) {
        startStr = t('recurrence.preview.firstOccurrence', { date: formatRecurrenceSummaryDate(firstISO) });
    }

    const repeatHtml = `<strong>${escapeHtml(repeatStr)}</strong>`;
    const suffixParts = [startStr, endStr].filter(Boolean).map((s) => escapeHtml(s));
    summaryText.innerHTML = t('recurrence.preview.repeats', { repeat: repeatHtml })
        + (suffixParts.length ? ', ' + suffixParts.join(', ') : '')
        + '.';
    if (summaryCard) summaryCard.classList.add('has-content');
}

function syncRecurrencePatternUI() {
    const { interval, intervalUnit, intervalUnitLabel, weeklyGroup, intervalGroup, monthlyOpts } = getRecurrenceModalElements();
    const pattern = recurrenceDraft?.pattern || 'daily';

    // Active pill
    document.querySelectorAll('[data-action="selectRecurrencePattern"]').forEach((button) => {
        button.classList.toggle('active', button.dataset.param === pattern);
    });

    // Show/hide contextual groups
    const showInterval = pattern !== 'monthly';
    if (intervalGroup) intervalGroup.style.display = showInterval ? 'flex' : 'none';
    if (weeklyGroup) weeklyGroup.hidden = pattern !== 'weekly';
    if (monthlyOpts) monthlyOpts.hidden = pattern !== 'monthly';

    // Inline unit label (days/weeks/months) vs custom unit select
    if (intervalUnit) {
        const unitTrigger = document.querySelector('[data-select-for="recurrence-interval-unit"]');
        if (unitTrigger) unitTrigger.hidden = pattern !== 'every-x-days';
        intervalUnit.value = recurrenceDraft?.intervalUnit || 'days';
    }
    if (intervalUnitLabel) {
        intervalUnitLabel.style.display = pattern === 'every-x-days' ? 'none' : 'inline';
        const unitKey = pattern === 'weekly'
            ? 'recurrence.unit.weeks'
            : pattern === 'monthly'
                ? 'recurrence.unit.months'
                : 'recurrence.unit.days';
        intervalUnitLabel.textContent = t(unitKey);
    }

    if (interval) interval.value = String(recurrenceDraft?.intervalDays || 1);

    // Day chips — data-param is JS day index (0=Sun..6=Sat)
    document.querySelectorAll('[data-action="toggleRecurrenceWeekday"]').forEach((button) => {
        const dayIndex = parseInt(button.dataset.param, 10);
        const selected = !!recurrenceDraft?.daysOfWeek?.includes(dayIndex);
        button.classList.toggle('active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    // Monthly radio sync
    const monthlyMode = recurrenceDraft?.monthlyMode || 'date';
    document.querySelectorAll('input[name="rec-monthly"]').forEach((input) => {
        input.checked = input.value === monthlyMode;
    });
}

function syncRecurrenceDateUI() {
    const { taskStartMode, taskEndMode, taskEndOffset, taskEndOffsetWrap, endDate, occurrencesRow, occurrencesInput, automationEndMode, automationEndDateWrap, firstOccurrence } = getRecurrenceModalElements();
    if (taskStartMode) taskStartMode.value = recurrenceDraft?.taskStartDateMode || '';
    if (taskEndMode) taskEndMode.value = normalizeRecurrenceTaskEndModeForUI(recurrenceDraft?.taskEndDateMode || '');
    if (taskEndOffset) taskEndOffset.value = String(recurrenceDraft?.taskEndDateOffset || 1);
    writeRecurrenceFirstOccurrenceValue(recurrenceDraft?.firstOccurrenceDate || '');
    if (endDate) endDate.value = recurrenceDraft?.endDate || '';
    if (occurrencesInput) occurrencesInput.value = String(recurrenceDraft?.maxOccurrences || 10);
    if (taskEndOffsetWrap) taskEndOffsetWrap.hidden = !isRecurrenceTaskEndOffsetMode(normalizeRecurrenceTaskEndModeForUI(recurrenceDraft?.taskEndDateMode || 'none'));
    syncRecurrenceTaskDateDependencies();

    // End-mode pill selection + contextual control visibility
    const endMode = recurrenceDraft?.endMode || 'never';
    document.querySelectorAll('.rec-pill[data-end]').forEach((pill) => {
        pill.classList.toggle('active', pill.dataset.end === endMode);
    });
    if (endDate) endDate.hidden = endMode !== 'date';
    if (occurrencesRow) occurrencesRow.hidden = endMode !== 'count';
    if (automationEndMode) automationEndMode.value = endMode === 'date' ? 'on-date' : 'never';
    if (automationEndDateWrap) automationEndDateWrap.hidden = endMode !== 'date';
}

function bindRecurrenceEndModePills() {
    document.querySelectorAll('.rec-pill[data-end]').forEach((pill) => {
        pill.onclick = () => {
            if (!recurrenceDraft) recurrenceDraft = createDefaultRecurrenceDraft();
            recurrenceDraft.endMode = pill.dataset.end;
            if (recurrenceDraft.endMode !== 'date') recurrenceDraft.endDate = '';
            if (recurrenceDraft.endMode !== 'count') recurrenceDraft.maxOccurrences = null;
            if (recurrenceDraft.endMode === 'count' && !recurrenceDraft.maxOccurrences) {
                recurrenceDraft.maxOccurrences = 10;
            }
            syncRecurrenceDateUI();
            syncRecurrenceSummary();
        };
    });
}

function openRecurrenceModal(taskId = null) {
    closeAutomationTypePicker();
    resetRecurrenceModalFields();

    const draft = createDefaultRecurrenceDraft();
    const explicitTaskId = taskId != null ? parseInt(taskId, 10) : NaN;
    const implicitTaskId = parseInt(document.getElementById('task-form')?.dataset?.editingTaskId || '', 10);
    const preferredTaskId = Number.isFinite(explicitTaskId)
        ? explicitTaskId
        : (Number.isFinite(implicitTaskId) ? implicitTaskId : null);
    const preselectedTask = preferredTaskId != null
        ? tasks.find((item) => item.id === preferredTaskId)
        : null;

    recurrenceDraft = {
        ...draft,
        sourceTaskId: preselectedTask ? preselectedTask.id : null
    };

    const { modal, title, ruleName, interval, intervalUnit, search } = getRecurrenceModalElements();
    if (!modal) return;

    if (title) title.setAttribute('data-i18n', 'recurrence.createTitle');
    applyTranslations(modal);
    if (interval) interval.value = String(recurrenceDraft.intervalDays || 1);
    if (intervalUnit) intervalUnit.value = recurrenceDraft.intervalUnit || 'days';
    if (ruleName) {
        ruleName.value = recurrenceDraft.name || '';
        ruleName.oninput = () => {
            if (recurrenceDraft) recurrenceDraft.name = ruleName.value;
        };
    }

    syncRecurrencePatternUI();
    syncRecurrenceDateUI();
    setRecurrenceSelectedTask(preselectedTask ? preselectedTask.id : null);
    syncRecurrenceSourceSelect(preselectedTask ? preselectedTask.id : null);
    clearRecurrenceValidationErrors();

    // Search combobox
    if (search) {
        search.oninput = handleRecurrenceSearchInput;
        search.onfocus = () => renderRecurrenceComboboxResults(search.value);
        search.onblur = () => {
            setTimeout(() => {
                const { comboboxList } = getRecurrenceModalElements();
                if (comboboxList) comboboxList.hidden = true;
            }, 150);
        };
    }

    // Interval / interval-unit → live preview
    if (intervalUnit) intervalUnit.onchange = () => {
        if (recurrenceDraft) recurrenceDraft.intervalUnit = intervalUnit.value || 'days';
        const { intervalUnitLabel } = getRecurrenceModalElements();
        if (intervalUnitLabel) intervalUnitLabel.textContent = t(`recurrence.unit.${intervalUnit.value || 'days'}`);
        syncRecurrenceSummary();
    };
    if (interval) interval.oninput = () => {
        if (recurrenceDraft) recurrenceDraft.intervalDays = Math.max(1, parseInt(interval.value, 10) || 1);
        syncRecurrenceSummary();
    };

    bindRecurrenceEndModePills();

    // Monthly radios
    document.querySelectorAll('input[name="rec-monthly"]').forEach((input) => {
        input.onchange = () => {
            if (!recurrenceDraft) recurrenceDraft = createDefaultRecurrenceDraft();
            recurrenceDraft.monthlyMode = input.value;
            syncRecurrenceSummary();
        };
    });

    // Keep the hidden task span in sync with the selected source task
    wireRecurrenceDateHandlers();

    // Source sliding pill toggle (Search existing / Create new)
    initRecurrenceSourceToggle();

    syncRecurrenceSubmitState();
    syncRecurrenceSummary();
    modal.classList.add('active');
}

function initRecurrenceSourceToggle() {
    const { sourceToggle, sourcePill, search } = getRecurrenceModalElements();
    if (!sourceToggle || !sourcePill) return;

    const movePill = () => {
        const active = sourceToggle.querySelector('.rec-source-btn.active');
        if (active) {
            sourcePill.style.left = active.offsetLeft + 'px';
            sourcePill.style.width = active.offsetWidth + 'px';
        }
    };

    sourceToggle.querySelectorAll('.rec-source-btn').forEach((btn) => {
        btn.onclick = () => {
            const mode = btn.dataset.mode;
            sourceToggle.querySelectorAll('.rec-source-btn').forEach((b) => b.classList.remove('active'));
            btn.classList.add('active');
            if (recurrenceDraft) recurrenceDraft.sourceMode = mode;
            movePill();
            if (mode === 'new') {
                // Route to existing "create new task as template" flow
                startRecurrenceNewTemplate();
            } else {
                if (search) {
                    search.placeholder = t('recurrence.searchPlaceholder');
                    search.focus();
                }
            }
        };
    });

    // Defer one frame so layout is ready before positioning the pill
    requestAnimationFrame(movePill);
    window.addEventListener('resize', movePill, { once: false });
}

function wireRecurrenceDateHandlers() {
    const { modal, taskStartMode, taskEndMode, taskEndOffset, taskEndOffsetWrap, automationEndMode, automationEndDateWrap, endDate, occurrencesInput } = getRecurrenceModalElements();
    if (taskStartMode) taskStartMode.onchange = () => {
        clearRecurrenceValidationErrors();
        if (recurrenceDraft) recurrenceDraft.taskStartDateMode = taskStartMode.value || '';
        syncRecurrenceTaskDateDependencies();
        syncRecurrenceSummary();
    };
    if (taskEndMode) taskEndMode.onchange = () => {
        clearRecurrenceValidationErrors();
        if (recurrenceDraft) recurrenceDraft.taskEndDateMode = taskEndMode.value || '';
        if (taskEndOffsetWrap) taskEndOffsetWrap.hidden = !isRecurrenceTaskEndOffsetMode(taskEndMode.value);
        syncRecurrenceSummary();
    };
    if (taskEndOffset) taskEndOffset.oninput = () => {
        clearRecurrenceValidationErrors();
        if (recurrenceDraft) recurrenceDraft.taskEndDateOffset = Math.max(1, parseInt(taskEndOffset.value, 10) || 1);
        syncRecurrenceSummary();
    };
    const { firstOccurrence, displayInput, flatpickrInstance } = getRecurrenceFirstOccurrenceElements();
    if (firstOccurrence) {
        const syncFirstOccurrence = () => {
            clearRecurrenceValidationErrors();
            const resolved = readRecurrenceFirstOccurrenceValue();
            if (recurrenceDraft) recurrenceDraft.firstOccurrenceDate = resolved || '';
            syncRecurrenceSummary();
        };
        firstOccurrence.oninput = syncFirstOccurrence;
        firstOccurrence.onchange = syncFirstOccurrence;
        firstOccurrence.onblur = syncFirstOccurrence;
        if (displayInput) {
            displayInput.oninput = syncFirstOccurrence;
            displayInput.onchange = syncFirstOccurrence;
            displayInput.onblur = syncFirstOccurrence;
            if (flatpickrInstance?.config?.onChange && !flatpickrInstance.__recurrenceFirstOccurrenceBound) {
                flatpickrInstance.__recurrenceFirstOccurrenceBound = true;
                flatpickrInstance.config.onChange.push(syncFirstOccurrence);
            }
        }
    }
    if (automationEndMode) automationEndMode.onchange = () => {
        clearRecurrenceValidationErrors();
        const isDate = automationEndMode.value === 'on-date';
        if (automationEndDateWrap) automationEndDateWrap.hidden = !isDate;
        if (!isDate && recurrenceDraft) recurrenceDraft.endDate = '';
        syncRecurrenceSummary();
    };
    if (endDate) {
        const syncEndDate = () => {
            clearRecurrenceValidationErrors();
            if (recurrenceDraft) {
                recurrenceDraft.endMode = 'date';
                recurrenceDraft.endDate = endDate.value || '';
            }
            if (automationEndMode) automationEndMode.value = 'on-date';
            if (automationEndDateWrap) automationEndDateWrap.hidden = false;
            syncRecurrenceSummary();
        };
        endDate.oninput = syncEndDate;
        endDate.onchange = syncEndDate;
        endDate.onblur = syncEndDate;
        const endDateDisplayInput = endDate.closest('.date-input-wrapper')?.querySelector('.date-display');
        const endDateFlatpickr = endDateDisplayInput?._flatpickr || null;
        if (endDateFlatpickr?.config?.onChange && !endDateFlatpickr.__recurrenceEndDateBound) {
            endDateFlatpickr.__recurrenceEndDateBound = true;
            endDateFlatpickr.config.onChange.push(syncEndDate);
        }
    }
    if (occurrencesInput) {
        const syncOccurrences = () => {
            clearRecurrenceValidationErrors();
            if (recurrenceDraft) recurrenceDraft.maxOccurrences = Math.max(1, parseInt(occurrencesInput.value, 10) || 1);
            syncRecurrenceSummary();
        };
        occurrencesInput.oninput = syncOccurrences;
        occurrencesInput.onchange = syncOccurrences;
        occurrencesInput.onblur = syncOccurrences;
    }
}

function closeRecurrenceModal() {
    resetRecurrenceModalFields();
    recurrenceDraft = null;
    closeModal('recurrence-modal');
}

function selectRecurrencePattern(pattern) {
    if (!recurrenceDraft) recurrenceDraft = createDefaultRecurrenceDraft();
    clearRecurrenceValidationErrors();
    recurrenceDraft.pattern = pattern || 'daily';
    if (recurrenceDraft.pattern === 'weekly' && (!Array.isArray(recurrenceDraft.daysOfWeek) || recurrenceDraft.daysOfWeek.length === 0)) {
        recurrenceDraft.daysOfWeek = [new Date().getDay()];
    }
    syncRecurrencePatternUI();
    syncRecurrenceSummary();
}

function toggleRecurrenceWeekday(dayIndex) {
    if (!recurrenceDraft) recurrenceDraft = createDefaultRecurrenceDraft();
    clearRecurrenceValidationErrors();
    const next = new Set(Array.isArray(recurrenceDraft.daysOfWeek) ? recurrenceDraft.daysOfWeek : []);
    if (next.has(dayIndex)) next.delete(dayIndex);
    else next.add(dayIndex);
    recurrenceDraft.daysOfWeek = [...next].sort((a, b) => a - b);
    syncRecurrencePatternUI();
    syncRecurrenceSummary();
}

function startRecurrenceNewTemplate() {
    pendingRecurrenceAfterTaskCreate = {
        draft: recurrenceDraft ? {
            pattern: recurrenceDraft.pattern,
            daysOfWeek: Array.isArray(recurrenceDraft.daysOfWeek) ? [...recurrenceDraft.daysOfWeek] : [],
            intervalDays: recurrenceDraft.intervalDays,
            intervalUnit: recurrenceDraft.intervalUnit || 'days',
            endDate: recurrenceDraft.endDate,
            endMode: recurrenceDraft.endMode || 'never',
            maxOccurrences: recurrenceDraft.maxOccurrences || null,
            monthlyMode: recurrenceDraft.monthlyMode || 'date',
            firstOccurrenceDate: recurrenceDraft.firstOccurrenceDate || '',
            taskStartDateMode: recurrenceDraft.taskStartDateMode || 'creation-day',
            taskEndDateMode: recurrenceDraft.taskEndDateMode || 'offset-days',
            taskEndDateOffset: recurrenceDraft.taskEndDateOffset || 1
        } : null,
        automationId: recurrenceDraft?.automationId || null
    };
    recurrenceDraft = null;
    closeModal('recurrence-modal');
    openTaskModal();
}

function resumeRecurrenceAfterTaskCreate(newTaskId) {
    const pending = pendingRecurrenceAfterTaskCreate;
    pendingRecurrenceAfterTaskCreate = null;
    if (!pending) return;
    openRecurrenceModal(newTaskId || null);
    if (pending.draft && recurrenceDraft) {
        Object.assign(recurrenceDraft, {
            pattern: pending.draft.pattern,
            daysOfWeek: Array.isArray(pending.draft.daysOfWeek) ? [...pending.draft.daysOfWeek] : [],
            intervalDays: pending.draft.intervalDays,
            intervalUnit: pending.draft.intervalUnit || 'days',
            endDate: pending.draft.endDate,
            endMode: pending.draft.endMode || 'never',
            maxOccurrences: pending.draft.maxOccurrences || null,
            monthlyMode: pending.draft.monthlyMode || 'date',
            firstOccurrenceDate: pending.draft.firstOccurrenceDate || '',
            taskStartDateMode: pending.draft.taskStartDateMode || 'creation-day',
            taskEndDateMode: pending.draft.taskEndDateMode || 'offset-days',
            taskEndDateOffset: pending.draft.taskEndDateOffset || 1,
            automationId: pending.automationId
        });
        const { interval, intervalUnit } = getRecurrenceModalElements();
        if (interval) interval.value = String(recurrenceDraft.intervalDays || 1);
        if (intervalUnit) intervalUnit.value = recurrenceDraft.intervalUnit || 'days';
        syncRecurrenceDateUI();
        syncRecurrencePatternUI();
        syncRecurrenceSummary();
        syncRecurrenceSubmitState();
    }
}

function normalizeAutomationTemplateTitle(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function findExistingAutomationSourceTask(template = {}) {
    const normalizedTitle = normalizeAutomationTemplateTitle(template.title);
    if (!normalizedTitle) return null;
    const normalizedProjectId = template.projectId != null ? Number(template.projectId) : null;
    const candidates = tasks.filter((task) => normalizeAutomationTemplateTitle(task.title) === normalizedTitle);
    if (!candidates.length) return null;
    if (normalizedProjectId != null) {
        const exactProjectMatches = candidates.filter((task) => Number(task.projectId) === normalizedProjectId);
        if (exactProjectMatches.length === 1) return exactProjectMatches[0];
        return null;
    }
    return candidates.length === 1 ? candidates[0] : null;
}

async function ensureAutomationSourceTask(template = {}, automationId = null) {
    const existingTask = findExistingAutomationSourceTask(template);
    if (existingTask) {
        return {
            task: existingTask,
            created: false
        };
    }

    const normalizedDescription = beautifyDescriptionLinksHtml(
        autoLinkifyDescription(
            convertPlainTextDescriptionToHtml(template.description || '')
        )
    );
    const taskResult = createTaskService({
        title: String(template.title || '').trim(),
        description: normalizedDescription,
        projectId: template.projectId != null ? Number(template.projectId) : null,
        startDate: '',
        endDate: '',
        priority: String(template.priority || 'medium').trim() || 'medium',
        status: 'todo',
        tags: Array.isArray(template.tags) ? [...template.tags] : [],
        automationId
    }, tasks, taskCounter);

    const newTask = {
        ...taskResult.task,
        isTemplate: true,
        automationId
    };
    tasks = taskResult.tasks.map((task) => task.id === newTask.id ? newTask : task);
    taskCounter = taskResult.taskCounter;
    await saveTaskCreate(newTask);
    return {
        task: newTask,
        created: true
    };
}

function cancelPendingRecurrenceAfterTaskCreate() {
    pendingRecurrenceAfterTaskCreate = null;
}

function finalizeCloseTaskModal({ returnToRecurrence = false } = {}) {
    // For existing tasks, persist latest description (including checkboxes) before closing
    const form = document.getElementById("task-form");
    if (form && form.dataset.editingTaskId) {
        updateTaskField('description', getTaskDescriptionHTML());
    }

    if (form) {
        form.reset();
        delete form.dataset.editingTaskId;

        // Reset status dropdown to default (keep in sync with create-mode defaults)
        const statusBadge = document.querySelector("#status-current .status-badge");
        if (statusBadge) {
            statusBadge.className = "status-badge todo";
            statusBadge.textContent = getStatusLabel("todo");
        }
        const hiddenStatus = document.getElementById("hidden-status");
        if (hiddenStatus) hiddenStatus.value = "todo";

        // Reset priority dropdown to default
        const priorityCurrentBtn = document.querySelector("#priority-current");
        if (priorityCurrentBtn) {
            const priorityLabel = getPriorityLabel("medium").toUpperCase();
            priorityCurrentBtn.innerHTML = `<span class="priority-pill priority-medium">${priorityLabel}</span> <span class="dropdown-arrow">▼</span>`;
        }
        const hiddenPriority = document.getElementById("hidden-priority");
        if (hiddenPriority) hiddenPriority.value = "medium";
    }

    initialTaskFormState = null;
    currentTaskNavigationContext = null;

    if (taskModalReturnHash) {
        safeReplaceState(null, '', taskModalReturnHash);
        taskModalReturnHash = null;
    }

    closeModal("task-modal");

    reopenAutomationAuditAfterTaskClose();

    if (returnToRecurrence) {
        resumeRecurrenceAfterTaskCreate(null);
    } else {
        cancelPendingRecurrenceAfterTaskCreate();
    }
}

async function submitRecurrenceModal() {
    if (!recurrenceDraft) recurrenceDraft = createDefaultRecurrenceDraft();

    const { ruleName, endDate, endDateError, interval, intervalError, intervalUnit, taskStartMode, taskEndMode, taskEndOffset, taskEndOffsetError, occurrencesInput, firstOccurrence, firstOccurrenceError, search, sourceError, weeklyError, taskStartModeError, taskEndModeError, formError, weeklyGroup } = getRecurrenceModalElements();
    clearRecurrenceValidationErrors();
    const sourceTaskData = tasks.find((task) => task.id === recurrenceDraft.sourceTaskId) || null;
    if (!sourceTaskData) {
        if (search) {
            search.classList.add('recurrence-invalid');
            search.setAttribute('aria-invalid', 'true');
            search.focus();
        }
        setRecurrenceFieldError(sourceError, t('recurrence.validation.sourceTaskMissing'));
        return;
    }

    // Require at least one weekday for weekly
    if (recurrenceDraft.pattern === 'weekly' && (!Array.isArray(recurrenceDraft.daysOfWeek) || !recurrenceDraft.daysOfWeek.length)) {
        if (weeklyGroup) {
            weeklyGroup.classList.add('recurrence-invalid');
            weeklyGroup.setAttribute('aria-invalid', 'true');
        }
        setRecurrenceFieldError(weeklyError, t('recurrence.errorNoDays'));
        return;
    }

    const { firstOccurrence: firstOccurrenceInput, displayInput } = getRecurrenceFirstOccurrenceElements();
    const resolvedFirstOccurrence = readRecurrenceFirstOccurrenceValue();
    if (!resolvedFirstOccurrence) {
        if (firstOccurrenceInput) {
            firstOccurrenceInput.classList.add('recurrence-invalid');
            firstOccurrenceInput.setAttribute('aria-invalid', 'true');
        }
        if (displayInput) {
            displayInput.classList.add('recurrence-invalid');
            displayInput.setAttribute('aria-invalid', 'true');
            displayInput.focus();
        } else if (firstOccurrenceInput) {
            firstOccurrenceInput.focus();
        }
        setRecurrenceFieldError(firstOccurrenceError, t('recurrence.validation.firstOccurrenceRequired'));
        return;
    }

    const todayISO = toISODate(new Date());
    if (resolvedFirstOccurrence < todayISO) {
        if (firstOccurrenceInput) {
            firstOccurrenceInput.classList.add('recurrence-invalid');
            firstOccurrenceInput.setAttribute('aria-invalid', 'true');
        }
        if (displayInput) {
            displayInput.classList.add('recurrence-invalid');
            displayInput.setAttribute('aria-invalid', 'true');
            displayInput.focus();
        } else if (firstOccurrenceInput) {
            firstOccurrenceInput.focus();
        }
        setRecurrenceFieldError(firstOccurrenceError, t('recurrence.validation.firstOccurrencePast'));
        return;
    }

    const taskStartModeControl = getSelectTrigger(taskStartMode);
    const taskEndModeControl = getSelectTrigger(taskEndMode);
    if (!taskStartMode?.value) {
        if (taskStartModeControl) {
            taskStartModeControl.classList.add('recurrence-invalid');
            taskStartModeControl.setAttribute('aria-invalid', 'true');
            taskStartModeControl.focus();
        }
        setRecurrenceFieldError(taskStartModeError, t('recurrence.validation.taskStartDateRequired'));
        return;
    }

    if (!taskEndMode?.value) {
        if (taskEndModeControl) {
            taskEndModeControl.classList.add('recurrence-invalid');
            taskEndModeControl.setAttribute('aria-invalid', 'true');
            taskEndModeControl.focus();
        }
        setRecurrenceFieldError(taskEndModeError, t('recurrence.validation.taskEndDateRequired'));
        return;
    }

    if (taskEndMode.value === 'offset-days' && taskStartMode.value !== 'creation-day') {
        if (taskStartModeControl) {
            taskStartModeControl.classList.add('recurrence-invalid');
            taskStartModeControl.setAttribute('aria-invalid', 'true');
            taskStartModeControl.focus();
        }
        setRecurrenceFieldError(taskStartModeError, t('recurrence.validation.taskStartDateRequiredForOffset'));
        return;
    }

    const endMode = recurrenceDraft.endMode || 'never';
    const resolvedEndDate = endMode === 'date'
        ? (endDate?.value?.trim() || recurrenceDraft.endDate || '').trim()
        : '';
    if (endMode === 'date' && !resolvedEndDate) {
        if (endDate) {
            endDate.classList.add('recurrence-invalid');
            endDate.setAttribute('aria-invalid', 'true');
            endDate.focus();
        }
        setRecurrenceFieldError(endDateError, t('recurrence.validation.endDateRequired'));
        return;
    }
    const resolvedMaxOccurrences = endMode === 'count'
        ? Math.max(1, parseInt(occurrencesInput?.value, 10) || recurrenceDraft.maxOccurrences || 1)
        : null;

    const nextDraft = {
        ...recurrenceDraft,
        name: (ruleName?.value ?? recurrenceDraft.name ?? '').trim(),
        endMode,
        endDate: resolvedEndDate,
        maxOccurrences: resolvedMaxOccurrences,
        firstOccurrenceDate: resolvedFirstOccurrence,
        intervalDays: Math.max(1, parseInt(interval?.value || recurrenceDraft.intervalDays || 1, 10)),
        intervalUnit: intervalUnit?.value || recurrenceDraft.intervalUnit || 'days',
        taskStartDateMode: taskStartMode?.value || recurrenceDraft.taskStartDateMode || '',
        taskEndDateMode: taskEndMode?.value || recurrenceDraft.taskEndDateMode || '',
        taskEndDateOffset: Math.max(1, parseInt(taskEndOffset?.value || recurrenceDraft.taskEndDateOffset || 1, 10))
    };

    if (nextDraft.taskEndDateMode === 'offset-days') {
        const rawOffset = parseInt(taskEndOffset?.value, 10);
        if (!Number.isFinite(rawOffset) || rawOffset < 1) {
            if (taskEndOffset) {
                taskEndOffset.classList.add('recurrence-invalid');
                taskEndOffset.setAttribute('aria-invalid', 'true');
                taskEndOffset.focus();
            }
            setRecurrenceFieldError(taskEndOffsetError, t('recurrence.validation.endDateOffsetRequired'));
            return;
        }
    }

    const validation = validateRule({
        pattern: nextDraft.pattern,
        daysOfWeek: nextDraft.daysOfWeek,
        intervalDays: nextDraft.intervalDays,
        intervalUnit: nextDraft.intervalUnit,
        endDate: nextDraft.endDate,
        taskStartDateMode: nextDraft.taskStartDateMode,
        taskEndDateMode: nextDraft.taskEndDateMode,
        taskEndDateOffset: nextDraft.taskEndDateOffset,
        firstOccurrenceDate: nextDraft.firstOccurrenceDate,
        template: { title: sourceTaskData.title || t('tasks.untitled') }
    });
    if (!validation.valid) {
        const validationErrors = Array.isArray(validation.errors) ? validation.errors : [];
        const firstMappedError = validationErrors.find((message) =>
            message === 'Select at least one weekday' ||
            message === 'Interval must be a positive integer' ||
            message === 'Start date mode is required' ||
            message === 'End date mode is required' ||
            message === 'First occurrence cannot be in the past' ||
            message === 'End date offset must be a positive integer' ||
            message === 'End date must be YYYY-MM-DD' ||
            message === 'Template title is required'
        );
        if (firstMappedError === 'Select at least one weekday') {
            setRecurrenceFieldError(weeklyError, t('recurrence.errorNoDays'));
        } else if (firstMappedError === 'Interval must be a positive integer') {
            if (interval) {
                interval.classList.add('recurrence-invalid');
                interval.setAttribute('aria-invalid', 'true');
                interval.focus();
            }
            setRecurrenceFieldError(intervalError, t('recurrence.validation.intervalDaysRequired'));
        } else if (firstMappedError === 'Start date mode is required') {
            setRecurrenceFieldError(taskStartModeError, t('recurrence.validation.taskStartDateRequired'));
        } else if (firstMappedError === 'End date mode is required') {
            setRecurrenceFieldError(taskEndModeError, t('recurrence.validation.taskEndDateRequired'));
        } else if (firstMappedError === 'First occurrence cannot be in the past') {
            if (firstOccurrenceInput) {
                firstOccurrenceInput.classList.add('recurrence-invalid');
                firstOccurrenceInput.setAttribute('aria-invalid', 'true');
            }
            if (displayInput) {
                displayInput.classList.add('recurrence-invalid');
                displayInput.setAttribute('aria-invalid', 'true');
            }
            setRecurrenceFieldError(firstOccurrenceError, t('recurrence.validation.firstOccurrencePast'));
        } else if (firstMappedError === 'End date must be YYYY-MM-DD') {
            if (endDate) {
                endDate.classList.add('recurrence-invalid');
                endDate.setAttribute('aria-invalid', 'true');
                endDate.focus();
            }
            setRecurrenceFieldError(endDateError, t('recurrence.validation.endDateRequired'));
        } else if (firstMappedError === 'End date offset must be a positive integer') {
            if (taskEndOffset) {
                taskEndOffset.classList.add('recurrence-invalid');
                taskEndOffset.setAttribute('aria-invalid', 'true');
                taskEndOffset.focus();
            }
            setRecurrenceFieldError(taskEndOffsetError, t('recurrence.validation.endDateOffsetRequired'));
        } else if (firstMappedError === 'Template title is required') {
            setRecurrenceFieldError(sourceError, t('recurrence.validation.sourceTaskMissing'));
        } else {
            setRecurrenceFieldError(formError, t('recurrence.validation.invalidRule'));
        }
        return;
    }

    const firstRunDate = resolvedFirstOccurrence || computeInitialAutomationRunDate(sourceTaskData, nextDraft);

    const nextId = recurrenceDraft.automationId || Math.max(0, ...automations.map((rule) => Number(rule.id) || 0)) + 1;
    const baseRule = buildRuleFromTask(sourceTaskData, {
        name: nextDraft.name,
        pattern: nextDraft.pattern,
        daysOfWeek: nextDraft.daysOfWeek,
        intervalDays: nextDraft.intervalDays,
        intervalUnit: nextDraft.intervalUnit,
        endDate: nextDraft.endDate,
        taskStartDateMode: nextDraft.taskStartDateMode,
        taskEndDateMode: nextDraft.taskEndDateMode,
        taskEndDateOffset: nextDraft.taskEndDateOffset,
        firstRunDate,
        maxOccurrences: resolvedMaxOccurrences
    }, nextId);
    if (!baseRule) {
        setRecurrenceFieldError(formError, t('recurrence.validation.invalidRule'));
        return;
    }

    // Override maxOccurrences so it always reflects the pill choice, even if builder ignored it
    baseRule.maxOccurrences = resolvedMaxOccurrences;

    const nextRules = [...automations];
    const existingIndex = recurrenceDraft.automationId
        ? nextRules.findIndex((rule) => rule.id === recurrenceDraft.automationId)
        : -1;

    if (existingIndex >= 0) {
        const previous = nextRules[existingIndex];
        const sourceTaskChanged = Number(previous.sourceTaskId ?? baseRule.sourceTaskId) !== Number(baseRule.sourceTaskId);
        nextRules[existingIndex] = {
            ...previous,
            ...baseRule,
            id: previous.id,
            active: previous.active,
            nextRunDate: sourceTaskChanged || previous.nextRunDate !== baseRule.nextRunDate
                ? baseRule.nextRunDate
                : previous.nextRunDate,
            occurrenceCount: previous.occurrenceCount ?? 0,
            maxOccurrences: resolvedMaxOccurrences,
            createdAt: previous.createdAt || baseRule.createdAt
        };
    } else {
        nextRules.push(baseRule);
    }

    await saveAutomationsData(nextRules);
    automations = nextRules;
    updateAutomationsCount();
    closeRecurrenceModal();
    if (document.getElementById('automations')?.classList.contains('active')) {
        renderAutomations();
    }
    renderTasks();
    renderListView();
    showSuccessNotification(t(existingIndex >= 0 ? 'recurrence.saved.updated' : 'recurrence.saved.created'));
}

async function applyAskKaiAutomationProposal(proposal) {
    const payload = proposal?.payload || {};
    const template = payload.template || {};
    const normalizedTemplate = {
        title: String(template.title || '').trim() || t('tasks.untitled'),
        description: template.description || '',
        projectId: template.projectId != null ? Number(template.projectId) : null,
        priority: template.priority || 'medium',
        tags: Array.isArray(template.tags) ? template.tags : []
    };
    const nextDraft = {
        pattern: payload.pattern,
        daysOfWeek: payload.daysOfWeek,
        intervalDays: payload.intervalDays,
        intervalUnit: payload.intervalUnit || 'days',
        taskStartDateMode: payload.taskStartDateMode || 'none',
        taskEndDateMode: payload.taskEndDateMode || 'none',
        taskEndDateOffset: payload.taskEndDateOffset || 1,
        endDate: payload.endDate || '',
        firstRunDate: payload.firstRunDate || null,
        template: { title: normalizedTemplate.title }
    };
    const validation = validateRule(nextDraft);
    if (!validation.valid) {
        showErrorNotification(t('recurrence.validation.invalidRule'));
        return;
    }
    const nextId = Math.max(0, ...automations.map((rule) => Number(rule.id) || 0)) + 1;
    const previousTasks = tasks;
    const previousTaskCounter = taskCounter;
    let createdSourceTaskId = null;
    let nextRules = null;
    try {
        const sourceResult = await ensureAutomationSourceTask(normalizedTemplate, nextId);
        const sourceTask = sourceResult?.task || null;
        if (sourceResult?.created && sourceTask?.id != null) {
            createdSourceTaskId = sourceTask.id;
        }
        const baseRule = buildRuleFromTask(sourceTask, nextDraft, nextId);
        if (!baseRule) {
            showErrorNotification(t('recurrence.validation.invalidRule'));
            return;
        }
        baseRule.template = {
            title: normalizedTemplate.title,
            description: normalizedTemplate.description,
            projectId: normalizedTemplate.projectId,
            priority: normalizedTemplate.priority || 'medium',
            tags: Array.isArray(normalizedTemplate.tags) ? [...normalizedTemplate.tags] : []
        };
        nextRules = [...automations, baseRule];
        await saveAutomationsData(nextRules);
        automations = nextRules;
        updateAutomationsCount();
    } catch (error) {
        tasks = previousTasks;
        taskCounter = previousTaskCounter;
        if (createdSourceTaskId != null) {
            try {
                await saveTaskDeleteData(createdSourceTaskId);
            } catch (cleanupError) {
                console.error('Failed to rollback Ask Kai automation source task:', cleanupError);
            }
        }
        throw error;
    }
    if (document.getElementById('automations')?.classList.contains('active')) {
        renderAutomations();
    }
    renderTasks();
    renderListView();
    showSuccessNotification(t('recurrence.saved.created'));
}

async function toggleAutomationActive(automationId) {
    const nextRules = automations.map((rule) => {
        if (rule.id !== automationId) return rule;
        return { ...rule, active: !rule.active };
    });
    await saveAutomationsData(nextRules);
    automations = nextRules;
    renderAutomations();
}

function requestDeleteAutomation(automationId) {
    document.querySelectorAll('.automation-row.is-confirming-delete').forEach(row => {
        row.classList.remove('is-confirming-delete');
    });
    const btn = document.querySelector(`.automation-icon-btn-danger[data-param="${automationId}"]`);
    const row = btn?.closest('.automation-row');
    if (row) row.classList.add('is-confirming-delete');
}

function cancelDeleteAutomation() {
    document.querySelectorAll('.automation-row.is-confirming-delete').forEach(row => {
        row.classList.remove('is-confirming-delete');
    });
}

async function deleteAutomation(automationId) {
    const previousTasks = JSON.parse(JSON.stringify(tasks));
    const previousAutomations = JSON.parse(JSON.stringify(automations));
    const rule = automations.find((item) => item.id === automationId);
    const nextRules = automations.filter((item) => item.id !== automationId);
    const sourceTask = rule?.sourceTaskId != null
        ? tasks.find((item) => item.id === rule.sourceTaskId)
        : null;
    const previousSourceTask = sourceTask ? JSON.parse(JSON.stringify(sourceTask)) : null;

    try {
        automations = nextRules;
        if (sourceTask) {
            tasks = tasks.map((item) => item.id === sourceTask.id
                ? { ...item, isTemplate: false, automationId: null, updatedAt: new Date().toISOString() }
                : item);
        }
        await saveAutomationsData(nextRules);
        if (sourceTask) {
            await saveTaskUpdateData(sourceTask.id, {
                isTemplate: false,
                automationId: null,
                updatedAt: new Date().toISOString()
            });
        }
        renderAfterFilterChange();
        renderAutomations();
        showSuccessNotification(t(
            (rule?.type || 'recurring-task') === 'auto-archive'
                ? 'automations.deleted'
                : 'recurrence.saved.deleted'
        ));
    } catch (error) {
        tasks = previousTasks;
        automations = previousAutomations;
        if (sourceTask && previousSourceTask) {
            await saveAutomationsData(previousAutomations).catch(() => {});
            await saveTaskUpdateData(sourceTask.id, {
                isTemplate: !!previousSourceTask.isTemplate,
                automationId: previousSourceTask.automationId ?? null,
                updatedAt: previousSourceTask.updatedAt || new Date().toISOString()
            }).catch(() => {});
        }
        renderAfterFilterChange();
        renderAutomations();
        showErrorNotification(t('error.saveChangesFailed'));
    }
}

function editAutomation(automationId) {
    const rule = automations.find((item) => item.id === automationId);
    if (!rule) return;

    if ((rule.type || 'recurring-task') === 'auto-archive') {
        openAutoArchiveModal(automationId);
        return;
    }

    const sourceTask = rule.sourceTaskId != null
        ? tasks.find((item) => item.id === rule.sourceTaskId)
        : null;

    recurrenceDraft = {
        automationId: rule.id,
        name: rule.name || '',
        sourceTaskId: sourceTask ? sourceTask.id : rule.sourceTaskId ?? null,
        pattern: rule.pattern || 'daily',
        daysOfWeek: Array.isArray(rule.daysOfWeek) ? [...rule.daysOfWeek] : [],
        intervalDays: rule.intervalDays || 1,
        intervalUnit: rule.intervalUnit || 'days',
        endMode: rule.endMode || (rule.endDate ? 'date' : (rule.maxOccurrences ? 'count' : 'never')),
        maxOccurrences: rule.maxOccurrences || null,
        endDate: rule.endDate || '',
        firstOccurrenceDate: rule.firstOccurrenceDate || rule.nextRunDate || '',
        taskStartDateMode: rule.taskStartDateMode || 'creation-day',
        taskEndDateMode: rule.taskEndDateMode || 'none',
        taskEndDateOffset: rule.taskEndDateOffset || 1
    };

    const { modal, title, ruleName, interval, intervalUnit, search } = getRecurrenceModalElements();
    if (!modal) return;
    if (title) title.setAttribute('data-i18n', 'recurrence.editTitle');
    applyTranslations(modal);

    if (ruleName) {
        ruleName.value = recurrenceDraft.name || '';
        ruleName.oninput = () => {
            if (recurrenceDraft) recurrenceDraft.name = ruleName.value;
        };
    }
    if (interval) interval.value = String(recurrenceDraft.intervalDays || 1);
    if (intervalUnit) intervalUnit.value = recurrenceDraft.intervalUnit || 'days';
    setRecurrenceSelectedTask(sourceTask ? sourceTask.id : null);
    if (!sourceTask && rule.sourceTaskId != null) {
        recurrenceDraft.sourceTaskId = rule.sourceTaskId;
        syncRecurrenceSourceSummary();
        syncRecurrenceSourceSelect(rule.sourceTaskId);
    }
    syncRecurrencePatternUI();
    syncRecurrenceDateUI();

    if (search) {
        search.oninput = handleRecurrenceSearchInput;
        search.onfocus = () => renderRecurrenceComboboxResults(search.value);
        search.onblur = () => {
            setTimeout(() => {
                const { comboboxList } = getRecurrenceModalElements();
                if (comboboxList) comboboxList.hidden = true;
            }, 150);
        };
    }
    if (intervalUnit) intervalUnit.onchange = () => {
        if (recurrenceDraft) recurrenceDraft.intervalUnit = intervalUnit.value || 'days';
        syncRecurrenceSummary();
    };
    if (interval) interval.oninput = () => {
        if (recurrenceDraft) recurrenceDraft.intervalDays = Math.max(1, parseInt(interval.value, 10) || 1);
        syncRecurrenceSummary();
    };
    bindRecurrenceEndModePills();
    wireRecurrenceDateHandlers();

    syncRecurrenceSubmitState();
    syncRecurrenceSummary();
    modal.classList.add('active');
}

function renderAutomationTypePicker() {
    const { typePickerGrid } = getRecurrenceModalElements();
    if (!typePickerGrid) return;
    const orderedTypes = [...AUTOMATION_TYPES].sort((a, b) => Number(b.available) - Number(a.available));
    typePickerGrid.innerHTML = orderedTypes.map((type) => {
        const label = escapeHtml(t(type.labelKey));
        const hint = escapeHtml(t(type.hintKey));
        const disabled = type.available ? '' : ' disabled';
        const action = type.available ? ` data-action="selectAutomationType" data-param="${escapeHtml(type.id)}"` : '';
        return `<button type="button" class="automation-type-tile${type.available ? '' : ' is-disabled'}"${action}${disabled}>
            <span class="automation-type-tile-icon" aria-hidden="true">${escapeHtml(type.icon || '•')}</span>
            <span class="automation-type-tile-main">
                <span class="automation-type-tile-label">${label}</span>
                <span class="automation-type-tile-hint">${hint}</span>
            </span>
        </button>`;
    }).join('');
}

function toggleAutomationTypePicker(mode = '') {
    const { typePicker, typePickerBtn, typePickerBackdrop } = getRecurrenceModalElements();
    if (!typePicker) return;
    const isOpen = !typePicker.hidden;
    if (isOpen) {
        closeAutomationTypePicker();
    } else {
        renderAutomationTypePicker();
        typePicker.classList.toggle('is-centered', mode === 'center');
        if (mode === 'center') {
            const sidebar = document.querySelector('.sidebar');
            const sidebarW = sidebar ? sidebar.getBoundingClientRect().width : 0;
            typePicker.style.left = sidebarW > 0
                ? `calc(${sidebarW}px + (100vw - ${sidebarW}px) / 2)`
                : '50%';
        } else {
            typePicker.style.left = '';
        }
        typePicker.hidden = false;
        if (typePickerBackdrop) typePickerBackdrop.hidden = false;
        if (typePickerBtn) typePickerBtn.setAttribute('aria-expanded', 'true');
        setTimeout(() => {
            document.addEventListener('click', handleAutomationTypePickerOutsideClick, { capture: true });
        }, 0);
    }
}

function closeAutomationTypePicker() {
    const { typePicker, typePickerBtn, typePickerBackdrop } = getRecurrenceModalElements();
    if (typePicker) {
        typePicker.hidden = true;
        typePicker.classList.remove('is-centered');
        typePicker.style.left = '';
    }
    if (typePickerBackdrop) typePickerBackdrop.hidden = true;
    if (typePickerBtn) typePickerBtn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', handleAutomationTypePickerOutsideClick, { capture: true });
}

function handleAutomationTypePickerOutsideClick(event) {
    const wrap = event.target.closest('.automation-type-picker-wrap');
    if (!wrap) closeAutomationTypePicker();
}

function selectAutomationType(typeId) {
    const type = getAutomationType(typeId);
    closeAutomationTypePicker();
    if (!type || !type.available) return;
    if (type.id === 'recurring-task') {
        openRecurrenceModal();
    } else if (type.id === 'auto-archive') {
        openAutoArchiveModal();
    }
}

function renderAutomations() {
    updateAutomationsCount();
    const list = document.getElementById('automations-list');
    if (!list) return;

    if (!automations.length) {
        list.innerHTML = `
            <div class="automations-empty">
                <h3 class="automations-empty-title">${escapeHtml(t('automations.emptyTitle'))}</h3>
                <p class="automations-empty-body">${escapeHtml(t('automations.emptySubtitle'))}</p>
                <button type="button" class="btn-primary automations-empty-cta" data-action="toggleAutomationTypePicker" data-param="center">${escapeHtml(t('automations.newRule'))}</button>
            </div>
        `;
        return;
    }

    // Group rules by their registered automation type so future kinds
    // (due-soon, auto-archive, …) render in their own section without
    // changing this function.
    const groupsMarkup = AUTOMATION_TYPES
        .filter((type) => type.available)
        .map((type) => {
            const rulesForType = automations
                .filter((rule) => (rule.type || 'recurring-task') === type.id)
                .sort((a, b) => String(a.nextRunDate || '').localeCompare(String(b.nextRunDate || '')));
            if (rulesForType.length === 0) return '';
            const rowsMarkup = rulesForType.map((rule) => renderAutomationRowHTML(rule)).join('');
            return `<section class="automation-group" data-type="${escapeHtml(type.id)}">
                <header class="automation-group-header">
                    <span class="automation-group-icon" aria-hidden="true">${escapeHtml(type.icon || '•')}</span>
                    <h2 class="automation-group-title">${escapeHtml(t(type.groupKey || type.labelKey))}</h2>
                    <span class="automation-group-count">${rulesForType.length}</span>
                </header>
                ${rowsMarkup}
            </section>`;
        }).filter(Boolean).join('');

    list.innerHTML = groupsMarkup;
}

// Render a single automation row. Recurring-task rules show the template title;
// auto-archive rules show a localized rule summary plus a meta line.
function renderAutomationRowHTML(rule) {
    const isArchive = (rule.type || 'recurring-task') === 'auto-archive';
    const badge = isArchive
        ? `<span class="task-automation-badge automation-row-badge" title="${escapeHtml(t('automations.type.autoArchive'))}">📦</span>`
        : getAutomationBadgeHTML(' automation-row-badge');
    const title = isArchive
        ? escapeHtml(getLocalizedAutoArchiveSummary(rule))
        : escapeHtml(rule.name || rule.template?.title || t('tasks.untitled'));
    const meta = isArchive ? getLocalizedAutoArchiveMeta(rule) : '';
    const metaHTML = meta ? `<p class="automation-row-meta">${escapeHtml(meta)}</p>` : '';
    return `
        <article class="automation-row" data-active="${rule.active}">
            <div class="automation-row-main">
                <div class="automation-row-title-wrap">
                    ${badge}
                    <div>
                        <h3 class="automation-row-title">${title}</h3>
                        ${metaHTML}
                    </div>
                </div>
            </div>
            <div class="automation-row-actions">
                <span class="automation-status-pill ${rule.active ? 'is-active' : 'is-paused'}"><span class="automation-status-dot" aria-hidden="true"></span>${escapeHtml(t(rule.active ? 'automations.status.active' : 'automations.status.paused'))}</span>
                <div class="automation-icon-group">
                    <button type="button" class="automation-icon-btn" data-action="openAutomationAudit" data-param="${rule.id}" title="${escapeHtml(t(isArchive ? 'automations.action.auditArchive' : 'automations.action.audit'))}">📋</button>
                    <button type="button" class="automation-icon-btn" data-action="toggleAutomationActive" data-param="${rule.id}" title="${escapeHtml(t(rule.active ? 'automations.action.pause' : 'automations.action.resume'))}">${rule.active ? '⏸' : '▶'}</button>
                    <button type="button" class="automation-icon-btn" data-action="editAutomation" data-param="${rule.id}" title="${escapeHtml(t('automations.action.edit'))}">✎</button>
                    <button type="button" class="automation-icon-btn automation-icon-btn-danger" data-action="requestDeleteAutomation" data-param="${rule.id}" title="${escapeHtml(t('automations.action.delete'))}">✕</button>
                </div>
                <div class="automation-delete-confirm">
                    <span class="automation-delete-confirm-text" data-i18n="automations.action.deleteConfirm">Delete this rule?</span>
                    <button type="button" class="automation-delete-cancel" data-action="cancelDeleteAutomation" data-i18n="common.cancel">Cancel</button>
                    <button type="button" class="automation-delete-ok" data-action="deleteAutomation" data-param="${rule.id}" data-i18n="automations.action.deleteConfirmYes">Delete</button>
                </div>
            </div>
        </article>
    `;
}

const automationAuditViewState = {
    ruleId: null,
    isArchive: false,
    query: '',
    sort: 'date-desc',
    returnAfterTaskClose: false
};

// Audit modal: for a recurring-task rule, lists the still-existing tasks it has
// generated (newest first, by creation time). For an auto-archive rule, lists the
// still-archived tasks it archived (newest first, by archive time). Both read live
// task state — a task that was later deleted (recurring) or restored (auto-archive)
// no longer appears. This isn't a persistent log.
function openAutomationAudit(ruleId, { preserveView = false } = {}) {
    const rule = automations.find((item) => item.id === Number(ruleId));
    if (!rule) return;
    const isArchive = (rule.type || 'recurring-task') === 'auto-archive';

    const modal = document.getElementById('automation-audit-modal');
    if (!modal) return;

    const titleEl = document.getElementById('automation-audit-modal-title');
    if (titleEl) {
        titleEl.setAttribute('data-i18n', isArchive ? 'automations.audit.archiveTitle' : 'automations.audit.title');
        titleEl.textContent = t(isArchive ? 'automations.audit.archiveTitle' : 'automations.audit.title');
    }

    const subtitleEl = document.getElementById('automation-audit-modal-subtitle');
    if (subtitleEl) {
        subtitleEl.textContent = isArchive
            ? getLocalizedAutoArchiveSummary(rule)
            : (rule.name || rule.template?.title || t('tasks.untitled'));
    }

    automationAuditViewState.ruleId = rule.id;
    automationAuditViewState.isArchive = isArchive;
    if (!preserveView) {
        automationAuditViewState.query = '';
        automationAuditViewState.sort = 'date-desc';
    }
    bindAutomationAuditModal();
    const search = modal.querySelector('#automation-audit-search');
    const sort = modal.querySelector('#automation-audit-sort');
    if (search) search.value = automationAuditViewState.query;
    if (sort) sort.value = automationAuditViewState.sort;
    renderAutomationAuditList(rule, isArchive);
    modal.classList.add('active');
}

function getAutomationAuditTasks(rule, isArchive) {
    const query = String(automationAuditViewState.query || '').trim().toLocaleLowerCase(getLocale());
    const timestampKey = isArchive ? 'archivedAt' : 'createdAt';
    const matchingTasks = tasks.filter((task) => {
        const matchesRule = isArchive
            ? Number(task.archivedByAutomationId) === Number(rule.id) && task.archivedAt
            : Number(task.automationId) === Number(rule.id);
        if (!matchesRule) return false;
        if (!query) return true;
        const project = projects.find((item) => item.id === task.projectId);
        return [task.title, project?.name]
            .filter(Boolean)
            .some((value) => String(value).toLocaleLowerCase(getLocale()).includes(query));
    });

    return matchingTasks.sort((a, b) => {
        const projectA = projects.find((item) => item.id === a.projectId)?.name || '';
        const projectB = projects.find((item) => item.id === b.projectId)?.name || '';
        switch (automationAuditViewState.sort) {
            case 'date-asc':
                return String(a[timestampKey] || '').localeCompare(String(b[timestampKey] || ''));
            case 'title-asc':
                return String(a.title || '').localeCompare(String(b.title || ''), getLocale());
            case 'project-asc':
                return projectA.localeCompare(projectB, getLocale()) || String(a.title || '').localeCompare(String(b.title || ''), getLocale());
            default:
                return String(b[timestampKey] || '').localeCompare(String(a[timestampKey] || ''));
        }
    });
}

function renderAutomationAuditList(rule, isArchive) {
    const list = document.getElementById('automation-audit-list');
    const count = document.getElementById('automation-audit-count');
    if (!list || !count) return;

    const matchingTasks = getAutomationAuditTasks(rule, isArchive);
    const isSearching = Boolean(String(automationAuditViewState.query || '').trim());
    count.textContent = t('automations.audit.count', { count: matchingTasks.length });

    if (matchingTasks.length === 0) {
        list.innerHTML = `
            <div class="docs-trash-empty-state">
                <h3>${escapeHtml(t(isSearching ? 'automations.audit.noResultsTitle' : (isArchive ? 'automations.audit.archiveEmptyTitle' : 'automations.audit.emptyTitle')))}</h3>
                <p>${escapeHtml(t(isSearching ? 'automations.audit.noResultsBody' : (isArchive ? 'automations.audit.archiveEmptyBody' : 'automations.audit.emptyBody')))}</p>
            </div>
        `;
        return;
    }

    const dateLabel = t(isArchive ? 'automations.audit.archivedDate' : 'automations.audit.createdDate');
    const rows = matchingTasks.map((task) => {
        const project = projects.find((p) => p.id === task.projectId);
        const projectLabel = project ? escapeHtml(project.name) : escapeHtml(t('tasks.noProject'));
        const timestamp = isArchive ? task.archivedAt : task.createdAt;
        return `
            <tr class="automation-audit-row" data-action="openAutomationAuditTask" data-param="${task.id}" role="button" tabindex="0">
                <td class="automation-audit-row-title" data-label="${escapeHtml(t('automations.audit.task'))}">${escapeHtml(task.title || t('tasks.untitled'))}</td>
                <td class="automation-audit-row-project" data-label="${escapeHtml(t('automations.audit.project'))}">${projectLabel}</td>
                <td class="automation-audit-row-date" data-label="${escapeHtml(dateLabel)}">${escapeHtml(formatAutomationAuditTimestamp(timestamp))}</td>
                ${isArchive ? '' : `<td class="automation-audit-row-status"><span class="status-badge ${escapeHtml(task.status || 'todo')}">${escapeHtml(getStatusLabel(task.status))}</span></td>`}
            </tr>
        `;
    }).join('');

    list.innerHTML = `
        <div class="automation-audit-table-wrap">
            <table class="automation-audit-table ${isArchive ? 'automation-audit-table--archive' : ''}" aria-label="${escapeHtml(t('automations.audit.title'))}">
                <thead>
                    <tr>
                        <th>${escapeHtml(t('automations.audit.task'))}</th>
                        <th>${escapeHtml(t('automations.audit.project'))}</th>
                        <th>${escapeHtml(dateLabel)}</th>
                        ${isArchive ? '' : `<th class="automation-audit-row-status">${escapeHtml(t('automations.audit.status'))}</th>`}
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>
        </div>
    `;
}

function bindAutomationAuditModal() {
    const modal = document.getElementById('automation-audit-modal');
    if (!modal || modal.dataset.bound === 'true') return;
    modal.dataset.bound = 'true';

    const search = modal.querySelector('#automation-audit-search');
    const sort = modal.querySelector('#automation-audit-sort');
    search?.addEventListener('input', () => {
        automationAuditViewState.query = search.value || '';
        const rule = automations.find((item) => item.id === Number(automationAuditViewState.ruleId));
        if (rule) renderAutomationAuditList(rule, automationAuditViewState.isArchive);
    });
    sort?.addEventListener('change', () => {
        automationAuditViewState.sort = sort.value || 'date-desc';
        const rule = automations.find((item) => item.id === Number(automationAuditViewState.ruleId));
        if (rule) renderAutomationAuditList(rule, automationAuditViewState.isArchive);
    });
}

function formatAutomationAuditTimestamp(value) {
    if (!value) return t('tasks.noDate');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t('tasks.noDate');
    return formatDisplayDateTime(date, { timeZone: getUserTimeZone() });
}

function openAutomationAuditTask(taskId) {
    automationAuditViewState.returnAfterTaskClose = true;
    closeModal('automation-audit-modal');
    openTaskDetails(Number(taskId));
}

function reopenAutomationAuditAfterTaskClose() {
    if (!automationAuditViewState.returnAfterTaskClose) return;
    automationAuditViewState.returnAfterTaskClose = false;
    const rule = automations.find((item) => item.id === Number(automationAuditViewState.ruleId));
    if (rule) openAutomationAudit(rule.id, { preserveView: true });
}

// ─────────────────────────────────────────────────────────────────────────────
// === Auto-archive automations ===
// ─────────────────────────────────────────────────────────────────────────────

function autoArchiveUnitWord(kind, count) {
    const one = count === 1;
    if (kind === 'day') return t(one ? 'autoArchive.unit.day' : 'autoArchive.unit.days');
    return t(one ? 'autoArchive.unit.task' : 'autoArchive.unit.tasks');
}

function getLocalizedAutoArchiveSummary(rule) {
    if (!rule) return '';
    if (rule.mode === 'done-age') {
        const n = parseInt(rule.thresholdDays, 10) || 0;
        return t('autoArchive.summary.doneAge', { count: n, unit: autoArchiveUnitWord('day', n) });
    }
    if (rule.mode === 'on-date') {
        return t('autoArchive.summary.onDate', { date: formatAutomationDateLabel(rule.archiveDate) });
    }
    if (rule.mode === 'periodic') {
        if (rule.pattern === 'daily') return t('autoArchive.summary.periodicDaily');
        if (rule.pattern === 'weekly') {
            const days = Array.isArray(rule.daysOfWeek)
                ? [...rule.daysOfWeek].sort((a, b) => a - b) : [];
            const dayNames = days.map(getLocalizedWeekdayShort).join(', ');
            return dayNames
                ? t('autoArchive.summary.periodicWeekly', { days: dayNames })
                : t('autoArchive.summary.periodicWeeklyShort');
        }
        if (rule.pattern === 'monthly') {
            const dom = rule.dayOfMonth;
            const dayLabel = dom === 0
                ? t('autoArchive.periodic.monthlyLastDay')
                : (Number.isFinite(dom) && dom >= 1 ? String(dom) : '');
            return dayLabel
                ? t('autoArchive.summary.periodicMonthly', { day: dayLabel })
                : t('autoArchive.summary.periodicMonthlyShort');
        }
        return '';
    }
    return '';
}

function getLocalizedAutoArchiveMeta(rule) {
    if (!rule) return '';
    const parts = [];
    if (rule.mode === 'done-age') {
        parts.push(t('autoArchive.meta.continuous'));
    } else if (rule.mode === 'on-date') {
        parts.push(rule.active
            ? t('autoArchive.meta.scheduled', { date: formatAutomationDateLabel(rule.archiveDate) })
            : t('autoArchive.meta.completed'));
    } else if (rule.mode === 'periodic') {
        parts.push(t('autoArchive.meta.nextRun', { date: formatAutomationDateLabel(rule.nextRunDate) }));
    }
    if (rule.archivedCount > 0) {
        parts.push(t('autoArchive.meta.archivedTotal', { count: rule.archivedCount }));
    }
    return parts.join(' · ');
}

// Evaluate every active auto-archive rule, archive matching tasks, advance the
// rules, and persist in the background. Idempotent: already-archived tasks are
// skipped and periodic/on-date rules will not re-fire the same day.
function processAutoArchiveRules() {
    if (!Array.isArray(automations) || !Array.isArray(tasks)) return 0;
    const hasArchiveRule = automations.some((r) => r && r.type === 'auto-archive' && r.active);
    if (!hasArchiveRule) return 0;

    const todayISO = toISODate(new Date());
    const nowMs = Date.now();
    const archivedNowISO = new Date().toISOString();
    const taskIdsToArchive = new Map(); // taskId -> ruleId (first matching rule wins)
    let rulesChanged = false;

    const nextRules = automations.map((rule) => {
        if (!rule || rule.type !== 'auto-archive' || !rule.active) return rule;
        const picked = selectTasksForAutoArchive(rule, tasks, { todayISO, nowMs });
        picked.forEach((task) => {
            if (!taskIdsToArchive.has(task.id)) taskIdsToArchive.set(task.id, rule.id);
        });
        if (rule.mode === 'done-age') {
            if (picked.length === 0) return rule;
            rulesChanged = true;
            return { ...rule, archivedCount: (rule.archivedCount || 0) + picked.length };
        }
        // on-date / periodic: advance when due, even if nothing matched.
        if (!archiveRuleShouldRun(rule, todayISO)) return rule;
        rulesChanged = true;
        const counted = { ...rule, archivedCount: (rule.archivedCount || 0) + picked.length };
        return advanceArchiveRule(counted, todayISO);
    });

    if (taskIdsToArchive.size === 0 && !rulesChanged) return 0;

    const updatedTaskIds = [];
    tasks.forEach((task) => {
        if (taskIdsToArchive.has(task.id) && !task.archivedAt) {
            task.archivedAt = archivedNowISO;
            task.updatedAt = archivedNowISO;
            task.archivedByAutomationId = taskIdsToArchive.get(task.id);
            updatedTaskIds.push(task.id);
        }
    });

    automations = nextRules;

    // Persist in the background. archivedAt is non-destructive: a failed save is
    // simply retried when the rule re-evaluates on the next load.
    (async () => {
        try {
            if (updatedTaskIds.length > 0) {
                await Promise.allSettled(updatedTaskIds.map((id) =>
                    saveTaskUpdateData(id, {
                        archivedAt: archivedNowISO,
                        updatedAt: archivedNowISO,
                        archivedByAutomationId: taskIdsToArchive.get(id)
                    })
                ));
            }
            if (rulesChanged) {
                await saveAutomationsData(nextRules);
            }
        } catch (err) {
            console.error('[auto-archive] persistence failed', err);
        }
    })();

    if (updatedTaskIds.length > 0) {
        const count = updatedTaskIds.length;
        setTimeout(() => {
            try {
                showNotification(
                    t('toast.autoArchive.archivedCount', { count, unit: autoArchiveUnitWord('task', count) }),
                    'info'
                );
            } catch (_) {}
        }, 900);
    }
    return updatedTaskIds.length;
}

let autoArchiveDraft = null;

function getAutoArchiveModalElements() {
    return {
        modal: document.getElementById('auto-archive-modal'),
        title: document.getElementById('auto-archive-modal-title'),
        doneAgeGroup: document.getElementById('aa-done-age-group'),
        onDateGroup: document.getElementById('aa-on-date-group'),
        periodicGroup: document.getElementById('aa-periodic-group'),
        weeklyDaysGroup: document.getElementById('aa-weekly-days-group'),
        monthlyDayGroup: document.getElementById('aa-monthly-day-group'),
        thresholdInput: document.getElementById('aa-threshold-days'),
        dateInput: document.getElementById('aa-archive-date'),
        dayOfMonthSelect: document.getElementById('aa-day-of-month'),
        includeProjectTasksInput: document.getElementById('aa-include-project-tasks'),
        summaryText: document.getElementById('aa-summary-text'),
        formError: document.getElementById('aa-form-error'),
        submitBtn: document.getElementById('aa-submit-btn')
    };
}

function populateAutoArchiveDayOfMonthSelect(select) {
    if (!select || select.dataset.populated) return;
    select.dataset.populated = '1';
    for (let d = 1; d <= 31; d++) {
        const opt = document.createElement('option');
        opt.value = String(d);
        opt.textContent = String(d);
        select.appendChild(opt);
    }
    const lastOpt = document.createElement('option');
    lastOpt.value = '0';
    lastOpt.textContent = t('autoArchive.periodic.monthlyLastDay');
    select.appendChild(lastOpt);
}

function syncAutoArchiveDraftDate() {
    const { dateInput } = getAutoArchiveModalElements();
    if (!autoArchiveDraft || !dateInput) return;
    autoArchiveDraft.archiveDate = looksLikeISO(dateInput.value)
        ? dateInput.value
        : (looksLikeDMY(dateInput.value) ? toISOFromDMY(dateInput.value) : '');
    syncAutoArchivePreview();
}

function writeAutoArchiveDateValue(value) {
    const { dateInput } = getAutoArchiveModalElements();
    if (!dateInput) return;
    const normalized = looksLikeISO(value)
        ? value
        : (looksLikeDMY(value) ? toISOFromDMY(value) : '');
    dateInput.value = normalized;

    const displayInput = dateInput.closest('.date-input-wrapper')?.querySelector('.date-display');
    const picker = dateInput._flatpickrInstance || displayInput?._flatpickr;
    if (picker) {
        if (normalized) picker.setDate(new Date(`${normalized}T00:00:00`), false);
        else picker.clear();
    } else if (displayInput) {
        displayInput.value = normalized ? toDMYFromISO(normalized) : '';
    }
}

function bindAutoArchiveDateInput() {
    const { dateInput } = getAutoArchiveModalElements();
    if (!dateInput) return;
    dateInput.onchange = syncAutoArchiveDraftDate;

    const displayInput = dateInput.closest('.date-input-wrapper')?.querySelector('.date-display');
    const picker = dateInput._flatpickrInstance || displayInput?._flatpickr;
    if (picker?.config?.onChange && !picker.__autoArchiveDateBound) {
        picker.__autoArchiveDateBound = true;
        picker.config.onChange.push(syncAutoArchiveDraftDate);
    }
}

function openAutoArchiveModal(ruleId = null) {
    closeAutomationTypePicker();
    const els = getAutoArchiveModalElements();
    if (!els.modal) return;

    const existing = ruleId != null
        ? automations.find((r) => r.id === ruleId && r.type === 'auto-archive')
        : null;

    const todayDate = new Date();
    const defaultDay = todayDate.getDate();
    const defaultDow = todayDate.getDay();
    autoArchiveDraft = existing
        ? {
            automationId: existing.id,
            mode: existing.mode || 'done-age',
            thresholdDays: existing.thresholdDays || 30,
            archiveDate: existing.archiveDate || '',
            pattern: existing.pattern || 'daily',
            daysOfWeek: Array.isArray(existing.daysOfWeek) && existing.daysOfWeek.length > 0
                ? [...existing.daysOfWeek] : [defaultDow],
            dayOfMonth: Number.isFinite(existing.dayOfMonth) ? existing.dayOfMonth : defaultDay,
            nextRunDate: existing.nextRunDate || null,
            active: existing.active !== false,
            archivedCount: existing.archivedCount || 0,
            includeProjectTasks: existing.includeProjectTasks === true,
            createdAt: existing.createdAt || null
        }
        : {
            automationId: null,
            mode: 'done-age',
            thresholdDays: 30,
            archiveDate: '',
            pattern: 'daily',
            daysOfWeek: [defaultDow],
            dayOfMonth: defaultDay,
            nextRunDate: null,
            active: true,
            archivedCount: 0,
            includeProjectTasks: false,
            createdAt: null
        };

    if (els.title) {
        els.title.setAttribute('data-i18n', existing ? 'autoArchive.editTitle' : 'autoArchive.createTitle');
    }
    if (els.thresholdInput) els.thresholdInput.value = String(autoArchiveDraft.thresholdDays || 30);
    if (els.includeProjectTasksInput) {
        els.includeProjectTasksInput.checked = autoArchiveDraft.includeProjectTasks === true;
        els.includeProjectTasksInput.onchange = () => {
            autoArchiveDraft.includeProjectTasks = els.includeProjectTasksInput.checked;
            syncAutoArchivePreview();
        };
    }
    writeAutoArchiveDateValue(autoArchiveDraft.archiveDate);
    if (els.formError) { els.formError.hidden = true; els.formError.textContent = ''; }

    if (els.thresholdInput) {
        els.thresholdInput.oninput = () => {
            autoArchiveDraft.thresholdDays = Math.max(1, Math.min(3650, parseInt(els.thresholdInput.value, 10) || 1));
            syncAutoArchivePreview();
        };
    }
    bindAutoArchiveDateInput();
    populateAutoArchiveDayOfMonthSelect(els.dayOfMonthSelect);
    if (els.dayOfMonthSelect) {
        els.dayOfMonthSelect.onchange = () => {
            autoArchiveDraft.dayOfMonth = parseInt(els.dayOfMonthSelect.value, 10);
            syncAutoArchivePreview();
        };
    }

    applyTranslations(els.modal);
    syncAutoArchiveModeUI();
    els.modal.classList.add('active');
}

function closeAutoArchiveModal() {
    const { modal } = getAutoArchiveModalElements();
    if (modal) modal.classList.remove('active');
    autoArchiveDraft = null;
}

function selectAutoArchiveMode(mode) {
    if (!autoArchiveDraft || !['done-age', 'on-date', 'periodic'].includes(mode)) return;
    autoArchiveDraft.mode = mode;
    syncAutoArchiveModeUI();
}

function selectAutoArchivePattern(pattern) {
    if (!autoArchiveDraft || !['daily', 'weekly', 'monthly'].includes(pattern)) return;
    autoArchiveDraft.pattern = pattern;
    // Ensure sensible defaults when switching patterns
    if (pattern === 'weekly' && (!Array.isArray(autoArchiveDraft.daysOfWeek) || autoArchiveDraft.daysOfWeek.length === 0)) {
        autoArchiveDraft.daysOfWeek = [new Date().getDay()];
    }
    if (pattern === 'monthly' && !Number.isFinite(autoArchiveDraft.dayOfMonth)) {
        const d = new Date().getDate();
        autoArchiveDraft.dayOfMonth = d <= 31 ? d : 31;
    }
    syncAutoArchiveModeUI();
}

function toggleAutoArchiveWeekday(day) {
    if (!autoArchiveDraft) return;
    const dayNum = parseInt(day, 10);
    if (!Number.isFinite(dayNum) || dayNum < 0 || dayNum > 6) return;
    const current = Array.isArray(autoArchiveDraft.daysOfWeek) ? [...autoArchiveDraft.daysOfWeek] : [];
    const idx = current.indexOf(dayNum);
    if (idx === -1) {
        current.push(dayNum);
    } else if (current.length > 1) {
        // Keep at least one day selected
        current.splice(idx, 1);
    }
    autoArchiveDraft.daysOfWeek = current.sort((a, b) => a - b);
    syncAutoArchiveModeUI();
}

function syncAutoArchiveModeUI() {
    if (!autoArchiveDraft) return;
    const els = getAutoArchiveModalElements();
    if (!els.modal) return;

    ['done-age', 'on-date', 'periodic'].forEach((mode) => {
        const pill = document.getElementById(`aa-mode-${mode}`);
        if (pill) pill.classList.toggle('active', autoArchiveDraft.mode === mode);
    });
    if (els.doneAgeGroup) els.doneAgeGroup.hidden = autoArchiveDraft.mode !== 'done-age';
    if (els.onDateGroup) els.onDateGroup.hidden = autoArchiveDraft.mode !== 'on-date';
    if (els.periodicGroup) els.periodicGroup.hidden = autoArchiveDraft.mode !== 'periodic';

    const isPeriodic = autoArchiveDraft.mode === 'periodic';
    ['daily', 'weekly', 'monthly'].forEach((pattern) => {
        const pill = document.getElementById(`aa-pattern-${pattern}`);
        if (pill) pill.classList.toggle('active', autoArchiveDraft.pattern === pattern);
    });

    // Weekly day chips
    if (els.weeklyDaysGroup) {
        els.weeklyDaysGroup.hidden = !(isPeriodic && autoArchiveDraft.pattern === 'weekly');
    }
    const daysOfWeek = autoArchiveDraft.daysOfWeek || [];
    els.modal.querySelectorAll('[data-action="toggleAutoArchiveDay"]').forEach((btn) => {
        btn.classList.toggle('active', daysOfWeek.includes(parseInt(btn.dataset.param, 10)));
    });

    // Monthly day-of-month select
    if (els.monthlyDayGroup) {
        els.monthlyDayGroup.hidden = !(isPeriodic && autoArchiveDraft.pattern === 'monthly');
    }
    if (els.dayOfMonthSelect) {
        els.dayOfMonthSelect.value = String(
            Number.isFinite(autoArchiveDraft.dayOfMonth) ? autoArchiveDraft.dayOfMonth : ''
        );
    }

    syncAutoArchivePreview();
}

function syncAutoArchivePreview() {
    if (!autoArchiveDraft) return;
    const { summaryText } = getAutoArchiveModalElements();
    if (!summaryText) return;

    let base = '';
    if (autoArchiveDraft.mode === 'done-age') {
        const n = autoArchiveDraft.thresholdDays || 0;
        base = t('autoArchive.preview.doneAge', { count: n, unit: autoArchiveUnitWord('day', n) });
    } else if (autoArchiveDraft.mode === 'on-date') {
        base = autoArchiveDraft.archiveDate
            ? t('autoArchive.preview.onDate', { date: formatAutomationDateLabel(autoArchiveDraft.archiveDate) })
            : t('autoArchive.preview.empty');
    } else if (autoArchiveDraft.mode === 'periodic') {
        if (autoArchiveDraft.pattern === 'daily') {
            base = t('autoArchive.preview.periodicDaily');
        } else if (autoArchiveDraft.pattern === 'weekly') {
            const days = Array.isArray(autoArchiveDraft.daysOfWeek)
                ? [...autoArchiveDraft.daysOfWeek].sort((a, b) => a - b) : [];
            const dayNames = days.map(getLocalizedWeekdayShort).join(', ');
            base = dayNames
                ? t('autoArchive.preview.periodicWeekly', { days: dayNames })
                : t('autoArchive.preview.periodicWeeklyNoDays');
        } else if (autoArchiveDraft.pattern === 'monthly') {
            const dom = autoArchiveDraft.dayOfMonth;
            const dayLabel = dom === 0
                ? t('autoArchive.periodic.monthlyLastDay')
                : (Number.isFinite(dom) && dom >= 1 ? String(dom) : '');
            base = dayLabel
                ? t('autoArchive.preview.periodicMonthly', { day: dayLabel })
                : t('autoArchive.preview.periodicMonthlyNoDay');
        }
    }

    const count = countAutoArchiveMatches(autoArchiveDraft);
    const matchText = count > 0
        ? t('autoArchive.preview.matchNow', { count, unit: autoArchiveUnitWord('task', count) })
        : t('autoArchive.preview.matchNone');
    summaryText.textContent = base ? `${base} ${matchText}` : matchText;
}

// Count Done tasks that would be archived right now under the draft rule.
function countAutoArchiveMatches(draft) {
    if (!draft || !Array.isArray(tasks)) return 0;
    const nowMs = Date.now();
    const isDone = (task) => task && !task.archivedAt && !task.isTemplate && task.status === 'done';
    const isInScope = (task) => draft.includeProjectTasks === true || task.projectId == null || task.projectId === '';
    if (draft.mode === 'done-age') {
        const n = parseInt(draft.thresholdDays, 10);
        if (!Number.isFinite(n) || n < 1) return 0;
        return tasks.filter((task) => {
            if (!isDone(task) || !isInScope(task)) return false;
            const raw = task.completedDate || task.completedAt || task.updatedAt || task.createdAt;
            const ms = raw ? new Date(raw).getTime() : NaN;
            if (!Number.isFinite(ms)) return false;
            return (nowMs - ms) / 86400000 >= n;
        }).length;
    }
    return tasks.filter((task) => isDone(task) && isInScope(task)).length;
}

async function submitAutoArchiveModal() {
    if (!autoArchiveDraft) return;
    const els = getAutoArchiveModalElements();

    syncAutoArchiveDraftDate();

    const validation = validateArchiveRule(autoArchiveDraft);
    if (!validation.valid) {
        const map = {
            'Invalid trigger mode': 'autoArchive.validation.mode',
            'Threshold must be between 1 and 3650 days': 'autoArchive.validation.threshold',
            'Choose a valid archive date': 'autoArchive.validation.date',
            'Choose a cleanup frequency': 'autoArchive.validation.pattern'
        };
        const key = map[validation.errors[0]] || 'autoArchive.validation.mode';
        if (els.formError) {
            els.formError.textContent = t(key);
            els.formError.hidden = false;
        }
        return;
    }

    const isEdit = autoArchiveDraft.automationId != null;
    const ruleId = isEdit
        ? autoArchiveDraft.automationId
        : (Math.max(0, ...automations.map((r) => Number(r.id) || 0)) + 1);
    const rule = buildArchiveRule(autoArchiveDraft, ruleId);
    if (!rule) return;

    const previousAutomations = JSON.parse(JSON.stringify(automations));
    const nextRules = isEdit
        ? automations.map((r) => (r.id === ruleId ? rule : r))
        : [...automations, rule];

    if (els.submitBtn) els.submitBtn.disabled = true;
    let saved = false;
    try {
        automations = nextRules;
        await saveAutomationsData(nextRules);
        saved = true;
    } catch (error) {
        automations = previousAutomations;
        renderAutomations();
        if (els.formError) {
            els.formError.textContent = t('error.saveChangesFailed');
            els.formError.hidden = false;
        }
        console.error('[auto-archive] save failed', error);
    } finally {
        if (els.submitBtn) els.submitBtn.disabled = false;
    }

    if (!saved) return;

    // Save committed — UI work below must never revert the persisted state.
    closeAutoArchiveModal();
    try { processAutoArchiveRules(); } catch (err) { console.error('[auto-archive] post-save run failed', err); }
    renderAfterFilterChange();
    updateCounts();
    renderAutomations();
    showSuccessNotification(t(isEdit ? 'toast.autoArchive.updated' : 'toast.autoArchive.created'));
}

window.openAutoArchiveModal = openAutoArchiveModal;
window.closeAutoArchiveModal = closeAutoArchiveModal;
window.submitAutoArchiveModal = submitAutoArchiveModal;
window.selectAutoArchiveMode = selectAutoArchiveMode;
window.selectAutoArchivePattern = selectAutoArchivePattern;
window.toggleAutoArchiveWeekday = toggleAutoArchiveWeekday;

window.openRecurrenceModal = openRecurrenceModal;
window.closeRecurrenceModal = closeRecurrenceModal;
window.submitRecurrenceModal = submitRecurrenceModal;
window.selectRecurrencePattern = selectRecurrencePattern;
window.toggleRecurrenceWeekday = toggleRecurrenceWeekday;
window.toggleAutomationActive = toggleAutomationActive;
window.requestDeleteAutomation = requestDeleteAutomation;
window.cancelDeleteAutomation = cancelDeleteAutomation;
window.deleteAutomation = deleteAutomation;
window.editAutomation = editAutomation;
window.toggleAutomationTypePicker = toggleAutomationTypePicker;
window.selectAutomationType = selectAutomationType;
window.startRecurrenceNewTemplate = startRecurrenceNewTemplate;
window.selectRecurrenceComboboxTask = selectRecurrenceComboboxTask;
window.clearRecurrenceCombobox = clearRecurrenceCombobox;
window.openAutomationAudit = openAutomationAudit;
window.openAutomationAuditTask = openAutomationAuditTask;

function openTaskDetails(taskId, navigationContext = null) {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const modal = document.getElementById("task-modal");
    if (!modal) return;

    // Show the modal BEFORE any bookkeeping that could throw. On iOS Chrome/Brave
    // (WKWebView) the URL bookkeeping below was throwing before the modal was ever
    // shown, so it silently failed to open (Safari did not hit the throw). Showing
    // first guarantees the modal opens regardless of what a later step does.
    modal.classList.add("active");

    // Preserve existing filters: save return hash (without any stale task= param),
    // then append task={id} so refresh re-opens this task. Wrapped because URL/
    // history APIs can throw in some iOS WKWebView browsers; a failure here must
    // not prevent the modal from opening.
    try {
        const currentHash = window.location.hash || '#tasks';
        const baseHash = currentHash.startsWith('#task-')
            ? '#tasks'                          // came from a clean permalink — return to bare tasks
            : hashWithoutTaskParam(currentHash); // strip any stale task= from filter URL
        taskModalReturnHash = baseHash;
        // Add task= to the base URL (preserving all other params)
        const [basePage, baseQs] = baseHash.slice(1).split('?');
        const baseParams = new URLSearchParams(baseQs || '');
        baseParams.set('task', taskId);
        safeReplaceState(null, '', '#' + basePage + '?' + baseParams.toString());
    } catch (err) {
        if (typeof window !== 'undefined' && typeof window.__diag === 'function') {
            window.__diag('openTaskDetails URL step threw: ' + (err && err.message));
        }
    }

    // Setup tag autocomplete listeners
    setupTagAutocompleteListeners();

    // Store navigation context if provided
    currentTaskNavigationContext = navigationContext;

    // Reset tabs to General tab
    const generalTab = modal.querySelector('.modal-tab[data-tab="general"]');
    const detailsTab = modal.querySelector('.modal-tab[data-tab="details"]');
    const historyTab = modal.querySelector('.modal-tab[data-tab="history"]');
    const generalContent = modal.querySelector('#task-details-tab');
    const historyContent = modal.querySelector('#task-history-tab');

    // Activate General tab (both desktop and mobile)
    if (generalTab) generalTab.classList.add('active');
    if (detailsTab) detailsTab.classList.remove('active');
    if (historyTab) historyTab.classList.remove('active');
    if (generalContent) generalContent.classList.add('active');
    if (historyContent) historyContent.classList.remove('active');

    // Remove mobile tab state
    document.body.classList.remove('mobile-tab-details-active');

    // Show History tab for editing existing tasks
    if (historyTab) historyTab.style.display = '';

    const modalContent = modal.querySelector('.modal-content');
    if (modalContent) {
        modalContent.style.minHeight = '';
        modalContent.style.maxHeight = '';
    }

    // Title
    // NOTE: must exclude the rich-text description editor's own <h2> headings
    // (user-inserted via the H2 toolbar button) — a bare modal.querySelector("h2")
    // matches those first and silently overwrites task content.
    const titleEl = Array.from(modal.querySelectorAll("h2")).find(h => !h.closest("#task-description-editor"));
    if (titleEl) {
        titleEl.dataset.i18n = 'tasks.modal.editTitle';
        titleEl.textContent = t('tasks.modal.editTitle');
    }

    // Show ❌ and ⋯ in edit mode
    const closeBtn = modal.querySelector(".modal-close");
    if (closeBtn) closeBtn.style.display = "inline-block";
    const optionsBtn = modal.querySelector("#task-options-btn");
    if (optionsBtn) optionsBtn.style.display = "inline-block";

    // Hide footer with "Create Task"
    const footer = modal.querySelector("#task-footer");
    if (footer) footer.style.display = "none";

    // Project dropdown (custom dropdown)
    populateProjectDropdownOptions();
    const hiddenProject = modal.querySelector("#hidden-project");
    const projectCurrentBtn = modal.querySelector("#project-current");
    if (hiddenProject && projectCurrentBtn) {
        hiddenProject.value = task.projectId || "";
        const projectTextSpan = projectCurrentBtn.querySelector(".project-text");
        if (projectTextSpan) {
            if (task.projectId) {
                const project = projects.find(p => p.id === task.projectId);
                if (project) {
                    projectTextSpan.innerHTML = buildProjectCurrentHTML(project.id, project.name);
                } else {
                    projectTextSpan.textContent = t('tasks.project.selectPlaceholder');
                }
            } else {
                projectTextSpan.textContent = t('tasks.project.selectPlaceholder');
            }
        }
        updateTaskProjectOpenBtn(task.projectId || "");
    }

    // Title/description
    const titleInput = modal.querySelector('#task-form input[name="title"]');
    if (titleInput) titleInput.value = task.title || "";

    try {
        setTaskDescriptionHTML(task.description || "");
    } catch (err) {
        // A malformed/edge-case stored description can make the rich-text editor
        // throw while parsing HTML (observed on iOS Chrome's WebKit). Don't let
        // it abort the rest of modal population (priority, status, dates, etc.).
        logDebug('task-modal', 'setTaskDescriptionHTML failed', { error: String(err) });
    }

    // Priority
    const hiddenPriority = modal.querySelector("#hidden-priority");
    if (hiddenPriority) hiddenPriority.value = task.priority || "medium";
    const priorityCurrentBtn = modal.querySelector("#priority-current");
    if (priorityCurrentBtn) {
        const priority = task.priority || "medium";
        const priorityLabel = getPriorityLabel(priority).toUpperCase();
        priorityCurrentBtn.innerHTML = `<span class="priority-pill priority-${priority}">${priorityLabel}</span> <span class="dropdown-arrow">▼</span>`;
        updatePriorityOptions(priority);
    }

    // Status
    const hiddenStatus = modal.querySelector("#hidden-status");
    if (hiddenStatus) hiddenStatus.value = task.status || "todo";
  const currentBtn = modal.querySelector("#status-current");
  if (currentBtn) {
    const statusBadge = currentBtn.querySelector(".status-badge");
    if (statusBadge) {
      statusBadge.className = "status-badge " + (task.status || "todo");
      statusBadge.textContent = getStatusLabel(task.status || "todo");
    }
    updateStatusOptions(task.status || "todo");
  }

    // Reset link type dropdown to "relates to"
    const linkTypeValue = modal.querySelector("#link-type-value");
    if (linkTypeValue) linkTypeValue.value = "relates_to";
    const linkTypeLabel = modal.querySelector("#link-type-label");
    if (linkTypeLabel) linkTypeLabel.setAttribute('data-i18n', 'tasks.links.relatesTo');
    if (linkTypeLabel) linkTypeLabel.textContent = t('tasks.links.relatesTo');
    // Hide web link inputs, show task search
    const webLinkInputs = modal.querySelector("#web-link-inputs");
    const taskLinkSearch = modal.querySelector("#task-link-search");
    if (webLinkInputs) webLinkInputs.style.display = "none";
    if (taskLinkSearch) taskLinkSearch.style.display = "block";

    // Prepare date values from task
    let startIso = "";
    if (typeof task.startDate === "string") {
        if (looksLikeISO(task.startDate)) startIso = task.startDate;
        else if (looksLikeDMY(task.startDate)) startIso = toISOFromDMY(task.startDate);
    }

    let endIso = "";
    if (typeof task.endDate === "string") {
        if (looksLikeISO(task.endDate)) endIso = task.endDate;
        else if (looksLikeDMY(task.endDate)) endIso = toISOFromDMY(task.endDate);
    }

    // Get input references
    const startInput = modal.querySelector('#task-form input[name="startDate"]');
    const endInput = modal.querySelector('#task-form input[name="endDate"]');

    // Check if flatpickr is already initialized (to avoid flicker when navigating)
    // Flatpickr is attached to the display input, not the hidden input
    const startWrapper = startInput ? startInput.parentElement : null;
    const endWrapper = endInput ? endInput.parentElement : null;
    const startDisplayInput = startWrapper && startWrapper.classList.contains('date-input-wrapper')
        ? startWrapper.querySelector('input.date-display') : null;
    const endDisplayInput = endWrapper && endWrapper.classList.contains('date-input-wrapper')
        ? endWrapper.querySelector('input.date-display') : null;

    const startDateAlreadyWrapped = startDisplayInput && startDisplayInput._flatpickr;
    const endDateAlreadyWrapped = endDisplayInput && endDisplayInput._flatpickr;

    // If flatpickr is already initialized, just update the values (no flicker)
    if (startDateAlreadyWrapped && endDateAlreadyWrapped) {
        // Update values directly through flatpickr API (attached to display inputs)
        if (startDisplayInput && startDisplayInput._flatpickr) {
            // Set hidden input value first (critical for validation)
            startInput.value = startIso || '';
            if (startIso) {
                startDisplayInput._flatpickr.setDate(new Date(startIso), false);
            } else {
                startDisplayInput._flatpickr.clear();
            }
        }
        if (endDisplayInput && endDisplayInput._flatpickr) {
            // Set hidden input value first (critical for validation)
            endInput.value = endIso || '';
            if (endIso) {
                endDisplayInput._flatpickr.setDate(new Date(endIso), false);
            } else {
                endDisplayInput._flatpickr.clear();
            }
        }
    } else {
        // First time opening or flatpickr not initialized - do full reset
        const dateInputs = modal.querySelectorAll('input[name="startDate"], input[name="endDate"]');
        dateInputs.forEach(input => {
            if (input._flatpickrInstance) {
                input._flatpickrInstance.destroy();
                input._flatpickrInstance = null;
            }
            input._wrapped = false;

            // Remove wrapper if it exists
            const wrapper = input.closest('.date-input-wrapper');
            if (wrapper) {
                const parent = wrapper.parentNode;
                parent.insertBefore(input, wrapper);
                wrapper.remove();
            }

            // Restore to type="date"
            input.type = "date";
            input.style.display = "";
        });
    }

    // Editing ID
    const form = modal.querySelector("#task-form");
    if (form) form.dataset.editingTaskId = String(taskId);

    // Toggle Archive vs Restore item in the task options menu based on state.
    const archiveBtn = modal.querySelector('#task-modal-archive-btn');
    const restoreBtn = modal.querySelector('#task-modal-restore-btn');
    const isArchived = !!task.archivedAt;
    if (archiveBtn) archiveBtn.style.display = isArchived ? 'none' : '';
    if (restoreBtn) restoreBtn.style.display = isArchived ? '' : 'none';

    renderAttachments(task.attachments || []);
    renderTags(task.tags || []);

    // Clear tag input field when opening task details
    const tagInput = modal.querySelector("#tag-input");
    if (tagInput) tagInput.value = "";

    // MOBILE: Dynamic field organization - move filled Details fields to General
    // Only applies when editing existing task (not creating new)
    if (getIsMobileCached() && taskId) {
        const hasTags = Array.isArray(task.tags) && task.tags.length > 0;
        const hasStartDate = typeof task.startDate === 'string' && task.startDate.trim() !== '';
        const hasEndDate = typeof task.endDate === 'string' && task.endDate.trim() !== '';
        const hasLinks = (
            (Array.isArray(task.attachments) && task.attachments.some(att =>
                att.type === 'link' || (att.url && att.type !== 'file')
            )) ||
            (Array.isArray(task.links) && task.links.length > 0)
        );

        // Store initial date state - dates stay in General if they were ever set (even if cleared later)
        // NOTE: Don't use property-existence because we add empty fields during migration.
        const startDateWasEverSet = !!task.startDateWasEverSet || hasStartDate;
        const endDateWasEverSet = !!task.endDateWasEverSet || hasEndDate;
        modal.dataset.initialStartDate = startDateWasEverSet ? 'true' : 'false';
        modal.dataset.initialEndDate = endDateWasEverSet ? 'true' : 'false';

        // Get form groups for Details fields (using parent traversal instead of :has())
        const tagInput = modal.querySelector('#tag-input');
        const tagsGroup = tagInput ? tagInput.closest('.form-group') : null;

        const startDateInputs = modal.querySelectorAll('input[name="startDate"]');
        let startDateGroup = null;
        for (const input of startDateInputs) {
            const group = input.closest('.form-group');
            if (group && group.classList.contains('mobile-details-field')) {
                startDateGroup = group;
                break;
            }
        }

        const endDateInputs = modal.querySelectorAll('input[name="endDate"]');
        let endDateGroup = null;
        for (const input of endDateInputs) {
            const group = input.closest('.form-group');
            if (group && group.classList.contains('mobile-details-field')) {
                endDateGroup = group;
                break;
            }
        }

        const linksList = modal.querySelector('#attachments-links-list');
        const linksGroup = linksList ? linksList.closest('.form-group') : null;

        // Move filled fields to General, keep empty in Details
        if (tagsGroup) {
            if (hasTags) {
                tagsGroup.classList.remove('mobile-details-field');
                tagsGroup.classList.add('mobile-general-field');
            } else {
                tagsGroup.classList.remove('mobile-general-field');
                tagsGroup.classList.add('mobile-details-field');
            }
        }

        if (startDateGroup) {
            if (startDateWasEverSet) {
                startDateGroup.classList.remove('mobile-details-field');
                startDateGroup.classList.add('mobile-general-field');
            } else {
                startDateGroup.classList.remove('mobile-general-field');
                startDateGroup.classList.add('mobile-details-field');
            }
        }

        if (endDateGroup) {
            if (endDateWasEverSet) {
                endDateGroup.classList.remove('mobile-details-field');
                endDateGroup.classList.add('mobile-general-field');
            } else {
                endDateGroup.classList.remove('mobile-general-field');
                endDateGroup.classList.add('mobile-details-field');
            }
        }

        if (linksGroup) {
            if (hasLinks) {
                linksGroup.classList.remove('mobile-details-field');
                linksGroup.classList.add('mobile-general-field');
            } else {
                linksGroup.classList.remove('mobile-general-field');
                linksGroup.classList.add('mobile-details-field');
            }
        }

        // Hide Details tab only if Tags, Links AND both dates are in General (unset dates live in Details)
        const allDetailsFilled = hasTags && hasLinks && startDateWasEverSet && endDateWasEverSet;
        console.log('\u{1F50D} Details Tab Logic:', {
            hasTags,
            hasStartDate,
            hasEndDate,
            hasLinks,
            allDetailsFilled,
            tags: task.tags,
            startDate: task.startDate,
            endDate: task.endDate,
            attachments: task.attachments
        });
        if (detailsTab) {
            if (allDetailsFilled) {
                console.log('\u{2705} Hiding Details tab - all fields filled');
                detailsTab.classList.add('hide-details-tab');
            } else {
                console.log('👁️ Showing Details tab - some fields empty');
                detailsTab.classList.remove('hide-details-tab');
            }
        }
    }

    // CRITICAL: Make modal visible FIRST (mobile browsers require visible inputs to accept values)
    modal.classList.add("active");

    // Update navigation buttons based on context
    updateTaskNavigationButtons();

    // Use setTimeout to ensure modal is rendered and visible before setting date values
    setTimeout(() => {
        // Only do full initialization if not already wrapped (reduces flicker)
        if (!startDateAlreadyWrapped || !endDateAlreadyWrapped) {
            // Set values BEFORE wrapping (now that modal is visible)
            if (startInput) startInput.value = startIso || "";
            if (endInput) endInput.value = endIso || "";

            // Initialize date pickers (creates wrappers)
            initializeDatePickers();
        }

        // ALWAYS update display values after wrapping (critical for both first load and navigation)
        if (startInput) {
            const wrapper = startInput.parentElement;
            if (wrapper && wrapper.classList.contains('date-input-wrapper')) {
                const displayInput = wrapper.querySelector('input.date-display');
                if (displayInput) {
                    // Set hidden input value (critical for validation)
                    startInput.value = startIso || '';

                    // Update flatpickr instance (attached to display input)
                    if (displayInput._flatpickr) {
                        if (startIso) {
                            displayInput._flatpickr.setDate(new Date(startIso), false);
                        } else {
                            displayInput._flatpickr.clear();
                        }
                    }
                }
            }
        }

        if (endInput) {
            const wrapper = endInput.parentElement;
            if (wrapper && wrapper.classList.contains('date-input-wrapper')) {
                const displayInput = wrapper.querySelector('input.date-display');
                if (displayInput) {
                    // Set hidden input value (critical for validation)
                    endInput.value = endIso || '';

                    // Update flatpickr instance (attached to display input)
                    if (displayInput._flatpickr) {
                        if (endIso) {
                            displayInput._flatpickr.setDate(new Date(endIso), false);
                        } else {
                            displayInput._flatpickr.clear();
                        }
                    }
                }
            }
        }
    }, 0);


    // Reset scroll position AFTER modal is active and rendered
    setTimeout(() => {
        const modalBody = modal.querySelector('.modal-body');
        if (modalBody) modalBody.scrollTop = 0;
    }, 0);
}


// Pass a taskId to delete a task without the task modal open (kanban context menu);
// with no argument it deletes the task open in the modal.
function deleteTask(taskIdArg) {
    const taskId = taskIdArg != null
        ? String(taskIdArg)
        : document.getElementById("task-form").dataset.editingTaskId;
    if (!taskId) return;

    const task = tasks.find((t) => t.id === parseInt(taskId));
    if (!task) return;

    // Show custom confirmation modal
    const confirmModalEl = document.getElementById("confirm-modal");
    confirmModalEl.dataset.taskId = taskId;
    confirmModalEl.classList.add("active");
    const confirmInput = document.getElementById("confirm-input");
    confirmInput.value = "";
    confirmInput.focus();

    // Listeners below are document/input-level: register them once, not on every open.
    if (confirmModalEl.dataset.listenersBound === "1") return;
    confirmModalEl.dataset.listenersBound = "1";

    // Auto-convert to lowercase as user types for better UX
    const lowercaseHandler = function(e) {
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        e.target.value = e.target.value.toLowerCase();
        e.target.setSelectionRange(start, end);
    };
    confirmInput.addEventListener('input', lowercaseHandler);

    // Add keyboard support for task delete modal
    document.addEventListener('keydown', function(e) {
        const confirmModal = document.getElementById('confirm-modal');
        if (!confirmModal || !confirmModal.classList.contains('active')) return;
        
        if (e.key === 'Escape') {
            e.preventDefault();
            closeConfirmModal();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            confirmDelete();
        }
    });  
}

// Copy a shareable permalink for a task (default: the one open in the modal) to the clipboard
function copyTaskLink(taskIdArg) {
    const form = document.getElementById("task-form");
    const editingTaskId = taskIdArg != null ? String(taskIdArg) : form?.dataset.editingTaskId;
    if (!editingTaskId) return;
    const url = `${window.location.origin}${window.location.pathname}#task-${editingTaskId}`;
    navigator.clipboard.writeText(url).then(() => {
        showNotification(t('tasks.modal.linkCopied'), 'success');
    }).catch(() => {
        showNotification(url, 'info'); // fallback: show the URL itself
    });
}

// Duplicate a task (default: the one open in the task modal)
async function duplicateTask(taskIdArg) {
    const form = document.getElementById("task-form");
    const editingTaskId = taskIdArg != null ? String(taskIdArg) : form?.dataset.editingTaskId;
    if (!editingTaskId) return;

    // Use task service to duplicate task
    const result = duplicateTaskService(parseInt(editingTaskId, 10), tasks, taskCounter);
    if (!result.task) return;

    // Update global state
    tasks = result.tasks;
    taskCounter = result.taskCounter;
    const cloned = result.task;

    // Mark project as updated and record project history when a task is duplicated into a project
    if (cloned && cloned.projectId) {
        const proj = touchProjectUpdatedAt(cloned.projectId);
        if (proj) saveProjectUpdate(cloned.projectId, { updatedAt: proj.updatedAt }).catch(() => {});
        recordProjectTaskLinkChange(cloned.projectId, 'added', cloned);
    }

    // Close options menu to avoid overlaying issues
    const menu = document.getElementById("options-menu");
    if (menu) menu.style.display = "none";

    // Sync date filter option visibility
    populateProjectOptions();
    populateTagOptions();
    populateCalendarTaskProjectOptions();
    populateCalendarTaskTagOptions();
    updateNoDateOptionVisibility();

    // Update UI: use immediate calendar render when on calendar (avoids debounce breaking layout)
    const inProjectDetails = document.getElementById("project-details").classList.contains("active");
    const isCalendarActive = document.getElementById("calendar-view")?.classList.contains("active");
    if (inProjectDetails && cloned.projectId) {
        showProjectDetails(cloned.projectId);
    }
    renderActivePageOnly({
        calendarChanged: true,
        calendarImmediate: !!isCalendarActive
    });
    // Double-render when on calendar (layout settle - same as changeMonth/goToToday)
    if (isCalendarActive) {
        renderCalendar();
    }

    updateCounts();

    // Save in background using row-level operation
    saveTaskCreate(cloned).catch(err => {
        console.error('Failed to save duplicated task:', err);
        showErrorNotification(t('error.saveTaskFailed'));
    });

    // Show the duplicated task in the modal (user expects to see/edit the copy)
    openTaskDetails(cloned.id);
}

function closeConfirmModal() {
    const confirmModalEl = document.getElementById("confirm-modal");
    confirmModalEl.classList.remove("active");
    delete confirmModalEl.dataset.taskId;
    document.getElementById("confirm-error").classList.remove("show");
}

function showStatusInfoModal() {
    const modal = document.getElementById('status-info-modal');
    if (modal) modal.classList.add('active');
}

async function confirmDelete() {
    const input = document.getElementById("confirm-input");
    const errorMsg = document.getElementById("confirm-error");
    if (isDeleteConfirmation(input.value)) {
        const taskId = document.getElementById("confirm-modal").dataset.taskId
            || document.getElementById("task-form").dataset.editingTaskId;
        if (!taskId) return;

        // Save snapshots for rollback
        const previousTasks = JSON.parse(JSON.stringify(tasks));
        const previousDependencies = JSON.parse(JSON.stringify(dependencies));
        const previousAutomations = JSON.parse(JSON.stringify(automations));

        // Use task service to delete task
        const result = deleteTaskService(parseInt(taskId, 10), tasks);
        if (!result.task) return;

        const wasInProjectDetails = result.task && result.projectId &&
            document.getElementById("project-details").classList.contains("active");
        const projectId = result.projectId;
        const affectedAutomations = automations.filter((rule) => Number(rule.sourceTaskId) === Number(taskId));
        const nextAutomations = affectedAutomations.length > 0
            ? automations.map((rule) => Number(rule.sourceTaskId) === Number(taskId) ? { ...rule, active: false } : rule)
            : automations;

        // Update global tasks array (optimistic)
        tasks = result.tasks;
        automations = nextAutomations;

        // Remove all dependencies involving this task (optimistic)
        const depResult = removeDependenciesForTask(parseInt(taskId, 10), dependencies);
        dependencies = depResult.dependencies;

        // Mark project as updated
        if (projectId) {
            const proj = touchProjectUpdatedAt(projectId);
            recordProjectTaskLinkChange(projectId, 'removed', result.task);
        }

        // Close modals and update UI (optimistic — task disappears immediately)
        closeConfirmModal();
        closeModal("task-modal");

        // Keep filter dropdowns in sync
        populateProjectOptions();
        populateTagOptions();
        populateCalendarTaskProjectOptions();
        populateCalendarTaskTagOptions();
        updateNoDateOptionVisibility();

        // Refresh view immediately
        if (wasInProjectDetails && projectId) {
            showProjectDetails(projectId);
        } else {
            render();
        }
        renderAutomations();

        const calendarView = document.getElementById("calendar-view");
        if (calendarView) {
            renderCalendar();
            if (calendarView.classList.contains("active")) {
                renderCalendar();
            }
        }

        updateCounts();

        // Persist — rollback to previous state if save fails (task reappears)
        try {
            if (affectedAutomations.length > 0) {
                await saveAutomationsData(nextAutomations);
            }
            await saveTaskDelete(parseInt(taskId, 10));
            // Record history only after confirmed deletion
            if (window.historyService) {
                window.historyService.recordTaskDeleted(result.task);
            }
            // Save project touch and dependency cleanup in background (non-critical)
            if (projectId) {
                const proj = projects.find(p => p.id === projectId);
                if (proj) saveProjectUpdate(projectId, { updatedAt: proj.updatedAt }).catch(err => console.error('Failed to save project touch after task delete:', err));
            }
            saveDependenciesD1(serializeDependencies(dependencies)).catch(err => {
                console.error('Failed to save dependency cleanup:', err);
                showErrorNotification(t('error.saveChangesFailed'));
            });
        } catch (err) {
            // Rollback — restore task and dependencies so data isn't lost
            console.error('Failed to persist task deletion:', err);
            tasks = previousTasks;
            dependencies = previousDependencies;
            automations = previousAutomations;
            if (affectedAutomations.length > 0) {
                await saveAutomationsData(previousAutomations).catch(() => {});
            }
            showErrorNotification(t('error.saveChangesFailed'));
            // Re-render so the task reappears (user must retry the deletion)
            if (wasInProjectDetails && projectId) {
                showProjectDetails(projectId);
            } else {
                render();
            }
            renderAutomations();
            updateCounts();
        }
    } else {
        errorMsg.classList.add('show');
        input.focus();
    }
}

// ============================================================================
// Dependency Management Handlers
// ============================================================================

/**
 * Add a dependency relationship between tasks
 * @param {number} dependentTaskId - Task that depends on another
 * @param {number} prerequisiteTaskId - Task that must be completed first
 */
async function handleAddDependency(dependentTaskId, prerequisiteTaskId) {
    const result = addDependency(dependentTaskId, prerequisiteTaskId, dependencies, tasks);
    
    if (result.error) {
        showErrorNotification(result.error);
        return false;
    }
    
    // Update dependencies state
    dependencies = result.dependencies;
    
    // Persist to storage
    try {
        await saveDependenciesD1(serializeDependencies(dependencies));
        showSuccessNotification(t('dependencies.added'));
        return true;
    } catch (error) {
        console.error('Failed to save dependency:', error);
        showErrorNotification(t('error.saveChangesFailed'));
        return false;
    }
}

/**
 * Remove a dependency relationship between tasks
 * @param {number} dependentTaskId - Task that depends on another
 * @param {number} prerequisiteTaskId - Task to remove as prerequisite
 */
async function handleRemoveDependency(dependentTaskId, prerequisiteTaskId) {
    const result = removeDependency(dependentTaskId, prerequisiteTaskId, dependencies);
    
    // Update dependencies state
    dependencies = result.dependencies;
    
    // Persist to storage
    try {
        await saveDependenciesD1(serializeDependencies(dependencies));
        showSuccessNotification(t('dependencies.removed'));
        return true;
    } catch (error) {
        console.error('Failed to save dependency removal:', error);
        showErrorNotification(t('error.saveChangesFailed'));
        return false;
    }
}

/**
 * Add a task relationship (uses existing dependencies system for depends_on)
 * @param {number} sourceTaskId - Source task ID
 * @param {number} targetTaskId - Target task ID
 * @param {string} linkType - Type of relationship
 */
async function addTaskRelationship(sourceTaskId, targetTaskId, linkType) {
    // Validate inputs
    if (!sourceTaskId || !targetTaskId) {
        showErrorNotification(t('error.invalidTaskIds'));
        return false;
    }
    
    if (sourceTaskId === targetTaskId) {
        showErrorNotification(t('error.cannotLinkToSelf'));
        return false;
    }
    
    const sourceTask = tasks.find(t => t.id === sourceTaskId);
    const targetTask = tasks.find(t => t.id === targetTaskId);
    
    if (!sourceTask || !targetTask) {
        showErrorNotification(t('error.taskNotFound'));
        return false;
    }
    
    if (linkType === 'depends_on') {
        // depends_on uses the existing dependencies system
        const success = await handleAddDependency(sourceTaskId, targetTaskId);
        if (success) {
            renderDependenciesInModal(sourceTask);
        }
        return success;
    } else if (linkType === 'blocks' || linkType === 'is_blocked_by' || linkType === 'relates_to') {
        // Initialize task links arrays if they don't exist
        if (!sourceTask.links) {
            sourceTask.links = [];
        }
        if (!targetTask.links) {
            targetTask.links = [];
        }
        
        // Check if link already exists
        const existingLink = sourceTask.links.find(
            link => link.taskId === targetTaskId && link.type === linkType
        );
        
        if (existingLink) {
            showErrorNotification(t('error.linkAlreadyExists'));
            return false;
        }
        
        // Determine the reciprocal link type
        let reciprocalType;
        if (linkType === 'blocks') {
            reciprocalType = 'is_blocked_by';
        } else if (linkType === 'is_blocked_by') {
            reciprocalType = 'blocks';
        } else if (linkType === 'relates_to') {
            reciprocalType = 'relates_to';
        }
        
        // Add the link from source to target
        sourceTask.links.push({
            type: linkType,
            taskId: targetTaskId,
            createdAt: new Date().toISOString()
        });
        
        // Add the reciprocal link from target to source
        const existingReciprocalLink = targetTask.links.find(
            link => link.taskId === sourceTaskId && link.type === reciprocalType
        );
        
        if (!existingReciprocalLink) {
            targetTask.links.push({
                type: reciprocalType,
                taskId: sourceTaskId,
                createdAt: new Date().toISOString()
            });
        }
        
        // Save tasks using row-level operations
        const now = new Date().toISOString();
        await Promise.all([
            saveTaskUpdate(sourceTaskId, { links: sourceTask.links, updatedAt: now }),
            saveTaskUpdate(targetTaskId, { links: targetTask.links, updatedAt: now })
        ]);

        // Record history for both tasks
        if (window.historyService) {
            const linkTypeLabels = { blocks: 'tasks.links.blocks', is_blocked_by: 'tasks.links.isBlockedBy', relates_to: 'tasks.links.relatesTo' };
            const linkLabel = t(linkTypeLabels[linkType] || 'tasks.links.relatesTo');
            window.historyService.recordHistory('task', sourceTaskId, sourceTask.title || 'Task', 'updated', {
                link: { before: null, after: { action: 'added', entity: 'task', id: targetTaskId, title: targetTask.title, linkType: linkLabel } }
            });
            window.historyService.recordHistory('task', targetTaskId, targetTask.title || 'Task', 'updated', {
                link: { before: null, after: { action: 'added', entity: 'task', id: sourceTaskId, title: sourceTask.title, linkType: linkLabel } }
            });
        }

        // Refresh the task modal to show the new link
        renderDependenciesInModal(sourceTask);

        showSuccessNotification(t('success.linkAdded'));
        return true;
    } else {
        showErrorNotification(`Relationship type "${linkType}" not yet implemented`);
        return false;
    }
}

// ============================================================================
// Kanban card context menu (right-click)
// ============================================================================

const KANBAN_MENU_STATUSES = ['backlog', 'todo', 'progress', 'review', 'done'];
const KANBAN_MENU_STATUS_SWATCH = {
    backlog: 'var(--text-muted)',
    todo: 'var(--accent-teal)',
    progress: 'var(--accent-blue)',
    review: 'var(--kb-review)',
    done: 'var(--accent-green)'
};
const KANBAN_MENU_PRIORITY_SWATCH = {
    high: 'var(--kb-priority-high)',
    medium: 'var(--kb-amber)',
    low: 'var(--accent-green)'
};

// Change status or priority of one or more tasks from the kanban context menu.
// Status changes follow the same side effects as a kanban drag between columns.
function applyKanbanQuickEdit(taskIds, field, value) {
    const changed = [];
    taskIds.forEach((id) => {
        const task = tasks.find(x => x.id === id);
        if (!task || task[field] === value) return;
        const oldTaskCopy = JSON.parse(JSON.stringify(task));
        task[field] = value;
        task.updatedAt = new Date().toISOString();

        if (field === 'status') {
            if (settings.autoSetStartDateOnStatusChange || settings.autoSetEndDateOnStatusChange) {
                const today = new Date().toISOString().split('T')[0];
                if (settings.autoSetStartDateOnStatusChange && value === 'progress' && !task.startDate) {
                    task.startDate = today;
                }
                if (settings.autoSetEndDateOnStatusChange && value === 'done' && !task.endDate) {
                    task.endDate = today;
                }
            }
            applyCompletedDateTransition(task, oldTaskCopy.status);
        }

        if (window.historyService) {
            window.historyService.recordTaskUpdated(oldTaskCopy, task);
        }
        if (field === 'status' && task.projectId) {
            checkAndRecordProjectStatusChange(task.projectId, task);
        }
        changed.push(task);
    });
    if (changed.length === 0) return;

    clearSelectedCards();
    render();
    const calendarView = document.getElementById("calendar-view");
    if (calendarView) renderCalendar();
    updateCounts();

    Promise.all(changed.map(task => saveTaskUpdate(task.id, field === 'status'
        ? {
            status:              task.status,
            completedDate:       task.completedDate || null,
            startDate:           task.startDate     || '',
            endDate:             task.endDate       || '',
            startDateWasEverSet: task.startDateWasEverSet,
            endDateWasEverSet:   task.endDateWasEverSet,
            updatedAt:           task.updatedAt,
        }
        : { priority: task.priority, updatedAt: task.updatedAt }
    ))).then(() => {
        if (changed.length > 1) {
            showNotification(t('tasks.contextMenu.updatedCount', { count: changed.length }), 'success');
        }
    }).catch(error => {
        console.error('Failed to save kanban context menu change:', error);
        showErrorNotification(t('error.saveChangesFailed'));
    });
}

async function archiveKanbanTasks(taskIds) {
    if (taskIds.length === 1) {
        await archiveTask(taskIds[0]);
        return;
    }
    const now = new Date().toISOString();
    const changed = [];
    taskIds.forEach((id) => {
        const task = tasks.find(x => x.id === id);
        if (!task || task.archivedAt) return;
        task.archivedAt = now;
        task.updatedAt = now;
        changed.push(task.id);
    });
    if (changed.length === 0) return;
    clearSelectedCards();
    render();
    updateCounts();
    try {
        await Promise.all(changed.map(id => saveTaskUpdate(id, { archivedAt: now })));
        showNotification(t('toast.tasks.archivedCount', { count: changed.length }), 'success');
    } catch (err) {
        console.error('[archive] kanban bulk archive failed', err);
        showErrorNotification(t('error.saveChangesFailed'));
    }
}

function openKanbanCardContextMenu(event, card) {
    const taskId = parseInt(card.dataset.taskId, 10);
    const task = tasks.find(x => x.id === taskId);
    if (!task) return;

    // A right-click on a card inside a multi-selection acts on the whole selection
    // (same rule as dragging); otherwise it acts on that card alone.
    const isBulk = appState.selectedCards.has(taskId) && appState.selectedCards.size > 1;
    const targetIds = isBulk ? Array.from(appState.selectedCards) : [taskId];
    const targetTasks = targetIds.map(id => tasks.find(x => x.id === id)).filter(Boolean);
    const sharedValue = (field) => {
        const first = targetTasks[0]?.[field];
        return targetTasks.every(x => x[field] === first) ? first : undefined;
    };
    const currentStatus = sharedValue('status');
    const currentPriority = sharedValue('priority');

    const statuses = KANBAN_MENU_STATUSES.filter(s => s !== 'review' || settings.enableReviewStatus !== false || currentStatus === 'review');
    const statusItems = statuses.map(status => ({
        id: `status:${status}`,
        label: getStatusLabel(status),
        swatch: KANBAN_MENU_STATUS_SWATCH[status],
        checked: currentStatus === status
    }));
    const priorityItems = ['high', 'medium', 'low'].map(priority => ({
        id: `priority:${priority}`,
        label: getPriorityLabel(priority),
        swatch: KANBAN_MENU_PRIORITY_SWATCH[priority],
        checked: currentPriority === priority
    }));

    const items = isBulk
        ? [
            { header: t('tasks.contextMenu.selectedCount', { count: targetIds.length }) },
            { id: 'status', label: t('tasks.contextMenu.status'), children: statusItems },
            { id: 'priority', label: t('tasks.contextMenu.priority'), children: priorityItems },
            { divider: true },
            { id: 'archive', label: t('tasks.contextMenu.archive') }
        ]
        : [
            { id: 'open', label: t('tasks.contextMenu.open') },
            { id: 'duplicate', label: t('tasks.contextMenu.duplicate') },
            { id: 'copyLink', label: t('tasks.contextMenu.copyLink') },
            { divider: true },
            { id: 'status', label: t('tasks.contextMenu.status'), children: statusItems },
            { id: 'priority', label: t('tasks.contextMenu.priority'), children: priorityItems },
            { divider: true },
            { id: 'archive', label: t('tasks.contextMenu.archive') },
            { id: 'delete', label: t('tasks.contextMenu.delete'), danger: true }
        ];

    const highlighted = isBulk
        ? Array.from(document.querySelectorAll('.kanban-board .task-card.selected'))
        : [card];
    highlighted.forEach(el => el.classList.add('context-menu-open'));

    openContextMenu({
        x: event.clientX,
        y: event.clientY,
        items,
        onClose: () => highlighted.forEach(el => el.classList.remove('context-menu-open')),
        onSelect: (item) => {
            const [action, value] = String(item.id).split(':');
            if (action === 'status' || action === 'priority') {
                applyKanbanQuickEdit(targetIds, action, value);
            } else if (action === 'open') {
                openTaskDetails(taskId);
            } else if (action === 'duplicate') {
                duplicateTask(taskId);
            } else if (action === 'copyLink') {
                copyTaskLink(taskId);
            } else if (action === 'archive') {
                archiveKanbanTasks(targetIds);
            } else if (action === 'delete') {
                deleteTask(taskId);
            }
        }
    });
}

// Translate a drag placeholder's visual position into an index within `orderedIds`.
// `orderedIds` holds ALL task IDs for the column (including ones hidden by an active
// filter); the placeholder only sits among the VISIBLE cards. Anchoring to the task IDs
// of the visible neighbours keeps hidden tasks in place and drops the card exactly where
// the user sees the gap — counting visible cards alone breaks whenever a filter is active.
function setupDragAndDrop() {
    let draggedTaskIds = [];
    let draggedFromStatus = null;
    let isSingleDrag = true;
    let dragPlaceholder = null;

    // Auto-scroll while dragging when near edges
    let autoScrollTimer = null;
    const SCROLL_ZONE = 80;
    const SCROLL_SPEED = 20;
    
    function getScrollableAncestor(el) {
        let node = el;
        while (node && node !== document.body) {
            const style = getComputedStyle(node);
            const overflowY = style.overflowY;
            if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && node.scrollHeight > node.clientHeight) {
                return node;
            }
            node = node.parentElement;
        }
        return window;
    }

    function startAutoScroll(direction, container) {
        stopAutoScroll();
        autoScrollTimer = setInterval(() => {
            try {
                if (container === window) {
                    window.scrollBy({ top: direction === 'down' ? SCROLL_SPEED : -SCROLL_SPEED, left: 0 });
                } else {
                    container.scrollTop += direction === 'down' ? SCROLL_SPEED : -SCROLL_SPEED;
                }
            } catch (err) {}
        }, 50);
    }

    function stopAutoScroll() {
        if (autoScrollTimer) {
            clearInterval(autoScrollTimer);
            autoScrollTimer = null;
        }
    }

    function cleanupDragStyles() {
        document.querySelectorAll(".kanban-column").forEach((col) => {
            col.style.backgroundColor = "";
            col.style.border = "";
            col.classList.remove('drag-over');
        });
        document.querySelectorAll(".task-card").forEach((c) => {
            c.classList.remove('dragging', 'drag-over-top', 'drag-over-bottom');
            c.style.opacity = "";
        });
    }

    const kanbanBoard = document.querySelector(".kanban-board");
    if (!kanbanBoard) return;
    if (kanbanBoard.dataset.dragDelegation === "1") return;
    kanbanBoard.dataset.dragDelegation = "1";

    const resolveStatusFromColumn = (column) => {
        const tasksContainer = column?.querySelector('[id$="-tasks"]');
        const id = tasksContainer?.id || '';
        return id ? id.replace('-tasks', '') : null;
    };

    kanbanBoard.addEventListener("dragstart", (e) => {
        const card = e.target.closest(".task-card");
        if (!card || !kanbanBoard.contains(card)) return;

        const taskId = parseInt(card.dataset.taskId, 10);
        if (Number.isNaN(taskId)) return;
        const taskObj = tasks.find(t => t.id === taskId);
        draggedFromStatus = taskObj ? taskObj.status : null;

        if (appState.selectedCards.has(taskId)) {
            draggedTaskIds = Array.from(appState.selectedCards);
        } else {
            draggedTaskIds = [taskId];
        }
        isSingleDrag = draggedTaskIds.length === 1;

        card.classList.add('dragging');
        card.style.opacity = "0.5";

        // Create placeholder with the SAME height as the dragged card
        dragPlaceholder = document.createElement('div');
        dragPlaceholder.className = 'drag-placeholder active';
        dragPlaceholder.setAttribute('aria-hidden', 'true');
        const cardHeight = card.offsetHeight;
        dragPlaceholder.style.height = cardHeight + 'px';
        dragPlaceholder.style.margin = '8px 0';
        dragPlaceholder.style.opacity = '1';

        document.querySelectorAll(".kanban-column").forEach((col) => {
            col.style.backgroundColor = "var(--bg-tertiary)";
            col.style.border = "2px dashed var(--accent-blue)";
        });

        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(taskId)); } catch (err) {}
    });

    kanbanBoard.addEventListener("dragend", (e) => {
        draggedTaskIds = [];
        draggedFromStatus = null;
        isSingleDrag = true;

        // Remove placeholder
        try {
            if (dragPlaceholder && dragPlaceholder.parentNode) {
                dragPlaceholder.parentNode.removeChild(dragPlaceholder);
            }
        } catch (err) {}
        dragPlaceholder = null;

        cleanupDragStyles();
        stopAutoScroll();
    });

    // 🔥 CLICK HANDLER - This is where selection happens
    kanbanBoard.addEventListener("click", (e) => {
        const card = e.target.closest(".task-card");
        if (!card || !kanbanBoard.contains(card)) return;
        const taskId = parseInt(card.dataset.taskId, 10);

        if (isNaN(taskId)) return;

        const isToggleClick = e.ctrlKey || e.metaKey;
        const isRangeClick = e.shiftKey;

        if (isRangeClick) {
            e.preventDefault();
            e.stopPropagation();

            const column = card.closest('.kanban-column');
            const scope = column || document;
            const cardsInScope = Array.from(scope.querySelectorAll('.task-card'));
            let anchorCard = null;

            if (Number.isInteger(appState.lastSelectedCardId)) {
                anchorCard = cardsInScope.find((c) => parseInt(c.dataset.taskId, 10) === appState.lastSelectedCardId);
            }
            if (!anchorCard) {
                anchorCard = cardsInScope.find((c) => appState.selectedCards.has(parseInt(c.dataset.taskId, 10))) || card;
            }

            const startIndex = cardsInScope.indexOf(anchorCard);
            const endIndex = cardsInScope.indexOf(card);

            if (!isToggleClick) {
                clearSelectedCards();
            }

            if (startIndex === -1 || endIndex === -1) {
                card.classList.add('selected');
                appState.selectedCards.add(taskId);
                appState.lastSelectedCardId = taskId;
                return;
            }

            const from = Math.min(startIndex, endIndex);
            const to = Math.max(startIndex, endIndex);
            for (let i = from; i <= to; i++) {
                const rangeCard = cardsInScope[i];
                const rangeId = parseInt(rangeCard.dataset.taskId, 10);
                if (isNaN(rangeId)) continue;
                rangeCard.classList.add('selected');
                appState.selectedCards.add(rangeId);
            }

            appState.lastSelectedCardId = taskId;
            return;
        }

        if (isToggleClick) {
            e.preventDefault();
            e.stopPropagation();

            card.classList.toggle('selected');

            if (card.classList.contains('selected')) {
                appState.selectedCards.add(taskId);
            } else {
                appState.selectedCards.delete(taskId);
            }

            appState.lastSelectedCardId = taskId;
            return;
        }

        // Normal click opens task details
        openTaskDetails(taskId);
    });

    // Right-click opens the card context menu. Shift+right-click keeps the browser menu;
    // touch long-press (Android fires contextmenu) is left alone so it can't fight drag.
    kanbanBoard.addEventListener("contextmenu", (e) => {
        if (e.shiftKey || e.pointerType === 'touch') return;
        const card = e.target.closest(".task-card");
        if (!card || !kanbanBoard.contains(card)) return;
        e.preventDefault();
        openKanbanCardContextMenu(e, card);
    });

    const columns = document.querySelectorAll(".kanban-column");

    columns.forEach((column) => {
        column.addEventListener("dragover", (e) => {
            e.preventDefault();
            column.classList.add('drag-over');
            column.style.backgroundColor = "var(--hover-bg)";
            
            if (!dragPlaceholder) return;
            
            // ⭐ KEY FIX: Get the actual tasks container, not the column wrapper
            const tasksContainer = column.querySelector('[id$="-tasks"]');
            if (!tasksContainer) return;
            const newStatus = resolveStatusFromColumn(column);
            if (!newStatus) return;
            
            // Get all non-dragging cards in this container
            const cards = Array.from(tasksContainer.querySelectorAll('.task-card:not(.dragging)'));
            const mouseY = e.clientY;
            
            // Remove placeholder from current position
            if (dragPlaceholder.parentNode) {
                dragPlaceholder.remove();
            }
            
            if (cards.length === 0) {
                // Empty column - append to tasks container
                tasksContainer.appendChild(dragPlaceholder);
            } else {
                // Find where to insert based on mouse position
                let insertBeforeCard = null;
                
                for (const card of cards) {
                    const rect = card.getBoundingClientRect();
                    const cardMiddle = rect.top + (rect.height / 2);
                    
                    if (mouseY < cardMiddle) {
                        insertBeforeCard = card;
                        break;
                    }
                }
                
                if (insertBeforeCard) {
                    tasksContainer.insertBefore(dragPlaceholder, insertBeforeCard);
                } else {
                    // Mouse is below all cards
                    tasksContainer.appendChild(dragPlaceholder);
                }
            }

            // Auto-scroll logic
            try {
                const scrollContainer = getScrollableAncestor(column);
                const y = e.clientY;
                const topDist = y;
                const bottomDist = window.innerHeight - y;
                if (topDist < SCROLL_ZONE) startAutoScroll('up', scrollContainer);
                else if (bottomDist < SCROLL_ZONE) startAutoScroll('down', scrollContainer);
                else stopAutoScroll();
            } catch (err) { stopAutoScroll(); }
        });

        column.addEventListener("dragleave", (e) => {
            if (!column.contains(e.relatedTarget)) {
                column.classList.remove('drag-over');
                column.style.backgroundColor = "var(--bg-tertiary)";
            }
        });

        column.addEventListener("drop", async (e) => {
            e.preventDefault();
            cleanupDragStyles();

            if (draggedTaskIds.length === 0) return;

            const newStatus = resolveStatusFromColumn(column);
            if (!newStatus) return;
            
            // ⭐ KEY FIX: Use the tasks container to get accurate position
            const tasksContainer = column.querySelector('[id$="-tasks"]');
            if (!tasksContainer) return;

            // Single-card drag: treat as reorder (enable manual mode)
            if (isSingleDrag) {
                // Ensure manual sorting mode is initialized from priority ordering when needed
                if (sortMode !== 'manual') {
                    sortMode = 'manual';
                    ['backlog','todo','progress','review','done'].forEach(st => {
                        // Using imported PRIORITY_ORDER
                        manualTaskOrder[st] = tasks
                            .filter(t => t.status === st)
                            .slice()
                            .sort((a,b) => (PRIORITY_ORDER[b.priority]||0) - (PRIORITY_ORDER[a.priority]||0))
                            .map(t => t.id);
                    });
                    updateSortUI();
                    saveSortPreferences();
                }

                // Remove dragged ids from any existing manual orders to avoid duplicates
                Object.keys(manualTaskOrder).forEach(st => {
                    if (!Array.isArray(manualTaskOrder[st])) return;
                    manualTaskOrder[st] = manualTaskOrder[st].filter(id => !draggedTaskIds.includes(id));
                });

                // Build the canonical ordered list of IDs for the destination column (excluding dragged cards)
                // Using imported PRIORITY_ORDER
                const currentColumnTasks = tasks.filter(t => t.status === newStatus && !draggedTaskIds.includes(t.id));

                let orderedIds;
                if (Array.isArray(manualTaskOrder[newStatus]) && manualTaskOrder[newStatus].length) {
                    const presentIds = new Set(currentColumnTasks.map(t => t.id));
                    orderedIds = manualTaskOrder[newStatus].filter(id => presentIds.has(id));

                    const missing = currentColumnTasks
                        .filter(t => !orderedIds.includes(t.id))
                        .slice()
                        .sort((a,b) => (PRIORITY_ORDER[b.priority]||0) - (PRIORITY_ORDER[a.priority]||0))
                        .map(t => t.id);
                    orderedIds = orderedIds.concat(missing);
                } else {
                    orderedIds = currentColumnTasks
                        .slice()
                        .sort((a,b) => (PRIORITY_ORDER[b.priority]||0) - (PRIORITY_ORDER[a.priority]||0))
                        .map(t => t.id);
                }

                // Find insertion position by anchoring to the VISIBLE neighbour cards.
                // orderedIds contains ALL tasks for this status (including ones hidden by an
                // active filter), but the placeholder sits among the visible cards only — so a
                // raw "count of visible cards before placeholder" index would land in the wrong
                // slot whenever a filter hides tasks. Anchor to neighbour task IDs instead.
                const insertIndex = computeManualInsertIndex(orderedIds, tasksContainer, dragPlaceholder);

                // Insert the dragged card at the calculated position
                orderedIds.splice(insertIndex, 0, ...draggedTaskIds);
                manualTaskOrder[newStatus] = orderedIds;

                const statusChangedTasks = [];
                draggedTaskIds.forEach(id => {
                    const t = tasks.find(x => x.id === id);
                    if (t) {
                        const statusChanged = t.status !== newStatus;
                        if (statusChanged) {
                            // Store old state for history
                            const oldTaskCopy = JSON.parse(JSON.stringify(t));

                            t.status = newStatus;
                            t.updatedAt = new Date().toISOString();

                            // Auto-set dates when status changes (if setting is enabled)
                            if (settings.autoSetStartDateOnStatusChange || settings.autoSetEndDateOnStatusChange) {
                                const today = new Date().toISOString().split('T')[0];
                                if (settings.autoSetStartDateOnStatusChange && newStatus === 'progress' && !t.startDate) {
                                    t.startDate = today;
                                }
                                if (settings.autoSetEndDateOnStatusChange && newStatus === 'done' && !t.endDate) {
                                    t.endDate = today;
                                }
                            }

                            applyCompletedDateTransition(t, oldTaskCopy.status);

                            // Record history for drag and drop changes
                            if (window.historyService) {
                                window.historyService.recordTaskUpdated(oldTaskCopy, t);
                            }

                            // Check and record project status change
                            if (t.projectId) {
                                checkAndRecordProjectStatusChange(t.projectId, t);
                            }

                            statusChangedTasks.push(t);
                        }
                    }
                });

                saveSortPreferences();
                clearSelectedCards();
                render(); // Update UI immediately for instant drag feedback
                cleanupDragStyles(); // Must run AFTER render() since columns persist in DOM
                const calendarView = document.getElementById("calendar-view");
                if (calendarView) renderCalendar();
                try { if (dragPlaceholder && dragPlaceholder.parentNode) dragPlaceholder.parentNode.removeChild(dragPlaceholder); } catch (err) {}
                dragPlaceholder = null;
                stopAutoScroll();

                // Reorders are already persisted by saveSortPreferences(); only save tasks whose status changed
                if (statusChangedTasks.length > 0) {
                    Promise.all(statusChangedTasks.map(t => saveTaskUpdate(t.id, {
                        status:              t.status,
                        completedDate:       t.completedDate  || null,
                        startDate:           t.startDate      || '',
                        endDate:             t.endDate        || '',
                        startDateWasEverSet: t.startDateWasEverSet,
                        endDateWasEverSet:   t.endDateWasEverSet,
                        updatedAt:           t.updatedAt,
                    }))).catch(error => {
                        console.error('Failed to save drag-and-drop changes:', error);
                        showErrorNotification(t('error.saveTaskPositionFailed'));
                    });
                }
            } else {
                // Multi-drag - same fix applies
                if (sortMode !== 'manual') {
                    sortMode = 'manual';
                    ['backlog','todo','progress','review','done'].forEach(st => {
                        // Using imported PRIORITY_ORDER
                        manualTaskOrder[st] = tasks
                            .filter(t => t.status === st)
                            .slice()
                            .sort((a,b) => (PRIORITY_ORDER[b.priority]||0) - (PRIORITY_ORDER[a.priority]||0))
                            .map(t => t.id);
                    });
                    updateSortUI();
                    saveSortPreferences();
                }

                Object.keys(manualTaskOrder).forEach(st => {
                    if (!Array.isArray(manualTaskOrder[st])) return;
                    manualTaskOrder[st] = manualTaskOrder[st].filter(id => !draggedTaskIds.includes(id));
                });

                // Using imported PRIORITY_ORDER
                const currentColumnTasks = tasks.filter(t => t.status === newStatus && !draggedTaskIds.includes(t.id));

                let orderedIds;
                if (Array.isArray(manualTaskOrder[newStatus]) && manualTaskOrder[newStatus].length) {
                    const presentIds = new Set(currentColumnTasks.map(t => t.id));
                    orderedIds = manualTaskOrder[newStatus].filter(id => presentIds.has(id));

                    const missing = currentColumnTasks
                        .filter(t => !orderedIds.includes(t.id))
                        .slice()
                        .sort((a,b) => (PRIORITY_ORDER[b.priority]||0) - (PRIORITY_ORDER[a.priority]||0))
                        .map(t => t.id);
                    orderedIds = orderedIds.concat(missing);
                } else {
                    orderedIds = currentColumnTasks
                        .slice()
                        .sort((a,b) => (PRIORITY_ORDER[b.priority]||0) - (PRIORITY_ORDER[a.priority]||0))
                        .map(t => t.id);
                }

                // Anchor to visible neighbour cards (same filter-aware fix as single-drag)
                const insertIndex = computeManualInsertIndex(orderedIds, tasksContainer, dragPlaceholder);

                orderedIds.splice(insertIndex, 0, ...draggedTaskIds);
                manualTaskOrder[newStatus] = orderedIds;

                const statusChangedTasksMulti = [];
                draggedTaskIds.forEach(id => {
                    const t = tasks.find(x => x.id === id);
                    if (t) {
                        const statusChanged = t.status !== newStatus;
                        if (statusChanged) {
                            // Store old state for history
                            const oldTaskCopy = JSON.parse(JSON.stringify(t));

                            t.status = newStatus;
                            t.updatedAt = new Date().toISOString();

                            // Auto-set dates when status changes (if setting is enabled)
                            if (settings.autoSetStartDateOnStatusChange || settings.autoSetEndDateOnStatusChange) {
                                const today = new Date().toISOString().split('T')[0];
                                if (settings.autoSetStartDateOnStatusChange && newStatus === 'progress' && !t.startDate) {
                                    t.startDate = today;
                                }
                                if (settings.autoSetEndDateOnStatusChange && newStatus === 'done' && !t.endDate) {
                                    t.endDate = today;
                                }
                            }

                            applyCompletedDateTransition(t, oldTaskCopy.status);

                            // Record history for drag and drop changes
                            if (window.historyService) {
                                window.historyService.recordTaskUpdated(oldTaskCopy, t);
                            }

                            // Check and record project status change
                            if (t.projectId) {
                                checkAndRecordProjectStatusChange(t.projectId, t);
                            }

                            statusChangedTasksMulti.push(t);
                        }
                    }
                });

                saveSortPreferences();
                clearSelectedCards();
                render(); // Update UI immediately for instant drag feedback
                cleanupDragStyles(); // Must run AFTER render() since columns persist in DOM
                const calendarView = document.getElementById("calendar-view");
                if (calendarView) renderCalendar();
                try { if (dragPlaceholder && dragPlaceholder.parentNode) dragPlaceholder.parentNode.removeChild(dragPlaceholder); } catch (err) {}
                dragPlaceholder = null;
                stopAutoScroll();

                // Reorders are already persisted by saveSortPreferences(); only save tasks whose status changed
                if (statusChangedTasksMulti.length > 0) {
                    Promise.all(statusChangedTasksMulti.map(t => saveTaskUpdate(t.id, {
                        status:              t.status,
                        completedDate:       t.completedDate  || null,
                        startDate:           t.startDate      || '',
                        endDate:             t.endDate        || '',
                        startDateWasEverSet: t.startDateWasEverSet,
                        endDateWasEverSet:   t.endDateWasEverSet,
                        updatedAt:           t.updatedAt,
                    }))).catch(error => {
                        console.error('Failed to save drag-and-drop changes:', error);
                        showErrorNotification(t('error.saveTaskPositionFailed'));
                    });
                }
            }
        });
    });

    document.addEventListener('dragend', () => stopAutoScroll());
}


function openProjectModal() {
    const modal = document.getElementById("project-modal");
    const descriptionEditor = document.getElementById("project-modal-description-editor");
    const descriptionHiddenField = document.getElementById("project-modal-description-hidden");

    // Clear temp project tags and color for new project
    window.tempProjectTags = [];
    window.tempProjectColor = PROJECT_COLORS[0];
    renderProjectTags([]);
    renderNewProjectColorPicker();

    // Reset template section to hidden
    const templateSection = document.getElementById('project-template-section');
    if (templateSection) templateSection.style.display = 'none';
    const templateBtn = document.getElementById('template-toggle-btn');
    if (templateBtn) templateBtn.classList.remove('active');

    // Clear editingProjectId since this is a new project
    document.getElementById('project-form').dataset.editingProjectId = '';

    // CRITICAL: Make modal visible FIRST (mobile browsers require visible inputs to accept values)
    modal.classList.add("active");

    if (descriptionEditor) {
        initializeProjectDescriptionEditor({
            editorSelector: descriptionEditor,
            hiddenSelector: descriptionHiddenField,
            content: "",
            placeholder: "",
            onUpdate: (text) => {
                if (descriptionHiddenField) descriptionHiddenField.value = text;
            },
            onBlur: (text) => {
                if (descriptionHiddenField) descriptionHiddenField.value = text;
            }
        });
        setProjectDescriptionText("");
        bindRichEditorLinkClicks(descriptionEditor);
    }

    // Use setTimeout to ensure modal is rendered and visible before setting date values
    setTimeout(() => {
        // Clear start date (allow user to optionally set it)
        document.querySelector('#project-form input[name="startDate"]').value = '';

        // Clear any existing flatpickr instances first
        const dateInputs = document.querySelectorAll('#project-modal input[type="date"]');
        dateInputs.forEach(input => {
            if (input._flatpickrInstance) {
                input._flatpickrInstance.destroy();
                input._flatpickrInstance = null;
                input._wrapped = false;
            }
        });

        // Initialize date pickers AFTER modal is visible and values are set
        initializeDatePickers();
    }, 150);

    // Reset scroll position AFTER modal is active and rendered
    setTimeout(() => {
        const modalBody = modal.querySelector('.modal-body');
        if (modalBody) modalBody.scrollTop = 0;
    }, 0);
}

// Task navigation functions
function updateTaskNavigationButtons() {
    const prevBtn = document.getElementById('task-nav-prev');
    const nextBtn = document.getElementById('task-nav-next');

    if (!prevBtn || !nextBtn) return;

    if (!currentTaskNavigationContext) {
        // No navigation context - hide buttons
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
        return;
    }

    const { taskIds, currentIndex } = currentTaskNavigationContext;

    // Show buttons
    prevBtn.style.display = 'flex';
    nextBtn.style.display = 'flex';

    // Enable/disable based on position
    prevBtn.disabled = currentIndex <= 0;
    nextBtn.disabled = currentIndex >= taskIds.length - 1;
}

function navigateToTask(direction) {
    if (!currentTaskNavigationContext) return;

    const { taskIds, currentIndex, projectId } = currentTaskNavigationContext;
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

    if (newIndex < 0 || newIndex >= taskIds.length) return;

    const newTaskId = taskIds[newIndex];

    // Create new navigation context with updated index
    const newContext = {
        projectId,
        taskIds,
        currentIndex: newIndex
    };

    // Open task with new context
    openTaskDetails(newTaskId, newContext);
}

function navigateToPreviousTask() {
    navigateToTask('prev');
}

function navigateToNextTask() {
    navigateToTask('next');
}

// Initialize task navigation event listeners (once)
if (!window.taskNavigationInitialized) {
    window.taskNavigationInitialized = true;

    document.addEventListener('DOMContentLoaded', () => {
        const prevBtn = document.getElementById('task-nav-prev');
        const nextBtn = document.getElementById('task-nav-next');

        if (prevBtn) {
            prevBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                navigateToPreviousTask();
            });
        }

        if (nextBtn) {
            nextBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                navigateToNextTask();
            });
        }

        // Keyboard shortcuts for task navigation
        document.addEventListener('keydown', (e) => {
            // Only handle if task modal is active
            const taskModal = document.getElementById('task-modal');
            if (!taskModal || !taskModal.classList.contains('active')) return;

            // Only handle if we have navigation context
            if (!currentTaskNavigationContext) return;

            // Ignore if user is typing in an input/textarea/contenteditable
            const target = e.target;
            if (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable) {
                return;
            }

            // Left arrow = previous task
            if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                navigateToPreviousTask();
            }

            // Right arrow = next task
            if (e.key === 'ArrowRight' && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                navigateToNextTask();
            }
        });
    });
}

function openTaskModal() {
    const modal = document.getElementById("task-modal");
    if (!modal) return;

    // Show the modal BEFORE the ~260 lines of setup below (tabs, flatpickr date
    // pickers, etc.). On iOS Chrome/Brave (WKWebView) a throw anywhere in that
    // setup left the modal invisible because `.active` was only added at the very
    // end. Showing first guarantees it opens; the later add is now idempotent.
    modal.classList.add("active");

    // Setup tag autocomplete listeners
    setupTagAutocompleteListeners();

    // Clear navigation context when opening new task modal
    currentTaskNavigationContext = null;

    // Reset tabs to General tab
    const generalTab = modal.querySelector('.modal-tab[data-tab="general"]');
    const detailsTab = modal.querySelector('.modal-tab[data-tab="details"]');
    const historyTab = modal.querySelector('.modal-tab[data-tab="history"]');
    const generalContent = modal.querySelector('#task-details-tab');
    const historyContent = modal.querySelector('#task-history-tab');

    // Activate General tab (both desktop and mobile)
    if (generalTab) generalTab.classList.add('active');
    if (detailsTab) detailsTab.classList.remove('active');
    if (historyTab) historyTab.classList.remove('active');
    if (generalContent) generalContent.classList.add('active');
    if (historyContent) historyContent.classList.remove('active');

    // Remove mobile tab state
    document.body.classList.remove('mobile-tab-details-active');

    // Hide History tab for new tasks (no history yet)
    if (historyTab) historyTab.style.display = 'none';

    const modalContent = modal.querySelector('.modal-content');
    if (modalContent) {
        modalContent.style.minHeight = '';
        modalContent.style.maxHeight = '';
    }

    // Re-initialize date pickers for task modal
    setTimeout(() => {
        // CRITICAL: Query by name, not type, since wrapped inputs become type="hidden"
        const dateInputs = modal.querySelectorAll('input[name="startDate"], input[name="endDate"]');
        dateInputs.forEach(input => {
            if (input._flatpickrInstance) {
                input._flatpickrInstance.destroy();
                input._flatpickrInstance = null;
            }
            input._wrapped = false;

            // Remove wrapper if it exists
            const wrapper = input.closest('.date-input-wrapper');
            if (wrapper) {
                const parent = wrapper.parentNode;
                parent.insertBefore(input, wrapper);
                wrapper.remove();
            }

            // Restore to type="date"
            input.type = "date";
            input.style.display = "";
        });
        initializeDatePickers();
    }, 150);

    // Title
    // NOTE: must exclude the rich-text description editor's own <h2> headings
    // (user-inserted via the H2 toolbar button) — a bare modal.querySelector("h2")
    // matches those first and silently overwrites task content.
    const titleEl = Array.from(modal.querySelectorAll("h2")).find(h => !h.closest("#task-description-editor"));
    if (titleEl) {
        titleEl.dataset.i18n = 'tasks.modal.createTitle';
        titleEl.textContent = t('tasks.modal.createTitle');
    }

    // Hide ❌ and ⋯ in create mode
    const closeBtn = modal.querySelector(".modal-close");
    if (closeBtn) closeBtn.style.display = "none";

    const optionsBtn = modal.querySelector("#task-options-btn");
    if (optionsBtn) optionsBtn.style.display = "none";

    // Show footer with "Create Task" and "Create Another"
    const footer = modal.querySelector("#task-footer");
    if (footer) footer.style.display = "flex";
    const createAnotherWrap = modal.querySelector('#task-create-another-wrap');
    if (createAnotherWrap) {
        createAnotherWrap.style.display = pendingRecurrenceAfterTaskCreate ? 'none' : '';
    }
    window.taskCreateAnotherProjectId = null;

    // DEBUG: Log modal dimensions
    setTimeout(() => {
        const modalContent = modal.querySelector('.modal-content');
        const modalBody = modal.querySelector('.modal-body');
        const modalFooter = modal.querySelector('.modal-footer');

        if (modalContent && modalFooter) {
            const contentRect = modalContent.getBoundingClientRect();
            const footerRect = modalFooter.getBoundingClientRect();
            const contentStyle = window.getComputedStyle(modalContent);
            const footerStyle = window.getComputedStyle(modalFooter);

            console.log('=== MODAL DEBUG ===');
            console.log('Viewport:', window.innerWidth, 'x', window.innerHeight);
            console.log('Zoom:', window.devicePixelRatio * 100 + '%');
            console.log('');
            console.log('MODAL-CONTENT:');
            console.log('  Position:', contentRect.top.toFixed(1), 'to', contentRect.bottom.toFixed(1));
            console.log('  Height:', contentRect.height.toFixed(1), '/', contentStyle.height, '/', contentStyle.maxHeight);
            console.log('  Padding:', contentStyle.padding);
            console.log('  Display:', contentStyle.display);
            console.log('  Overflow:', contentStyle.overflow);
            console.log('');
            console.log('MODAL-FOOTER:');
            console.log('  Position:', footerRect.top.toFixed(1), 'to', footerRect.bottom.toFixed(1));
            console.log('  Height:', footerRect.height.toFixed(1));
            console.log('  Flex-shrink:', footerStyle.flexShrink);
            console.log('  Padding:', footerStyle.padding);
            console.log('');
            const overflow = footerRect.bottom - contentRect.bottom;
            if (overflow > 1) {
                console.error('❌ FOOTER OVERFLOW:', overflow.toFixed(1) + 'px OUTSIDE modal-content');
            } else {
                console.log('\u{2705} Footer is inside modal-content');
            }
            console.log('==================');
        }
    }, 100);

    // Reset editing mode and clear fields (preserve "Create another" checkbox state)
    const form = modal.querySelector("#task-form");
    if (form) {
        const createAnotherChk = document.getElementById('task-create-another-chk');
        const wasCreateAnother = !!(createAnotherChk?.checked && !pendingRecurrenceAfterTaskCreate);
        delete form.dataset.editingTaskId;
        form.reset(); // This properly clears all form fields when opening
        if (createAnotherChk && wasCreateAnother) createAnotherChk.checked = true;
        if (createAnotherChk && pendingRecurrenceAfterTaskCreate) createAnotherChk.checked = false;
    }

    // Clear additional fields that might not be cleared by form.reset()
    clearTaskDescriptionEditor();

    // Clear tag input field
    const tagInput = modal.querySelector("#tag-input");
    if (tagInput) tagInput.value = "";

    const descHidden = modal.querySelector("#task-description-hidden");
    if (descHidden) descHidden.value = getTaskDescriptionHTML();

    // Reset custom Project field to default (no project)
    const hiddenProject = modal.querySelector('#hidden-project');
    if (hiddenProject) hiddenProject.value = "";
    const projectCurrentBtn = modal.querySelector('#project-current .project-text');
    if (projectCurrentBtn) projectCurrentBtn.textContent = t('tasks.project.selectPlaceholder');
    updateTaskProjectOpenBtn('');
    // Ensure floating portal is closed if it was open
    if (typeof hideProjectDropdownPortal === 'function') hideProjectDropdownPortal();

    // Reset priority
    const hiddenPriority = modal.querySelector("#hidden-priority");
    if (hiddenPriority) hiddenPriority.value = "medium";
    const priorityCurrentBtn = modal.querySelector("#priority-current");
    if (priorityCurrentBtn) {
        const priorityLabel = getPriorityLabel("medium").toUpperCase();
        priorityCurrentBtn.innerHTML = `<span class="priority-pill priority-medium">${priorityLabel}</span> <span class="dropdown-arrow">▼</span>`;
        updatePriorityOptions("medium");
    }

    // Reset link type dropdown to "relates to"
    const linkTypeValue = modal.querySelector("#link-type-value");
    if (linkTypeValue) linkTypeValue.value = "relates_to";
    const linkTypeLabel = modal.querySelector("#link-type-label");
    if (linkTypeLabel) linkTypeLabel.setAttribute('data-i18n', 'tasks.links.relatesTo');
    if (linkTypeLabel) linkTypeLabel.textContent = t('tasks.links.relatesTo');
    // Hide web link inputs, show task search
    const webLinkInputs = modal.querySelector("#web-link-inputs");
    const taskLinkSearch = modal.querySelector("#task-link-search");
    if (webLinkInputs) webLinkInputs.style.display = "none";
    if (taskLinkSearch) taskLinkSearch.style.display = "block";

    // Reset status (default for new tasks). New tasks always start in To Do;
    // Backlog is an explicit choice for work parked long-term.
    const defaultStatus = 'todo';

    const hiddenStatus = modal.querySelector("#hidden-status");
    if (hiddenStatus) hiddenStatus.value = defaultStatus;

    const currentBtn = modal.querySelector("#status-current");
    if (currentBtn) {
        const statusBadge = currentBtn.querySelector(".status-badge");
        if (statusBadge) {
            statusBadge.className = "status-badge " + defaultStatus;
            statusBadge.textContent = getStatusLabel(defaultStatus);
        }
    }

    // Clear modal-only draft state. The task-list filterState belongs to the
    // background view and must survive opening, saving, and closing this modal.
    tempAttachments = [];
    window.tempTags = [];
    window.tempLinks = [];
    renderAttachments([]);
    renderTags([]);
    renderTempLinks();

    // MOBILE: Reset field organization for new task - all Details fields should be in Details tab
    if (getIsMobileCached()) {
        // Clear initial date state for new tasks - dates start in Details
        modal.dataset.initialStartDate = 'false';
        modal.dataset.initialEndDate = 'false';

        const tagsGroup = modal.querySelector('.form-group:has(#tag-input)');
        const startDateGroup = modal.querySelector('.form-group:has([name="startDate"])');
        const endDateGroup = modal.querySelector('.form-group:has([name="endDate"])');
        const linksGroup = modal.querySelector('.form-group:has(#attachments-links-list)');

        // Ensure all Details fields are in Details tab for new tasks
        if (tagsGroup) {
            tagsGroup.classList.remove('mobile-general-field');
            tagsGroup.classList.add('mobile-details-field');
        }
        if (startDateGroup) {
            startDateGroup.classList.remove('mobile-general-field');
            startDateGroup.classList.add('mobile-details-field');
        }
        if (endDateGroup) {
            endDateGroup.classList.remove('mobile-general-field');
            endDateGroup.classList.add('mobile-details-field');
        }
        if (linksGroup) {
            linksGroup.classList.remove('mobile-general-field');
            linksGroup.classList.add('mobile-details-field');
        }

        // Show Details tab for new tasks
        if (detailsTab) {
            detailsTab.classList.remove('hide-details-tab');
        }
    }

    // Explicitly clear date pickers to prevent dirty form state
    const hiddenStart = modal.querySelector('#task-form input[name="startDate"]');
    if (hiddenStart) {
        const fp = hiddenStart._flatpickrInstance;
        if (fp) {
            fp.clear();
            fp.jumpToDate(new Date());
        }
        hiddenStart.value = "";
        const displayStart = hiddenStart.parentElement
            ? hiddenStart.parentElement.querySelector("input.date-display")
            : null;
        if (displayStart) displayStart.value = "";
    }

    const hiddenEnd = modal.querySelector('#task-form input[name="endDate"]');
    if (hiddenEnd) {
        const fp = hiddenEnd._flatpickrInstance;
        if (fp) {
            fp.clear();
            fp.jumpToDate(new Date());
        }
        hiddenEnd.value = "";
        const displayEnd = hiddenEnd.parentElement
            ? hiddenEnd.parentElement.querySelector("input.date-display")
            : null;
        if (displayEnd) displayEnd.value = "";
    }

    modal.classList.add("active");

    // Reset scroll position AFTER modal is active and rendered
    setTimeout(() => {
        const modalBody = modal.querySelector('.modal-body');
        if (modalBody) modalBody.scrollTop = 0;
    }, 0);

    setTimeout(() => {
        initializeDatePickers();
        // Capture initial state after all defaults are set
        captureInitialTaskFormState();
    }, 100);

}



function closeModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    // If closing settings modal and there are unsaved changes, show confirmation instead
    if (modalId === 'settings-modal' && window.settingsFormIsDirty) {
        showUnsavedChangesModal(modalId);
        return;
    }

    if (modalId === 'ask-kai-modal') {
        requestAskKaiModalClose({ preserveDraft: true });
        return;
    }

    // Call specific close functions for modals that need cleanup
    if (modalId === 'import-data-modal') {
        closeImportDataModal();
        return;
    }
    if (modalId === 'delete-account-modal') {
        closeDeleteAccountModal();
        return;
    }
    if (modalId === 'docs-trash-modal') {
        closeDocsTrashModal();
        return;
    }
    if (modalId === 'docs-trash-preview-modal') {
        closeDocsTrashPreviewModal();
        return;
    }
    if (modalId === 'docs-share-modal') {
        const shareModal = document.getElementById('docs-share-modal');
        if (shareModal) shareModal.style.display = 'none';
        return;
    }

    modal.classList.remove("active");

    // Close any floating portals associated with Settings
    if (modalId === 'settings-modal') {
        if (typeof hideNotificationTimePortal === 'function') hideNotificationTimePortal();
        if (typeof hideNotificationTimeZonePortal === 'function') hideNotificationTimeZonePortal();
    }

    // Clear any task-modal height pinning used for History tab stability
    if (modalId === 'task-modal') {
        try {
            const content = modal.querySelector('.modal-content');
            if (content) content.style.minHeight = '';
        } catch (e) {}
        // Drop the elevation used when the task was opened over the Kai drawer.
        modal.classList.remove('task-modal--over-kai');
    }

    // Only reset forms when manually closing (not after successful submission)
    // This prevents clearing user input after task creation
    if (modalId === "project-modal") {
        document.getElementById("project-form").reset();
        const activeModalProjectEditor = document.querySelector("#project-modal-description-editor.tiptap, #project-modal-description-editor.ProseMirror");
        if (activeModalProjectEditor) {
            clearProjectDescriptionEditor();
            destroyProjectDescriptionEditor();
        }
    }
}

// Separate function for manually closing task modal with reset
function closeTaskModal() {
    const shouldReturnToRecurrence = !!pendingRecurrenceAfterTaskCreate;
    // If there are unsaved fields in NEW task, show confirmation instead
    if (hasUnsavedNewTask && hasUnsavedNewTask()) {
        if (shouldReturnToRecurrence) {
            showUnsavedChangesModal({
                modalId: 'task-modal',
                onDiscard: () => finalizeCloseTaskModal({ returnToRecurrence: true })
            });
        } else {
            showUnsavedChangesModal('task-modal');
        }
        return;
    }
    finalizeCloseTaskModal({ returnToRecurrence: shouldReturnToRecurrence });
}

document
    .getElementById("project-form")
    .addEventListener("submit", async function (e) {
        e.preventDefault();
        const submitTimer = debugTimeStart("projects", "submit", {
            projectCount: projects.length,
            tagCount: (window.tempProjectTags || []).length
        });
        const formData = new FormData(e.target);

        // Get tags from temp storage or empty array
        const tags = window.tempProjectTags || [];

        if (!_storageHealthy) { showErrorNotification(t('error.cfOutageBlocked')); return; }

        // Disable submit button to prevent double-submission during save+retry window
        const projectSubmitBtn = e.target.querySelector('[type="submit"]');
        if (projectSubmitBtn) projectSubmitBtn.disabled = true;

        // Save previous state for rollback on failure (deep clone for nested objects)
        const previousProjects = JSON.parse(JSON.stringify(projects));
        const previousProjectCounter = projectCounter;
        let previousTasks = JSON.parse(JSON.stringify(tasks));
        let previousTaskCounter = taskCounter;

        // Use project service to create project
        const result = createProjectService({
            name: formData.get("name"),
            description: formData.get("description"),
            startDate: formData.get("startDate"),
            endDate: formData.get("endDate"),
            tags: tags
        }, projects, projectCounter);

        projects = result.projects;
        projectCounter = result.projectCounter;
        const project = result.project;

        // Apply chosen color for new project (if user picked one)
        if (window.tempProjectColor) {
            setProjectColor(project.id, window.tempProjectColor).catch(() => {});
        }

        // Apply template tasks if a template was selected
        const templateSelect = document.getElementById('project-template-select');
        const selectedTemplateId = templateSelect ? templateSelect.value : '';
        const newTemplateTasks = [];
        if (selectedTemplateId) {
            const tpl = templates.find(t => t.id === selectedTemplateId);
            if (tpl) {
                // Build task name transform from naming section
                const namingMode = document.getElementById('project-task-naming-mode')?.value || 'none';
                let taskNameTransform = (name) => name;
                if (namingMode === 'prefix') {
                    const prefix = document.getElementById('project-task-prefix-input')?.value || '';
                    if (prefix) taskNameTransform = (name) => `${prefix}${name}`;
                } else if (namingMode === 'suffix') {
                    const suffix = document.getElementById('project-task-suffix-input')?.value || '';
                    if (suffix) taskNameTransform = (name) => `${name}${suffix}`;
                }

                // Apply template tasks
                if (tpl.tasks && tpl.tasks.length > 0) {
                    tpl.tasks.forEach(tplTask => {
                        const taskResult = createTaskService({
                            title: taskNameTransform(tplTask.title),
                            description: tplTask.description,
                            priority: tplTask.priority,
                            status: 'backlog',
                            tags: Array.isArray(tplTask.tags) ? [...tplTask.tags] : [],
                            projectId: project.id,
                            startDate: '',
                            endDate: ''
                        }, tasks, taskCounter);
                        tasks = taskResult.tasks;
                        taskCounter = taskResult.taskCounter;
                        if (taskResult.task) newTemplateTasks.push(taskResult.task);
                    });
                }
                // Apply template project tags (merge with any user-entered tags)
                if (Array.isArray(tpl.tags) && tpl.tags.length > 0) {
                    const existingTags = Array.isArray(project.tags) ? project.tags : [];
                    project.tags = [...new Set([...existingTags, ...tpl.tags])];
                }
            }
            // Reset dropdown
            if (templateSelect) templateSelect.value = '';
        }

        // Save with retry — UI commits only after persistence succeeds, modal stays open on failure
        const MAX_RETRIES = 3;
        const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s
        let saveError = null;
        
        async function saveWithRetry(saveFn, operationName) {
            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                try {
                    await saveFn();
                    return true; // Success
                } catch (err) {
                    saveError = err;
                    console.error(`${operationName} attempt ${attempt + 1} failed:`, err);
                    
                    if (attempt < MAX_RETRIES - 1) {
                        // Show retry notification
                        const retryMsg = t('error.saveRetry') || `Retrying ${operationName.toLowerCase()}... (${attempt + 2}/${MAX_RETRIES})`;
                        showErrorNotification(retryMsg, 3000);
                        
                        // Wait before retry with exponential backoff
                        await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
                    }
                }
            }
            return false; // All retries failed
        }
        
        try {
            // Save projects with retry
            // Insert only the new row. saveProjects() rewrites the whole list (delete-all +
            // reinsert) and would erase projects added on other devices since this tab loaded.
            const projectsSaved = await saveWithRetry(() => saveProjectCreate(project), 'Save Projects');
            if (!projectsSaved) {
                throw saveError || new Error('Failed to save projects after retries');
            }
            
            // Also save template tasks if a template was selected (row-level inserts)
            if (selectedTemplateId && newTemplateTasks.length > 0) {
                const tasksSaved = await saveWithRetry(
                    () => Promise.all(newTemplateTasks.map(task => saveTaskCreate(task))),
                    'Save Tasks'
                );
                if (!tasksSaved) {
                    throw saveError || new Error('Failed to save tasks after retries');
                }
            }
            
            // Record history ONLY after successful persistence
            if (window.historyService) {
                window.historyService.recordProjectCreated(project);
            }

            // Only AFTER successful save: close modal, reset form, navigate
            appState.projectsSortedView = null;
            closeModal("project-modal");
            e.target.reset();
            setSelectedTemplate('', t('projects.modal.templateNone'));
            window.location.hash = 'projects';
            showPage('projects');
            render();
        } catch (err) {
            // Rollback to previous state on failure — modal stays open so user can retry
            console.error('Failed to save project after retries:', err);
            projects = previousProjects;
            projectCounter = previousProjectCounter;
            tasks = previousTasks;
            taskCounter = previousTaskCounter;

            const finalErrorMsg = t('error.saveProjectFailed') || 'Failed to save project. Please check your connection and try again.';
            showErrorNotification(finalErrorMsg);
            return;
        } finally {
            // Cleanup temp state and re-enable submit on both success and failure
            window.tempProjectTags = [];
            if (projectSubmitBtn) projectSubmitBtn.disabled = false;
        }
        debugTimeEnd("projects", submitTimer, {
            projectCount: projects.length
        });
    });

// Full-screen overlay shown after credential changes, then signs out
/**
 * Asks for the current PIN/password before a sensitive account action (email change,
 * account deletion) and runs `attempt(credentialBody)` with it. `attempt` must return
 * { resp, data } from the API call. The modal stays open on a wrong credential or a
 * rate limit so the user can retry; it closes on any other outcome.
 * Resolves with the final { resp, data }, or null if the user cancels.
 */
function requestCurrentCredential(hintKey, attempt) {
    return new Promise((resolve) => {
        const authMethod = window.authSystem?.getCurrentUser?.()?.authMethod || 'pin';
        const isPin = authMethod !== 'password';
        const modal = document.createElement('div');
        modal.className = 'modal active';
        modal.id = 'confirm-identity-modal-temp';
        modal.innerHTML = `
            <div class="modal-content reset-pin-modal-content">
                <div class="reset-pin-modal-inner">
                    <div class="reset-pin-header">
                        <div>
                            <h2 class="reset-pin-title" data-i18n="settings.confirmIdentity.title">${escapeHtml(t('settings.confirmIdentity.title'))}</h2>
                            <p class="reset-pin-description" data-i18n="${hintKey}">${escapeHtml(t(hintKey))}</p>
                        </div>
                    </div>
                    <form id="confirm-identity-form" class="reset-pin-form">
                        <div class="reset-pin-field">
                            <label class="reset-pin-label" for="confirm-identity-input" data-i18n="${isPin ? 'settings.confirmIdentity.pinLabel' : 'settings.confirmIdentity.passwordLabel'}">${escapeHtml(t(isPin ? 'settings.confirmIdentity.pinLabel' : 'settings.confirmIdentity.passwordLabel'))}</label>
                            <input
                                type="password"
                                id="confirm-identity-input"
                                class="reset-pin-input"
                                ${isPin ? 'maxlength="4" inputmode="numeric" placeholder="••••" autocomplete="off"' : 'autocomplete="current-password"'}
                                required
                            />
                            <div id="confirm-identity-error" class="reset-pin-error" style="display: none;"></div>
                        </div>
                        <div class="reset-pin-actions">
                            <button type="button" class="reset-pin-btn reset-pin-btn-cancel" id="confirm-identity-cancel" data-i18n="common.cancel">${escapeHtml(t('common.cancel'))}</button>
                            <button type="submit" class="reset-pin-btn reset-pin-btn-primary" id="confirm-identity-submit" data-i18n="settings.confirmIdentity.confirm">${escapeHtml(t('settings.confirmIdentity.confirm'))}</button>
                        </div>
                    </form>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
        if (!isPin && window.applyPasswordToggles) window.applyPasswordToggles(modal);

        const input = modal.querySelector('#confirm-identity-input');
        const errorEl = modal.querySelector('#confirm-identity-error');
        const submitBtn = modal.querySelector('#confirm-identity-submit');
        const showError = (key) => {
            errorEl.textContent = t(key);
            errorEl.style.display = 'block';
            input.focus();
        };
        const finish = (value) => {
            modal.remove();
            resolve(value);
        };

        modal.querySelector('#confirm-identity-cancel').addEventListener('click', () => finish(null));
        modal.querySelector('#confirm-identity-form').addEventListener('submit', async (e) => {
            e.preventDefault();
            errorEl.style.display = 'none';
            const value = input.value;
            if (!value || (isPin && !/^\d{4}$/.test(value))) {
                showError('settings.confirmIdentity.required');
                return;
            }

            submitBtn.disabled = true;
            try {
                const result = await attempt(isPin ? { currentPin: value } : { currentPassword: value });
                const code = result?.data?.code;
                if (result?.resp?.status === 401 && code === 'CURRENT_CREDENTIAL_INVALID') {
                    input.value = '';
                    showError('settings.confirmIdentity.incorrect');
                    return;
                }
                if (result?.resp?.status === 429) {
                    showError('settings.confirmIdentity.tooManyAttempts');
                    return;
                }
                finish(result);
            } catch (error) {
                modal.remove();
                resolve({ resp: null, data: {}, error });
            } finally {
                submitBtn.disabled = false;
            }
        });
        input.focus();
    });
}

function showCredentialChangeOverlay(message) {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;background:var(--bg-primary);opacity:0;transition:opacity 0.3s;';
    overlay.innerHTML = `
        <div style="text-align:center;max-width:360px;padding:32px;">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom:16px;">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <h2 style="margin:0 0 8px;color:var(--text-primary);font-size:20px;font-weight:600;">${message}</h2>
            <p style="margin:0;color:var(--text-secondary);font-size:14px;">Signing out&hellip;</p>
        </div>
    `;
    document.body.appendChild(overlay);
    requestAnimationFrame(() => { overlay.style.opacity = '1'; });
    setTimeout(() => {
        if (window.authSystem?.logout) window.authSystem.logout();
        else location.reload();
    }, 1500);
}

// Reset PIN handler
function resetPINFlow() {
    // Create a modal for PIN reset - ask for current PIN and new PIN
    const resetPinModal = document.createElement('div');
    resetPinModal.className = 'modal active';
    resetPinModal.id = 'reset-pin-modal-temp';
    resetPinModal.innerHTML = `
        <div class="modal-content reset-pin-modal-content">
            <div class="reset-pin-modal-inner">
                <div class="reset-pin-header">
                    <div>
                        <h2 class="reset-pin-title">Reset PIN</h2>
                        <p class="reset-pin-description">Verify your current PIN to set a new one</p>
                    </div>
                </div>
                
                <form id="reset-pin-form" class="reset-pin-form">
                    <div class="reset-pin-field">
                        <label class="reset-pin-label">Current PIN</label>
                        <input
                            type="password"
                            id="current-pin-input"
                            maxlength="4"
                            placeholder="••••"
                            class="reset-pin-input"
                            inputmode="numeric"
                            autocomplete="off"
                            required
                        />
                        <div id="current-pin-error" class="reset-pin-error" style="display: none;"></div>
                    </div>

                    <div class="reset-pin-actions">
                        <button type="button" class="reset-pin-btn reset-pin-btn-cancel" data-action="removeElementById" data-param="reset-pin-modal-temp">
                            Cancel
                        </button>
                        <button type="submit" class="reset-pin-btn reset-pin-btn-primary">
                            Continue
                        </button>
                    </div>
                </form>
            </div>
        </div>
    `;
    
    document.body.appendChild(resetPinModal);
    
    // Add form handler
    document.getElementById('reset-pin-form').addEventListener('submit', function(e) {
        e.preventDefault();
        const currentPin = document.getElementById('current-pin-input').value.trim();
        const errorEl = document.getElementById('current-pin-error');

        // Clear previous errors
        errorEl.style.display = 'none';
        errorEl.textContent = '';

        if (!currentPin || currentPin.length !== 4) {
            errorEl.textContent = 'PIN must be 4 digits';
            errorEl.style.display = 'block';
            return;
        }

        if (!/^\d{4}$/.test(currentPin)) {
            errorEl.textContent = 'PIN must contain only digits';
            errorEl.style.display = 'block';
            return;
        }

        // Remove current modal and show new PIN entry
        document.getElementById('reset-pin-modal-temp').remove();
        showNewPinEntry(currentPin);
    });
    
    document.getElementById('current-pin-input').focus();
}

function showNewPinEntry(currentPin) {
    const newPinModal = document.createElement('div');
    newPinModal.className = 'modal active';
    newPinModal.id = 'new-pin-modal-temp';
    newPinModal.innerHTML = `
        <div class="modal-content reset-pin-modal-content">
            <div class="reset-pin-modal-inner">
                <div class="reset-pin-header">
                    <div>
                        <h2 class="reset-pin-title">Set New PIN</h2>
                        <p class="reset-pin-description">Create your new 4-digit PIN</p>
                    </div>
                </div>
                
                <form id="new-pin-form" class="reset-pin-form">
                    <div class="reset-pin-field">
                        <label class="reset-pin-label">New PIN</label>
                        <input 
                            type="password" 
                            id="new-pin-input" 
                            maxlength="4" 
                            placeholder="••••"
                            class="reset-pin-input"
                            inputmode="numeric"
                            autocomplete="off"
                            required
                        />
                    </div>
                    
                    <div class="reset-pin-field">
                        <label class="reset-pin-label">Confirm PIN</label>
                        <input
                            type="password"
                            id="confirm-pin-input"
                            maxlength="4"
                            placeholder="••••"
                            class="reset-pin-input"
                            inputmode="numeric"
                            autocomplete="off"
                            required
                        />
                        <div id="new-pin-error" class="reset-pin-error" style="display: none;"></div>
                    </div>

                    <div class="reset-pin-actions">
                        <button type="button" class="reset-pin-btn reset-pin-btn-cancel" data-action="removeElementById" data-param="new-pin-modal-temp">
                            Cancel
                        </button>
                        <button type="submit" class="reset-pin-btn reset-pin-btn-primary">
                            Reset PIN
                        </button>
                    </div>
                </form>
            </div>
        </div>
    `;
    
    document.body.appendChild(newPinModal);
    
    document.getElementById('new-pin-form').addEventListener('submit', function(e) {
        e.preventDefault();
        const newPin = document.getElementById('new-pin-input').value.trim();
        const confirmPin = document.getElementById('confirm-pin-input').value.trim();
        const errorEl = document.getElementById('new-pin-error');

        // Clear previous errors
        errorEl.style.display = 'none';
        errorEl.textContent = '';

        if (!newPin || newPin.length !== 4) {
            errorEl.textContent = 'New PIN must be 4 digits';
            errorEl.style.display = 'block';
            return;
        }

        if (!/^\d{4}$/.test(newPin)) {
            errorEl.textContent = 'PIN must contain only digits';
            errorEl.style.display = 'block';
            return;
        }

        if (newPin !== confirmPin) {
            errorEl.textContent = 'PINs do not match';
            errorEl.style.display = 'block';
            return;
        }

        // Call the API to reset the PIN
        submitPINReset(currentPin, newPin);
    });
    
    document.getElementById('new-pin-input').focus();
}

async function submitPINReset(currentPin, newPin) {
    try {
        const token = localStorage.getItem('authToken');
        if (!token) {
            showErrorNotification(t('error.notLoggedInResetPin'));
            return;
        }
        
        const response = await fetch('/api/auth/change-pin', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                currentPin: currentPin,
                newPin: newPin
            })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            showErrorNotification(data.error || t('error.resetPinFailed'));
            return;
        }
        
        // Remove modal
        const modal = document.getElementById('new-pin-modal-temp');
        if (modal) modal.remove();
        closeSettings();
        showCredentialChangeOverlay(t('success.resetPin'));
        
    } catch (error) {
        console.error('PIN reset error:', error);
        showErrorNotification(t('error.resetPinError'));
    }
}

// Change password flow (for users already on password auth)
function changePasswordFlow() {
    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'change-password-modal-temp';
    modal.innerHTML = `
        <div class="modal-content reset-pin-modal-content">
            <div class="reset-pin-modal-inner">
                <div class="reset-pin-header">
                    <div>
                        <h2 class="reset-pin-title">${t('settings.changePassword.title')}</h2>
                    </div>
                </div>
                <form id="change-password-form" class="reset-pin-form">
                    <div class="reset-pin-field">
                        <label class="reset-pin-label">${t('settings.changePassword.currentLabel')}</label>
                        <input type="password" id="change-pw-current" class="reset-pin-input" autocomplete="current-password" required />
                        <div id="change-pw-error" class="reset-pin-error" style="display: none;"></div>
                    </div>
                    <div class="reset-pin-field">
                        <label class="reset-pin-label">${t('settings.changePassword.newLabel')}</label>
                        <input type="password" id="change-pw-new" class="reset-pin-input" autocomplete="new-password" placeholder="${t('auth.setup.newPasswordPlaceholder')}" required />
                    </div>
                    <div class="reset-pin-field">
                        <label class="reset-pin-label">${t('settings.changePassword.confirmLabel')}</label>
                        <input type="password" id="change-pw-confirm" class="reset-pin-input" autocomplete="new-password" required />
                        <div id="change-pw-new-error" class="reset-pin-error" style="display: none;"></div>
                    </div>
                    <div class="reset-pin-actions">
                        <button type="button" class="reset-pin-btn reset-pin-btn-cancel" data-action="removeElementById" data-param="change-password-modal-temp">${t('common.cancel')}</button>
                        <button type="submit" class="reset-pin-btn reset-pin-btn-primary">${t('settings.changePasswordButton')}</button>
                    </div>
                </form>
            </div>
        </div>
    `;
    document.body.appendChild(modal);
    if (window.applyPasswordToggles) window.applyPasswordToggles(modal);

    document.getElementById('change-password-form').addEventListener('submit', async function(e) {
        e.preventDefault();
        const currentPw = document.getElementById('change-pw-current').value;
        const newPw = document.getElementById('change-pw-new').value;
        const confirmPw = document.getElementById('change-pw-confirm').value;
        const errorEl = document.getElementById('change-pw-error');
        const newErrorEl = document.getElementById('change-pw-new-error');
        errorEl.style.display = 'none';
        newErrorEl.style.display = 'none';

        if (!currentPw) {
            errorEl.textContent = t('settings.switchAuth.verifyCurrentPassword');
            errorEl.style.display = 'block';
            return;
        }
        // Client-side password validation
        const pwRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
        if (!pwRegex.test(newPw)) {
            newErrorEl.textContent = t('auth.setup.passwordRequirements');
            newErrorEl.style.display = 'block';
            return;
        }
        if (newPw !== confirmPw) {
            newErrorEl.textContent = 'Passwords do not match';
            newErrorEl.style.display = 'block';
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch('/api/auth/change-credential', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw, newAuthMethod: 'password' })
            });
            const data = await response.json();
            if (!response.ok) {
                errorEl.textContent = data.error || 'Failed to change password';
                errorEl.style.display = 'block';
                return;
            }
            document.getElementById('change-password-modal-temp').remove();
            closeModal('settings-modal');
            showCredentialChangeOverlay(t('settings.changePassword.success'));
        } catch (err) {
            console.error('Change password error:', err);
            errorEl.textContent = 'An error occurred';
            errorEl.style.display = 'block';
        }
    });
    document.getElementById('change-pw-current').focus();
}

// Switch authentication method flow (PIN→Password or Password→PIN)
function switchAuthMethodFlow() {
    const cu = window.authSystem?.getCurrentUser?.();
    const currentMethod = cu?.authMethod || 'pin';
    const targetMethod = currentMethod === 'pin' ? 'password' : 'pin';

    const modal = document.createElement('div');
    modal.className = 'modal active';
    modal.id = 'switch-auth-modal-temp';

    const verifyLabel = currentMethod === 'pin'
        ? t('settings.switchAuth.verifyCurrentPin')
        : t('settings.switchAuth.verifyCurrentPassword');

    // Build new credential fields based on target
    let newCredentialFields = '';
    if (targetMethod === 'password') {
        newCredentialFields = `
            <div class="reset-pin-field">
                <label class="reset-pin-label">${t('settings.switchAuth.enterNewPassword')}</label>
                <input type="password" id="switch-new-credential" class="reset-pin-input" autocomplete="new-password" placeholder="${t('auth.setup.newPasswordPlaceholder')}" required />
            </div>
            <div class="reset-pin-field">
                <label class="reset-pin-label">${t('settings.switchAuth.confirmNewPassword')}</label>
                <input type="password" id="switch-confirm-credential" class="reset-pin-input" autocomplete="new-password" required />
                <div id="switch-new-error" class="reset-pin-error" style="display: none;"></div>
            </div>`;
    } else {
        newCredentialFields = `
            <div class="reset-pin-field">
                <label class="reset-pin-label">${t('settings.switchAuth.enterNewPin')}</label>
                <input type="password" id="switch-new-credential" class="reset-pin-input" maxlength="4" inputmode="numeric" autocomplete="off" placeholder="••••" required />
            </div>
            <div class="reset-pin-field">
                <label class="reset-pin-label">${t('settings.switchAuth.confirmNewPin')}</label>
                <input type="password" id="switch-confirm-credential" class="reset-pin-input" maxlength="4" inputmode="numeric" autocomplete="off" placeholder="••••" required />
                <div id="switch-new-error" class="reset-pin-error" style="display: none;"></div>
            </div>`;
    }

    modal.innerHTML = `
        <div class="modal-content reset-pin-modal-content">
            <div class="reset-pin-modal-inner">
                <div class="reset-pin-header">
                    <div>
                        <h2 class="reset-pin-title">${t('settings.switchAuth.title')}</h2>
                    </div>
                </div>
                <form id="switch-auth-form" class="reset-pin-form">
                    <div class="reset-pin-field">
                        <label class="reset-pin-label">${verifyLabel}</label>
                        <input type="${currentMethod === 'pin' ? 'password' : 'password'}" id="switch-current-credential" class="reset-pin-input"
                            ${currentMethod === 'pin' ? 'maxlength="4" inputmode="numeric" autocomplete="off" placeholder="••••"' : 'autocomplete="current-password"'} required />
                        <div id="switch-current-error" class="reset-pin-error" style="display: none;"></div>
                    </div>
                    ${newCredentialFields}
                    <div class="reset-pin-actions">
                        <button type="button" class="reset-pin-btn reset-pin-btn-cancel" data-action="removeElementById" data-param="switch-auth-modal-temp">${t('common.cancel')}</button>
                        <button type="submit" class="reset-pin-btn reset-pin-btn-primary">${t('common.continue')}</button>
                    </div>
                </form>
            </div>
        </div>
    `;
    document.body.appendChild(modal);
    if (window.applyPasswordToggles) window.applyPasswordToggles(modal);

    document.getElementById('switch-auth-form').addEventListener('submit', async function(e) {
        e.preventDefault();
        const currentCred = document.getElementById('switch-current-credential').value.trim();
        const newCred = document.getElementById('switch-new-credential').value.trim();
        const confirmCred = document.getElementById('switch-confirm-credential').value.trim();
        const currentErrorEl = document.getElementById('switch-current-error');
        const newErrorEl = document.getElementById('switch-new-error');
        currentErrorEl.style.display = 'none';
        newErrorEl.style.display = 'none';

        // Validate current credential
        if (!currentCred) {
            currentErrorEl.textContent = verifyLabel;
            currentErrorEl.style.display = 'block';
            return;
        }
        if (currentMethod === 'pin' && !/^\d{4}$/.test(currentCred)) {
            currentErrorEl.textContent = 'PIN must be 4 digits';
            currentErrorEl.style.display = 'block';
            return;
        }

        // Validate new credential
        if (targetMethod === 'password') {
            const pwRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
            if (!pwRegex.test(newCred)) {
                newErrorEl.textContent = t('auth.setup.passwordRequirements');
                newErrorEl.style.display = 'block';
                return;
            }
        } else {
            if (!/^\d{4}$/.test(newCred)) {
                newErrorEl.textContent = 'PIN must be 4 digits';
                newErrorEl.style.display = 'block';
                return;
            }
        }
        if (newCred !== confirmCred) {
            newErrorEl.textContent = targetMethod === 'password' ? 'Passwords do not match' : 'PINs do not match';
            newErrorEl.style.display = 'block';
            return;
        }

        try {
            const token = localStorage.getItem('authToken');
            const body = { newAuthMethod: targetMethod };
            if (currentMethod === 'pin') body.currentPin = currentCred;
            else body.currentPassword = currentCred;
            if (targetMethod === 'password') body.newPassword = newCred;
            else body.newPin = newCred;

            const response = await fetch('/api/auth/change-credential', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(body)
            });
            const data = await response.json();
            if (!response.ok) {
                currentErrorEl.textContent = data.error || 'Failed to switch authentication method';
                currentErrorEl.style.display = 'block';
                return;
            }
            document.getElementById('switch-auth-modal-temp').remove();
            closeModal('settings-modal');
            showCredentialChangeOverlay(targetMethod === 'password'
                ? t('settings.switchAuth.successPassword')
                : t('settings.switchAuth.successPin'));
        } catch (err) {
            console.error('Switch auth method error:', err);
            currentErrorEl.textContent = 'An error occurred';
            currentErrorEl.style.display = 'block';
        }
    });
    document.getElementById('switch-current-credential').focus();
}

  document
      .getElementById("settings-form")
      .addEventListener("submit", async function (e) {
        e.preventDefault();

        // If nothing has changed, ignore submit
        const saveBtn = this.querySelector('.settings-btn-save');
        if (saveBtn && saveBtn.disabled && !window.settingsFormIsDirty) {
            return;
        }
        
        // Save display name to KV-backed user record (persists across sign-out)
        const newName = document.getElementById('user-name').value.trim();
        if (!newName) {
            switchSettingsTab('account');
            showErrorNotification(t('error.userNameEmpty'));
            document.getElementById('user-name')?.focus();
            return; // prevent closing modal if name is empty
        }

        try {
            const token = window.authSystem?.getAuthToken?.() || localStorage.getItem('authToken');
            if (!token) throw new Error('Missing auth token');

            const resp = await fetch('/api/auth/change-name', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ newName })
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                throw new Error(data.error || 'Failed to update display name');
            }

            // Update in-memory user + UI immediately
            const currentUser = window.authSystem?.getCurrentUser?.();
            if (currentUser) currentUser.name = data.name || newName;
            updateUserDisplay(data.name || newName, window.authSystem?.getCurrentUser?.()?.avatarDataUrl);
        } catch (err) {
            console.error('Failed to save display name:', err);
            switchSettingsTab('account');
            showErrorNotification(t('error.saveDisplayNameFailed'));
            return;
        }

        // Save email to KV-backed user record (authoritative for notification delivery)
        const emailInput = document.getElementById('user-email');
        const newEmail = String(emailInput?.value || '').trim().toLowerCase();
        if (!newEmail || !isValidEmailAddress(newEmail)) {
            switchSettingsTab('account');
            showErrorNotification(t('error.invalidEmail'));
            emailInput?.focus();
            return;
        }

        try {
            const currentUser = window.authSystem?.getCurrentUser?.();
            const currentEmail = String(currentUser?.email || '').trim().toLowerCase();

            if (newEmail && newEmail !== currentEmail) {
                const token = window.authSystem?.getAuthToken?.() || localStorage.getItem('authToken');
                if (!token) throw new Error('Missing auth token');

                // The server requires the current PIN/password to change the email
                const result = await requestCurrentCredential('settings.confirmIdentity.emailHint', async (credential) => {
                    const resp = await fetch('/api/auth/change-email', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ newEmail, ...credential })
                    });
                    const data = await resp.json().catch(() => ({}));
                    return { resp, data };
                });

                // Cancelled: keep the settings modal open with the unsaved email
                if (!result) return;
                if (result.error) throw result.error;
                const { resp, data } = result;
                if (!resp.ok) {
                    throw new Error(data.error || 'Failed to update email');
                }

                // Update in-memory user + UI immediately
                if (currentUser) currentUser.email = data.email || newEmail;
                document.querySelectorAll('.user-email').forEach(el => {
                    if (el) el.textContent = data.email || newEmail;
                });
            }
        } catch (err) {
            console.error('Failed to save email:', err);
            switchSettingsTab('account');
            showErrorNotification(t('error.saveEmailFailed'));
            return;
        }

        // Save avatar to KV-backed user record
        try {
            if (avatarDraft.hasPendingChange) {
                const token = window.authSystem?.getAuthToken?.() || localStorage.getItem('authToken');
                if (!token) throw new Error('Missing auth token');

                const resp = await fetch('/api/auth/change-avatar', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ avatarDataUrl: avatarDraft.dataUrl || null })
                });

                const data = await resp.json().catch(() => ({}));
                if (!resp.ok) {
                    throw new Error(data.error || 'Failed to update avatar');
                }

                const currentUser = window.authSystem?.getCurrentUser?.();
                if (currentUser) currentUser.avatarDataUrl = data.avatarDataUrl || null;
                applyUserAvatarToHeader();
                refreshUserAvatarSettingsUI();

                avatarDraft.hasPendingChange = false;
                avatarDraft.dataUrl = null;
            }
        } catch (err) {
            console.error('Failed to save avatar:', err);
            switchSettingsTab('account');
            showErrorNotification(t('error.saveAvatarFailed'));
            return;
        }

        // Save application settings
        const autoStartToggle = document.getElementById('auto-start-date-toggle');
        const autoEndToggle = document.getElementById('auto-end-date-toggle');
        const enableReviewStatusToggle = document.getElementById('enable-review-status-toggle');
        const calendarIncludeBacklogToggle = document.getElementById('calendar-include-backlog-toggle');
        const debugLogsToggle = document.getElementById('debug-logs-toggle');
        const diagOverlayToggle = document.getElementById('diag-overlay-toggle');
        const historySortOrderSelect = document.getElementById('history-sort-order');
        const languageSelect = document.getElementById('language-select');

        settings.autoSetStartDateOnStatusChange = !!autoStartToggle?.checked;
        settings.autoSetEndDateOnStatusChange = !!autoEndToggle?.checked;
        settings.debugLogsEnabled = !!debugLogsToggle?.checked;
        applyDebugLogSetting(settings.debugLogsEnabled);
        settings.diagnosticsOverlayEnabled = !!diagOverlayToggle?.checked;
        if (typeof window.__diagSetEnabled === 'function') window.__diagSetEnabled(settings.diagnosticsOverlayEnabled);

        // Check if disabling IN REVIEW status with existing review tasks
        const wasEnabled = window.enableReviewStatus;
        const willBeEnabled = !!enableReviewStatusToggle?.checked;

        if (wasEnabled && !willBeEnabled) {
            // User is trying to disable IN REVIEW - check for existing review tasks
            const reviewTasks = tasks.filter(t => t.status === 'review');

            if (reviewTasks.length > 0) {
                // Show custom modal with task list
                const taskListContainer = document.getElementById('review-status-task-list');
                const displayTasks = reviewTasks.slice(0, 5);
                const hasMore = reviewTasks.length > 5;

                const taskListHTML = `
                    <div style="
                        color: var(--text-primary);
                        font-size: 14px;
                        line-height: 1.6;
                        margin-bottom: 16px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        flex-wrap: wrap;
                    ">
                        <span style="color: var(--text-secondary);">You have</span>
                        <span style="
                            color: var(--text-primary);
                            font-weight: 600;
                            font-size: 16px;
                        ">${reviewTasks.length}</span>
                        <span style="color: var(--text-secondary);">${reviewTasks.length === 1 ? 'task' : 'tasks'} with</span>
                        <span class="status-badge review" style="
                            display: inline-flex;
                            align-items: center;
                            padding: 4px 10px;
                            border-radius: 6px;
                            font-size: 11px;
                            font-weight: 600;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                        ">IN REVIEW</span>
                        <span style="color: var(--text-secondary);">status</span>
                    </div>
                    <div style="margin: 20px 0;">
                        ${displayTasks.map(t => `
                            <div style="
                                display: flex;
                                align-items: center;
                                gap: 10px;
                                padding: 8px 0;
                                color: var(--text-primary);
                                font-size: 14px;
                            ">
                                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style="flex-shrink: 0; color: var(--accent-green);">
                                    <rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/>
                                    <path d="M5 8L7 10L11 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                                    ${escapeHtml(t.title)}
                                </span>
                            </div>
                        `).join('')}
                        ${hasMore ? `
                            <div style="
                                margin-top: 12px;
                                padding-top: 12px;
                                border-top: 1px solid var(--border-color);
                            ">
                                <button type="button" style="
                                    background: none;
                                    border: none;
                                    color: var(--accent-blue);
                                    text-decoration: none;
                                    font-size: 13px;
                                    display: inline-flex;
                                    align-items: center;
                                    gap: 6px;
                                    cursor: pointer;
                                    padding: 0;
                                    font-family: inherit;
                                " data-action="openReviewTasksInNewTab">
                                    <span>View all ${reviewTasks.length} tasks in List view</span>
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="flex-shrink: 0;">
                                        <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                            </div>
                        ` : ''}
                    </div>
                    <div style="
                        color: var(--text-primary);
                        font-size: 14px;
                        line-height: 1.6;
                        margin-top: 20px;
                        padding: 16px;
                        background: var(--bg-tertiary);
                        border-radius: 8px;
                        border-left: 3px solid var(--accent-blue);
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        flex-wrap: wrap;
                    ">
                        <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style="flex-shrink: 0; color: var(--accent-blue);">
                            <path d="M8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2Z" stroke="currentColor" stroke-width="1.5"/>
                            <path d="M8 5V8L10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                        <span style="color: var(--text-secondary);">These tasks will be moved to</span>
                        <span class="status-badge progress" style="
                            display: inline-flex;
                            align-items: center;
                            padding: 4px 10px;
                            border-radius: 6px;
                            font-size: 11px;
                            font-weight: 600;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                        ">IN PROGRESS</span>
                    </div>
                `;
                taskListContainer.innerHTML = taskListHTML;

                // Store tasks to migrate and toggle reference for later
                window.pendingReviewTaskMigration = reviewTasks;
                window.pendingReviewStatusToggle = enableReviewStatusToggle;

                // Show modal and exit early - will continue in confirmDisableReviewStatus
                document.getElementById('review-status-confirm-modal').classList.add('active');
                return;
            }
        }

        settings.enableReviewStatus = willBeEnabled;
        settings.calendarIncludeBacklog = !!calendarIncludeBacklogToggle?.checked;
        settings.historySortOrder = historySortOrderSelect.value;
        settings.language = normalizeLanguage(languageSelect?.value || 'es');

        // Update global variable and localStorage
        window.enableReviewStatus = settings.enableReviewStatus;
        localStorage.setItem('enableReviewStatus', String(settings.enableReviewStatus));

        // Apply review status visibility
        applyReviewStatusVisibility();
        applyLanguage();
        
          settings.notificationEmail = newEmail;

          const emailNotificationsEnabledToggle = document.getElementById('email-notifications-enabled');
          const emailNotificationsWeekdaysOnlyToggle = document.getElementById('email-notifications-weekdays-only');
          const emailNotificationsIncludeStartDatesToggle = document.getElementById('email-notifications-include-start-dates');
          const emailNotificationsIncludeBacklogToggle = document.getElementById('email-notifications-include-backlog');
          const emailNotificationsIncludeWeekAheadToggle = document.getElementById('email-notifications-include-week-ahead');
          const emailNotificationTimeInput = document.getElementById('email-notification-time');
          const emailNotificationTimeZoneSelect = document.getElementById('email-notification-timezone');

          settings.emailNotificationsEnabled = !!emailNotificationsEnabledToggle?.checked;
          settings.emailNotificationsWeekdaysOnly = !!emailNotificationsWeekdaysOnlyToggle?.checked;
          settings.emailNotificationsIncludeStartDates = !!emailNotificationsIncludeStartDatesToggle?.checked;
          settings.emailNotificationsIncludeBacklog = !!emailNotificationsIncludeBacklogToggle?.checked;
          settings.emailNotificationsIncludeWeekAhead = !!emailNotificationsIncludeWeekAheadToggle?.checked;
          const snappedTime = snapHHMMToStep(
              normalizeHHMM(emailNotificationTimeInput?.value) || "09:00",
              30
          );
          settings.emailNotificationTime =
              clampHHMMToRange(snappedTime || "09:00", "08:00", "18:00") || "09:00";
          settings.emailNotificationTimeZone = coerceSupportedUserTimeZone(
              emailNotificationTimeZoneSelect?.value || DEFAULT_USER_TIMEZONE
          );

          if (emailNotificationTimeInput) {
              emailNotificationTimeInput.value = settings.emailNotificationTime;
          }

          if (workspaceLogoDraft.hasPendingChange) {
              settings.customWorkspaceLogo = workspaceLogoDraft.dataUrl || null;
          }
  
          saveSettings();
          applyWorkspaceLogo();

          // Refresh notification state to reflect new settings (e.g., backlog filter changes)
          updateNotificationState({ force: true });

          // Refresh calendar if it's currently active (to apply backlog filter changes)
          const calendarView = document.getElementById('calendar-view');
          if (calendarView && calendarView.classList.contains('active')) {
              renderCalendar();
          }

          workspaceLogoDraft.hasPendingChange = false;
          workspaceLogoDraft.dataUrl = null;

          // Also update the display in the user menu
          const userEmailEl = document.querySelector('.user-email');
          if (userEmailEl) userEmailEl.textContent = newEmail;

          showSuccessNotification(t('success.settingsSaved'));
          // Mark form as clean and close
          window.initialSettingsFormState = null;
          window.settingsFormIsDirty = false;
          if (saveBtn) {
              saveBtn.classList.remove('dirty');
              saveBtn.disabled = true;
          }
          closeModal('settings-modal');
      });

  function refreshUserAvatarSettingsUI() {
      const preview = document.getElementById('user-avatar-preview');
      const clearButton = document.getElementById('user-avatar-clear-btn');
      const dropzone = document.getElementById('user-avatar-dropzone');
      const row = preview?.closest?.('.user-avatar-input-row') || dropzone?.closest?.('.user-avatar-input-row') || null;

      const currentUser = window.authSystem?.getCurrentUser?.();
      const effectiveAvatar = avatarDraft.hasPendingChange
          ? avatarDraft.dataUrl
          : (currentUser?.avatarDataUrl || null);
      const hasAvatar = !!effectiveAvatar;

      if (preview && clearButton) {
          if (effectiveAvatar) {
              preview.style.display = 'block';
              preview.style.backgroundImage = `url(${effectiveAvatar})`;
              clearButton.style.display = 'inline-flex';
          } else {
              preview.style.display = 'none';
              preview.style.backgroundImage = '';
              clearButton.style.display = 'none';
          }
      }

      if (dropzone) {
          const uploadAria = t('settings.avatarUploadAriaUpload');
          const changeAria = t('settings.avatarUploadAriaChange');
          dropzone.setAttribute('aria-label', hasAvatar ? changeAria : uploadAria);

          const textEl = dropzone.querySelector('.workspace-logo-dropzone-text');
          if (textEl) {
              const defaultText = dropzone.dataset.defaultText || t('settings.avatarUploadDefault');
              const changeText = dropzone.dataset.changeText || t('settings.avatarUploadChange');
              textEl.textContent = hasAvatar ? changeText : defaultText;
          }

          // Make the dropzone more compact once an avatar exists.
          dropzone.style.minHeight = hasAvatar ? '40px' : '48px';
          dropzone.style.padding = hasAvatar ? '10px 12px' : '12px 16px';
      }

      if (row) {
          row.classList.toggle('has-avatar', hasAvatar);
      }
  }

  function applyUserAvatarToHeader() {
      const currentUser = window.authSystem?.getCurrentUser?.();
      if (!currentUser) return;
      updateUserDisplay(currentUser.name, currentUser.avatarDataUrl || null);
  }

  function setupWorkspaceLogoControls() {
      const dropzone = document.getElementById('workspace-logo-dropzone');
      const fileInput = document.getElementById('workspace-logo-input');
      const clearButton = document.getElementById('workspace-logo-clear-btn');
      const preview = document.getElementById('workspace-logo-preview');

      if (!dropzone || !fileInput) return;

      const defaultText = t('settings.logoUploadDefault');

      dropzone.dataset.defaultText = defaultText;

      function setDropzoneText(text) {
          dropzone.innerHTML = '';
          const textEl = document.createElement('span');
          textEl.className = 'workspace-logo-dropzone-text';
          textEl.textContent = text;
          dropzone.appendChild(textEl);
      }

      function applyDropzoneBaseStyles(el) {
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.justifyContent = 'center';
          el.style.gap = '10px';
          el.style.padding = '12px 16px';
          el.style.textAlign = 'center';
          el.style.cursor = 'pointer';
          el.style.userSelect = 'none';
          el.style.minHeight = '48px';
          el.style.border = '2px dashed var(--border)';
          el.style.borderRadius = '10px';
          el.style.background = 'var(--bg-tertiary)';
          el.style.boxShadow = 'none';
          el.style.color = 'var(--text-muted)';
          el.style.fontWeight = '500';
          el.style.transition = 'border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease';
      }

      function setDropzoneDragoverStyles(el, isActive) {
          if (isActive) {
              el.style.borderColor = 'var(--accent-blue)';
              el.style.background = 'rgba(59, 130, 246, 0.08)';
              el.style.boxShadow = '0 0 0 1px var(--accent-blue)';
          } else {
              el.style.border = '2px dashed var(--border)';
              el.style.background = 'var(--bg-tertiary)';
              el.style.boxShadow = 'none';
          }
      }

      function refreshWorkspaceLogoUI() {
          if (!preview || !clearButton) return;

          const row = preview.closest('.workspace-logo-input-row') || dropzone?.closest?.('.workspace-logo-input-row') || null;
          const effectiveLogo = workspaceLogoDraft.hasPendingChange
              ? workspaceLogoDraft.dataUrl
              : settings.customWorkspaceLogo;
          const hasLogo = !!effectiveLogo;

          if (effectiveLogo) {
              preview.style.display = 'block';
              preview.style.backgroundImage = `url(${effectiveLogo})`;
              clearButton.style.display = 'inline-flex';
          } else {
              preview.style.display = 'none';
              preview.style.backgroundImage = '';
              clearButton.style.display = 'none';
          }

          if (dropzone) {
              const uploadAria = t('settings.logoUploadAriaUpload');
              const changeAria = t('settings.logoUploadAriaChange');
              dropzone.setAttribute('aria-label', hasLogo ? changeAria : uploadAria);
              const defaultText = dropzone.dataset.defaultText || t('settings.logoUploadDefault');
              const changeText = dropzone.dataset.changeText || t('settings.logoUploadChange');
              if (dropzone.getAttribute('aria-busy') !== 'true' && !dropzone.classList.contains('workspace-logo-uploading')) {
                  setDropzoneText(hasLogo ? changeText : defaultText);
              }

              dropzone.style.minHeight = hasLogo ? '40px' : '48px';
              dropzone.style.padding = hasLogo ? '10px 12px' : '12px 16px';
          }

          if (row) {
              row.classList.toggle('has-logo', hasLogo);
          }
      }

      async function handleWorkspaceLogoFile(file, event) {
          if (!file) return;
          if (event) {
              event.preventDefault();
              event.stopPropagation();
          }

          if (!file.type.startsWith('image/')) {
              showErrorNotification(t('error.logoSelectFile'));
              return;
          }

          const maxSizeBytes = 2048 * 1024; // 2MB limit for workspace logo
          if (file.size > maxSizeBytes) {
              showErrorNotification(t('error.logoTooLarge'));
              return;
          }

          const defaultText = dropzone.dataset.defaultText || t('settings.logoUploadDefault');

          try {
              // Show uploading state
              dropzone.innerHTML = '';
              const textEl = document.createElement('span');
              textEl.className = 'workspace-logo-dropzone-text';
              textEl.textContent = t('common.uploadingFile', { name: file.name });
              dropzone.appendChild(textEl);
              dropzone.classList.add('workspace-logo-uploading');
              dropzone.setAttribute('aria-busy', 'true');

              const reader = new FileReader();
              reader.onload = function (event) {
                  const dataUrl = event.target && event.target.result;
                  if (!dataUrl) {
                      showErrorNotification(t('error.imageReadFailed'));
                      setDropzoneText(defaultText);
                      dropzone.classList.remove('workspace-logo-uploading');
                      dropzone.removeAttribute('aria-busy');
                      return;
                  }

                  const img = new Image();
                  img.onload = function () {
                      // Always open crop modal for user to adjust
                      openCropModal(dataUrl, img);
                      setDropzoneText(defaultText);
                      dropzone.classList.remove('workspace-logo-uploading');
                      dropzone.removeAttribute('aria-busy');
                  };
                  img.onerror = function () {
                      showErrorNotification(t('error.imageLoadFailed'));
                      setDropzoneText(defaultText);
                      dropzone.classList.remove('workspace-logo-uploading');
                      dropzone.removeAttribute('aria-busy');
                  };
                  img.src = dataUrl;
              };
              reader.onerror = function () {
                  showErrorNotification(t('error.imageReadFailed'));
                  setDropzoneText(defaultText);
                  dropzone.classList.remove('workspace-logo-uploading');
                  dropzone.removeAttribute('aria-busy');
              };

              reader.readAsDataURL(file);
          } catch (error) {
              showErrorNotification(t('error.logoUploadFailed', { message: error.message }));
              setDropzoneText(defaultText);
              dropzone.classList.remove('workspace-logo-uploading');
              dropzone.removeAttribute('aria-busy');
          }
      }

      // Initialize dropzone styles and text
      applyDropzoneBaseStyles(dropzone);
      setDropzoneText(defaultText);

      // Initialize preview UI
      refreshWorkspaceLogoUI();

      let dragDepth = 0;

      // Drag & Drop event handlers
      dropzone.addEventListener('dragenter', function(e) {
          e.preventDefault();
          dragDepth += 1;
          dropzone.classList.add('workspace-logo-dragover');
          setDropzoneDragoverStyles(dropzone, true);
      });

      dropzone.addEventListener('dragover', function(e) {
          e.preventDefault();
          dropzone.classList.add('workspace-logo-dragover');
          setDropzoneDragoverStyles(dropzone, true);
      });

      dropzone.addEventListener('dragleave', function(e) {
          e.preventDefault();
          dragDepth = Math.max(0, dragDepth - 1);
          if (dragDepth === 0) {
              dropzone.classList.remove('workspace-logo-dragover');
              setDropzoneDragoverStyles(dropzone, false);
          }
      });

      dropzone.addEventListener('drop', function(e) {
          dragDepth = 0;
          dropzone.classList.remove('workspace-logo-dragover');
          setDropzoneDragoverStyles(dropzone, false);
          const files = e.dataTransfer && e.dataTransfer.files;
          if (files && files.length > 0) {
              handleWorkspaceLogoFile(files[0], e);
          }
      });

      dropzone.addEventListener('dragend', function() {
          dragDepth = 0;
          dropzone.classList.remove('workspace-logo-dragover');
          setDropzoneDragoverStyles(dropzone, false);
      });

      // Paste support
      dropzone.addEventListener('paste', function(e) {
          if (!e.clipboardData) return;
          const files = e.clipboardData.files;
          if (files && files.length > 0) {
              handleWorkspaceLogoFile(files[0], e);
          }
      });

      // Click to upload
      dropzone.addEventListener('click', function() {
          fileInput.click();
      });

      // Keyboard accessibility
      dropzone.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInput.click();
          }
      });

      // File input change handler
      fileInput.addEventListener('change', function(e) {
          const files = e.target.files;
          if (files && files.length > 0) {
              handleWorkspaceLogoFile(files[0], e);
          }
          fileInput.value = '';
      });

      // Clear button handler
      if (clearButton) {
          clearButton.addEventListener('click', function (e) {
              e.preventDefault();
              workspaceLogoDraft.hasPendingChange = true;
              workspaceLogoDraft.dataUrl = null;
              if (fileInput) {
                  fileInput.value = '';
              }
              refreshWorkspaceLogoUI();
              if (window.markSettingsDirtyIfNeeded) {
                  window.markSettingsDirtyIfNeeded();
              }
          });
      }

      // Listen for refresh events from external code (like openSettingsModal)
      document.addEventListener('refresh-workspace-logo-ui', () => {
          refreshWorkspaceLogoUI();
      });

      // ============================================
      // Workspace Logo Crop Modal Functions
      // ============================================

  function openCropModal(dataUrl, image, options = null) {
      const modal = document.getElementById('workspace-logo-crop-modal');
      const canvas = document.getElementById('crop-canvas');
      const ctx = canvas.getContext('2d');
      const titleEl = document.getElementById('crop-modal-title');
      const instructionsEl = modal?.querySelector('.crop-instructions');

      // Store state
      cropState.originalImage = image;
      cropState.originalDataUrl = dataUrl;
      cropState.canvas = canvas;
      cropState.ctx = ctx;
      cropState.onApply = options?.onApply || null;
      cropState.successMessage = options?.successMessage || null;
      cropState.shape = options?.shape || 'square';
      cropState.outputMimeType = options?.outputMimeType || 'image/jpeg';
      cropState.outputMaxSize = typeof options?.outputMaxSize === 'number' ? options.outputMaxSize : null;

      if (titleEl) titleEl.textContent = options?.title || t('crop.title');
      if (instructionsEl && options?.instructions) instructionsEl.textContent = options.instructions;

      // Add accessibility
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'crop-modal-title');

      // Show modal first so layout is measurable
      modal.classList.add('active');

      // Next frame: draw and position selection after layout settles
      requestAnimationFrame(() => {
          // Calculate canvas size (fit to container, maintain aspect ratio)
          const containerMaxWidth = 600;
          const containerMaxHeight = window.innerHeight * 0.6;

          let displayWidth = image.width;
          let displayHeight = image.height;

          if (displayWidth > containerMaxWidth || displayHeight > containerMaxHeight) {
              const scale = Math.min(
                  containerMaxWidth / displayWidth,
                  containerMaxHeight / displayHeight
              );
              displayWidth = Math.floor(displayWidth * scale);
              displayHeight = Math.floor(displayHeight * scale);
          }

          canvas.width = displayWidth;
          canvas.height = displayHeight;

          // Draw image on canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(image, 0, 0, displayWidth, displayHeight);

          // Initialize selection (centered square, 80% of smallest dimension)
          const minDimension = Math.min(displayWidth, displayHeight);
          const initialSize = Math.floor(minDimension * 0.8);

          cropState.selection = {
              x: Math.floor((displayWidth - initialSize) / 2),
              y: Math.floor((displayHeight - initialSize) / 2),
              size: initialSize
          };

          // Setup event listeners
          setupCropEventListeners();

          // Double RAF to ensure canvas layout is complete before positioning overlay
          requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                  updateCropSelection();
              });
          });
      });
  }

  function closeCropModal() {
      const modal = document.getElementById('workspace-logo-crop-modal');
      modal.classList.remove('active');

      // Cleanup event listeners
      removeCropEventListeners();

      // Reset state
      cropState = {
          originalImage: null,
          originalDataUrl: null,
          canvas: null,
          ctx: null,
          selection: { x: 0, y: 0, size: 0 },
          isDragging: false,
          isResizing: false,
          dragStartX: 0,
          dragStartY: 0,
          activeHandle: null,
          shape: 'square',
          outputMimeType: 'image/jpeg',
          outputMaxSize: null,
          onApply: null,
          successMessage: null
      };
  }

  function updateCropSelection() {
      const selection = document.getElementById('crop-selection');
      const canvas = cropState.canvas;
      if (!canvas || !selection) return;

      selection.dataset.shape = cropState.shape || 'square';

      const canvasRect = canvas.getBoundingClientRect();
      const container = canvas.closest('.crop-canvas-container') || canvas.parentElement;
      const containerRect = container ? container.getBoundingClientRect() : canvasRect;
      const offsetX = canvasRect.left - containerRect.left;
      const offsetY = canvasRect.top - containerRect.top;

      // Use a uniform scale factor so the overlay matches what the user sees.
      // (CSS keeps aspect ratio, but rounding can make X/Y slightly differ.)
      const uniformScale = canvas.width / canvasRect.width;

      // Position selection div (in screen pixels)
      const displayX = cropState.selection.x / uniformScale;
      const displayY = cropState.selection.y / uniformScale;
      const displaySize = cropState.selection.size / uniformScale;

      selection.style.left = `${offsetX + displayX}px`;
      selection.style.top = `${offsetY + displayY}px`;
      selection.style.width = `${displaySize}px`;
      selection.style.height = `${displaySize}px`;

      // Update info display
      updateCropInfo();
  }

  function updateCropInfo() {
      const dimensionsEl = document.getElementById('crop-dimensions');
      const sizeEl = document.getElementById('crop-size-estimate');
      if (!dimensionsEl || !sizeEl) return;

      // Calculate actual pixel dimensions (in original image)
      const canvas = cropState.canvas;
      const image = cropState.originalImage;
      if (!canvas || !image) return;

      const selectionEl = document.getElementById('crop-selection');
      const canvasRect = canvas.getBoundingClientRect();
      const selectionRect = selectionEl?.getBoundingClientRect?.();

      // Derive the output size from what the user actually sees (DOM rects),
      // so the preview and saved crop match even if CSS rounding makes scales differ.
      let actualSize = 0;
      if (selectionRect && canvasRect.width > 0 && canvasRect.height > 0) {
          const scaleX = image.width / canvasRect.width;
          const scaleY = image.height / canvasRect.height;
          const cropSizeX = selectionRect.width * scaleX;
          const cropSizeY = selectionRect.height * scaleY;
          actualSize = Math.max(1, Math.floor(Math.min(cropSizeX, cropSizeY)));
      } else {
          const scale = image.width / canvas.width;
          actualSize = Math.max(1, Math.floor(cropState.selection.size * scale));
      }

      dimensionsEl.textContent = `${actualSize} × ${actualSize} px`;

      // Estimate file size (rough approximation: 3 bytes per pixel for JPEG)
      const estimatedBytes = actualSize * actualSize * 3;
      const estimatedKB = Math.floor(estimatedBytes / 1024);

      sizeEl.textContent = `~${estimatedKB} KB`;

      // Warning colors
      const maxSizeKB = 2048; // 2MB limit
      sizeEl.classList.remove('size-warning', 'size-error');

      if (estimatedKB > maxSizeKB) {
          sizeEl.classList.add('size-error');
      } else if (estimatedKB > maxSizeKB * 0.8) {
          sizeEl.classList.add('size-warning');
      }
  }

  async function applyCrop() {
      try {
          const canvas = cropState.canvas;
          const image = cropState.originalImage;
          const selection = cropState.selection;

          if (!canvas || !image) {
              showErrorNotification(t('error.cropInvalid'));
              return;
          }

          // Derive crop from the rendered selection rect (not just stored state),
          // ensuring the saved output matches the on-screen selection exactly.
          const canvasRect = canvas.getBoundingClientRect();
          const selectionEl = document.getElementById('crop-selection');
          const selectionRect = selectionEl?.getBoundingClientRect?.();

          let cropX = 0;
          let cropY = 0;
          let cropSize = 0;

          if (selectionRect && canvasRect.width > 0 && canvasRect.height > 0) {
              const centerXRatio =
                  (selectionRect.left + selectionRect.width / 2 - canvasRect.left) / canvasRect.width;
              const centerYRatio =
                  (selectionRect.top + selectionRect.height / 2 - canvasRect.top) / canvasRect.height;

              const centerXOrig = centerXRatio * image.width;
              const centerYOrig = centerYRatio * image.height;

              const cropSizeX = selectionRect.width / canvasRect.width * image.width;
              const cropSizeY = selectionRect.height / canvasRect.height * image.height;
              cropSize = Math.max(1, Math.floor(Math.min(cropSizeX, cropSizeY)));

              cropX = Math.floor(centerXOrig - cropSize / 2);
              cropY = Math.floor(centerYOrig - cropSize / 2);
          } else {
              // Fallback: use stored state mapping
              const scaleX = image.width / canvas.width;
              const scaleY = image.height / canvas.height;
              cropSize = Math.max(1, Math.floor(selection.size * Math.min(scaleX, scaleY)));
              cropX = Math.floor(selection.x * scaleX);
              cropY = Math.floor(selection.y * scaleY);
          }

          cropX = Math.max(0, Math.min(cropX, image.width - cropSize));
          cropY = Math.max(0, Math.min(cropY, image.height - cropSize));

          const maxSizeBytes = 2048 * 1024; // 2MB limit
          const maxAttempts = 6;

          const renderOutputDataUrl = (targetSize, quality) => {
              const outCanvas = document.createElement('canvas');
              outCanvas.width = targetSize;
              outCanvas.height = targetSize;
              const outCtx = outCanvas.getContext('2d');

              if (!outCtx) {
                  throw new Error('Canvas context unavailable');
              }

              const shape = cropState.shape || 'square';
              if (shape === 'circle') {
                  outCtx.clearRect(0, 0, targetSize, targetSize);
                  outCtx.save();
                  outCtx.beginPath();
                  outCtx.arc(targetSize / 2, targetSize / 2, targetSize / 2, 0, Math.PI * 2);
                  outCtx.closePath();
                  outCtx.clip();
              }

              outCtx.drawImage(
                  image,
                  cropX, cropY, cropSize, cropSize,  // Source rectangle
                  0, 0, targetSize, targetSize        // Destination rectangle
              );

              if (shape === 'circle') {
                  outCtx.restore();
              }

              const mimeType = cropState.outputMimeType || (shape === 'circle' ? 'image/png' : 'image/jpeg');
              if (mimeType === 'image/jpeg' || mimeType === 'image/webp') {
                  return outCanvas.toDataURL(mimeType, quality);
              }
              return outCanvas.toDataURL(mimeType);
          };

          let targetSize = cropState.outputMaxSize ? Math.min(cropSize, cropState.outputMaxSize) : cropSize;
          targetSize = Math.max(50, Math.floor(targetSize));

          let attempts = 0;
          let quality = 0.92;
          let croppedDataUrl = renderOutputDataUrl(targetSize, quality);

          while (croppedDataUrl.length > maxSizeBytes * 1.37 && attempts < maxAttempts) {
              const shape = cropState.shape || 'square';
              const mimeType = cropState.outputMimeType || (shape === 'circle' ? 'image/png' : 'image/jpeg');

              if (mimeType === 'image/jpeg' || mimeType === 'image/webp') {
                  quality = Math.max(0.5, quality - 0.1);
              } else {
                  targetSize = Math.floor(targetSize * 0.85);
              }

              croppedDataUrl = renderOutputDataUrl(targetSize, quality);
              attempts++;
          }

          if (croppedDataUrl.length > maxSizeBytes * 1.37) {
              showErrorNotification(t('error.cropTooLarge'));
              return;
          }

          if (typeof cropState.onApply === 'function') {
              cropState.onApply(croppedDataUrl);
              if (window.markSettingsDirtyIfNeeded) {
                  window.markSettingsDirtyIfNeeded();
              }
              closeCropModal();
              showSuccessNotification(cropState.successMessage || t('success.cropApplied'));
          } else {
              // Back-compat: Update workspace logo draft
              workspaceLogoDraft.hasPendingChange = true;
              workspaceLogoDraft.dataUrl = croppedDataUrl;

              // Refresh UI
              refreshWorkspaceLogoUI();
              if (window.markSettingsDirtyIfNeeded) {
                  window.markSettingsDirtyIfNeeded();
              }

              // Close modal
              closeCropModal();

              // Success notification
              showSuccessNotification(t('success.logoCroppedApplied'));
          }

      } catch (error) {
          showErrorNotification(t('error.cropFailed', { message: error.message }));
          console.error('Crop error:', error);
      }
  }

  function setupUserAvatarControls() {
      const dropzone = document.getElementById('user-avatar-dropzone');
      const fileInput = document.getElementById('user-avatar-input');
      const clearButton = document.getElementById('user-avatar-clear-btn');

      if (!dropzone || !fileInput) return;

      const defaultText = t('settings.avatarUploadDefault');

      dropzone.dataset.defaultText = defaultText;
      dropzone.dataset.changeText = t('settings.avatarUploadChange');

      function setDropzoneText(text) {
          dropzone.innerHTML = '';
          const textEl = document.createElement('span');
          textEl.className = 'workspace-logo-dropzone-text';
          textEl.textContent = text;
          dropzone.appendChild(textEl);
      }

      function applyDropzoneBaseStyles(el) {
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.justifyContent = 'center';
          el.style.gap = '10px';
          el.style.padding = '12px 16px';
          el.style.textAlign = 'center';
          el.style.cursor = 'pointer';
          el.style.userSelect = 'none';
          el.style.minHeight = '48px';
          el.style.border = '2px dashed var(--border)';
          el.style.borderRadius = '10px';
          el.style.background = 'var(--bg-tertiary)';
          el.style.boxShadow = 'none';
          el.style.color = 'var(--text-muted)';
          el.style.fontWeight = '500';
          el.style.transition = 'border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease';
      }

      function setDropzoneDragoverStyles(el, isActive) {
          if (isActive) {
              el.style.borderColor = 'var(--accent-blue)';
              el.style.background = 'rgba(59, 130, 246, 0.08)';
              el.style.boxShadow = '0 0 0 1px var(--accent-blue)';
          } else {
              el.style.border = '2px dashed var(--border)';
              el.style.background = 'var(--bg-tertiary)';
              el.style.boxShadow = 'none';
          }
      }

      async function handleAvatarFile(file, event) {
          if (!file) return;
          if (event) {
              event.preventDefault();
              event.stopPropagation();
          }

          if (!file.type.startsWith('image/')) {
              showErrorNotification(t('error.avatarSelectFile'));
              return;
          }

          const maxSizeBytes = 2048 * 1024; // 2MB limit for avatar
          if (file.size > maxSizeBytes) {
              showErrorNotification(t('error.avatarTooLarge'));
              return;
          }

          const defaultText = dropzone.dataset.defaultText || t('settings.avatarUploadDefault');

          try {
              dropzone.innerHTML = '';
              const textEl = document.createElement('span');
              textEl.className = 'workspace-logo-dropzone-text';
              textEl.textContent = t('common.uploadingFile', { name: file.name });
              dropzone.appendChild(textEl);
              dropzone.setAttribute('aria-busy', 'true');

              const reader = new FileReader();
              const dataUrl = await new Promise((resolve, reject) => {
                  reader.onload = () => resolve(reader.result);
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
              });

              const img = new Image();
              img.onload = () => {
                  openCropModal(dataUrl, img, {
                      title: 'Crop Avatar',
                      instructions: 'Drag to adjust the crop area. Your avatar will be displayed as a circle.',
                      shape: 'circle',
                      outputMimeType: 'image/png',
                      outputMaxSize: 512,
                      successMessage: 'Avatar cropped and applied successfully!',
                      onApply: (croppedDataUrl) => {
                          avatarDraft.hasPendingChange = true;
                          avatarDraft.dataUrl = croppedDataUrl;
                          refreshUserAvatarSettingsUI();
                      }
                  });
              };
              img.onerror = () => {
                  showErrorNotification(t('error.imageLoadFailed'));
              };
              img.src = dataUrl;
          } catch (err) {
              console.error('Avatar upload error:', err);
              showErrorNotification(t('error.avatarUploadFailed'));
          } finally {
              dropzone.setAttribute('aria-busy', 'false');
              setDropzoneText(defaultText);
          }
      }

      applyDropzoneBaseStyles(dropzone);
      setDropzoneText(defaultText);

      dropzone.addEventListener('click', () => fileInput.click());
      dropzone.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInput.click();
          }
      });

      fileInput.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          handleAvatarFile(file, e);
          fileInput.value = '';
      });

      dropzone.addEventListener('dragenter', (e) => {
          e.preventDefault();
          setDropzoneDragoverStyles(dropzone, true);
      });
      dropzone.addEventListener('dragover', (e) => {
          e.preventDefault();
          setDropzoneDragoverStyles(dropzone, true);
      });
      dropzone.addEventListener('dragleave', (e) => {
          e.preventDefault();
          setDropzoneDragoverStyles(dropzone, false);
      });
      dropzone.addEventListener('drop', (e) => {
          e.preventDefault();
          setDropzoneDragoverStyles(dropzone, false);
          const file = e.dataTransfer?.files?.[0];
          handleAvatarFile(file, e);
      });

      if (clearButton && !clearButton.__avatarClearBound) {
          clearButton.__avatarClearBound = true;
          clearButton.addEventListener('click', () => {
              avatarDraft.hasPendingChange = true;
              avatarDraft.dataUrl = null;
              refreshUserAvatarSettingsUI();
              if (window.markSettingsDirtyIfNeeded) {
                  window.markSettingsDirtyIfNeeded();
              }
          });
      }

      refreshUserAvatarSettingsUI();
  }

  function setupCropEventListeners() {
      const selection = document.getElementById('crop-selection');
      const handles = document.querySelectorAll('.crop-handle');

      // Mouse down on selection (start drag)
      if (selection) {
          selection.addEventListener('mousedown', onSelectionMouseDown);
          selection.addEventListener('touchstart', onSelectionTouchStart);
      }

      // Mouse down on handles (start resize)
      handles.forEach(handle => {
          handle.addEventListener('mousedown', onHandleMouseDown);
          handle.addEventListener('touchstart', onHandleTouchStart);
      });

      // Global mouse move and up
      document.addEventListener('mousemove', onDocumentMouseMove);
      document.addEventListener('mouseup', onDocumentMouseUp);
      document.addEventListener('touchmove', onDocumentTouchMove);
      document.addEventListener('touchend', onDocumentTouchEnd);
      document.addEventListener('keydown', onCropModalKeyDown);
  }

  function removeCropEventListeners() {
      const selection = document.getElementById('crop-selection');
      const handles = document.querySelectorAll('.crop-handle');

      if (selection) {
          selection.removeEventListener('mousedown', onSelectionMouseDown);
          selection.removeEventListener('touchstart', onSelectionTouchStart);
      }

      handles.forEach(handle => {
          handle.removeEventListener('mousedown', onHandleMouseDown);
          handle.removeEventListener('touchstart', onHandleTouchStart);
      });

      document.removeEventListener('mousemove', onDocumentMouseMove);
      document.removeEventListener('mouseup', onDocumentMouseUp);
      document.removeEventListener('touchmove', onDocumentTouchMove);
      document.removeEventListener('touchend', onDocumentTouchEnd);
      document.removeEventListener('keydown', onCropModalKeyDown);
  }

  function onSelectionMouseDown(e) {
      if (e.target.classList.contains('crop-handle')) return;

      e.preventDefault();
      e.stopPropagation();

      cropState.isDragging = true;
      cropState.dragStartX = e.clientX;
      cropState.dragStartY = e.clientY;
  }

  function onHandleMouseDown(e) {
      e.preventDefault();
      e.stopPropagation();

      cropState.isResizing = true;
      cropState.dragStartX = e.clientX;
      cropState.dragStartY = e.clientY;

      // Determine which handle
      if (e.target.classList.contains('crop-handle-nw')) {
          cropState.activeHandle = 'nw';
      } else if (e.target.classList.contains('crop-handle-ne')) {
          cropState.activeHandle = 'ne';
      } else if (e.target.classList.contains('crop-handle-sw')) {
          cropState.activeHandle = 'sw';
      } else if (e.target.classList.contains('crop-handle-se')) {
          cropState.activeHandle = 'se';
      }
  }

  function onDocumentMouseMove(e) {
      if (!cropState.isDragging && !cropState.isResizing) return;

      e.preventDefault();

      const canvas = cropState.canvas;
      if (!canvas) return;

      const canvasRect = canvas.getBoundingClientRect();
      const uniformScale = canvas.width / canvasRect.width;

      const deltaX = (e.clientX - cropState.dragStartX) * uniformScale;
      const deltaY = (e.clientY - cropState.dragStartY) * uniformScale;

      if (cropState.isDragging) {
          // Move selection
          let newX = cropState.selection.x + deltaX;
          let newY = cropState.selection.y + deltaY;

          // Constrain to canvas bounds
          newX = Math.max(0, Math.min(newX, canvas.width - cropState.selection.size));
          newY = Math.max(0, Math.min(newY, canvas.height - cropState.selection.size));

          cropState.selection.x = newX;
          cropState.selection.y = newY;

      } else if (cropState.isResizing) {
          // Resize selection (maintain square, intuitive handle directions)
          const dominant = (a, b) => (Math.abs(a) > Math.abs(b) ? a : b);
          const minSize = 50;

          const current = cropState.selection;
          let fixedX = 0;
          let fixedY = 0;
          let resizeDelta = 0;
          let maxSize = 0;

          switch (cropState.activeHandle) {
              case 'se':
                  resizeDelta = dominant(deltaX, deltaY);
                  fixedX = current.x;
                  fixedY = current.y;
                  maxSize = Math.min(canvas.width - fixedX, canvas.height - fixedY);
                  break;
              case 'nw':
                  resizeDelta = dominant(-deltaX, -deltaY);
                  fixedX = current.x + current.size;
                  fixedY = current.y + current.size;
                  maxSize = Math.min(fixedX, fixedY);
                  break;
              case 'ne':
                  resizeDelta = dominant(deltaX, -deltaY);
                  fixedX = current.x;
                  fixedY = current.y + current.size;
                  maxSize = Math.min(canvas.width - fixedX, fixedY);
                  break;
              case 'sw':
                  resizeDelta = dominant(-deltaX, deltaY);
                  fixedX = current.x + current.size;
                  fixedY = current.y;
                  maxSize = Math.min(fixedX, canvas.height - fixedY);
                  break;
          }

          let newSize = current.size + resizeDelta;
          newSize = Math.max(minSize, Math.min(newSize, maxSize));

          let newX = current.x;
          let newY = current.y;

          switch (cropState.activeHandle) {
              case 'se':
                  newX = fixedX;
                  newY = fixedY;
                  break;
              case 'nw':
                  newX = fixedX - newSize;
                  newY = fixedY - newSize;
                  break;
              case 'ne':
                  newX = fixedX;
                  newY = fixedY - newSize;
                  break;
              case 'sw':
                  newX = fixedX - newSize;
                  newY = fixedY;
                  break;
          }

          cropState.selection.size = newSize;
          cropState.selection.x = newX;
          cropState.selection.y = newY;
      }

      updateCropSelection();

      cropState.dragStartX = e.clientX;
      cropState.dragStartY = e.clientY;
  }

  function onDocumentMouseUp(e) {
      if (cropState.isDragging || cropState.isResizing) {
          e.preventDefault();
          cropState.isDragging = false;
          cropState.isResizing = false;
          cropState.activeHandle = null;
      }
  }

  function onSelectionTouchStart(e) {
      if (e.target.classList.contains('crop-handle')) return;

      e.preventDefault();

      const touch = e.touches[0];
      cropState.isDragging = true;
      cropState.dragStartX = touch.clientX;
      cropState.dragStartY = touch.clientY;
  }

  function onHandleTouchStart(e) {
      e.preventDefault();
      e.stopPropagation();

      const touch = e.touches[0];
      cropState.isResizing = true;
      cropState.dragStartX = touch.clientX;
      cropState.dragStartY = touch.clientY;

      // Determine handle (same as mouse)
      if (e.target.classList.contains('crop-handle-nw')) {
          cropState.activeHandle = 'nw';
      } else if (e.target.classList.contains('crop-handle-ne')) {
          cropState.activeHandle = 'ne';
      } else if (e.target.classList.contains('crop-handle-sw')) {
          cropState.activeHandle = 'sw';
      } else if (e.target.classList.contains('crop-handle-se')) {
          cropState.activeHandle = 'se';
      }
  }

  function onDocumentTouchMove(e) {
      if (!cropState.isDragging && !cropState.isResizing) return;

      e.preventDefault();

      const touch = e.touches[0];

      // Reuse mouse move logic with touch coordinates
      const fakeEvent = {
          clientX: touch.clientX,
          clientY: touch.clientY,
          preventDefault: () => {}
      };

      onDocumentMouseMove(fakeEvent);
  }

  function onDocumentTouchEnd(e) {
      if (cropState.isDragging || cropState.isResizing) {
          e.preventDefault();
          cropState.isDragging = false;
          cropState.isResizing = false;
          cropState.activeHandle = null;
      }
  }

  function onCropModalKeyDown(e) {
      if (e.key === 'Escape') {
          closeCropModal();
      }
  }

      // Expose crop functions globally for onclick handlers in HTML
      window.openCropModal = openCropModal;
      window.closeCropModal = closeCropModal;
      window.applyCrop = applyCrop;

      // Initialize avatar controls (shares crop modal)
      setupUserAvatarControls();
  }

  setupWorkspaceLogoControls();

// Expose functions for auth.js to call
window.initializeApp = init;
window.setupUserMenus = setupUserMenus;

// Auth.js will call init() when ready, no need for DOMContentLoaded listener

// Prevent data loss when user closes tab/browser with pending saves
window.addEventListener('beforeunload', (e) => {
    // Flush any pending localStorage writes for feedback queue
    if (feedbackLocalStorageTimer) {
        clearTimeout(feedbackLocalStorageTimer);
        persistFeedbackDeltaQueue(); // Immediate write on page close
    }

    if (docsWorkspaceActive) {
        flushPendingDocsCheckboxAutoSave();
        flushPendingDocsContentAutoSave();
    }

    if ((pendingSaves > 0 || (docsWorkspaceActive && getDocsState().dirty)) && !isSigningOut) {
        // Show browser warning dialog (but never block an intentional sign-out)
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
    }
});

// beforeunload often fires too late for the autosave fetch to leave the machine
// (and mobile Safari frequently skips it altogether). visibilitychange/pagehide
// fire while the page is still alive, so they are what actually protects an
// editing session against a tab switch, an app switch or an OS restart.
function flushDocsAutoSaveOnHide() {
    if (!docsWorkspaceActive) return;
    flushPendingDocsCheckboxAutoSave();
    flushPendingDocsContentAutoSave();
}

document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushDocsAutoSaveOnHide();
});
window.addEventListener('pagehide', flushDocsAutoSaveOnHide);

// --- Save-on-blur behavior for title and description (tasks/projects) ---
// Attach handlers after the DOM is ready. We only persist to storage when
// the user blurs the field (or closes the modal) to avoid per-keystroke saves.
document.addEventListener('DOMContentLoaded', function () {
    const taskModal = document.getElementById('task-modal');
    if (!taskModal) return;

    // Title input inside the task modal
    const titleInput = taskModal.querySelector('#task-form input[name="title"]');
    if (titleInput) {
        titleInput.addEventListener('blur', function (e) {
            const form = taskModal.querySelector('#task-form');
            if (form && form.dataset.editingTaskId) {
                updateTaskField('title', e.target.value);
            }
        });
    }

    // Description editor blur persistence is handled by TipTap hooks.

    // Modal close now relies on blur events for persistence
    // Title and description are already persisted via their respective event handlers
});

// Track first outside click after opening native color picker
let ignoreNextOutsideColorClick = false;

// Close color pickers when clicking outside
document.addEventListener("click", function(e) {
    if (!e.target.closest('.color-picker-container')) {
        if (ignoreNextOutsideColorClick) {
            ignoreNextOutsideColorClick = false;
            return;
        }
        document.querySelectorAll('.color-picker-dropdown').forEach(picker => {
            picker.style.display = 'none';
        });
    }
});

async function submitTaskForm() {
    const form = document.getElementById("task-form");
    const editingTaskId = form.dataset.editingTaskId;
    if (!editingTaskId && !_storageHealthy) { showErrorNotification(t('error.cfOutageBlocked')); return; }
    // Disable submit for new tasks to prevent double-submission during retry window
    const taskSubmitBtn = !editingTaskId ? document.getElementById('task-submit-btn') : null;
    if (taskSubmitBtn) taskSubmitBtn.disabled = true;
    const createAnotherChk = document.getElementById('task-create-another-chk');
    const createAnother = !!(createAnotherChk?.checked);
    const createAnotherProjectId = window.taskCreateAnotherProjectId || null;
    const submitTimer = debugTimeStart("tasks", "submit", {
        editing: !!editingTaskId,
        taskCount: tasks.length
    });
const title = form.querySelector('input[name="title"]').value;
    let description = document.getElementById("task-description-hidden").value;
    description = autoLinkifyDescription(description);
    // Read projectId from hidden input (custom dropdown), fallback to a select if present
    const projectIdRaw = (form.querySelector('input[name="projectId"]').value ||
                         (form.querySelector('select[name="projectId"]') ? form.querySelector('select[name="projectId"]').value : ""));

    const status = document.getElementById("hidden-status").value || "todo";
    const priority = form.querySelector('#hidden-priority').value || "medium";

    const startRaw = (form.querySelector('input[name="startDate"]')?.value || '').trim();
    const startISO = startRaw === '' ? '' : startRaw;

    const endRaw = (form.querySelector('input[name="endDate"]')?.value || '').trim();
    const endISO = endRaw === '' ? '' : endRaw;

    // Date range validation removed - flatpickr constraints prevent invalid selections

    if (editingTaskId) {
        // Capture old task state for history tracking
        const oldTask = tasks.find(t => t.id === parseInt(editingTaskId, 10));
        const oldTaskCopy = oldTask ? JSON.parse(JSON.stringify(oldTask)) : null;

const result = updateTaskService(parseInt(editingTaskId, 10), {title, description, projectId: projectIdRaw, startDate: startISO, endDate: endISO, priority, status}, tasks);
        if (result.task) {
            // Record history for task update
            if (window.historyService && oldTaskCopy) {
                window.historyService.recordTaskUpdated(oldTaskCopy, result.task);
            }

const oldProjectId = result.oldProjectId;
            tasks = result.tasks;
            const t = result.task;

            // Check and record project status change when task status changed via modal
            // (must run after global tasks is updated, since getProjectStatus reads from it)
            if (t.projectId && oldTaskCopy && oldTaskCopy.status !== t.status) {
                checkAndRecordProjectStatusChange(t.projectId, t);
            }

            // If the task moved between projects, mark projects as updated and record project history
            if (oldProjectId !== t.projectId) {
                if (oldProjectId) {
                    const oldProj = touchProjectUpdatedAt(oldProjectId);
                    if (oldProj) saveProjectUpdate(oldProjectId, { updatedAt: oldProj.updatedAt }).catch(() => {});
                    recordProjectTaskLinkChange(oldProjectId, 'removed', t);
                }
                if (t.projectId) {
                    const newProj = touchProjectUpdatedAt(t.projectId);
                    if (newProj) saveProjectUpdate(t.projectId, { updatedAt: newProj.updatedAt }).catch(() => {});
                    recordProjectTaskLinkChange(t.projectId, 'added', t);
                }
            }

// Close modal and update UI immediately (optimistic update)
            closeModal("task-modal");

// Refresh the appropriate view immediately
            if (document.getElementById("project-details").classList.contains("active")) {
const displayedProjectId = oldProjectId || t.projectId;
                if (displayedProjectId) {
                    showProjectDetails(displayedProjectId);
                }
            } else if (document.getElementById("projects").classList.contains("active")) {
                // Clear sorted view cache to force refresh with updated task
                appState.projectsSortedView = null;
                // Refresh projects list view while preserving expanded state
                renderProjects();
                updateCounts();
            }

            // Save in background using row-level operation (don't block UI)
            const updatedTask = tasks.find(t => t.id === parseInt(editingTaskId, 10));
            saveTaskUpdate(parseInt(editingTaskId, 10), {
                title: updatedTask.title,
                description: updatedTask.description,
                projectId: updatedTask.projectId,
                startDate: updatedTask.startDate || '',
                endDate: updatedTask.endDate || '',
                priority: updatedTask.priority,
                status: updatedTask.status,
                completedDate: updatedTask.completedDate || null,
                startDateWasEverSet: updatedTask.startDateWasEverSet,
                endDateWasEverSet: updatedTask.endDateWasEverSet,
                updatedAt: updatedTask.updatedAt
            }).catch(err => {
                console.error('Failed to save task:', err);
                showErrorNotification(t('error.saveChangesFailed'));
            });
        } else {
}
    } else {
        // Save previous state for rollback on failure (deep clone for nested objects)
        const previousTasks = JSON.parse(JSON.stringify(tasks));
        const previousTaskCounter = taskCounter;

        const result = createTaskService({title, description, projectId: projectIdRaw, startDate: startISO, endDate: endISO, priority, status, tags: window.tempTags ? [...window.tempTags] : []}, tasks, taskCounter, tempAttachments);
        tasks = result.tasks;
        taskCounter = result.taskCounter;
        const newTask = result.task;
        tempAttachments = [];
        window.tempTags = [];

        // When created as a recurrence template, hide from task views
        if (pendingRecurrenceAfterTaskCreate && newTask) {
            newTask.isTemplate = true;
            const idx = tasks.findIndex(t => t.id === newTask.id);
            if (idx >= 0) tasks[idx] = newTask;
        }

        // Mark project as updated and record project history when task is added to a project
        if (newTask && newTask.projectId) {
            const proj = touchProjectUpdatedAt(newTask.projectId);
            if (proj) saveProjectUpdate(newTask.projectId, { updatedAt: proj.updatedAt }).catch(() => {});
            recordProjectTaskLinkChange(newTask.projectId, 'added', newTask);
        }

        // Keep filter dropdowns in sync with new task data
        populateProjectOptions();
        populateTagOptions();
        populateCalendarTaskProjectOptions();
        populateCalendarTaskTagOptions();
        updateNoDateOptionVisibility();

        // Save with retry and rollback on failure - wait for persistence before committing UI
        const MAX_RETRIES = 3;
        const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s
        let saveError = null;
        
        async function saveWithRetry(saveFn, operationName) {
            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                try {
                    await saveFn();
                    return true; // Success
                } catch (err) {
                    saveError = err;
                    console.error(`${operationName} attempt ${attempt + 1} failed:`, err);
                    
                    if (attempt < MAX_RETRIES - 1) {
                        // Show retry notification
                        const retryMsg = t('error.saveRetry') || `Retrying ${operationName.toLowerCase()}... (${attempt + 2}/${MAX_RETRIES})`;
                        showErrorNotification(retryMsg, 3000);
                        
                        // Wait before retry with exponential backoff
                        await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
                    }
                }
            }
            return false; // All retries failed
        }
        
        try {
            // Save project first if task has projectId - with retry
            if (newTask && newTask.projectId) {
                const proj = projects.find(p => p.id === newTask.projectId);
                if (proj) {
                    const projectsSaved = await saveWithRetry(() => saveProjectUpdate(newTask.projectId, { updatedAt: proj.updatedAt }), 'Save Project');
                    if (!projectsSaved) {
                        throw saveError || new Error('Failed to save project after retries');
                    }
                }
            }
            
            // Then save task - with retry using row-level operation
            const tasksSaved = await saveWithRetry(() => saveTaskCreate(newTask), 'Save Task');
            if (!tasksSaved) {
                throw saveError || new Error('Failed to save task after retries');
            }
            
            // Record history ONLY after successful persistence
            if (window.historyService) {
                window.historyService.recordTaskCreated(newTask);
            }

            // Apply any task-to-task links queued during creation
            if (window.tempLinks && window.tempLinks.length > 0) {
                const linksToApply = [...window.tempLinks];
                window.tempLinks = [];
                for (const entry of linksToApply) {
                    await addTaskRelationship(newTask.id, entry.targetTaskId, entry.linkType).catch(() => {});
                }
            }

            // Only close modal and update UI after successful save
            closeModal("task-modal");

            if (!editingTaskId && pendingRecurrenceAfterTaskCreate) {
                resumeRecurrenceAfterTaskCreate(newTask.id);
                return;
            }

            // On mobile, show action toast so user can view the newly created task
            if (getIsMobileCached()) {
                const createdTaskId = newTask.id;
                showActionNotification(
                    t('tasks.created.successMobile'),
                    'success',
                    t('tasks.created.viewAction'),
                    () => openTaskDetails(createdTaskId)
                );
            }

            // Refresh the appropriate view
            if (newTask.projectId && document.getElementById("project-details").classList.contains("active")) {
                showProjectDetails(newTask.projectId);
                updateCounts();
            } else if (document.getElementById("projects").classList.contains("active")) {
                // Clear sorted view cache to force refresh with new task
                appState.projectsSortedView = null;
                renderProjects();
                updateCounts();
            }
        } catch (err) {
            // Rollback to previous state on failure
            console.error('Failed to save task after retries:', err);
            tasks = previousTasks;
            taskCounter = previousTaskCounter;
            
            // Show error notification - clear and show final error
            const finalErrorMsg = t('error.saveTaskFailed') || 'Failed to save task after multiple attempts. Please check your connection and try again.';
            showErrorNotification(finalErrorMsg);
            
            // Don't close modal - let user retry
            return;
        } finally {
            // Cleanup temp state and re-enable submit on both success and failure
            window.tempTags = [];
            tempAttachments = [];
            window.tempLinks = [];
            if (taskSubmitBtn) taskSubmitBtn.disabled = false;
        }
    }

    // Fallback for other views (edit path) - close modal and update UI immediately
    closeModal("task-modal");
    render();

    // Always refresh calendar if it exists
    const calendarView = document.getElementById("calendar-view");
    if (calendarView) {
        renderCalendar();
    }
    renderActivityFeed();

    // Edit path: already saved row-level via saveTaskUpdate() above
    debugTimeEnd("tasks", submitTimer, {
        taskCount: tasks.length,
        projectCount: projects.length
    });

    // "Create Another" — re-open modal with same project context after creation
    if (createAnother && !editingTaskId) {
        if (createAnotherProjectId) {
            openTaskModalForProject(createAnotherProjectId);
        } else {
            openTaskModal();
        }
    }
}


// Keep enter-to-submit working
document.getElementById("task-form").addEventListener("submit", function (e) {
    e.preventDefault();
    e.stopPropagation();
    submitTaskForm();
});


function setupStatusDropdown() {
    // Only set up once by checking if already initialized
    if (document.body.hasAttribute("data-status-dropdown-initialized")) {
        return;
    }
    document.body.setAttribute("data-status-dropdown-initialized", "true");
    document.addEventListener("click", handleStatusDropdown);
}

function handleStatusDropdown(e) {
    // Handle options button clicks FIRST
    if (e.target.closest("#task-options-btn")) {
        e.preventDefault();
        e.stopPropagation();
        const menu = document.getElementById("options-menu");
        if (menu) {
            menu.style.display =
                menu.style.display === "block" ? "none" : "block";
        }
        // Only one popup at a time: opening the ⋯ menu closes any open field dropdown.
        document
            .querySelectorAll(".status-dropdown.active, .priority-dropdown.active, .link-type-dropdown.active")
            .forEach((d) => d.classList.remove("active"));
        return;
    }


    // Close options menu when clicking outside
    const menu = document.getElementById("options-menu");
    if (
        menu &&
        !e.target.closest("#task-options-btn") &&
        !e.target.closest("#options-menu")
    ) {
        menu.style.display = "none";
    }

    // Handle link type dropdown clicks (EXACT same pattern as status dropdown)
    if (e.target.closest("#link-type-current")) {
        e.preventDefault();
        e.stopPropagation();
        const dropdown = e.target.closest(".link-type-dropdown");
        const isActive = dropdown.classList.contains("active");

        // Close all dropdowns first
        document.querySelectorAll(".status-dropdown.active, .priority-dropdown.active, .link-type-dropdown.active")
            .forEach((d) => d.classList.remove("active"));

        // Toggle this one if it wasn't active
        if (!isActive) {
            dropdown.classList.add("active");
        }
        return;
    }

    // Handle link type option clicks
    if (e.target.closest(".link-type-option")) {
        e.preventDefault();
        e.stopPropagation();
        const option = e.target.closest(".link-type-option");
        const linkType = option.dataset.linkType;
        const linkTypeText = option.querySelector("span").textContent.trim();

        // Update display
        const currentBtn = document.getElementById("link-type-current");
        const linkTypeValue = document.getElementById("link-type-value");
        const linkTypeLabel = document.getElementById("link-type-label");
        const webLinkInputs = document.getElementById("web-link-inputs");
        const taskLinkSearch = document.getElementById("task-link-search");
        const taskSearchResults = document.getElementById("task-search-results");

        if (currentBtn && linkTypeValue && linkTypeLabel) {
            linkTypeLabel.textContent = linkTypeText;
            linkTypeValue.value = linkType;

            // Switch inputs based on selection
            if (linkType === 'web_link') {
                webLinkInputs.style.display = 'flex';
                taskLinkSearch.style.display = 'none';
                taskSearchResults.style.display = 'none';
                taskLinkSearch.value = '';
            } else {
                webLinkInputs.style.display = 'none';
                taskLinkSearch.style.display = 'block';
                taskLinkSearch.focus();
            }
        }

        // Close dropdown
        const dropdown = e.target.closest(".link-type-dropdown");
        if (dropdown) dropdown.classList.remove("active");
        return;
    }

    // Handle status button clicks
    if (e.target.closest("#status-current")) {
        e.preventDefault();
        e.stopPropagation();
        const dropdown = e.target.closest(".status-dropdown");
        const isActive = dropdown.classList.contains("active");

        // Close all dropdowns first
        document
            .querySelectorAll(".status-dropdown.active")
            .forEach((d) => d.classList.remove("active"));

        // Toggle this one if it wasn't active
        if (!isActive) {
            dropdown.classList.add("active");
            // Update options to show only unselected
            const hiddenStatus = document.getElementById("hidden-status");
            if (hiddenStatus) {
                updateStatusOptions(hiddenStatus.value);
            }
        }
        return;
    }

    // Handle status option clicks
    if (e.target.closest(".status-option")) {
        e.preventDefault();
        e.stopPropagation();
        const option = e.target.closest(".status-option");
        const status = option.dataset.status;
        const statusBadge = option.querySelector(".status-badge");
        const statusText = statusBadge.textContent.trim();

        // Update display
        const currentBtn = document.getElementById("status-current");
        const hiddenStatus = document.getElementById("hidden-status");

        if (currentBtn && hiddenStatus) {
            const currentBadge = currentBtn.querySelector(".status-badge");
            if (currentBadge) {
                currentBadge.className = `status-badge ${status}`;
                currentBadge.textContent = statusText;
            }
            hiddenStatus.value = status;
            updateTaskField('status', status);

        }

        // Close dropdown
        const dropdown = option.closest(".status-dropdown");
        if (dropdown) dropdown.classList.remove("active");
        
        // Update options for next time (after dropdown closes)
        setTimeout(() => updateStatusOptions(status), 100);
        return;
    }

    // Close dropdown when clicking outside
    const activeDropdowns = document.querySelectorAll(
        ".status-dropdown.active"
    );
    activeDropdowns.forEach((dropdown) => {
        if (!dropdown.contains(e.target)) {
            dropdown.classList.remove("active");
        }
    });
}


function setupPriorityDropdown() {
    if (document.body.hasAttribute("data-priority-dropdown-initialized")) {
        return;
    }
    document.body.setAttribute("data-priority-dropdown-initialized", "true");
    document.addEventListener("click", handlePriorityDropdown);
}

function handlePriorityDropdown(e) {
    // Handle priority button clicks
    if (e.target.closest("#priority-current")) {
        e.preventDefault();
        e.stopPropagation();
        const dropdown = e.target.closest(".priority-dropdown");
        const isActive = dropdown.classList.contains("active");

        // Close all dropdowns first
        document
            .querySelectorAll(".priority-dropdown.active")
            .forEach((d) => d.classList.remove("active"));

        // Toggle this one if it wasn't active
        if (!isActive) {
            dropdown.classList.add("active");
            // Update options to show only unselected
            const hiddenPriority = document.getElementById("hidden-priority");
            if (hiddenPriority) {
                updatePriorityOptions(hiddenPriority.value);
            }
        }
        return;
    }

    // Handle priority option clicks
    if (e.target.closest(".priority-option")) {
        e.preventDefault();
        e.stopPropagation();
        const option = e.target.closest(".priority-option");
        const priority = option.dataset.priority;
        const priorityText = option.textContent.trim();

        // Update display
        const currentBtn = document.getElementById("priority-current");
        const hiddenPriority = document.getElementById("hidden-priority");

        if (currentBtn && hiddenPriority) {
            const priorityLabel = priorityText.toUpperCase();
            currentBtn.innerHTML = `<span class="priority-pill priority-${priority}">${priorityLabel}</span> <span class="dropdown-arrow">▼</span>`;
            hiddenPriority.value = priority;
            updateTaskField('priority', priority);
        }

        // Close dropdown
        const dropdown = option.closest(".priority-dropdown");
        if (dropdown) dropdown.classList.remove("active");
        
        // Update options for next time (after dropdown closes)
        setTimeout(() => updatePriorityOptions(priority), 100);
        return;
    }

    // Close dropdown when clicking outside
    const activePriorityDropdowns = document.querySelectorAll(
        ".priority-dropdown.active"
    );
    activePriorityDropdowns.forEach((dropdown) => {
        if (!dropdown.contains(e.target)) {
            dropdown.classList.remove("active");
        }
    });
}

function updatePriorityOptions(selectedPriority) {
    const priorityOptions = document.getElementById("priority-options");
    if (!priorityOptions) return;
    
    // Using imported PRIORITY_OPTIONS
    const allPriorities = PRIORITY_OPTIONS.map((priority) => ({
        ...priority,
        label: getPriorityLabel(priority.value)
    }));
    
    // Show only unselected priorities
    const availableOptions = allPriorities.filter(p => p.value !== selectedPriority);
    
    priorityOptions.innerHTML = availableOptions.map(priority => 
        `<div class="priority-option" data-priority="${priority.value}">
            <span class="priority-pill priority-${priority.value}">${priority.label.toUpperCase()}</span>
        </div>`
    ).join("");
}

function updateStatusOptions(selectedStatus) {
    const statusOptions = document.getElementById("status-options");
    if (!statusOptions) return;

    const allStatuses = [
        { value: "backlog", label: getStatusLabel("backlog") },
        { value: "todo", label: getStatusLabel("todo") },
        { value: "progress", label: getStatusLabel("progress") },
        { value: "review", label: getStatusLabel("review") },
        { value: "done", label: getStatusLabel("done") }
    ];

    // Filter out disabled statuses (e.g., review status when disabled)
    let enabledStatuses = allStatuses;
    if (window.enableReviewStatus === false) {
        enabledStatuses = allStatuses.filter(s => s.value !== "review");
    }

    // Show only unselected statuses
    const availableOptions = enabledStatuses.filter(s => s.value !== selectedStatus);

    statusOptions.innerHTML = availableOptions.map(status =>
        `<div class="status-option" data-status="${status.value}">
            <span class="status-badge ${status.value}">${status.label}</span>
        </div>`
    ).join("");
}

function setupProjectDropdown() {
    // Only set up once by checking if already initialized
    if (document.body.hasAttribute("data-project-dropdown-initialized")) {
        return;
    }
    document.body.setAttribute("data-project-dropdown-initialized", "true");
    document.addEventListener("click", handleProjectDropdown);
}

function handleProjectDropdown(e) {
    // Handle project button clicks
    if (e.target.closest("#project-current")) {
        e.preventDefault();
        e.stopPropagation();
    const dropdown = e.target.closest(".project-dropdown");
        const isActive = dropdown.classList.contains("active");

        // Close all dropdowns first
        document
            .querySelectorAll(".project-dropdown.active")
            .forEach((d) => d.classList.remove("active"));

        // Toggle this one if it wasn't active
        if (!isActive) {
            dropdown.classList.add("active");
            // Populate options when opening into this dropdown's options container
            showProjectDropdownPortal(dropdown);
        }
        return;
    }

    // Handle project option clicks
    if (e.target.closest(".project-option")) {
        e.preventDefault();
        e.stopPropagation();
        const option = e.target.closest(".project-option");
        const projectId = option.dataset.projectId;
        const projectText = option.textContent.trim();

        // Update display
    const currentBtn = document.getElementById("project-current");
    const hiddenProject = document.getElementById("hidden-project");

        if (currentBtn && hiddenProject) {
            const projectTextSpan = currentBtn.querySelector(".project-text");
            if (projectTextSpan) {
                if (projectId) {
                    projectTextSpan.innerHTML = buildProjectCurrentHTML(projectId, projectText);
                } else {
                    projectTextSpan.textContent = projectText;
                }
            }
            hiddenProject.value = projectId;
            updateTaskField('projectId', projectId);
            updateTaskProjectOpenBtn(projectId);
        }

        // Close dropdown
        const dropdown = option.closest(".project-dropdown");
        if (dropdown) dropdown.classList.remove("active");
        hideProjectDropdownPortal();
        return;
    }

    // Close dropdown when clicking outside
    const activeDropdowns = document.querySelectorAll(".project-dropdown.active");
    if (activeDropdowns.length > 0 && !e.target.closest(".project-dropdown")) {
        activeDropdowns.forEach((d) => d.classList.remove("active"));
        hideProjectDropdownPortal();
    }
}

function updateTaskProjectOpenBtn(projectId) {
    const btn = document.getElementById('task-project-open-btn');
    if (!btn) return;
    btn.style.display = projectId ? 'inline-flex' : 'none';
}

// Builds the inner HTML for the task modal's project dropdown trigger:
// color swatch + name, plus an ARCHIVED badge when the project is archived.
function buildProjectCurrentHTML(projectId, projectName) {
    if (!projectId) return escapeHtml(projectName);
    const numericId = parseInt(projectId);
    const colorSquare = `<span style="display: inline-block; width: 10px; height: 10px; background-color: ${getProjectColor(numericId)}; border-radius: 2px; margin-right: 8px; vertical-align: middle;"></span>`;
    const proj = projects.find(p => String(p.id) === String(projectId));
    const archivedTag = (proj && proj.archivedAt)
        ? `<span class="project-archived-tag">${escapeHtml(t('projects.status.archived'))}</span>`
        : '';
    return colorSquare + escapeHtml(projectName) + archivedTag;
}

function openSelectedProjectFromTask() {
    const hiddenProject = document.getElementById('hidden-project');
    if (!hiddenProject || !hiddenProject.value) return;
    const projectId = parseInt(hiddenProject.value, 10);
    if (!projectId) return;
    // Close task modal before navigating
    closeModal('task-modal');
    showProjectDetails(projectId, 'projects');
}

function populateProjectDropdownOptions(dropdownEl) {
    const projectOptions = dropdownEl ? dropdownEl.querySelector('.project-options') : null;
    const hiddenProject = document.getElementById('hidden-project');
    const selectedId = hiddenProject ? (hiddenProject.value || '') : '';
    // Only include the clear option when a specific project is selected
    let optionsHTML = selectedId ? `<div class="project-option" data-project-id="">${t('tasks.project.selectPlaceholder')}</div>` : '';
    if (projects && projects.length > 0) {
        optionsHTML += projects
            .slice()
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
            .filter(project => selectedId === '' || String(project.id) !== String(selectedId))
            .map(project => {
                const colorSquare = `<span style="display: inline-block; width: 10px; height: 10px; background-color: ${getProjectColor(project.id)}; border-radius: 2px; margin-right: 8px; vertical-align: middle;"></span>`;
                return `<div class="project-option" data-project-id="${project.id}">${colorSquare}${escapeHtml(project.name)}</div>`;
            })
            .join("");
    }
    if (projectOptions) projectOptions.innerHTML = optionsHTML;
    return optionsHTML;
}

// ===== Portal-based floating dropdown =====
let projectPortalEl = null;
let projectPortalAnchor = null;
let projectPortalScrollParents = [];

function showProjectDropdownPortal(dropdownEl) {
    // Ensure local options have the correct items for keyboard nav fallback
    const optionsHTML = populateProjectDropdownOptions(dropdownEl) || '';

    // Create portal container if needed
    if (!projectPortalEl) {
        projectPortalEl = document.createElement('div');
        projectPortalEl.className = 'project-options-portal';
        document.body.appendChild(projectPortalEl);
    }
    // Build portal with search input and options container
    projectPortalEl.innerHTML = `
        <div class="project-portal-search">
            <input type="text" class="form-input" id="project-portal-search-input" placeholder="${t('projects.modal.searchProjects')}" autocomplete="off" />
        </div>
        <div class="project-portal-options">${optionsHTML}</div>
    `;
    projectPortalEl.style.display = 'block';

    // Anchor is the trigger button
    const button = dropdownEl.querySelector('#project-current');
    projectPortalAnchor = button;
    positionProjectPortal(button, projectPortalEl);
    // Re-position on next frame to account for fonts/scrollbar layout
    requestAnimationFrame(() => positionProjectPortal(button, projectPortalEl));

    // Delegate clicks within portal to original handler
    projectPortalEl.addEventListener('click', (evt) => {
        evt.stopPropagation();
        const opt = evt.target.closest('.project-option');
        if (!opt) return;
        // Simulate selection using existing logic
        const projectId = opt.dataset.projectId;
        const projectText = opt.textContent.trim();
        const currentBtn = document.getElementById('project-current');
        const hiddenProject = document.getElementById('hidden-project');
        if (currentBtn && hiddenProject) {
            const projectTextSpan = currentBtn.querySelector('.project-text');
            if (projectTextSpan) {
                if (projectId) {
                    projectTextSpan.innerHTML = buildProjectCurrentHTML(projectId, projectText);
                } else {
                    projectTextSpan.textContent = projectText;
                }
            }
            hiddenProject.value = projectId;
            updateTaskField('projectId', projectId);
            updateTaskProjectOpenBtn(projectId);
        }
        const dd = button.closest('.project-dropdown');
        if (dd) dd.classList.remove('active');
        hideProjectDropdownPortal();
    }, { once: true });

    // Live filtering
    const searchInput = projectPortalEl.querySelector('#project-portal-search-input');
    if (searchInput) {
        searchInput.addEventListener('input', () => filterProjectPortalList(searchInput.value));
        // Autofocus for quick typing
        setTimeout(() => searchInput.focus(), 0);
    }

    // Close behaviors
    window.addEventListener('scroll', handleProjectPortalClose, true);
    window.addEventListener('resize', handleProjectPortalClose, true);
    document.addEventListener('keydown', handleProjectPortalEsc, true);

    // Hide inline options to avoid duplicate rendering underneath
    const inlineOptions = dropdownEl.querySelector('.project-options');
    if (inlineOptions) inlineOptions.style.display = 'none';

    // Listen to scroll on any scrollable ancestor (modal columns, body, etc.)
    attachScrollListeners(button);
}

function positionProjectPortal(button, portal) {
    const rect = button.getBoundingClientRect();
    // Match width to trigger
    portal.style.width = `${rect.width}px`;
    // Prefer below; if not enough space, flip above
    const viewportH = window.innerHeight;
    const portalHeight = Math.min(portal.scrollHeight, 200); // matches CSS max-height
    const spaceBelow = viewportH - rect.bottom;
    const showAbove = spaceBelow < portalHeight + 12; // small buffer
    const top = showAbove ? Math.max(8, rect.top - portalHeight - 4) : rect.bottom + 4;
    const viewportW = window.innerWidth;
    const portalWidth = portal.getBoundingClientRect().width || rect.width;
    const desiredLeft = rect.left;
    const clampedLeft = Math.min(
        Math.max(8, desiredLeft),
        Math.max(8, viewportW - portalWidth - 8)
    );
    portal.style.left = `${clampedLeft}px`;
    portal.style.top = `${top}px`;
}

function hideProjectDropdownPortal() {
    if (projectPortalEl) {
        projectPortalEl.style.display = 'none';
        projectPortalEl.innerHTML = '';
    }
    // Restore inline options visibility
    document.querySelectorAll('.project-dropdown .project-options').forEach(el => {
        el.style.display = '';
    });
    window.removeEventListener('scroll', handleProjectPortalClose, true);
    window.removeEventListener('resize', handleProjectPortalClose, true);
    document.removeEventListener('keydown', handleProjectPortalEsc, true);
    projectPortalAnchor = null;
    detachScrollListeners();
}

function handleProjectPortalClose() {
    if (!projectPortalAnchor || !projectPortalEl || projectPortalEl.style.display === 'none') return;
    // Reposition on scroll/resize if still open and anchor exists
    if (document.querySelector('.project-dropdown.active')) {
        positionProjectPortal(projectPortalAnchor, projectPortalEl);
    } else {
        hideProjectDropdownPortal();
    }
}

function attachScrollListeners(anchor) {
    detachScrollListeners();
    projectPortalScrollParents = getScrollableAncestors(anchor);
    projectPortalScrollParents.forEach(el => {
        el.addEventListener('scroll', handleProjectPortalClose, { capture: true, passive: true });
    });
}

function detachScrollListeners() {
    if (!projectPortalScrollParents || projectPortalScrollParents.length === 0) return;
    projectPortalScrollParents.forEach(el => {
        el.removeEventListener('scroll', handleProjectPortalClose, { capture: true });
    });
    projectPortalScrollParents = [];
}

function getScrollableAncestors(el) {
    const result = [];
    let node = el.parentElement;
    while (node && node !== document.body) {
        const style = getComputedStyle(node);
        const isScrollableY = /(auto|scroll|overlay)/.test(style.overflowY);
        if (isScrollableY && node.scrollHeight > node.clientHeight) {
            result.push(node);
        }
        node = node.parentElement;
    }
    // Always include documentElement and body scroll contexts
    result.push(document.documentElement);
    result.push(document.body);
    return result;
}

function handleProjectPortalEsc(e) {
    if (e.key === 'Escape') {
        hideProjectDropdownPortal();
        document.querySelectorAll('.project-dropdown.active').forEach(d => d.classList.remove('active'));
    }
}

function formatText(command) {
    document.execCommand(command, false, null);
    document.getElementById("description-editor").focus();
}

function insertHeading(level) {
    document.execCommand("formatBlock", false, level);
    document.getElementById("description-editor").focus();
}

function insertDivider() {
    document.execCommand("insertHTML", false, "<hr>");
    document.getElementById("description-editor").focus();
}

function formatTaskText(command) {
    runTaskDescriptionCommand(command);
    focusTaskDescriptionEditor();
}

function insertTaskHeading(level) {
    insertTaskDescriptionHeading(level);
    focusTaskDescriptionEditor();
}

function insertTaskDivider() {
    insertTaskDescriptionDivider();
    focusTaskDescriptionEditor();
}

function insertTaskChecklist() {
    insertTaskDescriptionChecklist();
    focusTaskDescriptionEditor();
}

function formatProjectDescriptionText(command) {
    runProjectDescriptionCommand(command);
}

function insertProjectDescriptionHeadingCmd(level) {
    insertProjectDescriptionHeading(level);
}

function insertProjectDescriptionDividerCmd() {
    insertProjectDescriptionDivider();
}

function insertProjectDescriptionChecklistCmd() {
    insertProjectDescriptionChecklist();
}

function insertProjectDescriptionDateChipCmd(date = null) {
    const state = getProjectDescriptionDateChipState();
    const nextDate = typeof date === "string" && date ? date : getLocalTodayIso();
    if (state.active) {
        updateProjectDescriptionDateChip({ date: nextDate });
    } else {
        insertProjectDescriptionDateChip({ date: nextDate });
    }
}

function insertTaskDateChip(date = null) {
    const state = getTaskDescriptionDateChipState();
    const nextDate = typeof date === "string" && date ? date : getLocalTodayIso();
    if (state.active) {
        updateTaskDescriptionDateChip(nextDate);
    } else {
        insertTaskDescriptionDateChip(nextDate);
    }
    focusTaskDescriptionEditor();
}

// ── Internal link beautification ─────────────────────────────────────────────
// Replaces raw internal links (e.g. https://nautilusapp.science/#project-24)
// with named chips showing the project/task title.
// Set force=true to beautify even in edit mode (e.g., for task descriptions)
function _findDocRecordById(docId) {
    return getDocsState().items.find((item) => String(item.id) === String(docId)) || null;
}

function buildLinkPresentationContext() {
    return {
        appOrigin: window.location.origin,
        projects,
        tasks,
        findDocById: _findDocRecordById,
        t
    };
}

function stripDescriptionHtml(html) {
    if (!html) return "";
    if (typeof document !== "undefined") {
        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || "";
    }
    return html.replace(/<[^>]+>/g, "");
}

setDescriptionLinkPresentationContextResolver(() => buildLinkPresentationContext());
setDocsLinkPresentationContextResolver(() => buildLinkPresentationContext());
setDocsFileAttachmentResolver((attrs = {}) => resolveDocsFileAttachmentPresentation(attrs));
setDocsFileUploadHandler((files = [], context = {}) => handleDocsEditorSurfaceFiles(files, context));

function applyLinkPresentationAttributes(link, presentation) {
    if (!link || !presentation) return;

    link.setAttribute("href", presentation.href || link.getAttribute("href") || "");
    link.className = presentation.className || "";
    link.textContent = presentation.displayLabel || link.textContent || "";
    link.style.fontWeight = "";
    delete link.dataset.linkDisplay;

    if (presentation.target) {
        link.setAttribute("target", presentation.target);
    } else {
        link.removeAttribute("target");
    }

    if (presentation.rel) {
        link.setAttribute("rel", presentation.rel);
    } else {
        link.removeAttribute("rel");
    }

    if (presentation.dataAction) {
        link.dataset.action = presentation.dataAction;
    } else {
        delete link.dataset.action;
    }

    if (presentation.dataParam) {
        link.dataset.param = presentation.dataParam;
    } else {
        delete link.dataset.param;
    }
}

function beautifyInternalLinks(container, force = false) {
    if (!container) return;
    
    // Skip beautification in docs edit mode unless forced
    if (!force) {
        const docsCanvas = container.closest('.docs-canvas');
        const docsMode = docsCanvas?.dataset.mode;
        if (docsMode === 'edit') {
            return;
        }
        
        // Also skip if container has ProseMirror-focused class (editing)
        if (container.classList.contains('ProseMirror-focused') || 
            container.closest('.ProseMirror-focused')) {
            return;
        }
    }
    
    const context = buildLinkPresentationContext();
    container.querySelectorAll('a[href]:not(.docs-child-list-block__link)').forEach(link => {
        const href = link.getAttribute('href') || '';
        const presentation = resolveLinkPresentation(href, context);
        if (!presentation) return;
        applyLinkPresentationAttributes(link, presentation);
    });

    // Defensive: Ensure ALL external links open in new tab
    container.querySelectorAll('a[href]:not(.docs-child-list-block__link)').forEach(link => {
        const href = link.getAttribute('href') || '';
        // Skip internal hash links, hash URLs, and internal app links
        if (href.startsWith('#') || href.startsWith('/') || href.startsWith('?') || 
            href.includes(window.location.origin)) {
            return;
        }
        // All other links are external - ensure they open in new tab
        if (!link.hasAttribute('target') || link.getAttribute('target') !== '_blank') {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer nofollow');
        }
    });
}
// ─────────────────────────────────────────────────────────────────────────────

// ── Docs Find & Replace ───────────────────────────────────────────────────────
const _docsFR = { query: '', matches: [], idx: -1 };

function _docsFRUpdateCount() {
    const el = document.getElementById('docs-find-count');
    if (!el) return;
    const { matches, idx, query } = _docsFR;
    el.textContent = !query ? '' : matches.length === 0 ? t('docs.findReplace.noMatch') : `${idx + 1}/${matches.length}`;
}

function _docsFRRefresh(jumpToFirst = true) {
    const input = document.getElementById('docs-find-input');
    _docsFR.query = input?.value ?? '';
    _docsFR.matches = _docsFR.query ? findDocsTextMatches(_docsFR.query) : [];
    _docsFR.idx = _docsFR.matches.length > 0 ? 0 : -1;
    if (jumpToFirst && _docsFR.idx >= 0) selectDocsMatch(_docsFR.matches[0].from, _docsFR.matches[0].to);
    _docsFRUpdateCount();
}

function openDocsFindReplace() {
    const panel = document.getElementById('docs-find-replace-panel');
    if (!panel) return;
    panel.hidden = false;
    const input = document.getElementById('docs-find-input');
    if (input) { input.focus(); input.select(); }
}

function closeDocsFindReplace() {
    const panel = document.getElementById('docs-find-replace-panel');
    if (panel) panel.hidden = true;
    _docsFR.query = ''; _docsFR.matches = []; _docsFR.idx = -1;
    _docsFRUpdateCount();
}

function docsFRNext() {
    if (!_docsFR.matches.length) { _docsFRRefresh(true); return; }
    _docsFR.idx = (_docsFR.idx + 1) % _docsFR.matches.length;
    selectDocsMatch(_docsFR.matches[_docsFR.idx].from, _docsFR.matches[_docsFR.idx].to);
    _docsFRUpdateCount();
}

function docsFRPrev() {
    if (!_docsFR.matches.length) { _docsFRRefresh(true); return; }
    _docsFR.idx = (_docsFR.idx - 1 + _docsFR.matches.length) % _docsFR.matches.length;
    selectDocsMatch(_docsFR.matches[_docsFR.idx].from, _docsFR.matches[_docsFR.idx].to);
    _docsFRUpdateCount();
}

function docsFRReplaceOne() {
    if (_docsFR.idx < 0 || !_docsFR.matches.length) return;
    const replacement = document.getElementById('docs-replace-input')?.value ?? '';
    const m = _docsFR.matches[_docsFR.idx];
    replaceDocsMatch(m.from, m.to, replacement);
    setTimeout(() => {
        _docsFRRefresh(false);
        if (_docsFR.matches.length > 0) {
            _docsFR.idx = Math.min(_docsFR.idx, _docsFR.matches.length - 1);
            selectDocsMatch(_docsFR.matches[_docsFR.idx].from, _docsFR.matches[_docsFR.idx].to);
        }
        _docsFRUpdateCount();
    }, 30);
}

function docsFRReplaceAll() {
    const query = document.getElementById('docs-find-input')?.value ?? '';
    const replacement = document.getElementById('docs-replace-input')?.value ?? '';
    if (!query) return;
    const count = replaceAllDocsText(query, replacement);
    _docsFR.query = ''; _docsFR.matches = []; _docsFR.idx = -1;
    _docsFRUpdateCount();
    if (count > 0) showSuccessNotification(t('docs.findReplace.replacedCount').replace('{count}', count));
}

function bindDocsFindReplacePanel() {
    const panel = document.getElementById('docs-find-replace-panel');
    if (!panel || panel.dataset.bound === 'true') return;
    panel.dataset.bound = 'true';
    // Prevent editor losing focus when interacting with panel
    panel.addEventListener('mousedown', e => { if (e.target.tagName !== 'INPUT') e.preventDefault(); });
    document.getElementById('docs-find-input')?.addEventListener('input', () => _docsFRRefresh(true));
    document.getElementById('docs-find-input')?.addEventListener('keydown', e => {
        if (e.key === 'Enter') { e.preventDefault(); e.shiftKey ? docsFRPrev() : docsFRNext(); }
        if (e.key === 'Escape') closeDocsFindReplace();
    });
    document.getElementById('docs-replace-input')?.addEventListener('keydown', e => {
        if (e.key === 'Enter') { e.preventDefault(); docsFRReplaceOne(); }
        if (e.key === 'Escape') closeDocsFindReplace();
    });
    document.getElementById('docs-find-prev-btn')?.addEventListener('click', docsFRPrev);
    document.getElementById('docs-find-next-btn')?.addEventListener('click', docsFRNext);
    document.getElementById('docs-find-close-btn')?.addEventListener('click', closeDocsFindReplace);
    document.getElementById('docs-replace-one-btn')?.addEventListener('click', docsFRReplaceOne);
    document.getElementById('docs-replace-all-btn')?.addEventListener('click', docsFRReplaceAll);
    // Find & Replace button in toolbar
    document.getElementById('docs-find-replace-btn')?.addEventListener('click', () => {
        const p = document.getElementById('docs-find-replace-panel');
        if (p?.hidden === false) closeDocsFindReplace(); else openDocsFindReplace();
    });
    // Ctrl+H / Cmd+H keyboard shortcut on the editor container
    document.getElementById('project-doc-editor')?.addEventListener('keydown', e => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'h') { e.preventDefault(); openDocsFindReplace(); }
    });
}
// ─────────────────────────────────────────────────────────────────────────────

function descriptionLooksLikeHtml(description) {
    return /<\/?[a-z][\s\S]*>/i.test(String(description || ''));
}

function convertPlainTextDescriptionToHtml(description = '') {
    const text = String(description || '').replace(/\r\n?/g, '\n').trim();
    if (!text) return '';
    if (descriptionLooksLikeHtml(text)) return text;

    const lines = text.split('\n');
    const blocks = [];
    let paragraphLines = [];
    let listType = null;
    let listItems = [];

    function flushParagraph() {
        if (!paragraphLines.length) return;
        blocks.push(`<p>${paragraphLines.map((line) => escapeHtml(line)).join('<br>')}</p>`);
        paragraphLines = [];
    }

    function flushList() {
        if (!listType || !listItems.length) {
            listType = null;
            listItems = [];
            return;
        }
        if (listType === 'checklist') {
            const itemsHtml = listItems.map((item) =>
                `<li data-type="taskItem" data-checked="${item.checked ? 'true' : 'false'}"><p>${escapeHtml(item.text)}</p></li>`
            ).join('');
            blocks.push(`<ul data-type="taskList">${itemsHtml}</ul>`);
        } else {
            const tagName = listType === 'ordered' ? 'ol' : 'ul';
            blocks.push(`<${tagName}>${listItems.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</${tagName}>`);
        }
        listType = null;
        listItems = [];
    }

    lines.forEach((rawLine) => {
        const line = String(rawLine || '').trim();
        if (!line) {
            flushParagraph();
            flushList();
            return;
        }

        const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
        if (headingMatch) {
            flushParagraph();
            flushList();
            const level = headingMatch[1].length;
            blocks.push(`<h${level}>${escapeHtml(headingMatch[2].trim())}</h${level}>`);
            return;
        }

        const orderedMatch = line.match(/^\d+[.)]\s+(.+)$/);
        if (orderedMatch) {
            flushParagraph();
            if (listType && listType !== 'ordered') flushList();
            listType = 'ordered';
            listItems.push(orderedMatch[1].trim());
            return;
        }

        const checklistMatch = line.match(/^(?:[-*•])\s+\[( |x|X)\]\s+(.+)$/);
        if (checklistMatch) {
            flushParagraph();
            if (listType && listType !== 'checklist') flushList();
            listType = 'checklist';
            listItems.push({ text: checklistMatch[2].trim(), checked: checklistMatch[1].toLowerCase() === 'x' });
            return;
        }

        const unorderedMatch = line.match(/^(?:[-*•])\s+(.+)$/);
        if (unorderedMatch) {
            flushParagraph();
            if (listType && listType !== 'unordered') flushList();
            listType = 'unordered';
            listItems.push(unorderedMatch[1].trim());
            return;
        }

        flushList();
        paragraphLines.push(line);
    });

    flushParagraph();
    flushList();

    return blocks.join('');
}

function beautifyDescriptionLinksHtml(html = '') {
    const normalizedHtml = String(html || '').trim();
    if (!normalizedHtml) return '';
    if (typeof document === 'undefined') return normalizedHtml;

    const container = document.createElement('div');
    container.innerHTML = normalizedHtml;
    beautifyInternalLinks(container, true);

    container.querySelectorAll('a[href]').forEach((link) => {
        const href = String(link.getAttribute('href') || '').trim();
        const presentation = resolveLinkPresentation(href, buildLinkPresentationContext());
        const variant = presentation?.variant || link.getAttribute('data-link-variant') || 'external';
        const label = String(link.textContent || presentation?.displayLabel || href).trim();

        link.setAttribute('data-link-chip', 'true');
        link.setAttribute('data-link-label', label);
        link.setAttribute('data-link-variant', variant);
        link.setAttribute('contenteditable', 'false');
        link.setAttribute('spellcheck', 'false');
        link.className = `docs-internal-link docs-internal-link--${variant} description-link-chip-node`;
        link.textContent = label;
    });

    return container.innerHTML;
}

// Auto-link plain URLs in description HTML
function autoLinkifyDescription(html) {
    if (!html) return html;

    const container = document.createElement('div');
    container.innerHTML = html;

    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];

    while (walker.nextNode()) {
        const node = walker.currentNode;
        if (!node || !node.nodeValue) continue;
        const parentEl = node.parentElement;
        if (parentEl && parentEl.closest('a')) continue;
        urlRegex.lastIndex = 0;
        if (!urlRegex.test(node.nodeValue)) continue;
        textNodes.push(node);
    }

    textNodes.forEach(node => {
        const text = node.nodeValue;
        let lastIndex = 0;
        urlRegex.lastIndex = 0;
        const frag = document.createDocumentFragment();
        let match;

        while ((match = urlRegex.exec(text)) !== null) {
            const url = match[0];
            if (match.index > lastIndex) {
                frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
            }
            const a = document.createElement('a');
            a.href = url;
            a.textContent = url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            frag.appendChild(a);
            lastIndex = match.index + url.length;
        }

        if (lastIndex < text.length) {
            frag.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        if (node.parentNode) {
            node.parentNode.replaceChild(frag, node);
        }
    });

    return container.innerHTML;
}

function bindRichEditorLinkClicks(editorElement) {
    if (!editorElement || editorElement.dataset.linkClicksBound === "true") return;

    editorElement.dataset.linkClicksBound = "true";
    editorElement.addEventListener("click", function (e) {
        const link = e.target.closest("a");
        if (!link) return;
        if (link.hasAttribute("data-link-chip") && !e.metaKey && !e.ctrlKey) {
            return;
        }
        const href = link.getAttribute("href");
        if (!href) return;
        const sel = window.getSelection();
        if (sel && sel.toString()) return;
        e.preventDefault();
        e.stopPropagation();

        const presentation = resolveLinkPresentation(href, buildLinkPresentationContext());
        if (presentation?.dataAction === "openTaskDetails" && presentation.dataParam) {
            openTaskDetails(parseInt(presentation.dataParam, 10));
            return;
        }

        const nextHref = presentation?.href || href;
        const shouldOpenInNewTab = presentation?.target === "_blank" || /^https?:\/\//i.test(nextHref);

        if (shouldOpenInNewTab) {
            window.open(nextHref, "_blank", "noopener,noreferrer");
            return;
        }

        window.location.href = nextHref;
    });
}

// Initialize task description editor and shared modal input handlers.
document.addEventListener("DOMContentLoaded", function () {
    const taskForm = document.getElementById("task-form");
    const taskHiddenField = document.getElementById("task-description-hidden");
    let originalDescriptionHTML = "";

    initializeTaskDescriptionEditor({
        placeholder: "",
        onFocus: (html) => {
            originalDescriptionHTML = html;
        },
        onUpdate: (html) => {
            if (taskHiddenField) taskHiddenField.value = html;
        },
        onBlur: (html) => {
            if (taskHiddenField) taskHiddenField.value = html;
            if (taskForm && taskForm.dataset.editingTaskId && html !== originalDescriptionHTML) {
                updateTaskField("description", html);
            }
        }
    });

    const taskEditor = document.getElementById("task-description-editor");
    if (taskEditor) {
        // If the user pastes an image into the description editor, treat it as an attachment
        // (same UX as feedback screenshot paste) instead of inserting a giant inline <img>.
        taskEditor.addEventListener("paste", function (e) {
            if (!e.clipboardData) return;
            const file = Array.from(e.clipboardData.files || [])[0];
            if (!file || !file.type || !file.type.startsWith("image/")) return;

            e.preventDefault();
            e.stopPropagation();

            const dropzone = document.getElementById("attachment-file-dropzone");
            if (typeof uploadTaskAttachmentFile === "function") {
                uploadTaskAttachmentFile(file, dropzone).catch((err) => {
                    console.error("Failed to upload pasted image as attachment:", err);
                });
            }
        });
        taskEditor.addEventListener("click", async (e) => {
            const chip = e.target.closest("[data-task-date-chip]");
            if (!chip) return;
            e.preventDefault();
            e.stopPropagation();
            await openTaskDateChipPicker(chip);
        });
        bindRichEditorLinkClicks(taskEditor);
    }

    // Task search with live results for link type dropdown
    const taskLinkSearch = document.getElementById('task-link-search');
    const taskSearchResults = document.getElementById('task-search-results');
    
    if (taskLinkSearch && taskSearchResults) {
        taskLinkSearch.addEventListener('input', function(e) {
            const searchTerm = e.target.value.trim().toLowerCase();
            const currentTaskId = parseInt(document.getElementById('task-form')?.dataset.editingTaskId);
            const currentTask = tasks.find(t => t.id === currentTaskId);
            const linkTypeValue = document.getElementById('link-type-value');
            const currentLinkType = linkTypeValue ? linkTypeValue.value : 'relates_to';
            
            if (searchTerm.length < 2) {
                taskSearchResults.style.display = 'none';
                taskSearchResults.innerHTML = '';
                return;
            }
            
            // Get already linked task IDs for the current link type
            const alreadyLinkedIds = new Set();
            if (currentTask && currentTask.links && Array.isArray(currentTask.links)) {
                currentTask.links.forEach(link => {
                    if (link.type === currentLinkType) {
                        alreadyLinkedIds.add(link.taskId);
                    }
                });
            }
            
            const matchingTasks = tasks.filter(task => {
                if (task.id === currentTaskId) return false;
                if (alreadyLinkedIds.has(task.id)) return false; // Filter out already linked tasks
                return task.title.toLowerCase().includes(searchTerm);
            }).slice(0, 10);
            
            if (matchingTasks.length === 0) {
                taskSearchResults.style.display = 'none';
                return;
            }
            
            taskSearchResults.style.display = 'block';
            taskSearchResults.innerHTML = matchingTasks.map(task => `
                <div class="task-search-result" data-task-id="${task.id}" style="padding: 10px; cursor: pointer; border-bottom: 1px solid var(--border); transition: background 0.15s;">
                    <div style="font-size: 13px; font-weight: 500; color: var(--text-primary);">Task #${task.id}: ${escapeHtml(task.title)}</div>
                    <div style="font-size: 12px; color: var(--text-muted); margin-top: 2px;">
                        <span class="status-badge ${task.status}">${escapeHtml(task.status)}</span>
                    </div>
                </div>
            `).join('');
            
            // Add click handlers
            taskSearchResults.querySelectorAll('.task-search-result').forEach(item => {
                item.addEventListener('mouseenter', function() {
                    this.style.background = 'var(--bg-tertiary)';
                });
                item.addEventListener('mouseleave', function() {
                    this.style.background = '';
                });
                item.addEventListener('click', async function() {
                    const targetId = parseInt(this.dataset.taskId);
                    const linkTypeValue = document.getElementById('link-type-value');
                    const linkType = linkTypeValue ? linkTypeValue.value : 'depends_on';
                    if (!currentTaskId || isNaN(currentTaskId)) {
                        // New task — queue link for after save
                        if (!window.tempLinks) window.tempLinks = [];
                        const alreadyQueued = window.tempLinks.some(l => l.targetTaskId === targetId && l.linkType === linkType);
                        if (!alreadyQueued) {
                            window.tempLinks.push({ targetTaskId: targetId, linkType });
                            renderTempLinks();
                        }
                    } else {
                        await addTaskRelationship(currentTaskId, targetId, linkType);
                    }
                    taskLinkSearch.value = '';
                    taskSearchResults.style.display = 'none';
                });
            });
        });
        
        taskLinkSearch.addEventListener('keydown', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                // Select first result if available
                const firstResult = taskSearchResults.querySelector('.task-search-result');
                if (firstResult) firstResult.click();
            }
        });
    }

    // Prevent Enter key in attachment fields from submitting form
    const attachmentUrl = document.getElementById('attachment-url');
    const attachmentName = document.getElementById('attachment-name');
    
    if (attachmentUrl) {
        attachmentUrl.addEventListener('keydown', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                addAttachment();
            }
        });
    }
    
    if (attachmentName) {
        attachmentName.addEventListener('keydown', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                addAttachment();
            }
        });
    }

    // Tag input Enter key support
    const tagInput = document.getElementById('tag-input');
    if (tagInput) {
        tagInput.addEventListener('keydown', function(e) {
            const dropdown = document.getElementById('tag-autocomplete-dropdown');
            const hasActiveAutocompleteItem = !!dropdown &&
                dropdown.style.display !== 'none' &&
                dropdown.querySelector('.tag-autocomplete-item.is-active');

            if (hasActiveAutocompleteItem) {
                return;
            }

            if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
            }
        });
    }

    // Project tag input Enter key support
    const projectTagInput = document.getElementById('project-tag-input');
    if (projectTagInput) {
        projectTagInput.addEventListener('keydown', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                addProjectTag();
            }
        });
    }

    // Existing feedback input code
    const feedbackInput = document.getElementById('feedback-description');
    if (feedbackInput) {
        const desktopPlaceholder = feedbackInput.getAttribute('placeholder') || '';
        const mobilePlaceholder = 'Describe the issue or idea.';
        const resolveIsMobile = () => {
            if (typeof window.matchMedia === 'function') {
                return window.matchMedia('(max-width: 768px)').matches;
            }
            return getIsMobileCached();
        };
        const applyFeedbackPlaceholder = () => {
            feedbackInput.placeholder = resolveIsMobile() ? mobilePlaceholder : desktopPlaceholder;
        };

        applyFeedbackPlaceholder();
        if (typeof window.matchMedia === 'function') {
            const mq = window.matchMedia('(max-width: 768px)');
            if (typeof mq.addEventListener === 'function') mq.addEventListener('change', applyFeedbackPlaceholder);
            else if (typeof mq.addListener === 'function') mq.addListener(applyFeedbackPlaceholder);
        }

        feedbackInput.addEventListener('keypress', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                addFeedbackItem();
            }
        });
    }
});


function loadCalendarState() {
    const today = new Date();
    let currentMonth = today.getMonth();
    let currentYear = today.getFullYear();

    // Restore the last viewed month so a refresh doesn't snap back to the
    // current month. (Values are written by saveCalendarState; the URL, when
    // present, takes precedence later via applyCalendarMonthFromURL.)
    try {
        const savedMonth = parseInt(localStorage.getItem('calendarMonth'), 10);
        const savedYear = parseInt(localStorage.getItem('calendarYear'), 10);
        if (Number.isInteger(savedMonth) && savedMonth >= 0 && savedMonth <= 11 &&
            Number.isInteger(savedYear) && savedYear >= 1970 && savedYear <= 9999) {
            currentMonth = savedMonth;
            currentYear = savedYear;
        }
    } catch (e) { /* localStorage unavailable: fall back to today */ }

    return { currentMonth, currentYear };
}

const calendarState = loadCalendarState();
let currentMonth = calendarState.currentMonth;
let currentYear = calendarState.currentYear;

// Save calendar state to localStorage
function saveCalendarState() {
localStorage.setItem('calendarMonth', currentMonth.toString());
    localStorage.setItem('calendarYear', currentYear.toString());
}

// Calendar month <-> URL persistence.
// The viewed month is encoded in the hash query as `month=YYYY-MM` so a page
// refresh restores it instead of snapping back to the current month.
function getCalendarMonthParam() {
    return `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
}

// Read `month` from the current hash and apply it to currentMonth/currentYear.
function applyCalendarMonthFromURL() {
    try {
        const [, queryString] = window.location.hash.slice(1).split('?');
        const monthStr = new URLSearchParams(queryString || '').get('month');
        const match = /^(\d{4})-(\d{1,2})$/.exec(monthStr || '');
        if (!match) return;
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        if (month >= 0 && month <= 11 && year >= 1970 && year <= 9999) {
            currentYear = year;
            currentMonth = month;
            saveCalendarState();
        }
    } catch (e) { /* non-fatal: fall back to in-memory month */ }
}

// capitalizeFirst, getCalendarDayNames, formatCalendarMonthYear imported from src/utils/string.js and src/utils/date.js

/**
 * Returns the projects array filtered by calendarProjectFilterState.
 * Used by renderCalendar().
 */
function hasCalendarProjectCriteria() {
    const s = calendarProjectFilterState;
    return !!s.search || s.statuses.size > 0 || s.tags.size > 0 || (s.updatedFilter && s.updatedFilter !== 'all');
}

// Funnel step 1: projects in scope = project criteria ∩ chosen projects.
function getCalendarScopedProjects() {
    const chosen = filterState.projects.size > 0
        ? new Set(Array.from(filterState.projects).map(id => parseInt(id, 10)))
        : null;
    return getCalendarFilteredProjects().filter(p => !chosen || chosen.has(Number(p.id)));
}

// Funnel: while project criteria are active, tasks only show inside those projects.
function getCalendarTaskProjectScope() {
    if (!hasCalendarProjectCriteria()) return null;
    return new Set(getCalendarFilteredProjects().map(p => Number(p.id)));
}

function applyCalendarProjectScopePreset(preset) {
    filterState.projects.clear();
    filterState.projectExcludeMode = false;
    calendarProjectFilterState.search = '';
    calendarProjectFilterState.statuses.clear();
    calendarProjectFilterState.statusExcludeMode = false;
    calendarProjectFilterState.tags.clear();
    calendarProjectFilterState.tagExcludeMode = false;
    calendarProjectFilterState.updatedFilter = 'all';
    if (preset === 'active') calendarProjectFilterState.statuses.add('active');

    document.querySelectorAll('#cal-group-project-status input[type="checkbox"]').forEach(cb => {
        cb.checked = calendarProjectFilterState.statuses.has(cb.value);
    });
    document.querySelectorAll('#cal-group-project-tags input[type="checkbox"]').forEach(cb => { cb.checked = false; });
    document.querySelectorAll('#cal-group-project-updated input[type="radio"]').forEach(rb => { rb.checked = rb.value === 'all'; });
    document.querySelectorAll('#cal-group-project-status .filter-mode-btn, #cal-group-project-tags .filter-mode-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.mode === 'include');
    });
    updateFilterModeUI('project');

    updateFilterBadges();
    updateCalendarProjectFilterBadges();
    updateCalClearBtn();
    renderAfterFilterChange();
    renderCalendar();
    updateClearButtonVisibility();
}

function hasCalendarTaskStepFilters() {
    return filterState.statuses.size > 0 || filterState.priorities.size > 0 || filterState.tags.size > 0
        || filterState.linkTypes.size > 0 || filterState.datePresets.size > 0 || !!filterState.dateFrom || !!filterState.dateTo;
}

function clearCalendarTaskStepFilters() {
    filterState.statuses.clear();
    filterState.statusExcludeMode = false;
    filterState.priorities.clear();
    filterState.priorityExcludeMode = false;
    filterState.tags.clear();
    filterState.tagExcludeMode = false;
    filterState.tagAndMode = false;
    filterState.linkTypes.clear();
    filterState.datePresets.clear();
    filterState.dateFrom = '';
    filterState.dateTo = '';
    document.querySelectorAll('#cal-filter-drawer .cal-task-filters .dropdown-panel input[type="checkbox"]:not([data-filter="project"])')
        .forEach((cb) => { cb.checked = false; });
    updateAllFilterModeUI();
    updateTagMatchToggleUI();
    updateFilterBadges();
    renderAfterFilterChange();
    updateClearButtonVisibility();
    renderCalendar();
}

// Step counts, shortcut state and chosen-project chips in the filter drawer.
function updateCalendarFunnelUI(scopedProjectCount, visibleTaskCount) {
    const projectsCount = document.getElementById('cal-step-projects-count');
    const liveProjectCount = projects.filter(p => !p.archivedAt).length;
    if (projectsCount) projectsCount.textContent = t('calendar.filters.projectsCount', { count: Math.min(scopedProjectCount, liveProjectCount), total: liveProjectCount });
    const tasksCount = document.getElementById('cal-step-tasks-count');
    if (tasksCount) tasksCount.textContent = t('calendar.filters.visibleCount', { count: visibleTaskCount });

    const s = calendarProjectFilterState;
    const noScope = filterState.projects.size === 0 && !hasCalendarProjectCriteria();
    const onlyActive = filterState.projects.size === 0 && !s.statusExcludeMode && s.statuses.size === 1
        && s.statuses.has('active') && s.tags.size === 0 && (!s.updatedFilter || s.updatedFilter === 'all') && !s.search;
    document.querySelectorAll('.cal-step-quick-btn').forEach(btn => {
        const pressed = btn.dataset.calScope === 'all' ? noScope : onlyActive;
        btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });
    const projectsClear = document.getElementById('cal-step-projects-clear');
    if (projectsClear) projectsClear.hidden = noScope;
    const tasksClear = document.getElementById('cal-step-tasks-clear');
    if (tasksClear) tasksClear.hidden = !hasCalendarTaskStepFilters();

    const wrap = document.getElementById('cal-scope-chips');
    if (!wrap) return;
    wrap.innerHTML = '';
    filterState.projects.forEach((id) => {
        const project = projects.find(p => String(p.id) === String(id));
        const label = id === 'none' ? t('tasks.noProject') : (project ? project.name : String(id));
        const chip = document.createElement('span');
        chip.className = 'cal-scope-chip';
        const dot = document.createElement('span');
        dot.className = 'cal-scope-chip-dot';
        if (project) dot.style.background = getProjectColor(project.id);
        const text = document.createElement('span');
        text.className = 'cal-scope-chip-text';
        text.textContent = label;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cal-scope-chip-remove';
        remove.setAttribute('aria-label', t('filters.chip.removeAria', { label }));
        remove.textContent = '×';
        remove.addEventListener('click', () => {
            filterState.projects.delete(id);
            syncFilterCheckboxesFromState('project');
            updateFilterBadges();
            renderAfterFilterChange();
            renderCalendar();
            updateClearButtonVisibility();
        });
        chip.append(dot, text, remove);
        wrap.appendChild(chip);
    });
}

function getCalendarFilteredProjects() {
    const { search, statuses, statusExcludeMode, tags, tagExcludeMode, updatedFilter } = calendarProjectFilterState;
    const searchLc = (search || '').toLowerCase();

    return projects.filter(p => {
        // Text search
        if (searchLc && !(p.name || '').toLowerCase().includes(searchLc) && !(p.description || '').toLowerCase().includes(searchLc)) return false;

        // Status filter
        if (statuses.size > 0) {
            // Same derived status the bars, Projects page and Kai use (not the raw stored field).
            const pStatus = getProjectStatus(p.id);
            const hasStatus = statuses.has(pStatus);
            if (statusExcludeMode ? hasStatus : !hasStatus) return false;
        }

        // Tags filter
        if (tags.size > 0) {
            const pTags = new Set(p.tags || []);
            const hasAnyTag = [...tags].some(t => pTags.has(t));
            if (tagExcludeMode ? hasAnyTag : !hasAnyTag) return false;
        }

        // Updated filter
        if (updatedFilter && updatedFilter !== 'all') {
            const updatedAt = p.updatedAt || p.createdAt;
            if (!updatedAt) return false;
            const ms = { '5m': 5*60*1000, '30m': 30*60*1000, '24h': 24*60*60*1000, week: 7*24*60*60*1000, month: 30*24*60*60*1000 };
            if (Date.now() - new Date(updatedAt).getTime() > (ms[updatedFilter] || Infinity)) return false;
        }

        return true;
    });
}

function renderCalendar() {
    const renderTimer = debugTimeStart("render", "calendar", {
        taskCount: tasks.length,
        month: currentMonth + 1,
        year: currentYear
    });
    const locale = getLocale();
    const dayNames = getCalendarDayNames(locale);
    const today = new Date();

    // Update month/year display. Render both a full and an abbreviated label;
    // CSS shows the full name on wide screens and the short one on narrow phones
    // (so a long month like "Septiembre de 2026" can't wrap/clip the header).
    const monthYearEl = document.getElementById("calendar-month-year");
    const fullLabel = formatCalendarMonthYear(locale, currentYear, currentMonth);
    const shortLabel = formatCalendarMonthYearShort(locale, currentYear, currentMonth);
    monthYearEl.innerHTML = '';
    const fullSpan = document.createElement('span');
    fullSpan.className = 'cal-month-full';
    fullSpan.textContent = fullLabel;
    const shortSpan = document.createElement('span');
    shortSpan.className = 'cal-month-short';
    shortSpan.textContent = shortLabel;
    monthYearEl.append(fullSpan, shortSpan);

    // Prepare calendar grid data
    const filteredProjectIds = filterState.projects.size > 0
        ? new Set(Array.from(filterState.projects).map(id => parseInt(id, 10)))
        : null;

    const scopedProjects = getCalendarScopedProjects();
    const calendarProjects = calendarShowProjects ? scopedProjects : [];
    const taskProjectScope = getCalendarTaskProjectScope();

    const calendarData = prepareCalendarData(currentYear, currentMonth, {
        tasks: calendarShowTasks ? scopeTasksToProjects(tasks, taskProjectScope) : [],
        projects: calendarProjects,
        filteredProjectIds,
        searchText: calendarShowTasks ? (filterState.search || '') : '',
        includeBacklog: !!settings.calendarIncludeBacklog,
        today
    });

    // UX: show "Today" button only when not viewing the current month
    try {
        const isMobile = typeof window.matchMedia === 'function'
            ? window.matchMedia('(max-width: 768px)').matches
            : getIsMobileCached();
        const show = !calendarData.isCurrentMonth;
        document.querySelectorAll('.calendar-today-btn--header').forEach(btn => {
            btn.style.display = show ? 'inline-flex' : 'none';
        });
        document.querySelectorAll('.calendar-today-btn--nav').forEach(btn => {
            btn.style.display = (show && !isMobile) ? 'inline-flex' : 'none';
        });
    } catch (e) {}

    // Build bar layout data (pure, no DOM)
    const cutoff = getKanbanUpdatedCutoffTime(window.kanbanUpdatedFilter);
    const baseTasks = calendarShowTasks
        ? scopeTasksToProjects(typeof getFilteredTasks === 'function' ? getFilteredTasks() : tasks.slice(), taskProjectScope)
        : [];
    const cutoffFilteredTasks = cutoff === null
        ? baseTasks
        : baseTasks.filter(t => getTaskUpdatedTime(t) >= cutoff);
    const barTasks = !!settings.calendarIncludeBacklog
        ? cutoffFilteredTasks
        : cutoffFilteredTasks.filter(t => t.status !== 'backlog');

    const barLayout = computeBarLayout(currentYear, currentMonth, {
        filteredProjects: calendarProjects,
        filteredTasks:    barTasks,
        getProjectColor,
        getProjectStatus,
        PRIORITY_COLORS,
        days: calendarData.days,
    });

    // Single DOM write — grid + bars rendered together, no flicker possible
    document.getElementById("calendar-grid").innerHTML =
        generateCalendarGridHTML(calendarData, barLayout, dayNames);

    updateCalendarFunnelUI(scopedProjects.filter(p => !p.archivedAt).length, countTasksInMonth(barTasks, currentYear, currentMonth));

    debugTimeEnd("render", renderTimer, {
        taskCount: tasks.length,
        projectCount: projects.length
    });
}

function animateCalendarMonthChange(delta) {
    changeMonth(delta);
}

function setupCalendarSwipeNavigation() {
    const stage = document.querySelector('.calendar-stage');
    if (!stage || stage.dataset.swipeReady === 'true') return;
    stage.dataset.swipeReady = 'true';

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let swiping = false;

    stage.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        const touch = e.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        tracking = true;
        swiping = false;
    }, { passive: true });

    stage.addEventListener('touchmove', (e) => {
        if (!tracking) return;
        const touch = e.touches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        if (!swiping) {
            if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
                swiping = true;
            } else if (Math.abs(dy) > 12) {
                tracking = false;
            }
        }

        if (swiping) {
            e.preventDefault();
        }
    }, { passive: false });

    stage.addEventListener('touchend', (e) => {
        if (!tracking) return;
        tracking = false;
        if (!swiping) return;

        const touch = e.changedTouches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;

        const delta = dx < 0 ? 1 : -1;
        animateCalendarMonthChange(delta);
    }, { passive: true });

    stage.addEventListener('touchcancel', () => {
        tracking = false;
        swiping = false;
    }, { passive: true });
}

document.addEventListener('DOMContentLoaded', () => {
    setupCalendarSwipeNavigation();
});

function changeMonth(delta) {
    const nav = calculateMonthNavigation(currentYear, currentMonth, delta);
    currentYear = nav.year;
    currentMonth = nav.month;
    saveCalendarState();
    try { syncURLWithFilters(); } catch (e) { /* URL update is non-essential */ }
    renderCalendar();
}

function goToToday() {
    const today = new Date();
    currentMonth = today.getMonth();
    currentYear = today.getFullYear();
    saveCalendarState();
    try { syncURLWithFilters(); } catch (e) { /* URL update is non-essential */ }
    renderCalendar();
}

/** A project's working tasks: archived tasks and hidden automation templates are excluded. */
function getLiveProjectTasks(projectId) {
    return tasks.filter(task => task.projectId === projectId && !task.isTemplate && !task.archivedAt);
}

/**
 * A project's completion percentage: done tasks / total tasks, 0-100.
 *
 * The same figure the project card's ring shows and the "% Completed" sort uses.
 * A project with no tasks is 0%, matching what its card displays.
 */
function getProjectCompletionPercent(projectId) {
    const projectTasks = getLiveProjectTasks(projectId);
    if (!projectTasks.length) return 0;
    const done = projectTasks.filter(task => task.status === 'done').length;
    return Math.round((done / projectTasks.length) * 100);
}

function getProjectStatus(projectId) {
    const project = projects.find(p => p.id === projectId);
    if (project && project.archivedAt) {
        return "archived";
    }
    const projectTasks = getLiveProjectTasks(projectId);

    if (projectTasks.length === 0) {
        return "backlog";
    }

    const allDone = projectTasks.every(t => t.status === "done");
    const hasInProgress = projectTasks.some(t => t.status === "progress" || t.status === "review");
    const hasTodo = projectTasks.some(t => t.status === "todo");
    const allBacklog = projectTasks.every(t => t.status === "backlog");

    if (allDone) {
        return "completed";
    } else if (hasInProgress) {
        return "active";
    }

    if (hasTodo) {
        return "planning";
    }

    if (allBacklog) {
        return "backlog";
    }

    // Mixed backlog/done without in-progress/review still counts as planning
    return "planning";
}

function showDayTasks(dateStr) {
    const cutoff = getKanbanUpdatedCutoffTime(window.kanbanUpdatedFilter);
    const baseTasks = scopeTasksToProjects(
        typeof getFilteredTasks === 'function' ? getFilteredTasks() : tasks.slice(),
        getCalendarTaskProjectScope()
    );
    const updatedFilteredTasks = cutoff === null
        ? baseTasks
        : baseTasks.filter((t) => getTaskUpdatedTime(t) >= cutoff);

    // Show tasks that either end on this date OR span across this date
    let dayTasks = updatedFilteredTasks.filter((task) => {
        if (task.startDate && task.endDate) {
            // Task with date range - check if it overlaps this day
            return dateStr >= task.startDate && dateStr <= task.endDate;
        } else if (task.endDate) {
            // Task with only end date - show on end date
            return task.endDate === dateStr;
        } else if (task.startDate) {
            // Task with only start date - show on start date
            return task.startDate === dateStr;
        }
        return false;
    });

    // Filter out backlog tasks if setting is disabled
    const includeBacklog = !!settings.calendarIncludeBacklog;
    if (!includeBacklog) {
        dayTasks = dayTasks.filter((task) => task.status !== 'backlog');
    }

    // Sort tasks by priority (high to low)
    // Using imported PRIORITY_ORDER
    dayTasks.sort((a, b) => {
        const priorityA = PRIORITY_ORDER[a.priority] || 0;
        const priorityB = PRIORITY_ORDER[b.priority] || 0;
        return priorityB - priorityA;
    });
    const dayProjects = getCalendarScopedProjects().filter((project) => {
        const startDate = new Date(project.startDate);
        const endDate = project.endDate
            ? new Date(project.endDate)
            : new Date(project.startDate);
        const currentDate = new Date(dateStr);
        return currentDate >= startDate && currentDate <= endDate;
    });

    if (dayTasks.length === 0 && dayProjects.length === 0) {
        // Show custom styled confirmation modal
        const message = `No tasks or projects for ${formatDate(dateStr)}. Would you like to create a new task?`;
        document.getElementById('calendar-create-message').textContent = message;
        document.getElementById('calendar-create-modal').classList.add('active');
        // Store the date for use when confirmed
        window.pendingCalendarDate = dateStr;
        return;
    }

    // Show custom modal
    const modal = document.getElementById('day-items-modal');
    const title = document.getElementById('day-items-modal-title');
    const body = document.getElementById('day-items-modal-body');

    title.textContent = t('calendar.dayItemsTitle', { date: formatDate(dateStr) });

    // Store the date for use by "Add Task" button
    window.dayItemsModalDate = dateStr;

    let html = '';

    // Projects section
    if (dayProjects.length > 0) {
        html += '<div class="day-items-section">';
        html += `<div class="day-items-section-title">\u{1F4CA} ${t('calendar.dayItemsProjects')}</div>`;
        dayProjects.forEach(project => {
            const projectStatus = getProjectStatus(project.id);
            html += `
                <div class="day-item" data-action="closeDayItemsAndShowProject" data-param="${project.id}">
                    <div class="day-item-title">${escapeHtml(project.name)}</div>
                    <div class="day-item-meta" style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap;">
                        <span>${formatDate(project.startDate)} - ${formatDate(project.endDate)}</span>
                        <span class="project-status-badge ${projectStatus}">${getProjectStatusLabel(projectStatus).toUpperCase()}</span>
                    </div>
                </div>
            `;
        });
        html += '</div>';
    }

    // Tasks section
    if (dayTasks.length > 0) {
        html += '<div class="day-items-section">';
        html += `<div class="day-items-section-title">\u{2705} ${t('calendar.dayItemsTasks')}</div>`;
        dayTasks.forEach(task => {
            let projectIndicator = "";
            if (task.projectId) {
                const project = projects.find(p => p.id === task.projectId);
                if (project) {
                    const projectColor = getProjectColor(task.projectId);
                    projectIndicator = `<span class="project-indicator" style="background-color: ${projectColor}; color: white; padding: 1px 6px; border-radius: 8px; font-size: 10px; margin-right: 6px; text-shadow: 0 1px 2px rgba(0,0,0,0.3); font-weight: 600;">${escapeHtml(project.name)}</span>`;
                } else {
                    // Project not found - show plain text like in Kanban  
                    projectIndicator = t('tasks.projectIndicatorNone');
                }
            } else {
                // No project assigned - show plain text like in Kanban
                projectIndicator = t('tasks.projectIndicatorNone');
            }
            
            // Create status badge instead of text
            const statusBadge = `<span class="status-badge ${task.status}" style="padding: 2px 8px; font-size: 10px; font-weight: 600;">${getStatusLabel(task.status)}</span>`;
            
            const priorityLabel = getPriorityLabel(task.priority || '').toUpperCase();
            html += `
                <div class="day-item" data-action="closeDayItemsAndOpenTask" data-param="${task.id}">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 8px;">
                        <div class="day-item-title" style="flex: 1; min-width: 0;">${escapeHtml(task.title)}</div>
                        <div class="day-item-meta" style="display: flex; align-items: center; gap: 6px; flex-shrink: 0;"><span class="task-priority priority-${task.priority}">${priorityLabel}</span>${statusBadge}</div>
                    </div>
                    <div style="margin-top: 8px; font-size: 11px;">${projectIndicator}</div>
                </div>
            `;
        });
        html += '</div>';
    }

    body.innerHTML = html;
    modal.classList.add('active');
}

function closeDayItemsModal() {
    document.getElementById('day-items-modal').classList.remove('active');
}

function closeDayItemsModalOnBackdrop(event) {
    // Event delegation sets `event.currentTarget` to `document`, so we must compare against the modal backdrop node.
    const modal = document.getElementById('day-items-modal');
    if (!modal) return;
    if (isBackdropClick(event, modal)) closeDayItemsModal();
}

function addTaskFromDayItemsModal() {
    const dateStr = window.dayItemsModalDate;
    closeDayItemsModal();
    if (dateStr) {
        openTaskModal();
        // Wait for flatpickr to initialize, then set the date using its API
        setTimeout(() => {
            const modal = document.getElementById('task-modal');
            if (!modal) return;

            const endInput = modal.querySelector('#task-form input[name="endDate"]');
            if (!endInput) return;

            // Find the display input (flatpickr is attached to it)
            const wrapper = endInput.closest('.date-input-wrapper');
            if (!wrapper) return;

            const displayInput = wrapper.querySelector('.date-display');
            if (!displayInput || !displayInput._flatpickr) return;

            // Set the hidden input value (ISO format)
            endInput.value = dateStr;

            // Set the display input using flatpickr API
            displayInput._flatpickr.setDate(new Date(dateStr), false);
        }, 100);
    }
    window.dayItemsModalDate = null;
}

// Add keyboard support for closing day items modal
document.addEventListener('keydown', function(e) {
    const dayModal = document.getElementById('day-items-modal');
    if (dayModal && dayModal.classList.contains('active') && e.key === 'Escape') {
        e.preventDefault();
        closeDayItemsModal();
    }
});

function closeProjectConfirmModal() {
    document.getElementById('project-confirm-modal').classList.remove('active');
    document.getElementById('delete-tasks-checkbox').checked = false;
    document.getElementById("project-confirm-error").classList.remove("show");
    projectToDelete = null;
}

// Account deletion functions
function openDeleteAccountModal() {
    const modal = document.getElementById('delete-account-modal');
    modal.classList.add('active');
    
    // Find elements that need HTML rendering BEFORE applyTranslations runs
    const warningEl = modal.querySelector('[data-i18n="settings.deleteAccount.warning"]');
    const confirmTextEl = modal.querySelector('[data-i18n="settings.deleteAccount.confirmText"]');
    
    // Temporarily remove data-i18n so applyTranslations skips them
    if (warningEl) warningEl.removeAttribute('data-i18n');
    if (confirmTextEl) confirmTextEl.removeAttribute('data-i18n');
    
    // Apply translations for elements that don't need HTML rendering
    applyTranslations(modal);
    
    // Now set HTML content for elements that need HTML rendering
    if (warningEl) {
        warningEl.innerHTML = t('settings.deleteAccount.warning');
    }
    
    if (confirmTextEl) {
        confirmTextEl.innerHTML = t('settings.deleteAccount.confirmText');
    }
    
    const confirmInput = document.getElementById('delete-account-confirm-input');
    confirmInput.value = '';
    confirmInput.focus();
    
    // Auto-convert to lowercase as user types for better UX
    const lowercaseHandler = function(e) {
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        e.target.value = e.target.value.toLowerCase();
        e.target.setSelectionRange(start, end);
    };
    confirmInput.addEventListener('input', lowercaseHandler);
    
    // Clear error message
    document.getElementById('delete-account-confirm-error').classList.remove('show');
    
    // Handle Enter key
    confirmInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            confirmDeleteAccount();
        }
    }, { once: true });
}

function closeDeleteAccountModal() {
    const modal = document.getElementById('delete-account-modal');
    modal.classList.remove('active');
    const confirmInput = document.getElementById('delete-account-confirm-input');
    confirmInput.value = '';
    document.getElementById('delete-account-confirm-error').classList.remove('show');
    
    // Remove lowercase handler to prevent duplicate listeners
    // (Handler will be re-added when modal opens again)
    const newInput = confirmInput.cloneNode(true);
    confirmInput.parentNode.replaceChild(newInput, confirmInput);
}

async function confirmDeleteAccount() {
    const input = document.getElementById('delete-account-confirm-input');
    const errorMsg = document.getElementById('delete-account-confirm-error');
    if (!isDeleteConfirmation(input.value)) {
        errorMsg.classList.add('show');
        input.focus();
        return;
    }
    
    // Get auth token
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
        showErrorNotification(t('error.unauthorized'));
        closeDeleteAccountModal();
        return;
    }
    
    try {
        // The typed "delete" confirmation is done: close that dialog (it stacks above
        // regular modals) and ask for the current PIN/password on its own.
        closeDeleteAccountModal();

        // Call API to delete account (the server requires the current PIN/password)
        const result = await requestCurrentCredential('settings.confirmIdentity.deleteHint', async (credential) => {
            const resp = await fetch('/api/auth/delete-account', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(credential)
            });
            const data = await resp.json().catch(() => ({}));
            return { resp, data };
        });

        // Cancelled: nothing is deleted; the user is back in Settings
        if (!result) return;
        if (result.error) throw result.error;
        const response = result.resp;
        const data = result.data;
        
        if (!response.ok) {
            showErrorNotification(data.error || t('error.deleteAccountFailed'));
            return;
        }
        
        // Account deleted successfully
        // Clear all local data
        localStorage.clear();
        sessionStorage.clear();
        
        // Close modal
        closeDeleteAccountModal();
        
        // Show success message briefly, then redirect to login
        showSuccessNotification(t('settings.deleteAccount.success'));
        
        // Redirect to login page after a short delay
        setTimeout(() => {
            window.location.href = '/auth.html';
        }, 1500);
        
    } catch (error) {
        console.error('Delete account error:', error);
        showErrorNotification(t('error.deleteAccountFailed'));
    }
}

// Unsaved changes modal functions
function showUnsavedChangesModal(modalId) {
    let config = {};
    if (modalId && typeof modalId === 'object') {
        config = modalId;
        modalId = config.modalId || '';
    }

    window.pendingModalToClose = modalId;
    window.pendingModalDiscardHandler = typeof config.onDiscard === 'function' ? config.onDiscard : null;
    const titleEl = document.querySelector('#unsaved-changes-modal [data-i18n="confirm.unsaved.title"]');
    const bodyEl = document.querySelector('#unsaved-changes-modal [data-i18n="confirm.unsaved.body"]');
    const discardBtn = document.querySelector('#unsaved-changes-modal [data-i18n="confirm.unsaved.discard"]');
    if (titleEl) titleEl.textContent = config.title || t(config.titleKey || 'confirm.unsaved.title');
    if (bodyEl) bodyEl.textContent = config.body || t(config.bodyKey || 'confirm.unsaved.body');
    if (discardBtn) discardBtn.textContent = config.discardLabel || t(config.discardKey || 'confirm.unsaved.discard');
    document.getElementById('unsaved-changes-modal').classList.add('active');
}

function closeUnsavedChangesModal() {
    document.getElementById('unsaved-changes-modal').classList.remove('active');
    window.pendingModalToClose = null;
    window.pendingModalDiscardHandler = null;
}

function confirmDiscardChanges() {
    const targetModal = window.pendingModalToClose || 'task-modal';
    const discardHandler = typeof window.pendingModalDiscardHandler === 'function'
        ? window.pendingModalDiscardHandler
        : null;
    closeUnsavedChangesModal();

    // Clear initial state tracking when discarding changes
    if (targetModal === 'task-modal') {
        initialTaskFormState = null;
    } else if (targetModal === 'settings-modal') {
        window.initialSettingsFormState = null;
        window.settingsFormIsDirty = false;
        const settingsForm = document.getElementById('settings-form');
        const saveBtn = settingsForm?.querySelector('.settings-btn-save');
        if (saveBtn) {
            saveBtn.classList.remove('dirty');
            saveBtn.disabled = true;
        }
    }

    window.pendingModalToClose = null;
    window.pendingModalDiscardHandler = null;
    if (discardHandler) {
        discardHandler();
        return;
    }
    closeModal(targetModal);
}

function closeReviewStatusConfirmModal() {
    document.getElementById('review-status-confirm-modal').classList.remove('active');
    // Reset toggle to enabled if user cancelled
    if (window.pendingReviewStatusToggle) {
        window.pendingReviewStatusToggle.checked = true;
        window.pendingReviewStatusToggle.dispatchEvent(new Event('change', { bubbles: true }));
        if (typeof window.markSettingsDirtyIfNeeded === 'function') {
            window.markSettingsDirtyIfNeeded();
        }
    }
    window.pendingReviewTaskMigration = null;
    window.pendingReviewStatusToggle = null;
}

async function confirmDisableReviewStatus() {
    // CRITICAL: Save tasks to local variable BEFORE closing modal (which clears pendingReviewTaskMigration)
    const tasksToMigrate = window.pendingReviewTaskMigration ? [...window.pendingReviewTaskMigration] : [];

    closeReviewStatusConfirmModal();

    // Migrate all review tasks to progress - find by ID to ensure we update the actual global tasks
    if (tasksToMigrate && tasksToMigrate.length > 0) {
        const taskIds = tasksToMigrate.map(t => t.id);

        // Update tasks in the global tasks array
        tasks.forEach(task => {
            if (taskIds.includes(task.id)) {
                task.status = 'progress';
            }
        });

        // Save tasks using row-level operations
        const now = new Date().toISOString();
        await Promise.all(taskIds.map(id => saveTaskUpdate(id, { status: 'progress', updatedAt: now })));
    }

    // Continue with saving settings
    const enableReviewStatusToggle = document.getElementById('enable-review-status-toggle');
    if (enableReviewStatusToggle) {
        // Ensure checkbox is unchecked to match the new state
        enableReviewStatusToggle.checked = false;

        // Update global variable and localStorage
        window.enableReviewStatus = false;
        localStorage.setItem('enableReviewStatus', 'false');

        // Update settings object
        settings.enableReviewStatus = false;
        await saveSettings();

        // Apply review status visibility (hides review column)
        applyReviewStatusVisibility();

        // Force immediate kanban refresh to show migrated tasks
        renderTasks();
    }

    // Mark form as clean and close settings modal
    const saveBtn = document.getElementById('save-settings-btn');
    window.initialSettingsFormState = null;
    window.settingsFormIsDirty = false;
    if (saveBtn) {
        saveBtn.classList.remove('dirty');
        saveBtn.disabled = true;
    }

    closeModal('settings-modal');
    showSuccessNotification(t('success.settingsSaved'));
}

// Calendar create task modal functions
function closeCalendarCreateModal() {
    document.getElementById('calendar-create-modal').classList.remove('active');
    window.pendingCalendarDate = null;
}

function confirmCreateTask() {
    const dateStr = window.pendingCalendarDate;
    closeCalendarCreateModal();
    if (dateStr) {
        openTaskModal();
        // Wait for flatpickr to initialize, then set the date using its API
        setTimeout(() => {
            const modal = document.getElementById('task-modal');
            if (!modal) return;

            const endInput = modal.querySelector('#task-form input[name="endDate"]');
            if (!endInput) return;

            // Find the display input (flatpickr is attached to it)
            const wrapper = endInput.closest('.date-input-wrapper');
            if (!wrapper) return;

            const displayInput = wrapper.querySelector('.date-display');
            if (!displayInput || !displayInput._flatpickr) return;

            // Set the hidden input value (ISO format)
            endInput.value = dateStr;

            // Set the display input using flatpickr API
            displayInput._flatpickr.setDate(new Date(dateStr), false);
        }, 100);
    }
}

// Calendar modal backdrop click handler
document.addEventListener('DOMContentLoaded', function() {
    const calendarModal = document.getElementById('calendar-create-modal');
    if (calendarModal) {
        calendarModal.addEventListener('click', function(event) {
            // Close modal if clicking on the backdrop (not the content)
            if (event.target === calendarModal) {
                closeCalendarCreateModal();
            }
        });
    }
});

async function confirmProjectDelete() {
  const input = document.getElementById('project-confirm-input');
  const errorMsg = document.getElementById("project-confirm-error");
  const deleteTasksCheckbox = document.getElementById('delete-tasks-checkbox');

  if (!isDeleteConfirmation(input.value)) {
    errorMsg.classList.add('show');
    input.focus();
    return;
  }

  const projectIdNum = parseInt(projectToDelete, 10);

  // Save snapshots for rollback
  const previousProjects = JSON.parse(JSON.stringify(projects));
  const previousTasks = JSON.parse(JSON.stringify(tasks));

  const projectToRecord = projects.find(p => p.id === projectIdNum);
  const deleteTasks = deleteTasksCheckbox.checked;

  if (deleteTasks) {
    tasks = tasks.filter(t => t.projectId !== projectIdNum);
  }

  // Delete project (and clear task associations if not deleting tasks)
  const result = deleteProjectService(projectIdNum, projects, tasks, !deleteTasks);
  projects = result.projects;

  // Update tasks if associations were cleared (not deleted)
  if (!deleteTasks && result.tasks) {
    tasks = result.tasks;
  }

  // Close modal and update UI immediately (optimistic — project disappears)
  closeProjectConfirmModal();
  appState.projectsSortedView = null;
  window.location.hash = "#projects";
  showPage("projects");
  render();

  // Clean up color maps for the deleted project
  const hadProjectColor = Object.prototype.hasOwnProperty.call(projectColorMap, projectIdNum);
  const previousProjectColor = projectColorMap[projectIdNum];
  const hadColorOverride = Object.prototype.hasOwnProperty.call(projectColorOverrides, projectIdNum);
  const previousColorOverride = projectColorOverrides[projectIdNum];
  delete projectColorMap[projectIdNum];
  delete projectColorOverrides[projectIdNum];

  // Persist both saves atomically — rollback both if either fails
  try {
    const tasksToModify = previousTasks.filter(t => t.projectId === projectIdNum);
    let taskPromises;

    if (deleteTasks) {
      // Delete each task individually
      taskPromises = tasksToModify.map(t => saveTaskDeleteData(t.id));
    } else {
      // Clear projectId on each task (unassign)
      taskPromises = tasksToModify.map(t => saveTaskUpdateData(t.id, { projectId: null, updatedAt: new Date().toISOString() }));
    }

    const colorSavePromises = [saveProjectColors({ showError: false })];
    if (hadColorOverride) colorSavePromises.push(saveProjectColorOverrides());

    await Promise.all([
      saveProjectDeleteData(projectIdNum),
      ...taskPromises,
      ...colorSavePromises
    ]);
    // Record history only after confirmed deletion
    if (window.historyService && projectToRecord) {
      window.historyService.recordProjectDeleted(projectToRecord);
    }
  } catch (err) {
    // Rollback — restore project and tasks so data isn't lost
    console.error('Failed to persist project deletion:', err);
    projects = previousProjects;
    tasks = previousTasks;
    if (hadProjectColor) {
      projectColorMap[projectIdNum] = previousProjectColor;
    } else {
      delete projectColorMap[projectIdNum];
    }
    if (hadColorOverride) {
      projectColorOverrides[projectIdNum] = previousColorOverride;
    } else {
      delete projectColorOverrides[projectIdNum];
    }
    showErrorNotification(t('error.saveChangesFailed'));
    // Re-render so the project reappears (user must retry the deletion)
    appState.projectsSortedView = null;
    render();
  }

}


const EMPTY_PROJECT_DOC_CONTENT = {
    type: 'doc',
    content: [
        {
            type: 'paragraph'
        }
    ]
};

const BUILTIN_DOC_TEMPLATES = [
    {
        id: 'meeting_notes',
        icon: '📋',
        title: 'Meeting Notes',
        description: 'Agenda, attendees, notes and action items',
        contentJson: { type: 'doc', content: [
            { type: 'paragraph', content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Date: ' }] },
            { type: 'paragraph', content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Attendees: ' }] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Agenda' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Notes' }] },
            { type: 'paragraph' },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Action Items' }] },
            { type: 'taskList', content: [
                { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph' }] }
            ] }
        ] }
    },
    {
        id: 'prd',
        icon: '📐',
        title: 'Product Spec (PRD)',
        description: 'Problem, goals, scope, requirements',
        contentJson: { type: 'doc', content: [
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Problem' }] },
            { type: 'paragraph', content: [{ type: 'text', text: 'What problem are we solving?' }] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Goals' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Out of Scope' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Requirements' }] },
            { type: 'paragraph' },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Success Metrics' }] },
            { type: 'paragraph' }
        ] }
    },
    {
        id: 'retrospective',
        icon: '🔄',
        title: 'Retrospective',
        description: 'What went well, what to improve, action items',
        contentJson: { type: 'doc', content: [
            { type: 'paragraph', content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Sprint / Period: ' }] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: '✅ What went well' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: '⚠️ What to improve' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: '🎯 Action items' }] },
            { type: 'taskList', content: [
                { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph' }] }
            ] }
        ] }
    },
    {
        id: 'sop',
        icon: '📖',
        title: 'How-to / SOP',
        description: 'Step-by-step standard operating procedure',
        contentJson: { type: 'doc', content: [
            { type: 'paragraph', content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Purpose: ' }, { type: 'text', text: 'Describe the goal of this guide.' }] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Prerequisites' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Steps' }] },
            { type: 'orderedList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Troubleshooting' }] },
            { type: 'paragraph' }
        ] }
    },
    {
        id: 'weekly_status',
        icon: '📊',
        title: 'Weekly Status',
        description: 'Progress, highlights, blockers and next steps',
        contentJson: { type: 'doc', content: [
            { type: 'paragraph', content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Week of: ' }] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: '🏆 Highlights' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: '🚧 In Progress' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: '🔴 Blockers' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: '📅 Next Week' }] },
            { type: 'bulletList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] }
        ] }
    },
    {
        id: 'bug_report',
        icon: '🐛',
        title: 'Bug Report',
        description: 'Reproduce steps, expected vs actual, impact',
        contentJson: { type: 'doc', content: [
            { type: 'paragraph', content: [{ type: 'text', marks: [{ type: 'bold' }], text: 'Severity: ' }, { type: 'text', text: 'Low / Medium / High / Critical' }] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Description' }] },
            { type: 'paragraph', content: [{ type: 'text', text: 'Brief description of the bug.' }] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Steps to Reproduce' }] },
            { type: 'orderedList', content: [
                { type: 'listItem', content: [{ type: 'paragraph' }] }
            ] },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Expected Behavior' }] },
            { type: 'paragraph' },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Actual Behavior' }] },
            { type: 'paragraph' },
            { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Impact' }] },
            { type: 'paragraph' }
        ] }
    }
];

const ASK_KAI_ACTION_AUTO = 'auto_intent';
const ASK_KAI_ACTION_DOCUMENTATION = 'draft_documentation';
const ASK_KAI_ACTION_REWRITE = 'rewrite_summarize';
const ASK_KAI_ACTION_APPLY_CHIPS = 'apply_status_chips';
const ASK_KAI_STATUS_CHIP_TONES = new Set(['grey', 'blue', 'green', 'amber', 'red', 'purple']);
const ASK_KAI_ACTION_AUTOMATION = 'create_automation';
// Find mode. Deliberately absent from ASK_KAI_ALLOWED_PROPOSAL_ACTIONS below:
// auto_intent must never resolve into a filter query. The user picks Find.
const ASK_KAI_ACTION_FIND = 'query_filter';
const ASK_KAI_DOC_CONTEXT_ACTIONS = new Set([
    ASK_KAI_ACTION_AUTO,
    ASK_KAI_ACTION_DOCUMENTATION,
    'create_project_with_tasks',
    'create_project_only',
    'create_tasks_only'
]);
const ASK_KAI_ALLOWED_PROPOSAL_ACTIONS = new Set([
    ASK_KAI_ACTION_DOCUMENTATION,
    'draft_template',
    ASK_KAI_ACTION_REWRITE,
    ASK_KAI_ACTION_AUTOMATION,
    'create_project_with_tasks',
    'create_project_only',
    'create_tasks_only',
    'create_multiple_projects'
]);

function doesAskKaiProposalMatchSelection(selectedAction, proposalAction) {
    const normalizedSelected = String(selectedAction || ASK_KAI_ACTION_AUTO).trim() || ASK_KAI_ACTION_AUTO;
    const normalizedProposal = String(proposalAction || '').trim();
    if (!normalizedProposal) return false;

    if (normalizedSelected === ASK_KAI_ACTION_AUTO) {
        return ASK_KAI_ALLOWED_PROPOSAL_ACTIONS.has(normalizedProposal);
    }

    if (normalizedProposal === normalizedSelected) {
        return true;
    }

    if (
        normalizedProposal === 'create_multiple_projects' &&
        (normalizedSelected === 'create_project_only' || 
         normalizedSelected === 'create_project_with_tasks' ||
         normalizedSelected === 'create_tasks_only')
    ) {
        return true;
    }

    // Handle documentation action aliases
    if (
        (normalizedProposal === 'draft_template' && normalizedSelected === ASK_KAI_ACTION_DOCUMENTATION) ||
        (normalizedProposal === ASK_KAI_ACTION_DOCUMENTATION && normalizedSelected === 'draft_template')
    ) {
        return true;
    }

    return false;
}
let askKaiState = {
    proposal: null,
    review: null,
    metadata: null,
    intent: null,
    busy: false,
    requestController: null,
    selectedAmbiguityMode: null,
    lastCreatedProjects: null,
    promptPresentation: 'expanded-editing',
    generationStartedAt: null,
    generationTimerId: null,
    lastGenerationSeconds: null,
    // Find-mode conversation. Survives resetAskKaiModalState() so that closing the
    // drawer and reopening it via Refine keeps the context of the applied query.
    // In memory only: the filters themselves round-trip through the URL, which is
    // the app's real source of truth.
    find: { query: null, vocabulary: null, turns: [] }
};
let askKaiDraft = {
    instruction: '',
    useDocContext: false,
    useGeneratedTags: false
};

function getHistoryActorLabel(actor = {}) {
    const actorType = String(actor?.type || '').trim().toLowerCase();
    if (actorType === 'kai') {
        return t('history.actor.kai');
    }
    return String(actor?.label || '').trim() || t('history.actor.user');
}

function getHistoryActorMeta(meta = {}) {
    const actor = meta?.actor && typeof meta.actor === 'object' ? meta.actor : null;
    return {
        type: String(actor?.type || 'user').trim().toLowerCase() === 'kai' ? 'kai' : 'user',
        label: getHistoryActorLabel(actor || {})
    };
}

function buildHistoryActorLineHtml(meta = {}, options = {}) {
    const actor = getHistoryActorMeta(meta);
    const text = t('history.attribution.by', { name: actor.label });
    const extraClassName = String(options.className || '').trim();
    const classes = ['history-actor-line', `history-actor-line--${actor.type}`];
    if (extraClassName) classes.push(extraClassName);
    
    let inner = actor.type === 'kai'
        ? `${escapeHtml(text)}<img src="src/assets/kai-logo.png" class="history-actor-line__kai-logo" aria-hidden="true" alt="">`
        : escapeHtml(text);
    
    // Add task trigger information if available
    if (meta.triggeringTaskTitle && meta.triggeringTaskStatus) {
        const triggerText = `Triggered by task "${meta.triggeringTaskTitle}" marked as ${meta.triggeringTaskStatus}`;
        inner += `<div class="history-trigger-info" style="font-size: 0.85em; opacity: 0.8; margin-top: 2px;">${escapeHtml(triggerText)}</div>`;
    }
    
    return `<div class="${classes.join(' ')}" data-history-actor-line data-actor-type="${escapeHtml(actor.type)}">${inner}</div>`;
}

function getAskKaiHistoryMeta() {
    return {
        actor: {
            type: 'kai',
            id: 'kai',
            label: 'Kai'
        },
        origin: 'ask_kai'
    };
}

function logAskKaiTrace(stage, details = {}) {
    if (typeof console === 'undefined' || !isDebugLogsEnabled()) return;
    const timestamp = new Date().toISOString();
    const sanitized = {};
    Object.entries(details || {}).forEach(([key, value]) => {
        if (/(instruction|sourceText|content|payload|proposal|review|parsed|systemPrompt|userPrompt)/i.test(key)) {
            if (typeof value === 'string') {
                sanitized[key] = `[redacted:${value.length}]`;
            } else if (Array.isArray(value)) {
                sanitized[key] = { type: 'array', length: value.length };
            } else if (value && typeof value === 'object') {
                sanitized[key] = { type: 'object', keys: Object.keys(value) };
            } else {
                sanitized[key] = value;
            }
            return;
        }
        sanitized[key] = value;
    });
    console.groupCollapsed(`[AskKai][${timestamp}] ${stage}`);
    Object.entries(sanitized).forEach(([key, value]) => {
        console.log(`${key}:`, value);
    });
    console.groupEnd();
}
const DOCS_TREE_META_STORAGE_KEY = 'docsTreeMeta';
const DOCS_EMPTY_ICON_PLACEHOLDER = '☺';

function getAskKaiElements() {
    return {
        modal: document.getElementById('ask-kai-modal'),
        action: document.getElementById('ask-kai-action'),
        composerSection: document.querySelector('#ask-kai-modal .ask-kai-drawer__section--prompt'),
        composerWrap: document.querySelector('#ask-kai-modal .ask-kai-composer-wrap'),
        composerToolbar: document.querySelector('#ask-kai-modal .ask-kai-composer-toolbar'),
        instruction: document.getElementById('ask-kai-instruction'),
        clearBtn: document.getElementById('ask-kai-clear-btn'),
        savedBtn: document.getElementById('ask-kai-saved-btn'),
        promptSummary: document.getElementById('ask-kai-prompt-summary'),
        promptSummaryText: document.getElementById('ask-kai-prompt-summary-text'),
        promptSummaryClearBtn: document.getElementById('ask-kai-summary-clear-btn'),
        expandBtn: document.getElementById('ask-kai-expand-btn'),
        shortcutHint: document.getElementById('ask-kai-shortcut-hint'),
        preview: document.getElementById('ask-kai-preview'),
        actionPicker: document.getElementById('ask-kai-action-picker'),
        modeButtons: Array.from(document.querySelectorAll('#ask-kai-modal [data-ask-kai-mode]')),
        contextChips: document.getElementById('ask-kai-context-chips'),
        docContextToggle: document.getElementById('ask-kai-doc-context-toggle'),
        docContextCheckbox: document.getElementById('ask-kai-doc-context-checkbox'),
        tagToggle: document.getElementById('ask-kai-tag-toggle'),
        tagCheckbox: document.getElementById('ask-kai-tag-checkbox'),
        tagToggleLabel: document.getElementById('ask-kai-tag-toggle-label'),
        actionIndicator: document.getElementById('ask-kai-action-indicator'),
        generateBtn: document.getElementById('ask-kai-generate-btn'),
        confirmBtn: document.getElementById('ask-kai-confirm-btn')
    };
}

function getAskKaiSubmitShortcutLabel() {
    try {
        const platform = String(window?.navigator?.platform || window?.navigator?.userAgent || '').toLowerCase();
        return /mac|iphone|ipad|ipod/.test(platform) ? 'Cmd+Enter' : 'Ctrl+Enter';
    } catch {
        return 'Ctrl+Enter';
    }
}

function getAskKaiOpenShortcutLabel() {
    try {
        const platform = String(window?.navigator?.platform || window?.navigator?.userAgent || '').toLowerCase();
        return /mac|iphone|ipad|ipod/.test(platform) ? 'Cmd+Shift+K' : 'Ctrl+Shift+K';
    } catch {
        return 'Ctrl+Shift+K';
    }
}

function updateAskKaiShortcutHint() {
    const { shortcutHint } = getAskKaiElements();
    if (!shortcutHint) return;
    shortcutHint.textContent = t('askKai.shortcutHint', {
        submitShortcut: getAskKaiSubmitShortcutLabel()
    });
}

function updateAskKaiLauncherShortcutTooltip() {
    const tooltip = t('askKai.openButtonTooltip', { shortcut: getAskKaiOpenShortcutLabel() });
    document.querySelectorAll('.ask-kai-launch-btn').forEach((button) => {
        button.setAttribute('title', tooltip);
    });
}

function getAskKaiGenerationElapsedSeconds() {
    if (!askKaiState.generationStartedAt) return 0;
    return Math.max(0, Math.floor((Date.now() - askKaiState.generationStartedAt) / 1000));
}

function getAskKaiGeneratingLabel() {
    if (!askKaiState.generationStartedAt) {
        return t('askKai.generating');
    }
    const seconds = getAskKaiGenerationElapsedSeconds();
    return t('askKai.generatingWithSeconds', { seconds });
}

function getAskKaiGeneratingButtonLabel() {
    return t('askKai.generating');
}

function setAskKaiGenerateButtonBusy(isBusy) {
    const { generateBtn } = getAskKaiElements();
    if (!generateBtn) return;
    generateBtn.disabled = !!isBusy;
    if (isBusy) generateBtn.hidden = false;
    generateBtn.dataset.busy = isBusy ? 'true' : 'false';
    generateBtn.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    generateBtn.textContent = isBusy ? getAskKaiGeneratingButtonLabel() : t('askKai.generateButton');
    syncAskKaiPromptPresentation();
}

// Generate is hidden while an applicable proposal is on screen (only Cancel/Apply act).
// Editing the prompt clears the proposal (see clearAskKaiProposalState), which brings Generate
// back. Error/ambiguity reviews keep Generate visible so the user can retry/regenerate.
function syncAskKaiGenerateAvailability() {
    const { generateBtn } = getAskKaiElements();
    if (!generateBtn) return;
    if (askKaiState.busy || generateBtn.dataset.busy === 'true') return; // busy state owns the button
    const hasApplicableProposal = !!askKaiState.proposal && !isAskKaiReviewApplyBlocked(askKaiState.review);
    generateBtn.disabled = hasApplicableProposal;
    generateBtn.hidden = hasApplicableProposal;
}

function stopAskKaiGenerationTimer(recordElapsed = false) {
    if (recordElapsed && askKaiState.generationStartedAt) {
        askKaiState.lastGenerationSeconds = getAskKaiGenerationElapsedSeconds();
    }
    if (askKaiState.generationTimerId) {
        clearInterval(askKaiState.generationTimerId);
        askKaiState.generationTimerId = null;
    }
    askKaiState.generationStartedAt = null;
}

function getAskKaiPromptPresentation() {
    return String(askKaiState.promptPresentation || 'expanded-editing');
}

function syncAskKaiPromptPresentation() {
    const {
        composerWrap,
        composerSection,
        composerToolbar,
        instruction,
        clearBtn,
        savedBtn,
        promptSummary,
        promptSummaryText,
        promptSummaryClearBtn,
        expandBtn
    } = getAskKaiElements();
    if (!composerWrap || !instruction) return;

    const presentation = getAskKaiPromptPresentation();
    const isCollapsed = presentation === 'collapsed-readonly';
    const summaryText = String(instruction.value || '').replace(/\s+/g, ' ').trim();

    composerWrap.classList.toggle('is-collapsed', isCollapsed);
    composerSection?.classList.toggle('is-collapsed', isCollapsed);
    instruction.hidden = isCollapsed;
    instruction.readOnly = isCollapsed;
    instruction.setAttribute('aria-hidden', isCollapsed ? 'true' : 'false');

    if (clearBtn) {
        clearBtn.disabled = !!askKaiState.busy;
        clearBtn.style.opacity = isCollapsed ? '0' : '1';
        clearBtn.style.pointerEvents = isCollapsed ? 'none' : 'auto';
    }
    if (savedBtn) {
        savedBtn.style.opacity = isCollapsed ? '0' : '1';
        savedBtn.style.pointerEvents = isCollapsed ? 'none' : 'auto';
    }
    if (composerToolbar) {
        composerToolbar.hidden = isCollapsed;
    }

    if (promptSummary) {
        promptSummary.hidden = !isCollapsed;
    }
    if (promptSummaryText) {
        promptSummaryText.textContent = summaryText;
        promptSummaryText.setAttribute('title', summaryText);
    }
    if (promptSummaryClearBtn) {
        promptSummaryClearBtn.disabled = !!askKaiState.busy;
    }
    if (expandBtn) {
        expandBtn.disabled = !!askKaiState.busy;
    }
    syncAskKaiCollapsedStar();
}

function setAskKaiPromptPresentation(nextPresentation, options = {}) {
    askKaiState.promptPresentation = nextPresentation || 'expanded-editing';
    syncAskKaiPromptPresentation();
    // Editing the prompt again brings the mode grid back.
    syncAskKaiModesCollapsed();
    if (options.focus === true && askKaiState.promptPresentation !== 'collapsed-readonly') {
        focusAskKaiInstructionField();
    }
}

function clearAskKaiPromptInput(options = {}) {
    const { instruction } = getAskKaiElements();
    if (!instruction || askKaiState.busy) return;
    instruction.value = '';
    rememberAskKaiDraft();
    syncAskKaiActionPicker();
    clearAskKaiProposalState();
    setAskKaiPromptPresentation('expanded-editing', { focus: options.focus !== false });
}

function focusAskKaiInstructionField() {
    const { instruction } = getAskKaiElements();
    if (!instruction) return;
    if (getAskKaiPromptPresentation() === 'collapsed-readonly' && !askKaiState.busy) {
        setAskKaiPromptPresentation('expanded-editing-after-results');
    }
    try {
        instruction.focus({ preventScroll: true });
    } catch {
        instruction.focus();
    }
    const length = String(instruction.value || '').length;
    try {
        instruction.setSelectionRange(length, length);
    } catch {
        // Ignore controls that do not support selection ranges.
    }
}

function handleAskKaiGlobalShortcut(event) {
    if (!event || event.defaultPrevented || event.isComposing || event.repeat) return;
    const pressedKey = String(event?.key || '').toLowerCase();
    if (pressedKey !== 'k') return;
    if (event.altKey || !event.shiftKey || (!event.ctrlKey && !event.metaKey)) return;

    event.preventDefault();
    const { modal } = getAskKaiElements();
    if (modal?.classList.contains('active')) {
        focusAskKaiInstructionField();
        return;
    }

    openAskKaiModal();
}

function isAskKaiDocumentationAction(action) {
    return action === ASK_KAI_ACTION_DOCUMENTATION || action === 'draft_template';
}

function isAskKaiRewriteModeAvailable(activePageId = getActivePageId()) {
    return isAskKaiDocPageActive(activePageId);
}

function normalizeAskKaiSelectedAction(selectedAction, activePageId = getActivePageId()) {
    const normalized = String(selectedAction || ASK_KAI_ACTION_AUTO).trim() || ASK_KAI_ACTION_AUTO;
    if (normalized === ASK_KAI_ACTION_REWRITE && !isAskKaiRewriteModeAvailable(activePageId)) {
        return ASK_KAI_ACTION_AUTO;
    }
    return normalized;
}

function getAskKaiActionClass(action) {
    switch (action) {
        case ASK_KAI_ACTION_FIND:
            return 'ask-kai-action-chip--query';
        case ASK_KAI_ACTION_AUTOMATION:
            return 'ask-kai-action-chip--tasks-only';
        case 'create_project_with_tasks':
        case 'create_multiple_projects':
            return 'ask-kai-action-chip--project';
        case 'create_project_only':
            return 'ask-kai-action-chip--project-only';
        case 'create_tasks_only':
            return 'ask-kai-action-chip--tasks-only';
        case ASK_KAI_ACTION_DOCUMENTATION:
        case 'draft_template':
            return 'ask-kai-action-chip--template';
        case 'rewrite_summarize':
        case 'apply_status_chips':
            return 'ask-kai-action-chip--rewrite';
        default:
            return 'ask-kai-action-chip--default';
    }
}

function updateAskKaiActionIndicator(action) {
    const indicator = document.getElementById('ask-kai-action-indicator');
    const iconElement = indicator?.querySelector('.ask-kai-action-icon');
    const textElement = indicator?.querySelector('.ask-kai-action-text');
    
    if (!indicator || !iconElement || !textElement) return;
    
    indicator.classList.remove(
        'ask-kai-action-chip--project',
        'ask-kai-action-chip--template',
        'ask-kai-action-chip--rewrite',
        'ask-kai-action-chip--project-only',
        'ask-kai-action-chip--tasks-only',
        'ask-kai-action-chip--default',
        'ask-kai-action-chip--status',
        'ask-kai-action-chip--ready'
    );

    if (!askKaiState.proposal || !action) {
        indicator.hidden = true;
        indicator.style.display = 'none';
        textElement.textContent = '';
        return;
    }

    const chipLabel = action === 'create_multiple_projects'
        ? t('askKai.actionIndicator.multiProject')
        : getAskKaiModeLabel(action);
    const wasAutoDetected = askKaiState.intent?.auto === true
        || askKaiState.intent?.requestedAction === ASK_KAI_ACTION_AUTO;

    indicator.classList.add(getAskKaiActionClass(action));
    textElement.textContent = wasAutoDetected
        ? chipLabel
        : `${chipLabel} ${t('askKai.actionIndicator.proposalSuffix')}`.trim();
    indicator.hidden = false;
    indicator.style.display = 'inline-flex';
}

// Rich text -> ProseMirror block array. Understands the Kai docs authoring DSL:
// markdown headings/lists/tables, real **bold**/*italic*/`code`/links, task lists,
// ``` code fences, --- dividers, [TOC], inline {{chip:tone:label}}, and fenced
// directives :::callout <tone> / :::columns + :::column / :::align <dir> (nestable).
// Returns an array of block nodes (not wrapped in a doc) so the selection-splice
// path can reuse it. Unknown directives/tones degrade gracefully — never throws.
function buildAskKaiDocBlocks(text) {
    const raw = String(text || '').trim();
    if (!raw) return [];

    const lines = raw.split(/\r?\n/);

    const MAX_FENCE_DEPTH = 4;
    const CALLOUT_TONES = new Set(['info', 'success', 'warning', 'danger', 'neutral']);
    const CALLOUT_TONE_ALIASES = new Set([
        'note', 'info', 'tip', 'success', 'ok', 'done',
        'warning', 'warn', 'caution', 'danger', 'error', 'critical', 'alert', 'neutral'
    ]);
    const ALIGNMENTS = new Set(['left', 'center', 'right', 'justify']);

    function normalizeCalloutTone(value) {
        const v = String(value || '').trim().toLowerCase();
        if (CALLOUT_TONES.has(v)) return v;
        if (/(tip|success|ok|done)/.test(v)) return 'success';
        if (/(warn|caution|attention)/.test(v)) return 'warning';
        if (/(danger|error|critical|alert|stop)/.test(v)) return 'danger';
        if (/(neutral|muted|gray|grey|plain)/.test(v)) return 'neutral';
        return 'info';
    }

    function normalizeHeadingText(value) {
        return String(value || '')
            .replace(/[\u{1F300}-\u{1FAFF}\u2600-\u27BF]/gu, ' ')
            .replace(/[^a-zA-Z0-9\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
    }

    function isTocHeadingText(value) {
        const normalized = normalizeHeadingText(value);
        return normalized === 'table of contents' || normalized === 'toc';
    }

    function isActionItemsHeadingText(value) {
        const normalized = normalizeHeadingText(value);
        return normalized === 'action items' || normalized === 'action item' || normalized === 'actions';
    }

    function isPotentialMarkdownTableRow(value) {
        const line = String(value || '').trim();
        if (!line) return false;
        const pipeCount = (line.match(/\|/g) || []).length;
        return pipeCount >= 2;
    }

    function parseMarkdownTableCells(value) {
        const line = String(value || '').trim();
        const stripped = line.replace(/^\|\s*/, '').replace(/\s*\|$/, '');
        return stripped.split('|').map((cell) => String(cell || '').trim());
    }

    function isMarkdownSeparatorCells(cells) {
        if (!Array.isArray(cells) || !cells.length) return false;
        return cells.every((cell) => /^:?-{3,}:?$/.test(String(cell || '').replace(/\s+/g, '')));
    }

    function normalizeTableCells(cells, columnCount) {
        const normalized = Array.isArray(cells) ? cells.slice(0, columnCount) : [];
        while (normalized.length < columnCount) {
            normalized.push('');
        }
        return normalized;
    }

    function buildTableNodeFromBuffer(rows) {
        if (!Array.isArray(rows) || rows.length < 2) return null;

        const parsedRows = rows.map((row) => parseMarkdownTableCells(row));
        if (parsedRows.length < 2) return null;

        const headerCells = parsedRows[0];
        const separatorCells = parsedRows[1];
        if (!isMarkdownSeparatorCells(separatorCells)) return null;

        const bodyRows = parsedRows.slice(2);
        const columnCount = Math.max(
            headerCells.length,
            separatorCells.length,
            ...bodyRows.map((row) => row.length),
            1
        );

        const makeTableRow = (cells, cellType) => ({
            type: 'tableRow',
            content: normalizeTableCells(cells, columnCount).map((cellText) => ({
                type: cellType,
                attrs: {
                    colspan: 1,
                    rowspan: 1,
                    colwidth: null
                },
                content: [{
                    type: 'paragraph',
                    content: parseInlineSegments(cellText)
                }]
            }))
        });

        return {
            type: 'table',
            content: [
                makeTableRow(headerCells, 'tableHeader'),
                ...bodyRows.map((row) => makeTableRow(row, 'tableCell'))
            ]
        };
    }

    function isFenceOpenLine(line) {
        return /^:::\s*[A-Za-z]/.test(String(line || '').trim());
    }

    function isFenceCloseLine(line) {
        return /^:::\s*$/.test(String(line || '').trim());
    }

    function parseFenceOpen(line) {
        const m = String(line || '').trim().match(/^:::\s*([A-Za-z][\w-]*)\s*(.*)$/);
        if (!m) return null;
        return { name: m[1].toLowerCase(), args: String(m[2] || '').trim() };
    }

    // Consume a fenced directive starting at startIdx (an open line). Tracks nesting
    // (nested :::column inside :::columns) so the matching bare ::: close is found.
    // Returns { name, args, inner: string[], next } where next is the index after close.
    function collectFence(arr, startIdx) {
        const open = parseFenceOpen(arr[startIdx]) || { name: '', args: '' };
        const inner = [];
        let fenceDepth = 1;
        let i = startIdx + 1;
        for (; i < arr.length; i++) {
            const ln = arr[i];
            if (isFenceOpenLine(ln)) { fenceDepth++; inner.push(ln); continue; }
            if (isFenceCloseLine(ln)) {
                fenceDepth--;
                if (fenceDepth === 0) { i++; break; }
                inner.push(ln);
                continue;
            }
            inner.push(ln);
        }
        return { name: open.name, args: open.args, inner, next: i };
    }

    function buildCalloutNode(innerLines, toneArg, depth) {
        const tone = normalizeCalloutTone(toneArg);
        const body = parseBlocks(innerLines, depth + 1);
        return {
            type: 'callout',
            attrs: { tone, icon: true },
            content: body.length ? body : [{ type: 'paragraph' }]
        };
    }

    function extractColumnBuckets(innerLines) {
        const buckets = [];
        let i = 0;
        while (i < innerLines.length) {
            const ln = innerLines[i];
            if (isFenceOpenLine(ln)) {
                const parsed = parseFenceOpen(ln);
                const fence = collectFence(innerLines, i);
                if (parsed && parsed.name === 'column') buckets.push(fence.inner);
                i = fence.next;
                continue;
            }
            i++;
        }
        return buckets;
    }

    // docColumns requires exactly two docColumn children — pad/truncate to 2.
    function buildColumnsNode(innerLines, depth) {
        let buckets = extractColumnBuckets(innerLines);
        if (!buckets.length) buckets = [innerLines];
        const columns = buckets.slice(0, 2).map((bucket) => {
            const blocks = parseBlocks(bucket, depth + 1);
            return { type: 'docColumn', content: blocks.length ? blocks : [{ type: 'paragraph' }] };
        });
        while (columns.length < 2) {
            columns.push({ type: 'docColumn', content: [{ type: 'paragraph' }] });
        }
        return { type: 'docColumns', content: columns };
    }

    function applyAlignment(innerLines, alignArg, depth) {
        const requested = String(alignArg || '').trim().toLowerCase();
        const align = ALIGNMENTS.has(requested) ? requested : 'left';
        const blocks = parseBlocks(innerLines, depth + 1);
        blocks.forEach((node) => {
            if (node && (node.type === 'paragraph' || node.type === 'heading')) {
                node.attrs = Object.assign({}, node.attrs || {}, { textAlign: align });
            }
        });
        return blocks;
    }

    function parseInlineSegments(line) {
        const textLine = String(line || '');
        if (!textLine) return [];
        const nodes = [];
        const pattern = /(\{\{\s*chip\s*:\s*(?:grey|blue|green|amber|red|purple)\s*:\s*[^{}]+?\s*\}\}|\{\{\s*date\s*:\s*\d{4}-\d{2}-\d{2}\s*\}\}|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^\)]+\))/gi;
        let lastIndex = 0;
        let match;

        while ((match = pattern.exec(textLine)) !== null) {
            if (match.index > lastIndex) {
                nodes.push({ type: 'text', text: textLine.slice(lastIndex, match.index) });
            }
            const token = match[0];
            const chipMatch = token.match(/^\{\{\s*chip\s*:\s*(grey|blue|green|amber|red|purple)\s*:\s*([^{}]+?)\s*\}\}$/i);
            const dateChipMatch = token.match(/^\{\{\s*date\s*:\s*(\d{4}-\d{2}-\d{2})\s*\}\}$/i);
            if (chipMatch) {
                const label = chipMatch[2].trim();
                if (label) {
                    nodes.push({
                        type: 'statusChip',
                        attrs: { tone: chipMatch[1].toLowerCase(), label }
                    });
                }
            } else if (dateChipMatch) {
                nodes.push({ type: 'dateChip', attrs: { date: dateChipMatch[1] } });
            } else if (/^\*\*[^*]+\*\*$/.test(token) || /^__[^_]+__$/.test(token)) {
                nodes.push({ type: 'text', text: token.slice(2, -2), marks: [{ type: 'bold' }] });
            } else if (/^`[^`]+`$/.test(token)) {
                nodes.push({ type: 'text', text: token.slice(1, -1), marks: [{ type: 'code' }] });
            } else if (/^\*[^*]+\*$/.test(token) || /^_[^_]+_$/.test(token)) {
                nodes.push({ type: 'text', text: token.slice(1, -1), marks: [{ type: 'italic' }] });
            } else {
                const linkMatch = token.match(/^\[([^\]]+)\]\(([^\)]+)\)$/);
                if (linkMatch) {
                    nodes.push({
                        type: 'text',
                        text: linkMatch[1],
                        marks: [{ type: 'link', attrs: { href: linkMatch[2] } }]
                    });
                }
            }
            lastIndex = match.index + token.length;
        }

        if (lastIndex < textLine.length) {
            nodes.push({ type: 'text', text: textLine.slice(lastIndex) });
        }

        return nodes.filter((node) => node.type === 'statusChip' || node.type === 'dateChip' || String(node?.text || '').length > 0);
    }

    function extractStandaloneStrongHeading(line) {
        const normalized = String(line || '').trim();
        if (!normalized) return '';
        const match = normalized.match(/^(?:\*\*|__)(.+?)(?:\*\*|__)$/);
        if (!match) return '';
        return String(match[1] || '').trim();
    }

    // The stateful engine. Recurses for fenced-directive bodies (each call owns its
    // own paragraph/list/table buffers). depth guards against runaway nesting.
    function parseBlocks(blockLines, depth) {
        const content = [];
        let paragraph = [];
        let listType = '';
        let listItems = [];
        let tableBuffer = [];
        let inTocSection = false;
        let currentSection = '';

        function pushParagraph() {
            if (!paragraph.length) return;
            const paragraphText = paragraph.join(' ').trim();
            if (!paragraphText) {
                paragraph = [];
                return;
            }
            content.push({
                type: 'paragraph',
                content: parseInlineSegments(paragraphText)
            });
            paragraph = [];
        }

        function pushList() {
            if (!listType || !listItems.length) {
                listType = '';
                listItems = [];
                return;
            }

            if (listType === 'taskList') {
                content.push({
                    type: 'taskList',
                    content: listItems.map((item) => ({
                        type: 'taskItem',
                        attrs: {
                            checked: !!item.checked
                        },
                        content: [{
                            type: 'paragraph',
                            content: parseInlineSegments(item.text)
                        }]
                    }))
                });
                listType = '';
                listItems = [];
                return;
            }

            content.push({
                type: listType,
                content: listItems.map((itemText) => ({
                    type: 'listItem',
                    content: [{
                        type: 'paragraph',
                        content: parseInlineSegments(itemText)
                    }]
                }))
            });
            listType = '';
            listItems = [];
        }

        function flushTableBuffer() {
            if (!tableBuffer.length) return;
            const tableNode = buildTableNodeFromBuffer(tableBuffer);
            if (tableNode) {
                pushParagraph();
                pushList();
                content.push(tableNode);
            } else {
                paragraph.push(...tableBuffer.map((line) => String(line || '').trim()).filter(Boolean));
            }
            tableBuffer = [];
        }

        function processLine(trimmed) {
            if (!trimmed) {
                pushParagraph();
                pushList();
                if (inTocSection) inTocSection = false;
                return;
            }

            if (/^\[(toc|table of contents)\]$/i.test(trimmed) || /^\{\{\s*toc\s*\}\}$/i.test(trimmed)) {
                pushParagraph();
                pushList();
                content.push({ type: 'tableOfContentsBlock' });
                inTocSection = true;
                currentSection = 'toc';
                return;
            }

            const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
            if (headingMatch) {
                pushParagraph();
                pushList();

                const headingText = headingMatch[2].trim();
                if (isTocHeadingText(headingText)) {
                    content.push({ type: 'tableOfContentsBlock' });
                    inTocSection = true;
                    currentSection = 'toc';
                    return;
                }

                inTocSection = false;
                currentSection = isActionItemsHeadingText(headingText) ? 'action_items' : '';
                content.push({
                    type: 'heading',
                    attrs: { level: Math.min(3, headingMatch[1].length) },
                    content: parseInlineSegments(headingText)
                });
                return;
            }

            const strongHeadingText = extractStandaloneStrongHeading(trimmed);
            if (strongHeadingText) {
                pushParagraph();
                pushList();

                if (isTocHeadingText(strongHeadingText)) {
                    content.push({ type: 'tableOfContentsBlock' });
                    inTocSection = true;
                    currentSection = 'toc';
                    return;
                }

                inTocSection = false;
                currentSection = isActionItemsHeadingText(strongHeadingText) ? 'action_items' : '';
                content.push({
                    type: 'heading',
                    attrs: { level: 2 },
                    content: parseInlineSegments(strongHeadingText)
                });
                return;
            }

            const plainSectionTitle = trimmed.replace(/[:：]\s*$/, '');
            if (!/^[-*•▪◦]\s+/.test(trimmed) && !/^\d+[\.)]\s+/.test(trimmed)) {
                if (isTocHeadingText(plainSectionTitle)) {
                    pushParagraph();
                    pushList();
                    content.push({ type: 'tableOfContentsBlock' });
                    inTocSection = true;
                    currentSection = 'toc';
                    return;
                }

                if (isActionItemsHeadingText(plainSectionTitle)) {
                    pushParagraph();
                    pushList();
                    inTocSection = false;
                    currentSection = 'action_items';
                    content.push({
                        type: 'heading',
                        attrs: { level: 3 },
                        content: parseInlineSegments(plainSectionTitle)
                    });
                    return;
                }
            }

            if (inTocSection && (/^[-*•▪◦]\s+/.test(trimmed) || /^\d+[\.)]\s+/.test(trimmed))) {
                return;
            }

            const checkboxMatch = trimmed.match(/^[-*]?\s*\[( |x|X)\]\s+(.+)$/);
            if (checkboxMatch) {
                pushParagraph();
                if (listType && listType !== 'taskList') {
                    pushList();
                }
                listType = 'taskList';
                listItems.push({
                    checked: checkboxMatch[1].toLowerCase() === 'x',
                    text: checkboxMatch[2].trim()
                });
                return;
            }

            const bulletMatch = trimmed.match(/^[-*•▪◦]\s+(.+)$/);
            if (bulletMatch) {
                pushParagraph();
                if (currentSection === 'action_items') {
                    if (listType && listType !== 'taskList') {
                        pushList();
                    }
                    listType = 'taskList';
                    listItems.push({ checked: false, text: bulletMatch[1].trim() });
                    return;
                }
                if (listType && listType !== 'bulletList') {
                    pushList();
                }
                listType = 'bulletList';
                listItems.push(bulletMatch[1].trim());
                return;
            }

            const orderedMatch = trimmed.match(/^\d+[\.)]\s+(.+)$/);
            if (orderedMatch) {
                pushParagraph();
                if (currentSection === 'action_items') {
                    if (listType && listType !== 'taskList') {
                        pushList();
                    }
                    listType = 'taskList';
                    listItems.push({ checked: false, text: orderedMatch[1].trim() });
                    return;
                }
                if (listType && listType !== 'orderedList') {
                    pushList();
                }
                listType = 'orderedList';
                listItems.push(orderedMatch[1].trim());
                return;
            }

            if (listType) {
                pushList();
            }

            paragraph.push(trimmed);
        }

        let i = 0;
        while (i < blockLines.length) {
            const rawLine = blockLines[i];
            const trimmed = String(rawLine || '').trim();

            // Accumulate markdown table rows (fence markers are never table rows).
            if (isPotentialMarkdownTableRow(trimmed) && !isFenceOpenLine(rawLine) && !isFenceCloseLine(rawLine)) {
                tableBuffer.push(trimmed);
                i++;
                continue;
            }

            flushTableBuffer();

            // Fenced code block ``` ... ```
            if (/^`{3,}/.test(trimmed)) {
                const langMatch = trimmed.match(/^`{3,}\s*([\w+#.-]*)\s*$/);
                const language = langMatch && langMatch[1] ? langMatch[1].toLowerCase() : null;
                const codeLines = [];
                let j = i + 1;
                for (; j < blockLines.length; j++) {
                    if (/^`{3,}\s*$/.test(String(blockLines[j] || '').trim())) { j++; break; }
                    codeLines.push(blockLines[j]);
                }
                pushParagraph();
                pushList();
                content.push({
                    type: 'codeBlock',
                    attrs: { language, wrap: false },
                    content: codeLines.length ? [{ type: 'text', text: codeLines.join('\n') }] : []
                });
                i = j;
                continue;
            }

            // Fenced directive :::callout / :::columns / :::align / shorthands
            if (depth < MAX_FENCE_DEPTH && isFenceOpenLine(rawLine)) {
                pushParagraph();
                pushList();
                const fence = collectFence(blockLines, i);
                const name = fence.name;
                let produced = null;
                if (name === 'callout' || CALLOUT_TONE_ALIASES.has(name)) {
                    const toneArg = name === 'callout' ? fence.args : name;
                    produced = buildCalloutNode(fence.inner, toneArg, depth);
                } else if (name === 'columns' || name === 'column') {
                    produced = buildColumnsNode(fence.inner, depth);
                } else if (name === 'align' || ALIGNMENTS.has(name)) {
                    const alignArg = ALIGNMENTS.has(name) ? name : fence.args;
                    produced = applyAlignment(fence.inner, alignArg, depth);
                } else {
                    // Unknown directive — unwrap its inner content as plain blocks.
                    produced = parseBlocks(fence.inner, depth + 1);
                }
                if (Array.isArray(produced)) {
                    produced.forEach((node) => { if (node) content.push(node); });
                } else if (produced) {
                    content.push(produced);
                }
                i = fence.next;
                continue;
            }

            // Stray fence markers (unmatched close, or open past max depth) — drop the marker.
            if (isFenceCloseLine(rawLine) || isFenceOpenLine(rawLine)) {
                i++;
                continue;
            }

            // Thematic break / divider
            if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
                pushParagraph();
                pushList();
                content.push({ type: 'horizontalRule' });
                i++;
                continue;
            }

            processLine(trimmed);
            i++;
        }

        flushTableBuffer();
        pushParagraph();
        pushList();
        return content;
    }

    return parseBlocks(lines, 0);
}

function buildAskKaiDocContentJson(text) {
    const raw = String(text || '').trim();
    if (!raw) return EMPTY_PROJECT_DOC_CONTENT;
    const blocks = buildAskKaiDocBlocks(raw);
    return {
        type: 'doc',
        content: blocks.length ? blocks : [{ type: 'paragraph' }]
    };
}

function createAskKaiElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined && text !== null) {
        element.textContent = String(text);
    }
    return element;
}

function getAskKaiActionText(action) {
    switch (action) {
        case ASK_KAI_ACTION_AUTOMATION:
            return t('askKai.actionIndicator.automation');
        case 'create_project_with_tasks':
            return t('askKai.actionIndicator.project');
        case 'create_project_only':
            return t('askKai.actionIndicator.projectOnly');
        case 'create_tasks_only':
            return t('askKai.actionIndicator.tasksOnly');
        case 'create_multiple_projects':
            return t('askKai.actionIndicator.multiProject');
        case ASK_KAI_ACTION_DOCUMENTATION:
        case 'draft_template':
            return t('askKai.actionIndicator.template');
        case 'rewrite_summarize':
            return t('askKai.actionIndicator.rewrite');
        case 'apply_status_chips':
            return t('askKai.actionIndicator.applyChips');
        default:
            return t('askKai.actionIndicator.default');
    }
}

function getAskKaiReviewTitle(review, proposal) {
    if (review?.title) return review.title;
    switch (proposal?.action) {
        case ASK_KAI_ACTION_AUTOMATION:
            return t('askKai.actionAutomation');
        case 'create_tasks_only':
            return t('askKai.actionTasksOnly');
        case 'create_multiple_projects':
            return t('askKai.actionIndicator.multiProject');
        case 'rewrite_summarize':
            return t('askKai.actionRewrite');
        case 'apply_status_chips':
            return t('askKai.actionApplyChips');
        case ASK_KAI_ACTION_DOCUMENTATION:
        case 'draft_template':
            return t('askKai.actionDraft');
        default:
            return t('askKai.previewLabel');
    }
}

function updateAskKaiIntentNote(intent, review) {
    void review;
    void intent;
}

function getAskKaiModeLabel(action) {
    switch (action) {
        // Auto-detect resolves to a real mode, and the indicator must name the mode
        // that actually ran — "Auto-detect proposal" tells the user nothing about
        // what Kai decided to do.
        case ASK_KAI_ACTION_FIND:
            return t('askKai.modeFind');
        case ASK_KAI_ACTION_AUTOMATION:
            return t('askKai.modeAutomation');
        case 'create_project_with_tasks':
            return t('askKai.modeProject');
        case 'create_project_only':
            return t('askKai.modeProjectOnly');
        case 'create_tasks_only':
            return t('askKai.modeTasksOnly');
        case ASK_KAI_ACTION_DOCUMENTATION:
        case 'draft_template':
            return t('askKai.modeTemplate');
        case 'rewrite_summarize':
            return t('askKai.modeRewrite');
        case 'apply_status_chips':
            return t('askKai.modeApplyChips');
        case 'create_multiple_projects':
            return t('askKai.actionIndicator.multiProject');
        default:
            return t('askKai.modeAuto');
    }
}

function getAskKaiSelectedAction() {
    const { action } = getAskKaiElements();
    return String(action?.value || ASK_KAI_ACTION_AUTO).trim() || ASK_KAI_ACTION_AUTO;
}

function getAskKaiDraftStorage() {
    const currentUser = window.authSystem?.getCurrentUser?.();
    const rawUserKey = String(
        currentUser?.userId
        || currentUser?.id
        || currentUser?.username
        || currentUser?.email
        || ''
    ).trim().toLowerCase();

    if (rawUserKey) {
        const safeUserKey = rawUserKey.replace(/[^a-z0-9_.-]/g, '_');
        return {
            storage: localStorage,
            key: `nautilus.askKai.draft.${safeUserKey}`
        };
    }

    return {
        storage: sessionStorage,
        key: 'nautilus.askKai.draft.session'
    };
}

function rememberAskKaiDraft() {
    const { instruction, docContextCheckbox, tagCheckbox } = getAskKaiElements();
    askKaiDraft = {
        instruction: String(instruction?.value || ''),
        useDocContext: !!docContextCheckbox?.checked,
        useGeneratedTags: !!tagCheckbox?.checked
    };
    try {
        const { storage, key } = getAskKaiDraftStorage();
        storage.setItem(key, JSON.stringify(askKaiDraft));
    } catch (_) { /* ignore quota/private-mode errors */ }
}

function restoreAskKaiDraftFromStorage() {
    try {
        const { storage, key } = getAskKaiDraftStorage();
        const raw = storage.getItem(key);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (saved && typeof saved === 'object') {
            askKaiDraft = {
                instruction: String(saved.instruction || ''),
                useDocContext: !!saved.useDocContext,
                useGeneratedTags: !!saved.useGeneratedTags
            };
        }
    } catch (_) { /* ignore */ }
}

function clearAskKaiDraftFromStorage() {
    try {
        const { storage, key } = getAskKaiDraftStorage();
        storage.removeItem(key);
    } catch (_) { /* ignore */ }
}

// ── Saved Kai prompts ──────────────────────────────────────────────────────
// User-bookmarked prompts, persisted per user in the D1 user-data blob (mirrors
// `automations`). Surfaced only in the Kai drawer: a bookmark button saves the
// current composer text, a "Saved" button opens a popover to reuse or delete.

const ASK_KAI_SAVED_MAX = 50;

function normalizeSavedPromptText(text) {
    return String(text || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

// Best-effort persistence. Optimistic UI already updated; on failure revert to
// the caller's snapshot and tell the user, so memory and D1 cannot diverge.
function persistSavedPrompts(previousSnapshot) {
    saveSavedPromptsData(savedPrompts).catch((error) => {
        console.error('Error saving Kai prompt:', error);
        if (Array.isArray(previousSnapshot)) {
            savedPrompts = previousSnapshot;
            syncAskKaiSavedPromptsUI();
        }
        showErrorNotification(t('askKai.savedError'));
    });
}

function saveAskKaiPrompt() {
    const { instruction } = getAskKaiElements();
    const text = String(instruction?.value || '').trim();
    if (!text) {
        focusAskKaiInstructionField();
        return;
    }
    const normalized = normalizeSavedPromptText(text);
    if (savedPrompts.some(entry => normalizeSavedPromptText(entry.text) === normalized)) {
        showSuccessNotification(t('askKai.promptAlreadySaved'));
        syncAskKaiSavedPromptsUI();
        return;
    }
    const previous = savedPrompts.slice();
    savedPrompts = [{ id: `sp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, text, createdAt: Date.now() }, ...savedPrompts]
        .slice(0, ASK_KAI_SAVED_MAX);
    syncAskKaiSavedPromptsUI();
    persistSavedPrompts(previous);
    showSuccessNotification(t('askKai.promptSaved'));
}

// Live search query + armed inline-delete target for the saved-prompts popover.
let askKaiSavedSearchQuery = '';
let askKaiPendingDeleteId = null;
let askKaiEditingSavedId = null;

// True when the given text already exists in savedPrompts (normalized compare).
function isAskKaiPromptSaved(text) {
    const normalized = normalizeSavedPromptText(text);
    if (!normalized) return false;
    return savedPrompts.some(entry => normalizeSavedPromptText(entry.text) === normalized);
}

// Step 1 of deletion: arm the inline "Are you sure?" confirm for this row.
function deleteAskKaiSavedPrompt(id) {
    askKaiEditingSavedId = null;
    askKaiPendingDeleteId = id;
    refreshAskKaiSavedListBody();
}

// Cancel the armed inline confirm and restore the normal row.
function cancelDeleteAskKaiSavedPrompt() {
    askKaiPendingDeleteId = null;
    refreshAskKaiSavedListBody();
}

// Step 2 of deletion: user confirmed — actually remove and persist.
function confirmDeleteAskKaiSavedPrompt(id) {
    askKaiPendingDeleteId = null;
    const previous = savedPrompts.slice();
    savedPrompts = savedPrompts.filter(entry => entry.id !== id);
    syncAskKaiSavedPromptsUI();
    persistSavedPrompts(previous);
}

function editAskKaiSavedPromptName(id) {
    if (!savedPrompts.some(entry => entry.id === id)) return;
    askKaiPendingDeleteId = null;
    askKaiEditingSavedId = id;
    refreshAskKaiSavedListBody();
    requestAnimationFrame(() => {
        const input = document.getElementById('ask-kai-saved-name-input');
        input?.focus();
        input?.select();
    });
}

function cancelEditAskKaiSavedPromptName() {
    askKaiEditingSavedId = null;
    refreshAskKaiSavedListBody();
}

function saveAskKaiSavedPromptName(id) {
    const entry = savedPrompts.find(item => item.id === id);
    const input = document.getElementById('ask-kai-saved-name-input');
    if (!entry || !input) return;
    const label = String(input.value || '').replace(/\s+/g, ' ').trim();
    if (!label) {
        input.focus();
        return;
    }
    const previous = savedPrompts.slice();
    savedPrompts = savedPrompts.map(item => item.id === id ? { ...item, label } : item);
    askKaiEditingSavedId = null;
    syncAskKaiSavedPromptsUI();
    persistSavedPrompts(previous);
    showSuccessNotification(t('askKai.promptNameUpdated'));
}

function applyAskKaiSavedPrompt(id) {
    const entry = savedPrompts.find(item => item.id === id);
    if (!entry) return;
    const { instruction } = getAskKaiElements();
    if (!instruction || askKaiState.busy) return;
    instruction.value = entry.text;
    instruction.dispatchEvent(new Event('input', { bubbles: true }));
    rememberAskKaiDraft();
    syncAskKaiActionPicker();
    setAskKaiPromptPresentation('expanded-editing', { focus: true });
    closeAskKaiSavedPromptsPopover();
}

// Collapsed-row star: save the current prompt if new, or un-save it if already
// stored (toggle). Mirrors saveAskKaiPrompt but with optimistic un-save support.
function toggleAskKaiSavedForCurrentPrompt() {
    if (askKaiState.busy) return;
    const { instruction } = getAskKaiElements();
    const text = String(instruction?.value || '').trim();
    if (!text) return;
    const normalized = normalizeSavedPromptText(text);
    const existing = savedPrompts.find(entry => normalizeSavedPromptText(entry.text) === normalized);
    const previous = savedPrompts.slice();
    if (existing) {
        savedPrompts = savedPrompts.filter(entry => entry.id !== existing.id);
        syncAskKaiSavedPromptsUI();
        persistSavedPrompts(previous);
        showSuccessNotification(t('askKai.promptRemoved'));
    } else {
        savedPrompts = [{ id: `sp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, text, createdAt: Date.now() }, ...savedPrompts]
            .slice(0, ASK_KAI_SAVED_MAX);
        syncAskKaiSavedPromptsUI();
        persistSavedPrompts(previous);
        showSuccessNotification(t('askKai.promptSaved'));
    }
}

// Reflect saved state on the collapsed-row star (☆ ↔ ★) and its label.
function syncAskKaiCollapsedStar() {
    const star = document.getElementById('ask-kai-summary-star-btn');
    if (!star) return;
    const { instruction } = getAskKaiElements();
    const text = String(instruction?.value || '').trim();
    const saved = isAskKaiPromptSaved(text);
    star.textContent = saved ? '★' : '☆';
    star.classList.toggle('is-saved', saved);
    star.disabled = !!askKaiState.busy || !text;
    const label = saved ? t('askKai.unsavePrompt') : t('askKai.savePrompt');
    star.setAttribute('aria-label', label);
    star.setAttribute('title', label);
    star.setAttribute('aria-pressed', saved ? 'true' : 'false');
}

// Keep the bookmark count, popover, and collapsed star in sync with state.
function syncAskKaiSavedPromptsUI() {
    const count = savedPrompts.length;
    const countEl = document.getElementById('ask-kai-saved-count');
    if (countEl) countEl.textContent = count ? `(${count})` : '';
    const btn = document.getElementById('ask-kai-saved-btn');
    if (btn) {
        const base = t('askKai.savedPrompts');
        const label = count ? `${base} (${count})` : base;
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
    }
    const popover = document.getElementById('ask-kai-saved-popover');
    if (popover && popover.style.display !== 'none') {
        popover.innerHTML = renderAskKaiSavedPromptsHtml();
    }
    syncAskKaiCollapsedStar();
}

// Re-render only the filtered list body, preserving the search field + focus.
function refreshAskKaiSavedListBody() {
    const body = document.getElementById('ask-kai-saved-list-body');
    if (body) {
        body.innerHTML = renderAskKaiSavedListBodyHtml();
    } else {
        syncAskKaiSavedPromptsUI();
    }
}

function renderAskKaiSavedPromptsHtml() {
    if (!savedPrompts.length) {
        return `<div class="ask-kai-saved-empty">${escapeHtml(t('askKai.savedEmpty'))}</div>`;
    }
    return `
        <div class="ask-kai-saved-search-wrap">
            <input type="text" id="ask-kai-saved-search" class="ask-kai-saved-search"
                placeholder="${escapeHtml(t('askKai.searchSavedPrompts'))}"
                aria-label="${escapeHtml(t('askKai.searchSavedPrompts'))}"
                value="${escapeHtml(askKaiSavedSearchQuery)}">
        </div>
        <div id="ask-kai-saved-list-body" class="ask-kai-saved-list-body">${renderAskKaiSavedListBodyHtml()}</div>`;
}

function renderAskKaiSavedListBodyHtml() {
    const query = normalizeSavedPromptText(askKaiSavedSearchQuery);
    const matches = query
        ? savedPrompts.filter(entry => normalizeSavedPromptText(`${entry.label || ''} ${entry.text}`).includes(query))
        : savedPrompts;
    if (!matches.length) {
        return `<div class="ask-kai-saved-empty">${escapeHtml(t('askKai.savedNoMatches'))}</div>`;
    }
    const rows = matches.map(entry => {
        const displayName = String(entry.label || entry.text || '').trim();
        if (entry.id === askKaiEditingSavedId) {
            return `
        <div class="ask-kai-saved-item ask-kai-saved-item--editing">
            <input type="text" id="ask-kai-saved-name-input" class="ask-kai-saved-name-input"
                value="${escapeHtml(displayName)}" maxlength="120"
                aria-label="${escapeHtml(t('askKai.promptName'))}">
            <button type="button" class="ask-kai-saved-item__action ask-kai-saved-item__save-name" data-action="saveAskKaiSavedPromptName" data-param="${escapeHtml(entry.id)}" aria-label="${escapeHtml(t('askKai.savePromptName'))}" title="${escapeHtml(t('askKai.savePromptName'))}">&#10003;</button>
            <button type="button" class="ask-kai-saved-item__action" data-action="cancelEditAskKaiSavedPromptName" aria-label="${escapeHtml(t('common.cancel'))}" title="${escapeHtml(t('common.cancel'))}">&times;</button>
        </div>`;
        }
        if (entry.id === askKaiPendingDeleteId) {
            return `
        <div class="ask-kai-saved-item ask-kai-saved-item--confirm">
            <span class="ask-kai-saved-confirm-text">${escapeHtml(t('askKai.deleteSavedPromptConfirm'))}</span>
            <div class="ask-kai-saved-confirm-actions">
                <button type="button" class="ask-kai-saved-confirm-cancel" data-action="cancelDeleteAskKaiSavedPrompt">${escapeHtml(t('common.cancel'))}</button>
                <button type="button" class="ask-kai-saved-confirm-delete" data-action="confirmDeleteAskKaiSavedPrompt" data-param="${escapeHtml(entry.id)}">${escapeHtml(t('common.delete'))}</button>
            </div>
        </div>`;
        }
        return `
        <div class="ask-kai-saved-item">
            <button type="button" class="ask-kai-saved-item__pick" data-action="applyAskKaiSavedPrompt" data-param="${escapeHtml(entry.id)}" title="${escapeHtml(entry.text)}">
                <span class="ask-kai-saved-item__text">${escapeHtml(displayName)}</span>
            </button>
            <button type="button" class="ask-kai-saved-item__action ask-kai-saved-item__edit" data-action="editAskKaiSavedPromptName" data-param="${escapeHtml(entry.id)}" aria-label="${escapeHtml(t('askKai.editPromptName'))}" title="${escapeHtml(t('askKai.editPromptName'))}">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4.2L19 9.2 14.8 5 4 15.8V20Zm2-3.4 8.8-8.8 1.4 1.4L7.4 18H6v-1.4ZM17.7 3.5a1.5 1.5 0 0 1 2.1 0l.7.7a1.5 1.5 0 0 1 0 2.1l-1.4 1.4-2.8-2.8 1.4-1.4Z"/></svg>
            </button>
            <button type="button" class="ask-kai-saved-item__action ask-kai-saved-item__delete" data-action="deleteAskKaiSavedPrompt" data-param="${escapeHtml(entry.id)}" aria-label="${escapeHtml(t('askKai.deleteSavedPrompt'))}" title="${escapeHtml(t('askKai.deleteSavedPrompt'))}">×</button>
        </div>`;
    }).join('');
    return `<div class="ask-kai-saved-list">${rows}</div>`;
}

let askKaiSavedPromptsScrollHandler = null;
let askKaiSavedPromptsOutsideHandler = null;

function toggleAskKaiSavedPrompts() {
    const existing = document.getElementById('ask-kai-saved-popover');
    if (existing && existing.style.display !== 'none') {
        closeAskKaiSavedPromptsPopover();
    } else {
        openAskKaiSavedPromptsPopover();
    }
}

function positionAskKaiSavedPromptsPopover(popover, anchor) {
    const width = Math.min(340, window.innerWidth - 24);
    popover.style.width = width + 'px';
    const r = anchor.getBoundingClientRect();
    let left = Math.min(Math.max(r.right - width, 12), window.innerWidth - width - 12);
    popover.style.left = left + 'px';
    popover.style.top = (r.bottom + 8) + 'px';
    // Flip above the button if it would overflow the viewport bottom.
    const maxBottom = window.innerHeight - 12;
    if (r.bottom + 8 + popover.offsetHeight > maxBottom && r.top - 8 - popover.offsetHeight > 12) {
        popover.style.top = (r.top - 8 - popover.offsetHeight) + 'px';
    }
}

function openAskKaiSavedPromptsPopover() {
    const anchor = document.getElementById('ask-kai-saved-btn');
    if (!anchor) return;
    // Fresh search + no armed delete each time the list opens.
    askKaiSavedSearchQuery = '';
    askKaiPendingDeleteId = null;
    askKaiEditingSavedId = null;
    let popover = document.getElementById('ask-kai-saved-popover');
    if (!popover) {
        popover = document.createElement('div');
        popover.id = 'ask-kai-saved-popover';
        popover.className = 'ask-kai-saved-popover';
        popover.setAttribute('role', 'menu');
        // Delegated so it survives body re-renders; filters as the user types.
        popover.addEventListener('input', (e) => {
            const target = e.target;
            if (target && target.id === 'ask-kai-saved-search') {
                askKaiSavedSearchQuery = target.value;
                askKaiPendingDeleteId = null;
                refreshAskKaiSavedListBody();
            }
        });
        popover.addEventListener('keydown', (e) => {
            if (e.target?.id !== 'ask-kai-saved-name-input') return;
            if (e.key === 'Enter') {
                e.preventDefault();
                saveAskKaiSavedPromptName(askKaiEditingSavedId);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelEditAskKaiSavedPromptName();
            }
        });
    }
    // Portal to body + position:fixed so the drawer's overflow can't clip it.
    if (popover.parentElement !== document.body) document.body.appendChild(popover);
    popover.style.position = 'fixed';
    popover.style.display = 'block';
    popover.innerHTML = renderAskKaiSavedPromptsHtml();
    anchor.setAttribute('aria-expanded', 'true');
    positionAskKaiSavedPromptsPopover(popover, anchor);

    askKaiSavedPromptsOutsideHandler = (e) => {
        const p = document.getElementById('ask-kai-saved-popover');
        if (p && p.style.display !== 'none' && !p.contains(e.target) && e.target !== anchor && !anchor.contains(e.target)) {
            closeAskKaiSavedPromptsPopover();
        }
    };
    document.addEventListener('mousedown', askKaiSavedPromptsOutsideHandler, true);

    askKaiSavedPromptsScrollHandler = () => {
        const p = document.getElementById('ask-kai-saved-popover');
        if (!p || p.style.display === 'none') return;
        positionAskKaiSavedPromptsPopover(p, anchor);
    };
    document.addEventListener('scroll', askKaiSavedPromptsScrollHandler, { capture: true, passive: true });
    window.addEventListener('resize', askKaiSavedPromptsScrollHandler, { passive: true });
}

function closeAskKaiSavedPromptsPopover() {
    const popover = document.getElementById('ask-kai-saved-popover');
    if (popover) popover.style.display = 'none';
    const anchor = document.getElementById('ask-kai-saved-btn');
    if (anchor) anchor.setAttribute('aria-expanded', 'false');
    if (askKaiSavedPromptsOutsideHandler) {
        document.removeEventListener('mousedown', askKaiSavedPromptsOutsideHandler, true);
        askKaiSavedPromptsOutsideHandler = null;
    }
    if (askKaiSavedPromptsScrollHandler) {
        document.removeEventListener('scroll', askKaiSavedPromptsScrollHandler, true);
        window.removeEventListener('resize', askKaiSavedPromptsScrollHandler);
        askKaiSavedPromptsScrollHandler = null;
    }
}

function shouldAskKaiOfferTagGeneration(selectedAction) {
    return selectedAction === 'create_project_only'
        || selectedAction === 'create_tasks_only'
        || selectedAction === 'create_project_with_tasks';
}

function getAskKaiTagToggleLabelKey(selectedAction) {
    if (selectedAction === 'create_project_only') return 'askKai.useGeneratedProjectTags';
    if (selectedAction === 'create_tasks_only') return 'askKai.useGeneratedTaskTags';
    return 'askKai.useGeneratedProjectTaskTags';
}

function isAskKaiDocsRouteActive() {
    const hash = String(window.location.hash || '').toLowerCase();
    return hash === '#docs' || hash.startsWith('#docs?');
}

function isAskKaiDocPageActive(activePageId = getActivePageId()) {
    if (activePageId !== 'docs' || !isAskKaiDocsRouteActive() || !isDocsPageActive()) {
        return false;
    }
    const route = getDocsRouteState();
    if (route.publicToken || route.docId || route.slug) {
        return true;
    }
    return Boolean(getCurrentDocsRecord()?.id);
}

function shouldAskKaiUseDocSource(selectedAction, instructionValue, activePageId = getActivePageId()) {
    return isAskKaiDocPageActive(activePageId)
        && (selectedAction === ASK_KAI_ACTION_REWRITE
            || (selectedAction === ASK_KAI_ACTION_AUTO && isAskKaiRewriteLikeInstruction(instructionValue)));
}

function shouldAskKaiOfferDocContext(selectedAction, activePageId = getActivePageId()) {
    return isAskKaiDocPageActive(activePageId) && ASK_KAI_DOC_CONTEXT_ACTIONS.has(selectedAction);
}

function renderAskKaiContextChips() {
    const { contextChips, instruction, action, docContextCheckbox, tagCheckbox } = getAskKaiElements();
    if (!contextChips || !action) return;

    const activePageId = getActivePageId();
    const selectedAction = getAskKaiSelectedAction();
    const instructionValue = String(instruction?.value || '').trim();
    const shouldUseDocSource = shouldAskKaiUseDocSource(selectedAction, instructionValue, activePageId);
    const shouldUseDocContext = shouldAskKaiOfferDocContext(selectedAction, activePageId) && !!docContextCheckbox?.checked;
    const shouldGenerateTags = shouldAskKaiOfferTagGeneration(selectedAction) && !!tagCheckbox?.checked;

    const chips = [];
    if (activePageId === 'projects') chips.push(t('askKai.contextProjects'));
    if (activePageId === 'tasks') chips.push(t('askKai.contextTasks'));
    if (shouldUseDocSource || shouldUseDocContext) chips.push(t('askKai.contextDocSource'));
    if (shouldGenerateTags) chips.push(t('askKai.contextGenerateTags'));

    contextChips.replaceChildren();
    chips.forEach((label) => {
        contextChips.appendChild(createAskKaiElement('span', 'ask-kai-helper-chip', label));
    });
    contextChips.hidden = chips.length === 0;
}

function buildAskKaiPreviewStatus(title, copy) {
    const shell = createAskKaiElement('div', 'ask-kai-empty-state');
    shell.append(
        createAskKaiElement('h3', 'ask-kai-empty-state__title', title),
        createAskKaiElement('p', 'ask-kai-empty-state__copy', copy)
    );
    return shell;
}

function renderAskKaiIdlePreview() {
    const { preview } = getAskKaiElements();
    if (!preview) return;
    preview.replaceChildren();
    preview.classList.add('ask-kai-preview-shell');
    preview.appendChild(
        buildAskKaiPreviewStatus(
            t('askKai.previewEmptyTitle'),
            t('askKai.previewEmptySummary')
        )
    );
}

function renderAskKaiLoadingPreview() {
    const { preview } = getAskKaiElements();
    if (!preview) return;
    preview.replaceChildren();
    preview.classList.add('ask-kai-preview-shell');
    preview.appendChild(
        buildAskKaiPreviewStatus(
            getAskKaiGeneratingLabel(),
            t('askKai.generatingSummaryWithSeconds', { seconds: getAskKaiGenerationElapsedSeconds() })
        )
    );
}

function buildAskKaiInlineErrorReview(message = '') {
    return {
        type: 'error',
        title: t('askKai.review.errorTitle'),
        summary: t('askKai.review.errorSummary'),
        message: String(message || '').trim() || t('askKai.errors.generateFailed'),
        canApply: false,
        warnings: [],
        stats: {}
    };
}

function startAskKaiGenerationTimer() {
    stopAskKaiGenerationTimer();
    askKaiState.lastGenerationSeconds = null;
    askKaiState.generationStartedAt = Date.now();
    const tick = () => {
        if (!askKaiState.busy || !askKaiState.generationStartedAt) return;
        setAskKaiGenerateButtonBusy(true);
        if (!askKaiState.proposal) {
            renderAskKaiLoadingPreview();
        }
    };
    tick();
    askKaiState.generationTimerId = setInterval(tick, 1000);
}

function syncAskKaiActionPicker() {
    const { action, modeButtons, docContextToggle, docContextCheckbox, tagToggle, tagCheckbox, tagToggleLabel } = getAskKaiElements();
    if (!action) return;

    const activePageId = getActivePageId();
    const rewriteAvailable = isAskKaiRewriteModeAvailable(activePageId);
    const rewriteUnavailableTitle = t('askKai.errors.rewriteDocsOnly');
    const selectedValue = normalizeAskKaiSelectedAction(getAskKaiSelectedAction(), activePageId);
    if (action.value !== selectedValue) {
        action.value = selectedValue;
        action.dispatchEvent(new Event('change', { bubbles: true }));
        return;
    }

    const rewriteOption = action.querySelector(`option[value="${ASK_KAI_ACTION_REWRITE}"]`);
    if (rewriteOption) {
        rewriteOption.disabled = !rewriteAvailable;
        rewriteOption.title = rewriteAvailable ? '' : rewriteUnavailableTitle;
    }

    document.querySelectorAll('#ask-kai-modal .ask-kai-dropdown-option').forEach((option) => {
        const isRewriteOption = option.dataset.value === ASK_KAI_ACTION_REWRITE;
        const isDisabled = isRewriteOption && !rewriteAvailable;
        option.classList.toggle('is-disabled', isDisabled);
        option.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        option.title = isDisabled ? rewriteUnavailableTitle : '';
    });

    modeButtons.forEach((button) => {
        const isRewriteButton = button.dataset.value === ASK_KAI_ACTION_REWRITE;
        const isDisabled = isRewriteButton && !rewriteAvailable;
        const isSelected = button.dataset.value === selectedValue;
        button.disabled = isDisabled;
        button.classList.toggle('is-unavailable', isDisabled);
        button.classList.toggle('is-active', isSelected);
        button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        button.title = isDisabled ? rewriteUnavailableTitle : '';
    });

    if (docContextToggle) {
        const shouldShowDocContext = shouldAskKaiOfferDocContext(selectedValue);
        docContextToggle.hidden = !shouldShowDocContext;
        if (!shouldShowDocContext && docContextCheckbox) {
            docContextCheckbox.checked = false;
            askKaiDraft.useDocContext = false;
        }
    }

    if (tagToggle) {
        const shouldShowTagToggle = shouldAskKaiOfferTagGeneration(selectedValue);
        tagToggle.hidden = !shouldShowTagToggle;
        if (tagToggleLabel) {
            const labelKey = getAskKaiTagToggleLabelKey(selectedValue);
            tagToggleLabel.dataset.i18n = labelKey;
            tagToggleLabel.textContent = t(labelKey);
        }
        if (tagCheckbox) {
            tagCheckbox.checked = shouldShowTagToggle ? askKaiDraft.useGeneratedTags === true : false;
        }
        if (!shouldShowTagToggle) {
            askKaiDraft.useGeneratedTags = false;
        }
    }
    renderAskKaiContextChips();
}

function clearAskKaiProposalState() {
    if (!askKaiState.proposal && !askKaiState.review && !askKaiState.metadata && !askKaiState.intent) return;
    askKaiState.proposal = null;
    askKaiState.review = null;
    askKaiState.metadata = null;
    askKaiState.intent = null;
    askKaiState.selectedAmbiguityMode = null;
    const { confirmBtn } = getAskKaiElements();
    if (confirmBtn) confirmBtn.disabled = true;
    renderAskKaiPreview(null, null, confirmBtn);
    updateAskKaiActionIndicator(null);
    updateAskKaiIntentNote(null, null);
    syncAskKaiGenerateAvailability();
}

function captureAskKaiProposalSnapshot() {
    return {
        proposal: askKaiState.proposal || null,
        review: askKaiState.review || null,
        metadata: askKaiState.metadata || null,
        intent: askKaiState.intent || null,
        selectedAmbiguityMode: askKaiState.selectedAmbiguityMode || null,
        promptPresentation: askKaiState.promptPresentation || 'expanded-editing'
    };
}

function restoreAskKaiProposalSnapshot(snapshot, confirmBtn) {
    askKaiState.proposal = snapshot?.proposal || null;
    askKaiState.review = snapshot?.review || null;
    askKaiState.metadata = snapshot?.metadata || null;
    askKaiState.intent = snapshot?.intent || null;
    askKaiState.selectedAmbiguityMode = snapshot?.selectedAmbiguityMode || null;
    askKaiState.promptPresentation = snapshot?.promptPresentation || 'expanded-editing';
    syncAskKaiPromptPresentation();

    if (askKaiState.review) {
        renderAskKaiPreview(askKaiState.review, askKaiState.proposal, confirmBtn);
        updateAskKaiActionIndicator(
            askKaiState.intent?.resolvedAction
            || String(askKaiState.proposal?.action || '').trim()
            || null
        );
        updateAskKaiIntentNote(askKaiState.intent, askKaiState.review);
        if (confirmBtn) {
            confirmBtn.disabled = isAskKaiReviewApplyBlocked(askKaiState.review);
        }
        syncAskKaiGenerateAvailability();
        return;
    }

    if (confirmBtn) confirmBtn.disabled = true;
    renderAskKaiIdlePreview();
    updateAskKaiActionIndicator(null);
    updateAskKaiIntentNote(null, null);
    syncAskKaiGenerateAvailability();
}

function setupAskKaiActionPicker() {
    const {
        action,
        modeButtons,
        instruction,
        docContextCheckbox,
        tagCheckbox,
        clearBtn,
        promptSummaryClearBtn,
        expandBtn
    } = getAskKaiElements();
    if (!action || action.dataset.askKaiBound) return;

    action.addEventListener('change', () => {
        syncAskKaiActionPicker();
        clearAskKaiProposalState();
        setAskKaiPromptPresentation('expanded-editing');
    });

    modeButtons.forEach((button) => {
        button.addEventListener('click', () => {
            if (button.disabled) return;
            const nextValue = String(button.dataset.value || ASK_KAI_ACTION_AUTO).trim() || ASK_KAI_ACTION_AUTO;
            if (action.value === nextValue) return;
            action.value = nextValue;
            action.dispatchEvent(new Event('change', { bubbles: true }));
        });
    });

    if (instruction) {
        instruction.addEventListener('input', () => {
            rememberAskKaiDraft();
            syncAskKaiActionPicker();
            clearAskKaiProposalState();
            syncAskKaiPromptPresentation();
        });

        instruction.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') return;
            if (!event.ctrlKey && !event.metaKey) return;
            if (event.isComposing) return;

            event.preventDefault();
            submitAskKaiRequest();
        });
    }

    if (docContextCheckbox) {
        docContextCheckbox.addEventListener('change', () => {
            rememberAskKaiDraft();
            renderAskKaiContextChips();
            clearAskKaiProposalState();
            setAskKaiPromptPresentation('expanded-editing');
        });
    }

    if (tagCheckbox) {
        tagCheckbox.addEventListener('change', () => {
            rememberAskKaiDraft();
            renderAskKaiContextChips();
            clearAskKaiProposalState();
            setAskKaiPromptPresentation('expanded-editing');
        });
    }

    clearBtn?.addEventListener('click', () => {
        console.log('Clear button clicked!');
        clearAskKaiPromptInput({ focus: true });
    });

    promptSummaryClearBtn?.addEventListener('click', () => {
        clearAskKaiPromptInput({ focus: true });
    });

    expandBtn?.addEventListener('click', () => {
        if (askKaiState.busy) return;
        setAskKaiPromptPresentation('expanded-editing-after-results', { focus: true });
    });

    action.dataset.askKaiBound = '1';
    syncAskKaiActionPicker();
    syncAskKaiPromptPresentation();
    
    // Setup custom dropdown for mobile
    setupMobileAskKaiDropdown();
}

function setupMobileAskKaiDropdown() {
    const customSelect = document.getElementById('ask-kai-action-select-custom');
    const dropdownOptions = document.getElementById('ask-kai-dropdown-options');
    const selectedText = document.getElementById('ask-kai-selected-text');
    const nativeSelect = document.getElementById('ask-kai-action');
    
    // DEBUG: Log what we found
    console.log('Mobile dropdown setup:', {
        customSelect: !!customSelect,
        dropdownOptions: !!dropdownOptions,
        selectedText: !!selectedText,
        nativeSelect: !!nativeSelect
    });
    
    if (!customSelect || !dropdownOptions || !selectedText || !nativeSelect) {
        console.warn('Mobile dropdown elements not found');
        return;
    }
    
    let isOpen = false;
    
    // Toggle dropdown
    function toggleDropdown(open) {
        isOpen = open !== undefined ? open : !isOpen;
        console.log('Toggling dropdown to:', isOpen);
        dropdownOptions.classList.toggle('show', isOpen);
        customSelect.setAttribute('aria-expanded', isOpen);
        
        // Close on escape
        if (isOpen) {
            const handleEscape = (e) => {
                if (e.key === 'Escape') {
                    toggleDropdown(false);
                    document.removeEventListener('keydown', handleEscape);
                }
            };
            document.addEventListener('keydown', handleEscape);
        }
    }
    
    // Update selected text
    function updateSelectedText() {
        const selectedOption = nativeSelect.options[nativeSelect.selectedIndex];
        selectedText.textContent = selectedOption.text;
        
        // Update selected state in custom dropdown
        const options = dropdownOptions.querySelectorAll('.ask-kai-dropdown-option');
        let selectedOptionElement = null;
        
        options.forEach(option => {
            const isSelected = option.dataset.value === nativeSelect.value;
            option.classList.toggle('selected', isSelected);
            if (isSelected) {
                selectedOptionElement = option;
            }
        });
        
        // Update the color dot in the selected text
        const selectedColorDot = customSelect.querySelector('.ask-kai-selected-option-color');
        if (selectedOptionElement && selectedColorDot) {
            const optionColorDot = selectedOptionElement.querySelector('.ask-kai-dropdown-option-color');
            if (optionColorDot) {
                const computedStyle = window.getComputedStyle(optionColorDot);
                selectedColorDot.style.background = computedStyle.background;
            }
        }
        
        // Hide selected option from the dropdown list
        updateOptionsVisibility();
    }
    
    // Hide the currently selected option from the dropdown
    function updateOptionsVisibility() {
        const options = dropdownOptions.querySelectorAll('.ask-kai-dropdown-option');
        options.forEach(option => {
            const isSelected = option.dataset.value === nativeSelect.value;
            option.style.display = isSelected ? 'none' : 'flex';
        });
    }
    
    // Handle custom select click
    customSelect.addEventListener('click', (e) => {
        console.log('Custom dropdown clicked!');
        e.preventDefault();
        toggleDropdown();
    });
    
    // Handle option clicks
    const options = dropdownOptions.querySelectorAll('.ask-kai-dropdown-option');
    console.log('Found options:', options.length);
    options.forEach(option => {
        option.addEventListener('click', (e) => {
            if (option.classList.contains('is-disabled') || option.getAttribute('aria-disabled') === 'true') {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            console.log('Option clicked:', option.dataset.value);
            e.preventDefault();
            e.stopPropagation(); // Prevent event bubbling
            const value = option.dataset.value;
            nativeSelect.value = value;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
            updateSelectedText();
            toggleDropdown(false); // Close dropdown after selection
        });
    });
    
    // Handle native select change (sync from other interactions)
    nativeSelect.addEventListener('change', () => {
        updateSelectedText();
    });
    
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
        if (!customSelect.contains(e.target)) {
            toggleDropdown(false);
        }
    });
    
    // Initialize
    updateSelectedText();
}

function updateProposalWithChoice(selectedMode) {
    askKaiState.selectedAmbiguityMode = selectedMode;
}

function getAskKaiStatLabels() {
    return {
        projects: t('askKai.stats.projects'),
        tasks: t('askKai.stats.tasks'),
        pages: t('askKai.stats.pages'),
        options: t('askKai.stats.options'),
        sections: t('askKai.stats.sections'),
        characters: t('askKai.stats.characters'),
        levels: t('askKai.stats.levels')
    };
}

function buildAskKaiStats(stats = {}) {
    const entries = Object.entries(stats).filter(([, value]) => value !== null && value !== undefined && value !== '' && Number(value) !== 0);
    if (!entries.length) return null;

    const list = createAskKaiElement('div', 'ask-kai-review-stats');
    const labels = getAskKaiStatLabels();
    entries.forEach(([key, value]) => {
        const chip = createAskKaiElement('div', 'ask-kai-review-stat');
        chip.append(
            createAskKaiElement('span', 'ask-kai-review-stat-value', value),
            createAskKaiElement('span', 'ask-kai-review-stat-label', labels[key] || key)
        );
        list.appendChild(chip);
    });
    return list;
}

function buildAskKaiInlineStats(stats = {}) {
    const entries = Object.entries(stats).filter(([, value]) => value !== null && value !== undefined && value !== '' && Number(value) !== 0);
    if (!entries.length) return null;
    const labels = getAskKaiStatLabels();
    const wrap = createAskKaiElement('div', 'ask-kai-review-chip-row ask-kai-review-chip-row--stats');
    entries.forEach(([key, value]) => {
        const chip = createAskKaiElement('div', 'ask-kai-review-chip ask-kai-review-chip--stat');
        chip.append(
            createAskKaiElement('span', 'ask-kai-review-chip-value', value),
            createAskKaiElement('span', 'ask-kai-review-chip-label', labels[key] || key)
        );
        wrap.appendChild(chip);
    });
    return wrap;
}

function buildAskKaiItems(items = [], overflowCount = 0, previewCount = items.length) {
    if (!Array.isArray(items) || !items.length) return null;
    const list = createAskKaiElement('div', 'ask-kai-review-items');
    const normalizedPreviewCount = Math.max(0, Math.min(Number(previewCount) || items.length, items.length));
    const hasOverflow = Number(overflowCount) > 0 && items.length > normalizedPreviewCount;
    const itemNodes = items.map((item, index) => {
        const card = createAskKaiElement('article', 'ask-kai-review-item');
        card.appendChild(createAskKaiElement('div', 'ask-kai-review-item-title', item?.title || ''));
        if (item?.description) {
            card.appendChild(createAskKaiElement('div', 'ask-kai-review-item-desc', item.description));
        }
        if (item?.meta) {
            card.appendChild(createAskKaiElement('div', 'ask-kai-review-item-meta', item.meta));
        }
        if (hasOverflow && index >= normalizedPreviewCount) {
            card.hidden = true;
        }
        list.appendChild(card);
        return card;
    });
    if (hasOverflow) {
        const toggle = createAskKaiElement(
            'button',
            'ask-kai-review-item ask-kai-review-item--overflow ask-kai-review-item-toggle',
            t('askKai.viewMore', { count: Number(overflowCount) })
        );
        toggle.type = 'button';
        toggle.dataset.expanded = 'false';
        toggle.addEventListener('click', () => {
            const expanded = toggle.dataset.expanded === 'true';
            itemNodes.forEach((node, index) => {
                if (index >= normalizedPreviewCount) {
                    node.hidden = expanded;
                }
            });
            toggle.dataset.expanded = expanded ? 'false' : 'true';
            toggle.textContent = expanded
                ? t('askKai.viewMore', { count: Number(overflowCount) })
                : t('askKai.showLess');
        });
        list.appendChild(toggle);
    }
    return list;
}

function buildAskKaiMetaChips(values = []) {
    const list = Array.isArray(values)
        ? values.map((value) => {
            const content = typeof value === 'object' && value !== null ? value.value : value;
            const className = typeof value === 'object' && value !== null ? value.className : '';
            return {
                value: String(content || '').trim(),
                className: String(className || '').trim()
            };
        }).filter((item) => item.value)
        : [];
    if (!list.length) return null;
    const wrap = createAskKaiElement('div', 'ask-kai-review-chip-row');
    list.forEach((item) => {
        const className = ['ask-kai-review-chip', item.className].filter(Boolean).join(' ');
        wrap.appendChild(createAskKaiElement('span', className, item.value));
    });
    return wrap;
}

function toAskKaiDisplayCase(value) {
    const normalized = String(value || '').trim();
    if (!normalized) return '';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function buildAskKaiDateChip(startDate, endDate) {
    if (startDate && endDate) {
        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    if (startDate) {
        return `${t('tasks.startDatePrefix')}${formatDate(startDate)}`;
    }
    if (endDate) {
        return `${t('tasks.endDatePrefix')}${formatDate(endDate)}`;
    }
    return '';
}

function buildAskKaiRowMetaValues(row = {}, options = {}) {
    const values = [];
    if (options.includeTaskCount && Number(row.taskCount) > 0) {
        values.push(t('askKai.meta.tasksCount', { count: Number(row.taskCount) }));
    }
    const dateChip = buildAskKaiDateChip(row.startDate, row.endDate);
    if (dateChip) values.push(dateChip);
    if (row.priority) values.push(toAskKaiDisplayCase(row.priority));
    if (Number(row.tagCount) > 0) {
        values.push(t('askKai.meta.tagsCount', { count: Number(row.tagCount) }));
    }
    if (row.projectAssociation) {
        values.push({
            value: row.projectAssociation,
            className: row.projectId != null
                ? 'ask-kai-review-chip--project'
                : 'ask-kai-review-chip--project-none'
        });
    }
    return values;
}

function buildAskKaiEmptyNote(text) {
    if (!text) return null;
    return createAskKaiElement('div', 'ask-kai-review-empty-note', text);
}

function appendAskKaiExpandableExcerpt(container, options = {}) {
    if (!container) return;

    const excerptText = String(options.excerpt || '').trim();
    const fullText = String(
        options.fullText
        || options.content
        || options.description
        || options.summary
        || options.excerpt
        || ''
    ).trim();

    const fallbackText = fullText || excerptText || t('askKai.meta.noExcerpt');
    const collapsedText = excerptText || fallbackText;
    const canExpand = !!excerptText && !!fullText && fullText.length > excerptText.length + 12;
    const textEl = createAskKaiElement('div', options.className || 'ask-kai-review-row-excerpt', collapsedText);

    container.appendChild(textEl);

    if (!canExpand) return;

    container.classList.add('ask-kai-expandable-content');

    let expanded = false;
    const toggle = createAskKaiElement(
        'button',
        'ask-kai-doc-detail-toggle',
        t('askKai.seeMore')
    );
    toggle.type = 'button';
    const head = container.querySelector('.ask-kai-review-row-head');
    if (head) {
        toggle.classList.add('ask-kai-doc-detail-toggle-inline');
        head.appendChild(toggle);
    } else {
        container.appendChild(toggle);
    }
    toggle.addEventListener('click', () => {
        expanded = !expanded;
        textEl.textContent = expanded ? fullText : collapsedText;
        toggle.textContent = expanded ? t('askKai.showLess') : t('askKai.seeMore');
    });
}

function buildAskKaiDenseRows(rows = [], options = {}) {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) return options.emptyText ? buildAskKaiEmptyNote(options.emptyText) : null;

    const normalizedPreviewCount = Math.max(0, Math.min(Number(options.previewCount) || list.length, list.length));
    const hasOverflow = Number(options.overflowCount) > 0 && list.length > normalizedPreviewCount;
    const wrap = createAskKaiElement('div', 'ask-kai-review-rows');
    const rowNodes = list.map((row, index) => {
        const article = createAskKaiElement('article', 'ask-kai-review-row');
        const head = createAskKaiElement('div', 'ask-kai-review-row-head');
        head.appendChild(createAskKaiElement('div', 'ask-kai-review-row-title', row?.title || ''));
        const chips = buildAskKaiMetaChips(buildAskKaiRowMetaValues(row, options));
        if (chips) head.appendChild(chips);
        article.appendChild(head);
        appendAskKaiExpandableExcerpt(article, {
            excerpt: row?.excerpt,
            fullText: row?.content || row?.description || row?.summary || row?.excerpt,
            className: 'ask-kai-review-row-excerpt'
        });
        if (hasOverflow && index >= normalizedPreviewCount) {
            article.hidden = true;
        }
        wrap.appendChild(article);
        return article;
    });

    if (hasOverflow) {
        const toggle = createAskKaiElement(
            'button',
            'ask-kai-review-list-toggle',
            t('askKai.viewMore', { count: Number(options.overflowCount) })
        );
        toggle.type = 'button';
        toggle.dataset.expanded = 'false';
        toggle.addEventListener('click', () => {
            const expanded = toggle.dataset.expanded === 'true';
            rowNodes.forEach((node, index) => {
                if (index >= normalizedPreviewCount) {
                    node.hidden = expanded;
                }
            });
            toggle.dataset.expanded = expanded ? 'false' : 'true';
            toggle.textContent = expanded
                ? t('askKai.viewMore', { count: Number(options.overflowCount) })
                : t('askKai.showLess');
        });
        wrap.appendChild(toggle);
    }

    return wrap;
}

function renderAskKaiProjectFocus(project, card, options = {}) {
    if (!project || !card) return;
    const summary = createAskKaiElement('div', 'ask-kai-review-focus');
    summary.appendChild(createAskKaiElement('div', 'ask-kai-review-row-title', project.title || ''));
    appendAskKaiExpandableExcerpt(summary, {
        excerpt: project.excerpt,
        fullText: project.content || project.description || project.summary || project.excerpt,
        className: 'ask-kai-review-row-excerpt'
    });
    const chips = buildAskKaiMetaChips(buildAskKaiRowMetaValues(project, {
        includeTaskCount: options.includeTaskCount === true
    }));
    if (chips) {
        summary.appendChild(chips);
    }
    card.appendChild(summary);
}

function buildAskKaiWarnings(warnings = []) {
    if (!Array.isArray(warnings) || !warnings.length) return null;
    const box = createAskKaiElement('div', 'ask-kai-review-warnings');
    warnings.forEach((warning) => {
        const message = typeof warning === 'string' ? warning : String(warning?.message || '').trim();
        if (!message) return;
        box.appendChild(createAskKaiElement('div', 'ask-kai-review-warning', message));
    });
    if (!box.childNodes.length) return null;
    return box;
}

function isAskKaiReviewApplyBlocked(review = null) {
    if (!review || typeof review !== 'object') return true;
    if (review.type === 'ambiguity') {
        return !askKaiState.selectedAmbiguityMode;
    }
    return review.canApply === false;
}

function buildAskKaiBlockingIssues(review = {}) {
    const issues = Array.isArray(review?.blockingIssues)
        ? review.blockingIssues
            .map((issue) => typeof issue === 'string' ? issue : String(issue?.message || '').trim())
            .filter(Boolean)
        : [];
    if (!issues.length) return null;

    const shell = createAskKaiElement('div', 'ask-kai-review-warnings');
    issues.forEach((issue) => {
        shell.appendChild(createAskKaiElement('div', 'ask-kai-review-warning', issue));
    });

    const repairAvailable = askKaiState.metadata?.repairAvailable === true;
    const repairExhausted = askKaiState.metadata?.repairExhausted === true;
    const repairContext = askKaiState.metadata?.repairContext && typeof askKaiState.metadata.repairContext === 'object'
        ? askKaiState.metadata.repairContext
        : null;
    if (repairExhausted) {
        shell.appendChild(createAskKaiElement('div', 'ask-kai-review-warning', t('askKai.review.blockedExhausted')));
    }
    if (repairAvailable && repairContext) {
        const actions = createAskKaiElement('div', 'modal-actions');
        const repairBtn = createAskKaiElement('button', 'btn-secondary', t('askKai.review.fixButton'));
        repairBtn.type = 'button';
        repairBtn.disabled = !!askKaiState.busy;
        repairBtn.addEventListener('click', () => {
            if (askKaiState.busy) return;
            submitAskKaiRequest({
                skipRepeatWarning: true,
                repairContext
            });
        });
        actions.appendChild(repairBtn);
        shell.appendChild(actions);
    }

    return shell;
}

function buildAskKaiAmbiguityOptions(review, confirmBtn, preview) {
    const shell = createAskKaiElement('div', 'ambiguity-choices');
    shell.appendChild(createAskKaiElement('div', 'ambiguity-header', review?.summary || t('askKai.ambiguityHeader')));

    const optionsWrap = createAskKaiElement('div', 'ambiguity-options');
    const footer = createAskKaiElement('div', 'ambiguity-footer', t('askKai.ambiguityFooter'));
    const detailWrap = createAskKaiElement('div', 'ambiguity-preview');
    const optionList = Array.isArray(review?.options) ? review.options : [];

    const renderSelectedOption = (option) => {
        detailWrap.replaceChildren();
        if (!option) return;

        const card = createAskKaiElement('section', 'ask-kai-review-card ambiguity-preview-card');
        const header = createAskKaiElement('div', 'ask-kai-review-header');
        const titleRow = createAskKaiElement('div', 'ask-kai-review-title-row');
        titleRow.appendChild(createAskKaiElement('h3', 'ask-kai-review-title', option.title || ''));
        header.append(
            createAskKaiElement('div', 'ask-kai-review-kicker', t('askKai.ambiguityPreviewLabel')),
            titleRow
        );
        if (option.description) {
            header.appendChild(createAskKaiElement('p', 'ask-kai-review-summary', option.description));
        }
        card.appendChild(header);

        const stats = buildAskKaiInlineStats(option.stats || {});
        if (stats) card.appendChild(stats);

        const items = buildAskKaiItems(option.items || [], option.overflowCount || 0, option.previewCount || option.items?.length || 0);
        if (items) card.appendChild(items);

        detailWrap.appendChild(card);
    };

    optionList.forEach((option) => {
        const card = createAskKaiElement('button', 'ambiguity-option');
        card.type = 'button';
        card.dataset.mode = option.mode || '';
        card.appendChild(createAskKaiElement('div', 'ambiguity-option-title', option.title || ''));
        card.appendChild(createAskKaiElement('div', 'ambiguity-option-desc', option.description || ''));

        const stats = buildAskKaiInlineStats(option.stats || {});
        if (stats) card.appendChild(stats);

        card.addEventListener('click', () => {
            optionsWrap.querySelectorAll('.ambiguity-option').forEach((node) => node.classList.remove('selected'));
            card.classList.add('selected');
            updateProposalWithChoice(card.dataset.mode || '');
            if (confirmBtn) confirmBtn.disabled = false;
            syncAskKaiGenerateAvailability();
            footer.textContent = `${t('askKai.ambiguitySelectedPrefix')} ${option.title || ''}`;
            footer.classList.add('success');
            renderSelectedOption(option);
        });

        optionsWrap.appendChild(card);
    });

    const selectedOption = optionList.find((option) => option.mode === askKaiState.selectedAmbiguityMode) || null;
    if (selectedOption) {
        const selectedCard = optionsWrap.querySelector(`[data-mode="${selectedOption.mode}"]`);
        if (selectedCard) selectedCard.classList.add('selected');
        if (confirmBtn) confirmBtn.disabled = false;
        syncAskKaiGenerateAvailability();
        footer.textContent = `${t('askKai.ambiguitySelectedPrefix')} ${selectedOption.title || ''}`;
        footer.classList.add('success');
        renderSelectedOption(selectedOption);
    } else if (confirmBtn) {
        confirmBtn.disabled = true;
    }

    shell.append(optionsWrap, detailWrap, footer);
    preview.appendChild(shell);
}

function buildAskKaiReviewCard(review, proposal) {
    const card = createAskKaiElement('section', 'ask-kai-review-card');
    const header = createAskKaiElement('div', 'ask-kai-review-header');
    const titleRow = createAskKaiElement('div', 'ask-kai-review-title-row');
    titleRow.appendChild(createAskKaiElement('h3', 'ask-kai-review-title', getAskKaiReviewTitle(review, proposal)));
    const kickerText = getAskKaiActionText(proposal?.action);
    header.append(
        createAskKaiElement('div', 'ask-kai-review-kicker', kickerText),
        titleRow
    );
    if (review.summary) {
        header.appendChild(createAskKaiElement('p', 'ask-kai-review-summary', review.summary));
    }
    card.appendChild(header);

    const blocking = buildAskKaiBlockingIssues(review);
    const warnings = buildAskKaiWarnings(review.warnings || []);
    let stats = buildAskKaiInlineStats(review.stats || {});
    if (askKaiState.lastGenerationSeconds != null) {
        if (!stats) stats = createAskKaiElement('div', 'ask-kai-review-chip-row ask-kai-review-chip-row--stats');
        const timeChip = createAskKaiElement('div', 'ask-kai-review-chip ask-kai-review-chip--stat ask-kai-review-chip--time');
        const _clockSvgNS = 'http://www.w3.org/2000/svg';
        const _clockSvg = document.createElementNS(_clockSvgNS, 'svg');
        _clockSvg.setAttribute('viewBox', '0 0 16 16');
        _clockSvg.setAttribute('width', '12');
        _clockSvg.setAttribute('height', '12');
        _clockSvg.setAttribute('fill', 'none');
        _clockSvg.setAttribute('stroke', 'currentColor');
        _clockSvg.setAttribute('stroke-width', '1.5');
        _clockSvg.setAttribute('stroke-linecap', 'round');
        _clockSvg.style.cssText = 'flex-shrink:0';
        const _clockCircle = document.createElementNS(_clockSvgNS, 'circle');
        _clockCircle.setAttribute('cx', '8'); _clockCircle.setAttribute('cy', '8'); _clockCircle.setAttribute('r', '6.5');
        const _clockHands = document.createElementNS(_clockSvgNS, 'polyline');
        _clockHands.setAttribute('points', '8,4.5 8,8 10.5,10');
        _clockSvg.appendChild(_clockCircle);
        _clockSvg.appendChild(_clockHands);
        timeChip.appendChild(_clockSvg);
        timeChip.appendChild(createAskKaiElement('span', 'ask-kai-review-chip-value', `${askKaiState.lastGenerationSeconds}s`));
        stats.appendChild(timeChip);
    }
    if (stats) card.appendChild(stats);
    if (blocking) card.appendChild(blocking);
    return { card, warnings };
}

function renderDocumentationProposalReview(review, proposal, preview) {
    const { card, warnings } = buildAskKaiReviewCard(review, proposal);
    const nodes = Array.isArray(review?.tree) ? review.tree : [];
    const nodesByRef = new Map(
        nodes.map((node) => [String(node?.ref || ''), node]).filter(([ref]) => ref)
    );
    const treeWrap = createAskKaiElement('div', 'ask-kai-doc-tree');
    const detailWrap = createAskKaiElement('div', 'ask-kai-doc-detail');
    const reviewWrap = createAskKaiElement('div', 'ask-kai-doc-review');
    const buttons = [];
    let expandedRef = null;
    const hasHierarchy = nodes.some((node) => Math.max(1, Number(node?.depth) || 1) > 1);
    treeWrap.classList.toggle('has-hierarchy', hasHierarchy);

    const renderDetail = (requestedRef) => {
        const activeNode = nodes.find((node) => String(node?.ref || '') === String(requestedRef))
            || nodes.find((node) => node?.isRoot)
            || nodes[0]
            || null;
        detailWrap.replaceChildren();
        if (!activeNode) return;
        const activeRef = String(activeNode?.ref || '');
        const fullContent = String(activeNode?.content || '').trim();
        const excerptText = String(activeNode?.excerpt || '').trim() || t('askKai.meta.noExcerpt');
        const canExpand = !!fullContent && fullContent.length > excerptText.length + 12;
        const isExpanded = canExpand && expandedRef === activeRef;
        const detailText = isExpanded ? fullContent : excerptText;
        detailWrap.appendChild(createAskKaiElement('div', 'ask-kai-doc-detail-title', activeNode.title || ''));
        let detailMeta = '';
        if (activeNode.isRoot) {
            detailMeta = t('askKai.meta.rootPage');
        } else {
            const parentTitle = nodesByRef.get(String(activeNode?.parentRef || ''))?.title || '';
            if (parentTitle) detailMeta = t('askKai.meta.childOf', { title: parentTitle });
        }
        if (detailMeta) {
            detailWrap.appendChild(createAskKaiElement('div', 'ask-kai-doc-detail-meta', detailMeta));
        }
        detailWrap.appendChild(createAskKaiElement(
            'div',
            'ask-kai-doc-detail-excerpt',
            detailText
        ));
        if (canExpand) {
            const toggle = createAskKaiElement(
                'button',
                'ask-kai-doc-detail-toggle',
                isExpanded ? t('askKai.showLess') : t('askKai.seeMore')
            );
            toggle.type = 'button';
            toggle.addEventListener('click', () => {
                expandedRef = isExpanded ? null : activeRef;
                renderDetail(activeRef);
            });
            detailWrap.appendChild(toggle);
        }
        buttons.forEach(({ ref, button }) => {
            button.classList.toggle('is-selected', String(ref) === activeRef);
        });
    };

    // Build map of children by parentRef
    const childrenByRef = new Map();
    nodes.forEach((node) => {
        const key = String(node?.parentRef || '');
        if (!childrenByRef.has(key)) childrenByRef.set(key, []);
        childrenByRef.get(key).push(node);
    });

    // Recursive renderer: li.tree-item > div.tree-row + ul.tree-children
    const renderNodes = (parentRef, ul) => {
        const children = childrenByRef.get(parentRef) || [];
        children.forEach((node) => {
            const li = document.createElement('li');
            li.className = 'ask-kai-doc-tree-item';

            const row = createAskKaiElement('div', 'ask-kai-doc-tree-row');
            const btn = createAskKaiElement('button', 'ask-kai-doc-tree-node');
            btn.type = 'button';
            if (node?.isRoot) btn.classList.add('is-root');
            btn.appendChild(createAskKaiElement('span', 'ask-kai-doc-tree-node-label', node?.title || 'Untitled page'));
            btn.addEventListener('click', () => renderDetail(node?.ref || ''));
            row.appendChild(btn);
            li.appendChild(row);
            buttons.push({ ref: node?.ref || '', button: btn });

            const nodeRef = String(node?.ref || '');
            if (childrenByRef.has(nodeRef)) {
                const subUl = document.createElement('ul');
                subUl.className = 'ask-kai-doc-tree-children';
                li.appendChild(subUl);
                renderNodes(nodeRef, subUl);
            }
            ul.appendChild(li);
        });
    };

    // Root node sits outside the list
    const rootNode = nodes.find((n) => n?.isRoot) || nodes[0];
    if (rootNode) {
        const rootBtn = createAskKaiElement('button', 'ask-kai-doc-tree-node is-root');
        rootBtn.type = 'button';
        rootBtn.appendChild(createAskKaiElement('span', 'ask-kai-doc-tree-node-label', rootNode?.title || 'Untitled page'));
        rootBtn.addEventListener('click', () => renderDetail(rootNode?.ref || ''));
        treeWrap.appendChild(rootBtn);
        buttons.push({ ref: rootNode?.ref || '', button: rootBtn });
    }

    const rootRef = String(rootNode?.ref || '');
    if (childrenByRef.has(rootRef)) {
        const rootUl = document.createElement('ul');
        rootUl.className = 'ask-kai-doc-tree-children';
        treeWrap.appendChild(rootUl);
        renderNodes(rootRef, rootUl);
    }

    renderDetail(review?.selectedRef || rootNode?.ref || nodes[0]?.ref || '');
    reviewWrap.append(treeWrap, detailWrap);
    if (warnings) card.appendChild(warnings);
    card.appendChild(reviewWrap);
    preview.appendChild(card);

    // Draw connectors via two SVG layers: lines behind nodes, dots in front.
    const svgNS = 'http://www.w3.org/2000/svg';
    const svgLines = document.createElementNS(svgNS, 'svg');
    const svgDots  = document.createElementNS(svgNS, 'svg');
    svgLines.setAttribute('aria-hidden', 'true');
    svgDots.setAttribute('aria-hidden', 'true');
    svgLines.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible;z-index:1';
    svgDots.style.cssText  = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;overflow:visible;z-index:3';
    treeWrap.style.position = 'relative';
    treeWrap.insertBefore(svgDots,  treeWrap.firstChild);
    treeWrap.insertBefore(svgLines, treeWrap.firstChild);

    setTimeout(() => {
        const rootRect = treeWrap.getBoundingClientRect();
        if (!rootRect.width) return;

        const LINE_COLOR = '#2e4f82';
        const DOT_COLOR  = '#4a78c0';
        const STROKE = 2;
        const DOT_R  = 2.8;
        const ARM    = 18;

        // Collect dots and draw them all after all lines (ensures dots are on top within each SVG)
        const pendingDots = [];

        function rel(el) {
            const r = el.getBoundingClientRect();
            return { left: r.left - rootRect.left, centerY: r.top - rootRect.top + r.height / 2 };
        }
        function mkLine(x1, y1, x2, y2) {
            const l = document.createElementNS(svgNS, 'line');
            l.setAttribute('x1', x1); l.setAttribute('y1', y1);
            l.setAttribute('x2', x2); l.setAttribute('y2', y2);
            l.setAttribute('stroke', LINE_COLOR);
            l.setAttribute('stroke-width', STROKE);
            l.setAttribute('stroke-linecap', 'round');
            svgLines.appendChild(l);
        }

        function drawGroup(parentEl, childrenUl, depth) {
            depth = depth || 1;
            const items = [...childrenUl.querySelectorAll(':scope > .ask-kai-doc-tree-item')];
            if (!items.length) return;
            // Measure buttons (not row divs) — row divs all start at x=0 regardless of nesting;
            // buttons start at (nested-ul padding-left * depth) + row padding-left = physical indent
            const btns = items.map(i => i.querySelector(':scope > .ask-kai-doc-tree-row > .ask-kai-doc-tree-node')).filter(Boolean);
            if (!btns.length) return;

            const pRect  = rel(parentEl);
            const rRects = btns.map(rel);
            const first  = rRects[0];
            const last   = rRects[rRects.length - 1];
            const spineX = first.left - ARM + 0.5;

            // Vertical descent: parent center → first child center
            mkLine(spineX, pRect.centerY, spineX, first.centerY - 1);
            // Sibling spine: first → last child center
            if (rRects.length > 1) mkLine(spineX, first.centerY - 1, spineX, last.centerY - 1);
            // Horizontal arms; dot sits ON the spine (left end of arm), not at button edge
            rRects.forEach(r => {
                mkLine(spineX, r.centerY - 1, r.left, r.centerY - 1);
                if (depth > 1) pendingDots.push({ cx: r.left, cy: r.centerY - 1 });
            });

            items.forEach(item => {
                const btn    = item.querySelector(':scope > .ask-kai-doc-tree-row > .ask-kai-doc-tree-node');
                const nested = item.querySelector(':scope > .ask-kai-doc-tree-children');
                if (btn && nested) drawGroup(btn, nested, depth + 1);
            });
        }

        const rootBtn = treeWrap.querySelector(':scope > .ask-kai-doc-tree-node.is-root');
        const rootUl  = treeWrap.querySelector(':scope > .ask-kai-doc-tree-children');
        if (rootBtn && rootUl) drawGroup(rootBtn, rootUl);

        // Draw all dots on top of all lines
        pendingDots.forEach(({ cx, cy }) => {
            const c = document.createElementNS(svgNS, 'circle');
            c.setAttribute('cx', cx); c.setAttribute('cy', cy);
            c.setAttribute('r', DOT_R); c.setAttribute('fill', DOT_COLOR);
            svgDots.appendChild(c);
        });

        const h = treeWrap.scrollHeight;
        [svgLines, svgDots].forEach(s => {
            s.setAttribute('viewBox', `0 0 ${rootRect.width} ${h}`);
            s.setAttribute('height', h);
        });
    }, 50);

}

function renderTaskProposalReview(review, proposal, preview) {
    const { card, warnings } = buildAskKaiReviewCard(review, proposal);
    // Task-only proposals may be attached to one existing project when the
    // prompt names it. Surface the same resolver result used on Apply so the
    // review never leaves the resulting association ambiguous.
    const projectReference = resolveAskKaiProjectReference(askKaiDraft.instruction, projects);
    const project = projectReference.matched
        ? projects.find((candidate) => String(candidate?.id) === String(projectReference.projectId))
        : null;
    const projectAssociation = project
        ? t('askKai.meta.project', { name: project.name })
        : t('askKai.meta.noProject');
    const reviewRows = (review?.rows || []).map((row) => ({
        ...row,
        projectId: project?.id ?? null,
        projectAssociation
    }));
    const rows = buildAskKaiDenseRows(reviewRows, {
        previewCount: review?.previewCount || review?.rows?.length || 0,
        overflowCount: review?.overflowCount || 0
    });
    if (warnings) card.appendChild(warnings);
    if (rows) card.appendChild(rows);
    preview.appendChild(card);
}

function renderProjectProposalReview(review, proposal, preview) {
    const { card, warnings } = buildAskKaiReviewCard(review, proposal);
    const project = review?.project || null;
    if (warnings) card.appendChild(warnings);
    renderAskKaiProjectFocus(project, card, { includeTaskCount: true });
    preview.appendChild(card);
}

function renderProjectWithTasksProposalReview(review, proposal, preview) {
    const { card, warnings } = buildAskKaiReviewCard(review, proposal);
    const project = review?.project || null;
    renderAskKaiProjectFocus(project, card, { includeTaskCount: false });
    const rows = buildAskKaiDenseRows(review?.taskRows || [], {
        previewCount: review?.previewCount || review?.taskRows?.length || 0,
        overflowCount: review?.overflowCount || 0
    });
    if (warnings) card.appendChild(warnings);
    // Indent the task rows under the project to signal the parent → child relationship.
    if (rows) {
        rows.classList.add('ask-kai-review-rows--nested');
        card.appendChild(rows);
    }
    preview.appendChild(card);
}

function renderMultipleProjectsProposalReview(review, proposal, preview) {
    const { card, warnings } = buildAskKaiReviewCard(review, proposal);
    const rows = buildAskKaiDenseRows(review?.projectRows || [], {
        previewCount: review?.previewCount || review?.projectRows?.length || 0,
        overflowCount: review?.overflowCount || 0,
        includeTaskCount: true
    });
    if (warnings) card.appendChild(warnings);
    if (rows) card.appendChild(rows);
    preview.appendChild(card);
}

function renderRewriteProposalReview(review, proposal, preview) {
    const { card, warnings } = buildAskKaiReviewCard(review, proposal);
    const focus = createAskKaiElement('div', 'ask-kai-review-focus');
    focus.appendChild(createAskKaiElement(
        'div',
        'ask-kai-review-row-excerpt',
        review?.excerpt || review?.summary || t('askKai.meta.noExcerpt')
    ));
    if (warnings) card.appendChild(warnings);
    card.appendChild(focus);
    preview.appendChild(card);
}

function renderGenericProposalReview(review, proposal, preview) {
    const { card, warnings } = buildAskKaiReviewCard(review, proposal);
    const items = buildAskKaiItems(review.items || [], review.overflowCount || 0, review.previewCount || review.items?.length || 0);
    if (warnings) card.appendChild(warnings);
    if (items) card.appendChild(items);
    preview.appendChild(card);
}

function renderStatusChipsProposalReview(review, proposal, preview) {
    const { card, warnings } = buildAskKaiReviewCard(review, proposal);
    if (warnings) card.appendChild(warnings);

    const targets = Array.isArray(proposal?.payload?.targets) ? proposal.payload.targets : [];
    const groups = createAskKaiElement('div', 'ask-kai-chip-review');

    targets.forEach((target) => {
        const group = createAskKaiElement('div', 'ask-kai-chip-review-group');
        group.appendChild(createAskKaiElement(
            'div',
            'ask-kai-chip-review-column',
            String(target?.columnHeader || '')
        ));
        const chips = createAskKaiElement('div', 'ask-kai-chip-review-chips');
        Object.entries(target?.mapping || {}).forEach(([label, tone]) => {
            const normalizedTone = ASK_KAI_STATUS_CHIP_TONES.has(String(tone).toLowerCase())
                ? String(tone).toLowerCase()
                : 'grey';
            const chip = createAskKaiElement(
                'span',
                `docs-status-chip docs-status-chip--${normalizedTone}`,
                String(label || '')
            );
            chip.setAttribute('data-tone', normalizedTone);
            chips.appendChild(chip);
        });
        group.appendChild(chips);
        groups.appendChild(group);
    });

    card.appendChild(groups);
    preview.appendChild(card);
}

function renderAskKaiErrorReview(review, proposal, preview) {
    const { card } = buildAskKaiReviewCard(review, proposal);
    card.appendChild(createAskKaiElement(
        'div',
        'ask-kai-review-warning',
        String(review?.message || review?.summary || t('askKai.errors.generateFailed')).trim()
    ));
    preview.appendChild(card);
}

/**
 * Collapse the mode grid once there is a proposal.
 *
 * Eight mode buttons eat most of the drawer's height, and once Kai has answered the
 * results are what matter. The mode chip beside "Review" still names the mode that
 * ran, so nothing is hidden — and editing the prompt brings the grid straight back.
 */
function syncAskKaiModesCollapsed() {
    const { modal } = getAskKaiElements();
    if (!modal) return;
    const collapsed = !!askKaiState.review && getAskKaiPromptPresentation() === 'collapsed-readonly';
    modal.classList.toggle('ask-kai-modes-collapsed', collapsed);
}

/*
 * Keep the mode chips A-Z by their *localized* label. The DOM order is A-Z in
 * English, but translated labels sort differently (e.g. "Consulta" vs "Query"),
 * so reorder the buttons by visible text using the active locale's collation.
 */
function sortAskKaiModeChips() {
    const picker = document.getElementById('ask-kai-action-picker');
    if (!picker) return;
    const lang = getCurrentLanguage();
    const collator = new Intl.Collator(lang, { sensitivity: 'base' });
    Array.from(picker.querySelectorAll('.ask-kai-mode-btn'))
        .sort((a, b) => collator.compare(a.textContent.trim(), b.textContent.trim()))
        .forEach((btn) => picker.appendChild(btn));
}

function renderAskKaiPreview(review, proposal, confirmBtn) {
    const { preview } = getAskKaiElements();
    if (!preview) return;

    preview.replaceChildren();
    preview.classList.add('ask-kai-preview-shell');
    syncAskKaiModesCollapsed();

    // Query mode relabels the primary button ("Show tasks"). Put it back for every
    // other mode, or the label leaks across a mode switch.
    const applyBtn = confirmBtn || getAskKaiElements().confirmBtn;
    if (applyBtn && review?.type !== 'query_filter') {
        applyBtn.textContent = t('askKai.confirmButton');
    }

    if (!review) {
        renderAskKaiIdlePreview();
        return;
    }

    if (review.type === 'ambiguity') {
        buildAskKaiAmbiguityOptions(review, confirmBtn, preview);
        return;
    }
    if (review.type === 'error') {
        renderAskKaiErrorReview(review, proposal, preview);
        return;
    }

    switch (review.type) {
        case 'query_filter':
            renderKaiQueryReview(review, proposal, preview);
            return;
        case 'draft_documentation':
            renderDocumentationProposalReview(review, proposal, preview);
            return;
        case 'create_tasks_only':
            renderTaskProposalReview(review, proposal, preview);
            return;
        case 'create_project_only':
            renderProjectProposalReview(review, proposal, preview);
            return;
        case 'create_project_with_tasks':
            renderProjectWithTasksProposalReview(review, proposal, preview);
            return;
        case 'create_multiple_projects':
            renderMultipleProjectsProposalReview(review, proposal, preview);
            return;
        case 'rewrite_summarize':
            renderRewriteProposalReview(review, proposal, preview);
            return;
        case 'apply_status_chips':
            renderStatusChipsProposalReview(review, proposal, preview);
            return;
        default:
            renderGenericProposalReview(review, proposal, preview);
    }
}

function splitAskKaiTitleAndIcon(rawTitle) {
    const normalized = String(rawTitle || '').trim();
    if (!normalized) {
        return { icon: '', title: '' };
    }

    const withTextMatch = normalized.match(/^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)\s+(.+)$/u);
    if (withTextMatch) {
        return {
            icon: String(withTextMatch[1] || '').trim(),
            title: String(withTextMatch[2] || '').trim()
        };
    }

    return { icon: '', title: normalized };
}

function extractAskKaiPlainTextFromDocNode(node) {
    if (!node) return '';
    if (Array.isArray(node)) {
        return node.map((item) => extractAskKaiPlainTextFromDocNode(item)).join(' ');
    }
    if (typeof node === 'string') {
        return node;
    }
    if (typeof node !== 'object') {
        return '';
    }

    const ownText = String(node.text || '');
    const childText = Array.isArray(node.content)
        ? node.content.map((item) => extractAskKaiPlainTextFromDocNode(item)).join(' ')
        : '';

    return `${ownText} ${childText}`.trim();
}

function getAskKaiCurrentDocSourceText() {
    const state = getDocsState();
    const current = state.currentDocId
        ? state.items.find((item) => String(item.id) === String(state.currentDocId))
        : null;
    if (!current) return '';

    const draft = getDocsDraft();
    const text = extractAskKaiPlainTextFromDocNode(draft?.contentJson || EMPTY_PROJECT_DOC_CONTENT);
    return String(text || '').replace(/\s+/g, ' ').trim();
}

function isAskKaiRewriteLikeInstruction(instruction) {
    const normalized = String(instruction || '').trim().toLowerCase();
    if (!normalized) return false;
    // Rewrite/summarize verbs, plus "beautify/format" and toolbar-formatting verbs
    // (bullets, columns, callouts, TOC, checklist). English + Spanish. Kept broad
    // because on a docs page the only alternative intent is drafting a new page.
    return /(rewrite|re-?write|summari[sz]e|rephrase|paraphrase|shorten|condense|improve|polish|edit|beautif|prett|format|re-?format|restyle|tidy|clean\s*up|bullet|checklist|two\s+columns?|2\s+columns?|callout|table\s+of\s+contents|embellec|formate|formato|mejora|reformul|pulir|acortar|resum|limpia|ordena|vi[ñn]eta|columnas?|aviso|encabezado)/i.test(normalized);
}

// Chip-annotation intent — colored status chips out of short table-cell labels.
// English + Spanish, phrasing-tolerant ("convert/turn/change ... into chips/badges").
function isAskKaiStatusChipLikeInstruction(instruction) {
    const normalized = String(instruction || '').trim().toLowerCase();
    if (!normalized) return false;
    return /\b(status\s*chips?|chips?|badges?|insignias?)\b/.test(normalized)
        || /chips?\s+de\s+estado/.test(normalized);
}

function askKaiDocNodeContainsStatusChip(node) {
    if (!node || typeof node !== 'object') return false;
    if (node.type === 'statusChip') return true;
    if (Array.isArray(node.content)) {
        return node.content.some((child) => askKaiDocNodeContainsStatusChip(child));
    }
    return false;
}

function askKaiCellPlainText(cell) {
    return String(extractAskKaiPlainTextFromDocNode(cell) || '').replace(/\s+/g, ' ').trim();
}

// Walk the live doc tree and describe each table column as { header, values[] }
// (distinct, non-empty, not-already-a-chip cells). This is what Kai receives —
// small and structured — so it can decide which column(s) to convert and pick a
// tone per label. The doc itself never leaves the client.
function extractAskKaiDocTableColumns() {
    const draft = getDocsDraft();
    const doc = draft?.contentJson || null;
    if (!doc || typeof doc !== 'object') return [];

    const orderedHeaders = [];
    const valuesByHeader = new Map();

    function visit(node) {
        if (!node || typeof node !== 'object') return;
        if (node.type === 'table' && Array.isArray(node.content)) {
            const rows = node.content.filter((row) => row?.type === 'tableRow');
            if (rows.length >= 2) {
                const headerCells = Array.isArray(rows[0].content) ? rows[0].content : [];
                headerCells.forEach((headerCell, colIndex) => {
                    const header = askKaiCellPlainText(headerCell);
                    if (!header) return;
                    let set = valuesByHeader.get(header);
                    if (!set) {
                        set = new Set();
                        valuesByHeader.set(header, set);
                        orderedHeaders.push(header);
                    }
                    for (let r = 1; r < rows.length; r++) {
                        const cells = Array.isArray(rows[r].content) ? rows[r].content : [];
                        const cell = cells[colIndex];
                        if (!cell || askKaiDocNodeContainsStatusChip(cell)) continue;
                        const value = askKaiCellPlainText(cell);
                        if (value) set.add(value);
                    }
                });
            }
        }
        if (Array.isArray(node.content)) {
            node.content.forEach((child) => visit(child));
        }
    }

    visit(doc);

    return orderedHeaders
        .map((header) => ({
            header,
            values: Array.from(valuesByHeader.get(header) || []).slice(0, 60)
        }))
        .filter((column) => column.values.length);
}

function resolveAskKaiErrorMessage(error) {
    const code = String(error?.payload?.error?.code || '').trim();
    const status = Number(error?.status || 0);
    if (code === 'RATE_LIMITED') {
        return t('askKai.errors.rateLimited');
    }
    if (code === 'INVALID_REQUEST' && /Field too long:/i.test(String(error?.message || ''))) {
        return t('askKai.errors.inputTooLong');
    }
    if (code === 'ASK_KAI_PROVIDER_FAILED' || code === 'ASK_KAI_PROVIDER_TIMEOUT' || status === 503 || status === 504) {
        return t('askKai.errors.generateTimedOut');
    }
    return t('askKai.errors.generateFailed');
}

function updateAskKaiActionUI() {
    const { action } = getAskKaiElements();
    if (!action) return;
    syncAskKaiActionPicker();
}

function resetAskKaiModalState(options = {}) {
    const preserveDraft = options.preserveDraft === true;
    askKaiState.requestController?.abort?.();
    stopAskKaiGenerationTimer();
    // The Find conversation outlives the drawer: after Apply the drawer closes, but
    // the applied query must still be there for Refine to build on. This assignment
    // replaces the whole object, so carry it across explicitly.
    const preservedFind = askKaiState.find || { query: null, vocabulary: null, turns: [] };
    askKaiState = {
        proposal: null,
        review: null,
        metadata: null,
        intent: null,
        busy: false,
        requestController: null,
        selectedAmbiguityMode: null,
        lastCreatedProjects: null,
        promptPresentation: 'expanded-editing',
        generationStartedAt: null,
        generationTimerId: null,
        find: preservedFind
    };
    // review is null again, so the mode grid must come back — the collapse class
    // lives on the modal element and would otherwise survive a close/reopen.
    syncAskKaiModesCollapsed();

    const { action, instruction, docContextCheckbox, tagCheckbox, confirmBtn } = getAskKaiElements();
    if (!preserveDraft) {
        askKaiDraft = {
            instruction: '',
            useDocContext: false,
            useGeneratedTags: false
        };
    }
    if (action) action.value = ASK_KAI_ACTION_AUTO;
    if (instruction) instruction.value = preserveDraft ? String(askKaiDraft.instruction || '') : '';
    if (docContextCheckbox) docContextCheckbox.checked = preserveDraft ? askKaiDraft.useDocContext === true : false;
    if (tagCheckbox) tagCheckbox.checked = false;
    askKaiDraft.useGeneratedTags = false;
    if (confirmBtn) confirmBtn.disabled = true;
    updateAskKaiShortcutHint();
    updateAskKaiLauncherShortcutTooltip();
    setAskKaiGenerateButtonBusy(false);
    updateAskKaiActionUI();
    updateAskKaiActionIndicator(null);
    updateAskKaiIntentNote(null, null);
    syncAskKaiPromptPresentation();
    renderAskKaiIdlePreview();
    syncAskKaiSavedPromptsUI();
}

function shouldWarnBeforeClosingAskKaiModal() {
    return !askKaiState.busy && (!!askKaiState.proposal || !!askKaiState.review);
}

function shouldWarnBeforeRegeneratingAskKaiProposal() {
    return !askKaiState.busy && (!!askKaiState.proposal || !!askKaiState.review);
}

function shouldBlockClosingAskKaiModal() {
    return !!askKaiState.requestController;
}

function finalizeAskKaiModalClose(options = {}) {
    const preserveDraft = options.preserveDraft !== false;
    if (preserveDraft) {
        rememberAskKaiDraft();
    }
    resetAskKaiModalState({ preserveDraft });
    closeAskKaiSavedPromptsPopover();
    const { modal } = getAskKaiElements();
    if (modal) modal.classList.remove('active', 'is-keyboard-open');
}

// iOS keeps the Ask Kai drawer at full height when the keyboard opens, leaving
// the prompt with ~0px between the header and the stacked footer buttons.
// While the keyboard is up (mobile only), fit the drawer to the visible
// viewport and switch to the compact layout (#ask-kai-modal.is-keyboard-open).
let askKaiViewportBaseline = window.visualViewport?.height || 0;
let askKaiViewportWidth = window.visualViewport?.width || 0;
function syncAskKaiKeyboardLayout() {
    const modal = document.getElementById('ask-kai-modal');
    const vv = window.visualViewport;
    if (!modal || !vv) return false;
    if (vv.width !== askKaiViewportWidth) {
        // Rotation: re-learn the keyboard-closed height.
        askKaiViewportWidth = vv.width;
        askKaiViewportBaseline = vv.height;
    }
    askKaiViewportBaseline = Math.max(askKaiViewportBaseline, vv.height);
    const focused = document.activeElement;
    const typing = !!focused && modal.contains(focused)
        && focused.matches('textarea, input:not([type="checkbox"]):not([type="radio"]), [contenteditable="true"]');
    const open = modal.classList.contains('active') && typing
        && window.matchMedia('(max-width: 768px)').matches
        && vv.height < askKaiViewportBaseline * 0.8;
    const opened = open && !modal.classList.contains('is-keyboard-open');
    modal.classList.toggle('is-keyboard-open', open);
    // Bring the field being edited into the now-smaller drawer.
    if (opened) requestAnimationFrame(() => focused.scrollIntoView?.({ block: 'nearest' }));
    if (open) {
        modal.style.setProperty('--ask-kai-vv-height', `${Math.round(vv.height)}px`);
        modal.style.setProperty('--ask-kai-vv-top', `${Math.round(vv.offsetTop)}px`);
    } else {
        modal.style.removeProperty('--ask-kai-vv-height');
        modal.style.removeProperty('--ask-kai-vv-top');
    }
    return open;
}
if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncAskKaiKeyboardLayout);
    window.visualViewport.addEventListener('scroll', syncAskKaiKeyboardLayout);
}
document.addEventListener('focusin', (event) => {
    if (!event.target.closest?.('#ask-kai-modal')) return;
    // The keyboard animates in after focus; re-check once it has settled.
    setTimeout(syncAskKaiKeyboardLayout, 350);
});
// Delay so a tap on a footer button lands before the layout changes back.
document.addEventListener('focusout', () => setTimeout(syncAskKaiKeyboardLayout, 150));

function requestAskKaiModalClose(options = {}) {
    const preserveDraft = options.preserveDraft !== false;
    if (shouldBlockClosingAskKaiModal()) {
        showErrorNotification(t('askKai.errors.generatingKeepOpen'));
        return;
    }
    if (shouldWarnBeforeClosingAskKaiModal()) {
        showUnsavedChangesModal({
            modalId: 'ask-kai-modal',
            titleKey: 'askKai.confirm.discardTitle',
            bodyKey: 'askKai.confirm.discardBody',
            discardKey: 'askKai.confirm.discardButton',
            onDiscard: () => finalizeAskKaiModalClose({ preserveDraft })
        });
        return;
    }
    finalizeAskKaiModalClose({ preserveDraft });
}

function requestAskKaiProposalRegeneration(onConfirm) {
    if (!shouldWarnBeforeRegeneratingAskKaiProposal()) {
        onConfirm?.();
        return;
    }

    showUnsavedChangesModal({
        modalId: 'ask-kai-modal',
        titleKey: 'askKai.confirm.regenerateTitle',
        bodyKey: 'askKai.confirm.regenerateBody',
        discardKey: 'askKai.confirm.regenerateButton',
        onDiscard: () => onConfirm?.()
    });
}

function openAskKaiModal(prefill = null) {
    const { modal } = getAskKaiElements();
    if (!modal) return;

    restoreAskKaiDraftFromStorage();
    setupAskKaiActionPicker();
    resetAskKaiModalState({ preserveDraft: true });

    // Optional prefill from dashboard Kai card: { instruction, action }
    if (prefill && (prefill.instruction || prefill.action)) {
        const { action, instruction } = getAskKaiElements();
        if (typeof prefill.instruction === 'string' && instruction) {
            instruction.value = prefill.instruction;
            askKaiDraft.instruction = prefill.instruction;
        }
        if (prefill.action && action) {
            action.value = prefill.action;
        }
        rememberAskKaiDraft();
        if (action) action.dispatchEvent(new Event('change', { bubbles: true }));
        syncAskKaiPromptPresentation();
    }

    modal.classList.add('active');
    requestAnimationFrame(() => {
        focusAskKaiInstructionField();
    });
}

// Dashboard Kai card: open the Ask Kai drawer pre-filled with the typed text
function dashKaiSubmit() {
    const input = document.getElementById('dash-kai-input');
    const value = String(input?.value || '').trim();
    openAskKaiModal(value ? { instruction: value } : null);
    if (input) input.value = '';
}

// Dashboard Kai suggestion chips → preset instruction + action mode
function dashKaiSuggest(param) {
    const presets = {
        plan_week: { instruction: t('dashboard.kai.promptPlanWeek'), action: ASK_KAI_ACTION_AUTO },
        summarize: { instruction: t('dashboard.kai.promptSummarize'), action: ASK_KAI_ACTION_AUTO },
        add_tasks: { instruction: t('dashboard.kai.promptAddTasks'), action: 'create_tasks_only' }
    };
    const preset = presets[param] || { instruction: '', action: ASK_KAI_ACTION_AUTO };
    openAskKaiModal(preset);
}

// Dashboard summary pills / "View all" → jump to the filtered Tasks board
function dashFilterAttention(param) {
    if (param === 'high') {
        navigateToFilteredTasks('priority', 'high');
    } else if (param === 'overdue') {
        navigateToFilteredTasks('overdue', 'true');
    } else if (param === 'today') {
        navigateToFilteredTasks('dueToday', 'true');
    } else if (param === 'progress') {
        navigateToFilteredTasks('status', 'progress');
    } else if (param === 'completed') {
        navigateToFilteredTasks('completedRecently', 'true');
    } else {
        // 'all' — open the Tasks board with no extra filter
        window.location.hash = 'tasks';
        showPage('tasks');
    }
}

// Dashboard shortcuts open active work by excluding terminal statuses.
async function dashOpenTasks() {
    window.location.hash = 'tasks';
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    document.querySelector('.nav-item[data-page="tasks"]')?.classList.add('active');
    await showPage('tasks');
    clearAllFilters();
    filterState.statuses.add('done');
    filterState.statusExcludeMode = true;

    const doneCheckbox = document.querySelector('input[data-filter="status"][value="done"]');
    if (doneCheckbox) doneCheckbox.checked = true;
    updateFilterModeUI('status');
    renderAfterFilterChange();
}

async function dashOpenProjects() {
    window.location.hash = 'projects';
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    document.querySelector('.nav-item[data-page="projects"]')?.classList.add('active');
    await showPage('projects');
    clearProjectFilters();
    projectFilterState.statuses.add('completed');
    projectFilterState.statusExcludeMode = true;

    const completedCheckbox = document.querySelector('input[data-filter="project-status"][value="completed"]');
    if (completedCheckbox) completedCheckbox.checked = true;
    updateProjectFilterModeUI('project-status');
    updateProjectStatusBadge();
    applyProjectFilters();
    syncURLWithProjectFilters();
}

window.dashOpenTasks = dashOpenTasks;
window.dashOpenProjects = dashOpenProjects;

// Dashboard "Needs attention" quick-complete a task in place
function dashCompleteTask(taskId) {
    const id = parseInt(taskId, 10);
    const oldTask = tasks.find(t => t.id === id);
    if (!oldTask) return;
    const oldTaskCopy = JSON.parse(JSON.stringify(oldTask));

    const result = updateTaskFieldService(id, 'status', 'done', tasks, settings);
    if (!result.task) return;
    tasks = result.tasks;
    const task = result.task;

    if (window.historyService && oldTaskCopy) {
        window.historyService.recordTaskUpdated(oldTaskCopy, task);
    }
    if (task.projectId) {
        checkAndRecordProjectStatusChange(task.projectId, task);
    }

    // Refresh dashboard widgets instantly
    renderDashboardGreeting();
    updateDashboardStats();
    renderDashboardUpcoming();
    renderDashboardTodayAgenda();
    renderDashboardTodayPanel();
    renderActivityFeed();
    showSuccessNotification(t('dashboard.attention.completedToast', { title: task.title || t('tasks.untitled') }));

    saveTaskUpdate(id, {
        status:              task.status,
        completedDate:       task.completedDate  || null,
        startDate:           task.startDate      || '',
        endDate:             task.endDate        || '',
        startDateWasEverSet: task.startDateWasEverSet,
        endDateWasEverSet:   task.endDateWasEverSet,
        updatedAt:           task.updatedAt,
    }).catch(error => {
        console.error('Failed to save task completion:', error);
        showErrorNotification(t('error.saveTaskFailed'));
    });
}

function cancelAskKaiModal() {
    requestAskKaiModalClose({ preserveDraft: true });
}

if (!document.documentElement.dataset.askKaiGlobalShortcutBound) {
    document.documentElement.dataset.askKaiGlobalShortcutBound = 'true';
    document.addEventListener('keydown', handleAskKaiGlobalShortcut);
}

updateAskKaiLauncherShortcutTooltip();

let _askKaiUndoTimer = null;

// Inject the Ask Kai result-modal styles once. Kept in JS (not style.css) so the modal is
// self-contained and cannot silently render unstyled if a CSS bundle is stale.
function ensureAskKaiResultModalStyles() {
    if (document.getElementById('ask-kai-result-modal-styles')) return;
    const style = document.createElement('style');
    style.id = 'ask-kai-result-modal-styles';
    style.textContent = `
        .ask-kai-result-overlay {
            position: fixed; inset: 0; z-index: 10050;
            display: flex; align-items: center; justify-content: center;
            padding: 24px;
            background: rgba(2, 6, 23, 0.55);
            animation: askKaiOverlayIn 0.18s ease-out;
        }
        .ask-kai-result-overlay.is-hiding { animation: askKaiOverlayOut 0.18s ease-in forwards; }
        .ask-kai-result-card {
            width: min(380px, 100%);
            background: var(--bg-card);
            color: var(--text-primary);
            border: 1px solid var(--border);
            border-radius: 16px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
            padding: 28px 28px 22px;
            text-align: center;
            animation: askKaiCardIn 0.26s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .ask-kai-result-overlay.is-hiding .ask-kai-result-card { animation: askKaiCardOut 0.16s ease-in forwards; }
        .ask-kai-result-icon {
            width: 54px; height: 54px; margin: 0 auto 16px;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            background: var(--accent-green); color: #fff;
        }
        .ask-kai-result-title { font-size: 17px; font-weight: 600; line-height: 1.3; margin: 0; }
        .ask-kai-result-sub { font-size: 13.5px; color: var(--text-secondary); line-height: 1.4; margin: 6px 0 0; }
        .ask-kai-result-actions { display: flex; gap: 10px; margin-top: 22px; }
        .ask-kai-result-btn {
            flex: 1; padding: 11px 16px; border-radius: 10px;
            font-size: 14px; font-weight: 600; cursor: pointer;
            border: 1px solid transparent; transition: filter .15s ease, background .15s ease, color .15s ease;
        }
        .ask-kai-result-btn--primary { background: var(--accent-blue); color: #fff; }
        .ask-kai-result-btn--primary:hover { filter: brightness(1.08); }
        .ask-kai-result-btn--ghost { background: transparent; color: var(--text-secondary); border-color: var(--border); }
        .ask-kai-result-btn--ghost:hover { background: var(--bg-hover); color: var(--text-primary); }
        @keyframes askKaiOverlayIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes askKaiOverlayOut { from { opacity: 1 } to { opacity: 0 } }
        @keyframes askKaiCardIn { from { opacity: 0; transform: translateY(10px) scale(0.97) } to { opacity: 1; transform: none } }
        @keyframes askKaiCardOut { from { opacity: 1; transform: none } to { opacity: 0; transform: translateY(6px) scale(0.98) } }
    `;
    document.head.appendChild(style);
}

function showAskKaiUndoToast({ projectIds = [], taskIds = [] } = {}) {
    document.getElementById('ask-kai-undo-toast')?.remove();
    if (_askKaiUndoTimer) { clearTimeout(_askKaiUndoTimer); _askKaiUndoTimer = null; }
    ensureAskKaiResultModalStyles();

    const hasProjects = Array.isArray(projectIds) && projectIds.length > 0;
    const hasTasks = Array.isArray(taskIds) && taskIds.length > 0;

    const overlay = document.createElement('div');
    overlay.id = 'ask-kai-undo-toast';
    overlay.className = 'ask-kai-result-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const card = document.createElement('div');
    card.className = 'ask-kai-result-card';

    const icon = document.createElement('div');
    icon.className = 'ask-kai-result-icon';
    icon.innerHTML = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
    card.appendChild(icon);

    const title = document.createElement('div');
    title.className = 'ask-kai-result-title';
    title.textContent = t('askKai.undoToast.label');
    card.appendChild(title);

    const sub = document.createElement('div');
    sub.className = 'ask-kai-result-sub';
    sub.textContent = t('askKai.undoToast.subtitle');
    card.appendChild(sub);

    let closed = false;
    const dismiss = () => {
        if (closed) return;
        closed = true;
        if (_askKaiUndoTimer) { clearTimeout(_askKaiUndoTimer); _askKaiUndoTimer = null; }
        document.removeEventListener('keydown', onKey);
        overlay.classList.add('is-hiding');
        setTimeout(() => overlay.remove(), 200);
    };
    const onKey = (event) => { if (event.key === 'Escape') dismiss(); };

    const actions = document.createElement('div');
    actions.className = 'ask-kai-result-actions';

    // "Ver" — navigate to the freshly generated content, pre-filtered (primary action).
    // Projects take precedence (project-with-tasks proposals map to the Projects view).
    if (hasProjects || hasTasks) {
        const viewBtn = document.createElement('button');
        viewBtn.type = 'button';
        viewBtn.className = 'ask-kai-result-btn ask-kai-result-btn--primary';
        viewBtn.textContent = t('askKai.undoToast.view');
        viewBtn.addEventListener('click', () => {
            if (hasProjects) {
                window.location.hash = 'projects?projectIds=' + projectIds.join(',');
            } else {
                // Force list view: the Kanban can have the backlog column disabled, which
                // would hide a freshly created task; the list always shows every match.
                // taskSrc=generated makes the filter chip read "Recently generated" (not "Email Tasks").
                window.location.hash = 'tasks?view=list&taskIds=' + taskIds.join(',') + '&taskSrc=generated';
            }
            dismiss();
        });
        actions.appendChild(viewBtn);
    }

    // Close simply dismisses. There is no undo here by design — if the user regrets a
    // generation they can remove the items with the bulk/mass-edit features.
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'ask-kai-result-btn ask-kai-result-btn--ghost';
    closeBtn.textContent = t('askKai.undoToast.close');
    closeBtn.addEventListener('click', () => dismiss());
    actions.appendChild(closeBtn);

    card.appendChild(actions);
    overlay.appendChild(card);
    overlay.addEventListener('click', (event) => { if (event.target === overlay) dismiss(); });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);
    _askKaiUndoTimer = setTimeout(dismiss, 12000);
}

function showAskKaiCreatedSummary(createdProjects = []) {
    if (!Array.isArray(createdProjects) || !createdProjects.length) return;
    const projectCount = createdProjects.length;
    const firstProject = createdProjects[0];
    const projectName = firstProject?.title || firstProject?.name || t('projects.title');
    const message = projectCount === 1
        ? t('askKai.success.singleProject').replace('{name}', projectName)
        : t('askKai.success.multiProject').replace('{count}', String(projectCount));
    showSuccessNotification(message);
}

function scrollToAskKaiProjects(createdProjects = []) {
    if (!Array.isArray(createdProjects) || !createdProjects.length) return;
    setTimeout(() => {
        const firstNewProjectName = String(createdProjects[0]?.name || '').trim();
        if (!firstNewProjectName) return;

        const projectElements = document.querySelectorAll('[id^="project-item-"]');
        let newProjectElement = null;

        for (const element of projectElements) {
            const titleElement = element.querySelector('.project-title, .project-name, [data-project-title], [data-project-name]');
            if (titleElement && titleElement.textContent.trim() === firstNewProjectName) {
                newProjectElement = element;
                break;
            }
        }

        if (!newProjectElement) return;

        const rect = newProjectElement.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
        if (!isVisible) {
            newProjectElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, 150);
}

async function submitAskKaiRequest(options = {}) {
    const { action, instruction, docContextCheckbox, tagCheckbox, generateBtn, confirmBtn } = getAskKaiElements();
    if (!action || !instruction || !generateBtn || !confirmBtn) return;
    if (askKaiState.busy) return;

    const userInstruction = String(instruction.value || '').trim();
    if (!userInstruction) {
        showErrorNotification(t('askKai.errors.instructionRequired'));
        return;
    }

    if (
        options?.skipRepeatWarning !== true
        && askKaiState.review?.canApply === false
        && askKaiState.metadata?.repairExhausted === true
    ) {
        showErrorNotification(t('askKai.errors.repairExhausted'));
        setAskKaiPromptPresentation('expanded-editing-after-results', { focus: true });
        return;
    }

    if (options?.skipRepeatWarning !== true && shouldWarnBeforeRegeneratingAskKaiProposal()) {
        requestAskKaiProposalRegeneration(() => submitAskKaiRequest({
            skipRepeatWarning: true,
            repairContext: options?.repairContext || null
        }));
        return;
    }

    rememberAskKaiDraft();
    updateAskKaiActionIndicator(null);

    let selectedAction = String(action?.value || ASK_KAI_ACTION_AUTO).trim() || ASK_KAI_ACTION_AUTO;

    // "Show me…", "Find…", "Which tasks…" are questions, not instructions to build
    // something. The server deliberately never infers query_filter (so no existing
    // create request can drift into it), so Auto-detect is routed here on the client.
    // The vocabulary lets a verbless "tareas de Campaña 3" count as a query too —
    // not in Docs, where a bare phrase is far more likely content to write.
    if (selectedAction === ASK_KAI_ACTION_AUTO
        && isKaiQueryLikeInstruction(
            userInstruction,
            getActivePageId() === 'docs' ? null : buildCurrentKaiVocabulary(),
            { activePage: getKaiDefaultEntity() }
        )) {
        selectedAction = ASK_KAI_ACTION_FIND;
    }

    const payload = { instruction: userInstruction };
    const activePageId = getActivePageId();

    // Turning table labels into colored status chips is a *skill of Rewrite*, not a
    // separate mode: from Rewrite (or Auto-detect), a chip-conversion instruction is
    // dispatched here to the structural chip path instead of a full-page rewrite.
    // The server never infers this on its own (it also needs the table columns, which
    // only the client holds), so the client routes it. Docs-only — chips live in
    // documentation tables.
    if ((selectedAction === ASK_KAI_ACTION_AUTO || selectedAction === ASK_KAI_ACTION_REWRITE)
        && activePageId === 'docs'
        && isAskKaiStatusChipLikeInstruction(userInstruction)) {
        selectedAction = ASK_KAI_ACTION_APPLY_CHIPS;
    }

    const shouldUseRewriteSource = shouldAskKaiUseDocSource(selectedAction, userInstruction, activePageId);
    const shouldUseDocContext = shouldAskKaiOfferDocContext(selectedAction, activePageId) && !!docContextCheckbox?.checked;
    const shouldGenerateTags = shouldAskKaiOfferTagGeneration(selectedAction) && !!tagCheckbox?.checked;
    payload.contextPage = activePageId === 'docs'
        ? ((shouldUseRewriteSource || shouldUseDocContext) ? 'docs' : '')
        : (activePageId || '');
    if (shouldGenerateTags) {
        payload.allowGeneratedTags = true;
    }
    if (options?.repairContext && typeof options.repairContext === 'object') {
        payload.repairContext = options.repairContext;
    }

    const needsRewriteSource = selectedAction === ASK_KAI_ACTION_REWRITE
        || (selectedAction === ASK_KAI_ACTION_AUTO && isAskKaiRewriteLikeInstruction(userInstruction));

    // Selection-scoped rewrite: if the user highlighted text in the docs editor,
    // transform ONLY that fragment and splice it back in place (see
    // applyAskKaiSelectionRewriteToCurrentDoc). Empty selection → whole-page rewrite.
    askKaiState.selectionRewrite = null;

    if (shouldUseRewriteSource || shouldUseDocContext) {
        let usedSelection = false;
        if (needsRewriteSource && shouldUseRewriteSource && activePageId === 'docs') {
            const selectionInfo = getDocsEditorSelectionInfo();
            const selectionText = String(selectionInfo?.text || '');
            if (!selectionInfo?.empty && selectionText.replace(/\s+/g, ' ').trim().length > 0) {
                const currentDocId = getCurrentDocsRecord()?.id || null;
                payload.sourceText = selectionText;
                payload.scope = 'selection';
                askKaiState.selectionRewrite = {
                    docId: currentDocId,
                    from: selectionInfo.from,
                    to: selectionInfo.to,
                    text: selectionText
                };
                usedSelection = true;
            }
        }
        if (!usedSelection) {
            const currentDocText = getAskKaiCurrentDocSourceText();
            if (!currentDocText) {
                showErrorNotification(t('askKai.errors.currentPageEmpty'));
                return;
            }
            payload.sourceText = currentDocText;
        }
    } else if (needsRewriteSource) {
        showErrorNotification(t('askKai.errors.rewriteDocsOnly'));
        return;
    }

    if (selectedAction === ASK_KAI_ACTION_APPLY_CHIPS) {
        if (activePageId !== 'docs') {
            showErrorNotification(t('askKai.errors.rewriteRequiresDocPage'));
            return;
        }
        const columns = extractAskKaiDocTableColumns();
        if (!columns.length) {
            showErrorNotification(t('askKai.errors.chipNoTable'));
            return;
        }
        payload.columns = columns;
        payload.contextPage = 'docs';
    }

    logAskKaiTrace('request.prepare', {
        page: activePageId,
        selectedAction,
        instruction: userInstruction,
        sourceText: String(payload.sourceText || ''),
        allowGeneratedTags: payload.allowGeneratedTags === true
    });

    const previousSnapshot = captureAskKaiProposalSnapshot();
    setAskKaiPromptPresentation('collapsed-readonly');
    askKaiState.busy = true;
    askKaiState.proposal = null;
    askKaiState.review = null;
    askKaiState.metadata = null;
    askKaiState.intent = null;
    askKaiState.selectedAmbiguityMode = null;
    askKaiState.requestController?.abort?.();
    const requestController = new AbortController();
    askKaiState.requestController = requestController;
    setAskKaiGenerateButtonBusy(true);
    confirmBtn.disabled = true;
    renderAskKaiLoadingPreview();
    startAskKaiGenerationTimer();

    try {
        // Find mode swaps only the generation step. Everything around it — the busy
        // state, the abort controller, the timer, the error rendering and the Apply
        // gating — is the same machinery the create modes already use.
        const response = selectedAction === ASK_KAI_ACTION_FIND
            ? await runKaiFindGeneration(userInstruction, requestController)
            : await askKaiGenerate(selectedAction, payload, { signal: requestController.signal });
        const proposal = response?.data?.proposal;
        const review = response?.data?.review || null;
        const metadata = response?.data?.metadata || null;
        const intent = response?.data?.intent || null;

        const proposalAction = String(proposal?.action || '').trim();
        const proposalMatchesSelection = doesAskKaiProposalMatchSelection(selectedAction, proposalAction);

        if (!proposal || !proposalMatchesSelection || !review || !intent) {
            throw new Error(t('askKai.errors.invalidProposal'));
        }

        logAskKaiTrace('response.proposal', {
            selectedAction,
            inferredAction: proposalAction,
            resolvedAction: intent?.resolvedAction || '',
            reviewType: review?.type || ''
        });

        askKaiState.proposal = proposal;
        askKaiState.review = review;
        askKaiState.metadata = metadata;
        askKaiState.intent = intent;
        askKaiState.promptPresentation = 'collapsed-readonly';
        syncAskKaiPromptPresentation();

        // A query is a question, not a proposal. When Kai understood it cleanly there
        // is nothing to review — asking the user to confirm a filter they can see on
        // the toolbar a moment later is just an extra click. Take them to the results.
        // The drawer only stays open when there IS something to say: an unknown name,
        // an ambiguity, an unsupported dimension, or no matches at all.
        //
        renderAskKaiPreview(review, proposal, confirmBtn);
        updateAskKaiActionIndicator(intent?.resolvedAction || proposalAction);
        updateAskKaiIntentNote(intent, review);

        confirmBtn.disabled = isAskKaiReviewApplyBlocked(review);
    } catch (error) {
        const { modal } = getAskKaiElements();
        const modalIsActive = !!modal?.classList.contains('active');
        const requestIsCurrent = askKaiState.requestController === requestController;
        if (error?.name === 'AbortError') {
            if (modalIsActive && requestIsCurrent) {
                restoreAskKaiProposalSnapshot(previousSnapshot, confirmBtn);
            }
            return;
        }
        if (error?.name === 'TimeoutError' || error?.code === 'TIMEOUT') {
            askKaiState.proposal = { action: selectedAction === ASK_KAI_ACTION_AUTO ? '' : selectedAction };
            askKaiState.review = buildAskKaiInlineErrorReview(t('askKai.errors.generateTimedOut'));
            askKaiState.metadata = null;
            askKaiState.intent = null;
            askKaiState.promptPresentation = 'expanded-editing-after-results';
            syncAskKaiPromptPresentation();
            renderAskKaiPreview(askKaiState.review, askKaiState.proposal, confirmBtn);
            confirmBtn.disabled = true;
            return;
        }
        askKaiState.proposal = { action: selectedAction === ASK_KAI_ACTION_AUTO ? '' : selectedAction };
        askKaiState.review = buildAskKaiInlineErrorReview(resolveAskKaiErrorMessage(error));
        askKaiState.metadata = null;
        askKaiState.intent = null;
        askKaiState.promptPresentation = 'expanded-editing-after-results';
        syncAskKaiPromptPresentation();
        renderAskKaiPreview(askKaiState.review, askKaiState.proposal, confirmBtn);
        confirmBtn.disabled = true;
    } finally {
        askKaiState.busy = false;
        stopAskKaiGenerationTimer(!!askKaiState.review);
        if (askKaiState.requestController === requestController) {
            askKaiState.requestController = null;
        }
        setAskKaiGenerateButtonBusy(false);
        if (askKaiState.review && confirmBtn) {
            renderAskKaiPreview(askKaiState.review, askKaiState.proposal, confirmBtn);
            confirmBtn.disabled = isAskKaiReviewApplyBlocked(askKaiState.review);
        }
        syncAskKaiGenerateAvailability();
    }
}

// ===========================================================================
// Ask Kai — Find mode (natural-language filtering)
//
// Kai does not get its own filter engine. It produces a KaiQuery, which is
// written straight into the existing filterState / projectFilterState and
// rendered by the existing renderAfterFilterChange(). The chips the user then
// sees and removes are the ordinary filter chips.
//
// The local parser handles the common phrasings with no network call at all;
// only sentences it cannot read confidently reach /api/ask-kai.
// ===========================================================================

/** Everything Kai is allowed to resolve names against. Built from live data. */
function buildCurrentKaiVocabulary() {
    return buildKaiVocabulary({
        tasks,
        projects,
        reviewStatusEnabled: !!settings?.enableReviewStatus,
        today: getTodayISO(new Date(), getUserTimeZone()),
        timezone: getUserTimeZone(),
        locale: getCurrentLanguage()
    });
}

/** Which entity a bare "show me…" refers to, based on where the user is. */
function getKaiDefaultEntity() {
    return getActivePageId() === 'projects' ? 'projects' : 'tasks';
}

/**
 * Produce a proposal for a Find request.
 *
 * Returns the same { data: { proposal, review, metadata, intent } } envelope the
 * server returns, so submitAskKaiRequest cannot tell the two apart.
 */
async function runKaiFindGeneration(instruction, requestController) {
    const vocabulary = buildCurrentKaiVocabulary();
    askKaiState.find.vocabulary = vocabulary;

    const parsed = parseKaiQuery(instruction, vocabulary, {
        now: new Date(),
        previousQuery: askKaiState.find.query,
        activePage: getKaiDefaultEntity()
    });

    // The LLM is given the same vocabulary the parser just searched, so there is
    // nothing it can resolve that the parser could not. Escalate only when the
    // *sentence* defeated us, never when the *name* did.
    if (parsed.escalate) {
        const response = await askKaiGenerate('query_filter', {
            instruction,
            vocabulary: serializeKaiVocabularyForLLM(vocabulary),
            previousQuery: askKaiState.find.query,
            conversation: askKaiState.find.turns
        }, { signal: requestController.signal });

        // The server already validated, but its answer is still untrusted input on
        // the way in. Strip any unasked "none" filter, then validate before it can
        // reach filterState.
        const deNoned = stripUnaskedNoneSentinels(
            response?.data?.proposal?.payload?.query,
            instruction
        );
        // The model conflates "closed"/"cerrado" with the archived scope; drop an
        // archived scope the user never actually asked for before it ANDs the result
        // to zero.
        const deArchived = stripUnaskedArchivedScope(deNoned, instruction);
        // Due/overdue means open work — default those queries to exclude done tasks.
        const dueScoped = excludeDoneForDueQueries(deArchived, instruction);
        const revalidated = validateKaiQueryAgainstVocabulary(dueScoped, vocabulary);
        return buildKaiFindEnvelope(revalidated, vocabulary, 'llm');
    }

    // Same default for the locally-parsed query, so local and LLM answers match.
    const dueScopedLocal = excludeDoneForDueQueries(parsed.query, instruction);
    const validation = validateKaiQueryAgainstVocabulary(dueScopedLocal, vocabulary);
    return buildKaiFindEnvelope(validation, vocabulary, 'local');
}

function buildKaiFindEnvelope(validation, vocabulary, source) {
    const query = validation.query;
    const review = buildKaiQueryReview(query, validation);

    // Run the query NOW and show the user the actual rows. The review is the answer
    // to their question, not a plan they have to approve first; the button then takes
    // them into the app to work with the results.
    //
    // But a query with NO applicable filter is not "all your tasks" — it usually
    // means the only thing the user asked for is unsupported ("which project has the
    // most tasks"). Dumping every row there is misleading, so show just the
    // explanation and let the unsupported note speak.
    const hasFilters = countKaiQueryFilters(query) > 0 || query.scope === 'archived';
    if (hasFilters) {
        const preview = previewKaiQueryMatches(query);
        review.matchCount = preview.count;
        review.items = preview.items;
        review.overflowCount = preview.overflowCount;
        review.emptyReasons = preview.count === 0 ? preview.reasons : [];
    }
    review.chips = describeKaiQuery(query, vocabulary);

    return {
        data: {
            proposal: { action: ASK_KAI_ACTION_FIND, payload: { query, mode: query.mode } },
            review,
            metadata: { source, warnings: validation.warnings },
            intent: { requestedAction: ASK_KAI_ACTION_FIND, resolvedAction: ASK_KAI_ACTION_FIND, auto: false }
        }
    };
}

/**
 * How many rows would this query match, and what would help if the answer is none?
 *
 * Tasks only. applyProjectFilters() renders rather than returning a list and reads
 * the global projectFilterState, so there is no way to dry-run it without either
 * duplicating its predicate chain (which would drift) or triggering a render with
 * throwaway state. A project query therefore shows no pre-count — count === null,
 * and the review copy simply omits it.
 */
/** Rows shown in the review. Enough to feel like the answer, few enough not to
    dominate the drawer; beyond this, the "Show tasks" button does the talking. */
const KAI_PREVIEW_LIMIT = 8;

function previewKaiQueryMatches(query) {
    const q = normalizeKaiQuery(query);

    if (q.entity === 'projects') {
        // applyProjectFilters() renders rather than returning a list, so we mirror only
        // the cheap parts here to preview the names. The real filtering still happens
        // in applyProjectFilters when the user hits the button.
        const matches = previewKaiProjectMatches(q);
        return {
            count: matches.length,
            items: matches.slice(0, KAI_PREVIEW_LIMIT).map(p => ({ id: p.id, title: p.name, projectStatus: getProjectStatus(p.id) })),
            overflowCount: Math.max(0, matches.length - KAI_PREVIEW_LIMIT),
            reasons: []
        };
    }

    const runTaskFilter = (state) => filterTasks(tasks, {
        ...state,
        dependencies,
        showArchivedOnly: q.scope === 'archived'
    }, { timeZone: getUserTimeZone() });

    // Show the date the user actually filtered on, so a "created this month" result
    // can be verified at a glance — showing the due date there was just confusing.
    const f = q.filters;
    const dateSource =
        (f.createdFrom || f.createdTo) ? { field: 'createdAt', label: t('askKai.find.dimension.created') }
        : (f.completedFrom || f.completedTo) ? { field: 'completedDate', label: t('askKai.find.dimension.completed') }
        : (f.updatedFrom || f.updatedTo) ? { field: 'updatedAt', label: t('askKai.find.dimension.updatedRange') }
        : { field: 'endDate', label: '' };

    const rowDate = (task) => {
        const raw = task[dateSource.field];
        if (!raw) return '';
        const iso = String(raw).slice(0, 10);
        const formatted = formatDate(iso);
        // Prefix non-due dates so the column is unambiguous ("Created 05/07").
        return dateSource.label ? `${dateSource.label} ${formatted}` : formatted;
    };

    let matches = runTaskFilter(kaiQueryToFilterState(q));
    
    // Apply updatedFilter cutoff if present (e.g., "24h" from "recently updated")
    // This matches what the actual UI does with window.kanbanUpdatedFilter
    if (f.updatedFilter && f.updatedFilter !== 'all') {
        const cutoff = getKanbanUpdatedCutoffTime(f.updatedFilter);
        if (cutoff !== null) {
            matches = matches.filter(t => getTaskUpdatedTime(t) >= cutoff);
        }
    }
    
    // Recency ranking. An explicit sort ("the 5 most recently modified/created")
    // wins; otherwise an updated-window filter still implies newest-first order.
    const createdTime = (t) => Date.parse(t?.createdAt || t?.createdDate || '') || 0;
    const hasUpdatedFilter = f.updatedFilter && f.updatedFilter !== 'all' || f.updatedFrom || f.updatedTo;
    if (f.sort === 'recent-created') {
        matches = [...matches].sort((a, b) => createdTime(b) - createdTime(a));
    } else if (f.sort === 'recent-updated' || hasUpdatedFilter) {
        matches = [...matches].sort((a, b) => getTaskUpdatedTime(b) - getTaskUpdatedTime(a));
    }

    // An explicit limit makes the capped set THE result: "the 5 most recent" is a
    // 5-row answer, not "5 of 120". Without a limit the full match set is the result
    // and the review shows a preview slice with an "…and N more" overflow.
    const hasLimit = f.limit && f.limit > 0;
    const resultSet = hasLimit ? matches.slice(0, f.limit) : matches;
    const rendered = resultSet.slice(0, KAI_PREVIEW_LIMIT);

    return {
        count: resultSet.length,
        items: rendered.map(task => ({
            id: task.id,
            title: task.title,
            status: task.status,
            priority: task.priority,
            date: rowDate(task)
        })),
        overflowCount: Math.max(0, resultSet.length - rendered.length),
        reasons: matches.length === 0 ? explainEmptyKaiResult(q, runTaskFilter) : []
    };
}

/** Read-only project match preview. Does not touch the live projectFilterState. */
function previewKaiProjectMatches(query) {
    const f = query.filters;
    const wantArchived = query.scope === 'archived';

    return projects.filter((project) => {
        if (!!project.archivedAt !== wantArchived) return false;

        if (f.projectStatuses.length) {
            const matches = f.projectStatuses.includes(getProjectStatus(project.id));
            if (f.projectStatusExcludeMode ? matches : !matches) return false;
        }
        if (f.tags.length) {
            const has = (project.tags || []).some(tag => f.tags.includes(tag));
            if (f.tagExcludeMode ? has : !has) return false;
        }
        if (f.taskFilter === 'has-tasks' && !tasks.some(task => task.projectId === project.id)) return false;
        if (f.taskFilter === 'no-tasks' && tasks.some(task => task.projectId === project.id)) return false;

        if (f.completionMin != null || f.completionMax != null) {
            const percent = getProjectCompletionPercent(project.id);
            if (f.completionMin != null && percent < f.completionMin) return false;
            if (f.completionMax != null && percent > f.completionMax) return false;
        }
        if (f.search) {
            const haystack = `${project.name || ''} ${project.description || ''}`.toLowerCase();
            if (!haystack.includes(f.search.toLowerCase())) return false;
        }
        return true;
    });
}

/** Apply a Find proposal: merge it onto the previous query, then route + filter. */
async function applyKaiQuery(rawQuery, mode) {
    const merged = mergeKaiQuery(askKaiState.find.query, rawQuery, mode);
    const query = normalizeKaiQuery(merged);

    askKaiState.find.query = query;
    askKaiState.find.turns = [...askKaiState.find.turns, String(askKaiDraft.instruction || '')]
        .filter(Boolean)
        .slice(-6);

    if (query.entity === 'projects') {
        await applyKaiQueryToProjects(query);
    } else {
        await applyKaiQueryToTasks(query);
    }
}

async function applyKaiQueryToTasks(query) {
    const isArchive = query.scope === 'archived';

    window.location.hash = isArchive ? 'tasks/archive' : 'tasks';
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    document.querySelector('.nav-item[data-page="tasks"]')?.classList.add('active');

    // Only navigate if we are not already there. showPage() re-runs the page's
    // loaders, and re-entering the page you are already on can leave its promise
    // unresolved — which strands the drawer on a dead "Generating…" card.
    if (getActivePageId() !== 'tasks') {
        await showPage('tasks');
    } else {
        // Calendar is a sub-view of the same #tasks page, so the guard above skips
        // showPage() when Kai is invoked from Calendar — which is the only place that
        // restores this chrome after showCalendarView() hides it. Restore it here too.
        document.querySelector('.view-toggle')?.classList.remove('hidden');
        const kanbanSettingsContainer = document.getElementById('kanban-settings-btn')?.parentElement;
        if (kanbanSettingsContainer) kanbanSettingsContainer.style.display = '';
        const archivedViewBtnEl = document.getElementById('archived-view-btn');
        if (archivedViewBtnEl) archivedViewBtnEl.style.display = '';
    }
    if (isArchive) showArchivedTasksView('kanban');

    clearAllFilters();

    // clearAllFilters() leaves these two behind. A stale "recently generated"
    // task-id filter would silently intersect with the query and quietly hide
    // rows the user asked to see, so Kai's query is made authoritative.
    filterState.taskIds = new Set();
    filterState.taskIdsSource = '';
    setKanbanUpdatedFilter(query.filters.updatedFilter || 'all', { render: false });

    Object.assign(filterState, kaiQueryToFilterState(query));

    // A recency query owns the list order. Drop any column sort the user left
    // active so "most recent first" actually shows on landing (renderListView
    // then defaults the sort to the Updated/Created column for kaiSort).
    if (filterState.kaiSort) {
        currentSort = { column: null, direction: 'asc' };
    }

    syncKaiFilterInputsFromState();
    updateFilterBadges();
    updateClearButtonVisibility();

    // Land in Kanban unless the query asked for something else.
    if (!isArchive) {
        switchTaskView(query.view || 'kanban');
    }

    renderAfterFilterChange();
}

async function applyKaiQueryToProjects(query) {
    // Archived projects live at their own route; "show me archived projects" must
    // actually land there, not on the active list.
    const isArchive = query.scope === 'archived';
    window.location.hash = isArchive ? 'projects/archive' : 'projects';
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    document.querySelector('.nav-item[data-page="projects"]')?.classList.add('active');
    if (getActivePageId() !== 'projects') {
        await showPage('projects');
    }
    setProjectsArchiveModeUI(isArchive);

    clearProjectFilters();
    Object.assign(projectFilterState, kaiQueryToProjectFilterState(query));

    document.querySelectorAll('input[type="checkbox"][data-filter="project-status"]').forEach((cb) => {
        cb.checked = projectFilterState.statuses.has(cb.value);
    });
    document.querySelectorAll('input[type="checkbox"][data-filter="project-tags"]').forEach((cb) => {
        cb.checked = projectFilterState.tags.has(cb.value);
    });
    const searchEl = document.getElementById('projects-search');
    if (searchEl) searchEl.value = projectFilterState.search;

    updateProjectFilterModeUI('project-status');
    updateProjectFilterModeUI('project-tags');
    updateProjectStatusBadge();
    updateProjectTagsBadge();
    updateProjectsUpdatedFilterUI();
    applyProjectFilters();
    syncURLWithProjectFilters();
    renderKaiFilterPill();
}

/**
 * Push filterState back onto the toolbar controls.
 *
 * The status/priority/project/tag checkboxes and their include/exclude toggles are
 * handled by updateAllFilterModeUI(), which uses querySelectorAll and therefore
 * covers the calendar's cal-* twin toolbar for free. Only the controls it does not
 * touch are handled here.
 */
function syncKaiFilterInputsFromState() {
    document.querySelectorAll('#filter-search, #cal-filter-search').forEach((input) => {
        input.value = filterState.search || '';
    });
    document.querySelectorAll('input[type="checkbox"][data-filter="date-preset"]').forEach((cb) => {
        const value = cb.dataset.presetValue || cb.value;
        cb.checked = filterState.datePresets.has(value);
    });
    document.querySelectorAll('input[type="checkbox"][data-filter="link-type"]').forEach((cb) => {
        cb.checked = filterState.linkTypes.has(cb.value);
    });
    const fromInput = document.getElementById('filter-date-from');
    const toInput = document.getElementById('filter-date-to');
    [[fromInput, filterState.dateFrom], [toInput, filterState.dateTo]].forEach(([input, value]) => {
        if (!input) return;
        input.value = value || '';
        input._flatpickrInstance?.setDate(value || null, false);
        const display = input.closest('.date-input-wrapper')?.querySelector('.date-display');
        if (display) display.value = value ? formatDisplayDate(value) : '';
    });
    syncExactDateFilterInputs();

    updateAllFilterModeUI();
    updateTagMatchToggleUI();
}

/**
 * Switch the tasks page to the requested view.
 *
 * Only kanban and list have a .view-btn; the calendar is opened by its own
 * function, so a selector-based click would silently do nothing for it.
 */
function switchTaskView(view) {
    if (view === 'calendar') {
        showCalendarView();
        return;
    }
    if (view === 'list') {
        document.querySelector('.view-btn[data-view="list"]')?.click();
        return;
    }
    if (view === 'kanban') {
        document.querySelector('.view-btn[data-view="kanban"]')?.click();
    }
}

/**
 * The Kai pill: a sibling of #active-filters showing what Kai understood.
 *
 * It cannot live INSIDE #active-filters — renderActiveFilterChips() wipes that
 * container with innerHTML = "" on every single filter change.
 */
function renderKaiFilterPill() {
    const query = askKaiState.find.query;
    const wraps = document.querySelectorAll('.kai-filter-pill-wrap');
    if (!wraps.length) return;

    const isProjects = getActivePageId() === 'projects';
    // The calendar has its own filter drawer and no chip strip; a pill there would
    // float above the month grid. Kai's query targets the task list.
    const isCalendar = !!document.getElementById('calendar-view')?.classList.contains('active');
    const relevant = query && !isCalendar && ((query.entity === 'projects') === isProjects);

    wraps.forEach((wrap) => {
        wrap.innerHTML = '';
        if (!relevant || !countKaiQueryFilters(query)) {
            wrap.hidden = true;
            return;
        }
        wrap.hidden = false;

        const pill = document.createElement('div');
        pill.className = 'kai-pill';

        const logo = document.createElement('img');
        logo.className = 'kai-pill__logo';
        logo.src = 'src/assets/kai-logo.png';
        logo.alt = '';
        pill.appendChild(logo);

        const text = document.createElement('span');
        text.className = 'kai-pill__text';
        text.textContent = query.explanation || summarizeKaiQueryForPill(query);
        pill.appendChild(text);

        const refine = document.createElement('button');
        refine.type = 'button';
        refine.className = 'kai-pill__btn';
        refine.dataset.action = 'refineKaiQuery';
        refine.textContent = t('askKai.find.refine');
        pill.appendChild(refine);

        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'kai-pill__btn kai-pill__btn--clear';
        clear.dataset.action = 'clearKaiQuery';
        clear.setAttribute('aria-label', t('askKai.find.clear'));
        clear.textContent = '×';
        pill.appendChild(clear);

        wrap.appendChild(pill);
    });
}

/**
 * Order chips alphabetically by their visible label.
 *
 * Sorted on the localized label rather than the internal dimension name, so the
 * order matches what the user actually reads in their language.
 */
function sortKaiChipsByLabel(chips) {
    return [...chips].sort((a, b) => (
        t(`askKai.find.dimension.${a.dimension}`)
            .localeCompare(t(`askKai.find.dimension.${b.dimension}`), getCurrentLanguage())
    ));
}

/** A short "Priority: High · Project: Nautilus" line for the pill. */
function summarizeKaiQueryForPill(query) {
    const chips = sortKaiChipsByLabel(describeKaiQuery(query, askKaiState.find.vocabulary || {}));
    return chips
        .map(chip => `${t(`askKai.find.dimension.${chip.dimension}`)}: ${chip.exclude ? `${t('askKai.find.not')} ` : ''}${chip.values.map(value => localizeKaiValue(chip.dimension, value)).join(', ')}`)
        .join(' · ');
}

/**
 * Render a raw filter value the way the rest of the UI already renders it.
 *
 * t() returns the key itself when there is no translation, so every lookup is
 * checked — a missing key must fall back to the raw value, never leak "status.done"
 * into the interface.
 */
function withResolvedPresetDate(label, preset) {
    if (!['end-today', 'start-today', 'end-tomorrow', 'start-tomorrow'].includes(preset)) return label;
    const day = getDateOffsetISO(preset.endsWith('tomorrow') ? 1 : 0, new Date(), getUserTimeZone());
    return `${label} (${formatDisplayDate(day)})`;
}

function localizeKaiValue(dimension, value) {
    if (value === 'none') return t('askKai.find.value.none');

    const keysByDimension = {
        status: `tasks.status.${value}`,
        priority: `tasks.priority.${value}`,
        datePreset: `askKai.find.preset.${value}`,
        linkType: `askKai.find.link.${value}`,
        projectStatus: `projects.status.${value}`,
        duplicate: `askKai.find.value.duplicate`,
        recency: `askKai.find.recency.${value}`
    };
    const key = keysByDimension[dimension];
    if (!key) return value;

    const label = t(key);
    const resolved = label && label !== key ? label : value;
    return dimension === 'datePreset' ? withResolvedPresetDate(resolved, value) : resolved;
}

/** "12 tasks · High priority, not Done" — the toast shown once the filters land. */
function describeKaiQueryResult(review) {
    const chips = sortKaiChipsByLabel(review.chips || []);
    const parts = chips.map(chip => (
        `${t(`askKai.find.dimension.${chip.dimension}`)}: ${chip.exclude ? `${t('askKai.find.not')} ` : ''}` +
        chip.values.map(value => localizeKaiValue(chip.dimension, value)).join(chip.and ? t('askKai.find.joinAnd') : t('askKai.find.joinOr'))
    ));

    const count = typeof review.matchCount === 'number'
        ? t('askKai.find.matchCount').replace('{count}', String(review.matchCount))
        : '';
    return [count, parts.join(' · ')].filter(Boolean).join(' — ');
}

/**
 * The review shown only when Kai has something to say: what it understood, what it
 * could not, and how many rows match.
 *
 * Apply is gated entirely by review.canApply via the existing
 * isAskKaiReviewApplyBlocked() — there is no new gating logic here.
 */
function renderKaiQueryReview(review, proposal, preview) {
    // The primary button applies a filter and navigates; "Apply" undersells that.
    const { confirmBtn } = getAskKaiElements();
    if (confirmBtn) {
        confirmBtn.textContent = review.entity === 'projects'
            ? t('askKai.find.showProjects')
            : t('askKai.find.showTasks');
    }

    const card = document.createElement('div');
    card.className = 'ask-kai-review-card ask-kai-find-review';

    if (review.summary) {
        const summary = document.createElement('p');
        summary.className = 'ask-kai-find-summary';
        summary.textContent = review.summary;
        card.appendChild(summary);
    }

    // The interpreted filters, as read-only chips. These mirror what will land on
    // the real toolbar, where they become removable.
    const chips = sortKaiChipsByLabel(
        review.chips || describeKaiQuery(proposal?.payload?.query, askKaiState.find.vocabulary || {})
    );
    if (chips.length) {
        const chipWrap = document.createElement('div');
        chipWrap.className = 'ask-kai-find-chips';
        chips.forEach((chip) => {
            const el = document.createElement('span');
            el.className = 'ask-kai-find-chip';
            if (chip.exclude) el.classList.add('ask-kai-find-chip--exclude');
            const label = t(`askKai.find.dimension.${chip.dimension}`);
            const values = chip.values.map(value => localizeKaiValue(chip.dimension, value));
            const joiner = chip.and ? t('askKai.find.joinAnd') : t('askKai.find.joinOr');
            const prefix = chip.exclude ? `${t('askKai.find.not')} ` : '';
            el.textContent = `${label}: ${prefix}${values.join(joiner)}`;
            chipWrap.appendChild(el);
        });
        card.appendChild(chipWrap);
    }

    // The answer itself: how many, and which ones. The query has already been run
    // against the local data, so these are the real rows — not a promise of them.
    if (typeof review.matchCount === 'number') {
        const count = document.createElement('p');
        count.className = 'ask-kai-find-count';
        if (review.matchCount === 0) {
            count.classList.add('ask-kai-find-count--empty');
            count.textContent = t('askKai.find.noMatches');
        } else {
            const key = review.entity === 'projects' ? 'askKai.find.resultsProjects' : 'askKai.find.resultsTasks';
            count.textContent = t(key).replace('{count}', String(review.matchCount));
        }
        card.appendChild(count);
    }

    if (review.items?.length) {
        const list = document.createElement('ul');
        list.className = 'ask-kai-find-results';
        review.items.forEach((item) => {
            const row = document.createElement('li');
            row.className = 'ask-kai-find-result';

            // Rows are a shortcut into the real thing: a task opens its modal stacked
            // ON TOP of the drawer (which stays open, so the result list is never
            // lost); a project opens in a new tab (deep-linked via #project-<id>).
            if (item.id != null) {
                const isProjectRow = !!item.projectStatus;
                row.classList.add('ask-kai-find-result--clickable');
                row.setAttribute('role', 'button');
                row.setAttribute('tabindex', '0');
                const open = () => {
                    if (isProjectRow) {
                        const url = `${window.location.origin}${window.location.pathname}#project-${item.id}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                        return;
                    }
                    // Leave the Kai drawer open behind the task modal, and lift the
                    // task modal above it (it defaults below the 2600 drawer). The
                    // class is cleared in closeModal('task-modal'). Closing the task
                    // returns the user to the same result list.
                    const taskModal = document.getElementById('task-modal');
                    if (taskModal) taskModal.classList.add('task-modal--over-kai');
                    openTaskDetails(item.id);
                };
                row.addEventListener('click', open);
                row.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        open();
                    }
                });
            }

            const title = document.createElement('span');
            title.className = 'ask-kai-find-result__title';
            title.textContent = item.title || t('tasks.untitled');
            row.appendChild(title);

            // Projects have a single status and no priority/date, so they get a
            // two-column row with the status badge pinned right — otherwise the lone
            // value floats in the task grid's priority column and looks broken.
            if (item.projectStatus) {
                row.classList.add('ask-kai-find-result--project');
                const status = document.createElement('span');
                status.className = `project-status-badge ${item.projectStatus}`;
                status.textContent = getProjectStatusLabel(item.projectStatus);
                row.appendChild(status);
                list.appendChild(row);
                return;
            }

            // Reuse the app's own pills rather than inventing a second visual language
            // for the same two fields.
            if (item.priority) {
                const priority = document.createElement('span');
                priority.className = `priority-pill priority-${item.priority}`;
                priority.textContent = getPriorityLabel(item.priority);
                row.appendChild(priority);
            }
            if (item.status) {
                const status = document.createElement('span');
                status.className = `status-badge ${item.status}`;
                status.textContent = getStatusLabel(item.status);
                row.appendChild(status);
            }
            // Date sits in its own right-hand column. When a task has no due date the
            // cell is empty, which keeps the pills aligned across every row.
            const date = document.createElement('span');
            date.className = 'ask-kai-find-result__meta';
            date.textContent = item.date || '';
            row.appendChild(date);

            list.appendChild(row);
        });
        card.appendChild(list);

        // Say what the user is actually looking at, and where the rest are. The
        // button name is rendered as an accent span (not quoted text) so it stands
        // out as the thing to click, in both languages.
        if (review.overflowCount > 0) {
            const more = document.createElement('p');
            more.className = 'ask-kai-find-more';
            const buttonLabel = review.entity === 'projects'
                ? t('askKai.find.showProjects')
                : t('askKai.find.showTasks');
            const template = t('askKai.find.showingOf')
                .replace('{shown}', String(review.items.length))
                .replace('{total}', String(review.matchCount));
            const [before, after] = template.split('{button}');
            more.appendChild(document.createTextNode(before ?? ''));
            // A real inline link that does the same thing as the primary button, for
            // users who reach for the words rather than the button.
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'ask-kai-find-more__btn';
            btn.textContent = buttonLabel;
            btn.addEventListener('click', () => confirmAskKaiProposal());
            more.appendChild(btn);
            more.appendChild(document.createTextNode(after ?? ''));
            card.appendChild(more);
        }
    }

    // We never broaden the search on our own — we only say what would help.
    if (review.emptyReasons?.length) {
        const hint = document.createElement('p');
        hint.className = 'ask-kai-find-hint';
        const best = review.emptyReasons[0];
        hint.textContent = t('askKai.find.emptyHint')
            .replace('{dimension}', t(`askKai.find.dimension.${best.dimension}`))
            .replace('{count}', String(best.count));
        card.appendChild(hint);
    }

    (review.unresolved || []).forEach((note) => {
        const row = document.createElement('div');
        row.className = 'ask-kai-find-note ask-kai-find-note--unresolved';

        const text = document.createElement('p');
        if (note.kinds?.length === 1) {
            // "You asked for a tag, but that name is a project." Say exactly that.
            text.textContent = t('askKai.find.wrongKind')
                .replace('{term}', note.term)
                .replace('{kind}', t(`askKai.find.kind.${note.kinds[0]}`).toLowerCase());
        } else if (note.kind === 'ambiguous') {
            text.textContent = t(note.kinds?.length ? 'askKai.find.ambiguousKind' : 'askKai.find.ambiguous')
                .replace('{term}', note.term);
        } else {
            text.textContent = t('askKai.find.unknownValue').replace('{term}', note.term);
        }
        row.appendChild(text);

        // Offer the possibilities as buttons rather than picking one for the user.
        const options = document.createElement('div');
        options.className = 'ask-kai-find-candidates';

        if (note.kinds?.length) {
            note.kinds.forEach((kind) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'ask-kai-find-candidate';
                btn.textContent = `${t(`askKai.find.kind.${kind}`)}: ${note.term}`;
                btn.addEventListener('click', () => resolveKaiCandidate(note, note.term, kind));
                options.appendChild(btn);
            });
        } else {
            (note.candidates || []).forEach((candidate) => {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'ask-kai-find-candidate';
                btn.textContent = candidate;
                btn.addEventListener('click', () => resolveKaiCandidate(note, candidate));
                options.appendChild(btn);
            });
        }
        if (options.childElementCount) row.appendChild(options);
        card.appendChild(row);
    });

    (review.unsupported || []).forEach((note) => {
        const row = document.createElement('div');
        row.className = 'ask-kai-find-note ask-kai-find-note--unsupported';
        const key = `askKai.find.unsupported.${note.reason}`;
        const message = t(key);
        row.textContent = message === key
            ? t('askKai.find.unsupported.generic').replace('{term}', note.term)
            : message;
        card.appendChild(row);
    });

    (review.blockingIssues || []).forEach((issue) => {
        const row = document.createElement('div');
        row.className = 'ask-kai-find-note ask-kai-find-note--blocking';
        const blockingMessages = {
            CONTRADICTORY_DATE_RANGE: 'askKai.find.contradictoryRange',
            CONTRADICTORY_COMPLETION: 'askKai.find.contradictoryCompletion'
        };
        row.textContent = t(blockingMessages[issue.code] || 'askKai.find.blocked');
        card.appendChild(row);
    });

    if (!chips.length && !review.unresolved?.length && !review.unsupported?.length) {
        const empty = document.createElement('p');
        empty.className = 'ask-kai-find-hint';
        empty.textContent = t('askKai.find.emptyQuery');
        card.appendChild(empty);
    }

    preview.appendChild(card);
}

/**
 * The user picked one of the "did you mean" candidates. Resolve it against the
 * vocabulary and re-render — this is the only place an ambiguous name is settled,
 * and it takes a human to do it.
 */
function resolveKaiCandidate(note, candidate, kind = '') {
    const proposal = askKaiState.proposal;
    const vocabulary = askKaiState.find.vocabulary || {};
    if (!proposal?.payload?.query) return;

    const query = normalizeKaiQuery(proposal.payload.query);
    const matchName = (name) => String(name).toLowerCase() === String(candidate).toLowerCase();
    const project = (vocabulary.projects || []).find(p => matchName(p.name));

    // `kind` is set when the user picked between "Tag: X" and "Project: X".
    const asProject = kind ? kind === 'project' : !!project;
    if (asProject && project) {
        if (!query.filters.projects.includes(project.id)) query.filters.projects.push(project.id);
    } else {
        const tag = (vocabulary.tags || [])
            .map(entry => (typeof entry === 'string' ? entry : entry?.name))
            .find(name => matchName(name)) || candidate;
        if (!query.filters.tags.includes(tag)) query.filters.tags.push(tag);
    }
    query.unresolved = query.unresolved.filter(entry => entry.term !== note.term);

    const validation = validateKaiQueryAgainstVocabulary(query, vocabulary);
    const envelope = buildKaiFindEnvelope(validation, vocabulary, 'local');
    const { confirmBtn } = getAskKaiElements();

    askKaiState.proposal = envelope.data.proposal;
    askKaiState.review = envelope.data.review;
    renderAskKaiPreview(askKaiState.review, askKaiState.proposal, confirmBtn);
    if (confirmBtn) confirmBtn.disabled = isAskKaiReviewApplyBlocked(askKaiState.review);
}

/** Reopen the drawer with the applied query as context, ready to refine. */
function refineKaiQuery() {
    // Seed from the live toolbar, not from the stored query, so filters the user
    // adjusted by hand after Kai ran are part of what gets refined.
    const isProjects = getActivePageId() === 'projects';
    askKaiState.find.query = filterStateToKaiQuery(
        isProjects ? projectFilterState : filterState,
        { entity: isProjects ? 'projects' : 'tasks' }
    );
    // Reopen on the prompt that produced this answer so the user edits real words,
    // not a blank box. turns holds each Find instruction; the last is what applied.
    const lastPrompt = askKaiState.find.turns?.at(-1) || '';
    openAskKaiModal({ instruction: lastPrompt, action: ASK_KAI_ACTION_FIND });
}

function clearKaiQuery() {
    resetKaiFindState();
    if (getActivePageId() === 'projects') {
        clearProjectFilters();
    } else {
        clearAllFilters();
    }
    renderKaiFilterPill();
}

function resetKaiFindState() {
    askKaiState.find = { query: null, vocabulary: null, turns: [] };
}

function normalizeAskKaiDocumentationPages(proposal) {
    const payload = proposal?.payload || {};
    const rawPages = Array.isArray(payload.pages) && payload.pages.length
        ? payload.pages
        : (proposal?.action === 'draft_template'
            ? [{
                ref: 'root',
                title: payload.title || t('docs.untitledPage'),
                icon: '',
                parentRef: null,
                order: 0,
                content: `${String(payload.description || '').trim()}\n\n${String(payload.content || '').trim()}`.trim()
            }]
            : []);

    return rawPages.map((page, index) => {
        const splitTitle = splitAskKaiTitleAndIcon(page?.title || '');
        return {
            ref: String(page?.ref || `page-${index + 1}`).trim() || `page-${index + 1}`,
            title: String(splitTitle.title || t('docs.untitledPage')).trim() || t('docs.untitledPage'),
            icon: String(page?.icon || splitTitle.icon || '').trim(),
            parentRef: page?.parentRef ? String(page.parentRef).trim() : '',
            order: Number.isFinite(Number(page?.order)) ? Number(page.order) : index,
            content: String(page?.content || '').trim(),
            originalIndex: index
        };
    });
}

function sortAskKaiDocumentationPages(pages = []) {
    const list = Array.isArray(pages) ? pages : [];
    const childrenByParent = new Map();
    list.forEach((page) => {
        const key = page.parentRef || '';
        if (!childrenByParent.has(key)) {
            childrenByParent.set(key, []);
        }
        childrenByParent.get(key).push(page);
    });

    for (const siblings of childrenByParent.values()) {
        siblings.sort((a, b) => (
            a.order - b.order ||
            a.originalIndex - b.originalIndex ||
            a.title.localeCompare(b.title)
        ));
    }

    const ordered = [];
    function visit(parentRef = '') {
        const siblings = childrenByParent.get(parentRef) || [];
        siblings.forEach((page) => {
            ordered.push(page);
            visit(page.ref);
        });
    }
    visit('');
    return ordered;
}

async function createAskKaiDocFromProposal(proposal) {
    await ensureDocsListLoaded();
    const state = getDocsState();

    let nextSortOrder = state.items.reduce((max, item) => Math.max(max, Number(item.sortOrder || 0)), 0) + 1;
    let nextTreeOrder = state.items.reduce((max, item) => Math.max(max, Number(item.treeOrder || 0)), 0) + 1;
    const orderedPages = sortAskKaiDocumentationPages(normalizeAskKaiDocumentationPages(proposal));
    if (!orderedPages.length) {
        throw new Error(t('askKai.errors.confirmFailed'));
    }

    const refToCreatedId = new Map();
    const createdRecords = [];
    let rootRecord = null;

    for (const page of orderedPages) {
        const response = await createProjectDoc({
            title: page.title,
            contentJson: buildAskKaiDocContentJson(page.content),
            sortOrder: nextSortOrder++,
            treeOrder: nextTreeOrder++,
            parentId: page.parentRef ? String(refToCreatedId.get(page.parentRef) || '') : '',
            icon: page.icon,
            widthMode: 'wide',
            historyMeta: {
                origin: 'ask_kai'
            }
        });

        if (!response?.item?.id) {
            throw new Error(t('askKai.errors.confirmFailed'));
        }

        const created = mergeDocsRecord(null, response.item);
        createdRecords.push(created);
        refToCreatedId.set(page.ref, String(created.id));
        if (!page.parentRef && !rootRecord) {
            rootRecord = created;
        }
    }

    logAskKaiTrace('transform.create_new_doc_tree', {
        proposalAction: proposal.action,
        pageCount: orderedPages.length,
        rootTitle: rootRecord?.title || '',
        pages: orderedPages.map((page) => ({
            ref: page.ref,
            title: page.title,
            parentRef: page.parentRef || '',
            order: page.order
        }))
    });

    state.currentDocId = rootRecord?.id || state.currentDocId;
    if (rootRecord?.id) {
        setDocsBaseline(null, rootRecord);
        syncDocsRouteState({
            docId: rootRecord.id,
            slug: rootRecord.slug || slugifyProjectDocTitle(rootRecord.title),
            mode: 'edit'
        });
        await ensureDocLoaded(null, rootRecord.id);
    }
    refreshDocsSurfaces();
    if (isDocsPageActive()) {
        focusDocsEditor();
    }
    return {
        root: rootRecord,
        createdPages: createdRecords
    };
}

// Apply a Kai rewrite/beautify to the current page as a SINGLE live-editor
// transaction — either a selection splice (only the highlighted range) or a
// whole-body replace — so one Undo (Ctrl+Z or the toolbar Undo) reverts the whole
// Kai edit. Mirrors the status-chip path: no persist+reload here (that rebuilds
// the editor state and wipes the undo stack); the edit lands as a dirty draft and
// the normal Publish flow saves it. Title is intentionally left unchanged — a
// rewrite reformats the body in place, it does not rename the page.
async function applyAskKaiProposalToCurrentDoc(proposal) {
    await ensureDocsListLoaded();
    const current = getCurrentDocsRecord();
    if (!current?.id) {
        throw new Error(t('askKai.errors.confirmFailed'));
    }
    if (!isDocsPageActive()) {
        throw new Error(t('askKai.errors.rewriteRequiresDocPage'));
    }

    const payload = proposal.payload || {};
    const content = proposal.action === 'draft_template'
        ? `${String(payload.description || '').trim()}\n\n${String(payload.content || '').trim()}`.trim()
        : String(payload.content || '').trim();

    const nodes = buildAskKaiDocBlocks(content);
    const safeNodes = (Array.isArray(nodes) && nodes.length) ? nodes : [{ type: 'paragraph' }];

    // Kai document edits are drafts: enter edit mode first so the editor is mounted
    // (and, for selection edits, the captured range still maps to the live doc).
    if (document.getElementById('docs-canvas-root')?.dataset.mode !== 'edit') {
        enterDocEditMode();
    }

    logAskKaiTrace('transform.update_current_doc.before', {
        currentDocId: current.id,
        incomingProposalAction: proposal.action,
        incomingContent: content,
        hasSelectionRewrite: !!askKaiState.selectionRewrite
    });

    // Selection-scoped rewrite: splice the transformed fragment back into the
    // captured range. Falls through to a whole-body replace if the range no longer
    // matches (page edited between generate and apply) or it targets another page.
    const selectionRewrite = askKaiState.selectionRewrite;
    askKaiState.selectionRewrite = null;
    if (selectionRewrite
        && proposal.action === 'rewrite_summarize'
        && String(selectionRewrite.docId) === String(current.id)) {
        const spliced = replaceDocsEditorSelection(
            { from: selectionRewrite.from, to: selectionRewrite.to },
            safeNodes,
            selectionRewrite.text
        );
        if (spliced) {
            if (isDocsPageActive()) focusDocsEditor();
            return current;
        }
    }

    // Whole-body replace as one undoable transaction.
    replaceDocsEditorContentLive(safeNodes);
    if (isDocsPageActive()) focusDocsEditor();
    return current;
}

// Apply a label→tone mapping as real statusChip nodes, in place, on the current
// page. Clones the live doc, swaps only the matching cells in the named
// column(s), persists via updateProjectDoc and reloads — preserving structure,
// other columns, and any chips already present. Goes through the same in-place
// update + revision history as a rewrite, so the existing preview/revert apply.
async function applyAskKaiStatusChipsToCurrentDoc(proposal) {
    const current = getCurrentDocsRecord();
    if (!current?.id) {
        throw new Error(t('askKai.errors.confirmFailed'));
    }

    const targets = Array.isArray(proposal?.payload?.targets) ? proposal.payload.targets : [];
    if (!targets.length) {
        throw new Error(t('askKai.errors.chipNoTable'));
    }

    // Kai document edits are drafts: enter the normal editor mode first so
    // Cancel has its pre-Kai snapshot and the user can Undo/Redo before an
    // explicit Publish creates a new page-history version.
    if (document.getElementById('docs-canvas-root')?.dataset.mode !== 'edit') {
        enterDocEditMode();
    }

    // Apply as a single live-editor ProseMirror transaction so Undo reverts ONLY
    // the chips (one clean step), not the whole table. No persist+reload, so the
    // editor's history stays intact. Marks the page dirty; the normal publish
    // flow persists it.
    const replaced = applyDocsStatusChips(targets);
    if (!replaced) {
        throw new Error(t('askKai.errors.chipNoMatch'));
    }

    if (isDocsPageActive()) {
        focusDocsEditor();
    }
    return current;
}

function buildAskKaiEntityPlan(proposal) {
    const payload = proposal?.payload || {};
    let workingProjects = JSON.parse(JSON.stringify(projects));
    let workingProjectCounter = projectCounter;
    let workingTasks = JSON.parse(JSON.stringify(tasks));
    let workingTaskCounter = taskCounter;
    const createdProjects = [];
    const createdTasks = [];

    // Build a map of existing tag names (lowercase → canonical) so KAI tags
    // reuse the exact casing already in the app instead of creating near-duplicates.
    const existingTagMap = new Map();
    tasks.forEach((task) => {
        (task.tags || []).forEach((tag) => {
            const key = String(tag || '').toLowerCase();
            if (key && !existingTagMap.has(key)) existingTagMap.set(key, tag);
        });
    });

    function normalizeTagsToExisting(proposedTags) {
        return (Array.isArray(proposedTags) ? proposedTags : []).map((tag) => {
            const key = String(tag || '').toLowerCase();
            return existingTagMap.get(key) || tag;
        });
    }

    // Resolve project/objective aliases against live client-side projects. The
    // resolver is deliberately order-independent and never guesses a tie.
    const projectReference = resolveAskKaiProjectReference(askKaiDraft.instruction, projects);

    function createProjectFromPayload(projectPayload) {
        const projectResult = createProjectService({
            name: String(projectPayload.projectName || '').trim(),
            description: String(projectPayload.projectDescription || '').trim(),
            startDate: String(projectPayload.startDate || '').trim(),
            endDate: String(projectPayload.endDate || '').trim(),
            tags: normalizeTagsToExisting(Array.isArray(projectPayload.tags) ? projectPayload.tags : [])
        }, workingProjects, workingProjectCounter);

        workingProjects = projectResult.projects;
        workingProjectCounter = projectResult.projectCounter;
        createdProjects.push(projectResult.project);
        return projectResult.project;
    }

    function createTaskFromPayload(taskPayload, projectId = null, preserveUnresolvedProjectReference = false) {
        const taskDescription = !preserveUnresolvedProjectReference || projectReference.matched
            ? taskPayload.description
            : appendAskKaiUnresolvedProjectNote(taskPayload.description, projectReference, askKaiDraft.instruction);
        const normalizedDescription = beautifyDescriptionLinksHtml(
            autoLinkifyDescription(
                convertPlainTextDescriptionToHtml(taskDescription || '')
            )
        );
        const taskResult = createTaskService({
            title: String(taskPayload.title || '').trim(),
            description: normalizedDescription,
            projectId,
            startDate: String(taskPayload.startDate || '').trim(),
            endDate: String(taskPayload.endDate || '').trim(),
            priority: String(taskPayload.priority || 'medium').trim(),
            status: 'todo',
            tags: normalizeTagsToExisting(Array.isArray(taskPayload.tags) ? taskPayload.tags : [])
        }, workingTasks, workingTaskCounter);

        workingTasks = taskResult.tasks;
        workingTaskCounter = taskResult.taskCounter;
        createdTasks.push(taskResult.task);
        return taskResult.task;
    }

    if (proposal.action === 'create_multiple_projects') {
        for (const projectPayload of (payload.projects || [])) {
            const newProject = createProjectFromPayload(projectPayload);
            for (const taskPayload of (projectPayload.tasks || [])) {
                createTaskFromPayload(taskPayload, newProject.id);
            }
        }
    } else if (proposal.action === 'create_tasks_only') {
        const inferredProjectId = projectReference.matched ? projectReference.projectId : null;
        for (const taskPayload of (payload.tasks || [])) {
            createTaskFromPayload(taskPayload, inferredProjectId, true);
        }
    } else if (proposal.action === 'create_project_only') {
        createProjectFromPayload(payload);
    } else {
        const newProject = createProjectFromPayload(payload);
        for (const taskPayload of (payload.tasks || [])) {
            createTaskFromPayload(taskPayload, newProject.id);
        }
    }

    return {
        projects: workingProjects,
        projectCounter: workingProjectCounter,
        tasks: workingTasks,
        taskCounter: workingTaskCounter,
        createdProjects,
        createdTasks
    };
}

async function rollbackAskKaiPersistedEntities(persisted) {
    const taskIds = Array.isArray(persisted?.taskIds) ? [...persisted.taskIds].reverse() : [];
    const projectIds = Array.isArray(persisted?.projectIds) ? [...persisted.projectIds].reverse() : [];

    for (const taskId of taskIds) {
        try {
            await saveTaskDeleteData(taskId);
        } catch (error) {
            console.error('[AskKai] Rollback task delete failed:', taskId, error?.message || error);
        }
    }

    for (const projectId of projectIds) {
        try {
            await saveProjectDeleteData(projectId);
        } catch (error) {
            console.error('[AskKai] Rollback project delete failed:', projectId, error?.message || error);
        }
    }
}

async function applyAskKaiEntityProposal(proposal) {
    const previousProjects = JSON.parse(JSON.stringify(projects));
    const previousProjectCounter = projectCounter;
    const previousTasks = JSON.parse(JSON.stringify(tasks));
    const previousTaskCounter = taskCounter;
    const persisted = {
        projectIds: [],
        taskIds: []
    };

    const plan = buildAskKaiEntityPlan(proposal);

    logAskKaiTrace('transform.project_plan', {
        proposalAction: proposal.action,
        createdProjects: plan.createdProjects.length,
        createdTasks: plan.createdTasks.length
    });

    try {
        for (const project of plan.createdProjects) {
            await saveProjectCreate(project);
            persisted.projectIds.push(project.id);
        }

        for (const task of plan.createdTasks) {
            await saveTaskCreate(task);
            persisted.taskIds.push(task.id);
        }

        projects = plan.projects;
        projectCounter = plan.projectCounter;
        tasks = plan.tasks;
        taskCounter = plan.taskCounter;

        if (window.historyService) {
            const askKaiHistoryMeta = getAskKaiHistoryMeta();
            plan.createdProjects.forEach((project) => window.historyService.recordProjectCreated(project, askKaiHistoryMeta));
            plan.createdTasks.forEach((task) => window.historyService.recordTaskCreated(task, askKaiHistoryMeta));
        }

        appState.projectsSortedView = null;
        const currentSort = projectSortState?.lastSort || DEFAULT_PROJECT_SORT;
        applyProjectsSort(currentSort, projects);
        render();

        return {
            createdProjects: plan.createdProjects,
            createdTasks: plan.createdTasks
        };
    } catch (error) {
        await rollbackAskKaiPersistedEntities(persisted);
        projects = previousProjects;
        projectCounter = previousProjectCounter;
        tasks = previousTasks;
        taskCounter = previousTaskCounter;
        appState.projectsSortedView = null;
        render();
        throw error;
    }
}

async function confirmAskKaiProposal() {
    let proposal = askKaiState.proposal;
    if (!proposal) return;
    if (askKaiState.busy) return;
    if (isAskKaiReviewApplyBlocked(askKaiState.review)) {
        showErrorNotification(t('askKai.errors.reviewBlocked'));
        return;
    }

    if (proposal._ambiguityInfo && proposal._ambiguityInfo.detected && askKaiState.selectedAmbiguityMode) {
        const selectedOption = proposal._ambiguityInfo.options.find(opt => opt.mode === askKaiState.selectedAmbiguityMode);
        if (selectedOption && selectedOption.proposal) {
            proposal = { ...selectedOption.proposal, _originalProposal: proposal };
            askKaiState.proposal = proposal;
        }
    } else if (proposal._ambiguityInfo && proposal._ambiguityInfo.detected) {
        showErrorNotification(t('askKai.errors.choiceRequired'));
        return;
    }

    const { confirmBtn, generateBtn } = getAskKaiElements();
    if (confirmBtn) confirmBtn.disabled = true;
    if (generateBtn) setAskKaiGenerateButtonBusy(true);
    askKaiState.busy = true;

    // Find mode applies filters instead of creating anything, so it exits before
    // the entity/doc/automation branches below and their undo-toast machinery.
    if (proposal.action === ASK_KAI_ACTION_FIND) {
        const review = askKaiState.review;
        try {
            await applyKaiQuery(proposal.payload?.query, proposal.payload?.mode);
            showSuccessNotification(describeKaiQueryResult(review));
        } catch (error) {
            // Never swallow this — a silent failure here strands the drawer.
            console.error('[AskKai] Failed to apply query filter', error);
            showErrorNotification(error?.message || t('askKai.errors.findFailed'));
        } finally {
            askKaiState.busy = false;
            if (generateBtn) setAskKaiGenerateButtonBusy(false);
            // Reset BEFORE closing: closeModal's guard prompts "Discard Kai proposal?"
            // when a proposal is still parked in state, and the user already applied it.
            resetAskKaiModalState();
            closeModal('ask-kai-modal');
            clearAskKaiDraftFromStorage();
        }
        return;
    }

    try {
        if (isAskKaiDocumentationAction(proposal.action)) {
            await createAskKaiDocFromProposal(proposal);
            if (!isDocsPageActive()) {
                showPage('docs');
            }
            renderDocsPage();
            closeModal('ask-kai-modal');
            resetAskKaiModalState();
            clearAskKaiDraftFromStorage();
            return;
        }

        if (proposal.action === 'create_automation') {
            await applyAskKaiAutomationProposal(proposal);
            closeModal('ask-kai-modal');
            resetAskKaiModalState();
            clearAskKaiDraftFromStorage();
            return;
        }

        if (proposal.action === ASK_KAI_ACTION_REWRITE) {
            if (isDocsPageActive()) {
                await applyAskKaiProposalToCurrentDoc(proposal);
            } else {
                showErrorNotification(t('askKai.errors.rewriteRequiresDocPage'));
                return;
            }
            // No renderDocsPage() here: the rewrite is applied as a single live
            // ProseMirror transaction (see applyAskKaiProposalToCurrentDoc). Re-
            // rendering would rehydrate from the still-saved document and erase that
            // transaction and its Undo history. The editor's onUpdate has already
            // marked the doc dirty; the normal Publish flow persists it.
            closeModal('ask-kai-modal');
            resetAskKaiModalState();
            clearAskKaiDraftFromStorage();
            return;
        }

        if (proposal.action === ASK_KAI_ACTION_APPLY_CHIPS) {
            if (isDocsPageActive()) {
                await applyAskKaiStatusChipsToCurrentDoc(proposal);
            } else {
                showErrorNotification(t('askKai.errors.rewriteRequiresDocPage'));
                return;
            }
            // The chip helper updates the live editor in one ProseMirror
            // transaction. Re-rendering here rehydrates from the still-saved
            // document and immediately erases that transaction (and its Undo
            // history). Its normal onUpdate hook has already marked the doc dirty.
            closeModal('ask-kai-modal');
            resetAskKaiModalState();
            clearAskKaiDraftFromStorage();
            return;
        }

        if (!_storageHealthy) {
            showErrorNotification(t('error.cfOutageBlocked'));
            return;
        }

        const applyResult = await applyAskKaiEntityProposal(proposal);
        const createdProjects = applyResult?.createdProjects || [];
        const createdTasks = applyResult?.createdTasks || [];
        closeModal('ask-kai-modal');
        resetAskKaiModalState();
        clearAskKaiDraftFromStorage();
        showAskKaiCreatedSummary(createdProjects);
        showAskKaiUndoToast({
            projectIds: createdProjects.map(p => p.id),
            taskIds: createdTasks.map(t => t.id)
        });
        scrollToAskKaiProjects(createdProjects);
    } catch (error) {
        const message = error?.message || t('askKai.errors.confirmFailed');
        showErrorNotification(message);
    } finally {
        askKaiState.busy = false;
        const { generateBtn: nextGenerateBtn, confirmBtn: nextConfirmBtn } = getAskKaiElements();
        if (nextGenerateBtn) setAskKaiGenerateButtonBusy(false);
        if (nextConfirmBtn) nextConfirmBtn.disabled = !askKaiState.proposal || isAskKaiReviewApplyBlocked(askKaiState.review);
    }
}

function getDocsTreeMetaStore() {
    try {
        const raw = localStorage.getItem(DOCS_TREE_META_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
        return {};
    }
}

function saveDocsTreeMetaStore(meta) {
    try {
        localStorage.setItem(DOCS_TREE_META_STORAGE_KEY, JSON.stringify(meta || {}));
    } catch (error) {}
}

function getDocsTreeMigrationKey() {
    return 'docsTreeMetaMigrated';
}

function getDocTreeMeta(docId) {
    const localMeta = getDocsTreeMetaStore();
    const persisted = getDocsState().items.find((item) => String(item.id) === String(docId)) || null;
    const entry = localMeta[String(docId)] || {};
    return {
        icon: String(persisted?.icon || ''),
        parentId: persisted?.parentId ? String(persisted.parentId) : '',
        collapsed: entry.collapsed !== false
    };
}

function setDocTreeMeta(docId, nextMeta = {}) {
    const meta = getDocsTreeMetaStore();
    const key = String(docId);
    meta[key] = {
        ...meta[key],
        collapsed: nextMeta.collapsed === true
    };
    if (meta[key].collapsed) {
        delete meta[key].collapsed;
    }
    if (!Object.keys(meta[key]).length) {
        delete meta[key];
    }
    saveDocsTreeMetaStore(meta);
    return getDocTreeMeta(docId);
}

function clearDocTreeMeta(docId) {
    const meta = getDocsTreeMetaStore();
    delete meta[String(docId)];
    saveDocsTreeMetaStore(meta);
}

function clearDocsTreeMetaMany(docIds = []) {
    const meta = getDocsTreeMetaStore();
    let changed = false;
    docIds.forEach((docId) => {
        const key = String(docId);
        if (key in meta) {
            delete meta[key];
            changed = true;
        }
    });
    if (changed) {
        saveDocsTreeMetaStore(meta);
    }
}

function resetDocsBulkSelection(projectId) {
    const state = getDocsState(projectId);
    state.bulkMode = false;
    state.selectedDocIds.clear();
}

function enterDocsBulkSelection(projectId) {
    const state = getDocsState(projectId);
    state.bulkMode = true;
    state.selectedDocIds.clear();
}

function toggleDocsBulkSelection(projectId, docId) {
    const state = getDocsState(projectId);
    const key = String(docId || '');
    if (!key) return;
    if (state.selectedDocIds.has(key)) {
        state.selectedDocIds.delete(key);
    } else {
        state.selectedDocIds.add(key);
    }
}

function getProjectDocsTreeMaps(projectId) {
    const state = getDocsState(projectId);
    const itemsById = new Map();
    const childrenByParent = new Map();

    state.items.forEach((item) => {
        const itemId = String(item.id);
        const parentId = String(item.parentId || '');
        itemsById.set(itemId, item);
        if (!childrenByParent.has(parentId)) {
            childrenByParent.set(parentId, []);
        }
        childrenByParent.get(parentId).push(itemId);
    });

    return { itemsById, childrenByParent };
}

function collectProjectDocDescendantIds(childrenByParent, rootId) {
    const result = [];
    const stack = [String(rootId)];
    const seen = new Set();

    while (stack.length) {
        const currentId = String(stack.pop());
        if (seen.has(currentId)) continue;
        seen.add(currentId);
        result.push(currentId);
        const children = childrenByParent.get(currentId) || [];
        for (let i = children.length - 1; i >= 0; i -= 1) {
            stack.push(String(children[i]));
        }
    }

    return result;
}

function getEffectiveProjectDocsDeletePlan(projectId, requestedDocIds = []) {
    const state = getDocsState(projectId);
    const { itemsById, childrenByParent } = getProjectDocsTreeMaps(projectId);
    const requested = Array.from(new Set((requestedDocIds || []).map((docId) => String(docId || '')).filter(Boolean)))
        .filter((docId) => itemsById.has(docId));
    const requestedSet = new Set(requested);

    const rootIds = requested.filter((docId) => {
        let parentId = String(itemsById.get(docId)?.parentId || '');
        while (parentId) {
            if (requestedSet.has(parentId)) return false;
            parentId = String(itemsById.get(parentId)?.parentId || '');
        }
        return true;
    });

    const effectiveSet = new Set();
    rootIds.forEach((docId) => {
        collectProjectDocDescendantIds(childrenByParent, docId).forEach((descendantId) => {
            effectiveSet.add(descendantId);
        });
    });

    const orderedIds = state.items
        .map((item) => String(item.id))
        .filter((docId) => effectiveSet.has(docId));

    return {
        requestedIds: requested,
        rootIds,
        effectiveIds: orderedIds,
        requestedItems: requested.map((docId) => itemsById.get(docId)).filter(Boolean),
        rootItems: rootIds.map((docId) => itemsById.get(docId)).filter(Boolean),
        effectiveItems: orderedIds.map((docId) => itemsById.get(docId)).filter(Boolean)
    };
}

function getNextProjectDocSelectionAfterDelete(projectId, deletedDocIds = []) {
    const state = getDocsState(projectId);
    const deletedSet = new Set((deletedDocIds || []).map((docId) => String(docId || '')).filter(Boolean));
    const currentId = String(state.currentDocId || '');
    if (!deletedSet.size) return currentId || null;
    if (currentId && !deletedSet.has(currentId)) return currentId;

    const orderedIds = state.items.map((item) => String(item.id));
    const currentIndex = currentId ? orderedIds.indexOf(currentId) : -1;
    if (currentIndex >= 0) {
        for (let i = currentIndex + 1; i < orderedIds.length; i += 1) {
            if (!deletedSet.has(orderedIds[i])) return orderedIds[i];
        }
        for (let i = currentIndex - 1; i >= 0; i -= 1) {
            if (!deletedSet.has(orderedIds[i])) return orderedIds[i];
        }
    }

    const remainingItems = state.items.filter((item) => !deletedSet.has(String(item.id)));
    const remainingIds = new Set(remainingItems.map((item) => String(item.id)));
    const firstRoot = remainingItems.find((item) => {
        const parentId = String(item.parentId || '');
        return !parentId || !remainingIds.has(parentId);
    });
    return String(firstRoot?.id || remainingItems[0]?.id || '') || null;
}

async function ensureProjectDocsTrashLoaded(projectId, options = {}) {
    const state = getDocsState(projectId);
    if (state.trashLoading && !options.force) {
        return state.trashItems;
    }

    state.trashLoading = true;
    if (isDocsTrashModalOpen()) {
        renderDocsTrashModal(projectId);
    }
    try {
        const response = await listTrashedProjectDocs();
        state.trashItems = (response?.items || []).map((item) => normalizeDocsRecord(projectId, item));
    } catch (error) {
        state.trashItems = [];
        showErrorNotification(error?.message || String(error));
    } finally {
        state.trashLoading = false;
        if (isDocsTrashModalOpen()) {
            renderDocsTrashModal(projectId);
        }
    }

    return state.trashItems;
}

async function restoreProjectDocFromTrash(projectId, docId) {
    const state = getDocsState(projectId);
    const restoreKey = String(docId || '');
    if (!restoreKey) return false;
    if (docsTrashViewState.emptying || docsTrashViewState.restoringIds.has(restoreKey) || docsTrashViewState.purgingIds.has(restoreKey)) {
        return false;
    }

    docsTrashViewState.restoringIds.add(restoreKey);
    renderDocsTrashModal(projectId);
    try {
        const response = await restoreTrashedProjectDoc(docId);
        const record = mergeDocsRecord(projectId, response?.item || {});
        state.trashItems = state.trashItems.filter((item) => String(item.id) !== restoreKey);
        const shouldSelectRecord = !state.currentDocId
            || !state.items.some((item) => String(item.id) === String(state.currentDocId));
        if (shouldSelectRecord) {
            state.currentDocId = record.id;
        }
        refreshDocsSurfaces(projectId);
        if (shouldSelectRecord) {
            await ensureDocLoaded(projectId, record.id);
        }
        if (String(docsTrashViewState.previewDocId || '') === restoreKey) {
            closeDocsTrashPreviewModal();
        }
        renderDocsTrashModal(projectId);
        showSuccessNotification(t('docs.trashRestored'));
        return true;
    } catch (error) {
        showErrorNotification(error?.message || String(error));
        return false;
    } finally {
        docsTrashViewState.restoringIds.delete(restoreKey);
        renderDocsTrashModal(projectId);
    }
}

async function permanentlyDeleteProjectDocFromTrash(projectId, docId) {
    const state = getDocsState(projectId);
    const purgeKey = String(docId || '');
    if (!purgeKey) return false;
    if (docsTrashViewState.emptying || docsTrashViewState.purgingIds.has(purgeKey) || docsTrashViewState.restoringIds.has(purgeKey)) {
        return false;
    }

    docsTrashViewState.purgingIds.add(purgeKey);
    renderDocsTrashModal(projectId);
    try {
        await permanentlyDeleteTrashedProjectDoc(docId);
        state.trashItems = state.trashItems.filter((item) => String(item.id) !== purgeKey);
        if (String(docsTrashViewState.previewDocId || '') === purgeKey) {
            closeDocsTrashPreviewModal({ reopenTrash: true });
        }
        renderDocsTrashModal(projectId);
        showSuccessNotification(t('docs.trashDeletedPermanent'));
        return true;
    } catch (error) {
        await ensureTrashedProjectDocsLoaded(projectId, { force: true });
        renderDocsTrashModal(projectId);
        showErrorNotification(error?.message || String(error));
        return false;
    } finally {
        docsTrashViewState.purgingIds.delete(purgeKey);
        renderDocsTrashModal(projectId);
    }
}

async function confirmPermanentlyDeleteProjectDocFromTrash(projectId, docId) {
    const state = getDocsState(projectId);
    const purgeKey = String(docId || '');
    if (!purgeKey) return false;
    const item = state.trashItems.find((entry) => String(entry.id) === purgeKey);
    if (!item) return false;

    const confirmed = await openDocsDeleteModal({
        mode: 'trash-purge-single',
        title: item.title || t('docs.untitledPage')
    });
    if (!confirmed) return false;
    return permanentlyDeleteProjectDocFromTrash(projectId, purgeKey);
}

async function emptyProjectDocsTrash(projectId) {
    const state = getDocsState(projectId);
    const allItems = Array.isArray(state.trashItems) ? [...state.trashItems] : [];
    if (!allItems.length || docsTrashViewState.emptying) return false;

    const confirmed = await openDocsDeleteModal({
        mode: 'trash-empty',
        effectiveItems: allItems
    });
    if (!confirmed) return false;

    docsTrashViewState.emptying = true;
    renderDocsTrashModal(projectId);
    try {
        const response = await emptyTrashedProjectDocs();
        const deletedIds = new Set((response?.deletedDocIds || []).map((docId) => String(docId)));
        state.trashItems = deletedIds.size
            ? state.trashItems.filter((item) => !deletedIds.has(String(item.id)))
            : [];

        if (isDocsTrashPreviewModalOpen()) {
            closeDocsTrashPreviewModal({ reopenTrash: true });
        }
        renderDocsTrashModal(projectId);
        showSuccessNotification(t('docs.trashEmptied', {
            count: response?.deletedCount || allItems.length
        }));
        return true;
    } catch (error) {
        await ensureTrashedProjectDocsLoaded(projectId, { force: true });
        renderDocsTrashModal(projectId);
        showErrorNotification(error?.message || String(error));
        return false;
    } finally {
        docsTrashViewState.emptying = false;
        docsTrashViewState.purgingIds.clear();
        renderDocsTrashModal(projectId);
    }
}

const docsTrashViewState = {
    loading: false,
    query: '',
    sort: 'deleted-desc',
    restoringIds: new Set(),
    purgingIds: new Set(),
    emptying: false,
    returnToSettings: false,
    previewDocId: null,
    previewReturnToTrash: false
};

const DEFAULT_PROJECT_DOC_ICON = '📄';

function isDocsTrashModalOpen() {
    return document.getElementById('docs-trash-modal')?.classList.contains('active') === true;
}

function isDocsTrashPreviewModalOpen() {
    return document.getElementById('docs-trash-preview-modal')?.classList.contains('active') === true;
}

function formatDocsTrashTimestamp(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(getLocale(), {
        dateStyle: 'medium',
        timeStyle: 'short'
    });
}

function formatDocsHistoryTimestamp(value) {
    if (!value) return t('tasks.noDate');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t('tasks.noDate');

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${formatDisplayDate(date)} ${hours}:${minutes} hs`;
}

function getProjectDocCustomIcon(item) {
    const icon = String(item?.icon || item?.meta?.icon || '').trim();
    if (!icon || icon === DEFAULT_PROJECT_DOC_ICON) {
        return '';
    }
    return icon;
}

function buildProjectDocIconStackHtml(item, options = {}) {
    const customIcon = getProjectDocCustomIcon(item);
    const compact = options.compact === true;
    const displayIcon = customIcon || DEFAULT_PROJECT_DOC_ICON;
    return `
        <span class="docs-page-icon-stack${compact ? ' docs-page-icon-stack--compact' : ''}" aria-hidden="true">
            <span class="docs-page-icon-stack__standard">${escapeHtml(displayIcon)}</span>
        </span>
    `;
}

function getDocsTrashParentPath(projectId, item) {
    const state = getDocsState(projectId);
    const itemsById = new Map();
    [...(state.items || []), ...(state.trashItems || [])].forEach((entry) => {
        itemsById.set(String(entry.id), entry);
    });

    let parentId = String(item?.parentId || '');
    if (!parentId) {
        return t('docs.trashRootLocation');
    }

    const parts = [];
    const visited = new Set();
    while (parentId) {
        if (visited.has(parentId)) break;
        visited.add(parentId);
        const parent = itemsById.get(parentId);
        if (!parent) {
            return parts.length ? parts.join(' / ') : t('docs.trashLocationUnknown');
        }
        parts.unshift(parent.title || t('docs.untitledPage'));
        parentId = String(parent.parentId || '');
    }

    return parts.join(' / ') || t('docs.trashRootLocation');
}

function getFilteredDocsTrashItems(projectId) {
    const state = getDocsState(projectId);
    const query = String(docsTrashViewState.query || '').trim().toLowerCase();
    const items = [...(state.trashItems || [])].filter((item) => {
        if (!query) return true;
        return String(item.title || '').toLowerCase().includes(query);
    });

    const { column, direction } = parseDocsTrashSortState(docsTrashViewState.sort);
    items.sort((a, b) => {
        const aValue = getDocsTrashSortValue(projectId, a, column);
        const bValue = getDocsTrashSortValue(projectId, b, column);

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            if (aValue !== bValue) {
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            }
        } else {
            const aText = String(aValue || '').toLowerCase();
            const bText = String(bValue || '').toLowerCase();
            const comparison = aText.localeCompare(bText, undefined, { sensitivity: 'base' });
            if (comparison !== 0) {
                return direction === 'asc' ? comparison : -comparison;
            }
        }

        const aFallback = String(a.title || '').toLowerCase();
        const bFallback = String(b.title || '').toLowerCase();
        return aFallback.localeCompare(bFallback, undefined, { sensitivity: 'base' });
    });

    return items;
}

function parseDocsTrashSortState(value) {
    const match = String(value || '').match(/^(title|location|created|deleted)-(asc|desc)$/);
    if (!match) {
        return { column: 'deleted', direction: 'desc' };
    }
    return { column: match[1], direction: match[2] };
}

function getDocsTrashDefaultSortDirection(column) {
    return column === 'title' || column === 'location' ? 'asc' : 'desc';
}

function getDocsTrashSortValue(projectId, item, column) {
    switch (column) {
        case 'title':
            return String(item?.title || '');
        case 'location':
            return getDocsTrashParentPath(projectId, item);
        case 'created':
            return new Date(item?.createdAt || item?.created_at || 0).getTime();
        case 'deleted':
        default:
            return new Date(item?.deletedAt || item?.updatedAt || item?.createdAt || 0).getTime();
    }
}

function getDocsTrashSortIndicator(column) {
    const state = parseDocsTrashSortState(docsTrashViewState.sort);
    if (state.column !== column) {
        return '↕';
    }
    return state.direction === 'asc' ? '↑' : '↓';
}

function setDocsTrashSort(column) {
    const state = parseDocsTrashSortState(docsTrashViewState.sort);
    if (state.column === column) {
        docsTrashViewState.sort = `${column}-${state.direction === 'asc' ? 'desc' : 'asc'}`;
        return;
    }
    docsTrashViewState.sort = `${column}-${getDocsTrashDefaultSortDirection(column)}`;
}

function renderDocsTrashModal(projectId = null) {
    const modal = document.getElementById('docs-trash-modal');
    if (!modal) return;

    const state = getDocsState(projectId);
    const searchInput = modal.querySelector('#docs-trash-search');
    const emptyBtn = modal.querySelector('#docs-trash-empty-btn');
    const countEl = modal.querySelector('#docs-trash-count');
    const listEl = modal.querySelector('#docs-trash-list');

    if (searchInput && searchInput.value !== docsTrashViewState.query) {
        searchInput.value = docsTrashViewState.query;
    }

    if (emptyBtn) {
        emptyBtn.disabled = docsTrashViewState.loading
            || state.trashLoading
            || docsTrashViewState.emptying
            || !(state.trashItems || []).length;
    }

    if (!countEl || !listEl) return;

    if (docsTrashViewState.loading || state.trashLoading) {
        countEl.textContent = '';
        listEl.innerHTML = `<div class="docs-trash-loading">${escapeHtml(t('docs.loading'))}</div>`;
        return;
    }

    const filteredItems = getFilteredDocsTrashItems(projectId);
    countEl.textContent = t('docs.trashCount', { count: filteredItems.length });

    if (!filteredItems.length) {
        const hasQuery = String(docsTrashViewState.query || '').trim().length > 0;
        listEl.innerHTML = `
            <div class="empty-state docs-trash-empty-state">
                <h3>${escapeHtml(hasQuery ? t('docs.trashNoResultsTitle') : t('docs.trashEmptyTitle'))}</h3>
                <p>${escapeHtml(hasQuery ? t('docs.trashNoResultsSubtitle') : t('docs.trashEmptySubtitle'))}</p>
            </div>
        `;
        return;
    }

    const rowsHtml = filteredItems.map((item) => {
        const restoreKey = String(item.id);
        const isBusy = docsTrashViewState.emptying
            || docsTrashViewState.restoringIds.has(restoreKey)
            || docsTrashViewState.purgingIds.has(restoreKey);
        const excerpt = String(item.excerpt || '').trim();
        return `
            <tr class="docs-trash-table__row" data-docs-trash-row="${restoreKey}">
                <td class="docs-trash-table__title-col" data-label="${escapeHtml(t('docs.trashTablePage'))}">
                    <div class="docs-trash-table__title-wrap">
                        ${buildProjectDocIconStackHtml(item, { compact: true })}
                        <div class="docs-trash-table__title-copy">
                            <div class="docs-trash-table__title">${escapeHtml(item.title || t('docs.untitledPage'))}</div>
                            <div class="docs-trash-table__excerpt">${escapeHtml(excerpt || t('docs.noSummary'))}</div>
                        </div>
                    </div>
                </td>
                <td class="docs-trash-table__location-col" data-label="${escapeHtml(t('docs.trashTableLocation'))}">${escapeHtml(getDocsTrashParentPath(projectId, item))}</td>
                <td class="docs-trash-table__date-col" data-label="${escapeHtml(t('docs.trashTableCreated'))}">${escapeHtml(formatDocsTrashTimestamp(item.createdAt || item.created_at || ''))}</td>
                <td class="docs-trash-table__date-col" data-label="${escapeHtml(t('docs.trashTableDeleted'))}">${escapeHtml(formatDocsTrashTimestamp(item.deletedAt || item.updatedAt || item.createdAt || ''))}</td>
                <td class="docs-trash-table__actions-col" data-label="${escapeHtml(t('docs.trashTableActions'))}">
                    <div class="docs-trash-table__actions">
                        <button type="button" class="nav-docs-tree-header-btn docs-trash-table__action-btn" data-docs-trash-preview="${restoreKey}" ${isBusy ? 'disabled' : ''}>${escapeHtml(t('docs.trashPreviewButton'))}</button>
                        <button type="button" class="btn-primary docs-trash-table__action-btn docs-trash-table__action-btn--restore" data-docs-trash-restore="${restoreKey}" ${isBusy ? 'disabled' : ''}>${escapeHtml(t('docs.trashRestore'))}</button>
                        <button
                            type="button"
                            class="docs-trash-table__action-btn docs-trash-table__action-btn--delete"
                            data-docs-trash-purge="${restoreKey}"
                            aria-label="${escapeHtml(t('docs.trashDeletePermanent'))}"
                            title="${escapeHtml(t('docs.trashDeletePermanent'))}"
                            ${isBusy ? 'disabled' : ''}
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true" class="docs-trash-table__action-icon">
                                <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9zm1 11h8a2 2 0 0 0 2-2V8H6v10a2 2 0 0 0 2 2z" fill="currentColor"></path>
                            </svg>
                        </button>
                    </div>
                </td>
            </tr>
        `;
    }).join('');

    const sortableHeaders = [
        { key: 'title', label: t('docs.trashTablePage') },
        { key: 'location', label: t('docs.trashTableLocation') },
        { key: 'created', label: t('docs.trashTableCreated') },
        { key: 'deleted', label: t('docs.trashTableDeleted') }
    ];
    const sortState = parseDocsTrashSortState(docsTrashViewState.sort);
    const headersHtml = sortableHeaders.map(({ key, label }) => {
        const direction = sortState.column === key ? sortState.direction : '';
        const ariaSort = sortState.column === key
            ? (sortState.direction === 'asc' ? 'ascending' : 'descending')
            : 'none';
        return `
            <th aria-sort="${ariaSort}">
                <button type="button" class="docs-trash-table__sort-btn" data-docs-trash-sort="${key}" data-direction="${direction}">
                    <span>${escapeHtml(label)}</span>
                    <span class="docs-trash-table__sort-indicator" aria-hidden="true">${getDocsTrashSortIndicator(key)}</span>
                </button>
            </th>
        `;
    }).join('');

    listEl.innerHTML = `
        <div class="docs-trash-table-wrap">
            <table class="docs-trash-table" aria-label="${escapeHtml(t('docs.trashTitle'))}">
                <thead>
                    <tr>
                        ${headersHtml}
                        <th>${escapeHtml(t('docs.trashTableActions'))}</th>
                    </tr>
                </thead>
                <tbody>${rowsHtml}</tbody>
            </table>
        </div>
    `;

    listEl.querySelectorAll('[data-docs-trash-restore]').forEach((button) => {
        button.addEventListener('click', async () => {
            await restoreProjectDocFromTrash(projectId, button.dataset.docsTrashRestore);
        });
    });
    listEl.querySelectorAll('[data-docs-trash-preview]').forEach((button) => {
        button.addEventListener('click', async () => {
            await openDocsTrashPreviewModal(projectId, button.dataset.docsTrashPreview);
        });
    });
    listEl.querySelectorAll('[data-docs-trash-purge]').forEach((button) => {
        button.addEventListener('click', async () => {
            await confirmPermanentlyDeleteProjectDocFromTrash(projectId, button.dataset.docsTrashPurge);
        });
    });
    listEl.querySelectorAll('[data-docs-trash-sort]').forEach((button) => {
        button.addEventListener('click', () => {
            setDocsTrashSort(String(button.dataset.docsTrashSort || 'deleted'));
            renderDocsTrashModal(projectId);
        });
    });
}

function bindDocsTrashModal() {
    const modal = document.getElementById('docs-trash-modal');
    if (!modal || modal.dataset.bound === 'true') return;
    modal.dataset.bound = 'true';

    const searchInput = modal.querySelector('#docs-trash-search');
    const emptyBtn = modal.querySelector('#docs-trash-empty-btn');

    searchInput?.addEventListener('input', () => {
        docsTrashViewState.query = searchInput.value || '';
        renderDocsTrashModal();
    });

    emptyBtn?.addEventListener('click', async () => {
        await emptyProjectDocsTrash(null);
    });
}

async function openDocsTrashModal(options = {}) {
    const modal = document.getElementById('docs-trash-modal');
    if (!modal) return;

    closeDocsTrashPreviewModal({ reopenTrash: false });
    bindDocsTrashModal();

    const settingsModal = document.getElementById('settings-modal');
    const shouldReturnToSettings = options.returnToSettings === true
        && settingsModal?.classList.contains('active') === true;

    docsTrashViewState.returnToSettings = shouldReturnToSettings;
    docsTrashViewState.query = '';
    docsTrashViewState.sort = 'deleted-desc';
    docsTrashViewState.loading = true;
    docsTrashViewState.restoringIds.clear();
    docsTrashViewState.purgingIds.clear();
    docsTrashViewState.emptying = false;

    if (shouldReturnToSettings && settingsModal) {
        settingsModal.classList.remove('active');
        if (typeof hideNotificationTimePortal === 'function') hideNotificationTimePortal();
        if (typeof hideNotificationTimeZonePortal === 'function') hideNotificationTimeZonePortal();
    }

    modal.classList.add('active');
    renderDocsTrashModal();
    window.setTimeout(() => {
        modal.querySelector('#docs-trash-search')?.focus();
    }, 0);

    try {
        await ensureDocsListLoaded();
        await ensureProjectDocsTrashLoaded(null, { force: true });
    } finally {
        docsTrashViewState.loading = false;
        renderDocsTrashModal();
    }
}

function closeDocsTrashModal() {
    const modal = document.getElementById('docs-trash-modal');
    if (modal) {
        modal.classList.remove('active');
    }

    closeDocsTrashPreviewModal({ reopenTrash: false });
    docsTrashViewState.loading = false;
    docsTrashViewState.restoringIds.clear();
    docsTrashViewState.purgingIds.clear();
    docsTrashViewState.emptying = false;

    if (docsTrashViewState.returnToSettings) {
        document.getElementById('settings-modal')?.classList.add('active');
    }
    docsTrashViewState.returnToSettings = false;
}

function renderDocsTrashPreviewModal(projectId = null) {
    const modal = document.getElementById('docs-trash-preview-modal');
    if (!modal) return;

    const state = getDocsState(projectId);
    const previewId = String(docsTrashViewState.previewDocId || '');
    const item = state.trashItems.find((entry) => String(entry.id) === previewId) || null;
    const contentEl = modal.querySelector('#docs-trash-preview-content');
    const locationEl = modal.querySelector('#docs-trash-preview-location');
    const createdEl = modal.querySelector('#docs-trash-preview-created');
    const deletedEl = modal.querySelector('#docs-trash-preview-deleted');
    const restoreBtn = modal.querySelector('#docs-trash-preview-restore-btn');

    if (!contentEl || !locationEl || !createdEl || !deletedEl || !restoreBtn) return;

    if (!item) {
        contentEl.innerHTML = `<div class="empty-state">${escapeHtml(t('docs.trashLocationUnknown'))}</div>`;
        locationEl.textContent = '';
        createdEl.textContent = '';
        deletedEl.textContent = '';
        restoreBtn.disabled = true;
        return;
    }

    locationEl.textContent = getDocsTrashParentPath(projectId, item);
    createdEl.textContent = formatDocsTrashTimestamp(item.createdAt || item.created_at || '');
    deletedEl.textContent = formatDocsTrashTimestamp(item.deletedAt || item.updatedAt || item.createdAt || '');
    restoreBtn.disabled = docsTrashViewState.emptying
        || docsTrashViewState.restoringIds.has(previewId)
        || docsTrashViewState.purgingIds.has(previewId);
    restoreBtn.dataset.docsTrashPreviewRestore = previewId;

    contentEl.innerHTML = buildProjectDocReadonlyPreview(item, {
        topbarLabel: ''
    });
    normalizeReadonlyDocsTables(contentEl);
}

function bindDocsTrashPreviewModal() {
    const modal = document.getElementById('docs-trash-preview-modal');
    if (!modal || modal.dataset.bound === 'true') return;
    modal.dataset.bound = 'true';

    modal.querySelector('#docs-trash-preview-restore-btn')?.addEventListener('click', async (event) => {
        const docId = event.currentTarget?.dataset?.docsTrashPreviewRestore;
        if (!docId) return;
        const restored = await restoreProjectDocFromTrash(null, docId);
        if (restored) {
            closeDocsTrashPreviewModal();
        } else {
            renderDocsTrashPreviewModal();
        }
    });
}

async function openDocsTrashPreviewModal(projectId, docId) {
    const modal = document.getElementById('docs-trash-preview-modal');
    if (!modal) return;

    const state = getDocsState(projectId);
    const previewId = String(docId || '');
    const item = state.trashItems.find((entry) => String(entry.id) === previewId) || null;
    if (!item) {
        showErrorNotification(t('docs.trashLocationUnknown'));
        return;
    }

    bindDocsTrashPreviewModal();
    docsTrashViewState.previewDocId = previewId;
    docsTrashViewState.previewReturnToTrash = isDocsTrashModalOpen();

    if (docsTrashViewState.previewReturnToTrash) {
        document.getElementById('docs-trash-modal')?.classList.remove('active');
    }

    renderDocsTrashPreviewModal(projectId);
    modal.classList.add('active');
}

function closeDocsTrashPreviewModal(options = {}) {
    const modal = document.getElementById('docs-trash-preview-modal');
    if (modal) {
        modal.classList.remove('active');
    }

    const reopenTrash = options.reopenTrash !== false && docsTrashViewState.previewReturnToTrash;
    docsTrashViewState.previewDocId = null;
    docsTrashViewState.previewReturnToTrash = false;

    if (reopenTrash) {
        document.getElementById('docs-trash-modal')?.classList.add('active');
        renderDocsTrashModal();
    }
}

function getDocsTree(projectId) {
    const state = getDocsState(projectId);
    const metaStore = getDocsTreeMetaStore();
    const itemsById = new Map(state.items.map((item) => [String(item.id), item]));
    const currentDocId = String(state.currentDocId || '');
    const childrenByParent = new Map();
    const roots = [];

    state.items.forEach((item) => {
        const meta = metaStore[String(item.id)] || {};
        const parentId = String(item.parentId || '');
        const normalized = {
            ...item,
            treeIcon: String(item.icon || ''),
            parentId: parentId && itemsById.has(parentId) ? parentId : '',
            collapsed: meta.collapsed !== false
        };

        if (normalized.parentId) {
            const children = childrenByParent.get(normalized.parentId) || [];
            children.push(normalized);
            childrenByParent.set(normalized.parentId, children);
        } else {
            roots.push(normalized);
        }
    });

    const buildBranch = (item, depth = 1, trail = new Set()) => {
        const itemId = String(item.id);
        if (trail.has(itemId) || depth > 3) {
            return {
                ...item,
                depth,
                collapsed: item.collapsed,
                children: []
            };
        }

        const nextTrail = new Set(trail);
        nextTrail.add(itemId);
        const children = (childrenByParent.get(itemId) || [])
            .slice(0, 20)
            .map((child) => buildBranch(child, depth + 1, nextTrail));
        const hasActiveDescendant = children.some((child) =>
            String(child.id) === currentDocId || child.hasActiveDescendant === true
        );

        return {
            ...item,
            depth,
            collapsed: hasActiveDescendant ? false : item.collapsed,
            hasActiveDescendant,
            children
        };
    };

    return roots.map((item) => buildBranch(item, 1));
}

function openDocsRowMenu(anchorEl, projectId, docId) {
    const existing = document.getElementById('docs-row-menu');
    if (existing) existing.remove();

    const meta = getDocTreeMeta(docId);
    const current = getDocsState(projectId).items.find((item) => String(item.id) === String(docId));
    if (!current) return;

    const menu = document.createElement('div');
    menu.id = 'docs-row-menu';
    menu.className = 'docs-row-menu';
    menu.innerHTML = `
        ${meta.parentId ? `<button type="button" class="docs-row-menu__item" data-doc-row-action="unnest">↰ ${t('docs.removeNesting')}</button>` : ''}
        <button type="button" class="docs-row-menu__item" data-doc-row-action="duplicate">⧉ ${t('docs.duplicate.title')}</button>
        <button type="button" class="docs-row-menu__item docs-row-menu__item--danger" data-doc-row-action="delete">🗑️ ${t('docs.deletePage')}</button>
    `;
    document.body.appendChild(menu);

    const rect = anchorEl.getBoundingClientRect();
    menu.style.top = `${Math.min(window.innerHeight - menu.offsetHeight - 8, rect.bottom + 4)}px`;
    menu.style.left = `${Math.min(window.innerWidth - menu.offsetWidth - 8, rect.right - menu.offsetWidth)}px`;

    const close = () => menu.remove();

    menu.addEventListener('click', async (event) => {
        const actionBtn = event.target.closest('[data-doc-row-action]');
        if (!actionBtn) return;

        const action = actionBtn.dataset.docRowAction;
        if (action === 'unnest') {
            await persistProjectDocMeta(projectId, docId, { parentId: '' });
            updateDocsNavTree(projectId);
        } else if (action === 'duplicate') {
            handleDuplicateDocPage(projectId, docId);
        } else if (action === 'delete') {
            const state = getDocsState(projectId);
            state.currentDocId = String(docId);
            await deleteCurrentProjectDoc(projectId);
            renderDocsPage();
        }

        close();
    });

    window.setTimeout(() => {
        document.addEventListener('click', function onOutside(event) {
            if (!menu.contains(event.target) && event.target !== anchorEl) {
                close();
                document.removeEventListener('click', onOutside);
            }
        });
    }, 0);
}

function getDocsState() {
    return docsState;
}

function sortProjectDocsItems(items) {
    return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
        const treeDiff = (Number(a?.treeOrder) || 0) - (Number(b?.treeOrder) || 0);
        if (treeDiff !== 0) return treeDiff;
        const orderDiff = (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0);
        if (orderDiff !== 0) return orderDiff;
        return String(b?.updatedAt || '').localeCompare(String(a?.updatedAt || ''));
    });
}

function getChildDocsPages(projectId, docId) {
    if (!docId) return [];
    const state = getDocsState(projectId);
    return state.items.filter((item) => String(item.parentId || '') === String(docId));
}

function slugifyProjectDocTitle(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-') || 'page';
}

const DOCS_ATTACHMENT_BLOCKED_EXTENSIONS = new Set(['bat', 'cjs', 'cmd', 'exe', 'htm', 'html', 'js', 'mjs']);
const DOCS_ATTACHMENT_BLOCKED_MIME_TYPES = new Set([
    'application/javascript',
    'application/x-bat',
    'application/x-msdos-program',
    'application/x-msdownload',
    'application/xhtml+xml',
    'text/html',
    'text/javascript'
]);
const DOCS_ATTACHMENT_ALLOWED_EXTENSIONS = new Set(['ai', 'csv', 'doc', 'docx', 'eps', 'json', 'md', 'odp', 'ods', 'odt', 'pdf', 'psd', 'ppt', 'pptx', 'rtf', 'sql', 'svg', 'txt', 'xls', 'xlsx', 'zip']);
const DOCS_ATTACHMENT_ALLOWED_MIME_TYPES = new Set([
    'application/json',
    'application/msword',
    'application/pdf',
    'application/postscript',
    'application/rtf',
    'application/vnd.adobe.photoshop',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'application/vnd.oasis.opendocument.presentation',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/x-zip-compressed',
    'application/zip',
    'image/svg+xml',
    'image/vnd.adobe.photoshop',
    'text/csv',
    'text/markdown',
    'text/plain',
    'text/rtf',
    'text/x-markdown',
    'text/x-sql'
]);
const DOCS_ATTACHMENT_FORCE_DOWNLOAD_EXTENSIONS = new Set(['svg']);
const DOCS_ATTACHMENT_FORCE_DOWNLOAD_MIME_TYPES = new Set([
    'image/svg+xml'
]);
const DOCS_ATTACHMENT_PREVIEWABLE_EXTENSIONS = new Set(['csv', 'json', 'md', 'pdf', 'sql', 'txt']);
const DOCS_ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.json,.sql,.zip,.rtf,.odt,.ods,.odp,.svg,.ai,.eps,.psd';
const DOCS_ATTACHMENT_MAX_BYTES = 350 * 1024 * 1024;

function normalizeDocsAttachmentMimeType(value) {
    return String(value || '').split(';')[0].trim().toLowerCase();
}

function isDocsSvgMimeType(value) {
    return normalizeDocsAttachmentMimeType(value) === 'image/svg+xml';
}

function getDocsAttachmentExtension(fileName = '') {
    const normalized = String(fileName || '').trim();
    if (!normalized.includes('.')) return '';
    return normalized.split('.').pop().trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function getDocsUploadFileKey(docId, file, fallbackExtension = 'bin') {
    const safeDocId = String(docId || 'draft').replace(/[^a-zA-Z0-9_-]/g, '');
    const extensionFromType = String(file?.type || '').split('/')[1] || '';
    const extensionFromName = String(file?.name || '').split('.').pop() || '';
    const extension = (extensionFromName || extensionFromType || fallbackExtension)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '') || fallbackExtension;
    const uniqueId = typeof crypto?.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    return `docs_${safeDocId}_${uniqueId}.${extension}`;
}

function buildDocsImageFileKey(docId, file) {
    return getDocsUploadFileKey(docId, file, 'png');
}

function buildDocsAttachmentFileKey(docId, file) {
    return getDocsUploadFileKey(docId, file, 'file');
}

function buildDocsFileAuthQuery() {
    // Read-only file token (see getFileLinkToken in src/utils/file.js); session token as fallback
    const token = localStorage.getItem('fileToken') || window.authSystem?.getAuthToken?.() || localStorage.getItem('authToken') || '';
    return token ? `&authToken=${encodeURIComponent(token)}` : '';
}

function buildDocsImageSrc(fileKey, options = {}) {
    if (options.publicToken) {
        return `/api/files?key=${encodeURIComponent(fileKey)}&mode=raw&publicToken=${encodeURIComponent(options.publicToken)}`;
    }
    const authQuery = options.includeAuth === false ? '' : buildDocsFileAuthQuery();
    return `/api/files?key=${encodeURIComponent(fileKey)}&mode=raw${authQuery}`;
}

function buildDocsAttachmentUrl(fileKey, options = {}) {
    return buildFileReadUrl(fileKey, {
        publicToken: options.publicToken || '',
        download: options.download === true
    });
}

function isDocsAttachmentBlockedType(mimeType, fileName) {
    const normalizedMimeType = normalizeDocsAttachmentMimeType(mimeType);
    const extension = getDocsAttachmentExtension(fileName);
    return DOCS_ATTACHMENT_BLOCKED_EXTENSIONS.has(extension) || DOCS_ATTACHMENT_BLOCKED_MIME_TYPES.has(normalizedMimeType);
}

function isDocsAttachmentAllowedType(mimeType, fileName) {
    const normalizedMimeType = normalizeDocsAttachmentMimeType(mimeType);
    const extension = getDocsAttachmentExtension(fileName);
    return DOCS_ATTACHMENT_ALLOWED_EXTENSIONS.has(extension) || DOCS_ATTACHMENT_ALLOWED_MIME_TYPES.has(normalizedMimeType);
}

function isDocsAttachmentForcedDownloadType(mimeType, fileName) {
    const normalizedMimeType = normalizeDocsAttachmentMimeType(mimeType);
    const extension = getDocsAttachmentExtension(fileName);
    return DOCS_ATTACHMENT_FORCE_DOWNLOAD_EXTENSIONS.has(extension) || DOCS_ATTACHMENT_FORCE_DOWNLOAD_MIME_TYPES.has(normalizedMimeType);
}

function getDocsAttachmentFileClass(mimeType, fileName) {
    const normalizedMimeType = normalizeDocsAttachmentMimeType(mimeType);
    const extension = getDocsAttachmentExtension(fileName);
    if (normalizedMimeType === 'application/pdf' || extension === 'pdf') return 'pdf';
    if (['xls', 'xlsx', 'csv', 'ods'].includes(extension) || normalizedMimeType.includes('spreadsheet') || normalizedMimeType.includes('excel')) return 'spreadsheet';
    if (['doc', 'docx', 'odt', 'rtf'].includes(extension) || normalizedMimeType.includes('word') || normalizedMimeType.includes('document')) return 'document';
    if (['ppt', 'pptx', 'odp'].includes(extension) || normalizedMimeType.includes('powerpoint') || normalizedMimeType.includes('presentation')) return 'presentation';
    if (['txt', 'md', 'json', 'sql'].includes(extension) || normalizedMimeType.startsWith('text/') || normalizedMimeType === 'application/json') return 'text';
    if (extension === 'zip' || normalizedMimeType.includes('zip')) return 'archive';
    return 'file';
}

function isDocsAttachmentPreviewable(attrs = {}) {
    const extension = getDocsAttachmentExtension(attrs.extension || attrs.name || '');
    const mimeType = normalizeDocsAttachmentMimeType(attrs.mimeType);
    if (isDocsAttachmentForcedDownloadType(mimeType, attrs.name || attrs.extension || '')) {
        return false;
    }
    return DOCS_ATTACHMENT_PREVIEWABLE_EXTENSIONS.has(extension)
        || mimeType === 'application/pdf'
        || mimeType === 'application/json'
        || mimeType.startsWith('text/');
}

function getDocsAttachmentBadgeLabel(attrs = {}) {
    const extension = getDocsAttachmentExtension(attrs.extension || attrs.name || '');
    if (extension) return extension.toUpperCase().slice(0, 4);
    const fileClass = String(attrs.fileClass || 'file').toUpperCase();
    return fileClass.slice(0, 4) || 'FILE';
}

function getDocsAttachmentTypeLabel(attrs = {}) {
    const extension = getDocsAttachmentExtension(attrs.extension || attrs.name || '');
    if (extension) return extension.toUpperCase();
    const fileClass = String(attrs.fileClass || '').trim();
    switch (fileClass) {
        case 'pdf': return 'PDF';
        case 'spreadsheet': return 'SHEET';
        case 'document': return 'DOC';
        case 'presentation': return 'SLIDE';
        case 'text': return 'TEXT';
        case 'archive': return 'ZIP';
        default: return 'FILE';
    }
}

function validateDocsAttachmentFile(file) {
    if (!file) {
        return t('docs.fileInvalidType');
    }
    if (isDocsAttachmentBlockedType(file.type || '', file.name || '')) {
        return t('docs.fileBlockedType');
    }
    if (!isDocsAttachmentAllowedType(file.type || '', file.name || '')) {
        return t('docs.fileInvalidType');
    }
    if (file.size > DOCS_ATTACHMENT_MAX_BYTES) {
        return t('docs.fileTooLarge', { maxMB: Math.round(DOCS_ATTACHMENT_MAX_BYTES / (1024 * 1024)) });
    }
    return '';
}

function resolveDocsFileAttachmentPresentation(attrs = {}) {
    const fileKey = String(attrs.fileKey || '').trim();
    const publicToken = String(getDocsRouteState().publicToken || '').trim();
    const fileClass = String(attrs.fileClass || getDocsAttachmentFileClass(attrs.mimeType || '', attrs.name || attrs.extension || '')).trim() || 'file';
    const extension = getDocsAttachmentExtension(attrs.extension || attrs.name || '');
    const previewable = isDocsAttachmentPreviewable({
        extension,
        mimeType: attrs.mimeType,
        name: attrs.name,
        fileClass
    });

    return {
        fileClass,
        extension,
        badgeLabel: getDocsAttachmentBadgeLabel({ extension, name: attrs.name, fileClass }),
        typeLabel: getDocsAttachmentTypeLabel({ extension, fileClass }),
        previewable,
        openUrl: fileKey
            ? buildDocsAttachmentUrl(fileKey, { publicToken, download: !previewable })
            : '',
        downloadUrl: fileKey
            ? buildDocsAttachmentUrl(fileKey, { publicToken, download: true })
            : ''
    };
}

function setDocsTransientStatus(projectId, message, flashState = 'saving') {
    const state = getDocsState(projectId);
    clearDocsFlash(projectId);
    state.flashMessage = String(message || '');
    state.flashState = flashState;
    updateDocsStatus(projectId);
}

function clearDocsTransientStatus(projectId) {
    const state = getDocsState(projectId);
    clearDocsFlash(projectId);
    updateDocsStatus(projectId);
}

function normalizeDocsUploadError(error) {
    const message = String(error?.message || '').trim();
    if (message === 'Blocked docs attachment type') return t('docs.fileBlockedType');
    if (message === 'Unsupported docs attachment type') return t('docs.fileInvalidType');
    if (message === 'Invalid docs attachment target') return t('docs.fileNoPage');
    if (message === 'Unsupported docs image type') return t('docs.imageInvalidType');
    if (message === 'Invalid docs image target') return t('docs.imageNoPage');
    return message || t('error.fileUploadFailed', { message: 'Unknown error' });
}

function extractDocsImageFileKeyFromAttrs(attrs = {}) {
    const directKey = String(attrs?.fileKey || '').trim();
    if (directKey) return directKey;

    const src = String(attrs?.src || '').trim();
    if (!src) return '';

    try {
        const url = new URL(src, window.location.origin);
        if (url.pathname === '/api/files') {
            return String(url.searchParams.get('key') || '').trim();
        }
    } catch {
        return '';
    }

    return '';
}

function rewriteDocsImageNodes(docJson, options = {}) {
    if (!docJson || typeof docJson !== 'object') return docJson;
    const clone = JSON.parse(JSON.stringify(docJson));
    const walk = (node) => {
        if (!node || typeof node !== 'object') return;
        if ((node.type === 'image' || node.type === 'docsImage') && node.attrs) {
            const fileKey = extractDocsImageFileKeyFromAttrs(node.attrs);
            if (fileKey) {
                node.attrs.fileKey = fileKey;
                node.attrs.src = buildDocsImageSrc(fileKey, options);
            }
        }
        if (Array.isArray(node.content)) {
            node.content.forEach(walk);
        }
    };
    walk(clone);
    return clone;
}

function dehydrateDocsImageNodes(docJson) {
    if (!docJson || typeof docJson !== 'object') return docJson;
    const clone = JSON.parse(JSON.stringify(docJson));
    const walk = (node) => {
        if (!node || typeof node !== 'object') return;
        if ((node.type === 'image' || node.type === 'docsImage') && node.attrs) {
            const fileKey = extractDocsImageFileKeyFromAttrs(node.attrs);
            if (fileKey) {
                node.attrs.fileKey = fileKey;
                node.attrs.src = buildDocsImageSrc(fileKey, { includeAuth: false });
            }
        }
        if (Array.isArray(node.content)) {
            node.content.forEach(walk);
        }
    };
    walk(clone);
    return clone;
}

function getCurrentDocsUploadTarget() {
    const docsCanvas = document.getElementById('docs-canvas-root');
    if (docsCanvas && docsCanvas.dataset.mode !== 'edit') {
        return null;
    }

    const currentDoc = getCurrentDocsRecord();
    if (!currentDoc?.id) {
        return null;
    }

    return { currentDoc };
}

async function uploadDocsImageFile(docId, file, onProgress = null) {
    if (!file || !String(file.type || '').startsWith('image/')) {
        throw new Error(t('docs.imageInvalidType'));
    }

    const maxBytes = 25 * 1024 * 1024;
    if (file.size > maxBytes) {
        throw new Error(t('docs.imageTooLarge'));
    }

    const fileKey = buildDocsImageFileKey(docId, file);
    await uploadFile(fileKey, file, {
        docId: String(docId),
        kind: 'docs_image',
        fileName: file.name || ''
    }, onProgress);

    return {
        fileKey,
        src: buildDocsImageSrc(fileKey),
        alt: String(file.name || '').replace(/\.[^.]+$/, '')
    };
}

async function uploadDocsAttachmentFile(docId, file, onProgress = null) {
    const validationError = validateDocsAttachmentFile(file);
    if (validationError) {
        throw new Error(validationError);
    }

    const fileKey = buildDocsAttachmentFileKey(docId, file);
    await uploadFile(fileKey, file, {
        docId: String(docId),
        kind: 'docs_attachment',
        fileName: file.name || ''
    }, onProgress);

    return {
        fileKey,
        name: String(file.name || '').trim() || t('docs.fileUntitled'),
        mimeType: String(file.type || 'application/octet-stream'),
        size: Number(file.size) || 0,
        fileClass: getDocsAttachmentFileClass(file.type || '', file.name || ''),
        extension: getDocsAttachmentExtension(file.name || '')
    };
}

async function insertUploadedDocsImage(file) {
    const target = getCurrentDocsUploadTarget();
    if (!target?.currentDoc?.id) {
        showErrorNotification(t('docs.imageNoPage'));
        return false;
    }

    try {
        setDocsTransientStatus(null, t('docs.fileUploading', {
            name: file.name || t('docs.toolbar.image'),
            progress: 0
        }));
        const uploaded = await uploadDocsImageFile(target.currentDoc.id, file, (progress) => {
            setDocsTransientStatus(null, t('docs.fileUploading', {
                name: file.name || t('docs.toolbar.image'),
                progress: Math.max(0, Math.min(100, Number(progress) || 0))
            }));
        });
        insertDocsImage(uploaded);
        focusDocsEditor();
        updateDocsDirtyState(null);
        showDocsFlash(null, t('docs.fileUploaded', {
            name: file.name || uploaded.alt || t('docs.toolbar.image')
        }), 'saved', 1600);
        return true;
    } catch (error) {
        console.error('Failed to upload docs image:', error);
        clearDocsTransientStatus(null);
        showErrorNotification(normalizeDocsUploadError(error));
        return false;
    }
}

async function insertUploadedDocsAttachment(file, options = {}) {
    const target = getCurrentDocsUploadTarget();
    if (!target?.currentDoc?.id) {
        showErrorNotification(t('docs.fileNoPage'));
        return false;
    }

    try {
        setDocsTransientStatus(null, t('docs.fileUploading', {
            name: file.name || t('docs.toolbar.file'),
            progress: 0
        }));
        const uploaded = await uploadDocsAttachmentFile(target.currentDoc.id, file, (progress) => {
            setDocsTransientStatus(null, t('docs.fileUploading', {
                name: file.name || t('docs.toolbar.file'),
                progress: Math.max(0, Math.min(100, Number(progress) || 0))
            }));
        });
        insertDocsFileAttachment(uploaded, { display: options.display || 'auto' });
        focusDocsEditor();
        updateDocsDirtyState(null);
        showDocsFlash(null, t('docs.fileUploaded', {
            name: uploaded.name || t('docs.toolbar.file')
        }), 'saved', 1600);
        return true;
    } catch (error) {
        console.error('Failed to upload docs attachment:', error);
        clearDocsTransientStatus(null);
        showErrorNotification(normalizeDocsUploadError(error));
        return false;
    }
}

function openDocsImagePicker() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', async () => {
        const files = Array.from(input.files || []).filter(Boolean);
        for (const file of files) {
            await insertUploadedDocsImage(file);
        }
        input.remove();
    }, { once: true });

    input.click();
}

async function handleDocsEditorSurfaceFiles(files = [], options = {}) {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return false;

    const target = getCurrentDocsUploadTarget();
    if (!target?.currentDoc?.id) {
        showErrorNotification(t('docs.fileNoPage'));
        return false;
    }

    let inserted = false;
    for (const file of list) {
        if (String(file?.type || '').startsWith('image/') && !isDocsSvgMimeType(file?.type)) {
            inserted = await insertUploadedDocsImage(file) || inserted;
            continue;
        }
        inserted = await insertUploadedDocsAttachment(file, options) || inserted;
    }

    return inserted;
}

function openDocsFilePicker() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = DOCS_ATTACHMENT_ACCEPT;
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', async () => {
        const files = Array.from(input.files || []);
        if (files.length) {
            await handleDocsEditorSurfaceFiles(files, { source: 'picker' });
        }
        input.remove();
    }, { once: true });

    input.click();
}

async function openDocsYoutubePicker() {
    const value = await openDocsVideoModal();
    if (!value) return false;

    const inserted = insertDocsYoutubeVideo(value);
    if (!inserted) {
        showErrorNotification(t('docs.videoInvalid'));
        return false;
    }

    focusDocsEditor();
    return true;
}

function updateDocsTableMenuVisibility() {
    const docsCanvas = document.getElementById('docs-canvas-root');
    const menu = document.getElementById('docs-table-menu');
    if (!menu) return;

    const isEditMode = docsCanvas?.dataset.mode === 'edit';
    const tableState = getDocsEditorTableState();
    const wrapper = isEditMode && tableState.active ? getDocsEditorTableWrapper() : null;
    const editorWrap = document.querySelector('.docs-canvas__editor-wrap');
    const optionsToggle = menu.querySelector('[data-table-menu-toggle="options"]');
    const optionsMenu = menu.querySelector('[data-table-menu-dropdown="options"]');
    const overflowToggle = menu.querySelector('[data-table-menu-toggle="overflow"]');
    const overflowMenu = menu.querySelector('[data-table-menu-dropdown="overflow"]');

    if (!wrapper) {
        menu.style.display = 'none';
        menu.style.left = '';
        menu.style.top = '';
        menu.style.width = '';
        menu.style.maxWidth = '';
        menu.querySelectorAll('[data-table-menu-dropdown]').forEach((node) => {
            node.hidden = true;
        });
        return;
    }

    if (editorWrap && menu.parentElement !== editorWrap) {
        editorWrap.appendChild(menu);
    }

    // Show first so scrollWidth is measurable, then position
    menu.style.display = 'block';

    if (editorWrap) {
        const wrapRect = editorWrap.getBoundingClientRect();
        const tableRect = wrapper.getBoundingClientRect();
        const menuWidth = menu.scrollWidth;
        const tableLeft = tableRect.left - wrapRect.left;
        const idealLeft = tableLeft + tableRect.width / 2 - menuWidth / 2;
        const clampedLeft = Math.max(0, Math.min(idealLeft, Math.max(0, wrapRect.width - menuWidth)));
        menu.style.position = 'absolute';
        menu.style.left = `${clampedLeft}px`;
        menu.style.top = `${Math.max(0, tableRect.bottom - wrapRect.top + 10)}px`;
        menu.style.width = '';
        menu.style.maxWidth = `${wrapRect.width}px`;
    }

    menu.querySelectorAll('[data-table-action="toggleHeaderRow"]').forEach((button) => {
        button.classList.toggle('is-active', Boolean(tableState.headerRow));
        button.setAttribute('aria-pressed', String(Boolean(tableState.headerRow)));
    });
    menu.querySelectorAll('[data-table-action="toggleHeaderColumn"]').forEach((button) => {
        button.classList.toggle('is-active', Boolean(tableState.headerColumn));
        button.setAttribute('aria-pressed', String(Boolean(tableState.headerColumn)));
    });
    menu.querySelectorAll('[data-table-action="numberColumn"]').forEach((button) => {
        button.classList.toggle('is-active', Boolean(tableState.numberedColumn));
        button.setAttribute('aria-pressed', String(Boolean(tableState.numberedColumn)));
    });
    if (optionsToggle) {
        const anyOptionActive = tableState.headerRow || tableState.headerColumn || tableState.numberedColumn;
        optionsToggle.classList.toggle('is-active', Boolean(anyOptionActive));
        optionsToggle.setAttribute('aria-expanded', optionsMenu?.hidden ? 'false' : 'true');
    }
    if (overflowToggle) {
        overflowToggle.setAttribute('aria-expanded', overflowMenu?.hidden ? 'false' : 'true');
    }
    if (optionsMenu?.hidden == null) {
        optionsMenu.hidden = true;
    }
    if (overflowMenu?.hidden == null) {
        overflowMenu.hidden = true;
    }
}

function bindDocsTableMenu(projectId) {
    const menu = document.getElementById('docs-table-menu');
    if (!menu) return;

    // Prevent the editor from losing focus/selection when clicking toolbar buttons
    if (!menu.dataset.mousedownBound) {
        menu.dataset.mousedownBound = 'true';
        menu.addEventListener('mousedown', (event) => {
            event.preventDefault();
        });
    }

    menu.querySelectorAll('[data-table-menu-toggle]').forEach((button) => {
        if (button.dataset.bound === 'true') return;
        button.dataset.bound = 'true';
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            const target = button.dataset.tableMenuToggle;
            const dropdown = menu.querySelector(`[data-table-menu-dropdown="${target}"]`);
            if (!dropdown) return;
            const nextHidden = !dropdown.hidden;
            menu.querySelectorAll('[data-table-menu-dropdown]').forEach((node) => {
                node.hidden = true;
            });
            dropdown.hidden = nextHidden;
            menu.querySelectorAll('[data-table-menu-toggle]').forEach((toggle) => {
                const key = toggle.dataset.tableMenuToggle;
                const related = menu.querySelector(`[data-table-menu-dropdown="${key}"]`);
                toggle.setAttribute('aria-expanded', related?.hidden ? 'false' : 'true');
            });
        });
    });

    menu.querySelectorAll('[data-table-action]').forEach((button) => {
        if (button.dataset.bound === 'true') return;
        button.dataset.bound = 'true';
        button.addEventListener('click', async () => {
            const action = button.dataset.tableAction;
            if (!action) return;

            if (action === 'copyTable') {
                copyDocsTableAsText();
                return;
            }

            runDocsEditorCommand(action);
            focusDocsEditor();
            updateDocsDirtyState(projectId);
            menu.querySelectorAll('[data-table-menu-dropdown]').forEach((node) => {
                node.hidden = true;
            });
            updateDocsTableMenuVisibility();
        });
    });

    if (!menu.dataset.outsideBound) {
        menu.dataset.outsideBound = 'true';
        document.addEventListener('click', (event) => {
            if (!menu.contains(event.target)) {
                menu.querySelectorAll('[data-table-menu-dropdown]').forEach((node) => {
                    node.hidden = true;
                });
                menu.querySelectorAll('[data-table-menu-toggle]').forEach((toggle) => {
                    toggle.setAttribute('aria-expanded', 'false');
                });
            }
        });
    }

    updateDocsTableMenuVisibility();
}

function getDocsRouteState() {
    const hash = String(window.location.hash || '').slice(1);
    const [page, queryString] = hash.split('?');
    if (page !== 'docs') {
        return {
            page,
            isDocsRoute: false,
            docId: '',
            slug: '',
            mode: 'view'
        };
    }

    const params = new URLSearchParams(queryString || '');
    // Legacy URLs may still carry ?project=X, but docs routing no longer uses it.
    return {
        page,
        isDocsRoute: true,
        docId: String(params.get('doc') || ''),
        slug: String(params.get('slug') || ''),
        publicToken: String(params.get('publicToken') || ''),
        width: params.get('width') === 'narrow' ? 'narrow' : (params.get('width') === 'wide' ? 'wide' : ''),
        mode: params.get('mode') === 'edit' ? 'edit' : 'view'
    };
}

/* ── (C) Deep-link snapshot recovery ──────────────────────────────────
   index.html <head> stores window.__initialDocsHash synchronously before
   any app code or history.replaceState runs. On iPad the hash-vs-items
   race can drop/clobber ?doc= before it is consumed; this parses the
   ORIGINAL hash the same way getDocsRouteState() parses the live one.
   Desktop-safe: identical to the live route on desktop. */
function getInitialDocsRouteDocId() {
    try {
        const hash = String(window.__initialDocsHash || '').replace(/^#/, '');
        const [page, queryString] = hash.split('?');
        if (page !== 'docs') return '';
        const params = new URLSearchParams(queryString || '');
        return String(params.get('doc') || '');
    } catch (_) {
        return '';
    }
}

/* ── (E) Last-viewed doc persistence ──────────────────────────────────
   On reload, WebKit (iPad) can lose the hash-vs-items race so the route
   docId isn't validated before the list loads → it falls back to the
   first doc. Persist the last-viewed doc so the cold-load path can
   restore it. docsState is a single global → one storage key is
   correct. sessionStorage = survives reload, clears on tab close (no
   stale cross-session surprise). Desktop-safe: only ever consulted when
   there is NO route docId (a real desktop deep-link/refresh has ?doc=). */
const DOCS_LAST_DOC_KEY = 'nautilus.docs.lastDocId';
function _persistLastDocId(docId, slug) {
    try {
        if (docId) {
            sessionStorage.setItem(DOCS_LAST_DOC_KEY, JSON.stringify({
                id: String(docId), slug: String(slug || '')
            }));
        }
    } catch (_) {}
}
function _readPersistedDocId() {
    try {
        const raw = sessionStorage.getItem(DOCS_LAST_DOC_KEY);
        if (!raw) return '';
        const o = JSON.parse(raw);
        return o && o.id ? String(o.id) : '';
    } catch (_) {
        return '';
    }
}

function isDocsPageActive() {
    return Boolean(document.getElementById('docs')?.classList.contains('active'));
}

function canSyncDocsRouteState() {
    const route = getDocsRouteState();
    return route.isDocsRoute || isDocsPageActive();
}

function buildProjectDocPublishedUrl(record) {
    const publicToken = String(record?.publicToken || '').trim();
    if (publicToken) {
        const params = new URLSearchParams();
        params.set('publicToken', publicToken);
        const slug = String(record?.publishedSlug || record?.slug || record?.title || '').trim();
        if (slug) {
            params.set('slug', slugifyProjectDocTitle(slug));
        }
        const widthMode = record?.widthMode === 'narrow' ? 'narrow' : 'wide';
        params.set('width', widthMode);
        return `${window.location.origin}${window.location.pathname}#docs?${params.toString()}`;
    }
    const params = new URLSearchParams();
    if (record?.id) {
        params.set('doc', String(record.id));
    }
    const slug = String(
        record?.slug
        || record?.publishedSlug
        || record?.title
        || ''
    ).trim();
    if (slug) {
        params.set('slug', record?.slug ? record.slug : slugifyProjectDocTitle(slug));
    }
    return `${window.location.origin}${window.location.pathname}#docs${params.toString() ? `?${params.toString()}` : ''}`;
}

function updatePublicDocsThemeToggle() {
    const button = document.getElementById('public-docs-theme-toggle');
    if (!button) return;
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    const text = button.querySelector('.public-docs-theme-toggle-text');
    const icon = button.querySelector('.public-docs-theme-toggle-icon');
    const label = isDark ? t('menu.lightMode') : t('menu.darkMode');
    if (text) text.textContent = label;
    if (icon) icon.textContent = isDark ? '☀️' : '🌙';
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
}

function bindPublicDocsWidthToggle() {
    const button = document.getElementById('public-docs-width-toggle');
    if (!button || button.dataset.bound === 'true') return;
    button.dataset.bound = 'true';
    updatePublicDocsWidthToggle();
    button.addEventListener('click', () => {
        const canvas = document.getElementById('docs-canvas-root');
        if (!canvas) return;
        const next = canvas.dataset.width === 'narrow' ? 'wide' : 'narrow';
        canvas.dataset.width = next;
        updatePublicDocsWidthToggle();
    });
}

function updatePublicDocsWidthToggle() {
    const button = document.getElementById('public-docs-width-toggle');
    if (!button) return;
    const canvas = document.getElementById('docs-canvas-root');
    const isNarrow = canvas?.dataset.width === 'narrow';
    const text = button.querySelector('.public-docs-theme-toggle-text');
    const icon = button.querySelector('.public-docs-theme-toggle-icon');
    const label = isNarrow ? t('docs.width.wide') : t('docs.width.narrow');
    if (text) text.textContent = label;
    if (icon) icon.textContent = isNarrow ? '↔' : '↕';
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
}

/* Serialises share-modal mutations (instant-apply, create, revoke). Each request
   bumps the generation and captures a token; a response whose token is no longer
   current is dropped, so a slow (or retried) apply can never re-publish and
   re-open the modal after the user revoked the link. */
let docsShareRequestGen = 0;

function setDocsShareBusy(busy) {
    document.querySelectorAll('#docs-share-expiry-pills .docs-share-expiry-pill')
        .forEach((p) => (p.disabled = busy));
    ['docs-share-expiry-apply', 'docs-share-primary-btn', 'docs-share-revoke-btn'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.disabled = busy;
    });
}

function bindDocsShareModal() {
    const modal = document.getElementById('docs-share-modal');
    if (!modal || modal.dataset.bound === 'true') return;
    modal.dataset.bound = 'true';
    const input = modal.querySelector('#docs-share-link-input');
    const inlineCopyBtn = modal.querySelector('#docs-share-inline-copy-btn');
    const primaryBtn = modal.querySelector('#docs-share-primary-btn');
    const revokeBtn = modal.querySelector('#docs-share-revoke-btn');
    const closeBtn = modal.querySelector('.docs-share-modal-close');
    const pillsWrap = modal.querySelector('#docs-share-expiry-pills');
    const applyBtn = modal.querySelector('#docs-share-expiry-apply');

    modal.addEventListener('click', (event) => {
        if (isBackdropClick(event, modal)) {
            closeModal('docs-share-modal');
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (modal.style.display === 'none') return;
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
        }
        closeDocsShareModal();
    }, true);

    closeBtn?.addEventListener('click', () => {
        closeModal('docs-share-modal');
    });

    const copyCurrentLink = async () => {
        const url = String(input?.value || '').trim();
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            if (inlineCopyBtn) {
                inlineCopyBtn.dataset.copied = 'true';
                inlineCopyBtn.setAttribute('aria-label', t('docs.shareModalCopyDone'));
                inlineCopyBtn.setAttribute('title', t('docs.shareModalCopyDone'));
            }
            showSuccessNotification(t('docs.shareModalCopied'));
            window.setTimeout(() => {
                if (inlineCopyBtn) {
                    delete inlineCopyBtn.dataset.copied;
                    inlineCopyBtn.setAttribute('aria-label', t('docs.shareModalCopy'));
                    inlineCopyBtn.setAttribute('title', t('docs.shareModalCopy'));
                }
            }, 1400);
        } catch (error) {
            showNotification(url, 'info');
        }
    };

    inlineCopyBtn?.addEventListener('click', async () => {
        await copyCurrentLink();
    });

    // Pills: CREATE selects (pending); MANAGE/EXPIRED apply instantly. 'Custom'
    // opens the date editor rather than applying, since it needs a date first.
    pillsWrap?.addEventListener('click', (event) => {
        const btn = event.target.closest('.docs-share-expiry-pill');
        if (!btn || btn.disabled) return;
        const key = btn.dataset.expiry;
        const mode = String(modal.dataset.mode || 'create');
        if (mode === 'create') {
            setDocsSharePill(key);
            toggleDocsShareCustom(key === 'custom');
            if (key === 'custom') document.getElementById('docs-share-expiry-date')?.focus();
            renderDocsShareExpiry();
        } else if (key === 'custom') {
            toggleDocsShareCustom(true);
            renderDocsShareExpiry();
            document.getElementById('docs-share-expiry-date')?.focus();
        } else {
            void applyDocsShareExpiry(key);
        }
    });

    applyBtn?.addEventListener('click', () => {
        if (String(modal.dataset.mode || 'create') === 'create') return; // hidden in create; safety
        void applyDocsShareExpiry('custom');
    });

    const dateInput = modal.querySelector('#docs-share-expiry-date');
    const timeInput = modal.querySelector('#docs-share-expiry-time');
    const onCustomInput = () => {
        const errorEl = document.getElementById('docs-share-expiry-error');
        if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
        if (String(modal.dataset.mode || 'create') === 'create') renderDocsShareExpiry();
    };
    dateInput?.addEventListener('input', onCustomInput);
    timeInput?.addEventListener('input', onCustomInput);

    primaryBtn?.addEventListener('click', () => { void createDocsShareLink(); });

    revokeBtn?.addEventListener('click', async () => {
        const projectId = modal.dataset.projectId || '';
        const docId = modal.dataset.docId || '';
        const activeDoc = getDocsRecordById(projectId, docId) || getCurrentDocsRecord(projectId);
        if (!activeDoc) {
            diagLog('SHARE revoke: no doc record (docId=' + docId + ') — aborting');
            return;
        }
        if (!activeDoc.isPublished) {
            // Stale modal state: the link is already gone. Treat as done instead
            // of returning silently and leaving a dead-looking button.
            diagLog('SHARE revoke: already unpublished — closing');
            refreshDocsSurfaces(projectId);
            closeDocsShareModal();
            showSuccessNotification(t('docs.revokeLinkSuccess'));
            return;
        }
        diagLog('SHARE revoke: request doc=' + activeDoc.id);
        const gen = ++docsShareRequestGen; // invalidates any in-flight expiry apply
        setDocsShareBusy(true);
        try {
            const response = await publishProjectDoc(activeDoc.id, { published: false });
            if (gen !== docsShareRequestGen) { diagLog('SHARE revoke: stale response dropped'); return; }
            diagLog('SHARE revoke: ok');
            mergeDocsRecord(projectId, response?.item || activeDoc);
            refreshDocsSurfaces(projectId);
            closeDocsShareModal();
            showSuccessNotification(t('docs.revokeLinkSuccess'));
        } catch (error) {
            diagLog('SHARE revoke: FAILED status=' + (error?.status ?? 'network') + ' ' + (error?.message || error));
            if (gen !== docsShareRequestGen) return;
            showErrorNotification(error?.status === 401
                ? t('docs.shareSessionExpired')
                : t('docs.revokeLinkError'));
        } finally {
            if (gen === docsShareRequestGen) setDocsShareBusy(false);
        }
    });

    input?.addEventListener('focus', () => input.select());
}

/** Initialise flatpickr on the share-expiry date input and wire native time picker */
function initDocsShareExpiryPickers() {
    const dateInput = document.getElementById('docs-share-expiry-date');
    const datePickerBtn = document.getElementById('docs-share-expiry-date-picker-btn');
    const timeInput = document.getElementById('docs-share-expiry-time');
    const timePickerBtn = document.getElementById('docs-share-expiry-time-picker-btn');
    const timeNative = document.getElementById('docs-share-expiry-time-native');

    // ── Date: flatpickr (appended to body, positioned via onOpen) ──
    if (dateInput && !dateInput._flatpickr && typeof window.flatpickr === 'function') {
        const fp = window.flatpickr(dateInput, {
            ...datePickerOptions(getFlatpickrLocale(), { altInput: false }),
            minDate: 'today',
            positionElement: dateInput,
            onReady(_, __, fpInstance) {
                addShareExpiryDateMask(dateInput, fpInstance);
            },
            onOpen(_, __, fpInstance) {
                // Position the calendar relative to the date input inside the modal
                const rect = dateInput.getBoundingClientRect();
                const cal = fpInstance.calendarContainer;
                if (cal) {
                    cal.style.position = 'fixed';
                    cal.style.zIndex = '100010';
                    cal.style.top = (rect.bottom + 4) + 'px';
                    cal.style.left = rect.left + 'px';
                }
            }
        });
        if (datePickerBtn) {
            datePickerBtn.addEventListener('click', () => fp.open());
        }
    }

    // ── Time: HH:MM mask ──
    if (timeInput && !timeInput.dataset.masked) {
        timeInput.dataset.masked = 'true';
        let timeClearedOnFirstKey = false;

        timeInput.addEventListener('keydown', (e) => {
            // Clear entire value on first digit key if field is full (HH:MM)
            if (!timeClearedOnFirstKey && timeInput.value.match(/^\d{2}:\d{2}$/) && e.key.length === 1 && /\d/.test(e.key)) {
                timeInput.value = '';
                timeClearedOnFirstKey = true;
            }
            if (e.key === 'Backspace' && timeInput.selectionStart === 0 && timeInput.selectionEnd === 0) {
                timeInput.value = '';
                timeClearedOnFirstKey = true;
                e.preventDefault();
            }
        });

        timeInput.addEventListener('input', (e) => {
            let numbers = e.target.value.replace(/\D/g, '');
            let formatted = '';
            if (numbers.length >= 1) {
                let hh = numbers.substring(0, 2);
                if (parseInt(hh, 10) > 23) hh = '23';
                formatted = hh;
            }
            if (numbers.length >= 3) {
                let mm = numbers.substring(2, 4);
                if (parseInt(mm, 10) > 59) mm = '59';
                formatted += ':' + mm;
            }
            if (e.target.value !== formatted) e.target.value = formatted;
        });

        timeInput.addEventListener('keypress', (e) => {
            const ch = String.fromCharCode(e.which);
            if (!/[\d:]/.test(ch) && e.which !== 8 && e.which !== 46) e.preventDefault();
        });

        timeInput.addEventListener('blur', () => { timeClearedOnFirstKey = false; });
    }

    // ── Time: custom picker portal ──
    if (timePickerBtn && !timePickerBtn.dataset.bound) {
        timePickerBtn.dataset.bound = 'true';
        timePickerBtn.addEventListener('click', () => {
            openShareExpiryTimePicker(timePickerBtn, timeInput);
        });
    }
}

/* ── Custom Time Picker Portal for Share Expiry ── */
let shareExpiryTimePortalEl = null;

function openShareExpiryTimePicker(anchorBtn, timeInput) {
    if (shareExpiryTimePortalEl) {
        closeShareExpiryTimePicker();
        return;
    }

    const currentValue = String(timeInput?.value || '23:59');
    const [curHH, curMM] = currentValue.split(':').map(Number);

    const portal = document.createElement('div');
    portal.className = 'share-expiry-time-portal';
    shareExpiryTimePortalEl = portal;

    // Build hours column (00–23)
    const hoursCol = document.createElement('div');
    hoursCol.className = 'share-expiry-time-col';
    for (let h = 0; h < 24; h++) {
        const opt = document.createElement('div');
        opt.className = 'share-expiry-time-option' + (h === curHH ? ' active' : '');
        opt.textContent = String(h).padStart(2, '0');
        opt.dataset.value = h;
        opt.dataset.type = 'hour';
        hoursCol.appendChild(opt);
    }

    // Build minutes column (00, 05, 10, ... 55)
    const minsCol = document.createElement('div');
    minsCol.className = 'share-expiry-time-col';
    for (let m = 0; m < 60; m += 5) {
        const opt = document.createElement('div');
        // Find closest 5-min slot for current value
        const closest5 = Math.round(curMM / 5) * 5;
        opt.className = 'share-expiry-time-option' + (m === (closest5 >= 60 ? 55 : closest5) ? ' active' : '');
        opt.textContent = String(m).padStart(2, '0');
        opt.dataset.value = m;
        opt.dataset.type = 'minute';
        minsCol.appendChild(opt);
    }

    const colsWrap = document.createElement('div');
    colsWrap.className = 'share-expiry-time-cols';
    const hLabel = document.createElement('div');
    hLabel.className = 'share-expiry-time-col-label';
    hLabel.textContent = t('docs.shareExpiryTime') || 'Time';
    const hWrap = document.createElement('div');
    hWrap.className = 'share-expiry-time-col-wrap';
    hWrap.appendChild(hLabel);
    hWrap.appendChild(hoursCol);
    const mLabel = document.createElement('div');
    mLabel.className = 'share-expiry-time-col-label';
    mLabel.textContent = t('docs.shareExpiryMinutes');
    const mWrap = document.createElement('div');
    mWrap.className = 'share-expiry-time-col-wrap';
    mWrap.appendChild(mLabel);
    mWrap.appendChild(minsCol);
    colsWrap.appendChild(hWrap);
    colsWrap.appendChild(mWrap);
    portal.appendChild(colsWrap);

    document.body.appendChild(portal);

    // Position relative to anchor
    const rect = anchorBtn.getBoundingClientRect();
    portal.style.position = 'fixed';
    portal.style.zIndex = '100010';
    portal.style.top = (rect.bottom + 4) + 'px';
    // Right-align to the button so it doesn't overflow right
    portal.style.right = (window.innerWidth - rect.right) + 'px';

    // Scroll active items into view
    requestAnimationFrame(() => {
        const activeH = hoursCol.querySelector('.active');
        const activeM = minsCol.querySelector('.active');
        if (activeH) activeH.scrollIntoView({ block: 'center', behavior: 'instant' });
        if (activeM) activeM.scrollIntoView({ block: 'center', behavior: 'instant' });
    });

    // Click handler for selection
    portal.addEventListener('click', (e) => {
        const opt = e.target.closest('.share-expiry-time-option');
        if (!opt) return;
        const col = opt.closest('.share-expiry-time-col');
        if (col) {
            col.querySelectorAll('.share-expiry-time-option').forEach(el => el.classList.remove('active'));
            opt.classList.add('active');
        }
        // Update the input
        const selectedH = hoursCol.querySelector('.active');
        const selectedM = minsCol.querySelector('.active');
        const hh = String(selectedH?.dataset.value || '23').padStart(2, '0');
        const mm = String(selectedM?.dataset.value || '0').padStart(2, '0');
        if (timeInput) timeInput.value = hh + ':' + mm;
    });

    // Close on outside click
    setTimeout(() => {
        document.addEventListener('click', handleShareExpiryTimeOutsideClick, true);
    }, 0);
}

function handleShareExpiryTimeOutsideClick(e) {
    if (shareExpiryTimePortalEl && !shareExpiryTimePortalEl.contains(e.target) && !e.target.closest('#docs-share-expiry-time-picker-btn')) {
        closeShareExpiryTimePicker();
    }
}

function closeShareExpiryTimePicker() {
    if (shareExpiryTimePortalEl) {
        shareExpiryTimePortalEl.remove();
        shareExpiryTimePortalEl = null;
    }
    document.removeEventListener('click', handleShareExpiryTimeOutsideClick, true);
}

/** Shared date typing mask for the share-expiry date input (see src/utils/datePicker.js). */
function addShareExpiryDateMask(input, fpInstance) {
    attachDateMask(input, {
        onComplete(iso) {
            if (!fpInstance) return;
            try { fpInstance.__suppressChange = true; fpInstance.setDate(new Date(`${iso}T00:00:00`), false); setTimeout(() => (fpInstance.__suppressChange = false), 0); } catch {}
        }
    });
}

/* ── Share-expiry pill helpers ──────────────────────────────────────────
   The expiration control is a pill group (24h / 7 days / 30 days / Never /
   Custom). In CREATE mode a pill is a pending selection; in MANAGE/EXPIRED
   mode a preset pill applies instantly. `aria-pressed` marks the current one
   so the user always sees which option is live — status is displayed, the
   URL never changes, and there is no "unsaved" state. */

/** Reverse-match a stored expiry to a preset key — used only to highlight the current pill. */
function matchDocsExpiryPreset(linkExpiresAt) {
    if (!linkExpiresAt) return 'never';
    const ms = new Date(linkExpiresAt).getTime() - Date.now();
    if (Number.isNaN(ms) || ms <= 0) return null;
    const DAY = 24 * 60 * 60 * 1000;
    if (Math.abs(ms - DAY) < 2 * 60 * 60 * 1000) return '1d';
    if (Math.abs(ms - 7 * DAY) < DAY) return '1w';
    if (Math.abs(ms - 30 * DAY) < 2 * DAY) return '1m';
    return 'custom';
}

/** The pill currently marked selected (CREATE mode); defaults to 7 days. */
function getDocsShareSelectedPillKey() {
    const active = document.querySelector('#docs-share-expiry-pills .docs-share-expiry-pill[aria-pressed="true"]');
    return active?.dataset.expiry || '1w';
}

/** Mark exactly one pill as selected. */
function setDocsSharePill(key) {
    document.querySelectorAll('#docs-share-expiry-pills .docs-share-expiry-pill').forEach((p) => {
        p.setAttribute('aria-pressed', String(p.dataset.expiry === key));
    });
}

/** Show/hide the custom date+time editor, pre-filling a sensible default date. */
function toggleDocsShareCustom(show) {
    const wrap = document.getElementById('docs-share-expiry-custom');
    if (!wrap) return;
    wrap.style.display = show ? 'block' : 'none';
    if (!show) return;
    const dateInput = document.getElementById('docs-share-expiry-date');
    const timeInput = document.getElementById('docs-share-expiry-time');
    if (dateInput && !dateInput.value) {
        const d = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
        dateInput.value = String(d.getDate()).padStart(2, '0') + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + d.getFullYear();
    }
    if (timeInput && !timeInput.value) timeInput.value = '23:59';
    initDocsShareExpiryPickers();
}

/** Build the publish payload for a pill key ('custom' reads the date editor). */
function getDocsSharePayloadForKey(key, options = {}) {
    if (key === 'custom') {
        const candidate = getDocsShareExpiryCandidate({ showError: options.showError === true });
        if (!candidate) return null;
        return { customExpiry: candidate.date.toISOString() };
    }
    return { expiryDuration: key };
}

/** Flash the address cell + status line so a change lands where the user copies. */
function flashDocsShareCells() {
    ['docs-share-link-input', 'docs-share-status-line'].forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.classList.remove('docs-share-flash');
        void el.offsetWidth; // restart the animation
        el.classList.add('docs-share-flash');
    });
}

/** Parse the custom date+time editor into a future Date, or null (with optional error). */
function getDocsShareExpiryCandidate(options = {}) {
    const showError = options.showError === true;
    const errorEl = document.getElementById('docs-share-expiry-error');
    if (errorEl && showError) { errorEl.style.display = 'none'; errorEl.textContent = ''; }

    const dateInput = document.getElementById('docs-share-expiry-date');
    const timeInput = document.getElementById('docs-share-expiry-time');
    const dateText = String(dateInput?.value || '').trim();
    const timeText = String(timeInput?.value || '').trim();

    // Shared parser (dd/mm/yyyy, '-' accepted). It used to require '/', but the field was
    // masked with '-', so a typed or picked expiry date never parsed.
    const dateIso = parseDisplayDate(dateText);
    if (!dateIso) {
        if (errorEl && showError) { errorEl.textContent = t('docs.shareExpiryInvalid'); errorEl.style.display = 'block'; }
        return null;
    }
    if (!/^\d{2}:\d{2}$/.test(timeText)) {
        if (errorEl && showError) { errorEl.textContent = t('docs.shareExpiryInvalid'); errorEl.style.display = 'block'; }
        return null;
    }

    const [year, month, day] = dateIso.split('-');
    const [hours, minutes] = timeText.split(':');

    // Build date in user's configured timezone
    const tz = settings?.emailNotificationTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    let candidate;
    try {
        // Create a date string that we can parse with timezone context
        const localStr = `${year}-${month}-${day}T${hours}:${minutes}:00`;
        // Use the formatter to figure out the UTC offset for this timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        });
        // Parse as local time, then adjust: create a Date from the local string
        candidate = new Date(localStr);
        // Calculate the offset difference between local browser TZ and the user's settings TZ
        const nowInTz = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
        const nowLocal = new Date(new Date().toLocaleString('en-US'));
        // Round to the minute: the locale-string round-trip loses sub-minute
        // precision, which otherwise leaks noise into the expiry instant.
        const tzDiffMs = Math.round((nowLocal.getTime() - nowInTz.getTime()) / 60000) * 60000;
        candidate = new Date(candidate.getTime() + tzDiffMs);
    } catch {
        candidate = new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes));
    }

    if (Number.isNaN(candidate.getTime()) || candidate.getTime() <= Date.now()) {
        if (errorEl && showError) { errorEl.textContent = t('docs.shareExpiryInvalid'); errorEl.style.display = 'block'; }
        return null;
    }

    // Validate parsed date components match input (catch invalid dates like 31/02)
    const checkDate = new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
    if (
        checkDate.getFullYear() !== Number(year) ||
        checkDate.getMonth() + 1 !== Number(month) ||
        checkDate.getDate() !== Number(day)
    ) {
        if (errorEl && showError) { errorEl.textContent = t('docs.shareExpiryInvalid'); errorEl.style.display = 'block'; }
        return null;
    }

    return { mode: 'custom', date: candidate };
}

function formatDocsShareExpiryDateTime(dateValue) {
    if (!dateValue) return '';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const locale = getLocale();
    const timeZone = settings?.emailNotificationTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    try {
        return date.toLocaleString(locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
            ...(timeZone ? { timeZone } : {})
        });
    } catch {
        return date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
    }
}

/* Single render pass for the expiry section: CREATE previews the pending
   selection; MANAGE/EXPIRED highlight the live pill and write the status line
   beneath the link (the status is the feedback — the URL itself never changes). */
function renderDocsShareExpiry() {
    const modal = document.getElementById('docs-share-modal');
    if (!modal) return;
    const mode = String(modal.dataset.mode || 'create');
    const preview = document.getElementById('docs-share-expiry-summary');
    const statusLine = document.getElementById('docs-share-status-line');
    const recordExpiryAt = modal.dataset.recordExpiryAt || '';
    const customVisible = document.getElementById('docs-share-expiry-custom')?.style.display === 'block';

    if (mode === 'create') {
        if (!preview) return;
        const key = getDocsShareSelectedPillKey();
        if (key === 'never') {
            preview.textContent = t('docs.shareCreatePreviewNever');
        } else if (key === 'custom') {
            const candidate = getDocsShareExpiryCandidate({ showError: false });
            preview.textContent = candidate
                ? t('docs.shareCreatePreview', { date: formatDocsShareExpiryDateTime(candidate.date) })
                : t('docs.shareExpiryChooseFuture');
        } else {
            const dur = key === '1d' ? 24 * 60 * 60 * 1000 : key === '1w' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000;
            preview.textContent = t('docs.shareCreatePreview', { date: formatDocsShareExpiryDateTime(new Date(Date.now() + dur)) });
        }
        return;
    }

    // MANAGE / EXPIRED — highlight the live pill (or 'custom' while its editor is open)
    const currentKey = customVisible ? 'custom' : (mode === 'expired' ? null : matchDocsExpiryPreset(recordExpiryAt || null));
    document.querySelectorAll('#docs-share-expiry-pills .docs-share-expiry-pill').forEach((p) => {
        p.setAttribute('aria-pressed', String(currentKey !== null && p.dataset.expiry === currentKey));
    });

    if (!statusLine) return;
    if (mode === 'expired') {
        statusLine.className = 'docs-share-status-line docs-share-status-line--bad';
        statusLine.textContent = t('docs.shareStatusExpired', { date: formatDocsShareExpiryDateTime(recordExpiryAt || null) });
    } else if (recordExpiryAt) {
        statusLine.className = 'docs-share-status-line';
        statusLine.textContent = t('docs.shareStatusExpiresOn', { date: formatDocsShareExpiryDateTime(recordExpiryAt) });
    } else {
        statusLine.className = 'docs-share-status-line';
        statusLine.textContent = t('docs.shareStatusNoExpiry');
    }
}

/* Commit a new expiry on the live link (MANAGE/EXPIRED). Instant apply: no
   save button. On success the modal re-renders from the fresh record and the
   address cell + status line flash so the change is unmissable. */
async function applyDocsShareExpiry(key) {
    const modal = document.getElementById('docs-share-modal');
    if (!modal) return;
    const projectId = modal.dataset.projectId || '';
    const docId = modal.dataset.docId || '';
    const wasExpired = String(modal.dataset.mode || '') === 'expired';
    let activeDoc = getDocsRecordById(projectId, docId) || getCurrentDocsRecord(projectId);
    if (!activeDoc?.id) return;

    const payload = getDocsSharePayloadForKey(key, { showError: true });
    if (!payload) return;

    const gen = ++docsShareRequestGen;
    setDocsShareBusy(true);
    try {
        const response = await publishProjectDoc(activeDoc.id, {
            published: true,
            publishedSlug: activeDoc.publishedSlug || activeDoc.slug || slugifyProjectDocTitle(activeDoc.title),
            widthMode: activeDoc.widthMode === 'narrow' ? 'narrow' : 'wide',
            ...payload
        });
        if (gen !== docsShareRequestGen) { diagLog('SHARE expiry apply: stale response dropped'); return; } // superseded (e.g. revoke)
        diagLog('SHARE expiry apply: ok key=' + key);
        activeDoc = mergeDocsRecord(projectId, response?.item || activeDoc);
        refreshDocsSurfaces(projectId);
        openDocsShareModal(projectId, activeDoc);
        flashDocsShareCells();
        showSuccessNotification(wasExpired ? t('docs.shareLinkReactivated') : t('docs.shareSettingsUpdated'));
    } catch (error) {
        diagLog('SHARE expiry apply: FAILED status=' + (error?.status ?? 'network') + ' ' + (error?.message || error));
        if (gen !== docsShareRequestGen) return;
        showErrorNotification(error?.status === 401 ? t('docs.shareSessionExpired') : t('docs.shareLinkError'));
    } finally {
        if (gen === docsShareRequestGen) setDocsShareBusy(false);
    }
}

/* Create the public link (CREATE mode primary action). */
async function createDocsShareLink() {
    const modal = document.getElementById('docs-share-modal');
    if (!modal) return;
    const projectId = modal.dataset.projectId || '';
    const docId = modal.dataset.docId || '';
    let activeDoc = getDocsRecordById(projectId, docId) || getCurrentDocsRecord(projectId);
    if (!activeDoc?.id) return;

    const key = getDocsShareSelectedPillKey();
    const payload = getDocsSharePayloadForKey(key, { showError: true });
    if (!payload) return;

    const currentWidthMode = activeDoc.widthMode === 'narrow' ? 'narrow' : 'wide';

    const gen = ++docsShareRequestGen;
    setDocsShareBusy(true);
    try {
        const response = await publishProjectDoc(activeDoc.id, {
            published: true,
            publishedSlug: activeDoc.publishedSlug || activeDoc.slug || slugifyProjectDocTitle(activeDoc.title),
            widthMode: currentWidthMode,
            ...payload
        });
        if (gen !== docsShareRequestGen) { diagLog('SHARE create: stale response dropped'); return; }
        diagLog('SHARE create: ok doc=' + activeDoc.id);
        activeDoc = mergeDocsRecord(projectId, response?.item || activeDoc);
        refreshDocsSurfaces(projectId);
        openDocsShareModal(projectId, activeDoc);
        flashDocsShareCells();
        showSuccessNotification(t('docs.shareLinkCreated'));
    } catch (error) {
        diagLog('SHARE create: FAILED status=' + (error?.status ?? 'network') + ' ' + (error?.message || error));
        if (gen !== docsShareRequestGen) return;
        showErrorNotification(error?.status === 401 ? t('docs.shareSessionExpired') : t('docs.shareLinkError'));
    } finally {
        if (gen === docsShareRequestGen) setDocsShareBusy(false);
    }
}

function closeDocsShareModal() {
    closeShareExpiryTimePicker();
    const dateInput = document.getElementById('docs-share-expiry-date');
    if (dateInput?._flatpickr) dateInput._flatpickr.close();
    const modal = document.getElementById('docs-share-modal');
    if (!modal) return;
    modal.dataset.mode = '';
    modal.dataset.projectId = '';
    modal.dataset.docId = '';
    modal.dataset.recordExpiryAt = '';
    modal.classList.remove('active');
    modal.style.display = 'none';
    diagLog('SHARE modal closed');
}
window.closeDocsShareModal = closeDocsShareModal;

/* Configure per-state visibility for the three modal states:
   create (no link yet), manage (live link), expired (dead link). */
function setDocsShareModalMode(mode, record) {
    const modal = document.getElementById('docs-share-modal');
    const linkContent = document.querySelector('.docs-share-modal-link-content');
    const input = document.getElementById('docs-share-link-input');
    const inlineCopyBtn = document.getElementById('docs-share-inline-copy-btn');
    const primaryBtn = document.getElementById('docs-share-primary-btn');
    const revokeBtn = document.getElementById('docs-share-revoke-btn');
    const createSub = document.getElementById('docs-share-create-sub');
    const heading = document.getElementById('docs-share-expiry-heading');
    const hint = document.getElementById('docs-share-expiry-hint');
    const preview = document.getElementById('docs-share-expiry-summary');
    const applyBtn = document.getElementById('docs-share-expiry-apply');
    const chipActive = document.getElementById('docs-share-chip-active');
    const chipExpired = document.getElementById('docs-share-chip-expired');
    if (!modal || !input || !primaryBtn || !revokeBtn) return;

    const isManage = mode === 'manage';
    const isExpired = mode === 'expired';
    const isPublished = isManage || isExpired;

    modal.dataset.mode = mode;
    modal.classList.toggle('docs-share-modal--create', mode === 'create');
    modal.classList.toggle('docs-share-modal--manage', isManage);
    modal.classList.toggle('docs-share-modal--expired', isExpired);

    input.value = isPublished ? buildProjectDocPublishedUrl(record) : '';
    if (linkContent) linkContent.style.display = isPublished ? '' : 'none';

    if (inlineCopyBtn) {
        inlineCopyBtn.removeAttribute('data-copied');
        inlineCopyBtn.setAttribute('aria-label', t('docs.shareModalCopy'));
        inlineCopyBtn.setAttribute('title', t('docs.shareModalCopy'));
        inlineCopyBtn.disabled = isExpired; // a dead link must not be copyable
    }

    if (createSub) createSub.hidden = mode !== 'create';
    if (hint) hint.hidden = !isPublished;
    if (preview) preview.hidden = isPublished; // live preview is CREATE-only

    if (chipActive) chipActive.hidden = !isManage;
    if (chipExpired) chipExpired.hidden = !isExpired;

    if (heading) {
        const key = mode === 'create'
            ? 'docs.shareExpiryLabel'
            : (isExpired ? 'docs.shareReactivateHeading' : 'docs.shareChangeExpiry');
        heading.textContent = t(key);
        heading.setAttribute('data-i18n', key);
    }

    // CREATE → "Create link" primary; MANAGE/EXPIRED → revoke only (expiry is instant-apply)
    primaryBtn.textContent = t('docs.shareCreateLink');
    primaryBtn.setAttribute('data-i18n', 'docs.shareCreateLink');
    primaryBtn.disabled = false;
    primaryBtn.hidden = mode !== 'create';
    revokeBtn.hidden = !isPublished;
    if (applyBtn) applyBtn.hidden = !isPublished; // custom "Apply date" only commits in manage/expired

    // Re-enable pills (they may have been disabled mid-apply)
    document.querySelectorAll('#docs-share-expiry-pills .docs-share-expiry-pill').forEach((p) => (p.disabled = false));
}

function openDocsShareModal(projectId, record) {
    const modal = document.getElementById('docs-share-modal');
    if (!modal) return;
    bindDocsShareModal();

    const isPublished = record?.isPublished === true;
    const linkExpiresAt = record?.linkExpiresAt || null;
    const isExpired = isPublished && linkExpiresAt && new Date(linkExpiresAt).getTime() <= Date.now();
    const mode = !isPublished ? 'create' : (isExpired ? 'expired' : 'manage');

    modal.dataset.projectId = projectId ? String(projectId) : '';
    modal.dataset.docId = record?.id ? String(record.id) : '';
    // Stored for the status line in both manage (future) and expired (past) states.
    // Pill highlighting ignores past dates (matchDocsExpiryPreset → null), and the
    // expired branch of renderDocsShareExpiry forces no pill selected.
    modal.dataset.recordExpiryAt = isPublished && linkExpiresAt ? linkExpiresAt : '';

    setDocsShareModalMode(mode, record);

    // Reset the custom editor
    const dateInput = document.getElementById('docs-share-expiry-date');
    const timeInput = document.getElementById('docs-share-expiry-time');
    const errorEl = document.getElementById('docs-share-expiry-error');
    if (dateInput) dateInput.value = '';
    if (timeInput) timeInput.value = '';
    if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }
    toggleDocsShareCustom(false);

    if (mode === 'create') {
        setDocsSharePill('1w'); // default: 7 days
    } else if (mode === 'manage' && matchDocsExpiryPreset(linkExpiresAt) === 'custom' && linkExpiresAt) {
        // Live link with a bespoke expiry: pre-fill the editor's fields so tapping
        // "Custom" reveals the exact date, but keep the editor COLLAPSED. The status
        // line already states the precise expiry, and auto-expanding a tall editor
        // pushed the action footer (Revoke) off-screen on mobile. The 'custom' pill
        // still highlights via renderDocsShareExpiry() → matchDocsExpiryPreset().
        const d = new Date(linkExpiresAt);
        if (dateInput) dateInput.value = String(d.getDate()).padStart(2, '0') + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + d.getFullYear();
        if (timeInput) timeInput.value = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
    }
    // expired: no pill pre-selected — the pills are renewal actions

    renderDocsShareExpiry();
    // 'active' class, not just inline display: body:has(.modal.active) is what
    // locks background scroll (style.css). Without it the docs page behind
    // stays scrollable, so on iOS a backdrop tap starts a background scroll
    // and the tap's click is never synthesized — backdrop-close goes dead.
    modal.classList.add('active');
    modal.style.display = 'flex';
    diagLog('SHARE modal open mode=' + mode + ' doc=' + (record?.id || '') + ' published=' + isPublished);

    // Silently repair file visibility for already-published pages. This fixes images missing
    // on public links for pages that were duplicated before the ownership fix.
    if (isPublished && record?.id) {
        repairDocPageFiles(String(record.id)).catch(() => null);
    }
}

function syncDocsRouteState({ docId = '', slug = '', mode = 'view' } = {}) {
    if (!canSyncDocsRouteState()) return;
    if (getDocsRouteState().publicToken) return;

    // (C) Never clobber an unconsumed deep-link during the iPad skeleton
    // phase. If we would write a docId-less #docs while the list is still
    // loading and the original URL carried a ?doc=, keep the deep-link so
    // ensureDocsListLoaded() can still recover it. Desktop-safe: the list
    // loads before any such call there, so this guard is inert on desktop.
    if (!docId && getInitialDocsRouteDocId() && getDocsState()?.loaded !== true) {
        return;
    }

    const params = new URLSearchParams();
    if (docId) {
        params.set('doc', String(docId));
    }
    if (slug) {
        params.set('slug', String(slug));
    }
    if (mode === 'edit') {
        params.set('mode', 'edit');
    }

    // (E) Remember the last-viewed doc so a cold reload can restore it
    // when the hash/items race drops the route docId. Write-only side
    // effect; harmless on desktop (only read when route has no docId).
    if (docId) _persistLastDocId(docId, slug);

    const nextHash = `#docs${params.toString() ? `?${params.toString()}` : ''}`;
    const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
    if (`#${window.location.hash.slice(1)}` === nextHash) return;
    safeReplaceState(null, '', nextUrl);
}

function restoreCurrentDocsRoute() {
    const current = getCurrentDocsRecord();
    syncDocsRouteState({
        docId: current?.id || '',
        slug: current?.slug || slugifyProjectDocTitle(current?.title || ''),
        mode: getDocsRouteState().mode
    });
}

function applyDocsRouteState() {
    const route = getDocsRouteState();
    if (!route.isDocsRoute || route.publicToken) return route;

    const state = getDocsState();
    if (route.docId) {
        // Only trust the route docId if items haven't loaded yet (will be validated after load),
        // or if the doc actually exists in the already-loaded list.
        const isKnown = !state.loaded || state.items.some((item) => item.id === route.docId);
        if (isKnown) state.currentDocId = route.docId;
    }

    return route;
}

function normalizeDocsRecord(projectId, record, options = {}) {
    const normalized = {
        ...record,
        title: String(record.title ?? ''),
        slug: String(record.slug || slugifyProjectDocTitle(record.title || '')),
        icon: String(record.icon || ''),
        parentId: record.parentId ? String(record.parentId) : '',
        widthMode: record.widthMode === 'narrow' ? 'narrow' : 'wide',
        publicToken: String(record.publicToken || ''),
        publishedSlug: String(record.publishedSlug || ''),
        contentCorrupted: record.contentCorrupted === true
    };
    normalized.contentJson = record.contentJson && typeof record.contentJson === 'object'
        ? rewriteDocsImageNodes(record.contentJson, options.publicToken ? { publicToken: options.publicToken } : {})
        : EMPTY_PROJECT_DOC_CONTENT;
    normalized.publishedUrl = buildProjectDocPublishedUrl(normalized);
    return normalized;
}

function mergeDocsRecord(projectId, record) {
    const state = getDocsState(projectId);
    const normalized = normalizeDocsRecord(projectId, record);

    const existingIndex = state.items.findIndex((item) => item.id === normalized.id);
    if (existingIndex >= 0) {
        state.items[existingIndex] = {
            ...state.items[existingIndex],
            ...normalized
        };
    } else {
        state.items.push(normalized);
    }

    state.items = sortProjectDocsItems(state.items);
    return state.items.find((item) => item.id === normalized.id) || normalized;
}

function getCurrentDocsRecord(projectId) {
    const state = getDocsState(projectId);
    if (!state.currentDocId) return null;
    return state.items.find((item) => item.id === state.currentDocId) || null;
}

function getDocsRecordById(projectId, docId) {
    if (!docId) return null;
    const state = getDocsState(projectId);
    return state.items.find((item) => String(item.id) === String(docId)) || null;
}

function getEditorBoundDocsRecord(projectId) {
    const state = getDocsState(projectId);
    return getDocsRecordById(projectId, state.editorBoundDocId || state.currentDocId);
}

function serializeProjectDocSnapshot(title, contentJson) {
    return JSON.stringify({
        title: String(title || '').trim(),
        contentJson: dehydrateDocsImageNodes(
            contentJson && typeof contentJson === 'object'
                ? contentJson
                : EMPTY_PROJECT_DOC_CONTENT
        ),
        icon: '',
        parentId: '',
        widthMode: 'wide'
    });
}

function serializeDocsDraftSnapshot(draft) {
    return JSON.stringify({
        title: String(draft?.title || '').trim(),
        contentJson: dehydrateDocsImageNodes(draft?.contentJson || EMPTY_PROJECT_DOC_CONTENT),
        icon: String(draft?.icon || ''),
        parentId: draft?.parentId ? String(draft.parentId) : '',
        widthMode: draft?.widthMode === 'narrow' ? 'narrow' : 'wide'
    });
}

function getDocsContentLength(contentJson) {
    try {
        return JSON.stringify(contentJson || EMPTY_PROJECT_DOC_CONTENT).length;
    } catch {
        return 0;
    }
}

function doesDocsBaselineMatchActiveSession(projectId) {
    const state = getDocsState(projectId);
    return Boolean(state.editorBoundDocId)
        && Boolean(state.baselineDocId)
        && String(state.baselineDocId) === String(state.editorBoundDocId)
        && Number(state.baselineGeneration || 0) === Number(state.editorGeneration || 0);
}

async function logDocsSaveEvent(projectId, payload = {}) {
    const docId = payload.docId || payload.doc_id || getDocsState(projectId).editorBoundDocId || getDocsState(projectId).currentDocId || '';
    if (!docId) return;
    try {
        await recordDocSaveEvent({
            docId: String(docId),
            source: String(payload.source || 'docs_client'),
            origin: String(payload.origin || 'client'),
            oldContentLen: Number(payload.oldContentLen) || 0,
            newContentLen: Number(payload.newContentLen) || 0,
            editorGeneration: Number(payload.editorGeneration ?? getDocsState(projectId).editorGeneration) || 0,
            rejected: payload.rejected === true,
            rejectionReason: payload.rejectionReason ? String(payload.rejectionReason) : '',
            actorType: payload.actorType ? String(payload.actorType) : '',
            actorLabel: payload.actorLabel ? String(payload.actorLabel) : '',
        });
    } catch (error) {
        console.warn('[docs] Failed to record save event', error);
    }
}

function setDocsBaseline(projectId, record, options = {}) {
    const state = getDocsState(projectId);
    state.baselineSnapshot = serializeDocsDraftSnapshot(record || {});
    state.baselineDocId = record?.id ? String(record.id) : null;
    state.baselineGeneration = Number(options.generation ?? state.editorGeneration ?? 0) || 0;
    state.dirty = false;
    state.status = 'saved';
    state.error = '';
    state.conflict = null;
    state.lastBaselineMismatchEventKey = '';
}

function hydrateDocsEditor(projectId, docId, contentJson) {
    const state = getDocsState(projectId);
    state.hydrating = true;
    try {
        setDocsEditorContent(contentJson || EMPTY_PROJECT_DOC_CONTENT);
    } finally {
        state.hydrating = false;
        state.editorGeneration = Number(state.editorGeneration || 0) + 1;
        state.editorBoundDocId = docId ? String(docId) : null;
    }
    return {
        docId: state.editorBoundDocId,
        editorGeneration: state.editorGeneration
    };
}

function clearDocsFlash(projectId) {
    const state = getDocsState(projectId);
    if (state.flashTimer) {
        clearTimeout(state.flashTimer);
        state.flashTimer = null;
    }
    state.flashMessage = '';
    state.flashState = '';
}

function showDocsFlash(projectId, message, flashState = 'saved', duration = 2200) {
    const state = getDocsState(projectId);
    clearDocsFlash(projectId);
    state.flashMessage = String(message || '');
    state.flashState = flashState;
    if (duration > 0) {
        state.flashTimer = window.setTimeout(() => {
            state.flashTimer = null;
            state.flashMessage = '';
            state.flashState = '';
            updateDocsStatus(projectId);
        }, duration);
    }
    updateDocsStatus(projectId);
}

function getDocsDraft(projectId, record = null) {
    const current = record || getEditorBoundDocsRecord(projectId) || getCurrentDocsRecord(projectId);
    const titleInput = document.getElementById('project-doc-title-input');
    const title = String(titleInput?.value || current?.title || t('docs.untitledPage')).trim() || t('docs.untitledPage');
    const contentJson = dehydrateDocsImageNodes(getDocsEditorJSON());
    return {
        id: current?.id || null,
        title,
        contentJson,
        icon: String(current?.icon || ''),
        parentId: current?.parentId ? String(current.parentId) : '',
        widthMode: current?.widthMode === 'narrow' ? 'narrow' : 'wide'
    };
}

function updateDocsStatus(projectId) {
    const state = getDocsState(projectId);
    const saveBtn = document.getElementById('project-doc-save-btn');
    const isEditing = document.getElementById('docs-canvas-root')?.dataset.mode === 'edit';

    let text = '';
    if (state.loading) text = t('docs.loading');
    else if (state.saving) text = t('docs.saving');
    else if (state.error) text = t('docs.saveFailed');
    else if (state.flashMessage) text = state.flashMessage;
    else if (isEditing && state.lastAutoSavedAt) {
        // Absolute clock time, not "2 minutes ago": a relative label would need a
        // ticking timer just to stay honest.
        text = t('docs.autosaveSavedAt', {
            time: new Date(state.lastAutoSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        });
    }

    const stateLabel = state.error
        ? 'error'
        : state.saving
            ? 'saving'
            : (state.flashState || (isEditing && state.lastAutoSavedAt ? 'saved' : ''));

    // Update whichever status element is currently visible
    ['project-doc-status', 'project-doc-status-view'].forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.textContent = text;
        el.dataset.state = stateLabel;
        el.hidden = !text;
    });

    // In edit mode Publish means "finish editing and go back to view". Autosave
    // keeps the doc clean most of the time, so gating it on `dirty` would leave
    // the button permanently greyed out with no way out of edit mode.
    const publishDisabled = state.saving
        || !getCurrentDocsRecord(projectId)
        || (!isEditing && !state.dirty);
    if (saveBtn) {
        saveBtn.disabled = publishDisabled;
    }
}

function closeDocsHistoryModal() {
    document.querySelector('[data-docs-history-modal]')?.remove();
}

function getDocsHistoryPreviewImageSrc(attrs = {}) {
    const fileKey = extractDocsImageFileKeyFromAttrs(attrs);
    if (fileKey) return buildDocsImageSrc(fileKey);
    const directSrc = String(attrs?.src || '').trim();
    return directSrc || '';
}

function buildDocsAttachmentDataAttrs(attrs = {}, type = 'chip') {
    const fileKey = String(attrs?.fileKey || '').trim();
    const name = String(attrs?.name || '').trim();
    const mimeType = String(attrs?.mimeType || '').trim();
    const size = Number(attrs?.size) || 0;
    const fileClass = String(attrs?.fileClass || getDocsAttachmentFileClass(mimeType, name || attrs?.extension || '')).trim() || 'file';
    const extension = getDocsAttachmentExtension(attrs?.extension || name || '');
    return [
        type === 'card' ? 'data-doc-file-card=""' : 'data-doc-file-chip=""',
        fileKey ? `data-file-key="${escapeHtml(fileKey)}"` : '',
        name ? `data-file-name="${escapeHtml(name)}"` : '',
        mimeType ? `data-mime-type="${escapeHtml(mimeType)}"` : '',
        size > 0 ? `data-file-size="${escapeHtml(String(size))}"` : '',
        `data-file-class="${escapeHtml(fileClass)}"`,
        extension ? `data-file-extension="${escapeHtml(extension)}"` : ''
    ].filter(Boolean).join(' ');
}

function buildDocsHistoryAttachmentHtml(attrs = {}, display = 'chip') {
    const fileKey = String(attrs?.fileKey || '').trim();
    const fileClass = String(attrs?.fileClass || getDocsAttachmentFileClass(attrs?.mimeType || '', attrs?.name || attrs?.extension || '')).trim() || 'file';
    const extension = getDocsAttachmentExtension(attrs?.extension || attrs?.name || '');
    const previewable = isDocsAttachmentPreviewable({
        extension,
        mimeType: attrs?.mimeType,
        name: attrs?.name,
        fileClass
    });
    const href = fileKey
        ? buildDocsAttachmentUrl(fileKey, { download: !previewable })
        : '';
    const downloadHref = fileKey
        ? buildDocsAttachmentUrl(fileKey, { download: true })
        : href;
    const name = escapeHtml(String(attrs?.name || t('docs.fileUntitled')));
    const badge = escapeHtml(getDocsAttachmentBadgeLabel({ extension, name: attrs?.name, fileClass }));
    const typeLabel = escapeHtml(getDocsAttachmentTypeLabel({ extension, fileClass }));
    const targetAttrs = previewable ? ' target="_blank" rel="noopener noreferrer"' : '';
    const dataAttrs = buildDocsAttachmentDataAttrs(attrs, display);
    const downloadButton = downloadHref
        ? `<a class="docs-history-preview-file-download" href="${escapeHtml(downloadHref)}">${escapeHtml(t('docs.fileDownload'))}</a>`
        : '';

    if (display === 'card') {
        return `
            <div class="docs-history-preview-file docs-file-card docs-file-card--${escapeHtml(fileClass)}" ${dataAttrs}>
                <div class="docs-file-card__shell">
                    <div class="docs-file-card__badge">${badge}</div>
                    <div class="docs-file-card__copy">
                        <div class="docs-file-card__eyebrow">${typeLabel}</div>
                        <div class="docs-file-card__title">${name}</div>
                    </div>
                    <div class="docs-file-card__actions">
                        ${href ? `<a class="docs-file-card__action docs-file-card__action--primary" href="${escapeHtml(href)}"${targetAttrs}>${escapeHtml(previewable ? t('docs.fileOpen') : t('docs.fileDownload'))}</a>` : ''}
                        ${downloadButton}
                    </div>
                </div>
            </div>
        `;
    }

    return href
        ? `<a class="docs-history-preview-file-chip docs-file-chip docs-file-chip--${escapeHtml(fileClass)}" href="${escapeHtml(href)}"${targetAttrs} ${dataAttrs}><span class="docs-file-chip__badge">${badge}</span><span class="docs-file-chip__copy"><span class="docs-file-chip__label">${name}</span><span class="docs-file-chip__meta">${typeLabel}</span></span></a>`
        : `<span class="docs-history-preview-file-chip docs-file-chip docs-file-chip--${escapeHtml(fileClass)}" ${dataAttrs}><span class="docs-file-chip__badge">${badge}</span><span class="docs-file-chip__copy"><span class="docs-file-chip__label">${name}</span><span class="docs-file-chip__meta">${typeLabel}</span></span></span>`;
}

function renderDocsHistoryInline(nodes = []) {
    return (Array.isArray(nodes) ? nodes : []).map((node) => {
        if (!node) return '';
        if (node.type === 'text') {
            let html = escapeHtml(decodeHtmlEntities(node.text || ''));
            const marks = Array.isArray(node.marks) ? node.marks : [];
            marks.forEach((mark) => {
                const type = mark?.type;
                if (type === 'bold') html = `<strong>${html}</strong>`;
                else if (type === 'italic') html = `<em>${html}</em>`;
                else if (type === 'underline') html = `<u>${html}</u>`;
                else if (type === 'strike') html = `<s>${html}</s>`;
                else if (type === 'code') html = `<code>${html}</code>`;
                else if (type === 'link') {
                    const href = escapeHtml(mark?.attrs?.href || '');
                    html = href ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${html}</a>` : html;
                }
            });
            return html;
        }
        if (node.type === 'hardBreak') return '<br>';
        if (node.type === 'statusChip') {
            const tone = escapeHtml(node?.attrs?.tone || 'grey');
            const label = escapeHtml(node?.attrs?.label || 'STATUS');
            return `<span class="docs-history-preview-status docs-status-chip docs-status-chip--${tone}">${label}</span>`;
        }
        if (node.type === 'dateChip') {
            const label = escapeHtml(node?.attrs?.date ? formatDatePretty(node.attrs.date) : t('docs.toolbar.dateChip'));
            return `<span class="docs-history-preview-date docs-date-chip">${label}</span>`;
        }
        if (node.type === 'docsFileChip') {
            return buildDocsHistoryAttachmentHtml(node?.attrs || {}, 'chip');
        }
        if (node.type === 'linkChip') {
            const href = escapeHtml(node?.attrs?.href || '');
            const label = escapeHtml(node?.attrs?.label || href);
            const variant = escapeHtml(node?.attrs?.variant || 'external');
            if (!href) return '';
            const isTask = variant === 'task';
            const targetRel = isTask ? '' : ' target="_blank" rel="noopener noreferrer"';
            return `<a href="${href}" data-link-chip="true" data-link-variant="${variant}" class="docs-internal-link docs-internal-link--${variant} description-link-chip-node"${targetRel}>${label}</a>`;
        }
        return renderDocsHistoryNode(node);
    }).join('');
}

function decodeHtmlEntities(value) {
    const text = String(value || '');
    if (!text || typeof document === 'undefined') return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
}

function buildDocsHistoryCalloutIcon(tone) {
    const paths = {
        info: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z',
        success: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14l-4-4 1.41-1.41L10 13.17l6.59-6.59L18 8l-8 8z',
        warning: 'M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z',
        danger: 'M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z'
    };
    const safeTone = ['info', 'success', 'warning', 'danger'].includes(tone) ? tone : '';
    const path = safeTone ? paths[safeTone] : '';
    if (!path) return '';
    return `<span class="docs-callout__icon docs-callout__icon--${safeTone}" aria-hidden="true"><svg viewBox="0 0 24 24" fill="currentColor"><path d="${path}"></path></svg></span>`;
}

let _historyRenderTocItems = null;

function slugifyHistoryHeading(value) {
    return String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'section';
}

function renderDocsHistoryNode(node) {
    if (!node || typeof node !== 'object') return '';
    const content = Array.isArray(node.content) ? node.content : [];
    const inline = renderDocsHistoryInline(content);

    switch (node.type) {
        case 'doc':
            return content.map(renderDocsHistoryNode).join('');
        case 'paragraph':
            return `<p>${inline || '<br>'}</p>`;
        case 'heading': {
            const level = Math.min(3, Math.max(1, Number(node?.attrs?.level) || 1));
            const text = inline || escapeHtml(t('docs.untitledPage'));
            const slug = escapeHtml(slugifyHistoryHeading(text.replace(/<[^>]*>/g, '')));
            return `<h${level} id="${slug}">${text}</h${level}>`;
        }
        case 'tableOfContentsBlock': {
            const items = Array.isArray(_historyRenderTocItems) ? _historyRenderTocItems : [];
            if (!items.length) return '';
            const links = items.map((item) =>
                `<a class="docs-history-toc-item level-${item.level}" href="#${escapeHtml(item.slug)}">${escapeHtml(item.text)}</a>`
            ).join('');
            return `<div class="docs-history-toc">${links}</div>`;
        }
        case 'bulletList':
            return `<ul>${content.map(renderDocsHistoryNode).join('')}</ul>`;
        case 'orderedList':
            return `<ol>${content.map(renderDocsHistoryNode).join('')}</ol>`;
        case 'listItem':
            return `<li>${content.map(renderDocsHistoryNode).join('')}</li>`;
        case 'taskList':
            return `<ul data-type="taskList">${content.map(renderDocsHistoryNode).join('')}</ul>`;
        case 'taskItem': {
            const checked = node?.attrs?.checked === true || node?.attrs?.checked === 'true';
            return `
                <li data-type="taskItem" data-checked="${checked ? 'true' : 'false'}">
                    <label contenteditable="false">
                        <input type="checkbox" ${checked ? 'checked' : ''} disabled>
                        <span></span>
                    </label>
                    <div>${content.map(renderDocsHistoryNode).join('') || '<p><br></p>'}</div>
                </li>
            `;
        }
        case 'blockquote':
            return `<blockquote>${content.map(renderDocsHistoryNode).join('')}</blockquote>`;
        case 'horizontalRule':
            return '<hr>';
        case 'codeBlock':
            return `<pre class="docs-history-preview-code"><code>${escapeHtml(content.map((child) => child?.text || '').join(''))}</code></pre>`;
        case 'image':
        case 'docsImage': {
            const src = getDocsHistoryPreviewImageSrc(node?.attrs || {});
            const alt = escapeHtml(node?.attrs?.alt || node?.attrs?.caption || 'Doc image');
            return src
                ? `<figure class="docs-image-wrap"><img class="docs-inline-image" src="${escapeHtml(src)}" alt="${alt}" loading="lazy"></figure>`
                : '';
        }
        case 'docsFileCard':
            return buildDocsHistoryAttachmentHtml(node?.attrs || {}, 'card');
        case 'table':
            return `<div class="tableWrapper tableWrapper--scrollable"><table>${content.map(renderDocsHistoryNode).join('')}</table></div>`;
        case 'tableRow':
            return `<tr>${content.map(renderDocsHistoryNode).join('')}</tr>`;
        case 'tableHeader':
            return `<th>${inline}</th>`;
        case 'tableCell':
            return `<td>${inline}</td>`;
        case 'callout': {
            const tone = escapeHtml(node?.attrs?.tone || 'info');
            const icon = buildDocsHistoryCalloutIcon(tone);
            return `<div data-doc-callout data-tone="${tone}" class="docs-callout docs-callout--${tone}">${icon}<div class="docs-callout__body">${content.map(renderDocsHistoryNode).join('')}</div></div>`;
        }
        default:
            return inline || content.map(renderDocsHistoryNode).join('');
    }
}

function buildProjectDocReadonlyPreview(item, options = {}) {
    const title = escapeHtml(decodeHtmlEntities(item?.title || t('docs.untitledPage')));
    const widthMode = item?.widthMode === 'narrow' ? 'narrow' : 'wide';
    const safeTopbarLabel = escapeHtml(options.topbarLabel || t('docs.historyTitle'));
    const safeSecondaryLabel = escapeHtml(options.secondaryLabel || '');
    const timestamp = escapeHtml(options.timestamp || formatDocsHistoryTimestamp(item?.createdAt || item?.created_at || ''));
    const contentJson = item?.contentJson || { type: 'doc', content: [] };
    _historyRenderTocItems = extractDocsToc(contentJson);
    const bodyHtml = renderDocsHistoryNode(contentJson);
    _historyRenderTocItems = null;

    return `
        <div class="docs-history-preview-shell">
            <div class="docs-history-preview-topbar">
                <div class="docs-history-preview-label">${safeTopbarLabel}</div>
                ${safeSecondaryLabel ? `<div class="docs-history-preview-version">${safeSecondaryLabel}</div>` : ''}
            </div>
            <div class="docs-history-preview-real docs-canvas" data-mode="view" data-width="${widthMode}">
                <div class="docs-canvas__body">
                    <div class="docs-canvas__title-row">
                        <div class="docs-canvas__title-main">
                            ${buildProjectDocIconStackHtml(item)}
                            <h1 class="docs-canvas__title-display">${title}</h1>
                        </div>
                    </div>
                    <div class="docs-history-preview-time">${timestamp}</div>
                    ${buildHistoryActorLineHtml(item?.meta || {}, { className: 'docs-history-preview-actor' })}
                    <div class="docs-canvas__editor-wrap docs-history-preview-editor-wrap">
                        <div class="docs-canvas__editor docs-history-preview-editor">
                            <div class="docs-editor-content" contenteditable="false">${bodyHtml || `<div class="empty-state">${escapeHtml(t('docs.historyEmpty'))}</div>`}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

function buildDocsHistoryRevisionPreview(item, versionLabel = '') {
    return buildProjectDocReadonlyPreview(item, {
        topbarLabel: t('docs.historyTitle'),
        secondaryLabel: versionLabel,
        timestamp: formatDocsHistoryTimestamp(item?.createdAt || item?.created_at || '')
    });
}

async function openDocsHistoryModal(projectId, docId) {
    closeDocsHistoryModal();
    const overlay = document.createElement('div');
    overlay.className = 'modal active';
    overlay.dataset.docsHistoryModal = 'true';
    overlay.innerHTML = `
        <div class="modal-content docs-history-modal">
            <div class="modal-header docs-history-modal__header">
                <div>
                    <h3>${escapeHtml(t('docs.historyTitle'))}</h3>
                    <p class="docs-history-modal__subtitle">${escapeHtml(t('docs.historySelectVersion'))}</p>
                </div>
                <button type="button" class="docs-history-modal__close" data-docs-history-close data-close aria-label="${escapeHtml(t('common.close') || 'Close')}">
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round"/></svg>
                </button>
            </div>
            <div class="modal-body docs-history-modal__body">
                <div class="docs-history-modal__layout">
                    <div class="docs-history-modal__list-pane">
                        <div id="docs-history-list">${escapeHtml(t('docs.loading'))}</div>
                    </div>
                    <div class="docs-history-modal__preview-pane">
                        <div id="docs-history-preview">${escapeHtml(t('docs.loading'))}</div>
                    </div>
                </div>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    const close = () => closeDocsHistoryModal();
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay || event.target.closest('[data-docs-history-close]')) {
            close();
        }
    });
    const keyHandler = (e) => {
        if (e.key !== 'Escape') return;
        document.removeEventListener('keydown', keyHandler, true);
        if (overlay.isConnected) { e.stopPropagation(); close(); }
    };
    document.addEventListener('keydown', keyHandler, true);

    const list = overlay.querySelector('#docs-history-list');
    const preview = overlay.querySelector('#docs-history-preview');
    try {
        const response = await listProjectDocHistory(docId);
        const items = Array.isArray(response?.items) ? response.items : [];
        if (!items.length) {
            list.innerHTML = `<div class="empty-state">${escapeHtml(t('docs.historyEmpty'))}</div>`;
            if (preview) preview.innerHTML = `<div class="empty-state">${escapeHtml(t('docs.historyEmpty'))}</div>`;
            return;
        }
        let activeRevisionId = String(items[0]?.id || '');

        const renderPreview = (item) => {
            if (!preview) return;
            const isCurrent = String(item?.id || '') === String(items[0]?.id || '');
            const itemIndex = items.findIndex((entry) => String(entry.id) === String(item?.id || ''));
            const versionLabel = t('docs.historyVersionLabel', { number: Math.max(1, items.length - itemIndex) });
            preview.innerHTML = `
                <div class="docs-history-preview-frame">
                    ${buildDocsHistoryRevisionPreview(item, versionLabel)}
                    <div class="docs-history-restore-bar">
                        <div class="docs-history-restore-copy">
                            <div class="docs-history-restore-title">${escapeHtml(isCurrent ? t('docs.historyCurrent') : t('docs.historyReadyToRestore'))}</div>
                            <div class="docs-history-restore-subtitle">${escapeHtml(isCurrent ? t('docs.historyCurrentHint') : t('docs.historyRestoreHint'))}</div>
                        </div>
                        ${isCurrent
                            ? `<span class="task-status-badge backlog">${escapeHtml(t('docs.historyCurrent'))}</span>`
                            : `<button type="button" class="btn-primary docs-history-restore-btn" data-doc-revision-restore="${escapeHtml(item.id)}">${escapeHtml(t('docs.historyRestore'))}</button>`}
                    </div>
                </div>
            `;
            normalizeReadonlyDocsTables(preview);

            preview.querySelector('[data-doc-revision-restore]')?.addEventListener('click', async (event) => {
                const button = event.currentTarget;
                const revisionId = button?.dataset?.docRevisionRestore;
                if (!revisionId) return;
                button.disabled = true;
                try {
                    const restoreResponse = await restoreProjectDocRevision(docId, revisionId);
                    const record = mergeDocsRecord(projectId, restoreResponse?.item || {});
                    getDocsState(projectId).currentDocId = record.id;
                    setDocsBaseline(projectId, record);
                    refreshDocsSurfaces(projectId);
                    showSuccessNotification(t('docs.historyRestored'));
                    close();
                } catch (error) {
                    button.disabled = false;
                    showErrorNotification(error?.message || String(error));
                }
            });
        };

        list.innerHTML = `
                <div class="docs-history-list-shell">
                ${items.map((item, index) => {
                    const versionLabel = t('docs.historyVersionLabel', { number: Math.max(1, items.length - index) });
                    return `
                        <button type="button" class="docs-history-item${index === 0 ? ' is-active' : ''}" data-doc-revision-select="${escapeHtml(item.id)}" data-doc-revision-id="${escapeHtml(item.id)}">
                            <div class="docs-history-item__top">
                                <div class="docs-history-item__identity">
                                    <div class="docs-history-item__version">${escapeHtml(versionLabel)}</div>
                                </div>
                                ${index === 0 ? `<span class="docs-history-item__badge">${escapeHtml(t('docs.historyCurrent'))}</span>` : ''}
                            </div>
                            <div class="docs-history-item__time">${escapeHtml(formatDocsHistoryTimestamp(item.createdAt || item.created_at || ''))}</div>
                            ${buildHistoryActorLineHtml(item?.meta || {}, { className: 'docs-history-item__actor' })}
                        </button>
                    `;
                }).join('')}
            </div>
        `;

        const selectRevision = async (revisionId) => {
            activeRevisionId = String(revisionId || '');
            list.querySelectorAll('[data-doc-revision-select]').forEach((node) => {
                node.classList.toggle('is-active', node.dataset.docRevisionSelect === activeRevisionId);
            });

            const cached = items.find((entry) => String(entry.id) === activeRevisionId);
            const loaded = await loadProjectDocRevision(docId, activeRevisionId);
            const item = loaded?.item || cached;
            if (!item) return;
            const normalized = normalizeDocsRecord(projectId, item);
            const index = items.findIndex((entry) => String(entry.id) === activeRevisionId);
            if (index >= 0) items[index] = { ...items[index], ...normalized };
            renderPreview(normalized);
            preview.querySelector('.docs-history-preview-shell')?.scrollTo({ top: 0, behavior: 'auto' });
        };

        list.querySelectorAll('[data-doc-revision-select]').forEach((button) => {
            button.addEventListener('click', () => {
                void selectRevision(button.dataset.docRevisionSelect);
            });
        });

        await selectRevision(activeRevisionId);
    } catch (error) {
        list.innerHTML = `<div class="empty-state">${escapeHtml(error?.message || String(error))}</div>`;
        if (preview) preview.innerHTML = `<div class="empty-state">${escapeHtml(error?.message || String(error))}</div>`;
    }
}

function buildProjectDocsTocHtml(items) {
    if (!Array.isArray(items) || items.length === 0) {
        return `<p class="project-docs-toc-empty">${t('docs.tocEmpty')}</p>`;
    }

    return items.map((item) => `
        <button type="button" class="project-docs-toc-item level-${item.level}" data-doc-heading-target="${escapeHtml(item.slug)}">
            ${escapeHtml(item.text)}
        </button>
    `).join('');
}

function buildDocsPageMenuHtml(projectId) {
    return `
        <div class="docs-page-menu-wrap" data-docs-menu-wrap>
            <button type="button" class="docs-page-menu-btn" data-docs-menu-btn title="${t('docs.pageOptions')}"><svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="M3 4h10M3 8h10M3 12h10"></path></svg></button>
            <div class="docs-page-menu-dropdown" data-docs-menu-dropdown>
                <button type="button" class="docs-page-menu-item" data-docs-mode-btn>👁 ${t('docs.switchToReading')}</button>
                <button type="button" class="docs-page-menu-item" data-docs-history-btn>📋 ${t('docs.history')}</button>
                <button type="button" class="docs-page-menu-item" data-docs-share-btn title="${escapeHtml(t('docs.shareLink'))}">🔗 ${t('docs.shareLink')}</button>
                <div class="docs-page-menu-width-group">
                    <div class="docs-page-menu-divider"></div>
                    <div class="docs-page-menu-label">${t('docs.width')}</div>
                    <button type="button" class="docs-page-menu-item" data-docs-width-btn data-width-mode="narrow">↔ ${t('docs.width.narrow')}</button>
                    <button type="button" class="docs-page-menu-item" data-docs-width-btn data-width-mode="wide">↔ ${t('docs.width.wide')}</button>
                    <div class="docs-page-menu-divider"></div>
                </div>
                <button type="button" class="docs-page-menu-item" data-docs-duplicate-btn>⧉ ${t('docs.duplicate.title')}</button>
                <button type="button" class="docs-page-menu-item" data-docs-save-template-btn>📄 ${t('docs.templates.saveAsTemplate')}</button>
                <button type="button" class="docs-page-menu-item docs-page-menu-item--danger" data-docs-delete-btn>🗑️ ${t('docs.deletePage')}</button>
            </div>
        </div>
    `;
}

// ── Mobile docs helpers ─────────────────────────────────

function _closeMobileDocSheets() {
    document.getElementById('docs-mobile-insert-sheet')?.classList.remove('active');
    document.getElementById('docs-mobile-more-sheet')?.classList.remove('active');
    document.getElementById('docs-mobile-link-sheet')?.classList.remove('active');
}

function syncMobileBarActiveStates(editor) {
    const bar = document.getElementById('docs-mobile-bar');
    if (!bar) return;
    const checks = [
        ['bold', 'bold'],
        ['italic', 'italic'],
        ['strike', 'strike'],
        ['link', 'link'],
        ['inlineCode', 'code']
    ];
    checks.forEach(([cmd, mark]) => {
        bar.querySelectorAll(`[data-doc-command="${cmd}"]`).forEach((btn) => {
            btn.classList.toggle('active', editor.isActive(mark));
        });
    });
}

let _docsKeyboardHandler = null;
function _startDocsKeyboardListener() {
    if (!window.visualViewport || _docsKeyboardHandler) return;
    _docsKeyboardHandler = () => {
        const vv = window.visualViewport;
        // Account for offsetTop (page auto-scrolls when keyboard opens) so
        // the calculated shift is always the true keyboard height regardless of scroll.
        const shift = Math.max(0, window.innerHeight - vv.offsetTop - vv.height);
        document.documentElement.style.setProperty('--docs-kb-offset', `${shift}px`);
    };
    window.visualViewport.addEventListener('resize', _docsKeyboardHandler);
    window.visualViewport.addEventListener('scroll', _docsKeyboardHandler);
}
function _stopDocsKeyboardListener() {
    if (!window.visualViewport || !_docsKeyboardHandler) return;
    window.visualViewport.removeEventListener('resize', _docsKeyboardHandler);
    window.visualViewport.removeEventListener('scroll', _docsKeyboardHandler);
    _docsKeyboardHandler = null;
    document.documentElement.style.removeProperty('--docs-kb-offset');
}

function buildDocsToolbarHtml(toolbarClass) {
    return `
        <div class="${toolbarClass}" id="project-doc-toolbar">
            <div class="docs-toolbar-groups">
                <div class="docs-toolbar-group">
                    <button type="button" class="editor-btn" data-doc-command="bold" title="${t('docs.toolbar.bold')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/></svg></button>
                    <button type="button" class="editor-btn" data-doc-command="italic" title="${t('docs.toolbar.italic')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/></svg></button>
                    <button type="button" class="editor-btn docs-btn-text" data-doc-command="underline" title="${t('docs.toolbar.underline')}" style="text-decoration:underline;font-style:normal">U</button>
                    <button type="button" class="editor-btn docs-btn-text" data-doc-command="strike" title="${t('docs.toolbar.strike')}" style="text-decoration:line-through;font-style:normal">S</button>
                </div>
                <span class="docs-toolbar-sep"></span>
                <div class="docs-toolbar-group docs-heading-wrap">
                    <button type="button" class="editor-btn docs-heading-toggle" data-docs-heading-toggle title="${t('docs.toolbar.textStyle')}">
                        <span class="docs-heading-label">¶</span>
                        <svg viewBox="0 0 8 5" width="8" height="5" fill="currentColor"><path d="M0 0l4 5 4-5z"/></svg>
                    </button>
                    <div class="docs-heading-dropdown" hidden>
                        <button type="button" class="editor-btn docs-heading-item" data-doc-command="paragraph" title="${t('docs.toolbar.paragraph')}"><span class="docs-heading-item__prefix">¶</span>${t('docs.toolbar.paragraph')}</button>
                        <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="1" title="${t('docs.toolbar.heading1')}"><span class="docs-heading-item__prefix">H1</span>${t('docs.toolbar.heading1')}</button>
                        <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="2" title="${t('docs.toolbar.heading2')}"><span class="docs-heading-item__prefix">H2</span>${t('docs.toolbar.heading2')}</button>
                        <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="3" title="${t('docs.toolbar.heading3')}"><span class="docs-heading-item__prefix">H3</span>${t('docs.toolbar.heading3')}</button>
                    </div>
                </div>
                <span class="docs-toolbar-sep"></span>
                <div class="docs-toolbar-group docs-align-wrap">
                    <button type="button" class="editor-btn docs-align-toggle" data-docs-align-toggle title="${t('docs.toolbar.alignment')}">
                        <svg class="docs-align-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 5h18v2H3zm0 4h12v2H3zm0 4h18v2H3zm0 4h12v2H3z"/></svg>
                        <svg viewBox="0 0 8 5" width="8" height="5" fill="currentColor"><path d="M0 0l4 5 4-5z"/></svg>
                    </button>
                    <div class="docs-align-dropdown" hidden>
                        <button type="button" class="editor-btn" data-doc-command="textAlign" data-doc-param="left" title="${t('docs.toolbar.alignLeft')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 5h18v2H3zm0 4h12v2H3zm0 4h18v2H3zm0 4h12v2H3z"/></svg></button>
                        <button type="button" class="editor-btn" data-doc-command="textAlign" data-doc-param="center" title="${t('docs.toolbar.alignCenter')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 5h18v2H3zm3 4h12v2H6zm-3 4h18v2H3zm3 4h12v2H6z"/></svg></button>
                        <button type="button" class="editor-btn" data-doc-command="textAlign" data-doc-param="right" title="${t('docs.toolbar.alignRight')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 5h18v2H3zm6 4h12v2H9zm-6 4h18v2H3zm6 4h12v2H9z"/></svg></button>
                        <button type="button" class="editor-btn" data-doc-command="textAlign" data-doc-param="justify" title="${t('docs.toolbar.alignJustify')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 5h18v2H3zm0 4h18v2H3zm0 4h18v2H3zm0 4h12v2H3z"/></svg></button>
                    </div>
                </div>
                <span class="docs-toolbar-sep"></span>
                <div class="docs-toolbar-group">
                    <button type="button" class="editor-btn" data-doc-command="bulletList" title="${t('docs.toolbar.bullets')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/></svg></button>
                    <button type="button" class="editor-btn docs-btn-text" data-doc-command="orderedList" title="${t('docs.toolbar.numbers')}">1.</button>
                    <button type="button" class="editor-btn" data-doc-command="divider" title="${t('docs.toolbar.divider')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><rect x="3" y="11" width="18" height="2" rx="1"/></svg></button>
                </div>
                <span class="docs-toolbar-sep"></span>
                <!-- Group 5: insert tools + macros -->
                <div class="docs-toolbar-group">
                    <button type="button" class="editor-btn" data-doc-command="image" title="${t('docs.toolbar.image')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 11.5l2.5 3.01L14.5 10l4.5 6H5l3.5-4.5zM8 8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/></svg></button>
                    <button type="button" class="editor-btn" data-doc-command="file" title="${t('docs.toolbar.file')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2V8l-6-6zm1 7V3.5L18.5 9H15z"/><path d="M8 13h8v1.6H8zm0 3h8v1.6H8z"/></svg></button>
                    <button type="button" class="editor-btn" data-doc-command="video" title="${t('docs.toolbar.video')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17 10.5V7c0-1.1-.9-2-2-2H5C3.9 5 3 5.9 3 7v10c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2v-3.5l4 4v-11l-4 4z"/></svg></button>
                    <button type="button" class="editor-btn" data-doc-command="table" title="${t('docs.toolbar.table')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14H5v-5h6v5zm0-7H5V5h6v5zm9 7h-7v-5h7v5zm0-7h-7V5h7v5z"/></svg></button>
                    <button type="button" class="editor-btn" data-doc-command="dateChip" title="${t('docs.toolbar.dateChip')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg></button>
                    <button type="button" class="editor-btn" data-doc-command="columns" title="${t('docs.toolbar.columns')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 19H5V5h4v14zm10 0h-4V5h4v14z"/></svg></button>
                    <button type="button" class="editor-btn" data-doc-command="taskList" title="${t('docs.toolbar.checklist')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg></button>
                    <button type="button" class="editor-btn docs-btn-text" data-doc-command="emoji" id="docs-emoji-btn" title="${t('docs.toolbar.emoji')}">😊</button>
                </div>
                <div class="docs-toolbar-group">
                    <button type="button" class="editor-btn" id="docs-find-replace-btn" title="${t('docs.findReplace.title')} (Ctrl+H)"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/><path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"/></svg></button>
                </div>
                <span class="docs-toolbar-sep"></span>
                <div class="docs-macros-wrap">
                    <button type="button" class="docs-macros-toggle" data-docs-macros-toggle title="${t('docs.toolbar.macros')}">
                        <span class="docs-macros-plus">+</span>
                        <svg class="docs-macros-caret" viewBox="0 0 8 5" width="7" height="5" fill="currentColor"><path d="M0 0l4 5 4-5z"/></svg>
                    </button>
                    <div class="docs-macros-dropdown" hidden>
                        <div class="docs-macros-header">${t('docs.toolbar.macros')}</div>
                        <button type="button" class="editor-btn docs-macros-item docs-btn-callout-info" data-doc-command="callout" data-doc-param="info" title="${t('docs.toolbar.callout')}">
                            <svg class="docs-macros-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
                            ${t('docs.toolbar.callout')}
                        </button>
                        <button type="button" class="editor-btn docs-macros-item" data-doc-command="status" title="${t('docs.toolbar.status')}">
                            <svg class="docs-macros-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><rect x="3" y="7" width="18" height="10" rx="5"/><circle cx="8" cy="12" r="2" fill="var(--bg-card)" opacity="0.7"/></svg>
                            ${t('docs.toolbar.status')}
                        </button>
                        <button type="button" class="editor-btn docs-macros-item" data-doc-command="toc" title="${t('docs.toolbar.toc')}">
                            <svg class="docs-macros-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3 9h14V7H3v2zm0 4h14v-2H3v2zm0 4h14v-2H3v2zm16 0h2v-2h-2v2zm0-10v2h2V7h-2zm0 6h2v-2h-2v2z"/></svg>
                            ${t('docs.toolbar.toc')}
                        </button>
                        <button type="button" class="editor-btn docs-macros-item" data-doc-command="childList" title="${t('docs.toolbar.childList')}">
                            <svg class="docs-macros-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3z"/></svg>
                            ${t('docs.toolbar.childList')}
                        </button>
                        <button type="button" class="editor-btn docs-macros-item" data-doc-command="programTimeline" title="${t('docs.toolbar.programTimeline')}">
                            <svg class="docs-macros-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 19V5M3 19h18"/><path d="M6 15h4V9H6zM12 12h5V7h-5zM12 17h8v-3h-8z"/></svg>
                            ${t('docs.toolbar.programTimeline')}
                        </button>
                        <button type="button" class="editor-btn docs-macros-item" data-doc-command="codeBlock" title="${t('docs.toolbar.code')}">
                            <svg class="docs-macros-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>
                            ${t('docs.toolbar.code')}
                        </button>
                    </div>
                </div>
                <span class="docs-toolbar-sep"></span>
                <div class="docs-toolbar-group">
                    <button type="button" class="editor-btn" data-doc-command="undo" title="${t('docs.toolbar.undo')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg></button>
                    <button type="button" class="editor-btn" data-doc-command="redo" title="${t('docs.toolbar.redo')}"><svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"/></svg></button>
                </div>
            </div>
            <div class="docs-find-replace-panel" id="docs-find-replace-panel" hidden>
                <div class="docs-fr-row">
                    <svg class="docs-fr-icon" viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
                    <input type="text" class="docs-fr-input" id="docs-find-input" placeholder="${t('docs.findReplace.findPlaceholder')}">
                    <span class="docs-fr-count" id="docs-find-count"></span>
                    <button type="button" class="docs-fr-nav" id="docs-find-prev-btn" title="${t('docs.findReplace.prev')}"><svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/></svg></button>
                    <button type="button" class="docs-fr-nav" id="docs-find-next-btn" title="${t('docs.findReplace.next')}"><svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/></svg></button>
                    <button type="button" class="docs-fr-close" id="docs-find-close-btn" title="${t('docs.findReplace.close')}">×</button>
                </div>
                <div class="docs-fr-row">
                    <svg class="docs-fr-icon" viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                    <input type="text" class="docs-fr-input" id="docs-replace-input" placeholder="${t('docs.findReplace.replacePlaceholder')}">
                    <button type="button" class="docs-fr-action" id="docs-replace-one-btn">${t('docs.findReplace.replace')}</button>
                    <button type="button" class="docs-fr-action" id="docs-replace-all-btn">${t('docs.findReplace.replaceAll')}</button>
                </div>
            </div>
            <!-- Mobile: context-aware bottom bar (hidden on desktop via CSS) -->
            <div class="docs-canvas__mobile-bar" id="docs-mobile-bar">
                <!-- Selection mode: inline mark buttons (shown when text is selected) -->
                <div class="docs-mobile-group-inline">
                    <button type="button" class="docs-mobile-bar-btn" data-doc-command="bold" title="${t('docs.toolbar.bold')}"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z"/></svg></button>
                    <button type="button" class="docs-mobile-bar-btn" data-doc-command="italic" title="${t('docs.toolbar.italic')}"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z"/></svg></button>
                    <button type="button" class="docs-mobile-bar-btn docs-btn-text" data-doc-command="strike" title="${t('docs.toolbar.strike')}" style="text-decoration:line-through;font-style:normal;font-size:15px;font-weight:700">S</button>
                    <button type="button" class="docs-mobile-bar-btn" data-doc-command="link" title="${t('docs.mobile.linkTitle')}"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg></button>
                    <button type="button" class="docs-mobile-bar-btn" data-doc-command="inlineCode" title="${t('docs.toolbar.inlineCode')}"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg></button>
                    <div class="docs-toolbar-group docs-heading-wrap docs-mobile-heading-wrap" style="display:flex">
                        <button type="button" class="docs-mobile-bar-btn docs-heading-toggle" data-docs-heading-toggle title="${t('docs.toolbar.textStyle')}">
                            <span class="docs-heading-label" style="font-size:12px;font-weight:600;letter-spacing:-0.5px">Aa</span>
                            <svg viewBox="0 0 8 5" width="7" height="5" fill="currentColor"><path d="M0 0l4 5 4-5z"/></svg>
                        </button>
                        <div class="docs-heading-dropdown" hidden>
                            <button type="button" class="editor-btn docs-heading-item" data-doc-command="paragraph" title="${t('docs.toolbar.paragraph')}"><span class="docs-heading-item__prefix">¶</span>${t('docs.toolbar.paragraph')}</button>
                            <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="1" title="${t('docs.toolbar.heading1')}"><span class="docs-heading-item__prefix">H1</span>${t('docs.toolbar.heading1')}</button>
                            <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="2" title="${t('docs.toolbar.heading2')}"><span class="docs-heading-item__prefix">H2</span>${t('docs.toolbar.heading2')}</button>
                            <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="3" title="${t('docs.toolbar.heading3')}"><span class="docs-heading-item__prefix">H3</span>${t('docs.toolbar.heading3')}</button>
                        </div>
                    </div>
                    <button type="button" class="docs-mobile-bar-btn" id="docs-mobile-insert-btn-inline" title="${t('docs.mobile.insertTitle')}"><svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/></svg></button>
                    <button type="button" class="docs-mobile-bar-btn" id="docs-mobile-more-btn-inline" title="${t('docs.mobile.moreTitle')}"><svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg></button>
                </div>
                <!-- Cursor mode: block structure buttons (default, no selection) -->
                <div class="docs-mobile-group-block">
                    <div class="docs-toolbar-group docs-heading-wrap docs-mobile-heading-wrap" style="display:flex">
                        <button type="button" class="docs-mobile-bar-btn docs-heading-toggle" data-docs-heading-toggle title="${t('docs.toolbar.textStyle')}">
                            <span class="docs-heading-label" style="font-size:12px;font-weight:600;letter-spacing:-0.5px">Aa</span>
                            <svg viewBox="0 0 8 5" width="7" height="5" fill="currentColor"><path d="M0 0l4 5 4-5z"/></svg>
                        </button>
                        <div class="docs-heading-dropdown" hidden>
                            <button type="button" class="editor-btn docs-heading-item" data-doc-command="paragraph" title="${t('docs.toolbar.paragraph')}"><span class="docs-heading-item__prefix">¶</span>${t('docs.toolbar.paragraph')}</button>
                            <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="1" title="${t('docs.toolbar.heading1')}"><span class="docs-heading-item__prefix">H1</span>${t('docs.toolbar.heading1')}</button>
                            <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="2" title="${t('docs.toolbar.heading2')}"><span class="docs-heading-item__prefix">H2</span>${t('docs.toolbar.heading2')}</button>
                            <button type="button" class="editor-btn docs-heading-item" data-doc-command="heading" data-doc-param="3" title="${t('docs.toolbar.heading3')}"><span class="docs-heading-item__prefix">H3</span>${t('docs.toolbar.heading3')}</button>
                        </div>
                    </div>
                    <button type="button" class="docs-mobile-bar-btn" data-doc-command="bulletList" title="${t('docs.toolbar.bullets')}"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/></svg></button>
                    <button type="button" class="docs-mobile-bar-btn" data-doc-command="orderedList" title="${t('docs.toolbar.numbers')}"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/></svg></button>
                    <button type="button" class="docs-mobile-bar-btn" data-doc-command="undo" title="${t('docs.toolbar.undo')}"><svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg></button>
                    <button type="button" class="docs-mobile-bar-btn" id="docs-mobile-insert-btn" title="${t('docs.mobile.insertTitle')}"><svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/></svg></button>
                    <button type="button" class="docs-mobile-bar-btn" id="docs-mobile-more-btn" title="${t('docs.mobile.moreTitle')}"><svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg></button>
                </div>
            </div>
        </div>
    `;
}

/* ── Docs page header action buttons ─────────────────────────────────────────
   Binds a single [data-docs-menu-wrap] element (⋮ overflow menu).
   Called from setupDocsPageWorkspace (canvas wraps) and
   _updateDocsPageHeaderActions (header slot wrap). */
function _bindDocsPageMenuWrap(menuWrap, projectId) {
    const menuBtn = menuWrap.querySelector('[data-docs-menu-btn]');
    const menuDropdown = menuWrap.querySelector('[data-docs-menu-dropdown]');
    if (!menuBtn || !menuDropdown) return;

    menuBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        document.querySelectorAll('[data-docs-menu-dropdown].open').forEach((dropdown) => {
            if (dropdown !== menuDropdown) dropdown.classList.remove('open');
        });
        menuDropdown.classList.toggle('open');
    });

    // Live Docs has no save ceremony, so switching between writing and reading
    // lives here rather than as a button in the bar — the same place Confluence
    // puts "Switch to viewing" / "Resume editing".
    const modeBtn = menuWrap.querySelector('[data-docs-mode-btn]');
    if (modeBtn) {
        const editing = document.getElementById('docs-canvas-root')?.dataset.mode === 'edit';
        modeBtn.textContent = editing ? `👁 ${t('docs.switchToReading')}` : `✎ ${t('docs.resumeEditing')}`;
        modeBtn.addEventListener('click', () => {
            menuDropdown.classList.remove('open');
            if (document.getElementById('docs-canvas-root')?.dataset.mode === 'edit') {
                void exitDocEditMode(projectId);
            } else {
                enterDocEditMode(projectId);
            }
        });
    }

    const shareBtn = menuWrap.querySelector('[data-docs-share-btn]');
    if (shareBtn) {
        shareBtn.addEventListener('click', async () => {
            diagLog('SHARE menu item tapped');
            const canContinue = await saveActiveDocIfDirty(projectId, { silent: true });
            if (!canContinue) {
                diagLog('SHARE open blocked: dirty doc failed to save');
                return;
            }
            let activeDoc = getCurrentDocsRecord(projectId);
            if (!activeDoc) {
                diagLog('SHARE open blocked: no current doc record');
                return;
            }
            openDocsShareModal(projectId, activeDoc);
            menuDropdown.classList.remove('open');
        });
    }

    const historyBtn = menuWrap.querySelector('[data-docs-history-btn]');
    if (historyBtn) {
        historyBtn.addEventListener('click', async () => {
            const currentDoc = getCurrentDocsRecord(projectId);
            if (!currentDoc) {
                diagLog('HISTORY open blocked: no current doc record');
                return;
            }
            menuDropdown.classList.remove('open');
            await openDocsHistoryModal(projectId, currentDoc.id);
        });
    }

    const revokeBtn = menuWrap.querySelector('[data-docs-revoke-btn]');
    if (revokeBtn) {
        revokeBtn.addEventListener('click', async () => {
            const currentDoc = getCurrentDocsRecord(projectId);
            if (!currentDoc?.isPublished) return;
            menuDropdown.classList.remove('open');
            try {
                const response = await publishProjectDoc(currentDoc.id, { published: false });
                mergeDocsRecord(projectId, response?.item || currentDoc);
                refreshDocsSurfaces(projectId);
                showSuccessNotification(t('docs.revokeLinkSuccess'));
            } catch (error) {
                showErrorNotification(error?.status === 401
                    ? t('docs.shareSessionExpired')
                    : t('docs.revokeLinkError'));
            }
        });
    }

    const duplicateBtn = menuWrap.querySelector('[data-docs-duplicate-btn]');
    if (duplicateBtn) {
        duplicateBtn.addEventListener('click', () => {
            menuDropdown.classList.remove('open');
            const currentDoc = getCurrentDocsRecord(projectId);
            if (currentDoc) handleDuplicateDocPage(projectId, currentDoc.id);
        });
    }

    const saveTemplateBtn = menuWrap.querySelector('[data-docs-save-template-btn]');
    if (saveTemplateBtn) {
        saveTemplateBtn.addEventListener('click', () => {
            menuDropdown.classList.remove('open');
            openSaveAsTemplateDialog(projectId);
        });
    }

    const deleteBtn = menuWrap.querySelector('[data-docs-delete-btn]');
    if (deleteBtn) {
        deleteBtn.addEventListener('click', async () => {
            menuDropdown.classList.remove('open');
            await deleteCurrentProjectDoc(projectId);
            renderDocsPage();
        });
    }

    menuWrap.querySelectorAll('[data-docs-width-btn]').forEach((widthBtn) => {
        const mode = widthBtn.dataset.widthMode === 'narrow' ? 'narrow' : 'wide';
        const docsCanvas = document.getElementById('docs-canvas-root');
        widthBtn.dataset.active = docsCanvas?.dataset.width === mode ? 'true' : 'false';

        widthBtn.addEventListener('click', () => {
            // Per page: saved on this page only (server), not as a global preference.
            const nextMode = mode;
            const canvas = document.getElementById('docs-canvas-root');
            if (canvas) canvas.dataset.width = nextMode;
            const activeDoc = getCurrentDocsRecord(projectId);
            if (activeDoc) {
                activeDoc.widthMode = nextMode;
                void persistProjectDocMeta(projectId, activeDoc.id, { widthMode: nextMode });
            }
            document.querySelectorAll('[data-docs-width-btn]').forEach((button) => {
                button.dataset.active = button.dataset.widthMode === nextMode ? 'true' : 'false';
            });
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    refreshDocsEditorLayout();
                });
            });
            menuDropdown.classList.remove('open');
        });
    });
}

/* Renders Edit/Publish/Cancel/⋮ into the sticky docs page header slot.
   Called after renderDocsPageContent(), enterDocEditMode(), exitDocEditMode(),
   and when navigating away from docs for all layouts. */
function _updateDocsPageHeaderActions(projectId, mode) {
    const slot = document.getElementById('docs-page-header-actions');
    if (!slot) return;

    if (!getCurrentDocsRecord(projectId)) {
        slot.innerHTML = '';
        return;
    }

    if (mode === 'edit') {
        // Edit ↔ Done is a view toggle, not a save ceremony — nothing is pending
        // when it is pressed. It stays an explicit button because view mode is
        // where tables sort and links navigate: clicking a table header sorts in
        // view but edits the header text in edit, so the two modes cannot be
        // collapsed. (This is what Confluence Live Docs gets wrong by keeping
        // you in edit mode permanently.) Cancel is gone — there is nothing left
        // to discard.
        slot.innerHTML = `
            <span class="docs-canvas__status" id="project-doc-status" hidden></span>
            <button type="button" class="docs-canvas__save-btn" id="project-doc-save-btn" title="${t('docs.doneEditingTooltip')}">${t('docs.doneEditing')}</button>
            ${buildDocsPageMenuHtml(projectId)}
        `;
    } else {
        slot.innerHTML = `
            <span class="docs-canvas__status" id="project-doc-status-view" hidden></span>
            <button type="button" class="docs-canvas__edit-btn" id="project-doc-edit-btn"><svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0"><path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H2.5v-2.5L11.5 2.5z"/></svg>${t('docs.edit')}</button>
            ${buildDocsPageMenuHtml(projectId)}
        `;
    }

    const editBtn = document.getElementById('project-doc-edit-btn');
    if (editBtn) editBtn.addEventListener('click', () => enterDocEditMode(projectId));

    const saveBtn = document.getElementById('project-doc-save-btn');
    if (saveBtn) saveBtn.addEventListener('click', async () => exitDocEditMode(projectId));

    const menuWrap = slot.querySelector('[data-docs-menu-wrap]');
    if (menuWrap) _bindDocsPageMenuWrap(menuWrap, projectId);

    updateDocsStatus(projectId);
}

// Live Docs: on desktop, clicking away from the canvas is how you stop editing.
// The listener sits on the docs page rather than on `document` on purpose —
// every docs popover (status, date, emoji, slash menu, table menu) is portaled
// to document.body, so binding here exempts all of them without enumerating a
// single selector, and a click on Bold can never be mistaken for "clicked out".
let _docsActiveProjectId = null;

function _canExitDocsEditModeNow() {
    if (getIsMobileCached() || isDocsTabletDevice()) return false;
    return document.getElementById('docs-canvas-root')?.dataset.mode === 'edit';
}

// Anything that owns Escape while it is open. Checked explicitly because the
// Escape handler runs in the capture phase (see below), so it would otherwise
// close edit mode out from under an open menu instead of closing the menu.
function _isAnyDocsOverlayOpen() {
    if (_docsSlashState?.open) return true;
    // Visibility, not mere presence: several of these (the emoji picker, the
    // find panel) stay in the DOM and are only hidden, so a presence check
    // would swallow Escape forever once they had been opened once.
    return Array.from(document.querySelectorAll(
        '#docs-status-popover, #docs-date-popover, .docs-slash-menu, .docs-row-menu,'
        + ' .confirm-modal, [data-docs-history-modal], [data-docs-menu-dropdown].open,'
        + ' .docs-emoji-picker-wrap, #docs-find-replace-panel'
    )).some((el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed' && el.offsetWidth > 0);
}

function _bindDocsEditModeClickAway() {
    const page = document.getElementById('docs-page-content');
    if (!page || page.dataset.clickAwayBound === 'true') return;
    page.dataset.clickAwayBound = 'true';

    // Clicking the empty area below a short document.
    page.addEventListener('pointerdown', (event) => {
        // Touch keeps its explicit button: a tap is ambiguous with placing the
        // caret and with starting a scroll.
        if (!_canExitDocsEditModeNow()) return;
        if (event.target?.closest?.('.docs-canvas')) return;
        void exitDocEditMode(_docsActiveProjectId);
    });

    // The primary desktop exit. .docs-canvas is width:100% of the page, so a
    // long document leaves no empty area to click — click-away alone would trap
    // the user in edit mode. Escape does not depend on the layout.
    //
    // Capture phase, and deliberately NOT gated on defaultPrevented: TipTap
    // calls preventDefault() on Escape whenever the editor has focus, so a
    // bubble-phase handler never fires. That means anything else that wants
    // Escape has to be checked for by hand, below.
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!_canExitDocsEditModeNow()) return;
        if (_isAnyDocsOverlayOpen()) return;
        event.preventDefault();
        void exitDocEditMode(_docsActiveProjectId);
    }, true);
}

function enterDocEditMode(projectId) {
    const docsCanvas = document.getElementById('docs-canvas-root');
    if (!docsCanvas) return;

    // Start of a writing session. sessionAutoSaved decides whether leaving has
    // to force a history snapshot: autosave throttles them, so a short session
    // could otherwise end without leaving any point to come back to.
    const editState = getDocsState(projectId);
    editState.sessionAutoSaved = false;
    editState.lastAutoSavedAt = 0;

    docsCanvas.dataset.mode = 'edit';
    setDocsEditorEditable(true);
    updateDocsTableMenuVisibility();
    clearDocsFlash(projectId);
    updateDocsStatus(projectId);
    const currentDoc = getCurrentDocsRecord(projectId);
    syncDocsRouteState({
        docId: currentDoc?.id || '',
        slug: currentDoc?.slug || slugifyProjectDocTitle(currentDoc?.title || ''),
        mode: 'edit'
    });
    if (getIsMobileCached() || isDocsTabletDevice()) {
        _startDocsKeyboardListener();
        // Set --docs-toolbar-h to the actual rendered toolbar height so sheets
        // align flush against it without hardcoded values
        requestAnimationFrame(() => {
            const toolbar = document.querySelector('.docs-canvas__toolbar');
            if (toolbar) {
                document.documentElement.style.setProperty(
                    '--docs-toolbar-h', toolbar.offsetHeight + 'px'
                );
            }
        });
    }
    _updateDocsPageHeaderActions(projectId, 'edit');
    showDocTemplateBanner(projectId);
}

async function exitDocEditMode(projectId) {
    const docsCanvas = document.getElementById('docs-canvas-root');
    if (!docsCanvas) return;

    const exitState = getDocsState(projectId);

    if (getIsMobileCached() || isDocsTabletDevice()) {
        _stopDocsKeyboardListener();
        _closeMobileDocSheets();
    }

    cancelPendingDocsContentAutoSave(projectId);
    const hadWrites = exitState.sessionAutoSaved;
    const saved = await saveActiveDocIfDirty(projectId, { mode: 'view' });
    if (!saved) return;

    // A short session can end with nothing dirty (autosave already flushed it)
    // and no snapshot (the interval had not elapsed). Force one, so every
    // writing session leaves at least one point to come back to.
    if (hadWrites && !exitState.dirty) {
        await forceDocsSessionSnapshot(projectId);
    }

    exitState.sessionAutoSaved = false;
    exitState.lastAutoSavedAt = 0;

    docsCanvas.dataset.mode = 'view';
    setDocsEditorEditable(false);
    closeDocsFindReplace();
    updateDocsTableMenuVisibility();
    hideDocTemplateBanner();
    // Remove loading class immediately so content is visible
    document.querySelectorAll('.docs-editor-content').forEach(el => el.classList.remove('docs-loading'));
    // Defer beautify until after ProseMirror finishes its setEditable transaction.
    // Running it synchronously here fights PM's DOM settle and skips links 3+.
    requestAnimationFrame(() => {
        beautifyInternalLinks(document.querySelector('#project-doc-editor .ProseMirror'), true);
    });

    // Sync display title from input (in case it was edited before publish)
    const titleInput = document.getElementById('project-doc-title-input');
    const titleDisplay = document.getElementById('project-doc-title-display');
    if (titleInput && titleDisplay) {
        titleDisplay.textContent = titleInput.value || t('docs.titlePlaceholder');
    }
    const current = getCurrentDocsRecord(projectId);
    syncDocsRouteState({
        docId: current?.id || '',
        slug: current?.slug || slugifyProjectDocTitle(current?.title || titleInput?.value || ''),
        mode: 'view'
    });
    if (!getDocsState(projectId).error) {
        showDocsFlash(projectId, t('docs.saved'), 'saved');
    }
    updateDocsStatus(projectId);
    // Update sticky page header to show Edit / ⋮ (desktop + mobile)
    _updateDocsPageHeaderActions(projectId, 'view');
}

/* Ctrl/Cmd+S while a docs page is in edit mode triggers the same Publish
   button click (exitDocEditMode with save:true) instead of the browser's
   "Save Page" dialog. Clicking the real button (rather than calling
   exitDocEditMode directly) reuses its disabled-state guard and the
   projectId already bound in its click closure. */
function handleDocsPublishShortcut(event) {
    if (!event || event.defaultPrevented || event.isComposing || event.repeat) return;
    const pressedKey = String(event?.key || '').toLowerCase();
    if (pressedKey !== 's') return;
    if (event.altKey || event.shiftKey || (!event.ctrlKey && !event.metaKey)) return;

    const docsCanvas = document.getElementById('docs-canvas-root');
    if (!docsCanvas || docsCanvas.dataset.mode !== 'edit') return;

    // Nothing to "save" under Live Docs, but Ctrl+S is deep muscle memory and
    // silently doing nothing reads as a failure. Flush the pending autosave so
    // the status line confirms the work is on the server, and stay in the editor
    // — the user asked to save, not to stop writing.
    event.preventDefault();
    flushPendingDocsContentAutoSave();
    void saveActiveDocIfDirty({ silent: true }).then(() => {
        showDocsFlash(null, t('docs.saved'), 'saved', 2000);
    });
}

if (!document.documentElement.dataset.docsPublishShortcutBound) {
    document.documentElement.dataset.docsPublishShortcutBound = 'true';
    document.addEventListener('keydown', handleDocsPublishShortcut);
}

/* ── iPad/WebKit docs compatibility ───────────────────────────────────
   Apple forces every iOS/iPadOS browser onto WebKit, which honors fixed
   colgroup <col width:Npx> and will NOT shrink tables to fit like Blink
   (Chrome, incl. its responsive emulator) or Gecko (Firefox). iPad is
   >=769px so it gets the desktop layout, but it is touch + WebKit. We
   keep the EXACT desktop column widths and turn each VIEW-mode table
   into its own horizontal-scroll box (the page itself never scrolls —
   .docs-page-content keeps overflow-x:hidden). HARD-GATED by
   isDocsTabletDevice(): on every non-iPad device initDocsTabletTableScaler()
   returns immediately, the markers are never set and none of this runs,
   so desktop and mobile are provably byte-for-byte unaffected.

   The markers are set mode-INDEPENDENTLY:
     - #docs-canvas-root[data-device="tablet"]  (table/edit CSS scoping)
     - body.is-docs-tablet                      (reaches the page header,
                                                  a sibling of the canvas)
   so the edit-mode (C) and nav-tree (D) CSS arm even though the table
   pass itself is view-mode only. */
function isDocsTabletDevice() {
    try {
        const ua = navigator.userAgent || '';
        // iPad only — NEVER iPhone/iPod. Phones already get the mobile card
        // layout (@media max-width:768px), and stamping data-device="tablet"
        // on a phone arms the fit-content table-scaler, which collapses the
        // stacked-card tables to content width (narrow cards, dead space to
        // the right). This helper's own gate comment promises "non-iPad
        // devices return immediately" — including iPhone here broke that.
        const iPad = /iPad/.test(ua);
        const iPadOSDesktop = (navigator.platform === 'MacIntel'
            || /Macintosh/.test(ua)) && (navigator.maxTouchPoints || 0) > 1;
        return iPad || iPadOSDesktop;
    } catch (_) {
        return false;
    }
}

function isDocsDesktopWidthTablet() {
    try {
        const ua = navigator.userAgent || '';
        if (/iPhone|iPod/.test(ua)) return false;
        const isDesktopWidth = typeof window.matchMedia === 'function'
            ? window.matchMedia('(min-width: 769px)').matches
            : window.innerWidth > 768;
        return isDocsTabletDevice() && isDesktopWidth;
    } catch (_) {
        return false;
    }
}

function _readDocsColWidth(col) {
    // Original authored px is captured once so repeated passes stay stable
    // even after we re-assert col.style.width.
    if (col.dataset.docsOrigW != null) return parseFloat(col.dataset.docsOrigW) || 0;
    let w = 0;
    const styleW = col.style && col.style.width;
    if (styleW && styleW.endsWith('px')) w = parseFloat(styleW) || 0;
    if (!w) {
        const attrW = col.getAttribute('width');
        if (attrW) w = parseFloat(attrW) || 0;
    }
    if (!w) w = col.getBoundingClientRect().width || 0;
    col.dataset.docsOrigW = String(w);
    return w;
}

function _stampDocsTabletMarkers() {
    const canvas = document.getElementById('docs-canvas-root');
    if (canvas && canvas.dataset.device !== 'tablet') canvas.dataset.device = 'tablet';
    if (!document.body.classList.contains('is-docs-tablet')) {
        document.body.classList.add('is-docs-tablet');
    }
    return canvas;
}

function _scaleDocsViewTablesToFit() {
    const canvas = _stampDocsTabletMarkers();
    if (!canvas || canvas.dataset.mode !== 'view') return; // A/B body view-only
    canvas.querySelectorAll('.docs-editor-content .tableWrapper').forEach((wrapper) => {
        const table = wrapper.querySelector('table');
        if (!table) return;
        // Clear any stale inline output from earlier approaches (#575/#576).
        table.style.transform = '';
        table.style.transformOrigin = '';
        table.style.width = '';
        table.style.maxWidth = '';
        table.style.minWidth = '';
        table.style.tableLayout = '';
        wrapper.style.height = '';
        // Restore the EXACT authored (= desktop) column widths.
        table.querySelectorAll('colgroup > col').forEach((col) => {
            const w = _readDocsColWidth(col);
            if (w > 0) col.style.width = w + 'px';
        });
        table.querySelectorAll('td img, th img, td video, th video').forEach((m) => {
            m.style.maxWidth = '';
            m.style.height = '';
        });
        // This table becomes its own horizontal scroller; page stays put.
        wrapper.style.overflowX = 'auto';
        wrapper.style.overflowY = 'visible';
        wrapper.style.webkitOverflowScrolling = 'touch';
        wrapper.style.maxWidth = '100%';
        wrapper.style.width = 'fit-content';
    });
}

let _docsTableScaleRaf = 0;
function _scheduleDocsTableScale() {
    if (_docsTableScaleRaf) cancelAnimationFrame(_docsTableScaleRaf);
    _docsTableScaleRaf = requestAnimationFrame(() => {
        _docsTableScaleRaf = 0;
        _scaleDocsViewTablesToFit();
    });
}

function resetDocsTabletScrollPosition() {
    if (!isDocsDesktopWidthTablet()) return;
    // iPad's effective scroller can be any of these depending on layout
    // mode, so zero them all (the contenteditable hydration + heading
    // scroll-margin can scroll a different one than .app).
    ['.app', '#docs-page-content', '.docs-canvas__body'].forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) el.scrollTop = 0;
    });
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    try { window.scrollTo(0, 0); } catch (_) {}
}

function scheduleDocsTabletScrollReset() {
    if (!isDocsDesktopWidthTablet()) return;
    resetDocsTabletScrollPosition();
    requestAnimationFrame(() => {
        resetDocsTabletScrollPosition();
        requestAnimationFrame(() => {
            resetDocsTabletScrollPosition();
            requestAnimationFrame(resetDocsTabletScrollPosition);
        });
    });
    // ProseMirror hydration + WebKit's heading auto-scroll land AFTER the
    // RAF chain, so keep a longer trailing reset to win that race.
    window.setTimeout(resetDocsTabletScrollPosition, 120);
    window.setTimeout(resetDocsTabletScrollPosition, 300);
}

let _docsTabletScalerBound = false;
function initDocsTabletTableScaler() {
    if (!isDocsTabletDevice()) return; // non-iPad: total no-op (provably safe)
    _stampDocsTabletMarkers(); // arm C/D CSS even before the first view pass
    _scheduleDocsTableScale();
    if (_docsTabletScalerBound) return;
    _docsTabletScalerBound = true;
    window.addEventListener('resize', _scheduleDocsTableScale);
    window.addEventListener('orientationchange', _scheduleDocsTableScale);
    // Images load after layout and change table width → re-fit.
    document.addEventListener('load', (e) => {
        const tgt = e.target;
        if (tgt && tgt.tagName === 'IMG'
            && tgt.closest('#docs-canvas-root .tableWrapper')) {
            _scheduleDocsTableScale();
        }
    }, true);
    const host = document.getElementById('docs-page-content');
    if (host && typeof MutationObserver !== 'undefined') {
        // childList only (content re-render) — never observe style/attrs,
        // so our own inline-style writes cannot retrigger the observer.
        new MutationObserver(_scheduleDocsTableScale)
            .observe(host, { childList: true, subtree: true });
        const canvas = document.getElementById('docs-canvas-root');
        if (canvas) {
            // Edit↔view and width toggles flip these attrs in place.
            new MutationObserver(_scheduleDocsTableScale)
                .observe(canvas, { attributes: true, attributeFilter: ['data-mode', 'data-width'] });
        }
    }
}

function renderDocsPageContent() {
    document.body.classList.remove('public-docs-standalone');
    const container = document.getElementById('docs-page-content');
    if (!container) return;

    const state = getDocsState();
    const current = getCurrentDocsRecord();

    // Show a skeleton while docs are being fetched so we never flash the
    // empty-state placeholder for a doc that is actually about to load.
    if ((!state.loaded || state.loading) && !current) {
        container.innerHTML = `
            <div class="docs-canvas" id="docs-canvas-root">
                <div class="docs-canvas__body">
                    <div class="docs-canvas__loading" aria-busy="true" aria-label="${t('docs.loading')}">
                        <div class="docs-canvas__loading-title"></div>
                        <div class="docs-canvas__loading-line docs-canvas__loading-line--wide"></div>
                        <div class="docs-canvas__loading-line docs-canvas__loading-line--mid"></div>
                        <div class="docs-canvas__loading-line docs-canvas__loading-line--short"></div>
                        <div class="docs-canvas__loading-line docs-canvas__loading-line--wide"></div>
                        <div class="docs-canvas__loading-line docs-canvas__loading-line--mid"></div>
                    </div>
                </div>
            </div>
        `;
        // Clear header slot while loading
        if (!getIsMobileCached()) {
            const slot = document.getElementById('docs-page-header-actions');
            if (slot) slot.innerHTML = '';
        }
        return;
    }
    const currentIcon = current ? String(current.icon || '') : '';

    /* New pages with no content start directly in edit mode */
    const route = getDocsRouteState();
    const initialMode = route.mode === 'edit'
        ? 'edit'
        : current
            ? 'view'
            : 'edit';
    // Width is per page (saved server-side in width_mode), never a global preference.
    const widthMode = current?.widthMode === 'narrow' ? 'narrow' : 'wide';

    container.innerHTML = `
        <div class="docs-canvas" data-mode="${initialMode}" data-width="${widthMode}" id="docs-canvas-root">
            ${current ? `
            <div class="docs-canvas__body">
                <!-- Toolbar row (edit mode only) — sticky, formatting left + Cancel/Publish/⋮ right -->
                ${buildDocsToolbarHtml('docs-canvas__toolbar')}

                <!-- Ancestor trail (nested pages only) — jump to a parent without
                     hunting the sidebar tree. -->
                ${buildDocsBreadcrumbHtml(null, current)}

                <!-- Title row (always visible) — title left, view-mode actions right -->
                <div class="docs-canvas__title-row">
                    <div class="docs-canvas__title-main">
                        <span class="docs-canvas__title-icon-display${currentIcon ? '' : ' is-hidden'}" id="project-doc-title-icon-view" aria-hidden="${currentIcon ? 'false' : 'true'}">${escapeHtml(currentIcon || '')}</span>
                        <div class="docs-canvas__title-icon-edit">
                            <button
                                type="button"
                                class="docs-canvas__icon-trigger${currentIcon ? ' has-icon' : ' is-empty'}"
                                id="project-doc-icon-btn"
                                data-doc-icon-trigger
                                title="${t('docs.pageIcon')}"
                                aria-label="${t('docs.pageIcon')}">${escapeHtml(currentIcon || DOCS_EMPTY_ICON_PLACEHOLDER)}</button>
                            <button
                                type="button"
                                class="docs-canvas__icon-clear${currentIcon ? '' : ' is-hidden'}"
                                id="project-doc-icon-clear"
                                title="${t('docs.removeIcon')}"
                                aria-label="${t('docs.removeIcon')}">×</button>
                        </div>
                        <h1 class="docs-canvas__title-display" id="project-doc-title-display">${escapeHtml(current.title || t('docs.titlePlaceholder'))}</h1>
                        <input type="text" id="project-doc-title-input" class="docs-canvas__title"
                            value="${escapeHtml(current.title || '')}" placeholder="${t('docs.titlePlaceholder')}" />
                    </div>
                    <div class="docs-canvas__meta">
                        <span>${t('docs.updatedLabel')}: ${formatDocTimestamp(current.updatedAt)}</span>
                    </div>
                </div>

                <!-- Table options menu (floating, shown when cursor is inside a table in edit mode) -->
                <div id="docs-table-menu" class="docs-table-menu" style="display:none" aria-label="${t('docs.table.actions')}">
                    <div class="docs-table-menu__bar" role="toolbar" aria-label="${t('docs.table.actions')}">
                        <div class="docs-table-menu__group">
                            <button type="button" class="docs-table-menu__action-btn" data-table-menu-toggle="options" aria-expanded="false">
                                <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor" aria-hidden="true"><rect x="1" y="1" width="14" height="4" rx="1"/><rect x="1" y="7" width="14" height="8" rx="1" opacity="0.4"/></svg>
                                <span data-i18n="docs.table.options">${t('docs.table.options')}</span>
                                <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true" class="docs-table-menu__caret"><path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </button>
                            <div class="docs-table-menu__dropdown" data-table-menu-dropdown="options" hidden>
                                <button type="button" class="docs-table-menu__menu-item docs-table-menu__menu-item--toggle" data-table-action="toggleHeaderRow" aria-pressed="false">
                                    <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor" aria-hidden="true"><rect x="1" y="1" width="14" height="4" rx="1"/><rect x="1" y="7" width="14" height="8" rx="1" opacity="0.35"/></svg>
                                    <span data-i18n="docs.table.headerRow">${t('docs.table.headerRow')}</span>
                                    <svg class="docs-table-menu__check" viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M2 6l3 3 5-5"/></svg>
                                </button>
                                <button type="button" class="docs-table-menu__menu-item docs-table-menu__menu-item--toggle" data-table-action="toggleHeaderColumn" aria-pressed="false">
                                    <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor" aria-hidden="true"><rect x="1" y="1" width="4" height="14" rx="1"/><rect x="7" y="1" width="8" height="14" rx="1" opacity="0.35"/></svg>
                                    <span data-i18n="docs.table.headerColumn">${t('docs.table.headerColumn')}</span>
                                    <svg class="docs-table-menu__check" viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M2 6l3 3 5-5"/></svg>
                                </button>
                                <button type="button" class="docs-table-menu__menu-item docs-table-menu__menu-item--toggle" data-table-action="numberColumn" aria-pressed="false">
                                    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M6.5 4h5M6.5 8h5M6.5 12h5M4.5 3v2M3.5 7h2l-2 2h2M3.5 11h1.5a1 1 0 110 2H3.5"/></svg>
                                    <span data-i18n="docs.table.numberColumn">${t('docs.table.numberColumn')}</span>
                                    <svg class="docs-table-menu__check" viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M2 6l3 3 5-5"/></svg>
                                </button>
                                <button type="button" class="docs-table-menu__menu-item" data-table-action="distributeColumnsEvenly">
                                    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M2 4h12M2 8h12M2 12h12M5 2v12M11 2v12"/></svg>
                                    <span data-i18n="docs.table.distributeEvenly">${t('docs.table.distributeEvenly')}</span>
                                </button>
                            </div>
                        </div>
                        <div class="docs-table-menu__group">
                            <button type="button" class="docs-table-menu__action-btn" data-table-menu-toggle="rows" aria-expanded="false">
                                <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="1" y="1" width="14" height="14" rx="1.5"/><path d="M1 6h14M1 10h14"/></svg>
                                <span data-i18n="docs.table.rows">${t('docs.table.rows')}</span>
                                <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true" class="docs-table-menu__caret"><path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </button>
                            <div class="docs-table-menu__dropdown" data-table-menu-dropdown="rows" hidden>
                                <button type="button" class="docs-table-menu__menu-item" data-table-action="addRowBefore">
                                    <span data-i18n="docs.table.addRowAbove">${t('docs.table.addRowAbove')}</span>
                                </button>
                                <button type="button" class="docs-table-menu__menu-item" data-table-action="addRowAfter">
                                    <span data-i18n="docs.table.addRowBelow">${t('docs.table.addRowBelow')}</span>
                                </button>
                                <div class="docs-table-menu__menu-divider"></div>
                                <button type="button" class="docs-table-menu__menu-item docs-table-menu__menu-item--danger" data-table-action="deleteRow">
                                    <span data-i18n="docs.table.deleteRow">${t('docs.table.deleteRow')}</span>
                                </button>
                            </div>
                        </div>
                        <div class="docs-table-menu__group">
                            <button type="button" class="docs-table-menu__action-btn" data-table-menu-toggle="columns" aria-expanded="false">
                                <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="1" y="1" width="14" height="14" rx="1.5"/><path d="M6 1v14M10 1v14"/></svg>
                                <span data-i18n="docs.table.columns">${t('docs.table.columns')}</span>
                                <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true" class="docs-table-menu__caret"><path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                            </button>
                            <div class="docs-table-menu__dropdown" data-table-menu-dropdown="columns" hidden>
                                <button type="button" class="docs-table-menu__menu-item" data-table-action="addColumnBefore">
                                    <span data-i18n="docs.table.addColLeft">${t('docs.table.addColLeft')}</span>
                                </button>
                                <button type="button" class="docs-table-menu__menu-item" data-table-action="addColumnAfter">
                                    <span data-i18n="docs.table.addColRight">${t('docs.table.addColRight')}</span>
                                </button>
                                <div class="docs-table-menu__menu-divider"></div>
                                <button type="button" class="docs-table-menu__menu-item docs-table-menu__menu-item--danger" data-table-action="deleteColumn">
                                    <span data-i18n="docs.table.deleteColumn">${t('docs.table.deleteColumn')}</span>
                                </button>
                            </div>
                        </div>
                        <div class="docs-table-menu__group docs-table-menu__group--overflow">
                            <button type="button" class="docs-table-menu__icon-btn" data-table-menu-toggle="overflow" aria-expanded="false" title="${t('docs.table.actions')}" aria-label="${t('docs.table.actions')}">
                                <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor"><circle cx="3" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/><circle cx="13" cy="8" r="1.4"/></svg>
                            </button>
                            <div class="docs-table-menu__dropdown docs-table-menu__dropdown--right" data-table-menu-dropdown="overflow" hidden>
                                <button type="button" class="docs-table-menu__menu-item" data-table-action="copyTable">
                                    <span data-i18n="docs.table.copy">${t('docs.table.copy')}</span>
                                </button>
                                <div class="docs-table-menu__menu-divider"></div>
                                <button type="button" class="docs-table-menu__menu-item docs-table-menu__menu-item--danger" data-table-action="deleteTable">
                                    <span data-i18n="docs.table.delete">${t('docs.table.delete')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="docs-canvas__editor-wrap">
                    <div id="project-doc-editor" class="docs-canvas__editor"></div>
                </div>
            </div>
            ` : `
            <div class="docs-canvas__empty">
                <div class="docs-canvas__empty-icon">📄</div>
                <p class="docs-canvas__empty-text">${t('docs.createFirstInline')}</p>
                <button type="button" class="btn-ghost docs-canvas__new-btn" id="project-doc-new-btn">+ ${t('docs.newPage')}</button>
            </div>
            `}
        </div>
    `;

    setupDocsPageWorkspace(null);
    // Populate sticky page header with the correct action buttons (desktop + mobile)
    _updateDocsPageHeaderActions(null, initialMode);
}

function renderDocsPage() {
    const route = getDocsRouteState();
    if (route.publicToken) {
        resetDocsBulkSelection();
        void renderPublicDocsStandalone(route.publicToken, route.width);
        updateDocsNavTree(null);
        return;
    }
    renderDocsPageContent();
    if (isDocsDesktopWidthTablet() && route.isDocsRoute) {
        scheduleDocsTabletScrollReset();
    }
    updateDocsNavTree();
    const state = getDocsState();
    // Clear any previous error so user-initiated navigation always retries.
    if (state.error) state.error = '';
    if (!state.loaded && !state.loading) {
        void ensureDocsListLoaded();
    } else if (state.loaded && route.docId && route.docId !== state.currentDocId) {
        // Docs already loaded but route points to a different doc (e.g. child page link click).
        // Select the target doc so the page actually navigates.
        void selectDocPage(null, route.docId);
    }
}

async function renderPublicDocsStandalone(publicToken, urlWidth) {
    docsWorkspaceActive = false;
    document.body.classList.add('public-docs-standalone');
    const docsPage = document.getElementById('docs');
    if (docsPage) {
        document.querySelectorAll('.page').forEach((page) => page.classList.remove('active'));
        docsPage.classList.add('active');
    }
    const container = document.getElementById('docs-page-content');
    if (!container) return;
    container.innerHTML = `<div class="docs-page-empty"><p>${escapeHtml(t('docs.loading'))}</p></div>`;
    try {
        const response = await loadPublishedProjectDoc(publicToken);
        const item = normalizeDocsRecord(response?.item?.projectId || 0, response?.item || {}, { publicToken });
        const itemIcon = String(item.icon || '');
        const itemWidthMode = urlWidth === 'narrow' || urlWidth === 'wide'
            ? urlWidth
            : (item.widthMode === 'narrow' ? 'narrow' : 'wide');
        container.innerHTML = `
            <div class="docs-canvas" data-mode="view" data-width="${itemWidthMode}" id="docs-canvas-root">
                <div class="docs-canvas__body">
                    <div class="public-docs-topbar">
                        <button type="button" id="public-docs-width-toggle" class="public-docs-theme-toggle" aria-label="">
                            <span class="public-docs-theme-toggle-icon" aria-hidden="true"></span>
                            <span class="public-docs-theme-toggle-text"></span>
                        </button>
                        <button type="button" id="public-docs-theme-toggle" class="public-docs-theme-toggle" data-action="toggleTheme" aria-label="" title="">
                            <span class="public-docs-theme-toggle-icon" aria-hidden="true"></span>
                            <span class="public-docs-theme-toggle-text"></span>
                        </button>
                    </div>
                    <div class="docs-canvas__title-row">
                        <div class="docs-canvas__title-main">
                            <span class="docs-canvas__title-icon-display${itemIcon ? '' : ' is-hidden'}" id="project-doc-title-icon-view" aria-hidden="${itemIcon ? 'false' : 'true'}">${escapeHtml(itemIcon)}</span>
                            <h1 class="docs-canvas__title-display" id="project-doc-title-display">${escapeHtml(item.title || t('docs.titlePlaceholder'))}</h1>
                        </div>
                    </div>
                    <div class="docs-canvas__editor-wrap">
                        <div id="project-doc-editor" class="docs-canvas__editor"></div>
                    </div>
                </div>
            </div>
        `;
        initializeDocsEditor({
            editorSelector: '#project-doc-editor',
            placeholder: '',
            onCreate: () => {
                setDocsEditorEditable(false);
            },
            onCheckboxChange: (contentJson) => {
                void savePublicDocCheckbox(publicToken, contentJson);
            }
        });
        setDocsEditorContent(item.contentJson || EMPTY_PROJECT_DOC_CONTENT);
        setDocsEditorEditable(false);
        updatePublicDocsThemeToggle();
        bindPublicDocsWidthToggle();
        // Run beautify in view mode only
        beautifyInternalLinks(document.querySelector('#project-doc-editor .ProseMirror'), true);
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                refreshDocsEditorLayout();
            });
        });
    } catch (error) {
        const isExpired = error?.status === 410 || error?.payload?.code === 'LINK_EXPIRED';
        const isRevoked = !isExpired && error?.status === 404;
        const title = isExpired
            ? t('docs.linkExpiredTitle')
            : (isRevoked ? t('docs.linkUnavailableTitle') : t('docs.emptyTitle'));
        const msg = isExpired
            ? t('docs.linkExpired')
            : (isRevoked ? t('docs.linkUnavailable') : (error?.message || String(error)));
        container.innerHTML = `<div class="docs-page-empty"><h2>${escapeHtml(title)}</h2><p>${escapeHtml(msg)}</p></div>`;
    }
}

function refreshDocsSurfaces(projectId, options = {}) {
    if (document.getElementById('docs')?.classList.contains('active')) {
        const preserveScroll = options.preserveScroll !== false;
        const scrollEl = document.getElementById('docs-page-content');
        const savedScroll = preserveScroll && scrollEl ? scrollEl.scrollTop : 0;
        renderDocsPageContent();
        if (preserveScroll && scrollEl && savedScroll > 0) {
            requestAnimationFrame(() => { scrollEl.scrollTop = savedScroll; });
        } else if (scrollEl) {
            scrollEl.scrollTop = 0;
        }
    }
    updateDocsNavTree(projectId);
}

/* ── Docs search (client-side) ─────────────────────────────────────────────
   GET /api/docs already returns content_json for every page (loadAllDocRows in
   functions/api/docs.js), so the full body text is in memory — searching it
   needs no new endpoint. Extracting plain text from the ProseMirror JSON is the
   only real cost, so results are memoised per doc and invalidated by updatedAt. */
let docsNavSearchQuery = '';
const _docsTextCache = new Map();

function getDocsItemPlainText(item) {
    if (!item?.id) return '';
    const key = `${item.id}:${item.updatedAt || ''}`;
    const cached = _docsTextCache.get(item.id);
    if (cached && cached.key === key) return cached.text;

    const parts = [];
    const walk = (node) => {
        if (!node || typeof node !== 'object') return;
        if (typeof node.text === 'string') parts.push(node.text);
        const children = Array.isArray(node.content) ? node.content : [];
        for (const child of children) walk(child);
    };
    try {
        walk(item.contentJson);
    } catch (_) { /* corrupted content must not break search */ }

    const text = parts.join(' ').toLowerCase();
    _docsTextCache.set(item.id, { key, text });
    return text;
}

/** Ancestor chain for a doc, outermost first. Excludes the doc itself. */
function getDocsAncestors(projectId, docId) {
    const state = getDocsState(projectId);
    const byId = new Map((state.items || []).map((item) => [String(item.id), item]));
    const chain = [];
    const seen = new Set();

    let parentId = String(byId.get(String(docId))?.parentId || '');
    while (parentId && !seen.has(parentId)) {
        seen.add(parentId);
        const parent = byId.get(parentId);
        if (!parent) break;
        chain.unshift(parent);
        parentId = String(parent.parentId || '');
    }
    return chain;
}

/** Pages whose title or body matches the query. Title hits rank above body hits. */
function getDocsSearchResults(projectId, rawQuery) {
    const query = String(rawQuery || '').trim().toLowerCase();
    if (!query) return [];

    const state = getDocsState(projectId);
    const results = [];
    for (const item of state.items || []) {
        const title = String(item.title || '').toLowerCase();
        const inTitle = title.includes(query);
        const inContent = !inTitle && getDocsItemPlainText(item).includes(query);
        if (inTitle || inContent) {
            results.push({ item, inTitle });
        }
    }

    results.sort((a, b) => {
        if (a.inTitle !== b.inTitle) return a.inTitle ? -1 : 1;
        return String(a.item.title || '').localeCompare(String(b.item.title || ''));
    });
    return results;
}

function buildDocsSearchResultsHtml(projectId, results) {
    if (!results.length) {
        return `<div class="nav-docs-search-empty">${escapeHtml(t('docs.searchNoResults'))}</div>`;
    }

    const rows = results.map(({ item, inTitle }) => {
        const ancestors = getDocsAncestors(projectId, item.id);
        const path = ancestors
            .map((ancestor) => escapeHtml(ancestor.title || t('docs.untitledPage')))
            .join(' / ');
        const slug = slugifyProjectDocTitle(item.title || '');
        const href = `#docs?doc=${encodeURIComponent(item.id)}${slug ? `&slug=${encodeURIComponent(slug)}` : ''}`;
        return `
            <div class="nav-docs-search-row">
                <a class="nav-docs-tree-item nav-docs-search-item" data-doc-nav-id="${item.id}" href="${href}">
                    <span class="nav-docs-tree-icon" aria-hidden="true">${escapeHtml(item.treeIcon || item.icon || '📄')}</span>
                    <span class="nav-docs-search-text">
                        <span class="nav-docs-tree-item-text">${escapeHtml(item.title || t('docs.untitledPage'))}</span>
                        ${path ? `<span class="nav-docs-search-path">${path}</span>` : ''}
                    </span>
                    <span class="nav-docs-search-where">${escapeHtml(t(inTitle ? 'docs.searchInTitle' : 'docs.searchInContent'))}</span>
                </a>
            </div>`;
    }).join('');

    return `
        <div class="nav-docs-search-count">${escapeHtml(t('docs.searchResultCount', { count: results.length }))}</div>
        ${rows}`;
}

/** Ancestor trail above the page title. Renders nothing for a root page. */
function buildDocsBreadcrumbHtml(projectId, current) {
    if (!current?.id) return '';
    const ancestors = getDocsAncestors(projectId, current.id);
    if (!ancestors.length) return '';

    const crumbs = ancestors.map((ancestor) => `
        <li class="docs-breadcrumb__crumb">
            <button type="button" class="docs-breadcrumb__link" data-doc-breadcrumb-id="${ancestor.id}">
                ${ancestor.icon ? `<span class="docs-breadcrumb__icon" aria-hidden="true">${escapeHtml(ancestor.icon)}</span>` : ''}
                <span class="docs-breadcrumb__label">${escapeHtml(ancestor.title || t('docs.untitledPage'))}</span>
            </button>
            <span class="docs-breadcrumb__sep" aria-hidden="true">/</span>
        </li>`).join('');

    return `
        <nav class="docs-breadcrumb" aria-label="${escapeHtml(t('docs.breadcrumbAria'))}">
            <ol class="docs-breadcrumb__list">
                ${crumbs}
                <li class="docs-breadcrumb__crumb docs-breadcrumb__crumb--current">
                    <span class="docs-breadcrumb__current" aria-current="page">${escapeHtml(current.title || t('docs.untitledPage'))}</span>
                </li>
            </ol>
        </nav>`;
}

function bindDocsBreadcrumb(projectId) {
    document.querySelectorAll('[data-doc-breadcrumb-id]').forEach((btn) => {
        if (btn.dataset.bound === 'true') return;
        btn.dataset.bound = 'true';
        btn.addEventListener('click', async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await selectDocPage(projectId, btn.dataset.docBreadcrumbId);
            renderDocsPage();
        });
    });
}

/* ── Docs slash-command menu ──────────────────────────────────────────────────
   Type "/" in the body to insert a block without reaching for the toolbar:
   "/" then "sta" then Enter drops in a status chip. Filtering matches the
   localized label AND English aliases, so "/status" works in Spanish too.

   Every entry routes through runDocsEditorCommand — the same executor the
   toolbar uses — so there is one command path, not two that can drift. */
const DOCS_SLASH_COMMANDS = [
    { id: 'paragraph',   icon: '¶',  labelKey: 'docs.toolbar.paragraph', aliases: ['paragraph', 'text', 'body', 'normal', 'plain'], run: () => runDocsEditorCommand('paragraph') },
    { id: 'heading1',    icon: 'H1', labelKey: 'docs.toolbar.heading1',  aliases: ['heading', 'title', 'h1'],                run: () => runDocsEditorCommand('heading', 1) },
    { id: 'heading2',    icon: 'H2', labelKey: 'docs.toolbar.heading2',  aliases: ['heading', 'subtitle', 'h2'],             run: () => runDocsEditorCommand('heading', 2) },
    { id: 'heading3',    icon: 'H3', labelKey: 'docs.toolbar.heading3',  aliases: ['heading', 'h3'],                         run: () => runDocsEditorCommand('heading', 3) },
    { id: 'bulletList',  icon: '•',  labelKey: 'docs.toolbar.bullets',   aliases: ['bullet', 'list', 'unordered'],           run: () => runDocsEditorCommand('bulletList') },
    { id: 'orderedList', icon: '1.', labelKey: 'docs.toolbar.numbers',   aliases: ['number', 'ordered', 'list'],             run: () => runDocsEditorCommand('orderedList') },
    { id: 'taskList',    icon: '☑',  labelKey: 'docs.toolbar.checklist', aliases: ['task', 'todo', 'checklist', 'checkbox'], run: () => runDocsEditorCommand('taskList') },
    // `picker: true` — opens its own dialog and handles insert + dirty state, so
    // the chip lands already labelled instead of as a placeholder to click later.
    // Both pickers are anchored to the CARET (coordsAtPos), never to the editor
    // element — anchoring to the editor drops the dialog at the bottom of the page,
    // far from where the user is typing.
    { id: 'status',      icon: '🏷',  labelKey: 'docs.toolbar.status',    aliases: ['status', 'chip', 'label', 'tag'],        picker: true, run: (pid) => openDocsStatusChipPicker(pid, null, getDocsCaretRect()) },
    // NOTE the shape difference: the date popover positions from {x, y}, the status
    // popover from a DOMRect-like {left, bottom}. getDocsCaretRect() returns the latter.
    { id: 'date',        icon: '📅', labelKey: 'docs.toolbar.dateChip',  aliases: ['date', 'calendar', 'day'],               picker: true, run: (pid) => {
        const caret = getDocsCaretRect();
        return openDocsDateChipPicker(pid, null, caret ? { x: caret.left, y: caret.bottom } : null);
    } },
    { id: 'callout',     icon: 'ℹ',  labelKey: 'docs.toolbar.callout',   aliases: ['callout', 'info', 'note', 'panel'],      run: () => runDocsEditorCommand('callout', 'info') },
    { id: 'table',       icon: '▦',  labelKey: 'docs.toolbar.table',     aliases: ['table', 'grid'],                         run: () => runDocsEditorCommand('table') },
    { id: 'codeBlock',   icon: '</>',labelKey: 'docs.toolbar.code',      aliases: ['code', 'snippet', 'pre'],                run: () => runDocsEditorCommand('codeBlock') },
    { id: 'divider',     icon: '—',  labelKey: 'docs.toolbar.divider',   aliases: ['divider', 'rule', 'separator', 'hr'],    run: () => runDocsEditorCommand('divider') },
    { id: 'columns',     icon: '▥',  labelKey: 'docs.toolbar.columns',   aliases: ['columns', 'layout', 'split'],            run: () => runDocsEditorCommand('columns') },
    { id: 'toc',         icon: '≡',  labelKey: 'docs.toolbar.toc',       aliases: ['toc', 'contents', 'outline', 'index'],   run: () => runDocsEditorCommand('toc') },
    { id: 'childList',   icon: '🗂',  labelKey: 'docs.toolbar.childList', aliases: ['child', 'children', 'pages', 'subpage'], run: () => runDocsEditorCommand('childList') },
    { id: 'programTimeline', icon: '▤', labelKey: 'docs.toolbar.programTimeline', aliases: ['timeline', 'roadmap', 'cronograma'], run: () => runDocsEditorCommand('programTimeline') }
];

let _docsSlashState = { open: false, index: 0, matches: [], range: null };
let _docsSlashScrollBound = false;

function getDocsSlashMenuEl() {
    let menu = document.getElementById('docs-slash-menu');
    if (menu) return menu;

    menu = document.createElement('div');
    menu.id = 'docs-slash-menu';
    menu.className = 'docs-slash-menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;
    // Portal to <body> + position:fixed — the editor and toolbar are inside
    // overflow/transform ancestors that would clip an absolutely positioned menu.
    document.body.appendChild(menu);
    return menu;
}

function filterDocsSlashCommands(query) {
    const q = String(query || '').trim().toLowerCase();
    const scored = [];

    for (const cmd of DOCS_SLASH_COMMANDS) {
        const label = String(t(cmd.labelKey) || '').toLowerCase();
        if (!q) {
            scored.push({ cmd, score: 0 });
            continue;
        }
        let score = -1;
        if (label.startsWith(q)) score = 3;
        else if (cmd.aliases.some((alias) => alias.startsWith(q))) score = 2;
        else if (label.includes(q)) score = 1;
        else if (cmd.aliases.some((alias) => alias.includes(q))) score = 0;
        if (score >= 0) scored.push({ cmd, score });
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.map((entry) => entry.cmd);
}

function positionDocsSlashMenu(menu) {
    const caret = getDocsCaretRect();
    if (!caret) return false;

    const width = menu.offsetWidth || 240;
    const height = menu.offsetHeight || 260;
    const left = Math.min(Math.max(caret.left, 8), window.innerWidth - width - 12);
    // Flip above the caret when there isn't room below.
    const below = caret.bottom + 6;
    const top = (below + height > window.innerHeight - 8)
        ? Math.max(8, caret.top - height - 6)
        : below;

    menu.style.left = `${Math.round(left)}px`;
    menu.style.top = `${Math.round(top)}px`;
    return true;
}

function renderDocsSlashMenu() {
    const menu = getDocsSlashMenuEl();
    const { matches, index } = _docsSlashState;

    menu.innerHTML = matches.map((cmd, i) => `
        <button type="button" class="docs-slash-item${i === index ? ' is-active' : ''}"
            role="option" aria-selected="${i === index ? 'true' : 'false'}" data-slash-index="${i}">
            <span class="docs-slash-item__icon" aria-hidden="true">${escapeHtml(cmd.icon)}</span>
            <span class="docs-slash-item__label">${escapeHtml(t(cmd.labelKey))}</span>
        </button>`).join('');

    menu.querySelectorAll('[data-slash-index]').forEach((btn) => {
        // mousedown, not click: click fires after the editor has already lost
        // focus/selection, which would break the range we need to replace.
        btn.addEventListener('mousedown', (event) => {
            event.preventDefault();
            runDocsSlashCommand(Number(btn.dataset.slashIndex));
        });
    });

    menu.querySelector('.docs-slash-item.is-active')?.scrollIntoView({ block: 'nearest' });
}

function closeDocsSlashMenu() {
    _docsSlashState = { open: false, index: 0, matches: [], range: null };
    const menu = document.getElementById('docs-slash-menu');
    if (menu) menu.hidden = true;
}

function updateDocsSlashMenu(projectId) {
    const ctx = getDocsSlashContext();
    if (!ctx) {
        closeDocsSlashMenu();
        return;
    }

    const matches = filterDocsSlashCommands(ctx.query);
    if (!matches.length) {
        // Nothing matches — get out of the way so typing behaves normally
        // instead of trapping Enter/Arrow keys in an empty menu.
        closeDocsSlashMenu();
        return;
    }

    const menu = getDocsSlashMenuEl();
    menu.dataset.projectId = projectId == null ? '' : String(projectId);
    _docsSlashState = {
        open: true,
        index: 0,
        matches,
        range: { from: ctx.from, to: ctx.to }
    };
    menu.hidden = false;
    renderDocsSlashMenu();
    if (!positionDocsSlashMenu(menu)) closeDocsSlashMenu();

    if (!_docsSlashScrollBound) {
        _docsSlashScrollBound = true;
        document.addEventListener('scroll', () => {
            if (!_docsSlashState.open) return;
            const el = document.getElementById('docs-slash-menu');
            if (el) positionDocsSlashMenu(el);
        }, { capture: true, passive: true });
    }
}

function moveDocsSlashSelection(delta) {
    const count = _docsSlashState.matches.length;
    if (!count) return;
    _docsSlashState.index = (_docsSlashState.index + delta + count) % count;
    renderDocsSlashMenu();
}

function runDocsSlashCommand(index) {
    const { matches, range } = _docsSlashState;
    const cmd = matches[index];
    if (!cmd || !range) return;

    const raw = document.getElementById('docs-slash-menu')?.dataset.projectId || '';
    const projectId = raw === '' ? null : raw;
    closeDocsSlashMenu();

    // Delete the typed "/query" FIRST so the node isn't inserted beside it.
    deleteDocsRange(range.from, range.to);

    if (cmd.picker) {
        // The picker inserts the node itself and owns focus + dirty state.
        // There is no global unhandledrejection handler, so catch here or a
        // failure is invisible.
        Promise.resolve(cmd.run(projectId)).catch((err) => {
            console.error('docs slash command failed', err);
            showErrorNotification(t('docs.slashCommandFailed'));
        });
        return;
    }

    cmd.run(projectId);
    focusDocsEditor();
    updateDocsDirtyState(projectId);
    updateDocsTableMenuVisibility();
}

/** Capture-phase so the menu claims the nav keys before ProseMirror sees them. */
function bindDocsSlashKeys(editorEl) {
    if (!editorEl || editorEl.dataset.slashBound === 'true') return;
    editorEl.dataset.slashBound = 'true';

    editorEl.addEventListener('keydown', (event) => {
        if (!_docsSlashState.open) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                event.stopPropagation();
                moveDocsSlashSelection(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                event.stopPropagation();
                moveDocsSlashSelection(-1);
                break;
            case 'Enter':
            case 'Tab':
                event.preventDefault();
                event.stopPropagation();
                runDocsSlashCommand(_docsSlashState.index);
                break;
            case 'Escape':
                event.preventDefault();
                event.stopPropagation();
                closeDocsSlashMenu();
                break;
            default:
                break;
        }
    }, { capture: true });

    editorEl.addEventListener('blur', () => {
        // Delay so a mousedown on a menu item still resolves.
        setTimeout(() => {
            if (!document.getElementById('docs-slash-menu')?.matches(':hover')) closeDocsSlashMenu();
        }, 120);
    });
}

/** Bound once — the input lives outside #nav-docs-tree so it survives re-renders. */
function bindDocsNavSearch() {
    const input = document.getElementById('docs-nav-search');
    if (!input || input.dataset.bound === 'true') return;
    input.dataset.bound = 'true';

    const clearBtn = document.getElementById('docs-nav-search-clear');
    const apply = () => {
        docsNavSearchQuery = input.value;
        if (clearBtn) clearBtn.hidden = !input.value;
        updateDocsNavTree();
    };

    input.addEventListener('input', apply);
    input.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        input.value = '';
        apply();
        input.blur();
    });
    clearBtn?.addEventListener('click', () => {
        input.value = '';
        apply();
        input.focus();
    });
}

// Docs sidebar hide/show. The user's choice lives in settings.docsNavHidden
// (saved server-side with the rest of settings, so it follows them across
// devices). Hiding is CSS-only (.is-docs-hidden on the section) — the tree
// keeps rendering underneath, so showing it again is instant.
function applyDocsNavVisibility() {
    const section = document.querySelector('.nav-section-docs');
    const toggle = document.getElementById('nav-docs-visibility-toggle');
    const hidden = settings.docsNavHidden === true;
    if (section) section.classList.toggle('is-docs-hidden', hidden);
    if (toggle) {
        const label = t(hidden ? 'nav.showDocs' : 'nav.hideDocs');
        toggle.setAttribute('aria-pressed', hidden ? 'true' : 'false');
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('title', label);
    }
}

function bindDocsNavVisibilityToggle() {
    const toggle = document.getElementById('nav-docs-visibility-toggle');
    if (!toggle || toggle.dataset.bound === 'true') return;
    toggle.dataset.bound = 'true';
    toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        settings.docsNavHidden = settings.docsNavHidden !== true;
        applyDocsNavVisibility();
        saveSettings();
    });
}

function updateDocsNavTree(projectId) {
    const tree = document.getElementById('nav-docs-tree');
    if (!tree) return;
    bindDocsNavSearch();
    const state = getDocsState();
    const treeItems = getDocsTree(null);
    const expandableTreeIds = [];
    const collectExpandableTreeIds = (items) => {
        items.forEach((item) => {
            if ((item.children || []).length) {
                expandableTreeIds.push(String(item.id));
                collectExpandableTreeIds(item.children || []);
            }
        });
    };
    collectExpandableTreeIds(treeItems);
    const hasCollapsedTreeItems = treeItems.some((item) => {
        const stack = [item];
        while (stack.length) {
            const current = stack.pop();
            if (!current) continue;
            if ((current.children || []).length && current.collapsed) {
                return true;
            }
            stack.push(...(current.children || []));
        }
        return false;
    });
    const bulkMode = state.bulkMode === true;
    const selectedDocIds = state.selectedDocIds || new Set();

    // Do not retry when a previous attempt errored — prevents the infinite loop
    // where the error catch calls refreshDocsSurfaces → updateDocsNavTree → retry.
    // Explicit navigation (renderDocsPage) clears state.error before calling
    // ensureProjectDocsListLoaded, so retries still work for user-initiated reloads.
    if (!state.loaded && !state.loading && !state.error) {
        state.loading = true;
        void ensureDocsListLoaded();
    }

    const docsRoute = getDocsRouteState();
    const docsPageOn = !!document.getElementById('docs')?.classList.contains('active');
    const projectDocsTabOn = !!document.querySelector('.project-docs-tab.active');
    const activeDocId = (docsRoute.isDocsRoute || docsPageOn || projectDocsTabOn) ? state.currentDocId : null;
    // Native HTML5 drag (dragstart/dragover/drop) never fires from touch input —
    // iOS Safari has never supported it — so draggable rows and their drag
    // listeners are dead weight on a phone, not a feature being taken away.
    // Searching flattens the tree into results, so reordering by drag makes no
    // sense while a query is active.
    const searching = !bulkMode && !!String(docsNavSearchQuery || '').trim();
    const useNativeTreeDrag = !bulkMode && !searching && !isDocsDesktopWidthTablet() && !getIsMobileCached();

    let pagesHtml;
    if (state.loading) {
        pagesHtml = `<div class="nav-docs-tree-loading">···</div>`;
    } else if (!state.items.length) {
        pagesHtml = `<div class="nav-docs-tree-empty">${t('docs.createFirstInline')}</div>`;
    } else if (searching) {
        pagesHtml = buildDocsSearchResultsHtml(null, getDocsSearchResults(null, docsNavSearchQuery));
    } else {
        const buildDocHref = (item) => {
            const slug = slugifyProjectDocTitle(item?.title || '');
            return `#docs?doc=${encodeURIComponent(item.id)}${slug ? `&slug=${encodeURIComponent(slug)}` : ''}`;
        };
        const renderRow = (item, level = 0) => `
            <div class="nav-docs-tree-row level-${level}${item.id === activeDocId ? ' active' : ''}${bulkMode ? ' is-bulk-mode' : ''}${selectedDocIds.has(String(item.id)) ? ' selected' : ''}" draggable="${useNativeTreeDrag ? 'true' : 'false'}" data-doc-id="${item.id}" data-doc-level="${level}" data-selected="${selectedDocIds.has(String(item.id)) ? 'true' : 'false'}">
                ${(item.children || []).length
                    ? `<button type="button" class="nav-docs-tree-toggle${item.collapsed ? ' is-collapsed' : ''}" data-doc-toggle="${item.id}" aria-label="${item.collapsed ? t('docs.expandPage') : t('docs.collapsePage')}">
                        <svg viewBox="0 0 12 12" aria-hidden="true" class="nav-docs-tree-toggle-icon">
                            <path d="M4 2.5L8 6L4 9.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>
                    </button>`
                    : `<span class="nav-docs-tree-toggle-spacer" aria-hidden="true"></span>`}
                <label class="nav-docs-tree-select${bulkMode ? ' is-visible' : ''}" aria-label="${t('docs.bulkSelectPage')}">
                    <input type="checkbox" class="nav-docs-tree-select-input" data-doc-bulk-toggle="${item.id}" ${selectedDocIds.has(String(item.id)) ? 'checked' : ''}>
                </label>
                <a class="nav-docs-tree-item${item.id === activeDocId ? ' active' : ''}${bulkMode ? ' is-bulk-toggle' : ''}" data-doc-nav-id="${item.id}" href="${buildDocHref(item)}">
                    <span class="nav-docs-tree-icon" aria-hidden="true">${escapeHtml(item.treeIcon || '📄')}</span>
                    <span class="nav-docs-tree-item-text">${escapeHtml(item.title || t('docs.untitledPage'))}</span>
                </a>
                <div class="nav-docs-tree-row-actions">
                    ${!bulkMode && level < 2 ? `<button type="button" class="nav-docs-tree-row-action nav-docs-tree-row-action--add" data-doc-add-child="${item.id}" title="${t('docs.newPage')}">
                        <svg viewBox="0 0 16 16" aria-hidden="true" class="nav-docs-tree-row-action-icon">
                            <path d="M8 3.25v9.5M3.25 8h9.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
                        </svg>
                    </button>` : ''}
                    ${!bulkMode ? `<button type="button" class="nav-docs-tree-row-action nav-docs-tree-row-action--more" data-doc-more="${item.id}" title="${t('docs.pageOptions')}">
                        <svg viewBox="0 0 16 16" aria-hidden="true" class="nav-docs-tree-row-action-icon">
                            <path d="M3 5.5h10M3 8h10M3 10.5h10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path>
                        </svg>
                    </button>` : ''}
                </div>
            </div>
            ${item.collapsed ? '' : (item.children || []).map((child) => renderRow(child, level + 1)).join('')}
        `;
        pagesHtml = treeItems.map((item) => renderRow(item)).join('');
    }

    const selectedCount = selectedDocIds.size;
    tree.innerHTML = `
        <div class="nav-docs-tree-header${bulkMode ? ' nav-docs-tree-header--bulk' : ''}">
            ${bulkMode ? `
                <span class="nav-docs-tree-bulk-count">${t('docs.bulkSelectCount', { count: selectedCount })}</span>
                <div class="nav-docs-tree-header-actions">
                    <button type="button" class="nav-docs-tree-header-btn nav-docs-tree-header-btn--cancel" data-doc-bulk-cancel>${t('docs.bulkSelectCancel')}</button>
                    <button type="button" class="nav-docs-tree-header-btn nav-docs-tree-header-btn--delete" data-doc-bulk-delete ${selectedCount === 0 ? 'disabled' : ''}>${t('common.delete')}</button>
                </div>
            ` : `
                <div class="nav-docs-tree-project-wrap">
                    <span class="nav-docs-tree-project">${t('docs.pages')}</span>
                    <button
                        type="button"
                        class="nav-docs-tree-header-btn nav-docs-tree-header-btn--icon nav-docs-tree-header-btn--tree-toggle"
                        data-doc-tree-toggle-all
                        aria-label="${escapeHtml(t(hasCollapsedTreeItems ? 'docs.expandAllPages' : 'docs.collapseAllPages'))}"
                        title="${escapeHtml(t(hasCollapsedTreeItems ? 'docs.expandAllPages' : 'docs.collapseAllPages'))}"
                        ${expandableTreeIds.length ? '' : 'disabled'}
                    >
                        <svg viewBox="0 0 16 16" aria-hidden="true" class="nav-docs-tree-header-btn-icon">
                            <path d="M2.95 3.35 5.35 6.15H0.55Z" fill="currentColor"/>
                            <path d="M2.95 10.05 0.55 7.25h4.8Z" fill="currentColor"/>
                            <path d="M7.1 4.35h6.15" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                            <path d="M7.1 8h6.15" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                            <path d="M3.95 11.65h9.3" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
                        </svg>
                    </button>
                </div>
                <div class="nav-docs-tree-header-actions">
                    <button
                        type="button"
                        class="nav-docs-tree-header-btn nav-docs-tree-header-btn--select nav-docs-tree-header-btn--icon"
                        data-doc-bulk-start
                        aria-label="${escapeHtml(t('docs.bulkSelect'))}"
                        title="${escapeHtml(t('docs.bulkSelect'))}"
                    >
                        <svg viewBox="0 0 20 20" aria-hidden="true" class="nav-docs-tree-header-btn-icon">
                            <path d="M3 5.5l1.6 1.6 2.4-2.4" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M3 10.5l1.6 1.6 2.4-2.4" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M3 15.5l1.6 1.6 2.4-2.4" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/>
                            <line x1="9" y1="6.5" x2="17.5" y2="6.5" stroke="currentColor" stroke-width="1.65" stroke-linecap="round"/>
                            <line x1="9" y1="11.5" x2="17.5" y2="11.5" stroke="currentColor" stroke-width="1.65" stroke-linecap="round"/>
                            <line x1="9" y1="16.5" x2="15" y2="16.5" stroke="currentColor" stroke-width="1.65" stroke-linecap="round"/>
                        </svg>
                    </button>
                    <button type="button" class="nav-docs-tree-header-add" title="${t('docs.newPage')}">+</button>
                </div>
            `}
        </div>
        ${pagesHtml}
    `;

    /* No select in tree — project switching handled by nav only */

    const newBtn = tree.querySelector('.nav-docs-tree-header-add');
    if (newBtn) {
        newBtn.addEventListener('click', async () => {
            if (getIsMobileCached() && !isDocsDesktopWidthTablet()) closeMobileSidebar();
            if (!document.getElementById('docs')?.classList.contains('active')) {
                window.location.hash = 'docs';
            }
            await createNewDocPage(null);
            renderDocsPage();
        });
    }

    const bulkStartBtn = tree.querySelector('[data-doc-bulk-start]');
    if (bulkStartBtn) {
        bulkStartBtn.addEventListener('click', () => {
            enterDocsBulkSelection(null);
            updateDocsNavTree();
        });
    }

    const treeToggleAllBtn = tree.querySelector('[data-doc-tree-toggle-all]');
    if (treeToggleAllBtn) {
        treeToggleAllBtn.addEventListener('click', () => {
            const nextCollapsed = !hasCollapsedTreeItems;
            expandableTreeIds.forEach((docId) => {
                setDocTreeMeta(docId, { collapsed: nextCollapsed });
            });
            updateDocsNavTree();
        });
    }

    const bulkCancelBtn = tree.querySelector('[data-doc-bulk-cancel]');
    if (bulkCancelBtn) {
        bulkCancelBtn.addEventListener('click', () => {
            resetDocsBulkSelection();
            updateDocsNavTree();
        });
    }

    const bulkDeleteBtn = tree.querySelector('[data-doc-bulk-delete]');
    if (bulkDeleteBtn) {
        bulkDeleteBtn.addEventListener('click', async () => {
            await deleteSelectedProjectDocs(null);
            renderDocsPage();
        });
    }

    tree.querySelectorAll('[data-doc-add-child]').forEach((btn) => {
        btn.addEventListener('click', async (event) => {
            event.stopPropagation();
            if (getIsMobileCached() && !isDocsDesktopWidthTablet()) closeMobileSidebar();
            await createNewDocPage(null, { parentId: btn.dataset.docAddChild });
            renderDocsPage();
        });
    });

    tree.querySelectorAll('[data-doc-toggle]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
            event.stopPropagation();
            const docId = btn.dataset.docToggle;
            const meta = getDocTreeMeta(docId);
            setDocTreeMeta(docId, { collapsed: !meta.collapsed });
            updateDocsNavTree();
        });
    });

    tree.querySelectorAll('[data-doc-more]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
            event.stopPropagation();
            openDocsRowMenu(btn, null, btn.dataset.docMore);
        });
    });

    // Every device — desktop, iPad, phone — navigates the tree through the
    // ordinary click path, exactly like .nav-item does. Touch devices used to
    // get a bespoke touchend/pointerup path here on the theory that WebKit
    // wouldn't fire the click; the real cause was the :hover reveal reshaping
    // the row under the finger, which is neutralised in CSS (see the
    // body.is-docs-tablet and @media (hover:none) blocks in style.css).
    tree.querySelectorAll('[data-doc-nav-id]').forEach((btn) => {
        btn.addEventListener('click', async (event) => {
            await activateDocsTreeNav(btn, event, { projectId, bulkMode });
        });
    });

    tree.querySelectorAll('[data-doc-bulk-toggle]').forEach((input) => {
        input.addEventListener('click', (event) => {
            event.stopPropagation();
        });
        input.addEventListener('change', () => {
            toggleDocsBulkSelection(null, input.dataset.docBulkToggle);
            updateDocsNavTree();
        });
    });

    if (useNativeTreeDrag) {
        initDocsTreeDragDrop(tree, null);
    }
}

async function activateDocsTreeNav(link, event, { projectId = null, bulkMode = false } = {}) {
    const docId = link?.dataset?.docNavId;
    const nonPrimaryMouseButton = typeof event?.button === 'number' && event.button !== 0;
    if (!docId || event?.defaultPrevented || nonPrimaryMouseButton || event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey) {
        return;
    }

    event?.preventDefault?.();
    if (bulkMode) {
        toggleDocsBulkSelection(projectId, docId);
        updateDocsNavTree(projectId);
        return;
    }

    if (getIsMobileCached() && !isDocsDesktopWidthTablet()) closeMobileSidebar();
    if (!document.getElementById('docs')?.classList.contains('active')) {
        // Pre-set target doc so the hash-change-triggered updateDocsNavTree
        // highlights the correct doc immediately, avoiding a stale-highlight jump.
        getDocsState(projectId).currentDocId = docId;
        window.location.hash = 'docs';
        await new Promise((r) => setTimeout(r, 60));
        // Reset so selectDocPage's early-return guard doesn't skip syncDocsRouteState,
        // which would leave the URL stuck at #docs without the doc/slug params.
        getDocsState(projectId).currentDocId = null;
    }

    await selectDocPage(projectId, docId);
    renderDocsPage();
}


// ── Docs Tree Drag-and-Drop ───────────────────────────────────────────────────

/** Returns true if potentialParentId is the same as docId or any ancestor of docId (cycle check). */
function wouldCreateCycle(stateItems, draggedId, newParentId) {
    if (!newParentId) return false;
    if (String(newParentId) === String(draggedId)) return true;
    const itemsById = new Map(stateItems.map((item) => [String(item.id), item]));
    let node = itemsById.get(String(newParentId));
    const visited = new Set();
    while (node) {
        if (visited.has(String(node.id))) break;
        visited.add(String(node.id));
        if (String(node.parentId || '') === String(draggedId)) return true;
        if (!node.parentId) break;
        node = itemsById.get(String(node.parentId));
    }
    return false;
}

async function applyDocsDrop(projectId, draggedId, targetId, dropMode) {
    const state = getDocsState(projectId);
    const target = state.items.find((item) => String(item.id) === String(targetId));
    if (!target) return;

    if (dropMode === 'child') {
        // Reparent: dragged becomes a child of target
        const siblings = state.items.filter((item) => String(item.parentId || '') === String(targetId));
        if (siblings.length >= 20) return; // max children
        const maxOrder = siblings.reduce((max, item) => Math.max(max, Number(item.treeOrder) || 0), 0);
        await persistProjectDocMeta(projectId, draggedId, {
            parentId: String(targetId),
            treeOrder: maxOrder + 10
        });
    } else {
        // Reorder: insert before/after target within target's parent
        const newParentId = String(target.parentId || '');
        const siblings = state.items
            .filter((item) => String(item.parentId || '') === newParentId && String(item.id) !== String(draggedId))
            .sort((a, b) => (Number(a.treeOrder) || 0) - (Number(b.treeOrder) || 0));

        const targetIdx = siblings.findIndex((item) => String(item.id) === String(targetId));
        if (targetIdx === -1) return;

        const targetOrder = Number(siblings[targetIdx].treeOrder) || 0;
        let newTreeOrder;
        if (dropMode === 'before') {
            const prevOrder = targetIdx > 0 ? (Number(siblings[targetIdx - 1].treeOrder) || 0) : (targetOrder - 10);
            newTreeOrder = (prevOrder + targetOrder) / 2;
        } else {
            const nextOrder = targetIdx < siblings.length - 1 ? (Number(siblings[targetIdx + 1].treeOrder) || 0) : (targetOrder + 10);
            newTreeOrder = (targetOrder + nextOrder) / 2;
        }
        await persistProjectDocMeta(projectId, draggedId, {
            parentId: newParentId,
            treeOrder: newTreeOrder
        });
    }
    updateDocsNavTree(projectId);
}

function initDocsTreeDragDrop(tree, projectId) {
    let draggedId = null;

    const clearDragStyles = () => {
        tree.querySelectorAll('.is-dragging, .drag-over-child, .drag-over-before, .drag-over-after')
            .forEach((el) => el.classList.remove('is-dragging', 'drag-over-child', 'drag-over-before', 'drag-over-after'));
    };

    tree.querySelectorAll('[data-doc-id]').forEach((row) => {
        row.addEventListener('dragstart', (e) => {
            draggedId = row.dataset.docId;
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', draggedId);
            requestAnimationFrame(() => row.classList.add('is-dragging'));
        });

        row.addEventListener('dragend', () => {
            clearDragStyles();
            draggedId = null;
        });

        row.addEventListener('dragover', (e) => {
            if (!draggedId) return;
            const targetId = row.dataset.docId;
            if (targetId === draggedId) return;

            const state = getDocsState(projectId);
            const target = state.items.find((item) => String(item.id) === String(targetId));
            if (!target) return;

            const targetLevel = parseInt(row.dataset.docLevel || '0', 10);
            const rect = row.getBoundingClientRect();
            const pct = (e.clientY - rect.top) / rect.height;

            let candidateMode;
            if (pct < 0.33) {
                candidateMode = 'before';
            } else if (pct > 0.67) {
                candidateMode = 'after';
            } else if (targetLevel < 2) {
                candidateMode = 'child';
            } else {
                candidateMode = 'after';
            }

            const newParentId = candidateMode === 'child' ? String(targetId) : String(target.parentId || '');
            if (wouldCreateCycle(state.items, draggedId, newParentId)) return;

            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';

            clearDragStyles();
            row.classList.remove('is-dragging'); // keep dragged item faded
            tree.querySelectorAll(`.is-dragging`).forEach((el) => el.classList.add('is-dragging'));
            row.classList.add(`drag-over-${candidateMode}`);
            // Re-apply is-dragging to the dragged row (cleared above)
            tree.querySelector(`[data-doc-id="${draggedId}"]`)?.classList.add('is-dragging');
        });

        row.addEventListener('dragleave', (e) => {
            if (!row.contains(e.relatedTarget)) {
                row.classList.remove('drag-over-child', 'drag-over-before', 'drag-over-after');
            }
        });

        row.addEventListener('drop', async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const targetId = row.dataset.docId;
            if (!draggedId || targetId === draggedId) { clearDragStyles(); draggedId = null; return; }

            const state = getDocsState(projectId);
            const target = state.items.find((item) => String(item.id) === String(targetId));
            if (!target) { clearDragStyles(); draggedId = null; return; }

            const targetLevel = parseInt(row.dataset.docLevel || '0', 10);
            const rect = row.getBoundingClientRect();
            const pct = (e.clientY - rect.top) / rect.height;

            let dropMode;
            if (pct < 0.33) {
                dropMode = 'before';
            } else if (pct > 0.67) {
                dropMode = 'after';
            } else if (targetLevel < 2) {
                dropMode = 'child';
            } else {
                dropMode = 'after';
            }

            const newParentId = dropMode === 'child' ? String(targetId) : String(target.parentId || '');
            if (wouldCreateCycle(state.items, draggedId, newParentId)) { clearDragStyles(); draggedId = null; return; }

            clearDragStyles();
            const savedDraggedId = draggedId;
            draggedId = null;
            await applyDocsDrop(projectId, savedDraggedId, targetId, dropMode);
        });
    });
}

function syncDocsListTitle(projectId) {
    const state = getDocsState(projectId);
    const current = getCurrentDocsRecord(projectId);
    const titleInput = document.getElementById('project-doc-title-input');
    const title = String(titleInput?.value || '').trim();
    const displayTitle = title || t('docs.untitledPage');
    if (!current) return;
    current.title = title;
    document.querySelectorAll('.project-docs-list-item.active .project-docs-list-item-title, .docs-shell__list-item.active .docs-shell__list-title, .nav-docs-tree-item.active .nav-docs-tree-item-text').forEach((node) => {
        node.textContent = displayTitle;
    });
    const crumb = document.querySelector('.docs-canvas__crumb');
    if (crumb) crumb.textContent = displayTitle;
}

async function persistProjectDocMeta(projectId, docId, nextMeta = {}) {
    const state = getDocsState(projectId);
    const current = state.items.find((item) => String(item.id) === String(docId));
    if (!current) return null;
    const response = await updateProjectDocTree(docId, {
        icon: nextMeta.icon !== undefined ? String(nextMeta.icon || '') : String(current.icon || ''),
        parentId: nextMeta.parentId !== undefined ? String(nextMeta.parentId || '') : String(current.parentId || ''),
        treeOrder: Number.isFinite(Number(nextMeta.treeOrder)) ? Number(nextMeta.treeOrder) : Number(current.treeOrder || current.sortOrder || 0),
        widthMode: nextMeta.widthMode !== undefined
            ? (nextMeta.widthMode === 'narrow' ? 'narrow' : 'wide')
            : (current.widthMode === 'narrow' ? 'narrow' : 'wide')
    });
    return mergeDocsRecord(projectId, response?.item || {});
}

function updateDocsPageIconUi(projectId) {
    const current = getCurrentDocsRecord(projectId);
    const icon = current ? String(current.icon || '') : '';

    const iconView = document.getElementById('project-doc-title-icon-view');
    if (iconView) {
        iconView.textContent = icon;
        iconView.classList.toggle('is-hidden', !icon);
        iconView.setAttribute('aria-hidden', icon ? 'false' : 'true');
    }

    const iconButton = document.getElementById('project-doc-icon-btn');
    if (iconButton) {
        iconButton.textContent = icon || DOCS_EMPTY_ICON_PLACEHOLDER;
        iconButton.classList.toggle('is-empty', !icon);
        iconButton.classList.toggle('has-icon', !!icon);
    }

    const clearButton = document.getElementById('project-doc-icon-clear');
    if (clearButton) {
        clearButton.classList.toggle('is-hidden', !icon);
    }
}

function updateProjectDocsToc(projectId) {
    const editorRoot = document.querySelector('#project-doc-editor .docs-editor-content');
    const tocList = document.getElementById('project-doc-toc-list');
    if (!editorRoot || !tocList) return;

    const seen = new Map();
    const items = Array.from(editorRoot.querySelectorAll('h1, h2, h3')).map((heading) => {
        const text = (heading.textContent || '').trim();
        if (!text) return null;
        const baseSlug = text
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-') || 'section';
        const count = seen.get(baseSlug) || 0;
        seen.set(baseSlug, count + 1);
        const slug = count === 0 ? baseSlug : `${baseSlug}-${count + 1}`;
        heading.id = `project-doc-heading-${slug}`;
        return {
            level: Number(heading.tagName.replace('H', '')) || 1,
            text,
            slug
        };
    }).filter(Boolean);

    tocList.innerHTML = buildProjectDocsTocHtml(items);
    tocList.querySelectorAll('[data-doc-heading-target]').forEach((button) => {
        button.addEventListener('click', () => {
            const target = document.getElementById(`project-doc-heading-${button.dataset.docHeadingTarget}`);
            if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    });
}

function updateDocsDirtyState(projectId) {
    const state = getDocsState(projectId);
    if (state.hydrating) return false;
    if (!doesDocsBaselineMatchActiveSession(projectId)) {
        const mismatchKey = [
            state.editorBoundDocId || '',
            state.editorGeneration || 0,
            state.baselineDocId || '',
            state.baselineGeneration || 0
        ].join(':');
        state.dirty = false;
        if (state.lastBaselineMismatchEventKey !== mismatchKey) {
            state.lastBaselineMismatchEventKey = mismatchKey;
            void logDocsSaveEvent(projectId, {
                docId: state.editorBoundDocId || state.currentDocId || '',
                source: 'dirty_state',
                origin: 'client_guard',
                rejected: true,
                rejectionReason: 'baseline_session_mismatch',
                editorGeneration: state.editorGeneration
            });
        }
        updateDocsStatus(projectId);
        return false;
    }
    state.lastBaselineMismatchEventKey = '';
    const draft = getDocsDraft(projectId, getEditorBoundDocsRecord(projectId));
    state.dirty = serializeDocsDraftSnapshot(draft) !== (state.baselineSnapshot || '');
    if (state.dirty && !state.saving) state.error = '';
    updateDocsStatus(projectId);
    updateDocsUndoRedoButtons();
    return state.dirty;
}

function updateDocsUndoRedoButtons() {
    const { undo, redo } = getDocsEditorHistoryAvailability();
    document.querySelectorAll('[data-doc-command="undo"]').forEach((button) => {
        button.disabled = !undo;
    });
    document.querySelectorAll('[data-doc-command="redo"]').forEach((button) => {
        button.disabled = !redo;
    });
}

async function migrateLegacyDocsTreeMeta(projectId, items) {
    const migrationKey = getDocsTreeMigrationKey(projectId);
    if (localStorage.getItem(migrationKey) === 'true') return;
    const legacyMeta = getDocsTreeMetaStore();
    const itemsById = new Map((items || []).map((item) => [String(item.id), item]));
    const updates = [];

    Object.entries(legacyMeta || {}).forEach(([docId, meta]) => {
        if (!itemsById.has(String(docId))) return;
        const current = itemsById.get(String(docId));
        const nextIcon = meta?.icon !== undefined ? String(meta.icon || '') : String(current.icon || '');
        const nextParentId = meta?.parentId ? String(meta.parentId) : '';
        if (nextParentId && !itemsById.has(nextParentId)) return;
        if (nextIcon !== String(current.icon || '') || nextParentId !== String(current.parentId || '')) {
            updates.push(updateProjectDocTree(docId, {
                icon: nextIcon,
                parentId: nextParentId,
                treeOrder: Number(current.treeOrder || current.sortOrder || 0)
            }));
        }
    });

    if (updates.length) {
        await Promise.all(updates);
    }
    localStorage.setItem(migrationKey, 'true');
}

async function ensureDocsListLoaded(projectId, options = {}) {
    const state = getDocsState(projectId);
    if (state.loaded && !options.force) {
        return state.items;
    }

    // (C) Only the very first cold list load may consult the deep-link
    // snapshot. A later forced refresh is user-initiated and the live
    // route is authoritative, so the snapshot must not re-hijack it.
    const wasColdLoad = !state.loaded;

    state.loading = true;
    state.error = '';
    refreshDocsSurfaces(projectId);

    try {
        const response = await listProjectDocs();
        state.items = sortProjectDocsItems((response?.items || []).map((item) => normalizeDocsRecord(projectId, item)));
        await migrateLegacyDocsTreeMeta(projectId, state.items);
        if (options.force !== true) {
            const refreshed = await listProjectDocs();
            state.items = sortProjectDocsItems((refreshed?.items || []).map((item) => normalizeDocsRecord(projectId, item)));
        }
        state.loaded = true;
        const route = getDocsRouteState();
        // Validate currentDocId (may have been set from a stale route param before items loaded)
        if (state.currentDocId && !state.items.some((item) => item.id === state.currentDocId)) {
            state.currentDocId = '';
        }
        if (!state.currentDocId && route.isDocsRoute && route.docId) {
            const routeDocExists = state.items.some((item) => item.id === route.docId);
            if (routeDocExists) state.currentDocId = route.docId;
        }
        // (C) iPad hash/items race: the live hash may have been dropped or
        // clobbered before it was consumed. The deep-link was snapshotted
        // synchronously in index.html <head> — recover the original ?doc=.
        // Cold-load only; desktop-safe (snapshot == live route on desktop,
        // so this only ever re-selects the doc the URL already asked for).
        if (!state.currentDocId && wasColdLoad && route.isDocsRoute) {
            const snapshotDocId = getInitialDocsRouteDocId();
            if (snapshotDocId && state.items.some((item) => item.id === snapshotDocId)) {
                state.currentDocId = snapshotDocId;
            }
        }
        // (E) Only when the route carried NO docId (e.g. WebKit reload
        // race on iPad dropped it): restore the last-viewed doc before
        // falling back to the first one. Desktop deep-link/refresh has
        // ?doc= so route.docId is truthy and this is skipped entirely.
        if (!state.currentDocId && route.isDocsRoute && !route.docId) {
            const persistedId = _readPersistedDocId();
            if (persistedId && state.items.some((item) => item.id === persistedId)) {
                state.currentDocId = persistedId;
            }
        }
        if (!state.currentDocId && state.items.length > 0 && route.isDocsRoute) {
            state.currentDocId = state.items[0].id;
        }
        state.selectedDocIds = new Set(
            Array.from(state.selectedDocIds || []).filter((docId) =>
                state.items.some((item) => String(item.id) === String(docId))
            )
        );
        if (!state.items.length) {
            resetDocsBulkSelection(projectId);
        }
        state.loading = false;
        refreshDocsSurfaces(projectId);

        if (state.currentDocId) {
            await ensureDocLoaded(projectId, state.currentDocId);
        }
        return state.items;
    } catch (error) {
        state.loading = false;
        state.error = error?.message || String(error);
        refreshDocsSurfaces(projectId);
        showErrorNotification(state.error);
        return [];
    }
}

async function ensureDocLoaded(projectId, docId, options = {}) {
    const state = getDocsState(projectId);
    const existing = state.items.find((item) => item.id === docId);
    if (existing?.contentJson && !options.force) {
        setDocsBaseline(projectId, existing);
        if (existing.contentCorrupted) {
            state.error = t('docs.contentCorrupted');
        }
        syncDocsRouteState({
            docId: existing.id,
            slug: existing.slug || slugifyProjectDocTitle(existing.title),
            mode: getDocsRouteState().mode
        });
        refreshDocsSurfaces(projectId);
        scheduleDocsTabletScrollReset();
        return existing;
    }

    state.loadingDocId = docId;
    updateDocsStatus(projectId);

    try {
        const response = await loadProjectDoc(docId);
        const record = mergeDocsRecord(projectId, response?.item || {});
        state.currentDocId = record.id;
        state.loadingDocId = null;
        setDocsBaseline(projectId, record);
        if (record.contentCorrupted) {
            state.error = t('docs.contentCorrupted');
            showErrorNotification(state.error);
        }
        syncDocsRouteState({
            docId: record.id,
            slug: record.slug || slugifyProjectDocTitle(record.title),
            mode: getDocsRouteState().mode
        });
        refreshDocsSurfaces(projectId);
        scheduleDocsTabletScrollReset();
        return record;
    } catch (error) {
        state.loadingDocId = null;
        state.error = error?.message || String(error);
        updateDocsStatus(projectId);
        showErrorNotification(state.error);
        return null;
    }
}

const _dismissedTemplateBanners = new Set();

function _isDocEmpty(contentJson) {
    if (!contentJson || contentJson.type !== 'doc') return true;
    const content = contentJson.content || [];
    if (content.length === 0) return true;
    if (content.length === 1 && content[0].type === 'paragraph') {
        const inner = content[0].content || [];
        return inner.length === 0 || (inner.length === 1 && !inner[0].text);
    }
    return false;
}

function hideDocTemplateBanner() {
    document.getElementById('doc-template-banner')?.remove();
}

function showDocTemplateBanner(projectId) {
    hideDocTemplateBanner();

    const currentDoc = getCurrentDocsRecord(projectId);
    if (!currentDoc?.id) return;
    if (_dismissedTemplateBanners.has(currentDoc.id)) return;
    if (!_isDocEmpty(currentDoc.contentJson)) return;

    // Only show in edit mode — confusing in published/view mode
    const canvasRoot = document.getElementById('docs-canvas-root');
    if (canvasRoot?.dataset.mode !== 'edit') return;

    const body = document.querySelector('.docs-canvas__body');
    const editorWrap = body?.querySelector('.docs-canvas__editor-wrap');
    if (!body || !editorWrap) return;

    const banner = document.createElement('div');
    banner.id = 'doc-template-banner';
    banner.className = 'doc-template-banner';
    banner.setAttribute('role', 'button');
    banner.setAttribute('tabindex', '0');
    banner.innerHTML = `
        <div class="doc-template-banner-icon" aria-hidden="true">✨</div>
        <div class="doc-template-banner-text">
            <div class="doc-template-banner-title" data-i18n="docs.templates.bannerTitle">${t('docs.templates.bannerTitle')}</div>
            <div class="doc-template-banner-subtitle" data-i18n="docs.templates.bannerSubtitle">${t('docs.templates.bannerSubtitle')}</div>
        </div>
        <button type="button" class="doc-template-banner-dismiss" data-template-banner-dismiss title="${t('docs.templates.dismiss')}" aria-label="${t('docs.templates.dismiss')}">×</button>
    `;

    body.insertBefore(banner, editorWrap);

    const docId = currentDoc.id;

    banner.querySelector('[data-template-banner-dismiss]')?.addEventListener('click', (e) => {
        e.stopPropagation();
        _dismissedTemplateBanners.add(docId);
        hideDocTemplateBanner();
    });

    banner.addEventListener('click', () => {
        openDocTemplatePicker(projectId, docId);
    });
    banner.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openDocTemplatePicker(projectId, docId);
        }
    });

    // Dismiss when user starts typing in the editor
    window.requestAnimationFrame(() => {
        const proseMirror = document.querySelector('#project-doc-editor .ProseMirror');
        if (proseMirror) {
            const onInput = () => {
                _dismissedTemplateBanners.add(docId);
                hideDocTemplateBanner();
                proseMirror.removeEventListener('input', onInput);
            };
            proseMirror.addEventListener('input', onInput);
        }
    });
}

function applyDocTemplate(projectId, tpl) {
    if (!tpl) return;
    const titleInput = document.getElementById('project-doc-title-input');
    const titleDisplay = document.getElementById('project-doc-title-display');
    const useTplTitle = tpl.title || tpl.name || '';
    if (titleInput && !titleInput.value.trim() && useTplTitle) {
        titleInput.value = useTplTitle;
        if (titleDisplay) titleDisplay.textContent = useTplTitle;
        titleInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    setDocsEditorContent(tpl.contentJson);
    updateDocsDirtyState(projectId);
}

function _showDocTemplateDeleteConfirm(name) {
    return new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'doc-template-confirm-overlay';
        overlay.innerHTML = `
            <div class="doc-template-confirm-dialog" role="alertdialog" aria-modal="true">
                <p class="doc-template-confirm-msg">${t('docs.templates.deleteConfirmTitle', { name: `<strong>${escapeHtml(name)}</strong>` })}</p>
                <div class="doc-template-confirm-warning-row">
                    <span class="doc-template-confirm-warning-chip">⚠ ${t('docs.templates.deleteConfirmWarning')}</span>
                </div>
                <div class="doc-template-confirm-actions">
                    <button type="button" class="doc-template-confirm-cancel">${t('common.cancel')}</button>
                    <button type="button" class="doc-template-confirm-ok">${t('docs.templates.deleteConfirmBtn')}</button>
                </div>
            </div>
        `;
        document.body.appendChild(overlay);
        const cleanup = (result) => { overlay.remove(); resolve(result); };
        overlay.querySelector('.doc-template-confirm-cancel').addEventListener('click', () => cleanup(false));
        overlay.querySelector('.doc-template-confirm-ok').addEventListener('click', () => cleanup(true));
        overlay.addEventListener('click', (e) => { if (isBackdropClick(e, overlay)) cleanup(false); });
        requestAnimationFrame(() => overlay.querySelector('.doc-template-confirm-ok')?.focus());
    });
}

async function openDocTemplatePicker(projectId, docId) {
    const modal = document.getElementById('doc-template-picker-modal');
    if (!modal) return;

    const builtinGrid = modal.querySelector('[data-template-picker-grid="builtin"]');
    const userSection = modal.querySelector('[data-template-picker-section="user"]');
    const userGrid = modal.querySelector('[data-template-picker-grid="user"]');
    const userEmpty = modal.querySelector('[data-template-picker-user-empty]');
    const userLoading = modal.querySelector('[data-template-picker-user-loading]');
    const closeBtn = modal.querySelector('[data-template-picker-close]');

    const renderCard = (tpl, source) => `
        <button type="button" class="doc-template-card" data-template-card data-template-id="${escapeHtml(tpl.id)}" data-template-source="${source}">
            <span class="doc-template-card-icon" aria-hidden="true">${escapeHtml(tpl.icon || '📄')}</span>
            <span class="doc-template-card-body">
                <span class="doc-template-card-title">${escapeHtml(tpl.title || tpl.name || '')}</span>
                ${tpl.description ? `<span class="doc-template-card-desc">${escapeHtml(tpl.description)}</span>` : ''}
            </span>
            ${source === 'user' ? `<span class="doc-template-card-delete" role="button" tabindex="0" data-template-delete data-template-id="${escapeHtml(tpl.id)}" title="${t('docs.templates.deleteTemplate')}" aria-label="${t('docs.templates.deleteTemplate')}"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path></svg></span>` : ''}
        </button>
    `;

    if (builtinGrid) {
        builtinGrid.innerHTML = BUILTIN_DOC_TEMPLATES.map((tpl) => renderCard(tpl, 'builtin')).join('');
    }
    if (userGrid) userGrid.innerHTML = '';
    if (userSection) userSection.hidden = true;
    if (userEmpty) userEmpty.hidden = true;
    if (userLoading) userLoading.hidden = false;

    let userTemplates = [];
    try {
        const result = await getDocTemplates();
        userTemplates = Array.isArray(result?.items) ? result.items : [];
    } catch (err) {
        showErrorNotification(err?.message || String(err));
        userTemplates = [];
    }
    if (userLoading) userLoading.hidden = true;
    if (userSection) {
        userSection.hidden = false;
        if (userGrid) {
            userGrid.innerHTML = userTemplates.map((tpl) => renderCard(tpl, 'user')).join('');
        }
        if (userEmpty) userEmpty.hidden = userTemplates.length > 0;
    }

    modal.classList.add('active');

    const closeModal = () => {
        modal.classList.remove('active');
        modal.removeEventListener('click', overlayClickHandler);
        document.removeEventListener('keydown', escHandler);
    };
    const overlayClickHandler = (e) => { if (isBackdropClick(e, modal)) closeModal(); };
    const escHandler = (e) => { if (e.key === 'Escape') closeModal(); };

    modal.addEventListener('click', overlayClickHandler);
    document.addEventListener('keydown', escHandler);
    closeBtn?.addEventListener('click', closeModal, { once: true });

    modal.querySelectorAll('[data-template-card]').forEach((card) => {
        card.addEventListener('click', (e) => {
            if (e.target.closest('[data-template-delete]')) return;
            const id = card.dataset.templateId;
            const source = card.dataset.templateSource;
            const tpl = source === 'user'
                ? userTemplates.find((u) => u.id === id)
                : BUILTIN_DOC_TEMPLATES.find((b) => b.id === id);
            if (!tpl) return;
            hideDocTemplateBanner();
            applyDocTemplate(projectId, tpl);
            closeModal();
        });
    });

    modal.querySelectorAll('[data-template-delete]').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const id = btn.dataset.templateId;
            if (!id) return;
            const tplToDelete = userTemplates.find((u) => u.id === id);
            if (!await _showDocTemplateDeleteConfirm(tplToDelete?.name || tplToDelete?.title || id)) return;
            try {
                await deleteDocTemplate(id);
                userTemplates = userTemplates.filter((u) => u.id !== id);
                if (userGrid) {
                    userGrid.innerHTML = userTemplates.map((tpl) => renderCard(tpl, 'user')).join('');
                }
                if (userEmpty) userEmpty.hidden = userTemplates.length > 0;
                rebindPickerHandlers();
            } catch (err) {
                showErrorNotification(err?.message || String(err));
            }
        });
    });

    function rebindPickerHandlers() {
        modal.querySelectorAll('[data-template-card]').forEach((card) => {
            card.addEventListener('click', (e) => {
                if (e.target.closest('[data-template-delete]')) return;
                const id = card.dataset.templateId;
                const source = card.dataset.templateSource;
                const tpl = source === 'user'
                    ? userTemplates.find((u) => u.id === id)
                    : BUILTIN_DOC_TEMPLATES.find((b) => b.id === id);
                if (!tpl) return;
                hideDocTemplateBanner();
                applyDocTemplate(projectId, tpl);
                closeModal();
            });
        });
        modal.querySelectorAll('[data-template-delete]').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const id = btn.dataset.templateId;
                if (!id) return;
                const tplToDelete = userTemplates.find((u) => u.id === id);
            if (!await _showDocTemplateDeleteConfirm(tplToDelete?.name || tplToDelete?.title || id)) return;
                try {
                    await deleteDocTemplate(id);
                    userTemplates = userTemplates.filter((u) => u.id !== id);
                    if (userGrid) {
                        userGrid.innerHTML = userTemplates.map((tpl) => renderCard(tpl, 'user')).join('');
                    }
                    if (userEmpty) userEmpty.hidden = userTemplates.length > 0;
                    rebindPickerHandlers();
                } catch (err) {
                    showErrorNotification(err?.message || String(err));
                }
            });
        });
    }
}

async function openSaveAsTemplateDialog(projectId) {
    const currentDoc = getCurrentDocsRecord(projectId);
    if (!currentDoc) return;

    const modal = document.getElementById('doc-save-as-template-modal');
    const nameInput = modal?.querySelector('#template-save-name');
    const iconBtn = modal?.querySelector('#template-save-icon-btn');
    const descInput = modal?.querySelector('#template-save-desc');
    if (!modal || !nameInput || !iconBtn || !descInput) return;

    nameInput.value = currentDoc.title || '';
    const initialIcon = currentDoc.icon || '📄';
    iconBtn.dataset.icon = initialIcon;
    iconBtn.textContent = initialIcon;
    descInput.value = '';

    const closeModal = () => { modal.classList.remove('active'); };
    modal.classList.add('active');

    const saveBtn = modal.querySelector('[data-save-template-confirm]');
    const cancelBtn = modal.querySelector('[data-save-template-cancel]');

    const iconClickHandler = (event) => {
        event.stopPropagation();
        toggleDocsEmojiPicker(iconBtn, {
            anchorSelector: '#template-save-icon-btn',
            onSelect: (emoji) => {
                const value = String(emoji || '📄');
                iconBtn.dataset.icon = value;
                iconBtn.textContent = value;
            }
        });
    };
    iconBtn.addEventListener('click', iconClickHandler);

    const doSave = async () => {
        const name = nameInput.value.trim();
        if (!name) { nameInput.focus(); return; }
        const icon = (iconBtn.dataset.icon || '').trim() || '📄';
        const description = descInput.value.trim();
        const contentJson = currentDoc.contentJson;
        try {
            await saveDocTemplate({ name, icon, description, contentJson });
            closeModal();
            showSuccessNotification(t('docs.templates.savedToast'));
        } catch (err) {
            showErrorNotification(err?.message || String(err));
        }
    };

    saveBtn?.addEventListener('click', doSave, { once: true });
    cancelBtn?.addEventListener('click', closeModal, { once: true });
    modal.addEventListener('click', (e) => { if (isBackdropClick(e, modal)) closeModal(); }, { once: false });
}

async function createNewDocPage(projectId, options = {}) {
    if (!_storageHealthy) { showErrorNotification(t('error.cfOutageBlocked')); return; }
    const state = getDocsState(projectId);
    const canContinue = await saveActiveDocIfDirty(projectId, { silent: true });
    if (!canContinue) return;
    const nextSortOrder = state.items.length ? Math.max(...state.items.map((item) => Number(item.sortOrder) || 0)) + 1 : 0;
    const nextTreeOrder = state.items.length ? Math.max(...state.items.map((item) => Number(item.treeOrder) || 0)) + 1 : 0;

    try {
        const response = await createProjectDoc({
            title: options.title ? String(options.title) : '',
            contentJson: options.contentJson || EMPTY_PROJECT_DOC_CONTENT,
            sortOrder: nextSortOrder,
            treeOrder: nextTreeOrder,
            parentId: options.parentId ? String(options.parentId) : '',
            icon: String(options.icon || ''),
            widthMode: 'wide'
        });

        const record = mergeDocsRecord(projectId, response?.item || {});
        state.currentDocId = record.id;
        setDocsBaseline(projectId, record);
        // A brand new page opens straight in edit mode without going through
        // enterDocEditMode, so start its writing session here too.
        state.sessionAutoSaved = false;
        state.lastAutoSavedAt = 0;
        syncDocsRouteState({
            docId: record.id,
            slug: record.slug || slugifyProjectDocTitle(record.title),
            mode: 'edit'
        });
        reportSaveSuccess();
        refreshDocsSurfaces(projectId);
        // A new page starts with naming it. Keep focus in the title rather
        // than jumping straight into the empty body editor.
        requestAnimationFrame(() => {
            const titleInput = document.getElementById('project-doc-title-input');
            if (!titleInput) return;
            titleInput.focus({ preventScroll: true });
            titleInput.select();
        });
        void showDocTemplateBanner(projectId);
    } catch (error) {
        // Only count outage-class failures — 400/401/404 are client/auth errors, not infra outages
        if (!error?.status || error.status >= 500) {
            reportSaveFailure();
        }
        showErrorNotification(error?.message || String(error));
    }
}

function clearDocsEditorSessionBinding(projectId) {
    const state = getDocsState(projectId);
    state.hydrating = false;
    state.editorBoundDocId = null;
    state.pendingCheckboxSaveContext = null;
    state.deferredSilentSaveContext = null;
    if (state.checkboxAutoSave?.cancel) {
        state.checkboxAutoSave.cancel();
    }
    state.checkboxAutoSave = null;
    if (state.contentAutoSave?.cancel) {
        state.contentAutoSave.cancel();
    }
    state.contentAutoSave = null;
    state.lastAutoSavedAt = 0;
    state.sessionAutoSaved = false;
}

function teardownDocsWorkspace(projectId) {
    hideDocTemplateBanner();
    destroyDocsEditor();
    clearDocsEditorSessionBinding(projectId);
    docsWorkspaceActive = false;
    const docsHeaderSlot = document.getElementById('docs-page-header-actions');
    if (docsHeaderSlot) docsHeaderSlot.innerHTML = '';
}

function cancelPendingDocsCheckboxAutoSave(projectId) {
    const state = getDocsState(projectId);
    state.pendingCheckboxSaveContext = null;
    if (state.checkboxAutoSave?.cancel) {
        state.checkboxAutoSave.cancel();
    }
}

function flushPendingDocsCheckboxAutoSave(projectId) {
    const state = getDocsState(projectId);
    if (state.checkboxAutoSave?.flush) {
        state.checkboxAutoSave.flush();
    }
}

function cancelPendingDocsContentAutoSave(projectId) {
    const state = getDocsState(projectId);
    if (state.contentAutoSave?.cancel) {
        state.contentAutoSave.cancel();
    }
}

function flushPendingDocsContentAutoSave(projectId) {
    const state = getDocsState(projectId);
    if (state.contentAutoSave?.flush) {
        state.contentAutoSave.flush();
    }
}

function captureDocsSaveContext(projectId, options = {}) {
    const state = getDocsState(projectId);
    const record = getEditorBoundDocsRecord(projectId) || getCurrentDocsRecord(projectId);
    if (!record) return null;
    if (!doesDocsBaselineMatchActiveSession(projectId)) {
        updateDocsDirtyState(projectId);
        return null;
    }
    const draft = getDocsDraft(projectId, record);
    const snapshot = serializeDocsDraftSnapshot(draft);
    return {
        projectId,
        docId: String(record.id),
        draft,
        snapshot,
        expectedUpdatedAt: options.noConflictCheck ? null : (record.updatedAt ? String(record.updatedAt) : null),
        editorGeneration: Number(state.editorGeneration || 0),
        source: String(options.source || (options.silent ? 'silent_save' : 'manual_save')),
        mode: String(options.mode || getDocsRouteState().mode || 'edit'),
        silent: options.silent === true,
        noConflictCheck: options.noConflictCheck === true,
        dirty: snapshot !== (state.baselineSnapshot || '')
    };
}

function isDocsSaveContextCurrent(projectId, context) {
    const state = getDocsState(projectId);
    return Boolean(context)
        && String(state.currentDocId || '') === String(context.docId || '')
        && String(state.editorBoundDocId || '') === String(context.docId || '')
        && Number(state.editorGeneration || 0) === Number(context.editorGeneration || 0);
}

function mergeStaleSaveResponse(projectId, row, context) {
    const record = mergeDocsRecord(projectId, row || {});
    void logDocsSaveEvent(projectId, {
        docId: record?.id || context?.docId || '',
        source: context?.source || 'silent_save',
        origin: 'stale_response_merge',
        oldContentLen: getDocsContentLength(context?.draft?.contentJson),
        newContentLen: getDocsContentLength(record?.contentJson),
        editorGeneration: context?.editorGeneration || 0,
        rejected: false,
        rejectionReason: 'stale_response_merge'
    });
    return record;
}

async function saveDocsContext(projectId, context) {
    const state = getDocsState(projectId);
    if (!context) return false;
    if (!context.dirty) return true;

    if (state.saving && context.silent) {
        state.deferredSilentSaveContext = context;
        return true;
    }

    state.saving = true;
    state.error = '';
    updateDocsStatus(projectId);

    try {
        const response = await updateProjectDoc(context.docId, {
            title: context.draft.title,
            contentJson: context.draft.contentJson,
            icon: context.draft.icon,
            parentId: context.draft.parentId,
            widthMode: context.draft.widthMode,
            ...(context.expectedUpdatedAt ? { updatedAt: context.expectedUpdatedAt } : {}),
            saveContext: {
                source: context.source,
                mode: context.mode,
                editorGeneration: context.editorGeneration
            }
        });

        state.saving = false;
        reportSaveSuccess();
        if (isDocsSaveContextCurrent(projectId, context)) {
            const record = mergeDocsRecord(projectId, response?.item || {});
            state.currentDocId = record.id;
            setDocsBaseline(projectId, record, { generation: context.editorGeneration });
            showDocsFlash(projectId, t('docs.saved'), 'saved', 2000);
            if (!context.silent) {
                const nextMode = context.mode === 'edit' || context.mode === 'view'
                    ? context.mode
                    : getDocsRouteState().mode;
                syncDocsRouteState({
                    docId: record.id,
                    slug: record.slug || slugifyProjectDocTitle(record.title),
                    mode: nextMode
                });
                refreshDocsSurfaces(projectId);
                showSuccessNotification(t('docs.savedToast'));
            } else {
                updateDocsStatus(projectId);
            }
        } else {
            mergeStaleSaveResponse(projectId, response?.item || {}, context);
            updateDocsStatus(projectId);
        }

        if (state.deferredSilentSaveContext) {
            const deferredContext = state.deferredSilentSaveContext;
            state.deferredSilentSaveContext = null;
            window.setTimeout(() => {
                void saveDocsContext(projectId, deferredContext);
            }, 50);
        }

        return true;
    } catch (error) {
        state.saving = false;

        // The server refuses a save that wipes most of a PUBLISHED page (an
        // integrity guard from #557). That used to be a rare, deliberate click;
        // with autosave it fires on an ordinary "select all and rewrite" and,
        // because autosave keeps resending the same content, the page would be
        // stuck unable to save at all. Ask once, then let it through.
        if (error?.status === 422
            && error?.payload?.code === 'PUBLISHED_CONTENT_REPLACEMENT_GUARD'
            && context.source !== 'confirmed_replacement'
            && isDocsSaveContextCurrent(projectId, context)) {
            cancelPendingDocsContentAutoSave(projectId);
            const confirmed = await openDocsReplacementModal();
            if (!confirmed) {
                state.error = t('docs.replaceGuardBlocked');
                updateDocsStatus(projectId);
                return false;
            }
            return saveDocsContext(projectId, {
                ...context,
                source: 'confirmed_replacement',
                expectedUpdatedAt: null,
                noConflictCheck: true
            });
        }

        if (isDocsSaveContextCurrent(projectId, context)) {
            state.error = error?.message || String(error);
            state.conflict = error?.status === 409 ? error.payload || { conflict: true } : null;
            updateDocsStatus(projectId);
            if (!context.silent) {
                showErrorNotification(state.error);
            }
        } else {
            updateDocsStatus(projectId);
            void logDocsSaveEvent(projectId, {
                docId: context.docId,
                source: context.source || 'silent_save',
                origin: 'background_save',
                oldContentLen: getDocsContentLength(context.draft?.contentJson),
                newContentLen: 0,
                editorGeneration: context.editorGeneration || 0,
                rejected: true,
                rejectionReason: `background_save_failed${error?.status ? `_${error.status}` : ''}`
            });
        }
        if (!error?.status || error.status >= 500) {
            reportSaveFailure();
        }
        if (error?.status === 409 && context.silent) {
            state.deferredSilentSaveContext = null;
        }
        return false;
    }
}

// Embedded macros (Program Roadmap) save the page immediately and learn whether the
// server confirmed it, so they never report "Saved" for work that only lives in the
// editor. Waits out an in-flight save, then saves the latest snapshot itself.
async function saveDocsForEmbed(projectId) {
    const state = getDocsState(projectId);
    for (let waited = 0; state.saving && waited < 15000; waited += 100) {
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (state.saving) return false;
    const isEditing = document.getElementById('docs-canvas-root')?.dataset.mode === 'edit';
    const context = captureDocsSaveContext(projectId, {
        silent: true,
        source: 'autosave',
        mode: isEditing ? 'edit' : 'view',
        noConflictCheck: !isEditing
    });
    if (!context) return false;
    if (!context.dirty) return true;
    // This snapshot already contains anything a deferred save was holding.
    state.deferredSilentSaveContext = null;
    return saveDocsContext(projectId, context);
}

// Writes the current content back with a source the server always snapshots.
// Used when a session ends clean — the content is already saved, but the
// autosave interval never elapsed, so there would be no history entry for it.
async function forceDocsSessionSnapshot(projectId) {
    const record = getEditorBoundDocsRecord(projectId) || getCurrentDocsRecord(projectId);
    if (!record) return false;
    try {
        const draft = getDocsDraft(projectId, record);
        const response = await updateProjectDoc(record.id, {
            title: draft.title,
            contentJson: draft.contentJson,
            icon: draft.icon,
            parentId: draft.parentId,
            widthMode: draft.widthMode,
            saveContext: { source: 'session_end', mode: 'view' }
        });
        const merged = mergeDocsRecord(projectId, response?.item || {});
        setDocsBaseline(projectId, merged);
        return true;
    } catch (error) {
        // History bookkeeping — the content is already safely saved.
        console.warn('[docs] Failed to snapshot editing session', error);
        return false;
    }
}

async function saveActiveDocIfDirty(projectIdOrOptions = null, maybeOptions = {}) {
    const options = maybeOptions && Object.keys(maybeOptions).length
        ? maybeOptions
        : (projectIdOrOptions && typeof projectIdOrOptions === 'object' && !Array.isArray(projectIdOrOptions)
            ? projectIdOrOptions
            : {});
    const projectId = projectIdOrOptions && typeof projectIdOrOptions !== 'object'
        ? projectIdOrOptions
        : null;
    const state = getDocsState(projectId);
    if (!getCurrentDocsRecord(projectId) || !state.dirty) return true;
    const context = captureDocsSaveContext(projectId, options);
    if (!context) return options.silent === true;
    return saveDocsContext(projectId, context);
}

async function deleteProjectDocsWithConfirmation(projectId, requestedDocIds = []) {
    const state = getDocsState(projectId);
    const deletePlan = getEffectiveProjectDocsDeletePlan(projectId, requestedDocIds);
    if (!deletePlan.effectiveIds.length) return false;

    const canContinue = await saveActiveDocIfDirty(projectId, { silent: true });
    if (!canContinue) return false;

    const singleDoc = deletePlan.effectiveItems.length === 1 ? deletePlan.effectiveItems[0] : null;
    const confirmed = await openDocsDeleteModal(singleDoc ? {
        mode: 'single',
        title: singleDoc.title || t('docs.untitledPage')
    } : {
        mode: 'bulk',
        rootItems: deletePlan.rootItems,
        effectiveItems: deletePlan.effectiveItems
    });
    if (!confirmed) return false;

    let failedDoc = null;
    try {
        for (const docId of deletePlan.effectiveIds) {
            failedDoc = state.items.find((item) => String(item.id) === String(docId)) || failedDoc;
            await deleteProjectDoc(docId);
        }
    } catch (error) {
        resetDocsBulkSelection(projectId);
        await ensureDocsListLoaded(projectId, { force: true });
        refreshDocsSurfaces(projectId);
        showErrorNotification(t('docs.bulkDeletePartialFailed', {
            title: failedDoc?.title || t('docs.untitledPage')
        }));
        return false;
    }

    const deletedSet = new Set(deletePlan.effectiveIds.map((docId) => String(docId)));
    const nextDocId = getNextProjectDocSelectionAfterDelete(projectId, deletePlan.effectiveIds);
    state.items = state.items.filter((item) => !deletedSet.has(String(item.id)));
    state.currentDocId = nextDocId || null;
    state.dirty = false;
    state.baselineSnapshot = '';
    state.baselineDocId = null;
    state.baselineGeneration = 0;
    state.editorBoundDocId = null;
    clearDocsTreeMetaMany(deletePlan.effectiveIds);
    resetDocsBulkSelection(projectId);

    const nextCurrent = state.items.find((item) => String(item.id) === String(state.currentDocId || '')) || null;
    syncDocsRouteState({
        docId: nextCurrent?.id || '',
        slug: nextCurrent?.slug || '',
        mode: 'view'
    });
    refreshDocsSurfaces(projectId);
    if (state.currentDocId) {
        await ensureDocLoaded(projectId, state.currentDocId);
    } else {
        destroyDocsEditor();
    }

    showSuccessNotification(singleDoc
        ? t('docs.movedToTrash')
        : t('docs.bulkMovedToTrash', { count: deletePlan.effectiveIds.length }));
    return true;
}

async function deleteCurrentProjectDoc(projectId) {
    const current = getCurrentDocsRecord(projectId);
    if (!current) return false;
    return deleteProjectDocsWithConfirmation(projectId, [current.id]);
}

async function deleteSelectedProjectDocs(projectId) {
    const state = getDocsState(projectId);
    if (!state.selectedDocIds.size) return false;
    return deleteProjectDocsWithConfirmation(projectId, Array.from(state.selectedDocIds));
}

async function selectDocPage(projectId, docId) {
    const state = getDocsState(projectId);
    if (state.currentDocId === docId) return;

    cancelPendingDocsCheckboxAutoSave(projectId);
    cancelPendingDocsContentAutoSave(projectId);
    const canContinue = await saveActiveDocIfDirty(projectId, { silent: true });
    if (!canContinue) return;

    state.currentDocId = docId;
    const selected = state.items.find((item) => item.id === docId);
    syncDocsRouteState({
        docId,
        slug: selected?.slug || slugifyProjectDocTitle(selected?.title || ''),
        mode: 'view'
    });
    const resetTabletScroll = isDocsDesktopWidthTablet();
    refreshDocsSurfaces(projectId, { preserveScroll: !resetTabletScroll });
    if (resetTabletScroll) scheduleDocsTabletScrollReset();
    await ensureDocLoaded(projectId, docId);
    if (resetTabletScroll) scheduleDocsTabletScrollReset();
}

function setupDocsPageWorkspace(projectId) {
    const state = getDocsState(projectId);
    const current = getCurrentDocsRecord(projectId);
    docsWorkspaceActive = true;
    const docsCanvas = document.getElementById('docs-canvas-root');

    bindDocsBreadcrumb(projectId);

    // Bind the empty-state "New page" button rendered in the docs canvas
    const newPageBtn = document.getElementById('project-doc-new-btn');
    if (newPageBtn) {
        newPageBtn.addEventListener('click', () => {
            createNewDocPage(projectId);
        });
    }

    document.querySelectorAll('.docs-shell__list-item[data-doc-id]').forEach((button) => {
        button.addEventListener('click', async () => {
            await selectDocPage(projectId, button.dataset.docId);
            renderDocsPage();
        });
    });

    // ⋯ overflow menu toggle — bind any wraps already in the canvas (none on desktop after refactor)
    document.querySelectorAll('[data-docs-menu-wrap]').forEach((menuWrap) => {
        _bindDocsPageMenuWrap(menuWrap, projectId);
    });

    if (!window._docsMenuOutsideClickBound) {
        document.addEventListener('click', (e) => {
            document.querySelectorAll('[data-docs-menu-dropdown].open').forEach((dropdown) => {
                if (!dropdown.closest('[data-docs-menu-wrap]')?.contains(e.target)) {
                    dropdown.classList.remove('open');
                }
            });
        });
        window._docsMenuOutsideClickBound = true;
    }

    // Action buttons (Edit / Cancel / Publish) are now in the sticky page header.
    // _updateDocsPageHeaderActions() is called at the end of renderDocsPageContent()
    // and by enterDocEditMode() / exitDocEditMode().

    updateDocsStatus(projectId);

    // ── Mobile sheet bindings ─────────────────────────────────────────────
    if (getIsMobileCached()) {
        // Helper: bind an open-button to a sheet (supports multiple trigger IDs)
        const bindSheetTrigger = (btnId, sheet) => {
            const btn = document.getElementById(btnId);
            if (!btn || !sheet) return;
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                _closeMobileDocSheets();
                sheet.classList.add('active');
            });
        };

        const insertSheet = document.getElementById('docs-mobile-insert-sheet');
        bindSheetTrigger('docs-mobile-insert-btn', insertSheet);
        bindSheetTrigger('docs-mobile-insert-btn-inline', insertSheet);
        if (insertSheet && !insertSheet.dataset.sheetCloseBound) {
            insertSheet.dataset.sheetCloseBound = 'true';
            insertSheet.addEventListener('click', () => insertSheet.classList.remove('active'));
            insertSheet.querySelectorAll('[data-doc-command]').forEach((btn) =>
                btn.addEventListener('click', () => insertSheet.classList.remove('active'))
            );
        }

        const moreSheet = document.getElementById('docs-mobile-more-sheet');
        bindSheetTrigger('docs-mobile-more-btn', moreSheet);
        bindSheetTrigger('docs-mobile-more-btn-inline', moreSheet);
        if (moreSheet && !moreSheet.dataset.sheetCloseBound) {
            moreSheet.dataset.sheetCloseBound = 'true';
            moreSheet.addEventListener('click', () => moreSheet.classList.remove('active'));
            moreSheet.querySelectorAll('[data-doc-command]').forEach((btn) =>
                btn.addEventListener('click', () => moreSheet.classList.remove('active'))
            );
        }

        // Link sheet — open on link button tap; unlink immediately if already active
        const linkSheet = document.getElementById('docs-mobile-link-sheet');
        const linkInput = document.getElementById('docs-mobile-link-input');
        const linkApply = document.getElementById('docs-mobile-link-apply');
        const linkCancel = document.getElementById('docs-mobile-link-cancel');
        if (linkSheet && !linkSheet.dataset.linkSheetBound) {
            linkSheet.dataset.linkSheetBound = 'true';
            document.querySelectorAll('#docs-mobile-bar [data-doc-command="link"]').forEach((btn) => {
                btn.addEventListener('click', () => {
                    const state = getDocsEditorLinkState();
                    if (state.active) {
                        runDocsEditorCommand('link');
                        updateDocsDirtyState(projectId);
                        return;
                    }
                    if (linkInput) linkInput.value = state.href || '';
                    _closeMobileDocSheets();
                    linkSheet.classList.add('active');
                    setTimeout(() => linkInput?.focus(), 120);
                });
            });
            linkApply?.addEventListener('click', () => {
                const href = linkInput?.value.trim() || '';
                if (href) {
                    runDocsEditorCommand('setLink', href);
                    updateDocsDirtyState(projectId);
                }
                linkSheet.classList.remove('active');
            });
            linkInput?.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') { e.preventDefault(); linkApply?.click(); }
                if (e.key === 'Escape') linkCancel?.click();
            });
            linkCancel?.addEventListener('click', () => linkSheet.classList.remove('active'));
            linkSheet.addEventListener('click', (e) => {
                if (e.target === linkSheet) linkSheet.classList.remove('active');
            });
        }
    }

    if (!current) return;

    const titleInput = document.getElementById('project-doc-title-input');
    if (titleInput) {
        titleInput.addEventListener('input', () => {
            syncDocsListTitle(projectId);
            updateDocsDirtyState(projectId);
            _docsContentAutoSave();
        });
    }

    const iconButton = document.getElementById('project-doc-icon-btn');
    if (iconButton) {
        iconButton.addEventListener('click', (event) => {
            event.stopPropagation();
            const activeDoc = getCurrentDocsRecord(projectId);
            if (!activeDoc || docsCanvas?.dataset.mode !== 'edit') return;
            toggleDocsEmojiPicker(iconButton, {
                anchorSelector: '[data-doc-command="emoji"], [data-doc-icon-trigger]',
                onSelect: async (emoji) => {
                    const record = await persistProjectDocMeta(projectId, activeDoc.id, { icon: String(emoji || '') });
                    if (!record) return;
                    updateDocsPageIconUi(projectId);
                    updateDocsNavTree(projectId);
                    updateDocsDirtyState(projectId);
                }
            });
        });
    }

    const iconClearButton = document.getElementById('project-doc-icon-clear');
    if (iconClearButton) {
        iconClearButton.addEventListener('click', (event) => {
            event.stopPropagation();
            const activeDoc = getCurrentDocsRecord(projectId);
            if (!activeDoc || docsCanvas?.dataset.mode !== 'edit') return;
            persistProjectDocMeta(projectId, activeDoc.id, { icon: '' }).then(() => {
                updateDocsPageIconUi(projectId);
                updateDocsNavTree(projectId);
                updateDocsDirtyState(projectId);
            }).catch((error) => {
                showErrorNotification(error?.message || String(error));
            });
        });
    }

    // Heading dropdown toggle (second toolbar instance)
    document.querySelectorAll('[data-docs-heading-toggle]').forEach((toggle) => {
        if (toggle.dataset.headingBound === 'true') return;
        toggle.dataset.headingBound = 'true';
        const wrap = toggle.closest('.docs-heading-wrap');
        const dropdown = wrap?.querySelector('.docs-heading-dropdown');
        if (!dropdown) return;
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            const nextHidden = !dropdown.hidden;
            document.querySelectorAll('.docs-heading-dropdown').forEach((menu) => {
                menu.hidden = true;
                menu.style.removeProperty('left');
                menu.style.removeProperty('top');
            });
            dropdown.hidden = nextHidden;
            if (!nextHidden) {
                positionDocsHeadingDropdown(toggle, dropdown);
            }
        });
        dropdown.querySelectorAll('[data-doc-command]').forEach((btn) => {
            btn.addEventListener('click', () => {
                dropdown.hidden = true;
                dropdown.style.removeProperty('left');
                dropdown.style.removeProperty('top');
            });
        });
        document.addEventListener('click', (e) => {
            if (!wrap.contains(e.target)) {
                dropdown.hidden = true;
                dropdown.style.removeProperty('left');
                dropdown.style.removeProperty('top');
            }
        });
    });

    // Alignment dropdown handled by global IIFE (initAlignDropdown) below

    document.querySelectorAll('[data-doc-command]').forEach((button) => {
        if (button.dataset.commandBound === 'true') return;
        button.dataset.commandBound = 'true';
        button.addEventListener('click', async (e) => {
            if (button.dataset.docCommand === 'emoji') {
                e.stopPropagation();
                toggleDocsEmojiPicker(button);
                return;
            }
            if (button.dataset.docCommand === 'image') {
                e.stopPropagation();
                openDocsImagePicker(projectId);
                return;
            }
            if (button.dataset.docCommand === 'file') {
                e.stopPropagation();
                openDocsFilePicker();
                return;
            }
            if (button.dataset.docCommand === 'video') {
                e.stopPropagation();
                await openDocsYoutubePicker();
                updateDocsDirtyState(projectId);
                return;
            }
            if (button.dataset.docCommand === 'status') {
                e.stopPropagation();
                // Open the label/tone picker FIRST (it focuses and selects the text),
                // so the chip lands with the wording the user actually wants. It used
                // to drop a grey "STATUS" chip that then had to be clicked to edit —
                // a pointless extra step.
                // Anchor to the CARET, not the toolbar button: the chip is inserted
                // where the user is typing, so that is where the picker belongs.
                await openDocsStatusChipPicker(projectId, button, getDocsCaretRect());
                return;
            }
            if (button.dataset.docCommand === 'dateChip') {
                e.stopPropagation();
                openDocsDateChipPicker(projectId, button);
                return;
            }
            runDocsEditorCommand(button.dataset.docCommand, button.dataset.docParam);
            focusDocsEditor();
            updateDocsDirtyState(projectId);
            updateDocsTableMenuVisibility();
        });
    });

    const startEditable = !docsCanvas || docsCanvas.dataset.mode === 'edit';

    const _docsCheckboxAutoSave = debounce(() => {
        const pendingContext = state.pendingCheckboxSaveContext;
        state.pendingCheckboxSaveContext = null;
        if (!pendingContext) return;
        void saveDocsContext(projectId, pendingContext);
    }, 400);
    state.checkboxAutoSave = _docsCheckboxAutoSave;

    // Autosave while typing: 3s after the user pauses, and never more than 20s
    // apart while they keep going. Publishing is no longer what makes a change
    // durable — a browser or OS crash used to lose the whole editing session.
    const _docsContentAutoSave = debounce(() => {
        if (document.getElementById('docs-canvas-root')?.dataset.mode !== 'edit') return;
        if (state.saving || !state.dirty) return;
        const context = captureDocsSaveContext(projectId, {
            silent: true,
            source: 'autosave',
            mode: 'edit'
        });
        if (!context?.dirty) return;
        state.sessionAutoSaved = true;
        void saveDocsContext(projectId, context).then((saved) => {
            if (!saved) return;
            state.lastAutoSavedAt = Date.now();
            updateDocsStatus(projectId);
        });
    }, 3000, { maxWait: 20000 });
    state.contentAutoSave = _docsContentAutoSave;

    initializeDocsEditor({
        editorSelector: '#project-doc-editor',
        placeholder: t('docs.editorPlaceholder'),
        onUpdate: () => {
            updateDocsDirtyState(projectId);
            _docsContentAutoSave();
            updateDocsTableMenuVisibility();
            hideDocTemplateBanner();
            updateDocsSlashMenu(projectId);
        },
        onSelectionUpdate: (editorArg) => {
            updateDocsTableMenuVisibility();
            updateCalloutTypePicker(projectId);
            updateDocsSlashMenu(projectId);
            if (getIsMobileCached()) {
                const editor = editorArg?.editor ?? editorArg;
                if (editor?.state) {
                    const hasSelection = !editor.state.selection.empty;
                    document.getElementById('docs-mobile-bar')
                        ?.classList.toggle('has-selection', hasSelection);
                    syncMobileBarActiveStates(editor);
                }
            }
        },
        onCheckboxChange: () => {
            updateDocsDirtyState(projectId);
            const isEditing = docsCanvas?.dataset.mode === 'edit';
            state.pendingCheckboxSaveContext = captureDocsSaveContext(projectId, {
                silent: true,
                source: 'checkbox_auto',
                mode: isEditing ? 'edit' : 'view',
                noConflictCheck: !isEditing
            });
            _docsCheckboxAutoSave();
        },
        onEmbedSave: () => saveDocsForEmbed(projectId),
        // Program Roadmap links items to tasks and lanes to projects.
        getEmbedData: () => ({
            tasks: tasks
                .filter((task) => !task.archivedAt && !task.isTemplate)
                .map((task) => ({
                    id: String(task.id),
                    title: String(task.title || ''),
                    projectId: task.projectId != null ? String(task.projectId) : '',
                    startDate: String(task.startDate || '').slice(0, 10),
                    endDate: String(task.endDate || '').slice(0, 10),
                    status: String(task.status || 'todo'),
                    statusLabel: getStatusLabel(task.status),
                    updatedAt: String(task.updatedAt || task.createdAt || '')
                })),
            projects: projects
                .filter((project) => !project.archivedAt)
                .map((project) => ({ id: String(project.id), name: String(project.name || '') })),
            openTask: (taskId) => {
                const task = tasks.find((candidate) => String(candidate.id) === String(taskId));
                if (task) openTaskDetails(task.id);
            },
            updateTaskDates: async (taskId, startDate, endDate) => {
                const task = tasks.find((candidate) => String(candidate.id) === String(taskId));
                // A side may be '' (the task had no start/end date and only the other was sent).
                const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
                const validSide = (value) => value === '' || isIsoDate(value);
                if (!task || !validSide(startDate) || !validSide(endDate) || (!startDate && !endDate) || (startDate && endDate && endDate < startDate)) {
                    throw new Error('Invalid task date update');
                }
                if (task.startDate === startDate && task.endDate === endDate) return { ok: true };

                const previous = { startDate: task.startDate, endDate: task.endDate };
                task.startDate = startDate;
                task.endDate = endDate;
                try {
                    await saveTaskUpdate(task.id, { startDate, endDate });
                    renderTasks();
                    return { ok: true };
                } catch (error) {
                    Object.assign(task, previous);
                    throw error;
                }
            }
        })
    });
    bindDocsCalloutTypePicker(projectId);
    bindDocsSlashKeys(document.querySelector('#project-doc-editor .ProseMirror'));
    refreshDocsChildListBlocks(getChildDocsPages(projectId, current.id));
    const hydratedSession = hydrateDocsEditor(projectId, current.id, current.contentJson);
    setDocsBaseline(projectId, current, { generation: hydratedSession.editorGeneration });
    setDocsEditorEditable(startEditable);
    initDocsTabletTableScaler();
    // (D) Re-run the tablet scroll reset AFTER the editor is hydrated:
    // iPad WebKit scrolls the first heading into view (honouring its
    // scroll-margin-top) once the contenteditable lays out, which is what
    // pushes the toolbar/title off-screen. No-op on non-iPad.
    scheduleDocsTabletScrollReset();

    // If the page was loaded directly with mode=edit (via URL), start the mobile
    // keyboard offset listener that normally gets set by enterDocEditMode().
    if (startEditable && (getIsMobileCached() || isDocsTabletDevice())) {
        _startDocsKeyboardListener();
        requestAnimationFrame(() => {
            const toolbar = document.querySelector('.docs-canvas__toolbar');
            if (toolbar) {
                document.documentElement.style.setProperty(
                    '--docs-toolbar-h', toolbar.offsetHeight + 'px'
                );
            }
        });
    }

    const editorSurface = document.querySelector('#project-doc-editor .docs-editor-content');
    if (editorSurface) {
        editorSurface.addEventListener('change', (e) => {
            if (e.target.matches("ul[data-type='taskList'] input[type='checkbox']")) {
                updateDocsDirtyState(projectId);
                const isEditing = docsCanvas?.dataset.mode === 'edit';
                state.pendingCheckboxSaveContext = captureDocsSaveContext(projectId, {
                    silent: true,
                    source: 'checkbox_auto',
                    mode: isEditing ? 'edit' : 'view',
                    noConflictCheck: !isEditing
                });
                _docsCheckboxAutoSave();
            }
        });
        bindDocsStatusChipEditing(projectId, editorSurface);
        bindDocsDateChipEditing(projectId, editorSurface);
    }

    _docsActiveProjectId = projectId;
    _bindDocsEditModeClickAway();

    updateDocsDirtyState(projectId);
    updateDocsPageIconUi(projectId);
    bindDocsTableMenu(projectId);
    bindDocsFindReplacePanel();
    updateDocsTableMenuVisibility();

    // Beautify only in view mode. Running it in edit mode fights ProseMirror's DOM
    // management and can revert chip classes on 3+ links after a state transaction.
    if (!startEditable) {
        const editor = document.querySelector('#project-doc-editor .ProseMirror');
        if (editor) beautifyInternalLinks(editor, true);
    }

    showDocTemplateBanner(projectId);
}

async function showProjectDetails(projectId, referrer, context) {
    const detailsTimer = debugTimeStart("projects", "details", {
        projectId
    });
    if (docsWorkspaceActive) {
        cancelPendingDocsCheckboxAutoSave();
        cancelPendingDocsContentAutoSave();
        const canContinue = await saveActiveDocIfDirty({ silent: true });
        if (!canContinue) {
            restoreCurrentDocsRoute();
            document.querySelectorAll(".nav-item").forEach((nav) => nav.classList.remove("active"));
            document.querySelectorAll(".nav-section-docs").forEach((section) => section.classList.remove("active"));
            document.querySelector('.nav-section-docs')?.classList.add("active");
            debugTimeEnd("projects", detailsTimer, { projectId, blockedByDirtyDocs: true });
            return false;
        }
        teardownDocsWorkspace();
    }
    // Always derive a sane referrer when one is not explicitly provided.
    // This prevents stale referrers (e.g. previous calendar context) from leaking.
    if (referrer === undefined) {
        const hash = (window.location.hash || '').toLowerCase();
        const projectsActive = document.getElementById('projects')?.classList.contains('active');
        const dashboardActive = document.getElementById('dashboard')?.classList.contains('active');
        const tasksActive = document.getElementById('tasks')?.classList.contains('active');
        const calendarSubViewActive = document.getElementById('calendar-view')?.classList.contains('active');
        const calendarActive = (tasksActive && calendarSubViewActive)
            || hash === '#calendar'
            || hash.includes('view=calendar');

        if (projectsActive || hash.startsWith('#projects')) {
            projectNavigationReferrer = 'projects';
        } else if (dashboardActive || hash.startsWith('#dashboard')) {
            projectNavigationReferrer = 'dashboard';
        } else if (calendarActive) {
            projectNavigationReferrer = 'calendar';
        } else {
            projectNavigationReferrer = 'projects';
        }
    } else {
        // Only update navigation context if explicitly provided
        projectNavigationReferrer = referrer;
        if (referrer === 'calendar') {
            const month = context && Number.isInteger(context.month) ? context.month : currentMonth;
            const year = context && Number.isInteger(context.year) ? context.year : currentYear;
            calendarNavigationState = { month, year };
        }
    }

    // Update URL hash
    window.location.hash = `project-${projectId}`;

    const project = projects.find((p) => p.id === projectId);
    if (!project) {
        debugTimeEnd("projects", detailsTimer, { projectId, found: false });
        return;
    }

    // Scroll to top on mobile when showing project details
    if (getIsMobileCached()) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Hide ALL pages first, then show project details
    document
        .querySelectorAll(".page")
        .forEach((page) => page.classList.remove("active"));
    document.getElementById("project-details").classList.add("active");
    
    // Hide user menu in project details view
    const userMenu = document.querySelector(".user-menu");
    if (userMenu) userMenu.style.display = "none";

    // Calculate project stats
    const projectTasks = getLiveProjectTasks(projectId);
    const completedTasks = projectTasks.filter((t) => t.status === "done");
    const inProgressTasks = projectTasks.filter((t) => t.status === "progress");
    const reviewTasks = projectTasks.filter((t) => t.status === "review");
    const todoTasks = projectTasks.filter((t) => t.status === "todo");
    const backlogTasks = projectTasks.filter((t) => t.status === "backlog");

    const completionPercentage =
        projectTasks.length > 0
            ? Math.round((completedTasks.length / projectTasks.length) * 100)
            : 0;

    // Calculate project status based on task statuses
    const projectStatus = getProjectStatus(projectId);

    // Pre-compute manual order flag so it can be used in both the header button and sort logic
    const hasManualOrder = projectTasks.some(t => typeof t.projectOrder === 'number');
    const taskGroupBy = getProjectTaskGroupBy();

    // Back button: dashboard/calendar referrers win; otherwise an archived
    // project belongs to the Archive, so return there rather than the active list.
    let backBtnAction, backBtnLabel;
    if (projectNavigationReferrer === 'dashboard') {
        backBtnAction = 'backToDashboard';
        backBtnLabel = t('projects.backTo.dashboard');
    } else if (projectNavigationReferrer === 'calendar') {
        backBtnAction = 'backToCalendar';
        backBtnLabel = t('projects.backTo.calendar');
    } else if (project.archivedAt) {
        backBtnAction = 'backToArchivedProjects';
        backBtnLabel = t('projects.backTo.archived');
    } else {
        backBtnAction = 'backToProjects';
        backBtnLabel = t('projects.backTo.projects');
    }

    // Daylight layout: one-line strip — segmented bar + % summary. Hovering (or tapping) a segment opens a
    // colored breakdown card listing every status; each row opens that status's tasks.
    const statusBreakdown = [
        { status: 'backlog', count: backlogTasks.length, label: t('tasks.status.backlog') },
        { status: 'todo', count: todoTasks.length, label: t('tasks.status.todo') },
        { status: 'progress', count: inProgressTasks.length, label: t('tasks.status.progress') },
        { status: 'review', count: reviewTasks.length, label: t('tasks.status.review') },
        { status: 'done', count: completedTasks.length, label: t('tasks.status.done') }
    ];
    // Reverse workflow order, so "done" (green) fills from the left like a progress bar.
    // Project details only — reports keep backlog→done order.
    const progressBarOrder = ['done', 'review', 'progress', 'todo', 'backlog']
        .map((status) => statusBreakdown.find((entry) => entry.status === status));
    const progressSegmentsHtml = projectTasks.length > 0
        ? progressBarOrder
            .filter((entry) => entry.count > 0)
            .map((entry) => `<button type="button" class="pd-segment" data-status="${entry.status}" style="width: ${(entry.count / projectTasks.length) * 100}%"
                    data-action="navigateToProjectStatus" data-param="${project.id}" data-param2="${entry.status}"
                    aria-label="${escapeHtml(`${entry.label}: ${entry.count}`)}"></button>`)
            .join('')
        : '';
    const progressCardHtml = projectTasks.length > 0
        ? `<div class="pd-seg-card" hidden>
                ${progressBarOrder.filter((entry) => entry.count > 0).map((entry) => `
                <button type="button" class="pd-seg-card-row" data-status="${entry.status}"
                        data-action="navigateToProjectStatus" data-param="${project.id}" data-param2="${entry.status}">
                    <span class="pd-seg-card-top">
                        <span class="pd-seg-card-dot" aria-hidden="true"></span>
                        <span class="pd-seg-card-label">${escapeHtml(entry.label)}</span>
                        <span class="pd-seg-card-pct">${Math.round((entry.count / projectTasks.length) * 100)}%</span>
                    </span>
                    <span class="pd-seg-card-count">${t('projects.details.segmentTip', { count: entry.count, total: projectTasks.length })}</span>
                    <span class="pd-seg-card-hint"></span>
                </button>`).join('')}
            </div>`
        : '';

    // Render project details
    const detailsHTML = `
        <div class="project-details-header">
                    <div class="pd-topbar"><button type="button" class="back-btn pd-back" data-action="${backBtnAction}"><svg class="pd-back-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="m11 18-6-6 6-6"/></svg><span>${backBtnLabel}</span></button></div>
                    <div class="project-details-title">
                        <div class="title-color-picker" style="position: relative; display: flex; align-items: center;">
                            <div class="current-color"
                                 style="background-color: ${getProjectColor(projectId)}; width: 28px; height: 28px; border-radius: 6px; border: 2px solid var(--border-color); cursor: pointer; display: block; margin-top: 4px;"
                                 data-action="toggleProjectColorPicker" data-param="${projectId}">
                            </div>
                            <div class="color-picker-dropdown" id="color-picker-${projectId}" style="display: none; position: absolute; background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 8px; margin-top: 4px; z-index: 1000; left: 0; top: 100%; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
                                <div class="color-grid" style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px;">
                                    ${PROJECT_COLORS.map(color => `
                                        <div class="color-option"
                                             style="width: 24px; height: 24px; background-color: ${color}; border-radius: 4px; cursor: pointer; border: 2px solid ${color === getProjectColor(projectId) ? 'white' : 'transparent'};"
                                             data-action="updateProjectColor" data-param="${projectId}" data-param2="${color}">
                                        </div>
                                    `).join('')}
                                </div>
                                <div class="color-picker-custom" style="margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--border-light); display: flex; align-items: center; justify-content: space-between; gap: 8px; position: relative;">
                                    <span style="font-size: 12px; color: var(--text-muted);">${t('projects.details.customColor')}</span>
                                    <div
                                        class="custom-color-swatch"
                                        data-action="openCustomProjectColorPicker"
                                        data-param="${projectId}"
                                        style="width: 24px; height: 24px; background-color: ${getProjectColor(projectId)}; border-radius: 4px; cursor: pointer; border: 2px solid transparent; box-sizing: border-box;">
                                    </div>
                                    <input
                                        type="color"
                                        id="project-color-input-${projectId}"
                                        class="project-color-input"
                                        data-project-id="${projectId}"
                                        value="${getProjectColor(projectId)}"
                                        style="position: absolute; opacity: 0; pointer-events: none; width: 0; height: 0;">
                                </div>
                            </div>
                        </div>
                        <span id="project-title-display" data-action="editProjectTitle" data-param="${projectId}" data-param2="${escapeHtml(project.name).replace(/'/g, '&#39;')}" class="pd-title-text">${escapeHtml(project.name)}</span>
                        <div id="project-title-edit" style="display: none;">
                            <input type="text" id="project-title-input" class="editable-project-title" value="${escapeHtml(project.name)}" style="font-size: 32px; font-weight: 700;">
                            <button class="title-edit-btn confirm" data-action="saveProjectTitle" data-param="${projectId}">✓</button>
                            <button class="title-edit-btn cancel" data-action="cancelProjectTitle">✕</button>
                        </div>
                        <span class="project-status-badge ${projectStatus}" data-action="showStatusInfoModal">${getProjectStatusLabel(projectStatus).toUpperCase()}</span>
                        <div style="margin-left: auto; position: relative;">
                            <button type="button" class="options-btn pd-options-btn" id="project-options-btn" data-action="toggleProjectMenu" aria-haspopup="menu" aria-label="${escapeHtml(t('projects.details.moreActions'))}" title="${escapeHtml(t('projects.details.moreActions'))}"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg></button>
                            <div class="options-menu" id="project-options-menu" style="position:absolute;top:calc(100% + 8px);right:0;display:none;">
                                <button type="button" class="duplicate-btn" data-action="handleDuplicateProject" data-param="${projectId}">📋 ${t('projects.duplicate.title')}</button>
                                <button type="button" class="template-btn" data-action="openSaveAsTemplateModal" data-param="${projectId}">🗂️ ${t('projects.template.saveAsTemplate')}</button>
                                ${project.archivedAt
                                    ? `<button type="button" data-action="openProjectRestoreModal" data-param="${projectId}">↩️ ${t('projects.restore.title')}</button>`
                                    : `<button type="button" data-action="openProjectArchiveModal" data-param="${projectId}">📦 ${t('projects.archive.title')}</button>`}
                                <button type="button" class="delete-btn" data-action="handleDeleteProject" data-param="${projectId}">🗑️ ${t('projects.delete.title')}</button>
                            </div>
                        </div>
	                    </div>

                    <div class="pd-meta" id="project-details-meta">${buildProjectDetailsMetaHtml(project, projectStatus)}</div>
	                    <div class="modal-tabs project-details-tabs">
	                        <button type="button" class="modal-tab active" data-tab="details">${t('projects.details.tab.details')}</button>
	                        <button type="button" class="modal-tab" data-tab="history">${t('projects.details.tab.history')}</button>
	                    </div>

	                    <div class="modal-tab-content active" id="project-details-tab">
                    <div class="pd-layout">
                        <div class="pd-main">
                            <div class="project-details-left pd-card pd-description-card" id="project-description-card">
                                <div class="pd-desc-head">
                                    <div class="pd-card-title" data-i18n="projects.details.descriptionTitle">${t('projects.details.descriptionTitle')}</div>
                                    <button type="button" class="pd-desc-edit-btn" id="project-description-edit-btn">
                                        <svg class="pd-desc-edit-icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M11.2 2.6l2.2 2.2-7.9 7.9-2.9.7.7-2.9z"/><path d="M9.8 4l2.2 2.2"/></svg>
                                        <span class="pd-desc-edit-label" data-i18n="projects.details.editDescription">${t('projects.details.editDescription')}</span>
                                        <span class="pd-desc-done-label" data-i18n="projects.details.doneEditing">${t('projects.details.doneEditing')}</span>
                                    </button>
                                </div>
                            <div class="project-details-description">
                                <div class="rich-editor">
                                    <div class="editor-toolbar">
                                        <button type="button" class="editor-btn" data-action="formatProjectDescriptionText" data-param="bold"><strong>B</strong></button>
                                        <button type="button" class="editor-btn" data-action="formatProjectDescriptionText" data-param="italic"><em>I</em></button>
                                        <button type="button" class="editor-btn" data-action="formatProjectDescriptionText" data-param="underline"><u>U</u></button>
                                        <button type="button" class="editor-btn" data-action="formatProjectDescriptionText" data-param="strikeThrough"><span style="text-decoration: line-through;">S</span></button>
                                        <span style="width:1px;height:20px;background:var(--border);margin:0 4px;"></span>
                                        <button type="button" class="editor-btn" data-action="formatProjectDescriptionText" data-param="insertUnorderedList">${t('tasks.modal.listBulleted')}</button>
                                        <button type="button" class="editor-btn" data-action="formatProjectDescriptionText" data-param="insertOrderedList">${t('tasks.modal.listNumbered')}</button>
                                        <span style="width:1px;height:20px;background:var(--border);margin:0 4px;"></span>
                                        <button type="button" class="editor-btn" data-action="insertProjectDescriptionHeading" data-param="h1">H1</button>
                                        <button type="button" class="editor-btn" data-action="insertProjectDescriptionHeading" data-param="h2">H2</button>
                                        <button type="button" class="editor-btn" data-action="insertProjectDescriptionHeading" data-param="h3">H3</button>
                                        <button type="button" class="editor-btn" data-action="insertProjectDescriptionDivider" title="${t('tasks.modal.insertDivider')}">─</button>
                                        <button type="button" class="editor-btn" data-action="insertProjectDescriptionDateChip" title="${t('docs.toolbar.dateChip')}" aria-label="${t('docs.toolbar.dateChip')}">
                                            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>
                                        </button>
                                        <button type="button" class="editor-btn" data-action="insertProjectDescriptionChecklist" title="${t('tasks.modal.insertCheckbox')}">☑</button>
                                    </div>
                                    <div class="editable-description" id="project-description-editor"></div>
                                </div>
                                <textarea id="project-description-hidden" style="display:none;"></textarea>
                            </div>
                        </div>
                            <section class="project-progress-section pd-progress" aria-label="${t('projects.details.progressOverview')}">
                                <div class="pd-progress-row">
                                    <div class="pd-segbar">${progressSegmentsHtml}</div>
                                    <div class="pd-progress-summary">
                                        <span class="progress-percentage">${completionPercentage}%</span>
                                        <span class="pd-progress-count">${t('projects.details.tasksDoneSummary', { done: completedTasks.length, total: projectTasks.length })}</span>
                                    </div>
                                </div>
                                ${progressCardHtml}
                            </section>
                            <div class="project-tasks-section" data-group-by="${taskGroupBy}">
                                <div class="section-header">
                                    <div class="section-title">${t('projects.details.tasksTitle', { count: projectTasks.length })}</div>
                                    <div style="display: flex; gap: 8px; align-items: center;">
                                        ${projectTasks.length > 0 ? buildProjectTaskGroupByControlHtml(projectId, taskGroupBy) : ''}
                                        ${hasManualOrder ? `<button class="add-btn auto-sort-btn" data-action="resetProjectTaskOrder" data-param="${projectId}" title="${t('projects.details.resetSortTitle')}">↕ ${t('projects.details.resetSort')}</button>` : ''}
                                        ${projectTasks.length > 0 ? `<button class="add-btn project-view-list-btn" data-action="navigateToProjectTasksList" data-param="${projectId}" title="${t('projects.details.viewInList')}">${t('projects.details.viewInListBtn')}</button>` : ''}
                                        <button class="add-btn" data-action="openTaskModalForProject" data-param="${projectId}">${t('tasks.addButton')}</button>
                                    </div>
                                </div>
                                <div id="project-tasks-list">${buildProjectTasksListHtml(projectTasks, taskGroupBy)}</div>
                            </div>
                        </div>
                        <aside class="pd-side">
                            <div class="project-details-dates">
                            <div class="project-sidebar-card">
                                <div class="sidebar-field">
                                    <div class="sidebar-field-label">${t('projects.details.startDate')}</div>
                                    <input type="text" class="form-input date-display editable-date datepicker"
                                        placeholder="${escapeHtml(t('common.datePlaceholder'))}" maxLength="10"
                                        value="${project.startDate ? formatDate(project.startDate) : ''}"
                                        data-project-id="${projectId}" data-field="startDate">
                                </div>
                                <div class="sidebar-field">
                                    <div class="sidebar-field-label">${t('projects.details.endDate')}</div>
                                    <input type="text" class="form-input date-display editable-date datepicker"
                                        placeholder="${escapeHtml(t('common.datePlaceholder'))}" maxLength="10"
                                        value="${project.endDate ? formatDate(project.endDate) : ''}"
                                        data-project-id="${projectId}" data-field="endDate">
                                </div>
                            </div>
                        </div>
                            <div class="project-details-tags-col">
                            <div class="project-sidebar-card">
                                <div class="sidebar-field sidebar-field-tags">
                                    <div class="sidebar-field-label">${t('projects.modal.tagsLabel')}</div>
                                    <div id="project-details-tags-display" style="display: flex; flex-wrap: wrap; gap: 6px; min-height: 24px;"></div>
                                    <div style="display: flex; gap: 6px; margin-top: 6px;">
                                        <input type="text" id="project-details-tag-input" class="form-input" placeholder="${t('projects.modal.addTagPlaceholder')}" style="flex: 1;">
                                        <button type="button" class="btn-secondary" data-action="addProjectDetailsTag" data-param="${projectId}">+</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        </aside>
                    </div>
                </div>

                <div class="modal-tab-content" id="project-history-tab">
	                    <div class="project-history-section">
                    <div class="section-header">
                        <div class="section-title">📜 ${t('projects.details.changeHistory')}</div>
                    </div>
                    <div class="history-timeline-inline" id="project-history-timeline-${projectId}">
                        <!-- Timeline will be populated by JavaScript -->
                    </div>
                    <div class="history-empty-inline" id="project-history-empty-${projectId}" style="display: none;">
                        <div style="font-size: 36px; margin-bottom: 12px; opacity: 0.3;">📜</div>
                        <p style="color: var(--text-muted); text-align: center;">${t('projects.details.noChanges')}</p>
                    </div>
	                    </div>
	                </div>
	        </div>
	            `;

	    document.getElementById("project-details-content").innerHTML = detailsHTML;

        const descEl = document.getElementById("project-description-editor");
        const descHiddenField = document.getElementById("project-description-hidden");
        const initialProjectDescription = typeof project.description === "string" ? project.description : "";
        let lastSavedProjectDescription = initialProjectDescription;

        if (descEl) {
            const saveProjectDescription = (text) => {
                const normalizedText = typeof text === "string" ? text : "";
                if (normalizedText === lastSavedProjectDescription) return;
                updateProjectField(projectId, "description", normalizedText, { render: false });
                lastSavedProjectDescription = normalizedText;
            };

            initializeProjectDescriptionEditor({
                editorSelector: descEl,
                hiddenSelector: descHiddenField,
                content: initialProjectDescription,
                placeholder: t('projects.details.descriptionPlaceholder'),
                onUpdate: (text) => {
                    if (descHiddenField) descHiddenField.value = text;
                },
                onBlur: (text) => {
                    if (descHiddenField) descHiddenField.value = text;
                    saveProjectDescription(text);
                }
            });
            bindRichEditorLinkClicks(descEl);
            setupProjectDescriptionEditMode();
        } else {
            destroyProjectDescriptionEditor();
        }
	    setupProjectDetailsTabs(projectId);
	    setupProjectProgressCard();

    const customColorInput = document.getElementById(`project-color-input-${projectId}`);
    if (customColorInput) {
        customColorInput.addEventListener('change', (e) => {
            handleProjectCustomColorChange(projectId, e.target.value);
        });
    }

    // Re-initialize date pickers for project details
	    setTimeout(() => {
	        // Clear any existing flatpickr instances first
	        document.querySelectorAll('input.datepicker').forEach(input => {
	            if (input._flatpickrInstance) {
	                input._flatpickrInstance.destroy();
	                delete input._flatpickrInstance;
	            }
	        });
		        // Then initialize new ones - specifically target project detail datepickers
		        initializeDatePickers();
		    }, 50);

    // Render project tags in details view
    renderProjectDetailsTags(project.tags || [], projectId);

    // Project tag input Enter key support
    const projectTagInput = document.getElementById('project-details-tag-input');
    if (projectTagInput) {
        // Remove any existing listener to prevent duplicates
        projectTagInput.replaceWith(projectTagInput.cloneNode(true));
        const newInput = document.getElementById('project-details-tag-input');
        newInput.addEventListener('keydown', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                addProjectDetailsTag(projectId);
            }
        });
    }
    setupProjectTasksDragDrop(projectId);

    debugTimeEnd("projects", detailsTimer, {
        projectId,
        taskCount: projectTasks.length,
        completionPercentage
    });
		}

	// Progress bar status card: shows only the pointed segment's status (color, count, %). Mouse: hover opens
	// it under the segment (stays open while the pointer is over the strip or card), click navigates.
	// Touch: first tap opens the card instead of navigating; tapping the card navigates (data-action).
	function setupProjectProgressCard() {
	    const section = document.querySelector("#project-details-content .pd-progress");
	    const bar = section?.querySelector(".pd-segbar");
	    const card = section?.querySelector(".pd-seg-card");
	    if (!bar || !card) return;
	    let closeTimer = null;
	    const close = () => {
	        clearTimeout(closeTimer);
	        card.hidden = true;
	        section.classList.remove("is-card-open");
	        bar.querySelectorAll(".pd-segment.is-active").forEach((s) => s.classList.remove("is-active"));
	    };
	    const open = (seg, touch = false) => {
	        clearTimeout(closeTimer);
	        const status = seg.dataset.status;
	        bar.querySelectorAll(".pd-segment").forEach((s) => s.classList.toggle("is-active", s === seg));
	        card.querySelectorAll(".pd-seg-card-row").forEach((row) => {
	            const active = row.dataset.status === status;
	            row.classList.toggle("is-active", active);
	            if (active) row.querySelector(".pd-seg-card-hint").textContent = t(touch ? "projects.details.segmentHintTouch" : "projects.details.segmentHint");
	        });
	        card.hidden = false;
	        section.classList.add("is-card-open");
	        // Center under the segment, clamped inside the strip
	        const sectionRect = section.getBoundingClientRect();
	        const segRect = seg.getBoundingClientRect();
	        const barRect = bar.getBoundingClientRect();
	        const maxLeft = Math.max(0, sectionRect.width - card.offsetWidth);
	        const left = segRect.left + segRect.width / 2 - sectionRect.left - card.offsetWidth / 2;
	        card.style.left = `${Math.min(maxLeft, Math.max(0, left))}px`;
	        card.style.top = `${barRect.bottom - sectionRect.top + 8}px`;
	    };
	    const scheduleClose = () => {
	        clearTimeout(closeTimer);
	        closeTimer = setTimeout(close, 150);
	    };

	    bar.addEventListener("pointerover", (ev) => {
	        if (ev.pointerType === "touch") return;
	        const seg = ev.target.closest?.(".pd-segment");
	        if (seg) open(seg);
	    });
	    section.addEventListener("pointerleave", (ev) => { if (ev.pointerType !== "touch") scheduleClose(); });
	    section.addEventListener("pointerenter", (ev) => { if (ev.pointerType !== "touch") clearTimeout(closeTimer); });
	    bar.addEventListener("focusin", (ev) => {
	        const seg = ev.target.closest?.(".pd-segment");
	        if (seg && seg.matches(":focus-visible")) open(seg);
	    });
	    section.addEventListener("focusout", () => {
	        setTimeout(() => { if (!section.contains(document.activeElement)) close(); }, 0);
	    });
	    section.addEventListener("keydown", (ev) => { if (ev.key === "Escape") close(); });

	    // Touch: a tap on the bar opens (or toggles) the card instead of navigating
	    let lastPointerType = "mouse";
	    bar.addEventListener("pointerdown", (ev) => { lastPointerType = ev.pointerType; });
	    bar.addEventListener("click", (ev) => {
	        const seg = ev.target.closest?.(".pd-segment");
	        if (!seg) return;
	        if (lastPointerType === "touch") {
	            ev.preventDefault();
	            ev.stopPropagation();
	            if (!card.hidden && seg.classList.contains("is-active")) {
	                close();
	            } else {
	                open(seg, true);
	                card.scrollIntoView({ block: "nearest", behavior: "smooth" });
	            }
	            return;
	        }
	        close();
	    }, true);
	    card.addEventListener("click", (ev) => { if (ev.target.closest(".pd-seg-card-row")) close(); });

	    // Tap/click outside closes it; listener removes itself once the strip leaves the DOM
	    const onOutside = (ev) => {
	        if (!section.isConnected) {
	            document.removeEventListener("pointerdown", onOutside, true);
	            return;
	        }
	        if (!card.hidden && !section.contains(ev.target)) close();
	    };
	    document.addEventListener("pointerdown", onOutside, true);
	}

	// Project description reads as plain text; clicking the text or "Edit" switches the card to the
	// editor (box + toolbar), and leaving it — or "Done" — switches back. Saving stays on the editor's blur.
	function setupProjectDescriptionEditMode() {
	    const card = document.getElementById("project-description-card");
	    const button = document.getElementById("project-description-edit-btn");
	    if (!card || !button) return;

	    const readSurface = card.querySelector(".rich-editor");
	    if (readSurface) readSurface.title = t("projects.details.clickToEdit");

	    const setEditing = (editing) => {
	        card.classList.toggle("is-editing", editing);
	        button.setAttribute("aria-pressed", String(editing));
	        if (readSurface) readSurface.title = editing ? "" : t("projects.details.clickToEdit");
	    };

	    card.addEventListener("focusin", () => setEditing(true));
	    // Popovers (e.g. the date chip picker) live outside the card, so re-check after focus settles
	    card.addEventListener("focusout", () => {
	        setTimeout(() => {
	            if (!card.contains(document.activeElement)) setEditing(false);
	        }, 120);
	    });

	    // Keep the caret in the editor while using the toolbar or the Edit/Done button
	    card.querySelector(".editor-toolbar")?.addEventListener("mousedown", (e) => e.preventDefault());
	    button.addEventListener("mousedown", (e) => e.preventDefault());
	    button.addEventListener("click", () => {
	        if (card.classList.contains("is-editing")) {
	            if (card.contains(document.activeElement)) document.activeElement.blur();
	            setEditing(false);
	        } else {
	            setEditing(true);
	            focusProjectDescriptionEditor("end");
	        }
	    });
	}

	function setupProjectDetailsTabs(projectId) {
	    const container = document.getElementById("project-details-content");
	    if (!container) return;

	    const tabsContainer = container.querySelector(".project-details-tabs");
	    if (!tabsContainer) return;

	    const detailsTab = container.querySelector("#project-details-tab");
	    const historyTab = container.querySelector("#project-history-tab");
	    if (!detailsTab || !historyTab) return;

	    const setActive = (tabName) => {
	        tabsContainer.querySelectorAll(".modal-tab").forEach((tab) => tab.classList.remove("active"));
	        container.querySelectorAll(".modal-tab-content").forEach((content) => content.classList.remove("active"));

	        const nextTab = tabsContainer.querySelector(`.modal-tab[data-tab="${tabName}"]`);
	        if (nextTab) nextTab.classList.add("active");

	        if (tabName === "history") {
	            historyTab.classList.add("active");
	            renderProjectHistory(projectId);

	            const historyContainer = historyTab.querySelector(".project-history-section");
	            if (historyContainer) historyContainer.scrollTop = 0;
	        } else {
	            detailsTab.classList.add("active");
	        }
	    };

	    tabsContainer.querySelectorAll(".modal-tab").forEach((tab) => {
	        tab.addEventListener("click", () => {
	            setActive(tab.dataset.tab);
	        });
	    });

	    setActive("details");
	}

function setupProjectTasksDragDrop(projectId) {
    const list = document.getElementById('project-tasks-list');
    if (!list) return;

    // On touch devices the `draggable` attribute suppresses the subsequent click
    // event in iOS Chrome, preventing the task modal from opening. iOS Chrome does
    // not reliably report `(pointer: coarse)`, so use getIsMobileCached() which also
    // matches `(max-width: 768px)`. Drag-to-reorder requires a mouse anyway.
    if (getIsMobileCached()) {
        list.querySelectorAll('[draggable]').forEach(el => el.removeAttribute('draggable'));
        return;
    }

    let draggingViaHandle = false;
    let dragSrc = null;

    const getItems = () => [...list.querySelectorAll('.project-task-item[data-task-id]')];

    // Keep dropping reliable even when pointer is between cards.
    list.addEventListener('dragover', (e) => { e.preventDefault(); });

    getItems().forEach(item => {
        const handle = item.querySelector('.drag-handle');

        // Only start drag when initiated from the handle
        if (handle) {
            handle.addEventListener('mousedown', () => { draggingViaHandle = true; });
            handle.addEventListener('touchstart', () => { draggingViaHandle = true; }, { passive: true });
            // Prevent handle tap from opening the task
            handle.addEventListener('click', (e) => e.stopPropagation());
        }

        item.addEventListener('dragstart', (e) => {
            if (!draggingViaHandle) { e.preventDefault(); return; }
            dragSrc = item;
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', item.dataset.taskId);
            // Defer class add so the ghost image renders normally
            setTimeout(() => item.classList.add('dragging'), 0);
        });

        item.addEventListener('dragend', () => {
            draggingViaHandle = false;
            dragSrc?.classList.remove('dragging');
            getItems().forEach(i => i.classList.remove('drag-over-top', 'drag-over-bottom'));
            dragSrc = null;
        });

        item.addEventListener('dragover', (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (!dragSrc || item === dragSrc) return;
            const mid = item.getBoundingClientRect().top + item.getBoundingClientRect().height / 2;
            getItems().forEach(i => i.classList.remove('drag-over-top', 'drag-over-bottom'));
            item.classList.add(e.clientY < mid ? 'drag-over-top' : 'drag-over-bottom');
        });

        item.addEventListener('dragleave', (e) => {
            // Only clear if leaving the item entirely (not entering a child)
            if (!item.contains(e.relatedTarget)) {
                item.classList.remove('drag-over-top', 'drag-over-bottom');
            }
        });

        item.addEventListener('drop', (e) => {
            e.preventDefault();
            if (!dragSrc || item === dragSrc) return;
            const before = item.classList.contains('drag-over-top');
            item.classList.remove('drag-over-top', 'drag-over-bottom');
            if (before) {
                list.insertBefore(dragSrc, item);
            } else {
                list.insertBefore(dragSrc, item.nextSibling);
            }
            // Persist new order to task objects and save, then re-render
            // so the ↕ Auto-sort button appears/disappears correctly
            const reorderUpdates = getItems().map((el, idx) => ({
                id: parseInt(el.dataset.taskId, 10),
                projectOrder: idx
            }));
            saveTasksReorder(reorderUpdates).catch(err => {
                console.error('Failed to save task reorder:', err);
                showErrorNotification(t('error.saveChangesFailed'));
            });
            syncProjectDetailsAutoSortBtn(projectId);
        });
    });
}

function resetProjectTaskOrder(projectId) {
    tasks.forEach(task => {
        if (task.projectId === projectId) {
            delete task.projectOrder;
        }
    });
    const reorderUpdates = tasks.filter(t => t.projectId === projectId).map(t => ({ id: t.id, projectOrder: null }));
    saveTasksReorder(reorderUpdates).catch(err => {
        console.error('Failed to save task order reset:', err);
        showErrorNotification(t('error.saveChangesFailed'));
    });
    showProjectDetails(projectId);
}

function syncProjectDetailsAutoSortBtn(projectId) {
    const section = document.querySelector('.project-tasks-section');
    if (!section) return;

    const actions = section.querySelector('.section-header > div:last-child');
    if (!actions) return;

    const existing = actions.querySelector('[data-action="resetProjectTaskOrder"]');
    const hasManualOrder = tasks.some(task => task.projectId === projectId && typeof task.projectOrder === 'number');

    if (hasManualOrder && !existing) {
        const btn = document.createElement('button');
        btn.className = 'add-btn auto-sort-btn';
        btn.dataset.action = 'resetProjectTaskOrder';
        btn.dataset.param = String(projectId);
        btn.title = t('projects.details.resetSortTitle') || '';
        btn.textContent = `↕ ${t('projects.details.resetSort') || 'Auto-sort'}`;

        const beforeBtn =
            actions.querySelector('[data-action="navigateToProjectTasksList"]') ||
            actions.querySelector('[data-action="openTaskModalForProject"]');
        if (beforeBtn) {
            actions.insertBefore(btn, beforeBtn);
        } else {
            actions.appendChild(btn);
        }
    } else if (!hasManualOrder && existing) {
        existing.remove();
    }
}

function resetExpandedProjectTaskOrder(projectId) {
    tasks.forEach(task => {
        if (task.projectId === projectId) {
            delete task.projectOrder;
        }
    });
    const reorderUpdates = tasks.filter(t => t.projectId === projectId).map(t => ({ id: t.id, projectOrder: null }));
    saveTasksReorder(reorderUpdates).catch(() => {});
    renderProjects();
}

function setupExpandedProjectsDragDrop() {
    const projectItems = document.querySelectorAll('.pc-item[id^="project-item-"], .project-list-item[id^="project-item-"]');

    // On touch devices the `draggable` attribute suppresses the subsequent click
    // event in iOS Chrome, preventing the task modal from opening. iOS Chrome does
    // not reliably report `(pointer: coarse)`, so use getIsMobileCached() which also
    // matches `(max-width: 768px)`. Drag-to-reorder requires a mouse anyway.
    if (getIsMobileCached()) {
        projectItems.forEach(p => p.querySelectorAll('[draggable]').forEach(el => el.removeAttribute('draggable')));
        return;
    }

    projectItems.forEach(projectItem => {
        const projectId = parseInt(projectItem.id.replace('project-item-', ''), 10);
        const container = projectItem.querySelector('.expanded-tasks-container');
        if (!container) return;

        let draggingViaHandle = false;
        let dragSrc = null;

        const getItems = () => [...container.querySelectorAll('.expanded-task-item[data-task-id]')];

        // Fallback: ensure dragover on container always allows drop
        container.addEventListener('dragover', (e) => { e.preventDefault(); });

        getItems().forEach(item => {
            const handle = item.querySelector('.expanded-drag-handle');
            if (handle) {
                handle.addEventListener('mousedown', () => { draggingViaHandle = true; });
                handle.addEventListener('touchstart', () => { draggingViaHandle = true; }, { passive: true });
                handle.addEventListener('click', (e) => e.stopPropagation());
            }

            item.addEventListener('dragstart', (e) => {
                if (!draggingViaHandle) { e.preventDefault(); return; }
                dragSrc = item;
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', item.dataset.taskId);
                setTimeout(() => item.classList.add('dragging'), 0);
            });

            item.addEventListener('dragend', () => {
                draggingViaHandle = false;
                dragSrc?.classList.remove('dragging');
                getItems().forEach(i => i.classList.remove('drag-over-top', 'drag-over-bottom'));
                dragSrc = null;
            });

            item.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (!dragSrc || item === dragSrc) return;
                const mid = item.getBoundingClientRect().top + item.getBoundingClientRect().height / 2;
                getItems().forEach(i => i.classList.remove('drag-over-top', 'drag-over-bottom'));
                item.classList.add(e.clientY < mid ? 'drag-over-top' : 'drag-over-bottom');
            });

            item.addEventListener('dragleave', (e) => {
                if (!item.contains(e.relatedTarget)) {
                    item.classList.remove('drag-over-top', 'drag-over-bottom');
                }
            });

            item.addEventListener('drop', (e) => {
                e.preventDefault();
                if (!dragSrc || item === dragSrc) return;
                const before = item.classList.contains('drag-over-top');
                item.classList.remove('drag-over-top', 'drag-over-bottom');
                if (before) {
                    container.insertBefore(dragSrc, item);
                } else {
                    container.insertBefore(dragSrc, item.nextSibling);
                }
                getItems().forEach((el, idx) => {
                    const task = tasks.find(t => t.id === parseInt(el.dataset.taskId, 10));
                    if (task) task.projectOrder = idx;
                });
                const reorderUpdates = getItems().map((el, idx) => ({
                    id: parseInt(el.dataset.taskId, 10),
                    projectOrder: idx
                }));
                saveTasksReorder(reorderUpdates).catch(err => {
                    console.error('Failed to save task reorder:', err);
                    showErrorNotification(t('error.saveChangesFailed'));
                });
                // Surgically update the Auto-sort button — avoids re-rendering the DOM
                // mid-drag which breaks subsequent drag operations.
                syncExpandedAutoSortBtn(projectId, container);
            });
        });
    });
}

/**
 * Show or hide the Auto-sort button inside an expanded project header
 * without triggering a full renderProjects() re-render.
 */
function syncExpandedAutoSortBtn(projectId, container) {
    const header = container.querySelector('.expanded-tasks-header');
    if (!header) return;
    const btnGroup = header.querySelector('div:last-child');
    if (!btnGroup) return;
    const existing = btnGroup.querySelector('[data-action="resetExpandedProjectTaskOrder"]');
    const hasManualOrder = tasks.some(task => task.projectId === projectId && typeof task.projectOrder === 'number');
    if (hasManualOrder && !existing) {
        const btn = document.createElement('button');
        btn.className = 'add-btn expanded-add-task-btn auto-sort-btn';
        btn.type = 'button';
        btn.dataset.action = 'resetExpandedProjectTaskOrder';
        btn.dataset.param = String(projectId);
        btn.dataset.stopPropagation = 'true';
        btn.title = t('projects.details.resetSortTitle') || '';
        btn.textContent = `↕ ${t('projects.details.resetSort') || 'Auto-sort'}`;
        btnGroup.insertBefore(btn, btnGroup.firstChild);
    } else if (!hasManualOrder && existing) {
        existing.remove();
    }
}

function handleDeleteProject(projectId) {
    projectToDelete = projectId;
    deleteProject();
}

function deleteProject() {
    const projectId = projectToDelete;
    if (!projectId) return;
    
    // Check if project has tasks
    const projectTasks = tasks.filter(t => t.projectId === projectId);
    const checkbox = document.getElementById('delete-tasks-checkbox');
    
    // Find the parent div that contains the checkbox label and description
    const checkboxContainer = checkbox ? checkbox.closest('div[style*="margin: 16px"]') : null;
    
    if (checkboxContainer) {
        if (projectTasks.length === 0) {
            // Hide entire checkbox section if no tasks
            checkboxContainer.style.display = 'none';
        } else {
            // Show checkbox section if tasks exist
            checkboxContainer.style.display = 'block';
        }
    }
    
    document.getElementById('project-confirm-modal').classList.add('active');
    const projectConfirmInput = document.getElementById('project-confirm-input');
    projectConfirmInput.value = '';
    projectConfirmInput.focus();

    // Auto-convert to lowercase as user types for better UX
    const projectLowercaseHandler = function(e) {
        const start = e.target.selectionStart;
        const end = e.target.selectionEnd;
        e.target.value = e.target.value.toLowerCase();
        e.target.setSelectionRange(start, end);
    };
    projectConfirmInput.addEventListener('input', projectLowercaseHandler);

    // Add keyboard support for project delete modal
    document.addEventListener('keydown', function(e) {
        const projectConfirmModal = document.getElementById('project-confirm-modal');
        if (!projectConfirmModal || !projectConfirmModal.classList.contains('active')) return;
        
        if (e.key === 'Escape') {
            e.preventDefault();
            closeProjectConfirmModal();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            confirmProjectDelete();
        }
    });

}

// ─── Project Templates ───────────────────────────────────────────────────────

async function saveTemplatesStorage() {
    try {
        await saveTemplatesData(templates);
    } catch (error) {
        console.error("Error saving templates:", error);
        showErrorNotification(t('error.saveDataFailed'));
        throw error;
    }
}

let templateSourceProjectId = null;

function openSaveAsTemplateModal(projectId) {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    // Close the project options menu before opening the modal
    const optionsMenu = document.getElementById('project-options-menu');
    if (optionsMenu) optionsMenu.style.display = 'none';

    templateSourceProjectId = projectId;
    const modal = document.getElementById('save-as-template-modal');
    if (!modal) return;

    // Pre-fill name from project
    const nameInput = modal.querySelector('#template-name-input');
    if (nameInput) nameInput.value = project.name;

    // Show task count
    const projectTasks = tasks.filter(t => t.projectId === projectId);
    const includeCheckbox = modal.querySelector('#template-include-tasks');
    const taskCountSpan = modal.querySelector('#template-task-count');
    if (taskCountSpan) taskCountSpan.textContent = projectTasks.length;
    if (includeCheckbox) {
        includeCheckbox.checked = projectTasks.length > 0;
        includeCheckbox.disabled = projectTasks.length === 0;
    }

    modal.style.display = 'flex';
    if (nameInput) {
        nameInput.focus();
        nameInput.select();
    }
}

function closeSaveAsTemplateModal() {
    const modal = document.getElementById('save-as-template-modal');
    if (modal) modal.style.display = 'none';
    templateSourceProjectId = null;
}

function confirmSaveAsTemplate() {
    const modal = document.getElementById('save-as-template-modal');
    if (!modal || templateSourceProjectId === null) return;

    const nameInput = modal.querySelector('#template-name-input');
    const includeCheckbox = modal.querySelector('#template-include-tasks');
    const name = nameInput ? nameInput.value.trim() : '';
    if (!name) {
        if (nameInput) nameInput.focus();
        return;
    }

    const project = projects.find(p => p.id === templateSourceProjectId);
    if (!project) return;

    const includeTasks = includeCheckbox ? includeCheckbox.checked : false;
    const projectTasks = includeTasks
        ? tasks
            .filter(t => t.projectId === templateSourceProjectId)
            .map(t => ({
                title: t.title || '',
                description: t.description || '',
                priority: t.priority || 'medium',
                status: 'backlog',
                tags: Array.isArray(t.tags) ? [...t.tags] : []
            }))
        : [];

    const template = {
        id: String(Date.now()),
        name,
        description: project.description || '',
        tags: Array.isArray(project.tags) ? [...project.tags] : [],
        tasks: projectTasks,
        createdAt: new Date().toISOString()
    };

    templates = [...templates, template];
    closeSaveAsTemplateModal();
    renderTemplateDropdown();

    saveTemplatesStorage().catch(() => {});
    showSuccessNotification(t('projects.template.savedSuccess'));
}

function deleteTemplateById(templateId) {
    templates = templates.filter(tpl => tpl.id !== templateId);
    renderTemplateDropdown();
    saveTemplatesStorage().catch(() => {});
    showSuccessNotification(t('projects.template.deletedSuccess'));
}

let templateToDelete = null;
let docsDeleteConfirmResolver = null;
let docsDeleteModalState = null;
let docsVideoInsertResolver = null;
let docsStatusChipResolver = null;
let docsStatusScrollHandler = null;
const DOCS_STATUS_TONES = ['grey', 'blue', 'green', 'amber', 'red', 'purple'];

function openDeleteTemplateModal(templateId) {
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    templateToDelete = templateId;
    const body = document.getElementById('delete-template-body');
    if (body) {
        const taskCount = tpl.tasks ? tpl.tasks.length : 0;
        body.textContent = `"${tpl.name}" and its ${taskCount} task${taskCount !== 1 ? 's' : ''} will be permanently removed.`;
    }
    const modal = document.getElementById('delete-template-modal');
    if (modal) modal.style.display = 'flex';
}

// Small yes/no confirm for docs. Buttons are bound directly rather than through
// data-action so the resolver stays local to the promise.
function openDocsConfirmModal({ title, body, acceptLabel, cancelLabel }) {
    const modal = document.getElementById('docs-confirm-modal');
    if (!modal) return Promise.resolve(true);

    const titleEl = document.getElementById('docs-confirm-title');
    const bodyEl = document.getElementById('docs-confirm-body');
    if (titleEl) titleEl.textContent = String(title || '');
    if (bodyEl) bodyEl.textContent = String(body || '');

    return new Promise((resolve) => {
        let settled = false;
        const close = (result) => {
            if (settled) return;
            settled = true;
            modal.style.display = 'none';
            keepBtn?.removeEventListener('click', onKeep);
            confirmBtn?.removeEventListener('click', onConfirm);
            modal.removeEventListener('click', onBackdrop);
            document.removeEventListener('keydown', onKeydown, true);
            resolve(result);
        };
        const onKeep = () => close(false);
        const onConfirm = () => close(true);
        const onBackdrop = (event) => {
            if (isBackdropClick(event, modal)) close(false);
        };
        const onKeydown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                close(false);
            }
        };

        const keepBtn = document.getElementById('docs-confirm-cancel-btn');
        const confirmBtn = document.getElementById('docs-confirm-accept-btn');
        if (keepBtn) keepBtn.textContent = String(cancelLabel || t('common.cancel'));
        if (confirmBtn) confirmBtn.textContent = String(acceptLabel || '');
        keepBtn?.addEventListener('click', onKeep);
        confirmBtn?.addEventListener('click', onConfirm);
        modal.addEventListener('click', onBackdrop);
        document.addEventListener('keydown', onKeydown, true);

        modal.style.display = 'flex';
        requestAnimationFrame(() => keepBtn?.focus({ preventScroll: true }));
    });
}

function openDocsReplacementModal() {
    return openDocsConfirmModal({
        title: t('docs.replaceGuardTitle'),
        body: t('docs.replaceGuardBody'),
        acceptLabel: t('docs.replaceGuardConfirm'),
        cancelLabel: t('docs.replaceGuardKeep')
    });
}

function openDocsDeleteModal(config = {}) {
    const modal = document.getElementById('docs-delete-modal');
    const titleEl = document.getElementById('docs-delete-title');
    const body = document.getElementById('docs-delete-body');
    const summary = document.getElementById('docs-delete-summary');
    const input = document.getElementById('docs-delete-confirm-input');
    const error = document.getElementById('docs-delete-confirm-error');
    const instruction = document.getElementById('docs-delete-confirm-instruction');
    const deleteBtn = document.getElementById('docs-delete-confirm-btn');
    const mode = ['bulk', 'trash-purge-single', 'trash-empty'].includes(String(config?.mode || ''))
        ? String(config.mode)
        : 'single';
    const requiresTypedDelete = mode === 'bulk' || mode === 'trash-empty';
    docsDeleteModalState = {
        mode,
        title: String(config?.title || t('docs.untitledPage')),
        rootItems: Array.isArray(config?.rootItems) ? config.rootItems : [],
        effectiveItems: Array.isArray(config?.effectiveItems) ? config.effectiveItems : []
    };

    if (titleEl) {
        if (mode === 'bulk') {
            titleEl.textContent = t('docs.bulkTrashTitle');
        } else if (mode === 'trash-purge-single') {
            titleEl.textContent = t('docs.trashPermanentDeleteTitle');
        } else if (mode === 'trash-empty') {
            titleEl.textContent = t('docs.trashEmptyConfirmTitle');
        } else {
            titleEl.textContent = t('docs.trashPage');
        }
    }
    if (body) {
        if (mode === 'bulk') {
            body.textContent = t('docs.bulkTrashBody', {
                count: docsDeleteModalState.effectiveItems.length
            });
        } else if (mode === 'trash-purge-single') {
            body.textContent = t('docs.trashPermanentDeleteBody', {
                title: docsDeleteModalState.title
            });
        } else if (mode === 'trash-empty') {
            body.textContent = t('docs.trashEmptyConfirmBody', {
                count: docsDeleteModalState.effectiveItems.length
            });
        } else {
            body.textContent = `"${docsDeleteModalState.title}" ${t('docs.trashConfirmBody')}`;
        }
    }
    if (summary) {
        if (mode === 'bulk') {
            const rootList = docsDeleteModalState.rootItems
                .map((item) => `<li>${escapeHtml(item.title || t('docs.untitledPage'))}</li>`)
                .join('');
            const effectivePreview = docsDeleteModalState.effectiveItems
                .slice(0, 8)
                .map((item) => `<li>${escapeHtml(item.title || t('docs.untitledPage'))}</li>`)
                .join('');
            const remainingCount = Math.max(0, docsDeleteModalState.effectiveItems.length - 8);
            summary.innerHTML = `
                <div class="docs-delete-summary__meta">${escapeHtml(t('docs.bulkTrashRoots', { count: docsDeleteModalState.rootItems.length }))}</div>
                <ul class="docs-delete-summary__list">${rootList}</ul>
                <div class="docs-delete-summary__note">${escapeHtml(t('docs.bulkTrashDescendants'))}</div>
                <div class="docs-delete-summary__meta">${escapeHtml(t('docs.bulkTrashAffected', { count: docsDeleteModalState.effectiveItems.length }))}</div>
                <ul class="docs-delete-summary__list">${effectivePreview}${remainingCount > 0 ? `<li>${escapeHtml(t('docs.bulkTrashMore', { count: remainingCount }))}</li>` : ''}</ul>
            `;
            summary.hidden = false;
        } else if (mode === 'trash-empty') {
            const effectivePreview = docsDeleteModalState.effectiveItems
                .slice(0, 8)
                .map((item) => `<li>${escapeHtml(item.title || t('docs.untitledPage'))}</li>`)
                .join('');
            const remainingCount = Math.max(0, docsDeleteModalState.effectiveItems.length - 8);
            summary.innerHTML = `
                <div class="docs-delete-summary__meta">${escapeHtml(t('docs.trashEmptyAffected', { count: docsDeleteModalState.effectiveItems.length }))}</div>
                <ul class="docs-delete-summary__list">${effectivePreview}${remainingCount > 0 ? `<li>${escapeHtml(t('docs.bulkTrashMore', { count: remainingCount }))}</li>` : ''}</ul>
            `;
            summary.hidden = false;
        } else {
            summary.hidden = true;
            summary.innerHTML = '';
        }
    }
    if (instruction) {
        instruction.hidden = !requiresTypedDelete;
    }
    if (input) {
        input.value = '';
        input.hidden = !requiresTypedDelete;
    }
    if (error) {
        error.textContent = t('docs.bulkDeleteTypeError');
        error.style.display = 'none';
    }
    if (deleteBtn) {
        if (mode === 'trash-empty') {
            deleteBtn.textContent = t('docs.trashEmptyButton');
        } else if (mode === 'trash-purge-single') {
            deleteBtn.textContent = t('docs.trashDeletePermanent');
        } else {
            deleteBtn.textContent = t('common.delete');
        }
    }
    if (modal) modal.style.display = 'flex';
    if (docsDeleteConfirmResolver) {
        docsDeleteConfirmResolver(false);
    }
    window.setTimeout(() => {
        if (requiresTypedDelete) input?.focus();
    }, 0);
    return new Promise((resolve) => {
        docsDeleteConfirmResolver = resolve;
    });
}

function closeDocsDeleteModal(result = false) {
    const modal = document.getElementById('docs-delete-modal');
    const input = document.getElementById('docs-delete-confirm-input');
    const error = document.getElementById('docs-delete-confirm-error');
    if (modal) modal.style.display = 'none';
    if (input) input.value = '';
    if (error) {
        error.textContent = t('docs.bulkDeleteTypeError');
        error.style.display = 'none';
    }
    docsDeleteModalState = null;
    if (docsDeleteConfirmResolver) {
        const resolve = docsDeleteConfirmResolver;
        docsDeleteConfirmResolver = null;
        resolve(result === true);
    }
}

function confirmDocsDeleteModal() {
    if (docsDeleteModalState?.mode === 'bulk' || docsDeleteModalState?.mode === 'trash-empty') {
        const input = document.getElementById('docs-delete-confirm-input');
        const error = document.getElementById('docs-delete-confirm-error');
        const value = String(input?.value || '').trim().toLowerCase();
        if (value !== 'delete') {
            if (error) {
                error.textContent = t('docs.bulkDeleteTypeError');
                error.style.display = 'block';
            }
            input?.focus();
            input?.select();
            return;
        }
    }
    closeDocsDeleteModal(true);
}

// ── Docs: Duplicate page ──────────────────────────────────────────────────
let _docDuplicateSource = null; // { projectId, docId }

function handleDuplicateDocPage(projectId, docId) {
    const state = getDocsState(projectId);
    const source = state.items.find((item) => String(item.id) === String(docId));
    if (!source) return;

    _docDuplicateSource = { projectId, docId };

    const titleInput = document.getElementById('docs-duplicate-title');
    const errorEl = document.getElementById('docs-duplicate-title-error');
    const childrenWrap = document.getElementById('docs-duplicate-children-wrap');
    const childrenToggle = document.getElementById('docs-duplicate-children');
    const confirmBtn = document.getElementById('docs-duplicate-confirm-btn');

    if (titleInput) titleInput.value = 'Copy of ' + (source.title || t('docs.untitledPage'));
    if (errorEl) { errorEl.textContent = ''; errorEl.style.display = 'none'; }
    if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.textContent = t('docs.duplicate.confirm'); }

    const countDescendants = (id) => {
        const kids = state.items.filter((item) => String(item.parentId || '') === String(id));
        return kids.reduce((sum, kid) => sum + 1 + countDescendants(kid.id), 0);
    };
    const descendantCount = countDescendants(docId);
    const hasChildren = descendantCount > 0;
    if (childrenWrap) {
        childrenWrap.style.display = hasChildren ? 'block' : 'none';
        const label = childrenWrap.querySelector('[data-i18n="docs.duplicate.includeChildren"]');
        if (label) label.textContent = `${t('docs.duplicate.includeChildren')} (${descendantCount})`;
    }
    if (childrenToggle) childrenToggle.checked = false;

    const modal = document.getElementById('docs-duplicate-modal');
    if (modal) {
        modal.style.display = 'flex';
        setTimeout(() => titleInput?.focus(), 50);
    }
}

function closeDocsDuplicateModal() {
    const modal = document.getElementById('docs-duplicate-modal');
    if (modal) modal.style.display = 'none';
    _docDuplicateSource = null;
}

async function confirmDuplicateDocPage() {
    if (!_docDuplicateSource) return;
    const { projectId, docId } = _docDuplicateSource;

    const titleInput = document.getElementById('docs-duplicate-title');
    const errorEl = document.getElementById('docs-duplicate-title-error');
    const childrenToggle = document.getElementById('docs-duplicate-children');
    const confirmBtn = document.getElementById('docs-duplicate-confirm-btn');

    const newTitle = (titleInput?.value || '').trim();

    const showError = (msg) => {
        if (errorEl) { errorEl.textContent = msg; errorEl.style.display = 'block'; }
    };

    if (!newTitle) { showError(t('docs.duplicate.errorEmptyTitle')); return; }

    const state = getDocsState(projectId);
    const source = state.items.find((item) => String(item.id) === String(docId));
    if (!source) return;

    const parentId = source.parentId || '';
    const siblings = state.items.filter(
        (item) => String(item.parentId || '') === String(parentId) && String(item.id) !== String(docId)
    );
    const clash = siblings.some((item) => (item.title || '').trim().toLowerCase() === newTitle.toLowerCase());
    if (clash) { showError(t('docs.duplicate.errorDuplicateTitle')); return; }

    if (errorEl) { errorEl.style.display = 'none'; }
    if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.textContent = '…'; }

    const nextSortOrder = state.items.length ? Math.max(...state.items.map((item) => Number(item.sortOrder) || 0)) + 1 : 0;
    const nextTreeOrder = state.items.length ? Math.max(...state.items.map((item) => Number(item.treeOrder) || 0)) + 1 : 0;

    try {
        const response = await createProjectDoc({
            title: newTitle,
            contentJson: JSON.parse(JSON.stringify(source.contentJson || { type: 'doc', content: [{ type: 'paragraph' }] })),
            sortOrder: nextSortOrder,
            treeOrder: nextTreeOrder,
            parentId: parentId,
            icon: String(source.icon || ''),
            widthMode: source.widthMode || 'wide'
        });

        const newRecord = mergeDocsRecord(projectId, response?.item || {});

        if (childrenToggle?.checked) {
            const children = state.items.filter((item) => String(item.parentId || '') === String(docId));
            let childSortOrder = nextSortOrder + 1;
            let childTreeOrder = nextTreeOrder + 1;
            for (const child of children) {
                const childResp = await createProjectDoc({
                    title: child.title || '',
                    contentJson: JSON.parse(JSON.stringify(child.contentJson || { type: 'doc', content: [{ type: 'paragraph' }] })),
                    sortOrder: childSortOrder++,
                    treeOrder: childTreeOrder++,
                    parentId: String(newRecord.id),
                    icon: String(child.icon || ''),
                    widthMode: child.widthMode || 'wide'
                });
                mergeDocsRecord(projectId, childResp?.item || {});
            }
        }

        state.currentDocId = newRecord.id;
        setDocsBaseline(projectId, newRecord);
        syncDocsRouteState({
            docId: newRecord.id,
            slug: newRecord.slug || slugifyProjectDocTitle(newRecord.title),
            mode: 'view'
        });
        refreshDocsSurfaces(projectId);
        showSuccessNotification(t('docs.duplicate.success'));
        closeDocsDuplicateModal();
    } catch (error) {
        showErrorNotification(error?.message || String(error));
        if (confirmBtn) { confirmBtn.disabled = false; confirmBtn.textContent = t('docs.duplicate.confirm'); }
    }
}
// ── End Docs: Duplicate page ──────────────────────────────────────────────

function openDocsVideoModal() {
    const modal = document.getElementById('docs-video-modal');
    const input = document.getElementById('docs-video-url');
    const error = document.getElementById('docs-video-error');

    if (input) input.value = '';
    if (error) {
        error.textContent = '';
        error.style.display = 'none';
    }

    if (modal) modal.style.display = 'flex';
    if (docsVideoInsertResolver) {
        docsVideoInsertResolver('');
    }

    window.setTimeout(() => {
        input?.focus();
        input?.select();
    }, 0);

    return new Promise((resolve) => {
        docsVideoInsertResolver = resolve;
    });
}

function closeDocsVideoModal(value = '') {
    const modal = document.getElementById('docs-video-modal');
    if (modal) modal.style.display = 'none';
    const error = document.getElementById('docs-video-error');
    if (error) {
        error.textContent = '';
        error.style.display = 'none';
    }
    if (docsVideoInsertResolver) {
        const resolve = docsVideoInsertResolver;
        docsVideoInsertResolver = null;
        resolve(String(value || ''));
    }
}

function confirmDocsVideoModal() {
    const input = document.getElementById('docs-video-url');
    const value = String(input?.value || '').trim();
    if (!value) {
        const error = document.getElementById('docs-video-error');
        if (error) {
            error.textContent = t('docs.videoRequired');
            error.style.display = 'block';
        }
        input?.focus();
        return;
    }
    closeDocsVideoModal(value);
}

function setDocsStatusToneSelection(tone) {
    const normalized = DOCS_STATUS_TONES.includes(tone) ? tone : 'grey';
    document.querySelectorAll('[data-docs-status-tone]').forEach((button) => {
        button.classList.toggle('active', button.dataset.docsStatusTone === normalized);
        button.setAttribute('aria-pressed', button.dataset.docsStatusTone === normalized ? 'true' : 'false');
    });
    const hiddenInput = document.getElementById('docs-status-tone');
    if (hiddenInput) hiddenInput.value = normalized;
}

function openDocsStatusModal(initial = {}, anchorEl = null) {
    return openDocsStatusPopover(initial, anchorEl);
}

function closeDocsStatusModal(value = null) {
    closeDocsStatusPopover(value);
}

function confirmDocsStatusModal() {
    confirmDocsStatusPopover();
}

/**
 * Keep a fixed-position docs popover inside the viewport.
 *
 * Anchoring below the caret is right until the caret is near the bottom of the
 * page — then the popover rendered below the fold, unreadable and unreachable.
 * Flip it ABOVE the reference rect when it doesn't fit underneath.
 *
 * @param {HTMLElement} popover  already displayed (offsetHeight must be real)
 * @param {number} refTop        top of the caret/anchor it is attached to
 */
function clampDocsPopoverToViewport(popover, refTop) {
    if (!popover) return;
    const margin = 8;
    const h = popover.offsetHeight || 0;
    const w = popover.offsetWidth || 0;

    let top = parseFloat(popover.style.top) || 0;
    let left = parseFloat(popover.style.left) || 0;

    if (top + h > window.innerHeight - margin) {
        const above = refTop - h - margin;
        // Flip above when there's room; otherwise pin to the bottom edge rather
        // than letting it hang off-screen.
        top = above >= margin ? above : Math.max(margin, window.innerHeight - h - margin);
    }

    popover.style.top = `${Math.round(Math.max(margin, top))}px`;
    popover.style.left = `${Math.round(Math.min(Math.max(left, margin), Math.max(margin, window.innerWidth - w - margin)))}px`;
}

function openDocsStatusPopover(initial = {}, anchorEl = null, coords = null) {
    const popover = document.getElementById('docs-status-popover');
    const input = document.getElementById('docs-status-label');
    const tone = DOCS_STATUS_TONES.includes(initial.tone) ? initial.tone : 'grey';
    const label = String(initial.label || '').trim() || t('docs.statusDefault');

    // Hide table menu while status popover is open
    const tableMenu = document.getElementById('docs-table-menu');
    if (tableMenu) tableMenu.dataset.statusPopoverOpen = 'true';

    // Bind tone buttons once
    popover?.querySelectorAll('[data-docs-status-tone]').forEach((button) => {
        if (button.dataset.bound === 'true') return;
        button.dataset.bound = 'true';
        button.addEventListener('mousedown', (e) => {
            e.preventDefault(); // prevent blur of input
            setDocsStatusToneSelection(button.dataset.docsStatusTone);
            confirmDocsStatusPopover();
        });
    });

    if (!popover?._keyBound) {
        if (popover) popover._keyBound = true;
        input?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); confirmDocsStatusPopover(); }
            if (e.key === 'Escape') { e.preventDefault(); closeDocsStatusPopover(null); }
        });
        document.addEventListener('mousedown', (e) => {
            const p = document.getElementById('docs-status-popover');
            if (p && p.style.display !== 'none' && !p.contains(e.target)) {
                closeDocsStatusPopover(null);
            }
        }, true);
        const copyBtn = document.getElementById('docs-status-copy-btn');
        if (copyBtn) {
            copyBtn.addEventListener('mousedown', (e) => e.preventDefault()); // keep input focus
            copyBtn.addEventListener('click', () => {
                const labelVal = document.getElementById('docs-status-label')?.value.trim().toUpperCase() || 'STATUS';
                const toneVal = document.getElementById('docs-status-tone')?.value || 'grey';
                const html = `<span data-doc-status-chip="" data-tone="${toneVal}" data-label="${labelVal}" class="docs-status-chip docs-status-chip--${toneVal}">${labelVal}</span>`;
                navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([labelVal], { type: 'text/plain' })
                    })
                ]).then(() => {
                    copyBtn.dataset.copied = 'true';
                    setTimeout(() => delete copyBtn.dataset.copied, 1500);
                }).catch(() => {
                    // Fallback for browsers without ClipboardItem
                    navigator.clipboard.writeText(labelVal).catch(() => {});
                });
            });
        }
    }

    if (input) input.value = label;
    setDocsStatusToneSelection(tone);

    if (docsStatusChipResolver) {
        docsStatusChipResolver(null);
    }

    if (popover) {
        // Move to body to escape any CSS transform/filter ancestor that breaks position:fixed
        if (popover.parentElement !== document.body) {
            document.body.appendChild(popover);
        }
        popover.style.position = 'fixed'; // inline override as belt-and-suspenders
        popover.style.display = 'block';
        // Position at text cursor position, fallback to anchor element
        // position: fixed uses viewport coords — no scrollX/Y offset
        const pw = 260;
        let positioned = false;
        let refTop = 0; // top of whatever we anchored to — used to flip above it

        // Caret coords straight from ProseMirror (coordsAtPos) when the caller has
        // them. A collapsed caret can produce an EMPTY DOM range rect, which used
        // to fall through to the anchor — and when the anchor is the whole editor,
        // the popover landed at the bottom of the page, far from where the user was
        // typing.
        if (coords && (coords.left || coords.bottom)) {
            const left = Math.min(Math.max(coords.left, 8), window.innerWidth - pw - 12);
            popover.style.left = left + 'px';
            popover.style.top = (coords.bottom + 8) + 'px';
            refTop = coords.top ?? coords.bottom;
            positioned = true;
        }

        const sel = !positioned ? window.getSelection() : null;
        if (sel && sel.rangeCount) {
            const range = sel.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            if (rect.width > 0 || rect.height > 0) {
                let left = rect.left;
                let top = rect.bottom + 8;
                left = Math.min(left, window.innerWidth - pw - 12);
                left = Math.max(left, 8);
                popover.style.left = left + 'px';
                popover.style.top = top + 'px';
                refTop = rect.top;
                positioned = true;
            }
        }
        if (!positioned) {
            const anchor = anchorEl || document.getElementById('project-doc-editor');
            if (anchor) {
                const rect = anchor.getBoundingClientRect();
                let left = rect.left;
                let top = rect.bottom + 8;
                left = Math.min(left, window.innerWidth - pw - 12);
                left = Math.max(left, 8);
                popover.style.left = left + 'px';
                popover.style.top = top + 'px';
                refTop = rect.top;
            }
        }

        clampDocsPopoverToViewport(popover, refTop);
    }

    window.setTimeout(() => { input?.focus(); input?.select(); }, 0);

    // Reposition on docs surface scroll so popover follows the anchor chip
    if (docsStatusScrollHandler) {
        document.removeEventListener("scroll", docsStatusScrollHandler, true);
    }
    if (anchorEl) {
        const pw = 260;
        docsStatusScrollHandler = () => {
            const p = document.getElementById('docs-status-popover');
            if (!p || p.style.display === 'none') {
                document.removeEventListener("scroll", docsStatusScrollHandler, true);
                docsStatusScrollHandler = null;
                return;
            }
            const r = anchorEl.getBoundingClientRect();
            // Close if anchor has scrolled above the page header
            const header = anchorEl.closest('.page') ?.querySelector('.page-header');
            const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
            if (r.top < headerBottom) {
                closeDocsStatusPopover(null);
                return;
            }
            let left = Math.min(Math.max(r.left, 8), window.innerWidth - pw - 12);
            p.style.left = left + 'px';
            p.style.top  = (r.bottom + 8) + 'px';
        };
        document.addEventListener("scroll", docsStatusScrollHandler, { capture: true, passive: true });
    }

    return new Promise((resolve) => {
        docsStatusChipResolver = resolve;
    });
}

function closeDocsStatusPopover(value = null) {
    const popover = document.getElementById('docs-status-popover');
    if (popover) popover.style.display = 'none';
    if (docsStatusScrollHandler) {
        document.removeEventListener("scroll", docsStatusScrollHandler, true);
        docsStatusScrollHandler = null;
    }
    // Restore table menu visibility
    const tableMenu = document.getElementById('docs-table-menu');
    if (tableMenu) delete tableMenu.dataset.statusPopoverOpen;
    if (docsStatusChipResolver) {
        const resolve = docsStatusChipResolver;
        docsStatusChipResolver = null;
        resolve(value);
    }
}

function confirmDocsStatusPopover() {
    const input = document.getElementById('docs-status-label');
    const toneInput = document.getElementById('docs-status-tone');
    const label = String(input?.value || '').trim().toUpperCase() || t('docs.statusDefault');
    const tone = DOCS_STATUS_TONES.includes(toneInput?.value) ? toneInput.value : 'grey';
    closeDocsStatusPopover({ label, tone });
}

async function openDocsStatusChipPicker(projectId, anchorEl = null, coords = null) {
    const current = getDocsEditorStatusChipState();
    const result = await openDocsStatusPopover({
        label: current.active ? current.label : t('docs.statusDefault'),
        tone: current.active ? current.tone : 'grey'
    }, anchorEl, coords);
    if (!result) return;
    runDocsEditorCommand(current.active ? 'updateStatusChip' : 'statusChip', result);
    focusDocsEditor();
    updateDocsDirtyState(projectId);
}

function bindDocsStatusChipEditing(projectId, editorSurface) {
    if (!editorSurface || editorSurface.dataset.docsStatusBound === 'true') return;
    editorSurface.dataset.docsStatusBound = 'true';
    editorSurface.addEventListener('click', async (event) => {
        const chip = event.target.closest('[data-doc-status-chip]');
        if (!chip) return;
        const docsCanvas = chip.closest('.docs-canvas');
        if (docsCanvas?.dataset.mode !== 'edit') return;
        event.preventDefault();
        event.stopPropagation();
        await openDocsStatusChipPicker(projectId, chip);
    });
}

// ── Date chip picker ───────────────────────────────────────────────────────

let docsDateChipResolver = null;
let docsDateFlatpickr = null;
let docsDateKeydownHandler = null;
let docsDateScrollHandler = null;

function getLocalTodayIso() {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

async function openDocsDateChipPicker(projectId, anchorEl = null, coords = null) {
    const state = getDocsEditorDateChipState();
    const today = getLocalTodayIso();
    const initial = state.active ? state.date : today;
    const result = await openDocsDatePopover(initial, anchorEl, coords);
    if (!result) { focusDocsEditor(); return; }
    runDocsEditorCommand(state.active ? "updateDateChip" : "dateChip", result);
    focusDocsEditor();
    updateDocsDirtyState(projectId);
}

async function openTaskDateChipPicker(anchorEl = null, coords = null) {
    const state = getTaskDescriptionDateChipState();
    const today = getLocalTodayIso();
    const initial = state.active ? state.date : today;
    const result = await openDocsDatePopover(initial, anchorEl, coords);
    if (!result) {
        focusTaskDescriptionEditor();
        return;
    }
    if (state.active) {
        updateTaskDescriptionDateChip(result);
    } else {
        insertTaskDescriptionDateChip(result);
    }
    focusTaskDescriptionEditor();
}

function openDocsDatePopover(initial = "", anchorEl = null, coords = null) {
    const popover = document.getElementById("docs-date-popover");
    const calContainer = document.getElementById("docs-date-calendar");
    if (!popover || !calContainer) return Promise.resolve(null);

    if (docsDateChipResolver) docsDateChipResolver(null);

    // Destroy previous flatpickr instance
    if (docsDateFlatpickr) { docsDateFlatpickr.destroy(); docsDateFlatpickr = null; }

    // Move to body to escape any CSS transform/filter ancestor that breaks position:fixed
    if (popover.parentElement !== document.body) {
        document.body.appendChild(popover);
    }
    popover.style.position = "fixed"; // inline override as belt-and-suspenders

    // Position popover — priority: explicit coords > anchor element > editor fallback
    popover.style.display = "block";
    const pw = 308;
    let left = 0, top = 0;
    let refTop = 0; // top of the caret/anchor — used to flip above it when needed
    // position: fixed uses viewport coords — no scrollX/Y offset
    if (coords && coords.x != null) {
        left = coords.x;
        top  = coords.y + 8;
        refTop = coords.y;
    } else if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        left = rect.left;
        top  = rect.bottom + 6;
        refTop = rect.top;
    } else {
        const editor = document.getElementById("project-doc-editor");
        if (editor) {
            const rect = editor.getBoundingClientRect();
            left = rect.left + 16;
            top  = rect.top + 60;
            refTop = rect.top + 60;
        }
    }
    left = Math.min(left, window.innerWidth - pw - 12);
    left = Math.max(left, 8);
    popover.style.left = left + "px";
    popover.style.top  = top + "px";

    // Mount flatpickr inline calendar
    const input = document.createElement("input");
    input.type = "text";
    input.style.cssText = "position:absolute;opacity:0;pointer-events:none;width:0;height:0;";
    calContainer.innerHTML = "";
    calContainer.appendChild(input);

    docsDateFlatpickr = flatpickr(input, {
        inline: true,
        dateFormat: "Y-m-d",
        defaultDate: initial || new Date(),
        appendTo: calContainer,
        onChange(selectedDates, dateStr) {
            closeDocsDatePopover(dateStr || null);
        }
    });

    // Clamp AFTER the calendar mounts — the popover is only ~40px tall until
    // flatpickr fills it, so measuring earlier would think it always fits.
    clampDocsPopoverToViewport(popover, refTop);

    // Close on outside click
    if (!popover._dateOutsideBound) {
        popover._dateOutsideBound = true;
        document.addEventListener("mousedown", (e) => {
            const p = document.getElementById("docs-date-popover");
            if (p && p.style.display !== "none" && !p.contains(e.target)) {
                closeDocsDatePopover(null);
            }
        }, true);
    }

    // Enter/Escape key handler — capture phase so ProseMirror doesn't intercept Enter
    if (docsDateKeydownHandler) {
        document.removeEventListener("keydown", docsDateKeydownHandler, true);
    }
    docsDateKeydownHandler = (e) => {
        const p = document.getElementById("docs-date-popover");
        if (!p || p.style.display === "none") {
            document.removeEventListener("keydown", docsDateKeydownHandler, true);
            docsDateKeydownHandler = null;
            return;
        }
        if (e.key === "Enter" || e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            if (e.key === "Enter") {
                const d = docsDateFlatpickr?.selectedDates?.[0];
                if (d) {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, "0");
                    const day = String(d.getDate()).padStart(2, "0");
                    closeDocsDatePopover(`${y}-${m}-${day}`);
                } else {
                    closeDocsDatePopover(null);
                }
            } else {
                closeDocsDatePopover(null);
            }
            document.removeEventListener("keydown", docsDateKeydownHandler, true);
            docsDateKeydownHandler = null;
        }
    };
    document.addEventListener("keydown", docsDateKeydownHandler, true);

    // Reposition on docs surface scroll so popover follows the anchor element.
    // For slash-command opens (coords only, no anchorEl) the popover stays fixed.
    if (docsDateScrollHandler) {
        document.removeEventListener("scroll", docsDateScrollHandler, true);
        docsDateScrollHandler = null;
    }
    if (anchorEl) {
        const pw2 = 308;
        docsDateScrollHandler = () => {
            const p = document.getElementById("docs-date-popover");
            if (!p || p.style.display === "none") {
                document.removeEventListener("scroll", docsDateScrollHandler, true);
                docsDateScrollHandler = null;
                return;
            }
            const r = anchorEl.getBoundingClientRect();
            // Close if anchor has scrolled above the page header
            const header = anchorEl.closest('.page')?.querySelector('.page-header');
            const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
            if (r.top < headerBottom) {
                closeDocsDatePopover(null);
                return;
            }
            let left = Math.min(Math.max(r.left, 8), window.innerWidth - pw2 - 12);
            p.style.left = left + "px";
            p.style.top  = (r.bottom + 6) + "px";
        };
        document.addEventListener("scroll", docsDateScrollHandler, { capture: true, passive: true });
    }

    return new Promise((resolve) => { docsDateChipResolver = resolve; });
}

function closeDocsDatePopover(value = null) {
    const popover = document.getElementById("docs-date-popover");
    if (popover) popover.style.display = "none";
    if (docsDateFlatpickr) { docsDateFlatpickr.destroy(); docsDateFlatpickr = null; }
    if (docsDateKeydownHandler) {
        document.removeEventListener("keydown", docsDateKeydownHandler, true);
        docsDateKeydownHandler = null;
    }
    if (docsDateScrollHandler) {
        document.removeEventListener("scroll", docsDateScrollHandler, true);
        docsDateScrollHandler = null;
    }
    const calContainer = document.getElementById("docs-date-calendar");
    if (calContainer) calContainer.innerHTML = "";
    if (docsDateChipResolver) {
        const resolve = docsDateChipResolver;
        docsDateChipResolver = null;
        resolve(value);
    }
}

function bindDocsDateChipEditing(projectId, editorSurface) {
    if (!editorSurface || editorSurface.dataset.docsDateBound === "true") return;
    editorSurface.dataset.docsDateBound = "true";
    editorSurface.addEventListener("click", async (event) => {
        const chip = event.target.closest("[data-doc-date-chip]");
        if (!chip) return;
        const docsCanvas = chip.closest(".docs-canvas");
        if (docsCanvas?.dataset.mode !== "edit") return;
        event.preventDefault();
        event.stopPropagation();
        await openDocsDateChipPicker(projectId, chip);
    });
}

// Single global listener for the /date slash-command trigger
if (typeof document !== "undefined" && !document._docsDateTriggerBound) {
    document._docsDateTriggerBound = true;
    document.addEventListener("docs-date-chip-trigger", async (e) => {
        if (!docsWorkspaceActive) return;
        await openDocsDateChipPicker(null, null, e.detail || null);
    });
}

if (typeof document !== "undefined" && !document._taskDateTriggerBound) {
    document._taskDateTriggerBound = true;
    document.addEventListener("task-date-chip-trigger", async (e) => {
        await openTaskDateChipPicker(null, e.detail || null);
    });
}

// --- Callout type picker ---

function getDocsCalloutIconToggleSvg(showIcon) {
    if (showIcon) {
        return `
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
                <path d="M2 12c1.8-3.7 5.3-6 10-6s8.2 2.3 10 6c-1.8 3.7-5.3 6-10 6S3.8 15.7 2 12Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
                <circle cx="12" cy="12" r="2.6" stroke="currentColor" stroke-width="1.8"></circle>
            </svg>
        `;
    }
    return `
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
            <path d="M2 12c1.8-3.7 5.3-6 10-6s8.2 2.3 10 6c-1.8 3.7-5.3 6-10 6S3.8 15.7 2 12Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
            <circle cx="12" cy="12" r="2.6" stroke="currentColor" stroke-width="1.8"></circle>
            <path d="M4 4l16 16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
        </svg>
    `;
}

function setDocsCalloutIconToggleVisual(button, showIcon, disabled = false) {
    if (!button) return;
    const effectiveShowIcon = disabled ? false : showIcon;
    button.innerHTML = getDocsCalloutIconToggleSvg(effectiveShowIcon);
    button.classList.toggle('active', effectiveShowIcon);
    button.classList.toggle('disabled', disabled);
    button.disabled = disabled;
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    button.setAttribute('aria-pressed', effectiveShowIcon ? 'true' : 'false');
    button.title = disabled
        ? 'Neutral callouts do not show icons'
        : (effectiveShowIcon ? 'Hide callout icon' : 'Show callout icon');
}

function syncDocsCalloutIconToggle(state) {
    const iconToggle = document.getElementById('docs-callout-icon-toggle');
    if (!iconToggle) return;
    const isNeutral = state?.tone === 'neutral';
    const showIcon = state?.icon !== false;
    setDocsCalloutIconToggleVisual(iconToggle, showIcon, isNeutral);
    iconToggle.style.display = '';
    const sep = iconToggle.previousElementSibling;
    if (sep) sep.style.display = '';
}

function updateCalloutTypePicker(projectId) {
    const picker = document.getElementById('docs-callout-type-picker');
    if (!picker) return;
    const docsCanvas = document.getElementById('docs-canvas-root');
    if (docsCanvas?.dataset.mode !== 'edit') {
        picker.style.display = 'none';
        return;
    }
    const state = getDocsCalloutState();
    if (!state.active) {
        picker.style.display = 'none';
        return;
    }
    // Highlight the active tone button
    picker.querySelectorAll('[data-callout-tone]').forEach((btn) => {
        btn.classList.toggle('active', btn.dataset.calloutTone === state.tone);
        btn.setAttribute('aria-pressed', btn.dataset.calloutTone === state.tone ? 'true' : 'false');
    });
    // Update icon toggle state
    syncDocsCalloutIconToggle(state);
    // Position at bottom-center of the callout block
    const sel = window.getSelection();
    let calloutEl = null;
    if (sel && sel.rangeCount) {
        const node = sel.getRangeAt(0).startContainer;
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        calloutEl = el?.closest('[data-doc-callout]');
    }
    if (calloutEl) {
        const rect = calloutEl.getBoundingClientRect();
        picker.style.display = 'flex';
        const pw = picker.offsetWidth || 180;
        let left = rect.left + window.scrollX + (rect.width - pw) / 2;
        left = Math.min(left, window.innerWidth + window.scrollX - pw - 12);
        left = Math.max(left, 8);
        picker.style.left = left + 'px';
        picker.style.top = (rect.bottom + window.scrollY + 6) + 'px';
    } else {
        picker.style.display = 'flex';
    }
}

function positionDocsHeadingDropdown(toggle, dropdown) {
    if (!toggle || !dropdown) return;
    const rect = toggle.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const dropdownWidth = dropdown.offsetWidth || 140;
    const dropdownHeight = dropdown.offsetHeight || 168;
    const maxLeft = Math.max(12, viewportWidth - dropdownWidth - 12);
    const left = Math.max(12, Math.min(rect.left, maxLeft));
    dropdown.style.left = `${left}px`;
    // Open upward when the button is in the bottom half of the viewport
    if (rect.top > viewportHeight / 2) {
        dropdown.style.top = `${rect.top - dropdownHeight - 4}px`;
    } else {
        dropdown.style.top = `${rect.bottom + 4}px`;
    }
}

function bindDocsCalloutTypePicker(projectId) {
    const picker = document.getElementById('docs-callout-type-picker');
    if (!picker || picker.dataset.calloutBound === 'true') return;
    picker.dataset.calloutBound = 'true';

    picker.addEventListener('mousedown', (e) => e.preventDefault()); // keep editor focus

    picker.querySelectorAll('[data-callout-tone]').forEach((btn) => {
        btn.addEventListener('mousedown', (e) => {
            e.preventDefault();
            runDocsEditorCommand('updateCallout', btn.dataset.calloutTone);
            focusDocsEditor();
            updateDocsDirtyState(projectId);
            // Update button active state immediately
            picker.querySelectorAll('[data-callout-tone]').forEach((b) => {
                b.classList.toggle('active', b === btn);
                b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
            });
            syncDocsCalloutIconToggle(getDocsCalloutState());
        });
    });

    const iconToggleBtn = document.getElementById('docs-callout-icon-toggle');
    if (iconToggleBtn && !iconToggleBtn.dataset.iconBound) {
        iconToggleBtn.dataset.iconBound = 'true';
        iconToggleBtn.addEventListener('mousedown', (e) => {
            e.preventDefault();
            if (iconToggleBtn.disabled) return;
            runDocsEditorCommand('toggleCalloutIcon');
            focusDocsEditor();
            updateDocsDirtyState(projectId);
            // Immediately flip the button visually
            const wasActive = iconToggleBtn.classList.contains('active');
            setDocsCalloutIconToggleVisual(iconToggleBtn, !wasActive);
        });
    }

    document.addEventListener('mousedown', (e) => {
        if (picker.style.display !== 'none' && !picker.contains(e.target)) {
            const callout = e.target.closest('[data-doc-callout]');
            if (!callout) picker.style.display = 'none';
        }
    }, true);
}

function closeDeleteTemplateModal() {
    const modal = document.getElementById('delete-template-modal');
    if (modal) modal.style.display = 'none';
    templateToDelete = null;
}

function confirmDeleteTemplate() {
    if (!templateToDelete) return;
    deleteTemplateById(templateToDelete);
    closeDeleteTemplateModal();
}

let templateToRename = null;

function openRenameTemplateModal(templateId) {
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    templateToRename = templateId;
    const modal = document.getElementById('rename-template-modal');
    if (!modal) return;
    const nameInput = modal.querySelector('#rename-template-input');
    if (nameInput) {
        nameInput.value = tpl.name;
    }
    modal.style.display = 'flex';
    if (nameInput) { nameInput.focus(); nameInput.select(); }
}

function closeRenameTemplateModal() {
    const modal = document.getElementById('rename-template-modal');
    if (modal) modal.style.display = 'none';
    templateToRename = null;
}

function confirmRenameTemplate() {
    if (!templateToRename) return;
    const modal = document.getElementById('rename-template-modal');
    const nameInput = modal ? modal.querySelector('#rename-template-input') : null;
    const name = nameInput ? nameInput.value.trim() : '';
    if (!name) { if (nameInput) nameInput.focus(); return; }
    templates = templates.map(tpl =>
        tpl.id === templateToRename ? { ...tpl, name } : tpl
    );
    closeRenameTemplateModal();
    renderTemplateDropdown();
    saveTemplatesStorage().catch(() => {});
    showSuccessNotification(t('projects.template.renamedSuccess'));
}

function renderTemplateDropdown() {
    // Show template toggle button in header (not the section itself — user toggles that)
    const toggleBtn = document.getElementById('template-toggle-btn');
    if (toggleBtn) {
        toggleBtn.style.display = templates.length > 0 ? 'flex' : 'none';
    }
    // Keep section hidden — only shown when user clicks the button
    const templateSection = document.getElementById('project-template-section');
    if (templateSection && templateSection.style.display !== 'none') {
        // Already open — keep it open, just re-populate
    }

    const panel = document.getElementById('template-dropdown-panel');
    if (!panel) return;

    const hiddenInput = document.getElementById('project-template-select');
    const currentId = hiddenInput ? hiddenInput.value : '';

    panel.innerHTML = '';

    // "None" row
    const noneRow = document.createElement('div');
    noneRow.className = 'template-dropdown-row' + (!currentId ? ' selected' : '');
    noneRow.innerHTML = `<span class="template-row-name">${t('projects.modal.templateNone')}</span>`;
    noneRow.addEventListener('click', () => setSelectedTemplate('', t('projects.modal.templateNone')));
    panel.appendChild(noneRow);

    // Template rows
    templates.forEach(tpl => {
        const row = document.createElement('div');
        row.className = 'template-dropdown-row' + (currentId === tpl.id ? ' selected' : '');
        const taskCount = tpl.tasks.length;
        row.innerHTML = `
            <span class="template-row-name">${escapeHtml(tpl.name)}</span>
            <span class="template-row-count">${t(taskCount === 1 ? 'projects.template.taskCountOne' : 'projects.template.taskCountMany', { count: taskCount })}</span>
            <div class="template-row-actions">
                <button type="button" class="template-row-btn template-row-rename" title="${t('projects.template.renameButton')}">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button type="button" class="template-row-btn template-row-delete" title="${t('common.delete')}">✕</button>
            </div>
        `;
        // Clicking name/count selects the template
        row.querySelector('.template-row-name').addEventListener('click', () => setSelectedTemplate(tpl.id, tpl.name));
        row.querySelector('.template-row-count').addEventListener('click', () => setSelectedTemplate(tpl.id, tpl.name));
        // Rename
        row.querySelector('.template-row-rename').addEventListener('click', (e) => {
            e.stopPropagation();
            const dropdown = document.getElementById('template-dropdown');
            if (dropdown) dropdown.classList.remove('active');
            openRenameTemplateModal(tpl.id);
        });
        // Delete
        row.querySelector('.template-row-delete').addEventListener('click', (e) => {
            e.stopPropagation();
            const dropdown = document.getElementById('template-dropdown');
            if (dropdown) dropdown.classList.remove('active');
            openDeleteTemplateModal(tpl.id);
        });
        panel.appendChild(row);
    });
}

function setSelectedTemplate(id, name) {
    const hiddenInput = document.getElementById('project-template-select');
    if (hiddenInput) hiddenInput.value = id;
    const label = document.getElementById('template-dropdown-label');
    if (label) label.textContent = name;
    const dropdown = document.getElementById('template-dropdown');
    if (dropdown) dropdown.classList.remove('active');
    renderTemplateDropdown();

    // Show/hide task naming section based on template selection
    const namingSection = document.getElementById('project-template-naming');
    if (namingSection) {
        const hasTemplate = !!id;
        namingSection.style.display = hasTemplate ? 'block' : 'none';

        if (hasTemplate) {
            // Reset to "Original" each time a template is chosen
            const modeInput = document.getElementById('project-task-naming-mode');
            const prefixInput = document.getElementById('project-task-prefix-input');
            const suffixInput = document.getElementById('project-task-suffix-input');
            if (modeInput) modeInput.value = 'none';
            if (prefixInput) { prefixInput.value = ''; prefixInput.style.display = 'none'; }
            if (suffixInput) { suffixInput.value = ''; suffixInput.style.display = 'none'; }
            namingSection.querySelectorAll('.naming-seg-btn').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.value === 'none');
            });

            if (!namingSection._listenersAttached) {
                namingSection._listenersAttached = true;
                namingSection.querySelectorAll('.naming-seg-btn').forEach(btn => {
                    btn.addEventListener('click', () => {
                        const val = btn.dataset.value;
                        if (modeInput) modeInput.value = val;
                        namingSection.querySelectorAll('.naming-seg-btn').forEach(b => b.classList.toggle('active', b === btn));
                        if (prefixInput) prefixInput.style.display = val === 'prefix' ? 'block' : 'none';
                        if (suffixInput) suffixInput.style.display = val === 'suffix' ? 'block' : 'none';
                        if (val === 'prefix' && prefixInput) setTimeout(() => prefixInput.focus(), 50);
                        else if (val === 'suffix' && suffixInput) setTimeout(() => suffixInput.focus(), 50);
                    });
                });
            }
        }
    }
}

function setupTemplateDropdown() {
    const trigger = document.getElementById('template-dropdown-trigger');
    if (!trigger || trigger._templateSetup) return;
    trigger._templateSetup = true;

    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        renderTemplateDropdown(); // refresh before opening
        const dropdown = document.getElementById('template-dropdown');
        if (dropdown) dropdown.classList.toggle('active');
    });

    document.addEventListener('click', (e) => {
        if (!e.target.closest('#template-dropdown')) {
            const dropdown = document.getElementById('template-dropdown');
            if (dropdown) dropdown.classList.remove('active');
        }
    });
}

// ─── Project Duplication ──────────────────────────────────────────────────────

// Project duplication
let projectToDuplicate = null;

// Tasks that belong in a duplicate: archived tasks and hidden automation
// source tasks stay with the original project.
function getDuplicableProjectTasks(projectId) {
    return getLiveProjectTasks(projectId);
}

// Earliest date of the project (its start date, else its earliest task date).
// Duplicating with a different start date shifts every date by the same delta.
function getProjectDuplicateAnchorDate(project, projectTasks) {
    if (project.startDate) return project.startDate;
    const taskDates = projectTasks
        .flatMap(t => [t.startDate, t.endDate])
        .filter(d => typeof d === 'string' && looksLikeISO(d))
        .sort();
    return taskDates[0] || '';
}

function isoDateToUtcMs(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
}

function shiftIsoDate(iso, days) {
    if (!days || typeof iso !== 'string' || !looksLikeISO(iso)) return iso || '';
    return new Date(isoDateToUtcMs(iso) + days * 86400000).toISOString().slice(0, 10);
}

function getDuplicateStartDateElements() {
    const input = document.getElementById('duplicate-project-start-date');
    const displayInput = input?.closest?.('.date-input-wrapper')?.querySelector?.('.date-display') || null;
    return { input, displayInput, flatpickrInstance: displayInput?._flatpickr || null };
}

function writeDuplicateStartDate(value) {
    const { input, displayInput, flatpickrInstance } = getDuplicateStartDateElements();
    if (input) input.value = value;
    if (!displayInput) return;
    if (flatpickrInstance) {
        if (value) flatpickrInstance.setDate(new Date(`${value}T00:00:00`), false);
        else flatpickrInstance.clear();
    } else {
        displayInput.value = value ? toDMYFromISO(value) : '';
    }
}

function readDuplicateStartDate() {
    const { input, displayInput } = getDuplicateStartDateElements();
    const hiddenValue = (input?.value || '').trim();
    if (hiddenValue) return hiddenValue;
    const displayValue = (displayInput?.value || '').trim();
    return looksLikeDMY(displayValue) ? toISOFromDMY(displayValue) : '';
}

function handleDuplicateProject(projectId) {
    const modal = document.getElementById('project-duplicate-modal');
    const project = projects.find(p => p.id === projectId);
    if (!modal || !project) return;
    projectToDuplicate = projectId;

    const projectTasks = getDuplicableProjectTasks(projectId);

    const nameInput = document.getElementById('duplicate-project-name');
    if (nameInput) nameInput.value = t('projects.duplicate.defaultName', { name: project.name });

    const includeTasksCheckbox = document.getElementById('duplicate-tasks-checkbox');
    if (includeTasksCheckbox) {
        includeTasksCheckbox.checked = projectTasks.length > 0;
        includeTasksCheckbox.disabled = projectTasks.length === 0;
    }
    const taskCountSpan = document.getElementById('duplicate-task-count');
    if (taskCountSpan) taskCountSpan.textContent = `(${projectTasks.length})`;

    // Start date only makes sense when the project or its tasks have dates
    const anchorDate = getProjectDuplicateAnchorDate(project, projectTasks);
    const startDateGroup = document.getElementById('duplicate-start-date-group');
    if (startDateGroup) startDateGroup.style.display = anchorDate ? '' : 'none';
    writeDuplicateStartDate(anchorDate);

    modal.classList.add('active');
    if (nameInput) {
        nameInput.focus();
        nameInput.select();
    }
}

function closeDuplicateProjectModal() {
    const modal = document.getElementById('project-duplicate-modal');
    modal.classList.remove('active');
    projectToDuplicate = null;
}

let duplicateProjectInProgress = false;

async function confirmDuplicateProject() {
    const projectId = projectToDuplicate;
    if (!projectId || duplicateProjectInProgress) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    const includeTasksCheckbox = document.getElementById('duplicate-tasks-checkbox');
    const includeTasks = !!(includeTasksCheckbox && includeTasksCheckbox.checked);
    const projectTasks = includeTasks ? getDuplicableProjectTasks(projectId) : [];

    // Make sure every new id comes from this tab's server-reserved blocks.
    duplicateProjectInProgress = true;
    try {
        await ensureReservedIds('project', 1);
        if (projectTasks.length > 0) await ensureReservedIds('task', projectTasks.length);
    } finally {
        duplicateProjectInProgress = false;
    }

    const typedName = (document.getElementById('duplicate-project-name')?.value || '').trim();
    const name = typedName || t('projects.duplicate.defaultName', { name: project.name });

    // Shift all dates by the gap between the original and the chosen start date
    const anchorDate = getProjectDuplicateAnchorDate(project, getDuplicableProjectTasks(projectId));
    const newStartDate = readDuplicateStartDate();
    const dayShift = anchorDate && newStartDate && looksLikeISO(newStartDate)
        ? Math.round((isoDateToUtcMs(newStartDate) - isoDateToUtcMs(anchorDate)) / 86400000)
        : 0;

    const now = new Date().toISOString();
    const newProject = {
        id: projectCounter,
        name,
        description: project.description || '',
        startDate: shiftIsoDate(project.startDate, dayShift),
        endDate: shiftIsoDate(project.endDate, dayShift),
        tags: project.tags ? [...project.tags] : [],
        createdAt: now,
        updatedAt: now
    };

    projectCounter++;
    projects.push(newProject);

    const newTasks = [];
    if (includeTasks) {
        // Map original → copy IDs first so links between copied tasks follow the copies
        const idMap = new Map();
        projectTasks.forEach(task => {
            idMap.set(task.id, taskCounter);
            taskCounter++;
        });

        projectTasks.forEach(task => {
            const newTask = {
                ...task,
                id: idMap.get(task.id),
                projectId: newProject.id,
                status: 'todo', // Reset to To Do (initial state) when duplicating
                startDate: shiftIsoDate(task.startDate, dayShift),
                endDate: shiftIsoDate(task.endDate, dayShift),
                automationId: null,
                createdAt: now,
                updatedAt: now,
                // Deep copy arrays
                tags: task.tags ? [...task.tags] : [],
                attachments: task.attachments ? JSON.parse(JSON.stringify(task.attachments)) : [],
                // Keep web links; re-point task links to the copies, drop links to tasks left behind
                links: Array.isArray(task.links)
                    ? task.links
                        .filter(link => link.taskId == null || idMap.has(link.taskId))
                        .map(link => (link.taskId == null ? { ...link } : { ...link, taskId: idMap.get(link.taskId) }))
                    : []
            };
            delete newTask.completedDate;
            delete newTask.archivedAt;
            tasks.push(newTask);
            newTasks.push(newTask);
        });
    }

    // Record history
    if (window.historyService) {
        window.historyService.recordProjectCreated(newProject);
    }

    // Close modal
    closeDuplicateProjectModal();

    // Clear cache and update UI
    appState.projectsSortedView = null;

    // Show success notification
    showSuccessNotification(
        includeTasks
            ? t('projects.duplicate.successWithTasks', { name: newProject.name })
            : t('projects.duplicate.success', { name: newProject.name })
    );

    // Save in background — insert only the new rows. saveProjects()/saveTasks() rewrite
    // the whole list (delete-all + reinsert) and would erase work saved on other devices
    // since this tab loaded. The create wrappers show their own error notification.
    Promise.all([
        saveProjectCreate(newProject),
        ...newTasks.map(task => saveTaskCreate(task))
    ]).catch(err => {
        console.error('Failed to save duplicated project:', err);
    });

    // Navigate to the new project
    showProjectDetails(newProject.id, 'projects');
}

async function backToProjects() {
    if (docsWorkspaceActive) {
        cancelPendingDocsCheckboxAutoSave();
        cancelPendingDocsContentAutoSave();
        const canContinue = await saveActiveDocIfDirty({ silent: true });
        if (!canContinue) {
            restoreCurrentDocsRoute();
            return;
        }
        teardownDocsWorkspace();
    }
    // Hide project details
    document.getElementById("project-details").classList.remove("active");

    // Show user menu again when leaving project details
    const userMenu = document.querySelector(".user-menu");
    if (userMenu) userMenu.style.display = "block";

    // Use the standard page switching mechanism
    // Ensure the URL reflects the projects route so users can bookmark/share
    try { window.location.hash = "#projects"; } catch (e) { /* ignore */ }
    await showPage("projects");

    // Update sidebar navigation
    document
        .querySelectorAll(".nav-item")
        .forEach((nav) => nav.classList.remove("active"));
    document
        .querySelector('.nav-item[data-page="projects"]')
        .classList.add("active");

    // Refresh projects list to show any changes made in project details
    renderProjects();
}

async function backToArchivedProjects() {
    if (docsWorkspaceActive) {
        cancelPendingDocsCheckboxAutoSave();
        cancelPendingDocsContentAutoSave();
        const canContinue = await saveActiveDocIfDirty({ silent: true });
        if (!canContinue) {
            restoreCurrentDocsRoute();
            return;
        }
        teardownDocsWorkspace();
    }
    // Hide project details
    document.getElementById("project-details").classList.remove("active");

    // Show user menu again when leaving project details
    const userMenu = document.querySelector(".user-menu");
    if (userMenu) userMenu.style.display = "block";

    // Return to the dedicated Archived Projects route/view.
    try { window.location.hash = "#projects/archive"; } catch (e) { /* ignore */ }
    await showPage("projects/archive");

    // Update sidebar navigation
    document
        .querySelectorAll(".nav-item")
        .forEach((nav) => nav.classList.remove("active"));
    document
        .querySelector('.nav-item[data-page="projects"]')
        ?.classList.add("active");

    renderProjects();
}

async function backToCalendar() {
    if (docsWorkspaceActive) {
        cancelPendingDocsCheckboxAutoSave();
        cancelPendingDocsContentAutoSave();
        const canContinue = await saveActiveDocIfDirty({ silent: true });
        if (!canContinue) {
            restoreCurrentDocsRoute();
            return;
        }
        teardownDocsWorkspace();
    }
    // Hide project details
    document.getElementById("project-details").classList.remove("active");

    // Show user menu again when leaving project details
    const userMenu = document.querySelector(".user-menu");
    if (userMenu) userMenu.style.display = "block";

    // Restore the calendar month/year the user came from (if known)
    if (calendarNavigationState && Number.isInteger(calendarNavigationState.month) && Number.isInteger(calendarNavigationState.year)) {
        currentMonth = calendarNavigationState.month;
        currentYear = calendarNavigationState.year;
        try { saveCalendarState(); } catch (e) {}
    }

    // Return to calendar and force a full render (month/year may have changed while in details)
    showCalendarView();
    renderCalendar();
}

function openTaskModalForProject(projectId) {
    openTaskModal();
    // Pre-select the project in the custom dropdown (only for this open)
    const modal = document.getElementById('task-modal');
    const hiddenProject = modal.querySelector('#hidden-project');
    const projectTextSpan = modal.querySelector('#project-current .project-text');
    const proj = projects.find(p => String(p.id) === String(projectId));
    if (hiddenProject) hiddenProject.value = String(projectId || '');
    if (projectTextSpan && proj) {
        projectTextSpan.innerHTML = buildProjectCurrentHTML(proj.id, proj.name);
    }
    // Close any portal that might be lingering
    if (typeof hideProjectDropdownPortal === 'function') hideProjectDropdownPortal();

    // Remember project context so "Create Another" reuses it
    window.taskCreateAnotherProjectId = String(projectId || '');

    // Recapture initial state after setting project
    setTimeout(() => captureInitialTaskFormState(), 150);
}

// Kanban column "+" / "Add task": open the create modal pre-set to that column's status
function openTaskModalForStatus(status) {
    const valid = ['backlog', 'todo', 'progress', 'review', 'done'];
    openTaskModal();
    if (!valid.includes(status)) return;
    const modal = document.getElementById('task-modal');
    const hiddenStatus = modal && modal.querySelector('#hidden-status');
    if (hiddenStatus) hiddenStatus.value = status;
    const statusBadge = modal && modal.querySelector('#status-current .status-badge');
    if (statusBadge) {
        statusBadge.className = 'status-badge ' + status;
        statusBadge.textContent = getStatusLabel(status);
    }
    setTimeout(() => captureInitialTaskFormState(), 150);
}

const SETTINGS_TABS =['account', 'preferences', 'notifications', 'data', 'advanced'];

// Which tab each dirty-tracked settings key lives on (drives the unsaved-changes dots)
const SETTINGS_KEY_TABS = {
    userName: 'account',
    notificationEmail: 'account',
    avatarState: 'account',
    language: 'preferences',
    logoState: 'preferences',
    autoSetStartDateOnStatusChange: 'preferences',
    autoSetEndDateOnStatusChange: 'preferences',
    enableReviewStatus: 'preferences',
    calendarIncludeBacklog: 'preferences',
    historySortOrder: 'preferences',
    emailNotificationsEnabled: 'notifications',
    emailNotificationsWeekdaysOnly: 'notifications',
    emailNotificationsIncludeStartDates: 'notifications',
    emailNotificationsIncludeBacklog: 'notifications',
    emailNotificationsIncludeWeekAhead: 'notifications',
    emailNotificationTime: 'notifications',
    emailNotificationTimeZone: 'notifications',
    debugLogsEnabled: 'advanced',
    diagnosticsOverlayEnabled: 'advanced'
};

function updateSettingsChangeIndicators(changedKeys = []) {
    const modal = document.getElementById('settings-modal');
    if (!modal) return;
    const dirtyTabs = new Set(changedKeys.map((key) => SETTINGS_KEY_TABS[key]).filter(Boolean));
    modal.querySelectorAll('.settings-nav-item').forEach((tab) => {
        tab.classList.toggle('has-changes', dirtyTabs.has(tab.dataset.param));
    });
    const status = modal.querySelector('#settings-save-status');
    if (status) status.hidden = changedKeys.length === 0;
}

// Mobile: the settings modal is a list of sections that drills into one panel
function setSettingsMobileDetail(isDetail) {
    const layout = document.querySelector('#settings-modal .settings-layout');
    if (layout) layout.classList.toggle('is-detail', !!isDetail);
}

function settingsMobileBack() {
    setSettingsMobileDetail(false);
    const modal = document.getElementById('settings-modal');
    const sidebar = modal?.querySelector('.settings-sidebar');
    if (sidebar) sidebar.scrollTop = 0;
    modal?.querySelector('.settings-nav-item.active')?.focus({ preventScroll: true });
}

// App-wide dropdowns (settings, recurrence, auto-archive, generated-tasks sort): a
// trigger button opens the same portal menu as the time zone picker; the hidden
// <select> keeps the value, so existing load/save/change logic is untouched.
function syncSettingsSelectTrigger(triggerBtn) {
    const select = document.getElementById(triggerBtn?.dataset.selectFor || '');
    const valueEl = triggerBtn?.querySelector('.settings-select-trigger-value');
    if (!select || !valueEl) return;
    valueEl.textContent = select.options?.[select.selectedIndex]?.textContent || select.value || '';
}

// The visible control of a styled dropdown is its trigger, not the hidden <select>
function getSelectTrigger(select) {
    if (!select) return null;
    return document.querySelector(`.settings-select-trigger[data-select-for="${select.id}"]`) || select;
}

function syncAllSelectTriggers() {
    document.querySelectorAll('.settings-select-trigger').forEach(syncSettingsSelectTrigger);
}

// Code sets these selects directly (`select.value = …` when a modal opens); re-sync
// the trigger label on every write so callers never need to know about the trigger.
function watchSelectValueWrites(select, triggerBtn) {
    const native = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value');
    if (!native || Object.prototype.hasOwnProperty.call(select, 'value')) return;
    Object.defineProperty(select, 'value', {
        configurable: true,
        get() { return native.get.call(this); },
        set(next) {
            native.set.call(this, next);
            syncSettingsSelectTrigger(triggerBtn);
        }
    });
}

function bindSettingsSelectTriggers(root = document) {
    root.querySelectorAll('.settings-select-trigger').forEach((triggerBtn) => {
        syncSettingsSelectTrigger(triggerBtn);
        if (triggerBtn.dataset.listenerAttached) return;
        triggerBtn.dataset.listenerAttached = 'true';

        const select = document.getElementById(triggerBtn.dataset.selectFor);
        if (!select) return;
        watchSelectValueWrites(select, triggerBtn);
        triggerBtn.addEventListener('click', (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            const isOpen = notificationTimeZonePortalEl
                && notificationTimeZonePortalEl.style.display !== 'none'
                && notificationTimeZonePortalAnchor === triggerBtn;
            if (isOpen) {
                hideNotificationTimeZonePortal();
                return;
            }
            showNotificationTimeZonePortal(triggerBtn, select, triggerBtn.querySelector('.settings-select-trigger-value'));
        });
        select.addEventListener('change', () => syncSettingsSelectTrigger(triggerBtn));
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindSettingsSelectTriggers());
} else {
    bindSettingsSelectTriggers();
}

function refreshSettingsIdentityCard() {
    const modal = document.getElementById('settings-modal');
    if (!modal) return;
    const user = window.authSystem?.getCurrentUser?.();
    const name = user?.name || '';
    const nameEl = modal.querySelector('#settings-nav-identity-name');
    const emailEl = modal.querySelector('#settings-nav-identity-email');
    const avatarEl = modal.querySelector('#settings-nav-identity-avatar');
    if (nameEl) nameEl.textContent = name;
    if (emailEl) emailEl.textContent = user?.email || user?.username || '';
    if (avatarEl) {
        const parts = name.split(' ').filter(Boolean);
        let initials = 'NL';
        if (parts.length === 1) initials = parts[0].slice(0, 2).toUpperCase();
        else if (parts.length > 1) initials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        if (user?.avatarDataUrl) {
            avatarEl.classList.add('has-image');
            avatarEl.style.backgroundImage = `url(${user.avatarDataUrl})`;
            avatarEl.textContent = '';
        } else {
            avatarEl.classList.remove('has-image');
            avatarEl.style.backgroundImage = '';
            avatarEl.textContent = initials;
        }
    }
}

function switchSettingsTab(tabId, options = {}) {
    const modal = document.getElementById('settings-modal');
    if (!modal) return;
    const target = SETTINGS_TABS.includes(tabId) ? tabId : 'account';

    let activeTab = null;
    modal.querySelectorAll('.settings-nav-item').forEach((tab) => {
        const isActive = tab.dataset.param === target;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive) activeTab = tab;
    });
    if (activeTab && options.focus) activeTab.focus();

    modal.querySelectorAll('.settings-panel').forEach((panel) => {
        const isActive = panel.dataset.settingsPanel === target;
        panel.classList.toggle('active', isActive);
        panel.hidden = !isActive;
    });

    const body = modal.querySelector('.settings-modal-body');
    if (body) body.scrollTop = 0;

    // Mobile: show the chosen panel full-width, titled with its tab label
    const mobileTitle = modal.querySelector('#settings-mobile-title');
    if (mobileTitle && activeTab) {
        mobileTitle.textContent = activeTab.querySelector('.settings-nav-label')?.textContent || '';
    }
    setSettingsMobileDetail(true);
}

// Size the settings dialog to its tallest tab: no empty band under the content,
// and no height jump when switching tabs. Flex-basis (not height) so the dialog
// still caps at max-height and the body scrolls on short screens.
function lockSettingsBodyHeight() {
    const modal = document.getElementById('settings-modal');
    const layout = modal?.querySelector('.settings-layout');
    const body = modal?.querySelector('.settings-modal-body');
    const nav = modal?.querySelector('.settings-sidebar');
    if (!layout || !body || !modal.classList.contains('active')) return;

    layout.style.flex = '';
    if (window.matchMedia('(max-width: 768px)').matches) return;

    const panels = [...modal.querySelectorAll('.settings-panel')];
    const wasHidden = panels.map((panel) => panel.hidden);
    // Measure notifications with its sub-options expanded so enabling them never overflows
    const details = modal.querySelector('#email-notification-details');
    const wasCollapsed = details?.classList.contains('is-collapsed');
    if (wasCollapsed) details.classList.remove('is-collapsed');

    let tallest = nav ? nav.scrollHeight : 0;
    panels.forEach((panel, i) => {
        panels.forEach((other, j) => { other.hidden = i !== j; });
        tallest = Math.max(tallest, body.scrollHeight);
    });

    panels.forEach((panel, i) => { panel.hidden = wasHidden[i]; });
    if (wasCollapsed) details.classList.add('is-collapsed');
    layout.style.flex = `0 1 ${tallest}px`;
}

function openSettingsModal(initialTab = 'account') {
      const modal = document.getElementById('settings-modal');
      if (!modal) return;

      const form = modal.querySelector('#settings-form');
      const userNameInput = form.querySelector('#user-name');
      const autoStartToggle = form.querySelector('#auto-start-date-toggle');
      const autoEndToggle = form.querySelector('#auto-end-date-toggle');
      const enableReviewStatusToggle = form.querySelector('#enable-review-status-toggle');
      const calendarIncludeBacklogToggle = form.querySelector('#calendar-include-backlog-toggle');
      const debugLogsToggle = form.querySelector('#debug-logs-toggle');
      const diagOverlayToggle = form.querySelector('#diag-overlay-toggle');
      const historySortOrderSelect = form.querySelector('#history-sort-order');
      const languageSelect = form.querySelector('#language-select');

    // Populate user name from authenticated user (KV-backed)
    const currentUser = window.authSystem?.getCurrentUser();
    userNameInput.value = currentUser?.name || '';

      // Update security section based on auth method
      const userAuthMethod = currentUser?.authMethod || 'pin';
      const authMethodDisplay = document.getElementById('current-auth-method-display');
      const switchAuthText = document.getElementById('switch-auth-method-text');
      const changeCredLabel = document.getElementById('change-credential-label');
      const changeCredHint = document.getElementById('change-credential-hint');
      const changeCredBtnText = document.getElementById('change-credential-btn-text');
      if (userAuthMethod === 'password') {
          if (authMethodDisplay) {
              authMethodDisplay.textContent = t('settings.authMethodHintPassword');
              authMethodDisplay.setAttribute('data-i18n', 'settings.authMethodHintPassword');
          }
          if (switchAuthText) {
              switchAuthText.textContent = t('settings.switchToPin');
              switchAuthText.setAttribute('data-i18n', 'settings.switchToPin');
          }
          if (changeCredLabel) {
              changeCredLabel.textContent = t('settings.passwordManagement');
              changeCredLabel.setAttribute('data-i18n', 'settings.passwordManagement');
          }
          if (changeCredHint) {
              changeCredHint.textContent = t('settings.passwordManagementHint');
              changeCredHint.setAttribute('data-i18n', 'settings.passwordManagementHint');
          }
          if (changeCredBtnText) {
              changeCredBtnText.textContent = t('settings.changePasswordButton');
              changeCredBtnText.setAttribute('data-i18n', 'settings.changePasswordButton');
          }
      } else {
          if (authMethodDisplay) {
              authMethodDisplay.textContent = t('settings.authMethodHintPin');
              authMethodDisplay.setAttribute('data-i18n', 'settings.authMethodHintPin');
          }
          if (switchAuthText) {
              switchAuthText.textContent = t('settings.switchToPassword');
              switchAuthText.setAttribute('data-i18n', 'settings.switchToPassword');
          }
          if (changeCredLabel) {
              changeCredLabel.textContent = t('settings.pinManagement');
              changeCredLabel.setAttribute('data-i18n', 'settings.pinManagement');
          }
          if (changeCredHint) {
              changeCredHint.textContent = t('settings.pinManagementHint');
              changeCredHint.setAttribute('data-i18n', 'settings.pinManagementHint');
          }
          if (changeCredBtnText) {
              changeCredBtnText.textContent = t('settings.resetPinButton');
              changeCredBtnText.setAttribute('data-i18n', 'settings.resetPinButton');
          }
      }

      const emailInput = form.querySelector('#user-email');
      emailInput.value = currentUser?.email || settings.notificationEmail || '';

      const emailEnabledToggle = form.querySelector('#email-notifications-enabled');
      const emailWeekdaysOnlyToggle = form.querySelector('#email-notifications-weekdays-only');
      const emailIncludeStartDatesToggle = form.querySelector('#email-notifications-include-start-dates');
      const emailIncludeBacklogToggle = form.querySelector('#email-notifications-include-backlog');
      const emailIncludeWeekAheadToggle = form.querySelector('#email-notifications-include-week-ahead');
      const emailTimeInput = form.querySelector('#email-notification-time');
      const emailTimeTrigger = form.querySelector('#email-notification-time-trigger');
      const emailTimeValueEl = form.querySelector('#email-notification-time-value');
      const emailTimeZoneSelect = form.querySelector('#email-notification-timezone');
      const emailTimeZoneTrigger = form.querySelector('#email-notification-timezone-trigger');
      const emailTimeZoneValueEl = form.querySelector('#email-notification-timezone-value');
      const emailDetails = form.querySelector('#email-notification-details');

      if (emailEnabledToggle) {
          emailEnabledToggle.checked = settings.emailNotificationsEnabled !== false;
      }
      if (emailWeekdaysOnlyToggle) {
          emailWeekdaysOnlyToggle.checked = !!settings.emailNotificationsWeekdaysOnly;
      }
      if (emailIncludeStartDatesToggle) {
          emailIncludeStartDatesToggle.checked = !!settings.emailNotificationsIncludeStartDates;
      }
      if (emailIncludeBacklogToggle) {
          emailIncludeBacklogToggle.checked = !!settings.emailNotificationsIncludeBacklog;
      }
      if (emailIncludeWeekAheadToggle) {
          emailIncludeWeekAheadToggle.checked = settings.emailNotificationsIncludeWeekAhead !== false;
      }
      if (emailTimeInput) {
          const snapped = snapHHMMToStep(
              normalizeHHMM(settings.emailNotificationTime) || "09:00",
              30
          );
          emailTimeInput.value = clampHHMMToRange(snapped || "09:00", "08:00", "18:00") || "09:00";
          if (emailTimeValueEl) emailTimeValueEl.textContent = emailTimeInput.value;
      }
      if (emailTimeZoneSelect) {
          const timezoneToUse = coerceSupportedUserTimeZone(settings.emailNotificationTimeZone);
          emailTimeZoneSelect.value = String(timezoneToUse);
          if (emailTimeZoneValueEl) {
              emailTimeZoneValueEl.textContent =
                  emailTimeZoneSelect.options?.[emailTimeZoneSelect.selectedIndex]?.textContent ||
                  emailTimeZoneSelect.value ||
                  '';
          }
      }

      const applyEmailNotificationInputState = () => {
          const enabled = !!emailEnabledToggle?.checked;
          if (emailDetails) {
              emailDetails.classList.toggle('is-collapsed', !enabled);
              emailDetails.setAttribute('aria-hidden', enabled ? 'false' : 'true');
          }
          if (emailWeekdaysOnlyToggle) emailWeekdaysOnlyToggle.disabled = !enabled;
          if (emailIncludeStartDatesToggle) emailIncludeStartDatesToggle.disabled = !enabled;
          if (emailIncludeWeekAheadToggle) emailIncludeWeekAheadToggle.disabled = !enabled;
          if (emailTimeInput) emailTimeInput.disabled = !enabled;
          if (emailTimeTrigger) emailTimeTrigger.disabled = !enabled;
          if (emailTimeZoneSelect) emailTimeZoneSelect.disabled = !enabled;
          if (emailTimeZoneTrigger) emailTimeZoneTrigger.disabled = !enabled;
      };
      applyEmailNotificationInputState();
  
      // Populate application settings
      if (autoStartToggle) autoStartToggle.checked = !!settings.autoSetStartDateOnStatusChange;
      if (autoEndToggle) autoEndToggle.checked = !!settings.autoSetEndDateOnStatusChange;
      if (enableReviewStatusToggle) enableReviewStatusToggle.checked = !!settings.enableReviewStatus;
      if (calendarIncludeBacklogToggle) calendarIncludeBacklogToggle.checked = !!settings.calendarIncludeBacklog;
      if (debugLogsToggle) debugLogsToggle.checked = !!settings.debugLogsEnabled;
      // On-screen diagnostics overlay: now a synced setting (like debug logs).
      // Reflects the saved value; applied on Save via the Save/dirty flow below.
      if (diagOverlayToggle) diagOverlayToggle.checked = !!settings.diagnosticsOverlayEnabled;
      historySortOrderSelect.value = settings.historySortOrder;
      if (languageSelect) languageSelect.value = getCurrentLanguage();

      const logoFileInput = form.querySelector('#workspace-logo-input');
      if (logoFileInput) {
          logoFileInput.value = '';
      }

      // Reset any unsaved draft and refresh logo preview/clear button based on persisted settings
      workspaceLogoDraft.hasPendingChange = false;
      workspaceLogoDraft.dataUrl = null;
      avatarDraft.hasPendingChange = false;
      avatarDraft.dataUrl = null;
      const logoPreview = form.querySelector('#workspace-logo-preview');
      const clearButton = form.querySelector('#workspace-logo-clear-btn');
      if (logoPreview && clearButton) {
          if (settings.customWorkspaceLogo) {
              logoPreview.style.display = 'block';
              logoPreview.style.backgroundImage = `url(${settings.customWorkspaceLogo})`;
              clearButton.style.display = 'inline-flex';
          } else {
              logoPreview.style.display = 'none';
              logoPreview.style.backgroundImage = '';
              clearButton.style.display = 'none';
          }
      }

      // Also refresh workspace logo UI to update dropzone text ("Change logo" vs default)
      // Note: setupWorkspaceLogoControls has its own refreshWorkspaceLogoUI function in its scope
      // So we trigger it indirectly by dispatching an event or calling it directly if available
      const refreshLogoEvent = new CustomEvent('refresh-workspace-logo-ui');
      document.dispatchEvent(refreshLogoEvent);

      const avatarFileInput = form.querySelector('#user-avatar-input');
      if (avatarFileInput) {
          avatarFileInput.value = '';
      }
      refreshUserAvatarSettingsUI();

      // Capture initial settings form state for dirty-checking
      window.initialSettingsFormState = {
          userName: userNameInput.value || '',
          notificationEmail: emailInput.value || '',
          emailNotificationsEnabled: !!(emailEnabledToggle?.checked),
          emailNotificationsWeekdaysOnly: !!(emailWeekdaysOnlyToggle?.checked),
          emailNotificationsIncludeStartDates: !!(emailIncludeStartDatesToggle?.checked),
          emailNotificationsIncludeBacklog: !!(emailIncludeBacklogToggle?.checked),
          emailNotificationsIncludeWeekAhead: !!(emailIncludeWeekAheadToggle?.checked),
          emailNotificationTime: emailTimeInput?.value || '',
          emailNotificationTimeZone: emailTimeZoneSelect?.value || '',
          autoSetStartDateOnStatusChange: !!settings.autoSetStartDateOnStatusChange,
          autoSetEndDateOnStatusChange: !!settings.autoSetEndDateOnStatusChange,
          enableReviewStatus: !!settings.enableReviewStatus,
          calendarIncludeBacklog: !!(calendarIncludeBacklogToggle?.checked),
          debugLogsEnabled: !!(debugLogsToggle?.checked),
          diagnosticsOverlayEnabled: !!(diagOverlayToggle?.checked),
          historySortOrder: settings.historySortOrder || 'newest',
          language: getCurrentLanguage(),
          logoState: settings.customWorkspaceLogo ? 'logo-set' : 'logo-none',
          avatarState: (window.authSystem?.getCurrentUser?.()?.avatarDataUrl ? 'avatar-set' : 'avatar-none')
      };

      // Reset save button dirty state
      const saveBtn = form.querySelector('.settings-btn-save');
      if (saveBtn) {
          saveBtn.classList.remove('dirty');
          saveBtn.disabled = true;
      }

      // Bind change listeners once to track dirtiness
      if (!form.__settingsDirtyBound) {
          form.__settingsDirtyBound = true;

          const markDirtyIfNeeded = () => {
              if (!window.initialSettingsFormState) return;

              const currentLogoState = workspaceLogoDraft.hasPendingChange
                  ? 'draft-changed'
                  : (settings.customWorkspaceLogo ? 'logo-set' : 'logo-none');

              const currentAvatarState = avatarDraft.hasPendingChange
                  ? 'draft-changed'
                  : (window.authSystem?.getCurrentUser?.()?.avatarDataUrl ? 'avatar-set' : 'avatar-none');

              const current = {
                  userName: userNameInput.value || '',
                  notificationEmail: emailInput.value || '',
                  emailNotificationsEnabled: !!(emailEnabledToggle?.checked),
                  emailNotificationsWeekdaysOnly: !!(emailWeekdaysOnlyToggle?.checked),
                  emailNotificationsIncludeStartDates: !!(emailIncludeStartDatesToggle?.checked),
                  emailNotificationsIncludeBacklog: !!(emailIncludeBacklogToggle?.checked),
                  emailNotificationsIncludeWeekAhead: !!(emailIncludeWeekAheadToggle?.checked),
                  emailNotificationTime: emailTimeInput?.value || '',
                  emailNotificationTimeZone: emailTimeZoneSelect?.value || '',
                  autoSetStartDateOnStatusChange: !!autoStartToggle?.checked,
                  autoSetEndDateOnStatusChange: !!autoEndToggle?.checked,
                  enableReviewStatus: !!enableReviewStatusToggle?.checked,
                  calendarIncludeBacklog: !!(calendarIncludeBacklogToggle?.checked),
                  debugLogsEnabled: !!(debugLogsToggle?.checked),
                  diagnosticsOverlayEnabled: !!(diagOverlayToggle?.checked),
                  historySortOrder: historySortOrderSelect.value,
                  language: languageSelect?.value || getCurrentLanguage(),
                  logoState: currentLogoState,
                  avatarState: currentAvatarState
              };

              const changedKeys = Object.keys(current)
                  .filter((key) => current[key] !== window.initialSettingsFormState[key]);
              const isDirty = changedKeys.length > 0;
              updateSettingsChangeIndicators(changedKeys);

              if (saveBtn) {
                  if (isDirty) {
                      saveBtn.classList.add('dirty');
                      saveBtn.disabled = false;
                  } else {
                      saveBtn.classList.remove('dirty');
                      saveBtn.disabled = true;
                  }
              }

              window.settingsFormIsDirty = isDirty;
          };

          // Expose for logo controls to call after async updates
          window.markSettingsDirtyIfNeeded = markDirtyIfNeeded;

          // Listen to relevant inputs
          [userNameInput, emailInput, emailEnabledToggle, emailWeekdaysOnlyToggle, emailIncludeBacklogToggle, emailIncludeStartDatesToggle, emailIncludeWeekAheadToggle, emailTimeInput, emailTimeZoneSelect, autoStartToggle, autoEndToggle, enableReviewStatusToggle, calendarIncludeBacklogToggle, debugLogsToggle, diagOverlayToggle, historySortOrderSelect, languageSelect, logoFileInput, avatarFileInput]
              .filter(Boolean)
              .forEach(el => {
                  el.addEventListener('change', markDirtyIfNeeded);
                  if (el.tagName === 'INPUT' && el.type === 'text' || el.type === 'email') {
                      el.addEventListener('input', markDirtyIfNeeded);
                  }
              });

          if (emailEnabledToggle && !emailEnabledToggle.__emailInputsBound) {
              emailEnabledToggle.__emailInputsBound = true;
              emailEnabledToggle.addEventListener('change', () => {
                  applyEmailNotificationInputState();
              });
          }

          if (emailTimeTrigger && !emailTimeTrigger.__notificationTimeBound) {
              emailTimeTrigger.__notificationTimeBound = true;
              emailTimeTrigger.addEventListener('click', (evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  const enabled = !!emailEnabledToggle?.checked;
                  if (!enabled) return;
                  const isOpen = notificationTimePortalEl && notificationTimePortalEl.style.display !== 'none';
                  if (isOpen) {
                      hideNotificationTimePortal();
                      return;
                  }
                  showNotificationTimePortal(emailTimeTrigger, emailTimeInput, emailTimeValueEl);
              });
          }

          if (emailTimeZoneTrigger && emailTimeZoneSelect && !emailTimeZoneTrigger.__notificationTimeZoneBound) {
              emailTimeZoneTrigger.__notificationTimeZoneBound = true;
              emailTimeZoneTrigger.addEventListener('click', (evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                  const enabled = !!emailEnabledToggle?.checked;
                  if (!enabled) return;
                  const isOpen = notificationTimeZonePortalEl && notificationTimeZonePortalEl.style.display !== 'none';
                  if (isOpen) {
                      hideNotificationTimeZonePortal();
                      return;
                  }
                  showNotificationTimeZonePortal(emailTimeZoneTrigger, emailTimeZoneSelect, emailTimeZoneValueEl, { searchable: true });
              });
          }

          if (emailTimeZoneSelect && emailTimeZoneValueEl && !emailTimeZoneSelect.__timezoneValueBound) {
              emailTimeZoneSelect.__timezoneValueBound = true;
              emailTimeZoneSelect.addEventListener('change', () => {
                  emailTimeZoneValueEl.textContent =
                      emailTimeZoneSelect.options?.[emailTimeZoneSelect.selectedIndex]?.textContent ||
                      emailTimeZoneSelect.value ||
                      '';
              });
          }
  }

      switchSettingsTab(initialTab);
      // Mobile opens on the section list; desktop ignores this class
      setSettingsMobileDetail(false);
      refreshSettingsIdentityCard();
      bindSettingsSelectTriggers(modal);
      updateSettingsChangeIndicators([]);
      modal.classList.add('active');
      lockSettingsBodyHeight();

      // Always reset scroll position to top when opening settings
      const body = modal.querySelector('.settings-modal-body');
      if (body) body.scrollTop = 0;
    
    // Add reset PIN / change password button event listener (dynamic based on auth method)
    const resetPinBtn = modal.querySelector('#reset-pin-btn');
    if (resetPinBtn && !resetPinBtn.dataset.listenerAttached) {
        resetPinBtn.dataset.listenerAttached = 'true';
        resetPinBtn.addEventListener('click', function(e) {
            e.preventDefault();
            const cu = window.authSystem?.getCurrentUser?.();
            const method = cu?.authMethod || 'pin';
            if (method === 'password') {
                changePasswordFlow();
            } else {
                resetPINFlow();
            }
        });
    }

    // Add switch auth method button handler
    const switchAuthBtn = modal.querySelector('#switch-auth-method-btn');
    if (switchAuthBtn && !switchAuthBtn.dataset.listenerAttached) {
        switchAuthBtn.dataset.listenerAttached = 'true';
        switchAuthBtn.addEventListener('click', function(e) {
            e.preventDefault();
            switchAuthMethodFlow();
        });
    }

    // Re-fit the dialog height when the window size changes while it's open
    if (!modal.dataset.resizeListenerAttached) {
        modal.dataset.resizeListenerAttached = 'true';
        let resizeFrame = 0;
        window.addEventListener('resize', () => {
            cancelAnimationFrame(resizeFrame);
            resizeFrame = requestAnimationFrame(lockSettingsBodyHeight);
        });
    }

    // Tab list keyboard navigation (WAI-ARIA tabs pattern)
    const settingsNav = modal.querySelector('.settings-nav');
    if (settingsNav && !settingsNav.dataset.listenerAttached) {
        settingsNav.dataset.listenerAttached = 'true';
        settingsNav.addEventListener('keydown', function(e) {
            const current = SETTINGS_TABS.indexOf(settingsNav.querySelector('.settings-nav-item.active')?.dataset.param);
            let next = null;
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (current + 1) % SETTINGS_TABS.length;
            else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (current - 1 + SETTINGS_TABS.length) % SETTINGS_TABS.length;
            else if (e.key === 'Home') next = 0;
            else if (e.key === 'End') next = SETTINGS_TABS.length - 1;
            if (next === null) return;
            e.preventDefault();
            switchSettingsTab(SETTINGS_TABS[next], { focus: true });
        });
    }

    const docsTrashBtn = modal.querySelector('#settings-docs-trash-btn');
    if (docsTrashBtn && !docsTrashBtn.dataset.listenerAttached) {
        docsTrashBtn.dataset.listenerAttached = 'true';
        docsTrashBtn.addEventListener('click', function(e) {
            e.preventDefault();
            void openDocsTrashModal({ returnToSettings: true });
        });
    }
}

// Simplified user menu setup
function closeUserDropdown() {
    const dropdown = document.getElementById("shared-user-dropdown");
    if (dropdown) {
        dropdown.classList.remove("active");
    }
}

function updateUserMenuRailWidth() {
    const root = document.documentElement;
    const userMenu = document.querySelector('.user-menu');
    if (!root) return;

    if (!userMenu) {
        root.style.setProperty(USER_MENU_RAIL_CSS_VAR, '0px');
        return;
    }

    const styles = window.getComputedStyle(userMenu);
    if (styles.display === 'none' || styles.visibility === 'hidden') {
        root.style.setProperty(USER_MENU_RAIL_CSS_VAR, '0px');
        return;
    }

    const rightOffset = parseFloat(styles.right) || 0;
    const railGap = Math.max(parseFloat(styles.columnGap || styles.gap) || 0, 12);
    const width = userMenu.getBoundingClientRect().width || userMenu.offsetWidth || 0;
    const railWidth = Math.max(0, Math.ceil(width + rightOffset + railGap));
    root.style.setProperty(USER_MENU_RAIL_CSS_VAR, `${railWidth}px`);
}

function scheduleUserMenuRailWidthUpdate() {
    if (typeof requestAnimationFrame !== 'function') {
        updateUserMenuRailWidth();
        return;
    }
    if (userMenuRailMeasureRaf) return;
    userMenuRailMeasureRaf = requestAnimationFrame(() => {
        userMenuRailMeasureRaf = 0;
        updateUserMenuRailWidth();
    });
}

function bindUserMenuRailWidthObserver() {
    if (userMenuRailResizeObserver) {
        userMenuRailResizeObserver.disconnect();
        userMenuRailResizeObserver = null;
    }

    const userMenu = document.querySelector('.user-menu');
    if (!userMenu || typeof ResizeObserver === 'undefined') {
        scheduleUserMenuRailWidthUpdate();
        return;
    }

    userMenuRailResizeObserver = new ResizeObserver(() => {
        scheduleUserMenuRailWidthUpdate();
    });
    userMenuRailResizeObserver.observe(userMenu);
    scheduleUserMenuRailWidthUpdate();
}

function setupUserMenus() {
    const avatar = document.getElementById("shared-user-avatar");
    const dropdown = document.getElementById("shared-user-dropdown");

    if (avatar && dropdown) {
        // Remove any existing listener by cloning and replacing
        const newAvatar = avatar.cloneNode(true);
        avatar.parentNode.replaceChild(newAvatar, avatar);

        // Add fresh listener
        newAvatar.addEventListener("click", function (e) {
            e.stopPropagation();
            closeNotificationDropdown();
            dropdown.classList.toggle("active");
        });

        // Close dropdown when clicking on any dropdown item (except disabled ones)
        dropdown.addEventListener("click", function (e) {
            const item = e.target.closest(".dropdown-item:not(.disabled)");
            if (item) {
                closeUserDropdown();
            }
        });
    }

    bindUserMenuRailWidthObserver();
}

function updateUserDisplay(name, avatarDataUrl) {
    const nameEl = document.querySelector(".user-name");
    const avatarEl = document.getElementById("shared-user-avatar");

    if (nameEl) nameEl.textContent = name;
    
    // Update avatar initials
    if (avatarEl) {
        const parts = name.split(" ").filter(Boolean);
        let initials = "NL";
        if (parts.length > 0) {
            if (parts.length === 1) {
                initials = parts[0].slice(0, 2).toUpperCase();
            } else {
                initials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
            }
        }

        if (avatarDataUrl) {
            avatarEl.classList.add('has-image');
            avatarEl.style.backgroundImage = `url(${avatarDataUrl})`;
            avatarEl.textContent = '';
        } else {
            avatarEl.classList.remove('has-image');
            avatarEl.style.backgroundImage = '';
            avatarEl.textContent = initials;
        }
    }

    scheduleUserMenuRailWidthUpdate();
}

function hydrateUserProfile() {
    // Get current user from auth system
    const currentUser = window.authSystem?.getCurrentUser();
    if (!currentUser) return; // Not logged in yet

    updateUserDisplay(currentUser.name, currentUser.avatarDataUrl);

    const emailEl = document.querySelector(".user-email");
    if (emailEl) emailEl.textContent = currentUser.email || currentUser.username;
}

function normalizeTaskModalAttachmentUI() {
    const addLinkBtn = document.querySelector('#task-modal [data-action="addAttachment"]');
    if (addLinkBtn) addLinkBtn.textContent = t('tasks.modal.addLink');
}

// Close dropdown when clicking outside
document.addEventListener("click", function (event) {
    const clickedInUserMenu = event.target.closest(".user-menu");
    if (!clickedInUserMenu) {
        closeUserDropdown();
    }
    const clickedInNotifyMenu = event.target.closest(".notify-menu");
    if (!clickedInNotifyMenu) {
        closeNotificationDropdown();
    }
});

function updateLogos() {
    const isDark = document.documentElement.getAttribute("data-theme") === "dark";
    const logoSrc = isDark ? "src/assets/Nautilus_logo.png" : "src/assets/Nautilus_logo_light.png";

    // Update all logo images (regular logos and boot splash logos)
    // EXCLUDE lock/auth screen logos - they always have dark backgrounds and need white-text logo
    document.querySelectorAll('img.logo, img[class*="boot-logo"]').forEach(logo => {
        // Skip logos inside lock screens or auth overlays (they always use white-text logo)
        if (logo.closest('.overlay') || logo.closest('.auth-overlay')) {
            return;
        }
        logo.src = logoSrc;
    });
}

function updateThemeMenuText() {
    const themeText = document.getElementById("theme-text");
    const isDark = document.documentElement.getAttribute("data-theme") === "dark";
    if (themeText) {
        themeText.textContent = isDark ? t('menu.lightMode') : t('menu.darkMode');
    }
    updatePublicDocsThemeToggle();
}

function applyTheme(theme, options = {}) {
    const { persist = true, emit = true } = options;
    const normalizedTheme = theme === "dark" ? "dark" : "light";
    const root = document.documentElement;

    root.setAttribute("data-theme", normalizedTheme);

    if (persist) {
        localStorage.setItem("theme", normalizedTheme);
    }

    updateThemeMenuText();
    updateLogos();

    if (emit) {
        document.dispatchEvent(new CustomEvent("nautilus:themechange", {
            detail: { theme: normalizedTheme }
        }));
    }
}

function toggleTheme() {
    const root = document.documentElement;
    const nextTheme = root.getAttribute("data-theme") === "dark" ? "light" : "dark";
    applyTheme(nextTheme);

    // Refresh calendar if visible (bars use project colors which may change with theme)
    if (document.getElementById('calendar-view')?.classList.contains('active')) {
        renderCalendar();
    }
}

// Load saved theme
const savedTheme = localStorage.getItem("theme");
applyTheme(savedTheme === "dark" ? "dark" : "light", { persist: false, emit: false });


// Resizable sidebar functionality
let isResizing = false;

document.querySelector(".resizer").addEventListener("mousedown", function (e) {
    isResizing = true;
    this.classList.add("dragging");
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    e.preventDefault();
});

document.addEventListener("mousemove", function (e) {
    if (!isResizing) return;

    const sidebar = document.querySelector(".sidebar");
    const newWidth = e.clientX;

    if (newWidth >= 200 && newWidth <= 500) {
        sidebar.style.width = newWidth + "px";
        localStorage.setItem('sidebarWidth', newWidth);
    }
});

document.addEventListener("mouseup", function () {
    if (isResizing) {
        isResizing = false;
        document.querySelector(".resizer").classList.remove("dragging");
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    }
});

function updateProjectField(projectId, field, value, options) {
    const opts = options || {};
    const shouldRender = opts.render !== false;

    // 1. CORRECCIÓN DE FORMATO: Convertir fecha de dd-mm-yyyy a yyyy-mm-dd
    let updatedValue = value;
    if (field === 'startDate' || field === 'endDate') {
        // Si el valor parece una fecha dd-mm-yyyy, la convertimos a ISO para guardarla
        if (looksLikeDMY(value)) {
            updatedValue = toISOFromDMY(value);
        }
    }

    // Capture old project state for history tracking
    const oldProject = projects.find(p => p.id === projectId);
    const oldProjectCopy = oldProject ? JSON.parse(JSON.stringify(oldProject)) : null;

    // Avoid unnecessary rerenders/saves when the value didn't actually change
    if (oldProject) {
        const prev = oldProject[field];
        const prevStr = typeof prev === 'string' ? prev : (prev || '');
        const nextStr = typeof updatedValue === 'string' ? updatedValue : (updatedValue || '');
        if (prevStr === nextStr) return;
    }

    // Use project service to update field
    const result = updateProjectFieldService(projectId, field, updatedValue, projects);

    if (result.project) {
        projects = result.projects;
        const project = result.project;

        // Set updatedAt timestamp
        project.updatedAt = new Date().toISOString();

        // Clear sorted view cache to force refresh with updated project
        appState.projectsSortedView = null;

        // Record history for project update
        if (window.historyService && oldProjectCopy) {
            window.historyService.recordProjectUpdated(oldProjectCopy, project);
        }

        // Update UI immediately (optimistic update)
        if (shouldRender) showProjectDetails(projectId);

        // Always refresh calendar if visible (date changes affect bar layout)
        if (document.getElementById('calendar-view')?.classList.contains('active')) {
            renderCalendar();
        }

        // Save in background (don't block UI)
        saveProjectUpdateData(projectId, { [field]: updatedValue, updatedAt: project.updatedAt }).catch(err => {
            console.error('Failed to save project field:', err);
            showErrorNotification(t('error.saveChangesFailed'));
        });
    }
}

// Expose for any inline handlers (HTML attribute callbacks run in global scope even when app.js is a module)
window.updateProjectField = updateProjectField;
window.debouncedUpdateProjectField = typeof debouncedUpdateProjectField === 'function' ? debouncedUpdateProjectField : undefined;
window.flushDebouncedProjectField = typeof flushDebouncedProjectField === 'function' ? flushDebouncedProjectField : undefined;
window.beautifyInternalLinks = beautifyInternalLinks;

// Re-render calendar if the view is active, used after date changes
function maybeRefreshCalendar() {
    const calendarActive = document.getElementById('calendar-view')?.classList.contains('active');
    if (calendarActive) {
        // Re-render to recompute rows/tracks and spacer heights
        renderCalendar();
    }
}

function showCalendarView() {
    // Track whether we're already on the calendar sub-view
    const alreadyOnCalendar =
        document.getElementById('tasks')?.classList.contains('active') &&
        document.getElementById('calendar-view')?.classList.contains('active');

    // Restore the viewed month from the URL when present (e.g. a refreshed
    // #tasks?view=calendar&month=YYYY-MM deep link).
    applyCalendarMonthFromURL();

    // Normalize to the #calendar route ONLY when arriving from a non-calendar
    // page. When already on a calendar route (incl. a #tasks?view=calendar&month
    // deep link), leave the URL untouched so the viewed month is preserved.
    const calHash = window.location.hash.slice(1);
    const calPage = calHash.split('?')[0];
    const calQuery = calHash.split('?')[1] || '';
    const onCalendarRoute =
        calPage === 'calendar' ||
        ((calPage === 'tasks' || calPage === 'tasks/archive') &&
            new URLSearchParams(calQuery).get('view') === 'calendar');
    if (!onCalendarRoute && window.location.hash !== '#calendar') {
        window.location.hash = 'calendar';
    }

    // Only switch page if not already on tasks page
    if (!document.getElementById('tasks')?.classList.contains('active')) {
        showPage("tasks");
    }

    // Keep Calendar highlighted in sidebar (not All Tasks)
    document
        .querySelectorAll(".nav-item")
        .forEach((nav) => nav.classList.remove("active"));
    document.querySelector('.nav-item[data-page="calendar"]').classList.add("active");

    // Hide the view toggle when accessing from Calendar nav
    const viewToggle = document.querySelector(".view-toggle");
    if (viewToggle) viewToggle.classList.add("hidden");

    // Hide kanban settings in calendar view
    const kanbanSettingsContainer = document.getElementById('kanban-settings-btn')?.parentElement;
    if (kanbanSettingsContainer) kanbanSettingsContainer.style.display = 'none';

    // Hide backlog button in calendar view (only show in Kanban)
    const backlogBtn = document.getElementById('backlog-quick-btn');
    if (backlogBtn) backlogBtn.style.display = 'none';

    // Hide the Archived button in calendar view (archive is a Kanban/List sub-view)
    const archivedViewBtnEl = document.getElementById('archived-view-btn');
    if (archivedViewBtnEl) archivedViewBtnEl.style.display = 'none';

    // Hide mass-edit toolbar in calendar view (bulk change doesn't apply to calendar)
    const massEditToolbar = document.getElementById('mass-edit-toolbar');
    if (massEditToolbar) massEditToolbar.style.display = 'none';

    // Hide other views and show calendar (idempotent)
    const kanban = document.querySelector(".kanban-board");
    const list = document.getElementById("list-view");
    const calendar = document.getElementById("calendar-view");
    if (kanban && !kanban.classList.contains('hidden')) kanban.classList.add('hidden');
    if (list && list.classList.contains('active')) list.classList.remove('active');
    if (calendar && !calendar.classList.contains('active')) calendar.classList.add('active');

    // mark header so the Add Task button aligns left like in Projects
    try{ document.querySelector('.kanban-header')?.classList.add('calendar-mode'); }catch(e){}

    // Update page title to "Calendar" on desktop
    const pageTitle = document.querySelector('#tasks .page-title');
    if (pageTitle) pageTitle.textContent = 'Calendar';

    // If we were already on calendar, re-render (single DOM write, no flicker)
    if (alreadyOnCalendar) {
        renderCalendar();
    } else {
        resetCalendarFilters();
        populateCalendarProjectTagsDropdown();
        renderCalendar();
    }
    // Make sure UI chrome (sort toggle) reflects the calendar state
    try { updateSortUI(); } catch (e) {}
    // Sync compact control bar state
    updateCalControlBar();
}



// Done tasks saved before completedDate was tracked get a one-time inferred date
// (end date, else created + 1 month) so "completed recently" starts clean.
// Each fix is saved per task, touching only completedDate.
let completedDateBackfillRunning = false;
async function backfillMissingCompletedDates() {
    if (isInitializing || completedDateBackfillRunning) return;
    const { isoDate } = getNowPartsInUserTimeZone();
    const changed = backfillLegacyCompletedDates(tasks, isoDate);
    if (changed.length === 0) return;
    completedDateBackfillRunning = true;
    logDebug('tasks', 'Backfilling completedDate on legacy done tasks', { count: changed.length });
    try {
        for (const { id, completedDate } of changed) {
            try {
                await saveTaskUpdateData(id, { completedDate });
            } catch (error) {
                console.error('[completedDate backfill] save failed for task', id, error);
            }
        }
    } finally {
        completedDateBackfillRunning = false;
    }
    updateDashboardStats();
}

function migrateDatesToISO() {
    let touched = false;

    tasks.forEach((t) => {
        if (t.startDate && looksLikeDMY(t.startDate)) {
            t.startDate = toISOFromDMY(t.startDate);
            touched = true;
        }
        if (t.endDate && looksLikeDMY(t.endDate)) {
            t.endDate = toISOFromDMY(t.endDate);
            touched = true;
        }
    });

    projects.forEach((p) => {
        if (p.startDate && looksLikeDMY(p.startDate)) {
            p.startDate = toISOFromDMY(p.startDate);
            touched = true;
        }
        if (p.endDate && looksLikeDMY(p.endDate)) {
            p.endDate = toISOFromDMY(p.endDate);
            touched = true;
        }
    });

    if (touched) persistAll();
}

let feedbackSubmitInProgress = false;

async function addFeedbackItem() {
    // Prevent duplicate submissions (double-click, multiple enter key presses)
    if (feedbackSubmitInProgress) {
        logDebug('feedback', 'Submission already in progress, ignoring duplicate call');
        return;
    }
    
    feedbackSubmitInProgress = true;
    logDebug('feedback', 'addFeedbackItem called');
    const typeRadio = document.querySelector('input[name="feedback-type"]:checked');
    const type = typeRadio ? typeRadio.value : 'bug';
    const description = document.getElementById('feedback-description').value.trim();
    const screenshotData = currentFeedbackScreenshotData || '';
    const attachmentFile = currentFeedbackAttachmentFile || null;

    if (!description) {
        feedbackSubmitInProgress = false;
        return;
    }

    const itemId = String(feedbackCounter++); // Convert to string immediately for D1 TEXT column
    logDebug('feedback', 'Generated ID:', { itemId });

    const item = {
        id: itemId,
        type: type,
        description: description,
        screenshotUrl: screenshotData ? 'uploading' : '',
        attachmentFileKey: attachmentFile ? 'uploading' : '',
        attachmentFileName: attachmentFile ? attachmentFile.name : '',
        attachmentFileType: attachmentFile ? getFileType(attachmentFile.type || '', attachmentFile.name) : '',
        createdAt: new Date().toISOString(),
        status: 'open'
    };

    // Add to array immediately (optimistic update)
    feedbackItems.unshift(item);
    
    // Clear input immediately for instant feedback
    document.getElementById('feedback-description').value = '';
    clearFeedbackScreenshot();

    // Reset to page 1 for pending items so new item is visible immediately
    feedbackPendingPage = 1;

    // Update UI INSTANTLY - no delays, no debouncing
    updateCounts();
    renderFeedback();

    // Cache immediately for instant persistence
    persistFeedbackCache();
    logDebug('feedback', 'UI updated, starting async operations');
    
    // Reset flag to allow next submission
    feedbackSubmitInProgress = false;

    // Upload screenshot in background (non-blocking)
    if (screenshotData) {
        logDebug('feedback', 'Uploading screenshot to KV...');
        const uploadStart = Date.now();
        fetch('/api/feedback-screenshot', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                feedbackId: itemId,
                data: screenshotData
            })
        })
        .then(response => {
            logDebug('feedback', 'Screenshot upload completed', { durationMs: Date.now() - uploadStart });
            if (response.ok) {
                return response.json();
            }
            throw new Error('Screenshot upload failed');
        })
        .then(result => {
            logDebug('feedback', 'Screenshot uploaded', { screenshotId: result.screenshotId });
            // Update item with screenshot ID
            item.screenshotUrl = result.screenshotId;
            persistFeedbackCache();
            
            // Re-render to show screenshot icon
            renderFeedback();
            
            // Save to D1 with screenshot
            logDebug('feedback', 'Saving to D1 with screenshot...');
            return saveSingleFeedbackItem(item);
        })
        .catch(error => {
            console.error('[Feedback] Screenshot upload failed:', error);
            // Save without screenshot
            logDebug('feedback', 'Saving to D1 without screenshot...');
            return saveSingleFeedbackItem(item);
        })
        .then(() => {
            logDebug('feedback', 'D1 save completed');
        })
        .catch(error => {
            console.error('[Feedback] Failed to save feedback item:', error);
            // Rollback on failure
            feedbackItems = feedbackItems.filter(f => f.id !== item.id);
            updateCounts();
            renderFeedback();
            persistFeedbackCache();
            showErrorNotification(t('error.saveFeedbackFailed'));
        });
    } else if (attachmentFile) {
        // Non-image document — upload via R2 file API
        logDebug('feedback', 'Uploading document attachment...');
        const fileKey = `feedback_${itemId}_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
        uploadFile(fileKey, attachmentFile, { kind: 'feedback_attachment' })
        .then(() => {
            item.attachmentFileKey = fileKey;
            persistFeedbackCache();
            renderFeedback();
            return saveSingleFeedbackItem(item);
        })
        .catch(error => {
            console.error('[Feedback] Document upload failed:', error);
            item.attachmentFileKey = '';
            return saveSingleFeedbackItem(item);
        })
        .catch(error => {
            console.error('[Feedback] Failed to save feedback item:', error);
            feedbackItems = feedbackItems.filter(f => f.id !== item.id);
            updateCounts();
            renderFeedback();
            persistFeedbackCache();
            showErrorNotification(t('error.saveFeedbackFailed'));
        });
    } else {
        // No attachment - save immediately
        logDebug('feedback', 'No attachment, saving to D1...');
        saveSingleFeedbackItem(item).catch((error) => {
            console.error('[Feedback] Failed to save feedback item:', error);
            // Rollback on failure
            feedbackItems = feedbackItems.filter(f => f.id !== item.id);
            updateCounts();
            renderFeedback();
            persistFeedbackCache();
            showErrorNotification(t('error.saveFeedbackFailed'));
        });
    }

    // Scroll to top of pending section on mobile for instant visibility
    if (getIsMobileCached()) {
        requestAnimationFrame(() => {
            const pendingContainer = document.getElementById('feedback-list-pending');
            if (pendingContainer) {
                pendingContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    }
}


// Add enter key support for feedback and initialize screenshot attachments
// Macros dropdown — event delegation + fixed positioning to escape overflow:auto clipping
(function initMacrosDropdown() {
    let _activeToggle = null;
    let _activeDropdown = null;
    let _scrollHandler = null;

    function closeDropdown() {
        if (_activeDropdown) {
            _activeDropdown.hidden = true;
            _activeDropdown = null;
        }
        _activeToggle = null;
        if (_scrollHandler) {
            document.removeEventListener('scroll', _scrollHandler, { capture: true });
            _scrollHandler = null;
        }
    }

    function positionDropdown(toggle, dropdown) {
        const rect = toggle.getBoundingClientRect();
        dropdown.style.top = (rect.bottom + 6) + 'px';
        dropdown.style.left = rect.left + 'px';
    }

    document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-docs-macros-toggle]');
        if (toggle) {
            const wrap = toggle.closest('.docs-macros-wrap');
            const dropdown = wrap?.querySelector('.docs-macros-dropdown');
            if (!dropdown) return;

            const isOpen = !dropdown.hidden;
            closeDropdown();

            if (!isOpen) {
                positionDropdown(toggle, dropdown);
                dropdown.hidden = false;
                _activeToggle = toggle;
                _activeDropdown = dropdown;

                // Scroll-aware: reposition or close if anchor scrolls off-screen
                _scrollHandler = () => {
                    if (!_activeToggle) { closeDropdown(); return; }
                    const r = _activeToggle.getBoundingClientRect();
                    if (r.bottom < 0 || r.top > window.innerHeight) {
                        closeDropdown();
                    } else {
                        positionDropdown(_activeToggle, _activeDropdown);
                    }
                };
                document.addEventListener('scroll', _scrollHandler, { capture: true, passive: true });
            }
            return;
        }
        // Close once a macro is picked (hiding it doesn't stop the item's own handler)
        if (_activeDropdown && e.target.closest('.docs-macros-item')) {
            closeDropdown();
            return;
        }
        // Close on outside click
        if (_activeDropdown && !e.target.closest('.docs-macros-dropdown') && !e.target.closest('.docs-macros-wrap')) {
            closeDropdown();
        }
    }, true);
    // Clicks inside an embed iframe (Program Roadmap) never reach this document;
    // the window losing focus is the only signal of that "outside click".
    window.addEventListener('blur', () => { if (_activeDropdown) closeDropdown(); });
}());

(function initAlignDropdown() {
    let _activeToggle = null;
    let _activeDropdown = null;
    let _scrollHandler = null;

    function closeDropdown() {
        if (_activeDropdown) { _activeDropdown.hidden = true; _activeDropdown = null; }
        _activeToggle = null;
        if (_scrollHandler) {
            document.removeEventListener('scroll', _scrollHandler, { capture: true });
            _scrollHandler = null;
        }
    }

    function positionDropdown(toggle, dropdown) {
        const rect = toggle.getBoundingClientRect();
        dropdown.style.top = (rect.bottom + 4) + 'px';
        dropdown.style.left = rect.left + 'px';
    }

    document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-docs-align-toggle]');
        if (toggle) {
            const wrap = toggle.closest('.docs-align-wrap');
            const dropdown = wrap?.querySelector('.docs-align-dropdown');
            if (!dropdown) return;
            const isOpen = !dropdown.hidden;
            closeDropdown();
            if (!isOpen) {
                positionDropdown(toggle, dropdown);
                dropdown.hidden = false;
                _activeToggle = toggle;
                _activeDropdown = dropdown;
                _scrollHandler = () => {
                    if (!_activeToggle) { closeDropdown(); return; }
                    const r = _activeToggle.getBoundingClientRect();
                    if (r.bottom < 0 || r.top > window.innerHeight) { closeDropdown(); }
                    else { positionDropdown(_activeToggle, _activeDropdown); }
                };
                document.addEventListener('scroll', _scrollHandler, { capture: true, passive: true });
            }
            return;
        }
        if (_activeDropdown && !e.target.closest('.docs-align-dropdown') && !e.target.closest('.docs-align-wrap')) {
            closeDropdown();
        }
    }, true);
}());

document.addEventListener('DOMContentLoaded', function() {
    // Initialize feedback type dropdown
    const feedbackTypeBtn = document.getElementById('feedback-type-btn');
    const feedbackTypeGroup = document.getElementById('feedback-type-group');
    const feedbackTypeLabel = document.getElementById('feedback-type-label');

    // Online/offline listeners removed - D1 individual operations handle connectivity automatically

    if (feedbackTypeBtn && feedbackTypeGroup) {
        feedbackTypeBtn.addEventListener('click', function(e) {
            e.stopPropagation();
            feedbackTypeGroup.classList.toggle('open');
        });

        const typeRadios = document.querySelectorAll('input[name="feedback-type"]');
        typeRadios.forEach(radio => {
            radio.addEventListener('change', function() {
                const labelMap = {
                    bug: t('feedback.type.bugLabel'),
                    idea: t('feedback.type.improvementOption')
                };
                const selectedLabel = labelMap[this.value] || this.closest('label').textContent.trim();
                feedbackTypeLabel.textContent = selectedLabel;
                feedbackTypeGroup.classList.remove('open');
            });
        });

        document.addEventListener('click', function(e) {
            if (!feedbackTypeGroup.contains(e.target)) {
                feedbackTypeGroup.classList.remove('open');
            }
        });
    }

    const feedbackInput = document.getElementById('feedback-description');
    if (feedbackInput) {
        feedbackInput.addEventListener('keypress', function(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                addFeedbackItem();
            }
        });

        // Allow pasting images directly into the feedback description
        feedbackInput.addEventListener('paste', function(e) {
            if (!e.clipboardData) return;
            const file = Array.from(e.clipboardData.files || [])[0];
            if (file && file.type && file.type.startsWith('image/')) {
                e.preventDefault();
                handleFeedbackImageFile(file);
            }
        });
    }

    const screenshotInput = document.getElementById('feedback-screenshot-url');
    const screenshotFileInput = document.getElementById('feedback-screenshot-file');
    const screenshotButton = document.getElementById('feedback-screenshot-upload');

    const isMobileScreen = (typeof window.matchMedia === 'function')
        ? window.matchMedia('(max-width: 768px)').matches
        : getIsMobileCached();
    const screenshotDefaultText = isMobileScreen
        ? t('feedback.screenshotDropzoneTap')
        : t('feedback.screenshotDropzoneDefault');

    if (screenshotInput) {
        screenshotInput.innerHTML = renderFeedbackDropzoneLabel(screenshotDefaultText);
        screenshotInput.classList.add('feedback-screenshot-dropzone');
        screenshotInput.dataset.defaultText = screenshotDefaultText;
    }

    let screenshotPreview = document.getElementById('feedback-screenshot-preview');
    if (!screenshotPreview) {
        const feedbackBar = document.querySelector('#feedback .feedback-input-bar');
        if (feedbackBar && feedbackBar.parentNode) {
            screenshotPreview = document.createElement('div');
            screenshotPreview.id = 'feedback-screenshot-preview';
            feedbackBar.parentNode.insertBefore(screenshotPreview, feedbackBar.nextSibling);
        }
    }

    function handleDropOrPasteFileList(fileList, event) {
        if (!fileList || fileList.length === 0) return;
        const file = fileList[0];
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (file.type && file.type.startsWith('image/')) {
            handleFeedbackImageFile(file);
        } else {
            handleFeedbackDocumentFile(file);
        }
    }

    if (screenshotInput) {
        // Drag-and-drop support on the screenshot field
        screenshotInput.addEventListener('dragover', function(e) {
            e.preventDefault();
            screenshotInput.classList.add('feedback-screenshot-dragover');
        });

        screenshotInput.addEventListener('dragleave', function(e) {
            e.preventDefault();
            screenshotInput.classList.remove('feedback-screenshot-dragover');
        });

        screenshotInput.addEventListener('drop', function(e) {
            screenshotInput.classList.remove('feedback-screenshot-dragover');
            const files = e.dataTransfer && e.dataTransfer.files;
            handleDropOrPasteFileList(files, e);
        });

        // Paste images into the screenshot field
        screenshotInput.addEventListener('paste', function(e) {
            if (!e.clipboardData) return;
            const files = e.clipboardData.files;
            if (files && files.length > 0) {
                handleDropOrPasteFileList(files, e);
            }
        });

        // Click / keyboard to open file picker
        screenshotInput.addEventListener('click', function() {
            if (screenshotFileInput) {
                screenshotFileInput.click();
            }
        });
        screenshotInput.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (screenshotFileInput) {
                    screenshotFileInput.click();
                }
            }
        });
    }

    if (screenshotButton) {
        // Hide legacy separate button, the dropzone now handles click
        screenshotButton.style.display = 'none';
    }

    if (screenshotFileInput) {
        screenshotFileInput.addEventListener('change', function(e) {
            const files = e.target.files;
            if (files && files.length > 0) {
                handleDropOrPasteFileList(files, e);
            }
            // Reset so selecting the same file again will still fire change
            screenshotFileInput.value = '';
        });
    }
});

function handleFeedbackImageFile(file) {
    if (!file || !file.type || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = function(ev) {
        const dataUrl = ev.target && ev.target.result;
        if (!dataUrl) return;
        currentFeedbackScreenshotData = dataUrl;

        const screenshotInput = document.getElementById('feedback-screenshot-url');
        if (screenshotInput && screenshotInput.dataset) {
            screenshotInput.dataset.hasInlineImage = 'true';
        }

        const preview = document.getElementById('feedback-screenshot-preview');
        if (preview) {
            preview.innerHTML = `
                <div class="feedback-screenshot-preview-card">
                    <div class="feedback-screenshot-thumb">
                        <img src="${dataUrl}" alt="${t('feedback.screenshotPreviewAlt')}">
                    </div>
                    <div class="feedback-screenshot-meta">
                        <div class="feedback-screenshot-title">${t('feedback.screenshotPreviewTitle')}</div>
                        <div class="feedback-screenshot-subtitle">${t('feedback.screenshotPreviewSubtitle')}</div>
                    </div>
                    <button type="button" class="feedback-screenshot-remove">${t('feedback.screenshotRemove')}</button>
                </div>
            `;

            preview.style.display = 'flex';

            const thumb = preview.querySelector('.feedback-screenshot-thumb');
            if (thumb && typeof viewImageLegacy === 'function') {
                thumb.onclick = function() {
                    viewImageLegacy(dataUrl, 'Feedback Screenshot');
                };
            }

            const removeBtn = preview.querySelector('.feedback-screenshot-remove');
            if (removeBtn) {
                removeBtn.onclick = function(e) {
                    e.preventDefault();
                    clearFeedbackScreenshot();
                };
            }
        }
    };
    reader.onerror = function() {
        if (typeof showErrorNotification === 'function') {
            showErrorNotification(t('error.feedbackReadImage'));
        }
    };
    reader.readAsDataURL(file);
}

// Splits "📷 Some label text" into icon + text spans for proper vertical alignment
function renderFeedbackDropzoneLabel(text) {
    const spaceIdx = text.indexOf(' ');
    if (spaceIdx === -1) return escapeHtml(text);
    const icon = text.slice(0, spaceIdx);
    const label = text.slice(spaceIdx + 1);
    return `<span class="feedback-dropzone-icon">${icon}</span><span class="feedback-dropzone-text">${escapeHtml(label)}</span>`;
}

function handleFeedbackDocumentFile(file) {
    if (!file) return;
    currentFeedbackAttachmentFile = file;

    const fileType = getFileType(file.type || '', file.name);
    const icon = fileType === 'pdf' ? '📕' : fileType === 'spreadsheet' ? '📊' : fileType === 'document' ? '📝' : '📄';

    const preview = document.getElementById('feedback-screenshot-preview');
    if (preview) {
        preview.innerHTML = `
            <div class="feedback-screenshot-preview-card">
                <div class="feedback-screenshot-thumb" style="font-size:28px; display:flex; align-items:center; justify-content:center;">${icon}</div>
                <div class="feedback-screenshot-meta">
                    <div class="feedback-screenshot-title">${t('feedback.attachmentPreviewTitle')}</div>
                    <div class="feedback-screenshot-subtitle" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:160px;">${escapeHtml(file.name)}</div>
                </div>
                <button type="button" class="feedback-screenshot-remove">${t('feedback.screenshotRemove')}</button>
            </div>
        `;
        preview.style.display = 'flex';
        const removeBtn = preview.querySelector('.feedback-screenshot-remove');
        if (removeBtn) {
            removeBtn.onclick = function(e) {
                e.preventDefault();
                clearFeedbackScreenshot();
            };
        }
    }

    const screenshotInput = document.getElementById('feedback-screenshot-url');
    if (screenshotInput && screenshotInput.dataset) {
        screenshotInput.dataset.hasInlineImage = 'true';
    }
}

function clearFeedbackScreenshot() {
    currentFeedbackScreenshotData = "";
    currentFeedbackAttachmentFile = null;
    const screenshotInput = document.getElementById('feedback-screenshot-url');
    if (screenshotInput) {
        const defaultText = screenshotInput.dataset.defaultText || t('feedback.screenshotDropzoneDefault');
        screenshotInput.innerHTML = renderFeedbackDropzoneLabel(defaultText);
        if (screenshotInput.dataset) {
            delete screenshotInput.dataset.hasInlineImage;
        }
    }
    const preview = document.getElementById('feedback-screenshot-preview');
    if (preview) {
        preview.innerHTML = '';
        preview.style.display = 'none';
    }
}



function toggleFeedbackItem(id) {
    const item = feedbackItems.find(f => f.id === id);
    if (!item) return;

    // Store old state for rollback
    const oldStatus = item.status;
    const oldResolvedAt = item.resolvedAt;

    // Optimistic update: change state immediately
    if (item.status === 'open') {
        item.status = 'done';
        item.resolvedAt = new Date().toISOString();
    } else {
        item.status = 'open';
        delete item.resolvedAt;
    }
    item.updatedAt = new Date().toISOString();
    
    // Update UI INSTANTLY - no delays
    updateCounts();
    renderFeedback();
    
    // Cache immediately for instant persistence
    persistFeedbackCache();

    // Only send the fields that changed so attachment metadata is never
    // overwritten by stale in-memory values during status toggles.
    const feedbackStatusPatch = {
        id: item.id,
        status: item.status,
        resolvedAt: item.resolvedAt || null
    };

    updateSingleFeedbackItem(feedbackStatusPatch).catch((error) => {
        console.error('Failed to update feedback item:', error);
        // Rollback on persistent failure
        item.status = oldStatus;
        if (oldResolvedAt) {
            item.resolvedAt = oldResolvedAt;
        } else {
            delete item.resolvedAt;
        }
        updateCounts();
        renderFeedback();
        persistFeedbackCache();
        showErrorNotification(t('error.feedbackStatusFailed'));
    });
}

function renderFeedback() {
    // Only render if feedback page is active (guarantee UI updates)
    const feedbackPage = document.getElementById('feedback');
    if (!feedbackPage || !feedbackPage.classList.contains('active')) {
        // Page not active - UI will update when page becomes active
        return;
    }

    const pendingContainer = document.getElementById('feedback-list-pending');
    const doneContainer = document.getElementById('feedback-list-done');
    if (!pendingContainer || !doneContainer) {
        // Containers don't exist yet - will render when page loads
        return;
    }

    const typeIcons = {
        bug: '\u{1F41E}',
        improvement: '\u{1F4A1}',
        // Legacy values for backward compatibility
        feature: '\u{1F4A1}',
        idea: '\u{1F4A1}'
    };

    // Filter and sort: pending by updatedAt||createdAt (newest first), done by resolvedAt (newest first)
    // Optimized: Use single pass filter for better performance on mobile
    const pendingItems = [];
    const doneItems = [];
    for (const f of feedbackItems) {
        if (f.status === 'open') {
            pendingItems.push(f);
        } else {
            doneItems.push(f);
        }
    }
    // Pending: sort strictly by original creation time so un-marking a done item
    // always returns it to its original position, never jumps to the top.
    // Items with missing createdAt fall to the bottom (0).
    pendingItems.sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
    });
    // Done: sort by when the item was last resolved (newest resolve on top).
    // Falls back to createdAt for items missing resolvedAt.
    doneItems.sort((a, b) => {
        const ta = a.resolvedAt ? new Date(a.resolvedAt).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
        const tb = b.resolvedAt ? new Date(b.resolvedAt).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
        return tb - ta;
    });

    // Pagination calculations for pending items
    const pendingTotalPages = Math.ceil(pendingItems.length / FEEDBACK_OPEN_ITEMS_PER_PAGE);
    if (feedbackPendingPage > pendingTotalPages && pendingTotalPages > 0) {
        feedbackPendingPage = pendingTotalPages;
    }
    const pendingStartIndex = (feedbackPendingPage - 1) * FEEDBACK_OPEN_ITEMS_PER_PAGE;
    const pendingEndIndex = pendingStartIndex + FEEDBACK_OPEN_ITEMS_PER_PAGE;
    const pendingPageItems = pendingItems.slice(pendingStartIndex, pendingEndIndex);

    // Pagination calculations for done items
    const doneTotalPages = Math.ceil(doneItems.length / FEEDBACK_ITEMS_PER_PAGE);
    if (feedbackDonePage > doneTotalPages && doneTotalPages > 0) {
        feedbackDonePage = doneTotalPages;
    }
    const doneStartIndex = (feedbackDonePage - 1) * FEEDBACK_ITEMS_PER_PAGE;
    const doneEndIndex = doneStartIndex + FEEDBACK_ITEMS_PER_PAGE;
    const donePageItems = doneItems.slice(doneStartIndex, doneEndIndex);

    // Render pending items - use DocumentFragment for better performance on mobile
    if (pendingItems.length === 0) {
        pendingContainer.innerHTML = `<div class="empty-state" style="padding: 20px;"><p>${t('feedback.empty.pending')}</p></div>`;
    } else {
        // Direct innerHTML assignment is fastest for instant rendering
        pendingContainer.innerHTML = pendingPageItems.map(item => `
            <div class="feedback-item ${item.status === 'done' ? 'done' : ''}">
                <div class="feedback-top-row">
                    <div class="feedback-top-icons">
                        <span class="feedback-type-icon">${typeIcons[item.type] || '\u{1F4A1}'}</span>
                        ${item.screenshotUrl && item.screenshotUrl !== 'uploading' ? `<button type="button" class="feedback-screenshot-link" data-action="viewFeedbackScreenshot" data-param="${encodeURIComponent(item.screenshotUrl)}" title="${t('feedback.viewScreenshotTitle')}">\u{1F5BC}\u{FE0F}</button>` : ''}
                        ${item.screenshotUrl === 'uploading' || item.attachmentFileKey === 'uploading' ? `<span class="feedback-screenshot-uploading" title="${t('feedback.uploading')}">\u{23F3}</span>` : ''}
                        ${item.attachmentFileKey && item.attachmentFileKey !== 'uploading' ? `<a href="${buildFileReadUrl(item.attachmentFileKey, item.attachmentFileName)}" class="feedback-attachment-link" target="_blank" rel="noopener" title="${escapeHtml(item.attachmentFileName || t('feedback.attachment'))}">${item.attachmentFileType === 'pdf' ? '📕' : item.attachmentFileType === 'spreadsheet' ? '📊' : item.attachmentFileType === 'document' ? '📝' : '📄'}</a>` : ''}
                    </div>
                    <input type="checkbox" class="feedback-checkbox"
                           data-feedback-id="${item.id}"
                           ${item.status === 'done' ? 'checked' : ''}>
                </div>
                <div class="feedback-description">${escapeHtml(item.description)}</div>
                <div class="feedback-date">${formatDate(item.createdAt)}</div>
                <button class="feedback-delete-btn" data-action="deleteFeedbackItemWithStop" data-param="${item.id}">❌</button>
            </div>
        `).join('');
    }

    // Render pagination controls for pending items
    renderFeedbackPagination('pending', pendingItems.length, pendingTotalPages, feedbackPendingPage, FEEDBACK_OPEN_ITEMS_PER_PAGE);

    // Render done items
    if (doneItems.length === 0) {
        doneContainer.innerHTML = `<div class="empty-state" style="padding: 20px;"><p>${t('feedback.empty.done')}</p></div>`;
    } else {
        doneContainer.innerHTML = donePageItems.map(item => `
            <div class="feedback-item done">
                <div class="feedback-top-row">
                    <div class="feedback-top-icons">
                        <span class="feedback-type-icon">${typeIcons[item.type] || '\u{1F4A1}'}</span>
                        ${item.screenshotUrl && item.screenshotUrl !== 'uploading' ? `<button type="button" class="feedback-screenshot-link" data-action="viewFeedbackScreenshot" data-param="${encodeURIComponent(item.screenshotUrl)}" title="${t('feedback.viewScreenshotTitle')}">\u{1F5BC}\u{FE0F}</button>` : ''}
                        ${item.screenshotUrl === 'uploading' || item.attachmentFileKey === 'uploading' ? `<span class="feedback-screenshot-uploading" title="${t('feedback.uploading')}">\u{23F3}</span>` : ''}
                        ${item.attachmentFileKey && item.attachmentFileKey !== 'uploading' ? `<a href="${buildFileReadUrl(item.attachmentFileKey, item.attachmentFileName)}" class="feedback-attachment-link" target="_blank" rel="noopener" title="${escapeHtml(item.attachmentFileName || t('feedback.attachment'))}">${item.attachmentFileType === 'pdf' ? '📕' : item.attachmentFileType === 'spreadsheet' ? '📊' : item.attachmentFileType === 'document' ? '📝' : '📄'}</a>` : ''}
                    </div>
                    <input type="checkbox" class="feedback-checkbox"
                           data-feedback-id="${item.id}"
                           checked>
                </div>
                <div class="feedback-description">${escapeHtml(item.description)}</div>
                <div class="feedback-date">${formatDate(item.createdAt)}</div>
                <button class="feedback-delete-btn" data-action="deleteFeedbackItemWithStop" data-param="${item.id}">❌</button>
            </div>
        `).join('');
    }

    // Render pagination controls for done items
    renderFeedbackPagination('done', doneItems.length, doneTotalPages, feedbackDonePage, FEEDBACK_ITEMS_PER_PAGE);
}

function renderFeedbackPagination(section, totalItems, totalPages, currentPage, pageSize = FEEDBACK_ITEMS_PER_PAGE) {
    const containerId = section === 'pending' ? 'feedback-pagination-pending' : 'feedback-pagination-done';
    const container = document.getElementById(containerId);
    if (!container) return;

    // Hide pagination if there's only one page or no items
    if (totalPages <= 1) {
        container.innerHTML = '';
        container.style.display = 'none';
        return;
    }

    container.style.display = 'flex';

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    let paginationHTML = `
        <div class="feedback-pagination-info">
            ${t('feedback.pagination.showing', { start: startItem, end: endItem, total: totalItems })}
        </div>
        <div class="feedback-pagination-controls">
            <button
                class="feedback-pagination-btn"
                data-action="changeFeedbackPage" data-param="${section}" data-param2="1"
                ${currentPage === 1 ? 'disabled' : ''}
                title="${t('feedback.pagination.first')}">
                &laquo;
            </button>
            <button
                class="feedback-pagination-btn"
                data-action="changeFeedbackPage" data-param="${section}" data-param2="${currentPage - 1}"
                ${currentPage === 1 ? 'disabled' : ''}
                title="${t('feedback.pagination.prev')}">
                &lsaquo;
            </button>
            <span class="feedback-pagination-page">
                ${t('feedback.pagination.pageOf', { current: currentPage, total: totalPages })}
            </span>
            <button
                class="feedback-pagination-btn"
                data-action="changeFeedbackPage" data-param="${section}" data-param2="${currentPage + 1}"
                ${currentPage === totalPages ? 'disabled' : ''}
                title="${t('feedback.pagination.next')}">
                &rsaquo;
            </button>
            <button
                class="feedback-pagination-btn"
                data-action="changeFeedbackPage" data-param="${section}" data-param2="${totalPages}"
                ${currentPage === totalPages ? 'disabled' : ''}
                title="${t('feedback.pagination.last')}">
                &raquo;
            </button>
        </div>
    `;

    container.innerHTML = paginationHTML;
}

function changeFeedbackPage(section, newPage) {
    const scrollContainer = document.querySelector('#feedback .page-content');
    const activeEl = document.activeElement;
    const wasPaginationClick = !!(activeEl && activeEl.classList && activeEl.classList.contains('feedback-pagination-btn'));
    const wasNearBottom = !!(scrollContainer && (scrollContainer.scrollHeight - (scrollContainer.scrollTop + scrollContainer.clientHeight) < 80));

    if (section === 'pending') {
        feedbackPendingPage = newPage;
    } else {
        feedbackDonePage = newPage;
    }
    renderFeedback();

    // Keep pagination usable when switching between short/long pages.
    // If the user was at (or near) the bottom, anchor them back to the pagination controls.
    const paginationId = section === 'pending' ? 'feedback-pagination-pending' : 'feedback-pagination-done';
    const sectionId = section === 'pending' ? 'feedback-list-pending' : 'feedback-list-done';
    const targetId = (wasPaginationClick || wasNearBottom) ? paginationId : sectionId;
    const target = document.getElementById(targetId);
    if (target) {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: targetId === paginationId ? 'end' : 'start'
                });
            });
        });
    }
}

// Expose to window for onclick handlers
window.changeFeedbackPage = changeFeedbackPage;

// === History Rendering - Inline for Tasks and Projects ===

// Modal tab switching
function setupModalTabs() {
    document.querySelectorAll('.modal-content .modal-tab').forEach(tab => {
        tab.addEventListener('click', (e) => {
            const tabName = e.target.dataset.tab;
            const modalContent = e.target.closest('.modal-content');
            if (!modalContent) return;

            // Preserve the current task modal size when switching to History (desktop),
            // so the modal doesn't shrink just because History has less content.
            try {
                const isTaskModal = !!modalContent.closest('#task-modal');
                if (isTaskModal) {
                    const footer = modalContent.querySelector('#task-footer');
                    const inEditMode = footer && window.getComputedStyle(footer).display === 'none';
                    if (inEditMode) {
                        if (tabName === 'history') {
                            const h = Math.round(modalContent.getBoundingClientRect().height);
                            if (h > 0) {
                                modalContent.style.minHeight = `${h}px`;
                                modalContent.style.maxHeight = `${h}px`;
                            }
                        } else if (tabName === 'general' || tabName === 'details') {
                            modalContent.style.minHeight = '';
                            modalContent.style.maxHeight = '';
                        }
                    }
                }
            } catch (err) {}

            // Update tab buttons
            modalContent.querySelectorAll('.modal-tab').forEach(t => t.classList.remove('active'));
            e.target.classList.add('active');

            // Mobile: Handle General/Details tab switching (on same content)
            if (tabName === 'general' || tabName === 'details') {
                // Both tabs show the same content (task-details-tab with different fields visible)
                const taskDetailsTab = modalContent.querySelector('#task-details-tab');
                const taskHistoryTab = modalContent.querySelector('#task-history-tab');

                // Only handle mobile tab switching if we have history tab (means modal is open)
                if (taskDetailsTab && taskHistoryTab) {
                    // Make sure details tab is active
                    modalContent.querySelectorAll('.modal-tab-content').forEach(content => {
                        content.classList.remove('active');
                    });
                    taskDetailsTab.classList.add('active');

                    // Toggle body class to show/hide appropriate fields
                    if (tabName === 'details') {
                        document.body.classList.add('mobile-tab-details-active');
                    } else {
                        document.body.classList.remove('mobile-tab-details-active');
                    }
                    return; // Don't run the rest of the tab switching logic
                }
            }

            // Remove mobile tab class when switching to History
            document.body.classList.remove('mobile-tab-details-active');

            // Update tab content
            modalContent.querySelectorAll('.modal-tab-content').forEach(content => {
                content.classList.remove('active');
            });

            const targetTab = modalContent.querySelector(`#task-${tabName}-tab`);
            if (targetTab) {
                targetTab.classList.add('active');

                // If switching to history tab, render the history and reset scroll
                if (tabName === 'history') {
                    const form = document.getElementById('task-form');
                    const editingTaskId = form?.dataset.editingTaskId;
                    if (editingTaskId) {
                        renderTaskHistory(parseInt(editingTaskId));
                    }

                    // Reset scroll to top when switching to history tab
                    const historyContainer = document.querySelector('.task-history-container');
                    if (historyContainer) {
                        historyContainer.scrollTop = 0;
                    }
                }
            }
        });
    });
}

// Render history for a specific task
function renderTaskHistory(taskId) {
    if (!window.historyService) return;

    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    let history = window.historyService.getEntityHistory('task', taskId);
    const timeline = document.getElementById('task-history-timeline');
    const emptyState = document.getElementById('task-history-empty');

    if (!timeline) return;

    if (history.length === 0) {
        timeline.innerHTML = '';
        if (emptyState) {
            emptyState.style.display = 'flex';
            emptyState.style.flexDirection = 'column';
            emptyState.style.alignItems = 'center';
            emptyState.style.padding = '48px 20px';
        }
        return;
    }

    if (emptyState) emptyState.style.display = 'none';

    // Apply sort order based on settings
    if (settings.historySortOrder === 'oldest') {
        history = [...history].reverse();
    }

    // Add sort toggle button if not already present
    let sortButton = document.getElementById('history-sort-toggle');
    if (!sortButton) {
        const historyContainer = timeline.parentElement;
        sortButton = document.createElement('button');
        sortButton.id = 'history-sort-toggle';
        sortButton.className = 'history-sort-toggle';
        sortButton.innerHTML = settings.historySortOrder === 'newest' ? t('history.sort.newest') : t('history.sort.oldest');
        sortButton.onclick = () => toggleHistorySortOrder('task', taskId);
        historyContainer.insertBefore(sortButton, timeline);
    } else {
        sortButton.innerHTML = settings.historySortOrder === 'newest' ? t('history.sort.newest') : t('history.sort.oldest');
    }

    // Render history entries
    timeline.innerHTML = history.map(entry => renderHistoryEntryInline(entry)).join('');
}

// Toggle history sort order
function toggleHistorySortOrder(entityType, entityId) {
    settings.historySortOrder = settings.historySortOrder === 'newest' ? 'oldest' : 'newest';
    saveSettings();

    // Re-render based on entity type
    if (entityType === 'task' && entityId) {
        renderTaskHistory(entityId);
    } else if (entityType === 'project' && entityId) {
        renderProjectHistory(entityId);
    } else {
        // Fallback: try to detect context
        const form = document.getElementById('task-form');
        const editingTaskId = form?.dataset.editingTaskId;
        if (editingTaskId) {
            renderTaskHistory(parseInt(editingTaskId));
        }

        const projectDetailsEl = document.getElementById('project-details');
        if (projectDetailsEl && projectDetailsEl.classList.contains('active')) {
            const projectIdMatch = projectDetailsEl.innerHTML.match(/renderProjectHistory\((\d+)\)/);
            if (projectIdMatch) {
                renderProjectHistory(parseInt(projectIdMatch[1]));
            }
        }
    }
}

// Render history for a specific project
function renderProjectHistory(projectId) {
    if (!window.historyService) return;

    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    let history = window.historyService.getEntityHistory('project', projectId);
    const timeline = document.getElementById(`project-history-timeline-${projectId}`);
    const emptyState = document.getElementById(`project-history-empty-${projectId}`);

    if (!timeline) return;

    if (history.length === 0) {
        timeline.innerHTML = '';
        if (emptyState) {
            emptyState.style.display = 'flex';
            emptyState.style.flexDirection = 'column';
            emptyState.style.alignItems = 'center';
            emptyState.style.padding = '48px 20px';
        }
        return;
    }

    if (emptyState) emptyState.style.display = 'none';

    // Apply sort order based on settings
    if (settings.historySortOrder === 'oldest') {
        history = [...history].reverse();
    }

    // Add sort toggle button if not already present
    let sortButton = document.getElementById(`project-history-sort-toggle-${projectId}`);
    if (!sortButton) {
        const historyContainer = timeline.parentElement;
        sortButton = document.createElement('button');
        sortButton.id = `project-history-sort-toggle-${projectId}`;
        sortButton.className = 'history-sort-toggle';
        sortButton.innerHTML = settings.historySortOrder === 'newest' ? t('history.sort.newest') : t('history.sort.oldest');
        sortButton.onclick = () => toggleHistorySortOrder('project', projectId);
        historyContainer.insertBefore(sortButton, timeline);
    } else {
        sortButton.innerHTML = settings.historySortOrder === 'newest' ? t('history.sort.newest') : t('history.sort.oldest');
    }

    // Render history entries
    timeline.innerHTML = history.map(entry => renderHistoryEntryInline(entry)).join('');
}

// Render a single history entry (inline version - simplified)
function renderHistoryEntryInline(entry) {
    const actionIcons = {
        created: '✨',
        updated: '',
        deleted: '🗑️'
    };

    const actionColors = {
        created: 'var(--accent-green)',
        updated: 'var(--text-secondary)',
        deleted: 'var(--accent-red)'
    };

    const time = new Date(entry.timestamp).toLocaleString(getLocale(), {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });

    const changes = Object.entries(entry.changes);
    const changeCount = changes.length;

    // Skip ghost 'updated' entries where all changes net to zero visible difference
    if (entry.action === 'updated') {
        const hasVisible = changeCount > 0 && changes.some(([field, { before, after }]) => {
            if (field === 'attachments') {
                const ba = Array.isArray(before) ? before : [];
                const aa = Array.isArray(after) ? after : [];
                const bk = new Set(ba.map(a => a.fileKey || a.url || a.name));
                const ak = new Set(aa.map(a => a.fileKey || a.url || a.name));
                return aa.some(a => !bk.has(a.fileKey || a.url || a.name)) || ba.some(a => !ak.has(a.fileKey || a.url || a.name));
            }
            if (field === 'tags') {
                const bs = new Set(Array.isArray(before) ? before : []);
                const as2 = Array.isArray(after) ? after : [];
                return as2.some(t => !bs.has(t)) || (Array.isArray(before) ? before : []).some(t => !(new Set(as2)).has(t));
            }
            return true;
        });
        if (!hasVisible) return '';
    }

    // Get summary of changes for display
    const fieldLabels = {
        title: t('history.field.title'),
        name: t('history.field.name'),
        description: t('history.field.description'),
        status: t('history.field.status'),
        priority: t('history.field.priority'),
        category: t('history.field.category'),
        startDate: t('history.field.startDate'),
        endDate: t('history.field.endDate'),
        link: t('history.field.link'),
        task: t('history.field.task'),
        projectId: t('history.field.projectId'),
        tags: t('history.field.tags'),
        attachments: t('history.field.attachments')
    };

    return `
        <div class="history-entry-inline">
            <div class="history-entry-header-inline">
                ${actionIcons[entry.action] ? `<span class="history-action-icon" style="color: ${actionColors[entry.action]};">${actionIcons[entry.action]}</span>` : ''}
                ${entry.action === 'created' || entry.action === 'deleted' ? `<span class="history-action-label-inline" style="color: ${actionColors[entry.action]};">${entry.action.charAt(0).toUpperCase() + entry.action.slice(1)}</span>` : ''}
                <span class="history-time-inline">${time}</span>
            </div>
            ${buildHistoryActorLineHtml(entry.meta || {})}

            ${changeCount > 0 ? (() => {
                const renderedChanges = changes.map(([field, { before, after }]) => {
                        const label = fieldLabels[field] || field;

                        if (field === 'link' || field === 'task') {
                            const action = after && typeof after === 'object' ? after.action : '';
                            const title = after && typeof after === 'object' ? (after.title || after.id || t('tasks.table.task')) : String(after);
                            const linkType = after && typeof after === 'object' ? after.linkType : null;
                            const isRemoved = action === 'removed';
                            const emoji = isRemoved ? '❌' : '➕';
                            const typeLabel = linkType || (after && typeof after === 'object' && after.entity === 'project' ? t('history.entity.project') : t('history.entity.task'));
                            return `
                                <div class="history-change-compact history-change-compact--single">
                                    <span class="change-field-label">${label}:</span>
                                    <span>${emoji} ${isRemoved ? t('history.link.removed') : t('history.link.added')} <span style="opacity:0.7;margin-right:4px;">${escapeHtml(typeLabel)}</span>"${escapeHtml(title)}"</span>
                                </div>
                            `;
                        }

                        // Tags: show only added/removed tag names
                        if (field === 'tags') {
                            const beforeArr = Array.isArray(before) ? before : [];
                            const afterArr = Array.isArray(after) ? after : [];
                            const beforeSet = new Set(beforeArr);
                            const afterSet = new Set(afterArr);
                            const added = afterArr.filter(tag => !beforeSet.has(tag));
                            const removed = beforeArr.filter(tag => !afterSet.has(tag));
                            if (!added.length && !removed.length) return '';
                            const lines = [
                                ...added.map(tag => {
                                    const tagColor = getTagColor(tag);
                                    return `➕ ${t('history.link.added')} <span style="${getTintedTagStyle(tagColor)}padding:1px 6px;border-radius:3px;font-size:10px;font-weight:500;margin-left:6px;">${escapeHtml(tag.toUpperCase())}</span>`;
                                }),
                                ...removed.map(tag => {
                                    const tagColor = getTagColor(tag);
                                    return `❌ ${t('history.link.removed')} <span style="${getTintedTagStyle(tagColor)}padding:1px 6px;border-radius:3px;font-size:10px;font-weight:500;opacity:0.6;margin-left:6px;">${escapeHtml(tag.toUpperCase())}</span>`;
                                })
                            ].join('<br>');
                            return `
                                <div class="history-change-compact history-change-compact--single">
                                    <span class="change-field-label">${label}:</span>
                                    <span style="line-height:2;">${lines}</span>
                                </div>
                            `;
                        }

                        // Attachments: show only added/removed filenames
                        if (field === 'attachments') {
                            const beforeArr = Array.isArray(before) ? before : [];
                            const afterArr = Array.isArray(after) ? after : [];
                            const beforeKeys = new Set(beforeArr.map(a => a.fileKey || a.url || a.name));
                            const afterKeys = new Set(afterArr.map(a => a.fileKey || a.url || a.name));
                            const added = afterArr.filter(a => !beforeKeys.has(a.fileKey || a.url || a.name));
                            const removed = beforeArr.filter(a => !afterKeys.has(a.fileKey || a.url || a.name));
                            if (!added.length && !removed.length) return '';
                            const lines = [
                                ...added.map(a => `➕ ${t('history.link.added')} ${escapeHtml(a.name)}`),
                                ...removed.map(a => `❌ ${t('history.link.removed')} ${escapeHtml(a.name)}`)
                            ].join('<br>');
                            return `
                                <div class="history-change-compact history-change-compact--single">
                                    <span class="change-field-label">${label}:</span>
                                    <span style="line-height:1.7;">${lines}</span>
                                </div>
                            `;
                        }

                        // Special handling for description - show full diff
                        if (field === 'description') {
                            const oldText = before ? before.replace(/<[^>]*>/g, '').trim() : '';
                            const newText = after ? after.replace(/<[^>]*>/g, '').trim() : '';

                            return `
                                <div class="history-change-description">
                                    <div class="change-field-label">${label}:</div>
                                    <div class="description-diff">
                                        ${oldText ? `<div class="description-before"><s>${escapeHtml(oldText)}</s></div>` : `<div class="description-before"><em style="opacity: 0.6;">${t('history.value.empty')}</em></div>`}
                                        ${newText ? `<div class="description-after">${escapeHtml(newText)}</div>` : `<div class="description-after"><em style="opacity: 0.6;">${t('history.value.empty')}</em></div>`}
                                    </div>
                                </div>
                            `;
                        }

                        // Standard handling for other fields
                        const beforeValue = formatChangeValueCompact(field, before, true, entry.entityType);
                        const afterValue = formatChangeValueCompact(field, after, false, entry.entityType);

                        return `
                            <div class="history-change-compact">
                                <span class="change-field-label">${label}:</span>
                                ${beforeValue !== null ? `<span class="change-before-compact">${beforeValue}</span>` : '<span class="change-null">—</span>'}
                                <span class="change-arrow-compact">${t('history.change.arrow')}</span>
                                ${afterValue !== null ? `<span class="change-after-compact">${afterValue}</span>` : '<span class="change-null">—</span>'}
                            </div>
                        `;
                }).filter(s => s.trim() !== '');
                if (!renderedChanges.length) return '';
                return `<div class="history-changes-compact">${renderedChanges.join('')}</div>`;
            })() : ''}
        </div>
    `;
}

// Compact format for inline display
function formatChangeValueCompact(field, value, isBeforeValue = false, entityType = 'task') {
    if (value === null || value === undefined) return null;
    if (value === '') return `<em style="opacity: 0.7;">${t('history.value.empty')}</em>`;

    // Special formatting for different field types
    if (field === 'startDate' || field === 'endDate') {
        const dateStr = formatDate(value);
        return isBeforeValue ? `<span style="opacity: 0.7;">${dateStr}</span>` : dateStr;
    }

    if (field === 'link' || field === 'task') {
        const action = value && typeof value === 'object' ? value.action : '';
        const title = value && typeof value === 'object' ? (value.title || value.id || t('tasks.table.task')) : String(value);
        const linkType = value && typeof value === 'object' ? value.linkType : null;
        const entity = (value && typeof value === 'object' ? value.entity : null) || 'task';
        const isRemoved = action === 'removed';
        const emoji = isRemoved ? '❌' : '➕';
        const typeLabel = linkType || (entity === 'project' ? t('history.entity.project') : t('history.entity.task'));
        return `${emoji} <span style="opacity:0.7;margin-right:4px;">${escapeHtml(typeLabel)}</span>"${escapeHtml(title)}"`;
    }

    if (field === 'status') {
        if (entityType === 'project') {
            // Project status: match project-status-badge chip style
            const statusLabel = getProjectStatusLabel(value).toUpperCase();
            const projectStatusStyles = {
                backlog: 'background: rgba(148, 163, 184, 0.2); color: var(--text-muted);',
                planning: 'background: rgba(24, 111, 149, 0.15); color: var(--accent-teal);',
                active: 'background: rgba(59, 130, 246, 0.2); color: var(--accent-blue);',
                completed: 'background: rgba(16, 185, 129, 0.2); color: var(--accent-green);',
                'on-hold': 'background: rgba(245, 158, 11, 0.2); color: var(--accent-amber);'
            };
            const style = projectStatusStyles[value] || 'background: rgba(107, 114, 128, 0.2); color: var(--text-muted);';
            return `<span style="${style} padding: 5px 14px; border-radius: 999px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; display: inline-block;">${escapeHtml(statusLabel)}</span>`;
        }
        // Task status badge
        const statusLabel = (getStatusLabel(value)).toUpperCase();
        const statusColors = {
            backlog: '#4B5563',
            todo: '#186f95',
            progress: 'var(--accent-blue)',
            review: 'var(--accent-amber)',
            done: 'var(--accent-green)'
        };
        const bgColor = statusColors[value] || '#4B5563';
        return `<span style="background: ${bgColor}; color: white; padding: 2px 8px; border-radius: 12px; font-size: 11.5px; font-weight: 600; text-transform: uppercase;">${escapeHtml(statusLabel)}</span>`;
    }

    if (field === 'priority') {
        // Use priority badge with proper colors - use border-radius: 6px to match priority-pill
        const priorityLabel = getPriorityLabel(value).toUpperCase();
        const priorityStyles = {
            high: 'background: rgba(239, 68, 68, 0.2); color: #ef4444;',
            medium: 'background: rgba(245, 158, 11, 0.2); color: #f59e0b;',
            low: 'background: rgba(16, 185, 129, 0.2); color: #10b981;'
        };
        const style = priorityStyles[value] || 'background: rgba(107, 114, 128, 0.2); color: #6b7280;';
        return `<span style="${style} padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; text-transform: uppercase;">${escapeHtml(priorityLabel)}</span>`;
    }

    if (field === 'projectId') {
        if (!value) return `<em style="opacity: 0.7;">${t('tasks.noProject')}</em>`;
        const project = projects.find(p => p.id === value);
        const projectName = project ? escapeHtml(project.name) : `#${value}`;
        return isBeforeValue ? `<span style="opacity: 0.7;">${projectName}</span>` : projectName;
    }

    if (field === 'tags') {
        // NO opacity for tags - show ALL tags, no truncation
        if (!Array.isArray(value) || value.length === 0) return `<em style="opacity: 0.7;">${t('history.value.none')}</em>`;
        return value.map(tag => {
            const tagColor = getTagColor(tag);
            return `<span style="${getTintedTagStyle(tagColor)} padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 500;">${escapeHtml(tag.toUpperCase())}</span>`;
        }).join(' ');
    }

    if (field === 'attachments') {
        if (!Array.isArray(value) || value.length === 0) return `<em style="opacity: 0.7;">${t('history.value.none')}</em>`;
        const names = value.map(att => escapeHtml(att.name || '?')).join(', ');
        return isBeforeValue ? `<span style="opacity: 0.7;">${names}</span>` : names;
    }

    if (field === 'description') {
        const text = value.replace(/<[^>]*>/g, '').trim();
        const shortText = text.length > 50 ? escapeHtml(text.substring(0, 50)) + '...' : escapeHtml(text) || `<em>${t('history.value.empty')}</em>`;
        return isBeforeValue ? `<span style="opacity: 0.7;">${shortText}</span>` : shortText;
    }

    // Default text fields - apply opacity for before values
    const escapedValue = escapeHtml(String(value));
    return isBeforeValue ? `<span style="opacity: 0.7;">${escapedValue}</span>` : escapedValue;
}

function toggleHistoryEntryInline(entryId) {
    const details = document.getElementById(`history-details-inline-${entryId}`);
    const btn = document.querySelector(`[data-action="toggleHistoryEntryInline"][data-param="${entryId}"]`);
    const icon = btn?.querySelector('.expand-icon-inline');

    if (details) {
        const isVisible = details.style.display !== 'none';
        details.style.display = isVisible ? 'none' : 'block';
        if (icon) {
            icon.textContent = isVisible ? '▼' : '▲';
        }
    }
}

// === Helper Functions for History Rendering ===

function renderChanges(changes) {
    const fieldLabels = {
        title: t('history.field.title'),
        name: t('history.field.name'),
        description: t('history.field.description'),
        status: t('history.field.status'),
        priority: t('history.field.priority'),
        category: t('history.field.category'),
        startDate: t('history.field.startDate'),
        endDate: t('history.field.endDate'),
        projectId: t('history.field.projectId'),
        tags: t('history.field.tags'),
        attachments: t('history.field.attachments')
    };

    return Object.entries(changes).map(([field, { before, after }]) => {
        const label = fieldLabels[field] || field;

        // Tags: show only the diff (added/removed tags), not full lists
        if (field === 'tags') {
            const beforeArr = Array.isArray(before) ? before : [];
            const afterArr = Array.isArray(after) ? after : [];
            const beforeSet = new Set(beforeArr);
            const afterSet = new Set(afterArr);
            const added = afterArr.filter(tag => !beforeSet.has(tag));
            const removed = beforeArr.filter(tag => !afterSet.has(tag));
            if (!added.length && !removed.length) return '';
            const lines = [
                ...added.map(tag => {
                    const tagColor = getTagColor(tag);
                    return `➕ ${t('history.link.added')} <span style="${getTintedTagStyle(tagColor)}padding:1px 6px;border-radius:3px;font-size:10px;font-weight:500;">${escapeHtml(tag.toUpperCase())}</span>`;
                }),
                ...removed.map(tag => {
                    const tagColor = getTagColor(tag);
                    return `❌ ${t('history.link.removed')} <span style="${getTintedTagStyle(tagColor)}padding:1px 6px;border-radius:3px;font-size:10px;font-weight:500;opacity:0.6;">${escapeHtml(tag.toUpperCase())}</span>`;
                })
            ].join('<br>');
            return `
                <div class="history-change">
                    <div class="history-change-field">${label}</div>
                    <div class="history-change-values" style="display:block;">
                        <div style="line-height:2;">${lines}</div>
                    </div>
                </div>
            `;
        }

        // Attachments: show only the diff (added/removed filenames), not full lists
        if (field === 'attachments') {
            const beforeArr = Array.isArray(before) ? before : [];
            const afterArr = Array.isArray(after) ? after : [];
            const beforeNames = new Set(beforeArr.map(a => a.name));
            const afterNames = new Set(afterArr.map(a => a.name));
            const added = afterArr.filter(a => !beforeNames.has(a.name));
            const removed = beforeArr.filter(a => !afterNames.has(a.name));
            if (!added.length && !removed.length) return '';
            const lines = [
                ...added.map(a => `➕ ${t('history.link.added')} ${escapeHtml(a.name)}`),
                ...removed.map(a => `❌ ${t('history.link.removed')} ${escapeHtml(a.name)}`)
            ].join('<br>');
            return `
                <div class="history-change">
                    <div class="history-change-field">${label}</div>
                    <div class="history-change-values" style="display:block;">
                        <div style="line-height:1.7;">${lines}</div>
                    </div>
                </div>
            `;
        }

        const beforeValue = formatChangeValue(field, before);
        const afterValue = formatChangeValue(field, after);

        return `
            <div class="history-change">
                <div class="history-change-field">${label}</div>
                <div class="history-change-values">
                    <div class="history-change-before">
                        ${beforeValue !== null ? `<span class="change-label">${t('history.change.beforeLabel')}</span> ${beforeValue}` : `<span class="change-label-null">${t('history.change.notSet')}</span>`}
                    </div>
                    <div class="history-change-arrow">${t('history.change.arrow')}</div>
                    <div class="history-change-after">
                        ${afterValue !== null ? `<span class="change-label">${t('history.change.afterLabel')}</span> ${afterValue}` : `<span class="change-label-null">${t('history.change.removed')}</span>`}
                    </div>
                </div>
            </div>
        `;
    }).join('');
}

function formatChangeValue(field, value) {
    if (value === null || value === undefined) return null;
    if (value === '') return `<em style="color: var(--text-muted);">${t('history.value.empty')}</em>`;

    if (field === 'link' || field === 'task') {
        const action = value && typeof value === 'object' ? value.action : '';
        const title = value && typeof value === 'object' ? (value.title || value.id || t('tasks.table.task')) : String(value);
        const linkType = value && typeof value === 'object' ? value.linkType : null;
        const entity = (value && typeof value === 'object' ? value.entity : null) || 'task';
        const isRemoved = action === 'removed';
        const emoji = isRemoved ? '❌' : '➕';
        const typeLabel = linkType || (entity === 'project' ? t('history.entity.project') : t('history.entity.task'));
        return `${emoji} <span style="opacity:0.7;margin-right:4px;">${escapeHtml(typeLabel)}</span>"${escapeHtml(title)}"`;
    }

    // Special formatting for different field types
    if (field === 'startDate' || field === 'endDate') {
        return formatDate(value);
    }

    if (field === 'projectId') {
        if (!value) return `<em style="color: var(--text-muted);">${t('tasks.noProject')}</em>`;
        const project = projects.find(p => p.id === value);
        return project ? escapeHtml(project.name) : t('history.project.fallback', { id: value });
    }

    if (field === 'tags') {
        if (!Array.isArray(value) || value.length === 0) {
            return `<em style="color: var(--text-muted);">${t('history.tags.none')}</em>`;
        }
        return value.map(tag => `<span class="history-tag">${escapeHtml(tag)}</span>`).join(' ');
    }

    if (field === 'attachments') {
        if (!Array.isArray(value) || value.length === 0) {
            return `<em style="color: var(--text-muted);">${t('history.attachments.none')}</em>`;
        }
        return value.map(att => escapeHtml(att.name || '?')).join(', ');
    }

    if (field === 'description') {
        // Strip HTML tags and truncate
        const text = value.replace(/<[^>]*>/g, '').trim();
        if (text.length > 100) {
            return escapeHtml(text.substring(0, 100)) + '...';
        }
        return escapeHtml(text) || `<em style="color: var(--text-muted);">${t('history.value.empty')}</em>`;
    }

    return escapeHtml(String(value));
}

// Old History page functions removed - now using inline history in modals

let feedbackItemToDelete = null;

function deleteFeedbackItem(id) {
    feedbackItemToDelete = id;
    document.getElementById('feedback-delete-modal').classList.add('active');
}

function closeFeedbackDeleteModal() {
    document.getElementById('feedback-delete-modal').classList.remove('active');
    feedbackItemToDelete = null;
}

async function confirmFeedbackDelete() {
    if (feedbackItemToDelete !== null) {
        const deleteId = feedbackItemToDelete;
        const deletedItem = feedbackItems.find(f => f.id === deleteId);
        
        // Remove from array immediately (optimistic update)
        feedbackItems = feedbackItems.filter(f => f.id !== deleteId);

        // Close modal and update UI INSTANTLY
        closeFeedbackDeleteModal();
        updateCounts();
        renderFeedback();
        
        // Cache immediately for instant persistence
        persistFeedbackCache();

        // Delete ONLY this item from D1 (not save entire array)
        deleteSingleFeedbackItem(deleteId).catch((error) => {
            console.error('Failed to delete feedback item:', error);
            // Rollback on failure
            if (deletedItem) {
                feedbackItems.push(deletedItem);
                updateCounts();
                renderFeedback();
                persistFeedbackCache();
            }
            showErrorNotification(t('error.deleteFeedbackFailed'));
        });
    }
}

// Delegated click handler for feedback checkbox only
document.addEventListener('click', function(e) {
    const checkbox = e.target.closest('.feedback-checkbox');
    if (checkbox) {
        const feedbackId = checkbox.dataset.feedbackId; // Keep as string to match D1 TEXT IDs
        if (feedbackId) {
            toggleFeedbackItem(feedbackId);
        }
    }
});

function editProjectTitle(projectId, currentName) {
    document.getElementById('project-title-display').style.display = 'none';
    document.getElementById('project-title-edit').style.display = 'flex';
    document.getElementById('project-title-input').focus();
    document.getElementById('project-title-input').select();
}

function saveProjectTitle(projectId) {
    const newTitle = document.getElementById('project-title-input').value.trim();
    if (newTitle) {
        updateProjectField(projectId, 'name', newTitle);
    } else {
        cancelProjectTitle();
    }
}

function cancelProjectTitle() {
    document.getElementById('project-title-display').style.display = 'inline';
    document.getElementById('project-title-edit').style.display = 'none';
}

function dismissKanbanTip() {
    document.getElementById('kanban-tip').style.display = 'none';
    localStorage.setItem('kanban-tip-dismissed', 'true');
}

function toggleProjectMenu(event) {
    event.stopPropagation();
    const menu = document.getElementById('project-options-menu');
    if (menu) {
        menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
    }
}

// Close project menu when clicking outside
document.addEventListener('click', function(e) {
    const menu = document.getElementById('project-options-menu');
    if (menu && !e.target.closest('#project-options-btn') && !e.target.closest('#project-options-menu')) {
        menu.style.display = 'none';
    }
});

// Check on page load if tip was dismissed
document.addEventListener('DOMContentLoaded', function() {
    if (localStorage.getItem('kanban-tip-dismissed') === 'true') {
        const tip = document.getElementById('kanban-tip');
        if (tip) tip.style.display = 'none';
    }
});

async function addAttachment() {
    const urlInput = document.getElementById('attachment-url');
    const nameInput = document.getElementById('attachment-name');
    const url = urlInput.value.trim();
    
    if (!url) {
        urlInput.style.border = '2px solid var(--accent-red, #ef4444)';
        urlInput.placeholder = 'URL is required';
        urlInput.focus();
        
        // Reset border after 2 seconds
        setTimeout(() => {
            urlInput.style.border = '';
            urlInput.placeholder = 'Paste link (Drive, Dropbox, etc.)';
        }, 2000);
        return;
    }
    
    // Reset any error styling
    urlInput.style.border = '';
    
    const taskId = document.getElementById('task-form').dataset.editingTaskId;
    
    // Use custom name if provided, otherwise auto-detect
    let name = nameInput.value.trim() || 'Attachment';
    let icon = '📁';
    
    if (!nameInput.value.trim()) {
        // Only auto-detect if no custom name provided
        try {
            const urlObj = new URL(url);
            let path = urlObj.pathname.toLowerCase();

            if (urlObj.hostname.includes("docs.google.com")) {
                if (path.includes("/document/")) {
                    name = t('tasks.attachments.googleDoc');
                    icon = "📄";
                } else if (path.includes("/spreadsheets/")) {
                    name = t('tasks.attachments.googleSheet');
                    icon = "📊";
                } else if (path.includes("/presentation/")) {
                    name = t('tasks.attachments.googleSlides');
                    icon = "📑";
                } else {
                    name = t('tasks.attachments.googleDriveFile');
                    icon = "📄";
                }
            } else if (urlObj.hostname.includes("drive.google.com")) {
                name = t('tasks.attachments.googleDriveFile');
                icon = "📄";
            } else if (path.endsWith(".pdf")) {
                name = path.split("/").pop() || t('tasks.attachments.pdf');
                icon = "📕";
            } else if (path.endsWith(".doc") || path.endsWith(".docx")) {
                name = path.split("/").pop() || t('tasks.attachments.word');
                icon = "📝";
            } else if (path.endsWith(".xls") || path.endsWith(".xlsx")) {
                name = path.split("/").pop() || t('tasks.attachments.excel');
                icon = "📊";
            } else if (path.endsWith(".ppt") || path.endsWith(".pptx")) {
                name = path.split("/").pop() || t('tasks.attachments.powerpoint');
                icon = "📑";
            } else {
                let lastPart = path.split("/").pop();
                name = lastPart && lastPart.length > 0 ? lastPart : urlObj.hostname;
                icon = "📁";
            }
        } catch (e) {
            name = url.substring(0, 30);
            icon = "📁";
        }
    }

    // Use a consistent icon for URL attachments
    icon = '🌐';
    const attachment = { name, icon, type: 'link', url, addedAt: new Date().toISOString() };

    if (taskId) {
        const task = tasks.find(t => t.id === parseInt(taskId));
        if (!task) return;
        if (!task.attachments) task.attachments = [];
        const oldTaskCopy = JSON.parse(JSON.stringify(task));
        task.attachments.push(attachment);
        if (window.historyService) {
            window.historyService.recordTaskUpdated(oldTaskCopy, task);
        }
        renderAttachments(task.attachments);

        // Save in background
        task.updatedAt = new Date().toISOString();
        saveTaskUpdateData(parseInt(taskId), { attachments: task.attachments, updatedAt: task.updatedAt }).catch(error => {
            console.error('Failed to save attachment:', error);
            showErrorNotification(t('error.attachmentSaveFailed'));
        });
    } else {
        tempAttachments.push(attachment);
        renderAttachments(tempAttachments);
    }

    urlInput.value = '';
    nameInput.value = '';
}

async function renderAttachments(attachments) {
    const filesContainer = document.getElementById('attachments-files-list');
    const linksContainer = document.getElementById('attachments-links-list');

    if (filesContainer && linksContainer) {
        await renderAttachmentsSeparated(attachments, filesContainer, linksContainer);
        return;
    }

    const container = document.getElementById('attachments-list');
    if (!container) return;

    if (!attachments || attachments.length === 0) {
        container.innerHTML = `<div style="color: var(--text-muted); font-size: 13px; padding: 8px 0;">${t('tasks.attachments.none')}</div>`;
        return;
    }

    const getUrlHost = (rawUrl) => {
        if (!rawUrl) return '';
        try {
            const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(rawUrl);
            const url = new URL(hasScheme ? rawUrl : `https://${rawUrl}`);
            return (url.host || rawUrl).replace(/^www\./, '');
        } catch {
            return rawUrl;
        }
    };

    // First render with placeholders
    container.innerHTML = attachments.map((att, index) => {
        const sizeInKB = att.size ? Math.round(att.size / 1024) : 0;
        const sizeText = sizeInKB > 1024 ? `${(sizeInKB/1024).toFixed(1)} MB` : `${sizeInKB} KB`;

        // New file system (with fileKey)
        if (att.type === 'file' && att.fileKey) {
            if (att._uploading) {
                return `
                <div class="attachment-item attachment-item--uploading" aria-busy="true">
                    <span class="attachment-thumb" aria-hidden="true">${att.icon}</span>
                    <span class="attachment-name">${escapeHtml(att.name)} <span class="attachment-meta">&middot; ${sizeText}</span></span>
                </div>
            `;}
            const isImage = att.fileType === 'image';
            // Show placeholder, will load image if it's an image
            const thumbnailHtml = `<div id="thumbnail-${att.fileKey}" class="attachment-thumb" aria-hidden="true">${att.icon}</div>`;
            const primaryAction = isImage ? 'viewFile' : 'downloadFileAttachment';
            const primaryParams = isImage
                ? `data-param="${att.fileKey}" data-param2="${escapeHtml(att.name)}" data-param3="${att.fileType}"`
                : `data-param="${att.fileKey}" data-param2="${escapeHtml(att.name)}" data-param3="${att.mimeType}"`;

            return `
                <div class="attachment-item">
                    <button type="button" class="attachment-link" data-action="${primaryAction}" ${primaryParams}>
                        ${thumbnailHtml}
                        <span class="attachment-name">${escapeHtml(att.name)} <span class="attachment-meta">&middot; ${sizeText}</span></span>
                    </button>
                    <button type="button" class="attachment-remove" data-action="removeAttachment" data-param="${index}" aria-label="${t('tasks.attachments.remove')}" title="${t('tasks.attachments.removeTitle')}">&times;</button>
                </div>
            `;
        }

        // Legacy: Old inline Base64 images (backward compatibility) - still show thumbnail since data is already in memory
        else if (att.type === 'image' && att.data) {
            return `
                <div class="attachment-item">
                    <button type="button" class="attachment-link" data-action="viewImageLegacy" data-param="${att.data}" data-param2="${escapeHtml(att.name)}">
                        <span class="attachment-thumb" aria-hidden="true"><img src="${att.data}" alt=""></span>
                        <span class="attachment-name">${escapeHtml(att.name)} <span class="attachment-meta">&middot; ${sizeText}</span></span>
                    </button>
                    <button type="button" class="attachment-remove" data-action="removeAttachment" data-param="${index}" aria-label="${t('tasks.attachments.remove')}" title="${t('tasks.attachments.removeTitle')}">&times;</button>
                </div>
            `;
        }

        // URL attachment
        else {
            return `
                <div class="attachment-item" style="display: flex; align-items: center; gap: 12px; padding: 10px; background: var(--bg-tertiary); border-radius: 8px; margin-bottom: 8px; border: 1px solid var(--border);">
                    <div style="width: 40px; height: 40px; background: transparent; border-radius: 0; display: flex; align-items: center; justify-content: center; font-size: 25px; line-height: 1;">🌐</div>
                    <div style="flex: 1; min-width: 0;">
                        <div style="font-size: 14px; font-weight: 500; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(att.name)}</div>
                        <div style="font-size: 12px; color: var(--text-muted); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: none;">${escapeHtml(att.url)}</div>
                    </div>
                    <div style="display: flex; gap: 6px; align-items: center;">
                        <button type="button" data-action="openUrlAttachment" data-param="${encodeURIComponent(att.url)}" style="padding: 0 12px; background: var(--bg-secondary); color: var(--text-primary); border: 1px solid var(--border); border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500; height: 32px; line-height: 1; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; appearance: none; -webkit-appearance: none;">${t('tasks.attachments.open')}</button>
                        <button type="button" class="attachment-remove" data-action="removeAttachment" data-param="${index}" aria-label="${t('tasks.attachments.removeLink')}" title="${t('tasks.attachments.removeTitle')}">&times;</button>
                    </div>
                </div>
            `;
        }
    }).join('');

    // Load image thumbnails via raw URL — browser fetches asynchronously, no JS round-trip
    for (const att of attachments) {
        if (att.type === 'file' && att.fileKey && att.fileType === 'image') {
            const thumbnailEl = document.getElementById(`thumbnail-${att.fileKey}`);
            if (thumbnailEl) {
                const img = document.createElement('img');
                img.alt = escapeHtml(att.name || '');
                img.onerror = () => { thumbnailEl.innerHTML = att.icon || '📄'; };
                img.src = buildFileReadUrl(att.fileKey);
                thumbnailEl.innerHTML = '';
                thumbnailEl.appendChild(img);
            }
        }
    }
}

async function renderAttachmentsSeparated(attachments, filesContainer, linksContainer) {
    if (!filesContainer || !linksContainer) return;

    const indexed = (attachments || []).map((att, index) => ({ att, index }));

    const fileItems = indexed.filter(({ att }) => {
        if (!att) return false;
        if (att.type === 'file') return true;
        if (att.fileKey) return true;
        if (att.type === 'image' && att.data) return true;
        return false;
    }).sort((a, b) => {
        // Sort file attachments alphabetically by name
        const nameA = a.att.name || '';
        const nameB = b.att.name || '';
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    const linkItems = indexed.filter(({ att }) => {
        if (!att) return false;
        if (att.type === 'link') return true;
        if (att.url && att.type !== 'file') return true; // backward compat for old link objects
        return false;
    }).sort((a, b) => {
        // Sort web links alphabetically by name
        const nameA = a.att.name || '';
        const nameB = b.att.name || '';
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });

    const getUrlHost = (rawUrl) => {
        if (!rawUrl) return '';
        try {
            const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(rawUrl);
            const url = new URL(hasScheme ? rawUrl : `https://${rawUrl}`);
            return (url.host || rawUrl).replace(/^www\./, '');
        } catch {
            return rawUrl;
        }
    };

    // Build gallery of image attachments (same sorted order as fileItems)
    currentImageGallery = fileItems
        .map(({ att }) => att)
        .filter(att =>
            (att.type === 'file' && att.fileKey && att.fileType === 'image') ||
            (att.type === 'image' && att.data)
        );

    const fileRows = fileItems.map(({ att, index }) => {
        const sizeInKB = att.size ? Math.round(att.size / 1024) : 0;
        const sizeText = sizeInKB > 1024 ? `${(sizeInKB / 1024).toFixed(1)} MB` : `${sizeInKB} KB`;

        if (att.type === 'file' && att.fileKey) {
            if (att._uploading) {
                return `
                <div class="attachment-item attachment-item--uploading" aria-busy="true">
                    <span class="attachment-thumb" aria-hidden="true">${att.icon}</span>
                    <span class="attachment-name">${escapeHtml(att.name)} <span class="attachment-meta">&middot; ${sizeText}</span></span>
                </div>
            `;}
            const isImage = att.fileType === 'image';
            const thumbnailHtml = `<div id="thumbnail-${att.fileKey}" class="attachment-thumb" aria-hidden="true">${att.icon}</div>`;
            const primaryAction = isImage ? 'viewFile' : 'downloadFileAttachment';
            const galleryIndex = isImage ? currentImageGallery.indexOf(att) : -1;
            const primaryParams = isImage
                ? `data-param="${att.fileKey}" data-param2="${escapeHtml(att.name)}" data-param3="${att.fileType}" data-param4="${galleryIndex}"`
                : `data-param="${att.fileKey}" data-param2="${escapeHtml(att.name)}" data-param3="${att.mimeType}"`;

            return `
                <div class="attachment-item">
                    <button type="button" class="attachment-link" data-action="${primaryAction}" ${primaryParams}>
                        ${thumbnailHtml}
                        <span class="attachment-name">${escapeHtml(att.name)} <span class="attachment-meta">&middot; ${sizeText}</span></span>
                    </button>
                    <button type="button" class="attachment-remove" data-action="removeAttachment" data-param="${index}" aria-label="${t('tasks.attachments.remove')}" title="${t('tasks.attachments.removeTitle')}">&times;</button>
                </div>
            `;
        }

        if (att.type === 'image' && att.data) {
            const galleryIndex = currentImageGallery.indexOf(att);
            return `
                <div class="attachment-item">
                    <button type="button" class="attachment-link" data-action="viewImageLegacy" data-param="${att.data}" data-param2="${escapeHtml(att.name)}" data-param3="${galleryIndex}">
                        <span class="attachment-thumb" aria-hidden="true"><img src="${att.data}" alt=""></span>
                        <span class="attachment-name">${escapeHtml(att.name)} <span class="attachment-meta">&middot; ${sizeText}</span></span>
                    </button>
                    <button type="button" class="attachment-remove" data-action="removeAttachment" data-param="${index}" aria-label="${t('tasks.attachments.remove')}" title="${t('tasks.attachments.removeTitle')}">&times;</button>
                </div>
            `;
        }

        return '';
    }).filter(Boolean).join('');

    const linkRows = linkItems.map(({ att, index }) => `
        <div class="attachment-item" style="display: flex; align-items: center; gap: 12px; padding: 10px; border-radius: 8px; margin-bottom: 8px; border: 1px solid var(--border);">
            <div style="width: 40px; height: 40px; background: transparent; border-radius: 0; display: flex; align-items: center; justify-content: center; font-size: 25px; line-height: 1;">🌐</div>
            <div style="flex: 1; min-width: 0;">
                <div style="font-size: 14px; font-weight: 500; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(att.name)}</div>
                <div style="font-size: 12px; color: var(--text-muted); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: none;">${escapeHtml(att.url)}</div>
            </div>
            <div style="display: flex; gap: 6px; align-items: center;">
                <button type="button" data-action="openUrlAttachment" data-param="${encodeURIComponent(att.url)}" style="padding: 0 12px; background: var(--bg-secondary); color: var(--text-primary); border: 1px solid var(--border); border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500; height: 32px; line-height: 1; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; appearance: none; -webkit-appearance: none;">${t('tasks.attachments.open')}</button>
                <button type="button" class="attachment-remove" data-action="removeAttachment" data-param="${index}" aria-label="${t('tasks.attachments.removeLink')}" title="${t('tasks.attachments.removeTitle')}">&times;</button>
            </div>
        </div>
    `).join('');

    // Get dependencies and other links for current task
    const taskForm = document.getElementById('task-form');
    const currentTaskId = taskForm ? parseInt(taskForm.dataset.editingTaskId) : null;
    let dependencyRows = '';
    let taskLinkRows = '';
    
    if (currentTaskId) {
        const currentTask = tasks.find(t => t.id === currentTaskId);
        
        // Render dependencies (depends_on) - sorted alphabetically by task title
        const prerequisites = getPrerequisites(currentTaskId, dependencies);
        const sortedPrerequisites = [...prerequisites].sort((a, b) => {
            const taskA = tasks.find(t => t.id === a);
            const taskB = tasks.find(t => t.id === b);
            if (!taskA || !taskB) return 0;
            return taskA.title.localeCompare(taskB.title, undefined, { sensitivity: 'base' });
        });
        
        dependencyRows = sortedPrerequisites.map(prereqId => {
            const prereqTask = tasks.find(t => t.id === prereqId);
            if (!prereqTask) return '';
            
            return `
                <div class="attachment-item" style="display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 6px; margin-bottom: 6px; border: 1px solid var(--border); min-height: 0;">
                    <div style="width: 24px; height: 24px; background: transparent; border-radius: 0; display: flex; align-items: center; justify-content: center; font-size: 18px; line-height: 1; flex-shrink: 0;">🔗</div>
                    <div style="flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px;">
                        <div style="font-size: 13px; font-weight: 500; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; line-height: 1.3; flex: 1; min-width: 0;">
                            ${t('tasks.links.dependsOn')} → ${t('tasks.title')} #${prereqTask.id}: ${escapeHtml(prereqTask.title)}
                        </div>
                        <span class="priority-pill priority-${prereqTask.priority}" style="flex-shrink: 0; font-size: 10px; padding: 3px 8px; min-width: 60px; text-align: center; height: 22px; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box;">${getPriorityLabel(prereqTask.priority).toUpperCase()}</span>
                        <span class="status-badge ${prereqTask.status}" style="flex-shrink: 0; font-size: 10px; min-width: 85px; text-align: center; height: 22px; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; padding: 3px 8px;">${getStatusLabel(prereqTask.status).toUpperCase()}</span>
                    </div>
                    <div style="display: flex; gap: 2px; align-items: center; flex-shrink: 0;">
                        <button type="button" data-action="openTaskDetails" data-param="${prereqTask.id}" data-stop-propagation="true" class="link-open-btn" title="${t('tasks.links.openTask')}" aria-label="${t('tasks.links.openTask')}">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                        </button>
                        <button type="button" class="attachment-remove link-remove-btn" data-action="removeDependency" data-param="${prereqId}" aria-label="${t('tasks.links.removeDependency')}" title="${t('tasks.links.removeDependency')}">&times;</button>
                    </div>
                </div>
            `;
        }).filter(Boolean).join('');
        
        // Render other task links (blocks, is_blocked_by, relates_to)
        // GROUPED BY TYPE for better space efficiency
        if (currentTask && currentTask.links && Array.isArray(currentTask.links)) {
            // Group links by type
            const groupedLinks = {
                'blocks': [],
                'is_blocked_by': [],
                'relates_to': []
            };
            
            currentTask.links.forEach(link => {
                const linkedTask = tasks.find(t => t.id === link.taskId);
                if (linkedTask) {
                    groupedLinks[link.type]?.push({ link, linkedTask });
                }
            });
            
            const linkTypeLabels = {
                'blocks': t('tasks.links.blocks'),
                'is_blocked_by': t('tasks.links.isBlockedBy'),
                'relates_to': t('tasks.links.relatesTo')
            };
            
            // Render each group (sorted: blocks, is_blocked_by, relates_to)
            const linkTypeOrder = ['blocks', 'is_blocked_by', 'relates_to'];
            taskLinkRows = linkTypeOrder
                .filter(type => groupedLinks[type] && groupedLinks[type].length > 0)
                .map(type => {
                    const items = groupedLinks[type];
                    
                    // Sort items alphabetically by task title within each group
                    const sortedItems = [...items].sort((a, b) => {
                        return a.linkedTask.title.localeCompare(b.linkedTask.title, undefined, { sensitivity: 'base' });
                    });
                    
                    const groupHeader = `
                        <div style="font-size: 12px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; margin: 12px 0 6px 0; padding: 0 4px;">
                            ${linkTypeLabels[type]} (${items.length})
                        </div>
                    `;
                    
                    const groupItems = sortedItems.map(({ link, linkedTask }, linkIndex) => {
                        // Find actual index in original links array for removal
                        const actualIndex = currentTask.links.findIndex(l => l.taskId === linkedTask.id && l.type === type);
                        
                        return `
                            <div class="attachment-item" style="display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 6px; margin-bottom: 6px; border: 1px solid var(--border); min-height: 0;">
                                <div style="font-size: 13px; font-weight: 500; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; line-height: 1.3; flex: 1; min-width: 0;">
                                    #${linkedTask.id} · ${escapeHtml(linkedTask.title)}
                                </div>
                                <span class="priority-pill priority-${linkedTask.priority}" style="flex-shrink: 0; font-size: 10px; padding: 3px 8px; width: 60px; text-align: center; height: 22px; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; line-height: 1;">${getPriorityLabel(linkedTask.priority).toUpperCase()}</span>
                                <span class="status-badge ${linkedTask.status}" style="flex-shrink: 0; font-size: 10px; width: 90px; text-align: center; height: 22px; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; padding: 3px 8px; line-height: 1;">${getStatusLabel(linkedTask.status).toUpperCase()}</span>
                                <div style="display: flex; gap: 2px; align-items: center; flex-shrink: 0;">
                                    <button type="button" data-action="openTaskDetails" data-param="${linkedTask.id}" data-stop-propagation="true" class="link-open-btn" title="${t('tasks.links.openTask')}" aria-label="${t('tasks.links.openTask')}">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <line x1="10" y1="14" x2="21" y2="3"></line>
                                        </svg>
                                    </button>
                                    <button type="button" class="attachment-remove link-remove-btn" data-action="removeTaskLink" data-param="${currentTaskId}" data-param2="${actualIndex}" aria-label="${t('tasks.attachments.removeLink')}" title="${t('tasks.attachments.removeLink')}">&times;</button>
                                </div>
                            </div>
                        `;
                    }).join('');
                    
                    return groupHeader + groupItems;
                }).join('');
        }
    }

    const hasAny = Boolean(fileRows) || Boolean(linkRows) || Boolean(dependencyRows) || Boolean(taskLinkRows);
    if (!hasAny) {
        filesContainer.innerHTML = `<div style="color: var(--text-muted); font-size: 13px; padding: 8px 0;">${t('tasks.attachments.none')}</div>`;
        linksContainer.innerHTML = '';
    } else {
        filesContainer.innerHTML = fileRows || '';
        linksContainer.innerHTML = linkRows + dependencyRows + taskLinkRows;
    }

    // Load image thumbnails via raw URL — browser fetches asynchronously, no JS round-trip
    for (const att of attachments || []) {
        if (att && att.type === 'file' && att.fileKey && att.fileType === 'image') {
            const thumbnailEl = document.getElementById(`thumbnail-${att.fileKey}`);
            if (thumbnailEl) {
                const img = document.createElement('img');
                img.alt = escapeHtml(att.name || '');
                img.onerror = () => { thumbnailEl.innerHTML = att.icon || '📄'; };
                img.src = buildFileReadUrl(att.fileKey);
                thumbnailEl.innerHTML = '';
                thumbnailEl.appendChild(img);
            }
        }
    }
}

function renderDependenciesInModal(task) {
    const filesContainer = document.getElementById('attachments-files-list');
    const linksContainer = document.getElementById('attachments-links-list');

    if (filesContainer && linksContainer) {
        renderAttachmentsSeparated(task.attachments || [], filesContainer, linksContainer);
    }
}

// Renders pending task-to-task links queued in window.tempLinks (new task creation mode)
function renderTempLinks() {
    const linksContainer = document.getElementById('attachments-links-list');
    if (!linksContainer) return;

    const pendingLinks = window.tempLinks || [];

    // Find or create the pending-links section within the container
    let pendingSection = linksContainer.querySelector('.temp-links-section');
    if (pendingLinks.length === 0) {
        if (pendingSection) pendingSection.remove();
        return;
    }

    if (!pendingSection) {
        pendingSection = document.createElement('div');
        pendingSection.className = 'temp-links-section';
        linksContainer.appendChild(pendingSection);
    }

    const linkTypeLabels = {
        'relates_to': t('tasks.links.relatesTo'),
        'blocks': t('tasks.links.blocks'),
        'is_blocked_by': t('tasks.links.isBlockedBy'),
        'depends_on': t('tasks.links.dependsOn') || 'depends on'
    };

    pendingSection.innerHTML = pendingLinks.map((entry, idx) => {
        const linkedTask = tasks.find(t => t.id === entry.targetTaskId);
        if (!linkedTask) return '';
        const typeLabel = linkTypeLabels[entry.linkType] || entry.linkType;
        return `
            <div class="attachment-item" style="display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:6px;margin-bottom:6px;border:1px solid var(--border);">
                <span style="font-size:11px;color:var(--text-muted);flex-shrink:0;">${escapeHtml(typeLabel)}</span>
                <div style="font-size:13px;font-weight:500;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0;">#${linkedTask.id} · ${escapeHtml(linkedTask.title)}</div>
                <button type="button" class="attachment-remove" data-action="removeTempLink" data-param="${idx}" aria-label="Remove" title="Remove">&times;</button>
            </div>
        `;
    }).filter(Boolean).join('');
}

// Removes a pending task-to-task link queued in window.tempLinks (new task creation mode)
function removeTempLink(idx) {
    if (!window.tempLinks) return;
    window.tempLinks.splice(idx, 1);
    renderTempLinks();
}

async function viewFile(fileKey, fileName, fileType, galleryIndex) {
    if (fileType !== 'image') return; // Only images can be viewed inline

    const gIdx = galleryIndex !== undefined && galleryIndex !== null && galleryIndex !== '' ? parseInt(galleryIndex) : -1;
    if (gIdx >= 0 && currentImageGallery.length > 0) {
        openImageGallery(gIdx);
        return;
    }

    try {
        viewImageLegacy(buildFileReadUrl(fileKey), fileName);
    } catch (error) {
        showErrorNotification(t('error.attachmentLoadFailed', { message: error.message }));
    }
}

function viewImageLegacy(base64Data, imageName, galleryIndex) {
    const gIdx = galleryIndex !== undefined && galleryIndex !== null && galleryIndex !== '' ? parseInt(galleryIndex) : -1;
    if (gIdx >= 0 && currentImageGallery.length > 0) {
        openImageGallery(gIdx);
        return;
    }

    const safeTitle = String(imageName || '').trim();

    const modal = document.createElement('div');
    modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        cursor: pointer;
    `;

    modal.innerHTML = `
        <div style="max-width: 90%; max-height: 90%; display: flex; align-items: center; justify-content: center;">
            <img src="${base64Data}" alt="${escapeHtml(safeTitle)}" style="max-width: 100%; max-height: 90vh; object-fit: contain; border-radius: 8px;">
        </div>
    `;

    modal.onclick = () => modal.remove();
    document.body.appendChild(modal);
}

async function openImageGallery(startIndex) {
    const images = currentImageGallery;
    if (!images || !images.length) return;

    let idx = Math.max(0, Math.min(startIndex, images.length - 1));
    const multi = images.length > 1;
    const showDots = multi && images.length <= 15;

    // SVG chevrons — clean paths, no selectable text
    const svgPrev = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events:none;display:block;"><polyline points="15 18 9 12 15 6"/></svg>`;
    const svgNext = `<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events:none;display:block;"><polyline points="9 18 15 12 9 6"/></svg>`;
    // Close X as SVG too
    const svgClose = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events:none;display:block;"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

    const navBtnBase = `user-select:none;-webkit-user-select:none;-webkit-tap-highlight-color:transparent;border:none;background:none;cursor:pointer;width:52px;height:52px;display:flex;align-items:center;justify-content:center;transition:transform 0.2s ease,opacity 0.2s ease;flex-shrink:0;outline:none;opacity:0.65;`;

    // Dots: button has generous touch padding, tiny visual inner span — prevents mobile browser inflating the hit target visually
    const dotsHtml = showDots
        ? `<div class="gal-dots" style="display:flex;justify-content:center;align-items:center;padding:12px 0 4px;flex-shrink:0;user-select:none;-webkit-user-select:none;">
            ${images.map((_, i) => `<button class="gal-dot" data-i="${i}" style="padding:6px;background:none;border:none;cursor:pointer;outline:none;appearance:none;-webkit-appearance:none;-webkit-tap-highlight-color:transparent;line-height:0;"><span style="display:block;width:6px;height:6px;border-radius:50%;pointer-events:none;background:rgba(255,255,255,${i === idx ? '0.9' : '0.28'});transform:scale(${i === idx ? '1.3' : '1'});transition:background 0.2s,transform 0.2s;"></span></button>`).join('')}
           </div>`
        : '';

    const overlay = document.createElement('div');
    overlay.style.cssText = `position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.92);display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:10000;user-select:none;-webkit-user-select:none;`;

    overlay.innerHTML = `
        <div style="width:100%;max-width:1200px;height:95vh;display:flex;flex-direction:column;box-sizing:border-box;padding:0 12px;">
            <div style="display:flex;align-items:center;gap:12px;padding:10px 4px 10px;flex-shrink:0;">
                <span class="gal-title" style="flex:1;color:rgba(255,255,255,0.8);font-size:13px;font-weight:400;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;user-select:none;"></span>
                <span class="gal-counter" style="color:rgba(255,255,255,0.35);font-size:12px;white-space:nowrap;font-variant-numeric:tabular-nums;user-select:none;"></span>
                <button class="gal-close" style="${navBtnBase}color:rgba(255,255,255,0.5);" title="Close">${svgClose}</button>
            </div>
            <div style="flex:1;display:flex;align-items:center;gap:8px;overflow:hidden;min-height:0;">
                <button class="gal-prev" style="${navBtnBase}color:rgba(255,255,255,0.85);">${svgPrev}</button>
                <div class="gal-viewport" style="flex:1;overflow:hidden;height:100%;position:relative;">
                    <div class="gal-strip" style="display:flex;width:300%;height:100%;will-change:transform;transform:translateX(-33.333%);">
                        <div style="width:33.333%;height:100%;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-sizing:border-box;">
                            <img class="gal-img-prev" src="" alt="" style="max-width:100%;max-height:100%;object-fit:contain;border-radius:4px;user-select:none;-webkit-user-select:none;-webkit-user-drag:none;">
                        </div>
                        <div style="width:33.333%;height:100%;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-sizing:border-box;">
                            <img class="gal-img-curr" src="" alt="" style="max-width:100%;max-height:100%;object-fit:contain;border-radius:4px;user-select:none;-webkit-user-select:none;-webkit-user-drag:none;">
                        </div>
                        <div style="width:33.333%;height:100%;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-sizing:border-box;">
                            <img class="gal-img-next" src="" alt="" style="max-width:100%;max-height:100%;object-fit:contain;border-radius:4px;user-select:none;-webkit-user-select:none;-webkit-user-drag:none;">
                        </div>
                    </div>
                </div>
                <button class="gal-next" style="${navBtnBase}color:rgba(255,255,255,0.85);">${svgNext}</button>
            </div>
            ${dotsHtml}
        </div>
    `;

    const titleEl = overlay.querySelector('.gal-title');
    const counterEl = overlay.querySelector('.gal-counter');
    const closeBtn = overlay.querySelector('.gal-close');
    const prevBtn = overlay.querySelector('.gal-prev');
    const nextBtn = overlay.querySelector('.gal-next');
    const dotsContainer = overlay.querySelector('.gal-dots');
    const viewport = overlay.querySelector('.gal-viewport');
    const strip = overlay.querySelector('.gal-strip');
    const prevImg = overlay.querySelector('.gal-img-prev');
    const currImg = overlay.querySelector('.gal-img-curr');
    const nextImg = overlay.querySelector('.gal-img-next');

    function setNavState(i) {
        const atFirst = i === 0;
        const atLast = i === images.length - 1;
        prevBtn.style.opacity = (!multi || atFirst) ? '0.2' : '0.65';
        prevBtn.style.cursor = (!multi || atFirst) ? 'default' : 'pointer';
        prevBtn.style.pointerEvents = (!multi || atFirst) ? 'none' : 'auto';
        nextBtn.style.opacity = (!multi || atLast) ? '0.2' : '0.65';
        nextBtn.style.cursor = (!multi || atLast) ? 'default' : 'pointer';
        nextBtn.style.pointerEvents = (!multi || atLast) ? 'none' : 'auto';
    }

    function updateDots(i) {
        if (!dotsContainer) return;
        dotsContainer.querySelectorAll('.gal-dot').forEach((dot, di) => {
            const active = di === i;
            const span = dot.querySelector('span');
            if (span) {
                span.style.background = active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.28)';
                span.style.transform = active ? 'scale(1.3)' : 'scale(1)';
            }
        });
    }

    async function loadSlot(el, i) {
        if (i < 0 || i >= images.length) { el.src = ''; el.alt = ''; return; }
        const att = images[i];
        try {
            // Use raw URL for file-backed images — works for both R2 and legacy KV files
            el.src = (att.type === 'file' && att.fileKey) ? buildFileReadUrl(att.fileKey) : (att.data || att.url || '');
            el.alt = att.name || '';
        } catch (err) {
            console.error('Gallery: failed to load image', err);
        }
    }

    async function loadTriple(i) {
        titleEl.textContent = images[i].name;
        counterEl.textContent = multi ? `${i + 1} / ${images.length}` : '';
        setNavState(i);
        updateDots(i);
        await Promise.all([loadSlot(prevImg, i - 1), loadSlot(currImg, i), loadSlot(nextImg, i + 1)]);
    }

    let isAnimating = false;
    function navigate(delta) {
        if (isAnimating) return;
        const newIdx = idx + delta;
        if (newIdx < 0 || newIdx >= images.length) return;
        isAnimating = true;
        let settled = false;
        const done = () => {
            if (settled) return;
            settled = true;
            idx = newIdx;
            // Rotate slot src values so the visible image is already in currImg — no flicker
            strip.style.transition = 'none';
            strip.style.transform = 'translateX(-33.333%)';
            if (delta > 0) {
                prevImg.src = currImg.src; prevImg.alt = currImg.alt;
                currImg.src = nextImg.src; currImg.alt = nextImg.alt;
                nextImg.src = ''; nextImg.alt = '';
                loadSlot(nextImg, idx + 1);
            } else {
                nextImg.src = currImg.src; nextImg.alt = currImg.alt;
                currImg.src = prevImg.src; currImg.alt = prevImg.alt;
                prevImg.src = ''; prevImg.alt = '';
                loadSlot(prevImg, idx - 1);
            }
            titleEl.textContent = images[idx].name;
            counterEl.textContent = multi ? `${idx + 1} / ${images.length}` : '';
            setNavState(idx);
            updateDots(idx);
            requestAnimationFrame(() => { isAnimating = false; });
        };
        strip.style.transition = 'transform 0.28s ease';
        strip.style.transform = delta > 0 ? 'translateX(-66.666%)' : 'translateX(0%)';
        strip.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 400);
    }

    function close() {
        overlay.remove();
        document.removeEventListener('keydown', keyHandler);
    }

    function keyHandler(e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); }
        else if (e.key === 'Escape') close();
    }

    // Touch swipe — full 1:1 strip drag
    let touchStartX = 0, touchStartY = 0, isSwiping = false, swipeDir = null;
    const SWIPE_THRESHOLD = 60;
    const ANGLE_MAX = 35;

    viewport.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1 || isAnimating) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        isSwiping = true;
        swipeDir = null;
        strip.style.transition = 'none';
    }, { passive: true });

    viewport.addEventListener('touchmove', (e) => {
        if (!isSwiping || e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - touchStartX;
        const dy = e.touches[0].clientY - touchStartY;
        if (swipeDir === null) {
            const angle = Math.abs(Math.atan2(Math.abs(dy), Math.abs(dx)) * 180 / Math.PI);
            swipeDir = angle < ANGLE_MAX ? 'h' : 'v';
        }
        if (swipeDir !== 'h') return;
        // Rubber-band at boundaries
        let clampedDx = dx;
        if (dx > 0 && idx === 0) clampedDx = dx * 0.2;
        if (dx < 0 && idx === images.length - 1) clampedDx = dx * 0.2;
        strip.style.transform = `translateX(calc(-33.333% + ${clampedDx}px))`;
    }, { passive: true });

    viewport.addEventListener('touchend', (e) => {
        if (!isSwiping) return;
        isSwiping = false;
        if (swipeDir !== 'h') return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) >= SWIPE_THRESHOLD) {
            navigate(dx < 0 ? 1 : -1);
        } else {
            strip.style.transition = 'transform 0.28s ease';
            strip.style.transform = 'translateX(-33.333%)';
        }
    }, { passive: true });

    // Hover: directional nudge + opacity lift. Chevron slides toward its direction.
    prevBtn.addEventListener('mouseenter', () => { prevBtn.style.transform = 'translateX(-5px)'; prevBtn.style.opacity = '1'; });
    prevBtn.addEventListener('mouseleave', () => { prevBtn.style.transform = 'translateX(0)'; prevBtn.style.opacity = prevBtn.style.pointerEvents === 'none' ? '0.2' : '0.65'; });
    nextBtn.addEventListener('mouseenter', () => { nextBtn.style.transform = 'translateX(5px)'; nextBtn.style.opacity = '1'; });
    nextBtn.addEventListener('mouseleave', () => { nextBtn.style.transform = 'translateX(0)'; nextBtn.style.opacity = nextBtn.style.pointerEvents === 'none' ? '0.2' : '0.65'; });
    closeBtn.addEventListener('mouseenter', () => { closeBtn.style.opacity = '1'; closeBtn.style.transform = 'scale(1.2)'; });
    closeBtn.addEventListener('mouseleave', () => { closeBtn.style.opacity = '0.65'; closeBtn.style.transform = 'scale(1)'; });

    prevBtn.addEventListener('click', (e) => { e.stopPropagation(); navigate(-1); });
    nextBtn.addEventListener('click', (e) => { e.stopPropagation(); navigate(1); });
    closeBtn.addEventListener('click', (e) => { e.stopPropagation(); close(); });

    if (dotsContainer) {
        dotsContainer.addEventListener('click', (e) => {
            e.stopPropagation();
            const dot = e.target.closest('.gal-dot');
            if (dot) { const i = parseInt(dot.dataset.i); if (i !== idx) { idx = i; strip.style.transition = 'none'; strip.style.transform = 'translateX(-33.333%)'; loadTriple(idx); } }
        });
    }

    overlay.addEventListener('click', (e) => { if (isBackdropClick(e, overlay)) close(); });
    document.addEventListener('keydown', keyHandler);

    document.body.appendChild(overlay);
    loadTriple(idx);
}

  async function downloadFileAttachment(fileKey, fileName, mimeType) {
    try {
        // Let the browser handle the transfer directly instead of buffering
        // the full file into JS memory with fetch().blob().
        const url = buildFileReadUrl(fileKey);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        showSuccessNotification(t('success.fileDownloaded'));
    } catch (error) {
        showErrorNotification(t('error.fileDownloadFailed', { message: error.message }));
    }
}

async function removeAttachment(index) {
    const taskId = document.getElementById('task-form').dataset.editingTaskId;

    if (taskId) {
        // Removing from existing task
        const task = tasks.find(t => t.id === parseInt(taskId));
        if (!task || !task.attachments) return;

        const attachment = task.attachments[index];

        // Delete file from NAUTILUS_FILES KV if it's a file attachment
        if (attachment.type === 'file' && attachment.fileKey) {
            try {
                await deleteFile(attachment.fileKey);
                showSuccessNotification(t('success.attachmentDeletedFromStorage', { name: attachment.name }));
            } catch (error) {
                console.error('Failed to delete file from storage:', error);
                showErrorNotification(t('error.fileDeleteFailed'));
                return; // Don't remove from task if storage deletion failed
            }
        } else {
            showSuccessNotification(t('success.attachmentRemoved'));
        }

        const oldTaskCopy = JSON.parse(JSON.stringify(task));
        task.attachments.splice(index, 1);
        if (window.historyService) {
            window.historyService.recordTaskUpdated(oldTaskCopy, task);
        }

        // Update UI immediately (optimistic update)
        renderAttachments(task.attachments);

        // Save in background (don't block UI)
        task.updatedAt = new Date().toISOString();
        saveTaskUpdateData(parseInt(taskId), { attachments: task.attachments, updatedAt: task.updatedAt }).catch(err => {
            console.error('Failed to save attachment removal:', err);
            showErrorNotification(t('error.saveChangesFailed'));
        });
    } else {
        // Removing from staged attachments
        const attachment = tempAttachments[index];

        // Delete file from NAUTILUS_FILES KV if it's a file attachment
        if (attachment.type === 'file' && attachment.fileKey) {
            try {
                await deleteFile(attachment.fileKey);
                showSuccessNotification(t('success.attachmentDeletedFromStorage', { name: attachment.name }));
            } catch (error) {
                console.error('Failed to delete file from storage:', error);
                showErrorNotification(t('error.fileDeleteFailed'));
                return; // Don't remove from staging if storage deletion failed
            }
        } else {
            showSuccessNotification(t('success.attachmentRemoved'));
        }

        tempAttachments.splice(index, 1);
        renderAttachments(tempAttachments);
    }
}

async function removeDependencyUI(prerequisiteTaskId) {
    const taskForm = document.getElementById('task-form');
    const currentTaskId = taskForm ? parseInt(taskForm.dataset.editingTaskId) : null;
    
    if (!currentTaskId) return;
    
    const success = await handleRemoveDependency(currentTaskId, prerequisiteTaskId);
    
    if (success) {
        // Refresh the dependencies display
        const task = tasks.find(t => t.id === currentTaskId);
        if (task) {
            renderDependenciesInModal(task);
        }
    }
}

async function removeTaskLink(taskId, linkIndex) {
    const task = tasks.find(t => t.id === taskId);
    if (!task || !task.links || !task.links[linkIndex]) {
        showErrorNotification(t('error.linkNotFound'));
        return;
    }
    
    const link = task.links[linkIndex];
    const linkedTask = tasks.find(t => t.id === link.taskId);
    
    // Remove the link from the source task
    task.links.splice(linkIndex, 1);
    
    // Remove the reciprocal link from the target task
    if (linkedTask && linkedTask.links) {
        // Determine the reciprocal link type
        let reciprocalType;
        if (link.type === 'blocks') {
            reciprocalType = 'is_blocked_by';
        } else if (link.type === 'is_blocked_by') {
            reciprocalType = 'blocks';
        } else if (link.type === 'relates_to') {
            reciprocalType = 'relates_to';
        }
        
        // Find and remove the reciprocal link
        const reciprocalIndex = linkedTask.links.findIndex(
            l => l.taskId === taskId && l.type === reciprocalType
        );
        
        if (reciprocalIndex !== -1) {
            linkedTask.links.splice(reciprocalIndex, 1);
        }
    }
    
    // Record history for both tasks
    if (window.historyService) {
        const linkTypeLabels = { blocks: 'tasks.links.blocks', is_blocked_by: 'tasks.links.isBlockedBy', relates_to: 'tasks.links.relatesTo' };
        const linkLabel = t(linkTypeLabels[link.type] || 'tasks.links.relatesTo');
        window.historyService.recordHistory('task', taskId, task.title || 'Task', 'updated', {
            link: { before: null, after: { action: 'removed', entity: 'task', id: link.taskId, title: linkedTask ? linkedTask.title : `#${link.taskId}`, linkType: linkLabel } }
        });
        if (linkedTask) {
            window.historyService.recordHistory('task', linkedTask.id, linkedTask.title || 'Task', 'updated', {
                link: { before: null, after: { action: 'removed', entity: 'task', id: taskId, title: task.title, linkType: linkLabel } }
            });
        }
    }

    // Save tasks using row-level operations
    const now = new Date().toISOString();
    await Promise.all([
        saveTaskUpdate(taskId, { links: task.links, updatedAt: now }),
        linkedTask ? saveTaskUpdate(linkedTask.id, { links: linkedTask.links, updatedAt: now }) : Promise.resolve()
    ]);

    // Refresh the task modal
    renderDependenciesInModal(task);

    showSuccessNotification(t('success.linkRemoved'));
}

function initTaskAttachmentDropzone() {
    const dropzone = document.getElementById('attachment-file-dropzone');
    const fileInput = document.getElementById('attachment-file');
    if (!dropzone || !fileInput) return;

    const isMobileScreen = getIsMobileCached();
    const defaultText = isMobileScreen
        ? t('tasks.modal.attachmentsDropzoneTap')
        : t('tasks.modal.attachmentsDropzoneDefault');

    dropzone.dataset.defaultText = defaultText;

    function setDropzoneText(text) {
        dropzone.innerHTML = '';
        const textEl = document.createElement('span');
        textEl.className = 'task-attachment-dropzone-text';
        textEl.textContent = text;
        dropzone.appendChild(textEl);
    }

    function applyDropzoneBaseStyles(el) {
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.gap = '10px';
        el.style.padding = '12px 16px';
        el.style.textAlign = 'center';
        el.style.cursor = 'pointer';
        el.style.userSelect = 'none';
        el.style.minHeight = '48px';
        el.style.border = '2px dashed var(--border)';
        el.style.borderRadius = '10px';
        el.style.background = 'var(--bg-tertiary)';
        el.style.boxShadow = 'none';
        el.style.color = 'var(--text-muted)';
        el.style.fontWeight = '500';
        el.style.transition = 'border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease';
    }

    function setDropzoneDragoverStyles(el, isActive) {
        if (isActive) {
            // Use same vibrant blue as feedback dropzone
            el.style.borderColor = 'var(--accent-blue)';
            el.style.background = 'rgba(59, 130, 246, 0.08)';
            el.style.boxShadow = '0 0 0 1px var(--accent-blue)';
        } else {
            el.style.border = '2px dashed var(--border)';
            el.style.background = 'var(--bg-tertiary)';
            el.style.boxShadow = 'none';
        }
    }

    applyDropzoneBaseStyles(dropzone);
    setDropzoneText(defaultText);

    const MAX_ATTACHMENTS_PER_UPLOAD = 10;

    async function handleDropOrPasteFileList(fileList, event) {
        if (!fileList || fileList.length === 0) return;
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        // Capture files into plain array immediately (DataTransfer files become invalid after sync handler returns)
        const files = Array.from(fileList);

        // Block entirely if too many files - don't upload any
        if (files.length > MAX_ATTACHMENTS_PER_UPLOAD) {
            const message = t('error.tooManyFiles', { max: MAX_ATTACHMENTS_PER_UPLOAD });
            showAttachmentErrorGlobal(message);
            return;
        }

        // Clear any previous error
        clearAttachmentErrorGlobal();

        for (const file of files) {
            await uploadTaskAttachmentFile(file, dropzone);
        }
    }

    let dragDepth = 0;

    dropzone.addEventListener('dragenter', function(e) {
        e.preventDefault();
        dragDepth += 1;
        dropzone.classList.add('task-attachment-dragover');
        setDropzoneDragoverStyles(dropzone, true);
    });

    dropzone.addEventListener('dragover', function(e) {
        e.preventDefault();
        dropzone.classList.add('task-attachment-dragover');
        setDropzoneDragoverStyles(dropzone, true);
    });

    dropzone.addEventListener('dragleave', function(e) {
        e.preventDefault();
        dragDepth = Math.max(0, dragDepth - 1);
        if (dragDepth === 0) {
            dropzone.classList.remove('task-attachment-dragover');
            setDropzoneDragoverStyles(dropzone, false);
        }
    });

    dropzone.addEventListener('drop', function(e) {
        dragDepth = 0;
        dropzone.classList.remove('task-attachment-dragover');
        setDropzoneDragoverStyles(dropzone, false);
        handleDropOrPasteFileList(e.dataTransfer && e.dataTransfer.files, e);
    });

    dropzone.addEventListener('dragend', function() {
        dragDepth = 0;
        dropzone.classList.remove('task-attachment-dragover');
        setDropzoneDragoverStyles(dropzone, false);
    });

    dropzone.addEventListener('paste', function(e) {
        if (!e.clipboardData) return;
        const files = e.clipboardData.files;
        if (files && files.length > 0) {
            handleDropOrPasteFileList(files, e);
        }
    });

    dropzone.addEventListener('click', function() {
        fileInput.click();
    });

    dropzone.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInput.click();
        }
    });

    fileInput.addEventListener('change', function(e) {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleDropOrPasteFileList(files, e);
        }
        fileInput.value = '';
    });
}

document.addEventListener('DOMContentLoaded', initTaskAttachmentDropzone);

async function uploadTaskAttachmentFile(file, uiEl) {
    if (!file) return null;

    const fileType = getFileType(file.type || '', file.name || '');
    const maxSize = getMaxFileSize(fileType);

    if (file.size > maxSize) {
        const maxMB = Math.round(maxSize / (1024 * 1024));
        showErrorNotification(t('error.fileSizeTooLarge', { maxMB }));
        return null;
    }

    const isButton = uiEl && uiEl.tagName === 'BUTTON';
    const originalText = isButton ? (uiEl.textContent || '📁 Upload File') : null;
    const defaultText = !isButton
        ? (uiEl?.dataset?.defaultText || t('tasks.modal.attachmentsDropzoneDefault'))
        : null;

    // Resolve task reference before the upload so we can show an optimistic
    // attachment entry immediately instead of waiting for bytes to finish uploading.
    const fileKey = `file_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const taskId = document.getElementById('task-form').dataset.editingTaskId;
    const task = taskId ? tasks.find(t => t.id === parseInt(taskId)) : null;

    if (taskId && !task) return null;

    const attachment = {
        name: file.name,
        icon: getFileIcon(fileType),
        type: 'file',
        fileType: fileType,
        fileKey: fileKey,
        mimeType: file.type,
        size: file.size,
        addedAt: new Date().toISOString(),
        _uploading: true
    };

    // Snapshot before mutating task (for history recording)
    const oldTaskCopy = task ? JSON.parse(JSON.stringify(task)) : null;

    // Optimistic add — attachment appears in the list before upload completes
    if (task) {
        if (!task.attachments) task.attachments = [];
        task.attachments.push(attachment);
        renderAttachments(task.attachments);
    } else if (!taskId) {
        tempAttachments.push(attachment);
        renderAttachments(tempAttachments);
    }

    try {
        if (uiEl) {
            if (isButton) {
                uiEl.textContent = `⏳ ${t('feedback.uploading')}`;
                uiEl.disabled = true;
            } else {
                uiEl.innerHTML = '';
                const textEl = document.createElement('span');
                textEl.className = 'task-attachment-dropzone-text';
                textEl.textContent = t('common.uploadingFile', { name: file.name });
                uiEl.appendChild(textEl);
                uiEl.classList.add('task-attachment-uploading');
                uiEl.setAttribute('aria-busy', 'true');
            }
        }

        await uploadFile(fileKey, file, { kind: 'task_attachment' }, (pct) => {
            if (uiEl) {
                const textEl = uiEl.querySelector('.task-attachment-dropzone-text');
                if (textEl) textEl.textContent = t('common.uploadingFileProgress', { name: file.name, progress: pct });
            }
        });

        // Upload complete — remove the uploading flag and persist metadata
        delete attachment._uploading;

        if (task) {
            if (window.historyService) {
                window.historyService.recordTaskUpdated(oldTaskCopy, task);
            }
            renderAttachments(task.attachments);
            task.updatedAt = new Date().toISOString();
            saveTaskUpdateData(parseInt(taskId), { attachments: task.attachments, updatedAt: task.updatedAt }).catch(err => {
                console.error('Failed to save attachment:', err);
                showErrorNotification(t('error.attachmentSaveFailed'));
            });
        } else if (!taskId) {
            renderAttachments(tempAttachments);
        }

        showSuccessNotification(t('success.fileUploaded'));
        return attachment;

    } catch (error) {
        // Rollback the optimistic add
        if (task) {
            task.attachments = (task.attachments || []).filter(a => a.fileKey !== fileKey);
            renderAttachments(task.attachments);
        } else if (!taskId) {
            const idx = tempAttachments.findIndex(a => a.fileKey === fileKey);
            if (idx !== -1) tempAttachments.splice(idx, 1);
            renderAttachments(tempAttachments);
        }
        showErrorNotification(t('error.fileUploadFailed', { message: error.message }));
        return null;
    } finally {
        if (uiEl) {
            if (isButton) {
                uiEl.textContent = originalText;
                uiEl.disabled = false;
            } else {
                uiEl.innerHTML = '';
                const textEl = document.createElement('span');
                textEl.className = 'task-attachment-dropzone-text';
                textEl.textContent = defaultText;
                uiEl.appendChild(textEl);
                uiEl.classList.remove('task-attachment-uploading');
                uiEl.removeAttribute('aria-busy');
            }
        }
    }
}

async function addFileAttachment(event) {
    const fileInput = document.getElementById('attachment-file');
    // Capture files into plain array immediately (FileList becomes invalid after input reset)
    const allFiles = fileInput && fileInput.files ? Array.from(fileInput.files) : [];

    if (allFiles.length === 0) {
        showErrorNotification(t('error.selectFile'));
        return;
    }

    const MAX_FILES = 10;
    // Block entirely if too many files - don't upload any
    if (allFiles.length > MAX_FILES) {
        const message = t('error.tooManyFiles', { max: MAX_FILES });
        showAttachmentErrorGlobal(message);
        if (fileInput) fileInput.value = '';
        return;
    }

    // Clear any previous error
    clearAttachmentErrorGlobal();

    const uiEl =
        document.getElementById('attachment-file-dropzone') ||
        event?.target ||
        null;

    for (const file of allFiles) {
        await uploadTaskAttachmentFile(file, uiEl);
    }

    if (fileInput) fileInput.value = '';
}

/**
 * Show an error message in the attachment modal (global helper)
 * @param {string} message - The error message to display
 */
function showAttachmentErrorGlobal(message) {
    let errorEl = document.getElementById('attachment-error');
    if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.id = 'attachment-error';
        errorEl.className = 'form-error';
        const dropzoneEl = document.getElementById('attachment-file-dropzone');
        dropzoneEl?.parentNode?.insertBefore(errorEl, dropzoneEl.nextSibling);
    }
    errorEl.textContent = message;
    errorEl.style.display = 'flex';
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
        if (errorEl) errorEl.style.display = 'none';
    }, 5000);
}

/**
 * Clear the attachment error message (global helper)
 */
function clearAttachmentErrorGlobal() {
    const errorEl = document.getElementById('attachment-error');
    if (errorEl) errorEl.style.display = 'none';
}

function getFileType(mimeType, filename) {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) return 'pdf';
    if (mimeType.includes('spreadsheet') || filename.match(/\.(xlsx?|csv)$/i)) return 'spreadsheet';
    if (mimeType.includes('document') || mimeType.includes('word') || filename.match(/\.docx?$/i)) return 'document';
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint') || filename.match(/\.pptx?$/i)) return 'presentation';
    return 'file';
}

function getMaxFileSize(fileType) {
    switch (fileType) {
        case 'pdf':
            return 750 * 1024 * 1024; // 750MB for PDFs (chunked upload for files >100MB)
        case 'image':
        case 'spreadsheet':
        case 'document':
        case 'presentation':
            return 25 * 1024 * 1024; // 25MB for others
        default:
            return 25 * 1024 * 1024; // 25MB default
    }
}

function getFileIcon(fileType) {
    switch (fileType) {
        case 'image': return '\u{1F5BC}\u{FE0F}';
        case 'pdf': return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 48" width="42" height="50"><path fill="#e53935" d="M4 0h22l14 14v30a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"/><path fill="#b71c1c" d="M26 0l14 14H26V0z"/><text x="20" y="36" text-anchor="middle" font-size="13" font-weight="bold" font-family="Arial,sans-serif" fill="#ffffff">PDF</text></svg>';
        case 'spreadsheet': return '📊';
        case 'document': return '📝';
        case 'presentation': return '📊';
        default: return '📄';
    }
}

// File utility functions imported from src/utils/file.js

// Expose file attachment functions to window for onclick handlers
window.addFileAttachment = addFileAttachment;
window.viewFile = viewFile;
window.viewImageLegacy = viewImageLegacy;
window.openImageGallery = openImageGallery;
window.downloadFileAttachment = downloadFileAttachment;
window.removeAttachment = removeAttachment;
window.removeDependency = removeDependencyUI;
window.removeTaskLink = removeTaskLink;
window.removeTempLink = removeTempLink;

// Kanban Settings
window.kanbanShowBacklog = localStorage.getItem('kanbanShowBacklog') === 'true'; // disabled by default
window.kanbanShowProjects = localStorage.getItem('kanbanShowProjects') !== 'false';
window.kanbanShowTags = localStorage.getItem('kanbanShowTags') !== 'false';
window.kanbanShowNoDate = localStorage.getItem('kanbanShowNoDate') === 'true';
window.kanbanUpdatedFilter = localStorage.getItem('kanbanUpdatedFilter') || 'all'; // all | 5m | 30m | 24h | week | month

// Status Settings
window.enableReviewStatus = localStorage.getItem('enableReviewStatus') === 'true'; // disabled by default

function getKanbanUpdatedFilterLabel(value) {
    switch (value) {
        case '5m': return '5m';
        case '30m': return '30m';
        case '24h': return '24h';
        case 'week': return t('filters.updated.week');
        case 'month': return t('filters.updated.month');
        case 'all':
        default:
            return '';
    }
}

function updateKanbanGridColumns() {
    const kanbanBoard = document.querySelector('.kanban-board');
    if (!kanbanBoard) return;

    // Count visible columns
    let visibleColumns = 3; // todo, progress, done (always visible)

    if (window.kanbanShowBacklog === true) visibleColumns++;
    if (window.enableReviewStatus !== false) visibleColumns++;

    // minmax() with a real floor, not a bare `1fr`. `1fr` means minmax(auto, 1fr),
    // and that `auto` minimum is min-content -- so the tracks only stop shrinking
    // when the TEXT stops shrinking. Measured at 769px: the board fitted itself
    // into the viewport by collapsing columns to 135px rather than scrolling.
    // --nx-col-min (set by the density layer, >=769px only) gives them a readable
    // floor so .page-content's existing overflow-x:auto scrolls instead, which is
    // what the pre-layer app already did. The `auto` fallback reproduces plain
    // `1fr` wherever the variable is unset, so mobile is untouched.
    kanbanBoard.style.gridTemplateColumns =
        `repeat(${visibleColumns}, minmax(var(--nx-col-min, auto), 1fr))`;
}

function applyReviewStatusVisibility() {
    const enabled = window.enableReviewStatus !== false;

    // Show/hide IN REVIEW kanban column
    const reviewColumn = document.getElementById('kanban-column-review');
    if (reviewColumn) {
        reviewColumn.style.display = enabled ? '' : 'none';
    }

    // Show/hide IN REVIEW filter option
    document.querySelectorAll('#filter-status-review, #cal-filter-status-review').forEach((reviewFilter) => {
        reviewFilter.style.display = enabled ? '' : 'none';
    });

    // Update grid columns
    updateKanbanGridColumns();

    // If disabled, clear any active review status filter
    if (!enabled && filterState.statuses.has('review')) {
        filterState.statuses.delete('review');
        updateFilterBadges();
        renderAfterFilterChange();
    }
}

function applyBacklogColumnVisibility() {
    const enabled = window.kanbanShowBacklog === true;

    // Show/hide BACKLOG kanban column
    const backlogColumn = document.getElementById('kanban-column-backlog');
    if (backlogColumn) {
        backlogColumn.style.display = enabled ? '' : 'none';
    }

    // Update grid columns
    updateKanbanGridColumns();

    applyBacklogFilterVisibility();
}

/**
 * When Tasks + Kanban + Backlog column hidden: hide Backlog in Status filter,
 * clear it from filter state, and refresh. Otherwise show Backlog option.
 * Mobile always uses list view (CSS), so Backlog must stay visible in filter.
 * (Plan: docs/plans/backlog-ux-golden-standard.md)
 */
function applyBacklogFilterVisibility() {
    const activeId = typeof getActivePageId === 'function' ? getActivePageId() : null;
    const kanbanBoard = document.querySelector('.kanban-board');
    const isKanban = kanbanBoard && !kanbanBoard.classList.contains('hidden');
    const isMobile = typeof getIsMobileCached === 'function' ? getIsMobileCached() : true;
    const hideBacklog = activeId === 'tasks' && isKanban && !isMobile && window.kanbanShowBacklog !== true;

    document.querySelectorAll('#filter-status-backlog, #cal-filter-status-backlog').forEach((backlogLi) => {
        backlogLi.style.display = hideBacklog ? 'none' : '';
    });

    if (hideBacklog && filterState.statuses.has('backlog')) {
        filterState.statuses.delete('backlog');
        const cb = document.querySelector('input[data-filter="status"][value="backlog"]');
        if (cb) cb.checked = false;
        updateFilterBadges();
        renderAfterFilterChange();
    }
}

function touchProjectUpdatedAt(projectId) {
    const pid = projectId ? parseInt(projectId, 10) : null;
    if (!pid) return null;
    const project = projects.find(p => p.id === pid);
    if (!project) return null;
    project.updatedAt = new Date().toISOString();
    return project;
}

function recordProjectTaskLinkChange(projectId, action, task) {
    if (!window.historyService) return;
    const pid = projectId ? parseInt(projectId, 10) : null;
    if (!pid) return;
    const project = projects.find(p => p.id === pid);
    if (!project) return;
    if (action === 'added' && window.historyService.recordProjectTaskAdded) {
        window.historyService.recordProjectTaskAdded(project, task);
    } else if (action === 'removed' && window.historyService.recordProjectTaskRemoved) {
        window.historyService.recordProjectTaskRemoved(project, task);
    }
}

function sanitizeKanbanUpdatedFilterButtonLabel() {
    document.querySelectorAll('#btn-filter-kanban-updated, #cal-btn-filter-kanban-updated').forEach((btn) => {
        const badge = btn.querySelector('.filter-count-badge');
        if (!badge) return;
        while (btn.firstChild) btn.removeChild(btn.firstChild);
        btn.appendChild(document.createTextNode(`${t('tasks.filters.updated')} `));
        btn.appendChild(badge);
        btn.appendChild(document.createTextNode(' '));
        const arrow = document.createElement('span');
        arrow.className = 'filter-arrow';
        arrow.textContent = '▼';
        btn.appendChild(arrow);
    });
}

function updateKanbanUpdatedFilterUI() {
    try { sanitizeKanbanUpdatedFilterButtonLabel(); } catch (e) {}
    document.querySelectorAll('#badge-kanban-updated, #cal-badge-kanban-updated').forEach((badge) => {
        badge.textContent = getKanbanUpdatedFilterLabel(window.kanbanUpdatedFilter);
        const button = badge.closest('.filter-button');
        if (button) button.classList.toggle('active', window.kanbanUpdatedFilter !== 'all');
    });

    try {
        document
            .querySelectorAll('input[type="radio"][data-filter="kanban-updated"]')
            .forEach((rb) => {
                rb.checked = rb.value === window.kanbanUpdatedFilter;
            });
    } catch (e) {}
}

function setKanbanUpdatedFilter(value, options = { render: true }) {
    const allowed = new Set(['all', '5m', '30m', '24h', 'week', 'month']);
    const normalized = allowed.has(value) ? value : (value === 'today' ? '24h' : 'all');

    window.kanbanUpdatedFilter = normalized;
    try { localStorage.setItem('kanbanUpdatedFilter', normalized); } catch (e) {}

    updateKanbanUpdatedFilterUI();
    try { renderActiveFilterChips(); } catch (e) {}

    // Sync updated filter to URL for shareable links
    try { syncURLWithFilters(); } catch (e) {}

    if (options && options.render === false) return;

    // Re-render whichever tasks view is currently active (never forces sorting).
    const isKanban = !document.querySelector('.kanban-board')?.classList.contains('hidden');
    const isList = document.getElementById('list-view')?.classList.contains('active');
    const isCalendar = document.getElementById('calendar-view')?.classList.contains('active');
    if (isKanban) renderTasks();
    if (isList || getIsMobileCached()) renderListView();
    if (isCalendar) renderCalendar();
}

function toggleKanbanSettings(event) {
    event.stopPropagation();
    const panel = document.getElementById('kanban-settings-panel');
    const isActive = panel.classList.contains('active');

    if (isActive) {
        panel.classList.remove('active');
    } else {
        panel.classList.add('active');
        // Load current state
        document.getElementById('kanban-show-backlog').checked = window.kanbanShowBacklog === true;
        document.getElementById('kanban-show-projects').checked = window.kanbanShowProjects !== false;
        document.getElementById('kanban-show-tags').checked = window.kanbanShowTags !== false;
        document.getElementById('kanban-show-no-date').checked = window.kanbanShowNoDate !== false;
    }
}

function openReviewTasksInNewTab() {
    const baseUrl = window.location.href.split('#')[0];
    window.open(baseUrl + '#tasks?status=review&view=list', '_blank', 'noopener');
}

function toggleKanbanBacklog() {
    const checkbox = document.getElementById('kanban-show-backlog');
    window.kanbanShowBacklog = checkbox.checked;
    localStorage.setItem('kanbanShowBacklog', checkbox.checked);

    // Show/hide backlog column
    const backlogColumn = document.getElementById('kanban-column-backlog');
    if (backlogColumn) {
        backlogColumn.style.display = checkbox.checked ? '' : 'none';
    }

    // Update kanban grid columns
    updateKanbanGridColumns();

    // Hide Backlog in filter when column OFF, clear from filter and refresh (plan: backlog-ux-golden-standard)
    applyBacklogFilterVisibility();

    renderTasks();
}

function toggleKanbanProjects() {
    const checkbox = document.getElementById('kanban-show-projects');
    window.kanbanShowProjects = checkbox.checked;
    localStorage.setItem('kanbanShowProjects', checkbox.checked);
    renderTasks();
}

function toggleKanbanTags() {
    const checkbox = document.getElementById('kanban-show-tags');
    window.kanbanShowTags = checkbox.checked;
    localStorage.setItem('kanbanShowTags', checkbox.checked);
    renderTasks();
}

function toggleKanbanNoDate() {
    const checkbox = document.getElementById('kanban-show-no-date');
    window.kanbanShowNoDate = checkbox.checked;
    localStorage.setItem('kanbanShowNoDate', checkbox.checked);
    renderTasks();
}

window.toggleKanbanSettings = toggleKanbanSettings;
window.toggleKanbanBacklog = toggleKanbanBacklog;
window.toggleKanbanProjects = toggleKanbanProjects;
window.toggleKanbanTags = toggleKanbanTags;
window.toggleKanbanNoDate = toggleKanbanNoDate;

// Close settings panel when clicking outside
document.addEventListener('click', (e) => {
    const panel = document.getElementById('kanban-settings-panel');
    const btn = document.getElementById('kanban-settings-btn');
    if (panel && !panel.contains(e.target) && e.target !== btn && !btn.contains(e.target)) {
        panel.classList.remove('active');
    }
});

async function updateTaskField(field, value) {
  const form = document.getElementById('task-form');
  const taskId = form?.dataset.editingTaskId;
  if (!taskId) return;

  // Capture old task state for history tracking
  const oldTask = tasks.find(t => t.id === parseInt(taskId, 10));
  const oldTaskCopy = oldTask ? JSON.parse(JSON.stringify(oldTask)) : null;

  // Use task service to update field
  const normalizedValue = field === 'description' ? autoLinkifyDescription(value) : value;

  // Avoid unnecessary background rerenders when the value didn't actually change
  if (oldTask) {
    const prev = oldTask[field];
    let isSame = false;

    if (field === 'projectId') {
      const nextProjectId =
        normalizedValue === '' || normalizedValue === null || typeof normalizedValue === 'undefined'
          ? null
          : parseInt(normalizedValue, 10);
      const prevProjectId = typeof oldTask.projectId === 'number' ? oldTask.projectId : null;
      isSame = (Number.isNaN(nextProjectId) ? null : nextProjectId) === prevProjectId;
    } else {
      const prevStr = typeof prev === 'string' ? prev : (prev || '');
      const nextStr = typeof normalizedValue === 'string' ? normalizedValue : (normalizedValue || '');
      isSame = prevStr === nextStr;
    }

    if (isSame) return;
  }

  const result = updateTaskFieldService(parseInt(taskId, 10), field, normalizedValue, tasks, settings);
  if (!result.task) return;

  // Record history for field update
  if (window.historyService && oldTaskCopy) {
    window.historyService.recordTaskUpdated(oldTaskCopy, result.task);
  }

  // Capture the project the detail view is currently showing this task under
  const prevProjectId = result.oldProjectId;

  // Update global tasks array BEFORE checking project status
  // (getProjectStatus reads from global tasks)
  tasks = result.tasks;
  const task = result.task;

  // Check and record project status change when task status changes
  if (field === 'status' && task.projectId) {
    checkAndRecordProjectStatusChange(task.projectId, task);
  }

  // Project-related changes can affect presence of "No Project" option
  if (field === 'projectId') {
    populateProjectOptions();
    populateCalendarTaskProjectOptions();
    const newProjectId = task.projectId;
    if (prevProjectId !== newProjectId) {
        if (prevProjectId) {
            const proj = touchProjectUpdatedAt(prevProjectId);
            if (proj) saveProjectUpdateData(prevProjectId, { updatedAt: proj.updatedAt }).catch(() => {});
            recordProjectTaskLinkChange(prevProjectId, 'removed', task);
        }
        if (newProjectId) {
            const proj = touchProjectUpdatedAt(newProjectId);
            if (proj) saveProjectUpdateData(newProjectId, { updatedAt: proj.updatedAt }).catch(() => {});
            recordProjectTaskLinkChange(newProjectId, 'added', task);
        }
    }
  }

    if (field === 'endDate') {
        // Toggle "No Date" option visibility on end date changes
        updateNoDateOptionVisibility();
    }

  // Save in background (non-blocking for instant UX)
  // This is safe because updateTaskField is triggered by blur events
  if (result.task) {
      const changedFields = { [field]: result.task[field], updatedAt: result.task.updatedAt };
      if (field === 'status') {
          // A status change can also stamp/clear completedDate and auto-set
          // start/end dates; persist them or they are lost on the next load.
          Object.assign(changedFields, {
              completedDate:       result.task.completedDate || null,
              startDate:           result.task.startDate     || '',
              endDate:             result.task.endDate       || '',
              startDateWasEverSet: result.task.startDateWasEverSet,
              endDateWasEverSet:   result.task.endDateWasEverSet,
          });
      }
      saveTaskUpdateData(parseInt(taskId, 10), changedFields).catch(error => {
        console.error('Failed to save task field update:', error);
        showErrorNotification(t('error.saveChangesFailed'));
      });
  }

  // Check if we're in project details view
  const isInProjectDetails = document.getElementById("project-details").classList.contains("active");
    // Calendar refresh for fields that affect date/project placement
    const affectsPlacement =
      field === 'startDate' ||
      field === 'endDate' ||
      field === 'projectId' ||
      field === 'status' ||
      field === 'priority' ||
      field === 'title' ||
      field === 'tags';

    if (affectsPlacement && document.getElementById('calendar-view')?.classList.contains('active')) {
        renderCalendar();
    }

    // For fields like description, updating data is enough; avoid repainting background views.
    if (!affectsPlacement) return;

    if (isInProjectDetails) {
        // Special handling when changing the project from within a project's view
        if (field === 'projectId') {
            const newProjectId = task.projectId; // after update
            if (!newProjectId) {
                // Cleared to "No Project" -> stay on current project and refresh it so the task disappears
                if (prevProjectId) {
                    showProjectDetails(prevProjectId);
                    return;
                }
            } else if (prevProjectId !== newProjectId) {
                // Moved to a different project -> navigate to the new project's details
                showProjectDetails(newProjectId);
                return;
            } else {
                // Still in the same project -> refresh current project view
                showProjectDetails(newProjectId);
                return;
            }
        } else {
            // For other field updates while viewing project details, refresh the current project
            const currentProjectId = prevProjectId
                || (window.location.hash && window.location.hash.startsWith('#project-')
                        ? parseInt(window.location.hash.replace('#project-',''), 10)
                        : null);
            if (currentProjectId) {
                showProjectDetails(currentProjectId);
                return;
            }
        }
    } else {
    // Otherwise refresh the main views
    renderTasks();
    // Always render list view on mobile (for mobile cards) or when active on desktop
    const isMobile = getIsMobileCached();
    if (isMobile || document.getElementById('list-view').classList.contains('active')) renderListView();
    if (document.getElementById('projects').classList.contains('active')) {
        // Re-apply filters so active search/filter chips are respected after task update
        applyProjectFilters();
        updateCounts();
    }
    // Keep dashboard widgets live when the task modal was opened from a dashboard gadget
    if (document.getElementById('dashboard')?.classList.contains('active')) {
        renderDashboard();
    }
  }

  // Refresh date fields in UI if status change auto-filled them
  // Use setTimeout to ensure this happens after any rendering completes
  if (field === 'status' && (settings.autoSetStartDateOnStatusChange || settings.autoSetEndDateOnStatusChange)) {
    setTimeout(() => {
      const formNow = document.getElementById('task-form');
      if (!formNow) return; // Form might have closed

      const startDateInput = formNow.querySelector('input[name="startDate"]');
      const endDateInput = formNow.querySelector('input[name="endDate"]');

      // Update Start Date using Flatpickr API
      if (startDateInput && task.startDate) {
        const fpStart = startDateInput._flatpickrInstance;
        if (fpStart) {
          fpStart.setDate(new Date(task.startDate), false);
        }
        startDateInput.value = task.startDate;

        // Also update display input if it exists
        const displayStart = startDateInput.parentElement?.querySelector("input.date-display");
        if (displayStart) {
          displayStart.value = toDMYFromISO(task.startDate);
        }
      }

      // Update End Date using Flatpickr API
      if (endDateInput && task.endDate) {
        const fpEnd = endDateInput._flatpickrInstance;
        if (fpEnd) {
          fpEnd.setDate(new Date(task.endDate), false);
        }
        endDateInput.value = task.endDate;

        // Also update display input if it exists
        const displayEnd = endDateInput.parentElement?.querySelector("input.date-display");
        if (displayEnd) {
          displayEnd.value = toDMYFromISO(task.endDate);
        }
      }
    }, 50);
  }
}

// === Click outside to close task/project modals (keep ESC intact) ===

// Store initial form state to detect actual changes (not just default values)
let initialTaskFormState = null;

// Capture initial form state after defaults are set
function captureInitialTaskFormState() {
  const form = document.getElementById('task-form');
  if (!form) return;

  initialTaskFormState = {
    title: form.querySelector('input[name="title"]')?.value.trim() || "",
    description: document.getElementById('task-description-hidden')?.value.trim() || "",
    projectId: form.querySelector('input[name="projectId"]')?.value || form.querySelector('select[name="projectId"]')?.value || "",
    startDate: form.querySelector('input[name="startDate"]')?.value || "",
    endDate: form.querySelector('input[name="endDate"]')?.value || "",
    priority: form.querySelector('#hidden-priority')?.value || "medium",
    status: form.querySelector('#hidden-status')?.value || "todo",
    tags: window.tempTags ? [...window.tempTags] : [],
    attachments: tempAttachments ? tempAttachments.length : 0
  };
}

// Detect unsaved data in NEW (not editing) task
function hasUnsavedNewTask() {
  const form = document.getElementById('task-form');
  if (!form) return false;
  if (form.dataset.editingTaskId) return false;
  if (!initialTaskFormState) return false; // No initial state captured

  const currentState = {
    title: form.querySelector('input[name="title"]')?.value.trim() || "",
    description: document.getElementById('task-description-hidden')?.value.trim() || "",
    projectId: form.querySelector('input[name="projectId"]')?.value || form.querySelector('select[name="projectId"]')?.value || "",
    startDate: form.querySelector('input[name="startDate"]')?.value || "",
    endDate: form.querySelector('input[name="endDate"]')?.value || "",
    priority: form.querySelector('#hidden-priority')?.value || "medium",
    status: form.querySelector('#hidden-status')?.value || "todo",
    tags: window.tempTags ? [...window.tempTags] : [],
    attachments: tempAttachments ? tempAttachments.length : 0
  };

  // Compare current state with initial state
  return (
    currentState.title !== initialTaskFormState.title ||
    currentState.description !== initialTaskFormState.description ||
    currentState.projectId !== initialTaskFormState.projectId ||
    currentState.startDate !== initialTaskFormState.startDate ||
    currentState.endDate !== initialTaskFormState.endDate ||
    currentState.priority !== initialTaskFormState.priority ||
    currentState.status !== initialTaskFormState.status ||
    currentState.tags.length !== initialTaskFormState.tags.length ||
    currentState.attachments !== initialTaskFormState.attachments
  );
}

// Attach click-outside behavior to specific modals
function bindOverlayClose(modalId) {
  const modal = document.getElementById(modalId);
  if (!modal || modal.__overlayBound) return;
  modal.__overlayBound = true;

  modal.addEventListener('pointerdown', e => {
    if (modalId === 'ask-kai-modal') {
      const drawer = modal.querySelector('.ask-kai-drawer');
      modal.__pointerStartedInsideContent = !!drawer && drawer.contains(e.target);
      modal.__pointerDownX = typeof e.clientX === 'number' ? e.clientX : 0;
      modal.__pointerDownY = typeof e.clientY === 'number' ? e.clientY : 0;
      modal.__pointerDragged = false;
    }
    modal.__downOnOverlay = (e.target === modal);
  });

  if (modalId === 'ask-kai-modal') {
    modal.addEventListener('pointermove', e => {
      if (typeof modal.__pointerDownX !== 'number' || typeof modal.__pointerDownY !== 'number') return;
      const dx = Math.abs((typeof e.clientX === 'number' ? e.clientX : 0) - modal.__pointerDownX);
      const dy = Math.abs((typeof e.clientY === 'number' ? e.clientY : 0) - modal.__pointerDownY);
      if (dx > 6 || dy > 6) {
        modal.__pointerDragged = true;
      }
    });
  }

  modal.addEventListener('click', e => {
    if (modalId === 'ask-kai-modal' && (modal.__pointerStartedInsideContent || modal.__pointerDragged)) {
      modal.__pointerStartedInsideContent = false;
      modal.__pointerDragged = false;
      modal.__pointerDownX = null;
      modal.__pointerDownY = null;
      modal.__downOnOverlay = false;
      return;
    }

    const releasedOnOverlay = (e.target === modal);
    const startedOnOverlay = !!modal.__downOnOverlay;
    modal.__downOnOverlay = false;
    if (modalId === 'ask-kai-modal') {
      modal.__pointerDownX = null;
      modal.__pointerDownY = null;
      modal.__pointerDragged = false;
    }
    if (!releasedOnOverlay || !startedOnOverlay) return;

    // If closing an existing task via overlay, persist description first
    if (modalId === 'task-modal') {
      const form = document.getElementById('task-form');
      if (form && form.dataset.editingTaskId) {
        updateTaskField('description', getTaskDescriptionHTML());
      }
    }

    if (modalId === 'task-modal' && hasUnsavedNewTask()) {
      // Show custom unsaved changes modal
      showUnsavedChangesModal(modalId);
      return;
    }
    if (modalId === 'task-modal') {
      closeTaskModal();
    } else {
      closeModal(modalId);
    }
  });
}

// Bind to your existing modals
document.addEventListener('DOMContentLoaded', () => {
  bindOverlayClose('task-modal');
  bindOverlayClose('project-modal');
  bindOverlayClose('settings-modal');
  bindOverlayClose('ask-kai-modal');
  bindOverlayClose('docs-trash-modal');
  bindOverlayClose('docs-trash-preview-modal');
  bindOverlayClose('docs-share-modal');
});

// === ESC key close (existing behavior retained) ===
document.addEventListener('keydown', async e => {
  if (e.key === 'Escape') {
        const modals = document.querySelectorAll('.modal.active');
        modals.forEach(m => {
            if (m.id === 'task-modal') {
                if (hasUnsavedNewTask()) {
                    showUnsavedChangesModal('task-modal');
                } else {
                    // Persist description for editing tasks before closing via ESC
                    const form = document.getElementById('task-form');
                    if (form && form.dataset.editingTaskId) {
                        updateTaskField('description', getTaskDescriptionHTML());
                    }
                    closeTaskModal();
                }
            } else {
                if (m.id === 'settings-modal' && window.settingsFormIsDirty) {
                    showUnsavedChangesModal('settings-modal');
                } else {
                    closeModal(m.id);
                }
            }
        });
  }

});


async function addTag() {
    const input = document.getElementById('tag-input');
    const tagName = input.value.trim().toLowerCase();
    
    if (!tagName) {
        input.style.border = '2px solid var(--accent-red, #ef4444)';
        setTimeout(() => { input.style.border = ''; }, 2000);
        return;
    }
    
    const taskId = document.getElementById('task-form').dataset.editingTaskId;
    
    if (taskId) {
        const task = tasks.find(t => t.id === parseInt(taskId));
        if (!task) return;
        if (!task.tags) task.tags = [];
        if (task.tags.includes(tagName)) {
            input.value = '';
            return; // Already exists
        }

        // Store old state for history
        const oldTaskCopy = JSON.parse(JSON.stringify(task));

        task.tags = [...task.tags, tagName];
        renderTags(task.tags);


        // Record history
        if (window.historyService) {
            window.historyService.recordTaskUpdated(oldTaskCopy, task);
        }

        const refreshedProjectViews = refreshProjectViews(task.projectId);
        if (!refreshedProjectViews) {
            // Refresh Kanban view immediately
            renderTasks();

            const isMobile = getIsMobileCached();
            if (isMobile || document.getElementById('list-view').classList.contains('active')) {
                renderListView();
            }
        }

        populateTagOptions(); // Refresh tag dropdown only
        populateCalendarTaskTagOptions();

        // Save in background
        task.updatedAt = new Date().toISOString();
        saveTaskUpdateData(parseInt(taskId), { tags: task.tags, updatedAt: task.updatedAt }).catch(error => {
            console.error('Failed to save tag addition:', error);
            showErrorNotification(t('error.saveTagFailed'));
        });
    } else {
        // Creating new task - use temp array
        if (!window.tempTags) window.tempTags = [];
        if (window.tempTags.includes(tagName)) {
            input.value = '';
            return;
        }
        window.tempTags = [...window.tempTags, tagName];
        renderTags(window.tempTags);
    }
    
    input.value = '';
}

async function removeTag(tagName) {
    const taskId = document.getElementById('task-form').dataset.editingTaskId;

    if (taskId) {
        const task = tasks.find(t => t.id === parseInt(taskId));
        if (!task || !task.tags) return;

        // Store old state for history
        const oldTaskCopy = JSON.parse(JSON.stringify(task));

        task.tags = task.tags.filter(t => t !== tagName);
        renderTags(task.tags);

        // Record history
        if (window.historyService) {
            window.historyService.recordTaskUpdated(oldTaskCopy, task);
        }

        const refreshedProjectViews = refreshProjectViews(task.projectId);

        // Save in background
        task.updatedAt = new Date().toISOString();
        saveTaskUpdateData(parseInt(taskId), { tags: task.tags, updatedAt: task.updatedAt }).catch(error => {
            console.error('Failed to save tag removal:', error);
            showErrorNotification(t('error.removeTagFailed'));
        });

        if (!refreshedProjectViews) {
            renderTasks();
            const isMobile = getIsMobileCached();
            if (isMobile || document.getElementById('list-view').classList.contains('active')) {
                renderListView();
            }
        }

        populateTagOptions(); // Refresh tag dropdown only
        populateCalendarTaskTagOptions();
    } else {
        if (!window.tempTags) window.tempTags = [];
        window.tempTags = window.tempTags.filter(t => t !== tagName);
        renderTags(window.tempTags);
    }
}

function renderTags(tags) {
    const container = document.getElementById('tags-display');
    const isMobile = getIsMobileCached();

    container.innerHTML = generateTagsDisplayHTML(tags, {
        isMobile,
        getTagColor,
        getTagTextColor,
        escapeHtml,
        noTagsText: t('tasks.tags.none')
    });
}

function refreshProjectViews(projectId) {
    const normalizedProjectId = parseInt(projectId, 10);
    if (!Number.isInteger(normalizedProjectId)) return false;

    const projectsPageActive = document.getElementById('projects')?.classList.contains('active');
    const projectDetailsActive = document.getElementById('project-details')?.classList.contains('active');
    if (!projectsPageActive && !projectDetailsActive) return false;

    appState.projectsSortedView = null;
    renderProjects();

    if (projectDetailsActive) {
        const currentDetailsProjectId = parseInt(
            document.getElementById('project-title-display')?.dataset.param,
            10
        );
        if (currentDetailsProjectId === normalizedProjectId) {
            showProjectDetails(normalizedProjectId);
        }
    }

    return true;
}

// === Project Color Picker (create modal) ===
function renderNewProjectColorPicker() {
    const currentColor = window.tempProjectColor || PROJECT_COLORS[0];

    // Update swatch button color
    const swatch = document.getElementById('new-project-color-swatch');
    if (swatch) swatch.style.backgroundColor = currentColor;

    // Update grid if open
    const grid = document.querySelector('.new-project-color-picker-dropdown .color-grid');
    if (!grid) return;
    grid.innerHTML = PROJECT_COLORS.map(color => {
        const active = color === currentColor ? ' active' : '';
        return `<div class="color-option${active}" style="background-color:${color};" data-action="selectNewProjectColor" data-param="${color}"></div>`;
    }).join('');
}

// Keep old name as alias so openProjectModal call still works
function renderNewProjectColorSwatches() { renderNewProjectColorPicker(); }

function toggleNewProjectColorPicker() {
    let dropdown = document.getElementById('new-project-color-picker-dropdown');
    if (!dropdown) {
        dropdown = document.createElement('div');
        dropdown.id = 'new-project-color-picker-dropdown';
        dropdown.className = 'new-project-color-picker-dropdown';
        dropdown.innerHTML = '<div class="color-grid"></div>';
        document.body.appendChild(dropdown);

        // Close on outside click
        document.addEventListener('click', function handler(e) {
            const trigger = document.getElementById('new-project-color-trigger');
            if (!dropdown.contains(e.target) && e.target !== trigger && !trigger?.contains(e.target)) {
                dropdown.style.display = 'none';
                document.removeEventListener('click', handler, true);
            }
        }, true);
    }

    if (dropdown.style.display === 'block') {
        dropdown.style.display = 'none';
        return;
    }

    // Position fixed below the trigger
    const trigger = document.getElementById('new-project-color-trigger');
    if (trigger) {
        const rect = trigger.getBoundingClientRect();
        dropdown.style.top = (rect.bottom + 6) + 'px';
        dropdown.style.left = rect.left + 'px';
    }

    dropdown.style.display = 'block';
    renderNewProjectColorPicker();
}

function toggleProjectTemplateSection() {
    const section = document.getElementById('project-template-section');
    const btn = document.getElementById('template-toggle-btn');
    if (!section) return;
    const isOpen = section.style.display !== 'none';
    section.style.display = isOpen ? 'none' : 'block';
    if (btn) btn.classList.toggle('active', !isOpen);
}

function selectNewProjectColor(color) {
    window.tempProjectColor = color;
    // Close dropdown
    const dropdown = document.getElementById('new-project-color-picker-dropdown');
    if (dropdown) dropdown.style.display = 'none';
    renderNewProjectColorPicker();
    // Re-render tags so they reflect the new color
    renderProjectTags(window.tempProjectTags || []);
}

// === Project Tag Management ===
async function addProjectTag() {
    const input = document.getElementById('project-tag-input');
    const tagName = input.value.trim().toLowerCase();

    if (!tagName) {
        input.style.border = '2px solid var(--accent-red, #ef4444)';
        setTimeout(() => { input.style.border = ''; }, 2000);
        return;
    }

    const projectId = document.getElementById('project-form').dataset.editingProjectId;

    if (projectId) {
        const project = projects.find(p => p.id === parseInt(projectId));
        if (!project) return;
        if (!project.tags) project.tags = [];
        if (project.tags.includes(tagName)) {
            input.value = '';
            return; // Already exists
        }

        project.tags = [...project.tags, tagName];
        renderProjectTags(project.tags);

        refreshProjectViews(projectId);

        // Refresh tag dropdown
        populateProjectTagOptions();

        // Save using row-level operation
        const now = new Date().toISOString();
        saveProjectUpdate(parseInt(projectId), { tags: project.tags, updatedAt: now }).catch(error => {
            console.error('Failed to save project tag addition:', error);
            showErrorNotification(t('error.saveTagFailed'));
        });
    } else {
        // Creating new project - use temp array
        if (!window.tempProjectTags) window.tempProjectTags = [];
        if (window.tempProjectTags.includes(tagName)) {
            input.value = '';
            return;
        }
        window.tempProjectTags = [...window.tempProjectTags, tagName];
        renderProjectTags(window.tempProjectTags);
    }

    input.value = '';
}

async function removeProjectTag(tagName) {
    const projectId = document.getElementById('project-form').dataset.editingProjectId;

    if (projectId) {
        const project = projects.find(p => p.id === parseInt(projectId));
        if (!project || !project.tags) return;

        project.tags = project.tags.filter(t => t !== tagName);
        renderProjectTags(project.tags);

        refreshProjectViews(projectId);

        // Refresh tag dropdown
        populateProjectTagOptions();

        // Save using row-level operation
        const now = new Date().toISOString();
        saveProjectUpdate(parseInt(projectId), { tags: project.tags, updatedAt: now }).catch(error => {
            console.error('Failed to save project tag removal:', error);
            showErrorNotification(t('error.saveTagFailed'));
        });
    } else {
        // Creating new project - remove from temp array
        if (!window.tempProjectTags) return;
        window.tempProjectTags = window.tempProjectTags.filter(t => t !== tagName);
        renderProjectTags(window.tempProjectTags);
    }
}

function renderProjectTags(tags) {
    const container = document.getElementById('project-tags-display');
    if (!tags || tags.length === 0) {
        container.innerHTML = `<span style="color: var(--text-muted); font-size: 13px;">${t('tasks.tags.none')}</span>`;
        return;
    }

    // Get project color: editing project → stored color, new project → chosen temp color or first palette color
    const projectId = document.getElementById('project-form')?.dataset.editingProjectId;
    const color = projectId ? getProjectColor(parseInt(projectId)) : (window.tempProjectColor || PROJECT_COLORS[0]);

    // Detect mobile for smaller tag sizes
    const isMobile = getIsMobileCached();
    const padding = isMobile ? '3px 6px' : '4px 8px';
    const fontSize = isMobile ? '11px' : '12px';
    const gap = isMobile ? '4px' : '4px';
    const buttonSize = isMobile ? '12px' : '14px';
    const lineHeight = isMobile ? '1.2' : '1.4';

    container.innerHTML = tags.map(tag => {
        return `
            <span class="task-tag" style="${getTintedTagStyle(color)} padding: ${padding}; border-radius: 4px; font-size: ${fontSize}; display: inline-flex; align-items: center; gap: ${gap}; line-height: ${lineHeight};">
                ${escapeHtml(tag.toUpperCase())}
                <button type="button" data-action="removeProjectTag" data-param="${escapeHtml(tag)}" style="background: none; border: none; color: inherit; cursor: pointer; padding: 0; margin: 0; font-size: ${buttonSize}; line-height: 1; display: inline-flex; align-items: center; justify-content: center; width: auto; min-width: auto;">×</button>
            </span>
        `;
    }).join('');
}

// === Project Details Tags Management ===
function renderProjectDetailsTags(tags, projectId) {
    const container = document.getElementById('project-details-tags-display');
    if (!container) return;

    if (!tags || tags.length === 0) {
        container.innerHTML = `<span style="color: var(--text-muted); font-size: 13px;">${t('tasks.tags.none')}</span>`;
        return;
    }

    // Use project color for all tags (or default gray if no projectId)
    const color = projectId ? getProjectColor(projectId) : '#6b7280';

    // Detect mobile for smaller tag sizes
    const isMobile = getIsMobileCached();
    const padding = isMobile ? '3px 6px' : '4px 8px';
    const fontSize = isMobile ? '11px' : '12px';
    const gap = isMobile ? '4px' : '4px';
    const buttonSize = isMobile ? '12px' : '14px';
    const lineHeight = isMobile ? '1.2' : '1.4';

    container.innerHTML = tags.map(tag => {
        return `
            <span class="task-tag" style="${getTintedTagStyle(color)} padding: ${padding}; border-radius: 4px; font-size: ${fontSize}; display: inline-flex; align-items: center; gap: ${gap}; line-height: ${lineHeight};">
                ${escapeHtml(tag.toUpperCase())}
                <button type="button" data-action="removeProjectDetailsTag" data-param="${escapeHtml(tag)}" style="background: none; border: none; color: inherit; cursor: pointer; padding: 0; margin: 0; font-size: ${buttonSize}; line-height: 1; display: inline-flex; align-items: center; justify-content: center; width: auto; min-width: auto;">×</button>
            </span>
        `;
    }).join('');
}

async function addProjectDetailsTag(projectId) {
    const input = document.getElementById('project-details-tag-input');
    if (!input) return;

    const tagName = input.value.trim().toLowerCase();

    if (!tagName) {
        input.style.border = '2px solid var(--accent-red, #ef4444)';
        setTimeout(() => { input.style.border = ''; }, 2000);
        return;
    }

    const project = projects.find(p => p.id === parseInt(projectId));
    if (!project) return;

    if (!project.tags) project.tags = [];
    if (project.tags.includes(tagName)) {
        input.value = '';
        return; // Already exists
    }

    project.tags = [...project.tags, tagName];
    renderProjectDetailsTags(project.tags, projectId);

    // Keep the projects list in sync without rebuilding the active details panel.
    appState.projectsSortedView = null;
    renderProjects();

    // Refresh tag dropdown
    populateProjectTagOptions();

    // Save using row-level operation
    const now = new Date().toISOString();
    saveProjectUpdate(projectId, { tags: project.tags, updatedAt: now }).catch(error => {
        console.error('Failed to save project tag addition:', error);
        showErrorNotification(t('error.saveTagFailed'));
    });

    input.value = '';
}

async function removeProjectDetailsTag(tagName) {
    // Get project ID from the current project details view
    const titleDisplay = document.getElementById('project-title-display');
    if (!titleDisplay) return;

    const projectId = parseInt(titleDisplay.dataset.param);
    const project = projects.find(p => p.id === projectId);
    if (!project || !project.tags) return;

    project.tags = project.tags.filter(t => t !== tagName);
    renderProjectDetailsTags(project.tags, projectId);

    // Keep the projects list in sync without rebuilding the active details panel.
    appState.projectsSortedView = null;
    renderProjects();

    // Refresh tag dropdown
    populateProjectTagOptions();

    // Save using row-level operation
    const now2 = new Date().toISOString();
    saveProjectUpdate(projectId, { tags: project.tags, updatedAt: now2 }).catch(error => {
        console.error('Failed to save project tag removal:', error);
        showErrorNotification(t('error.saveTagFailed'));
    });
}


// ─────────────────────────────────────────────────────────────────────────────
// === Archive / Restore — Tasks & Projects ===
// Non-destructive hide. archived_at is the only mutated field. Status is
// never touched. Active operational views exclude archived items by default.
// ─────────────────────────────────────────────────────────────────────────────

let projectArchiveState = { projectId: null };
let projectRestoreState = { projectId: null };

function _resolveTaskFromContext(taskId) {
    if (taskId == null || !Number.isFinite(taskId)) {
        const form = document.getElementById('task-form');
        const idStr = form?.dataset?.editingTaskId;
        if (idStr) taskId = parseInt(idStr, 10);
    }
    if (!Number.isFinite(taskId)) return null;
    return tasks.find(t => t.id === taskId) || null;
}

async function archiveTask(taskId) {
    const task = _resolveTaskFromContext(taskId);
    if (!task) return;
    if (task.archivedAt) return; // already archived
    const now = new Date().toISOString();
    task.archivedAt = now;
    task.updatedAt  = now;
    try { closeTaskModal && closeTaskModal(); } catch (_) {}
    try { render && render(); } catch (_) {}
    try { updateCounts && updateCounts(); } catch (_) {}
    try {
        await saveTaskUpdate(task.id, { archivedAt: now });
        showNotification(t('toast.task.archived'), 'success');
    } catch (err) {
        console.error('[archive] task save failed', err);
        task.archivedAt = null;
        try { render && render(); } catch (_) {}
    }
}

async function restoreTask(taskId) {
    const task = _resolveTaskFromContext(taskId);
    if (!task) return;
    if (!task.archivedAt) return;
    const previousArchivedAt = task.archivedAt;
    const previousArchivedByAutomationId = task.archivedByAutomationId ?? null;
    task.archivedAt = null;
    task.archivedByAutomationId = null;
    task.updatedAt  = new Date().toISOString();
    try { closeTaskModal && closeTaskModal(); } catch (_) {}
    try { render && render(); } catch (_) {}
    try { updateCounts && updateCounts(); } catch (_) {}
    try {
        await saveTaskUpdate(task.id, { archivedAt: null, archivedByAutomationId: null });
        // If parent project is archived, surface a nudge.
        const parent = task.projectId ? projects.find(p => p.id === task.projectId) : null;
        if (parent && parent.archivedAt) {
            showNotification(
                t('toast.task.restoredParentArchived', { project: parent.name || '' }),
                'info'
            );
        } else {
            showNotification(t('toast.task.restored'), 'success');
        }
    } catch (err) {
        console.error('[archive] task restore failed', err);
        task.archivedAt = previousArchivedAt;
        task.archivedByAutomationId = previousArchivedByAutomationId;
        try { render && render(); } catch (_) {}
    }
}

function _getBulkSelectedTaskIds() {
    if (!massEditState || !massEditState.selectedTaskIds) return [];
    return Array.from(massEditState.selectedTaskIds);
}

async function bulkArchiveTasks() {
    const ids = _getBulkSelectedTaskIds();
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    const changed = [];
    for (const id of ids) {
        const t = tasks.find(x => x.id === id);
        if (!t || t.archivedAt) continue;
        t.archivedAt = now;
        t.updatedAt  = now;
        changed.push(t.id);
    }
    if (changed.length === 0) return;
    clearMassEditSelection && clearMassEditSelection();
    render && render();
    updateCounts && updateCounts();
    try {
        await Promise.all(changed.map(id => saveTaskUpdate(id, { archivedAt: now })));
        showNotification(t('toast.tasks.archivedCount', { count: changed.length }), 'success');
    } catch (err) {
        console.error('[archive] bulk archive failed', err);
        showErrorNotification(t('error.saveChangesFailed'));
    }
}

async function bulkRestoreTasks() {
    const ids = _getBulkSelectedTaskIds();
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    const changed = [];
    for (const id of ids) {
        const t = tasks.find(x => x.id === id);
        if (!t || !t.archivedAt) continue;
        t.archivedAt = null;
        t.archivedByAutomationId = null;
        t.updatedAt  = now;
        changed.push(t.id);
    }
    if (changed.length === 0) return;
    clearMassEditSelection && clearMassEditSelection();
    render && render();
    updateCounts && updateCounts();
    try {
        await Promise.all(changed.map(id => saveTaskUpdate(id, { archivedAt: null, archivedByAutomationId: null })));
        showNotification(t('toast.tasks.restoredCount', { count: changed.length }), 'success');
    } catch (err) {
        console.error('[archive] bulk restore failed', err);
        showErrorNotification(t('error.saveChangesFailed'));
    }
}

let _viewBeforeArchived = 'kanban';
let archivedTaskSelectionState = {
    selectedTaskIds: new Set()
};
let archivedAutomatedOnly = false;

function toggleArchivedAutomatedFilter() {
    archivedAutomatedOnly = !archivedAutomatedOnly;
    const btn = document.getElementById('archived-automated-filter-btn');
    if (btn) btn.classList.toggle('is-active', archivedAutomatedOnly);
    renderArchivedView();
}
window.toggleArchivedAutomatedFilter = toggleArchivedAutomatedFilter;

function showArchivedTasksView(returnView = 'kanban') {
    _viewBeforeArchived = returnView || 'kanban';
    document.querySelectorAll('.view-btn').forEach((button) => button.classList.remove('active'));
    const archivedViewBtn = document.getElementById('archived-view-btn');
    if (archivedViewBtn) archivedViewBtn.classList.add('active');

    const pageTitle = document.querySelector('#tasks .page-title');
    if (pageTitle) pageTitle.textContent = t('tasks.archived.modalTitle');
    const pageSubtitle = document.querySelector('#tasks .page-subtitle');
    if (pageSubtitle) pageSubtitle.textContent = t('tasks.archived.modalSubtitle');

    document.querySelector('.kanban-board')?.classList.add('hidden');
    document.getElementById('list-view')?.classList.remove('active');
    document.getElementById('calendar-view')?.classList.remove('active');

    const kanbanHeader = document.querySelector('#tasks .kanban-header');
    if (kanbanHeader) {
        kanbanHeader.style.display = 'none';
        kanbanHeader.classList.remove('calendar-mode');
    }
    const globalFilters = document.getElementById('global-filters');
    if (globalFilters) globalFilters.style.display = '';

    const archivedViewEl = document.getElementById('archived-view');
    if (archivedViewEl) {
        archivedViewEl.style.display = '';
        archivedViewEl.classList.add('active');
    }
    renderArchivedView();
}

function getArchivedTasksForView() {
    return filterTasks(tasks, {
        search: filterState.search,
        statuses: filterState.statuses,
        statusExcludeMode: filterState.statusExcludeMode,
        priorities: filterState.priorities,
        priorityExcludeMode: filterState.priorityExcludeMode,
        projects: filterState.projects,
        projectExcludeMode: filterState.projectExcludeMode,
        tags: filterState.tags,
        tagExcludeMode: filterState.tagExcludeMode,
        tagAndMode: filterState.tagAndMode,
        datePresets: filterState.datePresets,
        dateFrom: filterState.dateFrom,
        dateTo: filterState.dateTo,
        dateField: filterState.dateField || 'endDate',
        taskIds: filterState.taskIds,
        linkTypes: filterState.linkTypes,
        duplicateTitles: filterState.duplicateTitles,
        dependencies,
        showArchivedOnly: true
    }, { timeZone: getUserTimeZone() })
        .filter((task) => !archivedAutomatedOnly || task.archivedByAutomationId != null)
        .sort((a, b) => (b.archivedAt || '').localeCompare(a.archivedAt || ''));
}

function updateArchivedSelectionUI(archivedTasks = getArchivedTasksForView()) {
    const visibleIds = new Set(archivedTasks.map(task => task.id));
    archivedTaskSelectionState.selectedTaskIds.forEach(id => {
        if (!visibleIds.has(id)) archivedTaskSelectionState.selectedTaskIds.delete(id);
    });

    const selectedCount = archivedTaskSelectionState.selectedTaskIds.size;
    const total = archivedTasks.length;
    const toolbar = document.getElementById('archived-bulk-toolbar');
    if (toolbar) toolbar.style.display = selectedCount > 0 ? 'flex' : 'none';

    const countEl = document.getElementById('archived-selection-count');
    if (countEl) {
        countEl.textContent = t('tasks.massEdit.selected', { count: selectedCount, total });
    }

    const syncSelectAll = (checkbox) => {
        if (!checkbox) return;
        checkbox.checked = total > 0 && selectedCount === total;
        checkbox.indeterminate = selectedCount > 0 && selectedCount < total;
    };
    syncSelectAll(document.getElementById('archived-select-all-toolbar'));
    syncSelectAll(document.getElementById('archived-select-all-table'));

    document.querySelectorAll('#archived-view-body tr[data-task-id]').forEach(row => {
        const taskId = parseInt(row.dataset.taskId, 10);
        const isSelected = archivedTaskSelectionState.selectedTaskIds.has(taskId);
        row.classList.toggle('mass-edit-selected', isSelected);
        const checkbox = row.querySelector('.archived-task-select-checkbox');
        if (checkbox) checkbox.checked = isSelected;
    });
}

function toggleArchivedTaskSelection(taskId) {
    const id = parseInt(taskId, 10);
    if (!Number.isFinite(id)) return;
    if (archivedTaskSelectionState.selectedTaskIds.has(id)) {
        archivedTaskSelectionState.selectedTaskIds.delete(id);
    } else {
        archivedTaskSelectionState.selectedTaskIds.add(id);
    }
    updateArchivedSelectionUI();
}

function toggleAllArchivedTasks() {
    const archived = getArchivedTasksForView();
    const allSelected = archived.length > 0
        && archived.every(task => archivedTaskSelectionState.selectedTaskIds.has(task.id));
    archivedTaskSelectionState.selectedTaskIds.clear();
    if (!allSelected) {
        archived.forEach(task => archivedTaskSelectionState.selectedTaskIds.add(task.id));
    }
    updateArchivedSelectionUI(archived);
}

function clearArchivedTaskSelection() {
    archivedTaskSelectionState.selectedTaskIds.clear();
    updateArchivedSelectionUI();
}

function renderArchivedView() {
    const tbody    = document.getElementById('archived-view-body');
    const countEl  = document.getElementById('archived-view-count');
    const countBottomEl = document.getElementById('archived-view-count-bottom');
    if (!tbody) return;

    const archived = getArchivedTasksForView();

    const countText = t('tasks.list.count', { count: archived.length }) || `${archived.length} results`;
    if (countEl) countEl.textContent = countText;
    if (countBottomEl) countBottomEl.textContent = countText;

    if (!archived.length) {
        const hasFilters = !!filterState.search
            || filterState.statuses.size > 0
            || filterState.priorities.size > 0
            || filterState.projects.size > 0
            || filterState.tags.size > 0
            || filterState.datePresets.size > 0
            || !!filterState.dateFrom
            || !!filterState.dateTo
            || filterState.linkTypes.size > 0;
        const emptyTitle = hasFilters ? t('tasks.archived.noResults') : t('tasks.archived.emptyTitle');
        const emptySubtitle = hasFilters ? t('tasks.archived.noResultsSubtitle') : t('tasks.archived.emptySubtitle');
        tbody.innerHTML = `<tr><td colspan="10" style="text-align:center;padding:48px 0;color:var(--text-muted);">${escapeHtml(emptyTitle)}<br><small>${escapeHtml(emptySubtitle)}</small></td></tr>`;
        updateArchivedSelectionUI(archived);
        return;
    }

    tbody.innerHTML = archived.map(task => {
        const project      = projects.find(p => p.id === task.projectId);
        const projectName  = project ? escapeHtml(project.name) : `<span style="color:var(--text-muted)">${escapeHtml(t('tasks.noProject'))}</span>`;
        const projectIndicator = project
            ? `<span style="display: inline-block; width: 10px; height: 10px; background-color: ${escapeHtml(getProjectColor(project.id))}; border-radius: 2px; margin-right: 8px; vertical-align: middle;"></span>`
            : '';
        const projectArchivedTag = (project && project.archivedAt)
            ? `<span class="project-archived-tag">${escapeHtml(t('projects.status.archived'))}</span>`
            : '';
        const priorityLabel = escapeHtml(getPriorityLabel ? getPriorityLabel(task.priority) : (task.priority || ''));
        const statusLabel  = escapeHtml(t(`tasks.status.${task.status}`) || task.status || '');
        const archivedDate = task.archivedAt ? formatDate(task.archivedAt.slice(0, 10)) : '—';
        const archiveRule = task.archivedByAutomationId != null
            ? automations.find(r => r.id === task.archivedByAutomationId)
            : null;
        const automatedTag = task.archivedByAutomationId != null
            ? `<span class="archived-automated-tag" title="${escapeHtml(archiveRule ? getLocalizedAutoArchiveSummary(archiveRule) : t('automations.type.autoArchive'))}">🔄 ${escapeHtml(t('tasks.archived.automated'))}</span>`
            : '';
        const startDate = task.startDate ? formatDate(task.startDate) : escapeHtml(t('tasks.noDate'));
        const endDate = task.endDate ? formatDate(task.endDate) : escapeHtml(t('tasks.noDate'));
        const selected = archivedTaskSelectionState.selectedTaskIds.has(task.id);
        const tags = task.tags && task.tags.length > 0
            ? (() => {
                const visibleTags = task.tags.slice(0, 10);
                const hiddenCount = Math.max(task.tags.length - visibleTags.length, 0);
                const rendered = visibleTags.map(tag => {
                    const bg = getTagColor ? getTagColor(tag) : '#555';
                    return `<span style="${getTintedTagStyle(escapeHtml(bg))} padding: 2px 6px; border-radius: 3px; font-size: 10px; margin-right: 4px; font-weight: 500; white-space: nowrap;">${escapeHtml(String(tag).toUpperCase())}</span>`;
                }).join('');
                const overflow = hiddenCount > 0
                    ? `<span style="background: var(--bg-tertiary); color: var(--text-secondary); border: 1px solid var(--border); padding: 2px 6px; border-radius: 3px; font-size: 10px; margin-right: 4px; font-weight: 600; white-space: nowrap;">+${hiddenCount}</span>`
                    : '';
                return rendered + overflow;
            })()
            : '<span style="color:var(--text-muted); font-size: 12px;">-</span>';

        return `<tr class="task-row${selected ? ' mass-edit-selected' : ''}" data-task-id="${task.id}">
            <td class="mass-edit-col">
                <input type="checkbox" class="task-select-checkbox archived-task-select-checkbox" data-action="toggleArchivedTaskSelection" data-param="${task.id}" ${selected ? 'checked' : ''}>
            </td>
            <td class="task-title-col" data-action="openTaskDetails" data-param="${task.id}" style="font-weight:500">${escapeHtml(task.title || t('tasks.untitled'))}</td>
            <td data-action="openTaskDetails" data-param="${task.id}"><span class="priority-badge priority-${escapeHtml(task.priority || '')}">${priorityLabel}</span></td>
            <td data-action="openTaskDetails" data-param="${task.id}"><span class="status-badge ${escapeHtml(task.status || '')}">${statusLabel}</span></td>
            <td class="archived-tags-col" data-action="openTaskDetails" data-param="${task.id}">${tags}</td>
            <td class="archived-project-col" data-action="openTaskDetails" data-param="${task.id}">${projectIndicator}${projectName}${projectArchivedTag}</td>
            <td data-action="openTaskDetails" data-param="${task.id}">${startDate}</td>
            <td data-action="openTaskDetails" data-param="${task.id}">${endDate}</td>
            <td data-action="openTaskDetails" data-param="${task.id}" style="color:var(--text-muted);font-size:13px">${escapeHtml(archivedDate)}${automatedTag}</td>
            <td class="archived-actions-col"><button type="button" class="archived-restore-btn"
                data-action="restoreArchivedTask" data-param="${task.id}">${escapeHtml(t('tasks.modal.restore').replace('↩️ ', ''))}</button></td>
        </tr>`;
    }).join('');
    updateArchivedSelectionUI(archived);
}

async function restoreArchivedTask(taskId) {
    await restoreTask(taskId);
    archivedTaskSelectionState.selectedTaskIds.delete(parseInt(taskId, 10));
    renderArchivedView();
}

async function bulkRestoreArchivedTasks() {
    const ids = Array.from(archivedTaskSelectionState.selectedTaskIds);
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    const changed = [];
    for (const id of ids) {
        const task = tasks.find(t => t.id === id);
        if (!task || !task.archivedAt) continue;
        task.archivedAt = null;
        task.archivedByAutomationId = null;
        task.updatedAt = now;
        changed.push(task.id);
    }
    if (changed.length === 0) {
        clearArchivedTaskSelection();
        return;
    }

    archivedTaskSelectionState.selectedTaskIds.clear();
    render && render();
    renderArchivedView();
    updateCounts && updateCounts();
    try {
        await Promise.all(changed.map(id => saveTaskUpdate(id, { archivedAt: null, archivedByAutomationId: null })));
        showNotification(t('toast.tasks.restoredCount', { count: changed.length }), 'success');
    } catch (err) {
        console.error('[archive] archived bulk restore failed', err);
        showErrorNotification(t('error.saveChangesFailed'));
    }
}

// ── Project archive modal ──────────────────────────────────────────────────
function openProjectArchiveModal(projectId) {
    const pid = parseInt(projectId, 10);
    const project = projects.find(p => p.id === pid);
    if (!project) return;
    projectArchiveState.projectId = pid;

    const counts = summarizeProjectTasks(pid, tasks);
    const titleEl = document.getElementById('project-archive-title');
    if (titleEl) titleEl.textContent = t('projects.archive.modalTitleWithName', { name: project.name || '' });

    const setCount = (id, val) => {
        const el = document.getElementById(id);
        if (el) el.textContent = String(val);
    };
    setCount('project-archive-count-open',      counts.open);
    setCount('project-archive-count-completed', counts.completed);
    setCount('project-archive-count-archived',  counts.archived);

    // Reset radio to safe default
    const def = document.querySelector('input[name="project-archive-mode"][value="completed"]');
    if (def) def.checked = true;

    const modal = document.getElementById('project-archive-modal');
    if (modal) modal.classList.add('active');

    // Close project options menu if open
    const menu = document.getElementById('project-options-menu');
    if (menu) menu.style.display = 'none';
}

function closeProjectArchiveModal() {
    const modal = document.getElementById('project-archive-modal');
    if (modal) modal.classList.remove('active');
    projectArchiveState.projectId = null;
}

async function confirmProjectArchive() {
    const pid = projectArchiveState.projectId;
    if (!pid) return;
    const modeEl = document.querySelector('input[name="project-archive-mode"]:checked');
    const mode   = modeEl ? modeEl.value : 'completed';

    const result = archiveProjectService(pid, projects, tasks, mode);
    if (!result.project) { closeProjectArchiveModal(); return; }
    projects = result.projects;
    tasks    = result.tasks;
    closeProjectArchiveModal();
    appState.projectsSortedView = null;
    if (document.getElementById('projects')?.classList.contains('active')) {
        applyProjectFilters();
    } else {
        render && render();
    }
    updateCounts && updateCounts();

    try {
        const reqs = [
            saveProjectUpdate(pid, { archivedAt: result.archivedAt })
        ];
        for (const tid of result.affectedTaskIds) {
            reqs.push(saveTaskUpdate(tid, { archivedAt: result.archivedAt }));
        }
        await Promise.all(reqs);
        const archivedTaskCount = result.affectedTaskIds.length;
        showNotification(
            t(getProjectArchiveToastKey(archivedTaskCount, 'archive'), { count: archivedTaskCount }),
            'success'
        );
    } catch (err) {
        console.error('[archive] project archive failed', err);
        showErrorNotification(t('error.saveChangesFailed'));
    }
}

// ── Project restore modal ──────────────────────────────────────────────────
function openProjectRestoreModal(projectId) {
    const pid = parseInt(projectId, 10);
    const project = projects.find(p => p.id === pid);
    if (!project) return;

    const counts = summarizeProjectTasks(pid, tasks);
    // No archived children → restore directly with no modal.
    if (counts.archived === 0) {
        projectRestoreState.projectId = pid;
        const restoreOnly = true;
        _executeProjectRestore(pid, !restoreOnly /* false */);
        return;
    }

    projectRestoreState.projectId = pid;
    const titleEl = document.getElementById('project-restore-title');
    if (titleEl) titleEl.textContent = t('projects.restore.modalTitleWithName', { name: project.name || '' });
    const countEl = document.getElementById('project-restore-archived-count');
    if (countEl) countEl.textContent = String(counts.archived);

    const def = document.querySelector('input[name="project-restore-mode"][value="project-only"]');
    if (def) def.checked = true;

    const modal = document.getElementById('project-restore-modal');
    if (modal) modal.classList.add('active');

    const menu = document.getElementById('project-options-menu');
    if (menu) menu.style.display = 'none';
}

function closeProjectRestoreModal() {
    const modal = document.getElementById('project-restore-modal');
    if (modal) modal.classList.remove('active');
    projectRestoreState.projectId = null;
}

async function confirmProjectRestore() {
    const pid = projectRestoreState.projectId;
    if (!pid) return;
    const modeEl = document.querySelector('input[name="project-restore-mode"]:checked');
    const restoreChildren = modeEl ? modeEl.value === 'with-children' : false;
    closeProjectRestoreModal();
    await _executeProjectRestore(pid, restoreChildren);
}

async function _executeProjectRestore(pid, restoreChildren) {
    const result = restoreProjectService(pid, projects, tasks, restoreChildren);
    if (!result.project) return;
    projects = result.projects;
    tasks    = result.tasks;
    render && render();
    updateCounts && updateCounts();
    try {
        const reqs = [ saveProjectUpdate(pid, { archivedAt: null }) ];
        for (const tid of result.affectedTaskIds) {
            reqs.push(saveTaskUpdate(tid, { archivedAt: null, archivedByAutomationId: null }));
        }
        await Promise.all(reqs);
        const restoredTaskCount = result.affectedTaskIds.length;
        showNotification(
            t(getProjectArchiveToastKey(restoredTaskCount, 'restore'), { count: restoredTaskCount }),
            'success'
        );
    } catch (err) {
        console.error('[archive] project restore failed', err);
        showErrorNotification(t('error.saveChangesFailed'));
    }
}

// ── Archived Projects table view ───────────────────────────────────────────
// Mirrors the Archived Tasks view: same table layout + bulk-restore toolbar,
// shown on the Projects page when on the #projects/archive route.
let archivedProjectSelectionState = { selectedProjectIds: new Set() };
let _archivedProjectsView = [];

function updateArchivedProjectSelectionUI(view = _archivedProjectsView) {
    const list = Array.isArray(view) ? view : [];
    const visibleIds = new Set(list.map(p => p.id));
    archivedProjectSelectionState.selectedProjectIds.forEach(id => {
        if (!visibleIds.has(id)) archivedProjectSelectionState.selectedProjectIds.delete(id);
    });

    const selectedCount = archivedProjectSelectionState.selectedProjectIds.size;
    const total = list.length;
    const toolbar = document.getElementById('archived-projects-bulk-toolbar');
    if (toolbar) toolbar.style.display = selectedCount > 0 ? 'flex' : 'none';

    const countEl = document.getElementById('archived-projects-selection-count');
    if (countEl) {
        countEl.textContent = t('projects.archived.selected', { count: selectedCount, total });
    }

    const syncSelectAll = (checkbox) => {
        if (!checkbox) return;
        checkbox.checked = total > 0 && selectedCount === total;
        checkbox.indeterminate = selectedCount > 0 && selectedCount < total;
    };
    syncSelectAll(document.getElementById('archived-projects-select-all-toolbar'));
    syncSelectAll(document.getElementById('archived-projects-select-all-table'));

    document.querySelectorAll('#archived-projects-view-body tr[data-project-id]').forEach(row => {
        const pid = parseInt(row.dataset.projectId, 10);
        const isSelected = archivedProjectSelectionState.selectedProjectIds.has(pid);
        row.classList.toggle('mass-edit-selected', isSelected);
        const checkbox = row.querySelector('.archived-project-select-checkbox');
        if (checkbox) checkbox.checked = isSelected;
    });
}

function toggleArchivedProjectSelection(projectId) {
    const id = parseInt(projectId, 10);
    if (!Number.isFinite(id)) return;
    if (archivedProjectSelectionState.selectedProjectIds.has(id)) {
        archivedProjectSelectionState.selectedProjectIds.delete(id);
    } else {
        archivedProjectSelectionState.selectedProjectIds.add(id);
    }
    updateArchivedProjectSelectionUI();
}

function toggleAllArchivedProjects() {
    const list = _archivedProjectsView;
    const allSelected = list.length > 0
        && list.every(p => archivedProjectSelectionState.selectedProjectIds.has(p.id));
    archivedProjectSelectionState.selectedProjectIds.clear();
    if (!allSelected) {
        list.forEach(p => archivedProjectSelectionState.selectedProjectIds.add(p.id));
    }
    updateArchivedProjectSelectionUI(list);
}

function clearArchivedProjectSelection() {
    archivedProjectSelectionState.selectedProjectIds.clear();
    updateArchivedProjectSelectionUI();
}

function renderArchivedProjectsView(view) {
    const list = Array.isArray(view) ? view : [];
    _archivedProjectsView = list;

    const tbody = document.getElementById('archived-projects-view-body');
    if (!tbody) return;

    updateProjectsListCount(list.length);
    const bottomCountEl = document.getElementById('archived-projects-view-count-bottom');
    if (bottomCountEl) {
        bottomCountEl.textContent = document.getElementById('projects-list-count')?.textContent || '';
    }

    if (!list.length) {
        const hasFilters = !!projectFilterState.search
            || projectFilterState.tags.size > 0
            || (projectFilterState.updatedFilter && projectFilterState.updatedFilter !== 'all')
            || projectFilterState.taskFilter === 'has-tasks'
            || projectFilterState.taskFilter === 'no-tasks';
        const emptyTitle = hasFilters ? t('projects.archived.noResults') : t('projects.archived.emptyTitle');
        const emptySubtitle = hasFilters ? t('projects.archived.noResultsSubtitle') : t('projects.archived.emptySubtitle');
        tbody.innerHTML = `<tr><td colspan="8" style="text-align:center;padding:48px 0;color:var(--text-muted);">${escapeHtml(emptyTitle)}<br><small>${escapeHtml(emptySubtitle)}</small></td></tr>`;
        // Mobile mirror: the archived table is display:none on phones, so render the
        // same empty state into the mobile card container.
        renderMobileProjects([], 'archived-projects-list-mobile', { title: emptyTitle, subtitle: emptySubtitle });
        updateArchivedProjectSelectionUI(list);
        return;
    }

    tbody.innerHTML = list.map(project => {
        const projectTasks = tasks.filter(tk => tk.projectId === project.id);
        const total = projectTasks.length;
        const done = projectTasks.filter(tk => tk.status === 'done').length;
        const pct = total ? Math.round((done / total) * 100) : 0;
        const progressText = total ? `${done}/${total} · ${pct}%` : '—';

        const colorDot = `<span style="display:inline-block;width:10px;height:10px;background-color:${escapeHtml(getProjectColor(project.id))};border-radius:2px;margin-right:8px;vertical-align:middle;"></span>`;
        const projectName = escapeHtml(project.name || t('projects.untitled') || 'Untitled');

        const tags = project.tags && project.tags.length > 0
            ? (() => {
                const visibleTags = project.tags.slice(0, 10);
                const hiddenCount = Math.max(project.tags.length - visibleTags.length, 0);
                const rendered = visibleTags.map(tag => {
                    const bg = getTagColor ? getTagColor(tag) : '#555';
                    return `<span style="${getTintedTagStyle(escapeHtml(bg))} padding: 2px 6px; border-radius: 3px; font-size: 10px; margin-right: 4px; font-weight: 500; white-space: nowrap;">${escapeHtml(String(tag).toUpperCase())}</span>`;
                }).join('');
                const overflow = hiddenCount > 0
                    ? `<span style="background: var(--bg-tertiary); color: var(--text-secondary); border: 1px solid var(--border); padding: 2px 6px; border-radius: 3px; font-size: 10px; margin-right: 4px; font-weight: 600; white-space: nowrap;">+${hiddenCount}</span>`
                    : '';
                return rendered + overflow;
            })()
            : '<span style="color:var(--text-muted); font-size: 12px;">-</span>';

        const startDate = project.startDate ? formatDate(project.startDate) : '—';
        const endDate = project.endDate ? formatDate(project.endDate) : '—';
        const archivedDate = project.archivedAt ? formatDate(project.archivedAt.slice(0, 10)) : '—';
        const selected = archivedProjectSelectionState.selectedProjectIds.has(project.id);

        return `<tr class="task-row${selected ? ' mass-edit-selected' : ''}" data-project-id="${project.id}">
            <td class="mass-edit-col">
                <input type="checkbox" class="task-select-checkbox archived-project-select-checkbox" data-action="toggleArchivedProjectSelection" data-param="${project.id}" ${selected ? 'checked' : ''}>
            </td>
            <td class="archived-project-col" data-action="showProjectDetails" data-param="${project.id}" style="font-weight:500">${colorDot}${projectName}</td>
            <td data-action="showProjectDetails" data-param="${project.id}"><span class="archived-progress-text">${escapeHtml(progressText)}</span></td>
            <td class="archived-tags-col" data-action="showProjectDetails" data-param="${project.id}">${tags}</td>
            <td data-action="showProjectDetails" data-param="${project.id}">${escapeHtml(startDate)}</td>
            <td data-action="showProjectDetails" data-param="${project.id}">${escapeHtml(endDate)}</td>
            <td data-action="showProjectDetails" data-param="${project.id}" style="color:var(--text-muted);font-size:13px">${escapeHtml(archivedDate)}</td>
            <td class="archived-actions-col">
                <div class="archived-row-actions">
                    <button type="button" class="archived-restore-btn" data-action="openProjectRestoreModal" data-param="${project.id}">${escapeHtml(t('projects.restore.confirm'))}</button>
                    <button type="button" class="archived-details-btn" data-action="showProjectDetails" data-param="${project.id}">${escapeHtml(t('projects.openDetailsTitle'))}</button>
                </div>
            </td>
        </tr>`;
    }).join('');

    // Mobile mirror: the archived table is display:none on phones. Reuse the shared
    // project mobile card markup (which already renders a Restore button for archived
    // projects) so the archive view is usable on mobile.
    renderMobileProjects(list, 'archived-projects-list-mobile');

    updateArchivedProjectSelectionUI(list);
}

let bulkProjectRestoreState = { projectIds: [] };

function bulkRestoreArchivedProjects() {
    const ids = Array.from(archivedProjectSelectionState.selectedProjectIds);
    if (ids.length === 0) return;

    // Count archived child tasks across the whole selected batch.
    let archivedChildren = 0;
    ids.forEach(pid => {
        const counts = summarizeProjectTasks(pid, tasks);
        archivedChildren += counts.archived;
    });

    // No archived children anywhere → nothing to ask; restore directly.
    if (archivedChildren === 0) {
        _executeBulkProjectRestore(ids, false);
        return;
    }

    // Otherwise prompt once for the whole batch.
    bulkProjectRestoreState.projectIds = ids;
    const countEl = document.getElementById('projects-bulk-restore-archived-count');
    if (countEl) countEl.textContent = String(archivedChildren);
    const def = document.querySelector('input[name="projects-bulk-restore-mode"][value="project-only"]');
    if (def) def.checked = true;
    const modal = document.getElementById('projects-bulk-restore-modal');
    if (modal) modal.classList.add('active');
}

function closeBulkProjectRestoreModal() {
    const modal = document.getElementById('projects-bulk-restore-modal');
    if (modal) modal.classList.remove('active');
    bulkProjectRestoreState.projectIds = [];
}

async function confirmBulkProjectRestore() {
    const ids = bulkProjectRestoreState.projectIds.slice();
    const modeEl = document.querySelector('input[name="projects-bulk-restore-mode"]:checked');
    const restoreChildren = modeEl ? modeEl.value === 'with-children' : false;
    closeBulkProjectRestoreModal();
    await _executeBulkProjectRestore(ids, restoreChildren);
}

async function _executeBulkProjectRestore(ids, restoreChildren) {
    if (!ids || ids.length === 0) return;
    archivedProjectSelectionState.selectedProjectIds.clear();

    const savedProjectIds = [];
    const savedTaskIds = [];
    let restored = 0;
    for (const pid of ids) {
        const project = projects.find(p => p.id === pid);
        if (!project || !project.archivedAt) continue;
        const result = restoreProjectService(pid, projects, tasks, restoreChildren);
        if (!result.project) continue;
        projects = result.projects;
        tasks = result.tasks;
        restored++;
        savedProjectIds.push(pid);
        (result.affectedTaskIds || []).forEach(tid => savedTaskIds.push(tid));
    }

    render && render();
    updateCounts && updateCounts();

    if (restored === 0) return;
    try {
        const reqs = savedProjectIds.map(pid => saveProjectUpdate(pid, { archivedAt: null }));
        savedTaskIds.forEach(tid => reqs.push(saveTaskUpdate(tid, { archivedAt: null, archivedByAutomationId: null })));
        await Promise.all(reqs);
        showNotification(t('projects.archived.bulkRestored', { count: restored }), 'success');
    } catch (err) {
        console.error('[archive] archived bulk project restore failed', err);
        showErrorNotification(t('error.saveChangesFailed'));
    }
}

// === Event Delegation System ===
export function initializeEventDelegation() {
    setupEventDelegation({
        toggleTheme,
        showCalendarView,
        toggleKanbanSettings,
        openProjectModal,
        openTaskModal,
        closeUserDropdown,
        openSettingsModal,
        switchSettingsTab,
        settingsMobileBack,
        openTaskModalForProject,
        openTaskModalForStatus,
        openSelectedProjectFromTask,
        closeModal,
        openAskKaiModal,
        submitAskKaiRequest,
        confirmAskKaiProposal,
        cancelAskKaiModal,
        // Formerly inline on*= handlers (blocked by the enforced CSP)
        toggleKanbanBacklog,
        toggleKanbanProjects,
        toggleKanbanTags,
        toggleKanbanNoDate,
        closeDocsShareModal,
        closeCropModal: () => window.closeCropModal?.(),
        applyCrop: () => window.applyCrop?.(),
        changeFeedbackPage,
        openReviewTasksInNewTab,
        refineKaiQuery,
        clearKaiQuery,
        saveAskKaiPrompt,
        toggleAskKaiSavedPrompts,
        toggleAskKaiSavedForCurrentPrompt,
        applyAskKaiSavedPrompt,
        editAskKaiSavedPromptName,
        cancelEditAskKaiSavedPromptName,
        saveAskKaiSavedPromptName,
        deleteAskKaiSavedPrompt,
        cancelDeleteAskKaiSavedPrompt,
        confirmDeleteAskKaiSavedPrompt,
        dashKaiSuggest,
        dashFilterAttention,
        dashCompleteTask,
        dashOpenTasks,
        dashOpenProjects,
        closeTaskModal,
        closeConfirmModal,
        closeFeedbackDeleteModal,
        closeProjectConfirmModal,
        closeUnsavedChangesModal,
        openDeleteAccountModal,
        closeDeleteAccountModal,
        confirmDeleteAccount,
        closeDayItemsModal,
        closeDayItemsModalOnBackdrop,
        openTaskDetails,
        deleteTask,
        duplicateTask,
        copyTaskLink,
        confirmDelete,
        openRecurrenceModal,
        closeRecurrenceModal,
        submitRecurrenceModal,
        selectRecurrencePattern,
        toggleRecurrenceWeekday,
        toggleAutomationActive,
        requestDeleteAutomation,
        cancelDeleteAutomation,
        deleteAutomation,
        editAutomation,
        openAutomationAudit,
        openAutomationAuditTask,
        toggleAutomationTypePicker,
        selectAutomationType,
        openAutoArchiveModal,
        closeAutoArchiveModal,
        submitAutoArchiveModal,
        selectAutoArchiveMode,
        selectAutoArchivePattern,
        toggleAutoArchiveWeekday,
        startRecurrenceNewTemplate,
        selectRecurrenceComboboxTask,
        clearRecurrenceCombobox,
        showProjectDetails,
        toggleProjectExpand,
        expandAllProjects,
        collapseAllProjects,
        setProjectsView,
        toggleProjectMenu,
        editProjectTitle,
        saveProjectTitle,
        cancelProjectTitle,
        handleDeleteProject,
        resetProjectTaskOrder,
        setProjectTaskGroupBy,
        toggleProjectTaskGroup,
        resetListViewTaskOrder,
        resetExpandedProjectTaskOrder,
        handleDuplicateProject,
        toggleProjectColorPicker,
        updateProjectColor,
        openCustomProjectColorPicker,
        navigateToProjectStatus,
        navigateToProjectTasksList,
        deleteProject,
        confirmProjectDelete,
        closeDuplicateProjectModal,
        confirmDuplicateProject,
        openSaveAsTemplateModal,
        closeSaveAsTemplateModal,
        confirmSaveAsTemplate,
        deleteTemplateById,
        openDocsDeleteModal,
        closeDocsDeleteModal,
        confirmDocsDeleteModal,
        handleDuplicateDocPage,
        closeDocsDuplicateModal,
        confirmDuplicateDocPage,
        openDocsVideoModal,
        closeDocsVideoModal,
        confirmDocsVideoModal,
        closeDocsStatusModal,
        confirmDocsStatusModal,
        openDeleteTemplateModal,
        closeDeleteTemplateModal,
        confirmDeleteTemplate,
        openRenameTemplateModal,
        closeRenameTemplateModal,
        confirmRenameTemplate,
        addFeedbackItem,
        deleteFeedbackItem,
        confirmFeedbackDelete,
        toggleHistoryEntryInline,
        formatTaskText,
        insertTaskHeading,
        insertTaskDivider,
        insertTaskChecklist,
        insertTaskDateChip,
        formatProjectDescriptionText,
        insertProjectDescriptionHeadingCmd,
        insertProjectDescriptionDividerCmd,
        insertProjectDescriptionChecklistCmd,
        insertProjectDescriptionDateChipCmd,
        sortTable,
        toggleSortMode,
        openMobileSortSheet,
        closeMobileSortSheet,
        applyMobileSort,
        animateCalendarMonthChange,
        goToToday,
        showDayTasks,
        addAttachment,
        addFileAttachment,
        addTag,
        removeTag,
        addProjectTag,
        removeProjectTag,
        selectNewProjectColor,
        toggleNewProjectColorPicker,
        toggleProjectTemplateSection,
        addProjectDetailsTag,
        removeProjectDetailsTag,
        removeAttachment,
        removeDependency,
        removeTaskLink,
        removeTempLink,
        downloadFileAttachment,
        viewFile,
        viewImageLegacy,
        openImageGallery,
        showErrorNotification,
        t,
        backToProjects,
        showAllActivity,
        backToDashboard,
        backToCalendar,
        openUpdatesFromNotification,
        openDueTodayFromNotification,
        dismissKanbanTip,
        confirmDiscardChanges,
        closeReviewStatusConfirmModal,
        confirmDisableReviewStatus,
        closeCalendarCreateModal,
        confirmCreateTask,
        addTaskFromDayItemsModal,
        signOut,
        exportDashboardData,
        closeExportDataModal,
        confirmExportData,
        exportTasksToExcel,
        openImportDataModal,
        closeImportDataModal,
        confirmImportData,
        generateReport,
        generateSummaryReport,
        closeSummaryReport,
        refreshSummaryReport,
        saveSummaryReportToDocs,
        printSummaryReport,
        getCurrentMonth: () => currentMonth,
        getCurrentYear: () => currentYear,
        showStatusInfoModal,
        // Mass Edit functions
        toggleTaskSelection,
        closeMassEditPopover,
        clearMassEditPopover,
        queueMassEditChange,
        applyAllMassEditChanges,
        closeMassEditConfirm,
        closeMassEditConfirmOnBackdrop,
        applyMassEditConfirmed,
        closeMassDeleteConfirmModal,
        confirmMassDelete,
        // Archive / Restore
        archiveTask,
        restoreTask,
        restoreArchivedTask,
        toggleArchivedAutomatedFilter,
        toggleArchivedTaskSelection,
        toggleAllArchivedTasks,
        clearArchivedTaskSelection,
        bulkRestoreArchivedTasks,
        bulkArchiveTasks,
        bulkRestoreTasks,
        openProjectArchiveModal,
        closeProjectArchiveModal,
        confirmProjectArchive,
        openProjectRestoreModal,
        closeProjectRestoreModal,
        confirmProjectRestore,
        toggleArchivedProjectSelection,
        toggleAllArchivedProjects,
        clearArchivedProjectSelection,
        bulkRestoreArchivedProjects,
        closeBulkProjectRestoreModal,
        confirmBulkProjectRestore,
        backToArchivedProjects
    });
}

// === Mass Edit Event Listeners ===
let massEditListenersInitialized = false;

export function initializeMassEditListeners() {
    // Prevent duplicate event listeners
    if (massEditListenersInitialized) return;
    massEditListenersInitialized = true;

    // Select all checkbox in toolbar
    const massEditSelectAll = document.getElementById('mass-edit-select-all');
    if (massEditSelectAll) {
        massEditSelectAll.addEventListener('change', (e) => {
            if (e.target.checked) {
                selectAllFilteredTasks();
            } else {
                clearMassEditSelection();
            }
        });
    }

    // Table header checkbox
    const tableSelectAll = document.getElementById('table-select-all');
    if (tableSelectAll) {
        tableSelectAll.addEventListener('change', (e) => {
            if (e.target.checked) {
                selectAllFilteredTasks();
            } else {
                clearMassEditSelection();
            }
        });
    }

    // Clear button
    const clearBtn = document.getElementById('mass-edit-clear-btn');
    if (clearBtn) {
        clearBtn.addEventListener('click', () => clearMassEditSelection());
    }

    // Field buttons (Status, Priority, Dates, Project, Tags)
    ['status', 'priority', 'dates', 'project', 'tags'].forEach(field => {
        const btn = document.getElementById(`mass-edit-${field}-btn`);
        if (btn) {
            btn.addEventListener('click', (e) => {
                openMassEditPopover(field, e.target);
            });
        }
    });

    // Close popover when clicking outside
    document.addEventListener('click', (e) => {
        const popover = document.getElementById('mass-edit-popover');
        if (!popover) return;

        const isPopoverClick = popover.contains(e.target);
        const isFieldButtonClick = e.target.closest('.mass-edit-btn');

        if (!isPopoverClick && !isFieldButtonClick) {
            closeMassEditPopover();
        }
    });

    // Close popover on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            const popover = document.getElementById('mass-edit-popover');
            if (popover && popover.style.display !== 'none') {
                closeMassEditPopover();
            }
        }
    });
}

function clearAllFilters() {
    // Clear all filter states
    filterState.statuses.clear();
    filterState.statusExcludeMode = false;
    filterState.priorities.clear();
    filterState.priorityExcludeMode = false;
    filterState.projects.clear();
    filterState.projectExcludeMode = false;
    filterState.tags.clear();
    filterState.tagExcludeMode = false;
    filterState.tagAndMode = false;
    filterState.linkTypes.clear();
    filterState.search = '';
    filterState.datePresets.clear();
    filterState.dateFrom = '';
    filterState.dateTo = '';
    filterState.createdFrom = '';
    filterState.createdTo = '';
    filterState.updatedFrom = '';
    filterState.updatedTo = '';
    filterState.completedFrom = '';
    filterState.completedTo = '';
    filterState.duplicateTitles = false;
    filterState.kaiSort = '';
    filterState.kaiLimit = null;

    // Clear search input
    document.querySelectorAll('#filter-search, #cal-filter-search').forEach((searchInput) => {
        searchInput.value = '';
    });

    // Clear date inputs
    const dueDateInput = document.getElementById('filter-due-date');
    const createdDateInput = document.getElementById('filter-created-date');
    if (dueDateInput) dueDateInput.value = '';
    if (createdDateInput) createdDateInput.value = '';

    // Clear date filter dropdown
    const dateFilterGlobal = document.getElementById('filter-date-global');
    if (dateFilterGlobal) dateFilterGlobal.value = '';

    // Uncheck all filter checkboxes
    const allCheckboxes = document.querySelectorAll('#global-filters input[type="checkbox"]');
    allCheckboxes.forEach(checkbox => {
        checkbox.checked = false;
    });

    // Update UI and sync URL (this calls updateFilterBadges, renderActiveFilterChips, syncURLWithFilters, and re-renders)
    renderAfterFilterChange();
}

// Super simple approach - just apply filters directly
function filterProjectTasks(projectId, status) {
    // Go to tasks page
    showPage('tasks');
    window.location.hash = 'tasks';
    
    // Set nav active
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    const tasksNav = document.querySelector('.nav-item[data-page="tasks"]');
    if (tasksNav) tasksNav.classList.add('active');
    
    // Clear all filters
    filterState.statuses.clear();
    filterState.priorities.clear();
    filterState.projects.clear();
    filterState.tags.clear();
    filterState.dateFrom = '';
    filterState.dateTo = '';
    filterState.search = '';
    document.querySelectorAll('#filter-search, #cal-filter-search').forEach((searchEl) => {
        searchEl.value = '';
    });
    
    // Set the filters we want
    filterState.statuses.add(status);
    filterState.projects.add(projectId.toString());
    
    // Update UI
    document.querySelectorAll('input[type="checkbox"]').forEach(cb => cb.checked = false);
    document.querySelectorAll(`input[data-filter="status"][value="${status}"]`).forEach(cb => cb.checked = true);
    document.querySelectorAll(`input[data-filter="project"][value="${projectId}"]`).forEach(cb => cb.checked = true);
    
    // Show list view
    document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
    const listBtn = document.querySelector('.view-btn:nth-child(2)');
    if (listBtn) listBtn.classList.add('active');
    
    const kanban = document.querySelector('.kanban-board');
    const list = document.getElementById('list-view');
    if (kanban) kanban.classList.add('hidden');
    if (list) list.classList.add('active');
    
    // Update display
    updateFilterBadges();
    renderListView();
}

window.filterProjectTasks = filterProjectTasks;

function navigateToProjectStatus(projectId, status) {
    // Use existing dashboard function and add project filter
    navigateToFilteredTasks('status', status);
    
    // Add project filter after the status filter is applied
    setTimeout(() => {
        // Ensure view toggle is visible
        const viewToggle = document.querySelector('.view-toggle');
        if (viewToggle) viewToggle.classList.remove('hidden');
        
        filterState.projects.add(projectId.toString());
        const projectCheckbox = document.querySelector(`input[data-filter="project"][value="${projectId}"]`);
        if (projectCheckbox) projectCheckbox.checked = true;
        updateFilterBadges();
        renderTasks(); // Re-render with both filters
    }, 200);
}

window.navigateToProjectStatus = navigateToProjectStatus;

function navigateToProjectTasksList(projectId) {
    window.location.hash = `tasks?view=list&project=${projectId}`;
}

window.navigateToProjectTasksList = navigateToProjectTasksList;

function navigateToAllTasks() {
    // Navigate to tasks page
    window.location.hash = 'tasks';
    
    // Clear all nav highlights and set tasks as active
    document.querySelectorAll('.nav-item').forEach(nav => nav.classList.remove('active'));
    const tasksNavItem = document.querySelector('.nav-item[data-page="tasks"]');
    if (tasksNavItem) tasksNavItem.classList.add('active');
    
    // Show tasks page
    showPage('tasks');
    
    // Switch to list view
    document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
    const listViewBtn = document.querySelector('.view-btn[onclick*="list"], .view-btn:nth-child(2)');
    if (listViewBtn) listViewBtn.classList.add('active');
    
    // Hide kanban and calendar, show list view
    const kanbanBoard = document.querySelector('.kanban-board');
    const listView = document.getElementById('list-view');
    const calendarView = document.getElementById('calendar-view');
    
    if (kanbanBoard) kanbanBoard.classList.add('hidden');
    if (calendarView) calendarView.classList.remove('active');
    if (listView) listView.classList.add('active');
    
    // Clear any existing filters to show all tasks
    clearAllFilters();
    
    // Render the list view
    setTimeout(() => {
        renderListView();
    }, 100);
}

function filterProjectPortalList(query) {
    const container = projectPortalEl && projectPortalEl.querySelector('.project-portal-options');
    if (!container) return;
    const hiddenProject = document.getElementById('hidden-project');
    const selectedId = hiddenProject ? (hiddenProject.value || '') : '';
    const q = (query || '').toLowerCase();
    let items = '';
    if (selectedId) {
        items += `<div class="project-option" data-project-id="">${t('tasks.project.selectPlaceholder')}</div>`;
    }
    items += projects
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
        .filter(p => selectedId === '' || String(p.id) !== String(selectedId))
        .filter(p => !q || (p.name || '').toLowerCase().includes(q))
        .map(p => `<div class="project-option" data-project-id="${p.id}">${p.name}</div>`)
        .join('');
    container.innerHTML = items;
}

// Toggle visibility of the "No Date" option depending on tasks data
function updateNoDateOptionVisibility() {
    const sel = document.getElementById('filter-date-global');
    if (!sel) return;
    const noDateOpt = Array.from(sel.options).find(o => o.value === 'no-date');
    if (!noDateOpt) return;
    const hasNoDateTasks = tasks.some(t => !t.endDate);
    noDateOpt.style.display = hasNoDateTasks ? '' : 'none';
    // If user had selected 'no-date' but it's not applicable anymore, clear it
    // updateNoDateOptionVisibility function no longer needed with new date range filter
}

// Lightweight non-destructive sort for Projects view

// Update project status filter badge
function updateProjectStatusBadge() {
    const badge = document.getElementById('badge-project-status');
    if (!badge) return;
    const count = projectFilterState.statuses.size;
    badge.textContent = count === 0 ? '' : count;

    // Update button active state
    const button = badge.closest('.filter-button');
    if (button) {
        if (count > 0) {
            button.classList.add('active');
        } else {
            button.classList.remove('active');
        }
    }
}

function updateProjectTagsBadge() {
    const badge = document.getElementById('badge-project-tags');
    if (!badge) return;
    const count = projectFilterState.tags.size;
    badge.textContent = count === 0 ? '' : count;

    // Update button active state
    const button = badge.closest('.filter-button');
    if (button) {
        if (count > 0) {
            button.classList.add('active');
        } else {
            button.classList.remove('active');
        }
    }
}

function populateProjectTagOptions() {
    const tagUl = document.getElementById("project-tags-options");
    if (!tagUl) return;

    // Preserve previously selected tags from state
    const currentlySelected = new Set(projectFilterState.tags);

    // Collect all unique tags from all projects
    const allTags = new Set();
    projects.forEach(p => {
        if (p.tags && p.tags.length > 0) {
            p.tags.forEach(tag => allTags.add(tag));
        }
    });

    tagUl.innerHTML = "";

    if (allTags.size === 0) {
        const li = document.createElement("li");
        li.textContent = t('filters.noOtherTags');
        li.style.color = "var(--text-muted)";
        li.style.padding = "8px 12px";
        tagUl.appendChild(li);
    } else {
        Array.from(allTags).sort().forEach((tag) => {
            const li = document.createElement("li");
            const id = `project-tag-${tag}`;
            const checked = currentlySelected.has(tag) ? 'checked' : '';
            li.innerHTML = `<label><input type="checkbox" id="${id}" value="${tag}" data-filter="project-tags" ${checked}> ${tag.toUpperCase()}</label>`;
            tagUl.appendChild(li);
        });
    }

    // Re-attach event listeners for new checkboxes
    tagUl.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener("change", () => {
            if (cb.checked) {
                projectFilterState.tags.add(cb.value);
            } else {
                projectFilterState.tags.delete(cb.value);
            }
            updateProjectTagsBadge();
            applyProjectFilters();
            syncURLWithProjectFilters();
        });
    });
}

function getProjectUpdatedFilterLabel(value) {
    switch (value) {
        case '5m': return '5m';
        case '30m': return '30m';
        case '24h': return '24h';
        case 'week': return t('filters.updated.week');
        case 'month': return t('filters.updated.month');
        case 'all':
        default:
            return '';
    }
}

function getProjectsUpdatedCutoffTime(value) {
    const now = Date.now();
    switch (value) {
        case '5m': return now - 5 * 60 * 1000;
        case '30m': return now - 30 * 60 * 1000;
        case '24h': return now - 24 * 60 * 60 * 1000;
        case 'week': return now - 7 * 24 * 60 * 60 * 1000;
        case 'month': return now - 30 * 24 * 60 * 60 * 1000;
        case 'all':
        default:
            return null;
    }
}

function getProjectUpdatedTime(project) {
    const raw = (project && (project.updatedAt || project.createdAt)) || "";
    const time = new Date(raw).getTime();
    return Number.isFinite(time) ? time : 0;
}

function updateProjectsUpdatedFilterUI() {
    const badge = document.getElementById('badge-project-updated');
    if (badge) {
        badge.textContent = getProjectUpdatedFilterLabel(projectFilterState.updatedFilter);

        // Update button active state
        const button = badge.closest('.filter-button');
        if (button) {
            if (projectFilterState.updatedFilter !== 'all') {
                button.classList.add('active');
            } else {
                button.classList.remove('active');
            }
        }
    }
    try {
        document
            .querySelectorAll('input[type="radio"][data-filter="project-updated"][name="project-updated-filter"]')
            .forEach((rb) => {
                rb.checked = rb.value === projectFilterState.updatedFilter;
            });
    } catch (e) {}
}

/** "≥ 75%", "≤ 25%", "100%", "0%" — how the completion filter reads on a chip. */
function describeProjectCompletionFilter() {
    const { completionMin: min, completionMax: max } = projectFilterState;
    if (min != null && max != null) {
        return min === max ? `${min}%` : `${min}–${max}%`;
    }
    if (min != null) return `≥ ${min}%`;
    if (max != null) return `≤ ${max}%`;
    return '';
}

function updateProjectCompletionBadge() {
    const badge = document.getElementById('badge-project-completion');
    if (!badge) return;
    const active = projectFilterState.completionMin != null || projectFilterState.completionMax != null;
    badge.textContent = active ? '1' : '';
    const button = badge.closest('.filter-button');
    if (button) button.classList.toggle('active', active);
}

/**
 * The toolbar presets. Kai can set any threshold ("more than 75%"); the dropdown
 * offers the round numbers a human actually clicks.
 */
const PROJECT_COMPLETION_PRESETS = {
    all: { min: null, max: null },
    'complete': { min: 100, max: 100 },
    'gte-75': { min: 75, max: null },
    'gte-50': { min: 50, max: null },
    'lt-50': { min: null, max: 49 },
    'not-started': { min: 0, max: 0 }
};

/**
 * Tick the radio matching the current bounds.
 *
 * Kai can set any threshold ("more than 63%"), which no preset represents. In that
 * case every radio is left unticked — the chip still shows the real filter and still
 * removes it, so the state is never hidden from the user.
 */
function syncProjectCompletionRadioFromState() {
    const { completionMin: min, completionMax: max } = projectFilterState;
    const key = Object.keys(PROJECT_COMPLETION_PRESETS).find((name) => {
        const preset = PROJECT_COMPLETION_PRESETS[name];
        return preset.min === min && preset.max === max;
    }) || '';
    document
        .querySelectorAll('input[type="radio"][data-filter="project-completion"]')
        .forEach(rb => { rb.checked = rb.value === key; });
}

function setProjectCompletionFilter(presetKey) {
    const preset = PROJECT_COMPLETION_PRESETS[presetKey] || PROJECT_COMPLETION_PRESETS.all;
    projectFilterState.completionMin = preset.min;
    projectFilterState.completionMax = preset.max;
    updateProjectCompletionBadge();
    applyProjectFilters();
    syncURLWithProjectFilters();
}

function renderProjectsActiveFilterChips() {
    const wrap = document.getElementById('projects-active-filters');
    if (!wrap) return;
    wrap.innerHTML = '';

    const addChip = (label, value, onRemove) => {
        const chip = document.createElement('span');
        chip.className = 'filter-chip';
        const text = document.createElement('span');
        text.className = 'chip-text';
        text.textContent = value != null && value !== '' ? `${label}: ${value}` : label;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'chip-remove';
        btn.setAttribute('aria-label', t('filters.chip.removeAria', { label }));
        btn.textContent = 'x';
        btn.addEventListener('click', onRemove);

        chip.appendChild(text);
        chip.appendChild(btn);
        wrap.appendChild(chip);
    };

    // Search chip
    if (projectFilterState.search) {
        addChip(t('filters.chip.search'), projectFilterState.search, () => {
            projectFilterState.search = '';
            const el = document.getElementById('projects-search');
            if (el) el.value = '';
            const cur = loadProjectsViewState() || {};
            saveProjectsViewState({ ...cur, search: '' });
            applyProjectFilters();
        });
    }

    // Focused project-ID chip (Ask Kai "Ver" on freshly generated projects)
    if (projectFilterState.projectIds && projectFilterState.projectIds.size > 0) {
        const fromReport = projectFilterState.projectIdsSource === 'report';
        addChip(
            fromReport ? t('projects.filters.reportChip') : t('projects.filters.generatedChip'),
            fromReport ? String(projectFilterState.projectIds.size) : '',
            () => {
                projectFilterState.projectIds.clear();
                projectFilterState.projectIdsSource = '';
                applyProjectFilters();
                syncURLWithProjectFilters();
            }
        );
    }

    // Completion chip
    if (projectFilterState.completionMin != null || projectFilterState.completionMax != null) {
        addChip(t('projects.filters.completion'), describeProjectCompletionFilter(), () => {
            projectFilterState.completionMin = null;
            projectFilterState.completionMax = null;
            document
                .querySelectorAll('input[type="radio"][data-filter="project-completion"]')
                .forEach(rb => { rb.checked = rb.value === 'all'; });
            updateProjectCompletionBadge();
            applyProjectFilters();
            syncURLWithProjectFilters();
        });
    }

    // Status chips (label = "Status" or "Excluding" when exclude mode)
    const statusChipLabel = projectFilterState.statusExcludeMode ? t('tasks.filters.excluding') : t('projects.filters.status');
    projectFilterState.statuses.forEach((v) => {
        addChip(statusChipLabel, getProjectStatusLabel(v), () => {
            projectFilterState.statuses.delete(v);
            const cb = document.querySelector(`input[type="checkbox"][data-filter="project-status"][value="${v}"]`);
            if (cb) cb.checked = false;
            updateProjectStatusBadge();
            applyProjectFilters();
            syncURLWithProjectFilters();
        });
    });

    // Updated chip
    if (projectFilterState.updatedFilter && projectFilterState.updatedFilter !== 'all') {
        addChip(t('filters.chip.updated'), getProjectUpdatedFilterLabel(projectFilterState.updatedFilter), () => {
            projectFilterState.updatedFilter = 'all';
            updateProjectsUpdatedFilterUI();
            const cur = loadProjectsViewState() || {};
            saveProjectsViewState({ ...cur, updatedFilter: 'all' });
            applyProjectFilters();
        });
    }

    // Task filter chip
    if (projectFilterState.taskFilter === 'has-tasks' || projectFilterState.taskFilter === 'no-tasks') {
        const label = projectFilterState.taskFilter === 'has-tasks'
            ? t('projects.filters.hasTasks')
            : t('projects.filters.noTasks');
        addChip(label, '', () => {
            projectFilterState.taskFilter = '';
            document
                .querySelectorAll('#projects .projects-filters .pf-chip')
                .forEach((c) => c.classList.remove('active'));
            const cur = loadProjectsViewState() || {};
            saveProjectsViewState({ ...cur, filter: '' });
            applyProjectFilters();
        });
    }

    // Tags chips (label = "Tags" or "Excluding" when exclude mode)
    const tagsChipLabel = projectFilterState.tagExcludeMode ? t('tasks.filters.excluding') : t('projects.filters.tags');
    projectFilterState.tags.forEach((tag) => {
        addChip(tagsChipLabel, tag.toUpperCase(), () => {
            projectFilterState.tags.delete(tag);
            const cb = document.querySelector(`input[type="checkbox"][data-filter="project-tags"][value="${tag}"]`);
            if (cb) cb.checked = false;
            updateProjectTagsBadge();
            applyProjectFilters();
            syncURLWithProjectFilters();
        });
    });
}

// Apply all project filters
function applyProjectFilters() {
    const filterTimer = debugTimeStart("filters", "projects", {
        totalProjects: projects.length,
        statusCount: projectFilterState.statuses.size,
        tagCount: projectFilterState.tags.size,
        taskFilter: projectFilterState.taskFilter || "",
        updatedFilter: projectFilterState.updatedFilter || "all",
        hasSearch: !!projectFilterState.search
    });
    let filtered = projects.slice();

    // Focused project-ID filter (e.g. Ask Kai "Ver" on freshly generated projects).
    // Like the tasks taskIds filter, this restricts the view to an explicit set of IDs.
    if (projectFilterState.projectIds && projectFilterState.projectIds.size > 0) {
        filtered = filtered.filter(p => projectFilterState.projectIds.has(String(p.id)));
    }

    // Archived projects are a separate destination, reached only via the Archive
    // route — never a status. The route alone decides which set we operate on.
    if (isProjectsArchiveRoute()) {
        filtered = filtered.filter(p => !!p.archivedAt);
    } else {
        // Active view never shows archived projects.
        filtered = filtered.filter(p => !p.archivedAt);

        // Status filter (with exclude mode support) applies to the active view only.
        if (projectFilterState.statuses.size > 0) {
            filtered = filtered.filter(p => {
                const status = getProjectStatus(p.id);
                const matches = projectFilterState.statuses.has(status);
                return projectFilterState.statusExcludeMode ? !matches : matches;
            });
        }
    }

    // Apply task filter
    if (projectFilterState.taskFilter === 'has-tasks') {
        filtered = filtered.filter(p => tasks.some(t => t.projectId === p.id));
    } else if (projectFilterState.taskFilter === 'no-tasks') {
        filtered = filtered.filter(p => !tasks.some(t => t.projectId === p.id));
    }

    // Apply tags filter (with exclude mode support)
    if (projectFilterState.tags.size > 0) {
        filtered = filtered.filter(p => {
            const hasTags = p.tags && p.tags.length > 0;
            const matches = hasTags && Array.from(projectFilterState.tags).some(tag => p.tags.includes(tag));
            return projectFilterState.tagExcludeMode ? !matches : matches;
        });
    }

    // Apply updated recency filter
    if (projectFilterState.updatedFilter && projectFilterState.updatedFilter !== 'all') {
        const cutoff = getProjectsUpdatedCutoffTime(projectFilterState.updatedFilter);
        if (cutoff != null) {
            filtered = filtered.filter(p => getProjectUpdatedTime(p) >= cutoff);
        }
    }

    // Apply completion filter (inclusive bounds). A project with no tasks counts as
    // 0% — the same value its card shows — so "0% done" finds empty projects too.
    const { completionMin, completionMax } = projectFilterState;
    if (completionMin != null || completionMax != null) {
        filtered = filtered.filter(p => {
            const percent = getProjectCompletionPercent(p.id);
            if (completionMin != null && percent < completionMin) return false;
            if (completionMax != null && percent > completionMax) return false;
            return true;
        });
    }

    // Apply search filter
    if (projectFilterState.search) {
        const q = projectFilterState.search.toLowerCase();
        filtered = filtered.filter(p => ((p.name || '') + ' ' + (p.description || '')).toLowerCase().includes(q));
    }

    // Apply the live sort state so URL-restored sorts stay active before localStorage catches up.
    const activeSort = projectSortState?.lastSort || DEFAULT_PROJECT_SORT;
    applyProjectsSort(activeSort, filtered);

    updateProjectsClearButtonVisibility();
    try { renderProjectsActiveFilterChips(); } catch (e) {}
    debugTimeEnd("filters", filterTimer, {
        totalProjects: projects.length,
        filteredCount: filtered.length
    });
}

/**
 * applyProjectsSort(value, base)
 * value: sort key (same as before)
 * base: optional array of projects to sort (e.g., filtered view). If omitted, use full projects array.
 */
function applyProjectsSort(value, base) {
    // Use provided base or full projects, but do not mutate original arrays
    const view = (base && Array.isArray(base) ? base.slice() : projects.slice());
    const direction = projectSortState.direction;
    const isDesc = direction === 'desc';
    const normalizedValue = (value === 'name-asc' || value === 'name-desc') ? 'name' : value;

    if (normalizedValue === 'default' || normalizedValue === 'status') {
        // Sort by status: active, planning, completed (or reversed)
        const statusOrder = { 'active': 0, 'planning': 1, 'backlog': 2, 'completed': 3 };
        view.sort((a, b) => {
            const statusA = getProjectStatus(a.id);
            const statusB = getProjectStatus(b.id);
            const result = (statusOrder[statusA] || 999) - (statusOrder[statusB] || 999);
            return isDesc ? -result : result;
        });
        appState.projectsSortedView = view;
        renderView(view);
        return;
    }

    if (normalizedValue === 'name') {
        view.sort((a,b) => {
            const result = (a.name||'').localeCompare(b.name||'');
            return isDesc ? -result : result;
        });
    } else if (normalizedValue === 'created-desc') {
        view.sort((a,b) => {
            const result = new Date(b.createdAt) - new Date(a.createdAt);
            return isDesc ? -result : result;
        });
    } else if (normalizedValue === 'updated-desc') {
        view.sort((a,b) => {
            const aDate = new Date(a.updatedAt || a.createdAt);
            const bDate = new Date(b.updatedAt || b.createdAt);
            const result = bDate - aDate;
            return isDesc ? -result : result;
        });
    } else if (normalizedValue === 'tasks-desc') {
        view.sort((a,b) => {
            const result = (tasks.filter(t=>t.projectId===b.id).length) - (tasks.filter(t=>t.projectId===a.id).length);
            return isDesc ? -result : result;
        });
    } else if (normalizedValue === 'completion-desc') {
        // Sort by completion percentage
        view.sort((a,b) => {
            const aTotal = tasks.filter(t => t.projectId === a.id).length;
            const aDone = tasks.filter(t => t.projectId === a.id && t.status === 'done').length;
            const aPercent = aTotal > 0 ? (aDone / aTotal) * 100 : 0;

            const bTotal = tasks.filter(t => t.projectId === b.id).length;
            const bDone = tasks.filter(t => t.projectId === b.id && t.status === 'done').length;
            const bPercent = bTotal > 0 ? (bDone / bTotal) * 100 : 0;

            const result = bPercent - aPercent;
            return isDesc ? -result : result;
        });
    }

    appState.projectsSortedView = view;
    renderView(view);
    return;
}

function getProjectSortLabel(sortKey) {
    const map = {
        'default': t('projects.sort.statusLabel'),
        'status': t('projects.sort.statusLabel'),
        'name': t('projects.sort.nameLabel'),
        'name-asc': t('projects.sort.nameLabel'),
        'name-desc': t('projects.sort.nameLabel'),
        'created-desc': t('projects.sort.newestLabel'),
        'updated-desc': t('projects.sort.lastUpdatedLabel'),
        'tasks-desc': t('projects.sort.mostTasksLabel'),
        'completion-desc': t('projects.sort.percentCompletedLabel')
    };
    return map[sortKey] || sortKey;
}

function refreshProjectsSortLabel() {
    const sortLabel = document.getElementById('projects-sort-label');
    if (!sortLabel) return;
    const sortKey = projectSortState?.lastSort || DEFAULT_PROJECT_SORT;
    const baseLabel = getProjectSortLabel(sortKey);
    const directionIndicator = projectSortState?.direction === 'asc' ? 'asc' : 'desc';
    sortLabel.textContent = t('projects.sort.prefix', { label: baseLabel, direction: directionIndicator });
    const arrow = document.querySelector('#projects-sort-btn .sort-label-arrow');
    if (arrow) setProjectsSortArrow(arrow, directionIndicator);
    try { updateProjectsSortOptionsUI(); } catch (e) {}
}

function setProjectsSortArrow(el, direction) {
    if (!el) return;
    el.classList.toggle('is-up', direction === 'asc');
    el.classList.toggle('is-down', direction !== 'asc');
    el.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20 L6 14 H10 V4 H14 V14 H18 Z" fill="currentColor"/></svg>';
}

function applyProjectsSortSelection(sortKey, { toggleDirection = false } = {}) {
    const nextSortKey = sortKey || DEFAULT_PROJECT_SORT;
    const sameSort = projectSortState.lastSort === nextSortKey;
    if (toggleDirection || sameSort) {
        projectSortState.direction = projectSortState.direction === 'asc' ? 'desc' : 'asc';
    } else {
        projectSortState.direction = 'asc';
    }
    projectSortState.lastSort = nextSortKey;
    refreshProjectsSortLabel();
    try { updateProjectsSortOptionsUI(); } catch (e) {}

    const saved = loadProjectsViewState() || { search: '', filter: '', sort: DEFAULT_PROJECT_SORT };
    saveProjectsViewState({ ...saved, sort: nextSortKey, sortDirection: projectSortState.direction });
    applyProjectFilters();
}

function updateProjectsSortOptionsUI() {
    const panel = document.getElementById('projects-sort-panel');
    if (!panel) return;
    const current = projectSortState?.lastSort || DEFAULT_PROJECT_SORT;
    const directionIndicator = projectSortState?.direction === 'asc' ? 'asc' : 'desc';
    panel.querySelectorAll('.projects-sort-option').forEach(opt => {
        const isActive = opt.dataset.sort === current;
        opt.classList.toggle('is-active', isActive);
        let indicator = opt.querySelector('.sort-option-indicator');
        if (!indicator) {
            indicator = document.createElement('button');
            indicator.type = 'button';
            indicator.className = 'sort-option-indicator';
            indicator.setAttribute('aria-label', t('projects.sort.help'));
            opt.appendChild(indicator);
        }
        if (isActive) {
            setProjectsSortArrow(indicator, directionIndicator);
        }
        indicator.style.visibility = isActive ? 'visible' : 'hidden';
    });
}
// Hook up the select after DOM ready
document.addEventListener('DOMContentLoaded', () => {
    // Sort button + panel handlers
    const sortBtn = document.getElementById('projects-sort-btn');
    const sortPanel = document.getElementById('projects-sort-panel');
    const sortLabel = document.getElementById('projects-sort-label');
    if (sortBtn && sortPanel) {
        const isMobileViewport = () => window.matchMedia('(max-width: 768px)').matches;
        const ensureDropdownBackdrop = () => {
            let backdrop = document.getElementById('dropdown-backdrop');
            if (!backdrop) {
                backdrop = document.createElement('div');
                backdrop.id = 'dropdown-backdrop';
                backdrop.setAttribute('aria-hidden', 'true');
                document.body.appendChild(backdrop);
            }
            return backdrop;
        };
        const closeProjectsSortPanel = () => {
            sortBtn.setAttribute('aria-expanded', 'false');
            if (sortPanel.contains(document.activeElement)) {
                sortBtn.focus();
            }
            sortPanel.setAttribute('aria-hidden', 'true');
            sortPanel.setAttribute('inert', '');
            if (isMobileViewport()) {
                document.body.classList.remove('dropdown-sheet-open');
                const backdrop = document.getElementById('dropdown-backdrop');
                if (backdrop) backdrop.classList.remove('open');
            }
        };
        const openProjectsSortPanel = () => {
            sortBtn.setAttribute('aria-expanded', 'true');
            sortPanel.setAttribute('aria-hidden', 'false');
            sortPanel.removeAttribute('inert');
            if (isMobileViewport()) {
                ensureDropdownBackdrop().classList.add('open');
                document.body.classList.add('dropdown-sheet-open');
            }
        };

        ensureDropdownBackdrop().addEventListener('click', () => closeProjectsSortPanel());

        sortBtn.addEventListener('click', (e) => {
            if (e.target.closest('.sort-label-arrow')) {
                e.preventDefault();
                e.stopPropagation();
                applyProjectsSortSelection(projectSortState?.lastSort || DEFAULT_PROJECT_SORT, { toggleDirection: true });
                return;
            }
            const open = sortBtn.getAttribute('aria-expanded') === 'true';
            const willOpen = !open;
            if (willOpen) {
                openProjectsSortPanel();
            } else {
                closeProjectsSortPanel();
            }
            try { updateProjectsSortOptionsUI(); } catch (e) {}
            e.stopPropagation();
        });
        // Clicking an option
        // If there are duplicate panels (from previous DOM states), select carefully
        const panel = document.getElementById('projects-sort-panel');
        if (!panel) return;
        panel.querySelectorAll('.projects-sort-option').forEach(opt => {
            opt.addEventListener('click', (ev) => {
                const sortKey = opt.dataset.sort || DEFAULT_PROJECT_SORT;
                const clickedIndicator = ev.target && ev.target.classList && ev.target.classList.contains('sort-option-indicator');
                applyProjectsSortSelection(sortKey, { toggleDirection: clickedIndicator || projectSortState.lastSort === sortKey });

                if (!clickedIndicator) {
                    closeProjectsSortPanel();
                } else {
                    sortPanel.removeAttribute('inert');
                    ev.stopPropagation();
                }
            });
        });

        // Close panel on outside click
        document.addEventListener('click', () => {
            closeProjectsSortPanel();
        });
    }

    // Project status filter (open/close handled by setupFilterEventListeners)
    const statusFilterGroup = document.getElementById('group-project-status');
    if (statusFilterGroup) {
        const checkboxes = statusFilterGroup.querySelectorAll('input[type="checkbox"][data-filter="project-status"]');
        checkboxes.forEach(cb => {
            cb.addEventListener('change', () => {
                if (cb.checked) projectFilterState.statuses.add(cb.value);
                else projectFilterState.statuses.delete(cb.value);
                updateProjectStatusBadge();
                applyProjectFilters();
                // Sync URL with new status filter
                syncURLWithProjectFilters();
            });
        });

        // Add include/exclude toggle handlers for project status
        const statusToggle = statusFilterGroup.querySelector('.filter-mode-toggle[data-filter-type="project-status"]');
        if (statusToggle) {
            const includeBtn = statusToggle.querySelector('.filter-mode-btn[data-mode="include"]');
            const excludeBtn = statusToggle.querySelector('.filter-mode-btn[data-mode="exclude"]');
            
            if (includeBtn) {
                includeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (projectFilterState.statusExcludeMode) {
                        projectFilterState.statusExcludeMode = false;
                        updateProjectFilterModeUI('project-status');
                        applyProjectFilters();
                        syncURLWithProjectFilters();
                    }
                });
            }
            if (excludeBtn) {
                excludeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (!projectFilterState.statusExcludeMode) {
                        projectFilterState.statusExcludeMode = true;
                        updateProjectFilterModeUI('project-status');
                        applyProjectFilters();
                        syncURLWithProjectFilters();
                    }
                });
            }
        }
    }

    // Project tags filter
    const tagsFilterGroup = document.getElementById('group-project-tags');
    if (tagsFilterGroup) {
        // Add include/exclude toggle handlers for project tags
        const tagsToggle = tagsFilterGroup.querySelector('.filter-mode-toggle[data-filter-type="project-tags"]');
        if (tagsToggle) {
            const includeBtn = tagsToggle.querySelector('.filter-mode-btn[data-mode="include"]');
            const excludeBtn = tagsToggle.querySelector('.filter-mode-btn[data-mode="exclude"]');

            if (includeBtn) {
                includeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (projectFilterState.tagExcludeMode) {
                        projectFilterState.tagExcludeMode = false;
                        updateProjectFilterModeUI('project-tags');
                        applyProjectFilters();
                        syncURLWithProjectFilters();
                    }
                });
            }
            if (excludeBtn) {
                excludeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (!projectFilterState.tagExcludeMode) {
                        projectFilterState.tagExcludeMode = true;
                        updateProjectFilterModeUI('project-tags');
                        applyProjectFilters();
                        syncURLWithProjectFilters();
                    }
                });
            }
        }
    }

    // Projects search
    const search = document.getElementById('projects-search');
    if (search) {
        search.addEventListener('input', debounce((e) => {
            projectFilterState.search = (e.target.value || '').trim();
            applyProjectFilters();
            const cur = loadProjectsViewState() || {};
            saveProjectsViewState({ ...cur, search: e.target.value });
        }, 220));
    }

    // Task filter chips (has-tasks, no-tasks)
    const chips = Array.from(document.querySelectorAll('.pf-chip'));
    chips.forEach(chip => {
        chip.addEventListener('click', () => {
            const v = chip.dataset.filter;

            // Toggle chip
            if (chip.classList.contains('active')) {
                chip.classList.remove('active');
                projectFilterState.taskFilter = '';
            } else {
                chips.forEach(c => c.classList.remove('active'));
                chip.classList.add('active');
                projectFilterState.taskFilter = v;
            }

            applyProjectFilters();
            const cur = loadProjectsViewState() || {};
            saveProjectsViewState({ ...cur, filter: projectFilterState.taskFilter || '' });
        });
    });

    // Clear filters button
    const clearBtn = document.getElementById('btn-clear-projects');
    if (clearBtn) {
        clearBtn.addEventListener('click', clearProjectFilters);
    }

    // Ensure visibility is synced at start
    updateProjectsClearButtonVisibility();
});

// Expose clearProjectFilters to window for potential external use
window.clearProjectFilters = clearProjectFilters;

function isProjectsArchiveRoute() {
    return window.location.hash.slice(1).split('?')[0] === 'projects/archive';
}

function setProjectsArchiveModeUI(isArchive) {
    const title = document.querySelector('#projects .page-title');
    const subtitle = document.querySelector('#projects .page-subtitle');
    const backBtn = document.getElementById('projects-archive-back-btn');
    const addBtn = document.querySelector('#projects .projects-list-add-btn');
    const archiveOpenBtn = document.getElementById('projects-archive-open-btn');

    if (title) {
        title.textContent = isArchive ? t('projects.archived.title') : t('projects.title');
    }
    if (subtitle) {
        subtitle.textContent = isArchive ? t('projects.archived.subtitle') : t('projects.subtitle');
    }
    if (backBtn) {
        backBtn.style.display = isArchive ? 'inline-flex' : 'none';
    }
    if (addBtn) {
        addBtn.style.display = isArchive ? 'none' : 'inline-flex';
    }
    if (archiveOpenBtn) {
        archiveOpenBtn.style.display = isArchive ? 'none' : 'inline-flex';
    }

    // Swap the card/row list for the archived-projects table.
    const listContainer = document.querySelector('#projects .project-list-container');
    if (listContainer) listContainer.style.display = isArchive ? 'none' : '';
    const archivedProjectsView = document.getElementById('archived-projects-view');
    if (archivedProjectsView) archivedProjectsView.style.display = isArchive ? '' : 'none';
    // Card/row toggle is meaningless in table mode.
    const viewToggle = document.querySelector('#projects .projects-view-toggle');
    if (viewToggle) viewToggle.style.display = isArchive ? 'none' : '';
    // The Status filter is meaningless in the archive view (every row is archived,
    // and archive is reached via its own button — not a status). Hide it there.
    const statusFilterGroup = document.getElementById('group-project-status');
    if (statusFilterGroup) statusFilterGroup.style.display = isArchive ? 'none' : '';
}

function openProjectsArchiveView() {
    // Archive is a route, not a status filter — switching is purely route + UI.
    setProjectsArchiveModeUI(true);
    safeReplaceState(null, '', '#projects/archive');
    applyProjectFilters();
    syncURLWithProjectFilters();
}

function leaveProjectsArchiveView() {
    clearArchivedProjectSelection();
    setProjectsArchiveModeUI(false);
    safeReplaceState(null, '', '#projects');
    applyProjectFilters();
    syncURLWithProjectFilters();
}

// Sync current project filter state to URL for shareable links and browser history
function syncURLWithProjectFilters() {
    // Only sync when the projects page is active (prevents overwriting other page URLs)
    if (!window.location.hash.startsWith('#projects')) return;
    const archiveRoute = isProjectsArchiveRoute();

    const params = new URLSearchParams();
    const state = loadProjectsViewState() || {};

    // Add search query from live filter state (avoid resurrecting stale URL params from saved state)
    const searchValue = projectFilterState.search || '';
    if (searchValue && searchValue.trim() !== "") {
        params.set("search", searchValue.trim());
    }

    // Add status filter (from projectFilterState - comma-separated).
    // 'archived' is never a status — the archive view is the #projects/archive route.
    if (projectFilterState.statuses.size > 0) {
        params.set("status", Array.from(projectFilterState.statuses).join(","));
    }

    // Add status exclude mode
    if (projectFilterState.statusExcludeMode) {
        params.set("statusExclude", "1");
    }

    // Add chip filter (has-tasks or no-tasks) from live state only
    const chipFilter = projectFilterState.taskFilter || '';
    if (chipFilter && ['has-tasks', 'no-tasks'].includes(chipFilter)) {
        params.set("filter", chipFilter);
    }

    const activeSort = projectSortState?.lastSort || state.sort || DEFAULT_PROJECT_SORT;
    const activeSortDirection = projectSortState?.direction || state.sortDirection || 'asc';

    // Add sort
    if (activeSort && activeSort !== DEFAULT_PROJECT_SORT) {
        params.set("sort", activeSort);
    }

    // Add sort direction (only if not default)
    if (activeSortDirection && activeSortDirection !== 'asc') {
        params.set("sortDirection", activeSortDirection);
    }

    // Add updated filter from live state only
    const updatedFilter = projectFilterState.updatedFilter || 'all';
    if (updatedFilter && updatedFilter !== 'all') {
        params.set("updatedFilter", updatedFilter);
    }

    // Add completion bounds
    if (projectFilterState.completionMin != null) {
        params.set("completionMin", String(projectFilterState.completionMin));
    }
    if (projectFilterState.completionMax != null) {
        params.set("completionMax", String(projectFilterState.completionMax));
    }

    // Add tags filter (comma-separated)
    if (projectFilterState.tags.size > 0) {
        params.set("tags", Array.from(projectFilterState.tags).join(","));
    }

    // Add tag exclude mode
    if (projectFilterState.tagExcludeMode) {
        params.set("tagExclude", "1");
    }

    // Add view mode (only if non-default)
    const viewMode = localStorage.getItem('projectsViewMode');
    if (viewMode && viewMode === 'rows') {
        params.set("view", "rows");
    }

    // Build new URL
    const queryString = params.toString();
    const baseHash = archiveRoute ? '#projects/archive' : '#projects';
    const newHash = queryString ? `${baseHash}?${queryString}` : baseHash;

    // Update URL without triggering hashchange event (prevents infinite loop)
    if (window.location.hash !== newHash) {
        safeReplaceState(null, "", newHash);
    }
}

function saveProjectsViewState(state) {
    try {
        localStorage.setItem('projectsViewState', JSON.stringify(state));
        // Sync URL with the new state
        syncURLWithProjectFilters();
    } catch (e) {}
}

function loadProjectsViewState() {
    try {
        const raw = localStorage.getItem('projectsViewState');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;

        // Normalize legacy/invalid values so UI doesn't show "Clear Filters" when nothing is applied.
        // `filter: "clear"` used to be treated as a sentinel; treat it as "no filter".
        if (parsed.filter === 'clear') parsed.filter = '';
        if (parsed.filter && parsed.filter !== 'has-tasks' && parsed.filter !== 'no-tasks') parsed.filter = '';
        if (parsed.sort === 'default') parsed.sort = 'status';
        if (!parsed.sort) parsed.sort = DEFAULT_PROJECT_SORT;
        if (parsed.updatedFilter == null) parsed.updatedFilter = 'all';
        if (parsed.updatedFilter && typeof parsed.updatedFilter === 'string') {
            const allowed = new Set(['all', '5m', '30m', '24h', 'week', 'month']);
            if (!allowed.has(parsed.updatedFilter)) parsed.updatedFilter = 'all';
        }

        return parsed;
    } catch (e) { return null; }
}


// Helper: render a view array of projects (used by search/chips)
function renderView(view) {
    // Archive route renders a table view instead of the card/row list.
    if (isProjectsArchiveRoute()) {
        renderArchivedProjectsView(view || []);
        return;
    }
    const container = document.getElementById('projects-list');
    if (!container) return;
    if (!view || view.length === 0) {
        container.innerHTML = `<div class="empty-state"><h3>${t('projects.empty.filteredTitle')}</h3></div>`;
        renderMobileProjects([]);
        syncProjectListRowsWidthToControls();
        updateProjectsListCount(0);
        updateProjectsExpandAllButton();
        return;
    }

    // Save expanded state before re-rendering
    const expandedProjects = loadProjectsExpandedIds();
    container.querySelectorAll('[id^="project-item-"].expanded').forEach(item => {
        const projectId = item.id.replace('project-item-', '');
        expandedProjects.add(projectId);
    });

    // Re-render with new list layout
    container.innerHTML = view.map(generateProjectItemHTML).join('');

    const renderedProjectIds = new Set(view.map(p => String(p.id)));
    const expandedToRestore = new Set(Array.from(expandedProjects).filter(id => renderedProjectIds.has(String(id))));

    // Restore expanded state
    expandedToRestore.forEach(projectId => {
        const item = document.getElementById(`project-item-${projectId}`);
        if (item) {
            item.classList.add('expanded');
        }
    });
    saveProjectsExpandedIds(expandedToRestore);

    renderMobileProjects(view);
    scheduleExpandedTaskRowLayoutUpdate(container);
    setupExpandedProjectsDragDrop();
    syncProjectListRowsWidthToControls();
    updateProjectsListCount(view.length);
    updateProjectsExpandAllButton();
}

// Initialize and persist project header controls
function setupProjectsControls() {
    const sel = document.getElementById('projects-sort');
    const sortBtn = document.getElementById('projects-sort-btn');
    const sortLabel = document.getElementById('projects-sort-label');
    const search = document.getElementById('projects-search');
    const chips = Array.from(document.querySelectorAll('.pf-chip'));

    // Load saved state from localStorage
    const saved = loadProjectsViewState() || { search: '', filter: '', sort: DEFAULT_PROJECT_SORT, sortDirection: 'asc', updatedFilter: 'all' };

    // Merge with URL parameters (URL params take priority for deep linking)
    const urlFilters = window.urlProjectFilters || {};
    const archiveRoute = isProjectsArchiveRoute() || urlFilters.isArchivedRoute === true;
    setProjectsArchiveModeUI(archiveRoute);

    const archiveBackBtn = document.getElementById('projects-archive-back-btn');
    if (archiveBackBtn && !archiveBackBtn.__projectsArchiveBackBound) {
        archiveBackBtn.__projectsArchiveBackBound = true;
        archiveBackBtn.addEventListener('click', leaveProjectsArchiveView);
    }
    const archiveOpenBtn = document.getElementById('projects-archive-open-btn');
    if (archiveOpenBtn && !archiveOpenBtn.__projectsArchiveOpenBound) {
        archiveOpenBtn.__projectsArchiveOpenBound = true;
        archiveOpenBtn.addEventListener('click', openProjectsArchiveView);
    }

    const mergedState = {
        search: urlFilters.search !== undefined ? urlFilters.search : saved.search,
        filter: urlFilters.filter !== undefined ? urlFilters.filter : saved.filter,
        sort: urlFilters.sort !== undefined ? urlFilters.sort : saved.sort,
        sortDirection: urlFilters.sortDirection !== undefined ? urlFilters.sortDirection : saved.sortDirection,
        updatedFilter: urlFilters.updatedFilter !== undefined ? urlFilters.updatedFilter : saved.updatedFilter
    };

    // Sanitize merged task filter value (prevents phantom "Clear Filters" on refresh)
    if (mergedState.filter === 'clear') mergedState.filter = '';
    if (mergedState.filter && mergedState.filter !== 'has-tasks' && mergedState.filter !== 'no-tasks') {
        mergedState.filter = '';
    }

    // Apply completion bounds from URL (if present)
    if (urlFilters.completionMin !== undefined || urlFilters.completionMax !== undefined) {
        projectFilterState.completionMin = urlFilters.completionMin ?? null;
        projectFilterState.completionMax = urlFilters.completionMax ?? null;
        syncProjectCompletionRadioFromState();
        updateProjectCompletionBadge();
    }

    // Apply status filters from URL (if present)
    if (Array.isArray(urlFilters.statuses)) {
        projectFilterState.statuses.clear();
        urlFilters.statuses.forEach(status => projectFilterState.statuses.add(status));

        // Update checkbox UI
        const statusCheckboxes = document.querySelectorAll('input[type="checkbox"][data-filter="project-status"]');
        statusCheckboxes.forEach(cb => {
            cb.checked = projectFilterState.statuses.has(cb.value);
        });
        updateProjectStatusBadge();
    }

    // Apply status exclude mode from URL
    if (urlFilters.statusExcludeMode !== undefined) {
        projectFilterState.statusExcludeMode = urlFilters.statusExcludeMode;
        updateProjectFilterModeUI('project-status');
    }

    // Apply focused project-ID filter from URL (Ask Kai "Ver"); reset when absent so it
    // does not linger across navigations, mirroring the tasks taskIds behavior.
    projectFilterState.projectIds.clear();
    projectFilterState.projectIdsSource = urlFilters.projectIdsSource || '';
    if (Array.isArray(urlFilters.projectIds)) {
        urlFilters.projectIds.forEach(id => projectFilterState.projectIds.add(String(id)));
    }

    // Apply tags filter from URL
    if (urlFilters.tags && Array.isArray(urlFilters.tags) && urlFilters.tags.length > 0) {
        projectFilterState.tags.clear();
        urlFilters.tags.forEach(tag => projectFilterState.tags.add(tag));
        updateProjectTagsBadge();
        // Re-populate tag options so checkboxes reflect the restored state
        // (populateProjectTagOptions ran before URL tags were applied in showPage)
        try { populateProjectTagOptions(); } catch (e) {}
    }

    // Apply tag exclude mode from URL
    if (urlFilters.tagExcludeMode !== undefined) {
        projectFilterState.tagExcludeMode = urlFilters.tagExcludeMode;
        updateProjectFilterModeUI('project-tags');
    }

    // Apply search to projectFilterState
    if (mergedState.search) {
        projectFilterState.search = mergedState.search;
    }

    // Apply chip filter to projectFilterState
    if (mergedState.filter) {
        projectFilterState.taskFilter = mergedState.filter;
    }

    // Clear URL filters after reading them (one-time use for deep linking)
    window.urlProjectFilters = null;

    // Restore sort state (normalize legacy name sort keys)
    let restoredSort = mergedState.sort || DEFAULT_PROJECT_SORT;
    let restoredDirection = mergedState.sortDirection || 'asc';
    if (restoredSort === 'name-asc') {
        restoredSort = 'name';
        restoredDirection = 'asc';
    } else if (restoredSort === 'name-desc') {
        restoredSort = 'name';
        restoredDirection = 'desc';
    } else if (restoredSort === 'default') {
        restoredSort = 'status';
    }
    projectSortState.lastSort = restoredSort;
    projectSortState.direction = restoredDirection;

    try {
        localStorage.setItem('projectsViewState', JSON.stringify({
            ...saved,
            search: mergedState.search || '',
            filter: mergedState.filter || '',
            sort: restoredSort,
            sortDirection: restoredDirection,
            updatedFilter: mergedState.updatedFilter || 'all'
        }));
    } catch (e) {}

    // Apply merged search value to the input (don't render yet)
    if (search) search.value = mergedState.search || '';

    // Apply merged chip selection only if it maps to an existing chip (has-tasks or no-tasks only)
    // Fall back to projectFilterState.taskFilter when no URL/localStorage value exists (navigation case)
    if (chips && chips.length) {
        chips.forEach(c => c.classList.remove('active'));
        const effectiveChipFilter = mergedState.filter || projectFilterState.taskFilter || '';
        if (effectiveChipFilter && ['has-tasks','no-tasks'].includes(effectiveChipFilter)) {
            const activeChip = chips.find(c => c.dataset.filter === effectiveChipFilter);
            if (activeChip) activeChip.classList.add('active');
        }
    }

    // Filtering/sorting/rendering is owned solely by applyProjectFilters() (called
    // at the end of setup). Never reimplement the filter here — a second copy drifts
    // out of sync (e.g. it would miss the archive route and leak active projects).

    // Apply merged sort label with direction indicator
    if (sortBtn) {
        const sortKey = mergedState.sort || DEFAULT_PROJECT_SORT;
        const baseLabel = getProjectSortLabel(sortKey);
        const directionIndicator = projectSortState.direction === 'asc' ? 'asc' : 'desc';
        const labelText = t('projects.sort.prefix', { label: baseLabel, direction: directionIndicator });
        if (sortLabel) sortLabel.textContent = labelText;
        const arrow = document.querySelector('#projects-sort-btn .sort-label-arrow');
        if (arrow) setProjectsSortArrow(arrow, directionIndicator);
    }

    // Restore updated filter state + UI
    {
        const allowed = new Set(['all', '5m', '30m', '24h', 'week', 'month']);
        const normalized = allowed.has(mergedState.updatedFilter) ? mergedState.updatedFilter : 'all';
        projectFilterState.updatedFilter = normalized;
        updateProjectsUpdatedFilterUI();
        try { renderProjectsActiveFilterChips(); } catch (e) {}

        document
            .querySelectorAll('input[type="radio"][data-filter="project-updated"][name="project-updated-filter"]')
            .forEach((rb) => {
                if (rb.__projectUpdatedBound) return;
                rb.__projectUpdatedBound = true;
                rb.addEventListener('change', () => {
                    if (!rb.checked) return;
                    projectFilterState.updatedFilter = allowed.has(rb.value) ? rb.value : 'all';
                    updateProjectsUpdatedFilterUI();
                    applyProjectFilters();
                    const cur = loadProjectsViewState() || mergedState;
                    saveProjectsViewState({ ...cur, updatedFilter: projectFilterState.updatedFilter });
                    updateProjectsClearButtonVisibility();
                });
            });

        document
            .querySelectorAll('input[type="radio"][data-filter="project-completion"][name="project-completion-filter"]')
            .forEach((rb) => {
                if (rb.__projectCompletionBound) return;
                rb.__projectCompletionBound = true;
                rb.addEventListener('change', () => {
                    if (!rb.checked) return;
                    setProjectCompletionFilter(rb.value);
                    updateProjectsClearButtonVisibility();
                });
            });
    }

    // Finally: run the single filter + sort + render pipeline (route-aware).
    applyProjectFilters();

    // Restore view mode (from URL/localStorage) — must run after render since applyProjectFilters
    // calls renderView() which only sets innerHTML and never touches the container's own classes.
    const restoredViewMode = localStorage.getItem('projectsViewMode') || 'cards';
    const projectListEl = document.getElementById('projects-list');
    if (projectListEl) projectListEl.classList.toggle('view-rows', restoredViewMode === 'rows');
    document.getElementById('btn-view-cards')?.classList.toggle('active', restoredViewMode === 'cards');
    document.getElementById('btn-view-rows')?.classList.toggle('active', restoredViewMode === 'rows');
    setupProjectsRowsWidthObserver();
    syncProjectListRowsWidthToControls();
    updateProjectsExpandAllButton();

    // Event listeners are set up once in DOMContentLoaded - no need to add duplicates here

    // Ensure visibility is synced after setup
    updateProjectsClearButtonVisibility();

    // Sync URL to reflect actual current filter state. This is the key step that makes
    // refresh work reliably: when the user navigates here via the nav (URL = #projects,
    // no params), the retained projectFilterState is applied above but the URL still shows
    // no filters. Without this call, a subsequent refresh would lose all those filters.
    try { syncURLWithProjectFilters(); } catch (e) {}
}

// Show/hide the Projects-specific Clear button when a projects filter/search is active
function updateProjectsClearButtonVisibility() {
    const btn = document.getElementById('btn-clear-projects');
    if (!btn) return;
    const hasSearch = projectFilterState.search && projectFilterState.search.trim() !== '';
    const hasStatusFilter = projectFilterState.statuses.size > 0;
    const hasTaskFilter = projectFilterState.taskFilter !== '';
    const hasUpdatedFilter = projectFilterState.updatedFilter && projectFilterState.updatedFilter !== 'all';
    const hasTagsFilter = projectFilterState.tags.size > 0;
    const hasProjectIdsFilter = projectFilterState.projectIds.size > 0;
    const hasCompletionFilter = projectFilterState.completionMin != null || projectFilterState.completionMax != null;
    const shouldShow = hasSearch || hasStatusFilter || hasTaskFilter || hasUpdatedFilter || hasTagsFilter || hasProjectIdsFilter || hasCompletionFilter;
    btn.style.display = shouldShow ? 'inline-flex' : 'none';
}

// Clear all project filters
function clearProjectFilters() {
    projectFilterState.search = '';
    projectFilterState.statuses.clear();
    projectFilterState.statusExcludeMode = false;
    projectFilterState.taskFilter = '';
    projectFilterState.updatedFilter = 'all';
    projectFilterState.tags.clear();
    projectFilterState.tagExcludeMode = false;
    projectFilterState.projectIds.clear();
    projectFilterState.projectIdsSource = '';
    projectFilterState.completionMin = null;
    projectFilterState.completionMax = null;

    // Clear UI
    const searchEl = document.getElementById('projects-search');
    if (searchEl) searchEl.value = '';
    document
        .querySelectorAll('input[type="radio"][data-filter="project-completion"]')
        .forEach(rb => { rb.checked = rb.value === 'all'; });
    updateProjectCompletionBadge();

    const chips = Array.from(document.querySelectorAll('.pf-chip'));
    chips.forEach(c => c.classList.remove('active'));

    const checkboxes = document.querySelectorAll('input[type="checkbox"][data-filter="project-status"]');
    checkboxes.forEach(cb => {
        cb.checked = false;
    });

    const tagCheckboxes = document.querySelectorAll('input[type="checkbox"][data-filter="project-tags"]');
    tagCheckboxes.forEach(cb => cb.checked = false);

    document
        .querySelectorAll('input[type="radio"][data-filter="project-updated"][name="project-updated-filter"]')
        .forEach(rb => rb.checked = rb.value === 'all');
    updateProjectsUpdatedFilterUI();
    updateProjectFilterModeUI('project-status');
    updateProjectFilterModeUI('project-tags');

    updateProjectStatusBadge();
    updateProjectTagsBadge();
    applyProjectFilters();
    const cur = loadProjectsViewState() || {};
    saveProjectsViewState({
        ...cur,
        search: '',
        filter: '',
        updatedFilter: 'all',
        statuses: [],
        tags: []
    });
    syncURLWithProjectFilters();
}

// ================================
// MOBILE NAVIGATION
// ================================

// Close mobile sidebar from anywhere
function closeMobileSidebar() {
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.getElementById('sidebar-overlay');
    if (!sidebar || !overlay) return;
    sidebar.classList.remove('active');
    overlay.classList.remove('active');
    document.body.style.overflow = '';
}

// Initialize mobile navigation
function initMobileNav() {
    const hamburgerBtn = document.getElementById('hamburger-btn');
    const sidebar = document.querySelector('.sidebar');
    const overlay = document.getElementById('sidebar-overlay');

    if (!hamburgerBtn || !sidebar || !overlay) return;

    // Toggle sidebar
    function toggleSidebar() {
        sidebar.classList.toggle('active');
        overlay.classList.toggle('active');
        document.body.style.overflow = sidebar.classList.contains('active') ? 'hidden' : '';
    }

    // Close sidebar
    function closeSidebar() {
        closeMobileSidebar();
    }

    // Hamburger button click
    hamburgerBtn.addEventListener('click', toggleSidebar);

    // Overlay click
    overlay.addEventListener('click', closeSidebar);

    // Close sidebar when clicking nav items
    const navItems = sidebar.querySelectorAll('.nav-item');
    navItems.forEach(item => {
        item.addEventListener('click', () => {
            if (getIsMobileCached()) {
                closeSidebar();
            }
        });
    });

    // Close sidebar on window resize to desktop
    window.addEventListener('resize', () => {
        if (window.innerWidth > 768) {
            closeSidebar();
        }
    });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileNav);
} else {
    initMobileNav();
}

// ================================
// DESKTOP SIDEBAR COLLAPSE
// ================================

function initDesktopSidebarToggle() {
    // Mobile has its own slide-in sidebar — this toggle is desktop only
    if (window.innerWidth <= 768) return;

    const sidebar = document.querySelector('.sidebar');
    const collapseBtn = document.getElementById('sidebar-collapse-btn');

    if (!sidebar || !collapseBtn) return;

    const ICON_RAIL_WIDTH = 52;   // px — icon-only mode
    const TRANSITION_MS = 260;

    function collapseSidebar() {
        // Save expanded width (skip if already in icon-rail mode)
        const currentWidth = sidebar.getBoundingClientRect().width;
        if (currentWidth > ICON_RAIL_WIDTH + 10) {
            localStorage.setItem('sidebarWidth', Math.round(currentWidth));
        }
        // Hide text FIRST (instant), then animate width — no squishing
        sidebar.classList.add('collapsed');
        sidebar.classList.add('animating');
        sidebar.style.width = ICON_RAIL_WIDTH + 'px';
        localStorage.setItem('sidebarCollapsed', 'true');
        setTimeout(() => sidebar.classList.remove('animating'), TRANSITION_MS);
    }

    function expandSidebar() {
        const savedWidth = parseInt(localStorage.getItem('sidebarWidth'), 10) || 280;
        // Pin sidebar's direct children at the target width so they don't reflow
        // as the sidebar grows. overflow:hidden on .sidebar creates a smooth
        // wipe-reveal — content is already in its final layout, just clipped.
        const children = Array.from(sidebar.children);
        children.forEach(el => {
            el.style.width = savedWidth + 'px';
            el.style.minWidth = savedWidth + 'px';
        });
        sidebar.classList.add('animating');
        sidebar.classList.remove('collapsed'); // switch to full layout immediately — no end-of-animation pop
        sidebar.style.width = savedWidth + 'px';
        localStorage.setItem('sidebarCollapsed', 'false');
        setTimeout(() => {
            sidebar.classList.remove('animating');
            children.forEach(el => {
                el.style.width = '';
                el.style.minWidth = '';
            });
        }, TRANSITION_MS);
    }

    // Single button acts as toggle
    collapseBtn.addEventListener('click', () => {
        if (sidebar.classList.contains('collapsed')) {
            expandSidebar();
        } else {
            collapseSidebar();
        }
    });

    const docsRailButton = document.getElementById('nav-docs-rail-button');
    if (docsRailButton) {
        docsRailButton.addEventListener('click', () => {
            if (sidebar.classList.contains('collapsed')) {
                expandSidebar();
            }
        });
    }

    // Restore saved state on load — no animation (instant)
    if (localStorage.getItem('sidebarCollapsed') === 'true') {
        sidebar.style.width = ICON_RAIL_WIDTH + 'px';
        sidebar.classList.add('collapsed');
    } else {
        const savedWidth = parseInt(localStorage.getItem('sidebarWidth'), 10);
        if (savedWidth) sidebar.style.width = savedWidth + 'px';
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDesktopSidebarToggle);
} else {
    initDesktopSidebarToggle();
}

window.addEventListener('resize', () => {
    scheduleExpandedTaskRowLayoutUpdate();
});
